import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  Archive,
  BadgeCheck,
  BadgeDollarSign,
  Banknote,
  Bell,
  BookOpen,
  Building2,
  CalendarClock,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  CreditCard,
  Download,
  ExternalLink,
  Filter,
  Gauge,
  Globe2,
  Grid3X3,
  Heart,
  Home,
  Image as ImageIcon,
  LayoutDashboard,
  ListFilter,
  Loader2,
  LockKeyhole,
  Mail,
  Map,
  MapPinned,
  MapPin,
  Menu,
  MessageCircle,
  MoreHorizontal,
  Navigation,
  Phone,
  Pencil,
  PlayCircle,
  Plus,
  RotateCcw,
  Save,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  Trophy,
  UserCheck,
  UserCircle,
  UserPlus,
  UsersRound,
  Video,
  WalletCards,
  X,
  XCircle
} from "lucide-react";
import { isPublicCourtHireCentre } from "./centreListingClassification.js";
import { policyDetailSections, supportWorkflows } from "./legalPolicyContent.js";
import { blogPosts, getBlogPost } from "./blogContent.js";
import {
  getVenueSourceCategory,
  isBlockedPublicVenueUrl,
  sanitisePublicVenueAmenities,
  sanitisePublicVenueDescription,
  sanitisePublicVenueDetailItems,
  sanitisePublicVenueLinks
} from "./publicVenueSafety.js";
import {
  amountToMinorUnits,
  currencyForCountryCode,
  formatMoney,
  formatMoneyFromMinorUnits,
  minorUnitsToAmount,
  normaliseCurrencyCode
} from "./currencyUtils.js";
import {
  buildSocialProfileUrl,
  detectMediaPlatform,
  getMediaPlatformMeta,
  getSocialPlatformMeta,
  mediaLabelForPlatform,
  mediaLinkInputError,
  mediaPlatformOptions,
  normaliseMediaLinkUrl,
  socialInputFromLink,
  socialPlatformOptions,
  socialProfileInputError
} from "./profileLinkUtils.js";
import { playerLandingFaq } from "./playerLandingFaq.js";
import {
  changedProfileFormFields,
  getPendingProfileDraftDirtyFields,
  getProfileDraftDirtyFields,
  mergeProfileDraftForm,
  profileFormFingerprint
} from "./profileDraftUtils.js";
import { ProfilePlanPricing, ProfileSubscriptionPanel } from "./ProfileSubscriptionUI.jsx";
import {
  accountProfileSubscription,
  getProfileSubscriptionPlan,
  profileSubscriptionRoleOrder
} from "./profileSubscriptionPlans.js";
import { resetPageScroll } from "./routeScroll.js";
import { initializeNativeAppShell } from "./nativeAppBridge.js";
import { initialiseNativeUpdates } from "./nativeUpdates.js";
import {
  confirmNativePhoneCode,
  isNativePhoneAuthAvailable,
  sendNativePhoneCode
} from "./nativePhoneAuth.js";
import "leaflet/dist/leaflet.css";
import "./styles.css";

initializeNativeAppShell().catch(() => undefined);
initialiseNativeUpdates();

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const PlayerSolutionLanding = React.lazy(() => import("./PlayerSolutionLanding.jsx"));
const RoleSolutionLanding = React.lazy(() => import("./RoleSolutionLanding.jsx"));

let initializeAppCheck;
let initializeRecaptchaConfig;
let ReCaptchaV3Provider;
let onAuthStateChanged;
let RecaptchaVerifier;
let signInWithPhoneNumber;
let signInWithCredential;
let PhoneAuthProvider;
let signOut;
let updateProfile;
let collection;
let deleteField;
let doc;
let getDocs;
let limit;
let onSnapshot;
let query;
let serverTimestamp;
let setDoc;
let where;
let writeBatch;
let httpsCallable;
let getDownloadURL;
let storageRef;
let uploadBytes;
let getPhoneCountries;
let getCountryCallingCode;
let parsePhoneNumberFromString;
let L = null;
let leafletRuntimePromise = null;

const loadLeafletRuntime = async () => {
  if (!leafletRuntimePromise) leafletRuntimePromise = (async () => {
    if (!L) {
      const leafletModule = await import("leaflet");
      L = leafletModule.default || leafletModule;
    }
    return L;
  })().catch((error) => {
    leafletRuntimePromise = null;
    throw error;
  });
  return leafletRuntimePromise;
};

const googleAnalyticsMeasurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID || "G-L2NHVFDLHE";
const microsoftClarityProjectId = import.meta.env.VITE_MICROSOFT_CLARITY_PROJECT_ID || "xlr5910mzf";
const metaPixelId = import.meta.env.VITE_META_PIXEL_ID || "3592858227528168";
const tiktokPixelId = import.meta.env.VITE_TIKTOK_PIXEL_ID || "D9Q2BQRC77U97D5QBLT0";
const marketingScriptIds = {
  clarity: "badminta-clarity",
  ga4: "badminta-ga4",
  meta: "badminta-meta-pixel",
  tiktok: "badminta-tiktok-pixel"
};
const marketingAttributionStorageKey = "badminta:marketing-attribution:v1";
const privacyConsentStorageKey = "badminta:privacy-consent:v1";
const legalDeclarationStorageKey = "badminta:legal-declaration:v1";
const legalPolicyVersion = "6 August 2026";
const signupIntentStorageKey = "badminta:signup-intent:v1";
const signupIdentityDraftStorageKey = "badminta:signup-identity-draft:v1";
const authReturnIntentStorageKey = "badminta:auth-return-intent:v1";
const marketingAttributionParams = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ttclid"];
const signupRoleIntents = {
  player: { role: "player", returnPage: "profileSetup", sourcePage: "solutionPlayers" },
  coach: { role: "coach", returnPage: "coachProfile", sourcePage: "solutionCoaches" },
  organiser: { role: "organiser", returnPage: "createGroup", sourcePage: "solutionOrganisers" },
  centre: { role: "centre", returnPage: "claimCentre", sourcePage: "solutionCentres" }
};
const signupSourceSlugs = {
  solutionPlayers: "badminton-players",
  solutionCoaches: "badminton-coaches",
  solutionOrganisers: "social-badminton-groups",
  solutionCentres: "badminton-centres"
};

const cleanMarketingValue = (value = "", maxLength = 180) => String(value || "").trim().slice(0, maxLength);

const readPrivacyConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(privacyConsentStorageKey) || "null");
    return value?.version === legalPolicyVersion ? value : null;
  } catch {
    return null;
  }
};

const analyticsConsentGranted = () => Boolean(readPrivacyConsent()?.analytics);
const advertisingConsentGranted = () => Boolean(readPrivacyConsent()?.advertising);

const readStoredMarketingAttribution = () => {
  if (typeof window === "undefined") return {};
  try {
    const stored = JSON.parse(window.localStorage.getItem(marketingAttributionStorageKey) || "{}");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
};

const captureMarketingAttribution = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const current = marketingAttributionParams.reduce((output, key) => {
    const value = cleanMarketingValue(params.get(key));
    if (value) output[key] = value;
    return output;
  }, {});
  if (!Object.keys(current).length) return readStoredMarketingAttribution();

  const attribution = {
    ...readStoredMarketingAttribution(),
    ...current,
    landing_path: `${window.location.pathname}${window.location.search}`.slice(0, 500),
    captured_at: new Date().toISOString()
  };
  try {
    window.localStorage.setItem(marketingAttributionStorageKey, JSON.stringify(attribution));
  } catch {
    // Attribution still reaches the current-page event when storage is unavailable.
  }
  return attribution;
};

const cleanSignupLocation = (location = null) => {
  if (!location || typeof location !== "object") return null;
  const lat = Number(location.lat);
  const lon = Number(location.lon ?? location.lng);
  const label = cleanMarketingValue(location.label || location.suburb || location.city, 180);
  const city = cleanMarketingValue(location.city, 120);
  const suburb = cleanMarketingValue(location.suburb, 120);
  if (!label && !city && !suburb && !Number.isFinite(lat) && !Number.isFinite(lon)) return null;
  return {
    id: cleanMarketingValue(location.id, 220),
    label,
    city,
    suburb,
    state: cleanMarketingValue(location.state, 120),
    postcode: cleanMarketingValue(location.postcode, 32),
    country: cleanMarketingValue(location.country, 120),
    countryCode: cleanMarketingValue(location.countryCode, 8).toUpperCase(),
    type: ["nearby", "suburb", "city", "country"].includes(location.type) ? location.type : suburb ? "suburb" : "city",
    lat: Number.isFinite(lat) ? lat : null,
    lon: Number.isFinite(lon) ? lon : null,
    bbox: Array.isArray(location.bbox) ? location.bbox.slice(0, 4).map(Number).filter(Number.isFinite) : undefined,
    source: cleanMarketingValue(location.source || "Signup location", 80)
  };
};

const saveSignupIntent = ({ role = "player", returnPage = "profileSetup", sourcePage = "", sourcePath = "", location = null } = {}) => {
  if (typeof window === "undefined") return;
  const safeRole = ["player", "coach", "organiser", "centre", "account"].includes(role) ? role : "account";
  const safeReturnPage = ["profileSetup", "coachProfile", "createGroup", "claimCentre"].includes(returnPage)
    ? returnPage
    : "onboarding";
  const existing = loadSignupIntent();
  try {
    window.localStorage.setItem(signupIntentStorageKey, JSON.stringify({
      role: safeRole,
      returnPage: safeReturnPage,
      sourcePage: cleanMarketingValue(sourcePage || existing.sourcePage, 80),
      sourcePath: cleanMarketingValue(sourcePath || existing.sourcePath || `${window.location.pathname}${window.location.search}`, 500),
      location: cleanSignupLocation(location || existing.location),
      createdAt: existing.role === safeRole && existing.createdAt ? existing.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
  } catch {
    // Signup still works without a persisted intent; the user can choose a role manually.
  }
};

const loadSignupIntent = () => {
  if (typeof window === "undefined") return {};
  try {
    const intent = JSON.parse(window.localStorage.getItem(signupIntentStorageKey) || "{}");
    const createdAt = Date.parse(String(intent?.createdAt || ""));
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > 7 * 24 * 60 * 60 * 1000) return {};
    return intent;
  } catch {
    return {};
  }
};

const clearSignupIntent = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(signupIntentStorageKey);
  } catch {
    // A stale intent is harmless when storage cannot be changed.
  }
};

const readLegalDeclaration = () => {
  if (typeof window === "undefined") return {};
  try {
    const declaration = JSON.parse(window.localStorage.getItem(legalDeclarationStorageKey) || "{}");
    return declaration && typeof declaration === "object" ? declaration : {};
  } catch {
    return {};
  }
};

const saveAuthReturnIntent = ({ path = "", action = "continue" } = {}) => {
  if (typeof window === "undefined") return;
  const safePath = String(path || "").trim();
  if (!safePath.startsWith("/") || safePath.startsWith("//")) return;
  try {
    window.sessionStorage.setItem(authReturnIntentStorageKey, JSON.stringify({
      path: safePath.slice(0, 1200),
      action: cleanMarketingValue(action, 80),
      createdAt: new Date().toISOString()
    }));
  } catch {
    // The user can still continue manually if private browsing blocks storage.
  }
};

const consumeAuthReturnIntent = () => {
  if (typeof window === "undefined") return {};
  try {
    const intent = JSON.parse(window.sessionStorage.getItem(authReturnIntentStorageKey) || "{}");
    window.sessionStorage.removeItem(authReturnIntentStorageKey);
    const createdAt = Date.parse(String(intent?.createdAt || ""));
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > 60 * 60 * 1000) return {};
    const path = String(intent?.path || "").trim();
    if (!path.startsWith("/") || path.startsWith("//")) return {};
    return { path, action: cleanMarketingValue(intent.action, 80) };
  } catch {
    return {};
  }
};

const loadSignupIdentityDraft = () => {
  if (typeof window === "undefined") return {};
  try {
    const draft = JSON.parse(window.localStorage.getItem(signupIdentityDraftStorageKey) || "{}");
    const updatedAt = Date.parse(String(draft?.updatedAt || ""));
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > 7 * 24 * 60 * 60 * 1000) return {};
    return draft && typeof draft === "object" ? draft : {};
  } catch {
    return {};
  }
};

const saveSignupIdentityDraft = (draft = {}) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(signupIdentityDraftStorageKey, JSON.stringify({
      role: ["player", "coach", "organiser", "centre"].includes(draft.role) ? draft.role : "",
      fullName: cleanMarketingValue(draft.fullName, 160),
      email: cleanMarketingValue(draft.email, 320).toLowerCase(),
      location: cleanSignupLocation(draft.location),
      updatedAt: new Date().toISOString()
    }));
  } catch {
    // The current signup can continue in memory when storage is unavailable.
  }
};

const clearSignupIdentityDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(signupIdentityDraftStorageKey);
  } catch {
    // A completed signup is still valid when local storage cannot be changed.
  }
};

const loadMarketingScript = (id, src) => {
  if (typeof document === "undefined" || document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const initialiseGoogleAnalytics = () => {
  if (typeof window === "undefined" || !analyticsConsentGranted() || window.__badmintaGa4Initialised) return;
  window.__badmintaGa4Initialised = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  loadMarketingScript(
    marketingScriptIds.ga4,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsMeasurementId)}`
  );
  window.gtag("js", new Date());
  window.gtag("config", googleAnalyticsMeasurementId, { send_page_view: false });
};

const initialiseMicrosoftClarity = () => {
  if (typeof window === "undefined" || !analyticsConsentGranted() || window.__badmintaClarityInitialised) return;
  window.__badmintaClarityInitialised = true;
  window.clarity = window.clarity || function clarity() {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };
  loadMarketingScript(
    marketingScriptIds.clarity,
    `https://www.clarity.ms/tag/${encodeURIComponent(microsoftClarityProjectId)}`
  );
};

const initialiseMetaPixel = () => {
  if (typeof window === "undefined" || !advertisingConsentGranted() || window.__badmintaMetaPixelInitialised) return;
  window.__badmintaMetaPixelInitialised = true;
  const fbq = window.fbq || function metaPixelQueue() {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
    else fbq.queue.push(arguments);
  };
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = fbq.queue || [];
  loadMarketingScript(marketingScriptIds.meta, "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", metaPixelId);
};

const initialiseTikTokPixel = () => {
  if (typeof window === "undefined" || !advertisingConsentGranted() || window.__badmintaTikTokPixelInitialised) return;
  window.__badmintaTikTokPixelInitialised = true;
  const ttq = window.ttq = window.ttq || [];
  const methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
  ttq.methods = methods;
  ttq.setAndDefer = (target, method) => {
    target[method] = (...args) => target.push([method, ...args]);
  };
  methods.forEach((method) => ttq.setAndDefer(ttq, method));
  ttq.instance = (pixelId) => {
    const instance = ttq._i?.[pixelId] || [];
    methods.forEach((method) => ttq.setAndDefer(instance, method));
    return instance;
  };
  ttq.load = (pixelId, options = {}) => {
    const eventsUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[pixelId] = [];
    ttq._i[pixelId]._u = eventsUrl;
    ttq._t = ttq._t || {};
    ttq._t[pixelId] = Date.now();
    ttq._o = ttq._o || {};
    ttq._o[pixelId] = options;
    loadMarketingScript(
      marketingScriptIds.tiktok,
      `${eventsUrl}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`
    );
  };
  window.TiktokAnalyticsObject = "ttq";
  ttq.load(tiktokPixelId);
};

const trackMarketingPageView = () => {
  if (typeof window === "undefined") return;
  const pagePath = `${window.location.pathname}${window.location.search}`;
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: pagePath,
      page_title: document.title
    });
  }
  if (typeof window.fbq === "function") window.fbq("track", "PageView");
  if (typeof window.ttq?.page === "function") window.ttq.page();
  if (typeof window.clarity === "function") window.clarity("set", "page_path", pagePath);
};

const initialiseMarketingAnalytics = () => {
  if (typeof window === "undefined") return;
  initialiseGoogleAnalytics();
  initialiseMicrosoftClarity();
  initialiseMetaPixel();
  initialiseTikTokPixel();
  if (window.__badmintaMarketingAnalyticsInitialised) return;
  window.__badmintaMarketingAnalyticsInitialised = true;
  trackMarketingPageView();

  const sendNextPageView = () => window.setTimeout(trackMarketingPageView, 0);
  ["pushState", "replaceState"].forEach((method) => {
    const original = window.history[method];
    window.history[method] = function patchedHistoryState(...args) {
      const result = original.apply(this, args);
      sendNextPageView();
      return result;
    };
  });
  window.addEventListener("popstate", sendNextPageView);
};

const marketingEventMetaNames = {
  view_player_signup_landing: "ViewContent",
  signup_started: "SignupStarted",
  phone_verification_requested: "PhoneVerificationRequested",
  phone_verification_completed: "Lead",
  role_selected: "RoleSelected",
  sign_up: "CompleteRegistration",
  profile_setup_started: "ProfileSetupStarted"
};

const marketingEventTikTokNames = {
  view_player_signup_landing: "ViewContent",
  signup_started: "ClickButton",
  phone_verification_requested: "SubmitForm",
  phone_verification_completed: "Contact",
  role_selected: "ClickButton",
  sign_up: "CompleteRegistration",
  profile_setup_started: "ClickButton"
};

const trackMarketingFunnelEvent = (eventName, {
  role = "player",
  dedupeId = "",
  dedupeScope = "session",
  extra = {}
} = {}) => {
  if (typeof window === "undefined" || !eventName) return false;
  if (!analyticsConsentGranted() && !advertisingConsentGranted()) return false;
  const safeRole = ["player", "coach", "organiser", "centre"].includes(role) ? role : "player";
  const safeDedupeId = cleanMarketingValue(dedupeId || `${window.location.pathname}:${safeRole}`, 220);
  const dedupeKey = `badminta:funnel:v3:${tiktokPixelId}:${eventName}:${safeDedupeId}`;
  const storage = dedupeScope === "persistent" ? window.localStorage : window.sessionStorage;
  try {
    if (storage.getItem(dedupeKey)) return false;
  } catch {
    // Tracking can continue when browser storage is unavailable.
  }

  const attribution = captureMarketingAttribution();
  initialiseMarketingAnalytics();
  const parameters = {
    profile_type: safeRole,
    funnel_stage: eventName,
    traffic_source: cleanMarketingValue(attribution.utm_source || (attribution.ttclid ? "tiktok" : attribution.fbclid ? "facebook" : "direct"), 80),
    traffic_medium: cleanMarketingValue(attribution.utm_medium || "", 80),
    traffic_campaign: cleanMarketingValue(attribution.utm_campaign || "", 120),
    traffic_content: cleanMarketingValue(attribution.utm_content || "", 120),
    ...extra
  };
  const eventId = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${eventName}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (typeof window.gtag === "function") window.gtag("event", eventName, parameters);
  const metaEventName = marketingEventMetaNames[eventName];
  if (typeof window.fbq === "function" && metaEventName) {
    const standardEvent = ["ViewContent", "Lead", "CompleteRegistration"].includes(metaEventName);
    window.fbq(standardEvent ? "track" : "trackCustom", metaEventName, {
      content_name: `${safeRole}_${eventName}`,
      status: true,
      ...parameters
    }, { eventID: eventId });
  }
  const tiktokEventName = marketingEventTikTokNames[eventName];
  if (typeof window.ttq?.track === "function" && tiktokEventName) {
    const contentId = `${safeRole}_${eventName}`;
    window.ttq.track(tiktokEventName, {
      content_id: contentId,
      content_name: contentId,
      content_type: "product",
      content_category: "badminton_profile",
      contents: [{
        content_id: contentId,
        content_name: contentId,
        content_type: "product",
        content_category: "badminton_profile"
      }],
      event_source_url: window.location.href,
      ...parameters
    }, { event_id: eventId });
  }
  if (typeof window.clarity === "function") {
    window.clarity("set", "profile_type", safeRole);
    if (parameters.traffic_campaign) window.clarity("set", "traffic_campaign", parameters.traffic_campaign);
    window.clarity("event", eventName);
  }
  try {
    storage.setItem(dedupeKey, new Date().toISOString());
  } catch {
    // Event delivery does not depend on local deduplication storage.
  }
  return true;
};

const scheduleMarketingAnalytics = () => {
  if (typeof window === "undefined") return;
  let fallbackTimer = 0;
  let idleCallback = 0;
  const start = () => {
    window.clearTimeout(fallbackTimer);
    if (idleCallback && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleCallback);
    window.removeEventListener("pointerdown", start);
    window.removeEventListener("keydown", start);
    window.removeEventListener("scroll", start);
    window.removeEventListener("wheel", start);
    initialiseMarketingAnalytics();
  };
  window.addEventListener("pointerdown", start, { once: true, passive: true });
  window.addEventListener("keydown", start, { once: true });
  window.addEventListener("scroll", start, { once: true, passive: true });
  window.addEventListener("wheel", start, { once: true, passive: true });
  fallbackTimer = window.setTimeout(() => {
    if (typeof window.requestIdleCallback === "function") {
      idleCallback = window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      start();
    }
  }, 12000);
};

const trackProfileCreated = ({ profileId = "", role = "player" } = {}) => {
  if (typeof window === "undefined" || !profileId) return false;
  const profileType = ["player", "coach", "organiser", "centre"].includes(role) ? role : "player";
  const dedupeKey = `badminta:conversion:v3:${tiktokPixelId}:${profileType}-profile:${String(profileId).trim()}`;
  try {
    if (window.localStorage.getItem(dedupeKey)) return false;
  } catch {
    // Tracking still works when storage is unavailable; the backend remains the source of truth.
  }

  initialiseMarketingAnalytics();
  const eventId = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${profileType}-profile-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  if (typeof window.gtag === "function") {
    window.gtag("event", "profile_created", {
      profile_type: profileType,
      registration_status: "published"
    });
  }
  if (typeof window.fbq === "function") {
    window.fbq("trackCustom", "ProfilePublished", {
      content_name: `${profileType}_profile`,
      status: true
    }, { eventID: eventId });
  }
  if (typeof window.ttq?.track === "function") {
    const contentId = `${profileType}_profile_${String(profileId).trim()}`.slice(0, 180);
    window.ttq.track("CompleteRegistration", {
      content_id: contentId,
      content_name: `${profileType}_profile_published`,
      content_type: "product",
      content_category: "badminton_profile",
      contents: [{
        content_id: contentId,
        content_name: `${profileType}_profile_published`,
        content_type: "product",
        content_category: "badminton_profile"
      }],
      event_source_url: window.location.href,
      profile_type: profileType,
      status: "published"
    }, { event_id: eventId });
  }
  if (typeof window.clarity === "function") {
    window.clarity("set", "profile_type", profileType);
    window.clarity("event", `profile_created_${profileType}`);
  }
  try {
    window.localStorage.setItem(dedupeKey, new Date().toISOString());
  } catch {
    // The completed backend transition prevents repeat events even without local storage.
  }
  return true;
};

captureMarketingAttribution();
scheduleMarketingAnalytics();

const publicDataUrl = (path) => `${import.meta.env.BASE_URL}${path.replace(/^\//, "")}`;

const loadPublicJson = async (path, fallback) => {
  try {
    const response = await fetch(publicDataUrl(path));
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    return await response.json();
  } catch (error) {
    console.error(`Could not load ${path}`, error);
    return fallback;
  }
};

const lightweightAuthBootstrap = /^\/account\/(?:signup|login)\/?$/.test(window.location.pathname);
const earlyDirectoryBootstrap = typeof window !== "undefined" ? window.__badmintaDirectoryBootstrap : null;
const loadFullDirectoryBootstrap = () => Promise.all([
  loadPublicJson("/data/badminton_centres.json", []),
  loadPublicJson("/data/runtime_city_index.json", []),
  loadPublicJson("/data/worldwide_country_coverage.json", { countries: {} }),
  loadPublicJson("/data/global-centres/manifest.json", { countries: {}, totalRows: 0, publicVenueRows: 0 }),
  loadPublicJson("/data/public_players.json", { players: [] }).then((payload) => Array.isArray(payload.players) ? payload.players : [])
]);
const loadRuntimeDirectoryBootstrap = async () => {
  const bootstrap = await loadPublicJson("/data/runtime_bootstrap.json", null);
  if (!bootstrap || !Array.isArray(bootstrap.centres) || !Array.isArray(bootstrap.cities)) {
    return loadFullDirectoryBootstrap();
  }
  return [
    bootstrap.centres,
    bootstrap.cities,
    bootstrap.coverage || { countries: {} },
    bootstrap.manifest || { countries: {}, totalRows: 0, publicVenueRows: 0 },
    Array.isArray(bootstrap.players) ? bootstrap.players : []
  ];
};
const directoryBootstrapPromise = lightweightAuthBootstrap
  ? Promise.resolve([[], [], { countries: {} }, { countries: {}, totalRows: 0, publicVenueRows: 0 }, []])
  : earlyDirectoryBootstrap
    ? earlyDirectoryBootstrap.catch(() => loadRuntimeDirectoryBootstrap())
    : loadRuntimeDirectoryBootstrap();

const [
  centreDbRows,
  globalCityRows,
  worldwideCountryCoverage,
  globalCentreShardManifest,
  publicPlayerBootstrapRows
] = await directoryBootstrapPromise;
if (earlyDirectoryBootstrap && typeof window !== "undefined") window.__badmintaDirectoryBootstrap = null;

const brandLogo = "/badminta-brand-logo.png";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || ""
};

const firebaseConfigured = ["apiKey", "authDomain", "projectId", "appId"].every((key) => Boolean(firebaseConfig[key]));
const firebaseAppCheckSiteKey = import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY || "";
let firebaseApp = null;
let firebaseAppCheck = null;
let firebaseAuth = null;
let firebaseDb = null;
let firebaseFunctions = null;
let firebaseStorage = null;
let firebaseRuntimePromise = null;
let firebaseRecaptchaConfigPromise = null;
let firebaseAppCheckInitialisationAttempted = false;
let firebaseAppCheckInitialisationError = null;

const loadFirebaseRuntime = async () => {
  if (!firebaseConfigured) return null;
  if (firebaseRuntimePromise) return firebaseRuntimePromise;

  firebaseRuntimePromise = Promise.all([
    import("firebase/app"),
    import("firebase/app-check"),
    import("firebase/auth"),
    import("firebase/firestore"),
    import("firebase/functions"),
    import("firebase/storage")
  ]).then(async ([appModule, appCheckModule, authModule, firestoreModule, functionsModule, storageModule]) => {
    initializeAppCheck = appCheckModule.initializeAppCheck;
    ReCaptchaV3Provider = appCheckModule.ReCaptchaV3Provider;
    initializeRecaptchaConfig = authModule.initializeRecaptchaConfig;
    onAuthStateChanged = authModule.onAuthStateChanged;
    RecaptchaVerifier = authModule.RecaptchaVerifier;
    signInWithPhoneNumber = authModule.signInWithPhoneNumber;
    signInWithCredential = authModule.signInWithCredential;
    PhoneAuthProvider = authModule.PhoneAuthProvider;
    signOut = authModule.signOut;
    updateProfile = authModule.updateProfile;
    collection = firestoreModule.collection;
    deleteField = firestoreModule.deleteField;
    doc = firestoreModule.doc;
    getDocs = firestoreModule.getDocs;
    limit = firestoreModule.limit;
    onSnapshot = firestoreModule.onSnapshot;
    query = firestoreModule.query;
    serverTimestamp = firestoreModule.serverTimestamp;
    setDoc = firestoreModule.setDoc;
    where = firestoreModule.where;
    writeBatch = firestoreModule.writeBatch;
    httpsCallable = functionsModule.httpsCallable;
    getDownloadURL = storageModule.getDownloadURL;
    storageRef = storageModule.ref;
    uploadBytes = storageModule.uploadBytes;

    firebaseApp = appModule.getApps().length ? appModule.getApp() : appModule.initializeApp(firebaseConfig);
    // App Check and Phone Auth use different reCAPTCHA modes. App Check must
    // claim the standard v3 runtime before Auth prewarms Enterprise, otherwise
    // App Check can receive an Enterprise wrapper without ready().
    ensureFirebaseAppCheck();
    firebaseAuth = authModule.getAuth(firebaseApp);
    // Capacitor can recreate its WebView process between launches. Pin Auth to
    // durable origin storage so a verified mobile session survives that restart.
    await authModule.setPersistence(firebaseAuth, authModule.browserLocalPersistence);
    firebaseDb = firestoreModule.getFirestore(firebaseApp);
    firebaseFunctions = functionsModule.getFunctions(firebaseApp, import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || "australia-southeast1");
    firebaseStorage = storageModule.getStorage(firebaseApp);
    firebaseAuth.useDeviceLanguage();
    // Fetch the Identity Platform reCAPTCHA Enterprise configuration before a
    // phone-auth request. The SDK also fetches this lazily, but prewarming it
    // avoids racing the first sendVerificationCode call during configuration
    // changes. Audit mode can still fall back to the v2 verifier if this fetch
    // fails, so a telemetry failure must not block account restoration.
    firebaseRecaptchaConfigPromise = Promise.resolve(initializeRecaptchaConfig?.(firebaseAuth))
      .catch(() => null);
    return { app: firebaseApp, auth: firebaseAuth, db: firebaseDb, functions: firebaseFunctions, storage: firebaseStorage };
  }).catch((error) => {
    firebaseRuntimePromise = null;
    throw error;
  });

  return firebaseRuntimePromise;
};

const ensureFirebaseAppCheck = () => {
  if (!firebaseApp || !firebaseAppCheckSiteKey || firebaseAppCheck || !initializeAppCheck || !ReCaptchaV3Provider) return firebaseAppCheck;
  if (firebaseAppCheckInitialisationAttempted) return firebaseAppCheck;
  firebaseAppCheckInitialisationAttempted = true;
  try {
    firebaseAppCheck = initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(firebaseAppCheckSiteKey),
      isTokenAutoRefreshEnabled: true
    });
    firebaseAppCheckInitialisationError = null;
  } catch (error) {
    firebaseAppCheckInitialisationError = error;
    console.warn("Firebase App Check could not initialise; continuing without crashing the page.", error);
  }
  return firebaseAppCheck;
};

const accountStorageKey = "badminta-account-profile-v1";
const profileSetupDraftStorageKey = "badminta-profile-setup-draft-v1";
const savedVenuesStorageKey = "badminta-saved-venues-v1";
const localAuthStorageKey = "badminta-local-test-auth-v1";
const rememberedFirebaseSessionKey = "badminta-firebase-session-present-v1";
const localTestPhoneNumber = import.meta.env.DEV
  ? String(import.meta.env.VITE_BADMINTA_LOCAL_TEST_PHONE || "").trim()
  : "";
const localTestSmsCode = import.meta.env.DEV
  ? String(import.meta.env.VITE_BADMINTA_LOCAL_TEST_SMS_CODE || "").trim()
  : "";
const smsRequestCooldownMs = 60 * 1000;
const allowLocalProfileFallbacks = import.meta.env.VITE_BADMINTA_ALLOW_LOCAL_PROFILE_FALLBACKS === "true";
const productionDataOnly = import.meta.env.VITE_BADMINTA_PRODUCTION_DATA_ONLY === "true"
  || (import.meta.env.PROD && !allowLocalProfileFallbacks);
const canUseAnonymousLocalProfile = (user = null) => !productionDataOnly && !user?.uid;
const canUseLegacyGlobalProfileFallback = (user = null) => Boolean(user?.uid && !productionDataOnly);
const localProfileSyncMessage = (user = null) => {
  if (user?.isLocalTestUser) return "Local test account. Cloud profile sync is disabled in local test mode.";
  if (isRealFirebaseUser(user)) return "Loading cloud account profile.";
  return productionDataOnly
    ? "Production data mode is active. Sign in with Firebase phone auth to load a cloud account profile."
    : "Using local account storage.";
};

const legalConfig = {
  entity: import.meta.env.VITE_BADMINTA_LEGAL_ENTITY || "Badminta",
  supportEmail: import.meta.env.VITE_BADMINTA_SUPPORT_EMAIL || "support@badminta.com",
  supportUrl: import.meta.env.VITE_BADMINTA_SUPPORT_URL || "https://badminta.com/support/help",
  governingLaw: import.meta.env.VITE_BADMINTA_GOVERNING_LAW || "Victoria, Australia",
  disputeVenue: import.meta.env.VITE_BADMINTA_DISPUTE_VENUE || "Courts of Victoria, Australia",
  launchCountry: import.meta.env.VITE_BADMINTA_LAUNCH_COUNTRY || "Worldwide",
  policyUpdated: import.meta.env.VITE_BADMINTA_POLICY_UPDATED || legalPolicyVersion
};

const localTestAuthEnabled = () => {
  if (typeof window === "undefined") return false;
  return import.meta.env.DEV || ["localhost", "127.0.0.1"].includes(window.location.hostname);
};

const isBadmintaTestPhone = (phoneNumber, countryCode = "AU") => Boolean(
  localTestPhoneNumber && normalisePhoneNumber(phoneNumber, countryCode) === localTestPhoneNumber
);
const isLocalTestPhone = (phoneNumber, countryCode = "AU") => localTestAuthEnabled() && isBadmintaTestPhone(phoneNumber, countryCode);

const loadLocalTestUser = () => {
  if (!localTestAuthEnabled()) return null;
  try {
    const stored = window.localStorage.getItem(localAuthStorageKey);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveLocalTestUser = (user) => {
  if (!localTestAuthEnabled()) return;
  window.localStorage.setItem(localAuthStorageKey, JSON.stringify(user));
};

const clearLocalTestUser = () => {
  try {
    window.localStorage.removeItem(localAuthStorageKey);
  } catch {
    // Local storage can be unavailable in private browsing.
  }
};

const loadStoredSavedVenues = () => {
  try {
    const stored = window.localStorage.getItem(savedVenuesStorageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const defaultAccountProfile = {
  fullName: "",
  email: "",
  photoUrl: "",
  socialLinks: [],
  roles: [],
  marketingAttribution: {},
  groupInvites: [],
  adminAuditLog: [],
  adminDecisions: {},
  adminRecordUpdates: {},
  player: {
    photoUrl: "",
    skillBand: "",
    availability: "",
    availabilitySchedule: [],
    suburb: "",
    city: "",
    country: "",
    countryCode: "",
    location: null,
    formats: "",
    about: "",
    style: "",
    mediaUrl: "",
    mediaLinks: [],
    socialLinks: [],
    visibility: {
      rating: true,
      invites: true,
      media: true,
      history: false
    }
  },
  group: {
    id: "",
    name: "",
    logoUrl: "",
    coverUrl: "",
    description: "",
    venue: "",
    venueId: "",
    date: "",
    time: "",
    schedule: [],
    sessions: [],
    isRecurring: true,
    format: "",
    skillBand: "",
    city: "",
    suburb: "",
    location: null,
    capacity: "",
    price: "",
    paymentManagedByBadminta: true,
    membershipEnabled: true,
    membershipMode: "free",
    membershipFee: "",
    membershipFeeCents: 0,
    membershipBillingInterval: "one_time",
    membershipBenefits: "",
    membershipProductId: "",
    membershipCheckoutReady: true,
    activeMemberCount: 0,
    pendingVenue: null,
    invites: "",
    inviteEmails: [],
    mediaLinks: [],
    socialLinks: []
  },
  centre: {
    name: "",
    logoUrl: "",
    coverUrl: "",
    description: "",
    suburb: "",
    city: "",
    location: null,
    address: "",
    contactEmail: "",
    phone: "",
    website: "",
    abn: "",
    courtCount: "",
    openingHours: "",
    openingSchedule: [],
    pricing: "",
    courtPricing: {
      standard: "",
      offPeak: "",
      peak: "",
      weekend: "",
      offPeakWindow: "Weekdays before 4pm",
      peakWindow: "Weekdays 4pm-10pm",
      weekendWindow: "Saturday and Sunday",
      rateRules: {},
      notes: ""
    },
    timeZone: "",
    bookingRules: "",
    courtInventory: [],
    membershipProducts: [],
    photoLinks: [],
    socialLinks: []
  },
  coach: {
    photoUrl: "",
    headline: "",
    bio: "",
    accreditation: "",
    hourlyRate: "",
    lessonServices: [],
    locations: "",
    availability: "",
    availabilitySchedule: [],
    timeZone: "",
    bookingNoticeHours: 12,
    bookingWindowDays: 30,
    bufferMinutes: 0,
    suburb: "",
    city: "",
    location: null,
    socialLinks: [],
    mediaLinks: []
  },
  events: [],
  joinedEvents: [],
  joinedGroups: [],
  bookings: [],
  payments: [],
  messages: [],
  savedItems: [],
  reviews: []
};

const accountRoleOptions = [
  {
    id: "player",
    title: "Player",
    menuTitle: "Player menu",
    description: "Find courts and social games that fit your level, save bookings, and show organisers when you are available.",
    icon: UserCircle,
    actions: [
      ["Player profile", "Level, suburb, availability, preferred games and privacy controls.", "profileSetup"],
      ["View public profile", "Check what organisers and other players see before they invite you.", "publicPlayerProfile"],
      ["Court bookings", "See court bookings, totals, payment status and refund support.", "bookings"],
      ["Groups this week", "Check joined social sessions and whether payment is complete.", "saved"],
      ["Messages", "Reply to invites, court confirmations and organiser questions.", "messages"]
    ],
    onboarding: [
      ["Create player profile", "Add your level, suburb, availability and game preferences so people can invite you properly.", "profileSetup"],
      ["Browse centres", "Find nearby courts, social groups and events you can actually use.", "results"]
    ]
  },
  {
    id: "organiser",
    title: "Social organiser",
    menuTitle: "Organiser menu",
    description: "Fill social sessions, collect player payments, manage members and reduce no-shows.",
    icon: UsersRound,
    actions: [
      ["Organiser dashboard", "Track sessions, joined players, money collected and waitlists.", "organiser"],
      ["Group profile", "Manage the reusable venue, weekly schedule, level, media and community details.", "createGroup"],
      ["Sessions", "Create dated sessions with their own capacity, price, joining and payment status.", "groupSessions"],
      ["View public group", "See the join page players use before paying.", "publicGroupProfile"],
      ["Members", "Manage invites, attendance, substitutions and repeat players.", "members"],
      ["Payouts", "Check session revenue, refunds and organiser payouts.", "payouts"],
      ["Messages", "Reply to player questions and session enquiries as the organiser.", "messages"]
    ],
    onboarding: [
      ["Create first group", "Set up a real session players can join with capacity, price and venue details.", "createGroup"],
      ["Open organiser dashboard", "Watch player numbers, payments and member activity from one page.", "organiser"]
    ]
  },
  {
    id: "centre",
    title: "Centre owner",
    menuTitle: "Centre menu",
    description: "Bring in more court bookings, publish prices and hours, sell events, and manage centre payments.",
    icon: Building2,
    actions: [
      ["Centre dashboard", "Track listing quality, court setup, bookings, events and revenue.", "centreDashboard"],
      ["Add or edit centre", "Update address, courts, prices, hours, contact details and photos.", "claimCentre"],
      ["Search and claim centre", "Find an existing directory listing and submit an ownership claim.", "centreClaimSearch"],
      ["Create event", "Sell tournaments, clinics, open days and club nights.", "createEvent"],
      ["View public centre", "See the page players use to decide whether to book.", "publicCentreProfile"],
      ["Court inventory", "Set court count, opening hours, booking rules and fee schedule.", "courtManager"],
      ["Centre payments", "Review court hire payments, refunds and payout records.", "centrePayments"]
    ],
    onboarding: [
      ["Add or edit centre", "Add the details players need before booking: address, hours, courts, pricing and contact.", "claimCentre"],
      ["Claim existing centre", "Search the directory first if your centre is already listed.", "centreClaimSearch"],
      ["Manage courts", "Create the court and fee setup needed before in-platform bookings can open.", "courtManager"]
    ]
  },
  {
    id: "coach",
    title: "Coach or trainer",
    menuTitle: "Coach menu",
    description: "Sell lessons, show proof, publish availability and turn enquiries into paid coaching sessions.",
    icon: Trophy,
    actions: [
      ["Coach profile", "Photo, bio, rate, locations, availability and proof.", "coachProfile"],
      ["View public coach", "See what players see before requesting a lesson.", "publicCoachProfile"],
      ["Coached session", "Create a clinic, training group or coached social.", "createGroup"],
      ["Player messages", "Handle lesson enquiries, goals and booking questions.", "messages"],
      ["Payouts", "Track paid lesson income, deposits and refunds.", "coachPayouts"]
    ],
    onboarding: [
      ["Create coach profile", "Add your rate, coaching areas, availability and proof so players can judge fit quickly.", "coachProfile"],
      ["Create coached session", "Publish a clinic or group session players can join.", "createGroup"]
    ]
  }
];

const roleTitle = (role) => accountRoleOptions.find((option) => option.id === role)?.title || role;
const onboardingRoleStorageKey = "badminta-active-onboarding-role";
const onboardingCompletedRoleStorageKey = "badminta-completed-onboarding-role";
const publicProfileShortcuts = [
  {
    role: "player",
    title: "Player public profile",
    text: "Help organisers know your level, location, availability and preferred games.",
    target: "publicPlayerProfile",
    setupTarget: "profileSetup",
    icon: UserCircle
  },
  {
    role: "coach",
    title: "Coach public profile",
    text: "Show what you teach, where you coach, what you charge and why players should book.",
    target: "publicCoachProfile",
    setupTarget: "coachProfile",
    icon: Trophy
  },
  {
    role: "organiser",
    title: "Group public profile",
    text: "Give players the venue, level, price and rules they need before joining.",
    target: "publicGroupProfile",
    setupTarget: "createGroup",
    icon: UsersRound
  },
  {
    role: "centre",
    title: "Centre public profile",
    text: "Turn search traffic into calls, booking requests, memberships and event attendance.",
    target: "publicCentreProfile",
    setupTarget: "claimCentre",
    icon: Building2
  }
];
const roleSetupSteps = {
  player: {
    page: "profileSetup",
    action: "Set up player profile",
    title: "Create your player profile",
    text: "Add the details that help organisers place you in the right games: level, suburb, availability and preferred format."
  },
  organiser: {
    page: "createGroup",
    action: "Create organiser group",
    title: "Create your first social group",
    text: "Create a session players can trust before paying: venue, day, level, capacity, price and invite list."
  },
  centre: {
    page: "claimCentre",
    action: "Set up centre profile",
    title: "Add or edit your centre profile",
    text: "Add the booking details players ask for first: address, hours, courts, prices, contact links and photos."
  },
  coach: {
    page: "coachProfile",
    action: "Set up coach profile",
    title: "Create your coach profile",
    text: "Show your coaching offer clearly: photo, headline, rate, locations, availability and proof."
  }
};

const rememberOnboardingRole = (roleId) => {
  try {
    window.sessionStorage.setItem(onboardingRoleStorageKey, roleId);
  } catch {
    // Session storage can be unavailable in private browsing.
  }
};

const completeOnboardingRole = (roleId) => {
  try {
    if (window.sessionStorage.getItem(onboardingRoleStorageKey) !== roleId) return false;
    window.sessionStorage.removeItem(onboardingRoleStorageKey);
    window.sessionStorage.setItem(onboardingCompletedRoleStorageKey, roleId);
    return true;
  } catch {
    return false;
  }
};

const getCompletedOnboardingRole = () => {
  try {
    return window.sessionStorage.getItem(onboardingCompletedRoleStorageKey) || "";
  } catch {
    return "";
  }
};

const roleProfileStarted = (profile = {}, roleId) => {
  if (roleId === "player") return Boolean(profile.player?.photoUrl || profile.player?.suburb || profile.player?.availability || cleanLinkRows(profile.player?.mediaLinks).length);
  if (roleId === "organiser") return Boolean(profile.group?.name || profile.group?.venue || profile.group?.logoUrl);
  if (roleId === "centre") return Boolean(profile.centre?.name || profile.centre?.address || profile.centre?.logoUrl);
  if (roleId === "coach") return Boolean(profile.coach?.headline || profile.coach?.bio || profile.coach?.photoUrl || profile.coach?.availability);
  return false;
};

const hasProfileValue = (value) => {
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.some(hasProfileValue);
  if (value && typeof value === "object") return Object.values(value).some(hasProfileValue);
  return Boolean(String(value || "").trim());
};
const playerSkillBandPlaceholders = new Set([
  "player",
  "playing level",
  "select level",
  "select playing level",
  "not added",
  "not available",
  "unknown",
  "none",
  "n/a"
]);
const hasValidPlayerSkillBand = (value) => {
  const normalised = String(value || "").trim().toLowerCase();
  return Boolean(normalised) && !playerSkillBandPlaceholders.has(normalised);
};
const safePublicPlayerDisplayName = (value = "") => {
  const name = String(value || "").trim().slice(0, 160);
  if (!name || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) return "";
  const digits = name.replace(/\D/g, "");
  if (digits.length >= 7 && /^[+\d\s().-]+$/.test(name)) return "";
  return name;
};
const publicPlayerDisplayName = (value = "", identity = "") => {
  const safeName = safePublicPlayerDisplayName(value);
  if (safeName) return safeName;
  const suffix = String(identity || "player").replace(/[^a-z0-9]/gi, "").slice(-5).toUpperCase() || "NEW";
  return `Badminta player ${suffix}`;
};
const hasAllowedUserMediaValue = (...sources) => sources
  .flatMap((source) => collectImageUrls(source))
  .some((url) => Boolean(getAllowedUserMediaUrl(url)));
const hasProfileAvailability = (profile = {}) => hasProfileValue(profile.availability) || normaliseAvailabilitySchedule(profile.availabilitySchedule).some((row) => row.enabled);
const hasPublicProfileLocation = (profile = {}) => Boolean([
  profile.suburb,
  profile.city,
  profile.locations,
  profile.location?.suburb,
  profile.location?.city,
  profile.location?.label
].map((value) => String(value || "").trim()).find(Boolean));
const publicProfileAreaLabel = (profile = {}, fallback = "") => [
  profile.suburb || profile.location?.suburb,
  profile.city || profile.location?.city || (profile.location?.type === "city" ? profile.location?.label : ""),
  profile.state || profile.location?.state,
  profile.country || profile.location?.country
]
  .map((value) => String(value || "").trim())
  .filter((value, index, values) => value && values.findIndex((item) => compactLocationText(item) === compactLocationText(value)) === index)
  .join(", ") || fallback;
const publicCentreStreetAddress = (profile = {}) => String(profile.address || "").trim();
const centreDescriptionMinimumCharacters = 120;
const centreMediaMinimumItems = 3;
const centreDescriptionIsReady = (value = "") => String(value || "").trim().length >= centreDescriptionMinimumCharacters;
const centrePublicMediaRows = (centre = {}) => cleanLinkRows(centre.photoLinks, "Centre media");
const centreHasPublicContact = (centre = {}) => Boolean(String(centre.contactEmail || centre.phone || "").trim());
const centreHasCourtCount = (centre = {}) => Number.parseInt(centre.courtCount || centre.courts, 10) > 0;
const centreHasStructuredHours = (centre = {}) => normaliseAvailabilitySchedule(centre.openingSchedule).some((row) => row.enabled);
const compactPlayerLocationValue = (value = "") => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const hasPublicPlayerLocation = (profile = {}) => {
  const locationType = String(profile.location?.type || "").trim().toLowerCase();
  if (locationType === "country") return false;
  const area = [profile.location?.suburb, profile.location?.city, profile.suburb, profile.city]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";
  if (!area) return false;
  const areaKey = compactPlayerLocationValue(area);
  const countryKeys = [profile.country, profile.countryCode, profile.location?.country, profile.location?.countryCode]
    .map(compactPlayerLocationValue)
    .filter(Boolean);
  return !countryKeys.includes(areaKey);
};
const publicRoleId = (role) => role === "group" ? "organiser" : role;
const publicSetupTarget = (role) => publicProfileShortcuts.find((item) => item.role === publicRoleId(role))?.setupTarget || "account";

const getPublicProfileMissingFields = (profile = {}, role) => {
  const roleId = publicRoleId(role);
  const missing = [];
  if (!profile.roles?.includes(roleId)) missing.push(`${roleTitle(roleId)} role saved`);

  if (roleId === "player") {
    [[profile.fullName, "Public display name"]]
      .forEach(([value, label]) => { if (!safePublicPlayerDisplayName(value)) missing.push(label); });
  }

  if (roleId === "coach") {
    const coach = profile.coach || {};
    [
      [profile.fullName, "Public display name"],
      [coach.headline, "Coach headline"],
      [hasPublicProfileLocation(coach), "Coaching location"]
    ].forEach(([value, label]) => { if (!value || !hasProfileValue(value)) missing.push(label); });
  }

  if (roleId === "organiser") {
    const group = profile.group || {};
    [
      [group.name, "Group name"],
      [group.venueId || group.venue, "Venue"],
      [hasGroupPlayingSchedule(group), "Playing days and times"],
      [Number.parseInt(group.capacity, 10) > 0, "Capacity"]
    ].forEach(([value, label]) => { if (!value || !hasProfileValue(value)) missing.push(label); });
  }

  if (roleId === "centre") {
    const centre = profile.centre || {};
    [
      [centre.name, "Centre name"],
      [hasPublicProfileLocation(centre), "Suburb or city"],
      [publicCentreStreetAddress(centre), "Street address"]
    ].forEach(([value, label]) => { if (!value || !hasProfileValue(value)) missing.push(label); });
  }

  return [...new Set(missing)];
};

const getPlayerDiscoveryMissingFields = (profile = {}) => {
  const player = profile.player || profile;
  const missing = [];
  if (!hasValidPlayerSkillBand(player.skillBand || player.band)) missing.push("Playing level");
  if (!hasPublicPlayerLocation(player)) missing.push("Home suburb");
  return missing;
};

const roleHasPublishedBackendProfile = (profile = {}, role) => {
  const roleId = publicRoleId(role);
  const roleProfile = roleId === "organiser" ? profile.group || {} : profile[roleId] || {};
  const statusField = {
    player: "publicPlayerProfileStatus",
    organiser: "publicGroupProfileStatus",
    centre: "publicCentreProfileStatus",
    coach: "publicCoachProfileStatus"
  }[roleId];
  const statuses = [roleProfile.backendStatus, statusField ? profile[statusField] : ""]
    .map((value) => String(value || "").toLowerCase());
  const backendProfileId = roleId === "player"
    ? roleProfile.publicPlayerId || roleProfile.firestoreSourceId || profile.publicPlayerProfileId
    : roleId === "coach"
      ? roleProfile.publicCoachId || roleProfile.firestoreSourceId || profile.publicCoachProfileId
      : roleId === "organiser"
        ? roleProfile.publicGroupId || roleProfile.serverGroupId || roleProfile.firestoreSourceId || profile.publicGroupProfileId
        : roleProfile.publicCentreId || roleProfile.serverCentreId || roleProfile.firestoreSourceId || profile.publicCentreProfileId;
  return statuses.some((status) => ["published", "active", "claimed", "verified"].includes(status))
    && (roleProfile.publicProfilePublished === true || Boolean(backendProfileId));
};

const getRoleOnboardingMissingFields = (profile = {}, role) => (
  getPublicProfileMissingFields(profile, role)
);

const isPublicProfileReady = (profile = {}, role) => (
  roleHasPublishedBackendProfile(profile, role) && getPublicProfileMissingFields(profile, role).length === 0
);

const availabilityDayOptions = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"]
];
const availabilityTimeOptions = Array.from({ length: 38 }, (_, index) => {
  const minutes = 5 * 60 + index * 30;
  const hour = String(Math.floor(minutes / 60)).padStart(2, "0");
  const minute = String(minutes % 60).padStart(2, "0");
  return `${hour}:${minute}`;
});
const defaultSocialLink = () => ({ platform: "Instagram", username: "", url: "" });
const defaultMediaLink = (platform = "YouTube") => ({
  platform,
  label: "",
  url: "",
  source: "user",
  rightsStatus: "owned_by_user",
  requiresModeration: false,
  moderationStatus: "approved"
});
const mediaRightsStatusOptions = [
  ["needs_review", "Needs rights review"],
  ["owned_by_user", "I own this media"],
  ["permission_granted", "I have permission"],
  ["official_source", "Official source link"]
];
const mediaRightsStatusLabels = Object.fromEntries(mediaRightsStatusOptions);
const mediaModerationStatusLabels = {
  approved: "Approved",
  pending_review: "Pending review",
  rejected: "Rejected"
};
const normaliseMediaRightsStatus = (status = "", fallback = "needs_review") => {
  const value = String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return mediaRightsStatusLabels[value] ? value : fallback;
};
const normaliseMediaModerationStatus = (status = "", fallback = "pending_review") => {
  const value = String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return mediaModerationStatusLabels[value] ? value : fallback;
};
const publicMediaNeedsModeration = (link = {}) => Boolean(link.requiresModeration || link.moderationStatus || link.source === "user");
const isApprovedPublicMediaLink = (link = {}) => !publicMediaNeedsModeration(link) || normaliseMediaModerationStatus(link.moderationStatus) === "approved";
const approvedPublicMediaLinks = (links = [], fallbackLabel = "Media") => cleanLinkRows(links, fallbackLabel)
  .filter(isApprovedPublicMediaLink);
const pendingMediaReviewCount = (links = []) => cleanLinkRows(links, "Media")
  .filter((link) => publicMediaNeedsModeration(link) && !isApprovedPublicMediaLink(link)).length;
const markUserSubmittedMediaLinks = (links = [], fallbackLabel = "Media") => {
  const submittedAt = new Date().toISOString();
  return cleanLinkRows(links, fallbackLabel).map((link) => ({
    ...link,
    source: link.source || "user",
    rightsStatus: normaliseMediaRightsStatus(link.rightsStatus, "owned_by_user"),
    requiresModeration: false,
    moderationStatus: link.url && parseMediaEmbed(link.url) ? "approved" : normaliseMediaModerationStatus(link.moderationStatus, "rejected"),
    submittedAt: link.submittedAt || submittedAt
  }));
};
const defaultAvailabilitySchedule = () => availabilityDayOptions.map(([day, label]) => ({
  day,
  label,
  enabled: false,
  start: "18:00",
  end: "21:00"
}));

const blockedUserMediaUrlTerms = [
  "unsplash",
  "pexels",
  "shutterstock",
  "freepik",
  "stock",
  "gravatar",
  "randomuser",
  "pravatar",
  "ui-avatars",
  "placehold",
  "placeholder",
  "dummy",
  "demo.",
  "opdsdemo",
  "wait.gif",
  "loremflickr",
  "picsum"
];

const isBlockedUserMediaUrl = (url = "") => {
  const value = String(url || "").trim().toLowerCase();
  if (!value) return true;
  if (/^data:image\//i.test(value)) return false;
  return blockedUserMediaUrlTerms.some((term) => value.includes(term));
};

const isRenderableUserMediaUrl = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return false;
  if (!/^data:image\//i.test(value)) return true;
  if (/^data:image\/svg\+xml/i.test(value)) return value.length > 120;
  const base64 = value.split(",")[1] || "";
  return /^data:image\/[a-z0-9.+-]+;base64,/i.test(value) && base64.length > 1500 && base64.length % 4 === 0;
};

const getAllowedUserMediaUrl = (url = "") => {
  const value = String(url || "").trim();
  return value && !isBlockedUserMediaUrl(value) && isRenderableUserMediaUrl(value) ? value : "";
};

const getAccountAvatarUrl = (profile = {}) => [
  profile.photoUrl,
  profile.player?.photoUrl,
  profile.coach?.photoUrl
].map(getAllowedUserMediaUrl).find(Boolean) || "";

const normaliseAvailabilitySchedule = (schedule = []) => {
  const byDay = new globalThis.Map((Array.isArray(schedule) ? schedule : []).map((item) => [item.day, item]));
  return defaultAvailabilitySchedule().map((row) => {
    const saved = byDay.get(row.day) || {};
    return {
      ...row,
      enabled: Boolean(saved.enabled),
      start: saved.start || row.start,
      end: saved.end || row.end
    };
  });
};

const summarizeAvailabilitySchedule = (schedule = []) => {
  const enabledRows = normaliseAvailabilitySchedule(schedule).filter((row) => row.enabled);
  if (!enabledRows.length) return "";
  return enabledRows.map((row) => `${row.label}: ${formatTimeLabel(row.start)}-${formatTimeLabel(row.end)}`).join("; ");
};

const summarizeAvailabilityScheduleCompact = (schedule = [], fallback = "") => {
  const enabledRows = normaliseAvailabilitySchedule(schedule).filter((row) => row.enabled);
  if (!enabledRows.length) return String(fallback || "").trim();

  const timeLabels = enabledRows.map((row) => `${formatTimeLabel(row.start)} - ${formatTimeLabel(row.end)}`);
  const sharedTime = timeLabels.every((value) => value === timeLabels[0]);
  if (!sharedTime) {
    const first = enabledRows[0];
    return `${enabledRows.length} weekly days · ${first.label.slice(0, 3)} ${timeLabels[0]}`;
  }

  const dayKeys = enabledRows.map((row) => row.day).join(",");
  const dayLabel = dayKeys === "mon,tue,wed,thu,fri,sat,sun"
    ? "Every day"
    : dayKeys === "mon,tue,wed,thu,fri"
      ? "Weekdays"
      : dayKeys === "sat,sun"
        ? "Weekends"
        : enabledRows.map((row) => row.label.slice(0, 3)).join(", ");
  return `${dayLabel} · ${timeLabels[0]}`;
};

const coachLessonFormatOptions = [
  ["private", "Private lesson"],
  ["group", "Group lesson"],
  ["junior", "Junior lesson"],
  ["clinic", "Clinic"],
  ["online", "Online coaching"]
];
const coachLessonFormatLabels = Object.fromEntries(coachLessonFormatOptions);
const coachLessonDurationOptions = [30, 45, 60, 75, 90, 120];
const coachLessonServiceLimit = 8;

const coachLessonServiceId = (service = {}, index = 0) => {
  const explicit = String(service.id || service.serviceId || "").trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-");
  return explicit || `lesson-${index + 1}`;
};

const normaliseCoachLessonServices = (services = [], hourlyRate = "", currency = "aud") => {
  const rows = Array.isArray(services) ? services : [];
  const normalised = rows.slice(0, coachLessonServiceLimit).map((service, index) => {
    const lessonFormat = String(service.lessonFormat || service.format || "private").toLowerCase().replace(/[\s-]+/g, "_");
    const durationMinutes = coachLessonDurationOptions.includes(Number(service.durationMinutes))
      ? Number(service.durationMinutes)
      : 60;
    const priceCents = Math.max(0, Number.parseInt(service.priceCents || service.amountCents || 0, 10) || 0);
    const explicitPrice = service.price ?? service.sessionPrice ?? service.fee;
    const price = String(explicitPrice ?? (priceCents ? minorUnitsToAmount(priceCents, currency) : "")).trim();
    const parsedExplicitPrice = Number.parseFloat(price.replace(/[^0-9.]/g, ""));
    const priceConfigured = service.priceConfigured === true
      || priceCents > 0
      || (price !== "" && Number.isFinite(parsedExplicitPrice));
    const maxPlayers = Math.max(1, Math.min(40, Number.parseInt(service.maxPlayers || (lessonFormat === "private" ? 1 : 4), 10) || 1));
    return {
      id: coachLessonServiceId(service, index),
      title: String(service.title || service.name || coachLessonFormatLabels[lessonFormat] || "Coaching lesson").trim().slice(0, 120),
      description: String(service.description || "").trim().slice(0, 600),
      lessonFormat: coachLessonFormatLabels[lessonFormat] ? lessonFormat : "private",
      durationMinutes,
      price,
      priceCents,
      priceConfigured,
      maxPlayers: lessonFormat === "private" ? 1 : maxPlayers,
      active: service.active !== false,
      currency: normaliseCurrencyCode(service.currency || currency)
    };
  }).filter((service) => service.title);

  if (normalised.length || !String(hourlyRate || "").trim()) return normalised;
  const legacyPrice = String(hourlyRate).replace(/[^0-9.]/g, "");
  return [{
    id: "private-60",
    title: "Private lesson",
    description: "",
    lessonFormat: "private",
    durationMinutes: 60,
    price: legacyPrice,
    priceCents: amountToMinorUnits(Number(legacyPrice) || 0, currency),
    priceConfigured: Boolean(legacyPrice && Number.isFinite(Number.parseFloat(legacyPrice))),
    maxPlayers: 1,
    active: true,
    currency: normaliseCurrencyCode(currency)
  }];
};

const coachLessonServicePriceCents = (service = {}, currency = "aud") => {
  const stored = Math.max(0, Number.parseInt(service.priceCents || service.amountCents || 0, 10) || 0);
  return stored || amountToMinorUnits(Number.parseFloat(String(service.price || "").replace(/[^0-9.]/g, "")) || 0, currency);
};

const activeCoachLessonServices = (coach = {}) => normaliseCoachLessonServices(
  coach.lessonServices,
  coach.hourlyRate,
  coach.currency
).filter((service) => service.active && service.priceConfigured);

const coachLessonServicePriceLabel = (service = {}, currency = "aud") => {
  if (service.priceConfigured !== true && String(service.price || "").trim() === "") return "Price needed";
  const cents = coachLessonServicePriceCents(service, currency);
  return cents ? formatCurrencyCents(cents, currency) : "Free";
};

const coachPrimaryRateLabel = (coach = {}) => {
  const currency = normaliseCurrencyCode(coach.currency || marketplaceCurrencyForCountry(coach.countryCode || coach.location?.countryCode));
  const services = activeCoachLessonServices(coach);
  if (!services.length) return String(coach.hourlyRate || "").trim();
  const paid = services.map((service) => coachLessonServicePriceCents(service, currency)).filter((value) => value > 0);
  if (!paid.length) return "Free sessions";
  return `From ${formatCurrencyCents(Math.min(...paid), currency)}`;
};

const currentBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC";
  } catch {
    return "Etc/UTC";
  }
};

const coachZonedDateParts = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone || "Etc/UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    dayKey: String(parts.weekday || "").toLowerCase().slice(0, 3),
    hour: Number(parts.hour),
    minute: Number(parts.minute)
  };
};

const coachLocalDateTimeToUtc = (dateKey, time, timeZone) => {
  const match = `${dateKey}T${time}`.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const target = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5])
  };
  const targetAsUtc = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute);
  let candidateMillis = targetAsUtc;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const actual = coachZonedDateParts(new Date(candidateMillis), timeZone);
    const actualAsUtc = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute);
    candidateMillis += targetAsUtc - actualAsUtc;
  }
  const candidate = new Date(candidateMillis);
  const resolved = coachZonedDateParts(candidate, timeZone);
  return ["year", "month", "day", "hour", "minute"].every((key) => resolved[key] === target[key]) ? candidate : null;
};

const clockMinutes = (value = "") => {
  const match = String(value).match(/^(\d{1,2}):(\d{2})$/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : 0;
};

const minutesClock = (minutes = 0) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const buildCoachBookableSlots = (coach = {}, service = null, dayCount = 14) => {
  if (!service) return [];
  const schedule = normaliseAvailabilitySchedule(coach.availabilitySchedule).filter((row) => row.enabled);
  if (!schedule.length) return [];
  const timeZone = coach.timeZone || currentBrowserTimeZone();
  const durationMinutes = Number(service.durationMinutes) || 60;
  const noticeHours = Math.max(0, Number(coach.bookingNoticeHours) || 0);
  const bookingWindowDays = Math.max(1, Number(coach.bookingWindowDays) || 30);
  const earliest = Date.now() + noticeHours * 60 * 60 * 1000;
  const latest = Date.now() + bookingWindowDays * 24 * 60 * 60 * 1000;
  const dateKeys = [];
  for (let offset = 0; dateKeys.length < dayCount && offset < dayCount + 4; offset += 1) {
    const parts = coachZonedDateParts(new Date(Date.now() + offset * 24 * 60 * 60 * 1000), timeZone);
    const dateKey = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
    if (!dateKeys.some((row) => row.dateKey === dateKey)) dateKeys.push({ dateKey, dayKey: parts.dayKey });
  }
  return dateKeys.flatMap(({ dateKey, dayKey }) => {
    const row = schedule.find((item) => item.day === dayKey);
    if (!row) return [];
    const startMinutes = clockMinutes(row.start);
    const endMinutes = clockMinutes(row.end);
    const stepMinutes = 30;
    const slots = [];
    for (let minute = startMinutes; minute + durationMinutes <= endMinutes; minute += stepMinutes) {
      const time = minutesClock(minute);
      const startAt = coachLocalDateTimeToUtc(dateKey, time, timeZone);
      if (!startAt || startAt.getTime() < earliest || startAt.getTime() > latest) continue;
      slots.push({
        id: `${service.id}-${dateKey}-${time}`,
        dateKey,
        time,
        startAt: startAt.toISOString(),
        dayLabel: new Intl.DateTimeFormat(undefined, { timeZone, weekday: "short" }).format(startAt),
        dateLabel: new Intl.DateTimeFormat(undefined, { timeZone, day: "numeric", month: "short" }).format(startAt)
      });
    }
    return slots;
  });
};

const groupScheduleFromLegacyDate = (group = {}) => {
  if (Array.isArray(group.schedule) && group.schedule.length) return group.schedule;
  if (!group.date || !group.time) return [];
  const date = localDateFromIso(group.date);
  if (!date) return [];
  const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][date.getDay()];
  return defaultAvailabilitySchedule().map((row) => ({
    ...row,
    enabled: row.day === dayKey,
    start: row.day === dayKey ? group.time : row.start
  }));
};
const normaliseGroupSchedule = (group = {}) => {
  const storedSchedule = Array.isArray(group.schedule) && group.schedule.length
    ? group.schedule
    : Array.isArray(group.recurringSchedule) && group.recurringSchedule.length
      ? group.recurringSchedule
      : [];
  const schedule = storedSchedule.length || group.isRecurring === false
    ? storedSchedule
    : groupScheduleFromLegacyDate(group);
  return normaliseAvailabilitySchedule(schedule);
};
const summarizeGroupSchedule = (group = {}) => summarizeAvailabilitySchedule(normaliseGroupSchedule(group));
const hasGroupPlayingSchedule = (group = {}) => Boolean(summarizeGroupSchedule(group) || (group.date && group.time) || group.when);

const cleanLinkRows = (links = [], fallbackLabel = "Link") => {
  if (!Array.isArray(links)) return [];
  return links
    .map((link) => {
      if (typeof link === "string") return { label: fallbackLabel, url: link.trim() };
      const clean = {
        platform: link.platform || "",
        label: link.label || link.platform || fallbackLabel,
        url: String(link.url || "").trim()
      };
      if (link.username) clean.username = String(link.username).trim().slice(0, 180);
      if (link.source) clean.source = link.source;
      if (link.requiresModeration) clean.requiresModeration = true;
      if (link.moderationStatus) clean.moderationStatus = normaliseMediaModerationStatus(link.moderationStatus);
      if (link.submittedAt) clean.submittedAt = link.submittedAt;
      if (link.reviewedAt) clean.reviewedAt = link.reviewedAt;
      if (link.reviewedBy) clean.reviewedBy = link.reviewedBy;
      if (link.rightsStatus || link.copyrightStatus) clean.rightsStatus = normaliseMediaRightsStatus(link.rightsStatus || link.copyrightStatus);
      if (link.moderationNote) clean.moderationNote = link.moderationNote;
      return clean;
    })
    .filter((link) => link.url);
};

const accountProfileStorageKeyForUser = (user) => {
  const uid = String(user?.uid || "").trim();
  return uid ? `${accountStorageKey}:${uid}` : accountStorageKey;
};

const firestoreProfileReservedKeys = new Set(["lastSyncedAt"]);
const firestoreUserAdminManagedKeys = new Set([
  "accountRestricted",
  "restrictionStatus",
  "restrictionReason",
  "restrictedByUid",
  "restrictedByPhone",
  "restrictedAt",
  "unrestrictedByUid",
  "unrestrictedByPhone",
  "unrestrictedAt",
  "restrictionUpdatedAt",
  "lastRestrictionAuditId"
]);
const accountActivityCollections = {
  events: { type: "array" },
  joinedEvents: { type: "array" },
  joinedGroups: { type: "array" },
  bookings: { type: "array" },
  payments: { type: "array" },
  messages: { type: "array" },
  savedItems: { type: "array" },
  reviews: { type: "array" },
  groupInvites: { type: "array" },
  adminAuditLog: { type: "array" },
  adminDecisions: { type: "map" },
  adminRecordUpdates: { type: "map" }
};

const accountActivityFieldNames = Object.keys(accountActivityCollections);
const platformLedgerCollections = ["users", "players", "coaches", "groups", "centres", "events", "claims", "reports", "reviews", "refunds", "bookings", "slotLocks", "sessionJoinRequests", "lessonRequests", "coachBookings", "payments", "membershipPurchases", "membershipRedemptions", "stripeAccounts", "stripeDisputes", "profileSubscriptions", "profileSubscriptionInvoices", "adminDecisions", "settlementExports", "supportEvidence", "notificationTemplates", "notificationQueue", "notificationReceipts", "emailDeliveryStats", "emailCampaigns", "rateLimits", "launchEvidence", "platformFeeSettings", "supportSlaPolicies", "centreMergeDecisions", "profileMediaReviews"];
const defaultPlatformLedgerData = {
  status: "idle",
  message: "",
  users: [],
  players: [],
  coaches: [],
  groups: [],
  centres: [],
  events: [],
  claims: [],
  reports: [],
  reviews: [],
  refunds: [],
  bookings: [],
  slotLocks: [],
  sessionJoinRequests: [],
  lessonRequests: [],
  coachBookings: [],
  payments: [],
  membershipPurchases: [],
  membershipRedemptions: [],
  stripeAccounts: [],
  stripeDisputes: [],
  profileSubscriptions: [],
  profileSubscriptionInvoices: [],
  adminDecisions: [],
  settlementExports: [],
  supportEvidence: [],
  notificationTemplates: [],
  notificationQueue: [],
  notificationReceipts: [],
  emailDeliveryStats: [],
  emailCampaigns: [],
  rateLimits: [],
  launchEvidence: [],
  platformFeeSettings: [],
  supportSlaPolicies: [],
  centreMergeDecisions: [],
  profileMediaReviews: []
};

const defaultAccountMarketplaceData = {
  status: "idle",
  message: "Marketplace activity waits for a verified Firebase session.",
  bookings: [],
  managedBookings: [],
  payments: [],
  managedPayments: [],
  joinedSessions: [],
  managedJoinRequests: [],
  membershipPurchases: [],
  managedMembershipPurchases: [],
  membershipRedemptions: [],
  managedMembershipRedemptions: [],
  lessonRequests: [],
  managedLessonRequests: [],
  coachBookings: [],
  managedCoachBookings: [],
  events: []
};

const marketplaceLedgerRowKey = (row = {}, fallback = "row") => String(
  row.id
  || row.bookingId
  || row.paymentId
  || row.joinRequestId
  || row.membershipPurchaseId
  || row.redemptionId
  || row.lessonRequestId
  || row.checkoutSessionId
  || row.sourceId
  || fallback
);

const mergeMarketplaceLedgerRows = (...collections) => {
  const rows = new globalThis.Map();
  collections.flat().filter(Boolean).forEach((row, index) => {
    const key = marketplaceLedgerRowKey(row, `row-${index}`);
    rows.set(key, { ...(rows.get(key) || {}), ...row });
  });
  return [...rows.values()];
};

const normaliseMarketplaceJoinRequest = (request = {}) => {
  const sessionStartValue = request.sessionStartAt || request.nextSessionAt || request.startAt || "";
  const sessionStart = sessionStartValue ? new Date(sessionStartValue) : null;
  const hasSessionStart = sessionStart && !Number.isNaN(sessionStart.getTime());
  const derivedTime = hasSessionStart
    ? `${String(sessionStart.getHours()).padStart(2, "0")}:${String(sessionStart.getMinutes()).padStart(2, "0")}`
    : "";
  const currency = normaliseCurrencyCode(request.currency || marketplaceCurrencyForCountry(request.countryCode));
  return {
    ...request,
    id: request.kind === "event"
      ? request.eventId || request.sourceId || request.id
      : request.groupId || request.sourceId || request.id,
    joinRequestId: request.joinRequestId || request.id || "",
    title: request.title || (request.kind === "event" ? "Badminton event" : "Social badminton group"),
    venue: request.venue || request.venueName || "",
    venueId: request.venueId || request.centreId || "",
    date: request.date || (hasSessionStart ? localIsoDateFromDate(sessionStart) : ""),
    time: request.time || derivedTime,
    timeLabel: request.timeLabel || (request.time || derivedTime ? formatTimeLabel(request.time || derivedTime) : ""),
    price: request.price || (Number(request.unitAmountCents || request.amountCents) ? String(minorUnitsToAmount(request.unitAmountCents || request.amountCents, currency)) : ""),
    currency,
    joinedAt: request.joinedAt || request.createdAt || "",
    joinedStatus: request.joinedStatus || request.status || "Registered",
    paymentStatus: request.paymentStatus || request.status || "Payment pending",
    ticketCount: Number(request.ticketCount) || 1,
    ledgerSource: "marketplace"
  };
};

const marketplaceDateFromValue = (value) => {
  if (!value) return null;
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normaliseCoachLesson = (lesson = {}, index = 0) => {
  const startAt = marketplaceDateFromValue(lesson.startAt);
  const endAt = marketplaceDateFromValue(lesson.endAt);
  const proposedStartAt = marketplaceDateFromValue(lesson.proposedStartAt);
  const currency = normaliseCurrencyCode(lesson.currency || marketplaceCurrencyForCountry(lesson.countryCode));
  const status = String(lesson.status || "requested").toLowerCase();
  const paymentStatus = String(lesson.paymentStatus || "not_started").toLowerCase();
  return {
    ...lesson,
    id: lesson.id || lesson.lessonRequestId || lesson.bookingId || `coach-lesson-${index}`,
    lessonRequestId: lesson.lessonRequestId || lesson.id || "",
    coachName: lesson.coachName || lesson.title || "Badminton coach",
    playerName: lesson.playerName || "Player",
    status,
    paymentStatus,
    startAtDate: startAt,
    endAtDate: endAt,
    proposedStartAtDate: proposedStartAt,
    date: startAt ? localIsoDateFromDate(startAt) : "",
    time: startAt ? `${String(startAt.getHours()).padStart(2, "0")}:${String(startAt.getMinutes()).padStart(2, "0")}` : "",
    proposedDate: proposedStartAt ? localIsoDateFromDate(proposedStartAt) : "",
    proposedTime: proposedStartAt ? `${String(proposedStartAt.getHours()).padStart(2, "0")}:${String(proposedStartAt.getMinutes()).padStart(2, "0")}` : "",
    durationMinutes: Number.parseInt(lesson.durationMinutes || 60, 10) || 60,
    proposedDurationMinutes: Number.parseInt(lesson.proposedDurationMinutes || lesson.durationMinutes || 60, 10) || 60,
    serviceId: lesson.serviceId || "",
    serviceName: lesson.serviceName || lesson.serviceTitle || lesson.title || "Coaching lesson",
    currency,
    totalCents: Number(lesson.totalCents || 0),
    totalLabel: Number(lesson.totalCents || 0)
      ? formatCurrencyCents(lesson.totalCents, currency)
      : String(lesson.paymentStatus || "").toLowerCase() === "not_required"
        ? "Free"
        : "Price pending"
  };
};

const accountProfileWithMarketplaceData = (profile = {}, marketplace = defaultAccountMarketplaceData) => {
  const playerBookings = (marketplace.bookings || []).map((booking, index) => ({
    ...normaliseCourtBooking(booking, index),
    ledgerSource: "marketplace"
  }));
  const playerPayments = (marketplace.payments || []).map((payment) => ({
    ...payment,
    amount: payment.amount || (Number.isFinite(Number(payment.amountCents)) ? formatCurrencyCents(payment.amountCents, payment.currency) : "Pending"),
    ledgerSource: "marketplace"
  }));
  const joinedSessions = (marketplace.joinedSessions || []).map(normaliseMarketplaceJoinRequest);
  const joinedGroups = joinedSessions.filter((row) => row.kind !== "event");
  const joinedEvents = joinedSessions.filter((row) => row.kind === "event");
  return {
    ...profile,
    events: mergeMarketplaceLedgerRows(profile.events || [], marketplace.events || []),
    bookings: mergeMarketplaceLedgerRows(profile.bookings || [], playerBookings),
    payments: mergeMarketplaceLedgerRows(profile.payments || [], playerPayments),
    joinedGroups: mergeMarketplaceLedgerRows(profile.joinedGroups || [], joinedGroups),
    joinedEvents: mergeMarketplaceLedgerRows(profile.joinedEvents || [], joinedEvents),
    membershipPurchases: mergeMarketplaceLedgerRows(profile.membershipPurchases || [], marketplace.membershipPurchases || []),
    membershipRedemptions: marketplace.membershipRedemptions || [],
    managedBookings: marketplace.managedBookings || [],
    managedPayments: marketplace.managedPayments || [],
    managedJoinRequests: marketplace.managedJoinRequests || [],
    managedMembershipPurchases: marketplace.managedMembershipPurchases || [],
    managedMembershipRedemptions: marketplace.managedMembershipRedemptions || [],
    lessonRequests: marketplace.lessonRequests || [],
    managedLessonRequests: marketplace.managedLessonRequests || [],
    coachBookings: marketplace.coachBookings || [],
    managedCoachBookings: marketplace.managedCoachBookings || [],
    marketplaceSyncStatus: marketplace.status,
    marketplaceSyncMessage: marketplace.message
  };
};

const toPlainFirestoreValue = (value) => {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(toPlainFirestoreValue).filter((item) => item !== undefined);
  if (typeof value === "object") {
    const output = {};
    Object.entries(value).forEach(([key, nested]) => {
      if (firestoreProfileReservedKeys.has(key)) return;
      const clean = toPlainFirestoreValue(nested);
      if (clean !== undefined) output[key] = clean;
    });
    return output;
  }
  return value;
};

const fromFirestoreProfileValue = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(fromFirestoreProfileValue);
  if (typeof value === "object") {
    if (typeof value.toDate === "function") return value.toDate().toISOString();
    const output = {};
    Object.entries(value).forEach(([key, nested]) => {
      const clean = fromFirestoreProfileValue(nested);
      if (clean !== undefined) output[key] = clean;
    });
    return output;
  }
  return value;
};

const isRealFirebaseUser = (user) => Boolean(firebaseDb && user?.uid && !user.isLocalTestUser);
const accountProfileDocRef = (user) => (isRealFirebaseUser(user) ? doc(firebaseDb, "users", user.uid) : null);
const accountActivityCollectionRef = (user, field) => (isRealFirebaseUser(user) ? collection(firebaseDb, "users", user.uid, field) : null);
const platformLedgerCollectionRef = (collectionName) => (firebaseDb
  ? query(collection(firebaseDb, collectionName), limit(250))
  : null);

const profileEditedAtMs = (profile = {}) => {
  const candidates = [profile.clientUpdatedAt, profile.updatedAt, profile.lastSyncedAt]
    .map((value) => Date.parse(String(value || "")))
    .filter(Number.isFinite);
  return candidates.length ? Math.max(...candidates) : 0;
};

const platformLedgerRowsFromSnapshot = (collectionName, snapshot) => snapshot.docs
  .map((item) => ({
    id: item.id,
    ledgerCollection: collectionName,
    ...fromFirestoreProfileValue(item.data())
  }))
  .sort((a, b) => String(b.createdAt || b.updatedAt || "").localeCompare(String(a.createdAt || a.updatedAt || "")));

const notificationCategoryFrom = (notification = {}) => {
  const source = `${notification.category || ""} ${notification.templateKey || ""} ${notification.subjectCollection || ""}`.toLowerCase();
  if (source.includes("message") || source.includes("conversation")) return "message";
  if (source.includes("follow") || source.includes("social")) return "social";
  if (source.includes("membership")) return "membership";
  if (source.includes("lesson") || source.includes("coach")) return "coaching";
  if (source.includes("session") || source.includes("event")) return "session";
  if (source.includes("booking") || source.includes("court")) return "booking";
  if (source.includes("payment") || source.includes("refund") || source.includes("payout") || source.includes("stripe")) return "payment";
  if (source.includes("claim") || source.includes("media") || source.includes("moderation") || source.includes("report") || source.includes("review")) return "trust";
  return "account";
};

const notificationRoutePages = new Set([
  "messages",
  "playerDashboard",
  "organiser",
  "groupSessions",
  "members",
  "eventManager",
  "joinedEvents",
  "centreDashboard",
  "centreClaimSearch",
  "centrePayments",
  "coachDashboard",
  "coachPayouts",
  "payouts",
  "saved",
  "help",
  "account"
]);

const notificationTargetPage = (notification = {}) => {
  const detailPage = String(notification.detail?.routePage || notification.detail?.routepage || "").trim();
  if (notificationRoutePages.has(detailPage)) return detailPage;
  const collectionName = String(notification.subjectCollection || "").toLowerCase();
  if (collectionName === "conversations") return "messages";
  if (collectionName === "membershippurchases") return notification.detail?.groupId ? "playerDashboard" : "saved";
  if (collectionName === "sessionjoinrequests") return notification.detail?.kind === "event" ? "joinedEvents" : "playerDashboard";
  if (collectionName === "lessonrequests" || collectionName === "coachbookings") return "playerDashboard";
  if (collectionName === "bookings") return "bookings";
  if (collectionName === "events") return "eventManager";
  if (collectionName === "groups") return "groupSessions";
  if (collectionName === "claims" || collectionName === "centres") return "centreDashboard";
  if (["payments", "refunds", "payouts", "stripeaccounts", "stripedisputes"].includes(collectionName)) return "account";
  if (["reports", "reviews", "profilemediareviews", "supportevidence"].includes(collectionName)) return "help";
  return "account";
};

const normaliseAccountNotification = (item) => {
  const data = fromFirestoreProfileValue(item.data());
  const createdAt = data.createdAt || data.updatedAt || "";
  return {
    id: item.id,
    ...data,
    title: data.notificationSubject || data.templateTitle || "Badminta update",
    body: data.body || data.message || "Open Badminta for the latest details.",
    category: notificationCategoryFrom(data),
    read: data.read === true || Boolean(data.readAt),
    createdAt,
    targetPage: notificationTargetPage(data)
  };
};

const notificationRelativeTime = (value) => {
  const timestamp = Date.parse(String(value || ""));
  if (!Number.isFinite(timestamp)) return "Just now";
  const elapsed = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const notificationIconFor = (category) => ({
  message: MessageCircle,
  social: UserPlus,
  membership: UsersRound,
  coaching: Trophy,
  session: CalendarDays,
  booking: CalendarClock,
  payment: CreditCard,
  trust: ShieldCheck,
  account: Bell
}[category] || Bell);

const adminTrustReportsFromPlatform = (platformLedgerData = defaultPlatformLedgerData) => {
  const evidenceRows = platformLedgerData.supportEvidence || [];
  const notificationRows = platformLedgerData.notificationQueue || [];
  const evidenceCountFor = (collectionName, subjectId) => evidenceRows.filter((item) => item.subjectCollection === collectionName && item.subjectId === subjectId).length;
  const notificationCountFor = (collectionName, subjectId) => notificationRows.filter((item) => item.subjectCollection === collectionName && item.subjectId === subjectId).length;
  const reportRows = (platformLedgerData.reports || []).map((report) => ({
    id: `server-report-${report.id}`,
    serverReportId: report.id,
    kind: report.kind || "trust",
    title: report.title || "Support report",
    subject: report.subject || report.subjectId || "Badminta support",
    comment: report.comment || report.note || "No report details were added.",
    status: report.status || "open",
    ownerUid: report.ownerUid || report.reporterUid || "",
    countryCode: report.countryCode || "",
    supportEvidenceCount: report.supportEvidenceCount || report.evidenceCount || evidenceCountFor("reports", report.id),
    notificationCount: notificationCountFor("reports", report.id),
    lastNotificationTemplateKey: report.lastNotificationTemplateKey || "",
    supportFollowUpStatus: report.supportFollowUpStatus || "",
    automated: report.automated === true,
    safetyCategories: Array.isArray(report.context?.categories) ? report.context.categories : [],
    safetyRules: Array.isArray(report.context?.rules) ? report.context.rules : [],
    safetyFields: Array.isArray(report.context?.fields) ? report.context.fields.map((field) => ({
      field: field?.field || "content",
      value: field?.value || "",
      categories: Array.isArray(field?.categories) ? field.categories : [],
      rules: Array.isArray(field?.rules) ? field.rules : []
    })) : [],
    createdAt: report.createdAt || report.updatedAt || "",
    ledgerCollection: "reports"
  }));
  const reviewRows = (platformLedgerData.reviews || []).map((review) => ({
    id: `server-review-${review.id}`,
    serverReviewId: review.id,
    kind: review.kind || "general",
    title: review.title || reviewKindLabels[review.kind] || "Public review",
    subject: review.subject || review.subjectId || "Badminta profile",
    comment: review.comment || "No review comment was added.",
    score: review.score,
    ratingEligible: review.ratingEligible,
    status: review.status || review.moderationStatus || "pending_review",
    ownerUid: review.ownerUid || review.reviewerUid || "",
    countryCode: review.countryCode || "",
    supportEvidenceCount: review.supportEvidenceCount || evidenceCountFor("reviews", review.id),
    notificationCount: notificationCountFor("reviews", review.id),
    lastNotificationTemplateKey: review.lastNotificationTemplateKey || "",
    supportFollowUpStatus: review.supportFollowUpStatus || "",
    createdAt: review.createdAt || review.updatedAt || "",
    ledgerCollection: "reviews"
  }));
  const refundRows = (platformLedgerData.refunds || []).map((refund) => ({
    id: `server-refund-${refund.id}`,
    kind: "trust",
    title: "Refund review request",
    subject: refund.sourceId || refund.bookingId || refund.paymentId || "Marketplace payment",
    comment: [refund.reason, refund.note].filter(Boolean).join(" · ") || "Refund review requested.",
    status: refund.status || refund.reviewStatus || "requested",
    refundReviewId: refund.id,
    paymentId: refund.paymentId || "",
    bookingId: refund.bookingId || "",
    ownerUid: refund.ownerUid || refund.requesterUid || "",
    countryCode: refund.countryCode || "",
    supportEvidenceCount: refund.supportEvidenceCount || refund.evidenceCount || evidenceCountFor("refunds", refund.id),
    notificationCount: notificationCountFor("refunds", refund.id),
    lastNotificationTemplateKey: refund.lastNotificationTemplateKey || "",
    supportFollowUpStatus: refund.supportFollowUpStatus || refund.refundFailureFollowUpStatus || "",
    createdAt: refund.createdAt || refund.updatedAt || "",
    ledgerCollection: "refunds"
  }));
  const disputeRows = (platformLedgerData.stripeDisputes || []).map((dispute) => ({
    id: `server-dispute-${dispute.id || dispute.stripeDisputeId}`,
    kind: "payment_dispute",
    title: "Stripe chargeback dispute",
    subject: dispute.bookingId || dispute.sourceId || dispute.paymentId || dispute.stripeDisputeId || "Stripe dispute",
    comment: [
      dispute.reason ? `Reason: ${dispute.reason}` : "",
      dispute.status ? `Status: ${dispute.status}` : "",
      dispute.evidenceDueAt ? `Evidence due ${formatAdminDateTime(dispute.evidenceDueAt)}` : ""
    ].filter(Boolean).join(" · ") || "Stripe dispute needs support evidence.",
    status: dispute.status || dispute.paymentStatus || "open",
    stripeDisputeId: dispute.stripeDisputeId || dispute.id,
    paymentId: dispute.paymentId || "",
    bookingId: dispute.bookingId || "",
    ownerUid: dispute.ownerUid || "",
    countryCode: dispute.countryCode || "",
    supportEvidenceCount: dispute.supportEvidenceCount || evidenceCountFor("payments", dispute.paymentId),
    notificationCount: notificationCountFor("payments", dispute.paymentId),
    lastNotificationTemplateKey: dispute.lastNotificationTemplateKey || "",
    supportFollowUpStatus: dispute.supportFollowUpStatus || "",
    createdAt: dispute.createdAt || dispute.updatedAt || "",
    ledgerCollection: "stripeDisputes"
  }));
  const seen = new Set();
  return [...reportRows, ...reviewRows, ...refundRows, ...disputeRows].filter((report) => {
    const key = `${report.ledgerCollection}-${report.serverReportId || report.serverReviewId || report.refundReviewId || report.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const platformLedgerDecisionSummary = (platformLedgerData = defaultPlatformLedgerData) => Object.fromEntries(
  (platformLedgerData.adminDecisions || [])
    .filter((decision) => decision.status === "active" && decision.recordId && decision.decision)
    .map((decision) => [decision.recordId, decision.decision])
);

const stripAccountActivityFields = (profile = {}) => {
  const output = { ...profile };
  accountActivityFieldNames.forEach((field) => {
    delete output[field];
  });
  firestoreUserAdminManagedKeys.forEach((field) => {
    delete output[field];
  });
  return output;
};

const pickAccountActivityFields = (profile = {}) => accountActivityFieldNames.reduce((output, field) => {
  const config = accountActivityCollections[field];
  output[field] = config.type === "map"
    ? { ...(profile[field] || {}) }
    : Array.isArray(profile[field]) ? profile[field] : [];
  return output;
}, {});

const mergeAccountActivityFields = (profile = {}, activity = {}) => accountActivityFieldNames.reduce((output, field) => {
  const config = accountActivityCollections[field];
  if (activity[field] === undefined) return output;
  return {
    ...output,
    [field]: config.type === "map"
      ? { ...(activity[field] || {}) }
      : Array.isArray(activity[field]) ? activity[field] : []
  };
}, profile);

const hasAccountActivityFields = (profile = {}) => accountActivityFieldNames.some((field) => {
  const config = accountActivityCollections[field];
  return config.type === "map"
    ? Object.keys(profile[field] || {}).length > 0
    : Array.isArray(profile[field]) && profile[field].length > 0;
});

const activityDocId = (field, item, index, mapKey = "") => {
  const rawId = mapKey
    || item?.id
    || item?.key
    || item?.threadId
    || item?.bookingId
    || item?.paymentId
    || item?.checkoutReference
    || item?.checkoutSessionId
    || item?.subjectId
    || `${field}-${shortStableHash(JSON.stringify(item || {}))}-${index}`;
  return slugifyPath(String(rawId || `${field}-${index}`)).slice(0, 140) || `${field}-${index}`;
};

const activityRowsFromProfile = (profile = {}, field) => {
  const config = accountActivityCollections[field];
  if (!config) return [];
  if (config.type === "map") {
    return Object.entries(profile[field] || {}).map(([key, value], index) => ({
      id: activityDocId(field, { value }, index, key),
      data: {
        key,
        value,
        order: index
      }
    }));
  }
  return (Array.isArray(profile[field]) ? profile[field] : []).map((item, index) => {
    const cleanItem = toPlainFirestoreValue(item);
    const data = cleanItem && typeof cleanItem === "object" && !Array.isArray(cleanItem)
      ? cleanItem
      : { value: cleanItem };
    return {
      id: activityDocId(field, item, index),
      data: {
        ...data,
        order: index
      }
    };
  });
};

const profileFromActivitySnapshot = (field, snapshot) => {
  const config = accountActivityCollections[field];
  const rows = snapshot.docs
    .map((activityDoc) => fromFirestoreProfileValue({ id: activityDoc.id, ...(activityDoc.data() || {}) }))
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

  if (config.type === "map") {
    return rows.reduce((output, row) => {
      if (row.key) output[row.key] = row.value;
      return output;
    }, {});
  }

  return rows.map(({ ownerUid, field: _field, order, syncedAt, ...row }) => row);
};

const commitFirestoreBatchOps = async (ops = []) => {
  ensureFirebaseAppCheck();
  for (let index = 0; index < ops.length; index += 450) {
    const batch = writeBatch(firebaseDb);
    ops.slice(index, index + 450).forEach((apply) => apply(batch));
    await batch.commit();
  }
};

const syncAccountActivityCollections = async (profile, user) => {
  if (!isRealFirebaseUser(user)) return;
  ensureFirebaseAppCheck();

  await Promise.all(accountActivityFieldNames.map(async (field) => {
    const collectionRef = accountActivityCollectionRef(user, field);
    const rows = activityRowsFromProfile(profile, field);
    const rowIds = new Set(rows.map((row) => row.id));
    const existing = await getDocs(collectionRef);
    const ops = [];

    rows.forEach((row) => {
      ops.push((batch) => batch.set(doc(collectionRef, row.id), {
        ...row.data,
        ownerUid: user.uid,
        field,
        syncedAt: serverTimestamp()
      }, { merge: true }));
    });

    existing.docs.forEach((activityDoc) => {
      if (!rowIds.has(activityDoc.id)) ops.push((batch) => batch.delete(activityDoc.ref));
    });

    if (ops.length) await commitFirestoreBatchOps(ops);
  }));
};

const saveStoredAccountProfile = (profile, user = null) => {
  try {
    const value = JSON.stringify(toPlainFirestoreValue(profile));
    if (user?.uid) {
      window.localStorage.setItem(accountProfileStorageKeyForUser(user), value);
      return;
    }
    if (canUseAnonymousLocalProfile(user)) window.localStorage.setItem(accountStorageKey, value);
  } catch {
    // Local storage can be unavailable or full; Firestore sync still handles real accounts.
  }
};

const isImageMediaUrl = (url = "") => /^data:image\//i.test(url)
  || /\.(png|jpe?g|webp|gif|avif)(\?|#|$)/i.test(url)
  || /images\.|unsplash|cloudinary|fbcdn|cdninstagram|firebasestorage\.googleapis\.com/i.test(url);
const emailPattern = /^[^\s@]{1,64}@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/i;

const cleanEmailTags = (emails = []) => {
  const source = Array.isArray(emails) ? emails : String(emails || "").split(/[\s,;]+/);
  return Array.from(new Set(source.map((email) => String(email || "").trim().toLowerCase()).filter((email) => emailPattern.test(email))));
};

const parseMediaEmbed = (url = "") => {
  const rawUrl = String(url || "").trim();
  if (!rawUrl) return null;
  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch {
    try {
      parsed = new URL(`https://${rawUrl}`);
    } catch {
      return null;
    }
  }
  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
  const path = parsed.pathname;
  const cleanUrl = parsed.href;

  if (host === "youtu.be") {
    const id = path.split("/").filter(Boolean)[0];
    if (id) return { provider: "YouTube", type: "iframe", embedUrl: `https://www.youtube.com/embed/${id}`, url: cleanUrl };
  }
  if (host.includes("youtube.com")) {
    const id = parsed.searchParams.get("v") || path.match(/\/(?:embed|shorts|live)\/([^/?#]+)/)?.[1];
    if (id) return { provider: "YouTube", type: "iframe", embedUrl: `https://www.youtube.com/embed/${id}`, url: cleanUrl };
  }
  if (host.includes("vimeo.com")) {
    const id = path.match(/\/(?:video\/)?(\d+)/)?.[1];
    if (id) return { provider: "Vimeo", type: "iframe", embedUrl: `https://player.vimeo.com/video/${id}`, url: cleanUrl };
  }
  if (host.includes("tiktok.com")) {
    const id = path.match(/\/video\/(\d+)/)?.[1];
    if (id) return { provider: "TikTok", type: "iframe", embedUrl: `https://www.tiktok.com/embed/v2/${id}`, url: cleanUrl, portrait: true };
  }
  if (host.includes("instagram.com")) {
    const match = path.match(/\/(p|reel|tv)\/([^/?#]+)/);
    if (match) return { provider: "Instagram", type: "iframe", embedUrl: `https://www.instagram.com/${match[1]}/${match[2]}/embed`, url: cleanUrl, portrait: true };
  }
  if (host.includes("facebook.com") || host === "fb.watch") {
    return { provider: "Facebook", type: "iframe", embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(cleanUrl)}&show_text=false&width=560`, url: cleanUrl };
  }
  if (host.includes("dailymotion.com") || host === "dai.ly") {
    const id = host === "dai.ly" ? path.split("/").filter(Boolean)[0] : path.match(/\/video\/([^/?#]+)/)?.[1];
    if (id) return { provider: "Dailymotion", type: "iframe", embedUrl: `https://www.dailymotion.com/embed/video/${id}`, url: cleanUrl };
  }
  return null;
};

const deepMergeAccountProfile = (base, patch) => ({
  ...base,
  ...patch,
  player: {
    ...base.player,
    ...(patch.player || {}),
    visibility: {
      ...base.player.visibility,
      ...(patch.player?.visibility || {})
    }
  },
  group: {
    ...base.group,
    ...(patch.group || {})
  },
  centre: {
    ...base.centre,
    ...(patch.centre || {})
  },
  coach: {
    ...base.coach,
    ...(patch.coach || {})
  }
});

const loadStoredAccountProfile = (user = null) => {
  try {
    const userScopedStored = user?.uid ? window.localStorage.getItem(accountProfileStorageKeyForUser(user)) : "";
    const anonymousStored = canUseAnonymousLocalProfile(user) || canUseLegacyGlobalProfileFallback(user)
      ? window.localStorage.getItem(accountStorageKey)
      : "";
    const stored = userScopedStored || anonymousStored;
    return stored ? deepMergeAccountProfile(defaultAccountProfile, JSON.parse(stored)) : defaultAccountProfile;
  } catch {
    return defaultAccountProfile;
  }
};

const buildFirestoreAccountProfilePayload = (profile, user) => ({
  ...toPlainFirestoreValue(stripAccountActivityFields(profile)),
  ...accountActivityFieldNames.reduce((output, field) => ({ ...output, [field]: deleteField() }), {}),
  uid: user.uid,
  ownerUid: user.uid,
  phoneNumber: user.phoneNumber || "",
  email: profile.email || user.email || "",
  displayName: profile.fullName || user.displayName || "",
  profileSchema: 1,
  activityCollections: accountActivityFieldNames,
  updatedAt: serverTimestamp()
});

const phonePlaceholderByCountry = {
  AU: "0412 345 678",
  NZ: "021 123 4567",
  US: "(415) 555-0123",
  CA: "(416) 555-0123",
  GB: "07123 456789",
  IE: "085 123 4567",
  PK: "0300 1234567",
  IN: "98765 43210",
  BD: "01712 345678",
  LK: "071 234 5678",
  NP: "984 1234567",
  MY: "012 345 6789",
  SG: "8123 4567",
  ID: "0812 3456 7890",
  PH: "0917 123 4567",
  TH: "081 234 5678",
  VN: "091 234 5678",
  CN: "138 0013 8000",
  HK: "5123 4567",
  TW: "0912 345 678",
  JP: "090 1234 5678",
  KR: "010 1234 5678",
  AE: "050 123 4567",
  SA: "050 123 4567",
  ZA: "071 123 4567",
  FR: "06 12 34 56 78",
  DE: "01512 3456789",
  BR: "11 91234-5678",
  MX: "55 1234 5678"
};

const preferredPhoneCountryCodes = [
  "AU", "NZ", "US", "CA", "GB", "IE", "PK", "IN", "BD", "LK", "NP", "MY", "SG", "ID", "PH", "TH", "VN", "CN", "HK", "TW", "JP", "KR", "AE", "SA", "QA", "KW", "BH", "OM", "ZA", "EG", "NG", "KE", "FR", "DE", "IT", "ES", "NL", "BE", "CH", "AT", "SE", "NO", "DK", "FI", "PL", "PT", "GR", "TR", "BR", "MX", "AR", "CL", "CO", "PE", "RU", "UA"
];

const phoneCountryDisplayNames = typeof Intl !== "undefined" && Intl.DisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

const buildPhoneCountry = (code) => ({
  code,
  name: phoneCountryDisplayNames?.of(code) || code,
  dialCode: `+${getCountryCallingCode ? getCountryCallingCode(code) : code === "AU" ? "61" : ""}`,
  placeholder: phonePlaceholderByCountry[code] || "Mobile number"
});

const sortPhoneCountries = (countries) => countries.sort((a, b) => {
    const preferredA = preferredPhoneCountryCodes.indexOf(a.code);
    const preferredB = preferredPhoneCountryCodes.indexOf(b.code);
    if (preferredA !== -1 || preferredB !== -1) {
      if (preferredA === -1) return 1;
      if (preferredB === -1) return -1;
      return preferredA - preferredB;
    }
    return a.name.localeCompare(b.name);
  });

let phoneDialingCountries = [buildPhoneCountry("AU")];
let phoneCountryByCode = { AU: phoneDialingCountries[0] };
let phoneNumberRuntimePromise = null;

const loadPhoneNumberRuntime = async () => {
  if (phoneNumberRuntimePromise) return phoneNumberRuntimePromise;
  phoneNumberRuntimePromise = import("libphonenumber-js/min").then((phoneModule) => {
    getPhoneCountries = phoneModule.getCountries;
    getCountryCallingCode = phoneModule.getCountryCallingCode;
    parsePhoneNumberFromString = phoneModule.parsePhoneNumberFromString;
    phoneDialingCountries = sortPhoneCountries([
      ...preferredPhoneCountryCodes,
      ...getPhoneCountries().filter((code) => !preferredPhoneCountryCodes.includes(code))
    ].map(buildPhoneCountry));
    phoneCountryByCode = Object.fromEntries(phoneDialingCountries.map((country) => [country.code, country]));
    return phoneDialingCountries;
  }).catch((error) => {
    phoneNumberRuntimePromise = null;
    throw error;
  });
  return phoneNumberRuntimePromise;
};

const phoneTimezoneRegionMap = {
  "Australia/": "AU",
  "Pacific/Auckland": "NZ",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Asia/Karachi": "PK",
  "Asia/Kolkata": "IN",
  "Asia/Dhaka": "BD",
  "Asia/Colombo": "LK",
  "Asia/Kathmandu": "NP",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Singapore": "SG",
  "Asia/Jakarta": "ID",
  "Asia/Manila": "PH",
  "Asia/Bangkok": "TH",
  "Asia/Ho_Chi_Minh": "VN",
  "Asia/Shanghai": "CN",
  "Asia/Hong_Kong": "HK",
  "Asia/Taipei": "TW",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Qatar": "QA",
  "Asia/Kuwait": "KW",
  "Asia/Bahrain": "BH",
  "Asia/Muscat": "OM",
  "Africa/Johannesburg": "ZA",
  "Africa/Cairo": "EG",
  "Africa/Lagos": "NG",
  "Africa/Nairobi": "KE",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Rome": "IT",
  "Europe/Madrid": "ES",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Zurich": "CH",
  "Europe/Vienna": "AT",
  "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI",
  "Europe/Warsaw": "PL",
  "Europe/Lisbon": "PT",
  "Europe/Athens": "GR",
  "Europe/Istanbul": "TR",
  "America/Sao_Paulo": "BR",
  "America/Mexico_City": "MX",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Santiago": "CL",
  "America/Bogota": "CO",
  "America/Lima": "PE",
  "Europe/Moscow": "RU",
  "Europe/Kyiv": "UA"
};

const browserPhoneCountryCode = (fallbackCountryCode = "") => {
  if (typeof Intl !== "undefined") {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const direct = phoneTimezoneRegionMap[timezone];
    if (direct) return direct;
    const prefixMatch = Object.entries(phoneTimezoneRegionMap).find(([prefix]) => prefix.endsWith("/") && timezone.startsWith(prefix));
    if (prefixMatch?.[1]) return prefixMatch[1];
  }
  if (typeof navigator !== "undefined") {
    const locales = [...(navigator.languages || []), navigator.language].filter(Boolean);
    for (const locale of locales) {
      try {
        const region = new Intl.Locale(locale).region?.toUpperCase();
        if (region) return region;
      } catch {
        const fallbackRegion = String(locale || "").match(/[-_]([A-Za-z]{2})\b/)?.[1]?.toUpperCase();
        if (fallbackRegion) return fallbackRegion;
      }
    }
  }
  const fallback = String(fallbackCountryCode || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(fallback)) return fallback;
  return "AU";
};

const isEmbeddedSocialBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /FBAN|FBAV|FB_IAB|FBIOS|FB4A|Instagram|Messenger/i.test(navigator.userAgent || "");
};

const isFirebaseCaptchaError = (error) => /captcha-check-failed|missing-recaptcha-token|invalid-recaptcha-token|missing-app-credential|invalid-app-credential/i.test(String(error?.code || error?.message || ""));
const isFirebaseTransientPhoneError = (error) => /network-request-failed|internal-error|failed to fetch|networkerror|err_failed/i.test(String(error?.code || error?.message || ""));

const getPhoneCountry = (countryCode = "") => phoneCountryByCode[String(countryCode || "").toUpperCase()] || phoneCountryByCode[browserPhoneCountryCode()] || phoneCountryByCode.AU;

const parseBadmintaPhoneNumber = (value = "", countryCode = "AU") => {
  const cleaned = String(value || "").replace(/[^\d+]/g, "");
  if (!cleaned) return null;
  const internationalValue = cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
  if (parsePhoneNumberFromString) return parsePhoneNumberFromString(internationalValue, getPhoneCountry(countryCode).code) || null;
  const selectedCountry = getPhoneCountry(countryCode);
  const nationalNumber = internationalValue.replace(/^\+?61/, "").replace(/^0+/, "");
  const number = internationalValue.startsWith("+") ? internationalValue : `${selectedCountry.dialCode}${nationalNumber}`;
  const digitCount = number.replace(/\D/g, "").length;
  return {
    country: selectedCountry.code,
    number,
    isPossible: () => digitCount >= 8 && digitCount <= 15
  };
};

const phoneCountryFromInternationalNumber = (value = "") => {
  const cleaned = String(value || "").replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+") && !cleaned.startsWith("00")) return null;
  const parsed = parseBadmintaPhoneNumber(cleaned);
  if (parsed?.country) return getPhoneCountry(parsed.country);
  const internationalValue = cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
  if (!internationalValue.startsWith("+")) return null;
  const digits = internationalValue.slice(1);
  return [...phoneDialingCountries]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((country) => digits.startsWith(country.dialCode.slice(1))) || null;
};

const normalisePhoneNumber = (value, countryCode = "AU") => {
  const selectedCountry = getPhoneCountry(countryCode);
  const cleaned = String(value || "").replace(/[^\d+]/g, "");
  if (!cleaned) return "";
  const parsed = parseBadmintaPhoneNumber(cleaned, selectedCountry.code);
  if (parsed?.number) return parsed.number;
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("00")) return `+${cleaned.slice(2)}`;
  const dialDigits = selectedCountry.dialCode.replace(/\D/g, "");
  if (cleaned.startsWith(dialDigits) && cleaned.length > dialDigits.length + 4) return `+${cleaned}`;
  const nationalNumber = cleaned.replace(/^0+/, "");
  return `${selectedCountry.dialCode}${nationalNumber}`;
};

const soleSuperAdminPhoneNumber = normalisePhoneNumber("0472729564");

const hasSuperAdminAllowlistAccess = (user) => {
  if (!user) return false;
  const userPhone = normalisePhoneNumber(user.phoneNumber);
  return Boolean(userPhone && userPhone === soleSuperAdminPhoneNumber);
};

const firebaseErrorMessage = (error) => {
  const code = error?.code || "";
  if (code.includes("invalid-phone-number")) return "Enter a valid mobile number for the selected country code, or paste the full international number starting with +.";
  if (code.includes("too-many-requests")) return "Too many attempts. Wait a moment before requesting another SMS.";
  if (code.includes("invalid-verification-code")) return "That verification code is not correct. Check the SMS and try again.";
  if (code.includes("captcha-check-failed")) return "The security check could not finish. Complete the visible security check below, then request a new code.";
  if (code.includes("missing-recaptcha-token") || code.includes("invalid-recaptcha-token")) return "The automatic security check could not be validated. Complete the visible check below, then request a new code.";
  if (code.includes("configuration-not-found")) return "Phone verification is connected, but Firebase Authentication still needs to be started in the Firebase console.";
  if (code.includes("operation-not-allowed")) return "Phone sign-in is not enabled yet in Firebase Authentication.";
  if (code.includes("app-not-authorized") || code.includes("unauthorized-domain")) return "This website domain is not authorised for Firebase Authentication yet.";
  if (code.includes("missing-app-credential") || code.includes("invalid-app-credential")) return "The browser blocked the automatic security check. Complete the visible check below, then request a new code.";
  if (code.includes("network-request-failed")) return "The automatic phone security check was interrupted. Complete the visible check below, then request a new code.";
  return error?.message || "Phone verification failed. Try again.";
};

const firebaseCodeVerificationErrorMessage = (error) => {
  const code = String(error?.code || "");
  if (code.includes("network-request-failed")) return "The connection was interrupted. Submit the same verification code again. Request a new code only if the retry still fails.";
  return firebaseErrorMessage(error);
};

const marketplaceFunctionErrorMessage = (error) => {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  if (/account bootstrap/i.test(message) && /too many|rate-limit/i.test(message)) return "Your saved profile is available. Background account recovery has refreshed several times and will resume automatically shortly.";
  if (/failed to fetch|networkerror|cors|err_failed/i.test(message)) return "Badminta could not reach the marketplace backend from this domain. The profile can still be saved to your account, but public publishing is pending.";
  if (/internal/i.test(code) && (!message || /^internal$/i.test(message))) return "The marketplace backend returned an internal error. Your account profile can still be saved, and public publishing can be retried after backend checks pass.";
  if (/unavailable|deadline-exceeded/i.test(code)) return "The marketplace backend is temporarily unavailable. Your profile can still be saved to your account.";
  if (/app-check/i.test(message)) return "Backend marketplace actions need Firebase App Check to be enabled for this site before production use.";
  if (/STRIPE_SECRET_KEY|secret/i.test(message)) return "Stripe backend secrets are not configured yet, so live checkout is disabled.";
  if (/onboarding|payout account|Stripe verification/i.test(message)) return message;
  if (/unauthenticated/i.test(code)) return "Sign in with your verified phone before using marketplace actions.";
  return message || "Marketplace backend action failed. Check setup and try again.";
};

const publicMissingFieldCopy = {
  "Player role saved": ["Choose player account type", "Add the player role to your account before publishing a player profile."],
  "Coach role saved": ["Choose coach account type", "Add the coach role to your account before publishing a coach profile."],
  "Organiser role saved": ["Choose group organiser account type", "Add the organiser role to your account before publishing a social group."],
  "Centre role saved": ["Choose centre owner account type", "Add the centre owner role to your account before publishing a centre profile."],
  "Social organiser role saved": ["Choose group organiser account type", "Add the organiser role to your account before publishing a social group."],
  "Centre owner role saved": ["Choose centre owner account type", "Add the centre owner role to your account before publishing a centre profile."],
  "Coach or trainer role saved": ["Choose coach account type", "Add the coach role to your account before publishing a coach profile."],
  "Public display name": ["Add your public name", "Enter the name people should see on your Badminta profile."],
  "Real player profile photo": ["Upload a real player photo", "Use an actual photo or image you own. Stock, placeholder and dummy images do not count."],
  "Playing level": ["Select your playing level", "Choose beginner, intermediate, advanced or competitive."],
  "Home suburb": ["Choose your home suburb", "Search and select the suburb or city where players should find you."],
  "Availability windows": ["Set availability windows", "Turn on at least one day and time window."],
  "Preferred play format": ["Select a play format", "Choose the session style you prefer."],
  "Why people should play with you": ["Write your player note", "Explain your experience, level and what games you enjoy."],
  "Real coach photo": ["Upload a real coach photo", "Use a real photo or media you own. Stock, placeholder and dummy images do not count."],
  "Coach headline": ["Add a coach headline", "Summarise what you coach and who you help."],
  "Coach bio": ["Write a coach bio", "Explain session style, experience and player outcomes."],
  "Accreditation": ["Add accreditation or proof", "List coaching proof, club role or relevant experience."],
  "Hourly rate": ["Add an hourly rate", "Show what a lesson costs before players enquire."],
  "Coaching location": ["Add coaching location", "Add suburbs, centres or coverage area."],
  "Coach availability": ["Set coach availability", "Turn on at least one day and time window."],
  "Group name": ["Add group name", "Give the social group a clear public name."],
  "Real group image or logo": ["Upload a real group image or logo", "Use an image you own. Stock, placeholder and dummy images do not count."],
  "Group description": ["Write group description", "Explain level, atmosphere, rules and who should join."],
  "Venue": ["Select venue", "Choose the real centre where the group plays."],
  "Playing days and times": ["Set playing days and times", "Choose the weekly day and time players should attend."],
  "Format": ["Select group format", "Choose doubles, singles, coaching, social or match-play format."],
  "Skill band": ["Select skill band", "Set the level this group is suitable for."],
  "Capacity": ["Add capacity", "Show how many players can join."],
  "Price per player": ["Add price per player", "Show the cost before players join."],
  "Centre name": ["Add centre name", "Enter the public name of the centre."],
  "Real centre logo": ["Upload a real centre logo", "Use an official logo or image you are allowed to use."],
  "Suburb or city": ["Add suburb or city", "Select where the centre should appear in search."],
  "Street address": ["Add street address", "Players need a clear address before booking."],
  "Contact email or phone": ["Add contact details", "Add an email or phone for centre enquiries."],
  "Website or booking link": ["Add website or booking link", "Add the official site or booking page."],
  "Court count": ["Add court count", "Show how many courts players can book."],
  "Opening hours": ["Add opening hours", "Add business hours or weekly opening times."],
  "Court fee schedule": ["Add court fees", "Set standard, off-peak, peak or weekend pricing."]
};

const missingFieldMeta = (field) => {
  const cleanField = String(field || "").trim();
  const [title, detail] = publicMissingFieldCopy[cleanField] || [`Complete ${cleanField || "required item"}`, "This required item must be added before the profile can be public."];
  return { field: cleanField, title, detail };
};

const missingFieldSummary = (missing = []) => {
  const titles = missing.map(missingFieldMeta).map((item) => item.title.toLowerCase());
  if (!titles.length) return "";
  if (titles.length <= 3) return titles.join(", ");
  return `${titles.slice(0, 3).join(", ")} and ${titles.length - 3} more item${titles.length - 3 === 1 ? "" : "s"}`;
};

const publicProfileSaveMessage = ({ label, backendError = null, missing = [] }) => {
  const cleanMissing = Array.from(new Set((missing || []).map((item) => String(item || "").trim()).filter(Boolean)));
  if (!cleanMissing.length && !backendError) return `${label} saved and published to your public profile.`;
  if (!cleanMissing.length && backendError) return `${label} saved to your account. Public publishing is pending: ${marketplaceFunctionErrorMessage(backendError)}`;
  const draftMessage = `${label} saved to your account, but it is still a private draft. Finish ${missingFieldSummary(cleanMissing)} before it can appear publicly.`;
  if (backendError) return `${draftMessage} Backend publishing is also pending: ${marketplaceFunctionErrorMessage(backendError)}`;
  return draftMessage;
};

const profileSaveMessageTone = (message = "") => {
  if (/saved and published/i.test(message)) return "success";
  if (/private draft|publishing is pending|saved to your account/i.test(message)) return "warning";
  return "error";
};

const continueAfterSave = (navigate, target, payload = {}, delayMs = 900) => {
  window.setTimeout(() => navigate(target, payload), delayMs);
};

const profileDraftIdentity = (accountProfile = {}) => {
  const currentUser = firebaseAuth?.currentUser;
  return [
    currentUser?.uid,
    accountProfile.uid,
    accountProfile.ownerUid,
    normalisePhoneNumber(currentUser?.phoneNumber || ""),
    normalisePhoneNumber(accountProfile.phoneNumber || ""),
    accountProfile.email,
    accountProfile.fullName
  ].map((value) => String(value || "").trim()).find(Boolean) || "local";
};

const profileSetupDraftKey = (roleId, accountProfile = {}) => {
  const safeRole = String(roleId || "profile").replace(/[^a-z0-9-]/gi, "-").toLowerCase();
  return `${profileSetupDraftStorageKey}:${safeRole}:${encodeURIComponent(profileDraftIdentity(accountProfile))}`;
};

const loadProfileSetupDraftByKey = (draftKey) => {
  if (typeof window === "undefined" || !draftKey) return null;
  try {
    const stored = window.localStorage.getItem(draftKey);
    if (!stored) return null;
    const draft = JSON.parse(stored);
    return [1, 2].includes(Number(draft?.schema)) && draft.form && typeof draft.form === "object" ? draft : null;
  } catch {
    return null;
  }
};

const saveProfileSetupDraftByKey = (draftKey, payload) => {
  if (typeof window === "undefined" || !draftKey) return;
  try {
    window.localStorage.setItem(draftKey, JSON.stringify(payload));
  } catch {
    // Draft persistence is best-effort; final profile saves still use account storage or Firestore.
  }
};

const clearProfileSetupDraftByKey = (draftKey) => {
  if (typeof window === "undefined" || !draftKey) return;
  try {
    window.localStorage.removeItem(draftKey);
  } catch {
    // Ignore storage cleanup failures.
  }
};

const appCheckOptionalProfileFunctions = new Set([
  "bootstrapBadmintaAccount",
  "listAdminSignupAccounts",
  "recoverEligibleLegacyPlayerDrafts",
  "saveBadmintaAccountProfile",
  "uploadBadmintaProfileMedia",
  "upsertPlayerProfile",
  "upsertCoachProfile",
  "upsertSocialGroup",
  "upsertGroupSession",
  "inviteGroupMembers",
  "upsertOwnedCentre",
  "upsertMarketplaceEvent",
  "startMarketplaceConversation",
  "listMarketplaceConversations",
  "sendMarketplaceMessage",
  "markMarketplaceConversationRead",
  "markNotificationsRead",
  "toggleMarketplaceFollow"
]);

const callProfileFunctionEndpoint = async (name, payload = {}, idToken = "") => {
  const region = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || "australia-southeast1";
  const endpoint = `https://${region}-${firebaseConfig.projectId}.cloudfunctions.net/${encodeURIComponent(name)}`;
  const response = await fetch(endpoint, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
    },
    body: JSON.stringify({ data: payload })
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.error) {
    const status = String(body.error?.status || response.status || "internal").toLowerCase().replace(/_/g, "-");
    const error = new Error(body.error?.message || "Badminta could not save this profile right now.");
    error.code = `functions/${status}`;
    throw error;
  }
  return body.result || body.data || {};
};

const callAuthenticatedProfileFunction = async (name, payload = {}) => {
  const currentUser = firebaseAuth?.currentUser;
  if (!currentUser) throw new Error("Sign in before saving profile details.");
  return callProfileFunctionEndpoint(name, payload, await currentUser.getIdToken());
};

const callPublicProfileFunction = async (name, payload = {}) => callProfileFunctionEndpoint(name, payload);

const callMarketplaceFunction = async (name, payload = {}) => {
  await loadFirebaseRuntime();
  if (!firebaseFunctions) {
    throw new Error("Firebase Functions are not configured for this environment.");
  }
  if (!firebaseAuth?.currentUser) {
    throw new Error("Sign in before using marketplace backend actions.");
  }
  if (appCheckOptionalProfileFunctions.has(name)) {
    return callAuthenticatedProfileFunction(name, payload);
  }
  ensureFirebaseAppCheck();
  const callable = httpsCallable(firebaseFunctions, name);
  const result = await callable(payload);
  return result.data || {};
};

const claimEvidenceMaxFileSize = 20 * 1024 * 1024;
const claimEvidenceAcceptedFileTypes = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp"
]);
const claimEvidenceAcceptedExtensions = new Set(["pdf", "doc", "docx", "heic", "heif", "jpeg", "jpg", "png", "webp"]);

const formatClaimFileSize = (size = 0) => {
  const bytes = Number(size) || 0;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const claimEvidenceFileExtension = (fileName = "") => String(fileName).split(".").pop()?.toLowerCase() || "";

const claimEvidenceFileError = (file) => {
  if (!file) return "";
  const extension = claimEvidenceFileExtension(file.name);
  if (file.size > claimEvidenceMaxFileSize) return "Attach a PDF, image or document under 20 MB.";
  if (!claimEvidenceAcceptedFileTypes.has(file.type) && !claimEvidenceAcceptedExtensions.has(extension)) {
    return "Attach a PDF, JPG, PNG, WebP, HEIC, DOC or DOCX file.";
  }
  return "";
};

const claimEvidenceAttachmentFromFile = (file) => ({
  name: file?.name || "Claim evidence",
  type: file?.type || "application/octet-stream",
  size: Number(file?.size || 0),
  sizeLabel: formatClaimFileSize(file?.size || 0),
  storagePath: "",
  url: "",
  attachedAt: new Date().toISOString()
});

const safeClaimEvidenceFileName = (fileName = "claim-evidence.pdf") => {
  const extension = claimEvidenceFileExtension(fileName);
  const base = slugifyPath(fileName.replace(/\.[^.]+$/g, "") || "claim-evidence").slice(0, 90);
  return extension ? `${base}.${extension}` : base;
};

const uploadClaimEvidenceFile = async ({ file, user, venue }) => {
  if (!user?.uid || !file) return null;
  await loadFirebaseRuntime();
  if (!firebaseStorage) return null;
  ensureFirebaseAppCheck();
  const error = claimEvidenceFileError(file);
  if (error) throw new Error(error);
  const centreSlug = slugifyPath(venue?.id || venue?.name || "centre-claim").slice(0, 90);
  const fileName = safeClaimEvidenceFileName(file.name);
  const path = `claims/${user.uid}/${centreSlug}/${Date.now()}-${fileName}`;
  const uploadRef = storageRef(firebaseStorage, path);
  await uploadBytes(uploadRef, file, {
    contentType: file.type || "application/octet-stream",
    customMetadata: {
      originalName: file.name || "Claim evidence",
      centreId: String(venue?.id || ""),
      centreName: String(venue?.name || "")
    }
  });
  const url = await getDownloadURL(uploadRef);
  return {
    ...claimEvidenceAttachmentFromFile(file),
    storagePath: path,
    url,
    uploadedAt: new Date().toISOString()
  };
};

const globalCitySeeds = globalCityRows
  .map((seed) => ({
    ...seed,
    city: String(seed.city || "").trim(),
    country: String(seed.country || "").trim().toUpperCase(),
    countryName: String(seed.country_name || seed.country || "").trim(),
    lat: Number(seed.lat),
    lon: Number(seed.lon),
    radiusM: Number(seed.radius_m || 45000)
  }))
  .filter((seed) => seed.city && seed.country && Number.isFinite(seed.lat) && Number.isFinite(seed.lon));

const cities = [...new Set(globalCitySeeds.map((seed) => seed.city))];

const cityCoordinates = Object.fromEntries(globalCitySeeds.map((seed) => {
  const span = Math.max(0.08, Math.min(0.75, (seed.radiusM || 45000) / 111000));
  return [seed.city, {
    lat: seed.lat,
    lon: seed.lon,
    bbox: [seed.lon - span, seed.lat - span, seed.lon + span, seed.lat + span],
    country: seed.country,
    countryName: seed.countryName
  }];
}));
const cityCoordinateKey = (cityName = "") => String(cityName || "").trim().toLowerCase();
const cityCoordinatesByCountryCity = Object.fromEntries(globalCitySeeds.map((seed) => {
  const span = Math.max(0.08, Math.min(0.75, (seed.radiusM || 45000) / 111000));
  return [`${seed.country}-${cityCoordinateKey(seed.city)}`, {
    lat: seed.lat,
    lon: seed.lon,
    bbox: [seed.lon - span, seed.lat - span, seed.lon + span, seed.lat + span],
    country: seed.country,
    countryName: seed.countryName
  }];
}));
const getCityCoordinate = (cityName = "", countryCode = "") => {
  const key = cityCoordinateKey(cityName);
  const countryKey = String(countryCode || "").trim().toUpperCase();
  if (countryKey && cityCoordinatesByCountryCity[`${countryKey}-${key}`]) return cityCoordinatesByCountryCity[`${countryKey}-${key}`];
  const matchingSeeds = globalCitySeeds.filter((seed) => cityCoordinateKey(seed.city) === key);
  if (matchingSeeds.length !== 1) return null;
  return cityCoordinatesByCountryCity[`${matchingSeeds[0].country}-${key}`] || null;
};

const cityCountryNames = Object.fromEntries(globalCitySeeds.map((seed) => [seed.city, seed.countryName || seed.country]));
const coverageCountryRows = Object.values(worldwideCountryCoverage?.countries || {});
const countryNameByCode = {
  ...Object.fromEntries(coverageCountryRows.map((country) => [String(country.code || "").toUpperCase(), country.name || country.code])),
  ...Object.fromEntries(globalCitySeeds.map((seed) => [seed.country, seed.countryName || seed.country])),
  AU: "Australia"
};
const countryCodeByName = Object.fromEntries(Object.entries(countryNameByCode).map(([code, name]) => [String(name || "").toLowerCase(), code]));
const normaliseCountryCode = (value = "", fallbackCity = "") => {
  const text = String(value || "").trim();
  if (!text && fallbackCity) return getCityCoordinate(fallbackCity)?.country || "";
  const upper = text.toUpperCase();
  if (countryNameByCode[upper]) return upper;
  return countryCodeByName[text.toLowerCase()] || (fallbackCity ? getCityCoordinate(fallbackCity)?.country || "" : "");
};
const getCountryName = (value = "", fallbackCity = "") => {
  const code = normaliseCountryCode(value, fallbackCity);
  return countryNameByCode[code] || String(value || "").trim() || cityCountryNames[fallbackCity] || "";
};
const marketplaceCurrencyForCountry = (countryCode = "", fallback = "aud") => (
  currencyForCountryCode(normaliseCountryCode(countryCode), fallback)
);
const marketplaceCurrencyForLocation = (location = {}, fallback = "aud") => (
  marketplaceCurrencyForCountry(location?.countryCode || location?.country, fallback)
);

const suburbCoordinates = {
  "Melbourne-Albert Park": { lat: -37.8417, lon: 144.9554 },
  "Melbourne-Brunswick": { lat: -37.7667, lon: 144.9620 },
  "Melbourne-Derrimut": { lat: -37.7922, lon: 144.7695 },
  "Melbourne-Templestowe": { lat: -37.7560, lon: 145.1330 },
  "Melbourne-Glen Waverley": { lat: -37.8797, lon: 145.1648 },
  "Melbourne-Ringwood": { lat: -37.8143, lon: 145.2278 },
  "Melbourne-Sunshine": { lat: -37.7885, lon: 144.8320 },
  "Sydney-Alexandria": { lat: -33.9023, lon: 151.2003 }
};

const australianStateCity = {
  "Victoria": "Melbourne",
  "New South Wales": "Sydney",
  "Queensland": "Brisbane",
  "Western Australia": "Perth",
  "South Australia": "Adelaide",
  "Australian Capital Territory": "Canberra",
  "Northern Territory": "Darwin",
  "Tasmania": "Hobart",
  VIC: "Melbourne",
  NSW: "Sydney",
  QLD: "Brisbane",
  WA: "Perth",
  SA: "Adelaide",
  ACT: "Canberra",
  NT: "Darwin",
  TAS: "Hobart"
};

const localIsoDateFromDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getIsoDate = (offset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return localIsoDateFromDate(date);
};

const nextSaturdayIso = () => {
  const date = new Date();
  const day = date.getDay();
  const offset = day === 6 ? 0 : (6 - day + 7) % 7;
  date.setDate(date.getDate() + offset);
  return localIsoDateFromDate(date);
};

const formatDateLabel = (value) => {
  if (!value) return "Any date";
  return new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" }).format(new Date(`${value}T00:00:00`));
};

const formatTimeLabel = (value) => {
  if (!value) return "Any time";
  const [hour, minute] = value.split(":").map(Number);
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(2026, 0, 1, hour, minute));
};

const localDateFromIso = (value) => {
  const dateValue = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return null;
  const date = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const happensInUpcomingWindow = (record = {}, days = 30) => {
  const date = localDateFromIso(record.date);
  if (!date) return false;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return date >= start && date < end;
};

const sortByPlayTime = (first = {}, second = {}) => `${first.date || ""}T${first.time || ""}`.localeCompare(`${second.date || ""}T${second.time || ""}`);

const paymentStatusTone = (status = "") => {
  const value = String(status || "").toLowerCase();
  if (/free/.test(value)) return "free";
  if (/paid|confirmed|complete|settled/.test(value)) return "paid";
  if (/refund|cancel|failed|dispute|declined/.test(value)) return "attention";
  if (/pending|checkout|requested|unpaid|hold|open/.test(value)) return "pending";
  return "neutral";
};

const playerWeeklyPlan = (courtBookings = [], joinedGroups = []) => ({
  bookings: courtBookings.filter((booking) => happensInUpcomingWindow(booking)).sort(sortByPlayTime),
  groups: joinedGroups.filter((group) => happensInUpcomingWindow(group)).sort(sortByPlayTime)
});

const profileMediaReviewConfigs = [
  { role: "player", roleLabel: "Player", field: "mediaLinks", fallbackLabel: "Player media" },
  { role: "coach", roleLabel: "Coach", field: "mediaLinks", fallbackLabel: "Coach media" },
  { role: "group", roleLabel: "Group", field: "mediaLinks", fallbackLabel: "Group media" },
  { role: "centre", roleLabel: "Centre", field: "photoLinks", fallbackLabel: "Centre photo" }
];

const profileMediaReviewKey = ({ userId = "", ownerUid = "", role = "", field = "", mediaUrl = "" }) => `${userId || ownerUid}|${role}|${field}|${mediaUrl}`;

const latestProfileMediaReviewMap = (reviews = []) => {
  const rows = [...reviews].sort((a, b) => String(b.createdAt || b.updatedAt || "").localeCompare(String(a.createdAt || a.updatedAt || "")));
  const reviewMap = new globalThis.Map();
  rows.forEach((review) => {
    const key = profileMediaReviewKey(review);
    if (!reviewMap.has(key)) reviewMap.set(key, review);
  });
  return reviewMap;
};

const adminProfileMediaRowsFromPlatform = (users = [], reviews = []) => {
  const reviewMap = latestProfileMediaReviewMap(reviews);
  const usersById = new globalThis.Map(users.map((user) => [user.uid || user.id || user.ownerUid || "", user]));
  const linkRows = users.flatMap((user) => {
    const userId = user.uid || user.id || user.ownerUid || "";
    const ownerName = user.fullName || user.displayName || user.email || user.phoneNumber || userId || "Badminta user";
    return profileMediaReviewConfigs.flatMap((config) => {
      const roleProfile = user[config.role] || {};
      const roleCountryCode = normaliseCountryCode(
        roleProfile.location?.countryCode || roleProfile.countryCode || user.countryCode || user.location?.countryCode || "",
        roleProfile.city || user.city || user.location?.city || ""
      );
      const links = cleanLinkRows(roleProfile[config.field], config.fallbackLabel)
        .filter(publicMediaNeedsModeration);
      return links.map((link) => {
        const latestReview = reviewMap.get(profileMediaReviewKey({
          userId,
          role: config.role,
          field: config.field,
          mediaUrl: link.url
        }));
        const status = normaliseMediaModerationStatus(link.moderationStatus || latestReview?.status || latestReview?.decision);
        const embed = parseMediaEmbed(link.url);
        return {
          id: `profile-media-${userId}-${config.role}-${shortStableHash(link.url)}`,
          userId,
          ownerUid: userId,
          ownerName,
          role: config.role,
          roleLabel: config.roleLabel,
          field: config.field,
          label: link.label || config.fallbackLabel,
          mediaUrl: link.url,
          status,
          statusLabel: mediaModerationStatusLabels[status] || status,
          source: link.source || "user",
          submittedAt: link.submittedAt || user.updatedAt || "",
          reviewedAt: link.reviewedAt || latestReview?.reviewedAt || latestReview?.createdAt || "",
          reviewedBy: link.reviewedBy || latestReview?.reviewedByPhone || latestReview?.reviewedByUid || "",
          reviewNote: link.moderationNote || latestReview?.note || "",
          rightsStatus: normaliseMediaRightsStatus(link.rightsStatus || latestReview?.rightsStatus),
          rightsLabel: mediaRightsStatusLabels[normaliseMediaRightsStatus(link.rightsStatus || latestReview?.rightsStatus)],
          countryCode: roleCountryCode,
          country: getCountryName(roleCountryCode) || roleProfile.location?.country || user.location?.country || "Country pending",
          city: roleProfile.city || roleProfile.location?.city || user.city || user.location?.city || "",
          mediaType: isImageMediaUrl(link.url) ? "image" : embed?.type === "iframe" ? "embed" : "link",
          provider: embed?.provider || "",
          latestReviewId: latestReview?.id || "",
          clientSafety: latestReview?.clientSafety || null,
          subjectCollection: latestReview?.subjectCollection || "",
          subjectId: latestReview?.subjectId || ""
        };
      });
    });
  });
  const existingReviewIds = new Set(linkRows.map((row) => row.latestReviewId).filter(Boolean));
  const reviewRows = reviews
    .filter((review) => review.mediaUrl && !existingReviewIds.has(review.id))
    .map((review) => {
      const userId = review.ownerUid || review.userId || "";
      const user = usersById.get(userId) || {};
      const role = String(review.role || "player").toLowerCase() === "organiser" ? "group" : String(review.role || "player").toLowerCase();
      const roleProfile = user[role] || {};
      const roleLabel = role === "group" ? "Group" : role === "centre" ? "Centre" : role === "coach" ? "Coach" : role === "event" ? "Event" : "Player";
      const ownerName = user.fullName || user.displayName || user.email || user.phoneNumber || userId || "Badminta user";
      const status = normaliseMediaModerationStatus(review.status || review.moderationStatus || review.decision);
      const embed = parseMediaEmbed(review.mediaUrl);
      const countryCode = normaliseCountryCode(
        roleProfile.location?.countryCode || roleProfile.countryCode || user.countryCode || user.location?.countryCode || review.countryCode || "",
        roleProfile.city || user.city || user.location?.city || ""
      );
      return {
        id: `profile-media-review-${review.id}`,
        userId,
        ownerUid: userId,
        ownerName,
        role,
        roleLabel,
        field: review.field || "image",
        label: review.label || `${roleLabel} ${String(review.field || "image").replace(/url/ig, "").replace(/([A-Z])/g, " $1").toLowerCase()}`,
        mediaUrl: review.mediaUrl,
        status,
        statusLabel: mediaModerationStatusLabels[status] || status,
        source: review.source || "user",
        submittedAt: review.createdAt || review.updatedAt || "",
        reviewedAt: review.reviewedAt || "",
        reviewedBy: review.reviewedByPhone || review.reviewedByUid || "",
        reviewNote: review.note || review.moderationNote || "",
        rightsStatus: normaliseMediaRightsStatus(review.rightsStatus),
        rightsLabel: mediaRightsStatusLabels[normaliseMediaRightsStatus(review.rightsStatus)],
        countryCode,
        country: getCountryName(countryCode) || roleProfile.location?.country || user.location?.country || "Country pending",
        city: roleProfile.city || roleProfile.location?.city || user.city || user.location?.city || "",
        mediaType: review.mediaType || (isImageMediaUrl(review.mediaUrl) ? "image" : embed?.type === "iframe" ? "embed" : "link"),
        provider: embed?.provider || "",
        latestReviewId: review.id,
        clientSafety: review.clientSafety || null,
        subjectCollection: review.subjectCollection || "",
        subjectId: review.subjectId || ""
      };
    });
  return [...linkRows, ...reviewRows].sort((a, b) => {
    const statusWeight = { pending_review: 0, rejected: 1, approved: 2 };
    return (statusWeight[a.status] ?? 3) - (statusWeight[b.status] ?? 3)
      || String(b.submittedAt || b.reviewedAt || "").localeCompare(String(a.submittedAt || a.reviewedAt || ""));
  });
};

const mapBboxAround = (lat, lon, span = 0.09) => [
  lon - span,
  lat - span,
  lon + span,
  lat + span
];

const inferKnownCity = (address = {}, displayName = "") => {
  const direct = [address.city, address.town, address.municipality, address.state_district, address.county].find(Boolean);
  const knownDirect = direct ? cities.find((item) => item.toLowerCase() === String(direct).toLowerCase()) : null;
  if (knownDirect) return knownDirect;
  const displayMatch = cities.find((item) => String(displayName || "").toLowerCase().includes(item.toLowerCase()));
  if (displayMatch) return displayMatch;
  if (direct) return String(direct).trim();
  return australianStateCity[address.state] || "";
};

const normaliseOsmPlace = (place) => {
  const address = place.address || {};
  const city = inferKnownCity(address, place.display_name || "");
  const rawLabel = address.suburb || address.neighbourhood || address.quarter || address.city_district || address.city || address.town || address.village || address.municipality || (place.display_name || "").split(",")[0] || city;
  const label = rawLabel.replace(/\s+$/, "");
  const lat = Number(place.lat);
  const lon = Number(place.lon);
  const bbox = Array.isArray(place.boundingbox)
    ? [Number(place.boundingbox[2]), Number(place.boundingbox[0]), Number(place.boundingbox[3]), Number(place.boundingbox[1])]
    : mapBboxAround(lat, lon);
  const countryCode = normaliseCountryCode(address.country_code || address.country, city);
  const country = getCountryName(countryCode || address.country, city);
  const isCountry = String(place.type || "").toLowerCase() === "country"
    || (!address.suburb && !address.city && !address.town && !address.village && country && compactLocationText(label) === compactLocationText(country));
  const labelMatchesAddressCity = [address.city, address.town, address.village, address.municipality]
    .filter(Boolean)
    .some((item) => compactLocationText(item) === compactLocationText(label));
  const type = isCountry
    ? "country"
    : !address.suburb && !address.neighbourhood && !address.quarter && !address.city_district && labelMatchesAddressCity
      ? "city"
      : "suburb";
  return {
    id: `osm-${place.place_id}`,
    label,
    city: city || (type === "city" ? label : ""),
    suburb: type === "suburb" ? label : "",
    state: address.state || "",
    postcode: address.postcode || "",
    country,
    countryCode,
    displayName: place.display_name || label,
    type,
    hint: type === "country" ? "Country" : type === "city" ? ["City", country].filter(Boolean).join(" · ") : ["Suburb", city, country].filter(Boolean).join(" · "),
    lat,
    lon,
    bbox,
    source: "Map search"
  };
};

const searchOpenStreetLocations = async (query) => {
  const params = new URLSearchParams({
    q: query,
    format: "jsonv2",
    addressdetails: "1",
    "accept-language": "en",
    limit: "8"
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error("Location search is unavailable right now.");
  const results = await response.json();
  return results
    .filter((place) => {
      const category = String(place.category || place.class || "").toLowerCase();
      const type = String(place.type || "").toLowerCase();
      const address = place.address || {};
      return ["place", "boundary"].includes(category)
        || ["administrative", "city", "town", "village", "municipality", "suburb", "neighbourhood", "quarter"].includes(type)
        || Boolean(address.city || address.town || address.village || address.municipality || address.suburb);
    })
    .map(normaliseOsmPlace)
    .filter((place) => place.type !== "country")
    .filter((place) => Number.isFinite(place.lat) && Number.isFinite(place.lon));
};

const reverseOpenStreetLocation = async (lat, lon) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error("Device location coordinates are unavailable.");
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    format: "jsonv2",
    addressdetails: "1",
    zoom: "16",
    layer: "address"
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error("Your suburb could not be resolved from this device.");
  const place = await response.json();
  const location = normaliseOsmPlace({ ...place, lat: String(lat), lon: String(lon) });
  if (!location.label && !location.city) throw new Error("Your suburb could not be resolved from this device.");
  return locationSnapshot({
    ...location,
    id: `device-${lat.toFixed(5)}-${lon.toFixed(5)}`,
    bbox: location.bbox || mapBboxAround(lat, lon, 0.08),
    source: "Device location"
  });
};

const resolveCurrentProfileLocation = async () => {
  const position = await getCurrentPosition();
  const lat = Number(position.coords.latitude);
  const lon = Number(position.coords.longitude);
  try {
    return await reverseOpenStreetLocation(lat, lon);
  } catch {
    const nearest = inferNearestKnownCitySeedFromPoint({ lat, lon }, "");
    if (!nearest?.city) throw new Error("Your location was found, but the suburb could not be identified. Search it manually instead.");
    const countryCode = normaliseCountryCode(nearest.country, nearest.city);
    return locationSnapshot({
      id: `device-city-${slugifyPath(nearest.city)}`,
      label: nearest.city,
      city: nearest.city,
      countryCode,
      country: getCountryName(countryCode, nearest.city),
      type: "city",
      lat,
      lon,
      bbox: mapBboxAround(lat, lon, 0.16),
      source: "Device location"
    });
  }
};

const locationSnapshot = (location = null) => {
  if (!location) return null;
  const lat = Number(location.lat);
  const lon = Number(location.lon);
  const label = location.label || location.suburb || location.city || "";
  const inferredCountry = location.country && compactLocationText(label) === compactLocationText(location.country);
  const type = location.type || (inferredCountry ? "country" : location.suburb ? "suburb" : location.city ? "city" : cities.includes(label) ? "city" : "suburb");
  const city = location.city || (type === "city" ? label : "");
  const countryCode = normaliseCountryCode(location.countryCode || location.country, city);
  const fallbackId = [label, city].filter(Boolean).join("-").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return {
    id: location.id || `location-${fallbackId || "global"}`,
    label,
    suburb: type === "suburb" ? location.suburb || label : "",
    city,
    state: location.state || "",
    postcode: location.postcode || "",
    country: location.country || getCountryName(countryCode, city),
    countryCode,
    type,
    lat: Number.isFinite(lat) ? lat : null,
    lon: Number.isFinite(lon) ? lon : null,
    bbox: Array.isArray(location.bbox) ? location.bbox : null,
    source: location.source || "Map search",
    displayName: location.displayName || location.hint || [label, city, location.state].filter(Boolean).join(", ")
  };
};

const locationAreaLabel = (location = null, fallback = "") => location?.suburb || location?.label || location?.city || fallback;

const signupRoleLocationPatch = (role, location = null, accountProfile = {}) => {
  const savedLocation = locationSnapshot(location);
  if (!savedLocation) return {};
  const area = savedLocation.suburb || savedLocation.label || savedLocation.city || "";
  const locationFields = {
    suburb: savedLocation.suburb || (savedLocation.type === "suburb" ? area : ""),
    city: savedLocation.city || area,
    country: savedLocation.country || "",
    countryCode: savedLocation.countryCode || "",
    location: savedLocation
  };
  if (role === "player") return { player: { ...(accountProfile.player || {}), ...locationFields } };
  if (role === "coach") return { coach: { ...(accountProfile.coach || {}), ...locationFields } };
  if (role === "organiser") return { group: { ...(accountProfile.group || {}), ...locationFields } };
  if (role === "centre") return { centre: { ...(accountProfile.centre || {}), ...locationFields } };
  return {};
};

const signupRolesLocationPatch = (roles = [], location = null, accountProfile = {}) => roles.reduce((patch, role) => ({
  ...patch,
  ...signupRoleLocationPatch(role, location, { ...accountProfile, ...patch })
}), {});

const locationFromVenue = (venue = null) => {
  if (!venue) return null;
  const label = venue.suburb || venue.city || venue.name || "";
  return locationSnapshot({
    id: venue.id ? `venue-location-${venue.id}` : "",
    label,
    suburb: venue.suburb || "",
    city: venue.city || "",
    state: venue.state || "",
    postcode: venue.postcode || "",
    country: venue.country || getCountryName(venue.countryCode || venue.country, venue.city),
    countryCode: normaliseCountryCode(venue.countryCode || venue.country, venue.city),
    type: venue.suburb ? "suburb" : "city",
    lat: venue.lat,
    lon: venue.lon,
    source: "Centre listing",
    displayName: [venue.suburb, venue.city, venue.state, venue.postcode].filter(Boolean).join(", ") || label
  });
};

const distanceKm = (from, to) => {
  if (!from?.lat || !from?.lon || !to?.lat || !to?.lon) return null;
  const radius = 6371;
  const toRad = (value) => (value * Math.PI) / 180;
  const latDelta = toRad(to.lat - from.lat);
  const lonDelta = toRad(to.lon - from.lon);
  const a = Math.sin(latDelta / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(lonDelta / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const inferNearestKnownCityFromPoint = (point, fallbackCity = "Melbourne") => {
  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) return fallbackCity;
  return globalCitySeeds
    .map((seed) => ({
      city: seed.city,
      distance: distanceKm(point, { lat: seed.lat, lon: seed.lon })
    }))
    .filter((item) => item.distance !== null)
    .sort((a, b) => a.distance - b.distance)[0]?.city || fallbackCity;
};

const inferNearestKnownCitySeedFromPoint = (point, fallbackCity = "Melbourne") => {
  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) {
    return globalCitySeeds.find((seed) => compactLocationText(seed.city) === compactLocationText(fallbackCity)) || null;
  }
  return globalCitySeeds
    .map((seed) => ({
      ...seed,
      distance: distanceKm(point, { lat: seed.lat, lon: seed.lon })
    }))
    .filter((item) => item.distance !== null)
    .sort((a, b) => a.distance - b.distance)[0] || null;
};

const formatDistance = (distance) => `${distance.toFixed(distance < 10 ? 1 : 0)} km`;

const inferVenueType = (tags = {}) => {
  if (tags.club) return "Club";
  if (tags.leisure === "sports_centre") return "Sports centre";
  if (tags.leisure === "pitch") return "Court";
  if (tags.amenity === "community_centre") return "Community venue";
  return "Centre";
};

const getOsmChargeLabel = (tags = {}) => {
  const charge = tags.charge || tags.fee || tags["payment:fee"];
  if (!charge) return null;
  if (charge === "yes") return "Fee listed";
  if (charge === "no") return "Free entry listed";
  return `Fee: ${charge}`;
};

const getOsmWebsite = (tags = {}) => tags.website || tags["contact:website"] || null;

const getOsmPhone = (tags = {}) => tags.phone || tags["contact:phone"] || null;

const getOsmAddress = (tags = {}, fallbackSuburb, fallbackCity) => {
  const street = [tags["addr:housenumber"], tags["addr:street"]].filter(Boolean).join(" ");
  return [street, tags["addr:suburb"] || fallbackSuburb, tags["addr:city"] || fallbackCity].filter(Boolean).join(", ");
};

const getOsmAmenities = (tags = {}) => {
  const amenities = [];
  if (tags.sport === "badminton") amenities.push("Badminton courts listed");
  if (tags.indoor) amenities.push(`Indoor: ${tags.indoor}`);
  if (tags.wheelchair) amenities.push(`Wheelchair: ${tags.wheelchair}`);
  if (tags.lit) amenities.push(`Lighting: ${tags.lit}`);
  if (tags.surface) amenities.push(`Surface: ${tags.surface}`);
  if (tags.opening_hours) amenities.push(`Hours listed`);
  if (getOsmWebsite(tags)) amenities.push("Website listed");
  if (getOsmPhone(tags)) amenities.push("Phone listed");
  return amenities.length ? amenities : ["Amenities unknown"];
};

const osmElementToVenue = (element, index, origin, location) => {
  const tags = element.tags || {};
  const lat = element.lat ?? element.center?.lat;
  const lon = element.lon ?? element.center?.lon;
  const distance = distanceKm(origin, { lat, lon });
  const suburb = tags["addr:suburb"] || tags["addr:city"] || location.suburb || location.label;
  const city = tags["addr:city"] || location.city || inferKnownCity({}, tags.name || "");
  const countryCode = normaliseCountryCode(tags["addr:country"] || location.countryCode || location.country, city);
  const country = getCountryName(countryCode || tags["addr:country"] || location.country, city);
  const courts = Number(tags.courts || tags["sport:badminton:courts"] || tags["capacity:courts"] || 0);
  const website = getOsmWebsite(tags);
  const phone = getOsmPhone(tags);
  return {
    id: `osm-${element.type}-${element.id}`,
    name: tags.name || tags.operator || `${suburb} badminton courts`,
    city,
    country,
    countryCode,
    suburb,
    price: null,
    priceLabel: getOsmChargeLabel(tags),
    courts: Number.isFinite(courts) && courts > 0 ? courts : null,
    rating: null,
    reviews: null,
    distance: distance ? `${distance.toFixed(distance < 10 ? 1 : 0)} km` : "Mapped",
    badge: "Mapped centre",
    availability: tags.opening_hours ? `Hours: ${tags.opening_hours}` : "Hours unknown",
    type: inferVenueType(tags),
    image: null,
    gallery: [],
    amenities: getOsmAmenities(tags),
    openingHours: tags.opening_hours || null,
    website: getAllowedUrl(website),
    phone,
    address: getOsmAddress(tags, suburb, city),
    lat,
    lon,
    osmUrl: `https://www.openstreetmap.org/${element.type}/${element.id}`,
    isOsm: true
  };
};

const nominatimPlaceToVenue = (place, index, origin, location) => {
  const address = place.address || {};
  const lat = Number(place.lat);
  const lon = Number(place.lon);
  const distance = distanceKm(origin, { lat, lon });
  const city = inferKnownCity(address, place.display_name || location.city);
  const suburb = address.suburb || address.city || address.town || location.suburb || location.label;
  const countryCode = normaliseCountryCode(address.country_code || address.country || location.countryCode || location.country, city);
  const country = getCountryName(countryCode || address.country || location.country, city);
  const name = place.name || place.namedetails?.name || (place.display_name || "").split(",")[0] || `${suburb} badminton`;
  const extratags = place.extratags || {};
  const website = getOsmWebsite(extratags);
  const phone = getOsmPhone(extratags);
  return {
    id: `osm-place-${place.place_id}`,
    name,
    city,
    country,
    countryCode,
    suburb,
    price: null,
    priceLabel: getOsmChargeLabel(extratags),
    courts: null,
    rating: null,
    reviews: null,
    distance: distance ? `${distance.toFixed(distance < 10 ? 1 : 0)} km` : "Mapped",
    badge: "Mapped centre",
    availability: extratags.opening_hours ? `Hours: ${extratags.opening_hours}` : "Hours unknown",
    type: place.type === "sports_centre" ? "Sports centre" : "Centre",
    image: null,
    gallery: [],
    amenities: getOsmAmenities(extratags),
    openingHours: extratags.opening_hours || null,
    website: getAllowedUrl(website),
    phone,
    address: place.display_name || getOsmAddress(extratags, suburb, city),
    lat,
    lon,
    osmUrl: `https://www.openstreetmap.org/search?query=${encodeURIComponent(place.display_name || name)}`,
    isOsm: true
  };
};

const searchNominatimBadmintonCentres = async (location) => {
  const query = ["badminton", location.label, location.city, location.country].filter(Boolean).join(" ");
  const params = new URLSearchParams({
    q: query,
    format: "jsonv2",
    addressdetails: "1",
    extratags: "1",
    namedetails: "1",
    limit: "12"
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error("Centre search is unavailable right now.");
  const results = await response.json();
  return results
    .map((place, index) => nominatimPlaceToVenue(place, index, location, location))
    .filter((venue) => Number.isFinite(venue.lat) && Number.isFinite(venue.lon));
};

const fetchBadmintonCentresFromOsm = async (location, radius = 35000) => {
  if (!location?.lat || !location?.lon) return [];
  const query = `
[out:json][timeout:25];
(
  node["sport"="badminton"](around:${radius},${location.lat},${location.lon});
  way["sport"="badminton"](around:${radius},${location.lat},${location.lon});
  relation["sport"="badminton"](around:${radius},${location.lat},${location.lon});
  node["name"~"badminton",i](around:${radius},${location.lat},${location.lon});
  way["name"~"badminton",i](around:${radius},${location.lat},${location.lon});
  relation["name"~"badminton",i](around:${radius},${location.lat},${location.lon});
);
out center tags 80;
`;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 6500);
  try {
    const response = await fetch("https://overpass-api.de/api/interpreter", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
      body: new URLSearchParams({ data: query }),
      signal: controller.signal
    });
    if (!response.ok) throw new Error("Centre search is unavailable right now.");
    const payload = await response.json();
    return (payload.elements || [])
      .filter((element) => element.tags?.name || element.tags?.sport === "badminton")
      .map((element, index) => osmElementToVenue(element, index, location, location))
      .filter((venue) => Number.isFinite(venue.lat) && Number.isFinite(venue.lon));
  } catch (error) {
    return searchNominatimBadmintonCentres(location);
  } finally {
    window.clearTimeout(timeout);
  }
};

const getCurrentPosition = () => new Promise((resolve, reject) => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    reject(new Error("Browser location is not available."));
    return;
  }
  navigator.geolocation.getCurrentPosition(resolve, reject, {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000
  });
});

const unknownValue = "Unknown";

const hasOsmField = (value) => value !== undefined && value !== null && value !== "";

const getVenueSourceLabel = (venue) => venue.badge || (venue.listingType === "club" ? "Club listing" : "Centre listing");

const hasVenueCourtCount = (venue) => hasOsmField(venue.courts);
const getConfirmedPriceLabel = (venue) => {
  const venueCurrency = normaliseCurrencyCode(venue.currency || marketplaceCurrencyForCountry(venue.countryCode || venue.country));
  const scheduleLabel = getCourtPricingSummary(venue.courtPricing, venue.pricing || venue.priceLabel || "", venueCurrency);
  if (scheduleLabel) return scheduleLabel;
  if (typeof venue.price === "number") return formatMoney(venue.price, venueCurrency);
  const label = String(venue.priceLabel || "").trim();
  const dollarMatches = label.match(/\$\s*\d+(?:\.\d{1,2})?(?:\s*(?:-|to)\s*\$?\s*\d+(?:\.\d{1,2})?)?/gi);
  if (dollarMatches?.length) return dollarMatches.slice(0, 2).join(" / ").replace(/\s+/g, " ");
  if (/^\d+(?:\.\d{1,2})?$/.test(label)) return formatMoney(Number(label), venueCurrency);
  return null;
};
const hasVenuePrice = (venue) => Boolean(getConfirmedPriceLabel(venue));
const getVenuePriceNumber = (venue = {}) => {
  const schedulePrice = getCourtPricingPrimaryNumber(venue.courtPricing, venue.pricing || venue.priceLabel || "");
  if (schedulePrice > 0) return schedulePrice;
  if (typeof venue.price === "number" && Number.isFinite(venue.price) && venue.price > 0) return venue.price;
  const label = getConfirmedPriceLabel(venue) || String(venue.priceLabel || "");
  const match = label.match(/\d+(?:\.\d{1,2})?/);
  const parsed = match ? Number.parseFloat(match[0]) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};
const hasBookableCourtPrice = (venue = {}) => getVenuePriceNumber(venue) > 0;
const hasVenueHours = (venue) => Boolean(venue.openingHours);
const hasVenueRating = (venue) => typeof venue.rating === "number" && venue.rating > 0;
const hasVenueContact = (venue) => Boolean(venue.phone || venue.website || venue.email);
const hasVenueDistance = (venue) => Boolean(venue.distance);

const getVenueCourtLabel = (venue) => {
  return hasVenueCourtCount(venue) ? `${venue.courts} court${venue.courts === 1 ? "" : "s"}` : "Court count unknown";
};

const getVenuePriceLabel = (venue) => {
  return getConfirmedPriceLabel(venue) || "Pricing not listed";
};

const getVenueHoursLabel = (venue) => {
  return hasVenueHours(venue) ? `Hours: ${venue.openingHours}` : "Hours unknown";
};

const getVenueRatingLabel = (venue) => hasVenueRating(venue) ? `${venue.rating.toFixed(1)} rating` : "Rating not available";

const getVenueContactLabel = (venue) => {
  if (venue.phone) return `Phone: ${venue.phone}`;
  if (venue.website) return "Website listed";
  if (venue.email) return "Email listed";
  return "Contact unknown";
};

const compactAddressSegments = (value) => {
  const seen = new Set();
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = compactLocationText(part);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(", ");
};

const getVenueAddressLabel = (venue) => compactAddressSegments(venue.address)
  || [venue.suburb, venue.city].filter(Boolean).filter((part, index, parts) => parts.findIndex((item) => compactLocationText(item) === compactLocationText(part)) === index).join(", ")
  || unknownValue;
const getVenueAreaLabel = (venue) => {
  const suburb = normaliseLocationText(venue.suburb);
  const city = normaliseLocationText(venue.city);
  const state = normaliseLocationText(venue.state);
  if (suburb && city && suburb.toLowerCase() !== city.toLowerCase()) return `${suburb}, ${city}`;
  return city || suburb || state || "Location to be confirmed";
};

const getVenueTopline = (venue) => {
  const area = getVenueAreaLabel(venue);
  const location = area === "Location to be confirmed" ? "" : ` in ${area}`;
  const typeText = String(venue.listingType || venue.type || venue.kind || "").toLowerCase();
  const label = typeText.includes("club") ? "Badminton club" : "Badminton centre";
  return `${label}${location}`;
};

const isBlockedPublicUrl = (url) => isBlockedPublicVenueUrl(url);

const getAllowedUrl = (url) => (url && !isBlockedPublicUrl(url) ? url : null);

const filterAllowedVenueLinks = (links = []) => sanitisePublicVenueLinks(links);

const getVenueExternalUrl = (venue) => {
  return getAllowedUrl(venue.website);
};

const getVenueDistanceLabel = (venue) => hasVenueDistance(venue) ? `${venue.distance} away` : "Distance unknown";

const sourceNames = {
  metrobadminton: "Public venue data",
  keepactive: "Public activity data",
  activeactivities: "Public activity data",
  badmintonaustralia: "National association data",
  badmintonvictoria: "State association data",
  badmintonnsw: "State association data",
  badmintonact: "State association data",
  badmintonnt: "State association data",
  openstreetmap: "Map data",
  globalopenstreetmap: "Global map data"
};

const getSourceName = (source) => sourceNames[source] || String(source || "Public listing");

const getVenueOverview = (venue) => {
  const safeDescription = sanitisePublicVenueDescription(venue);
  if (safeDescription) return safeDescription;
  const address = getVenueAddressLabel(venue);
  return `${venue.name} is listed as a ${venue.type || "badminton venue"}${address && address !== unknownValue ? ` at ${address}` : ""}. The centre owner can claim this listing to add confirmed courts, hours, pricing and booking options.`;
};

const getVenueHoursRows = (venue) => {
  const rows = Array.isArray(venue.openingHoursRows) ? venue.openingHoursRows : [];
  if (!rows.length && venue.openingHours) return [{ label: "Hours", value: venue.openingHours }];
  return rows.map((row) => {
    if (row.opening_hours) return { label: "Opening hours", value: row.opening_hours };
    const day = Array.isArray(row.dayOfWeek) ? row.dayOfWeek.join(", ") : row.dayOfWeek;
    if (day && row.opens && row.closes) return { label: day, value: `${row.opens} - ${row.closes}` };
    return null;
  }).filter(Boolean);
};

const getVenueDetailItems = (venue) => {
  const items = sanitisePublicVenueDetailItems(venue.detailItems);
  const seen = new Set();
  const baseItems = [
    ["Address", getVenueAddressLabel(venue), "Venue location"],
    ["Contact", getVenueContactLabel(venue), "Public contact"],
    ["Pricing", getVenuePriceLabel(venue), "Public pricing"],
    ["Court count", getVenueCourtLabel(venue), "Court information"]
  ];
  const mapped = baseItems
    .filter(([, value]) => value && !String(value).toLowerCase().includes("unknown") && value !== "Not listed" && value !== "Pricing not listed")
    .map(([label, value, source]) => ({ label, value, source }));
  for (const item of items) {
    if (!item?.label || !item?.value) continue;
    const key = `${item.label}-${item.value}`.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    mapped.push({ label: item.label, value: item.value, source: getSourceName(item.source) });
  }
  return mapped.slice(0, 20);
};

const explicitSocialSessionPattern = /\b(social\s+(badminton|play|session|sessions|games?|club)|social games?|club night|round robin|come and try|casual\s+(badminton|session|sessions|play|booking|games?)|visitor playing fees?|session fees?|weekly [^.,;]*badminton social)\b/i;
const explicitSocialLinkPattern = /\b(register for social|social badminton|social session|social games?|casual booking|members booking|club night|round robin|attend|session)\b/i;
const genericBookingLinkPattern = /\b(book online|online booking|booking|join now|join a club|membership|memberships|member benefits|court hire|free trial|gym|short course|manage my membership)\b/i;
const ignoredSessionLinkPattern = /\b(google\.com\/calendar|calendar\/event|action=template)\b/i;
const socialSupportLabels = new Set(["when", "where", "venue", "time", "times", "fees", "fee", "cost", "price", "program", "session", "sessions"]);

const normaliseSearchText = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const normaliseDetailLabel = (value) => String(value || "").trim().toLowerCase();

const getSocialDetailTitle = (label, value) => {
  const normalisedLabel = normaliseDetailLabel(label);
  const text = String(value || "").toLowerCase();
  if (["when", "time", "times", "session", "sessions"].includes(normalisedLabel)) return text.includes("social") ? "Social session time" : "Session time";
  if (["where", "venue"].includes(normalisedLabel)) return "Session venue";
  if (["fees", "fee", "cost", "price"].includes(normalisedLabel)) return "Cost or membership fee";
  if (normalisedLabel.includes("membership")) return "Membership note";
  if (normalisedLabel.includes("training") || text.includes("training")) return "Training program";
  if (normalisedLabel.includes("coaching") || text.includes("coaching")) return "Coaching program";
  if (normalisedLabel.includes("program")) return text.includes("social") ? "Social program" : "Club program";
  return "Public group detail";
};

const isExplicitSocialSessionDetail = (item) => {
  if (!item?.label || !item?.value) return false;
  const text = `${item.label} ${item.value}`;
  return explicitSocialSessionPattern.test(text);
};

const isSupportingSocialSessionDetail = (item, hasSessionContext) => {
  if (!hasSessionContext || !item?.label || !item?.value) return false;
  const label = normaliseDetailLabel(item.label);
  const value = String(item.value || "");
  if (["where", "venue"].includes(label)) return true;
  if (["fees", "fee", "cost", "price"].includes(label)) return /\b(session|casual|visitor|member|social|shuttle)\b/i.test(value);
  if (label === "program") return explicitSocialSessionPattern.test(value);
  return false;
};

const isExplicitSocialSessionLink = (link, hasSessionContext) => {
  if (!link?.url) return false;
  const label = String(link.label || "");
  const url = String(link.url || "");
  const text = `${label} ${url}`;
  if (ignoredSessionLinkPattern.test(text)) return false;
  if (explicitSocialLinkPattern.test(text)) {
    return !genericBookingLinkPattern.test(label) || /\b(social|casual|members booking|attend|register)\b/i.test(label);
  }
  if (!hasSessionContext) return false;
  if (/\b(register|registration)\b/i.test(label)) return true;
  return /\bevents?\/(?:register\/)?\d+\b/i.test(url) && /\b(attend|casual booking|members booking|register|social)\b/i.test(label);
};

const getVenueRealSocialListings = (venue) => {
  const detailItems = sanitisePublicVenueDetailItems(venue.detailItems);
  const bookingLinks = filterAllowedVenueLinks(venue.bookingLinks);
  const hasSessionContext = detailItems.some((item) => isExplicitSocialSessionDetail(item));
  const listings = [];
  const seen = new Set();
  const pushListing = (item) => {
    const key = normaliseSearchText(`${item.title}-${item.detail}-${item.href || ""}`);
    if (!key || seen.has(key)) return;
    seen.add(key);
    listings.push(item);
  };

  detailItems.forEach((item) => {
    if (!item?.label || !item?.value) return;
    const isDirectSocialDetail = isExplicitSocialSessionDetail(item);
    const isSupportingSocialDetail = isSupportingSocialSessionDetail(item, hasSessionContext) && socialSupportLabels.has(normaliseDetailLabel(item.label));
    if (!isDirectSocialDetail && !isSupportingSocialDetail) return;
    pushListing({
      id: `detail-${listings.length}`,
      title: getSocialDetailTitle(item.label, item.value),
      meta: item.label,
      detail: item.value,
      source: getSourceName(item.source)
    });
  });

  bookingLinks
    .filter((link) => isExplicitSocialSessionLink(link, hasSessionContext))
    .forEach((link) => {
      pushListing({
        id: `booking-${link.url}`,
        title: link.label || "Session action link",
        meta: "Session link",
        detail: "Public link found for this centre's social session or club event.",
        source: "Public listing",
        href: link.url
      });
    });

  return listings.slice(0, 10);
};

const venueMergeCompletenessScore = (venue = {}) => [
  venue.name,
  venue.address,
  venue.suburb,
  venue.city,
  venue.state,
  venue.postcode,
  venue.country || venue.countryCode,
  venue.phone,
  venue.email,
  venue.website,
  venue.openingHours,
  venue.price || venue.priceLabel || venue.pricing,
  venue.courts || venue.courtCount,
  Number.isFinite(venue.lat) && Number.isFinite(venue.lon),
  Array.isArray(venue.gallery) && venue.gallery.length,
  Array.isArray(venue.images) && venue.images.length,
  Array.isArray(venue.detailItems) && venue.detailItems.length,
  venue.qualityScore
].filter(Boolean).length;

const venueMergePriorityScore = (venue = {}) => {
  const ownership = venue.ownerCreated || venue.ownerUid ? 30000 : 0;
  const verification = venue.verified ? 20000 : 0;
  const quality = Math.max(0, Number(venue.qualityScore || 0)) * 100;
  return ownership + verification + quality + venueMergeCompletenessScore(venue);
};

const normaliseVenueWebsiteKey = (value = "") => compactLocationText(
  String(value || "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/g, "")
);

const getVenueDedupeKeys = (venue = {}) => {
  if (!venue?.id) return [];
  const keys = [`id:${venue.id}`];
  const nameKey = compactLocationText(venue.name);
  if (!nameKey) return keys;
  const addressKey = compactLocationText(venue.address);
  const countryKey = compactLocationText(venue.countryCode || venue.country);
  const postcodeKey = compactLocationText(venue.postcode);
  const phoneKey = compactLocationText(venue.phone);
  const websiteKey = normaliseVenueWebsiteKey(venue.website);
  const relatedNameKey = getVenueMatchName(venue);
  const point = getVenueExactPoint(venue);
  if (addressKey && addressKey.length >= 8) keys.push(`name-address:${nameKey}|${addressKey}`);
  if (countryKey && postcodeKey.length >= 3) {
    keys.push(`name-postcode:${nameKey}|${countryKey}|${postcodeKey}`);
    if (relatedNameKey.length >= 4) keys.push(`related-postcode:${relatedNameKey}|${countryKey}|${postcodeKey}`);
  }
  if (phoneKey && phoneKey.length >= 6) keys.push(`name-phone:${nameKey}|${phoneKey}`);
  if (websiteKey && websiteKey.length >= 6) keys.push(`name-website:${nameKey}|${websiteKey}`);
  if (point) keys.push(`name-point:${nameKey}|${point.lat.toFixed(4)}|${point.lon.toFixed(4)}`);
  return keys;
};

const mergeVenueAliases = (winner = {}, duplicate = {}) => {
  const aliases = new Set([
    ...(Array.isArray(winner.aliases) ? winner.aliases : []),
    duplicate.slug,
    ...(Array.isArray(duplicate.aliases) ? duplicate.aliases : [])
  ].map((value) => slugifyPath(value)).filter(Boolean));
  aliases.delete(slugifyPath(winner.slug || winner.name));
  return { ...winner, aliases: [...aliases] };
};

const uniqueVenueList = (items = []) => {
  const unique = [];
  const keyToIndex = new globalThis.Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item?.id) return;
    const keys = getVenueDedupeKeys(item);
    const existingIndex = keys.map((key) => keyToIndex.get(key)).find((index) => index !== undefined);
    if (existingIndex === undefined) {
      const nextIndex = unique.length;
      unique.push(item);
      keys.forEach((key) => keyToIndex.set(key, nextIndex));
      return;
    }
    const existing = unique[existingIndex];
    unique[existingIndex] = venueMergePriorityScore(item) > venueMergePriorityScore(existing)
      ? mergeVenueAliases(item, existing)
      : mergeVenueAliases(existing, item);
    keys.forEach((key) => keyToIndex.set(key, existingIndex));
  });
  return unique;
};

const isSameVenueEntity = (venue, candidate) => {
  if (!venue || !candidate) return false;
  if (venue.id && candidate.id && venue.id === candidate.id) return true;

  const venueName = compactLocationText(venue.name);
  const candidateName = compactLocationText(candidate.name);
  const sameName = Boolean(venueName && candidateName && venueName === candidateName);
  const similarName = Boolean(
    venueName.length >= 8
    && candidateName.length >= 8
    && (venueName.includes(candidateName) || candidateName.includes(venueName))
  );
  const sameAddress = Boolean(
    venue.address
    && candidate.address
    && compactLocationText(venue.address) === compactLocationText(candidate.address)
  );
  const samePhone = Boolean(
    venue.phone
    && candidate.phone
    && compactLocationText(venue.phone) === compactLocationText(candidate.phone)
  );
  const sameWebsite = Boolean(
    venue.website
    && candidate.website
    && compactLocationText(String(venue.website).replace(/^https?:\/\//i, "").replace(/^www\./i, "")) === compactLocationText(String(candidate.website).replace(/^https?:\/\//i, "").replace(/^www\./i, ""))
  );
  const venuePoint = getVenueExactPoint(venue);
  const candidatePoint = getVenueExactPoint(candidate);
  const samePoint = Boolean(venuePoint && candidatePoint && (distanceKm(venuePoint, candidatePoint) ?? Infinity) <= 0.08);

  return Boolean(
    ((sameName || similarName) && (sameAddress || samePoint || samePhone || sameWebsite))
    || (isRelatedVenueListing(venue, candidate) && (sameAddress || samePoint || samePhone || sameWebsite))
  );
};

const getNearbyCentres = (venue, allVenues, limit = 6) => {
  const origin = getVenueExactPoint(venue);
  const sameArea = (candidate) => {
    const candidatePoint = getVenueExactPoint(candidate);
    const exactDistance = origin && candidatePoint ? distanceKm(origin, candidatePoint) : null;
    return (
      (exactDistance !== null && exactDistance <= 80)
      || (candidate.city && candidate.city === venue.city)
      || (candidate.state && candidate.state === venue.state)
      || (candidate.suburb && candidate.suburb === venue.suburb)
    );
  };
  return uniqueVenueList(allVenues)
    .filter((candidate) => !candidate.isNewCentre && !isSameVenueEntity(venue, candidate) && sameArea(candidate))
    .map((candidate) => {
      const point = getVenueExactPoint(candidate);
      const distance = origin && point ? distanceKm(origin, point) : null;
      const areaScore = candidate.suburb === venue.suburb ? 0 : candidate.city === venue.city ? 1 : candidate.state === venue.state ? 2 : 3;
      const typeScore = candidate.listingType === "venue" ? 0 : 1;
      return {
        ...candidate,
        distanceFromSelected: distance,
        distanceFromSelectedLabel: distance !== null ? `${formatDistance(distance)} from this centre` : `${candidate.suburb || candidate.city || candidate.state || "Nearby"} listing`,
        nearbySort: [distance ?? Infinity, areaScore, typeScore, getVenueLocationQuality(candidate), -(candidate.qualityScore || 0), candidate.name]
      };
    })
    .sort((a, b) => {
      for (let index = 0; index < a.nearbySort.length; index += 1) {
        const av = a.nearbySort[index];
        const bv = b.nearbySort[index];
        if (typeof av === "number" && typeof bv === "number" && av !== bv) return av - bv;
        if (String(av) !== String(bv)) return String(av).localeCompare(String(bv));
      }
      return 0;
    })
    .slice(0, limit);
};

const getVenueExactPoint = (venue) => {
  if (venue.lat === null || venue.lat === undefined || venue.lon === null || venue.lon === undefined || venue.lat === "" || venue.lon === "") {
    return null;
  }
  const lat = Number(venue.lat);
  const lon = Number(venue.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  if (lat === 0 && lon === 0) return null;
  return { lat, lon };
};

const normaliseLocationText = (value) => String(value || "").trim().replace(/\s+/g, " ");
const compactLocationText = (value) => normaliseLocationText(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const getVenueLocationQuality = (venue) => {
  const address = normaliseLocationText(venue.address);
  const suburb = normaliseLocationText(venue.suburb);
  const city = normaliseLocationText(venue.city);
  const state = normaliseLocationText(venue.state);
  const postcode = normaliseLocationText(venue.postcode);
  const genericValues = [
    suburb,
    city,
    state,
    postcode,
    [suburb, city].filter(Boolean).join(" "),
    [city, state].filter(Boolean).join(" "),
    [city, state, postcode].filter(Boolean).join(" "),
    [suburb, city, state, postcode].filter(Boolean).join(" ")
  ].filter(Boolean).map(compactLocationText);
  const genericAddress = !address || genericValues.includes(compactLocationText(address));

  if (address && !genericAddress) return 0;
  if ((suburb && city && suburb.toLowerCase() !== city.toLowerCase()) || getVenueExactPoint(venue)) return 1;
  if (suburb || city || state) return 2;
  return 3;
};

const compareVenueResults = (a, b, origin = null) => {
  const hasOrigin = Number.isFinite(origin?.lat) && Number.isFinite(origin?.lon);
  const aPoint = getVenueExactPoint(a);
  const bPoint = getVenueExactPoint(b);
  const aDistance = hasOrigin && aPoint ? distanceKm(origin, aPoint) ?? Infinity : Infinity;
  const bDistance = hasOrigin && bPoint ? distanceKm(origin, bPoint) ?? Infinity : Infinity;
  if (hasOrigin && aDistance !== bDistance) return aDistance < bDistance ? -1 : 1;

  return getVenueLocationQuality(a) - getVenueLocationQuality(b)
    || (b.qualityScore || 0) - (a.qualityScore || 0)
    || a.name.localeCompare(b.name);
};

const sortVenueResults = (items, origin = null) => [...items].sort((a, b) => compareVenueResults(a, b, origin));

const getVenueMapPoint = (venue) => {
  const exactPoint = getVenueExactPoint(venue);
  if (exactPoint) return exactPoint;
  const suburbPoint = suburbCoordinates[`${venue.city}-${venue.suburb}`];
  if (suburbPoint) return suburbPoint;
  const cityPoint = getCityCoordinate(venue.city, venue.countryCode || venue.country);
  return cityPoint || null;
};

const getVenuePreviewMapPoint = (venue, point) => {
  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) return null;
  if (getVenueExactPoint(venue)) return point;
  const hash = parseInt(shortStableHash(`${venue?.id || venue?.name || ""}|${venue?.address || ""}|map-preview`), 36) || 0;
  const angle = ((hash % 360) * Math.PI) / 180;
  const radiusMeters = 180 + (hash % 720);
  const latOffset = Math.cos(angle) * (radiusMeters / 111320);
  const lonScale = Math.max(0.2, Math.cos((point.lat * Math.PI) / 180));
  const lonOffset = Math.sin(angle) * (radiusMeters / (111320 * lonScale));
  return {
    lat: point.lat + latOffset,
    lon: point.lon + lonOffset
  };
};

const getVenueMapBackgroundPosition = (venue) => {
  const hash = parseInt(shortStableHash(`${venue?.id || venue?.name || ""}|${venue?.address || ""}|map-crop`), 36) || 0;
  const x = 36 + (hash % 29);
  const y = 36 + (Math.floor(hash / 31) % 29);
  return `${x}% ${y}%`;
};

const getVenueMatchName = (venue) => compactLocationText(
  String(venue?.name || "")
    .replace(/\bmsac\b/gi, "melbourne sports and aquatic centre")
    .replace(/\b(wsba|inc|pty|ltd|limited|club|centre|center|badminton|sports?|courts?)\b/gi, " ")
);

const isRelatedVenueListing = (venue, candidate) => {
  if (!venue || !candidate || venue.id === candidate.id) return false;
  const venueName = getVenueMatchName(venue);
  const candidateName = getVenueMatchName(candidate);
  const nameMatches = venueName.length >= 6
    && candidateName.length >= 6
    && (venueName === candidateName || venueName.includes(candidateName) || candidateName.includes(venueName));
  if (!nameMatches) return false;
  const sameState = venue.state && candidate.state && venue.state === candidate.state;
  const samePostcode = venue.postcode && candidate.postcode && venue.postcode === candidate.postcode;
  const sameSuburb = venue.suburb && candidate.suburb && compactLocationText(venue.suburb) === compactLocationText(candidate.suburb);
  const sameCity = venue.city && candidate.city && venue.city === candidate.city;
  return Boolean((sameState && (samePostcode || sameSuburb || sameCity)) || samePostcode || sameSuburb);
};

const getRelatedVenueExactPoint = (venue) => {
  if (!venue?.name || typeof venues === "undefined") return null;
  const matches = venues
    .filter((candidate) => isRelatedVenueListing(venue, candidate))
    .map((candidate) => ({ candidate, point: getVenueExactPoint(candidate) }))
    .filter((item) => item.point)
    .sort((a, b) => getVenueLocationQuality(a.candidate) - getVenueLocationQuality(b.candidate)
      || (b.candidate.qualityScore || 0) - (a.candidate.qualityScore || 0));
  return matches[0]?.point || null;
};

const getVenueMediaPoint = (venue) => {
  const exactPoint = getVenueExactPoint(venue);
  if (exactPoint) return exactPoint;
  const relatedPoint = getRelatedVenueExactPoint(venue);
  if (relatedPoint) return relatedPoint;
  const suburbPoint = suburbCoordinates[`${venue.city}-${venue.suburb}`];
  return suburbPoint || null;
};

const getOsmTileUrl = (point, zoom = 14) => {
  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) return null;
  const lat = Math.max(-85.0511, Math.min(85.0511, point.lat));
  const lon = ((point.lon + 180) % 360 + 360) % 360 - 180;
  const scale = 2 ** zoom;
  const x = Math.floor(((lon + 180) / 360) * scale);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale);
  return `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
};

const mapMarkerIconMarkup = '<span class="badminta-marker-glyph" aria-hidden="true"></span>';

const createBadmintonMarkerIcon = (leaflet, venue = {}) => createMarketplaceMarkerIcon(leaflet, {
  id: venue.id || venue.slug || venue.name,
  kind: "centre",
  title: venue.name || "Badminton centre",
  subtitle: [venue.suburb, venue.city, venue.country].filter(Boolean).join(" "),
  image: getVenueImages(venue)[0]?.url || ""
});

const openStreetMapTileTemplate = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

const addOpenStreetMapTileLayer = (leaflet, map, onStateChange = () => {}) => {
  let tileLoaded = false;
  let tileErrors = 0;
  const loadTimeout = window.setTimeout(() => {
    if (!tileLoaded) onStateChange("tiles-error");
  }, 10000);
  const layer = leaflet.tileLayer(openStreetMapTileTemplate, {
    attribution: "&copy; OpenStreetMap contributors",
    maxZoom: 19,
    updateWhenIdle: true,
    keepBuffer: 3
  });
  layer.on("tileload", () => {
    if (tileLoaded) return;
    tileLoaded = true;
    window.clearTimeout(loadTimeout);
    onStateChange("ready");
  });
  layer.on("tileerror", () => {
    tileErrors += 1;
    if (!tileLoaded && tileErrors >= 4) {
      window.clearTimeout(loadTimeout);
      onStateChange("tiles-error");
    }
  });
  layer.on("remove", () => window.clearTimeout(loadTimeout));
  return layer.addTo(map);
};

const createBadmintonMarkerLayer = (leaflet) => leaflet.layerGroup();

const observeLeafletMapSize = (map, element) => {
  let animationFrame = 0;
  const timers = [];
  const invalidate = () => {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = window.requestAnimationFrame(() => {
      if (!element?.isConnected || element.clientWidth < 2 || element.clientHeight < 2) return;
      map.invalidateSize({ pan: false, animate: false });
    });
  };
  const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(invalidate) : null;
  resizeObserver?.observe(element);
  window.addEventListener("resize", invalidate, { passive: true });
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") invalidate();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  [0, 180, 700, 1600].forEach((delay) => timers.push(window.setTimeout(invalidate, delay)));
  return () => {
    window.cancelAnimationFrame(animationFrame);
    timers.forEach((timer) => window.clearTimeout(timer));
    resizeObserver?.disconnect();
    window.removeEventListener("resize", invalidate);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};

function MapLoadPanel({ status, onRetry, compact = false }) {
  if (status === "ready") return null;
  const failed = status === "error" || status === "tiles-error";
  return (
    <div className={`map-load-panel ${failed ? "error" : "loading"} ${compact ? "compact" : ""}`} role="status" aria-live="polite">
      {failed ? <MapPinned size={22} /> : <Loader2 className="spin" size={22} />}
      <div>
        <strong>{status === "tiles-error" ? "Map tiles did not load" : failed ? "Map could not load" : "Loading map"}</strong>
        {failed && <span>Your results are still available below the map.</span>}
      </div>
      {failed && <button type="button" onClick={onRetry}>Reload map</button>}
    </div>
  );
}

const escapeHtml = (value) => String(value || "").replace(/[&<>"']/g, (character) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#039;"
}[character]));

const marketplaceMarkerKinds = new Set(["centre", "group", "player", "coach", "event"]);

const centreMarkerVariants = ["shuttle", "racket", "court", "club"];
const stableMarkerVariant = (value = "") => {
  const hash = Array.from(String(value)).reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 7);
  return centreMarkerVariants[hash % centreMarkerVariants.length];
};

const centreMarkerIconMarkup = (variant) => {
  if (variant === "racket") return '<svg viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="9" cy="8" rx="5" ry="6"/><path d="m12.5 12.5 7 7M16.8 16.8l-2.2 2.2"/></svg>';
  if (variant === "court") return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v14H4zM12 5v14M4 12h16M8 5l-4 14M16 5l4 14"/></svg>';
  if (variant === "club") return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V8l7-4 7 4v12M3 20h18M9 20v-6h6v6M8 9h1M15 9h1"/></svg>';
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 4 6 9M9 3l4 9M13 4l2 8M5 4l4-1 4 1 4 3-2 5-4 2-4-2-2-8Z"/><path d="M10 14c0 4 1 6 3 7 2-1 3-3 3-7"/></svg>';
};

const getMarketplaceMarkerImage = (item = {}) => {
  const candidate = getAllowedUserMediaUrl(item.image || item.photoUrl || item.avatarUrl || "");
  return isImageMediaUrl(candidate) ? candidate : "";
};

const createMarketplaceMarkerIcon = (leaflet, item = {}) => {
  const kind = marketplaceMarkerKinds.has(item.kind) ? item.kind : "centre";
  const title = String(item.title || item.type || "Badminton").trim();
  const image = getMarketplaceMarkerImage(item);
  const centreVariant = kind === "centre" ? stableMarkerVariant(`${item.id || ""}-${title}-${item.subtitle || ""}`) : "";
  const initials = title.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase() || kind[0].toUpperCase();
  const avatar = image
    ? `<span class="marketplace-marker-photo-fallback">${escapeHtml(initials)}</span><img src="${escapeHtml(image)}" alt="" loading="eager" decoding="async" fetchpriority="low" draggable="false" />`
    : kind === "centre"
      ? `<span class="marketplace-marker-centre-icon">${centreMarkerIconMarkup(centreVariant)}</span>`
      : `<span class="marketplace-marker-initials">${escapeHtml(initials)}</span>`;
  return leaflet.divIcon({
    className: `marketplace-marker-shell marketplace-marker-${kind}${centreVariant ? ` marketplace-marker-variant-${centreVariant}` : ""}`,
    html: `
      <span class="marketplace-map-marker">
        <span class="marketplace-marker-avatar"><span class="marketplace-marker-face">${avatar}</span></span>
        <span class="marketplace-marker-label">${escapeHtml(title.slice(0, 42))}</span>
      </span>
    `,
    iconSize: [58, 58],
    iconAnchor: [25, 50],
    tooltipAnchor: [0, -46]
  });
};

const getVenueMapTooltip = (venue) => `
  <strong>${escapeHtml(venue.name)}</strong>
  <span>${escapeHtml([
    hasVenueDistance(venue) ? getVenueDistanceLabel(venue) : null,
    hasVenuePrice(venue) ? getVenuePriceLabel(venue) : null
  ].filter(Boolean).join(" - ") || "Details pending")}</span>
`;

const applyDistanceFromOrigin = (venue, origin) => {
  const point = getVenueExactPoint(venue);
  const distance = point && Number.isFinite(origin?.lat) && Number.isFinite(origin?.lon)
    ? distanceKm(origin, point)
    : null;
  return distance === null ? venue : { ...venue, distance: formatDistance(distance) };
};

const createCentreDraftFromSearch = (searchCriteria = {}) => {
  const location = locationSnapshot(searchCriteria.location) || getDefaultSearchLocation();
  const suburb = location.type === "city" ? "" : location.suburb || location.label;
  const city = location.city || cities[0];
  const point = Number.isFinite(location.lat) && Number.isFinite(location.lon)
    ? { lat: location.lat, lon: location.lon }
    : getCityCoordinate(city, location.countryCode || location.country) || null;
  const areaName = location.type === "nearby" ? "nearby" : location.label || city || "location";
  return {
    id: `new-centre-${String(location.id || areaName).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
    name: "",
    city,
    suburb,
    country: location.country || "",
    countryCode: location.countryCode || "",
    location,
    price: null,
    priceLabel: null,
    courts: null,
    rating: null,
    reviews: null,
    distance: "",
    badge: "Add centre",
    availability: "Details pending",
    type: "Centre",
    image: null,
    gallery: [],
    amenities: ["Details pending"],
    openingHours: null,
    website: "",
    phone: "",
    address: "",
    lat: point?.lat,
    lon: point?.lon,
    isOsm: false,
    isNewCentre: true
  };
};

const getVenueDetailFacts = (venue) => [
  getVenueCourtLabel(venue),
  getVenueHoursLabel(venue),
  getVenuePriceLabel(venue),
  getVenueContactLabel(venue)
];

const getVenueAmenityLabels = (venue) => {
  const amenities = sanitisePublicVenueAmenities(venue.amenities);
  return amenities.length ? amenities : ["Amenities unknown"];
};

const formatOpeningHours = (hours = []) => {
  if (!Array.isArray(hours) || !hours.length) return null;
  const first = hours[0];
  if (first?.opening_hours) return first.opening_hours;
  if (first?.dayOfWeek && first?.opens && first?.closes) {
    return `${first.dayOfWeek}: ${first.opens}-${first.closes}`;
  }
  return null;
};

const sourceLabel = (sources = [], listingType = "venue") => {
  if (listingType === "club") return "Club listing";
  if (sources.length > 1) return "Matched listing";
  return "Centre listing";
};

const collectImageUrls = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(collectImageUrls);
  if (typeof value === "object") {
    return collectImageUrls(value.url || value.src || value.image || value.image_url || value.imageUrl || value.photo || value.thumbnail);
  }
  if (typeof value !== "string") return [];
  const url = value.trim();
  if (!url || url === "null" || url === "undefined") return [];
  if (!/^(https?:\/\/|\/|data:image\/)/i.test(url)) return [];
  return [url];
};

const imageHost = (url = "") => {
  try {
    return new URL(url, window.location.origin).hostname.toLowerCase();
  } catch {
    return "";
  }
};

const normaliseImageFingerprint = (url = "") => {
  try {
    const parsed = new URL(url, window.location.origin);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const pathname = decodeURIComponent(parsed.pathname || "")
      .toLowerCase()
      .replace(/-\d{2,5}x\d{2,5}(?=\.[a-z0-9]+$)/i, "")
      .replace(/[_-](scaled|copy|orig|original|large|medium|small)(?=\.[a-z0-9]+$)/i, "");
    return `${host}${pathname}`;
  } catch {
    return String(url || "").trim().toLowerCase().split("?")[0].split("#")[0];
  }
};

const blockedListingImageTerms = [
  "facebook",
  "instagram",
  "twitter",
  "youtube",
  "linkedin",
  "favicon",
  "sprite",
  "icon",
  "logo",
  "avatar",
  "gravatar",
  "transparent",
  "placeholder",
  "default",
  "dummy",
  "unsplash",
  "pexels",
  "shutterstock",
  "freepik",
  "stock",
  "wait.gif",
  "loading",
  "loader",
  "banner",
  "webbanner",
  "web-banner",
  "map",
  "timetable",
  "time-table",
  "schedule",
  "poster",
  "promo",
  "advert",
  "tile",
  "goodsports",
  "rev-sport",
  "pbtr-light",
  "profile-pic"
];

const blockedListingImageHosts = [
  "cdn-static.revolutionise.com.au",
  "t.cfjump.com"
];

const looksLikeNonVenuePhoto = (url = "") => {
  const fingerprint = normaliseImageFingerprint(url);
  const host = imageHost(url);
  return blockedListingImageHosts.some((blockedHost) => host === blockedHost || host.endsWith(`.${blockedHost}`))
    || blockedListingImageTerms.some((term) => fingerprint.includes(term));
};

const hasApprovedImageRights = (row = {}) => {
  const note = String(row.image_rights_note || "").toLowerCase();
  return (note.includes("first-party") || note.includes("official-page embedded") || note.includes("licensed") || note.includes("approved"))
    && !note.includes("not displayed until")
    && !note.includes("permission is verified");
};

const collectApprovedDbImageUrls = (row = {}) => {
  if (!hasApprovedImageRights(row)) return [];
  return [
    ...collectImageUrls(row.image_url),
    ...collectImageUrls(row.image_urls),
    ...collectImageUrls(row.gallery),
    ...collectImageUrls(row.images),
    ...collectImageUrls(row.photos),
    ...collectImageUrls(row.gallery_images)
  ].filter((url) => isImageMediaUrl(url) && !looksLikeNonVenuePhoto(url));
};

const dbImageListingCounts = (() => {
  const rowsByImage = new globalThis.Map();
  centreDbRows.forEach((row) => {
    const rowKey = row.slug || row.name || Math.random().toString(36);
    const fingerprints = new Set(collectApprovedDbImageUrls(row).map(normaliseImageFingerprint));
    fingerprints.forEach((fingerprint) => {
      if (!rowsByImage.has(fingerprint)) rowsByImage.set(fingerprint, new Set());
      rowsByImage.get(fingerprint).add(rowKey);
    });
  });
  const counts = new globalThis.Map();
  rowsByImage.forEach((rows, fingerprint) => counts.set(fingerprint, rows.size));
  return counts;
})();

const isSharedDbListingImage = (url = "") => (dbImageListingCounts.get(normaliseImageFingerprint(url)) || 0) > 1;

const getVettedDbVenueImages = (row = {}) => {
  const seen = new Set();
  return collectApprovedDbImageUrls(row)
    .filter((url) => !isSharedDbListingImage(url))
    .filter((url) => {
      const fingerprint = normaliseImageFingerprint(url);
      if (seen.has(fingerprint)) return false;
      seen.add(fingerprint);
      return true;
    });
};

const getVenueImages = (venue = {}, extraImages = []) => {
  const urls = [
    ...collectImageUrls(venue.image),
    ...collectImageUrls(venue.image_url),
    ...collectImageUrls(venue.imageUrl),
    ...collectImageUrls(venue.gallery),
    ...collectImageUrls(venue.images),
    ...collectImageUrls(venue.photos),
    ...collectImageUrls(venue.image_urls),
    ...collectImageUrls(venue.imageUrls),
    ...collectImageUrls(venue.galleryImages),
    ...collectImageUrls(extraImages)
  ];
  const seen = new Set();
  return urls.filter((url) => {
    const fingerprint = normaliseImageFingerprint(url);
    if (seen.has(fingerprint)) return false;
    seen.add(fingerprint);
    return true;
  });
};

const inferCityFromDbRow = (row) => {
  if (row.city) return row.city;
  const text = [row.name, row.address, row.suburb, row.slug].filter(Boolean).join(" ");
  const cityMatch = cities.find((item) => new RegExp(`\\b${item}\\b`, "i").test(text));
  if (cityMatch) return cityMatch;
  return australianStateCity[row.state] || "";
};

const verifiedVenueLocationCorrections = {
  "melbourne-badminton-centre-6-16-joseph-st-vic-3130": {
    suburb: "Blackburn North",
    address: "6-16 Joseph Street, Blackburn North VIC 3130",
    lat: -37.8000634,
    lon: 145.1441305
  }
};

const normaliseDbCentre = (row) => {
  const city = inferCityFromDbRow(row);
  const countryCode = normaliseCountryCode(row.country, city);
  const locationCorrection = verifiedVenueLocationCorrections[row.slug] || null;
  const suburb = locationCorrection?.suburb || row.suburb || "";
  const hours = formatOpeningHours(row.opening_hours);
  const amenities = sanitisePublicVenueAmenities(row.amenities);
  const isClub = row.listing_type === "club";
  const sources = row.sources || [];
  const rowLat = Number.isFinite(locationCorrection?.lat) ? locationCorrection.lat : Number.isFinite(row.lat) ? row.lat : null;
  const rowLon = Number.isFinite(locationCorrection?.lon) ? locationCorrection.lon : Number.isFinite(row.lon) ? row.lon : null;
  const cityPoint = getCityCoordinate(city, countryCode || row.country);
  const isGenericCityPoint = cityPoint && rowLat !== null && rowLon !== null
    && Math.abs(rowLat - cityPoint.lat) < 0.0002
    && Math.abs(rowLon - cityPoint.lon) < 0.0002;
  const hasReliableCoordinates = rowLat !== null && rowLon !== null && !isGenericCityPoint;
  const gallery = getVettedDbVenueImages(row);
  return {
    id: `db-${row.slug}`,
    slug: row.slug,
    name: row.name,
    city,
    state: row.state || "",
    country: getCountryName(countryCode || row.country, city),
    countryCode,
    suburb,
    postcode: row.postcode || "",
    price: null,
    priceLabel: row.price_label || null,
    courts: Number.isFinite(row.court_count) ? row.court_count : null,
    rating: null,
    reviews: null,
    distance: "",
    badge: sourceLabel(sources, row.listing_type),
    availability: hours ? `Hours: ${hours}` : "Hours unknown",
    type: isClub ? "Club" : row.venue_type || "Centre",
    listingType: row.listing_type || "venue",
    image: gallery[0] || null,
    gallery,
    imageRightsNote: row.image_rights_note || "",
    imageSourceUrl: row.image_source_url || "",
    imageCandidateUrl: row.image_candidate_url || "",
    hiddenImageCandidateCount: Array.isArray(row.image_candidates) ? row.image_candidates.length : 0,
    amenities,
    openingHours: hours,
    openingHoursRows: Array.isArray(row.opening_hours) ? row.opening_hours : [],
    description: sanitisePublicVenueDescription(row),
    detailItems: sanitisePublicVenueDetailItems(row.detail_items),
    socialLinks: filterAllowedVenueLinks(row.social_links),
    bookingLinks: filterAllowedVenueLinks(row.booking_links),
    sources,
    website: getAllowedUrl(row.website),
    phone: row.phone || null,
    email: row.email || null,
    address: locationCorrection?.address || row.address || [suburb, city, row.state, row.postcode].filter(Boolean).join(", "),
    lat: hasReliableCoordinates ? rowLat : null,
    lon: hasReliableCoordinates ? rowLon : null,
    sourceUrls: (row.source_urls || []).filter((url) => getAllowedUrl(url)),
    aliases: Array.isArray(row.aliases) ? row.aliases.map((alias) => slugifyPath(alias)).filter(Boolean) : [],
    osmUrl: (row.source_urls || []).find((url) => getAllowedUrl(url)),
    qualityScore: row.quality_score || 0,
    verified: row.verified === true,
    verificationStatus: row.verification_status || row.verificationStatus || "",
    claimStatus: row.claim_status || row.claimStatus || "",
    ownerCreated: row.owner_created === true || row.ownerCreated === true,
    ownerUid: row.owner_uid || row.ownerUid || "",
    sourceCategory: getVenueSourceCategory(row),
    isOsm: true,
    isLocalDb: true
  };
};

const globalCentreShardCache = new globalThis.Map();

const getGlobalCentreCountryShard = async (countryCode) => {
  const code = String(countryCode || "").trim().toUpperCase();
  const shard = globalCentreShardManifest?.countries?.[code];
  if (!code || !shard?.path) return [];
  if (globalCentreShardCache.has(code)) return globalCentreShardCache.get(code);
  const rows = await loadPublicJson(shard.path, []);
  const nextRows = Array.isArray(rows) ? rows : [];
  globalCentreShardCache.set(code, nextRows);
  return nextRows;
};

const loadGlobalCentreRowsForLocation = async (location = {}) => {
  const locationCity = location?.city || "";
  const countryCode = normaliseCountryCode(location?.countryCode || location?.country, locationCity);
  if (!countryCode) return [];
  return getGlobalCentreCountryShard(countryCode);
};

const adminDirectoryBrowserRowLimit = 1500;

const loadAllGlobalCentreRows = async ({ onProgress, maxRows = Number.POSITIVE_INFINITY } = {}) => {
  const countries = Object.values(globalCentreShardManifest?.countries || {})
    .filter((country) => country?.code && country?.path)
    .sort((first, second) => String(first.code).localeCompare(String(second.code)));
  const rows = [];
  const batchSize = 10;
  for (let index = 0; index < countries.length; index += batchSize) {
    const batch = countries.slice(index, index + batchSize);
    const batchRows = await Promise.all(batch.map((country) => getGlobalCentreCountryShard(country.code)));
    batchRows.forEach((countryRows) => {
      if (rows.length < maxRows) rows.push(...countryRows.slice(0, Math.max(0, maxRows - rows.length)));
    });
    onProgress?.({
      loadedCountries: Math.min(index + batch.length, countries.length),
      totalCountries: countries.length,
      loadedRows: rows.length
    });
    if (rows.length >= maxRows) break;
    await new Promise((resolve) => window.setTimeout(resolve, 0));
  }
  return rows;
};

const globalCentreCountryRows = Object.values(globalCentreShardManifest?.countries || {});
const globalDirectoryTotalCount = Number(globalCentreShardManifest?.totalRows)
  || globalCentreCountryRows.reduce((sum, country) => sum + Number(country.rowCount || country.rows || 0), 0);
const globalCentreTotalCount = Number(globalCentreShardManifest?.publicVenueRows)
  || globalCentreCountryRows.reduce((sum, country) => sum + Number(country.venueRowCount || 0), 0)
  || globalDirectoryTotalCount;
const getGlobalCentreCountryCount = (countryCode = "") => {
  const code = String(countryCode || "").trim().toUpperCase();
  const country = code ? globalCentreShardManifest?.countries?.[code] : null;
  return Number(country?.venueRowCount || country?.rowCount || country?.rows || 0);
};

const rawCentreDbRows = [...centreDbRows];

const scrapedVenues = uniqueVenueList(rawCentreDbRows.map(normaliseDbCentre).filter(isPublicCourtHireCentre));

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const searchCentreDirectory = async (location, radiusMeters = 50000, directory = venues) => {
  await wait(650);
  const radiusKm = radiusMeters / 1000;
  const sourceVenues = Array.isArray(directory) ? directory : venues;
  const withDistance = sourceVenues.map((venue) => {
    const point = getVenueExactPoint(venue);
    const distance = point && Number.isFinite(location?.lat) && Number.isFinite(location?.lon)
      ? distanceKm(location, point)
      : null;
    return {
      ...venue,
      distance: distance !== null ? formatDistance(distance) : venue.distance
    };
  });

  const locationLabel = (location?.label || "").toLowerCase();
  const locationCity = location?.city || "";
  const locationCountryCode = normaliseCountryCode(location?.countryCode || location?.country, locationCity);
  const exactTextMatches = withDistance.filter((venue) => {
    const haystack = [venue.name, venue.suburb, venue.city, venue.state, venue.address, venue.postcode].filter(Boolean).join(" ").toLowerCase();
    return locationLabel && haystack.includes(locationLabel);
  });

  const geoMatches = withDistance.filter((venue) => {
    const point = getVenueExactPoint(venue);
    if (!point || !Number.isFinite(location?.lat) || !Number.isFinite(location?.lon)) return false;
    const distance = distanceKm(location, point);
    return distance !== null && distance <= radiusKm;
  });

  const locationCityText = compactLocationText(locationCity);
  const cityMatches = withDistance.filter((venue) => locationCityText && compactLocationText(venue.city) === locationCityText);
  const countryMatches = withDistance.filter((venue) => locationCountryCode && normaliseCountryCode(venue.countryCode || venue.country, venue.city) === locationCountryCode);
  const matches = location?.type === "city"
    ? cityMatches.filter((venue) => !locationCountryCode || normaliseCountryCode(venue.countryCode || venue.country, venue.city) === locationCountryCode)
    : location?.type === "country"
      ? countryMatches
    : [...exactTextMatches, ...geoMatches, ...cityMatches.filter((venue) => exactTextMatches.length === 0 && geoMatches.length === 0)];

  return uniqueVenueList(matches)
    .sort((a, b) => compareVenueResults(a, b, location));
};

const venues = scrapedVenues;
const defaultVenue = venues[0] || null;

const averagePoint = (points = []) => {
  const valid = points.filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lon));
  if (!valid.length) return null;
  return {
    lat: valid.reduce((sum, point) => sum + point.lat, 0) / valid.length,
    lon: valid.reduce((sum, point) => sum + point.lon, 0) / valid.length
  };
};

const countryBuckets = new globalThis.Map();
const addCountryBucketPoint = (countryCode, countryName, point = null, rowCount = 1) => {
  const code = normaliseCountryCode(countryCode || countryName);
  if (!code) return;
  const current = countryBuckets.get(code) || { code, name: getCountryName(code) || countryName || code, points: [], rows: 0 };
  current.name = current.name || getCountryName(code) || countryName || code;
  current.rows += Number(rowCount) || 0;
  if (point && Number.isFinite(point.lat) && Number.isFinite(point.lon)) current.points.push(point);
  countryBuckets.set(code, current);
};

globalCitySeeds.forEach((seed) => addCountryBucketPoint(seed.country, seed.countryName, { lat: seed.lat, lon: seed.lon }, 0));
globalCentreCountryRows.forEach((country) => addCountryBucketPoint(country.code, country.name, null, country.venueRowCount || 0));
venues.forEach((venue) => addCountryBucketPoint(venue.countryCode || venue.country, venue.country, getVenueExactPoint(venue)));

const countryDirectory = [...countryBuckets.values()]
  .filter((country) => country.rows > 0)
  .map((country) => {
    const point = averagePoint(country.points);
    return {
      id: `country-${country.code}`,
      label: country.name,
      city: "",
      suburb: "",
      country: country.name,
      countryCode: country.code,
      type: "country",
      hint: "Country",
      lat: point?.lat ?? null,
      lon: point?.lon ?? null,
      bbox: point ? mapBboxAround(point.lat, point.lon, 4) : null,
      source: "Country directory"
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

const cityBuckets = new globalThis.Map();
const addCityBucketPoint = ({ city, countryCode, countryName, point = null, seed = null, rowCount = 1 }) => {
  const cleanCity = String(city || "").trim();
  const code = normaliseCountryCode(countryCode || countryName, cleanCity);
  if (!cleanCity || cleanCity.toLowerCase() === "unknown" || !code) return;
  const key = `${code}-${cleanCity.toLowerCase()}`;
  const current = cityBuckets.get(key) || { city: cleanCity, countryCode: code, countryName: getCountryName(code) || countryName || code, points: [], seed: null, rows: 0 };
  current.rows += Number(rowCount) || 0;
  if (seed) current.seed = seed;
  if (point && Number.isFinite(point.lat) && Number.isFinite(point.lon)) current.points.push(point);
  cityBuckets.set(key, current);
};

globalCitySeeds.forEach((seed) => addCityBucketPoint({
  city: seed.city,
  countryCode: seed.country,
  countryName: seed.countryName,
  point: { lat: seed.lat, lon: seed.lon },
  seed,
  rowCount: 0
}));
venues.forEach((venue) => addCityBucketPoint({
  city: venue.city,
  countryCode: venue.countryCode || venue.country,
  countryName: venue.country,
  point: getVenueExactPoint(venue)
}));

const cityDirectory = [...cityBuckets.values()].map((bucket) => {
  const seedPoint = bucket.seed ? { lat: bucket.seed.lat, lon: bucket.seed.lon } : null;
  const point = seedPoint || averagePoint(bucket.points);
  const span = bucket.seed ? Math.max(0.08, Math.min(0.75, (bucket.seed.radiusM || 45000) / 111000)) : 0.35;
  return {
    id: `city-${bucket.countryCode}-${bucket.city}`,
    label: bucket.city,
    city: bucket.city,
    suburb: "",
    country: getCountryName(bucket.countryCode),
    countryCode: bucket.countryCode,
    type: "city",
    hint: ["City", getCountryName(bucket.countryCode)].filter(Boolean).join(" · "),
    lat: point?.lat ?? null,
    lon: point?.lon ?? null,
    bbox: point ? [point.lon - span, point.lat - span, point.lon + span, point.lat + span] : null,
    source: bucket.seed ? "Map baseline" : "Local venue directory"
  };
}).sort((a, b) => `${a.country} ${a.city}`.localeCompare(`${b.country} ${b.city}`));

const cityOptionByKey = Object.fromEntries(cityDirectory.map((option) => [`${option.countryCode}-${compactLocationText(option.city)}`, option]));

const suburbOptions = venues
  .filter((venue) => venue.city && venue.suburb)
  .map((venue) => {
    const countryCode = normaliseCountryCode(venue.countryCode || venue.country, venue.city);
    const cityOption = cityOptionByKey[`${countryCode}-${compactLocationText(venue.city)}`];
    const exactPoint = getVenueExactPoint(venue);
    const fallbackPoint = suburbCoordinates[`${venue.city}-${venue.suburb}`] || cityOption;
    const lat = exactPoint?.lat ?? fallbackPoint?.lat ?? null;
    const lon = exactPoint?.lon ?? fallbackPoint?.lon ?? null;
    return {
      id: `suburb-${countryCode}-${venue.city}-${venue.suburb}`,
      label: venue.suburb,
      city: venue.city,
      suburb: venue.suburb,
      country: getCountryName(countryCode, venue.city),
      countryCode,
      type: "suburb",
      hint: [venue.city, getCountryName(countryCode, venue.city)].filter(Boolean).join(" · "),
      lat,
      lon,
      bbox: Number.isFinite(lat) && Number.isFinite(lon) ? mapBboxAround(lat, lon) : cityOption?.bbox || null,
      source: "Local venue directory"
    };
  });

const locationOptionIds = new Set();
const locationOptions = [
  ...countryDirectory,
  ...cityDirectory,
  ...suburbOptions
].filter((option) => {
  if (!option?.id || locationOptionIds.has(option.id)) return false;
  locationOptionIds.add(option.id);
  return true;
});

const getDefaultSearchLocation = () => (
  locationOptions.find((option) => option.type === "city" && option.countryCode === "AU" && compactLocationText(option.city) === "melbourne")
  || cityDirectory.find((option) => option.type === "city" && option.countryCode === "AU" && compactLocationText(option.city) === "melbourne")
  || locationOptions.find((option) => option.type === "city" && option.countryCode === "AU")
  || cityDirectory[0]
  || locationOptions[0]
  || {
    id: "city-AU-Melbourne",
    label: "Melbourne",
    city: "Melbourne",
    suburb: "",
    country: "Australia",
    countryCode: "AU",
    type: "city",
    hint: "City · Australia",
    lat: cityCoordinates.Melbourne?.lat ?? -37.8136,
    lon: cityCoordinates.Melbourne?.lon ?? 144.9631,
    bbox: cityCoordinates.Melbourne?.bbox ?? [144.6, -38.05, 145.3, -37.55],
    source: "Default location"
  }
);

const getCityDirectoryPoint = (cityName = "", countryCode = "") => {
  const code = normaliseCountryCode(countryCode, cityName);
  const cityKey = compactLocationText(cityName);
  const directoryMatch = code
    ? cityOptionByKey[`${code}-${cityKey}`]
    : cityDirectory.find((option) => option.type === "city" && compactLocationText(option.city) === cityKey);
  if (Number.isFinite(directoryMatch?.lat) && Number.isFinite(directoryMatch?.lon)) {
    return { lat: directoryMatch.lat, lon: directoryMatch.lon };
  }
  const seedPoint = getCityCoordinate(cityName, code);
  return Number.isFinite(seedPoint?.lat) && Number.isFinite(seedPoint?.lon)
    ? { lat: seedPoint.lat, lon: seedPoint.lon }
    : null;
};

const getRepresentativeCityForCountry = (countryCode = "", originPoint = null, fallbackCity = "Melbourne") => {
  const code = normaliseCountryCode(countryCode, fallbackCity);
  if (!code) return fallbackCity;
  const sameCountryCities = cityDirectory
    .filter((option) => option.type === "city" && option.countryCode === code && option.city)
    .map((option) => ({
      city: option.city,
      point: getCityDirectoryPoint(option.city, option.countryCode)
    }));
  if (!sameCountryCities.length) return fallbackCity;
  if (originPoint) {
    return sameCountryCities
      .sort((a, b) => {
        const aDistance = a.point ? distanceKm(originPoint, a.point) : null;
        const bDistance = b.point ? distanceKm(originPoint, b.point) : null;
        return (aDistance ?? Infinity) - (bDistance ?? Infinity);
      })[0]?.city || fallbackCity;
  }
  return sameCountryCities[0]?.city || fallbackCity;
};

const normaliseSearchLocationForMap = (location = {}, fallbackCity = "Melbourne") => {
  const type = location.type || "city";
  const baseCity = location.city || (type === "city" ? location.label : "") || fallbackCity;
  const countryCode = normaliseCountryCode(location.countryCode || location.country, baseCity);
  const country = getCountryName(countryCode || location.country, baseCity);
  const countryMatch = countryDirectory.find((option) => option.countryCode === countryCode);
  const cityName = type === "country"
    ? getRepresentativeCityForCountry(countryCode, Number.isFinite(location.lat) && Number.isFinite(location.lon) ? location : null, fallbackCity)
    : baseCity;
  const cityMatch = cityDirectory.find((option) => (
    option.type === "city"
    && compactLocationText(option.city) === compactLocationText(cityName)
    && (!countryCode || option.countryCode === countryCode)
  ));
  const point = Number.isFinite(location.lat) && Number.isFinite(location.lon)
    ? { lat: location.lat, lon: location.lon }
    : type === "country" && Number.isFinite(countryMatch?.lat) && Number.isFinite(countryMatch?.lon)
      ? { lat: countryMatch.lat, lon: countryMatch.lon }
      : getCityDirectoryPoint(cityName, countryCode);
  return {
    ...location,
    type,
    label: location.label || location.suburb || location.city || country || cityName,
    suburb: type === "suburb" ? location.suburb || location.label || "" : "",
    city: cityName,
    country,
    countryCode,
    lat: Number.isFinite(point?.lat) ? point.lat : null,
    lon: Number.isFinite(point?.lon) ? point.lon : null,
    bbox: Array.isArray(location.bbox)
      ? location.bbox
      : type === "country" && countryMatch?.bbox
        ? countryMatch.bbox
        : cityMatch?.bbox || (Number.isFinite(point?.lat) && Number.isFinite(point?.lon) ? mapBboxAround(point.lat, point.lon, type === "country" ? 4 : 0.25) : null)
  };
};

function getVisitorCityStripCities(currentCity = "Melbourne", searchCriteria = {}) {
  const location = searchCriteria.location || {};
  const activeCity = location.city || currentCity || "Melbourne";
  const activeCountryCode = normaliseCountryCode(location.countryCode || location.country, activeCity)
    || cityCoordinates[activeCity]?.country
    || "AU";
  const originPoint = Number.isFinite(location.lat) && Number.isFinite(location.lon)
    ? { lat: location.lat, lon: location.lon }
    : getCityDirectoryPoint(activeCity, activeCountryCode);
  const sameCountryCities = cityDirectory
    .filter((option) => option.type === "city" && option.countryCode === activeCountryCode && option.city)
    .map((option) => ({
      city: option.city,
      countryCode: option.countryCode,
      point: getCityDirectoryPoint(option.city, option.countryCode)
    }));
  const rowsByCity = new globalThis.Map();
  sameCountryCities.forEach((row) => {
    const key = compactLocationText(row.city);
    if (!rowsByCity.has(key)) rowsByCity.set(key, row);
  });
  if (activeCity && !rowsByCity.has(compactLocationText(activeCity))) {
    rowsByCity.set(compactLocationText(activeCity), {
      city: activeCity,
      countryCode: activeCountryCode,
      point: getCityDirectoryPoint(activeCity, activeCountryCode)
    });
  }
  return [...rowsByCity.values()]
    .sort((a, b) => {
      if (compactLocationText(a.city) === compactLocationText(activeCity)) return -1;
      if (compactLocationText(b.city) === compactLocationText(activeCity)) return 1;
      const aDistance = originPoint && a.point ? distanceKm(originPoint, a.point) : null;
      const bDistance = originPoint && b.point ? distanceKm(originPoint, b.point) : null;
      if (aDistance !== null || bDistance !== null) return (aDistance ?? Infinity) - (bDistance ?? Infinity);
      return a.city.localeCompare(b.city);
    })
    .slice(0, 14)
    .map((row) => row.city);
}

const seoAreaIntents = {
  overview: {
    slug: "",
    label: "All",
    resultType: "centres",
    keyword: "badminton",
    title: (area) => `Badminton in ${area}`,
    emptyType: "community listings"
  },
  centres: {
    slug: "badminton-centres",
    label: "Centres",
    resultType: "centres",
    keyword: "badminton centres",
    title: (area) => `Badminton centres in ${area}`,
    emptyType: "badminton centres"
  },
  groups: {
    slug: "social-badminton-groups",
    label: "Social groups",
    resultType: "groups",
    keyword: "social badminton groups",
    title: (area) => `Social badminton groups in ${area}`,
    emptyType: "social groups"
  },
  players: {
    slug: "badminton-players",
    label: "Players",
    resultType: "players",
    keyword: "badminton players",
    title: (area) => `Badminton players in ${area}`,
    emptyType: "players"
  },
  coaches: {
    slug: "badminton-coaches",
    label: "Coaches",
    resultType: "coaches",
    keyword: "badminton coaches",
    title: (area) => `Badminton coaches in ${area}`,
    emptyType: "coaches"
  },
  events: {
    slug: "badminton-events",
    label: "Events",
    resultType: "events",
    keyword: "badminton events",
    title: (area) => `Badminton events in ${area}`,
    emptyType: "events"
  }
};

const seoAreaIntentList = Object.entries(seoAreaIntents).map(([id, config]) => ({ id, ...config }));
const seoAreaIntentBySlug = seoAreaIntentList.reduce((acc, item) => {
  acc[item.slug || ""] = item.id;
  return acc;
}, {});

const normaliseSeoAreaIntent = (value) => seoAreaIntents[value] ? value : "overview";
const seoAreaIntentFromSlug = (slug = "") => seoAreaIntentBySlug[slugifyPath(slug || "")] || null;

const getSeoAreaLocationLabel = (location = {}, fallbackCity = "Melbourne") => location.suburb || location.label || location.city || getCountryName(location.countryCode || location.country, fallbackCity) || fallbackCity;
const getLocationCountryCode = (location = {}, fallbackCity = "Melbourne") => normaliseCountryCode(location.countryCode || location.country, location.city || fallbackCity);
const getLocationCountryName = (location = {}, fallbackCity = "Melbourne") => getCountryName(getLocationCountryCode(location, fallbackCity), location.city || fallbackCity);

const findSeoAreaLocation = (citySlugValue, suburbSlugValue) => {
  const cityName = findCityBySlug(citySlugValue);
  const suburbSlug = slugifyPath(suburbSlugValue || "");
  if (!suburbSlug || suburbSlug === slugifyPath(cityName)) {
    return locationOptions.find((option) => option.type === "city" && compactLocationText(option.city) === compactLocationText(cityName)) || null;
  }
  const citySuburbs = locationOptions.filter((option) => (
    option.type === "suburb"
    && compactLocationText(option.city) === compactLocationText(cityName)
  ));
  const exactMatch = citySuburbs.find((option) => slugifyPath(option.suburb || option.label) === suburbSlug);
  if (exactMatch) return exactMatch;
  return citySuburbs
    .filter((option) => {
      const optionSlug = slugifyPath(option.suburb || option.label);
      return optionSlug.startsWith(suburbSlug) || suburbSlug.startsWith(optionSlug);
    })
    .sort((a, b) => slugifyPath(a.suburb || a.label).length - slugifyPath(b.suburb || b.label).length)[0] || null;
};

const findCountryBySlug = (slug = "") => {
  const value = slugifyPath(slug);
  return countryDirectory.find((country) => slugifyPath(country.countryCode) === value || slugifyPath(country.label) === value || slugifyPath(country.country) === value) || null;
};

const findCityLocationBySlug = (countrySlugValue = "", citySlugValue = "") => {
  const country = findCountryBySlug(countrySlugValue);
  const cityValue = slugifyPath(citySlugValue);
  if (!country || !cityValue) return null;
  return cityDirectory.find((option) => (
    option.countryCode === country.countryCode
    && slugifyPath(option.city) === cityValue
  )) || null;
};

const findSeoAreaLocationWorld = (countrySlugValue = "", citySlugValue = "", suburbSlugValue = "") => {
  const cityLocation = findCityLocationBySlug(countrySlugValue, citySlugValue);
  if (!cityLocation) return null;
  const suburbSlug = slugifyPath(suburbSlugValue || "");
  if (!suburbSlug || suburbSlug === slugifyPath(cityLocation.city)) return cityLocation;
  return locationOptions.find((option) => (
    option.type === "suburb"
    && option.countryCode === cityLocation.countryCode
    && compactLocationText(option.city) === compactLocationText(cityLocation.city)
    && slugifyPath(option.suburb || option.label) === suburbSlug
  )) || null;
};

const seoAreaLocationPath = (location = {}, intentId = "overview") => {
  const countrySegment = countrySlug(location.countryCode || location.country, location.city || "Melbourne");
  const citySegment = citySlug(location.city || "Melbourne");
  const suburbSegment = slugifyPath(location.suburb || location.label || location.city || "badminton");
  const intent = seoAreaIntents[normaliseSeoAreaIntent(intentId)] || seoAreaIntents.overview;
  const intentSegment = intent.slug ? `/${intent.slug}` : "";
  if (location.type === "country") return `/badminton/${countrySegment}${intentSegment}`;
  if (location.type === "city") return `/badminton/${countrySegment}/${citySegment}${intentSegment}`;
  return `/badminton/${countrySegment}/${citySegment}/${suburbSegment}${intentSegment}`;
};

const locationMatchesSeoArea = (location = {}, city = "Melbourne", suburb = "") => {
  if (location.type !== "suburb") return false;
  if (compactLocationText(location.city) !== compactLocationText(city)) return false;
  return compactLocationText(location.suburb || location.label) === compactLocationText(suburb);
};

const getVenueSeoLocation = (venue = {}) => {
  const explicitCity = venue.city || "";
  const explicitSuburb = venue.suburb || "";
  const countryCode = normaliseCountryCode(venue.countryCode || venue.country, explicitCity);
  if (explicitCity && explicitSuburb) return { city: explicitCity, suburb: explicitSuburb, countryCode, country: getCountryName(countryCode, explicitCity) };
  const venueText = compactLocationText([venue.name, venue.address, venue.description, venue.sourceName].filter(Boolean).join(" "));
  const inferred = locationOptions
    .filter((option) => option.type === "suburb")
    .filter((option) => {
      const suburbText = compactLocationText(option.suburb || option.label);
      const cityText = compactLocationText(option.city);
      return suburbText && venueText.includes(suburbText) && (!explicitCity || compactLocationText(explicitCity) === cityText);
    })
    .sort((a, b) => compactLocationText(b.suburb || b.label).length - compactLocationText(a.suburb || a.label).length)[0];
  return {
    city: explicitCity || inferred?.city || "",
    suburb: explicitSuburb || inferred?.suburb || inferred?.label || "",
    countryCode,
    country: getCountryName(countryCode, explicitCity)
  };
};

const getVenueSeoCompletenessScore = (venue = {}) => [
  venue.website,
  venue.phone,
  venue.email,
  venue.address,
  venue.openingHours,
  venue.price,
  venue.priceLabel,
  Number.isFinite(venue.lat) && Number.isFinite(venue.lon),
  Array.isArray(venue.images) && venue.images.length,
  Array.isArray(venue.detailItems) && venue.detailItems.length
].filter(Boolean).length;

const dedupeSeoVenues = (venueRows = []) => {
  const byKey = new globalThis.Map();
  venueRows.forEach((venue) => {
    const location = getVenueSeoLocation(venue);
    const key = compactLocationText([
      venue.name,
      location.countryCode || venue.countryCode || venue.country,
      location.suburb || venue.suburb || venue.postcode || venue.address,
      location.city
    ].filter(Boolean).join("|")) || venue.id || entitySlug(venue);
    const existing = byKey.get(key);
    if (!existing || getVenueSeoCompletenessScore(venue) > getVenueSeoCompletenessScore(existing)) {
      byKey.set(key, venue);
    }
  });
  return [...byKey.values()];
};

const venueMatchesSeoArea = (venue = {}, location = {}) => {
  const locationCountryCode = getLocationCountryCode(location, location.city);
  const venueCountryCode = normaliseCountryCode(venue.countryCode || venue.country, venue.city);
  if (locationCountryCode && venueCountryCode && locationCountryCode !== venueCountryCode) return false;
  if (location?.type === "country") return Boolean(locationCountryCode);
  if (!location?.city) return false;
  const venueLocation = getVenueSeoLocation(venue);
  if (compactLocationText(venueLocation.city) !== compactLocationText(location.city)) return false;
  if (location?.type === "city") return true;
  if (!location.suburb) return true;
  const venueSuburb = venueLocation.suburb || "";
  if (venueSuburb && compactLocationText(venueSuburb) === compactLocationText(location.suburb)) return true;
  const addressText = compactLocationText([venue.address, venue.name, venue.description].filter(Boolean).join(" "));
  return addressText.includes(compactLocationText(location.suburb));
};

const recordMatchesSeoArea = (record = {}, location = {}, type = "profile") => {
  if (!record) return false;
  const linkedVenue = record.venueId ? venues.find((venue) => venue.id === record.venueId) : null;
  if (linkedVenue) return venueMatchesSeoArea(linkedVenue, location);
  const locationCountryCode = getLocationCountryCode(location, location.city);
  const recordCountryCode = normaliseCountryCode(record.countryCode || record.country || record.location?.countryCode || record.location?.country, record.city || record.location?.city);
  if (locationCountryCode && recordCountryCode && locationCountryCode !== recordCountryCode) return false;
  if (location?.type === "country") return Boolean(locationCountryCode);
  if (!location?.city) return false;
  const recordCity = record.city || record.location?.city || linkedVenue?.city || "";
  if (!recordCity) return false;
  if (compactLocationText(recordCity) !== compactLocationText(location.city)) return false;
  if (location?.type === "city") return true;
  if (!location.suburb) return true;
  const recordSuburb = record.suburb || record.location?.suburb || "";
  if (recordSuburb) return compactLocationText(recordSuburb) === compactLocationText(location.suburb);
  if (type === "event") {
    const venueText = compactLocationText([record.venue, record.address, record.title].filter(Boolean).join(" "));
    return venueText.includes(compactLocationText(location.suburb));
  }
  return false;
};

const getSeoAreaNearbyLocations = (location = {}, limit = 10) => {
  const currentPoint = Number.isFinite(location.lat) && Number.isFinite(location.lon) ? { lat: location.lat, lon: location.lon } : null;
  const locationCountryCode = getLocationCountryCode(location, location.city);
  const targetType = location.type === "country" ? "city" : "suburb";
  return locationOptions
    .filter((option) => {
      if (option.type !== targetType) return false;
      if (locationCountryCode && option.countryCode !== locationCountryCode) return false;
      if (location.type === "city" && compactLocationText(option.city) !== compactLocationText(location.city)) return false;
      if (location.type === "suburb" && locationMatchesSeoArea(option, location.city, location.suburb)) return false;
      return option.id !== location.id;
    })
    .map((option) => {
      const point = Number.isFinite(option.lat) && Number.isFinite(option.lon) ? { lat: option.lat, lon: option.lon } : null;
      return {
        ...option,
        seoDistance: currentPoint && point ? distanceKm(currentPoint, point) : null,
        sameCity: compactLocationText(option.city) === compactLocationText(location.city)
      };
    })
    .sort((a, b) => {
      if (a.sameCity !== b.sameCity) return a.sameCity ? -1 : 1;
      if (a.seoDistance !== null && b.seoDistance !== null) return a.seoDistance - b.seoDistance;
      return String(a.label).localeCompare(String(b.label));
    })
    .slice(0, limit);
};

const groups = [];
const players = [];
const trainers = [];
const events = [];

const normalisePublicPlayerDocument = (documentId, data = {}) => {
  const location = data.location || {};
  const city = location.city || data.city || "";
  const suburb = location.suburb || data.suburb || "";
  const name = publicPlayerDisplayName(data.name || data.fullName || data.title, documentId);
  const rating = data.rating || data.ratingAverage || data.averageRating || "New";
  const plays = Number(data.plays || data.verifiedPlays || data.completedSessions || 0);
  return {
    ...data,
    id: documentId,
    firestoreSourceId: documentId,
    publicPlayerId: documentId,
    name,
    title: name,
    image: getPublicProfileImage(data.photoUrl, data.image, approvedPublicMediaLinks(data.mediaLinks, "Player media")),
    photoUrl: data.photoUrl || data.image || "",
    band: data.band || data.skillBand || "Level not added",
    skillBand: data.skillBand || data.band || "",
    city,
    suburb,
    country: location.country || data.country || "",
    countryCode: location.countryCode || data.countryCode || "",
    location,
    locationPending: data.locationPending === true || !hasPublicPlayerLocation({ ...data, location }),
    displayNamePending: data.displayNamePending === true,
    about: data.about || data.style || data.description || "",
    style: data.about || data.style || data.description || "",
    availability: data.availability || summarizeAvailabilitySchedule(data.availabilitySchedule),
    rating,
    plays,
    verifiedBy: plays > 0 ? "Community activity" : "Published profile",
    searchText: filterSearchText(name, data.skillBand, suburb, city, data.country, location.country, data.formats, data.about, data.style, data.availability)
  };
};

const normalisePublicCoachDocument = (documentId, data = {}) => {
  const location = data.location || {};
  const city = data.city || location.city || "";
  const suburb = data.suburb || location.suburb || "";
  const name = data.name || data.fullName || data.headline || data.title || "Badminta coach";
  const rating = data.rating || data.ratingAverage || data.averageRating || "New";
  const plays = Number(data.lessons || data.completedLessons || data.completedSessions || 0);
  return {
    ...data,
    id: documentId,
    firestoreSourceId: documentId,
    publicCoachId: documentId,
    name,
    fullName: data.fullName || data.name || name,
    title: data.title || data.headline || name,
    headline: data.headline || data.title || "Badminton coach",
    image: getPublicProfileImage(data.photoUrl, data.image, approvedPublicMediaLinks(data.mediaLinks, "Coach media")),
    photoUrl: data.photoUrl || data.image || "",
    band: data.skillBand || data.band || "Badminton coach",
    skillBand: data.skillBand || data.band || "",
    city,
    suburb,
    country: data.country || location.country || "",
    countryCode: data.countryCode || location.countryCode || "",
    location,
    style: data.bio || data.description || data.headline || "Published Badminta coach profile.",
    bio: data.bio || data.description || "",
    rating,
    plays,
    searchText: filterSearchText(name, data.headline, data.skillBand, suburb, city, data.locations, data.accreditation, data.bio)
  };
};

const accountProfileToPublicPlayerDirectoryProfile = (accountProfile = {}, fallbackCity = "Worldwide") => {
  const player = accountProfile.player || {};
  const name = [accountProfile.fullName, player.name, player.fullName]
    .map(safePublicPlayerDisplayName)
    .find(Boolean) || "";
  if (!name || !isPublicProfileReady(accountProfile, "player") || getPublicProfileMissingFields(accountProfile, "player").length) return null;
  const documentId = player.publicPlayerId
    || player.firestoreSourceId
    || accountProfile.publicPlayerProfileId
    || `player-${shortStableHash([accountProfile.uid, accountProfile.phoneNumber, name, player.suburb, player.city].filter(Boolean).join("|"))}`;
  return normalisePublicPlayerDocument(documentId, {
    ...player,
    name,
    fullName: name,
    title: name,
    photoUrl: player.photoUrl || accountProfile.photoUrl || "",
    image: player.photoUrl || accountProfile.photoUrl || "",
    city: player.location?.city || player.city || "",
    suburb: player.location?.suburb || player.suburb || "",
    location: locationSnapshot(player.location),
    countryCode: player.location?.countryCode || player.countryCode || "",
    country: player.location?.country || player.country || "",
    status: player.backendStatus || accountProfile.publicPlayerProfileStatus || "published",
    public: true
  });
};

const publicPlayerDirectoryPath = (accountProfile = {}, fallbackCity = "Worldwide") => {
  const directoryPlayer = accountProfileToPublicPlayerDirectoryProfile(accountProfile, fallbackCity);
  if (!directoryPlayer) return "";
  return `/players/${citySlug(directoryPlayer.suburb || directoryPlayer.city || directoryPlayer.country || directoryPlayer.countryCode || fallbackCity)}/${entitySlug(directoryPlayer)}`;
};

const mergePublicDirectoryProfiles = (baseProfiles = [], publicProfiles = []) => {
  const rows = new globalThis.Map();
  [...baseProfiles, ...publicProfiles].forEach((profile) => {
    const key = String(profile.firestoreSourceId || profile.publicPlayerId || profile.id || entitySlug(profile)).trim();
    if (!key) return;
    rows.set(key, { ...(rows.get(key) || {}), ...profile });
  });
  return [...rows.values()];
};

const bootstrappedPublicPlayerProfiles = (Array.isArray(publicPlayerBootstrapRows) ? publicPlayerBootstrapRows : [])
  .filter((player) => player?.id && safePublicPlayerDisplayName(player.name || player.fullName || player.title));
let staticPublicPlayerProfilesPromise = null;
const loadStaticPublicPlayerProfiles = async () => {
  if (!staticPublicPlayerProfilesPromise) {
    staticPublicPlayerProfilesPromise = fetch("/data/public_players.json", { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Public player directory could not be loaded (${response.status}).`);
        const payload = await response.json();
        return (payload.players || [])
          .map((player) => normalisePublicPlayerDocument(player.id || player.firestoreSourceId || player.publicPlayerId, player))
          .filter((player) => player.name);
      })
      .catch((error) => {
        staticPublicPlayerProfilesPromise = null;
        throw error;
      });
  }
  return staticPublicPlayerProfilesPromise;
};

let staticPublicMarketplaceProfilesPromise = null;
const loadStaticPublicMarketplaceProfiles = async () => {
  if (!staticPublicMarketplaceProfilesPromise) {
    staticPublicMarketplaceProfilesPromise = fetch("/data/public_marketplace.json", { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Public marketplace directory could not be loaded (${response.status}).`);
        const payload = await response.json();
        return {
          groups: Array.isArray(payload.groups) ? payload.groups : [],
          coaches: Array.isArray(payload.coaches) ? payload.coaches : [],
          events: Array.isArray(payload.events) ? payload.events : []
        };
      })
      .catch((error) => {
        staticPublicMarketplaceProfilesPromise = null;
        throw error;
      });
  }
  return staticPublicMarketplaceProfilesPromise;
};

const eventTypeOptions = ["Tournament", "Social event", "Coaching clinic", "Beginner open day", "Junior program", "Club night", "Centre promotion"];
const eventOwnerOptions = [
  ["organiser", "Social organiser"],
  ["centre", "Centre owner"]
];

const eventOwnerRoleLabel = (ownerType) => ownerType === "centre" ? "Centre owner" : "Social organiser";
const makeEventId = () => `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getEventVenue = (event = {}) => event.venueId ? venues.find((venue) => venue.id === event.venueId) : null;
const getEventCity = (event = {}) => event.city || getEventVenue(event)?.city || "Melbourne";
const getEventSuburb = (event = {}) => event.suburb || getEventVenue(event)?.suburb || "";
const getEventVenueLabel = (event = {}) => event.venue || getEventVenue(event)?.name || "Venue to be confirmed";
const getEventOwnerLabel = (event = {}) => event.ownerName || event.organiserName || event.centreName || eventOwnerRoleLabel(event.ownerType);
const participantMatchesAccount = (participant = {}, accountProfile = {}) => {
  const participantEmail = String(participant.email || "").trim().toLowerCase();
  const accountEmail = String(accountProfile.email || "").trim().toLowerCase();
  const participantName = String(participant.name || "").trim().toLowerCase();
  const accountName = String(accountProfile.fullName || "").trim().toLowerCase();
  return (participant.ownerGenerated === true || participant.selfJoinedAsOwner === true)
    || (participantEmail && accountEmail && participantEmail === accountEmail)
    || (participantName && accountName && participantName === accountName);
};
const getPublicGroupMembers = (group = {}, accountProfile = {}) => {
  const members = Array.isArray(group.publicMembers) ? group.publicMembers : [];
  return members.filter((member) => !participantMatchesAccount(member, accountProfile));
};
const getPublicEventAttendees = (event = {}, accountProfile = {}) => {
  const attendees = Array.isArray(event.attendees) ? event.attendees : [];
  return attendees.filter((attendee) => !participantMatchesAccount(attendee, accountProfile));
};
const getEventCapacityLabel = (event = {}, accountProfile = {}) => {
  const capacity = Number.parseInt(event.capacity, 10);
  const attendeeCount = getPublicEventAttendees(event, accountProfile).length;
  if (!capacity) return attendeeCount ? `${attendeeCount} attending` : "Capacity pending";
  return `${attendeeCount}/${capacity} attending`;
};

const getEventImage = (event = {}) => {
  const venue = getEventVenue(event);
  return event.coverUrl || venue?.image || venue?.gallery?.[0] || "";
};

const eventTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value === "object" && Number.isFinite(value.seconds)) return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getEventEndMillis = (event = {}) => {
  const explicitEnd = eventTimestampMillis(event.endAt);
  if (explicitEnd) return explicitEnd;
  const date = String(event.date || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 0;
  const time = /^\d{2}:\d{2}$/.test(String(event.endTime || "")) ? event.endTime : "23:59";
  return eventTimestampMillis(`${date}T${time}:59`);
};

const isEventPast = (event = {}, now = Date.now()) => {
  const endMillis = getEventEndMillis(event);
  return Boolean(endMillis && endMillis <= now);
};

const isPublicUpcomingEvent = (event = {}, now = Date.now()) => (
  ["published", "active"].includes(String(event.status || "published").toLowerCase())
  && event.public !== false
  && !isEventPast(event, now)
);

const normaliseEvent = (event = {}, index = 0) => {
  const venue = getEventVenue(event);
  const ownerType = event.ownerType === "centre" ? "centre" : "organiser";
  const eventLocation = locationSnapshot(event.location || locationFromVenue(venue));
  const countryCode = normaliseCountryCode(event.countryCode || event.country || eventLocation?.countryCode || venue?.countryCode || venue?.country, event.city || eventLocation?.city || venue?.city);
  const currency = normaliseCurrencyCode(event.currency || marketplaceCurrencyForCountry(countryCode));
  const rawEventPrice = event.price ?? event.ticketPrice ?? "";
  const pricingMode = event.pricingMode === "free" || String(rawEventPrice).trim() === "0" ? "free" : event.pricingMode === "paid" || String(rawEventPrice).trim() ? "paid" : "";
  return {
    ...event,
    id: event.id || `event-${index}`,
    title: event.title || "Untitled badminton event",
    ownerType,
    ownerName: event.ownerName || event.organiserName || event.centreName || eventOwnerRoleLabel(ownerType),
    eventType: event.eventType || "",
    venue: event.venue || venue?.name || "",
    venueId: event.venueId || "",
    city: event.city || eventLocation?.city || venue?.city || "Melbourne",
    suburb: event.suburb || eventLocation?.suburb || venue?.suburb || "",
    countryCode,
    currency,
    location: eventLocation,
    date: event.date || "",
    time: event.time || "",
    endTime: event.endTime || "",
    capacity: event.capacity || "",
    price: pricingMode === "free" ? "0" : rawEventPrice,
    pricingMode,
    skillBand: event.skillBand || "",
    status: event.status || "published",
    description: event.description || "",
    requirements: event.requirements || "",
    refundPolicy: event.refundPolicy || "",
    coverUrl: event.coverUrl || "",
    inviteEmails: cleanEmailTags(event.inviteEmails || event.invites),
    socialLinks: cleanLinkRows(event.socialLinks, "Event link"),
    attendees: Array.isArray(event.attendees) ? event.attendees : []
  };
};

const getAccountEvents = (accountProfile = {}) => (Array.isArray(accountProfile.events) ? accountProfile.events : [])
  .map(normaliseEvent)
  .sort((first, second) => `${first.date}T${first.time}`.localeCompare(`${second.date}T${second.time}`));

const getPublishedAccountEvents = (accountProfile = {}) => getAccountEvents(accountProfile).filter((event) => isPublicUpcomingEvent(event));
const normaliseJoinedEvent = (event = {}, index = 0) => ({
  ...normaliseEvent(event, index),
  joinedAt: event.joinedAt || "",
  joinedStatus: event.joinedStatus || "Registered",
  paymentStatus: event.paymentStatus || (getEventPricingMode(event) === "free" ? "Free registration" : "Payment pending"),
  ticketCount: event.ticketCount || 1
});
const getJoinedEvents = (accountProfile = {}) => (Array.isArray(accountProfile.joinedEvents) ? accountProfile.joinedEvents : [])
  .map(normaliseJoinedEvent)
  .sort((first, second) => `${first.date}T${first.time}`.localeCompare(`${second.date}T${second.time}`));
const eventPriceNumber = (event = {}) => Number.parseFloat(String(event.price || "").replace(/[^0-9.]/g, "")) || 0;
const getEventPricingMode = (event = {}) => event.pricingMode === "free" || String(event.price ?? event.ticketPrice ?? "").trim() === "0"
  ? "free"
  : event.pricingMode === "paid" || String(event.price ?? event.ticketPrice ?? "").trim()
    ? "paid"
    : "";
const hasEventTicketPrice = (event = {}) => getEventPricingMode(event) === "free" || (getEventPricingMode(event) === "paid" && eventPriceNumber(event) > 0);
const eventUsesOfflineSettlement = (event = {}) => (
  String(event.paymentCollectionMode || event.settlementMode || event.checkoutMode || "").toLowerCase() === "offline_settlement"
);
const getEventTicketPriceLabel = (event = {}) => getEventPricingMode(event) === "free"
  ? "Free"
  : getEventPricingMode(event) === "paid" && eventPriceNumber(event) > 0
    ? formatMoney(eventPriceNumber(event), event.currency || marketplaceCurrencyForLocation(event.location))
    : "Pending";
const accountHasRole = (accountProfile = {}, role) => Array.isArray(accountProfile.roles) && accountProfile.roles.includes(role);
const hasStartedRoleProfile = (accountProfile = {}, role) => accountHasRole(accountProfile, role) || roleProfileStarted(accountProfile, role);
const accountThread = ({ id, name, type, status = "Open", time = "Status", unread = 0, preview, meta = [], context = [], actions = [], messages = [], ...extra }) => ({
  ...extra,
  id,
  name,
  type,
  status,
  time,
  unread,
  preview,
  meta: meta.filter(Boolean),
  context: context.filter(([label, value]) => label && value),
  actions,
  messages
});
const isMarketplaceConversationThread = (thread = {}) => Boolean(thread.backendThread || thread.conversationThread === true);
const storedMessagePairs = (rows, valueKey = "value") => (Array.isArray(rows) ? rows : [])
  .map((row) => {
    if (Array.isArray(row)) return row;
    if (!row || typeof row !== "object") return [];
    return [row.label, row[valueKey], row.payload];
  })
  .filter((row) => row[0] && row[1]);
const getStoredMessages = (accountProfile = {}) => (Array.isArray(accountProfile.messages) ? accountProfile.messages : [])
  .filter((message) => (
    message.threadId
    && message.text
    && !message.notificationId
    && !String(message.id || "").startsWith("notification-")
  ))
  .map((message, index) => ({
    id: message.id || `message-${index}`,
    roleId: message.roleId || "player",
    threadId: message.threadId,
    threadName: message.threadName || "",
    threadType: message.threadType || "",
    status: message.status || "Open",
    meta: Array.isArray(message.meta) ? message.meta.filter(Boolean) : [],
    context: storedMessagePairs(message.context),
    actions: storedMessagePairs(message.actions, "target"),
    from: message.from || "me",
    text: String(message.text || ""),
    time: message.time || "Now",
    createdAt: message.createdAt || new Date().toISOString()
  }));
const getAccountPayments = (accountProfile = {}) => (Array.isArray(accountProfile.payments) ? accountProfile.payments : [])
  .map((payment, index) => ({
    ...payment,
    id: payment.id || `payment-${index}`,
    kind: payment.kind || "general",
    title: payment.title || payment.metadata?.title || paymentKindLabels[payment.kind] || "Payment",
    amount: payment.amount || (Number.isFinite(Number(payment.amountCents)) ? formatCurrencyCents(payment.amountCents, payment.currency) : "Pending"),
    subject: payment.subject || payment.metadata?.subject || payment.metadata?.sourceTitle || "",
    status: payment.status || "Recorded",
    createdAt: payment.createdAt || ""
  }))
  .sort((first, second) => `${second.createdAt || ""}`.localeCompare(`${first.createdAt || ""}`));

const formatCurrencyCents = (cents, currency = "aud") => {
  return formatMoneyFromMinorUnits(cents, currency);
};

const membershipProductTypeLabels = {
  credit_pack: "Court credit pack",
  membership: "Membership",
  off_peak_pass: "Off-peak pass",
  junior_pass: "Junior pass",
  coaching_bundle: "Coaching bundle"
};

const membershipBillingLabels = {
  one_time: "One-time purchase",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly"
};

const getAccountMembershipPurchases = (accountProfile = {}) => (Array.isArray(accountProfile.membershipPurchases) ? accountProfile.membershipPurchases : [])
  .map((purchase, index) => ({
    ...purchase,
    id: purchase.id || purchase.membershipPurchaseId || purchase.paymentId || `membership-purchase-${index}`,
    title: purchase.title || membershipProductTypeLabels[purchase.productType] || "Membership or credit pack",
    status: purchase.status || "checkout_created",
    remainingCreditHours: Math.max(0, Number(purchase.remainingCreditHours || 0)),
    remainingVisitCredits: Math.max(0, Number(purchase.remainingVisitCredits || 0)),
    amount: purchase.amount || formatCurrencyCents(purchase.amountCents, purchase.currency),
    centreId: purchase.centreId || "",
    expiresAt: purchase.expiresAt || "",
    createdAt: purchase.activatedAt || purchase.createdAt || purchase.updatedAt || ""
  }))
  .sort((first, second) => `${second.createdAt || ""}`.localeCompare(`${first.createdAt || ""}`));

const groupMembershipAccessStatuses = new Set(["active", "paid", "confirmed", "partially_refunded", "disputed", "dispute_won"]);
const groupMembershipPendingStatuses = new Set(["pending_payment", "requires_payment", "checkout_created", "payment_failed", "failed"]);
const groupMembershipId = (group = {}) => String(
  group.firestoreSourceId
  || group.serverGroupId
  || group.publicGroupId
  || group.groupId
  || group.id
  || ""
).trim();
const getGroupMembershipPurchases = (accountProfile = {}) => getAccountMembershipPurchases(accountProfile)
  .filter((purchase) => String(purchase.membershipScope || "").toLowerCase() === "group" || Boolean(purchase.groupId));
const getViewerGroupMembership = (accountProfile = {}, group = {}) => {
  const targetId = groupMembershipId(group);
  if (!targetId) return null;
  return getGroupMembershipPurchases(accountProfile)
    .filter((purchase) => String(purchase.groupId || "").trim() === targetId)
    .sort((first, second) => {
      const statusRank = (purchase) => groupMembershipAccessStatuses.has(String(purchase.status || "").toLowerCase())
        ? 0
        : groupMembershipPendingStatuses.has(String(purchase.status || purchase.paymentStatus || "").toLowerCase())
          ? 1
          : 2;
      return statusRank(first) - statusRank(second)
        || String(second.createdAt || "").localeCompare(String(first.createdAt || ""));
    })[0] || null;
};
const viewerHasActiveGroupMembership = (accountProfile = {}, group = {}) => groupMembershipAccessStatuses.has(
  String(getViewerGroupMembership(accountProfile, group)?.status || "").toLowerCase()
);
const groupMembershipMode = (group = {}) => group.membershipEnabled === false
  ? "closed"
  : String(group.membershipMode || "free").toLowerCase() === "paid"
    ? "paid"
    : "free";
const groupMembershipPriceLabel = (group = {}) => {
  const mode = groupMembershipMode(group);
  if (mode === "closed") return "Membership closed";
  if (mode === "free") return "Free to join";
  const currency = normaliseCurrencyCode(group.currency || marketplaceCurrencyForLocation(group.location));
  const fee = Number(group.membershipFee || minorUnitsToAmount(group.membershipFeeCents || 0, currency)) || 0;
  if (!fee) return "Fee pending";
  const billing = membershipBillingLabels[group.membershipBillingInterval || "one_time"] || "One-time";
  return `${formatMoney(fee, currency)}${billing === "One-time purchase" ? " once" : ` ${billing.toLowerCase()}`}`;
};
const getGroupMemberCount = (group = {}, accountProfile = {}) => Math.max(
  0,
  Number(group.activeMemberCount || group.memberCount || 0),
  getPublicGroupMembers(group, accountProfile).length
);

const membershipWalletSummary = (accountProfile = {}) => {
  const purchases = getAccountMembershipPurchases(accountProfile)
    .filter((purchase) => String(purchase.membershipScope || "").toLowerCase() !== "group" && !purchase.groupId);
  const active = purchases.filter((purchase) => purchase.status === "active");
  const courtHours = active.reduce((total, purchase) => total + purchase.remainingCreditHours, 0);
  const visits = active.reduce((total, purchase) => total + purchase.remainingVisitCredits, 0);
  return {
    purchases,
    active,
    courtHours,
    visits,
    label: courtHours
      ? `${courtHours} court hour${courtHours === 1 ? "" : "s"}`
      : visits
        ? `${visits} visit credit${visits === 1 ? "" : "s"}`
        : active.length
          ? `${active.length} active pass${active.length === 1 ? "" : "es"}`
          : "No active credits"
  };
};

const normaliseMembershipProductType = (value = "credit_pack") => {
  const key = slugifyPath(value).replace(/-/g, "_");
  return membershipProductTypeLabels[key] ? key : "credit_pack";
};

const membershipProductPriceNumber = (value) => Number(String(value || "").replace(/[^0-9.]/g, "")) || 0;

const normaliseMembershipProduct = (product = {}, index = 0) => {
  const productType = normaliseMembershipProductType(product.productType);
  const currency = normaliseCurrencyCode(product.currency || "aud");
  const price = product.price || (product.priceCents ? String(minorUnitsToAmount(product.priceCents, currency)) : "");
  return {
    ...product,
    id: product.id || product.membershipId || product.serverMembershipId || `membership-product-${index}`,
    serverMembershipId: product.serverMembershipId || product.membershipId || "",
    title: product.title || product.name || membershipProductTypeLabels[productType],
    productType,
    billingInterval: product.billingInterval || (productType === "membership" ? "monthly" : "one_time"),
    price,
    priceCents: product.priceCents || amountToMinorUnits(membershipProductPriceNumber(price), currency),
    currency,
    creditHours: product.creditHours || product.includedCourtHours || "",
    visitCredits: product.visitCredits || "",
    expiryDays: product.expiryDays || "",
    status: product.status || "draft",
    redemptionRules: product.redemptionRules || "",
    cancellationPolicy: product.cancellationPolicy || "",
    createdAt: product.createdAt || "",
    updatedAt: product.updatedAt || ""
  };
};

const getCentreMembershipProducts = (centre = {}) => (Array.isArray(centre.membershipProducts) ? centre.membershipProducts : [])
  .map(normaliseMembershipProduct)
  .sort((first, second) => {
    const statusRank = { published: 0, draft: 1, archived: 2 };
    return (statusRank[first.status] ?? 9) - (statusRank[second.status] ?? 9)
      || String(second.updatedAt || second.createdAt || "").localeCompare(String(first.updatedAt || first.createdAt || ""));
  });

const blankMembershipProduct = {
  id: "",
  serverMembershipId: "",
  title: "",
  productType: "credit_pack",
  billingInterval: "one_time",
  price: "",
  creditHours: "",
  visitCredits: "",
  expiryDays: "90",
  status: "draft",
  redemptionRules: "",
  cancellationPolicy: ""
};

const membershipPurchaseExpiryDate = (purchase = {}) => {
  const explicit = marketplaceDateFromValue(purchase.expiresAt);
  if (explicit) return explicit;
  const activated = marketplaceDateFromValue(purchase.activatedAt || purchase.createdAt);
  const expiryDays = Math.max(0, Number.parseInt(purchase.expiryDays || 0, 10) || 0);
  return activated && expiryDays ? new Date(activated.getTime() + expiryDays * 24 * 60 * 60 * 1000) : null;
};

const activeMembershipPurchasesForCentre = (accountProfile = {}, centreId = "") => getAccountMembershipPurchases(accountProfile)
  .filter((purchase) => {
    if (String(purchase.status || "").toLowerCase() !== "active") return false;
    if (centreId && String(purchase.centreId || "") !== String(centreId)) return false;
    const expiresAt = membershipPurchaseExpiryDate(purchase);
    return !expiresAt || expiresAt.getTime() > Date.now();
  });

const membershipBalanceLabel = (purchase = {}) => {
  const hours = Math.max(0, Number(purchase.remainingCreditHours || 0));
  const visits = Math.max(0, Number(purchase.remainingVisitCredits || 0));
  if (hours) return `${hours} court hour${hours === 1 ? "" : "s"} left`;
  if (visits) return `${visits} visit credit${visits === 1 ? "" : "s"} left`;
  return "No court credits left";
};

const mergeMembershipProducts = (...collections) => {
  const products = new globalThis.Map();
  collections.flat().filter(Boolean).forEach((product, index) => {
    const normalised = normaliseMembershipProduct(product, index);
    const key = normalised.serverMembershipId || normalised.membershipId || normalised.id;
    products.set(key, { ...(products.get(key) || {}), ...normalised });
  });
  return [...products.values()].filter((product) => product.status === "published");
};

function usePublicCentreMembershipProducts(centreId = "", seedProducts = []) {
  const seedKey = seedProducts.map((product) => `${product.serverMembershipId || product.membershipId || product.id}:${product.status}:${product.updatedAt || ""}`).join("|");
  const [state, setState] = useState(() => ({
    status: "idle",
    message: "",
    products: mergeMembershipProducts(seedProducts)
  }));

  useEffect(() => {
    let cancelled = false;
    const seeds = mergeMembershipProducts(seedProducts);
    if (!centreId || !firebaseConfigured) {
      setState({ status: "ready", message: "", products: seeds });
      return () => { cancelled = true; };
    }
    setState({ status: "loading", message: "Loading centre passes.", products: seeds });
    loadFirebaseRuntime().then(() => getDocs(query(
      collection(firebaseDb, "memberships"),
      where("centreId", "==", centreId),
      where("status", "==", "published"),
      limit(24)
    ))).then((snapshot) => {
      if (cancelled) return;
      const remoteProducts = snapshot.docs.map((item) => ({ id: item.id, ...fromFirestoreProfileValue(item.data()) }));
      setState({ status: "ready", message: "", products: mergeMembershipProducts(seeds, remoteProducts) });
    }).catch(() => {
      if (cancelled) return;
      setState({
        status: "error",
        message: seeds.length ? "Showing the centre's last published pass details." : "Passes could not be loaded right now.",
        products: seeds
      });
    });
    return () => { cancelled = true; };
  }, [centreId, seedKey]);

  return state;
}

function PublicCentreMemberships({ centreId, centreName, seedProducts = [], viewerProfile = defaultAccountProfile, ownerPreview = false, navigate }) {
  const { products, status, message } = usePublicCentreMembershipProducts(centreId, seedProducts);
  const [checkoutState, setCheckoutState] = useState({ productId: "", message: "" });
  const resumedCheckoutRef = useRef(false);
  const activePurchases = activeMembershipPurchasesForCentre(viewerProfile, centreId);
  const purchaseProduct = async (product) => {
    const sourceId = product.serverMembershipId || product.membershipId || product.id;
    if (ownerPreview) {
      navigate("centrePayments");
      return;
    }
    if (!firebaseAuth?.currentUser) {
      const returnPath = `${window.location.pathname}?membership_product=${encodeURIComponent(sourceId)}`;
      saveAuthReturnIntent({ path: returnPath, action: "centre_membership_checkout" });
      navigate("login");
      return;
    }
    if (!sourceId || String(sourceId).startsWith("membership-product-")) {
      setCheckoutState({ productId: "", message: "This pass is not published for checkout yet. Ask the centre to publish it again." });
      return;
    }
    setCheckoutState({ productId: sourceId, message: "" });
    try {
      const checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
        kind: "membership",
        sourceId,
        idempotencyKey: stripeReferenceId("membership", `${sourceId}-${firebaseAuth.currentUser.uid}`),
        successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href
      });
      if (!checkout.checkoutUrl) throw new Error("Checkout URL was not returned.");
      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setCheckoutState({ productId: "", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  useEffect(() => {
    if (resumedCheckoutRef.current || ownerPreview || !firebaseAuth?.currentUser || !products.length) return;
    const requestedProductId = new URLSearchParams(window.location.search).get("membership_product") || "";
    if (!requestedProductId) return;
    const product = products.find((item) => (item.serverMembershipId || item.membershipId || item.id) === requestedProductId);
    resumedCheckoutRef.current = true;
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("membership_product");
    window.history.replaceState(window.history.state, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    if (product) purchaseProduct(product);
    else setCheckoutState({ productId: "", message: "That membership is no longer available. Choose another centre pass." });
  }, [ownerPreview, products, viewerProfile.uid]);

  return (
    <PublicProfileSection eyebrow="Memberships and passes" title="Save on repeat court bookings">
      {activePurchases.length > 0 && (
        <div className="public-membership-wallet">
          <WalletCards size={20} />
          <div>
            <strong>Your active centre credit</strong>
            <span>{activePurchases.map(membershipBalanceLabel).join(" - ")}</span>
          </div>
        </div>
      )}
      {products.length > 0 ? (
        <div className="public-membership-products">
          {products.map((product) => {
            const sourceId = product.serverMembershipId || product.membershipId || product.id;
            const inclusions = [
              Number(product.creditHours) > 0 ? `${product.creditHours} court hours` : "",
              Number(product.visitCredits) > 0 ? `${product.visitCredits} visit credits` : "",
              Number(product.expiryDays) > 0 ? `Use within ${product.expiryDays} days` : ""
            ].filter(Boolean);
            return (
              <article key={sourceId}>
                <div className="public-membership-product-head">
                  <span>{membershipProductTypeLabels[product.productType] || "Centre pass"}</span>
                  <strong>{formatCurrencyCents(product.priceCents, product.currency)}</strong>
                </div>
                <h3>{product.title}</h3>
                <p>{membershipBillingLabels[product.billingInterval] || "One-time purchase"}</p>
                {inclusions.length > 0 && <ul>{inclusions.map((item) => <li key={item}><Check size={15} /> {item}</li>)}</ul>}
                {product.redemptionRules && <small>{product.redemptionRules}</small>}
                <button type="button" onClick={() => purchaseProduct(product)} disabled={checkoutState.productId === sourceId}>
                  {checkoutState.productId === sourceId ? <><Loader2 size={16} className="spin" /> Opening checkout</> : ownerPreview ? "Manage passes" : "Buy with Badminta"}
                </button>
              </article>
            );
          })}
        </div>
      ) : status === "loading" ? (
        <div className="public-membership-loading"><Loader2 size={18} className="spin" /> Loading centre passes</div>
      ) : (
        <div className="public-empty-panel">
          <WalletCards size={20} />
          <strong>{ownerPreview ? "No passes published yet" : "This centre has not published a pass yet"}</strong>
          <span>{ownerPreview ? "Create a court credit pack or recurring membership to bring players back more often." : `Court credits and memberships from ${centreName || "this centre"} will appear here when the owner publishes them.`}</span>
          {ownerPreview && <button type="button" onClick={() => navigate("centrePayments")}>Create a pass</button>}
        </div>
      )}
      {(checkoutState.message || message) && <div className="auth-message" aria-live="polite">{checkoutState.message || message}</div>}
    </PublicProfileSection>
  );
}

const mergeStoredMessagesIntoInboxes = (inboxes = [], accountProfile = {}) => {
  const storedMessages = getStoredMessages(accountProfile);
  if (!storedMessages.length) return inboxes;
  return inboxes.map((inbox) => ({
    ...inbox,
    threads: (() => {
      const existingThreadIds = new Set(inbox.threads.map((thread) => thread.id));
      const mergedThreads = inbox.threads.map((thread) => {
        const additions = storedMessages
          .filter((message) => message.roleId === inbox.id && message.threadId === thread.id)
          .sort((first, second) => `${first.createdAt}`.localeCompare(`${second.createdAt}`))
          .map((message) => [message.from, message.text, message.time]);
        if (!additions.length) return thread;
        const latest = additions[additions.length - 1];
        return {
          ...thread,
          preview: latest[1],
          time: latest[2],
          messages: [...thread.messages, ...additions]
        };
      });
      const createdThreads = Array.from(new Set(storedMessages
        .filter((message) => message.roleId === inbox.id && !existingThreadIds.has(message.threadId))
        .map((message) => message.threadId)))
        .map((threadId) => {
          const messages = storedMessages
            .filter((message) => message.roleId === inbox.id && message.threadId === threadId)
            .sort((first, second) => `${first.createdAt}`.localeCompare(`${second.createdAt}`));
          const first = messages[0] || {};
          const latest = messages[messages.length - 1] || first;
          return accountThread({
            id: threadId,
            name: first.threadName || first.threadType || "Public enquiry",
            type: first.threadType || "Public enquiry",
            status: first.status || "Open",
            unread: 0,
            preview: latest.text || "Conversation started from a public page.",
            meta: first.meta || [],
            context: first.context || [],
            actions: first.actions?.length ? first.actions : [[inbox.cta || "Open workspace", inbox.ctaTarget || "messages"], ["Messages", "messages"]],
            time: latest.time || "Now",
            messages: messages.map((message) => [message.from, message.text, message.time]),
            conversationThread: true
          });
        })
        .sort((first, second) => `${second.time || ""}`.localeCompare(`${first.time || ""}`));
      return [...createdThreads, ...mergedThreads];
    })()
  }));
};

const publicMessageThreadId = (roleId, subject, threadType = "public-enquiry") => (
  `public-${roleId}-${slugifyPath(subject || threadType)}-${shortStableHash(`${roleId}|${subject || ""}|${threadType}`)}`
);

const messageSenderRoleForAccount = (accountProfile = {}, preferredRole = "") => {
  const selectedRoleIds = accountRoleOptions
    .filter((role) => accountProfile.roles?.includes(role.id) || roleProfileStarted(accountProfile, role.id))
    .map((role) => role.id);
  if (preferredRole && selectedRoleIds.includes(preferredRole)) return preferredRole;
  if (selectedRoleIds.includes("player")) return "player";
  return selectedRoleIds[0] || "player";
};

const openPublicMessageThread = async ({
  accountProfile = {},
  saveAccountProfile,
  navigate,
  roleId = "player",
  senderRoleId = "",
  targetCollection = "",
  targetId = "",
  subject = "Public enquiry",
  threadType = "Public enquiry",
  text = "Conversation started from a public page.",
  status = "Open",
  meta = [],
  context = [],
  actions = []
}) => {
  const senderRole = messageSenderRoleForAccount(accountProfile, senderRoleId);
  if (firebaseFunctions && firebaseAuth?.currentUser && targetCollection && targetId) {
    try {
      const conversation = await callMarketplaceFunction("startMarketplaceConversation", {
        targetCollection,
        targetId,
        senderRole,
        subject,
        threadType,
        openingText: text,
        meta: meta.filter(Boolean),
        context: context.filter((row) => Array.isArray(row) && row[0] && row[1])
      });
      navigate("messages", {
        messageFocus: {
          roleId: conversation.currentRole || senderRole,
          threadId: conversation.conversationId,
          status: conversation.created ? "Conversation ready" : "Conversation reopened"
        }
      });
      return;
    } catch (error) {
      navigate("messages", {
        messageFocus: {
          roleId: senderRole,
          threadId: "",
          error: marketplaceFunctionErrorMessage(error)
        }
      });
      return;
    }
  }

  const threadId = publicMessageThreadId(senderRole, subject, threadType);
  const storedMessages = getStoredMessages(accountProfile);
  const hasThread = storedMessages.some((message) => message.roleId === senderRole && message.threadId === threadId);
  if (!hasThread && saveAccountProfile) {
    const createdAt = new Date().toISOString();
    saveAccountProfile({
      messages: [{
        id: `message-${Date.now()}`,
        roleId: senderRole,
        targetRole: roleId,
        threadId,
        threadName: subject,
        threadType,
        status,
        meta: meta.filter(Boolean),
        context: context.filter((row) => Array.isArray(row) && row[0] && row[1]),
        actions,
        recordType: "conversation",
        from: "system",
        text,
        time: "Now",
        createdAt
      }, ...storedMessages]
    });
  }
  navigate("messages", { messageFocus: { roleId: senderRole, threadId } });
};

const setupMessagesForMissing = (missing = [], roleLabel = "profile") => {
  if (!missing.length) return [["system", `Your ${roleLabel} has the details people need before they can take action.`, "Ready"]];
  return [["system", `Finish ${missing.slice(0, 4).map((item) => missingFieldMeta(item).title.toLowerCase()).join(", ")}${missing.length > 4 ? ` and ${missing.length - 4} more item${missing.length - 4 === 1 ? "" : "s"}` : ""} before this ${roleLabel} can work properly for users.`, "Next steps"]];
};
const buildAccountInboxData = (accountProfile = {}) => {
  const name = accountProfile.fullName || "Account member";
  const player = accountProfile.player || {};
  const group = accountProfile.group || {};
  const centre = accountProfile.centre || {};
  const coach = accountProfile.coach || {};
  const joinedEvents = getJoinedEvents(accountProfile);
  const joinedGroups = getJoinedGroups(accountProfile);
  const courtBookings = getAccountBookings(accountProfile);
  const payments = getAccountPayments(accountProfile);
  const organiserEvents = getOwnerEvents(accountProfile, "organiser");
  const centreEvents = getOwnerEvents(accountProfile, "centre");
  const reviews = Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [];
  const groupInvites = cleanEmailTags(group.inviteEmails?.length ? group.inviteEmails : group.invites);
  const nextGroupOccurrence = getGroupPublishedDatedOccurrences(group)[0] || null;
  const nextGroupDateLabel = nextGroupOccurrence?.dateLabel || "";
  const nextGroupTimeLabel = nextGroupOccurrence?.time || "";

  const playerThreads = [];
  if (hasStartedRoleProfile(accountProfile, "player")) {
    const missing = getRoleOnboardingMissingFields(accountProfile, "player");
    playerThreads.push(accountThread({
      id: "player-profile-status",
      name: "Player profile status",
      type: "Profile setup",
      status: missing.length ? "Needs details" : "Ready",
      unread: missing.length ? 1 : 0,
      preview: missing.length ? `${missing.length} detail${missing.length === 1 ? "" : "s"} left before organisers can rely on this profile.` : "Your player profile is ready for invites.",
      meta: [player.skillBand, player.suburb, hasProfileAvailability(player) ? "Availability added" : "Availability missing"],
      context: [["Player", name], ["Skill", player.skillBand || "Missing"], ["Availability", hasProfileAvailability(player) ? "Added" : "Missing"]],
      actions: [["Edit player profile", "profileSetup"], ["View public profile", "publicPlayerProfile"], ["Ratings", "ratings"]],
      time: "Profile status",
      messages: setupMessagesForMissing(missing, "player profile")
    }));
  }
  joinedEvents.forEach((event) => {
    playerThreads.push(accountThread({
      id: `joined-event-${event.id}`,
      name: event.title,
      type: "Joined event",
      status: event.paymentStatus || event.joinedStatus,
      preview: `${event.joinedStatus} for ${formatDateLabel(event.date)} at ${formatTimeLabel(event.time)}.`,
      meta: [formatDateLabel(event.date), event.venue, event.paymentStatus],
      context: [["Event", event.title], ["Ticket", `${event.ticketCount || 1}`], ["Payment", event.paymentStatus]],
      actions: [["View joined events", "joinedEvents"], ["Open event", "event", { event }], ["Join flow", "eventJoin", { event, bookingType: "event" }]],
      time: event.joinedAt ? formatDateLabel(event.joinedAt.slice(0, 10)) : formatDateLabel(event.date),
      messages: [["system", `You joined ${event.title}. Status: ${event.paymentStatus || event.joinedStatus}.`, event.joinedAt ? formatDateLabel(event.joinedAt.slice(0, 10)) : formatDateLabel(event.date)]]
    }));
  });
  joinedGroups.forEach((joinedGroup) => {
    playerThreads.push(accountThread({
      id: `joined-group-${joinedGroup.id}`,
      name: joinedGroup.title,
      type: "Joined social group",
      status: joinedGroup.paymentStatus || joinedGroup.joinedStatus,
      preview: `${joinedGroup.joinedStatus} for ${getGroupWhenLabel(joinedGroup)} at ${joinedGroup.venue}.`,
      meta: [joinedGroup.venue, getGroupWhenLabel(joinedGroup), joinedGroup.paymentStatus],
      context: [["Group", joinedGroup.title], ["Venue", joinedGroup.venue], ["Payment", joinedGroup.paymentStatus]],
      actions: [["Join flow", "join", { group: joinedGroup, bookingType: "social" }], ["Find groups", "groupsIndex"], ["Saved items", "saved"]],
      time: joinedGroup.joinedAt ? formatDateLabel(joinedGroup.joinedAt.slice(0, 10)) : "Registration",
      messages: [["system", `You joined ${joinedGroup.title}. Status: ${joinedGroup.paymentStatus || joinedGroup.joinedStatus}.`, joinedGroup.joinedAt ? formatDateLabel(joinedGroup.joinedAt.slice(0, 10)) : "Registration"]]
    }));
  });
  courtBookings.forEach((booking) => {
    playerThreads.push(accountThread({
      id: `court-booking-${booking.id}`,
      name: booking.venueName,
      type: "Court booking",
      status: booking.paymentStatus || booking.status,
      preview: `${booking.status} for ${formatDateLabel(booking.date)} at ${formatTimeLabel(booking.time)}.`,
      meta: [booking.venueName, `${booking.durationHours}h`, getBookingTotalLabel(booking) || booking.paymentStatus],
      context: [["Centre", booking.venueName], ["Time", `${formatDateLabel(booking.date)}, ${formatTimeLabel(booking.time)}`], ["Payment", booking.paymentStatus]],
      actions: [["View booking", "bookings"], ["Find centres", "results"]],
      time: booking.bookedAt ? formatDateLabel(booking.bookedAt.slice(0, 10)) : formatDateLabel(booking.date),
      messages: [["system", `Court booking requested at ${booking.venueName}. Payment status: ${booking.paymentStatus}.`, booking.bookedAt ? formatDateLabel(booking.bookedAt.slice(0, 10)) : formatDateLabel(booking.date)]]
    }));
  });
  payments.filter((payment) => ["booking", "social", "event", "coach", "centre", "general"].includes(payment.kind)).slice(0, 6).forEach((payment) => {
    playerThreads.push(accountThread({
      id: `payment-${payment.id}`,
      name: payment.title,
      type: "Payment record",
      status: payment.status,
      preview: `${payment.amount} recorded for ${payment.subject || payment.title}.`,
      meta: [payment.amount, payment.subject, payment.status],
      context: [["Payment", payment.title], ["Amount", payment.amount], ["Subject", payment.subject || "Account"]],
      actions: [["Saved and bookings", "saved"], ["Messages", "messages"]],
      time: payment.createdAt ? formatDateLabel(payment.createdAt.slice(0, 10)) : "Payment record",
      messages: [["system", `${payment.title} was recorded in your account history.`, payment.createdAt ? formatDateLabel(payment.createdAt.slice(0, 10)) : "Payment record"]]
    }));
  });
  reviews.filter((review) => ["player", "recommendation"].includes(review.kind)).slice(0, 4).forEach((review) => {
    playerThreads.push(accountThread({
      id: `player-review-${review.id}`,
      name: review.title || reviewKindLabels[review.kind],
      type: "Review record",
      status: "Saved",
      preview: review.comment || "A player review or recommendation was saved.",
      meta: [review.subject, review.score ? `${review.score}/5` : "No score"],
      context: [["Subject", review.subject], ["Score", review.score ? `${review.score}/5` : "Not scored"]],
      actions: [["Open ratings", "ratings"], ["View public profile", "publicPlayerProfile"]],
      time: review.createdAt ? formatDateLabel(review.createdAt.slice(0, 10)) : "Review record",
      messages: [["system", `A ${reviewKindLabels[review.kind].toLowerCase()} record was saved for ${review.subject}.`, review.createdAt ? formatDateLabel(review.createdAt.slice(0, 10)) : "Review record"]]
    }));
  });

  const organiserThreads = [];
  if (hasStartedRoleProfile(accountProfile, "organiser")) {
    const missing = getRoleOnboardingMissingFields(accountProfile, "organiser");
    const organiserNeedsDatedSession = !missing.length && !nextGroupOccurrence;
    organiserThreads.push(accountThread({
      id: "organiser-group-status",
      name: group.name || "Social group setup",
      type: "Group setup",
      status: missing.length ? "Needs details" : organiserNeedsDatedSession ? "Session needed" : "Ready",
      unread: missing.length || organiserNeedsDatedSession ? 1 : 0,
      preview: missing.length
        ? `${missing.length} group detail${missing.length === 1 ? "" : "s"} left before players can join confidently.`
        : organiserNeedsDatedSession
          ? "Your group profile is live. Publish a dated session before players can reserve a spot or pay."
          : "Your group page and next session are ready for players to join.",
      meta: [group.venue, nextGroupDateLabel || "No dated session", getGroupPriceLabel(group)],
      context: [["Venue", group.venue || "Missing"], ["Next dated session", [nextGroupDateLabel, nextGroupTimeLabel].filter(Boolean).join(" at ") || "Not published"], ["Regular schedule", hasGroupPlayingSchedule(group) ? getGroupScheduleLabel(group) : "Not scheduled"], ["Capacity", group.capacity || "Missing"], ["Invite emails", `${groupInvites.length}`]],
      actions: organiserNeedsDatedSession
        ? [["Create session", "createGroupSession"], ["Manage sessions", "groupSessions"], ["Edit group", "createGroup"], ["View public group", "publicGroupProfile"]]
        : [["Manage sessions", "groupSessions"], ["Edit group", "createGroup"], ["Members", "members"], ["View public group", "publicGroupProfile"]],
      time: "Group status",
      messages: missing.length
        ? setupMessagesForMissing(missing, "group profile")
        : organiserNeedsDatedSession
          ? [["system", "Your public group profile is ready. Create and publish a dated session so players can see an actual play date, reserve a place and pay through Badminta.", "Next step"]]
          : setupMessagesForMissing([], "group profile")
    }));
  }
  if (groupInvites.length) {
    organiserThreads.push(accountThread({
      id: "organiser-invite-list",
      name: "Group invite list",
      type: "Member invites",
      status: "Saved",
      preview: `${groupInvites.length} invite email${groupInvites.length === 1 ? "" : "s"} saved for this group.`,
      meta: [`${groupInvites.length} emails`, group.name || "Group draft"],
      context: [["Invite emails", `${groupInvites.length}`], ["Group", group.name || "Draft group"]],
      actions: [["Manage members", "members"], ["Edit group", "createGroup"]],
      time: "Invite list",
      messages: [["system", `${groupInvites.length} invite email${groupInvites.length === 1 ? "" : "s"} are saved privately in organiser tools.`, "Invite list"]]
    }));
  }
  organiserEvents.forEach((event) => {
    organiserThreads.push(accountThread({
      id: `organiser-event-${event.id}`,
      name: event.title,
      type: "Organiser event",
      status: event.status === "published" ? "Published" : "Draft",
      preview: `${event.eventType} on ${formatDateLabel(event.date)} at ${formatTimeLabel(event.time)}.`,
      meta: [event.status, event.venue, event.capacity ? `${event.capacity} capacity` : ""],
      context: [["Event", event.title], ["Status", event.status], ["Attendees", `${event.attendees?.length || 0}`]],
      actions: [["Manage event", "manageEvent", { event }], ["Public page", "event", { event }], ["Join flow", "eventJoin", { event, bookingType: "event" }]],
      time: event.date ? formatDateLabel(event.date) : "Event status",
      messages: [["system", `${event.title} is saved as ${event.status}.`, event.date ? formatDateLabel(event.date) : "Event status"]]
    }));
  });

  const centreThreads = [];
  if (hasStartedRoleProfile(accountProfile, "centre")) {
    const missing = getRoleOnboardingMissingFields(accountProfile, "centre");
    centreThreads.push(accountThread({
      id: "centre-profile-status",
      name: centre.name || "Centre listing setup",
      type: "Centre setup",
      status: missing.length ? "Needs details" : "Ready",
      unread: missing.length ? 1 : 0,
      preview: missing.length ? `${missing.length} centre detail${missing.length === 1 ? "" : "s"} left before players can trust the listing.` : "Centre listing has the booking details players need.",
      meta: [centre.address || centre.suburb, centre.courtCount ? `${centre.courtCount} courts` : "", centre.pricing],
      context: [["Centre", centre.name || "Missing"], ["Address", centre.address || "Missing"], ["Pricing", centre.pricing || "Missing"]],
      actions: [["Edit centre listing", "claimCentre"], ["Centre dashboard", "centreDashboard"], ["Court inventory", "courtManager"]],
      time: "Claim status",
      messages: setupMessagesForMissing(missing, "centre profile")
    }));
  }
  courtBookings.filter((booking) => bookingMatchesCentre(booking, centre)).forEach((booking) => {
    centreThreads.push(accountThread({
      id: `centre-court-booking-${booking.id}`,
      name: booking.venueName,
      type: "Court booking request",
      status: booking.paymentStatus || booking.status,
      preview: `${name} requested ${booking.durationHours}h on ${formatDateLabel(booking.date)} at ${formatTimeLabel(booking.time)}.`,
      meta: [getBookingTotalLabel(booking), booking.status, booking.paymentStatus],
      context: [["Player", name], ["Court time", `${booking.durationHours}h`], ["Payment", booking.paymentStatus]],
      actions: [["Court inventory", "courtManager"], ["Centre payments", "centrePayments"], ["Messages", "messages"]],
      time: booking.bookedAt ? formatDateLabel(booking.bookedAt.slice(0, 10)) : formatDateLabel(booking.date),
      messages: [["system", `${name} requested a court booking at ${booking.venueName}.`, booking.bookedAt ? formatDateLabel(booking.bookedAt.slice(0, 10)) : formatDateLabel(booking.date)]]
    }));
  });
  centreEvents.forEach((event) => {
    centreThreads.push(accountThread({
      id: `centre-event-${event.id}`,
      name: event.title,
      type: "Centre event",
      status: event.status === "published" ? "Published" : "Draft",
      preview: `${event.eventType} on ${formatDateLabel(event.date)} at ${formatTimeLabel(event.time)}.`,
      meta: [event.status, event.venue, event.capacity ? `${event.capacity} capacity` : ""],
      context: [["Event", event.title], ["Status", event.status], ["Attendees", `${event.attendees?.length || 0}`]],
      actions: [["Manage event", "manageEvent", { event }], ["Public page", "event", { event }], ["Centre dashboard", "centreDashboard"]],
      time: event.date ? formatDateLabel(event.date) : "Event status",
      messages: [["system", `${event.title} is connected to the centre account as ${event.status}.`, event.date ? formatDateLabel(event.date) : "Event status"]]
    }));
  });

  const coachThreads = [];
  if (hasStartedRoleProfile(accountProfile, "coach")) {
    const missing = getRoleOnboardingMissingFields(accountProfile, "coach");
    const coachRate = coachPrimaryRateLabel(coach);
    const coachServiceCount = activeCoachLessonServices(coach).length;
    coachThreads.push(accountThread({
      id: "coach-profile-status",
      name: coach.headline || "Coach profile status",
      type: "Coach setup",
      status: missing.length ? "Needs details" : "Ready",
      unread: missing.length ? 1 : 0,
      preview: missing.length ? `${missing.length} coach detail${missing.length === 1 ? "" : "s"} left before players can request lessons confidently.` : "Coach profile is ready for lesson enquiries.",
      meta: [coachRate, coachServiceCount ? `${coachServiceCount} lesson type${coachServiceCount === 1 ? "" : "s"}` : "", coach.locations || coach.suburb, hasProfileAvailability(coach) ? "Availability added" : "Availability missing"],
      context: [["Lessons", coachServiceCount ? `${coachServiceCount} active` : "Missing"], ["Price", coachRate || "Missing"], ["Location", coach.locations || coach.suburb || "Missing"], ["Availability", hasProfileAvailability(coach) ? "Added" : "Missing"]],
      actions: [["Edit coach profile", "coachProfile"], ["View public coach", "publicCoachProfile"], ["Create coached group", "createGroup"]],
      time: "Coach status",
      messages: setupMessagesForMissing(missing, "coach profile")
    }));
  }
  reviews.filter((review) => ["coach", "testimonial"].includes(review.kind)).slice(0, 4).forEach((review) => {
    coachThreads.push(accountThread({
      id: `coach-review-${review.id}`,
      name: review.title || reviewKindLabels[review.kind],
      type: "Coach review",
      status: "Saved",
      preview: review.comment || "A coach review or testimonial was saved.",
      meta: [review.subject, review.score ? `${review.score}/5` : "No score"],
      context: [["Subject", review.subject], ["Score", review.score ? `${review.score}/5` : "Not scored"]],
      actions: [["View public coach", "publicCoachProfile"], ["Edit coach profile", "coachProfile"]],
      time: review.createdAt ? formatDateLabel(review.createdAt.slice(0, 10)) : "Review record",
      messages: [["system", `A ${reviewKindLabels[review.kind].toLowerCase()} record was saved for ${review.subject}.`, review.createdAt ? formatDateLabel(review.createdAt.slice(0, 10)) : "Review record"]]
    }));
  });

  const playerStarted = hasStartedRoleProfile(accountProfile, "player");
  const organiserStarted = hasStartedRoleProfile(accountProfile, "organiser");
  const centreStarted = hasStartedRoleProfile(accountProfile, "centre");
  const coachStarted = hasStartedRoleProfile(accountProfile, "coach");
  const localInboxes = mergeStoredMessagesIntoInboxes([
    {
      id: "player",
      label: "Player",
      title: "Player messages",
      icon: UserCircle,
      summary: "Invites, joined games, recommendations, booking questions and playing feedback.",
      cta: playerStarted ? "Edit player profile" : "Create player profile",
      ctaTarget: "profileSetup",
      emptyTarget: playerStarted ? "groupsIndex" : "profileSetup",
      emptyLabel: playerStarted ? "Find a social game" : "Create player profile",
      emptyText: playerStarted
        ? "Join a social game or contact another member to start your first player conversation."
        : "Create your player profile so organisers and other players can contact you.",
      threads: playerThreads.map((thread) => ({ ...thread, operationalUpdate: true }))
    },
    {
      id: "organiser",
      label: "Organiser",
      title: "Organiser messages",
      icon: UsersRound,
      summary: "Player questions, invite replies, session updates, member changes and payments.",
      cta: organiserStarted ? "Open organiser dashboard" : "Create group",
      ctaTarget: organiserStarted ? "organiser" : "createGroup",
      emptyTarget: organiserStarted ? "groupSessions" : "createGroup",
      emptyLabel: organiserStarted ? "Manage group sessions" : "Create a social group",
      emptyText: organiserStarted
        ? "Publish a dated session and invite players to start organiser conversations."
        : "Create a social group before inviting players and managing session questions.",
      threads: organiserThreads.map((thread) => ({ ...thread, operationalUpdate: true }))
    },
    {
      id: "centre",
      label: "Centre",
      title: "Centre messages",
      icon: Building2,
      summary: "Court enquiries, claim updates, event questions, booking records and payment issues.",
      cta: "Open centre tools",
      ctaTarget: "centreDashboard",
      emptyTarget: centreStarted ? "centreDashboard" : "centreClaimSearch",
      emptyLabel: centreStarted ? "Open centre dashboard" : "Add or claim a centre",
      emptyText: centreStarted
        ? "Booking and court enquiries will appear here when players contact your centre."
        : "Add or claim a centre before managing booking and court enquiries.",
      threads: centreThreads.map((thread) => ({ ...thread, operationalUpdate: true }))
    },
    {
      id: "coach",
      label: "Coach",
      title: "Coach messages",
      icon: Trophy,
      summary: "Lesson enquiries, training goals, testimonials, clinic questions and payout updates.",
      cta: coachStarted ? "Open coach dashboard" : "Create coach profile",
      ctaTarget: coachStarted ? "coachDashboard" : "coachProfile",
      emptyTarget: coachStarted ? "coachDashboard" : "coachProfile",
      emptyLabel: coachStarted ? "Open coach dashboard" : "Create coach profile",
      emptyText: coachStarted
        ? "Lesson requests and coaching questions will appear here when players contact you."
        : "Create a coach profile before accepting lesson enquiries from players.",
      threads: coachThreads.map((thread) => ({ ...thread, operationalUpdate: true }))
    }
  ], accountProfile);
  return mergeMarketplaceConversationsIntoInboxes(localInboxes, accountProfile.marketplaceConversations || []);
};

const marketplaceConversationTimeLabel = (value = "") => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Now";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(date);
};

const marketplaceConversationActions = (conversation = {}) => {
  const browseTarget = {
    players: "playersIndex",
    coaches: "playersIndex",
    groups: "groupsIndex",
    centres: "results",
    events: "events"
  }[conversation.targetCollection];
  return [
    ...(browseTarget ? [[`Browse ${conversation.targetCollection}`, browseTarget]] : []),
    ["Saved items", "saved"],
    ["Help", "help"]
  ];
};

const normaliseMarketplaceConversationThread = (conversation = {}) => {
  const messages = Array.isArray(conversation.messages) ? conversation.messages.map((message) => {
    const attachmentText = Array.isArray(message.attachments) && message.attachments.length
      ? `\n\nAttached: ${message.attachments.map((attachment) => `${attachment.label} (${attachment.detail})`).join("; ")}`
      : "";
    return [
      message.system ? "system" : message.fromSelf ? "me" : "them",
      `${message.text || "Message"}${attachmentText}`,
      marketplaceConversationTimeLabel(message.createdAt)
    ];
  }) : [];
  const latestMessage = conversation.latestMessage || messages[messages.length - 1]?.[1] || "Conversation ready";
  return accountThread({
    id: conversation.conversationId || conversation.id,
    name: conversation.otherLabel || conversation.subject || "Badminta member",
    type: conversation.threadType || "Marketplace conversation",
    status: conversation.status === "open" ? "Open" : conversation.status || "Open",
    unread: conversation.unread || 0,
    preview: latestMessage,
    meta: [conversation.subject, ...(conversation.meta || [])],
    context: [["Subject", conversation.subject], ["Profile type", conversation.targetRole], ...(conversation.context || [])],
    actions: marketplaceConversationActions(conversation),
    time: marketplaceConversationTimeLabel(conversation.updatedAt || conversation.latestAt),
    messages: messages.length ? messages : [["system", "This conversation is ready for a message.", "Now"]],
    backendThread: true,
    conversationId: conversation.conversationId || conversation.id,
    roleId: conversation.currentRole || "player",
    targetRole: conversation.targetRole || conversation.otherRole || "player",
    targetCollection: conversation.targetCollection || "",
    targetId: conversation.targetId || "",
    otherUid: conversation.otherUid || ""
  });
};

const mergeMarketplaceConversationsIntoInboxes = (inboxes = [], conversations = []) => {
  const threadsByRole = conversations.reduce((accumulator, conversation) => {
    const roleId = ["player", "organiser", "centre", "coach"].includes(conversation.currentRole) ? conversation.currentRole : "player";
    if (!accumulator[roleId]) accumulator[roleId] = [];
    accumulator[roleId].push(normaliseMarketplaceConversationThread(conversation));
    return accumulator;
  }, {});
  return inboxes.map((inbox) => {
    const remoteThreads = threadsByRole[inbox.id] || [];
    const remoteIds = new Set(remoteThreads.map((thread) => thread.id));
    return {
      ...inbox,
      threads: [...remoteThreads, ...inbox.threads.filter((thread) => !remoteIds.has(thread.id))]
    };
  });
};

const messageThreadTone = (thread = {}) => {
  const source = `${thread.status || ""} ${thread.type || ""} ${thread.preview || ""}`.toLowerCase();
  if (/(needs|missing|pending|requested|attention|refund|unpaid|draft)/.test(source)) return "needs-action";
  if (/(ready|paid|published|confirmed|saved|verified|complete)/.test(source)) return "ready";
  if (/(open|enquiry|question|invite|reply)/.test(source)) return "open";
  return "neutral";
};

const messageThreadInitial = (thread = {}) => String(thread.name || thread.type || "B").slice(0, 1).toUpperCase();

const messageRoleStatLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const playerInviteProfileId = (player = {}) => String(
  player.firestoreSourceId || player.publicPlayerId || player.id || ""
).trim();

const playerInviteRecipientUid = (player = {}) => String(
  player.ownerUid || player.playerUid || player.createdByUid || ""
).trim();

const playerInviteLocationLabel = (player = {}) => {
  const values = [
    player.suburb || player.location?.suburb,
    player.city || player.location?.city,
    player.state || player.location?.state,
    player.country || player.location?.country
  ].map((value) => String(value || "").trim()).filter(Boolean);
  return [...new Set(values)].join(", ") || "Location not published";
};

const isSearchablePlayerInviteProfile = (player = {}) => {
  const status = String(player.status || player.backendStatus || player.visibilityStatus || "").toLowerCase();
  return Boolean(
    playerInviteProfileId(player)
    && (player.public === true || ["published", "active", "public"].includes(status))
  );
};

const normalisePlayerInviteSearchText = (...values) => filterSearchText(...values)
  .normalize("NFKD")
  .replace(/\p{Diacritic}/gu, "")
  .replace(/\s+/g, " ")
  .trim();

const buildMemberRowsFromAccount = (accountProfile = {}) => {
  const group = accountProfile.group || {};
  const inviteEmails = cleanEmailTags(group.inviteEmails?.length ? group.inviteEmails : group.invites);
  const groupIds = new Set([
    group.id,
    group.firestoreSourceId,
    group.serverGroupId,
    group.publicGroupId,
    accountProfile.publicGroupProfileId
  ].map((value) => String(value || "").trim()).filter(Boolean));
  const privateInviteRecords = (Array.isArray(accountProfile.groupInvites) ? accountProfile.groupInvites : [])
    .filter((invite) => !invite.groupId || !groupIds.size || groupIds.has(String(invite.groupId)));
  const inviteRecords = privateInviteRecords
    .reduce((records, invite) => {
      const email = cleanEmailTags([invite.recipientEmail || invite.email])[0];
      if (email) records.set(email, invite);
      return records;
    }, new globalThis.Map());
  const inviteStatus = (invite = {}) => {
    const status = String(invite.deliveryStatus || invite.status || "queued").toLowerCase();
    if (/delivered|opened|clicked/.test(status)) return { label: "Delivered", meta: "Email invitation delivered" };
    if (/failed|bounced|complained|rejected|invalid/.test(status)) return { label: "Needs attention", meta: "Badminta will retry or review delivery" };
    if (/requires_provider/.test(status)) return { label: "Waiting for delivery", meta: "Email delivery is waiting on platform operations" };
    return { label: "Queued", meta: "Email invitation queued" };
  };
  const publicMembers = getPublicGroupMembers(group, accountProfile);
  const publicMemberUids = new Set(publicMembers.map((member) => playerInviteRecipientUid(member)).filter(Boolean));
  const platformInviteRows = privateInviteRecords
    .filter((invite) => invite.inviteType === "platform_player" && !publicMemberUids.has(playerInviteRecipientUid(invite)))
    .map((invite, index) => {
      const status = String(invite.status || invite.deliveryStatus || "invited").toLowerCase();
      const declined = /declined|rejected|cancelled/.test(status);
      const accepted = /accepted|joined|member/.test(status);
      return {
        id: invite.id || invite.inviteId || `platform-invite-${playerInviteRecipientUid(invite) || index}`,
        name: invite.recipientName || "Badminta player",
        contact: "Badminta player",
        status: accepted ? "Member" : declined ? "Declined" : "Invited",
        meta: accepted ? "Joined this group" : declined ? "Invitation declined" : "Badminta invitation sent",
        source: group.name || invite.groupName || "Group invitation",
        photoUrl: invite.recipientPhotoUrl || "",
        playerId: invite.playerProfileId || "",
        recipientUid: playerInviteRecipientUid(invite),
        kind: "platform_player"
      };
    });
  const eventAttendees = getOwnerEvents(accountProfile, "organiser").flatMap((event) =>
    getPublicEventAttendees(event, accountProfile).map((attendee) => ({
      ...attendee,
      source: event.title,
      status: attendee.status || "Registered"
    }))
  );
  const marketplaceMembers = (Array.isArray(accountProfile.managedJoinRequests) ? accountProfile.managedJoinRequests : [])
    .filter((request) => request.kind !== "event")
    .map((request, index) => ({
      id: request.id || request.joinRequestId || `marketplace-member-${index}`,
      name: request.participantName || request.participantEmail || request.payerPhone || "Registered player",
      contact: request.participantEmail || request.payerPhone || "Contact held in account",
      status: request.status || request.joinedStatus || "Registered",
      meta: request.paymentStatus || "Payment status pending",
      source: request.title || group.name || "Marketplace session"
    }));
  const marketplaceMemberships = (Array.isArray(accountProfile.managedMembershipPurchases) ? accountProfile.managedMembershipPurchases : [])
    .filter((purchase) => {
      if (String(purchase.membershipScope || "").toLowerCase() !== "group" && !purchase.groupId) return false;
      return !groupIds.size || groupIds.has(String(purchase.groupId || ""));
    })
    .map((purchase, index) => ({
      id: purchase.id || purchase.membershipPurchaseId || `marketplace-membership-${index}`,
      name: purchase.participantName || purchase.participantEmail || "Group member",
      contact: purchase.participantEmail || "Contact held in account",
      status: groupMembershipAccessStatuses.has(String(purchase.status || "").toLowerCase()) ? "Member" : purchase.status || "Payment pending",
      meta: groupMembershipAccessStatuses.has(String(purchase.status || "").toLowerCase())
        ? purchase.membershipMode === "free" ? "Free membership" : purchase.paymentStatus === "paid" ? "Paid membership" : "Active membership"
        : purchase.paymentStatus || "Payment pending",
      source: purchase.groupName || group.name || "Group membership"
    }));
  const rows = [
    ...publicMembers.map((member, index) => ({
      id: `member-${member.email || member.name || index}`,
      name: member.name || member.email || "Public member",
      contact: member.email || "No email listed",
      status: member.status || "Public member",
      meta: member.level || member.paymentStatus || "Group member",
      source: group.name || "Group profile",
      photoUrl: member.photoUrl || member.image || "",
      playerId: playerInviteProfileId(member),
      recipientUid: playerInviteRecipientUid(member),
      kind: "member"
    })),
    ...platformInviteRows,
    ...inviteEmails.map((email) => {
      const delivery = inviteStatus(inviteRecords.get(email));
      return {
        id: `invite-${email}`,
        name: email.split("@")[0],
        contact: email,
        status: delivery.label,
        meta: delivery.meta,
        source: group.name || "Group invite list",
        kind: "email"
      };
    }),
    ...eventAttendees.map((attendee, index) => ({
      id: `attendee-${attendee.email || attendee.name || index}`,
      name: attendee.name || attendee.email || "Event attendee",
      contact: attendee.email || "No email listed",
      status: attendee.status || "Registered",
      meta: attendee.paymentStatus || "Event attendee",
      source: attendee.source
    })),
    ...marketplaceMemberships,
    ...marketplaceMembers
  ];
  return rows.filter((row, index, list) => list.findIndex((item) => `${item.contact}-${item.source}` === `${row.contact}-${row.source}`) === index);
};
const getEventDashboardStats = (event = {}, accountProfile = {}) => {
  const capacity = Number.parseInt(event.capacity, 10) || 0;
  const attendeeCount = getPublicEventAttendees(event, accountProfile).length;
  const price = eventPriceNumber(event);
  const revenue = attendeeCount * price;
  const fillRate = capacity ? Math.min(100, Math.round((attendeeCount / capacity) * 100)) : 0;
  return { capacity, attendeeCount, price, revenue, fillRate, remaining: Math.max(capacity - attendeeCount, 0) };
};

const reviewKindLabels = {
  player: "Rate player",
  recommendation: "Leave recommendation",
  coach: "Rate coach",
  testimonial: "Coach testimonial",
  group: "Rate social group",
  centre: "Review centre",
  event: "Review event",
  media: "Review media",
  trust: "Report or verify"
};

const ratingEligibleReviewKinds = new Set(["player", "recommendation", "coach", "testimonial", "group", "centre", "event", "media"]);
const messageRoleForReviewKind = (kind = "") => {
  if (kind === "coach" || kind === "testimonial") return "coach";
  if (kind === "group" || kind === "event") return "organiser";
  if (kind === "centre") return "centre";
  return "player";
};
const openTrustReportMessage = ({ report = {}, accountProfile = {}, saveAccountProfile, navigate }) => openPublicMessageThread({
  accountProfile,
  saveAccountProfile,
  navigate,
  roleId: messageRoleForReviewKind(report.kind),
  subject: report.subject || report.title || "Trust report",
  threadType: "Trust report",
  text: `Trust report conversation opened for ${report.subject || report.title || "a report"}. Keep report status, category, subject and moderation history visible while support follows up.`,
  status: report.status || "Open",
  meta: [reviewKindLabels[report.kind] || "Trust report", report.status, report.createdAt ? formatDateLabel(report.createdAt.slice(0, 10)) : ""],
  context: [["Report", report.title || "Trust report"], ["Subject", report.subject || "Pending"], ["Status", report.status || "Pending"], ["Category", reviewKindLabels[report.kind] || "Trust"]],
  actions: [["Trust queue", "trust"], ["Help", "help"], ["Messages", "messages"]]
});
const isRatingEligibleReview = (review = {}) => review.ratingEligible !== false && ratingEligibleReviewKinds.has(review.kind) && Number.isFinite(Number(review.score)) && Number(review.score) > 0;
const isApprovedPublicReviewRecord = (review = {}) => {
  const status = String(review.status || review.moderationStatus || "").toLowerCase();
  return isRatingEligibleReview(review) && (
    review.public === true
    || review.visibility === "public"
    || status === "approved"
  );
};
const isTrustReport = (review = {}) => review.kind === "trust" || review.ratingEligible === false;
const isBackendPublicReview = (review = {}) => review.ledgerCollection === "reviews" || Boolean(review.serverReviewId);
const isOpenTrustReport = (review = {}) => {
  const status = String(review.status || review.moderationStatus || "").toLowerCase();
  if (isBackendPublicReview(review)) return !["approved", "rejected", "removed", "resolved"].some((terminal) => status.includes(terminal));
  return isTrustReport(review) && !status.includes("resolved");
};

const getTrustReports = (accountProfile = {}) => (Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [])
  .filter(isTrustReport)
  .map((review) => ({
    ...review,
    status: review.status || "Submitted to trust queue",
    subject: review.subject || "Badminta report",
    title: review.title || "Trust report",
    comment: review.comment || "No report details supplied yet."
  }))
  .sort((first, second) => `${second.createdAt || ""}`.localeCompare(`${first.createdAt || ""}`));

const paymentKindLabels = {
  booking: "Booking confirmation",
  social: "Social session payment",
  event: "Event registration",
  coach: "Coach deposit",
  centre: "Court fee payment"
};

const normaliseReviewTarget = (target = {}) => {
  const collection = String(target.targetCollection || target.collection || target.subjectCollection || "").trim();
  const id = String(target.targetId || target.id || target.subjectId || "").trim();
  return {
    collection,
    id,
    subjectCollection: String(target.subjectCollection || collection || "").trim(),
    subjectId: String(target.subjectId || id || "").trim()
  };
};

const reviewContextFor = (kind, title, subject = "", target = {}) => {
  const cleanTarget = normaliseReviewTarget(target);
  return {
    kind,
    title: title || reviewKindLabels[kind] || "Leave review",
    subject: subject || "Badminta profile",
    targetCollection: cleanTarget.collection,
    targetId: cleanTarget.id,
    subjectCollection: cleanTarget.subjectCollection,
    subjectId: cleanTarget.subjectId
  };
};

const pluraliseReview = (count) => `${count} review${count === 1 ? "" : "s"}`;

const normaliseReviewSubject = (value = "") => String(value || "").trim().toLowerCase();

const emptyReviewSummary = (meta = "Rating average appears after real reviews") => ({
  count: 0,
  average: null,
  label: "No reviews yet",
  meta
});

const reviewMatchesTarget = (review = {}, target = {}) => {
  const cleanTarget = normaliseReviewTarget(target);
  if (!cleanTarget.id) return false;
  const reviewCollection = String(review.targetCollection || review.subjectCollection || "").trim();
  const collectionMatches = !cleanTarget.collection || !reviewCollection || reviewCollection === cleanTarget.collection;
  const reviewIds = [review.targetId, review.subjectId, review.sourceId, review.profileId]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  return collectionMatches && reviewIds.includes(cleanTarget.id);
};

const reviewSummaryFromRecords = (accountProfile = {}, kinds = [], subject = "", publicReviews = [], target = {}) => {
  const kindList = Array.isArray(kinds) ? kinds : [kinds];
  const kindSet = new Set(kindList.filter(Boolean));
  const targetSubject = normaliseReviewSubject(subject);
  const cleanTarget = normaliseReviewTarget(target);
  const seen = new Set();
  const records = [
    ...(Array.isArray(accountProfile.reviews) ? accountProfile.reviews : []),
    ...(Array.isArray(publicReviews) ? publicReviews : [])
  ].filter((review) => {
    if (!isApprovedPublicReviewRecord(review)) return false;
    const key = review.serverReviewId || review.id || `${review.kind}-${review.subject}-${review.score}-${review.createdAt}`;
    if (seen.has(key)) return false;
    seen.add(key);
    const kindMatches = kindSet.size ? kindSet.has(review.kind) : true;
    const subjectMatches = targetSubject ? normaliseReviewSubject(review.subject) === targetSubject : true;
    const identityMatches = cleanTarget.id
      ? reviewMatchesTarget(review, cleanTarget) || (targetSubject && subjectMatches)
      : subjectMatches;
    return kindMatches && identityMatches;
  });
  const scores = records
    .map((review) => Number(review.score))
    .filter((score) => Number.isFinite(score) && score > 0);
  if (!records.length || !scores.length) return emptyReviewSummary();
  const average = scores.reduce((total, score) => total + score, 0) / scores.length;
  return {
    count: records.length,
    average,
    label: `${average.toFixed(1)} avg`,
    meta: pluraliseReview(records.length)
  };
};

const reviewSummaryFromVenue = (venue = {}) => {
  const rating = Number(venue?.rating);
  const count = Number(venue?.reviews);
  if (!Number.isFinite(rating) || rating <= 0 || !Number.isFinite(count) || count <= 0) {
    return emptyReviewSummary("No public review count listed");
  }
  return {
    count,
    average: rating,
    label: `${rating.toFixed(1)} avg`,
    meta: pluraliseReview(count)
  };
};

const publicReviewSummary = (accountProfile = {}, kinds = [], subject = "", options = {}) => {
  if (options.hidden) {
    return {
      count: 0,
      average: null,
      label: "Rating hidden",
      meta: "This profile keeps ratings private"
    };
  }
  const ownSummary = reviewSummaryFromRecords(accountProfile, kinds, subject, options.publicReviews, options.target);
  return ownSummary.count ? ownSummary : options.fallback || emptyReviewSummary();
};

const paymentContextFor = (kind, title, amount = "", subject = "") => ({
  kind,
  title: title || paymentKindLabels[kind] || "Payment",
  amount,
  subject
});

const stripePaymentBlueprints = {
  centre: {
    owner: "Centre owner",
    checkout: "Server-created Stripe Checkout Session for court hire",
    payout: "Badminta-managed Stripe Connect payout after centre verification",
    ledger: "Booking, court, rate type, service fee, refund window and payout status",
    refund: "Booking rules control cancellations, late refunds and dispute evidence",
    items: ["Court hire", "Badminta service fee", "Refund protection"]
  },
  booking: {
    owner: "Centre owner",
    checkout: "Server-created Stripe Checkout Session for booking confirmation",
    payout: "Badminta-managed Stripe Connect payout after the booking is accepted",
    ledger: "Booking, venue, players, receipt, refund state and support thread",
    refund: "Refunds are reconciled against the booking and venue rules",
    items: ["Booking fee", "Platform fee", "Refund reserve"]
  },
  social: {
    owner: "Social organiser",
    checkout: "Server-created Stripe Checkout Session per joined player",
    payout: "Organiser payout reconciliation through Badminta-managed Stripe Connect",
    ledger: "Session, player, invite source, paid status, refund and no-show status",
    refund: "No-shows, swaps and waitlist movement stay tied to the member ledger",
    items: ["Session spot", "Badminta service fee", "No-show protection"]
  },
  event: {
    owner: "Centre or organiser",
    checkout: "Server-created Stripe Checkout Session for event registration",
    payout: "Owner payout reconciliation through Badminta-managed Stripe Connect after event rules clear",
    ledger: "Event, attendee, ticket, receipt, refund state and owner payout",
    refund: "Refunds and disputes stay visible in the event dashboard",
    items: ["Event ticket", "Platform fee", "Refund reserve"]
  },
  coach: {
    owner: "Coach",
    checkout: "Server-created Stripe Checkout Session for lesson deposits or packages",
    payout: "Coach payout reconciliation through Badminta-managed Stripe Connect after lesson rules clear",
    ledger: "Lesson, package, student, deposit, balance and testimonial follow-up",
    refund: "Deposit and cancellation policy belongs to the coach profile",
    items: ["Lesson deposit", "Package credits", "Platform fee"]
  },
  general: {
    owner: "Badminta account",
    checkout: "Server-created Stripe Checkout Session",
    payout: "Stripe balance and payout reconciliation",
    ledger: "Payment, receipt, status, refund and support thread",
    refund: "Refunds stay visible in account activity",
    items: ["Payment amount", "Platform fee", "Receipt"]
  }
};

const stripePaymentBlueprintFor = (kind = "general") => stripePaymentBlueprints[kind] || stripePaymentBlueprints.general;

const stripeReferenceId = (kind = "payment", subject = "badminta") => {
  const base = slugifyPath(`${kind}-${subject || "badminta"}-${Date.now().toString(36)}`)
    .replace(/-/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 180);
  return base || `badminta_${Date.now().toString(36)}`;
};

const eventMatchesLocation = (event = {}, city = "Melbourne", searchCriteria = {}) => {
  const eventCity = getEventCity(event);
  const location = searchCriteria.location || {};
  const locationCountryCode = getLocationCountryCode(location, city);
  const eventCountryCode = normaliseCountryCode(
    event.countryCode || event.country || event.location?.countryCode || event.location?.country,
    eventCity
  );
  if (locationCountryCode && eventCountryCode && locationCountryCode !== eventCountryCode) return false;
  if (eventCity && city && compactLocationText(eventCity) !== compactLocationText(city)) return false;
  if (location?.type === "suburb") {
    const eventSuburb = getEventSuburb(event);
    return !eventSuburb || compactLocationText(eventSuburb) === compactLocationText(location.suburb);
  }
  return true;
};

const getPublicEventsForLocation = (accountProfile = {}, city = "Melbourne", searchCriteria = {}) => getPublishedAccountEvents(accountProfile)
  .filter((event) => eventMatchesLocation(event, city, searchCriteria));

const getOwnerEvents = (accountProfile = {}, ownerType) => getAccountEvents(accountProfile).filter((event) => event.ownerType === ownerType);
const getGroupTitle = (group = {}) => group.title || group.name || "Social badminton session";
const getGroupVenueLabel = (group = {}) => group.venue || getEventVenue({ venueId: group.venueId })?.name || "Venue to be confirmed";
const getGroupScheduleLabel = (group = {}) => {
  if (group.when) return group.when;
  const scheduleSummary = summarizeGroupSchedule(group);
  if (scheduleSummary) return `${group.isRecurring === false ? "Plays" : "Repeats weekly"}: ${scheduleSummary}`;
  const parts = [group.date ? formatDateLabel(group.date) : "", group.time ? formatTimeLabel(group.time) : ""].filter(Boolean);
  return parts.length ? parts.join(" at ") : "Schedule to be confirmed";
};
const getGroupImage = (group = {}) => group.image || group.coverUrl || group.logoUrl || "";
const getGroupPriceNumber = (group = {}) => Number.parseFloat(String(group.price || "").replace(/[^0-9.]/g, "")) || 0;
const getGroupPriceLabel = (group = {}, suffix = "") => {
  const amount = getGroupPriceNumber(group);
  if (!amount) return "";
  const currency = normaliseCurrencyCode(group.currency || marketplaceCurrencyForLocation(group.location));
  return `${formatMoney(amount, currency)}${suffix}`;
};
const groupScheduleDayIndexes = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const isClockTimeValue = (value = "") => /^\d{2}:\d{2}$/.test(String(value || ""));
const formatGroupSessionTime = (start = "", end = "", fallback = "") => {
  if (isClockTimeValue(start) && isClockTimeValue(end)) return `${formatTimeLabel(start)} - ${formatTimeLabel(end)}`;
  if (isClockTimeValue(start)) return formatTimeLabel(start);
  if (start) return start;
  return fallback || "Time pending";
};
const getUpcomingIsoForScheduleDay = (dayKey = "", start = "", today = new Date()) => {
  const targetDay = groupScheduleDayIndexes[String(dayKey || "").toLowerCase()];
  if (targetDay === undefined) return "";
  const date = new Date(today);
  date.setHours(0, 0, 0, 0);
  const offset = (targetDay - date.getDay() + 7) % 7;
  date.setDate(date.getDate() + offset);
  if (offset === 0 && isClockTimeValue(start)) {
    const [hours, minutes] = start.split(":").map(Number);
    const startAt = new Date(date);
    startAt.setHours(hours, minutes, 0, 0);
    if (startAt.getTime() <= today.getTime()) date.setDate(date.getDate() + 7);
  }
  return localIsoDateFromDate(date);
};
const groupOccurrenceIsPast = (occurrence = {}, now = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(occurrence.date || ""))) return false;
  const date = localDateFromIso(occurrence.date);
  if (!date) return false;
  const clock = isClockTimeValue(occurrence.start)
    ? occurrence.start
    : isClockTimeValue(occurrence.time)
      ? occurrence.time
      : "23:59";
  const [hours, minutes] = clock.split(":").map(Number);
  date.setHours(hours, minutes, 0, 0);
  return date.getTime() <= now.getTime();
};
const rollRecurringGroupOccurrence = (group = {}, occurrence = {}, now = new Date()) => {
  if (group.isRecurring === false || occurrence.isRecurring === false || !groupOccurrenceIsPast(occurrence, now)) return occurrence;
  const originalDate = localDateFromIso(occurrence.date);
  if (!originalDate) return occurrence;
  const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][originalDate.getDay()];
  const nextDate = getUpcomingIsoForScheduleDay(dayKey, occurrence.start || occurrence.time, now);
  return nextDate ? { ...occurrence, rawDate: occurrence.date, date: nextDate, dateLabel: formatDateLabel(nextDate), status: "Weekly recurring" } : occurrence;
};
const buildGroupSessionOccurrence = (group = {}, session = {}, index = 0) => {
  const dateValue = session.date || "";
  const start = session.start || session.timeStart || session.time || "";
  const end = session.end || session.timeEnd || "";
  const rawPrice = session.price || group.price || "";
  const hasRawPrice = String(session.price ?? group.price ?? "").trim() !== "";
  const priceNumber = Number.parseFloat(String(session.price ?? group.price ?? ""));
  const currency = normaliseCurrencyCode(session.currency || group.currency || marketplaceCurrencyForLocation(session.location || group.location));
  const rawCapacity = session.capacity || group.capacity || "";
  return {
    ...session,
    id: session.id || `session-${index}`,
    rawDate: dateValue,
    date: dateValue && /^\d{4}-\d{2}-\d{2}$/.test(String(dateValue)) ? dateValue : "",
    dateLabel: dateValue && /^\d{4}-\d{2}-\d{2}$/.test(String(dateValue)) ? formatDateLabel(dateValue) : dateValue || "Day pending",
    start,
    end,
    time: formatGroupSessionTime(start, end, session.time),
    status: session.status || (group.isRecurring === false ? "Regular session" : "Weekly recurring"),
    price: hasRawPrice ? String(session.price ?? group.price) : rawPrice,
    priceLabel: hasRawPrice ? priceNumber > 0 ? formatMoney(priceNumber, currency) : "Free" : "Price pending",
    currency,
    capacity: rawCapacity,
    capacityLabel: rawCapacity ? `${rawCapacity} spots` : "Capacity pending",
    paymentStatus: session.paymentStatus || (getGroupPriceNumber({ price: rawPrice }) ? "Payment pending" : "Free registration")
  };
};
const getGroupPublishedDatedOccurrences = (group = {}) => {
  const now = new Date();
  const savedSessions = Array.isArray(group.sessions) ? group.sessions : [];
  return savedSessions
    .filter((session) => {
      const sessionId = String(session?.sessionId || session?.id || "").trim();
      const status = String(session?.status || "").toLowerCase();
      return Boolean(sessionId)
        && session?.isRecurring === false
        && ["published", "active"].includes(status);
    })
    .map((session, index) => buildGroupSessionOccurrence(group, session, index))
    .filter((session) => Boolean(session.date) && !groupOccurrenceIsPast(session, now))
    .sort((first, second) => `${first.date}T${first.start || first.time || ""}`.localeCompare(`${second.date}T${second.start || second.time || ""}`));
};
const getGroupUpcomingOccurrences = (group = {}, limit = 6) => {
  const now = new Date();
  const savedSessions = Array.isArray(group.sessions) ? group.sessions : [];
  const savedRows = savedSessions
    .map((session, index) => buildGroupSessionOccurrence(group, session, index))
    .map((session) => rollRecurringGroupOccurrence(group, session, now))
    .filter((session) => (session.isRecurring !== false && group.isRecurring !== false) || !groupOccurrenceIsPast(session, now));
  const scheduleRows = savedRows.length
    ? []
    : normaliseGroupSchedule(group)
      .filter((row) => row.enabled)
      .map((row, index) => buildGroupSessionOccurrence(group, {
        id: `weekly-${row.day}`,
        day: row.day,
        date: getUpcomingIsoForScheduleDay(row.day, row.start, now),
        start: row.start,
        end: row.end,
        status: group.isRecurring === false ? "Regular session" : "Weekly recurring"
      }, index));
  const legacyRows = !savedRows.length && !scheduleRows.length && (group.date || group.time)
    ? [buildGroupSessionOccurrence(group, {
      id: "saved-session",
      date: group.date,
      time: group.time,
      status: "Saved session"
    }, 0)]
      .map((session) => rollRecurringGroupOccurrence(group, session, now))
      .filter((session) => group.isRecurring !== false || !groupOccurrenceIsPast(session, now))
    : [];
  const uniqueRows = new globalThis.Map();
  [...savedRows, ...scheduleRows, ...legacyRows].forEach((session) => {
    const key = `${session.date || "pending"}|${session.start || session.time || "pending"}`;
    if (!uniqueRows.has(key)) uniqueRows.set(key, session);
  });
  return [...uniqueRows.values()]
    .sort((first, second) => `${first.date || "9999-99-99"}T${first.start || first.time || ""}`.localeCompare(`${second.date || "9999-99-99"}T${second.start || second.time || ""}`))
    .slice(0, limit);
};
const getPrimaryGroupOccurrence = (group = {}) => getGroupUpcomingOccurrences(group, 1)[0] || null;
const getGroupWhenLabel = (group = {}) => {
  const nextOccurrence = getGroupPublishedDatedOccurrences(group)[0] || null;
  if (nextOccurrence?.dateLabel) return `${nextOccurrence.dateLabel} at ${nextOccurrence.time}`;
  return getGroupScheduleLabel(group);
};
const getGroupStableId = (group = {}) => group.id || slugifyPath(`${getGroupTitle(group)} ${getGroupVenueLabel(group)} ${getGroupScheduleLabel(group)}`);
const groupMatchesAccountPublicProfile = (group = {}, accountProfile = {}) => {
  const accountGroup = accountProfile.group || {};
  if (!isPublicProfileReady(accountProfile, "group")) return false;
  if (group.id && accountGroup.id && group.id === accountGroup.id) return true;
  const groupKey = normaliseSearchText(`${getGroupTitle(group)} ${getGroupVenueLabel(group)} ${getGroupScheduleLabel(group)}`);
  const accountGroupKey = normaliseSearchText(`${getGroupTitle(accountGroup)} ${getGroupVenueLabel(accountGroup)} ${getGroupScheduleLabel(accountGroup)}`);
  return Boolean(groupKey && groupKey === accountGroupKey);
};
const normaliseJoinedGroup = (group = {}, index = 0) => {
  const nextOccurrence = getPrimaryGroupOccurrence(group) || {};
  const sessionDate = group.isRecurring === false ? group.date || nextOccurrence.date || "" : nextOccurrence.date || group.date || "";
  const sessionTime = group.isRecurring === false ? group.time || nextOccurrence.start || nextOccurrence.time || "" : nextOccurrence.start || group.time || nextOccurrence.time || "";
  const venue = getEventVenue({ venueId: group.venueId });
  const location = locationSnapshot(group.location) || locationFromVenue(venue);
  const countryCode = normaliseCountryCode(group.countryCode || group.country || location?.countryCode || venue?.countryCode || venue?.country, group.city || location?.city || venue?.city);
  const currency = normaliseCurrencyCode(group.currency || nextOccurrence.currency || marketplaceCurrencyForCountry(countryCode));
  return {
    ...group,
    id: getGroupStableId(group) || `joined-group-${index}`,
    title: getGroupTitle(group),
    venue: getGroupVenueLabel(group),
    venueId: group.venueId || "",
    city: group.city || group.location?.city || venue?.city || "Melbourne",
    suburb: group.suburb || group.location?.suburb || venue?.suburb || "",
    countryCode,
    currency,
    location,
    date: sessionDate,
    time: sessionTime,
    timeLabel: nextOccurrence.time || (isClockTimeValue(sessionTime) ? formatTimeLabel(sessionTime) : sessionTime),
    sessionId: group.sessionId || nextOccurrence.id || "",
    nextSession: nextOccurrence,
    schedule: normaliseGroupSchedule(group),
    isRecurring: group.isRecurring !== false,
    price: group.price || "",
    skillBand: group.skillBand || group.band || "",
    format: group.format || "",
    capacity: group.capacity || "",
    joinedAt: group.joinedAt || "",
    joinedStatus: group.joinedStatus || "Registered",
    paymentStatus: group.paymentStatus || nextOccurrence.paymentStatus || (getGroupPriceNumber(group) ? "Payment pending" : "Free registration"),
    ticketCount: group.ticketCount || 1
  };
};
const normalisePublicGroupDocument = (documentId, data = {}) => {
  const group = normaliseJoinedGroup({
    ...data,
    id: documentId,
    firestoreSourceId: documentId,
    publicGroupId: documentId,
    serverGroupId: documentId
  });
  const memberCount = getGroupMemberCount(group);
  return {
    ...group,
    id: documentId,
    firestoreSourceId: documentId,
    publicGroupId: documentId,
    serverGroupId: documentId,
    title: getGroupTitle(group),
    name: data.name || data.title || getGroupTitle(group),
    when: getGroupWhenLabel(group),
    band: data.skillBand || data.band || "All welcome",
    spots: Number(data.spots || memberCount || 0),
    capacity: Number(data.capacity || data.maxPlayers || 0),
    members: Array.isArray(data.members) ? data.members : [],
    searchText: filterSearchText(data.name, data.title, data.description, data.venue, data.skillBand, data.format, data.price, getGroupWhenLabel(group))
  };
};
const getJoinedGroups = (accountProfile = {}) => (Array.isArray(accountProfile.joinedGroups) ? accountProfile.joinedGroups : [])
  .map(normaliseJoinedGroup)
  .sort((first, second) => `${first.date}T${first.time}`.localeCompare(`${second.date}T${second.time}`));
const groupMatchesVenue = (group = {}, venue = {}) => {
  if (!group || !venue) return false;
  if (group.venueId && venue.id && group.venueId === venue.id) return true;
  const groupVenue = normaliseLocationText(group.venue || getGroupVenueLabel(group)).toLowerCase();
  const venueName = normaliseLocationText(venue.name).toLowerCase();
  const venueAddress = normaliseLocationText(venue.address).toLowerCase();
  const groupSuburb = normaliseLocationText(group.suburb).toLowerCase();
  const venueSuburb = normaliseLocationText(venue.suburb).toLowerCase();
  if (groupVenue && venueName && (groupVenue === venueName || groupVenue.includes(venueName) || venueName.includes(groupVenue))) return true;
  if (groupVenue && venueAddress && (venueAddress.includes(groupVenue) || groupVenue.includes(venueAddress))) return true;
  return Boolean(groupSuburb && venueSuburb && groupSuburb === venueSuburb && groupVenue && venue.city === (group.city || venue.city));
};
const getBadmintaVenueSocialListings = (venue = {}, accountProfile = {}, groupDirectory = []) => {
  const accountGroup = accountProfile.group || {};
  const candidates = [
    ...(Array.isArray(groupDirectory) ? groupDirectory : []),
    ...(isPublicProfileReady(accountProfile, "group") ? [accountGroup] : [])
  ];
  const matchedGroups = [];
  const seen = new Set();
  candidates.forEach((group) => {
    if (!groupMatchesVenue(group, venue)) return;
    const signature = normaliseSearchText(`${getGroupTitle(group)} ${getGroupVenueLabel(group)} ${getGroupScheduleLabel(group)}`);
    if (!signature || seen.has(signature)) return;
    seen.add(signature);
    matchedGroups.push(group);
  });

  return matchedGroups.slice(0, 10).map((group) => {
    const sessions = getGroupPublicSessions(group);
    const primarySession = sessions[0] || {};
    const hasPublishedSession = sessions.length > 0;
    const priceNumber = getGroupPriceNumber(hasPublishedSession ? primarySession : group);
    const ownerView = groupMatchesAccountPublicProfile(group, accountProfile);
    const publicGroup = normaliseJoinedGroup(group);
    const sessionGroup = hasPublishedSession ? groupForPublicSession(publicGroup, primarySession) : publicGroup;
    const scheduleLabel = hasPublishedSession
      ? `${primarySession.dateLabel || formatDateLabel(primarySession.date)} at ${primarySession.time}`
      : getGroupScheduleLabel(group);
    const detailParts = [
      scheduleLabel,
      group.skillBand || group.format,
      hasPublishedSession && group.capacity ? `${getPublicGroupMembers(group, accountProfile).length}/${group.capacity} joined` : "",
      hasPublishedSession ? priceNumber ? getGroupPriceLabel({ ...group, ...primarySession }, " per player") : "Free" : "No dated session published"
    ].filter(Boolean);
    return {
      id: `badminta-group-${getGroupStableId(group)}`,
      title: group.name || getGroupTitle(group),
      meta: hasPublishedSession ? "Upcoming Badminta session" : "Badminta social group profile",
      detail: detailParts.join(" - ") || "Published Badminta group at this centre.",
      source: "Badminta organiser profile",
      target: "publicGroupProfile",
      actionLabel: "Open group",
      secondaryTarget: ownerView ? "groupSessions" : hasPublishedSession ? "join" : "",
      secondaryLabel: ownerView ? "Manage sessions" : hasPublishedSession ? "Join session" : "",
      payload: { group: publicGroup },
      secondaryPayload: { group: sessionGroup }
    };
  });
};
const makeCourtBookingId = () => `court-booking-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normaliseCourtBooking = (booking = {}, index = 0) => {
  const startAt = booking.startAt ? new Date(booking.startAt) : null;
  const hasStartAt = startAt && !Number.isNaN(startAt.getTime());
  const derivedTime = hasStartAt
    ? `${String(startAt.getHours()).padStart(2, "0")}:${String(startAt.getMinutes()).padStart(2, "0")}`
    : "";
  const currency = normaliseCurrencyCode(booking.currency || marketplaceCurrencyForCountry(booking.countryCode));
  const reservationSegments = (Array.isArray(booking.reservationSegments) ? booking.reservationSegments : [])
    .map((segment) => {
      const segmentStart = segment.startAt ? new Date(segment.startAt) : null;
      const hasSegmentStart = segmentStart && !Number.isNaN(segmentStart.getTime());
      return {
        ...segment,
        date: segment.date || (hasSegmentStart ? localIsoDateFromDate(segmentStart) : ""),
        time: segment.time || (hasSegmentStart
          ? `${String(segmentStart.getHours()).padStart(2, "0")}:${String(segmentStart.getMinutes()).padStart(2, "0")}`
          : ""),
        durationHours: Number(segment.durationHours) || (Number(segment.durationMinutes) ? Number(segment.durationMinutes) / 60 : 1),
        courtCount: Math.max(1, Number(segment.courtCount) || 1),
        amount: Number(segment.subtotal) || minorUnitsToAmount(segment.amountCents, currency)
      };
    });
  return {
    ...booking,
    id: booking.id || booking.bookingId || `court-booking-${index}`,
    venueId: booking.venueId || booking.centreId || "",
    venueName: booking.venueName || booking.venue || booking.centreName || "Badminton centre",
    city: booking.city || "",
    suburb: booking.suburb || "",
    address: booking.address || "",
    date: booking.date || (hasStartAt ? localIsoDateFromDate(startAt) : ""),
    time: booking.time || derivedTime,
    durationHours: Number(booking.durationHours) || (Number(booking.durationMinutes) ? Number(booking.durationMinutes) / 60 : 2),
    courtCount: Number(booking.courtCount) || 1,
    courtId: booking.courtId || "",
    courtName: booking.courtName || "",
    courtType: booking.courtType || "",
    rateType: booking.rateType || booking.rateLabel || booking.rateKey || "",
    rateWindow: booking.rateWindow || "",
    currency,
    hourlyPrice: Number(booking.hourlyPrice) || minorUnitsToAmount(booking.hourlyPriceCents, currency),
    pricingSchedule: booking.pricingSchedule || booking.pricingSource || "",
    subtotal: Number(booking.subtotal) || minorUnitsToAmount(booking.amountCents, currency),
    fee: Number(booking.fee) || minorUnitsToAmount(booking.serviceFeeCents, currency),
    total: Number(booking.total) || minorUnitsToAmount(booking.totalCents, currency),
    status: booking.status || "Requested",
    paymentStatus: booking.paymentStatus || "Payment pending",
    bookedAt: booking.bookedAt || booking.createdAt || "",
    reservationSegments
  };
};
const getBookingTotalLabel = (booking = {}) => booking.total ? formatMoney(booking.total, booking.currency) : "";
const playerBookingHasCompletedCheckout = (booking = {}) => {
  const source = `${booking.status || ""} ${booking.bookingStatus || ""} ${booking.paymentStatus || ""} ${booking.settlementMode || ""}`.toLowerCase();
  return /confirmed|completed|paid_with_credits|paid with centre credits|not_required|pay_at_venue|pay at venue|offline_settlement|refunded|payment.*paid|\\bpaid\\b/.test(source)
    && !/pending_payment|requires_payment|checkout_created|checkout opened/.test(source);
};
const getAccountBookings = (accountProfile = {}) => (Array.isArray(accountProfile.bookings) ? accountProfile.bookings : [])
  .map(normaliseCourtBooking)
  .filter(playerBookingHasCompletedCheckout)
  .sort((first, second) => `${second.bookedAt || ""}`.localeCompare(`${first.bookedAt || ""}`));
const venueFromBooking = (booking = {}) => venues.find((venue) => venue.id === booking.venueId)
  || venues.find((venue) => normaliseLocationText(venue.name).toLowerCase() === normaliseLocationText(booking.venueName).toLowerCase())
  || {
    id: booking.venueId || `booking-venue-${slugifyPath(booking.venueName)}`,
    name: booking.venueName || "Badminton centre",
    city: booking.city || "Melbourne",
    suburb: booking.suburb || "",
    address: booking.address || "",
    pricing: booking.total ? formatMoney(booking.total, booking.currency) : "",
    source: "Booking record"
  };
const bookingMatchesCentre = (booking = {}, centre = {}) => {
  const centreName = normaliseLocationText(centre.name).toLowerCase();
  const centreAddress = normaliseLocationText(centre.address).toLowerCase();
  const bookingName = normaliseLocationText(booking.venueName).toLowerCase();
  const bookingAddress = normaliseLocationText(booking.address).toLowerCase();
  return Boolean((centreName && bookingName && (centreName === bookingName || bookingName.includes(centreName) || centreName.includes(bookingName)))
    || (centreAddress && bookingAddress && (centreAddress === bookingAddress || bookingAddress.includes(centreAddress) || centreAddress.includes(bookingAddress))));
};

const courtStatusOptions = ["Published", "Unavailable", "Maintenance"];
const courtTypeOptions = ["Badminton", "Multi-sport", "Training court", "Competition court"];
const defaultCourtPricing = defaultAccountProfile.centre.courtPricing;
const courtRateRuleDefaults = {
  offPeak: { enabled: false, days: ["mon", "tue", "wed", "thu", "fri"], start: "06:00", end: "16:00" },
  peak: { enabled: false, days: ["mon", "tue", "wed", "thu", "fri"], start: "16:00", end: "23:00" },
  weekend: { enabled: false, days: ["sat", "sun"], start: "06:00", end: "23:00" }
};
const courtRateRuleLabels = { offPeak: "Off-peak", peak: "Peak", weekend: "Weekend" };
const courtRateTimeOptions = Array.from({ length: 48 }, (_, index) => {
  const minutes = index * 30;
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
});
const courtRateEndTimeOptions = [...courtRateTimeOptions.slice(1), "23:59"];
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};
const supportedTimeZones = (() => {
  try {
    const values = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
    return Array.from(new Set([browserTimeZone(), "UTC", ...values])).filter(Boolean);
  } catch {
    return [browserTimeZone(), "UTC"];
  }
})();

const normaliseCourtRateRules = (rateRules = {}) => Object.fromEntries(
  Object.entries(courtRateRuleDefaults).map(([key, defaults]) => {
    const source = rateRules?.[key] && typeof rateRules[key] === "object" ? rateRules[key] : {};
    const days = Array.from(new Set((Array.isArray(source.days) ? source.days : defaults.days)
      .map((day) => String(day || "").toLowerCase().slice(0, 3))
      .filter((day) => availabilityDayOptions.some(([option]) => option === day))));
    return [key, {
      enabled: source.enabled === true,
      days,
      start: /^\d{2}:\d{2}$/.test(source.start || "") ? source.start : defaults.start,
      end: /^\d{2}:\d{2}$/.test(source.end || "") ? source.end : defaults.end
    }];
  })
);

const formatCourtFeeValue = (value = "", currency = "aud") => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/free|included|pending|varies/i.test(text)) return text;
  const number = text.match(/\d+(?:\.\d{1,2})?/);
  if (!number) return text;
  const leadingText = text.slice(0, number.index).trim();
  if (leadingText && !/^(?:from|about|approx\.?|approximately)$/i.test(leadingText)) return text;
  const formattedAmount = formatMoney(Number.parseFloat(number[0]), normaliseCurrencyCode(currency));
  const formattedText = text.replace(number[0], formattedAmount);
  return /(?:per\s+(?:hour|hr)|\/\s*(?:hour|hr)|hourly)/i.test(text) ? formattedText : `${formattedText}/hr`;
};

const getCourtFeeNumber = (value = "") => {
  const match = String(value || "").match(/\d+(?:\.\d{1,2})?/);
  const parsed = match ? Number.parseFloat(match[0]) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const isConfirmedCourtFeeValue = (value = "") => {
  const text = String(value || "").trim();
  return Boolean(text && (/free|included/i.test(text) || getCourtFeeNumber(text) > 0));
};

const normaliseCourtPricing = (courtPricing = {}, fallbackPrice = "") => ({
  ...defaultCourtPricing,
  ...(courtPricing || {}),
  standard: courtPricing?.standard || fallbackPrice || "",
  offPeak: courtPricing?.offPeak || "",
  peak: courtPricing?.peak || "",
  weekend: courtPricing?.weekend || "",
  offPeakWindow: courtPricing?.offPeakWindow || defaultCourtPricing.offPeakWindow,
  peakWindow: courtPricing?.peakWindow || defaultCourtPricing.peakWindow,
  weekendWindow: courtPricing?.weekendWindow || defaultCourtPricing.weekendWindow,
  rateRules: normaliseCourtRateRules(courtPricing?.rateRules),
  notes: courtPricing?.notes || ""
});

const courtPricingRows = (courtPricing = {}) => {
  const pricing = normaliseCourtPricing(courtPricing);
  return [
    { key: "standard", label: "Standard", fee: pricing.standard, window: "Default court hire" },
    { key: "offPeak", label: "Off-peak", fee: pricing.offPeak, window: pricing.offPeakWindow },
    { key: "peak", label: "Peak", fee: pricing.peak, window: pricing.peakWindow },
    { key: "weekend", label: "Weekend", fee: pricing.weekend, window: pricing.weekendWindow }
  ];
};

const hasCourtPricing = (courtPricing = {}) => courtPricingRows(courtPricing).some((row) => isConfirmedCourtFeeValue(row.fee));
const hasStandardCourtPricing = (courtPricing = {}, fallbackPrice = "") => isConfirmedCourtFeeValue(normaliseCourtPricing(courtPricing, fallbackPrice).standard);

const resolveCourtRateForLocalSelection = ({ courtPricing = {}, fallbackPrice = "", date, time, durationHours = 1 }) => {
  const pricing = normaliseCourtPricing(courtPricing, fallbackPrice);
  const rows = courtPricingRows(pricing);
  const standard = rows.find((row) => row.key === "standard") || rows[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "") || !/^\d{2}:\d{2}$/.test(time || "")) return standard;
  const [hour, minute] = time.split(":").map(Number);
  const startMinutes = hour * 60 + minute;
  const endMinutes = startMinutes + Math.round(Number(durationHours || 1) * 60);
  if (endMinutes > 24 * 60) return standard;
  const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date(`${date}T00:00:00Z`).getUTCDay()];
  const rules = pricing.rateRules;
  const matchedKey = ["weekend", "peak", "offPeak"].find((key) => {
    const rule = rules[key];
    const [startHour, startMinute] = rule.start.split(":").map(Number);
    const [endHour, endMinute] = rule.end.split(":").map(Number);
    return rule.enabled
      && rule.days.includes(dayKey)
      && startMinutes >= startHour * 60 + startMinute
      && endMinutes <= endHour * 60 + endMinute;
  });
  const matched = rows.find((row) => row.key === matchedKey && isConfirmedCourtFeeValue(row.fee));
  return matched || standard;
};

const getCourtPricingSummary = (courtPricing = {}, fallbackPrice = "", currency = "aud") => {
  const pricing = normaliseCourtPricing(courtPricing, fallbackPrice);
  const rows = courtPricingRows(pricing)
    .filter((row) => isConfirmedCourtFeeValue(row.fee))
    .map((row) => `${row.label} ${formatCourtFeeValue(row.fee, currency)}`);
  return rows.length ? rows.slice(0, 3).join(" / ") : "";
};

const courtPricingRateOptions = (courtPricing = {}, fallbackPrice = "", currency = "aud") => {
  const pricing = normaliseCourtPricing(courtPricing, fallbackPrice);
  return courtPricingRows(pricing)
    .map((row) => ({
      ...row,
      formattedFee: formatCourtFeeValue(row.fee, currency),
      price: getCourtFeeNumber(row.fee)
    }))
    .filter((row) => row.price > 0 || /free|included/i.test(row.fee));
};

const getCourtPricingPrimaryNumber = (courtPricing = {}, fallbackPrice = "") => {
  const pricing = normaliseCourtPricing(courtPricing, fallbackPrice);
  const firstValue = [pricing.standard, pricing.offPeak, pricing.peak, pricing.weekend, fallbackPrice].find(Boolean) || "";
  return getCourtFeeNumber(firstValue);
};

const centreCourtCountNumber = (centre = {}, venue = {}) => {
  venue = venue || {};
  const profileCount = Number.parseInt(centre.courtCount, 10);
  if (Number.isFinite(profileCount) && profileCount > 0) return profileCount;
  const venueCount = Number.parseInt(venue.courts, 10);
  return Number.isFinite(venueCount) && venueCount > 0 ? venueCount : 0;
};

const normaliseCourtInventory = (centre = {}, venue = {}) => {
  venue = venue || {};
  const count = Math.min(48, centreCourtCountNumber(centre, venue));
  const rows = Array.isArray(centre.courtInventory) ? centre.courtInventory : [];
  const courtPricing = normaliseCourtPricing(centre.courtPricing, centre.pricing || venue.priceLabel || "");
  const defaultPrice = courtPricing.standard || courtPricing.offPeak || centre.pricing || venue.priceLabel || "";
  return Array.from({ length: count }, (_, index) => {
    const saved = rows[index] || {};
    return {
      id: saved.id || `court-${index + 1}`,
      name: saved.name || `Court ${index + 1}`,
      type: saved.type || "Badminton",
      status: saved.status || "Published",
      price: saved.price || defaultPrice,
      notes: saved.notes || ""
    };
  });
};

const publishedCourtInventory = (centre = {}, venue = {}) => normaliseCourtInventory(centre, venue)
  .filter((court) => court.status === "Published");

const hasCentreOwnerBookingIdentity = (centre = {}) => Boolean(
  centre.claimedVenueId
  || centre.name
  || centre.address
  || centre.contactEmail
  || centre.phone
  || centre.website
);

const getCentreBookingReadiness = (centre = {}, venue = {}) => {
  const courtPricing = normaliseCourtPricing(centre.courtPricing, centre.pricing || venue.priceLabel || "");
  const currency = normaliseCurrencyCode(centre.currency || venue.currency || marketplaceCurrencyForCountry(centre.countryCode || centre.location?.countryCode || venue.countryCode || venue.country));
  const courts = publishedCourtInventory(centre, venue);
  const courtCount = centreCourtCountNumber(centre, venue);
  const openingHours = summarizeAvailabilitySchedule(centre.openingSchedule) || centre.openingHours || venue.openingHours || "";
  const missing = [];
  if (!(centre.name || venue.name)) missing.push("centre name");
  if (!(centre.suburb || centre.city || centre.location?.suburb || centre.location?.city || venue.suburb || venue.city)) missing.push("suburb or city");
  if (!(centre.address || venue.address)) missing.push("street address");
  if (!(courtCount || courts.length)) missing.push("court inventory");
  if (!hasStandardCourtPricing(courtPricing, centre.pricing || venue.priceLabel || "")) missing.push("standard court fee");
  if (!openingHours) missing.push("opening hours");
  if (!(centre.timeZone || venue.timeZone)) missing.push("centre time zone");
  return {
    ready: missing.length === 0,
    missing,
    courtCount,
    publishedCourtCount: courts.length || courtCount,
    openingHours,
    timeZone: centre.timeZone || venue.timeZone || "",
    priceLabel: getCourtPricingSummary(courtPricing, centre.pricing || venue.priceLabel || "", currency) || centre.pricing || venue.priceLabel || "",
    courtPricing,
    currency
  };
};

const centreVerificationApprovedPattern = /\b(verified|approved|owner_verified|claim_approved)\b/i;
const centreVerificationRejectedPattern = /\b(unverified|pending|review|requested|rejected|draft|evidence|needs_more_info|needs info)\b/i;

const isCentreVerificationApproved = (record = {}) => {
  const statusText = [
    record.verificationStatus,
    record.claimStatus,
    record.status,
    record.decision,
    record.platformVerificationStatus
  ].filter(Boolean).join(" ");
  if (centreVerificationRejectedPattern.test(statusText)) return false;
  if (record.verified === true || record.platformVerifiedByBadminta === true) return true;
  return centreVerificationApprovedPattern.test(statusText);
};

const isOwnerManagedCentreListing = (venue = {}) => {
  const source = String(venue.source || "").toLowerCase();
  return Boolean(
    ["account", "owner", "owner-created"].includes(source)
    || venue.platformOwnerKind
    || venue.ownerCreated
    || venue.ownerUid
    || venue.claimedVenueId
    || venue.serverCentreId
    || venue.firestoreSourceId
  );
};

const getCentreVerifiedProfileReadiness = (centre = {}, venue = {}) => {
  const missing = [];
  if (!(centre.name || venue.name)) missing.push("centre name");
  if (!(centre.suburb || centre.city || centre.location?.suburb || centre.location?.city || venue.suburb || venue.city)) missing.push("suburb or city");
  if (!(centre.address || venue.address)) missing.push("street address");
  if (!centreDescriptionIsReady(centre.description)) missing.push("centre description");
  if (!centre.logoUrl) missing.push("centre logo");
  if (!centre.coverUrl) missing.push("cover image");
  if (centrePublicMediaRows(centre).length < centreMediaMinimumItems) missing.push(`${centreMediaMinimumItems} venue media items`);
  if (!centreHasPublicContact(centre)) missing.push("phone or contact email");
  if (!centreHasCourtCount(centre)) missing.push("court count");
  if (!centreHasStructuredHours(centre)) missing.push("weekly opening hours");
  return {
    ready: missing.length === 0,
    missing: Array.from(new Set(missing))
  };
};

const isVerifiedCentreListing = (venue = {}) => {
  if (!isOwnerManagedCentreListing(venue)) return false;
  if (!isCentreVerificationApproved(venue)) return false;
  if (venue.platformOwnerVerified === true || venue.bookingEnabled === true) return true;
  return getCentreVerifiedProfileReadiness(venue, venue).ready;
};

const managedVenueFromCentreProfile = (centre = {}, venue = {}) => {
  venue = venue || {};
  const openingHours = summarizeAvailabilitySchedule(centre.openingSchedule) || centre.openingHours || venue.openingHours || "";
  const courts = centreCourtCountNumber(centre, venue);
  const countryCode = normaliseCountryCode(centre.countryCode || centre.location?.countryCode || venue.countryCode || venue.country, centre.city || centre.location?.city || venue.city);
  const currency = normaliseCurrencyCode(centre.currency || venue.currency || marketplaceCurrencyForCountry(countryCode));
  const courtPricing = normaliseCourtPricing(centre.courtPricing, centre.pricing || venue.priceLabel || "");
  const priceLabel = getCourtPricingSummary(courtPricing, centre.pricing || venue.priceLabel || "", currency) || centre.pricing || venue.priceLabel || (typeof venue.price === "number" ? formatMoney(venue.price, currency) : "");
  const bookingReadiness = getCentreBookingReadiness({ ...centre, courtPricing }, venue);
  const verificationReadiness = getCentreVerifiedProfileReadiness({ ...centre, courtPricing }, venue);
  const ownerProfileExists = hasCentreOwnerBookingIdentity(centre);
  const platformOwnerVerified = ownerProfileExists
    && (isCentreVerificationApproved(centre) || isCentreVerificationApproved(venue))
    && (centre.bookingEnabled === true || venue.bookingEnabled === true || verificationReadiness.ready);
  const backendCentreId = getCentreBackendSourceId(centre);
  const proProfile = centre.proProfile === true || String(centre.planTier || "").toLowerCase() === "pro";
  return {
    ...venue,
    id: backendCentreId || venue.id || `managed-${slugifyPath(centre.name || "centre")}`,
    name: centre.name || venue.name || "Managed badminton centre",
    address: centre.address || venue.address || "",
    suburb: centre.suburb || venue.suburb || "",
    city: centre.city || centre.location?.city || venue.city || inferEventCityFromText(`${centre.suburb} ${centre.address}`, "Melbourne"),
    state: centre.location?.state || venue.state || "",
    postcode: centre.location?.postcode || venue.postcode || "",
    country: centre.country || centre.location?.country || venue.country || "",
    countryCode,
    currency,
    lat: Number.isFinite(Number(centre.location?.lat)) ? Number(centre.location.lat) : venue.lat,
    lon: Number.isFinite(Number(centre.location?.lon)) ? Number(centre.location.lon) : venue.lon,
    location: locationSnapshot(centre.location) || locationFromVenue(venue),
    phone: centre.phone || venue.phone || "",
    email: centre.contactEmail || venue.email || "",
    website: centre.website || venue.website || "",
    description: centre.description || venue.description || "",
    logoUrl: centre.logoUrl || "",
    coverUrl: centre.coverUrl || "",
    image: centre.coverUrl || centre.logoUrl || venue.image || "",
    gallery: collectImageUrls([centre.coverUrl, centre.photoLinks, centre.logoUrl, venue.gallery, venue.image]),
    directorySource: venue.source || "",
    source: centre.claimedVenueId ? "owner" : ownerProfileExists ? "owner-created" : venue.source,
    courts,
    priceLabel,
    pricing: centre.pricing || courtPricing.standard || "",
    courtPricing,
    openingHours,
    openingSchedule: normaliseAvailabilitySchedule(centre.openingSchedule),
    timeZone: centre.timeZone || venue.timeZone || "",
    bookingRules: centre.bookingRules || "",
    bookingEnabled: centre.bookingEnabled === true || venue.bookingEnabled === true,
    courtInventory: normaliseCourtInventory({ ...centre, courtCount: courts }, venue),
    bookingReadiness,
    verificationReadiness,
    platformOwnerVerified,
    platformVerifiedByBadminta: platformOwnerVerified,
    verified: platformOwnerVerified,
    platformVerificationStatus: platformOwnerVerified ? "verified" : centre.verificationStatus || centre.claimStatus || venue.verificationStatus || "",
    claimStatus: centre.claimStatus || venue.claimStatus || "",
    verificationStatus: centre.verificationStatus || venue.verificationStatus || "",
    ownerCreated: ownerProfileExists && !centre.claimedVenueId,
    ownerUid: centre.ownerUid || venue.ownerUid || "",
    planTier: proProfile ? "pro" : "free",
    proProfile,
    platformOwnerKind: centre.claimedVenueId ? "claimed centre owner" : ownerProfileExists ? "owner-created centre" : "",
    claimedVenueId: centre.claimedVenueId && backendCentreId === centre.claimedVenueId ? centre.claimedVenueId : backendCentreId || "",
    serverCentreId: backendCentreId,
    firestoreSourceId: centre.firestoreSourceId || backendCentreId,
    isPlatformBookable: Boolean(backendCentreId) && platformOwnerVerified && bookingReadiness.ready && proProfile,
    badge: "Centre managed listing"
  };
};

const getCentreBackendSourceId = (centre = {}) => {
  const approvedClaim = isCentreVerificationApproved(centre);
  return centre.serverCentreId || centre.firestoreSourceId || (approvedClaim ? centre.claimedVenueId : "") || "";
};

const hasStructuredCentreHours = (centre = {}) => normaliseAvailabilitySchedule(centre.openingSchedule).some((row) => row.enabled);

const buildOwnedCentreBackendPayload = (centre = {}, venue = {}, { publishWhenReady = false } = {}) => {
  const location = locationSnapshot(centre.location) || locationFromVenue(venue);
  const courtPricing = normaliseCourtPricing(centre.courtPricing, centre.pricing || venue.priceLabel || "");
  const openingSchedule = normaliseAvailabilitySchedule(centre.openingSchedule);
  const nextCentre = {
    ...centre,
    location,
    courtPricing,
    openingSchedule,
    courtInventory: normaliseCourtInventory(centre, venue)
  };
  const canPublish = publishWhenReady && Boolean(
    String(nextCentre.name || venue.name || "").trim()
    && hasPublicProfileLocation({ ...venue, ...nextCentre, location })
    && String(nextCentre.address || venue.address || "").trim()
    && centreDescriptionIsReady(nextCentre.description)
    && String(nextCentre.logoUrl || "").trim()
    && String(nextCentre.coverUrl || "").trim()
    && centrePublicMediaRows(nextCentre).length >= centreMediaMinimumItems
    && centreHasPublicContact(nextCentre)
    && centreHasCourtCount(nextCentre)
    && centreHasStructuredHours(nextCentre)
  );
  const centreId = getCentreBackendSourceId(nextCentre);
  const approvedClaim = isCentreVerificationApproved(nextCentre);
  const claimedVenueId = approvedClaim ? nextCentre.claimedVenueId || "" : "";
  const countryCode = normaliseCountryCode(nextCentre.countryCode || location?.countryCode || venue.countryCode || venue.country, nextCentre.city || location?.city || venue.city);
  const currency = normaliseCurrencyCode(nextCentre.currency || marketplaceCurrencyForCountry(countryCode));
  return {
    centreId,
    serverCentreId: nextCentre.serverCentreId || "",
    firestoreSourceId: nextCentre.firestoreSourceId || "",
    claimedVenueId,
    claimId: nextCentre.claimId || "",
    name: nextCentre.name || venue.name || "",
    address: nextCentre.address || venue.address || "",
    suburb: nextCentre.suburb || venue.suburb || location?.suburb || "",
    city: nextCentre.city || location?.city || venue.city || "",
    state: nextCentre.state || location?.state || venue.state || "",
    postcode: nextCentre.postcode || location?.postcode || venue.postcode || "",
    country: nextCentre.country || location?.country || venue.country || "",
    countryCode,
    location,
    contactEmail: nextCentre.contactEmail || nextCentre.email || "",
    phone: nextCentre.phone || "",
    website: nextCentre.website || "",
    abn: nextCentre.abn || "",
    description: nextCentre.description || "",
    logoUrl: nextCentre.logoUrl || "",
    coverUrl: nextCentre.coverUrl || "",
    photoLinks: markUserSubmittedMediaLinks(nextCentre.photoLinks, "Centre photo"),
    socialLinks: cleanLinkRows(nextCentre.socialLinks, "Centre social"),
    courtCount: nextCentre.courtCount || String(nextCentre.courtInventory.length || ""),
    pricing: nextCentre.pricing || courtPricing.standard || "",
    priceLabel: getCourtPricingSummary(courtPricing, nextCentre.pricing || venue.priceLabel || "", currency),
    courtPricing,
    openingSchedule,
    timeZone: nextCentre.timeZone || venue.timeZone || browserTimeZone(),
    openingHours: summarizeAvailabilitySchedule(openingSchedule) || nextCentre.openingHours || venue.openingHours || "",
    bookingRules: nextCentre.bookingRules || "",
    courtInventory: nextCentre.courtInventory,
    currency,
    status: canPublish ? "published" : "draft"
  };
};

const buildPlayerBackendPayload = (player = {}, accountProfile = {}) => {
  const location = locationSnapshot(player.location);
  const availabilitySchedule = normaliseAvailabilitySchedule(player.availabilitySchedule);
  const nextProfile = { ...accountProfile, roles: Array.from(new Set([...(accountProfile.roles || []), "player"])), player: { ...player, location, availabilitySchedule } };
  const readyToPublish = getPublicProfileMissingFields(nextProfile, "player").length === 0;
  return {
    profileId: player.publicPlayerId || player.firestoreSourceId || "",
    publicPlayerId: player.publicPlayerId || "",
    name: accountProfile.fullName || accountProfile.displayName || "",
    fullName: accountProfile.fullName || accountProfile.displayName || "",
    photoUrl: player.photoUrl || accountProfile.photoUrl || "",
    skillBand: player.skillBand || "",
    suburb: location ? location.suburb || "" : player.suburb || "",
    city: location?.city || player.city || "",
    country: location?.country || player.country || "",
    countryCode: normaliseCountryCode(location?.countryCode || location?.country || player.countryCode || player.country || "", location?.city || player.city || ""),
    location,
    formats: player.formats || "",
    about: player.about || player.style || "",
    style: player.about || player.style || "",
    availability: summarizeAvailabilitySchedule(availabilitySchedule) || player.availability || "",
    availabilitySchedule,
    mediaLinks: markUserSubmittedMediaLinks(player.mediaLinks, "Player media"),
    socialLinks: cleanLinkRows(player.socialLinks, "Player social"),
    visibility: player.visibility || {},
    status: readyToPublish ? "published" : "draft"
  };
};

const buildCoachBackendPayload = (coach = {}, accountProfile = {}) => {
  const location = locationSnapshot(coach.location);
  const availabilitySchedule = normaliseAvailabilitySchedule(coach.availabilitySchedule);
  const resolvedSuburb = coach.suburb || location?.suburb || (location?.type === "suburb" ? location.label : "");
  const resolvedCity = coach.city || location?.city || (location?.type === "city" ? location.label : "");
  const nextProfile = {
    ...accountProfile,
    roles: Array.from(new Set([...(accountProfile.roles || []), "coach"])),
    coach: { ...coach, suburb: resolvedSuburb, city: resolvedCity, location, availabilitySchedule }
  };
  const readyToPublish = getPublicProfileMissingFields(nextProfile, "coach").length === 0;
  const countryCode = normaliseCountryCode(coach.countryCode || location?.countryCode || "", resolvedCity);
  const currency = normaliseCurrencyCode(coach.currency || marketplaceCurrencyForCountry(countryCode));
  const lessonServices = normaliseCoachLessonServices(coach.lessonServices, coach.hourlyRate, currency);
  return {
    profileId: coach.publicCoachId || coach.firestoreSourceId || "",
    publicCoachId: coach.publicCoachId || "",
    name: accountProfile.fullName || accountProfile.displayName || "",
    fullName: accountProfile.fullName || accountProfile.displayName || "",
    headline: coach.headline || "",
    bio: coach.bio || "",
    photoUrl: coach.photoUrl || accountProfile.photoUrl || "",
    accreditation: coach.accreditation || "",
    hourlyRate: coach.hourlyRate || "",
    locations: coach.locations || "",
    suburb: resolvedSuburb,
    city: resolvedCity,
    country: coach.country || location?.country || "",
    countryCode,
    location,
    availability: summarizeAvailabilitySchedule(availabilitySchedule) || coach.availability || "",
    availabilitySchedule,
    lessonServices,
    timeZone: coach.timeZone || currentBrowserTimeZone(),
    bookingNoticeHours: Math.max(0, Math.min(168, Number(coach.bookingNoticeHours) || 0)),
    bookingWindowDays: Math.max(1, Math.min(180, Number(coach.bookingWindowDays) || 30)),
    bufferMinutes: Math.max(0, Math.min(120, Number(coach.bufferMinutes) || 0)),
    mediaLinks: markUserSubmittedMediaLinks(coach.mediaLinks, "Coach media"),
    socialLinks: cleanLinkRows(coach.socialLinks, "Coach social"),
    currency,
    status: readyToPublish ? "published" : "draft"
  };
};

const isPlatformOwnedCentre = (venue = {}, accountProfile = defaultAccountProfile) => {
  const centre = accountProfile.centre || {};
  if (!hasCentreOwnerBookingIdentity(centre)) return false;
  const backendSourceId = getCentreBackendSourceId(centre);
  if (backendSourceId && [venue.id, venue.claimedVenueId, venue.serverCentreId, venue.firestoreSourceId].filter(Boolean).some((id) => String(id) === String(backendSourceId))) return true;
  if (isCentreClaimedByAccount(venue, accountProfile) && backendSourceId && String(backendSourceId) === String(centre.claimedVenueId)) return true;
  if (venue.id === "account-public-centre" || String(venue.id || "").startsWith("managed-")) return true;
  if (["account", "owner", "owner-created"].includes(String(venue.source || "").toLowerCase())) return true;
  return false;
};

const getPlatformBookingVenue = (venue = {}, accountProfile = defaultAccountProfile) => {
  const centre = accountProfile.centre || {};
  if (isPlatformOwnedCentre(venue, accountProfile)) {
    return managedVenueFromCentreProfile(centre, venue);
  }
  return venue;
};

const getPlatformCentreBookingState = (venue = {}, accountProfile = defaultAccountProfile) => {
  const ownedByViewer = isPlatformOwnedCentre(venue, accountProfile);
  const ownerManaged = ownedByViewer || isOwnerManagedCentreListing(venue);
  const bookingVenue = ownedByViewer ? getPlatformBookingVenue(venue, accountProfile) : venue;
  const readiness = bookingVenue.bookingReadiness || getCentreBookingReadiness(accountProfile.centre || {}, bookingVenue);
  const backendSourceId = (ownedByViewer ? getCentreBackendSourceId(accountProfile.centre || {}) : "")
    || bookingVenue.serverCentreId
    || bookingVenue.firestoreSourceId
    || bookingVenue.claimedVenueId
    || "";
  const verifiedOwner = ownerManaged && isVerifiedCentreListing(bookingVenue);
  const subscriptionReady = bookingVenue.proProfile === true || String(bookingVenue.planTier || "").toLowerCase() === "pro";
  const backendReady = verifiedOwner && Boolean(backendSourceId) && subscriptionReady;
  const missing = ownerManaged
    ? [
      ...(verifiedOwner ? [] : ["Badminta centre verification"]),
      ...(backendSourceId ? [] : ["backend centre source"]),
      ...(subscriptionReady ? [] : ["Centre Pro online bookings"]),
      ...readiness.missing
    ]
    : ["verified centre owner", ...readiness.missing];
  return {
    owned: ownerManaged,
    ownedByViewer,
    ready: backendReady && readiness.ready,
    venue: bookingVenue,
    backendSourceId,
    verifiedOwner,
    subscriptionReady,
    readiness: { ...readiness, ready: backendReady && readiness.ready, missing },
    ownershipLabel: verifiedOwner ? "Verified owner-managed centre" : ownerManaged ? "Owner-managed centre awaiting verification" : "Directory listing only"
  };
};
const inferEventCityFromText = (value = "", fallbackCity = "Melbourne") => cities.find((cityName) => String(value || "").toLowerCase().includes(cityName.toLowerCase())) || fallbackCity;
const inferEventSuburbFromText = (value = "") => String(value || "").split(",")[0]?.trim() || "";

const createEventDraft = (accountProfile = {}, ownerType = "organiser", city = "Melbourne") => {
  const group = accountProfile.group || {};
  const centre = accountProfile.centre || {};
  const sourceVenueId = ownerType === "centre" ? "" : group.venueId || "";
  const venue = sourceVenueId ? venues.find((item) => item.id === sourceVenueId) : null;
  const draftLocation = ownerType === "centre"
    ? locationSnapshot(centre.location) || locationSnapshot({
      label: centre.suburb || centre.city || city,
      suburb: centre.suburb || "",
      city: centre.city || inferEventCityFromText(`${centre.suburb} ${centre.address}`, city),
      type: centre.suburb ? "suburb" : "city"
    })
    : locationSnapshot(group.location) || locationFromVenue(venue);
  const ownerName = ownerType === "centre"
    ? centre.name || accountProfile.fullName || "Centre owner"
    : group.name || accountProfile.fullName || "Social organiser";
  return {
    id: "",
    ownerType,
    ownerName,
    title: "",
    eventType: "",
    description: "",
    venue: ownerType === "centre" ? centre.name || "" : group.venue || venue?.name || "",
    venueId: sourceVenueId,
    city: draftLocation?.city || (ownerType === "centre" ? inferEventCityFromText(`${centre.suburb} ${centre.address}`, city) : venue?.city || city),
    suburb: draftLocation?.suburb || (ownerType === "centre" ? inferEventSuburbFromText(centre.suburb) : venue?.suburb || ""),
    location: draftLocation,
    date: "",
    time: "",
    endTime: "",
    capacity: "",
    price: group.price || "",
    pricingMode: "paid",
    skillBand: group.skillBand || "",
    status: "published",
    coverUrl: ownerType === "centre" ? centre.logoUrl || "" : group.coverUrl || group.logoUrl || "",
    requirements: "",
    refundPolicy: "",
    inviteEmails: [],
    socialLinks: []
  };
};

const pageGroups = [
  {
    label: "Public marketplace",
    pages: [
      ["home", "Home directory", Home],
      ["results", "Search results + map", Map],
      ["city", "City page", Navigation],
      ["venue", "Venue detail", Building2],
      ["group", "Social group detail", UsersRound],
      ["player", "Player profile", UserCircle],
      ["event", "Event detail", Star]
    ]
  },
  {
    label: "Player flows",
    pages: [
      ["playerDashboard", "Player dashboard", LayoutDashboard],
      ["booking", "Court booking checkout", CreditCard],
      ["join", "Join social checkout", CalendarDays],
      ["profileSetup", "Player profile setup", Camera],
      ["coachDashboard", "Coach dashboard", LayoutDashboard],
      ["coachProfile", "Coach profile setup", Trophy],
      ["coachPayouts", "Coach payouts", WalletCards],
      ["ratings", "Smart rating system", Gauge],
      ["messages", "Invites and messages", MessageCircle],
      ["reviewFlow", "Reviews and reports", Star],
      ["paymentFlow", "Payment setup", WalletCards]
    ]
  },
  {
    label: "Organiser tools",
    pages: [
      ["organiser", "Organiser dashboard", LayoutDashboard],
      ["createGroup", "Group profile wizard", UsersRound],
      ["groupSessions", "Manage group sessions", ClipboardCheck],
      ["createGroupSession", "Create group session", Plus],
      ["createEvent", "Create event", Star],
      ["eventManager", "Event dashboard", CalendarDays],
      ["joinedEvents", "Joined events", ClipboardCheck],
      ["manageEvent", "Manage event", Activity],
      ["payouts", "Organiser payouts", WalletCards],
      ["members", "Member management", ClipboardCheck]
    ]
  },
  {
    label: "Centre tools",
    pages: [
      ["centreDashboard", "Centre owner dashboard", Building2],
      ["claimCentre", "Add/edit centre profile", Building2],
      ["centreClaimSearch", "Search and claim centre", ShieldCheck],
      ["courtManager", "Court inventory", Grid3X3],
      ["centrePayments", "Centre payments", Banknote]
    ]
  },
  {
    label: "Platform admin",
    pages: [
      ["admin", "Network admin", ShieldCheck],
      ["trust", "Trust and moderation", BadgeCheck],
      ["analytics", "Marketplace analytics", Activity]
    ]
  }
];

const topModes = [
  ["all", "All", Sparkles],
  ["centres", "Find centres", Building2],
  ["socials", "Join groups", UsersRound],
  ["players", "Players", Trophy],
  ["events", "Events", Star]
];

const playTagOptions = [
  { id: "centres", label: "Centres / court hire", short: "Centres", Icon: Building2, mode: "centres", filter: "nearby" },
  { id: "courts", label: "Courts", short: "Courts", Icon: CalendarDays, mode: "courts", filter: "late" },
  { id: "socials", label: "Social games / groups", short: "Socials", Icon: UsersRound, mode: "socials", filter: "beginner" },
  { id: "players", label: "Players", short: "Players", Icon: Trophy, mode: "players", filter: "advanced" },
  { id: "coaches", label: "Coaches", short: "Coaches", Icon: ClipboardCheck, mode: "players", filter: "coaching" },
  { id: "events", label: "Events", short: "Events", Icon: Star, mode: "events", filter: "nearby" }
];

const modeTargets = {
  all: "home",
  centres: "results",
  socials: "groupsIndex",
  courts: "courtAvailability",
  players: "playersIndex",
  events: "events"
};

const modeDefaultFilters = {
  all: "nearby",
  centres: "nearby",
  socials: "beginner",
  courts: "late",
  players: "advanced",
  events: "nearby"
};

const modeDefaultResultTypes = {
  all: "centres",
  centres: "centres",
  socials: "groups",
  courts: "centres",
  players: "players",
  events: "events"
};

const categoryFilters = [
  ["nearby", "Nearby", Navigation, "filterHub"],
  ["verified", "Verified", BadgeCheck, "filterHub"],
  ["beginner", "Beginner", Heart, "filterHub"],
  ["advanced", "Advanced", Trophy, "filterHub"],
  ["women", "Women friendly", ShieldCheck, "filterHub"],
  ["parking", "Parking", MapPin, "filterHub"],
  ["coaching", "Coaching", ClipboardCheck, "filterHub"],
  ["late", "Open late", Clock3, "filterHub"],
  ["payouts", "Paid groups", CircleDollarSign, "filterHub"]
];

const filterContent = {
  nearby: {
    mode: "centres",
    resultsTitle: "Nearby badminton in Melbourne",
    marketplaceTitle: "Nearby badminton options in Melbourne",
    marketplaceText: "Centres are venues for court hire. Social groups are player-organised games at those centres with shared session cost.",
    eyebrow: "Nearby"
  },
  verified: {
    mode: "centres",
    resultsTitle: "Verified badminton centres in Melbourne",
    marketplaceTitle: "Verified badminton options in Melbourne",
    marketplaceText: "Centres shown here are claimed or created by centre owners, have their core operating details completed, and have been reviewed by Badminta.",
    eyebrow: "Verified centres"
  },
  beginner: {
    mode: "socials",
    groupsTitle: "Beginner friendly social groups in Melbourne",
    marketplaceTitle: "Beginner friendly badminton in Melbourne",
    marketplaceText: "Book a court directly through a centre, or join a beginner social group where an organiser has already arranged the session.",
    eyebrow: "Beginner"
  },
  advanced: {
    mode: "players",
    playersTitle: "Advanced players near you",
    marketplaceTitle: "Advanced badminton in Melbourne",
    marketplaceText: "Find stronger venues and players, or join organised advanced groups where players share the court cost.",
    eyebrow: "Advanced"
  },
  women: {
    mode: "socials",
    groupsTitle: "Women friendly badminton groups in Melbourne",
    marketplaceTitle: "Women friendly badminton in Melbourne",
    marketplaceText: "Compare centres for court hire and women friendly social games organised by players at those venues.",
    eyebrow: "Women friendly"
  },
  parking: {
    mode: "centres",
    resultsTitle: "Centres with parking in Melbourne",
    marketplaceTitle: "Badminton with parking in Melbourne",
    marketplaceText: "Centres and sessions where parking is listed or implied in public venue details.",
    eyebrow: "Parking"
  },
  coaching: {
    mode: "players",
    playersTitle: "Coaches and training partners near you",
    marketplaceTitle: "Badminton coaching in Melbourne",
    marketplaceText: "Centres can host coaching court time. Groups and coaches can organise sessions players join and pay for together.",
    eyebrow: "Coaching"
  },
  late: {
    mode: "courts",
    courtsTitle: "Open late courts in Melbourne",
    marketplaceTitle: "Open late badminton in Melbourne",
    marketplaceText: "Open late centres are for court booking. Evening groups are scheduled games players can join.",
    eyebrow: "Open late"
  },
  payouts: {
    mode: "socials",
    groupsTitle: "Paid social groups accepting players",
    marketplaceTitle: "Paid badminton sessions in Melbourne",
    marketplaceText: "Centres list court hire prices. Paid groups split the organised session cost across the players who join.",
    eyebrow: "Paid groups"
  }
};

const solutionPages = {
  centres: {
    page: "solutionCentres",
    label: "Centres",
    navLabel: "For centres",
    path: "/solutions/badminton-centres",
    Icon: Building2,
    mediaType: "centre",
    heroTitle: "Get more players booking your badminton courts",
    heroText: "Publish or claim a public centre listing for free. Upgrade to Centre Pro when you want Badminta to run court bookings, pricing, memberships, events and payments.",
    primary: ["Add or claim your centre", "centreClaimSearch"],
    secondary: ["Manage centre tools", "centreDashboard"],
    stats: [["Court inventory", "Hours, courts, fees"], ["Bookings", "Court requests and payments"], ["Members", "Credits and repeat play"]],
    problems: [
      ["Players need answers before they book", "Show price, hours, address, courts, photos and contact details clearly."],
      ["Payments are spread across messages", "Reduce cash, bank transfer and casual booking confusion."],
      ["Claimed data matters", "Owner-managed centres should stand out from unclaimed directory records."]
    ],
    tools: [
      "Claim or create a centre profile",
      "Publish court count, opening hours and fee rules",
      "Set peak, off-peak and membership pricing",
      "Manage booking requests, payments and refunds",
      "Create events, tournaments, clinics and open days",
      "Track verification, reviews and support issues"
    ],
    workflow: ["Claim centre", "Add booking details", "Set courts and fees", "Open requests", "Sell memberships", "Review results"],
    audience: "Owners, managers, staff and multi-location badminton venues.",
    seoIntro: "Badminta centre pages are designed to rank for badminton centre software, court booking system, badminton court hire, venue management, membership credits and local badminton near me searches across every city.",
    outcomes: [
      ["More court utilisation", "Turn high-intent local searches into booking requests, membership enquiries and recurring play."],
      ["Cleaner operations", "Keep courts, hours, peak fees, off-peak fees, events, messages and support in one centre workspace."],
      ["Clear owner status", "Separate owner-managed centres from unclaimed directory records so players know what they can book."]
    ],
    marketing: {
      promise: "A centre page should sell confidence before a player contacts staff.",
      narrative: "Most badminton centres lose time answering the same questions: how many courts, what are the fees, is tonight available, do you run memberships, and can I book online. Badminta turns those answers into a complete public profile and a private operations workspace so players can decide faster and staff can manage everything from the same place.",
      proof: ["Verified owner badge after claim review", "Court fee tables for peak and off-peak sessions", "Membership and credit-pack merchandising", "Event and tournament promotion", "Booking and refund support records"],
      seoTargets: ["badminton court booking system", "badminton centre near me", "badminton court hire", "badminton venue management software", "badminton membership software"],
      conversion: ["Claim the centre", "Complete operating details", "Publish fees and availability", "Accept booking requests", "Sell memberships"]
    },
    faq: [
      ["Can unclaimed centres take bookings on Badminta?", "No. In-platform court booking should only be enabled after a centre owner creates or claims the centre and Badminta verifies the listing."],
      ["What can a centre publish for free?", "Centre Free includes an owner-managed public listing with address, contact details, opening hours, facilities, basic court information and public enquiries. A free listing cannot accept Badminta court bookings or use paid operating tools."],
      ["Can a centre sell memberships and court credits?", "Yes with Centre Pro. The paid centre workspace supports court bookings, fee schedules, memberships, credit packs, centre-run events and payment records."],
      ["How can more local players find a centre on Badminta?", "A complete centre profile adds the address, courts, hours, pricing, photos, events and verified ownership details players need when comparing nearby options."]
    ]
  },
  organisers: {
    page: "solutionOrganisers",
    label: "Social groups",
    navLabel: "For organisers",
    path: "/solutions/social-badminton-groups",
    Icon: UsersRound,
    mediaType: "group",
    heroTitle: "Fill social badminton sessions without payment chasing",
    heroText: "Create sessions at real venues, invite players, manage capacity, collect payments and keep every attendee clear on level, time and cost.",
    primary: ["Create a social group", "createGroup"],
    secondary: ["Open organiser dashboard", "organiser"],
    stats: [["Sessions", "Date, venue, level"], ["Invites", "Email tags and members"], ["Payouts", "Fees and payment history"]],
    problems: [
      ["Players join through scattered chats", "Organisers need one public page with time, venue, level, rules, capacity and price."],
      ["Payments create admin pain", "Shared court cost needs clear collection, attendance, refunds and payout visibility."],
      ["Skill mismatch hurts retention", "Players should understand level, rotation and expectations before they pay."]
    ],
    tools: [
      "Create group and public profile",
      "Attach the group to a real badminton centre",
      "Add sessions, capacity, level and pricing",
      "Invite members by email tags",
      "Show attendees and waitlist status",
      "Manage messages, ratings, refunds and payouts"
    ],
    workflow: ["Create group", "Choose venue", "Add session", "Invite players", "Collect payment", "Run the game"],
    audience: "Weekly social hosts, community captains, club night organisers and casual session managers.",
    seoIntro: "Badminta group pages target social badminton, join badminton group, badminton session near me, beginner badminton group, advanced badminton games and paid social badminton searches.",
    outcomes: [
      ["Less payment chasing", "Players see cost, venue, capacity and payment status before turning up."],
      ["Better player matching", "Public sessions can show level, rules, women-friendly notes, rotation style and attendance limits."],
      ["Repeatable sessions", "Organisers can turn one weekly group into a reliable calendar of joinable games."]
    ],
    marketing: {
      promise: "A group page should replace messy chat coordination with a clean session storefront.",
      narrative: "Social organisers usually carry the whole admin load: finding players, explaining level, confirming the venue, collecting money, handling cancellations and remembering who paid. Badminta gives each group a public profile, recurring session logic, member invitations and payment-ready tools so organisers can focus on running good games.",
      proof: ["Recurring weekly session setup", "Venue search plus quick venue pin creation", "Capacity, waitlist and attendee visibility", "Member invites by email tag", "Payment status and organiser payout records"],
      seoTargets: ["social badminton group near me", "join badminton session", "beginner badminton group", "paid badminton social", "badminton games this week"],
      conversion: ["Create group", "Add venue", "Set weekly schedule", "Invite members", "Open paid joins"]
    },
    faq: [
      ["Can a group use any badminton centre?", "A group can select or request a real venue, but the public session should clearly show whether the venue is verified or still unclaimed."],
      ["Can players pay to join a social session?", "Yes. Paid sessions are part of the platform model, with organiser payout and refund controls planned around Stripe-backed checkout."],
      ["Can organisers invite members by email?", "Yes. Group setup supports inviting members with email tags so recurring players can be managed in one place."]
    ]
  },
  coaches: {
    page: "solutionCoaches",
    label: "Coaches",
    navLabel: "For coaches",
    path: "/solutions/badminton-coaches",
    Icon: Trophy,
    mediaType: "coach",
    heroTitle: "Turn coaching interest into paid lesson enquiries",
    heroText: "Badminta gives coaches a clear page for rates, availability, locations, videos, recommendations, enquiries and lesson payments.",
    primary: ["Create coach profile", "coachProfile"],
    secondary: ["Open coach dashboard", "coachDashboard"],
    stats: [["Profile", "Media and credentials"], ["Lessons", "Enquiries and packages"], ["Payouts", "Payment records"]],
    problems: [
      ["Coaching demand is hard to compare", "Players need to see experience, rate, location, media and reviews before enquiring."],
      ["Players want to see your style", "Lesson clips, social links and recommendations should sit on the profile, not disappear in chat."],
      ["Bookings need structure", "Packages, deposits, lesson requests and payouts need clear tools."]
    ],
    tools: [
      "Publish coach profile and media grid",
      "List rates, suburbs, venues and availability",
      "Add credentials, playing history and lesson style",
      "Receive messages and lesson enquiries",
      "Create clinics or group lessons",
      "Track reviews, recommendations and payouts"
    ],
    workflow: ["Create profile", "Add media", "Set availability", "Publish rates", "Receive enquiries", "Track lessons"],
    audience: "Private coaches, academy trainers, club coaches and centre-based coaching teams.",
    seoIntro: "Badminta coach pages are built for badminton coach near me, private badminton lessons, junior badminton coaching, adult badminton training, academy profile and coach booking searches.",
    outcomes: [
      ["Confidence before enquiry", "Players can compare media, rates, locations, experience, recommendations and availability."],
      ["Better lesson packaging", "Coaches can present private lessons, group lessons, clinics, deposits and recurring packages."],
      ["Local discovery", "Coach profiles connect to suburb and city pages so players can find training options near them."]
    ],
    marketing: {
      promise: "A coach page should make a player comfortable enough to enquire or book.",
      narrative: "Badminton coaching is personal. Players want to know who teaches beginners, who trains juniors, who can improve footwork, what a lesson costs, where training happens and what the coach looks like in action. Badminta turns those answers into a media-led profile with availability, packages, reviews and payment-ready lesson workflows.",
      proof: ["Coach media and social video embeds", "Rates, venues and availability", "Lesson packages and clinic creation", "Recommendations and public reviews", "Payout statement visibility"],
      seoTargets: ["badminton coach near me", "private badminton lessons", "junior badminton coaching", "adult badminton training", "badminton academy profile"],
      conversion: ["Create coach profile", "Add coaching media", "Set rates", "Publish availability", "Receive lesson enquiries"]
    },
    faq: [
      ["Can coaches embed social media videos?", "Yes. Coach profiles support media and social links so lesson clips and technique examples can sit on the profile."],
      ["Can coaches take paid bookings?", "The coaching flow is designed for lesson deposits, packages and Stripe-backed payment records when marketplace payments are enabled."],
      ["Can a coach work from multiple venues?", "Yes. A coach can list suburbs, venues and availability so players understand where lessons can happen."]
    ]
  },
  players: {
    page: "solutionPlayers",
    label: "Players",
    navLabel: "For players",
    path: "/solutions/badminton-players",
    Icon: UserCircle,
    mediaType: "player",
    heroTitle: "Find badminton players and games near you",
    heroText: "Discover badminton players, social groups, centres, coaches and events worldwide, then keep profiles, bookings, messages and payment status together.",
    primary: ["Create player profile", "profileSetup"],
    secondary: ["Find badminton near me", "filterHub"],
    stats: [["Discovery", "Centres, groups, coaches"], ["Profile", "Media and availability"], ["Trust", "Ratings and reviews"]],
    problems: [
      ["Finding a game is fragmented", "Players jump between maps, group chats, websites and social pages."],
      ["Skill level is unclear", "Beginner, social, advanced and competitive players need better matching signals."],
      ["Profiles should help people decide", "Availability, ratings, recommendations and optional media should make invites easier."]
    ],
    tools: [
      "Search centres, groups, players, coaches and events",
      "Create a player profile with media and links",
      "Show availability, level and play preferences",
      "Join social sessions and events",
      "Rate players and leave recommendations",
      "Save centres, groups and events"
    ],
    workflow: ["Search area", "Compare options", "Create profile", "Join session", "Pay if needed", "Review after play"],
    audience: "New players, social players, competitive players, travellers and local badminton communities.",
    seoIntro: "Build a badminton player profile, find a badminton partner, join beginner or advanced social games, compare court hire, discover coaching and follow badminton events in cities worldwide.",
    outcomes: [
      ["Find the right game", "Players can compare centres, groups, coaches and events by location, level and schedule."],
      ["Show real experience", "Public player profiles can include media, recommendations, ratings, availability and playing background."],
      ["Build playing reputation", "Ratings, reviews and joined-session history help players decide who to play with."]
    ],
    marketing: {
      promise: "A player page should help people invite the right player to the right game.",
      narrative: "Players do not only need a directory. They need a way to find courts, join groups, manage bookings, see this week's games and build enough trust that other people want to play with them. Badminta player profiles combine availability, media, recommendations, booked courts and joined sessions into one simple playing identity.",
      proof: ["Profile media, links and quoted playing story", "Court bookings and payment status", "Groups joined this week", "Recommendations and reviews", "Saved centres, groups, coaches and events"],
      seoTargets: ["badminton players near me", "find badminton partner", "beginner badminton players", "advanced badminton players", "badminton games near me"],
      conversion: ["Create player profile", "Add availability", "Join a group", "Book a court", "Build recommendations"]
    },
    faq: playerLandingFaq
  },
  events: {
    page: "solutionEvents",
    label: "Events",
    navLabel: "For events",
    path: "/solutions/badminton-events",
    Icon: CalendarDays,
    mediaType: "event",
    heroTitle: "Sell badminton events with clear registration and payments",
    heroText: "Centres and organisers can run clinics, tournaments, open days and club nights with public pages, capacity, attendee lists and country-aware payments.",
    primary: ["Create an event", "createEvent"],
    secondary: ["Manage events", "eventManager"],
    stats: [["Event pages", "Venue, schedule, media"], ["Registration", "Capacity and attendee list"], ["Payments", "Checkout or direct settlement"]],
    problems: [
      ["Events need more than a poster", "Players need venue, time, level, price, capacity and refund rules before registering."],
      ["Attendance must be controlled", "Owners need joined users, waitlists, check-in state and payment status."],
      ["Public pages drive discovery", "Every city and suburb should surface events alongside centres, players and coaches."]
    ],
    tools: [
      "Create centre-owned or group-owned events",
      "Publish schedule, venue, capacity and price",
      "Show attendee state and registration logic",
      "Use managed checkout where supported or direct settlement elsewhere",
      "Manage refunds, support and event reviews",
      "Connect events to city and suburb discovery pages"
    ],
    workflow: ["Create event", "Attach owner", "Set venue", "Publish page", "Take registrations", "Manage event"],
    audience: "Tournament hosts, centres, social groups, coaching clinics and club night organisers.",
    seoIntro: "Badminta event pages are designed for badminton tournament near me, badminton clinic, badminton open day, club night, junior event and local badminton event searches.",
    outcomes: [
      ["More registrations", "Public event pages can show venue, schedule, level, capacity, price and media."],
      ["Cleaner attendance", "Owners can manage registrations, attendees, waitlists, payment state, refunds and messages."],
      ["City-wide reach", "Events appear on local discovery pages so badminton activity is easier to find by city and suburb."]
    ],
    marketing: {
      promise: "An event page should make registration clear before the player asks a question.",
      narrative: "Badminton events often rely on posters, social posts and manual bank transfers. Badminta gives tournaments, clinics, open days and club nights a public page with venue, price, schedule, capacity, attendee state and payment-ready registration so organisers can sell spots with less admin.",
      proof: ["Centre-owned or group-owned event setup", "Registration, capacity and attendee controls", "Payment and refund status", "Event messages and support records", "City and suburb event discovery"],
      seoTargets: ["badminton tournament near me", "badminton clinic", "badminton open day", "badminton event registration", "junior badminton event"],
      conversion: ["Create event", "Attach owner", "Set price", "Publish registration", "Manage attendees"]
    },
    faq: [
      ["Who can create badminton events?", "Centre owners and group organisers can create events such as tournaments, clinics, open days and club nights."],
      ["Can events collect payments?", "Yes. Paid events use Badminta Checkout where owner-country payouts and the ticket currency are supported. Elsewhere, Badminta confirms the place and records that the attendee pays the host directly."],
      ["Do events appear on local pages?", "Yes. Events are intended to surface alongside centres, groups, players and coaches on city and suburb discovery pages."]
    ]
  }
};

const solutionOverviewFaq = [
  ["What is Badminta?", "Badminta is a worldwide badminton marketplace for finding centres, groups, players, coaches and events, with operating tools for bookings, payments, memberships and trust."],
  ["How does Badminta rank in different countries?", "The platform creates country, city and suburb discovery pages and connects complete public profiles to those pages so local badminton searches have relevant content."],
  ["Who should create a Badminta profile?", "Centre owners, social organisers, coaches and players should create profiles when they want more bookings, better session management, useful public pages, messages and payment-ready tools."],
  ["How does Badminta make money?", "Badminta can earn from court booking fees, social session fees, coach bookings, centre SaaS subscriptions, membership credit flows, event ticketing and trusted promotional placements."]
];

const profileSubscriptionFaq = (role = "") => {
  if (!role) return [];
  const plan = getProfileSubscriptionPlan(role);
  return [
    [
      `Can I use a ${plan.roleLabel.toLowerCase()} profile for free?`,
      `Yes. ${plan.freeName} keeps the core public profile and basic role tools free. ${plan.proName} is optional and adds the higher limits and paid operating tools listed on this page, plus a public Pro badge.`
    ],
    [
      `How much does ${plan.proName} cost?`,
      `The pricing table on this page automatically shows the ${plan.proName} monthly and annual package for the visitor's country in its supported local currency. Badminta confirms the verified signup country and final amount again before secure checkout.`
    ],
    [
      `Can I cancel ${plan.proName}?`,
      "Yes. Billing, invoices, payment methods and future renewal cancellation are managed in Stripe's secure customer portal. The free public profile remains available after paid access ends."
    ]
  ];
};

const profileSubscriptionRoleBySolutionPage = {
  solutionPlayers: "player",
  solutionOrganisers: "organiser",
  solutionCoaches: "coach",
  solutionCentres: "centre"
};

const solutionPageByRoute = Object.fromEntries(Object.values(solutionPages).map((item) => [item.page, item]));
const solutionRouteBySlug = {
  "badminton-centres": "solutionCentres",
  "centres": "solutionCentres",
  "venues": "solutionCentres",
  "social-badminton-groups": "solutionOrganisers",
  "social-groups": "solutionOrganisers",
  "organisers": "solutionOrganisers",
  "badminton-coaches": "solutionCoaches",
  "coaches": "solutionCoaches",
  "badminton-players": "solutionPlayers",
  "players": "solutionPlayers",
  "badminton-events": "solutionEvents",
  "events": "solutionEvents"
};

const playerLandingVideo = {
  title: "Badminta player profile preview",
  src: "/media/badminta-player-profile-walkthrough.mp4"
};

const hubResultTypes = ["overview", "centres", "groups", "players", "coaches", "events"];
const hubResultTypeSegments = {
  overview: "all",
  centres: "centres",
  groups: "social-groups",
  players: "players",
  coaches: "coaches",
  events: "events"
};
const hubResultTypeFromSegment = {
  all: "overview",
  overview: "overview",
  centres: "centres",
  centre: "centres",
  "badminton-centres": "centres",
  courts: "centres",
  "court-hire": "centres",
  groups: "groups",
  socials: "groups",
  "social-groups": "groups",
  "social-games": "groups",
  "social-badminton-groups": "groups",
  players: "players",
  player: "players",
  "badminton-players": "players",
  coaches: "coaches",
  coaching: "coaches",
  "badminton-coaches": "coaches",
  events: "events",
  event: "events",
  "badminton-events": "events"
};
const filterDefaultResultTypes = {
  nearby: "overview",
  verified: "centres",
  beginner: "groups",
  advanced: "players",
  women: "groups",
  parking: "centres",
  coaching: "coaches",
  late: "centres",
  payouts: "groups"
};

const normaliseHubResultType = (value) => {
  const key = slugifyPath(value || "");
  return hubResultTypeFromSegment[key] || "";
};

const getHubResultTypeSegment = (resultType) => hubResultTypeSegments[normaliseHubResultType(resultType)] || "";

const getHubResultTypeFromTags = (tags = []) => {
  const selected = new Set(tags);
  if (!selected.size || selected.size > 1) return "overview";
  if (selected.size === 1) {
    if (selected.has("socials")) return "groups";
    if (selected.has("players")) return "players";
    if (selected.has("coaches")) return "coaches";
    if (selected.has("events")) return "events";
    return "centres";
  }
  if (selected.has("centres") || selected.has("courts")) return "centres";
  if (selected.has("socials")) return "groups";
  if (selected.has("players")) return "players";
  if (selected.has("coaches")) return "coaches";
  if (selected.has("events")) return "events";
  return "overview";
};

const getPreferredHubResultType = (filterKey, tags = []) => {
  if (!filterKey || filterKey === "nearby") return getHubResultTypeFromTags(tags);
  return filterDefaultResultTypes[filterKey] || "centres";
};

const routeLabels = {
  home: "Home directory",
  solutionIndex: "Solutions",
  solutionCentres: "Solutions for centres",
  solutionOrganisers: "Solutions for organisers",
  solutionCoaches: "Solutions for coaches",
  solutionPlayers: "Solutions for players",
  solutionEvents: "Solutions for events",
  filterHub: "Explore",
  seoArea: "Suburb badminton guide",
  results: "Search results",
  groupsIndex: "Social groups",
  playersIndex: "Players",
  courtAvailability: "Court hire",
  events: "Events",
  event: "Event detail",
  eventJoin: "Join event",
  createEvent: "Create event",
  eventManager: "Event dashboard",
  joinedEvents: "Joined events",
  manageEvent: "Manage event",
  reviewFlow: "Review",
  paymentFlow: "Payment",
  bookings: "Bookings",
  city: "City page",
  venue: "Venue detail",
  centreUnavailable: "Centre listing status",
  group: "Social group detail",
  groupMembership: "Join group",
  player: "Player profile",
  coach: "Coach profile",
  publicPlayerProfile: "Public player profile",
  playerDashboard: "Player dashboard",
  booking: "Court booking",
  join: "Join social",
  profileSetup: "Player profile setup",
  coachDashboard: "Coach dashboard",
  coachProfile: "Coach profile setup",
  coachPayouts: "Coach payouts",
  publicCoachProfile: "Public coach profile",
  ratings: "Rating system",
  messages: "Messages",
  notifications: "Notifications",
  organiser: "Organiser dashboard",
  createGroup: "Create group",
  groupSessions: "Group sessions",
  createGroupSession: "Create session",
  editGroupSession: "Edit session",
  publicGroupProfile: "Public group profile",
  payouts: "Organiser payouts",
  members: "Members",
  centreDashboard: "Centre dashboard",
  claimCentre: "Add or edit centre",
  centreClaimSearch: "Search and claim centre",
  publicCentreProfile: "Public centre profile",
  courtManager: "Court inventory",
  centrePayments: "Centre payments",
  admin: "Network admin",
  trust: "Trust and moderation",
  analytics: "Analytics",
  administrator: "Super administrator",
  login: "Log in",
  signup: "Sign up",
  onboarding: "Onboarding",
  saved: "Saved",
  following: "Following",
  account: "Account",
  accountDeletion: "Delete account",
  help: "Help",
  legalTerms: "Terms",
  privacyPolicy: "Privacy",
  refundPolicy: "Refunds",
  communityPolicy: "Community safety",
  cookiePolicy: "Cookie choices",
  youthPolicy: "Youth safety",
  centrePolicy: "Centre terms",
  organiserPolicy: "Organiser terms",
  coachPolicy: "Coach terms",
  blogIndex: "Badminton blog",
  blogArticle: "Blog article"
};

const marketplaceCategoryRailPages = new Set([
  "home",
  "results",
  "groupsIndex",
  "playersIndex",
  "ratings",
  "courtAvailability",
  "events",
  "city"
]);

const focusedProfileSetupPages = {
  profileSetup: { label: "Player profile", exitPage: "account", icon: UserCircle },
  coachProfile: { label: "Coach profile", exitPage: "account", icon: Trophy },
  createGroup: { label: "Social group", exitPage: "account", icon: UsersRound },
  claimCentre: { label: "Centre profile", exitPage: "account", icon: Building2 }
};

const routeSections = {
  solutionIndex: [{ label: "Solutions", page: "solutionIndex" }],
  solutionCentres: [{ label: "Solutions", page: "solutionIndex" }],
  solutionOrganisers: [{ label: "Solutions", page: "solutionIndex" }],
  solutionCoaches: [{ label: "Solutions", page: "solutionIndex" }],
  solutionPlayers: [{ label: "Solutions", page: "solutionIndex" }],
  solutionEvents: [{ label: "Solutions", page: "solutionIndex" }],
  blogIndex: [{ label: "Blog", page: "blogIndex" }],
  blogArticle: [{ label: "Blog", page: "blogIndex" }],
  filterHub: [{ label: "Explore", page: "filterHub" }],
  seoArea: [{ label: "Global badminton", page: "home" }],
  results: [{ label: "Centres", page: "results" }],
  groupsIndex: [{ label: "Social groups", page: "groupsIndex" }],
  playersIndex: [{ label: "Players", page: "playersIndex" }],
  courtAvailability: [{ label: "Court hire", page: "courtAvailability" }],
  events: [{ label: "Events", page: "events" }],
  event: [{ label: "Events", page: "events" }],
  eventJoin: [{ label: "Events", page: "events" }],
  createEvent: [{ label: "Events", page: "events" }],
  eventManager: [{ label: "Account", page: "account" }, { label: "Events", page: "eventManager" }],
  joinedEvents: [{ label: "Account", page: "account" }, { label: "Events", page: "eventManager" }],
  manageEvent: [{ label: "Account", page: "account" }, { label: "Events", page: "eventManager" }],
  reviewFlow: [{ label: "Account", page: "account" }, { label: "Reviews", page: "reviewFlow" }],
  paymentFlow: [{ label: "Account", page: "account" }, { label: "Payments", page: "paymentFlow" }],
  bookings: [{ label: "Account", page: "account" }, { label: "Bookings", page: "bookings" }],
  city: [{ label: "Cities", page: "home" }],
  venue: [{ label: "Centres", page: "results" }],
  centreUnavailable: [{ label: "Centres", page: "results" }],
  group: [{ label: "Social groups", page: "groupsIndex" }],
  groupMembership: [{ label: "Social groups", page: "groupsIndex" }],
  player: [{ label: "Players", page: "playersIndex" }],
  coach: [{ label: "Coaches", page: "playersIndex" }],
  playerDashboard: [{ label: "Account", page: "account" }, { label: "Player dashboard", page: "playerDashboard" }],
  publicPlayerProfile: [{ label: "Players", page: "playersIndex" }],
  booking: [{ label: "Centres", page: "results" }],
  join: [{ label: "Social groups", page: "groupsIndex" }],
  profileSetup: [{ label: "Players", page: "playersIndex" }],
  coachDashboard: [{ label: "Account", page: "account" }, { label: "Coach dashboard", page: "coachDashboard" }],
  coachProfile: [{ label: "Coaches", page: "playersIndex" }],
  coachPayouts: [{ label: "Coach", page: "coachDashboard" }],
  publicCoachProfile: [{ label: "Coaches", page: "playersIndex", payload: { activeFilter: "coaching" } }],
  ratings: [{ label: "Players", page: "playersIndex" }],
  messages: [{ label: "Account", page: "account" }],
  notifications: [{ label: "Account", page: "account" }],
  organiser: [{ label: "Organiser", page: "organiser" }],
  createGroup: [{ label: "Organiser", page: "organiser" }],
  groupSessions: [{ label: "Organiser", page: "organiser" }],
  createGroupSession: [{ label: "Organiser", page: "organiser" }, { label: "Sessions", page: "groupSessions" }],
  editGroupSession: [{ label: "Organiser", page: "organiser" }, { label: "Sessions", page: "groupSessions" }],
  publicGroupProfile: [{ label: "Social groups", page: "groupsIndex" }],
  payouts: [{ label: "Organiser", page: "organiser" }],
  members: [{ label: "Organiser", page: "organiser" }],
  centreDashboard: [{ label: "Centre tools", page: "centreDashboard" }],
  claimCentre: [{ label: "Centre tools", page: "centreDashboard" }],
  centreClaimSearch: [{ label: "Centre tools", page: "centreDashboard" }],
  publicCentreProfile: [{ label: "Centres", page: "results" }],
  courtManager: [{ label: "Centre tools", page: "centreDashboard" }],
  centrePayments: [{ label: "Centre tools", page: "centreDashboard" }],
  admin: [{ label: "Admin", page: "admin" }],
  trust: [{ label: "Admin", page: "admin" }],
  analytics: [{ label: "Admin", page: "admin" }],
  administrator: [{ label: "Administrator", page: "administrator" }],
  login: [{ label: "Account", page: "account" }],
  signup: [{ label: "Account", page: "account" }],
  onboarding: [{ label: "Account", page: "account" }],
  saved: [{ label: "Account", page: "account" }],
  following: [{ label: "Account", page: "account" }],
  account: [{ label: "Account", page: "account" }],
  accountDeletion: [{ label: "Account", page: "account" }],
  help: [{ label: "Support", page: "help" }],
  legalTerms: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  privacyPolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  refundPolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  communityPolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  centrePolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  organiserPolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }],
  coachPolicy: [{ label: "Support", page: "help" }, { label: "Legal", page: "legalTerms" }]
};

const breadcrumbLocationLabel = (searchCriteria, city) => {
  const location = searchCriteria?.location;
  if (!location) return city;
  if (location.type === "nearby") return "Near me";
  return location.suburb || location.label || city;
};

const entityBreadcrumbLabel = (page, context) => {
  const { selectedVenue, selectedGroup, selectedPlayer, selectedCoach, selectedEvent, bookingType } = context;
  if (page === "blogArticle") return context.blogPost?.title || routeLabels.blogArticle;
  if (page === "playersIndex" && context.activeFilter === "coaching") return "Coaches";
  if (page === "venue") return selectedVenue?.name || routeLabels.venue;
  if (page === "centreUnavailable") return selectedVenue?.name || routeLabels.centreUnavailable;
  if (page === "group") return selectedGroup?.title || routeLabels.group;
  if (page === "groupMembership") return "Join group";
  if (page === "publicGroupProfile") return selectedGroup?.title || selectedGroup?.name || routeLabels.publicGroupProfile;
  if (page === "player") return selectedPlayer?.name || routeLabels.player;
  if (page === "publicPlayerProfile") return selectedPlayer?.name || routeLabels.publicPlayerProfile;
  if (page === "coach") return selectedCoach?.name || routeLabels.coach;
  if (page === "publicCoachProfile") return selectedCoach?.headline || selectedCoach?.name || routeLabels.publicCoachProfile;
  if (page === "event") return selectedEvent?.title || routeLabels.event;
  if (page === "eventJoin") return "Join and pay";
  if (page === "manageEvent") return selectedEvent?.title || routeLabels.manageEvent;
  if (page === "reviewFlow") return context.reviewContext?.title || routeLabels.reviewFlow;
  if (page === "paymentFlow") return context.paymentContext?.title || routeLabels.paymentFlow;
  if (page === "booking") return bookingType === "social" ? "Checkout" : "Court booking";
  if (page === "join") return "Join and pay";
  return routeLabels[page] || "Badminta";
};

const buildBreadcrumbs = (page, context) => {
  if (page === "home") return [];
  const crumbs = [{ label: "Home", page: "home", icon: Home }];
  const sections = page === "playersIndex" && context.activeFilter === "coaching"
    ? [{ label: "Coaches", page: "playersIndex", payload: { activeFilter: "coaching" } }]
    : routeSections[page] || [];
  sections.forEach((crumb) => {
    if (!crumbs.some((item) => item.label === crumb.label)) crumbs.push(crumb);
  });

  if (page === "results") {
    crumbs.push({ label: breadcrumbLocationLabel(context.searchCriteria, context.city), page: "results", current: true });
    return crumbs;
  }

  if (page === "seoArea") {
    const location = context.searchCriteria?.location || {};
    const intent = seoAreaIntents[normaliseSeoAreaIntent(context.seoAreaIntent)] || seoAreaIntents.overview;
    const countryLocation = countryDirectory.find((country) => country.countryCode === getLocationCountryCode(location, context.city));
    if (countryLocation) crumbs.push({ label: countryLocation.label, page: "seoArea", payload: { location: countryLocation, city: countryLocation.city || context.city, seoAreaIntent: "overview" } });
    if (location.type === "city" || location.type === "suburb") {
      const cityLocation = cityDirectory.find((option) => option.countryCode === getLocationCountryCode(location, context.city) && compactLocationText(option.city) === compactLocationText(location.city));
      if (cityLocation) crumbs.push({ label: cityLocation.label, page: "seoArea", payload: { location: cityLocation, city: cityLocation.city, seoAreaIntent: "overview" } });
    }
    if (location.type === "suburb") crumbs.push({ label: getSeoAreaLocationLabel(location, context.city), page: "seoArea", payload: { location, city: location.city || context.city, seoAreaIntent: "overview" } });
    if (crumbs[crumbs.length - 1]?.page !== "seoArea") crumbs.push({ label: getSeoAreaLocationLabel(location, context.city), page: "seoArea", payload: { location, city: location.city || context.city, seoAreaIntent: "overview" } });
    if (intent.label !== "All") crumbs.push({ label: intent.label, current: true });
    else crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], current: true };
    return crumbs;
  }

  if (page === "city") {
    crumbs.push({ label: context.city || "City", current: true });
    return crumbs;
  }

  if (page === "venue" || page === "centreUnavailable") {
    const venueArea = getVenueAreaLabel(context.selectedVenue || {});
    if (venueArea) crumbs.push({ label: venueArea, page: "results" });
  }

  if (page === "booking" && context.bookingType === "social") {
    crumbs.splice(1, 1, { label: "Social groups", page: "groupsIndex" });
    if (context.selectedGroup) crumbs.push({ label: getGroupTitle(context.selectedGroup), page: "group", payload: { group: context.selectedGroup } });
  } else if (page === "booking" && context.selectedVenue?.name) {
    crumbs.push({ label: context.selectedVenue.name, page: "venue", payload: { venue: context.selectedVenue } });
  }

  if (page === "join" && context.selectedGroup) {
    crumbs.push({ label: getGroupTitle(context.selectedGroup), page: "group", payload: { group: context.selectedGroup } });
  }

  if (page === "eventJoin" && context.selectedEvent?.title) {
    crumbs.push({ label: context.selectedEvent.title, page: "event", payload: { event: context.selectedEvent } });
  }

  const currentLabel = entityBreadcrumbLabel(page, context);
  if (crumbs[crumbs.length - 1]?.label === currentLabel) {
    crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], current: true };
  } else {
    crumbs.push({ label: currentLabel, current: true });
  }
  return crumbs;
};

function slugifyPath(value) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "badminta";
}

const shortStableHash = (value = "") => {
  const text = String(value || "badminta");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash + text.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36).slice(0, 6);
};

const pexelsBadmintonSearchUrl = "https://www.pexels.com/search/badminton%20court/";
const pexelsPlayerSearchUrl = "https://www.pexels.com/search/badminton%20player/";
const pexelsImage = (photoId, slug, width = 1600) => `https://images.pexels.com/photos/${photoId}/${slug}.jpeg?auto=compress&cs=tinysrgb&w=${width}`;
const landingMedia = {
  home: {
    id: "pexels-aerial-badminton-home",
    url: "/media/badminta-home-hero-800.webp",
    label: "Professional badminton match",
    alt: "Aerial view of badminton players on a professional indoor court",
    types: ["centre", "court", "player", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/aerial-view-of-badminton-players-in-action-31790661/"
  },
  overview: {
    id: "pexels-shuttle-net-blue-overview",
    url: pexelsImage("35647228", "pexels-photo-35647228", 1800),
    label: "Shuttle at the net",
    alt: "Badminton shuttle caught in a blue court net",
    types: ["centre", "court", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsBadmintonSearchUrl
  },
  centres: {
    id: "pexels-empty-indoor-court-centres",
    url: pexelsImage("8933585", "pexels-photo-8933585", 1800),
    label: "Indoor court operations",
    alt: "Empty indoor badminton court with net",
    types: ["centre", "court", "coach", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsBadmintonSearchUrl
  },
  organisers: {
    id: "pexels-group-discussion",
    url: pexelsImage("32944292", "pexels-photo-32944292", 1800),
    label: "Social badminton group",
    alt: "Badminton players talking by an indoor court net",
    types: ["group", "player", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsPlayerSearchUrl
  },
  coaches: {
    id: "pexels-young-player-training",
    url: pexelsImage("35647221", "pexels-photo-35647221", 1800),
    label: "Badminton coaching session",
    alt: "Young badminton player training on an indoor court",
    types: ["coach", "player", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/young-boy-playing-badminton-indoors-35647221/"
  },
  players: {
    id: "pexels-badminton-victory",
    url: pexelsImage("29873465", "pexels-photo-29873465", 1800),
    label: "Player discovery",
    alt: "Young badminton player celebrating during an indoor match",
    types: ["player", "group", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/young-badminton-player-celebrating-victory-indoors-29873465/"
  },
  events: {
    id: "pexels-women-badminton-court",
    url: pexelsImage("8007171", "pexels-photo-8007171", 1800),
    label: "Badminton event moment",
    alt: "Badminton players gathering on an indoor court",
    types: ["event", "player", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/women-at-a-badminton-court-8007171/"
  },
  detail: {
    id: "pexels-racket-floor",
    url: pexelsImage("36815793", "pexels-photo-36815793", 1600),
    label: "Court equipment and players",
    alt: "Badminton racket resting near seated players on an indoor court",
    types: ["centre", "court", "player", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/close-up-of-a-badminton-racket-on-a-sports-court-floor-36815793/"
  }
};
const stockBadmintonMedia = [
  {
    id: "pexels-shuttle-net-blue",
    url: pexelsImage("35647228", "pexels-photo-35647228"),
    label: "Shuttle at the net",
    alt: "Badminton shuttle caught in a blue court net",
    types: ["centre", "court", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsBadmintonSearchUrl
  },
  {
    id: "pexels-empty-indoor-court",
    url: pexelsImage("8933585", "pexels-photo-8933585"),
    label: "Indoor court",
    alt: "Empty indoor badminton court with net",
    types: ["centre", "court", "coach", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsBadmintonSearchUrl
  },
  {
    id: "pexels-rackets-shuttles-court",
    url: pexelsImage("3660204", "pexels-photo-3660204"),
    label: "Rackets and shuttles",
    alt: "Badminton rackets and shuttlecocks on a green court",
    types: ["player", "coach", "centre", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsBadmintonSearchUrl
  },
  {
    id: "pexels-shuttle-red-court",
    url: pexelsImage("8007075", "pexels-photo-8007075"),
    label: "Shuttle on court",
    alt: "Badminton shuttle on a red indoor court",
    types: ["player", "group", "event", "general"],
    sourceName: "Pexels",
    sourceUrl: pexelsPlayerSearchUrl
  },
  {
    id: "pexels-court-break",
    url: pexelsImage("8007406", "pexels-photo-8007406"),
    label: "Court break",
    alt: "Two badminton players resting on an indoor court",
    types: ["group", "player", "event"],
    sourceName: "Pexels",
    sourceUrl: pexelsPlayerSearchUrl
  },
  {
    id: "pexels-young-player-training",
    url: pexelsImage("35647221", "pexels-photo-35647221"),
    label: "Young player training",
    alt: "Boy playing badminton on an indoor court",
    types: ["player", "coach", "group", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/young-boy-playing-badminton-indoors-35647221/"
  },
  {
    id: "pexels-badminton-victory",
    url: pexelsImage("29873465", "pexels-photo-29873465"),
    label: "Match celebration",
    alt: "Young badminton player celebrating during an indoor match",
    types: ["player", "event", "group", "general"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/young-badminton-player-celebrating-victory-indoors-29873465/"
  },
  {
    id: "pexels-woman-playing-badminton",
    url: pexelsImage("8007094", "pexels-photo-8007094"),
    label: "Player returning shot",
    alt: "Badminton player returning a shot on an indoor court",
    types: ["player", "coach", "event"],
    sourceName: "Pexels",
    sourceUrl: "https://www.pexels.com/photo/woman-playing-badminton-at-court-8007094/"
  },
  {
    id: "pexels-group-discussion",
    url: pexelsImage("32944292", "pexels-photo-32944292"),
    label: "Group session",
    alt: "Badminton players talking by an indoor court net",
    types: ["group", "coach", "event"],
    sourceName: "Pexels",
    sourceUrl: pexelsPlayerSearchUrl
  }
];
const stockBadmintonMediaUrls = new Set(stockBadmintonMedia.map((item) => item.url.split("?")[0]));
const isStockBadmintonImageUrl = (url = "") => stockBadmintonMediaUrls.has(String(url || "").split("?")[0]);
const getStockMediaFallbacks = (type = "general", seed = "", count = 1) => {
  const requestedType = String(type || "general").toLowerCase();
  const matching = stockBadmintonMedia.filter((item) => item.types.includes(requestedType));
  const pool = matching.length ? matching : stockBadmintonMedia.filter((item) => item.types.includes("general"));
  const base = pool.length ? pool : stockBadmintonMedia;
  const start = parseInt(shortStableHash(`${requestedType}|${seed || "badminta"}`), 36) % base.length;
  return Array.from({ length: Math.min(count, base.length) }, (_, offset) => base[(start + offset) % base.length]);
};
const getStockMediaFallback = (type = "general", seed = "") => getStockMediaFallbacks(type, seed, 1)[0] || stockBadmintonMedia[0];
const getLandingMedia = (key = "home", fallbackType = "general") => landingMedia[key] || getStockMediaFallback(fallbackType, `landing-${key}`);
const solutionMarketingMediaByKind = {
  centres: [
    landingMedia.centres,
    stockBadmintonMedia.find((item) => item.id === "pexels-shuttle-net-blue"),
    stockBadmintonMedia.find((item) => item.id === "pexels-rackets-shuttles-court")
  ],
  organisers: [
    landingMedia.organisers,
    stockBadmintonMedia.find((item) => item.id === "pexels-court-break"),
    stockBadmintonMedia.find((item) => item.id === "pexels-shuttle-red-court")
  ],
  coaches: [
    landingMedia.coaches,
    stockBadmintonMedia.find((item) => item.id === "pexels-woman-playing-badminton"),
    landingMedia.home
  ],
  events: [
    landingMedia.events,
    stockBadmintonMedia.find((item) => item.id === "pexels-badminton-victory"),
    landingMedia.detail
  ]
};
const getSolutionMarketingMediaSet = (kind = "players", fallbackType = "general", count = 5) => {
  const assigned = (solutionMarketingMediaByKind[kind] || [])
    .filter(Boolean)
    .filter((item, index, list) => list.findIndex((candidate) => candidate.url === item.url) === index);
  if (assigned.length >= Math.min(count, 3)) return assigned.slice(0, count);
  const preferred = [
    getLandingMedia(kind, fallbackType),
    getLandingMedia("home", fallbackType),
    getLandingMedia("detail", fallbackType),
    ...getStockMediaFallbacks(fallbackType, `solution-${kind}`, count + 2)
  ].filter(Boolean);
  return preferred
    .filter((item, index, list) => list.findIndex((candidate) => candidate.url === item.url) === index)
    .slice(0, count);
};
const stockMediaLinksFor = (type = "general", seed = "", count = 4) => getStockMediaFallbacks(type, seed, count).map((media) => ({
  label: media.label,
  url: media.url,
  source: "stock",
  sourceName: media.sourceName,
  sourceUrl: media.sourceUrl,
  rightsStatus: "licensed",
  requiresModeration: false,
  moderationStatus: "approved"
}));

const entitySlug = (entity, labelField = "name") => {
  if (!entity) return "badminta";
  if (entity.slug) return slugifyPath(entity.slug);
  const base = slugifyPath(entity[labelField] || entity.name || entity.title || "badminta");
  const rawUniqueId = entity.id || entity.osmId || entity.placeId || entity.sourceId || "";
  const uniqueId = rawUniqueId ? slugifyPath(rawUniqueId) : "";
  return uniqueId && !base.includes(uniqueId) ? `${base}-${uniqueId}` : base;
};

const publicProfileRoleConfig = {
  player: {
    section: "players",
    typeSegment: "badminton-player",
    keyword: "badminton-player",
    fallbackName: "badminta player",
    getName: (profile) => profile.fullName,
    getArea: (profile) => profile.player?.suburb || profile.player?.location?.suburb || profile.player?.city || profile.player?.location?.city || profile.player?.country || profile.player?.location?.country || profile.player?.countryCode || profile.player?.location?.countryCode
  },
  coach: {
    section: "coaches",
    typeSegment: "badminton-coach",
    keyword: "badminton-coach",
    fallbackName: "badminta coach",
    getName: (profile) => profile.coach?.headline || profile.fullName,
    getArea: (profile) => profile.coach?.suburb || profile.coach?.location?.suburb || profile.coach?.location?.city || profile.coach?.locations
  },
  group: {
    section: "social-groups",
    typeSegment: "badminton-social-group",
    keyword: "badminton-social-group",
    fallbackName: "badminta social group",
    getName: (profile) => profile.group?.name,
    getArea: (profile) => profile.group?.suburb || profile.group?.location?.suburb || profile.group?.location?.city || profile.group?.venue
  },
  centre: {
    section: "centres",
    typeSegment: "badminton-centre-profile",
    keyword: "badminton-centre",
    fallbackName: "badminta badminton centre",
    getName: (profile) => profile.centre?.name,
    getArea: (profile) => profile.centre?.suburb || profile.centre?.location?.suburb || profile.centre?.location?.city || profile.centre?.address
  }
};

const getPublicProfileParts = (profile = {}, role, fallbackCity = "Worldwide") => {
  const config = publicProfileRoleConfig[role] || publicProfileRoleConfig.player;
  const roleProfile = role === "group" ? profile.group || {} : profile[role] || {};
  const rawArea = config.getArea(profile) || roleProfile.publicAreaSlug || fallbackCity;
  const area = slugifyPath(String(rawArea).split(",")[0] || fallbackCity);
  const name = config.getName(profile) || config.fallbackName;
  const profileId = String({
    player: roleProfile.publicPlayerId || roleProfile.firestoreSourceId || profile.publicPlayerProfileId || roleProfile.id,
    coach: roleProfile.publicCoachId || roleProfile.firestoreSourceId || profile.publicCoachProfileId || roleProfile.id,
    group: roleProfile.firestoreSourceId || roleProfile.serverGroupId || roleProfile.publicGroupId || roleProfile.id,
    centre: getCentreBackendSourceId(roleProfile) || roleProfile.id
  }[role] || "").trim();
  const uniqueSeed = profileId ? `${role}|${profileId}` : [
    role,
    profile.email,
    profile.fullName,
    profile.player?.suburb,
    profile.coach?.headline,
    profile.group?.name,
    profile.centre?.name,
    rawArea
  ].filter(Boolean).join("|");
  const slug = slugifyPath(`${name} ${config.keyword} ${rawArea}`);
  return {
    section: config.section,
    typeSegment: config.typeSegment,
    area,
    slug: `${slug}-${shortStableHash(uniqueSeed || `${role}|${name}|${rawArea}`)}`,
    profileId
  };
};

const publicProfilePath = (profile = {}, role, fallbackCity = "Worldwide") => {
  const parts = getPublicProfileParts(profile, role, fallbackCity);
  const idSegment = parts.profileId ? `/${encodeURIComponent(parts.profileId)}` : "";
  return `/${parts.section}/${parts.area}/${parts.typeSegment}/${parts.slug}${idSegment}`;
};

const publicProfilePageByRole = {
  player: "publicPlayerProfile",
  coach: "publicCoachProfile",
  group: "publicGroupProfile",
  centre: "publicCentreProfile"
};

const publicProfileSlugBase = (value = "") => slugifyPath(value).replace(/-[a-z0-9]{1,6}$/, "");

const publicProfileRouteDescriptor = (role, areaSlug, typeSegment, profileSlug, profileId = "", action = "") => {
  const config = publicProfileRoleConfig[role];
  if (!config || config.typeSegment !== typeSegment || !areaSlug || !profileSlug) return null;
  const allowedActions = role === "group" ? ["join", "membership"] : role === "centre" ? ["book"] : [];
  return {
    role,
    areaSlug: slugifyPath(areaSlug),
    typeSegment,
    profileSlug: slugifyPath(profileSlug),
    profileId: String(profileId || "").trim().slice(0, 180),
    action: allowedActions.includes(action) ? action : ""
  };
};

const publicProfileRoutePath = (route = {}) => {
  const config = publicProfileRoleConfig[route.role];
  if (!config || !route.areaSlug || !route.profileSlug) return "";
  const profileId = route.profileId ? `/${encodeURIComponent(route.profileId)}` : "";
  const action = route.action ? `/${route.action}` : "";
  return `/${config.section}/${route.areaSlug}/${config.typeSegment}/${route.profileSlug}${profileId}${action}`;
};

const publicAccountProfileFromRecord = (role, documentId, rawProfile = {}) => {
  const data = fromFirestoreProfileValue(rawProfile) || {};
  const roleId = publicRoleId(role);
  const backendStatus = String(data.status || (data.public ? "published" : "draft")).toLowerCase();
  const ownerUid = String(data.ownerUid || data.playerUid || data.coachUid || data.organiserUid || data.centreOwnerUid || "").trim();
  const fullName = role === "group"
    ? data.organiserName || "Badminta organiser"
    : role === "centre"
      ? data.ownerName || data.name || "Badminton centre"
      : data.fullName || data.name || data.title || (role === "coach" ? "Badminta coach" : "Badminta player");
  const roleProfile = {
    ...data,
    id: documentId,
    firestoreSourceId: documentId,
    backendStatus,
    publicProfilePublished: true,
    publishMissing: []
  };
  if (role === "player") roleProfile.publicPlayerId = documentId;
  if (role === "coach") roleProfile.publicCoachId = documentId;
  if (role === "group") {
    roleProfile.serverGroupId = documentId;
    roleProfile.publicGroupId = documentId;
  }
  if (role === "centre") {
    roleProfile.serverCentreId = documentId;
    roleProfile.bookingEnabled = data.bookingEnabled === true;
  }
  const statusField = {
    player: "publicPlayerProfileStatus",
    coach: "publicCoachProfileStatus",
    group: "publicGroupProfileStatus",
    centre: "publicCentreProfileStatus"
  }[role];
  return {
    ...defaultAccountProfile,
    uid: ownerUid,
    ownerUid,
    fullName,
    roles: [roleId],
    [roleId === "organiser" ? "group" : roleId]: roleProfile,
    [statusField]: backendStatus
  };
};

const publicProfileNavigationPayload = (role, record = {}) => {
  const documentId = String(
    record.firestoreSourceId
    || record.publicPlayerId
    || record.publicCoachId
    || record.publicGroupId
    || record.serverGroupId
    || record.publicCentreId
    || record.serverCentreId
    || ""
  ).trim();
  if (!documentId || !publicProfilePageByRole[role]) return null;
  const accountProfile = publicAccountProfileFromRecord(role, documentId, record);
  const roleProfile = role === "group" ? accountProfile.group : accountProfile[role];
  const fallbackCity = roleProfile?.city || roleProfile?.location?.city || roleProfile?.publicAreaSlug || "Worldwide";
  const parts = getPublicProfileParts(accountProfile, role, fallbackCity);
  const payload = {
    accountProfile,
    publicProfileRoute: publicProfileRouteDescriptor(role, parts.area, parts.typeSegment, parts.slug, parts.profileId || documentId)
  };
  if (role === "group") payload.group = record;
  if (role === "coach") payload.coach = record;
  if (role === "player") payload.player = record;
  if (role === "centre") payload.venue = record;
  return payload;
};

const getAccountPublicReviewTarget = (profile = {}, role, subject = "", fallbackCity = "Melbourne", fallbackId = "") => {
  const cleanRole = publicRoleId(role);
  const collectionByRole = {
    player: "players",
    coach: "coaches",
    organiser: "groups",
    centre: "centres"
  };
  const roleProfile = cleanRole === "organiser" ? profile.group || {} : profile[cleanRole] || {};
  const idByRole = {
    player: roleProfile.publicPlayerId || roleProfile.firestoreSourceId || profile.publicPlayerProfileId,
    coach: roleProfile.publicCoachId || roleProfile.firestoreSourceId || profile.publicCoachProfileId,
    organiser: roleProfile.firestoreSourceId || roleProfile.serverGroupId || roleProfile.id,
    centre: getCentreBackendSourceId(roleProfile) || fallbackId
  };
  const targetId = String(idByRole[cleanRole] || fallbackId || publicProfilePath(profile, role, fallbackCity)).trim();
  return {
    targetCollection: collectionByRole[cleanRole] || "profiles",
    targetId,
    subjectCollection: collectionByRole[cleanRole] || "profiles",
    subjectId: targetId,
    subject
  };
};

const getEventReviewTarget = (event = {}) => {
  const targetId = String(event.firestoreSourceId || event.serverEventId || event.id || entitySlug(event, "title") || "").trim();
  return {
    targetCollection: "events",
    targetId,
    subjectCollection: "events",
    subjectId: targetId,
    subject: event.title || "Badminta event"
  };
};

const isPublicProfilePath = (profile = {}, role, areaSlug, typeSegment, profileSlug, fallbackCity = "Melbourne") => {
  const parts = getPublicProfileParts(profile, role, fallbackCity);
  return parts.area === areaSlug
    && parts.typeSegment === typeSegment
    && (parts.slug === profileSlug || publicProfileSlugBase(parts.slug) === publicProfileSlugBase(profileSlug));
};

const routeContextFromPayload = (page, payload = {}) => {
  const venue = payload.venue;
  const group = payload.group;
  const player = payload.player;
  const coach = payload.coach;
  const event = payload.event;
  const payloadLocation = payload.location || payload.searchCriteria?.location || null;
  const city = payload.city || payloadLocation?.city || venue?.city || group?.city || player?.city || coach?.city || event?.city || "Melbourne";
  const countryCode = normaliseCountryCode(payloadLocation?.countryCode || payloadLocation?.country || venue?.countryCode || venue?.country || group?.countryCode || group?.country || player?.countryCode || player?.country || coach?.countryCode || coach?.country, city);
  const routeLocation = payloadLocation || {
    city,
    suburb: venue?.suburb || group?.suburb || player?.suburb || coach?.suburb || "",
    label: venue?.suburb || group?.venue || player?.suburb || coach?.suburb || city,
    country: getCountryName(countryCode, city),
    countryCode,
    type: venue?.suburb || group?.suburb || player?.suburb || coach?.suburb ? "suburb" : "city"
  };
  return {
    city,
    selectedVenue: venue,
    selectedGroup: group,
    selectedPlayer: player,
    selectedCoach: coach,
    selectedEvent: event,
    bookingType: payload.bookingType,
    reviewContext: payload.reviewContext,
    paymentContext: payload.paymentContext,
    activeFilter: payload.activeFilter,
    activeResultType: payload.activeResultType,
    seoAreaIntent: payload.seoAreaIntent,
    publicProfileRoute: payload.publicProfileRoute || null,
    accountProfile: payload.accountProfile || loadStoredAccountProfile(),
    searchCriteria: {
      location: routeLocation
    }
  };
};

const routeHref = (page, payload = {}) => buildRoutePath(page, routeContextFromPayload(page, payload));

const handleSeoRouteClick = (event, navigate, page, payload = {}) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(page, payload);
};

const savedItemTypeLabels = {
  centre: "Centre",
  group: "Social group",
  player: "Player",
  coach: "Coach",
  event: "Event"
};

const savedItemTypeIcons = {
  centre: Building2,
  group: UsersRound,
  player: UserCircle,
  coach: Trophy,
  event: Star
};

const savedItemKey = (item = {}) => `${item.type || "item"}:${item.id || slugifyPath(item.title || item.subtitle || item.targetPath || "saved")}`;

const normaliseSavedItem = (item = {}) => {
  const type = item.type || "centre";
  const id = item.id || slugifyPath(`${type}-${item.title || item.subtitle || item.targetPath || "saved"}`);
  return {
    id,
    key: item.key || savedItemKey({ ...item, type, id }),
    type,
    title: item.title || savedItemTypeLabels[type] || "Saved item",
    subtitle: item.subtitle || "",
    meta: item.meta || savedItemTypeLabels[type] || "Saved",
    image: item.image || "",
    targetPage: item.targetPage || "saved",
    targetPath: item.targetPath || "",
    targetPayload: item.targetPayload || {},
    savedAt: item.savedAt || new Date().toISOString()
  };
};

const getSavedItems = (accountProfile = defaultAccountProfile) => {
  const rows = Array.isArray(accountProfile.savedItems) ? accountProfile.savedItems : [];
  const byKey = new globalThis.Map();
  rows.map(normaliseSavedItem).forEach((item) => {
    byKey.set(item.key, item);
  });
  return [...byKey.values()].sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
};

const savedItemFromVenue = (venue = {}) => {
  const image = getVenueImages(venue)[0] || "";
  return normaliseSavedItem({
    type: "centre",
    id: venue.id || entitySlug(venue),
    title: venue.name || "Badminton centre",
    subtitle: getVenueAddressLabel(venue),
    meta: [venue.suburb || venue.city, getVenueSourceLabel(venue)].filter(Boolean).join(" - "),
    image,
    targetPage: "venue",
    targetPath: routeHref("venue", { venue }),
    targetPayload: { venue }
  });
};

const savedItemFromEvent = (event = {}) => normaliseSavedItem({
  type: "event",
  id: event.id || entitySlug(event, "title"),
  title: event.title || "Badminton event",
  subtitle: [getEventVenueLabel(event), formatDateLabel(event.date), formatTimeLabel(event.time)].filter(Boolean).join(" - "),
  meta: event.eventType || "Community event",
  image: getEventImage(event),
  targetPage: "event",
  targetPath: routeHref("event", { event }),
  targetPayload: { event }
});

const savedItemFromGroup = (group = {}) => normaliseSavedItem({
  type: "group",
  id: getGroupStableId(group),
  title: getGroupTitle(group),
  subtitle: [getGroupVenueLabel(group), getGroupWhenLabel(group), getGroupPriceLabel(group)].filter(Boolean).join(" - "),
  meta: group.skillBand || group.format || "Social group",
  image: getGroupImage(group),
  targetPage: publicProfileNavigationPayload("group", group) ? "publicGroupProfile" : "group",
  targetPath: routeHref(
    publicProfileNavigationPayload("group", group) ? "publicGroupProfile" : "group",
    publicProfileNavigationPayload("group", group) || { group }
  ),
  targetPayload: publicProfileNavigationPayload("group", group) || { group }
});

const savedItemFromPublicProfile = (accountProfile = defaultAccountProfile, type = "player") => {
  const player = accountProfile.player || defaultAccountProfile.player;
  const coach = accountProfile.coach || defaultAccountProfile.coach;
  const group = accountProfile.group || defaultAccountProfile.group;
  const centre = accountProfile.centre || defaultAccountProfile.centre;
  const configs = {
    player: {
      title: accountProfile.fullName || "Badminta player",
      subtitle: [player.skillBand, player.suburb, player.formats].filter(Boolean).join(" - "),
      meta: "Player profile",
      image: getPublicProfileImage(player.photoUrl, accountProfile.photoUrl, approvedPublicMediaLinks(player.mediaLinks, "Player media")),
      targetPage: "publicPlayerProfile"
    },
    coach: {
      title: coach.headline || accountProfile.fullName || "Badminta coach",
      subtitle: [coach.suburb || coach.locations, coachPrimaryRateLabel(coach), coach.accreditation].filter(Boolean).join(" - "),
      meta: "Coach profile",
      image: getPublicProfileImage(coach.photoUrl, accountProfile.photoUrl, approvedPublicMediaLinks(coach.mediaLinks, "Coach media")),
      targetPage: "publicCoachProfile"
    },
    group: {
      title: group.name || "Social badminton group",
      subtitle: [group.venue, getGroupWhenLabel(group), getGroupPriceLabel(group)].filter(Boolean).join(" - "),
      meta: "Social group profile",
      image: getPublicProfileImage(group.coverUrl, group.logoUrl),
      targetPage: "publicGroupProfile"
    },
    centre: {
      title: centre.name || "Badminton centre",
      subtitle: [centre.address, centre.suburb, centre.courtCount ? `${centre.courtCount} courts` : ""].filter(Boolean).join(" - "),
      meta: "Centre profile",
      image: getPublicProfileImage(centre.logoUrl, approvedPublicMediaLinks(centre.photoLinks, "Centre media")),
      targetPage: "publicCentreProfile"
    }
  };
  const config = configs[type] || configs.player;
  return normaliseSavedItem({
    type,
    id: `public-${type}-${shortStableHash(publicProfilePath(accountProfile, type))}`,
    ...config,
    targetPath: publicProfilePath(accountProfile, type)
  });
};

const locationSlug = (location, fallbackCity = "Melbourne") => {
  if (location?.type === "nearby") return "near-me";
  return slugifyPath(location?.suburb || location?.label || fallbackCity);
};

function countrySlug(countryValue, fallbackCity = "Melbourne") {
  return slugifyPath(getCountryName(countryValue, fallbackCity) || countryValue || "worldwide");
}

function citySlug(cityName) {
  return slugifyPath(cityName || "Melbourne");
}

const cityNameFromRouteSlug = (slug = "") => String(slug || "")
  .trim()
  .replace(/[-_]+/g, " ")
  .replace(/\s+/g, " ")
  .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

function findCityBySlug(slug) {
  const value = slugifyPath(slug);
  return cityDirectory.find((item) => slugifyPath(item.city) === value)?.city
    || cities.find((item) => slugifyPath(item) === value)
    || cityNameFromRouteSlug(slug)
    || "Worldwide";
}

const syntheticCityLocation = (slug, cityName = findCityBySlug(slug)) => ({
  id: `route-city-${slugifyPath(slug || cityName)}`,
  label: cityName,
  city: cityName,
  suburb: "",
  country: "",
  countryCode: "",
  type: "city",
  hint: "City",
  lat: null,
  lon: null,
  bbox: null,
  source: "Route location"
});

const findLocationBySlug = (slug) => {
  if (!slug || slug === "near-me") return null;
  return locationOptions.find((option) => slugifyPath(option.suburb || option.label) === slug)
    || locationOptions.find((option) => slugifyPath(option.city) === slug)
    || null;
};

const venueMatchesSlug = (venue, slug) => {
  const cleanSlug = slugifyPath(slug);
  return entitySlug(venue) === cleanSlug
    || slugifyPath(venue.name) === cleanSlug
    || (Array.isArray(venue.aliases) && venue.aliases.some((alias) => slugifyPath(alias) === cleanSlug));
};
const centreProfileIdFromLegacySlug = (slug = "") => {
  const match = slugifyPath(slug).match(/(?:^|-)(centre)-([a-z0-9]+)-([a-f0-9]{40})$/);
  return match ? `${match[1]}_${match[2]}_${match[3]}` : "";
};
const findVenueBySlug = (slug) => venues.find((venue) => venueMatchesSlug(venue, slug)) || null;
const findGroupBySlug = (slug, accountProfile = null) => {
  const cleanSlug = slugifyPath(slug);
  const directoryGroup = groups.find((group) => entitySlug(group, "title") === cleanSlug || slugifyPath(getGroupTitle(group)) === cleanSlug);
  if (directoryGroup) return directoryGroup;
  const accountGroup = accountProfile?.group || null;
  if (!accountGroup || !roleProfileStarted(accountProfile, "organiser")) return null;
  const possibleSlugs = new Set([
    entitySlug(accountGroup, "title"),
    entitySlug({ ...accountGroup, title: getGroupTitle(accountGroup) }, "title"),
    slugifyPath(accountGroup.slug || ""),
    slugifyPath(getGroupTitle(accountGroup))
  ].filter(Boolean));
  return possibleSlugs.has(cleanSlug) ? normaliseJoinedGroup(accountGroup) : null;
};
const findPlayerBySlug = (slug) => players.find((player) => entitySlug(player) === slug) || null;
const findCoachBySlug = (slug) => trainers.find((coach) => entitySlug(coach) === slug || slugifyPath(coach.name) === slug) || null;

const pageMode = {
  home: "all",
  solutionIndex: "all",
  solutionCentres: "centres",
  solutionOrganisers: "socials",
  solutionCoaches: "players",
  solutionPlayers: "players",
  solutionEvents: "events",
  filterHub: "all",
  seoArea: "all",
  results: "centres",
  centreUnavailable: "centres",
  groupsIndex: "socials",
  group: "socials",
  playersIndex: "players",
  player: "players",
  coach: "players",
  publicPlayerProfile: "players",
  publicCoachProfile: "players",
  publicGroupProfile: "socials",
  groupSessions: "socials",
  createGroupSession: "socials",
  editGroupSession: "socials",
  publicCentreProfile: "centres",
  coachProfile: "players",
  centreClaimSearch: "centres",
  courtAvailability: "courts",
  booking: "courts",
  join: "socials",
  events: "events",
  event: "events",
  eventJoin: "events",
  createEvent: "events",
  eventManager: "events",
  joinedEvents: "events",
  manageEvent: "events",
  reviewFlow: "all",
  paymentFlow: "all"
};

const hubTopModeByResultType = {
  centres: "centres",
  groups: "socials",
  players: "players",
  coaches: "players",
  events: "events"
};

const getHubTopMode = (resultType, filterKey = "nearby") => {
  const resolvedResultType = normaliseHubResultType(resultType) || filterDefaultResultTypes[filterKey] || "centres";
  return hubTopModeByResultType[resolvedResultType] || "all";
};

const getRouteTopMode = (route = {}) => {
  if (route.page === "filterHub") return getHubTopMode(route.activeResultType, route.activeFilter);
  if (route.activeFilter) return filterContent[route.activeFilter]?.mode || pageMode[route.page] || "all";
  return pageMode[route.page] || "all";
};

const pageFallbackFilter = {
  solutionIndex: "nearby",
  solutionCentres: "nearby",
  solutionOrganisers: "beginner",
  solutionCoaches: "coaching",
  solutionPlayers: "advanced",
  solutionEvents: "nearby",
  filterHub: "nearby",
  seoArea: "nearby",
  results: "nearby",
  centreUnavailable: "nearby",
  groupsIndex: "nearby",
  group: "beginner",
  playersIndex: "nearby",
  player: "advanced",
  coach: "coaching",
  publicPlayerProfile: "advanced",
  publicCoachProfile: "coaching",
  publicGroupProfile: "beginner",
  groupSessions: "beginner",
  createGroupSession: "beginner",
  editGroupSession: "beginner",
  publicCentreProfile: "nearby",
  centreClaimSearch: "nearby",
  courtAvailability: "late",
  events: "nearby",
  event: "nearby",
  eventJoin: "nearby",
  createEvent: "nearby",
  eventManager: "nearby",
  joinedEvents: "nearby",
  manageEvent: "nearby",
  reviewFlow: "nearby",
  paymentFlow: "nearby"
};

const buildRoutePath = (page, context = {}) => {
  const venue = context.selectedVenue;
  const group = context.selectedGroup;
  const player = context.selectedPlayer;
  const coach = context.selectedCoach;
  const event = context.selectedEvent;
  const blogPost = context.blogPost;
  const reviewContext = context.reviewContext;
  const paymentContext = context.paymentContext;
  const accountProfile = context.accountProfile || loadStoredAccountProfile();
  const currentCity = context.city || context.searchCriteria?.location?.city || venue?.city || group?.city || player?.city || coach?.city || event?.city || "Melbourne";
  const currentLocation = context.searchCriteria?.location;
  const venueCity = citySlug(venue?.city || currentCity);
  const groupCity = citySlug(group?.city || currentCity);
  const playerCity = citySlug(player?.city || currentCity);
  const coachCity = citySlug(coach?.city || currentCity);
  const eventCity = citySlug(event?.city || currentCity);
  const activePublicRoute = context.publicProfileRoute;
  if (activePublicRoute && publicProfilePageByRole[activePublicRoute.role] === page) {
    return publicProfileRoutePath(activePublicRoute);
  }
  const groupProfileId = String(group?.firestoreSourceId || group?.serverGroupId || group?.publicGroupId || "").trim();
  const groupPublicPath = group && groupProfileId
    ? publicProfilePath(publicAccountProfileFromRecord("group", groupProfileId, group), "group", currentCity)
    : "";

  switch (page) {
    case "home":
      return "/";
    case "solutionIndex":
      return "/solutions";
    case "solutionCentres":
    case "solutionOrganisers":
    case "solutionCoaches":
    case "solutionPlayers":
    case "solutionEvents":
      return solutionPageByRoute[page]?.path || "/solutions";
    case "blogIndex":
      return "/blog";
    case "blogArticle":
      return context.blogPost ? `/blog/${context.blogPost.slug}` : "/blog";
    case "filterHub": {
      const filterSlug = slugifyPath(context.activeFilter || "nearby");
      const resultTypeSegment = getHubResultTypeSegment(context.activeResultType || getPreferredHubResultType(context.activeFilter || "nearby", context.searchCriteria?.tags));
      return `/badminton/${citySlug(currentCity)}/${filterSlug}${resultTypeSegment ? `/${resultTypeSegment}` : ""}`;
    }
    case "seoArea":
      return seoAreaLocationPath(currentLocation || { city: currentCity, label: currentCity }, context.seoAreaIntent || "overview");
    case "results":
      return `/centres/${locationSlug(currentLocation, currentCity)}`;
    case "venue":
      return venue ? `/centres/${venueCity}/${entitySlug(venue)}` : `/centres/${citySlug(currentCity)}`;
    case "centreUnavailable":
      return venue ? `/centres/${venueCity}/${entitySlug(venue)}` : `/centres/${citySlug(currentCity)}`;
    case "booking":
      if (context.bookingType === "social" && group) return groupPublicPath ? `${groupPublicPath}/join` : `/social-groups/${groupCity}/${entitySlug(group, "title")}/join`;
      if (venue?.serverCentreId || venue?.firestoreSourceId) {
        const centrePayload = publicProfileNavigationPayload("centre", venue);
        if (centrePayload?.publicProfileRoute) return publicProfileRoutePath({ ...centrePayload.publicProfileRoute, action: "book" });
      }
      return venue ? `/centres/${venueCity}/${entitySlug(venue)}/book` : `/centres/${citySlug(currentCity)}/book`;
    case "claimCentre":
      return venue && !venue.isNewCentre ? `/centres/${venueCity}/${entitySlug(venue)}/claim` : "/centre-tools/claim-centre";
    case "centreClaimSearch":
      return "/centre-tools/search-and-claim";
    case "courtManager":
      return venue ? `/centres/${venueCity}/${entitySlug(venue)}/courts` : "/centre-tools/court-inventory";
    case "groupsIndex":
      return `/social-groups/${citySlug(currentCity)}`;
    case "group":
      return groupPublicPath || (group ? `/social-groups/${groupCity}/${entitySlug(group, "title")}` : `/social-groups/${citySlug(currentCity)}`);
    case "join":
      if (groupPublicPath) return `${groupPublicPath}/join`;
      if (group && groupMatchesAccountPublicProfile(group, accountProfile)) return `${publicProfilePath(accountProfile, "group", currentCity)}/join`;
      return group ? `/social-groups/${groupCity}/${entitySlug(group, "title")}/join` : `/social-groups/${citySlug(currentCity)}/join`;
    case "groupMembership":
      if (groupPublicPath) return `${groupPublicPath}/membership`;
      if (group && groupMatchesAccountPublicProfile(group, accountProfile)) return `${publicProfilePath(accountProfile, "group", currentCity)}/membership`;
      return group ? `/social-groups/${groupCity}/${entitySlug(group, "title")}/membership` : `/social-groups/${citySlug(currentCity)}/membership`;
    case "playersIndex":
      return context.activeFilter === "coaching" ? `/coaches/${citySlug(currentCity)}` : `/players/${citySlug(currentCity)}`;
    case "player":
      {
        const publicPayload = player ? publicProfileNavigationPayload("player", player) : null;
        return publicPayload?.publicProfileRoute
          ? publicProfileRoutePath(publicPayload.publicProfileRoute)
          : player ? `/players/${playerCity}/${entitySlug(player)}` : `/players/${citySlug(currentCity)}`;
      }
    case "coach":
      {
        const publicPayload = coach ? publicProfileNavigationPayload("coach", coach) : null;
        return publicPayload?.publicProfileRoute
          ? publicProfileRoutePath(publicPayload.publicProfileRoute)
          : coach ? `/coaches/${coachCity}/${entitySlug(coach)}` : `/coaches/${citySlug(currentCity)}`;
      }
    case "playerDashboard":
      return "/players/dashboard";
    case "profileSetup":
      return "/players/profile/setup";
    case "publicPlayerProfile":
      return publicProfilePath(accountProfile, "player", currentCity);
    case "coachDashboard":
      return "/coaches/dashboard";
    case "coachProfile":
      return "/coaches/profile/setup";
    case "coachPayouts":
      return "/coaches/payouts";
    case "publicCoachProfile":
      return publicProfilePath(accountProfile, "coach", currentCity);
    case "ratings":
      return "/players/ratings";
    case "courtAvailability":
      return `/courts-tonight/${citySlug(currentCity)}`;
    case "events":
      return `/events/${citySlug(currentCity)}`;
    case "event":
      return event ? `/events/${eventCity}/${entitySlug(event, "title")}` : `/events/${citySlug(currentCity)}`;
    case "eventJoin":
      return event ? `/events/${eventCity}/${entitySlug(event, "title")}/join` : `/events/${citySlug(currentCity)}/join`;
    case "createEvent":
      return "/events/create";
    case "eventManager":
      return "/account/events";
    case "joinedEvents":
      return "/account/events/joined";
    case "manageEvent":
      return event ? `/account/events/${entitySlug(event, "title")}/manage` : "/account/events";
    case "reviewFlow":
      return `/reviews/${slugifyPath(reviewContext?.kind || "general")}`;
    case "paymentFlow":
      return `/payments/${slugifyPath(paymentContext?.kind || "general")}`;
    case "bookings":
      return "/account/bookings";
    case "city":
      return `/cities/${citySlug(currentCity)}`;
    case "messages":
      return "/account/messages";
    case "notifications":
      return "/account/notifications";
    case "login":
      return "/account/login";
    case "signup": {
      const signupParams = new URLSearchParams();
      if (context.signupStep === "roles") signupParams.set("step", "roles");
      if (["player", "coach", "organiser", "centre"].includes(context.signupRole)) signupParams.set("role", context.signupRole);
      if (context.signupSource) signupParams.set("from", signupSourceSlugs[context.signupSource] || slugifyPath(context.signupSource));
      const signupQuery = signupParams.toString();
      return `/account/signup${signupQuery ? `?${signupQuery}` : ""}`;
    }
    case "onboarding":
      return "/account/onboarding";
    case "saved":
      return "/account/saved";
    case "following":
      return "/account/following";
    case "account":
      return "/account";
    case "accountDeletion":
      return "/account/delete";
    case "help":
      return "/support/help";
    case "legalTerms":
      return "/legal/terms";
    case "privacyPolicy":
      return "/legal/privacy";
    case "refundPolicy":
      return "/legal/refunds";
    case "communityPolicy":
      return "/legal/community-safety";
    case "cookiePolicy":
      return "/legal/cookies";
    case "youthPolicy":
      return "/legal/youth-safety";
    case "centrePolicy":
      return "/legal/centre-terms";
    case "organiserPolicy":
      return "/legal/organiser-terms";
    case "coachPolicy":
      return "/legal/coach-terms";
    case "organiser":
      return "/organiser";
    case "createGroup":
      return "/organiser/create-group";
    case "groupSessions":
      return "/organiser/sessions";
    case "createGroupSession":
      return "/organiser/sessions/new";
    case "editGroupSession":
      {
        const currentSessionId = typeof window !== "undefined"
          ? window.location.pathname.match(/^\/organiser\/sessions\/([^/]+)\/edit\/?$/)?.[1] || ""
          : "";
        const sessionId = context.selectedGroupSession?.sessionId || context.selectedGroupSession?.id || currentSessionId;
        return sessionId ? `/organiser/sessions/${encodeURIComponent(decodeURIComponent(sessionId))}/edit` : "/organiser/sessions";
      }
    case "publicGroupProfile":
      return publicProfilePath(accountProfile, "group", currentCity);
    case "payouts":
      return "/organiser/payouts";
    case "members":
      return "/organiser/members";
    case "centreDashboard":
      return "/centre-tools/dashboard";
    case "publicCentreProfile":
      return publicProfilePath(accountProfile, "centre", currentCity);
    case "centrePayments":
      return "/centre-tools/payments";
    case "admin":
      return "/admin";
    case "trust":
      return "/admin/trust";
    case "analytics":
      return "/admin/analytics";
    case "administrator":
      return "/administrator";
    default:
      return `/${slugifyPath(routeLabels[page] || page)}`;
  }
};

const resolveRouteFromPath = (pathname = window.location.pathname) => {
  const rawSegments = pathname.split("/").map((segment) => decodeURIComponent(segment)).filter(Boolean);
  const segments = rawSegments.map((segment) => slugifyPath(segment));
  const storedProfile = loadStoredAccountProfile(loadLocalTestUser() || firebaseAuth?.currentUser || null);
  const fallbackLocation = getDefaultSearchLocation();
  const fallback = {
    page: "home",
    city: "Melbourne",
    location: fallbackLocation,
    venue: null,
    group: null,
    groupSession: null,
    player: null,
    coach: null,
    event: null,
    eventSlug: "",
    reviewKind: "",
    paymentKind: "",
    bookingType: "court",
    activeFilter: "",
    activeResultType: "",
    seoAreaIntent: "overview",
    pendingVenueRoute: null,
    publicProfileRoute: null
  };
  if (!segments.length) return fallback;

  const [section, first, second, third, fourth, fifth] = segments;
  const rawFourth = rawSegments[4] || "";
  const cityFromFirst = first ? findCityBySlug(first) : "Melbourne";
  const nearMeLocation = first === "near-me"
    ? { id: "nearby-url-location", label: "Near me", city: cityFromFirst, suburb: "", type: "nearby", hint: "Nearby search" }
    : null;
  const locationFromFirst = nearMeLocation || findLocationBySlug(first);
  const routeForLocation = locationFromFirst
    || (first ? locationOptions.find((option) => compactLocationText(option.city) === compactLocationText(cityFromFirst) && option.type === "city") : fallbackLocation)
    || (first ? syntheticCityLocation(first, cityFromFirst) : fallbackLocation)
    || fallbackLocation;

  if (section === "login") return { ...fallback, page: "login" };
  if (section === "signup") return { ...fallback, page: "signup" };
  if (section === "saved") return { ...fallback, page: "saved" };
  if (section === "messages") return { ...fallback, page: "messages" };
  if (section === "reviews") return { ...fallback, page: "reviewFlow", reviewKind: first || "general" };
  if (section === "payments" && first === "stripe" && second === "success") {
    return { ...fallback, page: "bookings", checkoutStatus: "success" };
  }
  if (section === "payments") return { ...fallback, page: "paymentFlow", paymentKind: first || "general" };
  if (section === "solutions") return { ...fallback, page: solutionRouteBySlug[first] || "solutionIndex" };
  if (section === "blog") {
    const blogPost = getBlogPost(first || "");
    return blogPost
      ? { ...fallback, page: "blogArticle", blogSlug: blogPost.slug, blogPost }
      : { ...fallback, page: "blogIndex", blogSlug: "", blogPost: null };
  }
  if (section === "badminton") {
    const countryLocation = first ? findCountryBySlug(first) : null;
    const secondIsFilter = Boolean(filterContent[second]);
    if (countryLocation && !secondIsFilter) {
      const countryIntent = seoAreaIntentFromSlug(second || "");
      if (!second || countryIntent) {
        const intentId = normaliseSeoAreaIntent(countryIntent || "overview");
        return {
          ...fallback,
          page: "seoArea",
          city: countryLocation.city || "Melbourne",
          location: countryLocation,
          seoAreaIntent: intentId,
          activeFilter: "nearby",
          activeResultType: seoAreaIntents[intentId]?.resultType || "centres"
        };
      }
      const countryCityLocation = findCityLocationBySlug(first, second);
      if (countryCityLocation) {
        const cityIntent = seoAreaIntentFromSlug(third || "");
        if (!third || cityIntent) {
          const intentId = normaliseSeoAreaIntent(cityIntent || "overview");
          return {
            ...fallback,
            page: "seoArea",
            city: countryCityLocation.city,
            location: countryCityLocation,
            seoAreaIntent: intentId,
            activeFilter: "nearby",
            activeResultType: seoAreaIntents[intentId]?.resultType || "centres"
          };
        }
        const countrySuburbLocation = findSeoAreaLocationWorld(first, second, third);
        const suburbIntent = seoAreaIntentFromSlug(fourth || "");
        if (countrySuburbLocation && (!fourth || suburbIntent)) {
          const intentId = normaliseSeoAreaIntent(suburbIntent || "overview");
          return {
            ...fallback,
            page: "seoArea",
            city: countrySuburbLocation.city,
            location: countrySuburbLocation,
            seoAreaIntent: intentId,
            activeFilter: "nearby",
            activeResultType: seoAreaIntents[intentId]?.resultType || "centres"
          };
        }
      }
    }
    const seoLocation = first && second ? findSeoAreaLocation(first, second) : null;
    const seoIntent = seoAreaIntentFromSlug(third || "");
    if (seoLocation && (!third || seoIntent)) {
      const intentId = normaliseSeoAreaIntent(seoIntent || "overview");
      return {
        ...fallback,
        page: "seoArea",
        city: seoLocation.city,
        location: seoLocation,
        seoAreaIntent: intentId,
        activeFilter: "nearby",
        activeResultType: seoAreaIntents[intentId]?.resultType || "centres"
      };
    }
    const filterKey = filterContent[second] ? second : filterContent[first] ? first : "nearby";
    const activeResultType = normaliseHubResultType(filterContent[second] ? third : second);
    return {
      ...fallback,
      page: "filterHub",
      city: cityFromFirst,
      location: routeForLocation,
      activeFilter: filterKey,
      activeResultType
    };
  }

  if (section === "centres") {
    if (first === "dashboard") return { ...fallback, page: "centreDashboard" };
    if (first === "claim-centre") return { ...fallback, page: "claimCentre", location: fallbackLocation, venue: null };
    if (first === "court-inventory") return { ...fallback, page: "courtManager" };
    if (first === "payments") return { ...fallback, page: "centrePayments" };
    if (first === "profile" && second === "public") return { ...fallback, page: "publicCentreProfile" };

    const centreAction = fifth === "book" ? "book" : "";
    const centrePublicProfileRoute = third
      ? publicProfileRouteDescriptor("centre", first, second, third, rawFourth, centreAction)
      : null;
    if (centrePublicProfileRoute) {
      return {
        ...fallback,
        page: centreAction ? "booking" : "publicCentreProfile",
        city: routeForLocation?.city || cityFromFirst,
        location: routeForLocation,
        bookingType: "court",
        publicProfileRoute: centrePublicProfileRoute
      };
    }

    const legacyOwnedCentreMatch = String(second || "").match(/^(.*)-centre-([a-z0-9]+)-([a-f0-9]{24,64})$/i);
    if (legacyOwnedCentreMatch) {
      const legacyCentreId = `centre_${legacyOwnedCentreMatch[2]}_${legacyOwnedCentreMatch[3]}`;
      const legacyPublicRoute = publicProfileRouteDescriptor(
        "centre",
        first,
        publicProfileRoleConfig.centre.typeSegment,
        legacyOwnedCentreMatch[1],
        legacyCentreId
      );
      if (legacyPublicRoute) {
        return {
          ...fallback,
          page: "publicCentreProfile",
          city: routeForLocation?.city || cityFromFirst,
          location: routeForLocation,
          publicProfileRoute: legacyPublicRoute
        };
      }
    }

    const venue = second ? findVenueBySlug(second) : null;
    if (venue && third === "book") return { ...fallback, page: "booking", city: venue.city || cityFromFirst, location: routeForLocation, venue, bookingType: "court" };
    if (venue && third === "claim") return { ...fallback, page: "centreClaimSearch", city: venue.city || cityFromFirst, location: routeForLocation, venue };
    if (venue && third === "courts") return { ...fallback, page: "courtManager", city: venue.city || cityFromFirst, location: routeForLocation, venue };
    const ownedCentreVenue = venue && isPlatformOwnedCentre(venue, storedProfile)
      ? managedVenueFromCentreProfile(storedProfile.centre || {}, venue)
      : venue;
    const ownedCentrePublicPayload = ownedCentreVenue && isOwnerManagedCentreListing(ownedCentreVenue)
      ? publicProfileNavigationPayload("centre", ownedCentreVenue)
      : null;
    if (venue && ownedCentrePublicPayload?.publicProfileRoute) {
      return {
        ...fallback,
        page: "publicCentreProfile",
        city: venue.city || cityFromFirst,
        location: routeForLocation,
        venue,
        publicProfileRoute: ownedCentrePublicPayload.publicProfileRoute
      };
    }
    if (venue) return { ...fallback, page: "venue", city: venue.city || cityFromFirst, location: routeForLocation, venue };
    if (third && isPublicProfilePath(storedProfile, "centre", first, second, third, cityFromFirst)) {
      return { ...fallback, page: "publicCentreProfile", city: routeForLocation?.city || cityFromFirst, location: routeForLocation };
    }
    const pendingAction = !third ? "" : ["book", "claim", "courts"].includes(third) ? third : null;
    if (second && pendingAction !== null) {
      return {
        ...fallback,
        page: "results",
        city: routeForLocation?.city || cityFromFirst,
        location: routeForLocation,
        pendingVenueRoute: {
          slug: second,
          action: pendingAction,
          city: routeForLocation?.city || cityFromFirst,
          location: routeForLocation
        }
      };
    }
    return { ...fallback, page: "results", city: routeForLocation?.city || cityFromFirst, location: routeForLocation };
  }

  if (section === "social-groups") {
    if (first === "profile" && second === "public") return { ...fallback, page: "publicGroupProfile" };
    const groupAction = ["join", "membership"].find((action) => fourth === action || fifth === action) || "";
    const groupProfileId = fourth && !["join", "membership"].includes(fourth) ? rawFourth : "";
    const groupPublicProfileRoute = third
      ? publicProfileRouteDescriptor("group", first, second, third, groupProfileId, groupAction)
      : null;
    if (groupPublicProfileRoute) {
      return {
        ...fallback,
        page: "publicGroupProfile",
        city: routeForLocation?.city || cityFromFirst,
        location: routeForLocation,
        bookingType: groupAction ? "social" : "court",
        publicProfileRoute: groupPublicProfileRoute
      };
    }
    const group = second ? findGroupBySlug(second, storedProfile) : null;
    if (group && third === "join") return { ...fallback, page: "join", city: group.city || cityFromFirst, location: routeForLocation, group, bookingType: "social" };
    if (group && third === "membership") return { ...fallback, page: "groupMembership", city: group.city || cityFromFirst, location: routeForLocation, group };
    if (group) return { ...fallback, page: "group", city: group.city || cityFromFirst, location: routeForLocation, group };
    if (second && (!third || ["join", "membership"].includes(third))) {
      const legacyGroupPublicProfileRoute = publicProfileRouteDescriptor(
        "group",
        first,
        publicProfileRoleConfig.group.typeSegment,
        second,
        "",
        ["join", "membership"].includes(third) ? third : ""
      );
      if (legacyGroupPublicProfileRoute) {
        return {
          ...fallback,
          page: "publicGroupProfile",
          city: routeForLocation?.city || cityFromFirst,
          location: routeForLocation,
          bookingType: third === "join" ? "social" : "court",
          publicProfileRoute: legacyGroupPublicProfileRoute
        };
      }
    }
    if (third && isPublicProfilePath(storedProfile, "group", first, second, third, cityFromFirst)) {
      const accountGroup = normaliseJoinedGroup(storedProfile.group || {});
      if (fourth === "join") {
        return { ...fallback, page: "join", city: routeForLocation?.city || cityFromFirst, location: routeForLocation, group: accountGroup, bookingType: "social" };
      }
      return { ...fallback, page: "publicGroupProfile", city: routeForLocation?.city || cityFromFirst, location: routeForLocation };
    }
    if (first === "join") return { ...fallback, page: "join", city: cityFromFirst, location: routeForLocation, bookingType: "social" };
    return { ...fallback, page: "groupsIndex", city: cityFromFirst, location: routeForLocation };
  }

  if (section === "players") {
    if (first === "dashboard") return { ...fallback, page: "playerDashboard" };
    if (first === "ratings") return { ...fallback, page: "ratings" };
    if (first === "profile" && second === "setup") return { ...fallback, page: "profileSetup" };
    if (first === "profile" && second === "public") return { ...fallback, page: "publicPlayerProfile" };
    const playerPublicProfileRoute = third
      ? publicProfileRouteDescriptor("player", first, second, third, rawFourth)
      : null;
    if (playerPublicProfileRoute) {
      return {
        ...fallback,
        page: "publicPlayerProfile",
        city: routeForLocation?.city || cityFromFirst,
        location: routeForLocation,
        publicProfileRoute: playerPublicProfileRoute
      };
    }
    if (third && isPublicProfilePath(storedProfile, "player", first, second, third, cityFromFirst)) {
      return { ...fallback, page: "publicPlayerProfile", city: routeForLocation?.city || cityFromFirst, location: routeForLocation };
    }
    const player = second ? findPlayerBySlug(second) : null;
    if (player) return { ...fallback, page: "player", city: player.city || cityFromFirst, location: routeForLocation, player };
    if (second) {
      const legacyPlayerPublicProfileRoute = publicProfileRouteDescriptor(
        "player",
        first,
        publicProfileRoleConfig.player.typeSegment,
        second
      );
      if (legacyPlayerPublicProfileRoute) {
        return {
          ...fallback,
          page: "publicPlayerProfile",
          city: routeForLocation?.city || cityFromFirst,
          location: routeForLocation,
          publicProfileRoute: legacyPlayerPublicProfileRoute
        };
      }
      return { ...fallback, page: "player", city: cityFromFirst, location: routeForLocation, playerSlug: second };
    }
    return { ...fallback, page: "playersIndex", city: cityFromFirst, location: routeForLocation };
  }

  if (section === "coaches") {
    if (first === "dashboard") return { ...fallback, page: "coachDashboard" };
    if (first === "payouts") return { ...fallback, page: "coachPayouts" };
    if (first === "profile" && second === "setup") return { ...fallback, page: "coachProfile" };
    if (first === "profile" && second === "public") return { ...fallback, page: "publicCoachProfile" };
    const coachPublicProfileRoute = third
      ? publicProfileRouteDescriptor("coach", first, second, third, rawFourth)
      : null;
    if (coachPublicProfileRoute) {
      return {
        ...fallback,
        page: "publicCoachProfile",
        city: routeForLocation?.city || cityFromFirst,
        location: routeForLocation,
        activeFilter: "coaching",
        publicProfileRoute: coachPublicProfileRoute
      };
    }
    const coach = second ? findCoachBySlug(second) : null;
    if (coach) return { ...fallback, page: "coach", city: coach.city || cityFromFirst, location: routeForLocation, coach, activeFilter: "coaching" };
    if (second && !third) {
      const legacyCoachPublicProfileRoute = publicProfileRouteDescriptor(
        "coach",
        first,
        publicProfileRoleConfig.coach.typeSegment,
        second
      );
      if (legacyCoachPublicProfileRoute) {
        return {
          ...fallback,
          page: "publicCoachProfile",
          city: routeForLocation?.city || cityFromFirst,
          location: routeForLocation,
          activeFilter: "coaching",
          publicProfileRoute: legacyCoachPublicProfileRoute
        };
      }
    }
    if (third && isPublicProfilePath(storedProfile, "coach", first, second, third, cityFromFirst)) {
      return { ...fallback, page: "publicCoachProfile", city: routeForLocation?.city || cityFromFirst, location: routeForLocation, activeFilter: "coaching" };
    }
    return { ...fallback, page: "playersIndex", city: cityFromFirst, location: routeForLocation, activeFilter: "coaching" };
  }

  if (section === "courts-tonight") return { ...fallback, page: "courtAvailability", city: cityFromFirst, location: routeForLocation };
  if (section === "events") {
    if (first === "create") return { ...fallback, page: "createEvent" };
    const eventSlug = second && second !== "join" ? second : "";
    if (eventSlug && third === "join") return { ...fallback, page: "eventJoin", city: cityFromFirst, location: routeForLocation, eventSlug, bookingType: "event" };
    if (eventSlug) return { ...fallback, page: "event", city: cityFromFirst, location: routeForLocation, eventSlug };
    if (first === "join") return { ...fallback, page: "eventJoin", city: cityFromFirst, location: routeForLocation, bookingType: "event" };
    return { ...fallback, page: "events", city: cityFromFirst, location: routeForLocation };
  }
  if (section === "cities") return { ...fallback, page: "city", city: cityFromFirst, location: routeForLocation };

  if (section === "account") {
    if (first === "events") {
      if (second === "joined") return { ...fallback, page: "joinedEvents" };
      if (second && third === "manage") return { ...fallback, page: "manageEvent", eventSlug: second };
      return { ...fallback, page: "eventManager" };
    }
    const accountRoutes = { login: "login", signup: "signup", onboarding: "onboarding", bookings: "bookings", saved: "saved", following: "following", messages: "messages", notifications: "notifications", delete: "accountDeletion" };
    return { ...fallback, page: accountRoutes[first] || "account" };
  }

  if (section === "support") {
    const supportRoutes = {
      help: "help",
      refunds: "refundPolicy",
      privacy: "privacyPolicy",
      terms: "legalTerms",
      "community-safety": "communityPolicy",
      cookies: "cookiePolicy",
      "youth-safety": "youthPolicy",
      "centre-terms": "centrePolicy",
      "organiser-terms": "organiserPolicy",
      "coach-terms": "coachPolicy"
    };
    return { ...fallback, page: supportRoutes[first] || "help" };
  }

  if (section === "legal") {
    const legalRoutes = {
      terms: "legalTerms",
      privacy: "privacyPolicy",
      refunds: "refundPolicy",
      "community-safety": "communityPolicy",
      cookies: "cookiePolicy",
      "youth-safety": "youthPolicy",
      "centre-terms": "centrePolicy",
      "organiser-terms": "organiserPolicy",
      "coach-terms": "coachPolicy"
    };
    return { ...fallback, page: legalRoutes[first] || "legalTerms" };
  }

  if (section === "organiser") {
    if (first === "group" && second === "public") return { ...fallback, page: "publicGroupProfile" };
    if (first === "sessions") {
      if (second === "new") return { ...fallback, page: "createGroupSession" };
      if (second && third === "edit") {
        const sessionId = rawSegments[2] || second;
        const groupSession = (Array.isArray(storedProfile.group?.sessions) ? storedProfile.group.sessions : [])
          .find((session) => String(session.sessionId || session.id) === String(sessionId)) || null;
        return { ...fallback, page: "editGroupSession", groupSession };
      }
      return { ...fallback, page: "groupSessions" };
    }
    const organiserRoutes = { "create-group": "createGroup", payouts: "payouts", members: "members" };
    return { ...fallback, page: organiserRoutes[first] || "organiser" };
  }

  if (section === "centre-tools") {
    if (first === "profile" && second === "public") return { ...fallback, page: "publicCentreProfile" };
    if (first === "claim-centre") return { ...fallback, page: "claimCentre", location: fallbackLocation, venue: null };
    if (first === "search-and-claim") return { ...fallback, page: "centreClaimSearch" };
    const centreToolRoutes = { dashboard: "centreDashboard", "court-inventory": "courtManager", payments: "centrePayments" };
    return { ...fallback, page: centreToolRoutes[first] || "centreDashboard" };
  }

  if (section === "admin") {
    const adminRoutes = { trust: "trust", analytics: "analytics" };
    return { ...fallback, page: adminRoutes[first] || "admin" };
  }

  if (section === "administrator") return { ...fallback, page: "administrator" };

  return fallback;
};

const authCriticalPath = (pathname = "") => {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  return /^\/(?:account(?:\/|$)|administrator(?:\/|$)|admin(?:\/|$)|players\/(?:dashboard|profile\/setup)(?:\/|$)|coaches\/(?:dashboard|profile\/setup|payouts)(?:\/|$)|organiser(?:\/|$)|centre-tools(?:\/|$)|reviews(?:\/|$)|payments(?:\/|$))/.test(path)
    || /^\/(?:centres|social-groups|events)\/.+\/(?:book|join|membership)$/.test(path);
};

function useFirebasePhoneAuth() {
  const authCriticalRoute = authCriticalPath(window.location.pathname);
  const embeddedBrowserRequiresVisibleCaptcha = isEmbeddedSocialBrowser();
  const rememberedFirebaseSession = (() => {
    try {
      return window.localStorage.getItem(rememberedFirebaseSessionKey) === "true";
    } catch {
      return false;
    }
  })();
  const [user, setUser] = useState(() => loadLocalTestUser() || firebaseAuth?.currentUser || null);
  const [loading, setLoading] = useState(firebaseConfigured && (authCriticalRoute || rememberedFirebaseSession));
  const [authError, setAuthError] = useState("");
  const [smsStatus, setSmsStatus] = useState("idle");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [captchaMode, setCaptchaMode] = useState(embeddedBrowserRequiresVisibleCaptcha ? "visible" : "invisible");
  const recaptchaVerifier = useRef(null);
  const forceVisibleRecaptcha = useRef(embeddedBrowserRequiresVisibleCaptcha);
  const lastSmsRequestAt = useRef(0);

  useEffect(() => {
    if (!firebaseConfigured) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let started = false;
    let unsubscribe = null;
    const startRuntime = async () => {
      if (started || cancelled) return;
      started = true;
      setLoading(true);
      try {
        await loadFirebaseRuntime();
        if (cancelled || !firebaseAuth) return;
        unsubscribe = onAuthStateChanged(firebaseAuth, (nextUser) => {
          if (cancelled) return;
          try {
            if (nextUser) window.localStorage.setItem(rememberedFirebaseSessionKey, "true");
            else window.localStorage.removeItem(rememberedFirebaseSessionKey);
          } catch {
            // Session restoration still works when local storage is unavailable.
          }
          setUser(loadLocalTestUser() || nextUser);
          setLoading(false);
        });
      } catch (error) {
        if (cancelled) return;
        setAuthError(firebaseErrorMessage(error));
        setLoading(false);
      }
    };

    if (authCriticalRoute || rememberedFirebaseSession) {
      startRuntime();
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const resetVerifier = () => {
    try {
      recaptchaVerifier.current?.clear?.();
    } catch {
      // Firebase can throw when the verifier was already removed by the SDK.
    }
    recaptchaVerifier.current = null;
    const container = document.getElementById("firebase-recaptcha-container");
    if (container) container.replaceChildren();
  };

  const getVerifier = async ({ visible = forceVisibleRecaptcha.current } = {}) => {
    await loadFirebaseRuntime();
    await firebaseRecaptchaConfigPromise;
    if (!firebaseAuth) throw new Error("Firebase phone authentication is not configured yet.");
    if (!document.getElementById("firebase-recaptcha-container")) throw new Error("Phone verification is still loading. Try again.");
    if (!recaptchaVerifier.current) {
      const nextMode = visible ? "visible" : "invisible";
      setCaptchaMode(nextMode);
      recaptchaVerifier.current = new RecaptchaVerifier(firebaseAuth, "firebase-recaptcha-container", {
        size: visible ? "normal" : "invisible",
        callback: () => {
          setAuthError("");
          setSmsStatus("sending");
        },
        "expired-callback": () => {
          resetVerifier();
          setSmsStatus("idle");
          setAuthError("The security check expired. Complete it again before requesting a new code.");
        }
      });
      await recaptchaVerifier.current.render();
    }
    return recaptchaVerifier.current;
  };

  const sendCode = async (rawPhoneNumber, countryCode = "AU") => {
    await loadPhoneNumberRuntime();
    const phoneCountry = getPhoneCountry(countryCode);
    const phoneNumber = normalisePhoneNumber(rawPhoneNumber, phoneCountry.code);
    const parsedPhoneNumber = parseBadmintaPhoneNumber(phoneNumber, phoneCountry.code);
    if (!phoneNumber || !parsedPhoneNumber?.isPossible()) {
      const message = `Enter a valid mobile number for ${phoneCountry.name} before requesting a code.`;
      setAuthError(message);
      throw new Error(message);
    }
    if (!isLocalTestPhone(phoneNumber, phoneCountry.code)) {
      const cooldownRemainingMs = smsRequestCooldownMs - (Date.now() - lastSmsRequestAt.current);
      if (cooldownRemainingMs > 0) {
        const cooldownRemainingSeconds = Math.ceil(cooldownRemainingMs / 1000);
        const message = `Wait ${cooldownRemainingSeconds} seconds before requesting another verification code.`;
        setAuthError(message);
        throw new Error(message);
      }
      // Record the attempt before the network call. A transient response can arrive after
      // Firebase has already sent an SMS, so immediately retrying can create a duplicate charge.
      lastSmsRequestAt.current = Date.now();
    }
    setSmsStatus(forceVisibleRecaptcha.current ? "checking" : "sending");
    setAuthError("");
    try {
      if (isLocalTestPhone(phoneNumber, phoneCountry.code)) {
        const result = { localTest: true, phoneNumber };
        setConfirmationResult(result);
        setSmsStatus("sent");
        return result;
      }
      if (isNativePhoneAuthAvailable()) {
        await loadFirebaseRuntime();
        const verificationId = await sendNativePhoneCode(phoneNumber);
        const result = { nativeVerificationId: verificationId, phoneNumber };
        setConfirmationResult(result);
        setSmsStatus("sent");
        return result;
      }
      const verifier = await getVerifier();
      // The Firebase SDK performs the Enterprise-to-v2 fallback in AUDIT mode.
      // Do not issue a second sendVerificationCode request from application code:
      // an ambiguous first response can otherwise create a duplicate SMS charge.
      const result = await signInWithPhoneNumber(firebaseAuth, phoneNumber, verifier);
      setConfirmationResult(result);
      setSmsStatus("sent");
      return result;
    } catch (error) {
      resetVerifier();
      if (isFirebaseCaptchaError(error) || isFirebaseTransientPhoneError(error)) {
        forceVisibleRecaptcha.current = true;
        setCaptchaMode("visible");
        try {
          await getVerifier({ visible: true });
        } catch {
          // The actionable Firebase error remains more useful than a second render error.
        }
      }
      const message = firebaseErrorMessage(error);
      setAuthError(message);
      setSmsStatus("idle");
      throw new Error(message);
    }
  };

  const verifyCode = async (code) => {
    if (!confirmationResult) {
      const message = "Request a verification code first.";
      setAuthError(message);
      throw new Error(message);
    }
    setSmsStatus("verifying");
    setAuthError("");
    try {
      if (confirmationResult.localTest) {
        if (String(code || "").trim() !== localTestSmsCode) throw new Error("That verification code is not correct. Use the local test code.");
        const localUser = {
          uid: `local-test-${localTestPhoneNumber}`,
          phoneNumber: localTestPhoneNumber,
          displayName: "Badminta test member",
          isLocalTestUser: true
        };
        saveLocalTestUser(localUser);
        setUser(localUser);
        setSmsStatus("verified");
        setConfirmationResult(null);
        return localUser;
      }
      if (confirmationResult.nativeVerificationId) {
        await loadFirebaseRuntime();
        const nativeCredentialInput = await confirmNativePhoneCode(
          confirmationResult.nativeVerificationId,
          String(code || "").trim(),
          confirmationResult.phoneNumber
        );
        const webCredential = PhoneAuthProvider.credential(
          nativeCredentialInput.verificationId,
          nativeCredentialInput.verificationCode
        );
        const credential = await signInWithCredential(firebaseAuth, webCredential);
        setUser(credential.user);
        setSmsStatus("verified");
        setConfirmationResult(null);
        return credential.user;
      }
      const credential = await confirmationResult.confirm(String(code || "").trim());
      setUser(credential.user);
      setSmsStatus("verified");
      setConfirmationResult(null);
      resetVerifier();
      return credential.user;
    } catch (error) {
      const message = firebaseCodeVerificationErrorMessage(error);
      setAuthError(message);
      setSmsStatus("sent");
      throw new Error(message);
    }
  };

  const updateDisplayName = async (displayName) => {
    if (user?.isLocalTestUser && displayName) {
      const nextUser = { ...user, displayName };
      saveLocalTestUser(nextUser);
      setUser(nextUser);
      return;
    }
    if (!firebaseAuth?.currentUser || !displayName) return;
    await updateProfile(firebaseAuth.currentUser, { displayName });
    setUser(firebaseAuth.currentUser);
  };

  const resetPhoneVerification = () => {
    setConfirmationResult(null);
    setSmsStatus("idle");
    setAuthError("");
    resetVerifier();
  };

  const logOut = async () => {
    if (user?.isLocalTestUser) {
      clearLocalTestUser();
      setUser(firebaseAuth?.currentUser || null);
      setSmsStatus("idle");
      setConfirmationResult(null);
      return;
    }
    if (!firebaseAuth) return;
    await signOut(firebaseAuth);
    setUser(null);
    setSmsStatus("idle");
    setConfirmationResult(null);
  };

  return {
    configured: firebaseConfigured || localTestAuthEnabled(),
    user,
    loading,
    authError,
    smsStatus,
    captchaMode,
    embeddedBrowser: isEmbeddedSocialBrowser(),
    sendCode,
    verifyCode,
    resetPhoneVerification,
    updateDisplayName,
    logOut
  };
}

const profileDraftNudgePages = new Set([
  ...marketplaceCategoryRailPages,
  "account",
  "filterHub",
  "seoArea",
  "venue",
  "group",
  "player",
  "coach",
  "event"
]);

function ProfileDraftNudge({ auth, accountProfile = defaultAccountProfile, page, navigate }) {
  if (!auth?.user || auth.loading || !profileDraftNudgePages.has(page)) return null;
  const draftRoles = accountRoleOptions
    .filter((role) => accountProfile.roles?.includes(role.id) && !roleHasPublishedBackendProfile(accountProfile, role.id))
    .map((role) => ({ ...role, missing: getPublicProfileMissingFields(accountProfile, role.id) }))
    .sort((first, second) => Number(first.missing.length > 0) - Number(second.missing.length > 0) || first.missing.length - second.missing.length);
  if (!draftRoles.length) return null;

  const role = draftRoles[0];
  const ready = role.missing.length === 0;
  const profileLabel = role.id === "organiser" ? "social group" : `${role.id} profile`;
  const remainingDrafts = draftRoles.length - 1;
  const Icon = role.icon || UserCircle;

  return (
    <aside className={`profile-draft-nudge ${ready ? "ready" : "needs-work"}`} aria-live="polite">
      <span className="profile-draft-nudge-icon"><Icon size={21} /></span>
      <div>
        <strong>{ready ? `Your ${profileLabel} is ready to publish` : `Finish your ${profileLabel}`}</strong>
        <p>{ready
          ? "Required details are complete. Publish now and Badminta will open your live public page."
          : `${missingFieldSummary(role.missing)} ${role.missing.length === 1 ? "is" : "are"} left. Optional photos, availability, links and media can be added after publishing.`}</p>
        {remainingDrafts > 0 && <small>{remainingDrafts} other selected user type{remainingDrafts === 1 ? "" : "s"} can be completed from your account.</small>}
      </div>
      <button type="button" onClick={() => navigate(publicSetupTarget(role.id))}>
        {ready ? "Publish now" : "Finish essentials"}
        <ChevronRight size={17} />
      </button>
    </aside>
  );
}

function App() {
  const initialRoute = useMemo(() => resolveRouteFromPath(), []);
  const auth = useFirebasePhoneAuth();
  const [page, setPage] = useState(initialRoute.page);
  const [mode, setMode] = useState(() => getRouteTopMode(initialRoute));
  const [city, setCity] = useState(initialRoute.city);
  const [venueDirectory, setVenueDirectory] = useState(() => venues);
  const [adminVenueDirectory, setAdminVenueDirectory] = useState(() => venues);
  const [adminDirectoryStatus, setAdminDirectoryStatus] = useState(() => ({
    state: "idle",
    loadedRows: venues.length,
    totalRows: globalDirectoryTotalCount,
    loadedCountries: 0,
    totalCountries: globalCentreCountryRows.length,
    message: "Administrator centre directory has not loaded the worldwide shards yet."
  }));
  const [selectedVenue, setSelectedVenue] = useState(initialRoute.page === "centreClaimSearch" ? null : initialRoute.venue || null);
  const [selectedGroup, setSelectedGroup] = useState(initialRoute.group);
  const [selectedGroupSession, setSelectedGroupSession] = useState(initialRoute.groupSession || null);
  const [selectedPlayer, setSelectedPlayer] = useState(initialRoute.player);
  const [selectedPlayerSlug, setSelectedPlayerSlug] = useState(initialRoute.playerSlug || "");
  const [selectedCoach, setSelectedCoach] = useState(initialRoute.coach);
  const [selectedEvent, setSelectedEvent] = useState(initialRoute.event);
  const [selectedEventSlug, setSelectedEventSlug] = useState(initialRoute.eventSlug || "");
  const [selectedBlogSlug, setSelectedBlogSlug] = useState(initialRoute.blogSlug || "");
  const [pendingVenueRoute, setPendingVenueRoute] = useState(initialRoute.pendingVenueRoute || null);
  const [centreRouteIssue, setCentreRouteIssue] = useState(null);
  const [messageFocus, setMessageFocus] = useState(null);
  const [reviewContext, setReviewContext] = useState(() => reviewContextFor(initialRoute.reviewKind || "general"));
  const [paymentContext, setPaymentContext] = useState(() => paymentContextFor(initialRoute.paymentKind || "general"));
  const [saved, setSaved] = useState(loadStoredSavedVenues);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [accountNotifications, setAccountNotifications] = useState([]);
  const [notificationSyncState, setNotificationSyncState] = useState({ status: "idle", message: "" });
  const [accountFollowing, setAccountFollowing] = useState([]);
  const [followingSyncState, setFollowingSyncState] = useState({ status: "idle", message: "" });
  const [activeFilter, setActiveFilter] = useState(initialRoute.activeFilter || pageFallbackFilter[initialRoute.page] || "nearby");
  const [seoAreaIntent, setSeoAreaIntent] = useState(initialRoute.seoAreaIntent || "overview");
  const [resultsMap, setResultsMap] = useState(true);
  const [bookingType, setBookingType] = useState("court");
  const [bookingDraft, setBookingDraft] = useState({});
  const [profileComplete, setProfileComplete] = useState(0);
  const [osmCentres, setOsmCentres] = useState([]);
  const [osmCentreStatus, setOsmCentreStatus] = useState("idle");
  const [osmCentreError, setOsmCentreError] = useState("");
  const [directoryLoadStatus, setDirectoryLoadStatus] = useState("idle");
  const [distanceOrigin, setDistanceOrigin] = useState(null);
  const [accountProfile, setAccountProfile] = useState(() => loadStoredAccountProfile(auth.user));
  const [accountMarketplaceData, setAccountMarketplaceData] = useState(defaultAccountMarketplaceData);
  const [marketplaceConversations, setMarketplaceConversations] = useState([]);
  const [marketplaceConversationSyncState, setMarketplaceConversationSyncState] = useState({ status: "idle", message: "" });
  const [accountSyncStatus, setAccountSyncStatus] = useState(() => ({
    state: isRealFirebaseUser(auth.user) ? "syncing" : "local",
    message: localProfileSyncMessage(auth.user)
  }));
  const [platformLedgerData, setPlatformLedgerData] = useState(defaultPlatformLedgerData);
  const [publicReviews, setPublicReviews] = useState([]);
  const [publicPlayerProfiles, setPublicPlayerProfiles] = useState(() => bootstrappedPublicPlayerProfiles
    .map((player) => normalisePublicPlayerDocument(player.id || player.firestoreSourceId || player.publicPlayerId, player))
    .filter((player) => player.name));
  const [publicGroupProfiles, setPublicGroupProfiles] = useState([]);
  const [publicCoachProfiles, setPublicCoachProfiles] = useState([]);
  const [publicEventProfiles, setPublicEventProfiles] = useState([]);
  const [publicProfileSyncStatus, setPublicProfileSyncStatus] = useState(() => bootstrappedPublicPlayerProfiles.length
    ? {
        state: "static",
        message: `${bootstrappedPublicPlayerProfiles.length.toLocaleString()} public player profiles loaded.`
      }
    : { state: "idle", message: "" });
  const [publicMarketplaceSyncStatus, setPublicMarketplaceSyncStatus] = useState({ state: "idle", message: "" });
  const [publicProfileRoute, setPublicProfileRoute] = useState(initialRoute.publicProfileRoute || null);
  const [resolvedPublicProfile, setResolvedPublicProfile] = useState(null);
  const [publicProfileRouteStatus, setPublicProfileRouteStatus] = useState(() => ({
    state: initialRoute.publicProfileRoute ? "loading" : "idle",
    message: initialRoute.publicProfileRoute ? "Loading the published profile." : ""
  }));
  const accountProfileRef = useRef(accountProfile);
  const accountProfileWriteRef = useRef(0);
  const accountProfileSaveQueueRef = useRef(Promise.resolve());
  const marketplaceConversationRequestRef = useRef(0);
  const publicProfileRouteRequestRef = useRef(new globalThis.Map());
  const autoNearbyStartedRef = useRef(false);
  const routeDirectoryLoadRef = useRef("");
  const adminDirectoryLoadRef = useRef("");
  const [searchCriteria, setSearchCriteria] = useState(() => ({
    location: initialRoute.location || getDefaultSearchLocation(),
    date: getIsoDate(0),
    time: "19:30",
    tags: ["courts", "socials", "players"]
  }));
  const [activeResultType, setActiveResultType] = useState(() => initialRoute.activeResultType || getPreferredHubResultType(initialRoute.activeFilter || pageFallbackFilter[initialRoute.page] || "nearby", ["courts", "socials", "players"]));
  const usingApiCentres = osmCentreStatus !== "idle";
  const accountRoleKey = [...(accountProfile.roles || [])].sort().join("|");
  const accountProfileView = useMemo(
    () => ({
      ...accountProfileWithMarketplaceData(accountProfile, accountMarketplaceData),
      marketplaceConversations,
      marketplaceConversationSyncState
    }),
    [accountProfile, accountMarketplaceData, marketplaceConversations, marketplaceConversationSyncState]
  );
  const marketplaceUnreadCount = useMemo(
    () => marketplaceConversations.reduce((total, conversation) => total + Math.max(0, Number(conversation.unread || 0)), 0),
    [marketplaceConversations]
  );
  const unreadNotificationCount = useMemo(
    () => accountNotifications.reduce((total, notification) => total + Number(!notification.read), 0),
    [accountNotifications]
  );
  const activeGroupSession = useMemo(() => {
    if (selectedGroupSession) return selectedGroupSession;
    if (page !== "editGroupSession") return null;
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const sessionId = pathSegments[0] === "organiser" && pathSegments[1] === "sessions" ? decodeURIComponent(pathSegments[2] || "") : "";
    return (Array.isArray(accountProfile.group?.sessions) ? accountProfile.group.sessions : [])
      .find((session) => String(session.sessionId || session.id) === String(sessionId)) || null;
  }, [selectedGroupSession, page, accountProfile.group?.sessions]);
  const publicProfileSubject = publicProfileRoute ? resolvedPublicProfile : accountProfile;
  const activePublicProfileRoute = useMemo(() => {
    if (!publicProfileRoute || !resolvedPublicProfile) return publicProfileRoute;
    const parts = getPublicProfileParts(resolvedPublicProfile, publicProfileRoute.role, city);
    return {
      ...publicProfileRoute,
      areaSlug: parts.area,
      typeSegment: parts.typeSegment,
      profileSlug: parts.slug,
      profileId: parts.profileId || publicProfileRoute.profileId
    };
  }, [publicProfileRoute, resolvedPublicProfile, city]);
  const publicPlayers = useMemo(() => mergePublicDirectoryProfiles(players, publicPlayerProfiles), [publicPlayerProfiles]);
  const publicGroups = useMemo(() => mergePublicDirectoryProfiles(groups, publicGroupProfiles), [publicGroupProfiles]);
  const publicCoaches = useMemo(() => mergePublicDirectoryProfiles(trainers, publicCoachProfiles), [publicCoachProfiles]);
  const accountEvents = useMemo(() => getAccountEvents(accountProfileView), [accountProfileView]);
  const publicEvents = useMemo(
    () => mergeMarketplaceLedgerRows(publicEventProfiles, getPublishedAccountEvents(accountProfileView))
      .filter((event) => isPublicUpcomingEvent(event)),
    [publicEventProfiles, accountProfileView]
  );
  const adminPlayerProfiles = useMemo(() => mergePublicDirectoryProfiles(
    publicPlayers,
    (platformLedgerData.players || []).map((player) => normalisePublicPlayerDocument(player.id, player))
  ), [publicPlayers, platformLedgerData.players]);
  const adminCoachProfiles = useMemo(() => (platformLedgerData.coaches || []).map((coach) => ({
    ...coach,
    name: coach.name || coach.fullName || coach.title || "Coach",
    specialty: coach.headline || coach.accreditation || "Badminton coach"
  })), [platformLedgerData.coaches]);
  const adminGroupProfiles = useMemo(() => platformLedgerData.groups || [], [platformLedgerData.groups]);
  const adminEventProfiles = useMemo(() => mergeMarketplaceLedgerRows(accountEvents, platformLedgerData.events || []), [accountEvents, platformLedgerData.events]);
  const adminCentreProfiles = useMemo(() => mergeMarketplaceLedgerRows(
    ...adminVenueDirectory,
    ...(platformLedgerData.centres || []).map((centre) => ({
      ...centre,
      firestoreSourceId: centre.firestoreSourceId || centre.id,
      serverCentreId: centre.serverCentreId || centre.id,
      source: centre.source || "owner"
    }))
  ), [adminVenueDirectory, platformLedgerData.centres]);
  const joinedEventSnapshots = useMemo(() => getJoinedEvents(accountProfileView), [accountProfileView]);
  const activeSelectedEvent = useMemo(() => {
    if (selectedEventSlug) {
      return accountEvents.find((event) => entitySlug(event, "title") === selectedEventSlug)
        || joinedEventSnapshots.find((event) => entitySlug(event, "title") === selectedEventSlug)
        || publicEvents.find((event) => entitySlug(event, "title") === selectedEventSlug)
        || selectedEvent;
    }
    return selectedEvent;
  }, [accountEvents, joinedEventSnapshots, publicEvents, selectedEvent, selectedEventSlug]);
  const activeBlogPost = useMemo(() => getBlogPost(selectedBlogSlug), [selectedBlogSlug]);

  useEffect(() => {
    accountProfileRef.current = accountProfile;
  }, [accountProfile]);

  const refreshMarketplaceConversations = async ({ quiet = false } = {}) => {
    if (!isRealFirebaseUser(auth.user)) {
      marketplaceConversationRequestRef.current += 1;
      setMarketplaceConversations([]);
      setMarketplaceConversationSyncState({ status: "idle", message: "" });
      return [];
    }
    const requestId = marketplaceConversationRequestRef.current + 1;
    marketplaceConversationRequestRef.current = requestId;
    if (!quiet) {
      setMarketplaceConversationSyncState({ status: "loading", message: "Syncing conversations across your Badminta roles." });
    }
    try {
      const result = await callMarketplaceFunction("listMarketplaceConversations", {});
      const conversations = Array.isArray(result.conversations) ? result.conversations : [];
      if (marketplaceConversationRequestRef.current !== requestId) return conversations;
      setMarketplaceConversations(conversations);
      setMarketplaceConversationSyncState({
        status: "ready",
        message: `${conversations.length} synced conversation${conversations.length === 1 ? "" : "s"}.`
      });
      return conversations;
    } catch (error) {
      if (marketplaceConversationRequestRef.current === requestId) {
        setMarketplaceConversationSyncState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
      }
      return [];
    }
  };

  useEffect(() => {
    if (auth.loading) return undefined;
    if (!isRealFirebaseUser(auth.user)) {
      marketplaceConversationRequestRef.current += 1;
      setMarketplaceConversations([]);
      setMarketplaceConversationSyncState({ status: "idle", message: "" });
      return undefined;
    }
    refreshMarketplaceConversations();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshMarketplaceConversations({ quiet: true });
    };
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") refreshMarketplaceConversations({ quiet: true });
    }, 60000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser]);

  useEffect(() => {
    if (auth.loading) return undefined;
    if (!isRealFirebaseUser(auth.user) || !firebaseDb) {
      setAccountNotifications([]);
      setNotificationSyncState({ status: "idle", message: "" });
      setNotificationPanelOpen(false);
      return undefined;
    }
    ensureFirebaseAppCheck();
    setNotificationSyncState({ status: "loading", message: "Loading notifications." });
    const notificationQuery = query(
      collection(firebaseDb, "notificationQueue"),
      where("recipientUid", "==", auth.user.uid)
    );
    return onSnapshot(notificationQuery, (snapshot) => {
      const notifications = snapshot.docs
        .map(normaliseAccountNotification)
        .sort((first, second) => String(second.createdAt || second.updatedAt || "").localeCompare(String(first.createdAt || first.updatedAt || "")));
      setAccountNotifications(notifications);
      setNotificationSyncState({
        status: "ready",
        message: `${notifications.length} notification${notifications.length === 1 ? "" : "s"} synced.`
      });
    }, (error) => {
      setNotificationSyncState({
        status: "error",
        message: firebaseErrorMessage(error) || "Notifications could not refresh."
      });
    });
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser]);

  useEffect(() => {
    if (auth.loading) return undefined;
    if (!isRealFirebaseUser(auth.user) || !firebaseDb) {
      setAccountFollowing([]);
      setFollowingSyncState({ status: "idle", message: "" });
      return undefined;
    }

    setFollowingSyncState({ status: "loading", message: "Loading the profiles you follow." });
    return onSnapshot(collection(firebaseDb, "users", auth.user.uid, "following"), (snapshot) => {
      const rows = snapshot.docs
        .map((item) => normaliseFollowingRecord({ id: item.id, ...fromFirestoreProfileValue(item.data()) }))
        .filter((item) => item.targetCollection && item.targetId && item.publicPath)
        .sort((first, second) => String(second.followedAt || second.updatedAt || "").localeCompare(String(first.followedAt || first.updatedAt || "")));
      setAccountFollowing(rows);
      setFollowingSyncState({
        status: "ready",
        message: `${rows.length} followed profile${rows.length === 1 ? "" : "s"} synced.`
      });
    }, (error) => {
      setFollowingSyncState({
        status: "error",
        message: firebaseErrorMessage(error) || "Following could not refresh."
      });
    });
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser]);

  const persistAccountProfile = async (next, user = auth.user, patch = {}) => {
    saveStoredAccountProfile(next, user);

    if (!isRealFirebaseUser(user)) {
      setAccountSyncStatus({
        state: "local",
        message: localProfileSyncMessage(user)
      });
      return { saved: true, storage: "local" };
    }

    const writeId = accountProfileWriteRef.current + 1;
    accountProfileWriteRef.current = writeId;
    setAccountSyncStatus({ state: "syncing", message: "Saving your account securely." });

    const corePatch = stripAccountActivityFields(patch);
    const activityChanged = accountActivityFieldNames.some((field) => Object.hasOwn(patch || {}, field));
    try {
      const result = Object.keys(corePatch).length
        ? await callMarketplaceFunction("saveBadmintaAccountProfile", { patch: corePatch })
        : { saved: true };
      let activityError = null;
      if (activityChanged) {
        try {
          await syncAccountActivityCollections(next, user);
        } catch (error) {
          activityError = error;
        }
      }
      if (result.profileRevision || result.playerPublication?.public === true) {
        const publicationPatch = result.playerPublication?.public === true ? {
          publicPlayerProfileId: result.playerPublication.profileId,
          publicPlayerProfileStatus: "published",
          player: {
            publicPlayerId: result.playerPublication.profileId,
            firestoreSourceId: result.playerPublication.profileId,
            backendStatus: "published",
            publicProfilePublished: true,
            publishMissing: []
          }
        } : {};
        const revised = deepMergeAccountProfile(accountProfileRef.current, {
          ...publicationPatch,
          ...(result.profileRevision ? { profileRevision: result.profileRevision } : {})
        });
        accountProfileRef.current = revised;
        setAccountProfile(revised);
        saveStoredAccountProfile(revised, user);
      }
      if (accountProfileWriteRef.current === writeId) {
        setAccountSyncStatus({
          state: activityError ? "error" : "cloud",
          message: activityError
            ? "Your profile is saved, but one activity list could not refresh yet."
            : "Your account profile is saved to Badminta."
        });
      }
      return {
        saved: true,
        storage: "cloud",
        activitySaved: !activityError,
        profileRevision: result.profileRevision || 0,
        playerPublication: result.playerPublication || null
      };
    } catch (error) {
      const message = marketplaceFunctionErrorMessage(error) || firebaseErrorMessage(error) || "Badminta could not save your account profile.";
      setAccountSyncStatus({ state: "error", message });
      return { saved: false, storage: "local", error: message };
    }
  };

  const saveAccountProfile = (patch) => {
    const stampedPatch = {
      ...patch,
      ...(Array.isArray(patch.roles) ? { roles: Array.from(new Set(["player", ...patch.roles])) } : {}),
      clientUpdatedAt: new Date().toISOString()
    };
    const next = deepMergeAccountProfile(accountProfileRef.current, stampedPatch);
    accountProfileRef.current = next;
    setAccountProfile(next);
    const user = auth.user;
    const queuedSave = accountProfileSaveQueueRef.current
      .catch(() => null)
      .then(() => persistAccountProfile(next, user, stampedPatch));
    accountProfileSaveQueueRef.current = queuedSave;
    return queuedSave;
  };

  useEffect(() => {
    if (auth.loading) return undefined;

    const user = auth.user;
    const cached = loadStoredAccountProfile(user);
    accountProfileRef.current = cached;
    setAccountProfile(cached);

    if (!isRealFirebaseUser(user)) {
      setAccountSyncStatus({
        state: "local",
        message: localProfileSyncMessage(user)
      });
      return undefined;
    }

    const profileRef = accountProfileDocRef(user);
    setAccountSyncStatus({ state: "syncing", message: "Loading cloud account profile." });
    ensureFirebaseAppCheck();

    let coreProfile = stripAccountActivityFields(cached);
    let activityProfile = pickAccountActivityFields(defaultAccountProfile);
    let bootstrapProfileLoaded = false;
    let firestoreProfileLoaded = false;
    let bootstrapStarted = false;
    let bootstrapAttempts = 0;
    let bootstrapRetryTimer = 0;
    let cancelled = false;
    const unsubscribers = [];
    const bootstrapSessionKey = `badminta-account-bootstrap:${user.uid}`;
    const bootstrapAlreadyCompleted = (() => {
      try {
        return window.sessionStorage.getItem(bootstrapSessionKey) === "complete";
      } catch {
        return false;
      }
    })();
    const rememberBootstrapCompletion = () => {
      try {
        window.sessionStorage.setItem(bootstrapSessionKey, "complete");
      } catch {
        // Session storage is optional; Firestore remains the source of truth.
      }
    };
    const applyRemoteProfile = (message = "Account profile and activity streams synced to Firestore.") => {
      const remoteProfile = mergeAccountActivityFields(
        deepMergeAccountProfile(defaultAccountProfile, fromFirestoreProfileValue(coreProfile)),
        activityProfile
      );
      if (cancelled) return;
      saveStoredAccountProfile(remoteProfile, user);
      accountProfileRef.current = remoteProfile;
      setAccountProfile(remoteProfile);
      setAccountSyncStatus({ state: "cloud", message });
    };

    const bootstrapAccount = () => {
      if (bootstrapStarted) return;
      bootstrapStarted = true;
      bootstrapAttempts += 1;
      callMarketplaceFunction("bootstrapBadmintaAccount", {
        attribution: readStoredMarketingAttribution()
      }).then((result) => {
        if (cancelled || !result.profile) return;
        rememberBootstrapCompletion();
        bootstrapProfileLoaded = true;
        coreProfile = stripAccountActivityFields(fromFirestoreProfileValue(result.profile));
        applyRemoteProfile(result.recoveredRoles?.length
          ? `Recovered and saved ${result.recoveredRoles.map(roleTitle).join(", ")} profile details.`
          : "Your Badminta account is loaded securely.");
      }).catch((error) => {
        if (cancelled) return;
        bootstrapStarted = false;
        const bootstrapRateLimited = /resource-exhausted/i.test(String(error?.code || "")) || /too many account bootstrap|rate-limit/i.test(String(error?.message || ""));
        if (bootstrapRateLimited) rememberBootstrapCompletion();
        if (firestoreProfileLoaded) {
          setAccountSyncStatus({
            state: "cloud",
            message: bootstrapRateLimited
              ? "Your cloud profile is loaded. Background account recovery will resume automatically shortly."
              : "Your cloud profile is loaded. Background recovery can retry later if needed."
          });
          return;
        }
        setAccountSyncStatus({
          state: "error",
          message: marketplaceFunctionErrorMessage(error) || "Badminta could not load this account. Refresh and try again."
        });
        if (!bootstrapRateLimited && bootstrapAttempts < 2) bootstrapRetryTimer = window.setTimeout(bootstrapAccount, 900);
      });
    };

    if (!bootstrapAlreadyCompleted) bootstrapAccount();

    unsubscribers.push(onSnapshot(profileRef, (snapshot) => {
      if (!snapshot.exists()) {
        setAccountSyncStatus({ state: "syncing", message: "Creating your secure Badminta account." });
        bootstrapAccount();
        return;
      }

      firestoreProfileLoaded = true;
      coreProfile = stripAccountActivityFields(fromFirestoreProfileValue(snapshot.data()));
      applyRemoteProfile();
    }, (error) => {
      if (!bootstrapProfileLoaded) {
        accountProfileRef.current = cached;
        setAccountProfile(cached);
      }
      setAccountSyncStatus({
        state: bootstrapProfileLoaded ? "cloud" : "error",
        message: bootstrapProfileLoaded
          ? "Your account is loaded securely. Live activity refresh is temporarily limited in this browser."
          : firebaseErrorMessage(error) || "Using the last saved profile because live activity sync failed."
      });
    }));

    accountActivityFieldNames.forEach((field) => {
      unsubscribers.push(onSnapshot(accountActivityCollectionRef(user, field), (snapshot) => {
        activityProfile = {
          ...activityProfile,
          [field]: profileFromActivitySnapshot(field, snapshot)
        };
        applyRemoteProfile();
      }, (error) => {
        setAccountSyncStatus({
          state: firestoreProfileLoaded || bootstrapProfileLoaded ? "cloud" : "error",
          message: firestoreProfileLoaded || bootstrapProfileLoaded
            ? "Your profile is loaded securely. Some live activity will refresh again automatically."
            : firebaseErrorMessage(error) || `Could not sync ${field} activity from Firestore.`
        });
      }));
    });

    return () => {
      cancelled = true;
      window.clearTimeout(bootstrapRetryTimer);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser]);

  useEffect(() => {
    if (auth.loading) return undefined;
    if (!isRealFirebaseUser(auth.user) || !firebaseDb) {
      setAccountMarketplaceData(defaultAccountMarketplaceData);
      return undefined;
    }
    ensureFirebaseAppCheck();

    const uid = auth.user.uid;
    const roles = new Set((accountRoleKey || "").split("|").filter(Boolean));
    const ownsMarketplaceContent = roles.has("centre") || roles.has("organiser") || roles.has("coach");
    const specs = [
      ["player-bookings", "bookings", "playerUid", "bookings"],
      ["player-payments", "payments", "payerUid", "payments"],
      ["player-joins", "sessionJoinRequests", "playerUid", "joinedSessions"],
      ["player-memberships", "membershipPurchases", "payerUid", "membershipPurchases"],
      ["player-membership-redemptions", "membershipRedemptions", "payerUid", "membershipRedemptions"],
      ["player-lessons", "lessonRequests", "playerUid", "lessonRequests"],
      ["player-coach-bookings", "coachBookings", "playerUid", "coachBookings"],
      ...(roles.has("centre") ? [
        ["centre-bookings", "bookings", "centreOwnerUid", "managedBookings"],
        ["centre-memberships", "membershipPurchases", "ownerUid", "managedMembershipPurchases"],
        ["centre-membership-redemptions", "membershipRedemptions", "ownerUid", "managedMembershipRedemptions"]
      ] : []),
      ...(roles.has("organiser") ? [
        ["organiser-memberships", "membershipPurchases", "ownerUid", "managedMembershipPurchases"]
      ] : []),
      ...(roles.has("organiser") || roles.has("centre") ? [
        ["owner-joins", "sessionJoinRequests", "ownerUid", "managedJoinRequests"],
        ["owner-events", "events", "ownerUid", "events"]
      ] : []),
      ...(roles.has("coach") ? [
        ["coach-lessons", "lessonRequests", "coachUid", "managedLessonRequests"],
        ["coach-bookings", "coachBookings", "coachUid", "managedCoachBookings"]
      ] : []),
      ...(ownsMarketplaceContent ? [
        ["owner-payments", "payments", "ownerUid", "managedPayments"]
      ] : [])
    ];
    const buckets = {};
    const settled = new Set();
    const errors = new globalThis.Map();

    const publishMarketplaceState = () => {
      const rowsByTarget = {};
      specs.forEach(([key, , , target]) => {
        rowsByTarget[target] = mergeMarketplaceLedgerRows(rowsByTarget[target] || [], buckets[key] || []);
      });
      const loaded = settled.size;
      const total = specs.length;
      setAccountMarketplaceData({
        ...defaultAccountMarketplaceData,
        ...rowsByTarget,
        status: errors.size ? "error" : loaded === total ? "cloud" : "syncing",
        message: errors.size
          ? `${loaded - errors.size} of ${total} marketplace activity streams synced. ${errors.size} need attention.`
          : loaded === total
            ? "Bookings, joins, payments, memberships and coaching activity are synced from Firestore."
            : `Syncing marketplace activity ${loaded} of ${total}.`
      });
    };

    setAccountMarketplaceData({
      ...defaultAccountMarketplaceData,
      status: "syncing",
      message: `Syncing ${specs.length} marketplace activity streams.`
    });

    const unsubscribers = specs.map(([key, collectionName, participantField]) => onSnapshot(
      query(collection(firebaseDb, collectionName), where(participantField, "==", uid), limit(100)),
      (snapshot) => {
        buckets[key] = platformLedgerRowsFromSnapshot(collectionName, snapshot);
        settled.add(key);
        errors.delete(key);
        publishMarketplaceState();
      },
      (error) => {
        buckets[key] = [];
        settled.add(key);
        errors.set(key, firebaseErrorMessage(error) || `Could not sync ${collectionName}.`);
        publishMarketplaceState();
      }
    ));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser, accountRoleKey]);

  useEffect(() => {
    if (auth.loading) return undefined;
    if (!isRealFirebaseUser(auth.user) || !hasSuperAdminAllowlistAccess(auth.user)) {
      setPlatformLedgerData(defaultPlatformLedgerData);
      return undefined;
    }
    ensureFirebaseAppCheck();

    setPlatformLedgerData((current) => ({
      ...current,
      status: "syncing",
      message: "Loading platform ledgers."
    }));

    let cancelled = false;
    Promise.all(platformLedgerCollections.map(async (collectionName) => {
      try {
        const snapshot = await getDocs(platformLedgerCollectionRef(collectionName));
        return [collectionName, platformLedgerRowsFromSnapshot(collectionName, snapshot), ""];
      } catch (error) {
        return [collectionName, [], firebaseErrorMessage(error) || `Could not load ${collectionName}.`];
      }
    })).then((results) => {
      if (cancelled) return;
      const next = { ...defaultPlatformLedgerData };
      const errors = [];
      results.forEach(([collectionName, rows, error]) => {
        next[collectionName] = rows;
        if (error) errors.push(error);
      });
      next.status = errors.length ? "error" : "cloud";
      next.message = errors.length
        ? `${platformLedgerCollections.length - errors.length} of ${platformLedgerCollections.length} admin ledgers loaded.`
        : `Admin snapshot loaded across ${platformLedgerCollections.length} ledgers.`;
      setPlatformLedgerData(next);
    });

    return () => {
      cancelled = true;
    };
  }, [auth.loading, auth.user?.uid, auth.user?.isLocalTestUser]);

  useEffect(() => {
    const publicProfilePages = new Set(["home", "filterHub", "playersIndex", "city", "seoArea", "player", "publicPlayerProfile", "ratings", "members"]);
    if (!publicProfilePages.has(page)) {
      setPublicPlayerProfiles([]);
      setPublicProfileSyncStatus({ state: "idle", message: "" });
      return undefined;
    }

    let cancelled = false;
    let startTimer = 0;
    let idleCallback = 0;
    let staticRows = [];
    setPublicProfileSyncStatus({ state: "syncing", message: "Loading public player profiles." });

    const applyStaticPublicPlayers = (messagePrefix = "Public player profiles loaded from the published directory.") => loadStaticPublicPlayerProfiles()
      .then((rows) => {
        if (cancelled) return [];
        staticRows = rows;
        setPublicPlayerProfiles(rows);
        setPublicProfileSyncStatus({
          state: rows.length ? "static" : "cloud",
          message: rows.length ? `${messagePrefix} ${rows.length} profile${rows.length === 1 ? "" : "s"} available.` : "No public player profiles are published yet."
        });
        return rows;
      });

    const applyBackendPublicPlayers = () => callProfileFunctionEndpoint("listPublicPlayerProfiles", { limit: 1000 })
      .then((result) => {
        if (cancelled) return [];
        const rows = (result.players || [])
          .map((player) => normalisePublicPlayerDocument(player.id, player))
          .filter((player) => player.name);
        const publishedRows = mergePublicDirectoryProfiles(staticRows, rows);
        setPublicPlayerProfiles(publishedRows);
        setPublicProfileSyncStatus({
          state: rows.length ? "cloud" : staticRows.length ? "static" : "cloud",
          message: publishedRows.length ? `${publishedRows.length} public player profile${publishedRows.length === 1 ? "" : "s"} loaded.` : "No public player profiles are published yet."
        });
        return publishedRows;
      });

    const refreshFromBackend = () => applyBackendPublicPlayers()
      .catch(() => {
        if (cancelled || staticRows.length) return staticRows;
        return applyStaticPublicPlayers("Live profile refresh is unavailable, so Badminta is using the published public directory.");
      })
      .catch((error) => {
        if (cancelled) return [];
        setPublicPlayerProfiles([]);
        setPublicProfileSyncStatus({
          state: "error",
          message: firebaseErrorMessage(error) || "Could not load public player profiles."
        });
        return [];
      });

    const staticLoad = applyStaticPublicPlayers().catch(() => []);
    const refreshPublishedDirectory = () => staticLoad.then(() => (
      firebaseConfigured ? refreshFromBackend() : staticRows
    ));
    if (page === "home" && typeof window.requestIdleCallback === "function") {
      idleCallback = window.requestIdleCallback(refreshPublishedDirectory, { timeout: 5000 });
    } else {
      startTimer = window.setTimeout(refreshPublishedDirectory, page === "home" ? 4000 : 0);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (idleCallback && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleCallback);
    };
  }, [page]);

  useEffect(() => {
    const publicMarketplacePages = new Set([
      "home", "filterHub", "groupsIndex", "playersIndex", "events", "event", "city", "seoArea",
      "group", "coach", "venue", "publicGroupProfile", "publicCoachProfile", "publicPlayerProfile"
    ]);
    if (!publicMarketplacePages.has(page)) {
      setPublicGroupProfiles([]);
      setPublicCoachProfiles([]);
      setPublicEventProfiles([]);
      setPublicMarketplaceSyncStatus({ state: "idle", message: "" });
      return undefined;
    }

    let cancelled = false;
    let startTimer = 0;
    let idleCallback = 0;
    let staticRows = { groups: [], coaches: [], events: [] };
    setPublicMarketplaceSyncStatus({ state: "syncing", message: "Loading published groups, coaches and events." });

    const normaliseMarketplaceRows = (result = {}) => ({
      groups: (result.groups || [])
        .map((group) => normalisePublicGroupDocument(group.id, group))
        .filter((group) => group.name && (group.city || group.suburb || group.venue)),
      coaches: (result.coaches || [])
        .map((coach) => normalisePublicCoachDocument(coach.id, coach))
        .filter((coach) => coach.name && (coach.city || coach.suburb || coach.locations)),
      events: (result.events || [])
        .map((event, index) => normaliseEvent(event, index))
        .filter((event) => event.title && (event.city || event.suburb || event.venue))
        .filter((event) => isPublicUpcomingEvent(event))
    });

    const applyMarketplaceRows = (rows, state = "static") => {
      if (cancelled) return rows;
      setPublicGroupProfiles(rows.groups);
      setPublicCoachProfiles(rows.coaches);
      setPublicEventProfiles(rows.events);
      setPublicMarketplaceSyncStatus({
        state,
        message: `${rows.groups.length} groups, ${rows.coaches.length} coaches and ${rows.events.length} events loaded.`
      });
      return rows;
    };

    const applyStaticMarketplace = () => loadStaticPublicMarketplaceProfiles()
      .then((result) => {
        staticRows = normaliseMarketplaceRows(result);
        return applyMarketplaceRows(staticRows, "static");
      });

    const loadPublishedMarketplace = () => callProfileFunctionEndpoint("listPublicMarketplaceProfiles", { limit: 300 })
      .then((result) => {
        const cloudRows = normaliseMarketplaceRows(result);
        const nextRows = {
          groups: cloudRows.groups.length ? cloudRows.groups : staticRows.groups,
          coaches: cloudRows.coaches.length ? cloudRows.coaches : staticRows.coaches,
          events: cloudRows.events.length ? cloudRows.events : staticRows.events
        };
        return applyMarketplaceRows(nextRows, cloudRows.groups.length || cloudRows.coaches.length || cloudRows.events.length ? "cloud" : "static");
      })
      .catch((error) => {
        if (cancelled) return;
        if (staticRows.groups.length || staticRows.coaches.length || staticRows.events.length) {
          applyMarketplaceRows(staticRows, "static");
          return;
        }
        setPublicMarketplaceSyncStatus({
          state: "error",
          message: marketplaceFunctionErrorMessage(error) || "Published community profiles could not be loaded."
        });
      });

    const staticLoad = applyStaticMarketplace().catch(() => staticRows);
    const refreshPublishedMarketplace = () => staticLoad.then(loadPublishedMarketplace);

    if (page === "home" && typeof window.requestIdleCallback === "function") {
      idleCallback = window.requestIdleCallback(refreshPublishedMarketplace, { timeout: 3000 });
    } else {
      startTimer = window.setTimeout(refreshPublishedMarketplace, page === "home" ? 2000 : 0);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (idleCallback && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleCallback);
    };
  }, [page]);

  useEffect(() => {
    if (page !== "player") return;
    const matchedPlayer = selectedPlayer || (selectedPlayerSlug
      ? publicPlayers.find((player) => entitySlug(player) === selectedPlayerSlug || slugifyPath(player.name || "") === selectedPlayerSlug)
      : null);
    if (!matchedPlayer) return;
    const publicPayload = publicProfileNavigationPayload("player", matchedPlayer);
    if (!publicPayload) {
      if (!selectedPlayer) setSelectedPlayer(matchedPlayer);
      return;
    }
    setSelectedPlayer(matchedPlayer);
    setResolvedPublicProfile(publicPayload.accountProfile);
    setPublicProfileRoute(publicPayload.publicProfileRoute);
    setPublicProfileRouteStatus({ state: "loading", message: "Loading the canonical published player profile." });
    setPage("publicPlayerProfile");
  }, [page, selectedPlayer, selectedPlayerSlug, publicPlayers]);

  useEffect(() => {
    if (!publicProfileRoute) {
      setResolvedPublicProfile(null);
      setPublicProfileRouteStatus({ state: "idle", message: "" });
      return undefined;
    }

    let cancelled = false;
    let hasUsableProfile = false;
    const recordId = (record = {}) => String(
      record.firestoreSourceId
      || record.publicPlayerId
      || record.publicCoachId
      || record.publicGroupId
      || record.serverGroupId
      || record.id
      || ""
    ).trim();
    const routeRecords = publicProfileRoute.role === "player"
      ? publicPlayers
      : publicProfileRoute.role === "group"
        ? publicGroups
        : publicProfileRoute.role === "coach"
          ? publicCoaches
          : [];
    const routeMatchesRecord = (record) => {
      const id = recordId(record);
      if (publicProfileRoute.profileId) return id === publicProfileRoute.profileId;
      if (!id) return false;
      const profile = publicAccountProfileFromRecord(publicProfileRoute.role, id, record);
      const parts = getPublicProfileParts(profile, publicProfileRoute.role, city);
      return parts.area === publicProfileRoute.areaSlug
        && parts.typeSegment === publicProfileRoute.typeSegment
        && (parts.slug === publicProfileRoute.profileSlug || publicProfileSlugBase(parts.slug) === publicProfileSlugBase(publicProfileRoute.profileSlug));
    };
    const applyResolvedRouteProfile = (resolved, profileId) => {
      if (cancelled || !resolved) return;
      hasUsableProfile = true;
      setResolvedPublicProfile(resolved);
      const roleProfile = publicProfileRoute.role === "group" ? resolved.group : resolved[publicProfileRoute.role];
      const resolvedCity = roleProfile?.city || roleProfile?.location?.city || city;
      const resolvedSuburb = roleProfile?.suburb || roleProfile?.location?.suburb || "";
      if (resolvedCity) setCity(resolvedCity);
      if (publicProfileRoute.role === "group") {
        const groupProfile = normaliseJoinedGroup(roleProfile);
        setSelectedGroup(groupProfile);
        const skillText = normaliseSearchText(groupProfile.skillBand || groupProfile.band);
        setActiveFilter(skillText.includes("advanced") ? "advanced" : skillText.includes("beginner") ? "beginner" : "nearby");
        setMode("socials");
      }
      if (publicProfileRoute.role === "coach") {
        setSelectedCoach(normalisePublicCoachDocument(profileId, roleProfile));
        setActiveFilter("coaching");
        setMode("players");
      }
      if (publicProfileRoute.role === "player") {
        const playerProfile = normalisePublicPlayerDocument(profileId, roleProfile);
        setSelectedPlayer(playerProfile);
        const skillText = normaliseSearchText(playerProfile.skillBand || playerProfile.band);
        setActiveFilter(skillText.includes("advanced") ? "advanced" : skillText.includes("beginner") ? "beginner" : "nearby");
        setMode("players");
      }
      if (publicProfileRoute.role === "centre") {
        setSelectedVenue(managedVenueFromCentreProfile(roleProfile));
        setActiveFilter("nearby");
        setMode("centres");
      }
      const canonicalParts = getPublicProfileParts(resolved, publicProfileRoute.role, resolvedCity || city);
      const canonicalRoute = {
        ...publicProfileRoute,
        areaSlug: canonicalParts.area,
        typeSegment: canonicalParts.typeSegment,
        profileSlug: canonicalParts.slug,
        profileId: canonicalParts.profileId || profileId
      };
      const canonicalPath = publicProfileRoutePath(canonicalRoute);
      const currentPath = window.location.pathname;
      if (canonicalPath && currentPath !== canonicalPath) {
        window.history.replaceState({ page: publicProfilePageByRole[publicProfileRoute.role] }, "", `${canonicalPath}${window.location.search}`);
      }
      if (resolvedCity || resolvedSuburb) {
        setSearchCriteria((current) => ({
          ...current,
          location: {
            ...(roleProfile?.location || {}),
            id: roleProfile?.location?.id || `public-${publicProfileRoute.role}-${profileId}`,
            label: resolvedSuburb || resolvedCity,
            suburb: resolvedSuburb,
            city: resolvedCity || current.location?.city || city,
            country: roleProfile?.country || roleProfile?.location?.country || "",
            countryCode: roleProfile?.countryCode || roleProfile?.location?.countryCode || "",
            type: resolvedSuburb ? "suburb" : "city"
          }
        }));
      }
      setPublicProfileRouteStatus({ state: "ready", message: "Published profile loaded." });
    };

    const existingParts = resolvedPublicProfile
      ? getPublicProfileParts(resolvedPublicProfile, publicProfileRoute.role, city)
      : null;
    const existingMatches = existingParts && (
      publicProfileRoute.profileId
        ? existingParts.profileId === publicProfileRoute.profileId
        : existingParts.area === publicProfileRoute.areaSlug
          && publicProfileSlugBase(existingParts.slug) === publicProfileSlugBase(publicProfileRoute.profileSlug)
    );
    if (existingMatches) {
      hasUsableProfile = true;
      setPublicProfileRouteStatus({ state: "ready", message: "Published profile loaded." });
    } else {
      const seedRecord = routeRecords.find(routeMatchesRecord);
      if (seedRecord) {
        const seedId = recordId(seedRecord);
        applyResolvedRouteProfile(publicAccountProfileFromRecord(publicProfileRoute.role, seedId, seedRecord), seedId);
      } else {
        setPublicProfileRouteStatus({
          state: "loading",
          message: `Loading this published ${publicProfileRoute.role === "group" ? "social group" : publicProfileRoute.role} profile.`
        });
      }
    }

    const requestPayload = {
      role: publicProfileRoute.role,
      areaSlug: publicProfileRoute.areaSlug,
      typeSegment: publicProfileRoute.typeSegment,
      profileSlug: publicProfileRoute.profileSlug,
      profileId: publicProfileRoute.profileId
    };
    const routeViewerKey = auth.user?.uid || "public";
    const requestKey = [requestPayload.role, requestPayload.areaSlug, requestPayload.typeSegment, requestPayload.profileSlug, requestPayload.profileId, routeViewerKey].join("|");
    let routeRequest = publicProfileRouteRequestRef.current.get(requestKey);
    if (!routeRequest) {
      routeRequest = (firebaseAuth?.currentUser
        ? callAuthenticatedProfileFunction("resolvePublicProfileRoute", requestPayload)
        : callProfileFunctionEndpoint("resolvePublicProfileRoute", requestPayload))
        .catch((error) => {
          publicProfileRouteRequestRef.current.delete(requestKey);
          throw error;
        });
      publicProfileRouteRequestRef.current.set(requestKey, routeRequest);
    }

    routeRequest.then((result) => {
      if (cancelled) return;
      if (!result?.profile || !result?.profileId) throw new Error("This published profile could not be found.");
      const resolved = publicAccountProfileFromRecord(publicProfileRoute.role, result.profileId, result.profile);
      applyResolvedRouteProfile(resolved, result.profileId);
    }).catch((error) => {
      if (cancelled) return;
      if (hasUsableProfile) return;
      setResolvedPublicProfile(null);
      setPublicProfileRouteStatus({
        state: "error",
        message: marketplaceFunctionErrorMessage(error) || "This profile is not currently public."
      });
    });

    return () => {
      cancelled = true;
    };
  }, [publicProfileRoute?.role, publicProfileRoute?.areaSlug, publicProfileRoute?.typeSegment, publicProfileRoute?.profileSlug, publicProfileRoute?.profileId, publicPlayers, publicGroups, publicCoaches, auth.loading, auth.user?.uid]);

  useEffect(() => {
    if (!firebaseDb || !["event", "player", "coach", "group", "venue", "publicPlayerProfile", "publicCoachProfile", "publicGroupProfile", "publicCentreProfile"].includes(page)) {
      setPublicReviews([]);
      return undefined;
    }
    ensureFirebaseAppCheck();
    const publicReviewQuery = query(collection(firebaseDb, "reviews"), where("public", "==", true), limit(300));
    return onSnapshot(publicReviewQuery, (snapshot) => {
      const rows = platformLedgerRowsFromSnapshot("reviews", snapshot)
        .filter(isApprovedPublicReviewRecord)
        .sort((first, second) => String(second.createdAt || second.updatedAt || "").localeCompare(String(first.createdAt || first.updatedAt || "")));
      setPublicReviews(rows);
    }, () => {
      setPublicReviews([]);
    });
  }, [page, auth.loading]);

  const savedItems = useMemo(() => getSavedItems(accountProfile), [accountProfile]);

  const saveAccountItem = (item) => {
    const savedItem = normaliseSavedItem({ ...item, savedAt: new Date().toISOString() });
    const nextSavedItems = [savedItem, ...getSavedItems(accountProfile).filter((current) => current.key !== savedItem.key)];
    saveAccountProfile({ savedItems: nextSavedItems });
  };

  const cityVenues = useMemo(() => {
    if (osmCentreStatus === "loading" && !venueDirectory.length && !osmCentres.length) return [];
    const locationOrigin = searchCriteria.location?.type === "nearby"
      ? distanceOrigin
      : searchCriteria.location;
    if (usingApiCentres) {
      const apiCentres = locationOrigin ? osmCentres.map((venue) => applyDistanceFromOrigin(venue, locationOrigin)) : osmCentres;
      const localNearbyCentres = locationOrigin
        ? venueDirectory
          .map((venue) => {
            const point = getVenueExactPoint(venue);
            const distance = point ? distanceKm(locationOrigin, point) : null;
            return distance !== null && distance <= 80 ? applyDistanceFromOrigin(venue, locationOrigin) : null;
          })
          .filter(Boolean)
        : [];
      return sortVenueResults(uniqueVenueList([...apiCentres, ...localNearbyCentres]), locationOrigin || searchCriteria.location);
    }
    const cityKey = compactLocationText(city);
    const cityMatches = venueDirectory.filter((venue) => compactLocationText(venue.city) === cityKey);
    const suburb = searchCriteria.location.type === "suburb" ? searchCriteria.location.suburb : "";
    const suburbKey = compactLocationText(suburb);
    const matches = suburb ? cityMatches.filter((venue) => compactLocationText(venue.suburb) === suburbKey) : cityMatches;
    const nextMatches = locationOrigin ? matches.map((venue) => applyDistanceFromOrigin(venue, locationOrigin)) : matches;
    return sortVenueResults(nextMatches, locationOrigin || (suburb ? searchCriteria.location : null));
  }, [city, searchCriteria.location, osmCentres, usingApiCentres, distanceOrigin, venueDirectory, osmCentreStatus]);

  const cityGroups = useMemo(() => {
    const location = searchCriteria.location || { type: "city", city };
    return publicGroups.filter((group) => recordMatchesSeoArea(group, location, "group"));
  }, [city, searchCriteria.location, publicGroups]);

  const cityEvents = useMemo(
    () => publicEvents.filter((event) => eventMatchesLocation(event, city, searchCriteria)),
    [publicEvents, city, searchCriteria]
  );

  const knownVenueDirectory = useMemo(() => uniqueVenueList([...venueDirectory, ...osmCentres]), [venueDirectory, osmCentres]);

  useEffect(() => {
    if (page !== "administrator") return undefined;
    const loadKey = `${globalDirectoryTotalCount}|${globalCentreCountryRows.length}`;
    if (adminDirectoryLoadRef.current === loadKey) return undefined;
    adminDirectoryLoadRef.current = loadKey;

    setAdminDirectoryStatus({
      state: "loading",
      loadedRows: knownVenueDirectory.length,
      totalRows: globalDirectoryTotalCount,
      loadedCountries: 0,
      totalCountries: globalCentreCountryRows.length,
      message: `Loading worldwide centre directory for administrator tools. ${knownVenueDirectory.length.toLocaleString()} startup records are visible now.`
    });

    loadAllGlobalCentreRows({
      maxRows: adminDirectoryBrowserRowLimit,
      onProgress: ({ loadedCountries, totalCountries, loadedRows }) => {
        setAdminDirectoryStatus({
          state: "loading",
          loadedRows,
          totalRows: globalDirectoryTotalCount,
          loadedCountries,
          totalCountries,
          message: `Loading country shards ${loadedCountries.toLocaleString()} of ${totalCountries.toLocaleString()} for administrator tools.`
        });
      }
    })
      .then((rows) => {
        const shardVenues = uniqueVenueList(rows.map(normaliseDbCentre));
        const merged = uniqueVenueList([...knownVenueDirectory, ...shardVenues]);
        setAdminVenueDirectory(merged);
        const isBoundedSnapshot = merged.length < globalDirectoryTotalCount;
        setAdminDirectoryStatus({
          state: isBoundedSnapshot ? "loaded_partial" : "loaded",
          loadedRows: merged.length,
          totalRows: globalDirectoryTotalCount,
          loadedCountries: Math.min(globalCentreCountryRows.length, adminDirectoryBrowserRowLimit),
          totalCountries: globalCentreCountryRows.length,
          message: isBoundedSnapshot
            ? `Loaded a browser-safe snapshot of ${merged.length.toLocaleString()} centres from the ${globalDirectoryTotalCount.toLocaleString()}-record worldwide directory. The complete database remains available to public search without overloading administrator tools.`
            : `Loaded ${merged.length.toLocaleString()} administrator centre records from ${globalCentreCountryRows.length.toLocaleString()} countries.`
        });
      })
      .catch((error) => {
        adminDirectoryLoadRef.current = "";
        setAdminVenueDirectory(knownVenueDirectory);
        setAdminDirectoryStatus({
          state: "error",
          loadedRows: knownVenueDirectory.length,
          totalRows: globalDirectoryTotalCount,
          loadedCountries: 0,
          totalCountries: globalCentreCountryRows.length,
          message: error?.message || "Could not load the worldwide administrator centre directory."
        });
      });
  }, [page, knownVenueDirectory]);

  const currentRouteContext = (overrides = {}) => ({
    city,
    searchCriteria,
    activeFilter,
    selectedVenue,
    selectedGroup,
    selectedGroupSession,
    selectedPlayer,
    selectedCoach,
    selectedEvent: activeSelectedEvent,
    blogPost: activeBlogPost,
    reviewContext,
    paymentContext,
    bookingType,
    activeResultType,
    seoAreaIntent,
    accountProfile: publicProfileRoute && resolvedPublicProfile ? resolvedPublicProfile : accountProfile,
    viewerAccountProfile: accountProfile,
    publicProfileRoute: activePublicProfileRoute,
    signupStep: new URLSearchParams(window.location.search).get("step") || "",
    signupRole: new URLSearchParams(window.location.search).get("role") || "",
    signupSource: new URLSearchParams(window.location.search).get("from") || "",
    ...overrides
  });

  const writeRoute = (nextPage, overrides = {}, mode = "push") => {
    const nextPath = buildRoutePath(nextPage, currentRouteContext(overrides));
    const currentPathname = window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/+$/, "");
    const currentPath = nextPath.includes("?") ? `${currentPathname}${window.location.search}` : currentPathname;
    const canonicalPath = nextPath === "/" ? "/" : nextPath.replace(/\/+$/, "");
    if (currentPath === canonicalPath) return;
    const method = mode === "replace" ? "replaceState" : "pushState";
    window.history[method]({ page: nextPage }, "", nextPath);
  };

  useEffect(() => {
    const applyBrowserRoute = () => {
      const route = resolveRouteFromPath();
      setPage(route.page);
      setMode(getRouteTopMode(route));
      setActiveFilter(route.activeFilter || pageFallbackFilter[route.page] || "nearby");
      setActiveResultType(route.activeResultType || getPreferredHubResultType(route.activeFilter || pageFallbackFilter[route.page] || "nearby", searchCriteria.tags));
      setSeoAreaIntent(route.seoAreaIntent || "overview");
      setCity(route.city);
      setSelectedVenue(route.page === "centreClaimSearch" ? null : route.venue || null);
      setSelectedGroup(route.group || null);
      setSelectedGroupSession(route.groupSession || null);
      setSelectedPlayer(route.player || null);
      setSelectedPlayerSlug(route.playerSlug || (route.player ? entitySlug(route.player) : ""));
      setSelectedCoach(route.coach || null);
      setSelectedEvent(route.event || null);
      setSelectedEventSlug(route.eventSlug || "");
      setSelectedBlogSlug(route.blogSlug || "");
      setPendingVenueRoute(route.pendingVenueRoute || null);
      setCentreRouteIssue(null);
      setPublicProfileRoute(route.publicProfileRoute || null);
      setResolvedPublicProfile(null);
      setPublicProfileRouteStatus({
        state: route.publicProfileRoute ? "loading" : "idle",
        message: route.publicProfileRoute ? "Loading the published profile." : ""
      });
      setReviewContext(reviewContextFor(route.reviewKind || "general"));
      setPaymentContext(paymentContextFor(route.paymentKind || "general"));
      setBookingType(route.bookingType || "court");
      if (route.location) {
        setSearchCriteria((current) => ({ ...current, location: route.location }));
      }
      setMenuOpen(false);
      resetPageScroll();
    };
    window.addEventListener("popstate", applyBrowserRoute);
    return () => window.removeEventListener("popstate", applyBrowserRoute);
  }, [searchCriteria.tags]);

  useEffect(() => {
    if (pendingVenueRoute) return;
    if (page === "player" && selectedPlayerSlug && !selectedPlayer) return;
    if (["event", "eventJoin", "manageEvent"].includes(page) && selectedEventSlug && !activeSelectedEvent) return;
    writeRoute(page, {}, "replace");
  }, [page, city, searchCriteria.location, activeFilter, activeResultType, seoAreaIntent, selectedVenue, selectedGroup, selectedGroupSession, selectedPlayer, selectedPlayerSlug, selectedCoach, activeSelectedEvent, activeBlogPost, reviewContext, paymentContext, bookingType, accountProfile, pendingVenueRoute, publicProfileRoute, resolvedPublicProfile]);

  useEffect(() => {
    applySeoMeta(buildSeoMeta(page, currentRouteContext()));
  }, [page, city, searchCriteria, activeFilter, activeResultType, seoAreaIntent, selectedVenue, selectedGroup, selectedPlayer, selectedCoach, activeSelectedEvent, activeBlogPost, reviewContext, paymentContext, bookingType, accountProfile, publicProfileRoute, resolvedPublicProfile]);

  const updateCity = (nextCity) => {
    const nextLocation = normaliseSearchLocationForMap(locationOptions.find((option) => option.type === "city" && option.city === nextCity) || { type: "city", label: nextCity, city: nextCity }, nextCity);
    setCity(nextCity);
    setDistanceOrigin(null);
    setOsmCentres([]);
    setOsmCentreStatus("idle");
    setOsmCentreError("");
    setSearchCriteria((current) => ({ ...current, location: nextLocation }));
    loadOsmCentres(nextLocation, 45000);
  };

  const updateSearchLocation = (location) => {
    const nextLocation = normaliseSearchLocationForMap(location, city);
    if (nextLocation.type !== "nearby") setDistanceOrigin(null);
    setCity(nextLocation.city || city);
    setSearchCriteria((current) => ({ ...current, location: nextLocation }));
  };

  const ensureVenueDirectoryForLocation = async (location) => {
    const globalRows = await loadGlobalCentreRowsForLocation(location);
    if (!globalRows.length) return venueDirectory;
    const shardVenues = uniqueVenueList(globalRows.map(normaliseDbCentre).filter(isPublicCourtHireCentre));
    const merged = uniqueVenueList([...venueDirectory, ...shardVenues]);
    setVenueDirectory(merged);
    return merged;
  };

  const loadOsmCentres = async (location, radius = 35000) => {
    setOsmCentreStatus("loading");
    setOsmCentreError("");
    setDirectoryLoadStatus("loading");
    setOsmCentres([]);
    try {
      const directory = await ensureVenueDirectoryForLocation(location);
      setDirectoryLoadStatus("loaded");
      const centres = await searchCentreDirectory(location, radius, directory);
      setOsmCentres(centres);
      setOsmCentreStatus("loaded");
      if (!centres.length) {
        setOsmCentreError("No badminton centres were found for this area.");
      }
      return centres;
    } catch (error) {
      setDirectoryLoadStatus("error");
      setOsmCentreStatus("error");
      setOsmCentreError(error.message || "Centre search is unavailable right now. Try another suburb or city.");
      return [];
    }
  };

  useEffect(() => {
    const location = searchCriteria.location;
    const countryCode = normaliseCountryCode(location?.countryCode || location?.country, location?.city || city);
    if (!location || !countryCode) return undefined;
    const key = [countryCode, location.type, location.city, location.suburb || location.label].filter(Boolean).join("|");
    if (!key || routeDirectoryLoadRef.current === key) return undefined;
    const loadDirectory = () => {
      routeDirectoryLoadRef.current = key;
      return loadOsmCentres(location, location.type === "suburb" ? 15000 : location.type === "country" ? 90000 : 45000);
    };
    if (page === "home") return undefined;
    loadDirectory();
    return undefined;
  }, [page, city, searchCriteria.location?.id, searchCriteria.location?.countryCode, searchCriteria.location?.country, searchCriteria.location?.city, searchCriteria.location?.suburb, searchCriteria.location?.label, searchCriteria.location?.type]);

  useEffect(() => {
    if (!pendingVenueRoute?.slug) return;
    if (auth.loading) return;
    if (isRealFirebaseUser(auth.user) && accountSyncStatus.state === "syncing") return;
    let cancelled = false;

    const finishUnavailableRoute = (venue, kind, message = "") => {
      if (cancelled) return;
      const fallbackVenue = venue || {
        id: `unresolved-${pendingVenueRoute.slug}`,
        slug: pendingVenueRoute.slug,
        name: "Centre listing unavailable",
        city: pendingVenueRoute.city || city,
        countryCode: pendingVenueRoute.location?.countryCode || "",
        country: pendingVenueRoute.location?.country || "",
        suburb: pendingVenueRoute.location?.suburb || "",
        address: ""
      };
      setSelectedVenue(fallbackVenue);
      setCentreRouteIssue({
        kind,
        message,
        requestedAction: pendingVenueRoute.action || "",
        requestedSlug: pendingVenueRoute.slug
      });
      setCity(fallbackVenue.city || pendingVenueRoute.city || city);
      setBookingType("court");
      setPendingVenueRoute(null);
      setPage("centreUnavailable");
    };

    const resolvePendingVenueRoute = async () => {
      setCentreRouteIssue(null);
      const accountCentre = accountProfile.centre || {};
      const managedAccountVenue = hasCentreOwnerBookingIdentity(accountCentre)
        ? managedVenueFromCentreProfile(accountCentre)
        : null;
      let venue = [
        ...(managedAccountVenue ? [managedAccountVenue] : []),
        ...venueDirectory
      ].find((item) => venueMatchesSlug(item, pendingVenueRoute.slug));

      if (!venue) {
        const globalRows = await loadGlobalCentreRowsForLocation(pendingVenueRoute.location || {
          city: pendingVenueRoute.city || city
        });
        const requestedSlug = slugifyPath(pendingVenueRoute.slug);
        const rawVenue = globalRows.find((row) => {
          const aliases = Array.isArray(row.aliases) ? row.aliases.map(slugifyPath) : [];
          return slugifyPath(row.slug || "") === requestedSlug
            || aliases.includes(requestedSlug)
            || slugifyPath(row.name || "") === requestedSlug;
        });
        if (rawVenue) venue = normaliseDbCentre(rawVenue);
      }

      if (!venue) {
        const legacyProfileId = centreProfileIdFromLegacySlug(pendingVenueRoute.slug);
        if (legacyProfileId) {
          try {
            const requestPayload = {
              role: "centre",
              areaSlug: slugifyPath(pendingVenueRoute.city || city || "worldwide"),
              typeSegment: publicProfileRoleConfig.centre.typeSegment,
              profileSlug: slugifyPath(pendingVenueRoute.slug),
              profileId: legacyProfileId
            };
            const result = firebaseAuth?.currentUser
              ? await callAuthenticatedProfileFunction("resolvePublicProfileRoute", requestPayload)
              : await callProfileFunctionEndpoint("resolvePublicProfileRoute", requestPayload);
            if (result?.profile && result?.profileId) {
              const publicCentreAccount = publicAccountProfileFromRecord("centre", result.profileId, result.profile);
              venue = managedVenueFromCentreProfile(publicCentreAccount.centre || {});
            }
          } catch {
            // Continue to the standard unavailable route for genuinely stale records.
          }
        }
      }

      if (cancelled) return;
      if (!venue) {
        finishUnavailableRoute(null, "not-found");
        return;
      }
      if (!isPublicCourtHireCentre(venue) && !isOwnerManagedCentreListing(venue)) {
        finishUnavailableRoute(venue, "community-organisation");
        return;
      }

      const nextPage = {
        book: "booking",
        claim: "centreClaimSearch",
        courts: "courtManager"
      }[pendingVenueRoute.action] || "venue";
      setVenueDirectory((current) => uniqueVenueList([...current, venue]));
      setSelectedVenue(venue);
      setCentreRouteIssue(null);
      setCity(venue.city || pendingVenueRoute.city || city);
      setBookingType("court");
      setPendingVenueRoute(null);
      setPage(nextPage);
    };

    resolvePendingVenueRoute().catch((error) => {
      finishUnavailableRoute(null, "lookup-error", error?.message || "Centre details could not be loaded.");
    });

    return () => {
      cancelled = true;
    };
  }, [
    pendingVenueRoute,
    venueDirectory,
    city,
    auth.loading,
    auth.user?.uid,
    accountSyncStatus.state,
    accountProfile.centre?.name,
    accountProfile.centre?.serverCentreId,
    accountProfile.centre?.firestoreSourceId,
    accountProfile.centre?.claimedVenueId
  ]);

  const activateNearbySearch = async ({ silent = false, replaceRoute = false } = {}) => {
    setOsmCentreStatus("loading");
    setOsmCentreError("");
    try {
      const position = await getCurrentPosition();
      const locationPoint = { lat: position.coords.latitude, lon: position.coords.longitude };
      const nearbySeed = inferNearestKnownCitySeedFromPoint(locationPoint, city);
      const nearbyCity = nearbySeed?.city || inferNearestKnownCityFromPoint(locationPoint, city);
      const nearbyCountryCode = nearbySeed?.country || getCityCoordinate(nearbyCity)?.country || "";
      const nearbyLocation = {
        id: "nearby-current-location",
        label: "Near me",
        city: nearbyCity,
        suburb: "",
        country: getCountryName(nearbyCountryCode, nearbyCity),
        countryCode: nearbyCountryCode,
        type: "nearby",
        hint: "Your current location",
        lat: locationPoint.lat,
        lon: locationPoint.lon,
        bbox: mapBboxAround(locationPoint.lat, locationPoint.lon, 0.16),
        source: "Browser location"
      };
      setDistanceOrigin(nearbyLocation);
      setCity(nearbyCity);
      setSearchCriteria((current) => ({ ...current, location: nearbyLocation }));
      setMode("all");
      setActiveFilter("nearby");
      setActiveResultType("overview");
      if (replaceRoute) {
        writeRoute("filterHub", {
          city: nearbyCity,
          searchCriteria: { ...searchCriteria, location: nearbyLocation },
          activeFilter: "nearby",
          activeResultType: "overview"
        }, "replace");
        setPage("filterHub");
      } else {
        navigate("filterHub", { city: nearbyCity, location: nearbyLocation, activeFilter: "nearby", activeResultType: "overview" });
      }
      return loadOsmCentres(nearbyLocation, 30000);
    } catch (error) {
      if (silent) {
        setOsmCentreStatus("idle");
        setOsmCentreError("");
        return [];
      }
      setOsmCentreStatus("error");
      setOsmCentreError(error.message || "Could not access your location. Search a suburb or city instead.");
      setMode("all");
      setActiveFilter("nearby");
      setActiveResultType("overview");
      navigate("filterHub", { activeFilter: "nearby", activeResultType: "overview" });
      return [];
    }
  };

  const navigate = (nextPage, payload = {}) => {
    const publicRoleByLegacyPage = { player: "player", coach: "coach", group: "group" };
    const publicRecordByLegacyPage = { player: payload.player, coach: payload.coach, group: payload.group };
    const publicRole = publicRoleByLegacyPage[nextPage];
    const canonicalPublicPayload = publicRole && publicRecordByLegacyPage[nextPage]
      ? publicProfileNavigationPayload(publicRole, publicRecordByLegacyPage[nextPage])
      : null;
    if (canonicalPublicPayload) {
      nextPage = publicProfilePageByRole[publicRole];
      payload = { ...payload, ...canonicalPublicPayload };
    }
    setCentreRouteIssue(null);
    const setupPagesClearContext = new Set(["claimCentre", "centreClaimSearch", "profileSetup", "coachProfile", "createGroup", "signup", "onboarding"]);
    const nextVenue = payload.venue
      || (nextPage === "claimCentre" ? createCentreDraftFromSearch(searchCriteria) : nextPage === "centreClaimSearch" ? null : selectedVenue);
    const nextGroup = payload.group || (setupPagesClearContext.has(nextPage) ? null : selectedGroup);
    const nextGroupSession = payload.session || (nextPage === "createGroupSession" ? null : selectedGroupSession);
    const nextPlayer = payload.player || (setupPagesClearContext.has(nextPage) ? null : selectedPlayer);
    const nextCoach = payload.coach || (setupPagesClearContext.has(nextPage) ? null : selectedCoach);
    const nextEvent = payload.event || activeSelectedEvent;
    const nextBlogPost = payload.blogPost || activeBlogPost;
    const nextReviewContext = payload.reviewContext || reviewContext;
    const nextPaymentContext = payload.paymentContext || paymentContext;
    const nextBookingType = payload.bookingType || bookingType;
    const nextSearchCriteria = payload.location ? { ...searchCriteria, location: payload.location } : searchCriteria;
    const nextActiveFilter = payload.activeFilter || activeFilter;
    const nextActiveResultType = normaliseHubResultType(payload.activeResultType)
      || (payload.activeFilter ? getPreferredHubResultType(nextActiveFilter, searchCriteria.tags) : activeResultType);
    const nextSeoAreaIntent = normaliseSeoAreaIntent(payload.seoAreaIntent || (nextPage === "seoArea" ? seoAreaIntent : "overview"));
    const nextPublicProfileRoute = payload.publicProfileRoute || null;
    const nextRouteOverrides = {
      city: payload.city || payload.location?.city || city,
      searchCriteria: nextSearchCriteria,
      selectedVenue: nextVenue,
      selectedGroup: nextGroup,
      selectedGroupSession: nextGroupSession,
      selectedPlayer: nextPlayer,
      selectedCoach: nextCoach,
      selectedEvent: nextEvent,
      blogPost: nextBlogPost,
      reviewContext: nextReviewContext,
      paymentContext: nextPaymentContext,
      bookingType: nextBookingType,
      activeFilter: nextActiveFilter,
      activeResultType: nextActiveResultType,
      seoAreaIntent: nextSeoAreaIntent,
      publicProfileRoute: nextPublicProfileRoute,
      signupStep: payload.signupStep || "",
      signupRole: payload.signupRole || "",
      signupSource: payload.signupSource || "",
      accountProfile
    };
    const directoryDependentPages = new Set(["home", "filterHub", "results", "city", "country", "seoArea", "venue", "centreUnavailable", "groupsIndex", "group", "playersIndex", "player", "coach", "events", "event", "courtAvailability"]);
    if (lightweightAuthBootstrap && directoryDependentPages.has(nextPage)) {
      window.location.assign(buildRoutePath(nextPage, currentRouteContext(nextRouteOverrides)));
      return;
    }
    writeRoute(nextPage, nextRouteOverrides);
    if (payload.location) {
      setCity(payload.location.city || city);
      setSearchCriteria(nextSearchCriteria);
    }
    if (payload.venue || nextPage === "claimCentre" || nextPage === "centreClaimSearch") setSelectedVenue(nextVenue);
    if (payload.group) setSelectedGroup(payload.group);
    if (payload.session || nextPage === "createGroupSession") setSelectedGroupSession(nextGroupSession);
    if (payload.player) {
      setSelectedPlayer(payload.player);
      setSelectedPlayerSlug(entitySlug(payload.player));
    } else if (nextPage !== "player") {
      setSelectedPlayerSlug("");
    }
    if (payload.coach) setSelectedCoach(payload.coach);
    if (!payload.group && setupPagesClearContext.has(nextPage)) setSelectedGroup(null);
    if (!payload.player && setupPagesClearContext.has(nextPage)) setSelectedPlayer(null);
    if (!payload.coach && setupPagesClearContext.has(nextPage)) setSelectedCoach(null);
    if (payload.event) {
      setSelectedEvent(payload.event);
      setSelectedEventSlug(entitySlug(payload.event, "title"));
    }
    if (payload.blogPost) setSelectedBlogSlug(payload.blogPost.slug);
    if (payload.messageFocus) setMessageFocus(payload.messageFocus);
    else if (nextPage === "messages") setMessageFocus(null);
    if (payload.reviewContext) setReviewContext(payload.reviewContext);
    if (payload.paymentContext) setPaymentContext(payload.paymentContext);
    if (payload.activeFilter) setActiveFilter(payload.activeFilter);
    if (nextPage === "filterHub" && (payload.activeFilter || payload.activeResultType)) {
      setMode(getHubTopMode(nextActiveResultType, nextActiveFilter));
    } else if (payload.activeFilter) {
      setMode(filterContent[payload.activeFilter]?.mode || pageMode[nextPage] || mode);
    }
    if (payload.activeFilter || payload.activeResultType) setActiveResultType(nextActiveResultType);
    if (payload.seoAreaIntent || nextPage === "seoArea") setSeoAreaIntent(nextSeoAreaIntent);
    if (payload.bookingType) setBookingType(payload.bookingType);
    if (payload.bookingDraft) setBookingDraft(payload.bookingDraft);
    setPublicProfileRoute(nextPublicProfileRoute);
    if (nextPublicProfileRoute && payload.accountProfile) {
      setResolvedPublicProfile(payload.accountProfile);
      setPublicProfileRouteStatus({ state: "ready", message: "Published profile loaded." });
    } else if (nextPublicProfileRoute) {
      setResolvedPublicProfile(null);
      setPublicProfileRouteStatus({ state: "loading", message: "Loading the published profile." });
    } else {
      setResolvedPublicProfile(null);
      setPublicProfileRouteStatus({ state: "idle", message: "" });
    }
    setPage(nextPage);
    setMenuOpen(false);
    setNotificationPanelOpen(false);
    resetPageScroll();
  };

  const toggleFollowing = async (target, follow = true) => {
    const socialTarget = normaliseSocialFollowTarget(target);
    if (!socialTarget.targetCollection || !socialTarget.targetId || !socialTarget.publicPath) {
      throw new Error("This profile is not ready to follow yet.");
    }
    if (!isRealFirebaseUser(auth.user)) {
      navigate("login", { returnTo: window.location.pathname });
      return { redirected: true, following: false };
    }

    const entityId = socialTarget.entityId || socialEntityIdForTarget(socialTarget);
    const optimisticRecord = normaliseFollowingRecord({
      ...socialTarget,
      entityId,
      followedAt: new Date().toISOString(),
      status: "active"
    });
    const previousFollowing = accountFollowing;
    setAccountFollowing((current) => {
      const withoutTarget = current.filter((item) => String(item.entityId || socialEntityIdForTarget(item)) !== entityId);
      return follow ? [optimisticRecord, ...withoutTarget] : withoutTarget;
    });
    try {
      const result = await callMarketplaceFunction("toggleMarketplaceFollow", {
        targetCollection: socialTarget.targetCollection,
        targetId: socialTarget.targetId,
        follow,
        targetSnapshot: {
          label: socialTarget.label,
          area: socialTarget.area,
          image: socialTarget.image,
          countryCode: socialTarget.countryCode,
          publicPath: socialTarget.publicPath
        }
      });
      const confirmedEntityId = result.entityId || entityId;
      setAccountFollowing((current) => {
        const withoutTarget = current.filter((item) => String(item.entityId || socialEntityIdForTarget(item)) !== confirmedEntityId);
        return result.following === false ? withoutTarget : [{ ...optimisticRecord, entityId: confirmedEntityId }, ...withoutTarget];
      });
      return result;
    } catch (error) {
      setAccountFollowing(previousFollowing);
      throw error;
    }
  };

  const markNotificationIdsRead = async (notificationIds = []) => {
    const ids = Array.from(new Set(notificationIds.filter(Boolean))).slice(0, 100);
    if (!ids.length) return;
    setAccountNotifications((current) => current.map((notification) => ids.includes(notification.id)
      ? { ...notification, read: true, readAt: notification.readAt || new Date().toISOString() }
      : notification));
    try {
      await callMarketplaceFunction("markNotificationsRead", { notificationIds: ids });
    } catch (error) {
      setAccountNotifications((current) => current.map((notification) => ids.includes(notification.id)
        ? { ...notification, read: false, readAt: null }
        : notification));
      setNotificationSyncState({
        status: "error",
        message: marketplaceFunctionErrorMessage(error) || "Notification read status could not be saved."
      });
    }
  };

  const markAllNotificationsRead = async () => {
    const notificationIds = accountNotifications
      .filter((notification) => !notification.read)
      .map((notification) => notification.id);
    for (let index = 0; index < notificationIds.length; index += 100) {
      await markNotificationIdsRead(notificationIds.slice(index, index + 100));
    }
  };

  const openAccountNotification = (notification) => {
    if (!notification) return;
    if (!notification.read) markNotificationIdsRead([notification.id]);
    setNotificationPanelOpen(false);
    const notificationPublicPath = String(notification.detail?.publicPath || notification.detail?.publicpath || "").trim();
    if (/^\/(?:players|coaches|social-groups|centres)\/[^?#\s]+/i.test(notificationPublicPath)) {
      window.history.pushState({}, "", notificationPublicPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    const conversationId = notification.detail?.conversationId || notification.detail?.conversationid || "";
    navigate(notification.targetPage || notificationTargetPage(notification), notification.targetPage === "messages" && conversationId
      ? { messageFocus: { conversationId } }
      : {});
  };

  useEffect(() => {
    const autoNearbyPages = new Set(["home", "filterHub", "results", "city"]);
    if (autoNearbyStartedRef.current) return undefined;
    if (pendingVenueRoute) return undefined;
    if (!autoNearbyPages.has(page)) return undefined;
    if (searchCriteria.location?.type === "nearby") return undefined;
    if (typeof navigator === "undefined" || !navigator.geolocation) return undefined;

    let cancelled = false;
    autoNearbyStartedRef.current = true;
    const runAutoNearbySearch = async () => {
      try {
        if (!navigator.permissions?.query) return;
        const permission = await navigator.permissions.query({ name: "geolocation" });
        if (cancelled || permission.state !== "granted") return;
      } catch {
        return;
      }
      if (!cancelled) await activateNearbySearch({ silent: true, replaceRoute: true });
    };
    runAutoNearbySearch();
    return () => {
      cancelled = true;
    };
  }, [page, pendingVenueRoute, searchCriteria.location?.type]);

  const openMode = (key) => {
    const nextFilter = modeDefaultFilters[key] || "nearby";
    const nextResultType = modeDefaultResultTypes[key] || getPreferredHubResultType(nextFilter, searchCriteria.tags);
    setMode(key);
    setActiveFilter(nextFilter);
    setActiveResultType(nextResultType);
    navigate(modeTargets[key] || "home", { activeFilter: nextFilter, activeResultType: nextResultType });
  };

  const openFilter = async (key, target) => {
    if (key === "nearby") {
      await activateNearbySearch();
      return;
    }
    const nextResultType = getPreferredHubResultType(key, searchCriteria.tags);
    setActiveFilter(key);
    setActiveResultType(nextResultType);
    setMode(target === "filterHub" ? getHubTopMode(nextResultType, key) : filterContent[key]?.mode || "all");
    navigate(target, { activeFilter: key, activeResultType: nextResultType });
  };

  const submitSearch = async (overrides = {}) => {
    const nextLocation = normaliseSearchLocationForMap(overrides.location || searchCriteria.location, city);
    const nextSearchCriteria = { ...searchCriteria, location: nextLocation };
    const selectedTags = searchCriteria.tags;
    const nextFilter = "nearby";
    const nextResultType = getHubResultTypeFromTags(selectedTags);
    const nextMode = getHubTopMode(nextResultType, nextFilter);
    if (nextLocation.type !== "nearby") setDistanceOrigin(null);
    setCity(nextLocation.city || city);
    setSearchCriteria(nextSearchCriteria);
    setMode(nextMode);
    setActiveFilter(nextFilter);
    setActiveResultType(nextResultType);
    navigate("filterHub", { city: nextLocation.city || city, location: nextLocation, activeFilter: nextFilter, activeResultType: nextResultType });
    loadOsmCentres(nextLocation, nextLocation.type === "suburb" ? 15000 : nextLocation.type === "country" ? 90000 : 45000);
  };

  const toggleSaved = (id) => {
    setSaved((current) => {
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      try {
        window.localStorage.setItem(savedVenuesStorageKey, JSON.stringify(next));
      } catch {
        // Saved venues can still work in memory when local storage is unavailable.
      }
      return next;
    });
  };

  const preferredPhoneCountryCode = normaliseCountryCode(
    searchCriteria.location?.countryCode || searchCriteria.location?.country,
    searchCriteria.location?.city || city
  ) || browserPhoneCountryCode();

  const rememberSolutionSignup = ({ role = "player", returnPage = "", sourcePage = "", authenticated = false } = {}) => {
    const config = signupRoleIntents[role] || signupRoleIntents.player;
    const targetPage = returnPage || config.returnPage;
    const entryPage = sourcePage || config.sourcePage;
    if (!authenticated) {
      saveSignupIntent({
        role: config.role,
        returnPage: targetPage,
        sourcePage: entryPage,
        sourcePath: `${window.location.pathname}${window.location.search}`
      });
    }
    trackMarketingFunnelEvent(authenticated ? "profile_setup_started" : "signup_started", {
      role: config.role,
      dedupeId: authenticated ? auth.user?.uid || `signed-in-${config.role}` : `${config.role}-solution-cta`,
      dedupeScope: authenticated ? "persistent" : "session",
      extra: { entry_point: entryPage, return_page: targetPage }
    });
    return { role: config.role, returnPage: targetPage, sourcePage: entryPage };
  };

  const beginPlayerSignup = ({ authenticated = false } = {}) => rememberSolutionSignup({
    ...signupRoleIntents.player,
    authenticated
  });

  useEffect(() => {
    if (page !== "profileSetup" || !auth.user) return;
    trackMarketingFunnelEvent("profile_setup_started", {
      role: "player",
      dedupeId: auth.user.uid || "verified-player",
      dedupeScope: "persistent",
      extra: { entry_point: "profile_setup" }
    });
  }, [page, auth.user?.uid]);

  const protectedPage = (children, intent = "Continue", options = {}) => (
    <RequireVerifiedAccount
      auth={auth}
      navigate={navigate}
      intent={intent}
      accountProfile={accountProfile}
	  accountSyncStatus={accountSyncStatus}
      preferredPhoneCountryCode={preferredPhoneCountryCode}
      completionIntent={options.completionIntent || null}
      authContext={options.authContext || null}
    >
      {children}
    </RequireVerifiedAccount>
  );
  return (
    <main className={`app-shell app-page-${page}`}>
      {page === "administrator" ? (
        <AdministratorShellHeader
          navigate={navigate}
          auth={auth}
          unreadNotificationCount={unreadNotificationCount}
          openNotifications={() => setNotificationPanelOpen(true)}
        />
      ) : focusedProfileSetupPages[page] ? (
        <ProfileSetupShellHeader
          page={page}
          navigate={navigate}
          auth={auth}
          unreadNotificationCount={unreadNotificationCount}
          openNotifications={() => setNotificationPanelOpen(true)}
        />
      ) : (
        <Header
          city={city}
          searchCriteria={searchCriteria}
          setSearchCriteria={setSearchCriteria}
          updateSearchLocation={updateSearchLocation}
          mode={mode}
          openMode={openMode}
          submitSearch={submitSearch}
          activateNearbySearch={activateNearbySearch}
          osmCentreStatus={osmCentreStatus}
          auth={auth}
          accountProfile={accountProfileView}
          unreadMessageCount={marketplaceUnreadCount}
          unreadNotificationCount={unreadNotificationCount}
          openNotifications={() => setNotificationPanelOpen(true)}
          openMenu={() => setMenuOpen(true)}
          navigate={navigate}
        />
      )}
      {marketplaceCategoryRailPages.has(page) && <CategoryRail activeFilter={activeFilter} openFilter={openFilter} />}
      {!focusedProfileSetupPages[page] && page !== "administrator" && (
        <>
          <PageCrumbs
            page={page}
            city={city}
            searchCriteria={searchCriteria}
            activeFilter={activeFilter}
            selectedVenue={selectedVenue}
            selectedGroup={selectedGroup}
            selectedPlayer={selectedPlayer}
            selectedCoach={selectedCoach}
            selectedEvent={activeSelectedEvent}
            blogPost={activeBlogPost}
            reviewContext={reviewContext}
            paymentContext={paymentContext}
            bookingType={bookingType}
            navigate={navigate}
          />
          <ProfileDraftNudge auth={auth} accountProfile={accountProfileView} page={page} navigate={navigate} />
        </>
      )}

      {page === "home" && (
        <HomePage
          city={city}
          setCity={updateCity}
          searchCriteria={searchCriteria}
          osmCentreStatus={osmCentreStatus}
          osmCentreError={osmCentreError}
          usingApiCentres={usingApiCentres}
          venues={cityVenues}
          groups={cityGroups}
          players={publicPlayers}
          trainers={publicCoaches}
          events={cityEvents}
          accountProfile={accountProfile}
          saved={saved}
          toggleSaved={toggleSaved}
          navigate={navigate}
        />
      )}
      {page === "blogIndex" && <BlogIndexPage posts={blogPosts} navigate={navigate} />}
      {page === "blogArticle" && <BlogArticlePage post={activeBlogPost} posts={blogPosts} navigate={navigate} />}
      {page === "solutionIndex" && <SolutionOverviewPage navigate={navigate} />}
      {page === "solutionCentres" && <SolutionDetailPage kind="centres" navigate={navigate} auth={auth} onSignupIntent={rememberSolutionSignup} />}
      {page === "solutionOrganisers" && <SolutionDetailPage kind="organisers" navigate={navigate} auth={auth} onSignupIntent={rememberSolutionSignup} />}
      {page === "solutionCoaches" && <SolutionDetailPage kind="coaches" navigate={navigate} auth={auth} onSignupIntent={rememberSolutionSignup} />}
      {page === "solutionPlayers" && (
        <SolutionDetailPage
          kind="players"
          navigate={navigate}
          auth={auth}
          onPlayerSignupIntent={beginPlayerSignup}
          trackFunnelEvent={trackMarketingFunnelEvent}
        />
      )}
      {page === "solutionEvents" && <SolutionDetailPage kind="events" navigate={navigate} auth={auth} onSignupIntent={rememberSolutionSignup} />}
      {page === "filterHub" && (
        <FilterHubPage
          city={city}
          searchCriteria={searchCriteria}
          activeFilter={activeFilter}
          activeResultType={activeResultType}
          setActiveResultType={setActiveResultType}
          venues={cityVenues}
          groups={cityGroups}
          players={publicPlayers}
          trainers={publicCoaches}
          events={cityEvents}
          saved={saved}
          toggleSaved={toggleSaved}
          navigate={navigate}
          accountProfile={accountProfile}
          osmCentreStatus={osmCentreStatus}
          osmCentreError={osmCentreError}
          usingApiCentres={usingApiCentres}
        />
      )}
      {page === "seoArea" && (
        <SeoAreaPage
          city={city}
          searchCriteria={searchCriteria}
          seoAreaIntent={seoAreaIntent}
          venueDirectory={knownVenueDirectory}
          groupDirectory={publicGroups}
          playerDirectory={publicPlayers}
          coachDirectory={publicCoaches}
          events={publicEvents}
          accountProfile={accountProfile}
          saved={saved}
          toggleSaved={toggleSaved}
          navigate={navigate}
        />
      )}
      {page === "groupsIndex" && <GroupsIndexPage city={city} groups={cityGroups} venues={cityVenues} navigate={navigate} activeFilter={activeFilter} searchCriteria={searchCriteria} accountProfile={accountProfile} />}
      {page === "playersIndex" && <PlayersIndexPage city={city} players={publicPlayers} trainers={publicCoaches} venues={cityVenues} navigate={navigate} activeFilter={activeFilter} searchCriteria={searchCriteria} accountProfile={accountProfile} />}
      {page === "courtAvailability" && <CourtAvailabilityPage city={city} venues={cityVenues} navigate={navigate} activeFilter={activeFilter} searchCriteria={searchCriteria} osmCentreStatus={osmCentreStatus} osmCentreError={osmCentreError} usingApiCentres={usingApiCentres} />}
      {page === "events" && <EventsPage city={city} events={cityEvents} venues={cityVenues} navigate={navigate} searchCriteria={searchCriteria} accountProfile={accountProfile} activeFilter={activeFilter} />}
      {page === "event" && <EventDetail event={activeSelectedEvent} navigate={navigate} accountProfile={accountProfile} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} publicReviews={publicReviews} />}
      {page === "eventJoin" && protectedPage(<EventCheckout event={activeSelectedEvent} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />, "Join event")}
      {page === "eventManager" && protectedPage(<EventManagerPage accountProfile={accountProfileView} navigate={navigate} />, "Manage events")}
      {page === "joinedEvents" && protectedPage(<JoinedEventsPage accountProfile={accountProfileView} navigate={navigate} />, "View joined events")}
      {page === "manageEvent" && protectedPage(
        activeSelectedEvent ? (
          <RequireRoleProfile role={activeSelectedEvent.ownerType === "centre" ? "centre" : "organiser"} accountProfile={accountProfile} navigate={navigate} context="managed event dashboard">
            <ManagedEventDashboard event={activeSelectedEvent} accountProfile={accountProfileView} navigate={navigate} />
          </RequireRoleProfile>
        ) : <ManagedEventDashboard event={activeSelectedEvent} accountProfile={accountProfile} navigate={navigate} />,
        "Manage event"
      )}
      {page === "reviewFlow" && protectedPage(<ReviewFlowPage reviewContext={reviewContext} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} navigate={navigate} />, reviewContext?.title || "Leave a review")}
      {page === "paymentFlow" && protectedPage(<PaymentFlowPage paymentContext={paymentContext} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} navigate={navigate} />, "Confirm payment")}
      {page === "results" && pendingVenueRoute && <CentreRouteLoading />}
      {page === "results" && !pendingVenueRoute && (
        <ResultsPage
          city={city}
          venues={cityVenues}
          groups={cityGroups}
          saved={saved}
          toggleSaved={toggleSaved}
          mapVisible={resultsMap}
          setMapVisible={setResultsMap}
          activeFilter={activeFilter}
          searchCriteria={searchCriteria}
          osmCentreStatus={osmCentreStatus}
          osmCentreError={osmCentreError}
          usingApiCentres={usingApiCentres}
          navigate={navigate}
        />
      )}
      {page === "city" && <CityPage city={city} venues={cityVenues} groups={cityGroups} players={publicPlayers} trainers={publicCoaches} events={cityEvents} searchCriteria={searchCriteria} accountProfile={accountProfile} navigate={navigate} />}
      {page === "venue" && <VenueDetail venue={selectedVenue} allVenues={knownVenueDirectory} groups={publicGroups} navigate={navigate} saved={saved.includes(selectedVenue.id)} toggleSaved={toggleSaved} saveAccountItem={saveAccountItem} accountProfile={accountProfile} following={accountFollowing} toggleFollowing={toggleFollowing} />}
      {page === "centreUnavailable" && <CentreListingUnavailable venue={selectedVenue} issue={centreRouteIssue} navigate={navigate} />}
      {page === "group" && <GroupDetail group={selectedGroup} navigate={navigate} accountProfile={accountProfileView} saveAccountProfile={saveAccountProfile} saveAccountItem={saveAccountItem} />}
      {page === "player" && <PlayerProfile player={selectedPlayer} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} publicReviews={publicReviews} publicProfileSyncStatus={publicProfileSyncStatus} />}
      {page === "coach" && <CoachDirectoryProfile coach={selectedCoach} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} saveAccountItem={saveAccountItem} publicReviews={publicReviews} />}
      {page === "booking" && publicProfileRoute?.role === "centre" && !selectedVenue && <CentreRouteLoading />}
      {page === "booking" && !(publicProfileRoute?.role === "centre" && !selectedVenue) && protectedPage(
        <BookingCheckout venue={selectedVenue} group={selectedGroup} type={bookingType} bookingDraft={bookingDraft} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />,
        bookingType === "social" ? "Join a social session" : "Book a court",
        {
          authContext: bookingType === "social" ? {
            label: "Session selected",
            title: getGroupTitle(selectedGroup) || "Social badminton session",
            detail: [getGroupVenueLabel(selectedGroup), getGroupWhenLabel(selectedGroup)].filter(Boolean).join(" · "),
            backTarget: "group",
            backPayload: { group: selectedGroup }
          } : {
            label: "Court selected",
            title: selectedVenue?.name || "Badminton court booking",
            detail: [bookingDraft.date ? formatDateLabel(bookingDraft.date) : "", bookingDraft.time ? formatTimeLabel(bookingDraft.time) : "", getVenueAddressLabel(selectedVenue || {})].filter(Boolean).join(" · "),
            backTarget: "publicCentreProfile",
            backPayload: publicProfileNavigationPayload("centre", selectedVenue) || { venue: selectedVenue }
          }
        }
      )}
      {page === "join" && protectedPage(<JoinSocialCheckout group={selectedGroup} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />, "Join a social session")}
      {page === "groupMembership" && protectedPage(
        <GroupMembershipCheckout group={selectedGroup} navigate={navigate} accountProfile={accountProfileView} />,
        "Join this social group"
      )}
      {page === "playerDashboard" && protectedPage(
        <RequireRoleProfile role="player" accountProfile={accountProfile} navigate={navigate} context="player dashboard">
          <PlayerDashboard navigate={navigate} accountProfile={accountProfileView} />
        </RequireRoleProfile>,
        "Open player dashboard"
      )}
      {page === "profileSetup" && protectedPage(
        <ProfileSetup profileComplete={profileComplete} setProfileComplete={setProfileComplete} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />,
        "Create a player profile",
        { completionIntent: signupRoleIntents.player }
      )}
      {page === "publicPlayerProfile" && <PublicProfilePreview type="player" accountProfile={publicProfileSubject} viewerProfile={accountProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={accountFollowing} toggleFollowing={toggleFollowing} publicReviews={publicReviews} routeStatus={publicProfileRoute ? publicProfileRouteStatus : null} externalProfile={Boolean(publicProfileRoute)} />}
      {page === "coachDashboard" && protectedPage(
        <RequireRoleProfile role="coach" accountProfile={accountProfile} navigate={navigate} context="coach dashboard">
          <CoachDashboard navigate={navigate} accountProfile={accountProfileView} />
        </RequireRoleProfile>,
        "Open coach dashboard"
      )}
      {page === "coachProfile" && protectedPage(
        <CoachProfileSetup navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />,
        "Create a coach profile",
        { completionIntent: signupRoleIntents.coach }
      )}
      {page === "publicCoachProfile" && <PublicProfilePreview type="coach" accountProfile={publicProfileSubject} viewerProfile={accountProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={accountFollowing} toggleFollowing={toggleFollowing} publicReviews={publicReviews} routeStatus={publicProfileRoute ? publicProfileRouteStatus : null} externalProfile={Boolean(publicProfileRoute)} />}
      {page === "ratings" && <RatingsSystem players={publicPlayers} navigate={navigate} />}
      {page === "messages" && protectedPage(
        <MessagesPage
          navigate={navigate}
          accountProfile={accountProfileView}
          saveAccountProfile={saveAccountProfile}
          messageFocus={messageFocus}
          marketplaceConversations={marketplaceConversations}
          setMarketplaceConversations={setMarketplaceConversations}
          conversationSyncState={marketplaceConversationSyncState}
          refreshMarketplaceConversations={refreshMarketplaceConversations}
        />,
        "Open messages"
      )}
      {page === "organiser" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="organiser dashboard">
          <OrganiserDashboard navigate={navigate} accountProfile={accountProfileView} />
        </RequireRoleProfile>,
        "Open organiser tools"
      )}
      {page === "createGroup" && protectedPage(
        <CreateGroupWizard navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />,
        "Create a social group",
        { completionIntent: signupRoleIntents.organiser }
      )}
      {page === "groupSessions" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="group session management">
          <GroupSessionManager navigate={navigate} accountProfile={accountProfileView} saveAccountProfile={saveAccountProfile} />
        </RequireRoleProfile>,
        "Manage group sessions"
      )}
      {page === "createGroupSession" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="group session creation">
          <GroupSessionWizard navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />
        </RequireRoleProfile>,
        "Create a group session"
      )}
      {page === "editGroupSession" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="group session editing">
          {activeGroupSession ? (
            <GroupSessionWizard navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} session={activeGroupSession} />
          ) : (
            <CommunityEmptyState
              type="organiser session"
              title="This session could not be found"
              text="Open the session manager and choose a session that belongs to your current organiser account."
              primary={{ label: "Open session manager", target: "groupSessions" }}
              secondary={{ label: "Create session", target: "createGroupSession" }}
              icon={CalendarDays}
              navigate={navigate}
            />
          )}
        </RequireRoleProfile>,
        "Edit a group session"
      )}
      {page === "createEvent" && protectedPage(
        <RequireAnyRoleProfile roles={["organiser", "centre"]} accountProfile={accountProfile} navigate={navigate} context="event creation tools">
          <CreateEventWizard navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} city={city} />
        </RequireAnyRoleProfile>,
        "Create an event"
      )}
      {page === "publicGroupProfile" && (activePublicProfileRoute?.action === "join" && resolvedPublicProfile ? protectedPage(
        <JoinSocialCheckout group={normaliseJoinedGroup(resolvedPublicProfile.group)} navigate={navigate} accountProfile={accountProfileView} saveAccountProfile={saveAccountProfile} />,
        "Join this social session"
      ) : activePublicProfileRoute?.action === "membership" && resolvedPublicProfile ? protectedPage(
        <GroupMembershipCheckout group={normaliseJoinedGroup(resolvedPublicProfile.group)} navigate={navigate} accountProfile={accountProfileView} />,
        "Join this social group"
      ) : <PublicProfilePreview type="group" accountProfile={publicProfileSubject} viewerProfile={accountProfileView} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={accountFollowing} toggleFollowing={toggleFollowing} publicReviews={publicReviews} routeStatus={publicProfileRoute ? publicProfileRouteStatus : null} externalProfile={Boolean(publicProfileRoute)} />)}
      {page === "payouts" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="organiser payouts">
          <PayoutsPage role="Organiser" accountProfile={accountProfileView} auth={auth} saveAccountProfile={saveAccountProfile} navigate={navigate} />
        </RequireRoleProfile>,
        "Open organiser payouts"
      )}
      {page === "coachPayouts" && protectedPage(
        <RequireRoleProfile role="coach" accountProfile={accountProfile} navigate={navigate} context="coach payouts">
          <PayoutsPage role="Coach" accountProfile={accountProfileView} auth={auth} saveAccountProfile={saveAccountProfile} navigate={navigate} />
        </RequireRoleProfile>,
        "Open coach payouts"
      )}
      {page === "members" && protectedPage(
        <RequireRoleProfile role="organiser" accountProfile={accountProfile} navigate={navigate} context="member management">
          <MemberManagement
            navigate={navigate}
            accountProfile={accountProfileView}
            saveAccountProfile={saveAccountProfile}
            playerDirectory={publicPlayers}
            playerDirectoryStatus={publicProfileSyncStatus}
          />
        </RequireRoleProfile>,
        "Manage members"
      )}
      {page === "centreDashboard" && protectedPage(
        <RequireRoleProfile role="centre" accountProfile={accountProfile} navigate={navigate} context="centre dashboard">
          <CentreDashboard venue={selectedVenue} navigate={navigate} accountProfile={accountProfileView} />
        </RequireRoleProfile>,
        "Open centre tools"
      )}
      {page === "claimCentre" && protectedPage(
        <ClaimCentrePage venue={selectedVenue} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />,
        "Add or claim a centre",
        { completionIntent: signupRoleIntents.centre }
      )}
      {page === "centreClaimSearch" && protectedPage(<CentreClaimSearchPage selectedVenue={selectedVenue} venueDirectory={knownVenueDirectory} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />, "Search and claim a centre")}
      {page === "publicCentreProfile" && <PublicProfilePreview type="centre" accountProfile={publicProfileSubject} viewerProfile={accountProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={accountFollowing} toggleFollowing={toggleFollowing} publicReviews={publicReviews} routeStatus={publicProfileRoute ? publicProfileRouteStatus : null} externalProfile={Boolean(publicProfileRoute)} />}
      {page === "courtManager" && protectedPage(
        <RequireRoleProfile role="centre" accountProfile={accountProfile} navigate={navigate} context="court management">
          <RequireRoleSubscription
            role="centre"
            entitlement="inPlatformBookings"
            accountProfile={accountProfile}
            navigate={navigate}
            title="Centre Pro unlocks court operations."
            text="Your centre listing, contact details, hours and facilities remain free. Upgrade to manage court inventory, booking rules, advanced fees and player checkout."
          >
            <CourtManager venue={selectedVenue} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />
          </RequireRoleSubscription>
        </RequireRoleProfile>,
        "Manage courts"
      )}
      {page === "centrePayments" && protectedPage(
        <RequireRoleProfile role="centre" accountProfile={accountProfile} navigate={navigate} context="centre payments">
          <RequireRoleSubscription
            role="centre"
            entitlement="inPlatformBookings"
            accountProfile={accountProfile}
            navigate={navigate}
            title="Centre Pro unlocks payments and memberships."
            text="Your free listing remains visible. Upgrade before setting up Badminta Pay, accepting court payments, selling memberships or managing credit packs."
          >
            <PayoutsPage role="Centre" accountProfile={accountProfileView} auth={auth} saveAccountProfile={saveAccountProfile} navigate={navigate} />
          </RequireRoleSubscription>
        </RequireRoleProfile>,
        "Open centre payments"
      )}
      {page === "admin" && protectedPage(<AdminNetwork navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} platformLedgerData={platformLedgerData} />, "Open network admin")}
      {page === "trust" && protectedPage(<TrustModeration navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} platformLedgerData={platformLedgerData} />, "Open trust moderation")}
      {page === "analytics" && <AnalyticsPage accountProfile={accountProfile} venueCount={knownVenueDirectory.length} />}
      {page === "administrator" && (
        <SuperAdministratorPage
          auth={auth}
          accountProfile={accountProfile}
          accountSyncStatus={accountSyncStatus}
          platformLedgerData={platformLedgerData}
          saveAccountProfile={saveAccountProfile}
          navigate={navigate}
          venueDirectory={adminCentreProfiles}
          adminDirectoryStatus={adminDirectoryStatus}
          groupDirectory={adminGroupProfiles}
          playerDirectory={adminPlayerProfiles}
          coachDirectory={adminCoachProfiles}
          events={adminEventProfiles}
          preferredPhoneCountryCode={preferredPhoneCountryCode}
        />
      )}
      {page === "login" && <LoginPage navigate={navigate} auth={auth} preferredPhoneCountryCode={preferredPhoneCountryCode} />}
      {page === "signup" && <SignupPage navigate={navigate} auth={auth} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} preferredPhoneCountryCode={preferredPhoneCountryCode} />}
      {page === "onboarding" && protectedPage(<OnboardingPage navigate={navigate} accountProfile={accountProfile} />, "Finish account setup")}
      {page === "bookings" && protectedPage(<SavedPage mode="bookings" venues={[]} bookings={getAccountBookings(accountProfileView)} savedItems={[]} accountProfile={accountProfileView} saveAccountProfile={saveAccountProfile} navigate={navigate} toggleSaved={toggleSaved} />, "Open bookings")}
      {page === "saved" && protectedPage(<SavedPage mode="saved" venues={knownVenueDirectory.filter((venue) => saved.includes(venue.id))} bookings={[]} savedItems={savedItems} accountProfile={accountProfileView} saveAccountProfile={saveAccountProfile} navigate={navigate} toggleSaved={toggleSaved} />, "Open saved items")}
      {page === "following" && protectedPage(<FollowingPage following={accountFollowing} syncState={followingSyncState} navigate={navigate} toggleFollowing={toggleFollowing} />, "Open followed profiles")}
      {page === "notifications" && protectedPage(
        <NotificationsPage
          notifications={accountNotifications}
          syncState={notificationSyncState}
          unreadCount={unreadNotificationCount}
          markAllRead={markAllNotificationsRead}
          openNotification={openAccountNotification}
        />,
        "Open notifications"
      )}
      {page === "account" && <AccountPage navigate={navigate} auth={auth} accountProfile={accountProfileView} accountSyncStatus={{ ...accountSyncStatus, marketplace: accountMarketplaceData }} saveAccountProfile={saveAccountProfile} preferredPhoneCountryCode={preferredPhoneCountryCode} />}
      {page === "accountDeletion" && <AccountDeletionPage navigate={navigate} auth={auth} accountProfile={accountProfileView} />}
      {page === "help" && <HelpPage navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "legalTerms" && <PolicyPage type="terms" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "privacyPolicy" && <PolicyPage type="privacy" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "refundPolicy" && <PolicyPage type="refunds" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "communityPolicy" && <PolicyPage type="community" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "cookiePolicy" && <PolicyPage type="cookies" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "youthPolicy" && <PolicyPage type="youth" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "centrePolicy" && <PolicyPage type="centre" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "organiserPolicy" && <PolicyPage type="organiser" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      {page === "coachPolicy" && <PolicyPage type="coach" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}

      <PrivacyConsentPanel navigate={navigate} />

      {!focusedProfileSetupPages[page] && page !== "administrator" && <MobileNav page={page} navigate={navigate} openMenu={() => setMenuOpen(true)} unreadMessageCount={marketplaceUnreadCount} />}
      {menuOpen && <AccountMenu page={page} navigate={navigate} close={() => setMenuOpen(false)} auth={auth} accountProfile={accountProfileView} unreadMessageCount={marketplaceUnreadCount} unreadNotificationCount={unreadNotificationCount} />}
      {notificationPanelOpen && auth.user && (
        <NotificationPanel
          notifications={accountNotifications}
          syncState={notificationSyncState}
          unreadCount={unreadNotificationCount}
          close={() => setNotificationPanelOpen(false)}
          markAllRead={markAllNotificationsRead}
          openNotification={openAccountNotification}
          openAll={() => navigate("notifications")}
        />
      )}
    </main>
  );
}

function ProfileSetupShellHeader({ page, navigate, auth, unreadNotificationCount = 0, openNotifications }) {
  const config = focusedProfileSetupPages[page] || focusedProfileSetupPages.profileSetup;
  const Icon = config.icon;
  return (
    <header className="profile-setup-shell-header">
      <button className="profile-setup-shell-brand" type="button" onClick={() => navigate("home")} aria-label="Badminta home">
        <img src={brandLogo} alt="Badminta" width="448" height="144" decoding="sync" fetchPriority="high" />
      </button>
      <div className="profile-setup-shell-context" aria-label={config.label}>
        <Icon size={18} />
        <span>{config.label}</span>
      </div>
      <div className="profile-setup-shell-actions">
        {auth.user && (
          <NotificationBellButton
            unreadCount={unreadNotificationCount}
            onClick={openNotifications}
            compact
          />
        )}
        <button className="profile-setup-shell-exit" type="button" onClick={() => navigate(config.exitPage)} aria-label="Save this draft and exit to account">
          <span>Save and exit</span>
          <X size={18} />
        </button>
      </div>
    </header>
  );
}

function AdministratorShellHeader({ navigate, auth, unreadNotificationCount = 0, openNotifications }) {
  return (
    <header className="administrator-shell-header">
      <button type="button" className="administrator-shell-brand" onClick={() => navigate("administrator")} aria-label="Badminta administrator home">
        <img src={brandLogo} alt="Badminta" width="448" height="144" decoding="sync" fetchPriority="high" />
        <span>Administrator</span>
      </button>
      <div className="administrator-shell-actions">
        {auth.user && <NotificationBellButton unreadCount={unreadNotificationCount} onClick={openNotifications} compact />}
        <button type="button" onClick={() => navigate("home")}>Marketplace <ExternalLink size={15} /></button>
        <span><ShieldCheck size={16} /> Secure owner access</span>
      </div>
    </header>
  );
}

function NotificationBellButton({ unreadCount = 0, onClick, compact = false }) {
  const label = unreadCount
    ? `Open notifications, ${unreadCount} unread`
    : "Open notifications";
  return (
    <button
      type="button"
      className={`notification-bell${compact ? " compact" : ""}`}
      onClick={onClick}
      aria-label={label}
      title="Notifications"
    >
      <Bell size={compact ? 20 : 21} />
      {unreadCount > 0 && (
        <b aria-hidden="true">{unreadCount > 99 ? "99+" : unreadCount}</b>
      )}
    </button>
  );
}

const notificationCategoryLabels = {
  message: "Message",
  membership: "Membership",
  coaching: "Coaching",
  session: "Session",
  booking: "Booking",
  payment: "Payment",
  trust: "Trust and safety",
  account: "Account"
};

function NotificationRows({ notifications = [], openNotification }) {
  return (
    <div className="notification-list">
      {notifications.map((notification) => {
        const Icon = notificationIconFor(notification.category);
        return (
          <button
            type="button"
            key={notification.id}
            className={`notification-row${notification.read ? "" : " unread"}`}
            onClick={() => openNotification(notification)}
          >
            <span className={`notification-row-icon ${notification.category}`}><Icon size={19} /></span>
            <span className="notification-row-copy">
              <span className="notification-row-title">
                <strong>{notification.title}</strong>
                {!notification.read && <i aria-label="Unread notification" />}
              </span>
              <span>{notification.body}</span>
              <small>
                {notificationCategoryLabels[notification.category] || "Account"}
                <b aria-hidden="true">·</b>
                {notificationRelativeTime(notification.createdAt)}
              </small>
            </span>
            <ChevronRight size={17} />
          </button>
        );
      })}
    </div>
  );
}

function NotificationPanel({ notifications = [], syncState, unreadCount = 0, close, markAllRead, openNotification, openAll }) {
  const recentNotifications = notifications.slice(0, 8);
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [close]);
  return (
    <div className="notification-overlay">
      <button type="button" className="notification-backdrop" onClick={close} aria-label="Close notifications" />
      <aside className="notification-panel" role="dialog" aria-modal="true" aria-label="Notifications">
        <header>
          <div>
            <p>Updates</p>
            <h2>Notifications</h2>
          </div>
          <button type="button" className="notification-close" onClick={close} aria-label="Close notifications"><X size={20} /></button>
        </header>
        <div className="notification-panel-tools">
          <span>{unreadCount ? `${unreadCount} unread` : "You're all caught up"}</span>
          {unreadCount > 0 && <button type="button" onClick={markAllRead}><Check size={16} /> Mark all read</button>}
        </div>
        <div className="notification-panel-body">
          <div className="notification-sync-slot">
            {syncState?.status === "error" && <p className="notification-sync-error">{syncState.message}</p>}
          </div>
          {syncState?.status === "loading" && !recentNotifications.length ? (
            <div className="notification-loading"><Loader2 size={20} className="spin" /> Loading notifications</div>
          ) : recentNotifications.length ? (
            <NotificationRows notifications={recentNotifications} openNotification={openNotification} />
          ) : (
            <div className="notification-empty">
              <span><Bell size={24} /></span>
              <strong>No notifications yet</strong>
              <p>Messages, bookings, sessions, memberships, coaching, payments and support updates will appear here.</p>
            </div>
          )}
        </div>
        <footer>
          <button type="button" onClick={openAll}>
            View all notifications
            <ChevronRight size={17} />
          </button>
        </footer>
      </aside>
    </div>
  );
}

function NotificationsPage({ notifications = [], syncState, unreadCount = 0, markAllRead, openNotification }) {
  const [filter, setFilter] = useState("all");
  const visibleNotifications = filter === "unread"
    ? notifications.filter((notification) => !notification.read)
    : notifications;
  return (
    <section className="page notifications-page">
      <header className="notifications-page-head">
        <div>
          <p>Account activity</p>
          <h1>Notifications</h1>
          <span>Important updates from every Badminta role, in one place.</span>
        </div>
        {unreadCount > 0 && <button type="button" onClick={markAllRead}><Check size={17} /> Mark all read</button>}
      </header>
      <div className="notifications-summary" aria-label="Notification summary">
        <span><strong>{notifications.length}</strong>All updates</span>
        <span><strong>{unreadCount}</strong>Unread</span>
        <span><strong>{notifications.filter((notification) => notification.category === "payment").length}</strong>Payments</span>
      </div>
      <div className="notifications-toolbar">
        <div className="notifications-filter" role="group" aria-label="Filter notifications">
          <button type="button" className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>All</button>
          <button type="button" className={filter === "unread" ? "active" : ""} onClick={() => setFilter("unread")}>Unread</button>
        </div>
        <span>{syncState?.status === "ready" ? "Live updates on" : syncState?.status === "error" ? "Refresh unavailable" : "Syncing"}</span>
      </div>
      {syncState?.status === "error" && <p className="notification-sync-error">{syncState.message}</p>}
      {syncState?.status === "loading" && !visibleNotifications.length ? (
        <div className="notifications-page-state"><Loader2 size={22} className="spin" /> Loading notifications</div>
      ) : visibleNotifications.length ? (
        <NotificationRows notifications={visibleNotifications} openNotification={openNotification} />
      ) : (
        <div className="notifications-page-state">
          <span><Bell size={26} /></span>
          <strong>{filter === "unread" ? "No unread notifications" : "No notifications yet"}</strong>
          <p>{filter === "unread" ? "New important updates will be highlighted here." : "Your messages, bookings, memberships, sessions, payments and support outcomes will appear here."}</p>
        </div>
      )}
    </section>
  );
}

function Header({ searchCriteria, setSearchCriteria, updateSearchLocation, mode, openMode, submitSearch, activateNearbySearch, osmCentreStatus, auth, accountProfile, unreadMessageCount = 0, unreadNotificationCount = 0, openNotifications, openMenu, navigate }) {
  const [activePanel, setActivePanel] = useState(null);
  const [whereDraft, setWhereDraft] = useState(searchCriteria.location.label);
  const [osmResults, setOsmResults] = useState([]);
  const [osmStatus, setOsmStatus] = useState("idle");
  const [osmError, setOsmError] = useState("");
  const selectedTags = playTagOptions.filter((tag) => searchCriteria.tags.includes(tag.id));
  const whenSummary = `${formatDateLabel(searchCriteria.date)}, ${formatTimeLabel(searchCriteria.time)}`;
  const playSummary = selectedTags.length ? selectedTags.map((tag) => tag.short).join(", ") : "Any play";
  const mobilePlaySummary = selectedTags.length > 1 ? `${selectedTags[0].short} +${selectedTags.length - 1}` : playSummary;
  const mobileSummary = `${searchCriteria.location.label} · ${formatDateLabel(searchCriteria.date)} · ${formatTimeLabel(searchCriteria.time)} · ${mobilePlaySummary}`;
  const accountAvatar = getAccountAvatarUrl(accountProfile);
  const rankLocationOption = (option, query) => {
    const compactQuery = compactLocationText(query);
    if (!compactQuery) return option.type === "country" ? 8 : option.type === "city" ? 0 : 2;
    const label = compactLocationText(option.label);
    const cityText = compactLocationText(option.city);
    const suburbText = compactLocationText(option.suburb);
    const countryText = compactLocationText(option.country);
    let score = 80;
    if (cityText === compactQuery) score = Math.min(score, 0);
    if (suburbText === compactQuery) score = Math.min(score, 1);
    if (label === compactQuery) score = Math.min(score, option.type === "city" ? 0 : option.type === "suburb" ? 1 : 6);
    if (label.startsWith(compactQuery)) score = Math.min(score, option.type === "country" ? 20 : 10);
    if (cityText.startsWith(compactQuery) || suburbText.startsWith(compactQuery)) score = Math.min(score, 12);
    if (countryText === compactQuery) score = Math.min(score, option.type === "country" ? 6 : 14);
    return score + (option.type === "country" ? 4 : option.type === "city" ? 0 : 1);
  };
  const filteredLocations = locationOptions
    .filter((option) => {
      const query = compactLocationText(whereDraft);
      const label = compactLocationText(option.label);
      const city = compactLocationText(option.city);
      const country = compactLocationText(option.country);
      return !query
        || label.startsWith(query)
        || (option.type === "city" && city.startsWith(query))
        || (option.type === "country" && country.startsWith(query))
        || compactLocationText(option.countryCode) === query;
    })
    .sort((a, b) => rankLocationOption(a, whereDraft) - rankLocationOption(b, whereDraft) || a.label.localeCompare(b.label))
    .slice(0, 6);
  const mergedLocations = [...filteredLocations, ...osmResults].filter(
    (option, index, list) => list.findIndex((item) => `${item.label}-${item.city}-${item.countryCode}-${item.type}` === `${option.label}-${option.city}-${option.countryCode}-${option.type}`) === index
  ).slice(0, 6);

  const openPanel = (panel) => {
    setWhereDraft(searchCriteria.location.label);
    if (panel === "where") {
      setOsmResults([]);
      setOsmStatus("idle");
      setOsmError("");
    }
    setActivePanel(panel);
  };

  const updateDateTime = (field, value) => {
    setSearchCriteria((current) => ({ ...current, [field]: value }));
  };

  const toggleTag = (tagId) => {
    setSearchCriteria((current) => {
      const exists = current.tags.includes(tagId);
      const tags = exists ? current.tags.filter((item) => item !== tagId) : [...current.tags, tagId];
      return { ...current, tags };
    });
  };

  const clearActiveSearchPanel = () => {
    if (activePanel === "where") {
      setWhereDraft("");
      setOsmResults([]);
      setOsmStatus("idle");
      setOsmError("");
      return;
    }
    if (activePanel === "when") {
      setSearchCriteria((current) => ({ ...current, date: getIsoDate(0), time: "18:30" }));
      return;
    }
    setSearchCriteria((current) => ({ ...current, tags: [] }));
  };

  const chooseLocation = (location) => {
    updateSearchLocation(location);
    setWhereDraft(location.label);
    setOsmError("");
  };

  const resolveDraftLocation = async () => {
    const query = whereDraft.trim();
    if (!query || compactLocationText(query) === compactLocationText(searchCriteria.location.label)) {
      return searchCriteria.location;
    }
    const localMatch = mergedLocations
      .filter((location) => location.id !== searchCriteria.location.id)
      .sort((a, b) => rankLocationOption(a, query) - rankLocationOption(b, query))[0];
    if (localMatch && rankLocationOption(localMatch, query) < 40) {
      chooseLocation(localMatch);
      return normaliseSearchLocationForMap(localMatch, searchCriteria.location.city);
    }
    const results = await searchOpenStreetLocations(query);
    setOsmResults(results);
    setOsmStatus("loaded");
    if (results[0]) {
      chooseLocation(results[0]);
      return normaliseSearchLocationForMap(results[0], searchCriteria.location.city);
    }
    return searchCriteria.location;
  };

  const searchOpenStreetMap = async (chooseFirst = false) => {
    const query = whereDraft.trim();
    if (!query) return;
    setOsmStatus("loading");
    setOsmError("");
    try {
      const results = await searchOpenStreetLocations(query);
      setOsmResults(results);
      setOsmStatus("loaded");
      if (chooseFirst && results[0]) chooseLocation(results[0]);
      if (!results.length) setOsmError("No suburb or city found for that search. Try adding the country or a nearby city.");
    } catch (error) {
      setOsmStatus("error");
      setOsmError(error.message || "Location search failed. Try again.");
    }
  };

  const runSearch = async () => {
    let resolvedLocation = null;
    if (activePanel === "where") {
      setOsmStatus("loading");
      setOsmError("");
      try {
        resolvedLocation = await resolveDraftLocation();
      } catch (error) {
        setOsmStatus("error");
        setOsmError(error.message || "Location search failed. Try again.");
        return;
      }
    }
    setActivePanel(null);
    await submitSearch(resolvedLocation ? { location: resolvedLocation } : {});
  };

  const useNearbyFromHeader = async () => {
    setActivePanel(null);
    await activateNearbySearch();
  };

  return (
    <header className={activePanel ? "air-header search-open" : "air-header"}>
      <div className="header-line">
        <button className="brand-mark" type="button" onClick={() => navigate("home")} aria-label="Badminta home">
          <img src={brandLogo} alt="Badminta" width="448" height="144" decoding="sync" fetchPriority="high" />
        </button>

        <nav className="mode-tabs" aria-label="Marketplace modes">
          {topModes.map(([key, label, Icon]) => (
            <button
              type="button"
              key={key}
              className={mode === key ? "active" : ""}
              onClick={() => openMode(key)}
            >
              <Icon size={22} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="header-actions">
          <button type="button" className="host-link" onClick={() => navigate("solutionIndex")}>
            Solutions
          </button>
          {auth.user && (
            <NotificationBellButton
              unreadCount={unreadNotificationCount}
              onClick={openNotifications}
            />
          )}
          {auth.user ? (
            <button type="button" className="signup-pill" onClick={() => navigate("account")}>
              {accountProfile.fullName || "Account"}
            </button>
          ) : (
            <>
              <button type="button" className="login-link" onClick={() => navigate("login")}>
                Log in
              </button>
              <button type="button" className="signup-pill" onClick={() => navigate("signup")}>
                Sign up
              </button>
            </>
          )}
          <button type="button" className="menu-pill" onClick={openMenu}>
            <Menu size={18} />
            {accountAvatar ? <img className="menu-account-photo" src={accountAvatar} alt="" /> : <span className="menu-account-icon" aria-hidden="true"><UserCircle size={21} /></span>}
            {unreadMessageCount > 0 && <b className="nav-unread-badge" aria-label={`${unreadMessageCount} unread messages`}>{Math.min(99, unreadMessageCount)}</b>}
          </button>
        </div>

        <div className="mobile-header-actions">
          {auth.user && (
            <NotificationBellButton
              unreadCount={unreadNotificationCount}
              onClick={openNotifications}
              compact
            />
          )}
          <button
            type="button"
            className="mobile-menu-button"
            onClick={openMenu}
            aria-label="Open account and navigation menu"
            title="Open menu"
          >
            <Menu size={22} />
            {unreadMessageCount > 0 && <b className="nav-unread-badge" aria-label={`${unreadMessageCount} unread messages`}>{Math.min(99, unreadMessageCount)}</b>}
          </button>
        </div>
      </div>

      <div className="search-pod" role="search" aria-label="Search Badminta">
        <button type="button" className={`search-segment search-where ${activePanel === "where" ? "active" : ""}`} onPointerDown={() => openPanel("where")} onClick={() => openPanel("where")}>
          <span className="search-label">Where</span>
          <strong className="search-value">{searchCriteria.location.label}</strong>
          <span className="mobile-search-title">Search badminton</span>
          <strong className="mobile-search-copy">{mobileSummary}</strong>
        </button>
        <button type="button" className={`search-segment ${activePanel === "when" ? "active" : ""}`} onPointerDown={() => openPanel("when")} onClick={() => openPanel("when")}>
          <span>When</span>
          <strong>{whenSummary}</strong>
        </button>
        <button type="button" className={`search-segment ${activePanel === "play" ? "active" : ""}`} onPointerDown={() => openPanel("play")} onClick={() => openPanel("play")}>
          <span>Play</span>
          <strong>{playSummary}</strong>
        </button>
        <button type="button" className="search-submit" onClick={runSearch} aria-label="Search">
          <Search size={19} />
        </button>
      </div>
      {activePanel && (
        <div className="search-popover" role="dialog" aria-label="Search options">
          <div className="search-popover-head">
            <div>
              <span>{activePanel === "where" ? "Choose a location" : activePanel === "when" ? "Choose when to play" : "What are you looking for?"}</span>
              <strong>{activePanel === "where" ? "Search any city or suburb worldwide" : activePanel === "when" ? "Set a date and preferred start time" : "Select one or more result types"}</strong>
            </div>
            <button type="button" onClick={() => setActivePanel(null)} aria-label="Close search"><X size={19} /></button>
          </div>
          <div className="search-popover-tabs" aria-label="Search sections">
            {[
              ["where", "Where"],
              ["when", "When"],
              ["play", "Play"]
            ].map(([key, label]) => (
              <button type="button" key={key} className={activePanel === key ? "active" : ""} onClick={() => setActivePanel(key)}>
                {label}
              </button>
            ))}
          </div>

          {activePanel === "where" && (
            <div className="search-panel">
              <label className="search-input-line">
                <Search size={18} />
                <input
                  value={whereDraft}
                  onChange={(event) => {
                    setWhereDraft(event.target.value);
                    setOsmStatus("idle");
                    setOsmError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      searchOpenStreetMap(true);
                    }
                  }}
                  placeholder="Search country, city or suburb"
                  autoFocus
                />
                <button type="button" onClick={() => searchOpenStreetMap(false)} disabled={osmStatus === "loading"} aria-label="Find this location">
                  {osmStatus === "loading" ? <Loader2 className="spin" size={18} /> : <ChevronRight size={18} />}
                </button>
              </label>
              <div className="osm-search-row">
                <button type="button" onClick={useNearbyFromHeader} disabled={osmCentreStatus === "loading"}>
                  <Navigation size={17} />
                  {osmCentreStatus === "loading" ? "Finding nearby" : "Use my location"}
                </button>
                <span>Or enter a place above and press Enter.</span>
              </div>
              <p className="search-result-label">{whereDraft.trim() ? "Suggested locations" : "Popular nearby places"}</p>
              <div className="location-results">
                {mergedLocations.map((location) => (
                  <button
                    type="button"
                    key={location.id}
                    className={searchCriteria.location.id === location.id ? "active" : ""}
                    onClick={() => chooseLocation(location)}
                  >
                    <MapPin size={18} />
                    <span>
                      <strong>{location.label}</strong>
                      <em>{location.hint}{location.type === "suburb" && !location.hint.includes(location.city) ? ` · ${location.city}` : ""}</em>
                    </span>
                  </button>
                ))}
              </div>
              {osmError && <p className="search-message">{osmError}</p>}
            </div>
          )}

          {activePanel === "when" && (
            <div className="search-panel when-panel">
              <div className="date-time-grid">
                <label>
                  <span>Date</span>
                  <input type="date" value={searchCriteria.date} onChange={(event) => updateDateTime("date", event.target.value)} />
                </label>
                <label>
                  <span>Time</span>
                  <input type="time" value={searchCriteria.time} onChange={(event) => updateDateTime("time", event.target.value)} />
                </label>
              </div>
              <div className="quick-date-row">
                {[
                  ["Today", getIsoDate(0), "18:30"],
                  ["Tomorrow", getIsoDate(1), "19:00"],
                  ["This weekend", nextSaturdayIso(), "10:00"]
                ].map(([label, date, time]) => (
                  <button type="button" key={label} onClick={() => setSearchCriteria((current) => ({ ...current, date, time }))}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activePanel === "play" && (
            <div className="search-panel">
              <div className="play-chip-grid">
                {playTagOptions.map(({ id, label, Icon }) => (
                  <button type="button" key={id} className={searchCriteria.tags.includes(id) ? "active" : ""} onClick={() => toggleTag(id)}>
                    <Icon size={18} />
                    <span>{label}</span>
                    {searchCriteria.tags.includes(id) && <Check size={16} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="search-popover-actions">
            <button type="button" onClick={clearActiveSearchPanel}>
              {activePanel === "where" ? "Clear input" : activePanel === "when" ? "Reset" : "Clear selection"}
            </button>
            <button type="button" onClick={runSearch}>{osmCentreStatus === "loading" ? "Searching" : "Show results"}</button>
          </div>
        </div>
      )}
    </header>
  );
}

function GlobalHomeHero({ activeLocation, navigate }) {
  const heroMedia = getLandingMedia("home", "centre");
  return (
    <section className="global-home-hero">
      <img
        src={heroMedia.url}
        srcSet="/media/badminta-home-hero-800.webp 800w, /media/badminta-home-hero-1400.webp 1400w"
        sizes="100vw"
        width="1400"
        height="2100"
        alt=""
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
      <div className="global-home-overlay" />
      <div className="global-home-copy">
        <p><Globe2 size={16} /> Worldwide badminton marketplace</p>
        <h1>Badminta</h1>
        <span>Find where to play, join social games, publish profiles, manage centres, sell events and move badminton operations into one platform.</span>
        <div className="global-home-actions">
          <button type="button" onClick={() => navigate("filterHub", { activeFilter: "nearby", activeResultType: "overview" })}>
            <Search size={17} /> Find badminton near me
          </button>
          <button type="button" onClick={() => navigate("solutionIndex")}>
            <Sparkles size={17} /> See solutions
          </button>
        </div>
        <div className="global-home-stats" aria-label="Badminta marketplace coverage">
          {[
            [globalCentreTotalCount.toLocaleString(), "centre records"],
            [globalCentreCountryRows.length.toLocaleString(), "countries"],
            [activeLocation, "active search area"]
          ].map(([value, label]) => (
            <strong key={label}><span>{value}</span>{label}</strong>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeSolutionBand({ navigate }) {
  return (
    <section className="home-solution-band" aria-label="Badminta solutions">
      <div className="home-solution-head">
        <p>One platform, different jobs</p>
        <h2>Everything the badminton community needs to play, book, organise and grow.</h2>
        <span>Centres can run bookings and memberships. Organisers can fill sessions. Coaches can build trust. Players can discover where to play. Events can sell registrations.</span>
      </div>
      <div className="home-solution-grid">
        {Object.values(solutionPages).map(({ page, label, heroText, Icon, primary }) => (
          <article key={page}>
            <Icon size={22} />
            <strong>{label}</strong>
            <p>{heroText}</p>
            <div>
              <button type="button" onClick={() => navigate(page)}>Learn more</button>
              <button type="button" onClick={() => navigate(primary[1])}>{primary[0]}</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DeferredImage({ src, alt = "", className = "", rootMargin = "240px", onError }) {
  const imageRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!imageRef.current || isVisible) return undefined;
    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setIsVisible(true);
      observer.disconnect();
    }, { rootMargin });
    observer.observe(imageRef.current);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <img
      ref={imageRef}
      className={className}
      src={isVisible ? src : "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="}
      alt={isVisible ? alt : ""}
      loading="lazy"
      decoding="async"
      fetchPriority="low"
      onError={onError}
    />
  );
}

const playerProfileInitials = (name = "Player") => {
  const words = String(name || "Player").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "P";
  if (words.length === 1) return Array.from(words[0]).slice(0, 2).join("").toUpperCase();
  return `${Array.from(words[0])[0] || ""}${Array.from(words.at(-1))[0] || ""}`.toUpperCase();
};

const playerProfileColourTone = (name = "Player") => {
  const value = Array.from(String(name || "Player").toLowerCase());
  const hash = value.reduce((total, character, index) => total + character.codePointAt(0) * (index + 3), 0);
  return hash % 6;
};

function PlayerInitialsMedia({ name = "Player", className = "" }) {
  const initials = playerProfileInitials(name);
  return (
    <div
      className={["player-initials-media", className].filter(Boolean).join(" ")}
      data-tone={playerProfileColourTone(name)}
      role="img"
      aria-label={`${name || "Player"} profile initials ${initials}`}
    >
      <span>{initials}</span>
    </div>
  );
}

function PlayerCardMedia({ src = "", name = "Player", className = "", rootMargin = "80px" }) {
  const safeSrc = getAllowedUserMediaUrl(src);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [safeSrc]);

  if (!safeSrc || failed) return <PlayerInitialsMedia name={name} className={className} />;
  return <DeferredImage src={safeSrc} alt={name} className={className} rootMargin={rootMargin} onError={() => setFailed(true)} />;
}

function LandingMediaPanel({ title, text, media, eyebrow, reverse = false }) {
  return (
    <article className={`landing-media-panel${reverse ? " reverse" : ""}`}>
      <div>
        <p>{eyebrow}</p>
        <h3>{title}</h3>
        <span>{text}</span>
      </div>
      <DeferredImage src={media.url} alt={media.alt || ""} />
    </article>
  );
}

function HomeLandingSeoSections({ navigate }) {
  return (
    <>
      <section className="landing-seo-band">
        <div className="section-title-row">
          <div>
            <p>Worldwide badminton growth</p>
            <h2>Local discovery connected to real booking, profile and payment tools.</h2>
            <span>Badminta is built for searches like badminton centre near me, social badminton groups, badminton coach, court hire, badminton events and players near me in every country, city and suburb.</span>
          </div>
          <button type="button" onClick={() => navigate("solutionIndex")}>Explore solutions</button>
        </div>
        <div className="landing-media-grid">
          <LandingMediaPanel
            eyebrow="Discovery"
            title="Players arrive from local search with intent."
            text="The home page introduces the worldwide platform, then city and suburb pages help players compare real centres, sessions, coaches and events near them."
            media={getLandingMedia("overview")}
          />
          <LandingMediaPanel
            eyebrow="Operations"
            title="Centres and organisers need bookings, payments and members in one place."
            text="Badminta connects court bookings, memberships, paid sessions, event registrations, reviews, support and verified ownership."
            media={getLandingMedia("centres")}
            reverse
          />
          <LandingMediaPanel
            eyebrow="Community"
            title="Profiles should help people decide."
            text="Players, coaches, centres and groups need useful public pages with availability, prices, media, ratings, recommendations and clear actions."
            media={getLandingMedia("organisers")}
          />
        </div>
      </section>

      <SolutionFaqSection
        title="Badminta platform questions"
        intro="Helpful answers for players, centre owners, coaches and organisers deciding how Badminta can help them play, book or manage badminton."
        items={solutionOverviewFaq}
      />
    </>
  );
}

function SolutionOverviewPage({ navigate }) {
  const media = getLandingMedia("overview", "general");
  return (
    <section className="page solution-page solution-overview-page">
      <section className="solution-hero">
        <img src={media.url} alt="" loading="eager" decoding="async" fetchPriority="high" />
        <div className="solution-hero-overlay" />
        <div className="solution-hero-copy">
          <p><Sparkles size={16} /> Badminta solutions</p>
          <h1>Sell, organise and discover badminton in every city.</h1>
          <span>Badminta helps people find badminton locally, then gives centres, organisers and coaches the tools to turn interest into bookings, sessions and payments.</span>
          <div className="solution-hero-actions">
            <button type="button" onClick={() => navigate("signup")}>Start free</button>
            <button type="button" onClick={() => navigate("filterHub", { activeFilter: "nearby", activeResultType: "overview" })}>Explore marketplace</button>
          </div>
        </div>
      </section>

      <section className="solution-metric-strip" aria-label="Marketplace scale">
        {[
          [globalCentreTotalCount.toLocaleString(), "worldwide centre records"],
          [globalCentreCountryRows.length.toLocaleString(), "countries indexed"],
          ["5", "core community roles"],
          ["1", "shared trust and payment layer"]
        ].map(([value, label]) => (
          <article key={label}><strong>{value}</strong><span>{label}</span></article>
        ))}
      </section>

      <section className="solution-card-grid">
        {Object.values(solutionPages).map(({ page, label, heroText, Icon, stats }) => (
          <article key={page} className="solution-card">
            <span className="solution-card-icon"><Icon size={24} /></span>
            <h2>{label}</h2>
            <p>{heroText}</p>
            <div>
              {stats.map(([value, labelText]) => <em key={labelText}><b>{value}</b>{labelText}</em>)}
            </div>
            <button type="button" onClick={() => navigate(page)}>Open solution</button>
          </article>
        ))}
      </section>

      <section className="solution-operating-band">
        <div>
          <p>Business model</p>
          <h2>Badminta makes money by improving operations, not by being only a directory.</h2>
        </div>
        <div className="solution-operating-grid">
          {[
            [CreditCard, "Booking fees", "Take a platform fee when centres sell court time through Badminta."],
            [UsersRound, "Session fees", "Take a small fee when players pay to join organiser-led social games."],
            [Trophy, "Coach bookings", "Support coach lesson deposits, packages and profile upgrades."],
            [WalletCards, "Centre SaaS", "Charge centres for court inventory, memberships, reports, staff tools and payouts."],
            [CalendarDays, "Events", "Charge ticketing or registration fees for tournaments, clinics and open days."],
            [BadgeCheck, "Verified growth", "Give complete, owner-managed profiles stronger visibility without weakening organic discovery."]
          ].map(([Icon, title, text]) => (
            <article key={title}><Icon size={22} /><strong>{title}</strong><span>{text}</span></article>
          ))}
        </div>
      </section>

      <section className="solution-media-showcase">
        <div className="section-title-row">
          <div>
            <p>Find badminton near you</p>
            <h2>Start nearby, then move straight into the right action.</h2>
            <span>Explore centres, groups, coaches, players and events by country, city or suburb, with the details needed to decide what to do next.</span>
          </div>
          <button type="button" onClick={() => navigate("signup")}>Create account</button>
        </div>
        <div className="solution-media-grid">
          {[
            ["Find", "Badminton centres, groups, players, coaches and events near the user.", getLandingMedia("home")],
            ["Operate", "Court inventory, social sessions, coaching, events, payments and memberships.", getLandingMedia("centres")],
            ["Confidence", "Public profiles, media, reviews, verification and support.", getLandingMedia("players")]
          ].map(([title, text, item]) => (
            <article key={title}>
              <img src={item.url} alt={item.alt || ""} loading="lazy" decoding="async" />
              <div><strong>{title}</strong><span>{text}</span></div>
            </article>
          ))}
        </div>
      </section>

      <SolutionFaqSection
        title="Badminta solution FAQs"
        intro="Answers that help players, centres, coaches and organisers understand what the platform does."
        items={solutionOverviewFaq}
      />
    </section>
  );
}

function PlayerSolutionLandingFallback() {
  return (
    <article className="page player-world-page" aria-busy="true">
      <section className="player-world-hero" aria-labelledby="player-world-loading-title">
        <img
          src="/media/player-landing-hero.jpg"
          alt=""
          width="1200"
          height="1800"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="player-world-hero-shade" />
        <div className="player-world-hero-copy">
          <p className="player-world-eyebrow"><Globe2 size={16} /> For badminton players worldwide</p>
          <h1 id="player-world-loading-title">Find badminton players and games near you.</h1>
          <p className="player-world-hero-intro">Badminta brings local players, social groups, centres, coaches, events, bookings and payments into one clear playing life.</p>
        </div>
      </section>
    </article>
  );
}

function SolutionDetailPage({ kind, navigate, auth, onPlayerSignupIntent, onSignupIntent, trackFunnelEvent }) {
  const config = solutionPages[kind] || solutionPages.players;
  if (kind === "players") {
    return (
      <React.Suspense fallback={<PlayerSolutionLandingFallback />}>
        <PlayerSolutionLanding
          navigate={navigate}
          videoSrc={playerLandingVideo.src}
          auth={auth}
          onPlayerSignupIntent={onPlayerSignupIntent}
          trackFunnelEvent={trackFunnelEvent}
        />
      </React.Suspense>
    );
  }

  const related = Object.values(solutionPages).filter((item) => item.page !== config.page);
  const mediaSet = getSolutionMarketingMediaSet(kind, config.mediaType, 5);
  const roleBySolutionKind = { centres: "centre", organisers: "organiser", coaches: "coach" };
  const solutionRole = roleBySolutionKind[kind] || "";
  const openSolutionSignup = () => {
    if (!solutionRole) {
      navigate(config.primary[1]);
      return;
    }
    const intent = signupRoleIntents[solutionRole];
    onSignupIntent?.({ ...intent, authenticated: Boolean(auth?.user) });
    navigate(auth?.user ? intent.returnPage : "signup", auth?.user ? {} : {
      signupRole: intent.role,
      signupSource: intent.sourcePage
    });
  };
  const openSolutionSecondary = () => {
    if (!auth?.user && solutionRole) {
      openSolutionSignup();
      return;
    }
    navigate(config.secondary[1]);
  };
  const openPricingPlan = ({ tier }) => {
    if (tier === "pro" && auth?.user && solutionRole) {
      navigate({
        centre: "centreDashboard",
        organiser: "organiser",
        coach: "coachDashboard"
      }[solutionRole] || "account");
      return;
    }
    openSolutionSignup();
  };
  const fallbackMedia = mediaSet[0] || getLandingMedia(kind, config.mediaType);
  const RoleIcon = config.Icon;

  return (
    <React.Suspense
      fallback={(
        <article className={`page role-world-page role-world-${kind}`} aria-busy="true">
          <section className="role-world-hero">
            <img src={fallbackMedia.url} alt="" loading="eager" decoding="async" fetchPriority="high" />
            <div className="role-world-hero-shade" />
            <div className="role-world-hero-copy">
              <p className="role-world-eyebrow"><RoleIcon size={17} /> {config.navLabel}</p>
              <h1>{config.heroTitle}</h1>
              <p className="role-world-hero-intro">{config.heroText}</p>
            </div>
          </section>
        </article>
      )}
    >
      <RoleSolutionLanding
        kind={kind}
        config={config}
        mediaSet={mediaSet}
        related={related}
        solutionRole={solutionRole}
        authenticated={Boolean(auth?.user)}
        faqItems={[...config.faq, ...profileSubscriptionFaq(solutionRole)]}
        navigate={navigate}
        onPrimaryAction={openSolutionSignup}
        onSecondaryAction={openSolutionSecondary}
        onPricingPlan={openPricingPlan}
      />
    </React.Suspense>
  );
}


function SolutionFaqSection({ title, intro, items = [] }) {
  if (!items.length) return null;
  return (
    <section className="solution-faq-section">
      <div className="section-title-row">
        <div>
          <p>FAQ</p>
          <h2>{title}</h2>
          <span>{intro}</span>
        </div>
      </div>
      <div className="solution-faq-list">
        {items.map(([question, answer]) => (
          <details key={question}>
            <summary><CircleHelp size={18} /> {question}</summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function CategoryRail({ activeFilter, openFilter }) {
  return (
    <section className="category-rail" aria-label="Marketplace filters">
      {categoryFilters.map(([key, label, Icon, target]) => (
        <button
          type="button"
          key={key}
          className={activeFilter === key ? "active" : ""}
          onClick={() => openFilter(key, target)}
        >
          <Icon size={20} />
          <span>{label}</span>
        </button>
      ))}
    </section>
  );
}

function PageCrumbs({ page, navigate, city, searchCriteria, activeFilter, selectedVenue, selectedGroup, selectedPlayer, selectedCoach, selectedEvent, blogPost, reviewContext, paymentContext, bookingType }) {
  const crumbs = buildBreadcrumbs(page, { city, searchCriteria, activeFilter, selectedVenue, selectedGroup, selectedPlayer, selectedCoach, selectedEvent, blogPost, reviewContext, paymentContext, bookingType });
  if (!crumbs.length) return null;

  return (
    <nav className="page-crumbs" aria-label="Breadcrumb">
      <ol>
        {crumbs.map((crumb, index) => {
          const Icon = crumb.icon;
          return (
            <li key={`${crumb.label}-${index}`}>
              {index > 0 && <ChevronRight className="crumb-separator" size={14} aria-hidden="true" />}
              {crumb.current ? (
                <span className="crumb-current" aria-current="page">
                  {Icon && <Icon size={15} />}
                  <span>{crumb.label}</span>
                </span>
              ) : (
                <button type="button" onClick={() => navigate(crumb.page || "home", crumb.payload || {})}>
                  {Icon && <Icon size={15} />}
                  <span>{crumb.label}</span>
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SearchSummary({ searchCriteria }) {
  const tags = playTagOptions.filter((tag) => searchCriteria.tags.includes(tag.id));
  return (
    <div className="search-summary-row" aria-label="Active search">
      <span><MapPin size={15} /> {searchCriteria.location.label}</span>
      <span><CalendarDays size={15} /> {formatDateLabel(searchCriteria.date)}</span>
      <span><Clock3 size={15} /> {formatTimeLabel(searchCriteria.time)}</span>
      <span><ListFilter size={15} /> {tags.length ? tags.map((tag) => tag.short).join(", ") : "Any play"}</span>
    </div>
  );
}

function OsmStatusNotice() {
  return null;
}

function CentreEmptyState({ searchCriteria, status, error, navigate }) {
  const isLoading = status === "loading";
  const centreDraft = createCentreDraftFromSearch(searchCriteria);
  return (
    <article className={isLoading ? "centre-empty-state loading" : "centre-empty-state"} aria-live="polite" aria-busy={isLoading}>
      {isLoading ? <SearchingIndicator /> : <Map size={22} />}
      <div>
        <strong>{isLoading ? "Searching badminton listings" : "No badminton centres found"}</strong>
        <p>
          {isLoading
            ? `Checking court-hire centre listings around ${searchCriteria.location.label}.`
            : error || `We could not find badminton centres around ${searchCriteria.location.label}. Try a nearby suburb or a broader city search.`}
        </p>
        {!isLoading && (
          <>
            <div className="centre-empty-actions">
              <button type="button" onClick={() => navigate("claimCentre", { venue: centreDraft })}>
                <Plus size={17} /> Add a centre
              </button>
              <button type="button" onClick={() => navigate("createGroup")}>
                Create a social group
              </button>
            </div>
            <div className="centre-empty-steps" aria-label="Add centre steps">
              {[
                ["1", "Add the centre name, suburb and contact details."],
                ["2", "Verify ownership or submit it for Badminta review."],
                ["3", "Add courts, prices, photos and booking rules."],
                ["4", "Publish the listing so players can find and book it."]
              ].map(([number, text]) => (
                <span key={number}><b>{number}</b>{text}</span>
              ))}
            </div>
          </>
        )}
      </div>
    </article>
  );
}

function SearchingIndicator() {
  return (
    <div className="searching-indicator" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );
}

function locationTitle(searchCriteria, fallbackCity) {
  return searchCriteria.location?.label || fallbackCity;
}

function CommunityEmptyState({ type = "groups", title, text, primary, secondary, navigate, icon: Icon = UsersRound, compact = false }) {
  return (
    <article className={["community-empty-state", compact ? "compact" : ""].filter(Boolean).join(" ")}>
      <div className="community-empty-icon"><Icon size={22} /></div>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
        <div className="community-empty-actions">
          {primary && <button type="button" onClick={() => navigate(primary.target, primary.payload || {})}>{primary.label}</button>}
          {secondary && <button type="button" onClick={() => navigate(secondary.target, secondary.payload || {})}>{secondary.label}</button>}
        </div>
      </div>
    </article>
  );
}

const emptySocialGroupState = (location) => ({
  type: "social groups",
  icon: UsersRound,
  title: `No social groups listed in ${location} yet`,
  text: "Social groups are organised games at badminton centres. The organiser chooses the venue, date, level, capacity and price per player so the court cost is shared by people who join.",
  primary: { label: "Create the first group", target: "createGroup" },
  secondary: { label: "Explore organiser tools", target: "solutionOrganisers" }
});

const emptyPlayerState = (location) => ({
  type: "players",
  icon: UserCircle,
  title: `No player profiles listed in ${location} yet`,
  text: "No players have published a Badminta profile here yet. Be the first player to add your skill level, availability and rating profile.",
  primary: { label: "Create player profile", target: "profileSetup" },
  secondary: { label: "Explore player profiles", target: "solutionPlayers" }
});

const emptyTrainerState = (location) => ({
  type: "trainers",
  icon: ClipboardCheck,
  title: `No trainers or coaches listed in ${location} yet`,
  text: "No coach or trainer profiles are available for this search yet. Be the first trainer to sign up and list coaching availability.",
  primary: { label: "Create coach profile", target: "coachProfile" },
  secondary: { label: "Explore coach tools", target: "solutionCoaches" }
});

const emptyEventState = (location) => ({
  type: "events",
  icon: CalendarDays,
  title: `No badminton events listed in ${location} yet`,
  text: "No tournaments, clinics or public events have been created for this area yet. Create the first event when details are confirmed.",
  primary: { label: "Create first event", target: "createEvent" },
  secondary: { label: "Manage my events", target: "eventManager" }
});

function MarketplaceTypeGuide({ navigate, compact = false }) {
  return (
    <section className={["marketplace-type-guide", compact ? "compact" : ""].filter(Boolean).join(" ")} aria-label="Centre and social group difference">
      <button type="button" className="type-guide-card centre" onClick={() => navigate("results")}>
        <Building2 size={22} />
        <span>Centre</span>
        <strong>Book a court</strong>
        <p>Venue listing with address, court count, opening hours, prices and booking details.</p>
      </button>
      <button type="button" className="type-guide-card group" onClick={() => navigate("groupsIndex")}>
        <UsersRound size={22} />
        <span>Social group</span>
        <strong>Join an organised game</strong>
        <p>Players or organisers arrange a session at a centre, set the level, split the cost and let others join.</p>
      </button>
    </section>
  );
}

const filterSearchText = (...values) => values
  .flat(Infinity)
  .filter(Boolean)
  .map((value) => typeof value === "object" ? Object.values(value).join(" ") : String(value))
  .join(" ")
  .toLowerCase();

const textHasAny = (text, terms) => terms.some((term) => text.includes(term));

const isLateText = (text) => /late|open until|10\s*:?\s*00?\s*pm|11\s*:?\s*00?\s*pm|midnight|22:|23:|10pm|11pm/i.test(text);

const entityMatchesFilter = (text, filterKey, kind = "") => {
  const haystack = String(text || "").toLowerCase();
  if (!filterKey || filterKey === "nearby") return true;
  if (filterKey === "verified") return textHasAny(haystack, ["badminta verified", "verified", "approved", "owner-managed", "claimed centre owner"]);
  if (filterKey === "beginner") return textHasAny(haystack, ["beginner", "new player", "friendly", "social", "kids", "junior", "family"]);
  if (filterKey === "advanced") return textHasAny(haystack, ["advanced", "competitive", "competition", "tournament", "league", "ladder", "match play"]);
  if (filterKey === "women") return textHasAny(haystack, ["women", "woman", "female", "ladies", "girls", "women friendly"]);
  if (filterKey === "parking") return textHasAny(haystack, ["parking", "car park", "carpark"]);
  if (filterKey === "coaching") return kind === "coach" || textHasAny(haystack, ["coach", "coaching", "training", "lesson", "clinic", "junior program"]);
  if (filterKey === "late") return isLateText(haystack);
  if (filterKey === "payouts") return textHasAny(haystack, ["paid", "price", "fee", "booking", "$", "aud", "ticket", "payment"]);
  return true;
};

const venueMatchesFilter = (venue = {}, filterKey) => {
  if (filterKey === "verified") {
    return isVerifiedCentreListing(venue);
  }
  const text = filterSearchText(
    venue.name,
    venue.badge,
    venue.type,
    venue.listingType,
    venue.availability,
    venue.openingHours,
    venue.description,
    venue.priceLabel,
    venue.price,
    venue.amenities,
    venue.detailItems
  );
  return entityMatchesFilter(text, filterKey, "centre");
};

const groupMatchesFilter = (group = {}, filterKey) => {
  const text = filterSearchText(group.title, group.name, group.level, group.band, group.venue, group.when, group.price, group.description, group.skillBand, group.format);
  return entityMatchesFilter(text, filterKey, "group");
};

const playerMatchesFilter = (player = {}, filterKey) => {
  const text = filterSearchText(player.name, player.band, player.city, player.style, player.rating, player.plays);
  return entityMatchesFilter(text, filterKey, "player");
};

const coachMatchesFilter = (coach = {}, filterKey) => {
  const text = filterSearchText(coach.name, coach.band, coach.city, coach.style, coach.rating, coach.plays, coach.headline, coach.bio, coach.accreditation, coach.locations);
  return entityMatchesFilter(text, filterKey, "coach");
};

const eventMatchesFilter = (event = {}, filterKey) => {
  const text = filterSearchText(event.title, event.eventType, event.skillBand, event.venue, event.price, event.description, event.requirements, event.time);
  return entityMatchesFilter(text, filterKey, "event");
};

const buildAccountProfileCards = (accountProfile = {}, city = "Melbourne") => {
  const cards = { centres: [], groups: [], players: [], coaches: [] };
  if (isPublicProfileReady(accountProfile, "centre")) {
    const centre = accountProfile.centre || {};
    cards.centres.push({
      id: "account-public-centre",
      type: "Centre profile",
      title: centre.name,
      subtitle: [centre.suburb || city, centre.courtCount ? `${centre.courtCount} courts` : "", centre.pricing].filter(Boolean).join(" - "),
      meta: centre.openingHours || "Hours added",
      image: getPublicProfileImage(centre.logoUrl, approvedPublicMediaLinks(centre.photoLinks, "Centre media")),
      target: "publicCentreProfile",
      payload: { accountProfile },
      profile: centre,
      venue: centre.name,
      venueId: centre.venueId || "",
      city: centre.city || centre.location?.city || city,
      suburb: centre.suburb || "",
      searchText: filterSearchText(centre.name, centre.suburb, centre.address, centre.pricing, centre.openingHours, centre.photoLinks, centre.socialLinks)
    });
  }
  if (isPublicProfileReady(accountProfile, "group")) {
    const group = accountProfile.group || {};
    const matchedGroupVenue = group.venueId
      ? venues.find((venue) => venue.id === group.venueId)
      : venues.find((venue) => compactLocationText(venue.name) === compactLocationText(group.venue || ""));
    const normalisedGroup = normaliseJoinedGroup({
      ...group,
      venue: group.venue || matchedGroupVenue?.name || "",
      venueId: group.venueId || matchedGroupVenue?.id || "",
      city: group.city || matchedGroupVenue?.city || city,
      suburb: group.suburb || matchedGroupVenue?.suburb || ""
    });
    cards.groups.push({
      id: "account-public-group",
      type: "Social group profile",
      title: normalisedGroup.name || normalisedGroup.title,
      subtitle: [normalisedGroup.venue, normalisedGroup.skillBand, normalisedGroup.format].filter(Boolean).join(" - "),
      meta: [normalisedGroup.date ? formatDateLabel(normalisedGroup.date) : "", normalisedGroup.time ? formatTimeLabel(normalisedGroup.time) : "", normalisedGroup.priceLabel || ""].filter(Boolean).join(" - "),
      image: getPublicProfileImage(group.coverUrl, group.logoUrl),
      target: "publicGroupProfile",
      payload: { accountProfile, group: normalisedGroup },
      profile: normalisedGroup,
      venue: normalisedGroup.venue || "",
      venueId: normalisedGroup.venueId || "",
      city: normalisedGroup.city || city,
      suburb: normalisedGroup.suburb || "",
      searchText: filterSearchText(normalisedGroup.name, group.description, normalisedGroup.venue, normalisedGroup.skillBand, normalisedGroup.format, normalisedGroup.price, normalisedGroup.time)
    });
  }
  if (isPublicProfileReady(accountProfile, "player")) {
    const player = accountProfile.player || {};
    const directoryPlayer = accountProfileToPublicPlayerDirectoryProfile(accountProfile, city);
    const publicPayload = directoryPlayer ? publicProfileNavigationPayload("player", directoryPlayer) : null;
    cards.players.push({
      id: "account-public-player",
      type: "Player profile",
      title: accountProfile.fullName,
      subtitle: [player.skillBand, player.suburb || city, player.formats].filter(Boolean).join(" - "),
      meta: player.style || "Player profile",
      image: getPublicProfileImage(player.photoUrl, accountProfile.photoUrl, approvedPublicMediaLinks(player.mediaLinks, "Player media")),
      target: "publicPlayerProfile",
      payload: publicPayload || { accountProfile },
      profile: directoryPlayer || player,
      city: player.city || player.location?.city || city,
      suburb: player.suburb || "",
      searchText: filterSearchText(accountProfile.fullName, player.skillBand, player.suburb, player.formats, player.style)
    });
  }
  if (isPublicProfileReady(accountProfile, "coach")) {
    const coach = accountProfile.coach || {};
    cards.coaches.push({
      id: "account-public-coach",
      type: "Coach profile",
      title: coach.headline || accountProfile.fullName,
      subtitle: [coach.locations || coach.suburb || city, coachPrimaryRateLabel(coach), coach.accreditation].filter(Boolean).join(" - "),
      meta: coach.bio || "Coach profile",
      image: getPublicProfileImage(coach.photoUrl, accountProfile.photoUrl, approvedPublicMediaLinks(coach.mediaLinks, "Coach media")),
      target: "publicCoachProfile",
      payload: { accountProfile },
      profile: coach,
      city: coach.city || coach.location?.city || city,
      suburb: coach.suburb || "",
      searchText: filterSearchText(accountProfile.fullName, coach.headline, coach.bio, coach.locations, coach.suburb, coach.accreditation)
    });
  }
  return cards;
};

function ProfileMiniCard({ card, navigate }) {
  const target = card.target;
  const isPlayerCard = String(card.type || "").toLowerCase().includes("player");
  return (
    <article className="profile-mini-card">
      <a href={routeHref(target, card.payload || {})} onClick={(event) => handleSeoRouteClick(event, navigate, target, card.payload || {})}>
        {isPlayerCard
          ? <PlayerCardMedia src={card.image} name={card.title} className="profile-mini-stock" />
          : card.image
            ? <DeferredImage src={card.image} alt="" rootMargin="80px" />
            : <NoImagePlaceholder label={`${card.type || "Profile"} image not added`} className="profile-mini-stock" />}
        <span>{card.type}</span>
        <strong>{card.title}</strong>
        <p>{card.subtitle}</p>
        <b>{card.meta}</b>
      </a>
    </article>
  );
}

const profileCardMatchesLocation = (card = {}, city = "Melbourne", searchCriteria = {}) => {
  const location = searchCriteria?.location || {};
  const cardCity = card.city || card.profile?.city || card.profile?.location?.city || "";
  const locationCountryCode = getLocationCountryCode(location, city);
  const cardCountryCode = normaliseCountryCode(
    card.countryCode || card.country || card.profile?.countryCode || card.profile?.country || card.profile?.location?.countryCode || card.profile?.location?.country,
    cardCity
  );
  if (locationCountryCode && cardCountryCode && locationCountryCode !== cardCountryCode) return false;
  if (cardCity && city && compactLocationText(cardCity) !== compactLocationText(city)) return false;
  if (location.type === "suburb") {
    const cardSuburb = card.suburb || card.profile?.suburb || card.profile?.location?.suburb || "";
    return !cardSuburb || compactLocationText(cardSuburb) === compactLocationText(location.suburb);
  }
  return true;
};

const directoryRecordMatchesAccountCard = (card = {}, record = {}) => {
  const cardProfile = card.profile || {};
  const identifiers = (value = {}) => [
    value.firestoreSourceId,
    value.publicPlayerId,
    value.publicCoachId,
    value.publicGroupId,
    value.serverGroupId,
    value.serverCentreId,
    value.venueId,
    value.id
  ].map((id) => String(id || "").trim()).filter(Boolean);
  const cardIds = new Set(identifiers(cardProfile));
  if (identifiers(record).some((id) => cardIds.has(id))) return true;
  const cardName = compactLocationText(card.title || cardProfile.name || cardProfile.title || cardProfile.headline || "");
  const recordName = compactLocationText(record.name || record.title || record.headline || "");
  if (!cardName || !recordName || cardName !== recordName) return false;
  const cardCity = compactLocationText(card.city || cardProfile.city || cardProfile.location?.city || "");
  const recordCity = compactLocationText(record.city || record.location?.city || "");
  return !cardCity || !recordCity || cardCity === recordCity;
};

function FilterHubSection({ id, title, count, action, onAction, children }) {
  return (
    <section className="filter-hub-section" id={id}>
      <div className="section-title-row">
        <div>
          <p>{count} listed</p>
          <h2>{title}</h2>
        </div>
        <button type="button" onClick={onAction}>{action} <ChevronRight size={16} /></button>
      </div>
      {children}
    </section>
  );
}

const findVenueForHubItem = (item = {}, venueList = []) => {
  const id = item.venueId || item.centreId || item.locationId || item.venue?.id;
  if (id) {
    const byId = venueList.find((venue) => venue.id === id);
    if (byId) return byId;
  }
  const venueName = compactLocationText(item.venue?.name || item.venue || item.venueName || item.centreName || item.locationName || "");
  if (venueName) {
    const byName = venueList.find((venue) => compactLocationText(venue.name) === venueName);
    if (byName) return byName;
    return venueList.find((venue) => {
      const candidateName = compactLocationText(venue.name);
      return candidateName && (candidateName.includes(venueName) || venueName.includes(candidateName));
    });
  }
  return null;
};

const getProfilePoint = (profile = {}) => {
  const location = profile.location || profile.suburbLocation || {};
  if (location.type === "country") return null;
  const lat = profile.lat ?? profile.latitude ?? location.lat ?? location.latitude;
  const lon = profile.lon ?? profile.lng ?? profile.longitude ?? location.lon ?? location.lng ?? location.longitude;
  return getVenueExactPoint({ lat, lon });
};

const australiaCountryOnlyProfilePoints = [
  { lat: -31.9523, lon: 115.8613 },
  { lat: -34.9285, lon: 138.6007 },
  { lat: -37.8136, lon: 144.9631 },
  { lat: -33.8688, lon: 151.2093 },
  { lat: -27.4698, lon: 153.0251 },
  { lat: -35.2809, lon: 149.1300 },
  { lat: -12.4634, lon: 130.8456 },
  { lat: -42.8821, lon: 147.3272 }
];

const getCountryOnlyProfilePoint = (profile = {}, countryCode = "") => {
  if (countryCode !== "AU") return null;
  const identity = String(profile.id || profile.firestoreSourceId || profile.ownerUid || profile.name || "australia");
  const hash = parseInt(shortStableHash(identity), 36) || 0;
  return australiaCountryOnlyProfilePoints[hash % australiaCountryOnlyProfilePoints.length];
};

const getProfileMapPosition = (profile = {}) => {
  const exactPoint = getProfilePoint(profile);
  const location = profile.location && typeof profile.location === "object" ? profile.location : {};
  const city = String(profile.city || location.city || "").trim();
  const suburb = String(profile.suburb || location.suburb || "").trim();
  const countryCode = normaliseCountryCode(
    profile.countryCode || location.countryCode || profile.country || location.country,
    city
  );
  const country = getCountryName(countryCode || profile.country || location.country, city);
  if (exactPoint) {
    return {
      point: exactPoint,
      approximate: true,
      accuracy: suburb ? "suburb" : city ? "city" : "saved area",
      areaLabel: [suburb, city && compactLocationText(city) !== compactLocationText(suburb) ? city : "", country].filter(Boolean).join(", ")
    };
  }

  const suburbOption = suburb ? locationOptions.find((option) => (
    option.type === "suburb"
    && compactLocationText(option.suburb || option.label) === compactLocationText(suburb)
    && (!city || compactLocationText(option.city) === compactLocationText(city))
    && (!countryCode || option.countryCode === countryCode)
  )) : null;
  const cityOption = city ? cityDirectory.find((option) => (
    option.type === "city"
    && compactLocationText(option.city) === compactLocationText(city)
    && (!countryCode || option.countryCode === countryCode)
  )) : null;
  const countrySeedPoint = countryCode ? averagePoint(countryBuckets.get(countryCode)?.points || []) : null;
  const countryOption = countryCode
    ? countryDirectory.find((option) => option.countryCode === countryCode)
      || (countrySeedPoint ? {
        countryCode,
        country,
        type: "country",
        lat: countrySeedPoint.lat,
        lon: countrySeedPoint.lon
      } : null)
    : null;
  const savedAreaPoint = getVenueExactPoint({
    lat: location.lat ?? location.latitude,
    lon: location.lon ?? location.lng ?? location.longitude
  });
  const countryOnlyPoint = !suburb && !city ? getCountryOnlyProfilePoint(profile, countryCode) : null;
  const fallback = suburbOption || cityOption || savedAreaPoint || countryOnlyPoint || countryOption;
  const point = fallback && Number.isFinite(Number(fallback.lat)) && Number.isFinite(Number(fallback.lon))
    ? { lat: Number(fallback.lat), lon: Number(fallback.lon) }
    : null;
  return {
    point,
    approximate: Boolean(point),
    accuracy: suburbOption ? "suburb" : cityOption ? "city" : savedAreaPoint ? "saved area" : countryOnlyPoint || countryOption ? "country" : "unmapped",
    areaLabel: [suburb, city && compactLocationText(city) !== compactLocationText(suburb) ? city : "", country].filter(Boolean).join(", ")
  };
};

const dedupeMarketplaceMapItems = (items = []) => {
  const byId = new globalThis.Map();
  items.forEach((item) => {
    const point = item?.point;
    if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) return;
    const key = `${item.kind || item.type || "listing"}|${item.id || `${compactLocationText(item.title)}|${point.lat.toFixed(5)}|${point.lon.toFixed(5)}`}`;
    const existing = byId.get(key);
    if (!existing || (!existing.image && item.image)) byId.set(key, item);
  });
  return [...byId.values()];
};

const spreadOverlappingMapItems = (items = []) => {
  const pointCounts = new globalThis.Map();
  items.forEach((item) => {
    const key = `${Number(item.point?.lat).toFixed(5)}:${Number(item.point?.lon).toFixed(5)}`;
    pointCounts.set(key, (pointCounts.get(key) || 0) + 1);
  });
  return items.map((item) => {
    const key = `${Number(item.point?.lat).toFixed(5)}:${Number(item.point?.lon).toFixed(5)}`;
    if ((pointCounts.get(key) || 0) < 2) return item;
    const hash = parseInt(shortStableHash(`${item.kind || item.type}|${item.id}|${item.title}`), 36) || 0;
    const angle = ((hash % 360) * Math.PI) / 180;
    const radiusMeters = 22 + (hash % 34);
    const latOffset = Math.cos(angle) * (radiusMeters / 111320);
    const lonScale = Math.max(0.2, Math.cos((item.point.lat * Math.PI) / 180));
    const lonOffset = Math.sin(angle) * (radiusMeters / (111320 * lonScale));
    return {
      ...item,
      point: { lat: item.point.lat + latOffset, lon: item.point.lon + lonOffset }
    };
  });
};

const getAccountCardMapItems = (cards = [], venueList = []) => cards
  .map((card) => {
    const profile = card.profile || {};
    const venue = findVenueForHubItem({ ...profile, ...card }, venueList);
    const profilePosition = getProfileMapPosition(profile);
    const point = venue ? getVenueExactPoint(venue) : profilePosition.point;
    return {
      id: `${card.id}-map`,
      type: card.type,
      kind: String(card.type || "").toLowerCase().includes("coach") ? "coach"
        : String(card.type || "").toLowerCase().includes("player") ? "player"
          : String(card.type || "").toLowerCase().includes("group") ? "group"
            : "centre",
      title: card.title,
      subtitle: [card.subtitle, venue?.name].filter(Boolean).join(" - "),
      image: card.image || profile.photoUrl || profile.coverUrl || profile.logoUrl || "",
      point,
      approximate: venue ? !getVenueExactPoint(venue) : profilePosition.approximate,
      accuracy: venue ? "venue" : profilePosition.accuracy,
      target: card.target,
      payload: card.payload || {}
    };
  })
  .filter((item) => Number.isFinite(item.point?.lat) && Number.isFinite(item.point?.lon));

function FilterHubMap({ items, searchCriteria, title, emptyTitle, emptyText, navigate, viewportKey = "", onViewportChange = null }) {
  const location = searchCriteria.location || {};
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const leafletRef = useRef(null);
  const resizeCleanupRef = useRef(null);
  const initialViewKeyRef = useRef("");
  const reportedViewportKeyRef = useRef("");
  const [mapReady, setMapReady] = useState(0);
  const [mapStatus, setMapStatus] = useState("loading");
  const [mapMountKey, setMapMountKey] = useState(0);
  const [viewportState, setViewportState] = useState({ count: 0, zoom: 0 });
  const mappedItems = useMemo(() => spreadOverlappingMapItems(
    dedupeMarketplaceMapItems(items).slice(0, 2000)
  ), [items]);
  const locationViewKey = [location.id, location.type, location.lat, location.lon, ...(Array.isArray(location.bbox) ? location.bbox : [])].join("|");

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return undefined;
    let cancelled = false;
    setMapStatus("loading");

    loadLeafletRuntime().then((leaflet) => {
      if (cancelled || !mapElementRef.current || mapRef.current) return;
      leafletRef.current = leaflet;
      const hasLocationPoint = Number.isFinite(location?.lat) && Number.isFinite(location?.lon);
      const fallbackPoint = hasLocationPoint ? [location.lat, location.lon] : [20, 0];
      const map = leaflet.map(mapElementRef.current, {
        center: fallbackPoint,
        zoom: hasLocationPoint ? 11 : 2,
        scrollWheelZoom: true,
        zoomControl: false
      });

      addOpenStreetMapTileLayer(leaflet, map, (status) => {
        if (!cancelled) setMapStatus(status);
      });
      leaflet.control.zoom({ position: "bottomright" }).addTo(map);

      mapRef.current = map;
      markerLayerRef.current = createBadmintonMarkerLayer(leaflet).addTo(map);
      resizeCleanupRef.current = observeLeafletMapSize(map, mapElementRef.current);
      setMapReady((value) => value + 1);
    }).catch(() => {
      if (!cancelled) setMapStatus("error");
    });

    return () => {
      cancelled = true;
      resizeCleanupRef.current?.();
      resizeCleanupRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      leafletRef.current = null;
      initialViewKeyRef.current = "";
      reportedViewportKeyRef.current = "";
    };
  }, [mapMountKey]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    const markerLayer = markerLayerRef.current;
    if (!leaflet || !markerLayer) return;
    markerLayer.clearLayers();
    mappedItems.forEach((item) => {
      const marker = leaflet.marker([item.point.lat, item.point.lon], {
        icon: createMarketplaceMarkerIcon(leaflet, item),
        title: item.title,
        keyboard: true,
        riseOnHover: true,
        zIndexOffset: getMarketplaceMarkerImage(item) ? 600 : 0
      });
      marker.bindTooltip(`
        <strong>${escapeHtml(item.title)}</strong>
        <span>${escapeHtml([
          item.subtitle || item.type || "Open result",
          item.approximate ? `Approximate ${item.accuracy || "area"}` : ""
        ].filter(Boolean).join(" - "))}</span>
      `, {
        className: "badminta-marker-tooltip",
        direction: "top",
        opacity: 1,
        offset: [0, -16]
      });
      marker.on("add", () => {
        const markerElement = marker.getElement();
        const markerImage = markerElement?.querySelector(".marketplace-marker-avatar img");
        if (!markerImage) return;
        const revealFallback = () => markerElement.classList.add("marketplace-marker-image-error");
        markerImage.addEventListener("error", revealFallback, { once: true });
        if (markerImage.complete && markerImage.naturalWidth === 0) revealFallback();
      });
      marker.on("click", () => navigate(item.target, item.payload || {}));
      markerLayer.addLayer(marker);
    });
  }, [mappedItems, navigate, mapReady]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    const map = mapRef.current;
    if (!leaflet || !map || initialViewKeyRef.current === locationViewKey) return;
    initialViewKeyRef.current = locationViewKey;
    if (Array.isArray(location?.bbox) && location.bbox.length === 4) {
      const [west, south, east, north] = location.bbox;
      map.fitBounds([[south, west], [north, east]], { padding: [32, 32], maxZoom: location.type === "suburb" ? 14 : 12, animate: false });
    } else if (Number.isFinite(location?.lat) && Number.isFinite(location?.lon)) {
      map.setView([location.lat, location.lon], location.type === "country" ? 5 : location.type === "suburb" ? 13 : 11, { animate: false });
    } else if (mappedItems.length > 1) {
      const bounds = leaflet.latLngBounds(mappedItems.map((item) => [item.point.lat, item.point.lon]));
      map.fitBounds(bounds, { padding: [44, 44], maxZoom: 12, animate: false });
    } else if (mappedItems.length === 1) {
      map.setView([mappedItems[0].point.lat, mappedItems[0].point.lon], 11, { animate: false });
    }
    window.setTimeout(() => map.invalidateSize(), 0);
  }, [locationViewKey, mappedItems, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const updateViewport = () => {
      const bounds = map.getBounds();
      const visibleItems = mappedItems.filter((item) => bounds.contains([item.point.lat, item.point.lon]));
      const count = visibleItems.length;
      const zoom = map.getZoom();
      const center = map.getCenter();
      setViewportState((current) => current.count === count && current.zoom === zoom ? current : { count, zoom });
      if (onViewportChange) {
        const itemIds = visibleItems.map((item) => String(item.id));
        const reportKey = `${viewportKey}|${zoom}|${center.lat.toFixed(5)}|${center.lng.toFixed(5)}|${itemIds.join(",")}`;
        if (reportedViewportKeyRef.current !== reportKey) {
          reportedViewportKeyRef.current = reportKey;
          onViewportChange({
            viewportKey,
            count,
            zoom,
            itemIds,
            center: { lat: center.lat, lon: center.lng }
          });
        }
      }
    };
    map.on("moveend zoomend", updateViewport);
    updateViewport();
    return () => map.off("moveend zoomend", updateViewport);
  }, [mappedItems, mapReady, onViewportChange, viewportKey]);

  return (
    <aside className="filter-hub-map">
      <div className="map-canvas">
        <div ref={mapElementRef} className="leaflet-map" aria-label={`${title} map around ${location.label || "selected area"}`} />
        <MapLoadPanel status={mapStatus} onRetry={() => {
          leafletRuntimePromise = null;
          setMapMountKey((value) => value + 1);
        }} />
        {!mappedItems.length && (
          <div className="map-empty-panel">
            <strong>{emptyTitle}</strong>
            <span>{emptyText}</span>
          </div>
        )}
      </div>
      <div className="map-summary" aria-live="polite">
        <strong>{title}</strong>
        <span>
          {mappedItems.length
            ? `${viewportState.count} result${viewportState.count === 1 ? "" : "s"} visible at this zoom. Move or zoom the map to explore ${mappedItems.length} mapped result${mappedItems.length === 1 ? "" : "s"}. Tap a pin to open the profile.`
            : emptyText}
        </span>
      </div>
    </aside>
  );
}

function FilterHubPage({ city, searchCriteria, activeFilter, activeResultType, setActiveResultType, venues, groups, players, trainers, events, saved, toggleSaved, navigate, accountProfile, osmCentreStatus, osmCentreError, usingApiCentres }) {
  const copy = filterContent[activeFilter] || filterContent.nearby;
  const activeLocation = locationTitle(searchCriteria, city);
  const label = copy.eyebrow || "Explore";
  const Icon = categoryFilters.find(([key]) => key === activeFilter)?.[2] || Sparkles;
  const accountCards = useMemo(() => buildAccountProfileCards(accountProfile, city), [accountProfile, city]);
  const centreDirectoryLoading = osmCentreStatus === "loading";
  const discoveryLocation = searchCriteria.location || { type: "city", city };
  const filteredVenues = centreDirectoryLoading ? [] : venues.filter((venue) => venueMatchesFilter(venue, activeFilter));
  const filteredGroups = groups
    .filter((group) => recordMatchesSeoArea(group, discoveryLocation, "group"))
    .filter((group) => groupMatchesFilter(group, activeFilter));
  const filteredPlayers = useMemo(() => players
    .filter((player) => recordMatchesSeoArea(player, discoveryLocation, "player"))
    .filter((player) => playerMatchesFilter(player, activeFilter)), [
    players,
    activeFilter,
    discoveryLocation.id,
    discoveryLocation.type,
    discoveryLocation.city,
    discoveryLocation.suburb,
    discoveryLocation.countryCode
  ]);
  const mapPlayerProfiles = useMemo(
    () => players.filter((player) => playerMatchesFilter(player, activeFilter)),
    [players, activeFilter]
  );
  const filteredCoaches = trainers
    .filter((coach) => recordMatchesSeoArea(coach, discoveryLocation, "coach"))
    .filter((coach) => coachMatchesFilter(coach, activeFilter));
  const mapCoachProfiles = filteredCoaches;
  const filteredEvents = events
    .filter((event) => recordMatchesSeoArea(event, discoveryLocation, "event"))
    .filter((event) => eventMatchesFilter(event, activeFilter));
  const centreCards = (centreDirectoryLoading ? [] : accountCards.centres)
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "centre"))
    .filter((card) => !filteredVenues.some((venue) => directoryRecordMatchesAccountCard(card, venue)));
  const groupCards = accountCards.groups
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "group"))
    .filter((card) => !filteredGroups.some((group) => directoryRecordMatchesAccountCard(card, group)));
  const playerCards = useMemo(() => accountCards.players
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "player"))
    .filter((card) => !filteredPlayers.some((player) => directoryRecordMatchesAccountCard(card, player))), [
    accountCards.players,
    city,
    searchCriteria,
    activeFilter,
    filteredPlayers
  ]);
  const mapPlayerCards = useMemo(() => accountCards.players
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "player"))
    .filter((card) => !mapPlayerProfiles.some((player) => directoryRecordMatchesAccountCard(card, player))), [
    accountCards.players,
    activeFilter,
    mapPlayerProfiles
  ]);
  const coachCards = accountCards.coaches
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "coach"))
    .filter((card) => !filteredCoaches.some((coach) => directoryRecordMatchesAccountCard(card, coach)));
  const showCentreEmpty = usingApiCentres && !filteredVenues.length && !centreCards.length;
  const preferredResultType = useMemo(
    () => getPreferredHubResultType(activeFilter, searchCriteria.tags),
    [activeFilter, searchCriteria.tags]
  );
  const [hubView, setHubView] = useState("map");
  const [playerMapViewport, setPlayerMapViewport] = useState(null);
  const playerMapViewportKey = [
    searchCriteria.location?.id,
    searchCriteria.location?.type,
    searchCriteria.location?.lat,
    searchCriteria.location?.lon,
    activeFilter
  ].join("|");

  useEffect(() => {
    setHubView("map");
  }, [preferredResultType, searchCriteria.location?.id]);

  const venueMapItems = useMemo(() => filteredVenues.map((venue) => ({
    id: venue.id,
    type: "Centre",
    kind: "centre",
    title: venue.name,
    subtitle: [getVenueAddressLabel(venue), hasVenueDistance(venue) ? getVenueDistanceLabel(venue) : ""].filter(Boolean).join(" - "),
    image: getPublicProfileImage(venue.logoUrl, getVenueImages(venue)),
    point: getVenueMapPoint(venue),
    approximate: !getVenueExactPoint(venue),
    target: "venue",
    payload: { venue }
  })), [filteredVenues]);
  const centreAccountMapItems = useMemo(() => getAccountCardMapItems(centreCards, venues), [centreCards, venues]);
  const groupMapItems = useMemo(() => filteredGroups.map((group) => {
    const venue = findVenueForHubItem(group, venues);
    const publicPayload = publicProfileNavigationPayload("group", group);
    return {
      id: group.id,
      type: "Social group",
      kind: "group",
      title: group.title,
      subtitle: [group.venue || venue?.name, group.when, getGroupPriceLabel(group, " / player")].filter(Boolean).join(" - "),
      image: getPublicProfileImage(getGroupImage(group)),
      point: venue ? getVenueExactPoint(venue) : getProfilePoint(group),
      approximate: false,
      target: publicPayload ? "publicGroupProfile" : "group",
      payload: publicPayload || { group }
    };
  }), [filteredGroups, venues]);
  const groupAccountMapItems = useMemo(() => getAccountCardMapItems(groupCards, venues), [groupCards, venues]);
  const playerMapItems = useMemo(() => mapPlayerProfiles.map((player) => {
    const publicPayload = publicProfileNavigationPayload("player", player);
    const position = getProfileMapPosition(player);
    return {
      id: player.id,
      type: "Player",
      kind: "player",
      title: player.name,
      subtitle: [player.band, player.suburb || player.city || player.country].filter(Boolean).join(" - "),
      image: getPublicProfileImage(player.photoUrl, player.image, approvedPublicMediaLinks(player.mediaLinks, "Player media")),
      point: position.point,
      approximate: position.approximate,
      accuracy: position.accuracy,
      target: publicPayload ? "publicPlayerProfile" : "player",
      payload: publicPayload || { player }
    };
  }), [mapPlayerProfiles]);
  const playerAccountMapItems = useMemo(() => getAccountCardMapItems(mapPlayerCards, venues), [mapPlayerCards, venues]);
  const combinedPlayerMapItems = useMemo(
    () => [...playerAccountMapItems, ...playerMapItems],
    [playerAccountMapItems, playerMapItems]
  );
  const coachMapItems = useMemo(() => mapCoachProfiles.map((coach) => {
    const publicPayload = publicProfileNavigationPayload("coach", coach);
    const position = getProfileMapPosition(coach);
    return {
      id: coach.id,
      type: "Coach",
      kind: "coach",
      title: coach.name,
      subtitle: [coach.band, coach.city].filter(Boolean).join(" - "),
      image: getPublicProfileImage(coach.photoUrl, coach.image, approvedPublicMediaLinks(coach.mediaLinks, "Coach media")),
      point: position.point,
      approximate: position.approximate,
      accuracy: position.accuracy,
      target: publicPayload ? "publicCoachProfile" : "coach",
      payload: publicPayload || { coach }
    };
  }), [mapCoachProfiles]);
  const coachAccountMapItems = useMemo(() => getAccountCardMapItems(coachCards, venues), [coachCards, venues]);
  const eventMapItems = useMemo(() => filteredEvents.map((event) => {
    const venue = findVenueForHubItem(event, venues) || getEventVenue(event);
    const position = getProfileMapPosition(event);
    const venuePoint = venue ? getVenueExactPoint(venue) : null;
    return {
      id: event.id,
      type: "Event",
      kind: "event",
      title: event.title,
      subtitle: [getEventVenueLabel(event), formatDateLabel(event.date), formatTimeLabel(event.time)].filter(Boolean).join(" - "),
      image: getPublicProfileImage(getEventImage(event)),
      point: venuePoint || position.point,
      approximate: !venuePoint && position.approximate,
      accuracy: venuePoint ? "venue" : position.accuracy,
      target: "event",
      payload: { event }
    };
  }), [filteredEvents, venues]);
  const hasCurrentPlayerViewport = playerMapViewport?.viewportKey === playerMapViewportKey;
  const playerViewportIds = useMemo(
    () => new Set(hasCurrentPlayerViewport ? playerMapViewport.itemIds : []),
    [hasCurrentPlayerViewport, playerMapViewport]
  );
  const visiblePlayerProfiles = hasCurrentPlayerViewport
    ? mapPlayerProfiles.filter((player) => playerViewportIds.has(String(player.id)))
    : filteredPlayers;
  const visiblePlayerCards = hasCurrentPlayerViewport
    ? mapPlayerCards.filter((card) => playerViewportIds.has(`${card.id}-map`))
    : playerCards;
  const playerViewportLocation = hasCurrentPlayerViewport && playerMapViewport?.center
    ? playerMapViewport.zoom <= 2
      ? "current map area"
      : inferNearestKnownCityFromPoint(playerMapViewport.center, activeLocation)
    : activeLocation;
  const tabs = [
    {
      id: "overview",
      label: "All",
      description: "Explore nearby",
      Icon: MapPinned,
      count: filteredVenues.length + centreCards.length + filteredGroups.length + groupCards.length
        + visiblePlayerProfiles.length + visiblePlayerCards.length + filteredCoaches.length + coachCards.length + filteredEvents.length,
      mapItems: [
        ...combinedPlayerMapItems,
        ...coachAccountMapItems,
        ...coachMapItems,
        ...groupAccountMapItems,
        ...groupMapItems,
        ...eventMapItems,
        ...centreAccountMapItems,
        ...venueMapItems
      ],
      emptyMapTitle: `No community profiles mapped in ${activeLocation}`,
      emptyMapText: "Published players, coaches, groups, events and centres appear here as the community grows."
    },
    {
      id: "centres",
      label: "Centres",
      description: "Find centres",
      Icon: Building2,
      count: filteredVenues.length + centreCards.length,
      mapItems: [...centreAccountMapItems, ...venueMapItems],
      emptyMapTitle: `No centre pins in ${activeLocation}`,
      emptyMapText: "Search a wider area or add a centre when you know the details are real."
    },
    {
      id: "groups",
      label: "Groups",
      description: "Find social groups",
      Icon: UsersRound,
      count: filteredGroups.length + groupCards.length,
      mapItems: [...groupAccountMapItems, ...groupMapItems],
      emptyMapTitle: `No group venues mapped in ${activeLocation}`,
      emptyMapText: "Groups appear on the map after they are tied to a real venue."
    },
    {
      id: "players",
      label: "Players",
      description: "Find profiles",
      Icon: UserCircle,
      count: visiblePlayerProfiles.length + visiblePlayerCards.length,
      mapItems: combinedPlayerMapItems,
      emptyMapTitle: `No player locations mapped in ${activeLocation}`,
      emptyMapText: "Players appear after a public country, city or suburb has been saved. Country-only pins remain approximate until the player adds a local area."
    },
    {
      id: "coaches",
      label: "Coaches",
      description: "Training profiles",
      Icon: Trophy,
      count: filteredCoaches.length + coachCards.length,
      mapItems: [...coachAccountMapItems, ...coachMapItems],
      emptyMapTitle: `No coach locations mapped in ${activeLocation}`,
      emptyMapText: "Coaches appear on the map after they publish where they coach."
    },
    {
      id: "events",
      label: "Events",
      description: "Play dates",
      Icon: CalendarDays,
      count: filteredEvents.length,
      mapItems: eventMapItems,
      emptyMapTitle: `No events mapped in ${activeLocation}`,
      emptyMapText: "Events appear on the map after organisers connect them to a venue."
    }
  ];
  useEffect(() => {
    if (!normaliseHubResultType(activeResultType)) setActiveResultType(preferredResultType);
  }, [activeResultType, preferredResultType, setActiveResultType]);
  const resolvedActiveResultType = normaliseHubResultType(activeResultType) || preferredResultType;
  const activeTab = tabs.find((tab) => tab.id === resolvedActiveResultType) || tabs.find((tab) => tab.id === preferredResultType) || tabs[0];
  const centreTabLoading = activeTab.id === "centres" && centreDirectoryLoading;
  const activeTabResultLabel = ({
    overview: "community",
    centres: "centre",
    groups: "group",
    players: "player",
    coaches: "coach",
    events: "event"
  })[activeTab.id] || "result";
  const activeCountryCode = normaliseCountryCode(searchCriteria.location?.countryCode || searchCriteria.location?.country, city);
  const activeCountryCount = getGlobalCentreCountryCount(activeCountryCode);
  const directoryCoverageLabel = activeTab.id === "centres" ? [
    globalCentreTotalCount ? `${globalCentreTotalCount.toLocaleString()} worldwide centre records` : "",
    activeCountryCount ? `${activeCountryCount.toLocaleString()} in ${getCountryName(activeCountryCode, city) || activeCountryCode}` : "",
    `${filteredVenues.length.toLocaleString()} matched to this search`
  ].filter(Boolean).join(" - ") : "";

  const renderCentresList = () => {
    if (showCentreEmpty) {
      return <CentreEmptyState searchCriteria={searchCriteria} status={osmCentreStatus} error={osmCentreError} navigate={navigate} />;
    }
    if (filteredVenues.length || centreCards.length) {
      return (
        <div className="filter-centre-grid">
          {centreCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
          {filteredVenues.map((venue) => (
            <HorizontalVenueCard key={venue.id} venue={venue} saved={saved.includes(venue.id)} toggleSaved={toggleSaved} navigate={navigate} />
          ))}
        </div>
      );
    }
    return (
      <CommunityEmptyState
        type="centres"
        icon={Building2}
        title={`No ${label.toLowerCase()} centres listed in ${activeLocation} yet`}
        text="Try a broader city search or add a centre when you know the details are real."
        primary={{ label: "Search all centres", target: "results" }}
        secondary={{ label: "Add a centre", target: "claimCentre" }}
        navigate={navigate}
      />
    );
  };

  const renderGroupsList = () => (filteredGroups.length || groupCards.length ? (
    <div className="listing-grid-page">
      {groupCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {filteredGroups.map((group) => <GroupTile key={group.id} group={group} navigate={navigate} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptySocialGroupState(activeLocation)} title={`No ${label.toLowerCase()} social groups yet`} navigate={navigate} />
  ));

  const renderPlayersList = () => (visiblePlayerProfiles.length || visiblePlayerCards.length ? (
    <div className="player-directory-grid">
      {visiblePlayerCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {visiblePlayerProfiles.map((player) => <ProfileMiniCard key={player.id} navigate={navigate} card={publicCardFromDirectoryProfile(player, "player")} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyPlayerState(playerViewportLocation)} title={`No ${label.toLowerCase()} players visible here yet`} navigate={navigate} />
  ));

  const renderCoachesList = () => (filteredCoaches.length || coachCards.length ? (
    <div className="player-directory-grid">
      {coachCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {filteredCoaches.map((coach) => <ProfileMiniCard key={coach.id} navigate={navigate} card={publicCardFromDirectoryProfile(coach, "coach")} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyTrainerState(activeLocation)} title={`No ${label.toLowerCase()} coaches yet`} navigate={navigate} />
  ));

  const renderEventsList = () => (filteredEvents.length ? (
    <div className="event-grid">
      {filteredEvents.map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyEventState(activeLocation)} title={`No ${label.toLowerCase()} events yet`} navigate={navigate} />
  ));

  const renderActiveList = () => {
    if (activeTab.id === "overview") return (
      <div className="discovery-overview-results">
        <section className="discovery-overview-section discovery-overview-players">
          <div className="discovery-overview-heading">
            <div><p>Community near you</p><h3>Badminton players</h3></div>
            <button type="button" onClick={() => setActiveResultType("players")}>See all players <ChevronRight size={16} /></button>
          </div>
          {renderPlayersList()}
        </section>
        {(filteredGroups.length || groupCards.length) > 0 && (
          <section className="discovery-overview-section">
            <div className="discovery-overview-heading">
              <div><p>Play together</p><h3>Social groups</h3></div>
              <button type="button" onClick={() => setActiveResultType("groups")}>See all groups <ChevronRight size={16} /></button>
            </div>
            {renderGroupsList()}
          </section>
        )}
        <section className="discovery-overview-section">
          <div className="discovery-overview-heading">
            <div><p>Places to play</p><h3>Badminton centres</h3></div>
            <button type="button" onClick={() => setActiveResultType("centres")}>See all centres <ChevronRight size={16} /></button>
          </div>
          {renderCentresList()}
        </section>
      </div>
    );
    if (activeTab.id === "groups") return renderGroupsList();
    if (activeTab.id === "players") return renderPlayersList();
    if (activeTab.id === "coaches") return renderCoachesList();
    if (activeTab.id === "events") return renderEventsList();
    return renderCentresList();
  };

  return (
    <section className="page filter-hub-page">
      <div className="filter-hub-hero">
        <div>
          <p><Icon size={16} /> {label}</p>
          <h1>{(copy.marketplaceTitle || `Badminton in ${activeLocation}`).replace("Melbourne", activeLocation)}</h1>
          <span>{copy.marketplaceText || "Browse centres, groups, players, coaches and events under this filter."}</span>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <OsmStatusNotice status={osmCentreStatus} error={osmCentreError} />
      <div className="filter-hub-tabs" aria-label={`${label} result types`}>
        {tabs.map(({ id, label: tabLabel, description, Icon: TabIcon, count }) => (
          <a
            href={routeHref("filterHub", { city, activeFilter, activeResultType: id })}
            key={id}
            className={activeTab.id === id ? "active" : ""}
            onClick={(event) => {
              setHubView("map");
              handleSeoRouteClick(event, navigate, "filterHub", { city, activeFilter, activeResultType: id });
            }}
            aria-current={activeTab.id === id ? "page" : undefined}
          >
            <TabIcon size={18} />
            <span><b>{tabLabel}<strong>{Number(count || 0).toLocaleString()}</strong></b><em>{description}</em></span>
          </a>
        ))}
      </div>
      <div className="filter-results-toolbar">
        <div>
          <p>{activeTab.count} result{activeTab.count === 1 ? "" : "s"}</p>
          <h2>{activeTab.label} in {activeTab.id === "players" ? playerViewportLocation : activeLocation}</h2>
          {directoryCoverageLabel && <span className="directory-coverage-label">{directoryCoverageLabel}</span>}
        </div>
        <div className="view-toggle" aria-label="Switch result view">
          <button type="button" className={hubView === "map" ? "active" : ""} onClick={() => setHubView("map")} disabled={centreTabLoading}><Map size={16} /> Map</button>
          <button type="button" className={hubView === "list" ? "active" : ""} onClick={() => setHubView("list")} disabled={centreTabLoading}><ListFilter size={16} /> List</button>
        </div>
      </div>
      <div className="filter-result-surface">
        {centreTabLoading ? renderCentresList() : hubView === "map" ? (
          <>
            <FilterHubMap
              items={activeTab.mapItems}
              searchCriteria={searchCriteria}
              title={`${activeTab.label} map for ${activeLocation}`}
              emptyTitle={activeTab.emptyMapTitle}
              emptyText={activeTab.emptyMapText}
              navigate={navigate}
              viewportKey={["overview", "players"].includes(activeTab.id) ? playerMapViewportKey : ""}
              onViewportChange={["overview", "players"].includes(activeTab.id) ? setPlayerMapViewport : null}
            />
            <section className="map-results-stack" aria-label={`${activeTab.label} results below map`}>
              <div className="map-results-heading">
                <p>Results below map</p>
                <h3>{activeTab.count} {activeTabResultLabel} result{activeTab.count === 1 ? "" : "s"}</h3>
              </div>
              {renderActiveList()}
            </section>
          </>
        ) : renderActiveList()}
      </div>
      <MarketplaceTypeGuide navigate={navigate} compact />
    </section>
  );
}

function SeoAreaPage({ city, searchCriteria, seoAreaIntent, venueDirectory, groupDirectory, playerDirectory, coachDirectory, events, accountProfile, saved, toggleSaved, navigate }) {
  const location = ["country", "city", "suburb"].includes(searchCriteria.location?.type)
    ? searchCriteria.location
    : locationOptions.find((option) => option.type === "city" && option.city === city) || searchCriteria.location || getDefaultSearchLocation();
  const activeCity = location.type === "country" ? "" : location.city || city;
  const areaLabel = getSeoAreaLocationLabel(location, activeCity);
  const countryName = getLocationCountryName(location, activeCity);
  const areaContextLabel = [areaLabel, location.type !== "country" ? countryName : ""].filter(Boolean).join(", ");
  const pageScopeLabel = location.type === "country" ? "country" : location.type === "city" ? "city" : "suburb";
  const intentId = normaliseSeoAreaIntent(seoAreaIntent);
  const intent = seoAreaIntents[intentId] || seoAreaIntents.overview;
  const areaSearchCriteria = { ...searchCriteria, location };
  const accountCards = buildAccountProfileCards(accountProfile, activeCity);
  const areaVenues = useMemo(
    () => sortVenueResults(dedupeSeoVenues(venueDirectory.filter((venue) => venueMatchesSeoArea(venue, location))), location),
    [venueDirectory, location]
  );
  const [centreDisplayLimit, setCentreDisplayLimit] = useState(240);
  const areaGroups = groupDirectory.filter((group) => recordMatchesSeoArea(group, location, "group"));
  const areaPlayers = playerDirectory.filter((player) => recordMatchesSeoArea(player, location, "player"));
  const areaCoaches = coachDirectory.filter((coach) => recordMatchesSeoArea(coach, location, "coach"));
  const areaEvents = events.filter((event) => recordMatchesSeoArea(event, location, "event"));
  const centreCards = accountCards.centres
    .filter((card) => profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaVenues.some((venue) => directoryRecordMatchesAccountCard(card, venue)));
  const groupCards = accountCards.groups
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "group") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaGroups.some((group) => directoryRecordMatchesAccountCard(card, group)));
  const playerCards = accountCards.players
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "player") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaPlayers.some((player) => directoryRecordMatchesAccountCard(card, player)));
  const coachCards = accountCards.coaches
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "coach") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaCoaches.some((coach) => directoryRecordMatchesAccountCard(card, coach)));
  const nearbyLocations = getSeoAreaNearbyLocations(location, 8);
  const centreMapItems = [
    ...getAccountCardMapItems(centreCards, venueDirectory),
    ...areaVenues.map((venue) => ({
      id: `centre-map-${venue.id}`,
      type: "Centre",
      kind: "centre",
      title: venue.name,
      subtitle: getVenueAddressLabel(venue),
      image: getPublicProfileImage(venue.logoUrl, getVenueImages(venue)),
      point: getVenueExactPoint(venue),
      approximate: false,
      target: "venue",
      payload: { venue }
    }))
  ];
  const groupMapItems = [
    ...getAccountCardMapItems(groupCards, venueDirectory),
    ...areaGroups.map((group) => groupDirectoryMapItem(group, venueDirectory))
  ];
  const playerMapItems = [
    ...getAccountCardMapItems(playerCards, venueDirectory),
    ...playerDirectory.map((player) => profileDirectoryMapItem(player, "player"))
  ];
  const coachMapItems = [
    ...getAccountCardMapItems(coachCards, venueDirectory),
    ...coachDirectory.map((coach) => profileDirectoryMapItem(coach, "coach"))
  ];
  const eventMapItems = areaEvents.map((event) => eventDirectoryMapItem(event, venueDirectory));
  const mapConfigs = {
    overview: {
      label: "Badminton community",
      items: [...centreMapItems, ...groupMapItems, ...playerMapItems, ...coachMapItems, ...eventMapItems],
      emptyTitle: `No mapped badminton listings in ${areaLabel}`,
      emptyText: "Published listings appear here after a centre, profile, group or event saves a real location."
    },
    centres: {
      label: "Centres",
      items: centreMapItems,
      emptyTitle: `No centre locations mapped in ${areaLabel}`,
      emptyText: "Centre pins appear after a real street or map location has been saved."
    },
    groups: {
      label: "Social groups",
      items: groupMapItems,
      emptyTitle: `No social group locations mapped in ${areaLabel}`,
      emptyText: "Published groups appear here after an organiser adds a real venue or playing location."
    },
    players: {
      label: "Players",
      items: playerMapItems,
      emptyTitle: `No player locations mapped in ${areaLabel}`,
      emptyText: "Published players appear after a public country, city or suburb is available. Country-only pins stay approximate until a local area is added."
    },
    coaches: {
      label: "Coaches",
      items: coachMapItems,
      emptyTitle: `No coach locations mapped in ${areaLabel}`,
      emptyText: "Published coaches appear here after saving where they offer training."
    },
    events: {
      label: "Events",
      items: eventMapItems,
      emptyTitle: `No event locations mapped in ${areaLabel}`,
      emptyText: "Published events appear here after an organiser connects them to a real venue."
    }
  };
  const activeMapConfig = mapConfigs[intentId === "overview" ? "overview" : intent.resultType] || mapConfigs.overview;
  const counts = {
    centres: areaVenues.length + centreCards.length,
    groups: areaGroups.length + groupCards.length,
    players: areaPlayers.length + playerCards.length,
    coaches: areaCoaches.length + coachCards.length,
    events: areaEvents.length
  };
  const totalCount = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const resultCount = intentId === "overview" ? totalCount : counts[intent.resultType] || 0;
  const seoSummary = [
    `${counts.centres} centre${counts.centres === 1 ? "" : "s"}`,
    `${counts.groups} social group${counts.groups === 1 ? "" : "s"}`,
    `${counts.players} player${counts.players === 1 ? "" : "s"}`,
    `${counts.coaches} coach${counts.coaches === 1 ? "" : "es"}`,
    `${counts.events} event${counts.events === 1 ? "" : "s"}`
  ].join(", ");
  const centreListLimit = intentId === "overview" ? 6 : centreDisplayLimit;
  const visibleAreaVenues = areaVenues.slice(0, centreListLimit);
  const hasMoreAreaVenues = intentId !== "overview" && areaVenues.length > visibleAreaVenues.length;

  useEffect(() => {
    setCentreDisplayLimit(240);
  }, [location?.id, location?.city, location?.suburb, location?.countryCode, intentId]);

  const renderCentres = () => counts.centres ? (
    <div className="seo-area-list">
      {centreCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {visibleAreaVenues.map((venue) => (
        <HorizontalVenueCard key={venue.id} venue={venue} saved={saved.includes(venue.id)} toggleSaved={toggleSaved} navigate={navigate} />
      ))}
      {hasMoreAreaVenues && (
        <div className="seo-area-load-more">
          <span>{visibleAreaVenues.length.toLocaleString()} of {areaVenues.length.toLocaleString()} centre records shown for this area.</span>
          <button type="button" onClick={() => setCentreDisplayLimit((current) => Math.min(areaVenues.length, current + 240))}>
            Show 240 more
          </button>
          <button type="button" onClick={() => setCentreDisplayLimit(areaVenues.length)}>
            Show all {areaVenues.length.toLocaleString()}
          </button>
        </div>
      )}
    </div>
  ) : (
    <CommunityEmptyState
      type="centres"
      icon={Building2}
      title={`No badminton centres listed in ${areaLabel} yet`}
      text="If you know a real badminton centre in this suburb, add it so players can find accurate court and contact details."
      primary={{ label: "Add a centre", target: "claimCentre" }}
      secondary={{ label: "Search nearby suburbs", target: "results" }}
      navigate={navigate}
      compact={intentId === "overview"}
    />
  );

  const renderGroups = () => counts.groups ? (
    <div className="listing-grid-page">
      {groupCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {areaGroups.slice(0, intentId === "overview" ? 4 : 24).map((group) => <GroupTile key={group.id} group={group} navigate={navigate} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptySocialGroupState(areaLabel)} navigate={navigate} compact={intentId === "overview"} />
  );

  const renderPlayers = () => counts.players ? (
    <div className="player-directory-grid">
      {playerCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {areaPlayers.slice(0, intentId === "overview" ? 4 : 24).map((player) => (
        <ProfileMiniCard key={player.id} navigate={navigate} card={publicCardFromDirectoryProfile(player, "player")} />
      ))}
    </div>
  ) : (
    <CommunityEmptyState {...emptyPlayerState(areaLabel)} navigate={navigate} compact={intentId === "overview"} />
  );

  const renderCoaches = () => counts.coaches ? (
    <div className="player-directory-grid">
      {coachCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {areaCoaches.slice(0, intentId === "overview" ? 4 : 24).map((coach) => (
        <ProfileMiniCard key={coach.id} navigate={navigate} card={publicCardFromDirectoryProfile(coach, "coach")} />
      ))}
    </div>
  ) : (
    <CommunityEmptyState {...emptyTrainerState(areaLabel)} navigate={navigate} compact={intentId === "overview"} />
  );

  const renderEvents = () => counts.events ? (
    <div className="event-grid">
      {areaEvents.slice(0, intentId === "overview" ? 4 : 24).map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyEventState(areaLabel)} navigate={navigate} compact={intentId === "overview"} />
  );

  const sections = [
    { id: "centres", title: `Badminton centres in ${areaLabel}`, text: "Centres are venue listings for court hire, opening hours, contact details and map location.", render: renderCentres },
    { id: "groups", title: `Social badminton groups in ${areaLabel}`, text: "Groups are organised sessions where players join a date, skill level and shared court cost.", render: renderGroups },
    { id: "players", title: `Badminton players in ${areaLabel}`, text: "Player profiles help locals understand skill level, availability, rating history and media.", render: renderPlayers },
    { id: "coaches", title: `Badminton coaches in ${areaLabel}`, text: "Coaching profiles show training locations, rates, experience, media and public reviews.", render: renderCoaches },
    { id: "events", title: `Badminton events in ${areaLabel}`, text: "Events include tournaments, clinics, public play dates and club nights published by centres or organisers.", render: renderEvents }
  ];
  const visibleSections = intentId === "overview" ? sections : sections.filter((section) => section.id === intent.resultType);

  return (
    <section className="page seo-area-page">
      <div className="seo-area-hero">
        <div className="seo-area-hero-copy">
          <p><MapPin size={16} /> {areaContextLabel}</p>
          <h1>{intent.title(areaLabel)}</h1>
          <span>
            Find badminton courts, social sessions, players, coaches and events around {areaLabel}. This {pageScopeLabel} page is built from published Badminta profiles and verified local directory records.
          </span>
          <div className="seo-area-actions">
            <a href={routeHref("seoArea", { location, city: activeCity, seoAreaIntent: "centres" })} onClick={(event) => handleSeoRouteClick(event, navigate, "seoArea", { location, city: activeCity, seoAreaIntent: "centres" })}>See centres</a>
            <button type="button" onClick={() => navigate("claimCentre")}>Add local listing</button>
          </div>
        </div>
        <div className="seo-area-map-card">
          <FilterHubMap
            items={activeMapConfig.items}
            searchCriteria={areaSearchCriteria}
            title={`${activeMapConfig.label} map for ${areaLabel}`}
            emptyTitle={activeMapConfig.emptyTitle}
            emptyText={activeMapConfig.emptyText}
            navigate={navigate}
          />
        </div>
      </div>

      <div className="seo-area-tabs" aria-label={`Badminton directory pages for ${areaLabel}`}>
        {seoAreaIntentList.map((item) => (
          <a
            key={item.id}
            href={routeHref("seoArea", { location, city: activeCity, seoAreaIntent: item.id })}
            className={item.id === intentId ? "active" : ""}
            onClick={(event) => handleSeoRouteClick(event, navigate, "seoArea", { location, city: activeCity, seoAreaIntent: item.id })}
            aria-current={item.id === intentId ? "page" : undefined}
          >
            <span>{item.label}</span>
            <strong>{item.id === "overview" ? totalCount : counts[item.resultType] || 0}</strong>
          </a>
        ))}
      </div>

      <div className="seo-area-stats">
        {[
          ["Centres", counts.centres, Building2],
          ["Social groups", counts.groups, UsersRound],
          ["Players", counts.players, UserCircle],
          ["Coaches", counts.coaches, Trophy],
          ["Events", counts.events, CalendarDays]
        ].map(([label, value, Icon]) => (
          <span key={label}><Icon size={18} /><b>{value}</b>{label}</span>
        ))}
      </div>

      <div className="seo-area-intro">
        <h2>{resultCount ? `${resultCount} ${intent.emptyType} around ${areaLabel}` : `Build the ${intent.keyword} community in ${areaLabel}`}</h2>
        <p>
          Badminta separates centre listings from social groups so players understand the difference: centres let you book a court, while social groups are organised games where members join and split the session cost. Current local coverage includes {seoSummary}.
        </p>
      </div>

      <div className="seo-area-results">
        {visibleSections.map((section) => (
          <section key={section.id} className="seo-area-section" id={section.id}>
            <div className="section-title-row">
              <div>
                <p>{section.text}</p>
                <h2>{section.title}</h2>
              </div>
              {intentId === "overview" && (
                <a
                  href={routeHref("seoArea", { location, city: activeCity, seoAreaIntent: section.id })}
                  onClick={(event) => handleSeoRouteClick(event, navigate, "seoArea", { location, city: activeCity, seoAreaIntent: section.id })}
                >
                  View all <ChevronRight size={16} />
                </a>
              )}
            </div>
            {section.render()}
          </section>
        ))}
      </div>

      <div className="seo-area-link-panels">
        <section>
          <h2>Popular badminton searches in {areaLabel}</h2>
          <div className="seo-area-link-grid">
            {seoAreaIntentList.filter((item) => item.id !== "overview").map((item) => (
              <a
                key={item.id}
                href={routeHref("seoArea", { location, city: activeCity, seoAreaIntent: item.id })}
                onClick={(event) => handleSeoRouteClick(event, navigate, "seoArea", { location, city: activeCity, seoAreaIntent: item.id })}
              >
                {item.keyword} in {areaLabel}
              </a>
            ))}
          </div>
        </section>
        <section>
          <h2>{location.type === "country" ? `Badminton cities in ${areaLabel}` : `Badminton near ${areaLabel}`}</h2>
          <div className="seo-area-link-grid">
            {nearbyLocations.map((nearby) => (
              <a
                key={nearby.id}
                href={routeHref("seoArea", { location: nearby, city: nearby.city, seoAreaIntent: "centres" })}
                onClick={(event) => handleSeoRouteClick(event, navigate, "seoArea", { location: nearby, city: nearby.city, seoAreaIntent: "centres" })}
              >
                {nearby.type === "city" ? `Badminton in ${nearby.label}` : `Badminton centres in ${nearby.label}`}
              </a>
            ))}
          </div>
        </section>
      </div>

      <section className="seo-area-faq" aria-label={`Badminton questions for ${areaLabel}`}>
        <h2>Badminton in {areaLabel}: quick answers</h2>
        {[
          [`Where can I play badminton in ${areaLabel}?`, `Use this page to compare listed badminton centres, social groups, players, coaches and events around ${areaLabel}. Centre listings are for courts, while groups are organised games at a venue.`],
          [`How do I join a social badminton group in ${areaLabel}?`, "Open a group profile, check the session date, level, venue and price, then use the join action when the organiser has enabled public joining."],
          [`Can a centre add court booking details for ${areaLabel}?`, "Yes. A centre owner can add or claim a centre, publish opening hours, court details, pricing, booking rules and future events."],
          [`Can players and coaches create public profiles in ${areaLabel}?`, "Yes. Players and coaches can publish profiles with skill level, availability, media, reviews and links so locals can decide who to play or train with."]
        ].map(([question, answer]) => (
          <details key={question}>
            <summary>{question}</summary>
            <p>{answer}</p>
          </details>
        ))}
      </section>
    </section>
  );
}

function HomePage({ city, setCity, searchCriteria, osmCentreStatus, osmCentreError, usingApiCentres, venues, groups, players, trainers = [], events = [], accountProfile, saved, toggleSaved, navigate }) {
  const activeLocation = locationTitle(searchCriteria, city);
  const showCentreEmpty = usingApiCentres && !venues.length;
  const featuredVenues = venues.slice(0, 12);
  const location = searchCriteria?.location || {};
  const visibleGroups = groups.filter((group) => recordMatchesSeoArea(group, location, "group"));
  const visiblePlayers = players
    .filter((player) => recordMatchesSeoArea(player, location, "player"))
    .slice(0, 8);
  const visibleCoaches = trainers
    .filter((coach) => recordMatchesSeoArea(coach, location, "coach"))
    .slice(0, 8);
  const visibleEvents = events
    .filter((event) => eventMatchesLocation(event, city, searchCriteria))
    .slice(0, 6);
  const accountCards = buildAccountProfileCards(accountProfile, city);
  const homeGroupCards = accountCards.groups
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visibleGroups.some((group) => directoryRecordMatchesAccountCard(card, group)))
    .slice(0, 4);
  const homePlayerCards = accountCards.players
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visiblePlayers.some((player) => directoryRecordMatchesAccountCard(card, player)))
    .slice(0, 4);
  const homeCoachCards = accountCards.coaches
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visibleCoaches.some((coach) => directoryRecordMatchesAccountCard(card, coach)))
    .slice(0, 4);
  const groupEmpty = emptySocialGroupState(activeLocation);
  const playerEmpty = emptyPlayerState(activeLocation);
  const coachEmpty = emptyTrainerState(activeLocation);
  const eventEmpty = emptyEventState(activeLocation);
  const visitorCities = getVisitorCityStripCities(city, searchCriteria);
  return (
    <section className="page home-page">
      <GlobalHomeHero activeLocation={activeLocation} navigate={navigate} />
      <HomeSolutionBand navigate={navigate} />
      <HomeLandingSeoSections navigate={navigate} />

      <>
          <div className="city-strip" aria-label={`Cities near ${activeLocation}`}>
            {visitorCities.map((item) => (
              <button type="button" key={item} className={city === item ? "active" : ""} onClick={() => setCity(item)}>
                {item}
              </button>
            ))}
          </div>

          <SearchSummary searchCriteria={searchCriteria} />
          <OsmStatusNotice status={osmCentreStatus} error={osmCentreError} />
          <MarketplaceTypeGuide navigate={navigate} compact />

          <RowSection title={`Badminton centres in ${activeLocation}`} action="See centre map" onAction={() => navigate("results")}>
            {showCentreEmpty ? <CentreEmptyState searchCriteria={searchCriteria} status={osmCentreStatus} error={osmCentreError} navigate={navigate} /> : featuredVenues.map((venue) => (
              <VenueTile
                key={venue.id}
                venue={venue}
                saved={saved.includes(venue.id)}
                toggleSaved={toggleSaved}
                navigate={navigate}
              />
            ))}
          </RowSection>
      </>

      <>
          <RowSection title="Social games near you" action="Join a group" onAction={() => navigate("groupsIndex")}>
            {homeGroupCards.length || visibleGroups.length ? (
              <>
                {homeGroupCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
                {visibleGroups.map((group) => <GroupTile key={group.id} group={group} navigate={navigate} />)}
              </>
            ) : (
              <CommunityEmptyState {...groupEmpty} navigate={navigate} compact />
            )}
          </RowSection>

      <RowSection title="Players near you" action="Find players" onAction={() => navigate("playersIndex")}>
        {homePlayerCards.length || visiblePlayers.length ? (
          <>
            {homePlayerCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
            {visiblePlayers.map((player) => <PlayerTile key={player.id} player={player} navigate={navigate} />)}
          </>
        ) : (
          <CommunityEmptyState {...playerEmpty} navigate={navigate} compact />
        )}
      </RowSection>

      <RowSection title="Coaches near you" action="Find coaches" onAction={() => navigate("playersIndex", { activeFilter: "coaching" })}>
        {homeCoachCards.length || visibleCoaches.length ? (
          <>
            {homeCoachCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
            {visibleCoaches.map((coach) => <ProfileMiniCard card={publicCardFromDirectoryProfile(coach, "coach")} navigate={navigate} key={coach.id} />)}
          </>
        ) : (
          <CommunityEmptyState {...coachEmpty} navigate={navigate} compact />
        )}
      </RowSection>

          <RowSection title="Badminton events near you" action="View events" onAction={() => navigate("events")}>
            {visibleEvents.length ? (
              visibleEvents.map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} compact />)
            ) : (
              <CommunityEmptyState {...eventEmpty} navigate={navigate} compact />
            )}
          </RowSection>
      </>

      <section className="workflow-band">
        <div>
          <p>For players, organisers and centres</p>
          <h2>Find a court, fill a session, manage payments and keep every badminton plan together.</h2>
        </div>
        <div className="workflow-grid">
          {[
            ["Create a group", "Organisers create paid sessions, invite players and manage waitlists.", "createGroup", Plus],
            ["Add or edit centre", "Badminton centres publish courts, pricing, photos and booking rules.", "claimCentre", Building2],
            ["Build a player profile", "Players add media, availability, skill level and verified rating history.", "profileSetup", Camera],
            ["Trust and support", "Report issues, manage refunds and keep groups safe.", "help", ShieldCheck]
          ].map(([title, text, target, Icon]) => (
            <button type="button" key={title} onClick={() => navigate(target)}>
              <Icon size={22} />
              <strong>{title}</strong>
              <span>{text}</span>
            </button>
          ))}
        </div>
      </section>
    </section>
  );
}

const groupDirectoryMapItem = (group = {}, venueList = []) => {
  const venue = findVenueForHubItem(group, venueList);
  const publicPayload = publicProfileNavigationPayload("group", group);
  return {
    id: `group-map-${group.id || getGroupStableId(group)}`,
    type: "Social group",
    kind: "group",
    title: getGroupTitle(group),
    subtitle: [getGroupVenueLabel(group), getGroupScheduleLabel(group), getGroupPriceLabel(group, " / player")].filter(Boolean).join(" - "),
    image: getPublicProfileImage(getGroupImage(group)),
    point: venue ? getVenueExactPoint(venue) : getProfilePoint(group),
    approximate: false,
    target: publicPayload ? "publicGroupProfile" : "group",
    payload: publicPayload || { group }
  };
};

const profileDirectoryMapItem = (profile = {}, role = "player") => {
  const publicPayload = publicProfileNavigationPayload(role, profile);
  const position = getProfileMapPosition(profile);
  return {
    id: `${role}-map-${profile.id || slugifyPath(profile.name || role)}`,
    type: role === "coach" ? "Coach" : "Player",
    kind: role,
    title: profile.name || profile.fullName || roleTitle(role),
    subtitle: [profile.band || profile.skillBand, profile.suburb || profile.city || profile.country].filter(Boolean).join(" - "),
    image: getPublicProfileImage(profile.photoUrl, profile.image, approvedPublicMediaLinks(profile.mediaLinks, `${roleTitle(role)} media`)),
    point: position.point,
    approximate: position.approximate,
    accuracy: position.accuracy,
    target: publicPayload ? publicProfilePageByRole[role] : role === "coach" ? "coach" : "player",
    payload: publicPayload || (role === "coach" ? { coach: profile } : { player: profile })
  };
};

const eventDirectoryMapItem = (event = {}, venueList = []) => {
  const venue = findVenueForHubItem(event, venueList) || getEventVenue(event);
  const position = getProfileMapPosition(event);
  const venuePoint = venue ? getVenueExactPoint(venue) : null;
  return {
    id: `event-map-${event.id || entitySlug(event, "title")}`,
    type: "Event",
    kind: "event",
    title: event.title || "Badminton event",
    subtitle: [getEventVenueLabel(event), formatDateLabel(event.date), formatTimeLabel(event.time)].filter(Boolean).join(" - "),
    image: getPublicProfileImage(getEventImage(event)),
    point: venuePoint || position.point,
    approximate: !venuePoint && position.approximate,
    accuracy: venuePoint ? "venue" : position.accuracy,
    target: "event",
    payload: { event }
  };
};

function DirectoryDiscoverySurface({ label, title, items, searchCriteria, emptyTitle, emptyText, navigate, count, children }) {
  const [view, setView] = useState("map");
  useEffect(() => setView("map"), [searchCriteria.location?.id, label]);
  return (
    <section className="directory-discovery-surface">
      <div className="filter-results-toolbar directory-results-toolbar">
        <div>
          <p>{count} result{count === 1 ? "" : "s"}</p>
          <h2>{title}</h2>
        </div>
        <div className="view-toggle" aria-label={`Switch ${label.toLowerCase()} view`}>
          <button type="button" className={view === "map" ? "active" : ""} onClick={() => setView("map")}><Map size={16} /> Map</button>
          <button type="button" className={view === "list" ? "active" : ""} onClick={() => setView("list")}><ListFilter size={16} /> List</button>
        </div>
      </div>
      {view === "map" ? (
        <>
          <FilterHubMap
            items={items}
            searchCriteria={searchCriteria}
            title={`${label} map`}
            emptyTitle={emptyTitle}
            emptyText={emptyText}
            navigate={navigate}
          />
          <div className="directory-results-below-map">
            <div className="map-results-heading">
              <p>Results below map</p>
              <h3>{count} {label.toLowerCase()} result{count === 1 ? "" : "s"}</h3>
            </div>
            {children}
          </div>
        </>
      ) : children}
    </section>
  );
}

function GroupsIndexPage({ city, groups, venues, navigate, activeFilter, searchCriteria, accountProfile }) {
  const copy = filterContent[activeFilter] || {};
  const activeLocation = locationTitle(searchCriteria, city);
  const filterLabel = activeFilter === "nearby" ? "Social groups" : copy.eyebrow || "Social groups";
  const title = copy.groupsTitle ? copy.groupsTitle.replace("Melbourne", city) : `Join organised badminton games in ${activeLocation}.`;
  const emptyState = emptySocialGroupState(activeLocation);
  const discoveryLocation = searchCriteria.location || { type: "city", city };
  const visibleGroups = groups
    .filter((group) => recordMatchesSeoArea(group, discoveryLocation, "group"))
    .filter((group) => groupMatchesFilter(group, activeFilter));
  const groupCards = buildAccountProfileCards(accountProfile, city).groups
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, "group"))
    .filter((card) => !visibleGroups.some((group) => directoryRecordMatchesAccountCard(card, group)));
  const groupMapItems = useMemo(() => [
    ...getAccountCardMapItems(groupCards, venues),
    ...visibleGroups.map((group) => groupDirectoryMapItem(group, venues))
  ], [groupCards, visibleGroups, venues]);
  const groupList = groupCards.length || visibleGroups.length ? (
    <div className="listing-grid-page">
      {groupCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {visibleGroups.map((group) => <GroupTile key={group.id} group={group} navigate={navigate} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyState} title={activeFilter === "nearby" ? `No social groups in ${activeLocation} yet` : `No ${copy.eyebrow?.toLowerCase() || "matching"} social groups yet`} navigate={navigate} />
  );
  return (
    <section className="page index-page">
      <div className="index-hero">
        <div>
          <p>{filterLabel}</p>
          <h1>{title}</h1>
          <span>Social groups are not centres. They are games organised by players or organisers at a centre, with level, date, capacity and cost per player clearly listed.</span>
        </div>
        <div className="index-actions">
          <button type="button" onClick={() => navigate("createGroup")}>Create a group</button>
          <button type="button" onClick={() => navigate("messages", { messageFocus: { roleId: "organiser", threadId: "organiser-invite-list" } })}>View invites</button>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <MarketplaceTypeGuide navigate={navigate} compact />
      <DirectoryDiscoverySurface
        label="Social groups"
        title={`Social groups in ${activeLocation}`}
        items={groupMapItems}
        searchCriteria={searchCriteria}
        emptyTitle={`No social group locations in ${activeLocation}`}
        emptyText="Published groups appear here after an organiser adds a venue or local playing area."
        navigate={navigate}
        count={groupCards.length + visibleGroups.length}
      >{groupList}</DirectoryDiscoverySurface>
    </section>
  );
}

function PlayersIndexPage({ city, players, trainers, venues, navigate, activeFilter, searchCriteria, accountProfile }) {
  const copy = filterContent[activeFilter] || {};
  const activeLocation = locationTitle(searchCriteria, city);
  const showingTrainers = activeFilter === "coaching";
  const accountCards = buildAccountProfileCards(accountProfile, city);
  const discoveryLocation = searchCriteria.location || { type: "city", city };
  const list = showingTrainers
    ? trainers
      .filter((coach) => recordMatchesSeoArea(coach, discoveryLocation, "coach"))
      .filter((coach) => coachMatchesFilter(coach, activeFilter))
    : players
      .filter((player) => recordMatchesSeoArea(player, discoveryLocation, "player"))
      .filter((player) => playerMatchesFilter(player, activeFilter));
  const mapProfiles = showingTrainers
    ? trainers.filter((coach) => coachMatchesFilter(coach, activeFilter))
    : players.filter((player) => playerMatchesFilter(player, activeFilter));
  const profileCards = (showingTrainers ? accountCards.coaches : accountCards.players)
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => entityMatchesFilter(card.searchText, activeFilter, showingTrainers ? "coach" : "player"))
    .filter((card) => !list.some((profile) => directoryRecordMatchesAccountCard(card, profile)));
  const emptyState = showingTrainers ? emptyTrainerState(activeLocation) : emptyPlayerState(activeLocation);
  const profileRole = showingTrainers ? "coach" : "player";
  const profileMapItems = useMemo(() => [
    ...getAccountCardMapItems(profileCards, venues),
    ...mapProfiles.map((profile) => profileDirectoryMapItem(profile, profileRole))
  ], [profileCards, mapProfiles, profileRole, venues]);
  const profileList = profileCards.length || list.length ? (
    <div className="player-directory-grid">
      {profileCards.map((card) => <ProfileMiniCard card={card} navigate={navigate} key={card.id} />)}
      {list.map((profile) => {
        const role = showingTrainers ? "coach" : "player";
        const publicPayload = publicProfileNavigationPayload(role, profile);
        const targetPage = publicPayload ? publicProfilePageByRole[role] : showingTrainers ? "coach" : "player";
        const payload = publicPayload || (showingTrainers ? { coach: profile } : { player: profile });
        return (
          <article className="player-directory-card" key={profile.id}>
            <a
              href={routeHref(targetPage, payload)}
              onClick={(event) => handleSeoRouteClick(event, navigate, targetPage, payload)}
            >
              {showingTrainers
                ? profile.image
                  ? <DeferredImage src={profile.image} alt={profile.name} rootMargin="80px" />
                  : <NoImagePlaceholder label="Coach photo not added" className="profile-mini-stock" />
                : <PlayerCardMedia src={profile.image} name={profile.name} className="profile-mini-stock" />}
              <strong>{profile.name}</strong>
              <span>{profile.band} - {profile.city}</span>
              <p>{profile.style}</p>
              <b>{profile.rating} rating - {profile.plays} verified plays</b>
            </a>
          </article>
        );
      })}
    </div>
  ) : (
    <CommunityEmptyState {...emptyState} navigate={navigate} />
  );
  return (
    <section className="page index-page">
      <div className="index-hero">
        <div>
          <p>{showingTrainers ? "Coaches" : activeFilter === "nearby" ? "Players" : copy.eyebrow || "Players"}</p>
          <h1>{showingTrainers ? `Find badminton coaches and trainers in ${activeLocation}.` : copy.playersTitle || "Find rated players and invite the right people to your session."}</h1>
          <span>{showingTrainers ? "Compare published coaching offers, locations, rates and availability before requesting a lesson." : "Find players whose level, location and availability match your next game."}</span>
        </div>
        <div className="index-actions">
          <button type="button" onClick={() => navigate(showingTrainers ? "coachProfile" : "profileSetup")}>{showingTrainers ? "Create coach profile" : "Build profile"}</button>
          <button type="button" onClick={() => navigate(showingTrainers ? "playersIndex" : "ratings", showingTrainers ? { activeFilter: "nearby" } : {})}>{showingTrainers ? "Find players" : "How ratings work"}</button>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <DirectoryDiscoverySurface
        label={showingTrainers ? "Coaches" : "Players"}
        title={`${showingTrainers ? "Coaches" : "Players"} in ${activeLocation}`}
        items={profileMapItems}
        searchCriteria={searchCriteria}
        emptyTitle={`No ${showingTrainers ? "coach" : "player"} locations in ${activeLocation}`}
        emptyText={`Published ${showingTrainers ? "coaches" : "players"} appear here after adding a public location to their profile.`}
        navigate={navigate}
        count={profileCards.length + list.length}
      >{profileList}</DirectoryDiscoverySurface>
    </section>
  );
}

function CourtAvailabilityPage({ city, venues, navigate, activeFilter, searchCriteria, osmCentreStatus, osmCentreError, usingApiCentres }) {
  const copy = filterContent[activeFilter] || {};
  const title = copy.courtsTitle ? copy.courtsTitle.replace("Melbourne", city) : `Compare court hire details in ${locationTitle(searchCriteria, city)}.`;
  const visibleVenues = venues.filter((venue) => venueMatchesFilter(venue, activeFilter));
  const showCentreEmpty = usingApiCentres && !visibleVenues.length;
  return (
    <section className="page index-page">
      <div className="index-hero">
        <div>
          <p>{copy.eyebrow || "Court hire"}</p>
          <h1>{title}</h1>
          <span>Players can compare real centre facts like court count, price, contact details and opening hours before requesting a booking.</span>
        </div>
        <div className="index-actions">
          <button type="button" onClick={() => navigate("results")}>Map view</button>
          <button type="button" onClick={() => navigate("results")}>Find centres</button>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <OsmStatusNotice status={osmCentreStatus} error={osmCentreError} />
      <div className="availability-board">
        {showCentreEmpty ? <CentreEmptyState searchCriteria={searchCriteria} status={osmCentreStatus} error={osmCentreError} navigate={navigate} /> : visibleVenues.map((venue) => (
          <article key={venue.id}>
            <VenueMedia venue={venue} className="availability-media" mapFallback="any" />
            <div>
              <strong>{venue.name}</strong>
              <span className="venue-address-line">{getVenueAddressLabel(venue)} <HoursStatus venue={venue} /></span>
              <div className="slot-row">
                {hasVenuePrice(venue) && (
                  <a
                    href={routeHref("venue", { venue })}
                    onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}
                  >
                    Price <b><PriceStatus venue={venue} compact /></b>
                  </a>
                )}
                {["Courts", "Contact"].map((label) => (
                  <a
                    href={routeHref("venue", { venue })}
                    key={label}
                    onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}
                  >
                    {label} <b>{label === "Courts" ? <CourtStatus venue={venue} /> : <ContactStatus venue={venue} />}</b>
                  </a>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const blogDateLabel = (value = "") => new Intl.DateTimeFormat("en", {
  day: "numeric",
  month: "long",
  year: "numeric"
}).format(new Date(`${value}T00:00:00Z`));

function BlogPostCard({ post, navigate, featured = false }) {
  return (
    <article className={`blog-card${featured ? " featured" : ""}`}>
      <a href={`/blog/${post.slug}`} onClick={(event) => handleSeoRouteClick(event, navigate, "blogArticle", { blogPost: post })} className="blog-card-media">
        <img src={post.image} alt={post.imageAlt} width="1800" height="1125" loading={featured ? "eager" : "lazy"} decoding="async" />
      </a>
      <div className="blog-card-copy">
        <span>{post.category}</span>
        <h2><a href={`/blog/${post.slug}`} onClick={(event) => handleSeoRouteClick(event, navigate, "blogArticle", { blogPost: post })}>{post.title}</a></h2>
        <p>{post.description}</p>
        <div><time dateTime={post.publishedAt}>{blogDateLabel(post.publishedAt)}</time><small>{post.readingTime}</small></div>
        <a href={`/blog/${post.slug}`} onClick={(event) => handleSeoRouteClick(event, navigate, "blogArticle", { blogPost: post })}>Read guide <ChevronRight size={17} /></a>
      </div>
    </article>
  );
}

function BlogIndexPage({ posts, navigate }) {
  return (
    <section className="page blog-index-page">
      <header className="blog-index-hero">
        <div>
          <p>Badminta journal</p>
          <h1>Practical badminton guides for better games and stronger communities</h1>
          <span>Clear advice for finding players, organising social sessions, choosing centres, booking courts, coaching and running badminton events.</span>
        </div>
        <button type="button" onClick={() => navigate("filterHub", { activeFilter: "nearby", activeResultType: "overview" })}>Explore badminton near you</button>
      </header>
      <div className="blog-featured-grid">
        {posts.map((post, index) => <BlogPostCard post={post} navigate={navigate} featured={index === 0} key={post.slug} />)}
      </div>
      <section className="blog-topic-guides" aria-labelledby="blog-topic-heading">
        <div className="blog-topic-intro">
          <p>Explore by goal</p>
          <h2 id="blog-topic-heading">Badminton advice built around real playing decisions</h2>
          <span>Badminta guides connect useful advice with the next action: finding a compatible player, joining a well-run session or choosing a court that suits the game.</span>
        </div>
        <div className="blog-topic-grid">
          <article><UserCircle size={23} /><h3>Find players</h3><p>Learn how to compare level, location and availability, create a useful public profile and arrange a safe first game.</p><button type="button" onClick={() => navigate("playersIndex")}>Browse players <ChevronRight size={16} /></button></article>
          <article><UsersRound size={23} /><h3>Build social groups</h3><p>Plan recurring sessions, fair capacity, transparent fees, player rotations and communication that keeps the group reliable.</p><button type="button" onClick={() => navigate("groupsIndex")}>Browse groups <ChevronRight size={16} /></button></article>
          <article><Building2 size={23} /><h3>Choose courts</h3><p>Compare court conditions, full prices, amenities, booking states and cancellation rules before paying.</p><button type="button" onClick={() => navigate("results")}>Find centres <ChevronRight size={16} /></button></article>
        </div>
      </section>
      <section className="blog-index-band">
        <div>
          <p>Start playing</p>
          <h2>Turn useful reading into your next game</h2>
          <span>Search your area, compare real profiles and organise the booking or session from one place.</span>
        </div>
        <div>
          <button type="button" onClick={() => navigate("playersIndex")}>Find players</button>
          <button type="button" onClick={() => navigate("groupsIndex")}>Browse groups</button>
          <button type="button" onClick={() => navigate("results")}>Find centres</button>
        </div>
      </section>
    </section>
  );
}

function BlogArticlePage({ post, posts, navigate }) {
  if (!post) return <BlogIndexPage posts={posts} navigate={navigate} />;
  const related = posts.filter((item) => item.slug !== post.slug).slice(0, 2);
  const shareArticle = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: post.title, text: post.description, url });
      else await navigator.clipboard.writeText(url);
    } catch {
      // Closing the native share sheet is not an error the reader needs to see.
    }
  };
  return (
    <article className="page blog-article-page">
      <header className="blog-article-hero">
        <div className="blog-article-heading">
          <span>{post.category}</span>
          <h1>{post.title}</h1>
          <p>{post.description}</p>
          <div className="blog-byline">
            <strong>Badminta editorial team</strong>
            <time dateTime={post.updatedAt}>Reviewed {blogDateLabel(post.updatedAt)}</time>
            <small>{post.readingTime}</small>
          </div>
        </div>
        <figure>
          <img src={post.image} alt={post.imageAlt} width="1800" height="1125" loading="eager" decoding="async" fetchPriority="high" />
          <figcaption>Photo by <a href={post.imageCredit.url} target="_blank" rel="noreferrer">{post.imageCredit.name}</a> · {post.imageCredit.licence}</figcaption>
        </figure>
      </header>
      <div className="blog-article-layout">
        <aside className="blog-article-toc">
          <strong>In this guide</strong>
          <a href="#quick-answer">Quick answer</a>
          {post.sections.map((section, index) => <a href={`#section-${index + 1}`} key={section.heading}>{section.heading}</a>)}
          <a href="#frequently-asked-questions">Common questions</a>
          <button type="button" onClick={shareArticle}><Send size={15} /> Share guide</button>
        </aside>
        <div className="blog-article-body">
          <p className="blog-article-intro">{post.intro}</p>
          <section className="blog-answer-box" id="quick-answer">
            <p>Quick answer</p>
            <h2>{post.title.replace(/:.+$/, "")}</h2>
            <span>{post.answer}</span>
          </section>
          <section className="blog-key-takeaways">
            <h2>Key takeaways</h2>
            <ul>{post.keyTakeaways.map((item) => <li key={item}><Check size={18} /><span>{item}</span></li>)}</ul>
          </section>
          {post.sections.map((section, index) => (
            <section id={`section-${index + 1}`} key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>
          ))}
          <section className="blog-checklist">
            <p>Quick checklist</p>
            <h2>Before you continue</h2>
            <ul>{post.checklist.map((item) => <li key={item}><Check size={17} /> <span>{item}</span></li>)}</ul>
          </section>
          <section className="blog-faq" id="frequently-asked-questions">
            <p>Frequently asked questions</p>
            <h2>Common questions about {post.category.toLowerCase()}</h2>
            <div>{post.faq.map(([question, answer]) => <details key={question}><summary>{question}<ChevronDown size={18} /></summary><p>{answer}</p></details>)}</div>
          </section>
          <section className="blog-sources">
            <h2>Sources and further reading</h2>
            <p>Badminta articles combine practical marketplace guidance with established sporting resources. External policies and programmes remain controlled by their publishers.</p>
            {post.sources.map((source) => <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>{source.label} <ExternalLink size={15} /></a>)}
          </section>
          <section className="blog-article-cta">
            <div><p>Next step</p><h2>Put this guide into practice</h2></div>
            <div>
              <button type="button" onClick={() => navigate(post.primaryCta.page, post.primaryCta.payload || {})}>{post.primaryCta.label}</button>
              <button type="button" className="secondary" onClick={() => navigate(post.secondaryCta.page, post.secondaryCta.payload || {})}>{post.secondaryCta.label}</button>
            </div>
          </section>
        </div>
      </div>
      <section className="blog-related">
        <p>Continue reading</p>
        <h2>More from the Badminta journal</h2>
        <div>{related.map((item) => <BlogPostCard post={item} navigate={navigate} key={item.slug} />)}</div>
      </section>
    </article>
  );
}

function EventCard({ event, navigate, compact = false, accountProfile }) {
  const image = getPublicProfileImage(getEventImage(event));
  const fallbackMedia = getStockMediaFallback("event", `${event?.id || event?.title || "event"}|directory`);
  return (
    <article className={compact ? "event-card compact" : "event-card"}>
      <div className="event-card-media">
        {image
          ? <img src={image} alt="" />
          : <StockMediaImage media={fallbackMedia} alt={`${event?.title || "Badminton event"} event image`} />}
      </div>
      <div>
        <span>{formatDateLabel(event.date)} · {formatTimeLabel(event.time)}</span>
        <strong>{event.title}</strong>
        <p>{getEventVenueLabel(event)} · {event.eventType} · {event.skillBand}</p>
        <div className="event-card-meta">
          <em>{getEventTicketPriceLabel(event)}</em>
          <em>{getEventCapacityLabel(event, accountProfile)}</em>
        </div>
        <a href={routeHref("event", { event })} onClick={(clickEvent) => handleSeoRouteClick(clickEvent, navigate, "event", { event })}>Open event</a>
      </div>
    </article>
  );
}

function EventsPage({ city, events, venues, navigate, searchCriteria, accountProfile, activeFilter = "nearby" }) {
  const activeLocation = locationTitle(searchCriteria, city);
  const emptyState = emptyEventState(activeLocation);
  const organiserEvents = getOwnerEvents(accountProfile, "organiser").length;
  const centreEvents = getOwnerEvents(accountProfile, "centre").length;
  const discoveryLocation = searchCriteria.location || { type: "city", city };
  const visibleEvents = events
    .filter((event) => recordMatchesSeoArea(event, discoveryLocation, "event"))
    .filter((event) => eventMatchesFilter(event, activeFilter));
  const eventMapItems = useMemo(() => visibleEvents.map((event) => eventDirectoryMapItem(event, venues)), [visibleEvents, venues]);
  const eventList = visibleEvents.length ? (
    <div className="event-grid">
      {visibleEvents.map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyState} navigate={navigate} />
  );
  return (
    <section className="page index-page">
      <div className="index-hero">
        <div>
          <p>Events</p>
          <h1>Badminton events, tournaments and clinics in {city}.</h1>
          <span>Centre owners and social organisers can publish tournaments, clinics, open days, club nights and paid community events.</span>
        </div>
        <div className="index-actions">
          <button type="button" onClick={() => navigate("createEvent")}>Create event</button>
          <button type="button" onClick={() => navigate("eventManager")}>My events</button>
          <button type="button" onClick={() => navigate("joinedEvents")}>Joined events</button>
          <button type="button" onClick={() => navigate("signup")}>Get updates</button>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <div className="event-logic-strip">
        {[
          ["Public events", visibleEvents.length],
          ["Organiser events", organiserEvents],
          ["Centre events", centreEvents],
          ["Payment ready", "Join flow"]
        ].map(([label, value]) => (
          <span key={label}><b>{value}</b>{label}</span>
        ))}
      </div>
      <DirectoryDiscoverySurface
        label="Events"
        title={`Events in ${activeLocation}`}
        items={eventMapItems}
        searchCriteria={searchCriteria}
        emptyTitle={`No event locations in ${activeLocation}`}
        emptyText="Published events appear here after an organiser or centre adds a real venue or local area."
        navigate={navigate}
        count={visibleEvents.length}
      >{eventList}</DirectoryDiscoverySurface>
    </section>
  );
}

function EventDetail({ event, navigate, accountProfile, saveAccountItem, saveAccountProfile, publicReviews = [] }) {
  if (!event) {
    return (
      <section className="page public-profile-page">
        <CommunityEmptyState
          type="events"
          title="Event not found"
          text="This event could not be loaded. Open the events page to see currently published events."
          primary={{ label: "View events", target: "events" }}
          secondary={{ label: "Get updates", target: "signup" }}
          icon={Star}
          navigate={navigate}
        />
      </section>
    );
  }
  const image = getEventImage(event);
  const venue = getEventVenue(event);
  const eventLinks = cleanLinkRows(event.socialLinks, "Event link");
  const attendeeCount = getPublicEventAttendees(event, accountProfile).length;
  const eventReviewTarget = getEventReviewTarget(event);
  const eventReviewSummary = publicReviewSummary(accountProfile, ["event"], event.title, { publicReviews, target: eventReviewTarget });
  const eventMediaLinks = [
    ...(image ? [{ label: "Event image", url: image }] : []),
    ...cleanLinkRows(event.mediaLinks, "Event media")
  ];
  const eventPriceLabel = getEventTicketPriceLabel(event);
  const eventIsFree = getEventPricingMode(event) === "free";
  const eventPaymentReady = hasEventTicketPrice(event);
  const eventOfflineSettlement = eventUsesOfflineSettlement(event);
  const saveEvent = () => {
    saveAccountItem?.(savedItemFromEvent(event));
    navigate("saved");
  };
  const messageEventOwner = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: event.ownerType === "centre" ? "centre" : "organiser",
    targetCollection: eventReviewTarget.targetCollection,
    targetId: eventReviewTarget.targetId,
    subject: event.title,
    threadType: "Event enquiry",
    text: `A player opened an event enquiry for ${event.title}. Confirm date, venue, availability, payment and refund details before they attend.`,
    meta: [formatDateLabel(event.date), getEventVenueLabel(event), eventPriceLabel],
    context: [["Event", event.title], ["Venue", getEventVenueLabel(event)], ["When", `${formatDateLabel(event.date)}, ${formatTimeLabel(event.time)}`], ["Owner", getEventOwnerLabel(event)]],
    actions: [["Browse events", "events"], ["Saved items", "saved"], ["Messages", "messages"]]
  });
  return (
    <section className="page public-profile-page public-listing-page">
      <PublicListingHero
        variant="event"
        eyebrow={`${event.eventType} · ${eventOwnerRoleLabel(event.ownerType)}`}
        title={event.title}
        subtitle={[getEventVenueLabel(event), formatDateLabel(event.date), formatTimeLabel(event.time), event.skillBand].filter(Boolean).join(" - ")}
        image={image}
        icon={Star}
        facts={[eventPriceLabel, getEventCapacityLabel(event, accountProfile), event.status === "published" ? "Published" : "Draft"]}
        qualityLabel="Event reviews"
        qualityValue={eventReviewSummary.label}
        qualityMeta={eventReviewSummary.meta}
        primaryAction={["Join event", () => navigate("eventJoin", { event, bookingType: "event" })]}
        secondaryAction={["Message owner", messageEventOwner]}
      />
      <PublicMediaShowcase
        title="Event media"
        links={eventMediaLinks}
        exclude={[image]}
        emptyTitle="No event media added yet"
        emptyText="Photos, posters and venue media help players understand the event before registering."
      />
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [CalendarDays, "When", `${formatDateLabel(event.date)}, ${formatTimeLabel(event.time)}-${formatTimeLabel(event.endTime)}`, "Event time"],
            [MapPin, "Venue", getEventVenueLabel(event), getEventSuburb(event) || "Area pending"],
            [Gauge, "Level", event.skillBand || "Open level", "Who should attend"],
            [CircleDollarSign, "Price", eventPriceLabel, "Per attendee"]
          ]} />
          <PublicProfileSection eyebrow="Event details" title="Before you register">
            <p className="public-profile-copy">{event.description || "The owner has not added the full event description yet. A useful event page explains the schedule, format, level, what is included, payment rules and check-in details."}</p>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Registration" title="How joining works">
            <div className="event-process-grid">
              {[
                ["1", "Register", "Reserve a place from this event page."],
                ["2", eventIsFree ? "Confirm" : eventOfflineSettlement ? "Pay the host" : "Pay", eventIsFree
                  ? "Confirm your free place; no card payment is required."
                  : eventPaymentReady
                    ? eventOfflineSettlement
                      ? `Reserve on Badminta, then pay ${eventPriceLabel} directly to the host or at the venue.`
                      : `Pay ${eventPriceLabel} per attendee through Badminta Checkout.`
                    : "Payment waits until the owner adds a price."],
                ["3", "Attend", venue ? `Go to ${venue.name}.` : "Check venue details before attending."],
                ["4", "Review", "After the event, leave feedback for the owner."]
              ].map(([step, title, text]) => (
                <article key={step}>
                  <span>{step}</span>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </PublicProfileSection>
          <PublicInteractionGrid
            eyebrow="Event actions"
            title="Join, save or ask a question"
            text="Use these actions when the date, venue, price and level fit what you want to attend."
            actions={[
              [CalendarDays, "Join event", "Reserve your place and continue to payment if required.", () => navigate("eventJoin", { event, bookingType: "event" }), "primary"],
              [Heart, "Save event", "Keep this event in your saved list for later.", saveEvent],
              [Star, "Review event", "Rate the event after attending.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("event", "Review event", event.title, eventReviewTarget) })],
              [MessageCircle, "Ask owner", "Ask the organiser or centre a question.", messageEventOwner],
              [ShieldCheck, "Report event", "Flag a safety, payment or listing issue.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report event", event.title, eventReviewTarget) })]
            ]}
          />
          <PublicProfileSection eyebrow="Attendees" title="Public attendee list">
            <div className="event-attendee-panel">
              <UsersRound size={22} />
              <div>
                <strong>{attendeeCount ? `${attendeeCount} attendee${attendeeCount === 1 ? "" : "s"} public` : "No public attendees yet"}</strong>
                <span>{event.capacity ? `${event.capacity} total places available before waitlist.` : "Capacity has not been finalised."}</span>
              </div>
              <button type="button" onClick={() => navigate("eventJoin", { event, bookingType: "event" })}>Join this event</button>
            </div>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Rules and refunds" title="What happens after booking">
            <div className="public-flow-list">
              {[
                ["Requirements", event.requirements || "Requirements not added yet."],
                ["Refunds", event.refundPolicy || "Refund policy not added yet."],
                ["Owner", `${getEventOwnerLabel(event)} manages questions, attendance and payment issues.`]
              ].map(([title, text], index) => (
                <article key={title}>
                  <span>{index + 1}</span>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </PublicProfileSection>
          {venue && (
            <PublicProfileSection eyebrow="Venue" title="Where this event happens">
              <div className="public-venue-preview">
                <VenueMapEmbed venue={venue} point={getVenueMediaPoint(venue)} className="public-venue-map" />
                <div>
                  <strong>{venue.name}</strong>
                  <span>{getVenueAddressLabel(venue)}</span>
                  <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}>Open centre listing</a>
                </div>
              </div>
            </PublicProfileSection>
          )}
          <PublicProfileSection eyebrow="Event links" title="Official event links">
            <PublicLinkList links={eventLinks} emptyText="No event links added yet." />
          </PublicProfileSection>
        </main>
        <PublicActionPanel
          title="Join event"
          value={eventIsFree ? "Free" : eventPaymentReady ? eventPriceLabel : "Price pending"}
          caption={`${formatDateLabel(event.date)}, ${formatTimeLabel(event.time)} at ${getEventVenueLabel(event)}`}
          rows={[
            [UsersRound, "Capacity", getEventCapacityLabel(event, accountProfile)],
            [ShieldCheck, "Owner", getEventOwnerLabel(event)],
            [WalletCards, "Payment", eventIsFree ? "No payment" : eventPaymentReady ? eventOfflineSettlement ? "Pay host directly" : "Checkout ready" : "Pending price"]
          ]}
          primary={[eventIsFree ? "Register free" : eventOfflineSettlement ? "Reserve a place" : "Join and pay", () => navigate("eventJoin", { event, bookingType: "event" })]}
          secondary={["Ask a question", messageEventOwner]}
          note={eventOfflineSettlement
            ? "Badminta records the reservation, but the host collects payment directly and manages offline payment questions."
            : "Payment, attendance and refund questions go to the centre or organiser running this event."}
          links={eventLinks}
        />
      </div>
    </section>
  );
}

function EventCheckout({ event, navigate, accountProfile, saveAccountProfile }) {
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [ticketCount, setTicketCount] = useState(1);
  if (!event) {
    return (
      <section className="page checkout-page">
        <CommunityEmptyState
          type="events"
          title="Select an event first"
          text="Open a public event before joining or paying."
          primary={{ label: "View events", target: "events" }}
          icon={Star}
          navigate={navigate}
        />
      </section>
    );
  }
  const joinedEvents = getJoinedEvents(accountProfile);
  const alreadyJoined = joinedEvents.some((joinedEvent) => joinedEvent.id === event.id);
  const backendEventId = event.firestoreSourceId || event.serverEventId || event.id || "";
  const eventPrice = eventPriceNumber(event);
  const eventPricingMode = getEventPricingMode(event);
  const hasEventPrice = hasEventTicketPrice(event);
  const isPaidEvent = eventPricingMode === "paid" && eventPrice > 0;
  const eventOfflineSettlement = eventUsesOfflineSettlement(event);
  const eventCurrency = normaliseCurrencyCode(event.currency || marketplaceCurrencyForCountry(event.countryCode || event.location?.countryCode));
  const eventPriceLabel = isPaidEvent ? formatMoney(eventPrice, eventCurrency) : "Free";
  const eventAttendeeCount = getPublicEventAttendees(event, accountProfile).length;
  const eventCapacity = Math.max(0, Number.parseInt(event.capacity, 10) || 0);
  const remainingPlaces = eventCapacity ? Math.max(0, eventCapacity - eventAttendeeCount) : 10;
  const maximumTickets = Math.max(1, Math.min(10, remainingPlaces || 1));
  const eventTotal = isPaidEvent ? eventPrice * ticketCount : 0;
  const eventTotalLabel = isPaidEvent ? formatMoney(eventTotal, eventCurrency) : formatMoney(0, eventCurrency);
  const confirmJoin = async () => {
    setCheckoutMessage("");
    if (alreadyJoined) {
      navigate("joinedEvents");
      return;
    }
    if (eventCapacity && remainingPlaces < ticketCount) {
      setCheckoutMessage(remainingPlaces ? `Only ${remainingPlaces} place${remainingPlaces === 1 ? " is" : "s are"} currently available.` : "This event is full. Registration is no longer available.");
      return;
    }
    if (!hasEventPrice) {
      setCheckoutMessage("The host must publish a ticket price or mark this event as free before registration can open.");
      return;
    }
    if (!firebaseFunctions || !firebaseAuth?.currentUser) {
      setCheckoutMessage("Log in to reserve a place. Your registration and payment status will stay attached to your Badminta account.");
      return;
    }
    if (!backendEventId) {
      setCheckoutMessage("This event is not connected to a published Badminta event record yet. Ask the host to publish it again.");
      return;
    }
    setCheckoutBusy(true);
    try {
      const joinRequest = await callMarketplaceFunction("createSessionJoinRequest", {
        kind: "event",
        eventId: backendEventId,
        ticketCount,
        participantName: accountProfile.fullName || "",
        participantEmail: accountProfile.email || "",
        idempotencyKey: `event-${backendEventId}-${firebaseAuth.currentUser.uid}`
      });
      let checkout = null;
      if (isPaidEvent && joinRequest.checkoutRequired === true) {
        checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
          kind: "event",
          sourceId: joinRequest.joinRequestId,
          idempotencyKey: joinRequest.joinRequestId,
          successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href
        });
      }
      const joinedSnapshot = normaliseJoinedEvent({
        ...event,
        joinedAt: new Date().toISOString(),
        joinedStatus: isPaidEvent && joinRequest.checkoutRequired === true ? "Pending payment" : "Registered",
        paymentStatus: isPaidEvent
          ? joinRequest.settlementMode === "offline_settlement"
            ? "Pay host directly"
            : "Badminta checkout opened"
          : "Free registration",
        settlementMode: joinRequest.settlementMode || event.settlementMode || "not_required",
        checkoutRequired: joinRequest.checkoutRequired === true,
        ticketCount,
        joinRequestId: joinRequest.joinRequestId,
        checkoutSessionId: checkout?.checkoutSessionId || ""
      });
      const nextJoinedEvents = alreadyJoined
        ? joinedEvents.map((joinedEvent) => joinedEvent.id === event.id ? joinedSnapshot : joinedEvent)
        : [...joinedEvents, joinedSnapshot];
      const patch = { joinedEvents: nextJoinedEvents };
      if (isPaidEvent) {
        const paidAmount = formatCurrencyCents(joinRequest.totalCents, joinRequest.currency || eventCurrency);
        const directSettlement = joinRequest.settlementMode === "offline_settlement";
        patch.payments = [{
          id: checkout?.paymentId || joinRequest.joinRequestId,
          kind: "event",
          title: "Event registration",
          amount: paidAmount,
          amountCents: joinRequest.totalCents || amountToMinorUnits(eventPrice, eventCurrency),
          currency: joinRequest.currency || eventCurrency,
          subject: event.title,
          status: directSettlement ? "Pay host directly" : "Badminta checkout opened",
          provider: directSettlement ? "offline_settlement" : "stripe_checkout_session",
          checkoutReference: checkout?.checkoutSessionId || "",
          createdAt: new Date().toISOString()
        }, ...getAccountPayments(accountProfile)];
      }
      saveAccountProfile?.(patch);
      if (checkout?.checkoutUrl) {
        window.location.assign(checkout.checkoutUrl);
        return;
      }
      navigate("joinedEvents");
    } catch (error) {
      setCheckoutMessage(marketplaceFunctionErrorMessage(error));
    } finally {
      setCheckoutBusy(false);
    }
  };
  return (
    <section className="page checkout-page">
      <div className="checkout-title">
        <div>
          <p>Event checkout</p>
          <h1>Join {event.title}</h1>
          <span>{getEventVenueLabel(event)} · {formatDateLabel(event.date)} · {formatTimeLabel(event.time)}</span>
        </div>
        <button type="button" onClick={() => navigate("event", { event })}>Back to event</button>
      </div>
      <div className="checkout-layout">
        <section className="booking-card">
          <h2>Registration</h2>
          <div className="booking-fields">
            <div className="event-ticket-quantity">
              <span>Places</span>
              <span className="event-ticket-stepper">
                <button type="button" aria-label="Remove one place" onClick={() => setTicketCount((count) => Math.max(1, count - 1))} disabled={ticketCount <= 1 || checkoutBusy}>-</button>
                <strong>{ticketCount}</strong>
                <button type="button" aria-label="Add one place" onClick={() => setTicketCount((count) => Math.min(maximumTickets, count + 1))} disabled={ticketCount >= maximumTickets || checkoutBusy}>+</button>
              </span>
            </div>
            <div><span>Price per place</span><strong>{hasEventPrice ? eventPriceLabel : "Pending"}</strong></div>
            <div><span>Capacity</span><strong>{getEventCapacityLabel(event, accountProfile)}</strong></div>
          </div>
          <div className="event-checkout-note managed-checkout-note">
            <ShieldCheck size={20} />
            <span><strong>Badminta manages registration.</strong> {isPaidEvent
              ? eventOfflineSettlement
                ? `Your place is confirmed here, then you pay the ${eventOwnerRoleLabel(event.ownerType).toLowerCase()} directly or at the venue. Badminta does not hold those funds.`
                : `Checkout, receipts, refunds and the ${eventOwnerRoleLabel(event.ownerType).toLowerCase()} payout stay connected to this ticket.`
              : "Free registrations still reserve capacity and stay visible to the host and attendee."}</span>
          </div>
          {checkoutMessage && <div className="auth-message">{checkoutMessage}</div>}
          <button type="button" onClick={confirmJoin} disabled={checkoutBusy || !hasEventPrice || (!alreadyJoined && eventCapacity > 0 && remainingPlaces === 0)}>{checkoutBusy
            ? eventOfflineSettlement ? "Reserving place" : "Preparing checkout"
            : !hasEventPrice
              ? "Waiting for ticket price"
              : alreadyJoined
                ? "View my registration"
                : isPaidEvent
                  ? eventOfflineSettlement ? "Reserve and pay host" : "Reserve ticket and pay"
                  : "Register free"}</button>
        </section>
        <aside className="payment-summary">
          <strong>{event.title}</strong>
          <span>{event.description || "Event details will be confirmed by the owner."}</span>
          <div><span>{ticketCount === 1 ? "Event fee" : `${ticketCount} places`}</span><b>{hasEventPrice ? eventTotalLabel : "Pending"}</b></div>
          <div><span>Badminta service fee</span><b>{isPaidEvent && !eventOfflineSettlement ? "Calculated at checkout" : "None"}</b></div>
          <div><span>{isPaidEvent ? "Ticket subtotal" : "Total today"}</span><b>{hasEventPrice ? eventTotalLabel : "Pending"}</b></div>
          <div><span>Payment</span><b>{!hasEventPrice ? "Not open" : isPaidEvent ? eventOfflineSettlement ? "Pay host directly" : "Badminta Checkout" : "No payment"}</b></div>
        </aside>
      </div>
    </section>
  );
}

function ReviewFlowPage({ reviewContext, accountProfile, saveAccountProfile, navigate }) {
  const context = reviewContext || reviewContextFor("general");
  const isReportFlow = context.kind === "trust";
  const ratingEligible = ratingEligibleReviewKinds.has(context.kind || "") && !isReportFlow;
  const [score, setScore] = useState(ratingEligible ? 5 : 0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const existingReviews = Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [];
  const submitReview = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitMessage("");
    const cleanComment = comment.trim();
    const reviewTarget = normaliseReviewTarget(context);
    const review = {
      id: `review-${Date.now()}`,
      kind: context.kind || "general",
      title: context.title || "Review",
      subject: context.subject || "",
      targetCollection: reviewTarget.collection,
      targetId: reviewTarget.id,
      subjectCollection: reviewTarget.subjectCollection,
      subjectId: reviewTarget.subjectId,
      score: ratingEligible ? score : null,
      comment: cleanComment,
      status: isReportFlow ? "Submitted to trust queue" : "Pending moderation",
      ratingEligible,
      createdAt: new Date().toISOString()
    };
    let nextReview = review;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      try {
        if (isReportFlow) {
          const result = await callMarketplaceFunction("submitSupportReport", {
            kind: "trust",
            title: context.title || "Report issue",
            subject: context.subject || "Badminta profile",
            subjectType: reviewKindLabels[context.kind] || "Trust report",
            subjectId: review.subjectId || review.targetId,
            comment: cleanComment,
            context: {
              localReviewId: review.id,
              source: "reviewFlow",
              visibility: "private support",
              targetCollection: review.targetCollection,
              targetId: review.targetId,
              subjectCollection: review.subjectCollection,
              subjectId: review.subjectId
            }
          });
          nextReview = { ...review, id: result.reportId || review.id, serverReportId: result.reportId || "", status: result.status || review.status };
        } else {
          const result = await callMarketplaceFunction("submitPublicReview", {
            kind: review.kind,
            title: review.title,
            subject: review.subject,
            subjectType: reviewKindLabels[context.kind] || "Public review",
            subjectId: review.subjectId || review.targetId,
            subjectCollection: review.subjectCollection || review.targetCollection,
            targetCollection: review.targetCollection,
            targetId: review.targetId,
            score: ratingEligible ? score : null,
            comment: cleanComment,
            context: {
              localReviewId: review.id,
              source: "reviewFlow",
              visibility: "pending moderation"
            }
          });
          nextReview = {
            ...review,
            id: result.reviewId || review.id,
            serverReviewId: result.reviewId || "",
            status: result.status || review.status,
            moderationStatus: result.moderationStatus || "pending_review"
          };
        }
      } catch (error) {
        setSubmitMessage(marketplaceFunctionErrorMessage(error));
        setSubmitting(false);
        return;
      }
    }
    saveAccountProfile({ reviews: [nextReview, ...existingReviews] });
    setSubmitted(true);
    setSubmitting(false);
  };
  const reviewMessageRole = messageRoleForReviewKind(context.kind);
  const reviewMessageTarget = normaliseReviewTarget(context);
  const messageInstead = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: reviewMessageRole,
    targetCollection: reviewMessageTarget.collection,
    targetId: reviewMessageTarget.id,
    subject: context.subject || context.title || "Review conversation",
    threadType: isReportFlow ? "Trust support" : "Review follow-up",
    text: `${isReportFlow ? "Trust support" : "Review follow-up"} started from ${context.title || "a review flow"}. Keep the subject, rating context and next action visible before continuing.`,
    meta: [reviewKindLabels[context.kind], context.subject, isReportFlow ? "Private report" : "Public feedback"],
    context: [["Subject", context.subject || "Badminta profile"], ["Type", reviewKindLabels[context.kind] || "Review"], ["Visibility", isReportFlow ? "Private support" : "Reviewable feedback"]],
    actions: [["Open messages", "messages"], ["Saved items", "saved"], ["Help", "help"]]
  });

  return (
    <section className="page review-flow-page">
      <div className="screen-head">
        <p>Reviews and recommendations</p>
        <h1>{context.title || "Leave a review"}</h1>
        <button type="button" onClick={() => navigate("account")}>Back to account</button>
      </div>
      <div className="review-flow-layout">
        <section className="review-card">
          <div className="review-subject">
            {isReportFlow ? <ShieldCheck size={22} /> : <Star size={22} />}
            <div>
              <strong>{context.subject || "Badminta profile"}</strong>
              <span>{isReportFlow ? "Reports are private and do not affect public ratings." : reviewKindLabels[context.kind] || "Feedback is saved to your account and can be shown publicly after review."}</span>
            </div>
          </div>
          {submitted ? (
            <div className="review-submitted">
              <BadgeCheck size={28} />
              <strong>{isReportFlow ? "Report submitted" : "Review saved"}</strong>
              <span>{isReportFlow ? "Your report is saved privately in your account activity for support review." : "Your feedback is stored in your account review history. It can be checked before it appears on profiles."}</span>
              <button type="button" onClick={() => navigate("account")}>Done</button>
            </div>
          ) : (
            <>
              {ratingEligible && (
                <div className="score-picker" role="group" aria-label="Score">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button type="button" key={value} className={score >= value ? "active" : ""} onClick={() => setScore(value)}>
                      <Star size={19} />
                      <span>{value}</span>
                    </button>
                  ))}
                </div>
              )}
              <label className="review-comment">
                <span>{isReportFlow ? "Issue details" : "Comment"}</span>
                <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder={isReportFlow ? "Explain the safety, payment, listing, level or moderation issue so support can follow up." : "Write what happened, why you recommend this player/coach/group/centre, or what should be improved."} />
              </label>
              {submitMessage && <div className="auth-message error">{submitMessage}</div>}
              <div className="review-actions">
                <button type="button" onClick={submitReview} disabled={submitting}>{submitting ? "Submitting..." : isReportFlow ? "Submit report" : "Submit review"}</button>
                <button type="button" onClick={messageInstead}>Message instead</button>
              </div>
            </>
          )}
        </section>
        <aside className="review-history-panel">
          <strong>Recent feedback activity</strong>
          {existingReviews.length ? existingReviews.slice(0, 4).map((review) => (
            <div key={review.id}>
              <span>{review.title}</span>
              <b>{isRatingEligibleReview(review) ? `${Number(review.score).toFixed(0)}/5` : "Private"}</b>
            </div>
          )) : (
            <p>No feedback history yet. Reviews, recommendations and reports you submit from public profiles will appear here.</p>
          )}
        </aside>
      </div>
    </section>
  );
}

function PaymentFlowPage({ paymentContext, accountProfile = defaultAccountProfile, saveAccountProfile, navigate }) {
  const context = paymentContext || paymentContextFor("general");
  const blueprint = stripePaymentBlueprintFor(context.kind);
  const amountReady = Boolean(context.amount && !/pending|missing/i.test(String(context.amount)));
  const backendSourceId = context.bookingId || context.sourceId || context.firestoreSourceId || "";
  const backendCheckoutKind = context.kind === "centre" ? "booking" : ["booking", "social", "event", "coach", "membership"].includes(context.kind) ? context.kind : "";
  const backendCheckoutReady = Boolean(firebaseFunctions && firebaseAuth?.currentUser && backendSourceId && backendCheckoutKind);
  const paymentReady = Boolean(backendCheckoutReady && amountReady);
  const paymentNeedsSource = Boolean(amountReady && !backendCheckoutReady);
  const paymentState = paymentReady ? "ready" : paymentNeedsSource ? "needs-link" : "needs-price";
  const paymentStateLabel = backendCheckoutReady ? "Secure checkout ready" : paymentNeedsSource ? "Booking record required" : "Pricing incomplete";
  const setupTarget = {
    booking: "courtManager",
    centre: "courtManager",
    coach: "coachProfile",
    event: "createEvent",
    social: "createGroup",
    membership: "centrePayments"
  }[context.kind] || "account";
  const setupActionLabel = {
    booking: "Complete court payment setup",
    centre: "Complete centre payment setup",
    coach: "Complete coach payment setup",
    event: "Complete event payment setup",
    social: "Complete group payment setup",
    membership: "Complete membership setup"
  }[context.kind] || "Complete payment setup";
  const payments = getAccountPayments(accountProfile);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedPaymentId, setConfirmedPaymentId] = useState("");
  const [backendMessage, setBackendMessage] = useState("");
  const confirmPayment = () => {
    const paymentId = `payment-${Date.now()}`;
    const payment = {
      id: paymentId,
      kind: context.kind || "general",
      title: context.title || paymentKindLabels[context.kind] || "Payment",
      amount: context.amount || "Pending",
      subject: context.subject || "",
      status: amountReady ? "Stripe checkout pending" : "Needs live price",
      createdAt: new Date().toISOString()
    };
    saveAccountProfile?.({ payments: [payment, ...payments] });
    setConfirmedPaymentId(paymentId);
    setConfirmed(true);
  };
  const openManagedCheckout = async () => {
    setBackendMessage("");
    if (backendCheckoutReady && amountReady) {
      const reference = stripeReferenceId(backendCheckoutKind, backendSourceId || context.subject || context.title);
      try {
        const data = await callMarketplaceFunction("createStripeCheckoutSession", {
          kind: backendCheckoutKind,
          bookingId: backendCheckoutKind === "booking" ? backendSourceId : undefined,
          sourceId: backendCheckoutKind === "booking" ? undefined : backendSourceId,
          idempotencyKey: reference,
          successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href
        });
        saveAccountProfile?.({
          payments: [{
            id: data.paymentId || reference,
            kind: context.kind || "general",
            title: context.title || paymentKindLabels[context.kind] || "Payment",
            amount: context.amount || "Pending",
            subject: context.subject || "",
            status: "Backend Stripe checkout opened",
            provider: "stripe_checkout_session",
            checkoutReference: data.checkoutSessionId || "",
            createdAt: new Date().toISOString()
          }, ...payments]
        });
        setConfirmedPaymentId(data.paymentId || reference);
        setConfirmed(true);
        if (data.checkoutUrl) window.location.assign(data.checkoutUrl);
        return;
      } catch (error) {
        setBackendMessage(marketplaceFunctionErrorMessage(error));
        return;
      }
    }
    setBackendMessage(amountReady
      ? "Complete the booking or registration step first so Badminta can create a secure checkout against a real marketplace record."
      : "Add a real price before checkout can open.");
  };
  const primaryPaymentAction = () => {
    if (paymentReady) {
      openManagedCheckout();
      return;
    }
    navigate(setupTarget);
  };
  const openPaymentMessages = () => navigate("messages", { messageFocus: { roleId: "player", threadId: `payment-${confirmedPaymentId}` } });
  const workflowRows = [
    [WalletCards, "Checkout", backendCheckoutReady ? "Secure session ready" : paymentNeedsSource ? "Needs booking record" : "Price required", blueprint.checkout],
    [BadgeDollarSign, "Payout owner", blueprint.owner, blueprint.payout],
    [ClipboardCheck, "Ledger", "Account synced", blueprint.ledger],
    [ShieldCheck, "Refunds", "Policy controlled", blueprint.refund]
  ];
  const readinessRows = [
    ["Price", amountReady ? context.amount : "Missing", amountReady ? "ready" : "blocked"],
    ["Backend source", backendSourceId ? "Connected" : "Missing", backendSourceId ? "ready" : "blocked"],
    ["Stripe Connect", backendCheckoutReady ? "Callable ready" : "Owner setup required", backendCheckoutReady ? "ready" : "blocked"]
  ];
  return (
    <section className="page checkout-page">
      <div className="checkout-title">
        <div>
          <p>Payment and confirmation</p>
          <h1>{context.title || "Continue payment"}</h1>
          <span>{context.subject || "Badminta payment setup"}</span>
        </div>
        <button className="checkout-back-button" type="button" onClick={() => navigate("account")}><ChevronLeft size={17} /> Account</button>
      </div>
      <div className="checkout-layout payment-flow-layout">
        <section className="booking-card payment-flow-card">
          <div className="payment-flow-card-head">
            <span className="payment-flow-icon"><CreditCard size={24} /></span>
            <div>
              <p>Badminta Checkout</p>
              <h2>{confirmed ? "Payment setup saved" : "Payment setup status"}</h2>
            </div>
            <em className={`payment-state-pill ${paymentState}`}>{paymentStateLabel}</em>
          </div>
          {confirmed ? (
            <div className="review-submitted">
              <BadgeCheck size={28} />
              <strong>{amountReady ? "Stripe checkout is ready to connect" : "Add a real price before charging"}</strong>
              <span>This payment setup is now attached to account history, dashboards, messages and payout records.</span>
              <button type="button" onClick={openPaymentMessages}>Message owner</button>
            </div>
          ) : (
            <>
              <div className={`event-checkout-note payment-flow-note ${paymentState}`}>
                <WalletCards size={20} />
                <span>{backendCheckoutReady && amountReady ? `${context.amount} will open a server-created Stripe Checkout Session. Badminta never sees card details or stores secret keys in the browser.` : amountReady ? "Complete the real booking or registration record and connect the owner payout account before checkout can open." : "Live card charging stays blocked until the owner adds a real price and payout account."}</span>
              </div>
              {backendMessage && <div className="auth-message">{backendMessage}</div>}
              <div className="payment-readiness-strip" aria-label="Payment readiness">
                {readinessRows.map(([label, value, state]) => (
                  <article className={state} key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </article>
                ))}
              </div>
              <div className="stripe-workflow-grid">
                {workflowRows.map(([RowIcon, label, value, detail]) => (
                  <article key={label}>
                    <RowIcon size={18} />
                    <div>
                      <span>{label}</span>
                      <strong>{value}</strong>
                      <p>{detail}</p>
                    </div>
                  </article>
                ))}
              </div>
              <div className="payment-action-bar">
                <button className={`payment-primary-action ${paymentState}`} type="button" onClick={primaryPaymentAction}>
                  {paymentReady ? <ExternalLink size={18} /> : <SlidersHorizontal size={18} />}
                  {paymentReady ? "Open secure checkout" : setupActionLabel}
                </button>
                <button className="payment-secondary-action" type="button" onClick={confirmPayment}>
                  <ClipboardCheck size={17} />
                  Save setup task
                </button>
              </div>
            </>
          )}
        </section>
        <aside className="payment-summary payment-flow-summary">
          <div className="payment-summary-head">
            <span><WalletCards size={18} /> Payment summary</span>
            <strong>{context.title || paymentKindLabels[context.kind] || "Payment"}</strong>
            <p>{context.subject || "Payment details"}</p>
          </div>
          <div><span>Amount</span><b>{context.amount || "Pending"}</b></div>
          <div><span>Status</span><b>{backendCheckoutReady ? "Secure checkout ready" : paymentNeedsSource ? "Booking record required" : "Price required"}</b></div>
          <div><span>Owner type</span><b>{blueprint.owner}</b></div>
          <div><span>Receipt</span><b>Account and email</b></div>
          <div className="payment-summary-items">
            {blueprint.items.map((item) => <em key={item}>{item}</em>)}
          </div>
        </aside>
      </div>
    </section>
  );
}

function EventManagementCard({ event, navigate, accountProfile }) {
  const stats = getEventDashboardStats(event, accountProfile);
  const image = getEventImage(event);
  const fallbackMedia = getStockMediaFallback("event", `${event?.title || ""}|manager`);
  const past = isEventPast(event);
  return (
    <article className="event-management-card">
      <div className="event-management-media">
        {image ? <img src={image} alt="" /> : <StockMediaImage media={fallbackMedia} alt={`${event?.title || "Badminton event"} stock image`} />}
      </div>
      <div>
        <span>{past ? "Past event" : event.status === "published" ? "Public event" : "Draft event"} · {eventOwnerRoleLabel(event.ownerType)}</span>
        <strong>{event.title}</strong>
        <p>{getEventVenueLabel(event)} · {formatDateLabel(event.date)} · {formatTimeLabel(event.time)}</p>
        <div className="event-stat-row">
          <em><b>{stats.attendeeCount}</b>Attendees</em>
          <em><b>{stats.capacity || "Pending"}</b>Capacity</em>
          <em><b>{stats.fillRate}%</b>Fill rate</em>
          <em><b>{formatMoney(stats.revenue, event.currency || marketplaceCurrencyForCountry(event.countryCode || event.location?.countryCode))}</b>Gross</em>
        </div>
        <div className="event-manager-actions">
          <button type="button" onClick={() => navigate("manageEvent", { event })}>Manage</button>
          {!past && <button type="button" onClick={() => navigate("event", { event })}>Public page</button>}
          {!past && <button type="button" onClick={() => navigate("eventJoin", { event, bookingType: "event" })}>Join flow</button>}
        </div>
      </div>
    </article>
  );
}

function JoinedEventCard({ event, navigate }) {
  const image = getEventImage(event);
  const fallbackMedia = getStockMediaFallback("event", `${event?.title || ""}|joined`);
  const openEventMessages = () => navigate("messages", { messageFocus: { roleId: "player", threadId: `joined-event-${event.id}` } });
  return (
    <article className="joined-event-card">
      {image ? <img src={image} alt="" /> : <StockMediaImage media={fallbackMedia} alt={`${event?.title || "Badminton event"} stock image`} className="joined-event-stock" />}
      <div>
        <span>{event.joinedStatus} · {event.paymentStatus}</span>
        <strong>{event.title}</strong>
        <p>{getEventVenueLabel(event)} · {formatDateLabel(event.date)} · {formatTimeLabel(event.time)}</p>
        <div className="event-manager-actions">
          <button type="button" onClick={() => navigate("event", { event })}>View event</button>
          <button type="button" onClick={openEventMessages}>Message owner</button>
        </div>
      </div>
    </article>
  );
}

function EventManagerPage({ accountProfile, navigate }) {
  const managedEvents = getAccountEvents(accountProfile);
  const joinedEvents = getJoinedEvents(accountProfile);
  const upcomingEvents = managedEvents.filter((event) => !isEventPast(event));
  const pastEvents = managedEvents.filter((event) => isEventPast(event)).reverse();
  const publishedEvents = upcomingEvents.filter((event) => event.status === "published");
  const attendeeTotal = managedEvents.reduce((sum, event) => sum + getEventDashboardStats(event, accountProfile).attendeeCount, 0);
  const eventDashboardCurrency = managedEvents.length
    ? normaliseCurrencyCode(managedEvents[0].currency || marketplaceCurrencyForCountry(managedEvents[0].countryCode || managedEvents[0].location?.countryCode))
    : accountRoleCurrency(accountProfile, accountProfile.roles?.includes("organiser") ? "organiser" : "centre");
  const revenueLabel = formatMoneyBreakdown(managedEvents.map((event) => ({
    amount: getEventDashboardStats(event, accountProfile).revenue,
    currency: event.currency || marketplaceCurrencyForCountry(event.countryCode || event.location?.countryCode)
  })), eventDashboardCurrency);
  return (
    <section className="page event-dashboard-page">
      <div className="screen-head">
        <p>Account events</p>
        <h1>Manage events, registrations and payments from one place.</h1>
        <div className="view-toggle">
          <button type="button" onClick={() => navigate("createEvent")}>Create event</button>
          <button type="button" onClick={() => navigate("joinedEvents")}>Joined events</button>
          <button type="button" onClick={() => navigate("events")}>Public events</button>
        </div>
      </div>
      <div className="event-dashboard-summary">
        {[
          ["Managed events", managedEvents.length],
          ["Published", publishedEvents.length],
          ["Registrations", attendeeTotal],
          ["Gross event fees", revenueLabel]
        ].map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      <div className="event-workspace-grid">
        <section className="event-workspace-panel">
          <div className="section-title-row">
            <div>
              <p>Upcoming events</p>
              <h2>Events you are running</h2>
            </div>
            <button type="button" onClick={() => navigate("createEvent")}>Create</button>
          </div>
          {upcomingEvents.length ? (
            <div className="event-management-list">
              {upcomingEvents.map((event) => <EventManagementCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} />)}
            </div>
          ) : (
            <CommunityEmptyState
              type="events"
              title="No managed events yet"
              text="Centre owners and social organisers can create tournaments, clinics, open days and club nights from here."
              primary={{ label: "Create event", target: "createEvent" }}
              icon={Star}
              navigate={navigate}
              compact
            />
          )}
        </section>
        <section className="event-workspace-panel">
          <div className="section-title-row">
            <div>
              <p>Past events</p>
              <h2>Your event history</h2>
            </div>
            <span>{pastEvents.length} completed</span>
          </div>
          {pastEvents.length ? (
            <div className="event-management-list">
              {pastEvents.map((event) => <EventManagementCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} />)}
            </div>
          ) : (
            <div className="ops-empty compact">
              <strong>No past events yet</strong>
              <span>Completed events move here automatically after their end time.</span>
            </div>
          )}
        </section>
        <section className="event-workspace-panel">
          <div className="section-title-row">
            <div>
              <p>Joined events</p>
              <h2>Events you attend</h2>
            </div>
            <button type="button" onClick={() => navigate("joinedEvents")}>Open</button>
          </div>
          {joinedEvents.length ? (
            <div className="joined-event-list">
              {joinedEvents.slice(0, 4).map((event) => <JoinedEventCard event={event} navigate={navigate} key={event.id} />)}
            </div>
          ) : (
            <CommunityEmptyState
              type="joined events"
              title="No joined events yet"
              text="Events you register for will appear here with payment status, event messages and attendance details."
              primary={{ label: "Browse events", target: "events" }}
              icon={CalendarDays}
              navigate={navigate}
              compact
            />
          )}
        </section>
      </div>
    </section>
  );
}

function JoinedEventsPage({ accountProfile, navigate }) {
  const joinedEvents = getJoinedEvents(accountProfile);
  const upcomingCount = joinedEvents.filter((event) => `${event.date}T${event.time}` >= getIsoDate(0)).length;
  return (
    <section className="page event-dashboard-page">
      <div className="screen-head">
        <p>Joined events</p>
        <h1>Your event registrations and attendance.</h1>
        <div className="view-toggle">
          <button type="button" onClick={() => navigate("events")}>Browse events</button>
          <button type="button" onClick={() => navigate("eventManager")}>Manage events</button>
        </div>
      </div>
      <div className="event-dashboard-summary">
        {[
          ["Joined", joinedEvents.length],
          ["Upcoming", upcomingCount],
          ["Payments pending", joinedEvents.filter((event) => /pending/i.test(event.paymentStatus)).length],
          ["Tickets", joinedEvents.reduce((sum, event) => sum + Number(event.ticketCount || 1), 0)]
        ].map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      {joinedEvents.length ? (
        <div className="joined-event-page-list">
          {joinedEvents.map((event) => <JoinedEventCard event={event} navigate={navigate} key={event.id} />)}
        </div>
      ) : (
        <CommunityEmptyState
          type="joined events"
          title="You have not joined any events yet"
          text="Open a public event, join it, and it will appear here with payment status and event messages."
          primary={{ label: "Browse events", target: "events" }}
          secondary={{ label: "Create event", target: "createEvent" }}
          icon={CalendarDays}
          navigate={navigate}
        />
      )}
    </section>
  );
}

function ManagedEventDashboard({ event, accountProfile, navigate }) {
  if (!event) {
    return (
      <section className="page event-dashboard-page">
        <CommunityEmptyState
          type="events"
          title="Choose an event to manage"
          text="Open your event dashboard and select a managed event to see attendance, payments and operations."
          primary={{ label: "Open event dashboard", target: "eventManager" }}
          icon={Star}
          navigate={navigate}
        />
      </section>
    );
  }
  const stats = getEventDashboardStats(event, accountProfile);
  const ownerEvents = getOwnerEvents(accountProfile, event.ownerType);
  const eventCurrency = normaliseCurrencyCode(event.currency || marketplaceCurrencyForCountry(event.countryCode || event.location?.countryCode));
  return (
    <section className="page event-dashboard-page">
      <div className="screen-head">
        <p>{eventOwnerRoleLabel(event.ownerType)} event dashboard</p>
        <h1>{event.title}</h1>
        <div className="view-toggle">
          <button type="button" onClick={() => navigate("event", { event })}>Public page</button>
          <button type="button" onClick={() => navigate("eventJoin", { event, bookingType: "event" })}>Join flow</button>
          <button type="button" onClick={() => navigate("eventManager")}>All events</button>
        </div>
      </div>
      <div className="event-dashboard-summary">
        {[
          ["Registrations", stats.attendeeCount],
          ["Capacity", stats.capacity || "Pending"],
          ["Fill rate", `${stats.fillRate}%`],
          ["Gross fees", formatMoney(stats.revenue, eventCurrency)],
          ["Remaining spots", stats.capacity ? stats.remaining : "Pending"],
          ["Ticket price", getEventTicketPriceLabel(event)]
        ].map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      <div className="event-admin-layout">
        <section className="ops-table">
          <h2>Event operations</h2>
          {[
            ["Registration status", event.status === "published" ? "Public and joinable" : "Draft"],
            ["Venue", getEventVenueLabel(event)],
            ["Schedule", `${formatDateLabel(event.date)}, ${formatTimeLabel(event.time)}-${formatTimeLabel(event.endTime)}`],
            ["Payment state", getEventPricingMode(event) === "free"
              ? "Free registration"
              : hasEventTicketPrice(event)
                ? eventUsesOfflineSettlement(event) ? "Reservations open; host collects payment" : "Badminta Checkout prepared"
                : "Ticket price required"],
            ["Refund policy", event.refundPolicy || "Not added"],
            ["Requirements", event.requirements || "Not added"]
          ].map((row) => <OpsRow key={row.join("-")} row={row} />)}
        </section>
        <aside className="event-admin-side">
          <strong>Management actions</strong>
          <button type="button" onClick={() => navigate("messages", { messageFocus: { roleId: event.ownerType === "centre" ? "centre" : "organiser", threadId: `${event.ownerType === "centre" ? "centre" : "organiser"}-event-${event.id}` } })}>Message attendees</button>
          <button type="button" onClick={() => navigate(event.ownerType === "centre" ? "centrePayments" : "payouts")}>Review payouts</button>
          <button type="button" onClick={() => navigate("trust")}>Report issue</button>
          <button type="button" onClick={() => navigate("createEvent")}>Create another event</button>
          <span>{ownerEvents.length} event{ownerEvents.length === 1 ? "" : "s"} under this {eventOwnerRoleLabel(event.ownerType).toLowerCase()} profile.</span>
        </aside>
      </div>
    </section>
  );
}

function CreateEventWizard({ navigate, accountProfile, saveAccountProfile, city }) {
  const selectedOwnerTypes = eventOwnerOptions.filter(([ownerType]) => accountProfile.roles?.includes(ownerType));
  const availableOwnerTypes = eventOwnerOptions.filter(([ownerType]) => roleReadyForTools(accountProfile, ownerType));
  const initialOwnerType = availableOwnerTypes[0]?.[0] || "organiser";
  const centreEventSubscription = accountProfileSubscription(accountProfile, "centre");
  const centreEventToolsReady = centreEventSubscription.entitlements?.managedEvents === true;
  const [ownerType, setOwnerType] = useState(initialOwnerType);
  const [message, setMessage] = useState("");
  const [savingEvent, setSavingEvent] = useState(false);
  const initialEventForm = useMemo(() => createEventDraft(accountProfile, ownerType, city), [accountProfile, ownerType, city]);
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId
  } = useProfileSetupDraft(`event-${ownerType}`, accountProfile, initialEventForm, createEventWizardSteps);

  const updateEventField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const eventPricingMode = form.pricingMode === "free" || String(form.price).trim() === "0" ? "free" : "paid";
  const eventFormCurrency = normaliseCurrencyCode(form.currency || (form.location?.countryCode
    ? marketplaceCurrencyForLocation(form.location)
    : accountRoleCurrency(accountProfile, ownerType)));
  const eventPublishing = form.status !== "draft";
  const eventPayoutOwnerProfileId = ownerType === "centre"
    ? accountProfile.centre?.serverCentreId || accountProfile.centre?.firestoreSourceId || accountProfile.publicCentreProfileId || ""
    : accountProfile.group?.publicGroupId || accountProfile.group?.serverGroupId || accountProfile.group?.firestoreSourceId || accountProfile.publicGroupProfileId || "";
  const eventCapacity = Number.parseInt(form.capacity, 10);
  const eventPrice = Number.parseFloat(form.price);
  const paidPublishedEvent = eventPublishing && eventPricingMode === "paid" && Number.isFinite(eventPrice) && eventPrice > 0;
  const [eventPaymentState, setEventPaymentState] = useState({
    status: "idle",
    ready: true,
    availability: null,
    message: "Free events and private drafts do not require payout setup."
  });
  useEffect(() => {
    if (!paidPublishedEvent) {
      setEventPaymentState({
        status: "idle",
        ready: true,
        availability: null,
        message: "Free events and private drafts do not require payout setup."
      });
      return undefined;
    }
    if (!firebaseAuth?.currentUser || !firebaseFunctions || !eventPayoutOwnerProfileId) {
      setEventPaymentState({
        status: "needs-action",
        ready: false,
        availability: null,
        message: "Sign in and publish the owner profile before opening paid registration."
      });
      return undefined;
    }
    let active = true;
    setEventPaymentState({
      status: "checking",
      ready: false,
      availability: null,
      message: "Checking payment availability for this owner country and ticket currency..."
    });
    (async () => {
      try {
        const availability = await callMarketplaceFunction("getStripeConnectCountryAvailability", {
          role: ownerType,
          ownerProfileId: eventPayoutOwnerProfileId,
          country: form.location?.countryCode || "",
          currency: eventFormCurrency
        });
        if (!active) return;
        if (!availability.supported) {
          setEventPaymentState({
            status: "offline",
            ready: true,
            availability,
            message: availability.paymentCurrencySupported === false
              ? `${eventFormCurrency.toUpperCase()} card collection is unavailable here. Players can still reserve on Badminta and pay the host directly.`
              : "Stripe payouts are unavailable for this owner country. Players can still reserve on Badminta and pay the host directly."
          });
          return;
        }
        try {
          const payout = await callMarketplaceFunction("refreshStripeConnectAccount", {
            role: ownerType,
            ownerProfileId: eventPayoutOwnerProfileId
          });
          if (!active) return;
          const ready = Boolean(payout.readyForCheckout);
          setEventPaymentState({
            status: ready ? "ready" : "needs-action",
            ready,
            availability,
            message: ready
              ? "Badminta Pay is ready for event checkout."
              : "Finish payout verification before publishing paid tickets."
          });
        } catch (error) {
          if (!active) return;
          const payoutMessage = marketplaceFunctionErrorMessage(error);
          const onboardingNotStarted = /create stripe onboarding|has not been created|start onboarding/i.test(payoutMessage);
          setEventPaymentState({
            status: onboardingNotStarted ? "needs-action" : "error",
            ready: false,
            availability,
            message: onboardingNotStarted ? "Set up Badminta Pay before publishing paid tickets." : payoutMessage
          });
        }
      } catch (error) {
        if (!active) return;
        setEventPaymentState({
          status: "error",
          ready: false,
          availability: null,
          message: marketplaceFunctionErrorMessage(error)
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [paidPublishedEvent, eventPayoutOwnerProfileId, eventFormCurrency, ownerType, form.location?.countryCode]);
  const eventSettlementMode = paidPublishedEvent && eventPaymentState.availability?.supported === false
    ? "offline_settlement"
    : paidPublishedEvent
      ? "badminta_checkout"
      : "not_required";
  const eventOwnerStepComplete = Boolean(
    availableOwnerTypes.length
    && (ownerType !== "centre" || centreEventToolsReady)
    && form.title
    && form.eventType
    && (!eventPublishing || form.description)
  );
  const eventVenueStepComplete = Boolean(form.venue || form.venueId);
  const eventScheduleStepComplete = Boolean(form.date && form.time && (!eventPublishing || (form.endTime && form.skillBand)));
  const eventTicketsStepComplete = Number.isInteger(eventCapacity) && eventCapacity > 0
    && (eventPricingMode === "free" || (Number.isFinite(eventPrice) && eventPrice > 0));
  const eventReadyToSave = eventOwnerStepComplete
    && eventVenueStepComplete
    && eventScheduleStepComplete
    && eventTicketsStepComplete
    && (ownerType !== "centre" || centreEventToolsReady)
    && (!paidPublishedEvent || eventPaymentState.ready);
  const eventCompletedStepIds = [
    eventOwnerStepComplete && "event-step-owner",
    eventVenueStepComplete && "event-step-venue",
    eventScheduleStepComplete && "event-step-schedule",
    eventTicketsStepComplete && "event-step-tickets",
    eventReadyToSave && "event-step-publish"
  ].filter(Boolean);
  const changeOwnerType = (nextOwnerType) => {
    if (nextOwnerType === ownerType) return;
    setOwnerType(nextOwnerType);
    setMessage("");
  };

  const saveEvent = async () => {
    setMessage("");
    const publishing = form.status !== "draft";
    const required = [
      [form.title, "Event title", "event-step-owner"],
      [form.eventType, "Event type", "event-step-owner"],
      ...(publishing ? [[form.description, "Description", "event-step-owner"]] : []),
      [form.venue || form.venueId, "Venue", "event-step-venue"],
      [form.date, "Date", "event-step-schedule"],
      [form.time, "Start time", "event-step-schedule"],
      ...(publishing ? [[form.endTime, "End time", "event-step-schedule"], [form.skillBand, "Skill band", "event-step-schedule"]] : []),
      [form.capacity, "Capacity", "event-step-tickets"],
      ...(publishing && eventPricingMode === "paid" ? [[form.price, "Ticket price", "event-step-tickets"]] : [])
    ].filter(([value]) => !String(value || "").trim());

    if (!availableOwnerTypes.length) {
      setMessage(selectedOwnerTypes.length
        ? "Complete your Social organiser or Centre owner profile before creating public events."
        : "Choose Social organiser or Centre owner as a user type before creating public events.");
      return;
    }
    if (ownerType === "centre" && !centreEventToolsReady) {
      setMessage("Centre-run events are a Centre Pro operating tool. Your public centre listing remains live and free.");
      scrollToStep("event-step-owner");
      return;
    }

    if (required.length) {
      setMessage(`Add ${required.map(([, label]) => label).join(", ")} before ${publishing ? "publishing" : "saving this draft"}.`);
      scrollToStep(required[0][2]);
      return;
    }
    if (paidPublishedEvent && !eventPaymentState.ready) {
      setMessage(eventPaymentState.status === "checking"
        ? "Wait while Badminta checks event payment availability."
        : eventPaymentState.message || "Finish payout setup before publishing paid tickets.");
      scrollToStep("event-step-tickets");
      return;
    }

    const venue = form.venueId ? venues.find((item) => item.id === form.venueId) : null;
    let savedEvent = normaliseEvent({
      ...form,
      id: form.id || makeEventId(),
      ownerType,
      ownerName: form.ownerName || (ownerType === "centre" ? accountProfile.centre?.name : accountProfile.group?.name) || accountProfile.fullName || eventOwnerRoleLabel(ownerType),
      venue: form.venue || venue?.name || "",
      city: form.city || form.location?.city || venue?.city || city,
      suburb: form.suburb || form.location?.suburb || venue?.suburb || "",
      location: locationSnapshot(form.location) || locationFromVenue(venue),
      inviteEmails: cleanEmailTags(form.inviteEmails),
      socialLinks: cleanLinkRows(form.socialLinks, "Event link"),
      pricingMode: eventPricingMode,
      price: eventPricingMode === "free" ? "0" : form.price,
      paymentLink: "",
      paymentCollectionMode: eventSettlementMode,
      settlementMode: eventSettlementMode === "badminta_checkout" ? "stripe_connect" : eventSettlementMode,
      paymentManagedByBadminta: eventSettlementMode === "badminta_checkout",
      status: form.status || "published"
    });
    const eventCountryCode = normaliseCountryCode(savedEvent.countryCode || savedEvent.location?.countryCode || venue?.countryCode || venue?.country, savedEvent.city);
    const eventCurrency = normaliseCurrencyCode(savedEvent.currency || marketplaceCurrencyForCountry(eventCountryCode));
    savedEvent = { ...savedEvent, countryCode: eventCountryCode, currency: eventCurrency };
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingEvent(true);
      try {
        const backendEvent = await callMarketplaceFunction("upsertMarketplaceEvent", {
          eventId: savedEvent.firestoreSourceId || savedEvent.serverEventId || savedEvent.id,
          ownerType,
          ownerProfileId: ownerType === "centre"
            ? accountProfile.centre?.serverCentreId || accountProfile.centre?.firestoreSourceId || accountProfile.publicCentreProfileId || ""
            : accountProfile.group?.publicGroupId || accountProfile.group?.serverGroupId || accountProfile.group?.firestoreSourceId || accountProfile.publicGroupProfileId || "",
          ownerName: savedEvent.ownerName,
          title: savedEvent.title,
          eventType: savedEvent.eventType,
          description: savedEvent.description,
          venue: savedEvent.venue,
          venueId: savedEvent.venueId,
          city: savedEvent.city,
          suburb: savedEvent.suburb,
          countryCode: eventCountryCode,
          location: locationSnapshot(savedEvent.location),
          date: savedEvent.date,
          time: savedEvent.time,
          endTime: savedEvent.endTime,
          capacity: savedEvent.capacity,
          price: savedEvent.price,
          pricingMode: savedEvent.pricingMode,
          currency: eventCurrency,
          skillBand: savedEvent.skillBand,
          requirements: savedEvent.requirements,
          refundPolicy: savedEvent.refundPolicy,
          inviteEmails: savedEvent.inviteEmails,
          socialLinks: savedEvent.socialLinks,
          coverUrl: savedEvent.coverUrl,
          status: savedEvent.status
        });
        savedEvent = {
          ...savedEvent,
          coverUrl: backendEvent.coverUrl || savedEvent.coverUrl,
          id: backendEvent.eventId || savedEvent.id,
          firestoreSourceId: backendEvent.eventId || savedEvent.firestoreSourceId,
          serverEventId: backendEvent.eventId || savedEvent.serverEventId,
          priceCents: backendEvent.priceCents ?? savedEvent.priceCents,
          currency: backendEvent.currency || savedEvent.currency,
          paymentCountryCode: backendEvent.paymentCountryCode || savedEvent.paymentCountryCode || "",
          paymentCollectionMode: backendEvent.paymentCollectionMode || savedEvent.paymentCollectionMode,
          settlementMode: backendEvent.settlementMode || savedEvent.settlementMode,
          paymentManagedByBadminta: backendEvent.paymentManagedByBadminta === true,
          checkoutReady: backendEvent.checkoutReady === true,
          joinReady: backendEvent.joinReady !== false,
          backendStatus: backendEvent.status || savedEvent.status
        };
      } catch (error) {
        setSavingEvent(false);
        setMessage(marketplaceFunctionErrorMessage(error));
        return;
      }
      setSavingEvent(false);
    }
    const existingEvents = getAccountEvents(accountProfile);
    const nextEvents = existingEvents.some((event) => event.id === savedEvent.id)
      ? existingEvents.map((event) => event.id === savedEvent.id ? savedEvent : event)
      : [...existingEvents, savedEvent];
    saveAccountProfile({ events: nextEvents });
    clearDraft();
    navigate("event", { event: savedEvent });
  };

  if (!availableOwnerTypes.length) {
    return (
      <section className="page form-page">
        <CommunityEmptyState
          type="events"
          title={selectedOwnerTypes.length ? "Complete an owner profile first" : "Create a centre or organiser profile first"}
          text="Public events can only be created by completed centre owner and social organiser profiles, because each event needs a responsible owner for attendance, payments and refunds."
          primary={{ label: selectedOwnerTypes.length ? "Open onboarding" : "Choose user type", target: selectedOwnerTypes.length ? "onboarding" : "signup" }}
          secondary={{ label: "Create social group", target: "createGroup" }}
          icon={Star}
          navigate={navigate}
        />
      </section>
    );
  }

  return (
    <section className="page wizard-page">
      <div className="wizard-head">
        <p>Event creation</p>
        <h1>Publish a public badminton event.</h1>
        <button type="button" onClick={() => navigate("events")}>Back to events</button>
      </div>
      <div className="wizard-layout event-wizard-layout">
        <WizardStepRail steps={createEventWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Event creation steps" completedStepIds={eventCompletedStepIds} />
        <section className="form-card wizard-form-card">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={createEventWizardSteps} />
          {message && <div className="auth-message error">{message}</div>}
          {activeStepId === "event-step-owner" && <section className="wizard-form-section active" id="event-step-owner" tabIndex="-1">
            <h2>Event owner</h2>
            <div className="event-owner-switch">
              {availableOwnerTypes.map(([value, label]) => (
                <button type="button" key={value} className={ownerType === value ? "active" : ""} onClick={() => changeOwnerType(value)}>
                  {value === "centre" ? <Building2 size={18} /> : <UsersRound size={18} />}
                  <span>{label}</span>
                </button>
              ))}
            </div>
            {ownerType === "centre" && !centreEventToolsReady && (
              <div className="managed-payment-note session-payout-readiness needs-action">
                <ShieldCheck size={20} />
                <div>
                  <strong>Centre Pro is required for centre-run events</strong>
                  <span>Your centre listing stays public for free. Upgrade to publish centre tournaments, clinics and open days with registration and payment controls.</span>
                </div>
                <button type="button" onClick={() => navigate("centreDashboard")}>View Centre Pro</button>
              </div>
            )}
            <label><span>Owner display name</span><input value={form.ownerName} onChange={(event) => updateEventField("ownerName", event.target.value)} placeholder="Club, group or centre name" /></label>
            <ImageUploadField
              label="Event cover image"
              value={form.coverUrl}
              onChange={(value) => updateEventField("coverUrl", value)}
              mediaRole="event"
              mediaField="coverUrl"
              emptyLabel="No event image yet"
            />
            <label><span>Event title</span><input value={form.title} onChange={(event) => updateEventField("title", event.target.value)} placeholder="Saturday beginner tournament" /></label>
            <label><span>Event type</span><select value={form.eventType} onChange={(event) => updateEventField("eventType", event.target.value)}><option value="">Select event type</option>{eventTypeOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
            <label><span>Description</span><textarea value={form.description} onChange={(event) => updateEventField("description", event.target.value)} placeholder="Explain format, schedule, level, inclusions, prizes, coaching, check-in and what attendees should expect." /></label>
          </section>}
          {activeStepId === "event-step-venue" && <section className="wizard-form-section active" id="event-step-venue" tabIndex="-1">
            <h2>Venue</h2>
            <VenueTagPicker
              value={form.venue}
              venueId={form.venueId}
              onChange={({ venue, venueId }) => {
                const matchedVenue = venueId ? venues.find((item) => item.id === venueId) : null;
                const eventLocation = locationFromVenue(matchedVenue);
                updateEventField("venue", venue);
                updateEventField("venueId", venueId);
                if (matchedVenue) {
                  updateEventField("city", eventLocation?.city || matchedVenue.city);
                  updateEventField("suburb", eventLocation?.suburb || matchedVenue.suburb);
                  updateEventField("location", eventLocation);
                }
              }}
            />
            <SuburbPicker
              label="Event suburb or city"
              placeholder="Search event suburb or city"
              value={form.suburb || form.city}
              selectedLocation={form.location}
              required
              onChange={({ suburb, city, location }) => {
                updateEventField("suburb", location?.type === "city" ? "" : suburb);
                updateEventField("city", city || suburb);
                updateEventField("location", location);
              }}
            />
          </section>}
          {activeStepId === "event-step-schedule" && <section className="wizard-form-section active" id="event-step-schedule" tabIndex="-1">
            <h2>Schedule</h2>
            <div className="field-pair">
              <label><span>Date</span><input type="date" value={form.date} onInput={(event) => updateEventField("date", event.currentTarget.value)} onChange={(event) => updateEventField("date", event.target.value)} /></label>
              <label><span>Start time</span><input type="time" value={form.time} onInput={(event) => updateEventField("time", event.currentTarget.value)} onChange={(event) => updateEventField("time", event.target.value)} /></label>
            </div>
            <div className="field-pair">
              <label><span>End time</span><input type="time" value={form.endTime} onInput={(event) => updateEventField("endTime", event.currentTarget.value)} onChange={(event) => updateEventField("endTime", event.target.value)} /></label>
              <label><span>Skill band</span><select value={form.skillBand} onChange={(event) => updateEventField("skillBand", event.target.value)}><option value="">Select skill band</option><option>Beginner friendly</option><option>Beginner to intermediate</option><option>Intermediate</option><option>Advanced</option><option>Open level</option></select></label>
            </div>
          </section>}
          {activeStepId === "event-step-tickets" && <section className="wizard-form-section active" id="event-step-tickets" tabIndex="-1">
            <h2>Tickets</h2>
            <div className="ticket-price-mode" role="group" aria-label="Event ticket type">
              <button type="button" className={eventPricingMode === "free" ? "active" : ""} onClick={() => setForm((current) => ({ ...current, pricingMode: "free", price: "0" }))}>
                <BadgeCheck size={18} />
                <span>Free event</span>
              </button>
              <button type="button" className={eventPricingMode === "paid" ? "active" : ""} onClick={() => setForm((current) => ({ ...current, pricingMode: "paid", price: String(current.price).trim() === "0" ? "" : current.price }))}>
                <WalletCards size={18} />
                <span>Paid tickets</span>
              </button>
            </div>
            <div className="field-pair">
              <label><span>Capacity</span><input value={form.capacity} onChange={(event) => updateEventField("capacity", event.target.value)} inputMode="numeric" placeholder="32" /></label>
              <label><span>Ticket price ({eventFormCurrency.toUpperCase()})</span>{eventPricingMode === "paid"
                ? <input value={form.price} onChange={(event) => updateEventField("price", event.target.value)} inputMode="decimal" placeholder="25" />
                : <div className="free-ticket-value"><BadgeCheck size={18} /><strong>Free registration</strong></div>}</label>
            </div>
            <div className="event-checkout-note managed-checkout-note">
              <ShieldCheck size={20} />
              <span>
                <strong>{eventSettlementMode === "offline_settlement" ? "Badminta manages registration." : "Badminta manages event checkout."}</strong>{" "}
                {eventSettlementMode === "offline_settlement"
                  ? "Players reserve a capacity-controlled place here, then pay you directly or at the venue. Badminta does not hold those funds."
                  : "Where payouts are supported, paid registrations use a server-created Stripe Checkout Session with receipts, refunds and owner payout status kept against this event."}
              </span>
            </div>
            <label><span>Requirements</span><textarea value={form.requirements} onChange={(event) => updateEventField("requirements", event.target.value)} placeholder="Bring racquet, non-marking shoes, water bottle..." /></label>
            <label><span>Refund policy</span><textarea value={form.refundPolicy} onChange={(event) => updateEventField("refundPolicy", event.target.value)} placeholder="Refunds available up to 24 hours before start..." /></label>
          </section>}
          {activeStepId === "event-step-publish" && <section className="wizard-form-section active" id="event-step-publish" tabIndex="-1">
            <h2>Publish</h2>
            <EmailTagInput emails={form.inviteEmails} onChange={(emails) => updateEventField("inviteEmails", emails)} />
            <LinkListEditor
              title="Event links"
              text="Choose a platform and enter its username, or paste an official registration, rules or sponsor page."
              links={form.socialLinks}
              onChange={(links) => updateEventField("socialLinks", links)}
            />
            <label><span>Status</span><select value={form.status} onChange={(event) => updateEventField("status", event.target.value)}><option value="published">Published</option><option value="draft">Draft</option></select></label>
            {paidPublishedEvent && (
              <div className={`managed-payment-note session-payout-readiness ${eventPaymentState.status}`}>
                <WalletCards size={20} />
                <div>
                  <strong>{eventPaymentState.status === "offline"
                    ? "Direct settlement available"
                    : eventPaymentState.ready
                      ? "Event payments ready"
                      : eventPaymentState.status === "checking"
                        ? "Checking event payments"
                        : "Payout setup needed"}</strong>
                  <span>{eventPaymentState.message}</span>
                </div>
                {!eventPaymentState.ready && eventPaymentState.status !== "checking" && (
                  <button type="button" onClick={() => navigate(ownerType === "centre" ? "centrePayments" : "payouts")}>Set up payouts</button>
                )}
              </div>
            )}
            <button type="button" onClick={saveEvent} disabled={savingEvent}>{savingEvent ? "Publishing..." : form.status === "draft" ? "Save draft event" : "Publish event"}</button>
          </section>}
          <WizardNavigation steps={createEventWizardSteps} activeStepId={activeStepId} onStepChange={scrollToStep} readyToSave={eventReadyToSave} />
        </section>
        <aside className="preview-card event-preview-card">
          {form.coverUrl ? <img src={form.coverUrl} alt="" /> : <NoImagePlaceholder label="Event cover preview" />}
          <strong>{form.title || "Your badminton event"}</strong>
          <span>{[form.eventType, form.venue, form.date, form.time].filter(Boolean).join(" · ") || "Add event details to preview."}</span>
          <b>{eventPricingMode === "free" ? "Free" : form.price ? formatMoney(Number(form.price), eventFormCurrency) : "Price pending"}</b>
          <div className="event-publish-checklist">
            {[
              ["Owner", form.ownerName || eventOwnerRoleLabel(ownerType)],
              ["Venue", form.venue || "Missing"],
              ["Capacity", form.capacity || "Missing"],
              ["Payment", eventPricingMode === "free"
                ? "Free registration"
                : form.price
                  ? eventSettlementMode === "offline_settlement" ? "Pay host directly" : "Badminta checkout"
                  : "Price required"],
              ["Visibility", form.status === "published" ? "Public" : "Draft"]
            ].map(([label, value]) => <span key={label}><b>{value}</b>{label}</span>)}
          </div>
          <PublicLinkList links={form.socialLinks} emptyText="Event links will appear here." />
        </aside>
      </div>
    </section>
  );
}

function RowSection({ title, action, onAction, children }) {
  return (
    <section className="row-section">
      <div className="row-title">
        <h2>{title}</h2>
        <button type="button" onClick={onAction}>{action} <ChevronRight size={17} /></button>
      </div>
      <div className="card-rail">{children}</div>
    </section>
  );
}

function NoImagePlaceholder({ label = "No images found", className = "" }) {
  return (
    <div className={["no-image-placeholder", className].filter(Boolean).join(" ")} aria-label={label}>
      <ImageIcon size={26} />
      <span>{label}</span>
    </div>
  );
}

function StockMediaImage({ media, alt = "Badminton stock image", className = "", chipLabel = "Stock image" }) {
  const item = media || getStockMediaFallback("general", alt);
  return (
    <div className={["stock-media-frame", className].filter(Boolean).join(" ")}>
      <DeferredImage src={item.url} alt={alt || item.alt || item.label} />
      <span className="stock-media-chip" title={`Licensed stock image from ${item.sourceName || "Pexels"}`}>
        {chipLabel}
      </span>
    </div>
  );
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(new Error("Could not read that image."));
  reader.readAsDataURL(file);
});

let profileImageSafetyModelPromise = null;

const profileImageElementFromFile = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => resolve({ image, objectUrl });
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("That image could not be checked. Choose a JPG, PNG or WebP image."));
  };
  image.src = objectUrl;
});

const loadProfileImageSafetyModel = async () => {
  if (!profileImageSafetyModelPromise) {
    profileImageSafetyModelPromise = (async () => {
      const tensorflow = await import("@tensorflow/tfjs");
      tensorflow.enableProdMode();
      await tensorflow.ready();
      const [{ load }, { MobileNetV2MidModel }] = await Promise.all([
        import("nsfwjs/core"),
        import("nsfwjs/models/mobilenet_v2_mid")
      ]);
      return load("MobileNetV2Mid", {
        modelDefinitions: [MobileNetV2MidModel]
      });
    })().catch((error) => {
      profileImageSafetyModelPromise = null;
      throw error;
    });
  }
  return profileImageSafetyModelPromise;
};

const screenProfileImageFile = async (file) => {
  try {
    const model = await loadProfileImageSafetyModel();
    const { image, objectUrl } = await profileImageElementFromFile(file);
    try {
      const predictions = await model.classify(image, 5);
      const scores = Object.fromEntries(predictions.map((prediction) => [
        String(prediction.className || "").toLowerCase(),
        Number(prediction.probability || 0)
      ]));
      const porn = scores.porn || 0;
      const hentai = scores.hentai || 0;
      const sexy = scores.sexy || 0;
      const blocked = porn >= 0.12
        || hentai >= 0.12
        || porn + hentai >= 0.18
        || sexy >= 0.78
        || porn + hentai + sexy >= 0.86;
      return {
        engine: "nsfwjs",
        model: "MobileNetV2Mid",
        result: blocked ? "blocked" : "passed",
        scores
      };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return {
      engine: "nsfwjs",
      model: "MobileNetV2Mid",
      result: "unavailable",
      scores: {}
    };
  }
};

const prepareProfileImage = async (file) => {
  if (file.size > 15 * 1024 * 1024) throw new Error("Choose an image smaller than 15 MB.");
  const original = await readFileAsDataUrl(file);
  if (original.length <= 420000) return original;
  const image = await new Promise((resolve, reject) => {
    const preview = new Image();
    preview.onload = () => resolve(preview);
    preview.onerror = () => reject(new Error("That image format could not be processed. Choose a JPG, PNG or WebP image."));
    preview.src = original;
  });
  const maxSide = 1400;
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("This browser could not prepare the image.");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  let quality = 0.86;
  let compressed = canvas.toDataURL("image/jpeg", quality);
  while (compressed.length > 520000 && quality > 0.48) {
    quality -= 0.08;
    compressed = canvas.toDataURL("image/jpeg", quality);
  }
  if (compressed.length > 600000) throw new Error("This image is still too large. Choose a smaller photo.");
  return compressed;
};

function ImageUploadField({ label, value, onChange, mediaRole = "profile", mediaField = "image", placeholder = "Paste image URL or upload an image", helper = "Use a clear square image or paste a public image URL.", emptyLabel = "No image added yet", required = false, variant = "" }) {
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingLabel, setProcessingLabel] = useState("");
  const [notice, setNotice] = useState("");

  const handleFile = async (event) => {
    const [file] = Array.from(event.target.files || []);
    setError("");
    setNotice("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file.");
      return;
    }
    setProcessing(true);
    try {
      setProcessingLabel("Checking image...");
      const clientSafety = await screenProfileImageFile(file);
      if (clientSafety.result === "blocked") {
        throw new Error("This image may contain nudity or adult content. Choose a clear badminton or profile photo instead.");
      }
      setProcessingLabel("Preparing image...");
      const dataUrl = await prepareProfileImage(file);
      setProcessingLabel("Uploading privately...");
      const uploaded = await callMarketplaceFunction("uploadBadmintaProfileMedia", {
        dataUrl,
        role: mediaRole,
        field: mediaField,
        clientSafety
      });
      if (!uploaded.url) throw new Error("Badminta could not finish this image upload.");
      onChange(uploaded.url);
      setNotice("Image uploaded privately and queued for safety review. It will not appear publicly until approved.");
    } catch (imageError) {
      setError(marketplaceFunctionErrorMessage(imageError) || imageError.message || "Could not upload that image.");
    } finally {
      setProcessing(false);
      setProcessingLabel("");
      event.target.value = "";
    }
  };

  return (
    <div className={`asset-upload-field${variant ? ` ${variant}` : ""}`}>
      <div className="form-section-head">
        <h3>{label} {required && <FieldRequirement />}</h3>
        <span>{helper}</span>
      </div>
      <div className="asset-upload-preview">
        {value ? <img src={value} alt="" /> : <NoImagePlaceholder label={emptyLabel} />}
        <div className="asset-upload-copy">
          <strong>{value ? "Image ready" : emptyLabel}</strong>
          <span>{value ? "Badminta automatically checks new images and publishes safe uploads." : "Add a clean badminton or profile photo. Every new image is checked automatically."}</span>
          <div className="asset-upload-actions">
            <label className="file-upload-button">
              {processing ? <Loader2 size={17} className="spin" /> : <Camera size={17} />}
              <span>{processing ? processingLabel || "Checking image..." : "Upload image"}</span>
              <input type="file" accept="image/*" onChange={handleFile} disabled={processing} />
            </label>
            {value && <button type="button" onClick={() => onChange("")}>Remove</button>}
          </div>
        </div>
      </div>
      <details className="asset-url-disclosure">
        <summary><ExternalLink size={15} /> Use an image URL instead</summary>
        <label className="asset-url-input">
          <span>Image URL</span>
          <input value={value || ""} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} inputMode="url" />
        </label>
      </details>
      {notice && <div className="field-notice"><ShieldCheck size={16} /> {notice}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function CentreMediaUploadField({ links = [], onChange, minimum = centreMediaMinimumItems }) {
  const [processing, setProcessing] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const rows = cleanLinkRows(links, "Venue media");
  const imageRows = rows.filter((row) => isImageMediaUrl(row.url));
  const remainingSlots = Math.max(0, minimum - rows.length);

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, Math.max(0, 8 - rows.length));
    setError("");
    setNotice("");
    if (!files.length) return;
    setProcessing(true);
    const uploadedRows = [];
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        if (!file.type.startsWith("image/")) throw new Error(`${file.name} is not an image file.`);
        setProgressLabel(`Checking photo ${index + 1} of ${files.length}`);
        const clientSafety = await screenProfileImageFile(file);
        if (clientSafety.result === "blocked") {
          throw new Error(`${file.name} may contain adult content. Choose a clear photo of the centre or courts.`);
        }
        const dataUrl = await prepareProfileImage(file);
        setProgressLabel(`Uploading photo ${index + 1} of ${files.length}`);
        const uploaded = await callMarketplaceFunction("uploadBadmintaProfileMedia", {
          dataUrl,
          role: "centre",
          field: "photoLinks",
          clientSafety
        });
        if (!uploaded.url) throw new Error(`Badminta could not finish uploading ${file.name}.`);
        uploadedRows.push({
          platform: "Image",
          label: `Venue photo ${rows.length + uploadedRows.length + 1}`,
          url: uploaded.url,
          source: "user",
          requiresModeration: true,
          rightsStatus: "owner_uploaded",
          moderationStatus: uploaded.moderationStatus || "pending_review",
          submittedAt: new Date().toISOString()
        });
      }
      onChange([...rows, ...uploadedRows]);
      const approvedCount = uploadedRows.filter((row) => row.moderationStatus === "approved").length;
      setNotice(`${uploadedRows.length} photo${uploadedRows.length === 1 ? "" : "s"} checked automatically. ${approvedCount} ready to publish.`);
    } catch (uploadError) {
      if (uploadedRows.length) onChange([...rows, ...uploadedRows]);
      setError(marketplaceFunctionErrorMessage(uploadError) || uploadError.message || "Could not upload those venue photos.");
    } finally {
      setProcessing(false);
      setProgressLabel("");
      event.target.value = "";
    }
  };

  const removeImage = (url) => onChange(rows.filter((row) => row.url !== url));

  return (
    <section className="centre-gallery-upload">
      <div className="form-section-head">
        <h3>Venue gallery <FieldRequirement /></h3>
        <span>Add at least {minimum} clear photos of the courts, venue entrance, reception or amenities. You can select several photos at once.</span>
      </div>
      <div className={`centre-gallery-count ${remainingSlots ? "incomplete" : "complete"}`}>
        {remainingSlots ? <CircleHelp size={17} /> : <Check size={17} />}
        <strong>{rows.length} of {minimum} required media items added</strong>
        <span>{remainingSlots ? `${remainingSlots} more needed` : "Gallery requirement complete"}</span>
      </div>
      {imageRows.length > 0 && (
        <div className="centre-gallery-preview" aria-label="Uploaded centre photos">
          {imageRows.map((row, index) => (
            <figure key={`${row.url}-${index}`}>
              <img src={row.url} alt="" />
              <button type="button" onClick={() => removeImage(row.url)} aria-label={`Remove venue photo ${index + 1}`} title="Remove photo"><X size={15} /></button>
            </figure>
          ))}
        </div>
      )}
      <label className="centre-gallery-upload-button">
        {processing ? <Loader2 size={18} className="spin" /> : <ImageIcon size={18} />}
        <span>{processing ? progressLabel || "Uploading photos" : imageRows.length ? "Add more photos" : "Upload venue photos"}</span>
        <input type="file" accept="image/*" multiple onChange={uploadFiles} disabled={processing || rows.length >= 8} />
      </label>
      <div className="media-moderation-note">
        <ShieldCheck size={17} />
        <span>Upload media owned by the centre. Badminta checks every photo automatically and publishes safe files.</span>
      </div>
      {notice && <div className="field-notice"><Check size={16} /> {notice}</div>}
      {error && <div className="field-error">{error}</div>}
    </section>
  );
}

function ManagedCheckoutNote({ title = "Badminta manages checkout.", children }) {
  return (
    <div className="event-checkout-note managed-checkout-note">
      <ShieldCheck size={20} />
      <span><strong>{title}</strong> {children}</span>
    </div>
  );
}

function MediaLinkEditorPreview({ url, label, error }) {
  if (!url || error) return null;
  const embed = parseMediaEmbed(url);
  const displayLabel = label || embed?.provider || "Media";

  return (
    <div className={embed?.portrait ? "media-link-editor-preview portrait" : "media-link-editor-preview"}>
      {isImageMediaUrl(url) ? (
        <img src={url} alt={`${displayLabel} preview`} loading="lazy" decoding="async" />
      ) : embed?.type === "iframe" ? (
        <iframe
          title={`${displayLabel} preview`}
          src={embed.embedUrl}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <span className="media-link-ready-icon"><Video size={24} /></span>
      )}
      <div>
        <span><Check size={15} /> Link ready</span>
        <strong>{displayLabel}</strong>
        <a href={url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open source</a>
      </div>
    </div>
  );
}

function LinkListEditor({ title, text, links, onChange, addLabel = "Add link", type = "social", defaultMediaPlatform = "YouTube" }) {
  const rows = Array.isArray(links) ? links : [];
  const addRow = () => onChange([...rows, type === "social" ? defaultSocialLink() : defaultMediaLink(defaultMediaPlatform)]);

  const patchRow = (index, patch, mediaUrlChanged = false) => {
    onChange(rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...row, ...patch };
      if (type === "media") {
        next.source = next.source || "user";
        next.rightsStatus = mediaUrlChanged ? "owned_by_user" : normaliseMediaRightsStatus(next.rightsStatus, "owned_by_user");
        next.requiresModeration = false;
        next.moderationStatus = mediaUrlChanged
          ? (next.url && parseMediaEmbed(next.url) ? "approved" : "rejected")
          : normaliseMediaModerationStatus(next.moderationStatus);
        next.submittedAt = next.submittedAt || new Date().toISOString();
        if (mediaUrlChanged) {
          delete next.reviewedAt;
          delete next.reviewedBy;
          delete next.moderationNote;
        }
      }
      return next;
    }));
  };

  const updateSocialPlatform = (index, platform) => {
    const row = rows[index] || {};
    const previousPlatform = row.platform || "Instagram";
    const previousMeta = getSocialPlatformMeta(previousPlatform);
    const nextMeta = getSocialPlatformMeta(platform);
    let currentInput = socialInputFromLink(row);
    if (nextMeta.inputKind === "url" && previousMeta.inputKind !== "url") {
      currentInput = row.url || "";
    } else if (nextMeta.inputKind !== "url" && previousMeta.inputKind === "url") {
      const derivedInput = socialInputFromLink({ platform, url: row.url });
      currentInput = derivedInput && derivedInput !== row.url ? derivedInput : "";
    }
    const url = buildSocialProfileUrl(platform, currentInput);
    patchRow(index, {
      platform,
      inputValue: currentInput,
      username: nextMeta.inputKind === "username" ? currentInput : "",
      url,
      label: platform
    });
  };

  const updateSocialInput = (index, value) => {
    const row = rows[index] || {};
    const platform = row.platform || "Instagram";
    const meta = getSocialPlatformMeta(platform);
    patchRow(index, {
      platform,
      inputValue: value,
      username: meta.inputKind === "username" ? value : "",
      url: buildSocialProfileUrl(platform, value),
      label: platform
    });
  };

  const updateMediaInput = (index, value) => {
    const row = rows[index] || {};
    const detectedPlatform = detectMediaPlatform(value);
    const platform = detectedPlatform && detectedPlatform !== "Other"
      ? detectedPlatform
      : row.platform || defaultMediaPlatform;
    const url = normaliseMediaLinkUrl(platform, value);
    patchRow(index, { platform, inputValue: value, url }, url !== row.url);
  };

  const updateMediaPlatform = (index, platform) => {
    const row = rows[index] || {};
    const inputValue = row.inputValue ?? row.url ?? "";
    const url = normaliseMediaLinkUrl(platform, inputValue);
    patchRow(index, { platform, inputValue, url }, url !== row.url);
  };

  const removeRow = (index) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    onChange(next.length ? next : []);
  };

  return (
    <section className={`link-list-editor ${type === "media" ? "media-links-editor" : "social-links-editor"}`}>
      <div className="form-section-head">
        <h3>{title}</h3>
        {text && <span>{text}</span>}
      </div>
      {type === "media" && (
        <div className="media-moderation-note">
          <ShieldCheck size={17} />
          <span>Only add media you own, have permission to publish, or can share from an official source. Supported links are validated and published automatically.</span>
        </div>
      )}
      {rows.length > 0 ? <div className="profile-link-rows">
        {rows.map((row, index) => {
          if (type === "social") {
            const platform = socialPlatformOptions.includes(row.platform) ? row.platform : "Instagram";
            const meta = getSocialPlatformMeta(platform);
            const inputValue = socialInputFromLink(row);
            const url = buildSocialProfileUrl(platform, inputValue);
            const error = socialProfileInputError(platform, inputValue);
            return (
              <article className="profile-link-row social-profile-link-row" key={`${platform}-${index}`}>
                <div className="profile-link-fields">
                  <label className="profile-link-platform-field">
                    <span>Platform</span>
                    <select value={platform} onChange={(event) => updateSocialPlatform(index, event.target.value)}>
                      {socialPlatformOptions.map((option) => <option key={option}>{option}</option>)}
                    </select>
                  </label>
                  <label className="profile-link-value-field">
                    <span>{meta.label}</span>
                    <div className="profile-link-input-shell">
                      {meta.prefix && <b aria-hidden="true">{meta.prefix}</b>}
                      <input
                        value={inputValue}
                        onChange={(event) => updateSocialInput(index, event.target.value)}
                        placeholder={meta.placeholder}
                        inputMode={meta.inputKind === "whatsapp" ? "tel" : meta.inputKind === "url" ? "url" : "text"}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </div>
                  </label>
                  <button className="profile-link-remove" type="button" onClick={() => removeRow(index)} aria-label={`Remove ${platform} link`} title="Remove link"><X size={17} /></button>
                </div>
                {error ? <div className="profile-link-error">{error}</div> : url ? (
                  <a className="generated-profile-link" href={url} target="_blank" rel="noreferrer">
                    <Check size={15} />
                    <span><strong>Profile link ready</strong><small>{url}</small></span>
                    <ExternalLink size={14} />
                  </a>
                ) : (
                  <div className="profile-link-helper">Enter the username only. Badminta creates the complete profile link.</div>
                )}
              </article>
            );
          }

          const detectedPlatform = detectMediaPlatform(row.url);
          const platform = mediaPlatformOptions.includes(row.platform)
            ? row.platform
            : detectedPlatform || defaultMediaPlatform;
          const meta = getMediaPlatformMeta(platform);
          const inputValue = row.inputValue ?? row.url ?? "";
          const url = normaliseMediaLinkUrl(platform, inputValue);
          const error = mediaLinkInputError(platform, inputValue);
          const previewLabel = row.label || mediaLabelForPlatform(platform);
          return (
            <article className="profile-link-row media-profile-link-row" key={`${platform}-${index}`}>
              <div className="profile-link-fields">
                <label className="profile-link-platform-field">
                  <span>Media source</span>
                  <select value={platform} onChange={(event) => updateMediaPlatform(index, event.target.value)}>
                    {mediaPlatformOptions.map((option) => <option key={option}>{option}</option>)}
                  </select>
                </label>
                <label className="profile-link-value-field">
                  <span>{meta.label}</span>
                  <div className="profile-link-input-shell">
                    <ExternalLink size={16} aria-hidden="true" />
                    <input
                      value={inputValue}
                      onChange={(event) => updateMediaInput(index, event.target.value)}
                      placeholder={meta.placeholder}
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                  </div>
                </label>
                <button className="profile-link-remove" type="button" onClick={() => removeRow(index)} aria-label="Remove media link" title="Remove media"><X size={17} /></button>
              </div>
              {error && <div className="profile-link-error">{error}</div>}
              <MediaLinkEditorPreview url={url} label={previewLabel} error={error} />
              <div className="media-link-settings">
                <label>
                  <span>Title <small>optional</small></span>
                  <input value={row.label || ""} onChange={(event) => patchRow(index, { label: event.target.value })} placeholder={previewLabel} />
                </label>
                <label className="media-rights-field">
                  <span>Permission to publish</span>
                  <select value={normaliseMediaRightsStatus(row.rightsStatus)} onChange={(event) => patchRow(index, { rightsStatus: event.target.value })}>
                    {mediaRightsStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <span className={`media-review-pill ${normaliseMediaModerationStatus(row.moderationStatus)}`}>
                  {mediaModerationStatusLabels[normaliseMediaModerationStatus(row.moderationStatus)]}
                </span>
              </div>
            </article>
          );
        })}
      </div> : (
        <button type="button" className="link-editor-empty-action" onClick={addRow}>
          <Plus size={18} />
          <span><strong>{addLabel}</strong><small>{type === "social" ? "Add a username from one social platform." : "Add a photo or video share link."}</small></span>
          <ChevronRight size={17} />
        </button>
      )}
      {rows.length > 0 && <button type="button" className="inline-add-button" onClick={addRow}>
        <Plus size={16} /> {addLabel}
      </button>}
    </section>
  );
}

function SuburbPicker({
  value,
  selectedLocation,
  onChange,
  label = "Home suburb",
  placeholder = "Search suburb, city or neighbourhood",
  helper = "Search suburbs and cities worldwide.",
  autoLocationStatus = "",
  onUseCurrentLocation = null,
  required = false
}) {
  const [query, setQuery] = useState(value || selectedLocation?.label || "");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [resultsVisible, setResultsVisible] = useState(false);
  const blurCommitTimerRef = useRef(0);

  useEffect(() => {
    setQuery(value || selectedLocation?.label || "");
  }, [value, selectedLocation?.label]);

  const localMatches = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (text.length < 2) return [];
    return locationOptions
      .filter((option) => option.label.toLowerCase().includes(text) || option.city.toLowerCase().includes(text))
      .slice(0, 6);
  }, [query, selectedLocation]);

  const mergedResults = [...results, ...localMatches].filter(
    (option, index, list) => list.findIndex((item) => `${item.label}-${item.city}-${item.type}` === `${option.label}-${option.city}-${option.type}`) === index
  ).slice(0, 8);

  useEffect(() => () => window.clearTimeout(blurCommitTimerRef.current), []);

  const validateSelectedLocation = (rawValue = query) => {
    const text = String(rawValue || "").trim();
    const selectedLabels = [
      value,
      selectedLocation?.suburb,
      selectedLocation?.city,
      selectedLocation?.label
    ].map((item) => String(item || "").trim().toLowerCase()).filter(Boolean);
    if (text && selectedLabels.includes(text.toLowerCase())) return true;
    if (!text) {
      setMessage(required ? "Choose a suburb or city before continuing." : "");
      return !required;
    }
    setMessage("Choose a suburb or city from the search results so Badminta can place it accurately.");
    return false;
  };

  const selectSuburb = (location) => {
    window.clearTimeout(blurCommitTimerRef.current);
    const savedLocation = locationSnapshot(location);
    if (!savedLocation || savedLocation.type === "country") {
      setMessage("Choose a suburb, neighbourhood or city rather than a whole country.");
      return;
    }
    const area = locationAreaLabel(savedLocation);
    setQuery(area);
    setMessage("");
    setResultsVisible(false);
    onChange({ suburb: savedLocation.suburb || "", city: savedLocation.city || "", location: savedLocation });
  };

  const searchSuburbs = async ({ selectFirst = false } = {}) => {
    window.clearTimeout(blurCommitTimerRef.current);
    const text = query.trim();
    setResultsVisible(true);
    if (text.length < 2) {
      setMessage("Type at least two letters to search suburbs.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const places = await searchOpenStreetLocations(text);
      setResults(places);
      if (selectFirst && places[0]) {
        selectSuburb(places[0]);
      } else if (!places.length) {
        setMessage("No matching suburb or city was found. Check the spelling and search again.");
      }
    } catch (error) {
      setMessage(error.message || "Suburb search is unavailable right now.");
    } finally {
      setStatus("idle");
    }
  };

  return (
    <div className="suburb-picker">
      <label>
        <span>{label} <FieldRequirement optional={!required} /></span>
        <input
          required={required}
          value={query}
          onChange={(event) => {
            const nextQuery = event.target.value;
            const selectedLabels = [
              value,
              selectedLocation?.suburb,
              selectedLocation?.city,
              selectedLocation?.label
            ].map((item) => String(item || "").trim().toLowerCase()).filter(Boolean);
            const stillSelected = selectedLabels.includes(nextQuery.trim().toLowerCase());
            if (!stillSelected && selectedLabels.length) onChange({ suburb: "", city: "", location: null });
            setQuery(nextQuery);
            setResults([]);
            setMessage("");
            setResultsVisible(true);
          }}
          onFocus={() => {
            if (query.trim().length >= 2) setResultsVisible(true);
          }}
          onBlur={() => {
            window.clearTimeout(blurCommitTimerRef.current);
            blurCommitTimerRef.current = window.setTimeout(() => validateSelectedLocation(), 220);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (mergedResults[0]) selectSuburb(mergedResults[0]);
              else searchSuburbs({ selectFirst: true });
            }
          }}
          placeholder={placeholder}
        />
      </label>
      <div className="osm-search-row">
        <button type="button" onClick={() => searchSuburbs()} disabled={status === "loading"}>
          <Search size={15} /> {status === "loading" ? "Searching suburbs" : "Search suburbs"}
        </button>
        {helper && <span>{helper}</span>}
      </div>
      {autoLocationStatus && autoLocationStatus !== "idle" && (
        <div className={`auto-location-status ${autoLocationStatus}`} role="status" aria-live="polite">
          {autoLocationStatus === "locating" ? <Loader2 className="spin" size={17} /> : <Navigation size={17} />}
          <span>
            {autoLocationStatus === "locating" && "Finding your current suburb or city..."}
            {autoLocationStatus === "saved" && "Current location added automatically."}
            {autoLocationStatus === "denied" && "Location access was not allowed. Search manually or try again."}
            {autoLocationStatus === "error" && "Current location could not be resolved. Search manually or try again."}
          </span>
          {["denied", "error"].includes(autoLocationStatus) && onUseCurrentLocation && (
            <button type="button" onClick={onUseCurrentLocation}>Use my location</button>
          )}
        </div>
      )}
      {resultsVisible && query.trim().length >= 2 && (
        <div className="location-results compact">
          {mergedResults.map((location) => (
            <button
              type="button"
              key={location.id}
              className={selectedLocation?.id === location.id || value === (location.suburb || location.label) ? "active" : ""}
              onClick={() => selectSuburb(location)}
            >
              <MapPin size={16} />
              <span>
                <strong>{location.suburb || location.label}</strong>
                <em>{location.city} · {location.hint || "Suburb"}</em>
              </span>
            </button>
          ))}
        </div>
      )}
      {message && <p className="search-message">{message}</p>}
    </div>
  );
}

function CentreAddressPicker({ value = "", location = null, onChange }) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const searchTimerRef = useRef(0);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => () => window.clearTimeout(searchTimerRef.current), []);

  const searchAddresses = async (searchValue) => {
    const text = String(searchValue || "").trim();
    if (text.length < 5) {
      setResults([]);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const places = await searchOpenStreetLocations(text);
      setResults(places);
      setOpen(true);
      if (!places.length) setMessage("No mapped address found. Add more detail such as street number, city and country.");
    } catch (error) {
      setMessage(error.message || "Address search is unavailable right now.");
    } finally {
      setStatus("idle");
    }
  };

  const updateQuery = (nextValue) => {
    setQuery(nextValue);
    setMessage("");
    setOpen(true);
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => searchAddresses(nextValue), 450);
  };

  const selectAddress = (place) => {
    const savedLocation = locationSnapshot(place);
    const address = place.displayName || place.label || query;
    setQuery(address);
    setResults([]);
    setOpen(false);
    setMessage("");
    onChange?.({ address, location: savedLocation });
  };

  return (
    <div className="centre-address-picker">
      <label>
        <span>Full street address <FieldRequirement /></span>
        <div className="centre-address-input-shell">
          <MapPin size={18} />
          <input
            required
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
            onFocus={() => query.trim().length >= 5 && setOpen(true)}
            placeholder="Start typing the street number, road, city and country"
            autoComplete="street-address"
          />
          {status === "loading" && <Loader2 size={17} className="spin" />}
        </div>
      </label>
      {open && results.length > 0 && (
        <div className="location-results centre-address-results">
          {results.map((place) => (
            <button type="button" key={place.id} onClick={() => selectAddress(place)}>
              <MapPin size={16} />
              <span>
                <strong>{place.displayName || place.label}</strong>
                <em>{[place.city, place.state, place.country].filter(Boolean).join(" · ")}</em>
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="centre-address-map-status">
        <Navigation size={15} />
        <span>{location?.lat && location?.lon ? "Mapped address selected for discovery and directions." : "Choose a suggested address so the centre is placed correctly on the map."}</span>
      </div>
      {message && <p className="search-message">{message}</p>}
    </div>
  );
}

function WeeklyAvailabilityEditor({ schedule, onChange, legacySummary = "", title = "Availability", description = "Select the days and times players or organisers can invite you. This works like business hours." }) {
  const rows = normaliseAvailabilitySchedule(schedule);
  const summary = summarizeAvailabilitySchedule(rows);

  const updateRow = (day, patch) => {
    onChange(rows.map((row) => row.day === day ? { ...row, ...patch } : row));
  };

  return (
    <section className="weekly-availability-editor">
      <div className="form-section-head">
        <h3>{title}</h3>
        <span>{description}</span>
      </div>
      <div className="availability-day-list">
        {rows.map((row) => (
          <div className={row.enabled ? "availability-day-row active" : "availability-day-row"} key={row.day}>
            <label className="availability-day-toggle">
              <input type="checkbox" checked={row.enabled} onChange={(event) => updateRow(row.day, { enabled: event.target.checked })} />
              <span>{row.label}</span>
            </label>
            <div className="availability-time-fields">
              <label>
                <span>From</span>
                <select value={row.start} onChange={(event) => updateRow(row.day, { start: event.target.value })} disabled={!row.enabled}>
                  {availabilityTimeOptions.map((time) => <option key={`${row.day}-start-${time}`} value={time}>{formatTimeLabel(time)}</option>)}
                </select>
              </label>
              <label>
                <span>To</span>
                <select value={row.end} onChange={(event) => updateRow(row.day, { end: event.target.value })} disabled={!row.enabled}>
                  {availabilityTimeOptions.map((time) => <option key={`${row.day}-end-${time}`} value={time}>{formatTimeLabel(time)}</option>)}
                </select>
              </label>
            </div>
          </div>
        ))}
      </div>
      <div className="availability-summary-box">
        <Clock3 size={17} />
        <span>{summary || legacySummary || "No availability selected yet"}</span>
      </div>
    </section>
  );
}

function CoachLessonServicesEditor({ services = [], hourlyRate = "", currency = "aud", onChange }) {
  const rows = normaliseCoachLessonServices(services, hourlyRate, currency);
  const updateService = (id, patch) => {
    onChange?.(rows.map((service) => service.id === id ? { ...service, ...patch } : service));
  };
  const removeService = (id) => onChange?.(rows.filter((service) => service.id !== id));
  const addService = (lessonFormat = "private") => {
    if (rows.length >= coachLessonServiceLimit) return;
    const suffix = `${Date.now().toString(36)}-${rows.length + 1}`;
    onChange?.([...rows, {
      id: `${lessonFormat}-${suffix}`,
      title: coachLessonFormatLabels[lessonFormat] || "Coaching lesson",
      description: "",
      lessonFormat,
      durationMinutes: 60,
      price: "",
      priceCents: 0,
      priceConfigured: false,
      maxPlayers: lessonFormat === "private" ? 1 : 4,
      active: true,
      currency: normaliseCurrencyCode(currency)
    }]);
  };
  const activePricedCount = rows.filter((service) => service.active && service.priceConfigured && coachLessonServicePriceCents(service, currency) > 0).length;
  const activeFreeCount = rows.filter((service) => service.active && service.priceConfigured && coachLessonServicePriceCents(service, currency) === 0).length;
  const missingPriceCount = rows.filter((service) => service.active && !service.priceConfigured).length;

  return (
    <section className="coach-service-editor">
      <div className="form-section-head">
        <div>
          <h3>Lesson types and per-session prices</h3>
          <span>Players choose one clear service before selecting a calendar time. Prices are charged once per session, not recalculated in the browser.</span>
        </div>
        <em>{rows.length}/{coachLessonServiceLimit}</em>
      </div>
      {rows.length ? (
        <div className="coach-service-list">
          {rows.map((service, index) => (
            <article className={service.active ? "coach-service-row active" : "coach-service-row"} key={service.id}>
              <div className="coach-service-row-head">
                <span className="coach-service-number">{index + 1}</span>
                <label className="coach-service-active">
                  <input type="checkbox" checked={service.active} onChange={(event) => updateService(service.id, { active: event.target.checked })} />
                  <span>{service.active ? "Bookable" : "Hidden"}</span>
                </label>
                <button type="button" className="icon-only secondary" onClick={() => removeService(service.id)} aria-label={`Remove ${service.title || "lesson type"}`} title="Remove lesson type">
                  <X size={16} />
                </button>
              </div>
              <div className="coach-service-fields">
                <label className="wide">
                  <span>Session name</span>
                  <input value={service.title} onChange={(event) => updateService(service.id, { title: event.target.value })} placeholder="Private badminton lesson" />
                </label>
                <label>
                  <span>Format</span>
                  <select value={service.lessonFormat} onChange={(event) => {
                    const lessonFormat = event.target.value;
                    updateService(service.id, {
                      lessonFormat,
                      maxPlayers: lessonFormat === "private" ? 1 : Math.max(2, Number(service.maxPlayers) || 4)
                    });
                  }}>
                    {coachLessonFormatOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Duration</span>
                  <select value={service.durationMinutes} onChange={(event) => updateService(service.id, { durationMinutes: Number(event.target.value) })}>
                    {coachLessonDurationOptions.map((minutes) => <option value={minutes} key={minutes}>{minutes < 60 ? `${minutes} minutes` : minutes === 60 ? "1 hour" : `${minutes / 60} hours`}</option>)}
                  </select>
                </label>
                <label>
                  <span>Price per session ({normaliseCurrencyCode(currency).toUpperCase()})</span>
                  <input
                    value={service.price}
                    onChange={(event) => {
                      const price = event.target.value;
                      updateService(service.id, {
                        price,
                        priceCents: 0,
                        priceConfigured: price.trim() !== "" && Number.isFinite(Number.parseFloat(price.replace(/[^0-9.]/g, "")))
                      });
                    }}
                    inputMode="decimal"
                    placeholder="60"
                  />
                </label>
                <label>
                  <span>Maximum players</span>
                  <input
                    type="number"
                    min="1"
                    max="40"
                    disabled={service.lessonFormat === "private"}
                    value={service.lessonFormat === "private" ? 1 : service.maxPlayers}
                    onChange={(event) => updateService(service.id, { maxPlayers: Math.max(1, Math.min(40, Number(event.target.value) || 1)) })}
                  />
                </label>
                <label className="wide">
                  <span>What is included <FieldRequirement optional /></span>
                  <textarea value={service.description} onChange={(event) => updateService(service.id, { description: event.target.value })} placeholder="Who this session suits, what you cover and anything the player should bring." />
                </label>
              </div>
              <div className="coach-service-row-summary">
                <strong>{service.title || "Untitled lesson"}</strong>
                <span>{service.durationMinutes} min · {coachLessonServicePriceLabel(service, currency)} · {service.maxPlayers} player{service.maxPlayers === 1 ? "" : "s"}</span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="coach-service-empty">
          <Banknote size={22} />
          <strong>No lesson types added</strong>
          <span>Add at least one session with a duration and price before players can use the booking calendar.</span>
        </div>
      )}
      <div className="coach-service-add-actions">
        <button type="button" className="secondary" onClick={() => addService("private")} disabled={rows.length >= coachLessonServiceLimit}><Plus size={16} /> Add private lesson</button>
        <button type="button" className="secondary" onClick={() => addService("group")} disabled={rows.length >= coachLessonServiceLimit}><UsersRound size={16} /> Add group lesson</button>
      </div>
      <div className="coach-service-readiness">
        <BadgeCheck size={17} />
        <span>{activePricedCount ? `${activePricedCount} paid lesson type${activePricedCount === 1 ? "" : "s"}` : "No paid lesson types"}{activeFreeCount ? ` · ${activeFreeCount} free` : ""}{missingPriceCount ? ` · ${missingPriceCount} need${missingPriceCount === 1 ? "s" : ""} a price` : ""}</span>
      </div>
    </section>
  );
}

function PublicLinkList({ links, emptyText = "No public links added yet" }) {
  const cleanLinks = cleanLinkRows(links);
  if (!cleanLinks.length) return <div className="profile-media-empty"><ExternalLink size={20} /><span>{emptyText}</span></div>;
  return (
    <div className="public-link-grid">
      {cleanLinks.map((link, index) => (
        <a key={`${link.url}-${index}`} href={link.url} target="_blank" rel="noreferrer">
          <ExternalLink size={16} />
          <strong>{link.platform || link.label || "Link"}</strong>
          <span>{link.url}</span>
        </a>
      ))}
    </div>
  );
}

function MediaEmbedGrid({ links, emptyTitle = "No media added yet", emptyText = "Photos and clips will appear here after links are added.", limit = 6, showStockFallback = false, stockType = "general", stockSeed = "" }) {
  const cleanLinks = cleanLinkRows(links, "Media");
  if (!cleanLinks.length) {
    if (showStockFallback) {
      const fallbackLinks = stockMediaLinksFor(stockType, stockSeed || emptyTitle, Math.min(3, limit));
      return (
        <div className="media-grid embed-grid stock-preview-grid">
          {fallbackLinks.map((item, index) => (
            <a href={item.sourceUrl || item.url} target="_blank" rel="noreferrer" key={`${item.url}-${index}`} className="media-image-card stock">
              <img src={item.url} alt={item.label} loading="lazy" decoding="async" />
              <span><ImageIcon size={17} /> Stock image</span>
            </a>
          ))}
        </div>
      );
    }
    return (
      <div className="profile-media-empty">
        <Video size={22} />
        <strong>{emptyTitle}</strong>
        <span>{emptyText}</span>
      </div>
    );
  }

  return (
    <div className="media-grid embed-grid">
      {cleanLinks.slice(0, limit).map((item, index) => {
        const embed = parseMediaEmbed(item.url);
        const genericLabels = new Set(["Media", "Player media", "Coach media", "Centre photo"]);
        const label = item.label && !genericLabels.has(item.label) ? item.label : embed?.provider || item.label || `Media ${index + 1}`;
        const reviewStatus = publicMediaNeedsModeration(item) ? normaliseMediaModerationStatus(item.moderationStatus) : "";
        const reviewBadge = reviewStatus ? (
          <em className={`media-review-pill ${reviewStatus}`}>{mediaModerationStatusLabels[reviewStatus]}</em>
        ) : null;
        if (isImageMediaUrl(item.url)) {
          return (
            <a href={item.url} target="_blank" rel="noreferrer" key={`${item.url}-${index}`} className="media-image-card">
              <img src={item.url} alt="" loading="lazy" decoding="async" />
              <span><ImageIcon size={17} /> {label}</span>
              {reviewBadge}
            </a>
          );
        }
        if (embed?.type === "iframe") {
          return (
            <article className={embed.portrait ? "media-embed-card portrait" : "media-embed-card"} key={`${item.url}-${index}`}>
              <iframe
                title={`${label} embed`}
                src={embed.embedUrl}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
              <a href={embed.url} target="_blank" rel="noreferrer">
                <PlayCircle size={17} /> {label}
              </a>
              {reviewBadge}
            </article>
          );
        }
        return (
          <a href={item.url} target="_blank" rel="noreferrer" key={`${item.url}-${index}`} className="media-link-card">
            <Video size={28} />
            <strong>{label}</strong>
            <span>Open media link</span>
            {reviewBadge}
          </a>
        );
      })}
    </div>
  );
}

const mediaLinksFromImages = (sources = [], label = "Photo") => collectImageUrls(sources)
  .map((url, index) => ({ label: `${label} ${index + 1}`, url }));

const normalisePublicMedia = (...sources) => {
  const seen = new Set();
  return sources
    .flatMap((source) => cleanLinkRows(source, "Media"))
    .map((item, index) => {
      const embed = parseMediaEmbed(item.url);
      const type = isImageMediaUrl(item.url) ? "image" : embed?.type === "iframe" ? "embed" : "link";
      const genericLabels = new Set(["Media", "Player media", "Coach media", "Centre media", "Centre photo", "Group media", "Event media"]);
      const label = item.label && !genericLabels.has(item.label) ? item.label : embed?.provider || item.label || `Media ${index + 1}`;
      return { ...item, embed, type, label };
    })
    .filter((item) => {
      if (item.type === "image" && !getAllowedUserMediaUrl(item.url)) return false;
      const key = /^data:image\//i.test(item.url) ? item.url : normaliseImageFingerprint(item.url);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const publicMediaIdentity = (url = "") => /^data:image\//i.test(url) ? url : normaliseImageFingerprint(url);
const publicMediaWithout = (links = [], exclude = []) => {
  const excludedMedia = new Set(collectImageUrls(exclude).map(publicMediaIdentity));
  return normalisePublicMedia(links).filter((item) => !excludedMedia.has(publicMediaIdentity(item.url)));
};

function PublicMediaShowcase({ title, eyebrow = "Media", links, exclude = [], emptyTitle = "No media added yet", emptyText = "Photos, clips and public media will appear here after they are added.", requireModeration = false, showStockFallback = false, stockType = "general", stockSeed = "" }) {
  const allMedia = publicMediaWithout(links, exclude);
  const media = requireModeration ? allMedia.filter(isApprovedPublicMediaLink) : allMedia;
  const hiddenMediaCount = requireModeration ? allMedia.length - media.length : 0;
  const stockMedia = media.length || !showStockFallback ? [] : normalisePublicMedia(stockMediaLinksFor(stockType, stockSeed || title, 4))
    .map((item) => ({ ...item, source: "stock" }));
  const visibleMedia = media.length ? media : stockMedia;
  const showingStockFallback = !media.length && stockMedia.length > 0;
  const [activeIndex, setActiveIndex] = useState(null);
  const activeItem = activeIndex === null ? null : visibleMedia[activeIndex];

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowRight") setActiveIndex((current) => (current + 1) % visibleMedia.length);
      if (event.key === "ArrowLeft") setActiveIndex((current) => (current - 1 + visibleMedia.length) % visibleMedia.length);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, visibleMedia.length]);
  useEffect(() => {
    if (activeIndex !== null && activeIndex >= visibleMedia.length) setActiveIndex(null);
  }, [activeIndex, visibleMedia.length]);

  const showPrevious = () => setActiveIndex((current) => (current - 1 + visibleMedia.length) % visibleMedia.length);
  const showNext = () => setActiveIndex((current) => (current + 1) % visibleMedia.length);
  const visibleEmptyTitle = hiddenMediaCount ? "Media waiting for review" : emptyTitle;
  const visibleEmptyText = hiddenMediaCount
    ? `${hiddenMediaCount} media item${hiddenMediaCount === 1 ? " is" : "s are"} saved and will appear after Badminta review.`
    : emptyText;

  return (
    <section className="public-media-showcase">
      <div className="public-media-head">
        <div>
          <p>{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        {media.length > 0 ? <span>{media.length} item{media.length === 1 ? "" : "s"}</span> : showingStockFallback ? <span>Stock preview</span> : null}
      </div>
      {visibleMedia.length ? (
        <>
          <div className={`public-media-grid count-${Math.min(visibleMedia.length, 6)}${showingStockFallback ? " stock-preview" : ""}`}>
            {visibleMedia.map((item, index) => (
            <button type="button" key={`${item.url}-${index}`} onClick={() => setActiveIndex(index)} className={`public-media-card ${item.type}${item.source === "stock" ? " stock" : ""}`}>
              {item.type === "image" ? (
                <img src={item.url} alt={item.label} loading="lazy" decoding="async" />
              ) : item.type === "embed" ? (
                <div className="public-media-embed-preview">
                  <PlayCircle size={34} />
                  <span>{item.embed.provider}</span>
                </div>
              ) : (
                <div className="public-media-link-preview">
                  <ExternalLink size={30} />
                  <span>Open media link</span>
                </div>
              )}
              <em>{item.label}</em>
              {item.source === "stock" && <span className="stock-media-chip">Stock image</span>}
            </button>
            ))}
          </div>
          {showingStockFallback && (
            <div className="public-media-stock-note">
              <ImageIcon size={17} />
              <span>{visibleEmptyTitle}. {visibleEmptyText} Licensed stock badminton imagery is shown until approved profile media is published.</span>
            </div>
          )}
        </>
      ) : (
        <div className="public-media-empty">
          <Video size={24} />
          <strong>{visibleEmptyTitle}</strong>
          <span>{visibleEmptyText}</span>
        </div>
      )}

      {activeItem && (
        <div className="public-media-lightbox" role="dialog" aria-modal="true" aria-label={`${title} media`}>
          <div className="public-media-lightbox-panel">
            <div className="public-media-lightbox-head">
              <div>
                <strong>{activeItem.label}</strong>
                <span>{activeIndex + 1} of {visibleMedia.length}{activeItem.source === "stock" ? " - stock image" : ""}</span>
              </div>
              <button type="button" onClick={() => setActiveIndex(null)} aria-label="Close media"><X size={20} /></button>
            </div>
            <div className="public-media-lightbox-stage">
              {visibleMedia.length > 1 && <button type="button" onClick={showPrevious} aria-label="Previous media"><ChevronLeft size={25} /></button>}
              {activeItem.type === "image" ? (
                <img src={activeItem.url} alt={activeItem.label} />
              ) : activeItem.type === "embed" ? (
                <iframe
                  title={`${activeItem.label} media`}
                  src={activeItem.embed.embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <div className="public-media-lightbox-link">
                  <ExternalLink size={34} />
                  <strong>{activeItem.label}</strong>
                  <a href={activeItem.url} target="_blank" rel="noreferrer">Open media link</a>
                </div>
              )}
              {visibleMedia.length > 1 && <button type="button" onClick={showNext} aria-label="Next media"><ChevronRight size={25} /></button>}
            </div>
            {visibleMedia.length > 1 && (
              <div className="public-media-lightbox-strip">
                {visibleMedia.map((item, index) => (
                  <button type="button" key={`${item.url}-thumb-${index}`} className={index === activeIndex ? "active" : ""} onClick={() => setActiveIndex(index)} aria-label={`Open ${item.label}`}>
                    {item.type === "image" ? <img src={item.url} alt="" loading="lazy" decoding="async" /> : <PlayCircle size={17} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function VenueTagPicker({ value, venueId, onChange, allowQuickAdd = false }) {
  const selectedVenue = venues.find((venue) => venue.id === venueId || venue.name === value) || null;
  const [query, setQuery] = useState(value || "");
  const [quickCentreName, setQuickCentreName] = useState(value || "");
  const [quickLocation, setQuickLocation] = useState(null);
  const [quickMessage, setQuickMessage] = useState("");
  const matches = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (text.length < 2 || (selectedVenue && text === String(selectedVenue.name || "").trim().toLowerCase())) return [];
    const scoreVenue = (venue) => {
      const name = String(venue.name || "").toLowerCase();
      const suburb = String(venue.suburb || "").toLowerCase();
      const city = String(venue.city || "").toLowerCase();
      const address = String(venue.address || "").toLowerCase();
      const typeBoost = venue.listingType === "venue" ? 35 : 0;
      if (name === text) return 900 + typeBoost;
      if (name.startsWith(text)) return 820 + typeBoost;
      if (suburb === text || city === text) return 760 + typeBoost;
      if (name.includes(text)) return 700 + typeBoost;
      if (suburb.includes(text) || city.includes(text)) return 620 + typeBoost;
      if (address.includes(text)) return 480 + typeBoost;
      return 0;
    };
    return venues
      .map((venue) => ({ venue, score: scoreVenue(venue) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || String(a.venue.name || "").localeCompare(String(b.venue.name || "")))
      .map((item) => item.venue)
      .slice(0, 8);
  }, [query, selectedVenue]);

  useEffect(() => {
    setQuery(selectedVenue?.name || value || "");
  }, [selectedVenue?.name, value]);

  useEffect(() => {
    if (!quickCentreName && query.trim()) setQuickCentreName(query.trim());
  }, [query, quickCentreName]);

  const selectVenue = (venue) => {
    setQuery(venue.name);
    setQuickMessage("");
    onChange({ venue: venue.name, venueId: venue.id });
  };

  const addMissingCentre = () => {
    const name = quickCentreName.trim() || query.trim();
    const savedLocation = locationSnapshot(quickLocation);
    if (!name || !savedLocation) {
      setQuickMessage("Add the centre name and select its suburb or city first.");
      return;
    }
    const pendingVenueId = `pending-centre-${slugifyPath(name)}-${shortStableHash(`${savedLocation.label || ""}-${savedLocation.city || ""}-${savedLocation.countryCode || ""}`)}`;
    setQuery(name);
    setQuickMessage("Pending centre added to this group. Badminta can verify the full listing later.");
    onChange({
      venue: name,
      venueId: pendingVenueId,
      pendingVenue: {
        id: pendingVenueId,
        name,
        status: "pending_badminton_centre",
        source: "organiser_quick_pin",
        city: savedLocation.city || "",
        suburb: savedLocation.suburb || locationAreaLabel(savedLocation),
        country: savedLocation.country || "",
        countryCode: savedLocation.countryCode || "",
        location: savedLocation
      },
      city: savedLocation.city || "",
      suburb: savedLocation.suburb || locationAreaLabel(savedLocation),
      location: savedLocation
    });
  };

  return (
    <section className="tag-picker-field venue-tag-picker">
      <div className="form-section-head">
        <h3>Venue</h3>
        <span>Search and select the centre players will attend.</span>
      </div>
      <label className="tag-picker-search">
        <span>Search venue</span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search centre, suburb or city" />
      </label>
      {(selectedVenue || value) && (
        <div className="tag-chip-row">
          <span className="tag-chip">
            <MapPin size={14} />
            {selectedVenue?.name || value}
            {!selectedVenue && <em>Pending centre</em>}
            <button type="button" onClick={() => onChange({ venue: "", venueId: "", pendingVenue: null })} aria-label="Remove selected venue"><X size={13} /></button>
          </span>
        </div>
      )}
      <div className="tag-search-results">
        {matches.map((venue) => (
          <button type="button" key={venue.id} className={selectedVenue?.id === venue.id ? "active" : ""} onClick={() => selectVenue(venue)}>
            <Building2 size={16} />
            <span>
              <strong>{venue.name}</strong>
              <em>{[venue.suburb, venue.city, venue.address].filter(Boolean).join(" · ") || "Centre listing"}</em>
            </span>
          </button>
        ))}
      </div>
      {allowQuickAdd && !selectedVenue && query.trim().length >= 2 && (
        <div className="quick-centre-pin">
          <div>
            <MapPinned size={18} />
            <span>
              <strong>Can’t find the centre?</strong>
              <em>Add a quick pending centre with its name and location. It will be linked to this group now and can be verified later.</em>
            </span>
          </div>
          <label>
            <span>Centre name</span>
            <input value={quickCentreName} onChange={(event) => setQuickCentreName(event.target.value)} placeholder="Centre or school hall name" />
          </label>
          <SuburbPicker
            label="Centre suburb or city"
            value={quickLocation ? locationAreaLabel(quickLocation) : ""}
            selectedLocation={quickLocation}
            required
            onChange={({ location }) => {
              setQuickLocation(location);
              setQuickMessage("");
            }}
            placeholder="Search suburb or city"
            helper="Use the suburb or city where the group actually plays."
          />
          <button type="button" onClick={addMissingCentre}>
            <Plus size={15} /> Add pending centre
          </button>
          {quickMessage && <p className="search-message">{quickMessage}</p>}
        </div>
      )}
    </section>
  );
}

function EmailTagInput({ emails = [], onChange }) {
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState("");
  const cleanEmails = cleanEmailTags(emails);

  const addDraftEmails = () => {
    const candidates = String(draft || "").split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean);
    if (!candidates.length) return;
    const valid = cleanEmailTags(candidates);
    if (!valid.length) {
      setMessage("Enter a valid email address.");
      return;
    }
    onChange(cleanEmailTags([...cleanEmails, ...valid]));
    setDraft("");
    setMessage(valid.length < candidates.length ? "Some invalid email addresses were skipped." : "");
  };

  const removeEmail = (email) => onChange(cleanEmails.filter((item) => item !== email));

  return (
    <section className="tag-picker-field email-tag-field">
      <div className="form-section-head">
        <h3>Invite members</h3>
        <span>Add email addresses as private invite tags.</span>
      </div>
      <label className="tag-picker-search">
        <span>Email addresses</span>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={addDraftEmails}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
              event.preventDefault();
              addDraftEmails();
            }
          }}
          inputMode="email"
          placeholder="member@example.com, teammate@example.com"
        />
      </label>
      <div className="tag-chip-row">
        {cleanEmails.map((email) => (
          <span className="tag-chip" key={email}>
            <Mail size={14} />
            {email}
            <button type="button" onClick={() => removeEmail(email)} aria-label={`Remove ${email}`}><X size={13} /></button>
          </span>
        ))}
      </div>
      <div className="tag-field-actions">
        <button type="button" onClick={addDraftEmails}>
          <Plus size={15} /> Add email
        </button>
        <span>{cleanEmails.length ? `${cleanEmails.length} email${cleanEmails.length === 1 ? "" : "s"} added` : "Emails become invite tags."}</span>
      </div>
      {message && <p className="search-message">{message}</p>}
    </section>
  );
}

function VenueMedia({ venue, className = "", mapFallback = "exact" }) {
  const [image] = getVenueImages(venue);
  if (image) {
    return <img className={["venue-media-image", className].filter(Boolean).join(" ")} src={image} alt={venue.name} loading="lazy" decoding="async" />;
  }
  const rawPoint = mapFallback === "any" ? getVenueMapPoint(venue) : getVenueMediaPoint(venue);
  const point = getVenuePreviewMapPoint(venue, rawPoint);
  if (point) {
    return <VenueMapPreview venue={venue} point={point} className={className} />;
  }
  const stockMedia = getStockMediaFallback("centre", `${venue?.name || ""}|${venue?.city || ""}|${venue?.suburb || ""}`);
  if (stockMedia) {
    return <StockMediaImage media={stockMedia} alt={`${venue?.name || "Badminton centre"} stock badminton image`} className={className} />;
  }
  return <NoImagePlaceholder className={className} />;
}

function VenueMapPreview({ venue, point, className = "", label = "Map location" }) {
  const tileUrl = getOsmTileUrl(point, 16);
  const previewRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!previewRef.current || isVisible) return undefined;
    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setIsVisible(true);
      observer.disconnect();
    }, { rootMargin: "240px" });
    observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div
      ref={previewRef}
      className={["venue-map-preview", !isVisible ? "loading" : "", className].filter(Boolean).join(" ")}
      role="img"
      aria-label={`${venue?.name || "Badminton centre"} map preview`}
      style={{
        backgroundImage: isVisible ? `url("${tileUrl}")` : "none",
        backgroundPosition: getVenueMapBackgroundPosition(venue)
      }}
    >
      <span className="venue-map-pin"><Target size={17} strokeWidth={3} /></span>
      <span className="venue-map-label">{label}</span>
      <span className="venue-map-attribution">© OSM</span>
    </div>
  );
}

const getVenueMapAddressLabel = (venue = {}) => {
  const parts = [
    getVenueAddressLabel(venue),
    venue.suburb || venue.location?.suburb,
    venue.city || venue.location?.city,
    venue.state || venue.location?.state,
    venue.postcode || venue.location?.postcode,
    venue.country || venue.location?.country
  ].map((value) => String(value || "").trim()).filter(Boolean);
  return parts.filter((part, index) => {
    const key = compactLocationText(part);
    return key && parts.findIndex((candidate) => compactLocationText(candidate) === key) === index
      && !parts.slice(0, index).some((candidate) => compactLocationText(candidate).includes(key));
  }).join(", ");
};

function VenueMapEmbed({ venue, point, className = "" }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const resizeCleanupRef = useRef(null);
  const [mapStatus, setMapStatus] = useState("loading");
  const [mapMountKey, setMapMountKey] = useState(0);

  useEffect(() => {
    if (!mapContainerRef.current || !Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) return undefined;
    let cancelled = false;
    setMapStatus("loading");
    resizeCleanupRef.current?.();
    resizeCleanupRef.current = null;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    loadLeafletRuntime().then((leaflet) => {
      if (cancelled || !mapContainerRef.current) return;
      const map = leaflet.map(mapContainerRef.current, {
        attributionControl: true,
        zoomControl: true,
        scrollWheelZoom: false
      }).setView([point.lat, point.lon], 15);
      addOpenStreetMapTileLayer(leaflet, map, (status) => {
        if (!cancelled) setMapStatus(status);
      });
      const mapAddress = getVenueMapAddressLabel(venue);
      leaflet.marker([point.lat, point.lon], { icon: createBadmintonMarkerIcon(leaflet, venue) })
        .addTo(map)
        .bindTooltip(`<strong>${escapeHtml(venue.name)}</strong><span>${escapeHtml(mapAddress)}</span>`, {
          direction: "top",
          offset: [0, -36],
          opacity: 0.98,
          permanent: true,
          className: "venue-map-address-tooltip"
        });
      mapInstanceRef.current = map;
      resizeCleanupRef.current = observeLeafletMapSize(map, mapContainerRef.current);
    }).catch(() => {
      if (!cancelled) setMapStatus("error");
    });
    return () => {
      cancelled = true;
      resizeCleanupRef.current?.();
      resizeCleanupRef.current = null;
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
    };
  }, [point?.lat, point?.lon, venue.id, venue.name, mapMountKey]);

  if (!Number.isFinite(point?.lat) || !Number.isFinite(point?.lon)) {
    return (
      <StockMediaImage
        media={getStockMediaFallback("centre", `${venue?.name || ""}|map-fallback`)}
        alt={`${venue?.name || "Badminton centre"} stock image`}
        className={className}
      />
    );
  }

  return (
    <div className={["venue-map-embed", className].filter(Boolean).join(" ")}>
      <div className="venue-map-embed-map" aria-label={`${venue.name} map`}>
        <div className="venue-map-embed-leaflet" ref={mapContainerRef} />
        {mapStatus !== "ready" && <VenueMapPreview venue={venue} point={point} label={mapStatus === "loading" ? "Loading map" : "Map preview"} />}
        <MapLoadPanel status={mapStatus} compact onRetry={() => {
          leafletRuntimePromise = null;
          setMapMountKey((value) => value + 1);
        }} />
      </div>
      <div className="venue-map-embed-label">
        <MapPin size={16} />
        <span>{getVenueMapAddressLabel(venue)}</span>
      </div>
    </div>
  );
}

function StatusHint({ label, icon: Icon = CircleHelp, className = "", tone = "missing" }) {
  return (
    <span
      className={["status-hint", tone, className].filter(Boolean).join(" ")}
      title={label}
      aria-label={label}
      data-tooltip={label}
    >
      <Icon size={15} />
    </span>
  );
}

function FactValue({ children, icon: Icon, className = "" }) {
  return (
    <span className={["fact-value", className].filter(Boolean).join(" ")}>
      {Icon && <Icon size={14} />}
      <span>{children}</span>
    </span>
  );
}

function PriceStatus({ venue, compact = false }) {
  const hasPrice = hasVenuePrice(venue);
  const label = hasPrice ? getVenuePriceLabel(venue) : "Pricing not listed";
  if (!hasPrice) {
    return <StatusHint label={label} icon={BadgeDollarSign} className={compact ? "price-status compact missing" : "price-status missing"} />;
  }
  return (
    <span className="price-status known" title={label} aria-label={label}>
      <CircleDollarSign size={15} />
      <span>{label}</span>
    </span>
  );
}

function PendingAmount({ label = "Pending" }) {
  return <StatusHint label={label} icon={CircleHelp} className="pending-amount" />;
}

function CourtStatus({ venue }) {
  return hasVenueCourtCount(venue)
    ? <FactValue>{getVenueCourtLabel(venue)}</FactValue>
    : <StatusHint label="Court count unknown" icon={Building2} />;
}

function HoursStatus({ venue }) {
  return hasVenueHours(venue)
    ? <FactValue>{getVenueHoursLabel(venue)}</FactValue>
    : <StatusHint label="Hours unknown" icon={Clock3} />;
}

function ContactStatus({ venue }) {
  return hasVenueContact(venue)
    ? <FactValue>{getVenueContactLabel(venue)}</FactValue>
    : <StatusHint label="Contact unknown" icon={Phone} />;
}

function DistanceStatus({ venue }) {
  return hasVenueDistance(venue)
    ? <FactValue icon={Navigation}>{getVenueDistanceLabel(venue)}</FactValue>
    : <StatusHint label="Distance unknown" icon={Navigation} />;
}

function RatingStatus({ venue }) {
  return hasVenueRating(venue)
    ? <FactValue icon={Star}>{getVenueRatingLabel(venue)}</FactValue>
    : <StatusHint label="Rating not available" icon={Star} />;
}

function AmenityStatus({ label }) {
  return label && !String(label).toLowerCase().includes("unknown")
    ? <span><Check size={16} /> {label}</span>
    : <StatusHint label="Amenities unknown" icon={CircleHelp} />;
}

function VenueFactLine({ venue, includeDistance = false, includePrice = false }) {
  const facts = [
    includeDistance && hasVenueDistance(venue) ? <DistanceStatus venue={venue} /> : null,
    hasVenueCourtCount(venue) ? <CourtStatus venue={venue} /> : null,
    hasVenueHours(venue) ? <HoursStatus venue={venue} /> : null,
    includePrice && hasVenuePrice(venue) ? <PriceStatus venue={venue} compact /> : null
  ].filter(Boolean);

  if (!facts.length) return null;

  return (
    <span className="venue-fact-line">
      {facts.map((fact, index) => <React.Fragment key={index}>{fact}</React.Fragment>)}
    </span>
  );
}

function VenueTile({ venue, saved, toggleSaved, navigate }) {
  const fallbackArea = getVenueAreaLabel(venue);
  const showFallbackArea = !hasVenueDistance(venue)
    && !hasVenueCourtCount(venue)
    && !hasVenueHours(venue)
    && fallbackArea !== "Location to be confirmed";
  return (
    <article className="listing-card venue-tile">
      <div className="photo-frame">
        <VenueMedia venue={venue} mapFallback="any" />
        <span className="listing-kind-badge centre"><Building2 size={13} /> Centre / court booking</span>
        <button type="button" className={saved ? "save active" : "save"} onClick={() => toggleSaved(venue.id)} aria-label="Save venue">
          <Heart size={19} fill={saved ? "currentColor" : "none"} />
        </button>
      </div>
      <a
        className="listing-copy"
        href={routeHref("venue", { venue })}
        onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}
      >
        <strong>{venue.name}</strong>
        <VenueFactLine venue={venue} includeDistance />
        {showFallbackArea && <span>{fallbackArea}</span>}
        {hasVenuePrice(venue) && <b><PriceStatus venue={venue} compact /></b>}
      </a>
    </article>
  );
}

function GroupTile({ group, navigate }) {
  const groupImage = getPublicProfileImage(getGroupImage(group));
  const publicPayload = publicProfileNavigationPayload("group", group);
  const target = publicPayload ? "publicGroupProfile" : "group";
  const payload = publicPayload || { group };
  const publicSessions = getGroupPublicSessions(group);
  const nextSession = publicSessions[0] || null;
  const hasPublishedSession = Boolean(nextSession);
  const playerCount = Number(group.spots || 0);
  const capacity = Number(group.capacity || 0);
  const participationLabel = hasPublishedSession && capacity
    ? `${playerCount}/${capacity} players joined`
    : capacity ? `${capacity} player capacity` : "Capacity available on profile";
  const scheduleLabel = hasPublishedSession
    ? `${nextSession.dateLabel || formatDateLabel(nextSession.date)} at ${nextSession.time}`
    : getGroupScheduleLabel(group);
  const priceNumber = getGroupPriceNumber(hasPublishedSession ? nextSession : group);
  const priceCurrency = normaliseCurrencyCode(nextSession?.currency || group.currency || marketplaceCurrencyForLocation(group.location));
  const actionLabel = hasPublishedSession
    ? priceNumber ? `Join published session - ${formatMoney(priceNumber, priceCurrency)} / player` : "Join published session - Free"
    : priceNumber ? `View group - ${formatMoney(priceNumber, priceCurrency)} regular price` : "View group details";
  return (
    <article className="listing-card group-tile">
      <div className="photo-frame">
        {groupImage ? <img src={groupImage} alt={group.title} loading="lazy" decoding="async" /> : <NoImagePlaceholder label="No group image added" />}
        <span className="listing-kind-badge group"><UsersRound size={13} /> {hasPublishedSession ? "Published session / shared cost" : "Weekly social group profile"}</span>
      </div>
      <a
        className="listing-copy"
        href={routeHref(target, payload)}
        onClick={(event) => handleSeoRouteClick(event, navigate, target, payload)}
      >
        <strong>{group.title}</strong>
        <span>Hosted at {group.venue}</span>
        <span>{scheduleLabel} - {participationLabel}</span>
        <b>{actionLabel} <em>{group.band}</em></b>
      </a>
      <AvatarStack images={group.members} />
    </article>
  );
}

function CourtSlotTile({ venue, navigate }) {
  return (
    <article className="slot-tile">
      <VenueMedia venue={venue} mapFallback="any" />
      <div>
        <span><HoursStatus venue={venue} /></span>
        <strong>{venue.name}</strong>
        <p>{venue.suburb} <CourtStatus venue={venue} /></p>
        <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}>View details</a>
      </div>
    </article>
  );
}

function PlayerTile({ player, navigate }) {
  const publicPayload = publicProfileNavigationPayload("player", player);
  const target = publicPayload ? "publicPlayerProfile" : "player";
  const payload = publicPayload || { player };
  return (
    <article className="player-tile">
      <a href={routeHref(target, payload)} onClick={(event) => handleSeoRouteClick(event, navigate, target, payload)}>
        <PlayerCardMedia src={player.image} name={player.name} className="player-tile-stock" />
        <strong>{player.name}</strong>
        <span>{player.band} - {player.city}</span>
        <b>{player.rating} Badminta rating</b>
      </a>
    </article>
  );
}

const resultFilterOptions = [
  { key: "verified", label: "Verified", Icon: BadgeCheck, matches: (venue) => venueMatchesFilter(venue, "verified") },
  { key: "price", label: "Price listed", Icon: CircleDollarSign, matches: (venue) => hasVenuePrice(venue) },
  { key: "courts", label: "Courts listed", Icon: Grid3X3, matches: (venue) => hasVenueCourtCount(venue) },
  { key: "parking", label: "Parking", Icon: MapPin, matches: (venue) => venueMatchesFilter(venue, "parking") },
  { key: "coaching", label: "Coaching", Icon: ClipboardCheck, matches: (venue) => venueMatchesFilter(venue, "coaching") },
  { key: "late", label: "Open late", Icon: Clock3, matches: (venue) => venueMatchesFilter(venue, "late") }
];

const getInitialResultFilters = (activeFilter) => {
  if (resultFilterOptions.some((option) => option.key === activeFilter)) return [activeFilter];
  return [];
};

function ResultsPage({ city, venues, groups, saved, toggleSaved, mapVisible, setMapVisible, activeFilter, searchCriteria, osmCentreStatus, osmCentreError, usingApiCentres, navigate }) {
  const [resultFilters, setResultFilters] = useState(() => getInitialResultFilters(activeFilter));
  const copy = filterContent[activeFilter] || {};
  useEffect(() => {
    setResultFilters(getInitialResultFilters(activeFilter));
  }, [activeFilter, searchCriteria.location?.id]);
  const filteredVenues = useMemo(() => {
    if (!resultFilters.length) return venues;
    return venues.filter((venue) => resultFilters.every((key) => {
      const option = resultFilterOptions.find((item) => item.key === key);
      return option ? option.matches(venue) : true;
    }));
  }, [venues, resultFilters]);
  const toggleResultFilter = (key) => {
    setResultFilters((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };
  const clearResultFilters = () => setResultFilters([]);
  const title = copy.resultsTitle ? copy.resultsTitle.replace("Melbourne", city) : `Badminton centres for court booking in ${locationTitle(searchCriteria, city)}`;
  const activeCountryCode = normaliseCountryCode(searchCriteria.location?.countryCode || searchCriteria.location?.country, city);
  const activeCountryCount = getGlobalCentreCountryCount(activeCountryCode);
  const centreCountLabel = usingApiCentres
    ? `${filteredVenues.length} of ${venues.length} court hire venue${venues.length === 1 ? "" : "s"} shown`
    : resultFilters.length ? `${filteredVenues.length} of ${venues.length} centres match filters` : copy.eyebrow || `${venues.length} court hire venue${venues.length === 1 ? "" : "s"}`;
  const directoryCoverageLabel = [
    globalCentreTotalCount ? `${globalCentreTotalCount.toLocaleString()} worldwide centre records` : "",
    activeCountryCount ? `${activeCountryCount.toLocaleString()} in ${getCountryName(activeCountryCode, city) || activeCountryCode}` : "",
    `${venues.length.toLocaleString()} matched to this search`
  ].filter(Boolean).join(" - ");
  const showCentreEmpty = usingApiCentres && !venues.length;
  const showFilteredEmpty = !showCentreEmpty && !filteredVenues.length;
  const renderResultsList = () => (
    <section className="results-list">
      {showCentreEmpty ? <CentreEmptyState searchCriteria={searchCriteria} status={osmCentreStatus} error={osmCentreError} navigate={navigate} /> : showFilteredEmpty ? (
        <CommunityEmptyState
          type="filtered centres"
          title="No centres match these filters"
          text="Remove a filter or open the full marketplace view to compare centres, groups, players, coaches and events in this area."
          primary={{ label: "Clear filters", target: "results" }}
          secondary={{ label: "Explore all", target: "filterHub" }}
          icon={SlidersHorizontal}
          navigate={(target) => target === "results" ? clearResultFilters() : navigate(target)}
          compact
        />
      ) : filteredVenues.map((venue) => (
        <HorizontalVenueCard
          key={venue.id}
          venue={venue}
          saved={saved.includes(venue.id)}
          toggleSaved={toggleSaved}
          navigate={navigate}
        />
      ))}
    </section>
  );
  return (
    <section className="page results-page">
      <div className="results-head">
        <div>
          <p>{centreCountLabel}</p>
          <h1>{title}</h1>
          {directoryCoverageLabel && <span className="directory-coverage-label">{directoryCoverageLabel}</span>}
        </div>
        <div className="view-toggle">
          <button type="button" className={!mapVisible ? "active" : ""} onClick={() => setMapVisible(false)}><ListFilter size={17} /> List</button>
          <button type="button" className={mapVisible ? "active" : ""} onClick={() => setMapVisible(true)}><Map size={17} /> Map</button>
        </div>
      </div>
      <SearchSummary searchCriteria={searchCriteria} />
      <OsmStatusNotice status={osmCentreStatus} error={osmCentreError} />
      <MarketplaceTypeGuide navigate={navigate} compact />
      {!showCentreEmpty && (
        <FilterBar
          activeFilters={resultFilters}
          toggleFilter={toggleResultFilter}
          clearFilters={clearResultFilters}
          navigate={navigate}
          resultCount={filteredVenues.length}
          totalCount={venues.length}
        />
      )}
      <div className={mapVisible ? "results-layout map-with-list" : "results-layout list-only"}>
        {mapVisible ? (
          <>
            <MapView venues={filteredVenues} searchCriteria={searchCriteria} navigate={navigate} />
            <section className="map-results-stack" aria-label="Centre results below map">
              <div className="map-results-heading">
                <p>Results below map</p>
                <h3>{filteredVenues.length} centre result{filteredVenues.length === 1 ? "" : "s"}</h3>
              </div>
              {renderResultsList()}
            </section>
          </>
        ) : renderResultsList()}
      </div>
    </section>
  );
}

function FilterBar({ activeFilters, toggleFilter, clearFilters, navigate, resultCount, totalCount }) {
  return (
    <div className="filter-bar" aria-label="Filter centre results">
      {resultFilterOptions.map(({ key, label, Icon }) => (
        <button type="button" key={key} className={activeFilters.includes(key) ? "active" : ""} onClick={() => toggleFilter(key)} aria-pressed={activeFilters.includes(key)}>
          <Icon size={15} /> {label}
        </button>
      ))}
      {activeFilters.length > 0 && <button type="button" className="reset" onClick={clearFilters}>Clear filters</button>}
      <button type="button" onClick={() => navigate("filterHub")}><SlidersHorizontal size={15} /> Explore all</button>
      <span>{resultCount} of {totalCount} shown</span>
    </div>
  );
}

function HorizontalVenueCard({ venue, saved, toggleSaved, navigate }) {
  const hasPrimaryFacts = hasVenueDistance(venue) || hasVenueCourtCount(venue) || hasVenueHours(venue);
  const fallbackArea = getVenueAreaLabel(venue);
  const showFallbackArea = !hasVenueDistance(venue)
    && !hasVenueCourtCount(venue)
    && !hasVenueHours(venue)
    && fallbackArea !== "Location to be confirmed";
  return (
    <article className="horizontal-card">
      <VenueMedia venue={venue} className="horizontal-media" mapFallback="any" />
      <div className="horizontal-copy">
        <div className="card-topline">
          <button
            type="button"
            className={saved ? "save inline active" : "save inline"}
            aria-label={saved ? `Remove ${venue.name} from saved centres` : `Save ${venue.name}`}
            title={saved ? "Remove from saved centres" : "Save centre"}
            onClick={() => toggleSaved(venue.id)}
          >
            <Heart size={18} fill={saved ? "currentColor" : "none"} />
          </button>
        </div>
        <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}>
          <h2>{venue.name}</h2>
        </a>
        {hasPrimaryFacts && <p><VenueFactLine venue={venue} includeDistance /></p>}
        {showFallbackArea && <p>{fallbackArea}</p>}
        {(hasVenueRating(venue) || hasVenuePrice(venue)) && (
          <div className="card-bottomline">
            {hasVenueRating(venue) && <span><RatingStatus venue={venue} /></span>}
            {hasVenuePrice(venue) && <strong><PriceStatus venue={venue} compact /></strong>}
          </div>
        )}
      </div>
    </article>
  );
}

function MapView({ venues, searchCriteria, navigate }) {
  const location = searchCriteria.location;
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const leafletRef = useRef(null);
  const resizeCleanupRef = useRef(null);
  const [mapReady, setMapReady] = useState(0);
  const [mapStatus, setMapStatus] = useState("loading");
  const [mapMountKey, setMapMountKey] = useState(0);
  const visibleVenues = useMemo(() => venues
    .map((venue) => ({ venue, point: getVenueExactPoint(venue) }))
    .filter((item) => Number.isFinite(item.point?.lat) && Number.isFinite(item.point?.lon))
    .slice(0, 48), [venues]);

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return undefined;
    let cancelled = false;
    setMapStatus("loading");

    loadLeafletRuntime().then((leaflet) => {
      if (cancelled || !mapElementRef.current || mapRef.current) return;
      leafletRef.current = leaflet;
      const hasLocationPoint = Number.isFinite(location?.lat) && Number.isFinite(location?.lon);
      const fallbackPoint = hasLocationPoint
        ? [location.lat, location.lon]
        : [20, 0];
      const map = leaflet.map(mapElementRef.current, {
        center: fallbackPoint,
        zoom: hasLocationPoint ? 11 : 2,
        scrollWheelZoom: true,
        zoomControl: false
      });

      addOpenStreetMapTileLayer(leaflet, map, (status) => {
        if (!cancelled) setMapStatus(status);
      });
      leaflet.control.zoom({ position: "bottomright" }).addTo(map);

      mapRef.current = map;
      markerLayerRef.current = createBadmintonMarkerLayer(leaflet).addTo(map);
      resizeCleanupRef.current = observeLeafletMapSize(map, mapElementRef.current);
      setMapReady((value) => value + 1);
    }).catch(() => {
      if (!cancelled) setMapStatus("error");
    });

    return () => {
      cancelled = true;
      resizeCleanupRef.current?.();
      resizeCleanupRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
      leafletRef.current = null;
    };
  }, [mapMountKey]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    const map = mapRef.current;
    const markerLayer = markerLayerRef.current;
    if (!leaflet || !map || !markerLayer) return;

    markerLayer.clearLayers();
    visibleVenues.forEach(({ venue, point }, index) => {
      const marker = leaflet.marker([point.lat, point.lon], {
        icon: createBadmintonMarkerIcon(leaflet, venue),
        title: venue.name,
        keyboard: true
      });
      marker.bindTooltip(getVenueMapTooltip(venue), {
        className: "badminta-marker-tooltip",
        direction: "top",
        opacity: 1,
        offset: [0, -16]
      });
      marker.on("click", () => navigate("venue", { venue }));
      markerLayer.addLayer(marker);
    });

    if (visibleVenues.length > 1) {
      const bounds = leaflet.latLngBounds(visibleVenues.map(({ point }) => [point.lat, point.lon]));
      map.fitBounds(bounds, { padding: [44, 44], maxZoom: 14, animate: false });
    } else if (visibleVenues.length === 1) {
      const point = visibleVenues[0].point;
      map.setView([point.lat, point.lon], 13, { animate: false });
    } else if (location?.bbox) {
      const [west, south, east, north] = location.bbox;
      map.fitBounds([[south, west], [north, east]], { padding: [32, 32], animate: false });
    } else if (Number.isFinite(location?.lat) && Number.isFinite(location?.lon)) {
      map.setView([location.lat, location.lon], 11, { animate: false });
    }

    window.setTimeout(() => map.invalidateSize(), 0);
  }, [visibleVenues, location, navigate, mapReady]);

  return (
    <aside className="map-view">
      <div className="map-canvas">
        <div ref={mapElementRef} className="leaflet-map" aria-label={`Map around ${location.label}`} />
        <MapLoadPanel status={mapStatus} onRetry={() => {
          leafletRuntimePromise = null;
          setMapMountKey((value) => value + 1);
        }} />
        {!visibleVenues.length && (
          <div className="map-empty-panel">
            <strong>No centre pins for this search</strong>
            <span>Search a wider area or try another suburb.</span>
          </div>
        )}
      </div>
      <div className="map-summary">
        <strong>Map view for {location.label}</strong>
        <span>
          {visibleVenues.length
            ? visibleVenues.length === venues.length
              ? `Showing ${visibleVenues.length} mapped badminton centre${visibleVenues.length === 1 ? "" : "s"} from this result set. Map icons open centre details.`
              : `Showing ${visibleVenues.length} mapped badminton centre${visibleVenues.length === 1 ? "" : "s"} from ${venues.length} list result${venues.length === 1 ? "" : "s"}. Results without a verified street address remain in the list.`
            : "No centre listings are available to pin for this search."}
        </span>
      </div>
    </aside>
  );
}

function CityDiscoverySection({ eyebrow, title, actionLabel, actionTarget, navigate, children }) {
  return (
    <section className="city-discovery-section">
      <div className="section-title-row">
        <div>
          <p>{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <button type="button" onClick={() => navigate(actionTarget)}>{actionLabel} <ChevronRight size={16} /></button>
      </div>
      {children}
    </section>
  );
}

const publicCardFromDirectoryProfile = (profile = {}, kind = "player") => {
  const publicPayload = publicProfileNavigationPayload(kind, profile);
  return {
    id: `city-${kind}-${profile.id || slugifyPath(profile.name || kind)}`,
    type: kind === "coach" ? "Coach" : "Player",
    title: profile.name,
    subtitle: [profile.band, profile.city].filter(Boolean).join(" - "),
    meta: kind === "coach" ? profile.style || `${profile.rating || "No"} rating` : `${profile.rating || "No"} rating - ${profile.plays || 0} plays`,
    image: profile.image,
    target: publicPayload ? publicProfilePageByRole[kind] : kind === "coach" ? "coach" : "player",
    payload: publicPayload || (kind === "coach" ? { coach: profile } : { player: profile })
  };
};

function CityPage({ city, venues, groups, players = [], trainers = [], events = [], searchCriteria, accountProfile, navigate }) {
  const activeLocation = locationTitle(searchCriteria, city);
  const groupEmpty = emptySocialGroupState(activeLocation);
  const playerEmpty = emptyPlayerState(activeLocation);
  const coachEmpty = emptyTrainerState(activeLocation);
  const eventEmpty = emptyEventState(activeLocation);
  const location = searchCriteria?.location || {};
  const visibleGroupRecords = groups.filter((group) => recordMatchesSeoArea(group, location, "group"));
  const visiblePlayerRecords = players.filter((player) => recordMatchesSeoArea(player, location, "player"));
  const visibleCoachRecords = trainers.filter((coach) => recordMatchesSeoArea(coach, location, "coach"));
  const accountCards = buildAccountProfileCards(accountProfile, city);
  const accountGroupCards = accountCards.groups
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visibleGroupRecords.some((group) => directoryRecordMatchesAccountCard(card, group)));
  const accountPlayerCards = accountCards.players
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visiblePlayerRecords.some((player) => directoryRecordMatchesAccountCard(card, player)));
  const accountCoachCards = accountCards.coaches
    .filter((card) => profileCardMatchesLocation(card, city, searchCriteria))
    .filter((card) => !visibleCoachRecords.some((coach) => directoryRecordMatchesAccountCard(card, coach)));
  const visiblePlayers = visiblePlayerRecords.map((player) => publicCardFromDirectoryProfile(player, "player"));
  const visibleCoaches = visibleCoachRecords.map((coach) => publicCardFromDirectoryProfile(coach, "coach"));
  const visibleEvents = events.filter((event) => eventMatchesLocation(event, city, searchCriteria));
  const hasSocialSchedule = accountGroupCards.length || visibleGroupRecords.length;
  const hasPeople = accountPlayerCards.length || visiblePlayers.length || accountCoachCards.length || visibleCoaches.length;
  const hasEvents = visibleEvents.length;
  return (
    <section className="page city-page">
      <SplitHero
        eyebrow="City directory"
        title={`${city} badminton guide`}
        text="Compare local centres, social games, players, coaches, events and court options in one place."
        image={venues[0]?.image || null}
      />
      <div className="city-grid">
        <section>
          <h2>Centres in {city}</h2>
          {venues.map((venue) => <HorizontalVenueCard key={venue.id} venue={venue} saved={false} toggleSaved={() => {}} navigate={navigate} />)}
        </section>
        <aside className="city-side">
          <h2>Social schedule</h2>
          {hasSocialSchedule ? (
            <>
              {accountGroupCards.map((card) => (
                <a
                  href={routeHref(card.target, card.payload || {})}
                  key={card.id}
                  onClick={(event) => handleSeoRouteClick(event, navigate, card.target, card.payload || {})}
                >
                  <span>{card.meta || "Published Badminta group"}</span>
                  <strong>{card.title}</strong>
                  <em>{card.subtitle || "Open group profile"}</em>
                </a>
              ))}
              {visibleGroupRecords.map((group) => (
                (() => {
                  const publicPayload = publicProfileNavigationPayload("group", group);
                  const target = publicPayload ? "publicGroupProfile" : "group";
                  const payload = publicPayload || { group };
                  return (
                    <a
                      href={routeHref(target, payload)}
                      key={group.id}
                      onClick={(event) => handleSeoRouteClick(event, navigate, target, payload)}
                    >
                      <span>{group.when}</span>
                      <strong>{group.title}</strong>
                      <em>{group.capacity ? `${group.spots || 0}/${group.capacity} booked` : "Open group profile"}{getGroupPriceLabel(group) ? ` - ${getGroupPriceLabel(group)}` : ""}</em>
                    </a>
                  );
                })()
              ))}
            </>
          ) : (
            <CommunityEmptyState {...groupEmpty} navigate={navigate} compact />
          )}
        </aside>
      </div>
      <div className="city-discovery-sections">
        <CityDiscoverySection
          eyebrow={`${accountPlayerCards.length + visiblePlayers.length + accountCoachCards.length + visibleCoaches.length} public profile${accountPlayerCards.length + visiblePlayers.length + accountCoachCards.length + visibleCoaches.length === 1 ? "" : "s"}`}
          title={`Players and coaches in ${activeLocation}`}
          actionLabel="View players"
          actionTarget="playersIndex"
          navigate={navigate}
        >
          {hasPeople ? (
            <div className="city-profile-grid">
              {[...accountPlayerCards, ...visiblePlayers, ...accountCoachCards, ...visibleCoaches].slice(0, 8).map((card) => (
                <ProfileMiniCard card={card} navigate={navigate} key={card.id} />
              ))}
            </div>
          ) : (
            <div className="city-empty-grid">
              <CommunityEmptyState {...playerEmpty} navigate={navigate} compact />
              <CommunityEmptyState {...coachEmpty} navigate={navigate} compact />
            </div>
          )}
        </CityDiscoverySection>
        <CityDiscoverySection
          eyebrow={`${visibleEvents.length} public event${visibleEvents.length === 1 ? "" : "s"}`}
          title={`Events, tournaments and clinics in ${activeLocation}`}
          actionLabel="View events"
          actionTarget="events"
          navigate={navigate}
        >
          {hasEvents ? (
            <div className="event-grid city-event-grid">
              {visibleEvents.slice(0, 4).map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} key={event.id} compact />)}
            </div>
          ) : (
            <CommunityEmptyState {...eventEmpty} navigate={navigate} compact />
          )}
        </CityDiscoverySection>
      </div>
    </section>
  );
}

function CentreRouteLoading() {
  return (
    <section className="page centre-route-page" aria-live="polite" aria-busy="true">
      <div className="centre-route-loading">
        <Loader2 className="spin" size={26} />
        <div>
          <strong>Checking this centre listing</strong>
          <span>Confirming the venue type, location and available court details.</span>
        </div>
      </div>
    </section>
  );
}

function CentreListingUnavailable({ venue = {}, issue = {}, navigate }) {
  const isCommunityRecord = issue?.kind === "community-organisation";
  const isLookupError = issue?.kind === "lookup-error";
  const externalUrl = getVenueExternalUrl(venue);
  const locationLabel = venue.address || [venue.suburb, venue.city, venue.state, venue.postcode].filter(Boolean).join(", ");
  const browseLocation = locationFromVenue(venue);
  const safeDescription = sanitisePublicVenueDescription(venue);
  const title = isCommunityRecord
    ? `${venue.name} is not a confirmed court-hire centre`
    : isLookupError
      ? "This centre listing could not be loaded"
      : "This centre listing could not be confirmed";
  const description = isCommunityRecord
    ? "The available public information describes a badminton association, club program or community organisation. It does not confirm that this record operates courts that players can hire."
    : isLookupError
      ? "Badminta could not finish checking this record. No booking or centre-management action has been enabled from incomplete information."
      : "This older link does not match a current public court-hire centre. It may have moved, changed name or referred to an organisation instead of a physical venue.";

  return (
    <section className="page centre-route-page">
      <button type="button" className="back-link" onClick={() => navigate("results", { location: browseLocation, city: venue.city })}>
        <ChevronLeft size={17} /> Browse court-hire centres
      </button>
      <div className="centre-unavailable-hero">
        <div className="centre-unavailable-copy">
          <span className="centre-record-label"><CircleHelp size={17} /> {isCommunityRecord ? "Community directory record" : "Listing status"}</span>
          <h1>{title}</h1>
          <p>{description}</p>
          {isCommunityRecord && safeDescription && <blockquote>{safeDescription}</blockquote>}
        </div>
        <aside className="centre-record-status" aria-label="Centre listing checks">
          <strong>What is confirmed</strong>
          <span><Building2 size={18} /> {isCommunityRecord ? "Badminton organisation or program" : "No current centre match"}</span>
          <span><CalendarDays size={18} /> No published court slots</span>
          <span><ShieldCheck size={18} /> No approved centre owner</span>
        </aside>
      </div>

      {(isCommunityRecord && (locationLabel || venue.phone || externalUrl)) && (
        <section className="centre-record-facts" aria-label="Public organisation details">
          <div>
            <span>Public record</span>
            <strong>{venue.name}</strong>
          </div>
          {locationLabel && (
            <div>
              <span>Listed location</span>
              <strong>{locationLabel}</strong>
            </div>
          )}
          {venue.phone && (
            <div>
              <span>Public phone</span>
              <a href={`tel:${venue.phone}`}>{venue.phone}</a>
            </div>
          )}
          {externalUrl && (
            <div>
              <span>Organisation website</span>
              <a href={externalUrl} target="_blank" rel="noreferrer">Open website <ExternalLink size={14} /></a>
            </div>
          )}
        </section>
      )}

      <section className="centre-route-actions">
        <div>
          <span>Continue on Badminta</span>
          <h2>Choose the right next step</h2>
          <p>Browse facilities for court hire, publish organised play as a social group, or add the actual physical venue if it is missing.</p>
        </div>
        <div className="centre-route-action-buttons">
          {isLookupError ? (
            <button type="button" className="primary" onClick={() => window.location.reload()}><Loader2 size={17} /> Try again</button>
          ) : (
            <button type="button" className="primary" onClick={() => navigate("results", { location: browseLocation, city: venue.city })}><Search size={17} /> Find court-hire centres</button>
          )}
          <button type="button" className="secondary" onClick={() => navigate("createGroup")}><UsersRound size={17} /> Create a social group</button>
          <button type="button" className="secondary" onClick={() => navigate("centreClaimSearch")}><MapPinned size={17} /> Add or claim a venue</button>
        </div>
      </section>
    </section>
  );
}

function VenueDetail({ venue, allVenues, groups = [], navigate, saved, toggleSaved, saveAccountItem, accountProfile = defaultAccountProfile, following = [], toggleFollowing = null }) {
  const nearbyCentres = getNearbyCentres(venue, allVenues);
  const detailItems = getVenueDetailItems(venue);
  const hoursRows = getVenueHoursRows(venue);
  const bookingLinks = filterAllowedVenueLinks(venue.bookingLinks);
  const socialLinks = filterAllowedVenueLinks(venue.socialLinks);
  const realSocialListings = [
    ...getBadmintaVenueSocialListings(venue, accountProfile, groups),
    ...getVenueRealSocialListings(venue)
  ];
  const externalUrl = getVenueExternalUrl(venue);
  const platformBookingState = getPlatformCentreBookingState(venue, accountProfile);
  const ownerPreview = platformBookingState.owned;
  const socialTarget = socialTargetFromVenue(venue);
  const toggleCentreSave = () => {
    toggleSaved(venue.id);
    if (!saved) saveAccountItem?.(savedItemFromVenue(venue));
  };
  return (
    <section className="page detail-page">
      <button type="button" className="back-link" onClick={() => navigate("results")}>
        <ChevronLeft size={17} /> Back to results
      </button>
      <div className="detail-title">
        <div>
          <h1>{venue.name}</h1>
          <p><MapPin size={15} /> {getVenueSourceLabel(venue)} - {getVenueAddressLabel(venue)} <RatingStatus venue={venue} /></p>
        </div>
        <div className="detail-title-actions">
          <PublicFollowControl
            target={socialTarget}
            following={following}
            toggleFollowing={toggleFollowing}
            ownerPreview={ownerPreview}
            compact
          />
          <button type="button" className={ownerPreview ? "save-detail active" : saved ? "save-detail active" : "save-detail"} onClick={ownerPreview ? () => navigate("centreDashboard") : toggleCentreSave}>
            {ownerPreview ? <SlidersHorizontal size={18} /> : <Heart size={18} fill={saved ? "currentColor" : "none"} />} {ownerPreview ? "Manage centre" : "Save"}
          </button>
        </div>
      </div>
      <PhotoGallery venue={venue} images={venue.gallery} />
      <VenueActionBand
        venue={venue}
        navigate={navigate}
        saved={saved}
        onSave={toggleCentreSave}
        socialCount={realSocialListings.length}
        externalUrl={externalUrl}
        platformBookingState={platformBookingState}
        ownerPreview={ownerPreview}
      />
      <div className="detail-layout">
        <section className="detail-main">
          <InfoStrip items={[
            [<CourtStatus venue={venue} />, "Confirmed court count if listed"],
            [<HoursStatus venue={venue} />, "Confirmed opening hours if listed"],
            [<PriceStatus venue={venue} />, "Pricing is not guessed"]
          ]} />
          <DetailSection title="Overview">
            <p>{getVenueOverview(venue)}</p>
          </DetailSection>
          <DetailSection title="Public centre details">
            <div className="detail-fact-grid">
              {detailItems.length ? detailItems.map((item) => (
                <article key={`${item.label}-${item.value}`}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                  {item.source && <em>{item.source}</em>}
                </article>
              )) : (
                <article>
                  <span>Details</span>
                  <strong>Centre owner has not supplied extended details yet.</strong>
                  <em>Public listing</em>
                </article>
              )}
            </div>
          </DetailSection>
          <DetailSection title="Amenities and play notes">
            <div className="amenity-grid">
              {getVenueAmenityLabels(venue).map((item) => <AmenityStatus key={item} label={item} />)}
            </div>
          </DetailSection>
          <DetailSection title="Opening hours">
            <div className="hours-list">
              {hoursRows.length ? hoursRows.map((row) => (
                <div key={`${row.label}-${row.value}`}>
                  <span><Clock3 size={15} /> {row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              )) : (
                <div>
                  <span><Clock3 size={15} /> Hours</span>
                  <strong><StatusHint label="Opening hours not listed yet" icon={Clock3} /></strong>
                </div>
              )}
            </div>
          </DetailSection>
          <DetailSection title="Nearby badminton centres">
            <div className="nearby-centre-list">
              {nearbyCentres.length ? nearbyCentres.map((nearby) => (
                <a
                  href={routeHref("venue", { venue: nearby })}
                  key={nearby.id}
                  onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue: nearby })}
                >
                  <VenueMedia venue={nearby} className="nearby-centre-media" />
                  <span>{nearby.distanceFromSelectedLabel}</span>
                  <strong>{nearby.name}</strong>
                  <em><CourtStatus venue={nearby} /> <span>{nearby.suburb || nearby.city}</span></em>
                </a>
              )) : (
                <div className="detail-empty">
                  <MapPinned size={18} />
                  <span>No nearby centre listings with enough location data yet.</span>
                </div>
              )}
            </div>
          </DetailSection>
          <DetailSection title="Social groups at this centre">
            {realSocialListings.length ? (
              <div className="real-social-list">
                {realSocialListings.map((item) => (
                  <article key={item.id}>
                    <span>{item.meta}</span>
                    <strong>{item.title}</strong>
                    <em>{item.detail}</em>
                    <small>{item.source}</small>
                    {item.target && (
                      <button type="button" onClick={() => navigate(item.target, item.payload || {})}>
                        <UsersRound size={14} /> {item.actionLabel || "Open group"}
                      </button>
                    )}
                    {item.secondaryTarget && (
                      <button type="button" onClick={() => navigate(item.secondaryTarget, { ...(item.secondaryPayload || item.payload || {}), bookingType: "social" })}>
                        <WalletCards size={14} /> {item.secondaryLabel || "Join"}
                      </button>
                    )}
                    {item.href && <a href={item.href} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open public link</a>}
                  </article>
                ))}
              </div>
            ) : (
              <CommunityEmptyState
                type="social groups"
                title="No social groups listed at this centre yet"
                text="No organiser has published a group, session, or registration flow for this centre on Badminta yet. Be the first to create one."
                primary={{ label: "Create group here", target: "createGroup" }}
                secondary={{ label: "Sign up as organiser", target: "signup" }}
                icon={UsersRound}
                navigate={navigate}
                compact
              />
            )}
          </DetailSection>
        </section>
        <aside className="booking-card">
          <strong><PriceStatus venue={venue} /> <span>{getVenueSourceLabel(venue)}</span></strong>
          <p><DistanceStatus venue={venue} /></p>
          <div className="booking-fields">
            <div><span>Hours</span><b><HoursStatus venue={venue} /></b></div>
            <div><span>Courts</span><b><CourtStatus venue={venue} /></b></div>
            <div><span>Contact</span><b><ContactStatus venue={venue} /></b></div>
          </div>
          <div className="contact-actions">
            {venue.phone && <a href={`tel:${venue.phone}`}><Phone size={15} /> Call</a>}
            {venue.email && <a href={`mailto:${venue.email}`}><Mail size={15} /> Email</a>}
            {externalUrl && <a href={externalUrl} target="_blank" rel="noreferrer"><Globe2 size={15} /> Website</a>}
          </div>
          {(bookingLinks.length > 0 || socialLinks.length > 0) && (
            <div className="external-link-list">
              {bookingLinks.slice(0, 3).map((link) => <a key={link.url} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> {link.label || "Booking link"}</a>)}
              {socialLinks.slice(0, 3).map((link) => <a key={link.url} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> {link.label}</a>)}
            </div>
          )}
          <button type="button" className="primary" onClick={ownerPreview ? () => navigate("courtManager") : () => externalUrl ? window.open(externalUrl, "_blank", "noopener,noreferrer") : navigate("centreClaimSearch", { venue })}>{ownerPreview ? "Manage courts" : externalUrl ? "Visit official website" : "Search and claim this centre"}</button>
          <button type="button" className="secondary" onClick={ownerPreview ? () => navigate("claimCentre") : () => navigate("centreClaimSearch", { venue })}>{ownerPreview ? "Edit centre profile" : "Search and claim this listing"}</button>
          <small>{ownerPreview ? "These management actions are shown because this centre belongs to your verified Badminta account." : "Details are shown only where they were found in public listings or supplied by the centre. Bookings stay disabled until a centre owner confirms availability and pricing."}</small>
        </aside>
      </div>
    </section>
  );
}

function VenueActionBand({ venue, navigate, saved, onSave, socialCount = 0, externalUrl, platformBookingState, ownerPreview = false }) {
  const bookingState = platformBookingState || getPlatformCentreBookingState(venue);
  const courtPriceReady = bookingState.ready && (hasBookableCourtPrice(bookingState.venue) || hasCourtPricing(bookingState.venue.courtPricing));
  const courtActionLabel = !bookingState.owned ? "Claim centre to enable bookings" : courtPriceReady ? "Book court" : "Complete booking setup";
  const courtActionText = !bookingState.owned
    ? "In-platform bookings open only after the centre owner claims or creates this centre."
    : courtPriceReady
      ? "Choose date, time, court count and continue to checkout."
      : `Bookings need ${bookingState.readiness.missing.join(", ")} before players can book.`;
  const openCourtAction = () => {
    if (ownerPreview) {
      navigate("courtManager");
      return;
    }
    if (!bookingState.owned || !bookingState.ready) {
      navigate("centreClaimSearch", { venue });
      return;
    }
    navigate("booking", { venue: bookingState.venue, bookingType: "court" });
  };
  return (
    <section className="venue-action-band" aria-label={`${venue.name} actions`}>
      <div className="venue-action-copy">
        <p>{ownerPreview ? "Centre management" : "Centre actions"}</p>
        <h2>{ownerPreview ? `Manage ${venue.name}` : `What would you like to do at ${venue.name}?`}</h2>
        <span>{ownerPreview ? "Update courts, profile details, payments and events from your centre workspace." : bookingState.owned ? "Book courts, join organised play, contact the centre, or manage the listing." : "This is a public directory listing. Badminta bookings require a verified centre owner."}</span>
      </div>
      <div className="venue-action-grid">
        <button
          type="button"
          className={`venue-action-tile primary ${bookingState.owned ? "" : "disabled"}`}
          onClick={openCourtAction}
        >
          <CalendarDays size={21} />
          <strong>{ownerPreview ? "Manage courts" : courtActionLabel}</strong>
          <span>{ownerPreview ? "Set inventory, rates, off-peak windows and booking availability." : courtActionText}</span>
        </button>
        <button type="button" className="venue-action-tile" onClick={() => navigate("groupsIndex")}>
          <UsersRound size={21} />
          <strong>{socialCount ? `View ${socialCount} social group${socialCount === 1 ? "" : "s"}` : "Create or find socials"}</strong>
          <span>{socialCount ? "Open organised sessions connected to this centre." : "Be the first organiser to publish a group at this centre."}</span>
        </button>
        <button type="button" className="venue-action-tile" onClick={ownerPreview ? () => navigate("claimCentre") : () => navigate("centreClaimSearch", { venue })}>
          <ShieldCheck size={21} />
          <strong>{ownerPreview ? "Edit centre profile" : "Claim or manage centre"}</strong>
          <span>{ownerPreview ? "Update address, contact details, hours, photos and booking rules." : "Verify ownership, update courts, add pricing, photos, hours and booking rules."}</span>
        </button>
        <button type="button" className={ownerPreview || saved ? "venue-action-tile saved" : "venue-action-tile"} onClick={ownerPreview ? () => navigate("centrePayments") : onSave}>
          {ownerPreview ? <WalletCards size={21} /> : <Heart size={21} fill={saved ? "currentColor" : "none"} />}
          <strong>{ownerPreview ? "Centre payments" : saved ? "Saved to account" : "Save centre"}</strong>
          <span>{ownerPreview ? "Review booking income, refunds, memberships and payout readiness." : "Keep this centre in your saved list for bookings, groups and future updates."}</span>
        </button>
      </div>
      {(venue.phone || venue.email || externalUrl) && (
        <div className="venue-quick-contact" aria-label="Centre contact actions">
          {venue.phone && <a href={`tel:${venue.phone}`}><Phone size={15} /> Call centre</a>}
          {venue.email && <a href={`mailto:${venue.email}`}><Mail size={15} /> Email centre</a>}
          {externalUrl && <a href={externalUrl} target="_blank" rel="noreferrer"><Globe2 size={15} /> Official website</a>}
        </div>
      )}
    </section>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="detail-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function PhotoGallery({ venue, images = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const galleryImages = getVenueImages(venue, images);
  const visibleImages = galleryImages.slice(0, 5);
  const hiddenCount = Math.max(0, galleryImages.length - visibleImages.length);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
      if (event.key === "ArrowRight") setActiveIndex((current) => (current + 1) % galleryImages.length);
      if (event.key === "ArrowLeft") setActiveIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [galleryImages.length, isOpen]);

  if (!galleryImages.length) {
    const point = getVenueMediaPoint(venue);
    return (
      <div className={point ? "photo-gallery no-photos map-gallery" : "photo-gallery no-photos"}>
        {point ? (
          <VenueMapEmbed
            venue={venue}
            point={point}
            className="detail-map-preview"
          />
        ) : (
          <StockMediaImage
            media={getStockMediaFallback("centre", `${venue?.name || ""}|gallery-fallback`)}
            alt={`${venue?.name || "Badminton centre"} stock image`}
            className="detail-stock-preview"
          />
        )}
      </div>
    );
  }

  const openAt = (index) => {
    setActiveIndex(index);
    setIsOpen(true);
  };

  const showPrevious = () => setActiveIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
  const showNext = () => setActiveIndex((current) => (current + 1) % galleryImages.length);

  return (
    <>
      <div className={`photo-gallery image-count-${Math.min(visibleImages.length, 5)}`}>
        {visibleImages.map((image, index) => (
          <button
            type="button"
            className="gallery-photo"
            key={`${image}-${index}`}
            onClick={() => openAt(index)}
            aria-label={`Open ${venue.name} photo ${index + 1}`}
          >
            <img src={image} alt={`${venue.name} photo ${index + 1}`} loading={index === 0 ? "eager" : "lazy"} decoding="async" />
            {index === visibleImages.length - 1 && hiddenCount > 0 && <span className="gallery-more-overlay">+{hiddenCount}</span>}
          </button>
        ))}
        {galleryImages.length > 1 && (
          <button type="button" className="gallery-show-all" onClick={() => openAt(0)}>
            <ImageIcon size={17} /> Show all {galleryImages.length} photos
          </button>
        )}
      </div>
      {isOpen && (
        <div className="photo-lightbox" role="dialog" aria-modal="true" aria-label={`${venue.name} photos`}>
          <div className="photo-lightbox-panel">
            <div className="photo-lightbox-head">
              <div>
                <strong>{venue.name}</strong>
                <span>{activeIndex + 1} of {galleryImages.length}</span>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Close photos"><X size={20} /></button>
            </div>
            <div className="photo-lightbox-stage">
              {galleryImages.length > 1 && <button type="button" onClick={showPrevious} aria-label="Previous photo"><ChevronLeft size={24} /></button>}
              <img src={galleryImages[activeIndex]} alt={`${venue.name} photo ${activeIndex + 1}`} />
              {galleryImages.length > 1 && <button type="button" onClick={showNext} aria-label="Next photo"><ChevronRight size={24} /></button>}
            </div>
            {galleryImages.length > 1 && (
              <div className="photo-lightbox-strip">
                {galleryImages.map((image, index) => (
                  <button
                    type="button"
                    className={index === activeIndex ? "active" : ""}
                    key={`${image}-lightbox-${index}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show photo ${index + 1}`}
                  >
                    <img src={image} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function InfoStrip({ items }) {
  return (
    <div className="info-strip">
      {items.map(([title, text], index) => (
        <div key={`${text}-${index}`}>
          <strong>{title}</strong>
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
}

function GroupDetail({ group, navigate, accountProfile = defaultAccountProfile, saveAccountProfile, saveAccountItem }) {
  if (!group) {
    return (
      <section className="page detail-page">
        <button type="button" className="back-link" onClick={() => navigate("groupsIndex")}>
          <ChevronLeft size={17} /> Back to groups
        </button>
        <CommunityEmptyState
          {...emptySocialGroupState("this area")}
          title="No social group selected"
          text="There are no public social groups to open yet. Create the first group and invite players from your centre or suburb."
          navigate={navigate}
        />
      </section>
    );
  }
  const detailGroup = {
    ...defaultAccountProfile.group,
    ...group,
    id: group.id || group.groupId || group.firestoreSourceId || "",
    name: group.name || group.title || "Social badminton group",
    coverUrl: group.coverUrl || group.image || "",
    logoUrl: group.logoUrl || "",
    description: group.description || "",
    venue: group.venue || getGroupVenueLabel(group),
    venueId: group.venueId || "",
    schedule: normaliseGroupSchedule(group),
    isRecurring: group.isRecurring !== false,
    skillBand: group.skillBand || group.level || group.band || "",
    format: group.format || group.recurring || "",
    capacity: group.capacity || "",
    price: group.price || "",
    publicMembers: getPublicGroupMembers(group, accountProfile)
  };
  const normalisedGroup = normaliseJoinedGroup(detailGroup);
  const venue = normalisedGroup.venueId
    ? venues.find((item) => item.id === normalisedGroup.venueId)
    : venues.find((item) => normaliseLocationText(item.name).toLowerCase() === normaliseLocationText(normalisedGroup.venue).toLowerCase());
  const venuePoint = venue ? getVenueMediaPoint(venue) : null;
  const groupSessions = getGroupPublicSessions(normalisedGroup);
  const hasUpcomingGroupSession = groupSessions.length > 0;
  const nextJoinableGroup = hasUpcomingGroupSession ? groupForPublicSession(normalisedGroup, groupSessions[0]) : normalisedGroup;
  const priceLabel = normalisedGroup.price ? formatMoney(getGroupPriceNumber(normalisedGroup), normalisedGroup.currency) : "Price pending";
  const groupWhen = getGroupWhenLabel(normalisedGroup);
  const groupLinks = cleanLinkRows(normalisedGroup.socialLinks, "Group link");
  const groupMediaLinks = [
    ...mediaLinksFromImages([normalisedGroup.coverUrl, normalisedGroup.logoUrl, normalisedGroup.image], "Group image"),
    ...cleanLinkRows(normalisedGroup.mediaLinks, "Group media")
  ];
  const publicMembers = getPublicGroupMembers(normalisedGroup, accountProfile);
  const memberCount = getGroupMemberCount(normalisedGroup, accountProfile);
  const membershipActive = viewerHasActiveGroupMembership(accountProfile, normalisedGroup);
  const membershipLabel = groupMembershipPriceLabel(normalisedGroup);
  const openGroupMembership = () => navigate("groupMembership", { group: normalisedGroup });
  const groupReviewTarget = {
    targetCollection: "groups",
    targetId: String(normalisedGroup.firestoreSourceId || normalisedGroup.publicGroupId || normalisedGroup.id || getGroupStableId(normalisedGroup)).trim(),
    subjectCollection: "groups",
    subjectId: String(normalisedGroup.firestoreSourceId || normalisedGroup.publicGroupId || normalisedGroup.id || getGroupStableId(normalisedGroup)).trim()
  };
  const messageGroupOrganiser = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: "organiser",
    targetCollection: groupReviewTarget.targetCollection,
    targetId: groupReviewTarget.targetId,
    subject: getGroupTitle(normalisedGroup),
    threadType: "Group enquiry",
    text: `A player opened an enquiry for ${getGroupTitle(normalisedGroup)}. Confirm venue, level, schedule, price, capacity, guest rules and waitlist status before they join.`,
    meta: [getGroupVenueLabel(normalisedGroup), groupWhen, normalisedGroup.skillBand, priceLabel],
    context: [["Group", getGroupTitle(normalisedGroup)], ["Venue", getGroupVenueLabel(normalisedGroup)], ["When", groupWhen], ["Price", priceLabel]],
    actions: [["Browse social groups", "groupsIndex"], ["Saved items", "saved"], ["Messages", "messages"]]
  });
  return (
    <section className="page public-profile-page public-listing-page">
      <button type="button" className="back-link" onClick={() => navigate("groupsIndex")}>
        <ChevronLeft size={17} /> Back to groups
      </button>
      <PublicListingHero
        variant="group"
        eyebrow="Social group"
        title={getGroupTitle(normalisedGroup)}
        subtitle={[getGroupVenueLabel(normalisedGroup), groupWhen, normalisedGroup.skillBand || "Level pending"].join(" - ")}
        image={getPublicProfileImage(normalisedGroup.coverUrl, normalisedGroup.logoUrl, normalisedGroup.image)}
        avatar={normalisedGroup.logoUrl}
        icon={UsersRound}
        facts={[normalisedGroup.format || "Format pending", memberCount ? `${memberCount} members` : "New group", membershipLabel]}
        qualityLabel="Joined players"
        qualityValue={memberCount ? `${memberCount}` : "New"}
        qualityMeta={memberCount ? "Active group members" : "Be the first member"}
        primaryAction={[membershipActive ? "You’re a member" : "Join group", openGroupMembership]}
        secondaryAction={hasUpcomingGroupSession
          ? ["Join next session", () => navigate("join", { group: nextJoinableGroup, bookingType: "social" })]
          : ["Ask about the next session", messageGroupOrganiser]}
      />
      <PublicMediaShowcase
        title="Session photos and group proof"
        links={groupMediaLinks}
        exclude={[getPublicProfileImage(normalisedGroup.coverUrl, normalisedGroup.logoUrl, normalisedGroup.image), normalisedGroup.logoUrl]}
        emptyTitle="No group media added yet"
        emptyText="Photos and clips will appear after the organiser adds media they have permission to publish."
      />
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [CalendarDays, "When", groupWhen, normalisedGroup.isRecurring === false ? "Regular schedule" : "Weekly recurring"],
            [MapPin, "Venue", getGroupVenueLabel(normalisedGroup), "Where to play"],
            [Gauge, "Level", normalisedGroup.skillBand || "Not added", "Who this session suits"],
            [CircleDollarSign, "Session price", priceLabel, "Per player"],
            [UsersRound, "Membership", membershipLabel, memberCount ? `${memberCount} members` : "Open to new members"]
          ]} />
          <PublicProfileSection eyebrow="Session details" title="What players need to know">
            <p className="public-profile-copy">{normalisedGroup.description || "The organiser has not added a full description yet. A useful group page explains the level, rotation style, what to bring, payment rules and what happens if the session fills."}</p>
            <button type="button" className="public-inline-action" onClick={messageGroupOrganiser}>Message organiser</button>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Sessions and members" title="Upcoming sessions and joined players">
            <PublicSessionBoard sessions={groupSessions} group={normalisedGroup} navigate={navigate} accountProfile={accountProfile} onMessageOrganiser={messageGroupOrganiser} />
          </PublicProfileSection>
          <PublicInteractionGrid
            eyebrow="Group actions"
            title="Join the group or reserve a session"
            text="Membership and session attendance are managed separately so every payment and booking stays clear."
            actions={[
              [UsersRound, membershipActive ? "You’re a member" : "Join group", membershipActive ? "Open your active group membership." : membershipLabel, openGroupMembership, "primary"],
              ...(hasUpcomingGroupSession ? [
                [CalendarDays, "Join next session", "Reserve a place in the next listed game.", () => navigate("join", { group: nextJoinableGroup, bookingType: "social" })],
                [WalletCards, "Pay session fee", "Use Badminta checkout for this published session.", () => navigate("join", { group: nextJoinableGroup, bookingType: "social" })]
              ] : [
                [CalendarDays, "Ask about the next session", "This organiser has not published another playable session yet.", messageGroupOrganiser]
              ]),
              [MessageCircle, "Message organiser", "Ask about level, venue, equipment or availability.", messageGroupOrganiser],
              [BadgeCheck, "Rate group", "Rate organisation and level fit after attending.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("group", "Rate social group", getGroupTitle(normalisedGroup), groupReviewTarget) })],
              [ShieldCheck, "Report session", "Report no-shows, unsafe play, wrong level or payment issues.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report social session", getGroupTitle(normalisedGroup), groupReviewTarget) })]
            ]}
          />
          <PublicProfileSection eyebrow="Venue context" title="Where the group plays">
            {venue && venuePoint ? (
              <div className="public-venue-preview">
                <VenueMapEmbed venue={venue} point={venuePoint} className="public-venue-map" />
                <div>
                  <strong>{venue.name}</strong>
                  <span>{getVenueAddressLabel(venue)}</span>
                  <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}>Open centre listing</a>
                </div>
              </div>
            ) : (
              <div className="public-empty-panel">
                <MapPinned size={20} />
                <strong>{getGroupVenueLabel(normalisedGroup)}</strong>
                <span>{[normalisedGroup.suburb, normalisedGroup.city].filter(Boolean).join(" - ") || "Venue location is pending."}</span>
                <button type="button" onClick={() => navigate("centreClaimSearch")}>Help verify centre</button>
              </div>
            )}
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Public links" title="Group socials and community links">
            <PublicLinkList links={groupLinks} emptyText="No group links added yet." />
          </PublicProfileSection>
        </main>
        <PublicActionPanel
          title="Group membership"
          value={membershipLabel}
          caption={getGroupVenueLabel(normalisedGroup)}
          rows={[
            [UsersRound, "Members", memberCount ? `${memberCount} active` : "New group"],
            [Clock3, "Time", groupWhen],
            [Gauge, "Level", normalisedGroup.skillBand || "Not added"],
            [CircleDollarSign, "Session fee", priceLabel]
          ]}
          primary={[membershipActive ? "Membership active" : "Join group", openGroupMembership]}
          secondary={hasUpcomingGroupSession
            ? ["Join next session", () => navigate("join", { group: nextJoinableGroup, bookingType: "social" })]
            : ["Message organiser", messageGroupOrganiser]}
          note="Group membership does not reserve a place in a dated session. Join each session separately when you want to play."
          links={groupLinks}
        />
      </div>
    </section>
  );
}

function PlayerProfile({ player, navigate, accountProfile = defaultAccountProfile, saveAccountProfile, publicReviews = [], publicProfileSyncStatus = { state: "idle", message: "" } }) {
  if (!player) {
    const loadingPublicProfile = publicProfileSyncStatus.state === "syncing";
    return (
      <section className="page player-profile-page">
        {loadingPublicProfile ? (
          <section className="public-unpublished-card profile-loading-card">
            <div className="public-unpublished-icon"><Loader2 size={28} className="spin" /></div>
            <p>Loading player profile</p>
            <h1>Finding the published Badminta profile.</h1>
            <span>{publicProfileSyncStatus.message || "Loading the public player directory so this profile can open after logout."}</span>
          </section>
        ) : (
          <CommunityEmptyState
            {...emptyPlayerState("this area")}
            title="No player profile selected"
            text={publicProfileSyncStatus.state === "error"
              ? publicProfileSyncStatus.message || "Badminta could not load the public player directory right now. Refresh or search again."
              : "No public player profile is available yet. Published player profiles appear here after the player completes setup and Badminta saves the profile to the public directory."}
            navigate={navigate}
          />
        )}
      </section>
    );
  }
  const firstName = String(player.name || "This player").split(" ")[0] || "This player";
  const playerSocialLinks = cleanLinkRows(player.socialLinks, "Player social");
  const playerMedia = cleanLinkRows([
    ...(Array.isArray(player.mediaLinks) ? player.mediaLinks : []),
    ...(player.mediaUrl ? [{ label: "Profile media", url: player.mediaUrl }] : []),
    ...collectImageUrls([player.media, player.gallery, player.clips]).map((url) => ({ label: "Player media", url }))
  ], "Player media").filter((item) => item.url !== player.image);
  const publicMediaLinks = playerMedia.filter(isApprovedPublicMediaLink);
  const hiddenMediaCount = pendingMediaReviewCount(playerMedia);
  const heroImage = getPublicProfileImage(player.photoUrl, player.image, publicMediaLinks);
  const availabilityRows = getPublicAvailabilityRows(player.availabilitySchedule, player.availability);
  const playerAbout = player.about || player.style || "";
  const playerArea = publicPlayerAreaLabel(player);
  const playerSuburb = publicPlayerSuburbLabel(player);
  const hasPlayerExperience = Boolean(String(playerAbout).trim());
  const directoryPlayerReviewTarget = {
    targetCollection: "players",
    targetId: String(player.firestoreSourceId || player.publicPlayerId || player.id || entitySlug(player)).trim(),
    subjectCollection: "players",
    subjectId: String(player.firestoreSourceId || player.publicPlayerId || player.id || entitySlug(player)).trim()
  };
  const ratingSummary = publicReviewSummary(accountProfile, ["player", "recommendation"], player.name, {
    publicReviews,
    target: directoryPlayerReviewTarget
  });
  const ownerPreview = viewerOwnsPublicProfile(player, accountProfile, "player");
  const messageDirectoryPlayer = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: "player",
    targetCollection: directoryPlayerReviewTarget.targetCollection,
    targetId: directoryPlayerReviewTarget.targetId,
    subject: player.name,
    threadType: "Player enquiry",
    text: `A play invite started from ${player.name}'s directory profile. Confirm level, city, availability, session fit and whether this player wants singles, doubles or social games.`,
    meta: [player.rating, player.band, player.city, player.availability],
    context: [["Player", player.name], ["City", player.city || "Pending"], ["Rating", player.rating || "Pending"], ["Availability", player.availability || "Pending"]],
    actions: [["Find players", "playersIndex"], ["Open ratings", "ratings"], ["Messages", "messages"]]
  });
  const saveDirectoryPlayer = () => {
    saveAccountProfile?.({
      savedItems: [normaliseSavedItem({
        type: "player",
        id: `directory-player-${directoryPlayerReviewTarget.targetId}`,
        title: player.name,
        subtitle: [player.skillBand || player.band, player.suburb || player.city, player.formats].filter(Boolean).join(" - "),
        meta: player.rating ? `${player.rating} rating` : "Player profile",
        image: heroImage,
        targetPage: "player",
        targetPath: buildRoutePath("player", { selectedPlayer: player, city: player.city }),
        payload: { player }
      }), ...(accountProfile.savedItems || [])]
    });
    navigate("saved");
  };
  const directoryPlayerActions = ownerPreview ? [
    [UserCircle, "Edit player profile", "Update your level, location, playing note and public details.", () => navigate("profileSetup"), "primary"],
    [LayoutDashboard, "Player dashboard", "Open your bookings, joined groups, payments and profile status.", () => navigate("playerDashboard")],
    [Clock3, "Update availability", "Keep your weekly playing windows accurate for invitations.", () => navigate("profileSetup")],
    [CalendarDays, "Court bookings", "Review court requests and payment status.", () => navigate("bookings")],
    [MessageCircle, "Player messages", "Open invitations, organiser questions and booking conversations.", () => navigate("messages")]
  ] : [
    [MessageCircle, `Message ${firstName}`, "Ask about availability and level fit.", messageDirectoryPlayer, "primary"],
    [CalendarDays, "Invite to play", "Send a play request with venue and time context.", messageDirectoryPlayer],
    [Heart, "Save player", "Keep this profile in your account.", saveDirectoryPlayer],
    [BadgeCheck, "Recommend", "Leave a recommendation after playing together.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("recommendation", "Recommend player", player.name, directoryPlayerReviewTarget) })],
    [Star, "Rate after play", "Rate level fit after a real session.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("player", "Rate player", player.name, directoryPlayerReviewTarget) })],
    [ShieldCheck, "Report problem", "Report unsafe behaviour or misleading details.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report player profile", player.name, directoryPlayerReviewTarget) })]
  ];
  return (
    <section className="page public-profile-page public-listing-page player-profile-page">
      <PublicListingHero
        variant="player"
        eyebrow="Player public listing"
        title={player.name}
        subtitle={[player.skillBand || player.band, playerArea, player.formats].filter(Boolean).join(" - ")}
        image={heroImage}
        avatar={heroImage}
        icon={UserCircle}
        facts={[player.skillBand || player.band, player.formats, playerArea].filter(Boolean)}
        qualityLabel="Player reviews"
        qualityValue={ratingSummary.label || player.rating || "New"}
        qualityMeta={ratingSummary.meta || (Number(player.plays || 0) ? `${player.plays} verified plays` : "No verified play history yet")}
        primaryAction={ownerPreview ? ["Edit player profile", () => navigate("profileSetup")] : ["Invite to play", messageDirectoryPlayer]}
        secondaryAction={ownerPreview ? ["Player dashboard", () => navigate("playerDashboard")] : ["Recommend player", () => navigate("reviewFlow", { reviewContext: reviewContextFor("recommendation", "Recommend player", player.name, directoryPlayerReviewTarget) })]}
      />
      {playerMedia.length > 0 && (
        <PublicMediaShowcase
          title="Playing photos and clips"
          links={playerMedia}
          exclude={[heroImage]}
          requireModeration
          emptyTitle="Playing media is being reviewed"
          emptyText="Approved playing photos and clips will appear here."
        />
      )}
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [Star, "Level", player.skillBand || player.band || "Not added", "Used by organisers when inviting players"],
            ...(player.formats ? [[Grid3X3, "Likes to play", player.formats, "Singles, doubles or social sessions"]] : []),
            [MapPin, player.location?.suburb || player.suburb ? "Home suburb" : "Local area", playerArea, `Exact public area: ${playerSuburb}`],
            [BadgeCheck, "Activity", Number(player.plays || 0) ? `${player.plays} verified plays` : "New profile", player.verifiedBy || "Published profile"]
          ]} />
          {availabilityRows.length > 0 && (
            <PublicProfileSection eyebrow="Availability" title="When this player is open to play">
              <PublicAvailabilityPanel rows={availabilityRows} />
            </PublicProfileSection>
          )}
          {hasPlayerExperience && (
            <PublicProfileSection title={`Why play with ${firstName}`}>
              <blockquote className="public-player-quote">
                <p>{playerAbout}</p>
              </blockquote>
            </PublicProfileSection>
          )}
          <PublicInteractionGrid
            eyebrow={ownerPreview ? "Player controls" : "Player actions"}
            title={ownerPreview ? "Manage your player presence" : `Connect with ${firstName}`}
            text={ownerPreview ? "These controls are shown because this is your published player profile." : "Use these actions when the player's level, suburb and availability fit your game."}
            actions={directoryPlayerActions}
          />
          {playerSocialLinks.length > 0 && (
            <PublicProfileSection eyebrow="Public links" title="Social and playing links">
              <PublicLinkList links={playerSocialLinks} />
            </PublicProfileSection>
          )}
        </main>
        <PublicActionPanel
          title={ownerPreview ? "Player controls" : "Player contact"}
          value={ownerPreview ? "Profile live" : player.visibility?.invites === false ? "Invites hidden" : "Invite ready"}
          caption={ownerPreview ? "Keep your public details and availability accurate." : [player.skillBand || player.band, playerArea].filter(Boolean).join(" - ") || "Player profile"}
          rows={[
            [ShieldCheck, "Rating", player.rating || "New profile"],
            ...(publicMediaLinks.length || hiddenMediaCount ? [[Video, "Media", publicMediaLinks.length ? `${publicMediaLinks.length} approved` : "Pending review"]] : []),
            ...(availabilityRows.length ? [[Clock3, "Availability", `${availabilityRows.length} window${availabilityRows.length === 1 ? "" : "s"}`]] : []),
            ...(playerSocialLinks.length ? [[Globe2, "Links", `${playerSocialLinks.length} public`]] : [])
          ]}
          primary={ownerPreview ? ["Edit player profile", () => navigate("profileSetup")] : ["Message player", messageDirectoryPlayer]}
          secondary={ownerPreview ? ["Open player dashboard", () => navigate("playerDashboard")] : ["Save player", saveDirectoryPlayer]}
          note={ownerPreview ? "Only you see these management actions." : "Ratings and recommendations should be based on real play."}
          links={playerSocialLinks}
        />
      </div>
    </section>
  );
}

function CoachDirectoryProfile({ coach, navigate, accountProfile = defaultAccountProfile, saveAccountProfile, saveAccountItem, publicReviews = [] }) {
  if (!coach) {
    return (
      <section className="page player-profile-page">
        <CommunityEmptyState
          {...emptyTrainerState("this area")}
          title="No coach profile selected"
          text="No public coach profile is available yet. Coaches will appear here once they publish a profile."
          navigate={navigate}
        />
      </section>
    );
  }
  const directoryCoachProfile = {
    uid: coach.ownerUid || coach.coachUid || coach.createdByUid || "",
    ownerUid: coach.ownerUid || coach.coachUid || coach.createdByUid || "",
    fullName: coach.fullName || coach.name || "Badminta coach",
    photoUrl: coach.photoUrl || coach.image || "",
    coach: {
      ...defaultAccountProfile.coach,
      ...coach,
      photoUrl: coach.photoUrl || coach.image || "",
      headline: coach.headline || "",
      bio: coach.bio || coach.description || coach.style || coach.specialty || "",
      hourlyRate: coach.hourlyRate || coach.rate || "",
      lessonServices: normaliseCoachLessonServices(coach.lessonServices, coach.hourlyRate || coach.rate, coach.currency),
      timeZone: coach.timeZone || "",
      bookingNoticeHours: coach.bookingNoticeHours ?? 12,
      bookingWindowDays: coach.bookingWindowDays ?? 30,
      bufferMinutes: coach.bufferMinutes ?? 0,
      locations: coach.locations || coach.suburb || coach.city || "",
      accreditation: coach.accreditation || "",
      socialLinks: cleanLinkRows(coach.socialLinks, "Coach social"),
      mediaLinks: cleanLinkRows([
        ...(Array.isArray(coach.mediaLinks) ? coach.mediaLinks : []),
        ...(coach.mediaUrl ? [{ label: "Coach media", url: coach.mediaUrl }] : []),
        ...collectImageUrls([coach.media, coach.gallery, coach.clips]).map((url) => ({ label: "Coach media", url }))
      ], "Coach media").filter((item) => item.url !== coach.image)
    }
  };
  return (
    <PublicCoachListing
      accountProfile={directoryCoachProfile}
      viewerProfile={accountProfile}
      navigate={navigate}
      saveAccountItem={saveAccountItem}
      saveAccountProfile={saveAccountProfile}
      publicReviews={publicReviews}
    />
  );
}

function BookingCheckout({ venue, group, type, navigate, accountProfile = defaultAccountProfile, saveAccountProfile, bookingDraft = {} }) {
  const isSocial = type === "social";
  const [courtDate, setCourtDate] = useState(bookingDraft.date || getIsoDate(0));
  const [courtTime, setCourtTime] = useState(bookingDraft.time || "19:30");
  const [durationHours, setDurationHours] = useState(Number(bookingDraft.durationHours) || 2);
  const [courtCount, setCourtCount] = useState(1);
  const [selectedCourtId, setSelectedCourtId] = useState("");
  const [additionalCourtSlots, setAdditionalCourtSlots] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");
  const [selectedMembershipPurchaseId, setSelectedMembershipPurchaseId] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const socialVenue = isSocial
    ? venue
      || getEventVenue({ venueId: group?.venueId })
      || group?.pendingVenue
      || (group?.venue ? {
        id: group.venueId || `group-venue-${slugifyPath(group.venue)}`,
        name: group.venue,
        city: group.city || "",
        suburb: group.suburb || "",
        location: locationSnapshot(group.location),
        source: "group_venue_pin"
      } : null)
    : null;
  if (isSocial && !group) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("groupsIndex")}>
          <ChevronLeft size={17} /> Back to groups
        </button>
        <CommunityEmptyState
          {...emptySocialGroupState("this area")}
          title="No social group selected"
          text="There is no public social session to join yet. Create the first group, then players can join and pay once it is published."
          navigate={navigate}
        />
      </section>
    );
  }
  if (isSocial && !socialVenue) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("group")}>
          <ChevronLeft size={17} /> Back to group
        </button>
        <CommunityEmptyState
          type="social groups"
          title="Venue location needs confirmation before joining"
          text="This social group needs a centre name and suburb or city before players can join. If the centre is not in the directory yet, the organiser can add a quick pending centre from group setup."
          primary={{ label: "Edit group venue", target: "createGroup" }}
          secondary={{ label: "Find groups", target: "groupsIndex" }}
          icon={MapPinned}
          navigate={navigate}
        />
      </section>
    );
  }
  if (!isSocial && !venue) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("results")}>
          <ChevronLeft size={17} /> Back to centres
        </button>
        <CommunityEmptyState
          type="centres"
          title="No centre selected for booking"
          text="Choose a real centre listing first, or add a missing centre so court prices, booking rules and payment details can be confirmed before players request a court."
          primary={{ label: "Find centres", target: "results" }}
          secondary={{ label: "Add a centre", target: "claimCentre" }}
          icon={Building2}
          navigate={navigate}
        />
      </section>
    );
  }
  const nextSocialSession = isSocial ? getGroupPublicSessions(group)[0] || null : null;
  if (isSocial && !nextSocialSession) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("group", { group })}>
          <ChevronLeft size={17} /> Back to group
        </button>
        <CommunityEmptyState
          type="social groups"
          title="This group has no upcoming session"
          text="The saved session has finished and the organiser has not published another playable day and time. No place or payment can be taken until a future session is available."
          primary={{ label: "Find upcoming groups", target: "groupsIndex" }}
          secondary={{ label: "Open group profile", target: "group" }}
          icon={CalendarDays}
          navigate={(target) => target === "group" ? navigate("group", { group }) : navigate(target)}
        />
      </section>
    );
  }
  const platformBookingState = !isSocial ? getPlatformCentreBookingState(venue, accountProfile) : null;
  const bookingVenue = !isSocial ? platformBookingState.venue : venue;
  if (!isSocial && !platformBookingState.owned) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("venue", { venue })}>
          <ChevronLeft size={17} /> Back to centre
        </button>
        <CommunityEmptyState
          type="court bookings"
          title="Booking is not available for this centre yet"
          text="This centre is currently a public directory listing. Badminta can only take court bookings after the centre owner creates or claims the centre and adds booking rules, courts and payment details."
          primary={{ label: "Claim this centre", target: "centreClaimSearch" }}
          secondary={{ label: "Find other centres", target: "results" }}
          icon={ShieldCheck}
          navigate={(target) => target === "centreClaimSearch" ? navigate("centreClaimSearch", { venue }) : navigate(target)}
        />
      </section>
    );
  }
  const bookingReadiness = !isSocial ? platformBookingState.readiness : null;
  if (!isSocial && bookingReadiness && !bookingReadiness.ready) {
    return (
      <section className="page checkout-page">
        <button type="button" className="back-link" onClick={() => navigate("publicCentreProfile")}>
          <ChevronLeft size={17} /> Back to centre profile
        </button>
        <CommunityEmptyState
          type="court bookings"
          title="Court booking is not live yet"
          text={`This centre still needs ${bookingReadiness.missing.join(", ")} before players can book and pay through Badminta.`}
          primary={{ label: "Manage courts", target: "courtManager" }}
          secondary={{ label: "Edit centre profile", target: "claimCentre" }}
          icon={CalendarDays}
          navigate={navigate}
        />
      </section>
    );
  }
  const groupTitle = isSocial ? getGroupTitle(group) : "";
  const groupVenue = isSocial ? getGroupVenueLabel(group) : "";
  const groupWhen = isSocial ? getGroupWhenLabel(group) : "";
  const groupImage = isSocial ? getGroupImage(group) : "";
  const rawSocialPrice = isSocial ? String(group.price || "").trim() : "";
  const hasSocialPrice = Boolean(rawSocialPrice);
  const socialPrice = isSocial ? getGroupPriceNumber(group) : 0;
  const socialOfflineSettlement = isSocial
    && String(group.paymentCollectionMode || group.settlementMode || group.checkoutMode || "").toLowerCase() === "offline_settlement";
  const checkoutCurrency = normaliseCurrencyCode(isSocial
    ? group.currency || marketplaceCurrencyForLocation(group.location)
    : bookingVenue.currency || marketplaceCurrencyForCountry(bookingVenue.countryCode || bookingVenue.country));
  const publishedCourtOptions = !isSocial && Array.isArray(bookingVenue.courtInventory)
    ? bookingVenue.courtInventory.filter((court) => court.status === "Published")
    : [];
  const selectedCourt = !isSocial && Math.max(1, Number(courtCount) || 1) === 1
    ? publishedCourtOptions.find((court) => court.id === selectedCourtId) || publishedCourtOptions[0] || null
    : null;
  const safeDurationHours = Math.max(1, Number(durationHours) || 2);
  const selectedCourtRateRow = isSocial ? null : resolveCourtRateForLocalSelection({
    courtPricing: bookingVenue.courtPricing,
    fallbackPrice: selectedCourt?.price || bookingVenue.pricing || bookingVenue.priceLabel || "",
    date: courtDate,
    time: courtTime,
    durationHours: safeDurationHours
  });
  const selectedCourtRate = selectedCourtRateRow ? {
    ...selectedCourtRateRow,
    formattedFee: formatCourtFeeValue(selectedCourtRateRow.fee, checkoutCurrency),
    price: getCourtFeeNumber(selectedCourtRateRow.fee)
  } : null;
  const selectedCourtRateIsFree = Boolean(selectedCourtRate && /free|included/i.test(selectedCourtRate.formattedFee));
  const courtHourlyPrice = isSocial ? 0 : selectedCourtRate ? selectedCourtRate.price : getVenuePriceNumber(bookingVenue);
  const hasKnownCourtPrice = !isSocial && (courtHourlyPrice > 0 || selectedCourtRateIsFree);
  const safeCourtCount = Math.max(1, Number(courtCount) || 1);
  const courtReservationSegments = !isSocial ? [
    {
      id: "primary",
      date: courtDate,
      time: courtTime,
      durationHours: safeDurationHours,
      courtCount: safeCourtCount,
      courtId: safeCourtCount === 1 ? selectedCourt?.id || "any-court" : "any-court",
      courtName: safeCourtCount === 1 ? selectedCourt?.name || "" : ""
    },
    ...additionalCourtSlots.map((slot) => {
      const slotCourtCount = Math.max(1, Number(slot.courtCount) || 1);
      const slotCourt = slotCourtCount === 1
        ? publishedCourtOptions.find((court) => court.id === slot.courtId) || null
        : null;
      return {
        ...slot,
        durationHours: Math.max(1, Number(slot.durationHours) || 1),
        courtCount: slotCourtCount,
        courtId: slotCourtCount === 1 ? slotCourt?.id || "any-court" : "any-court",
        courtName: slotCourt?.name || ""
      };
    })
  ] : [];
  const pricedCourtReservationSegments = courtReservationSegments.map((segment) => {
    const rate = resolveCourtRateForLocalSelection({
      courtPricing: bookingVenue.courtPricing,
      fallbackPrice: publishedCourtOptions.find((court) => court.id === segment.courtId)?.price || bookingVenue.pricing || bookingVenue.priceLabel || "",
      date: segment.date,
      time: segment.time,
      durationHours: segment.durationHours
    });
    const price = rate ? getCourtFeeNumber(rate.fee) : 0;
    const free = Boolean(rate && /free|included/i.test(formatCourtFeeValue(rate.fee, checkoutCurrency)));
    return {
      ...segment,
      rate,
      price,
      free,
      subtotal: free ? 0 : price * segment.durationHours * segment.courtCount
    };
  });
  const subtotal = isSocial
    ? socialPrice
    : pricedCourtReservationSegments.reduce((total, segment) => total + segment.subtotal, 0);
  const canConfirmPayment = isSocial
    ? hasSocialPrice
    : pricedCourtReservationSegments.length > 0 && pricedCourtReservationSegments.every((segment) => segment.price > 0 || segment.free);
  const bookingCentreBackendId = !isSocial
    ? bookingVenue.claimedVenueId || bookingVenue.serverCentreId || bookingVenue.firestoreSourceId || bookingVenue.id || ""
    : "";
  const requiredMembershipHours = Math.round(pricedCourtReservationSegments.reduce(
    (total, segment) => total + segment.durationHours * segment.courtCount,
    0
  ) * 100) / 100;
  const requiredMembershipVisits = pricedCourtReservationSegments.reduce(
    (total, segment) => total + Math.max(1, Math.ceil((segment.durationHours * 60) / 120)) * segment.courtCount,
    0
  );
  const eligibleMembershipPurchases = !isSocial ? activeMembershipPurchasesForCentre(accountProfile, bookingCentreBackendId)
    .filter((purchase) => {
      const productType = normaliseMembershipProductType(purchase.productType);
      if (productType === "coaching_bundle") return false;
      if (productType === "off_peak_pass" && selectedCourtRate?.key !== "offPeak") return false;
      return Number(purchase.remainingCreditHours || 0) >= requiredMembershipHours
        || Number(purchase.remainingVisitCredits || 0) >= requiredMembershipVisits;
    }) : [];
  const selectedMembershipPurchase = selectedPaymentMethod === "membership"
    ? eligibleMembershipPurchases.find((purchase) => purchase.id === selectedMembershipPurchaseId) || null
    : null;
  const usingMembershipCredits = Boolean(selectedMembershipPurchase);
  const socialBackendCheckoutReady = isSocial && Boolean(
    firebaseFunctions
      && firebaseAuth?.currentUser
      && (group.firestoreSourceId || group.serverGroupId || group.groupId || group.id || getGroupStableId(group))
      && (group.sessionId || group.nextSessionId || nextSocialSession?.sessionId)
  );
  const courtBackendCheckoutReady = !isSocial && Boolean(firebaseFunctions && firebaseAuth?.currentUser && platformBookingState?.owned && bookingReadiness?.ready);
  const updateAdditionalCourtSlot = (slotId, patch) => {
    setAdditionalCourtSlots((current) => current.map((slot) => slot.id === slotId ? { ...slot, ...patch } : slot));
  };
  const addCourtReservationTime = () => {
    if (additionalCourtSlots.length >= 7) {
      setCheckoutMessage("A single reservation can contain up to eight court times.");
      return;
    }
    const previous = additionalCourtSlots[additionalCourtSlots.length - 1] || {
      date: courtDate,
      time: courtTime,
      durationHours: safeDurationHours,
      courtCount: safeCourtCount
    };
    const previousStart = new Date(`${previous.date}T${previous.time || "00:00"}:00`);
    const nextStart = new Date(previousStart.getTime() + Math.max(1, Number(previous.durationHours) || 1) * 60 * 60 * 1000);
    setAdditionalCourtSlots((current) => [...current, {
      id: `court-slot-${Date.now()}-${current.length}`,
      date: nextStart.toISOString().slice(0, 10),
      time: nextStart.toTimeString().slice(0, 5),
      durationHours: 1,
      courtCount: 1,
      courtId: ""
    }]);
    setCheckoutMessage("");
  };
  const overlappingReservationSegments = pricedCourtReservationSegments.some((segment, index, rows) => {
    const start = new Date(`${segment.date}T${segment.time || "00:00"}:00`).getTime();
    const end = start + segment.durationHours * 60 * 60 * 1000;
    return rows.slice(0, index).some((other) => {
      const otherStart = new Date(`${other.date}T${other.time || "00:00"}:00`).getTime();
      const otherEnd = otherStart + other.durationHours * 60 * 60 * 1000;
      return start < otherEnd && end > otherStart;
    });
  });
  const confirmSocialJoin = async () => {
    if (!isSocial) return;
    setCheckoutMessage("");
    const backendGroupId = group.firestoreSourceId || group.serverGroupId || group.groupId || group.id || getGroupStableId(group);
    const backendSessionId = group.sessionId || group.nextSessionId || "";
    if (!hasSocialPrice) {
      setCheckoutMessage("The organiser must publish a session price or mark the session as free before players can join.");
      return;
    }
    if (!firebaseFunctions || !firebaseAuth?.currentUser) {
      setCheckoutMessage("Log in to reserve your place. Badminta will keep your attendance and payment status with your player account.");
      return;
    }
    if (!backendGroupId) {
      setCheckoutMessage("This social session is not connected to a published Badminta group record yet. Ask the organiser to publish it again.");
      return;
    }
    if (!backendSessionId) {
      setCheckoutMessage("Choose a published dated session before joining. A group's regular weekly schedule is not a payable session.");
      return;
    }
    setCheckoutBusy(true);
    try {
      const joinRequest = await callMarketplaceFunction("createSessionJoinRequest", {
        kind: "social",
        groupId: backendGroupId,
        sessionId: backendSessionId,
        ticketCount: 1,
        participantName: accountProfile.fullName || "",
        participantEmail: accountProfile.email || "",
        idempotencyKey: `social-${backendGroupId}-${backendSessionId || "group"}-${firebaseAuth.currentUser.uid}`
      });
      let checkout = null;
      if (joinRequest.checkoutRequired === true) {
        checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
          kind: "social",
          sourceId: joinRequest.joinRequestId,
          idempotencyKey: joinRequest.joinRequestId,
          successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href
        });
      }
      const joinedSnapshot = normaliseJoinedGroup({
        ...group,
        id: getGroupStableId(group),
        joinedAt: new Date().toISOString(),
        joinedStatus: joinRequest.status === "confirmed" ? "Registered" : "Pending payment",
        paymentStatus: joinRequest.settlementMode === "offline_settlement"
          ? "Pay organiser directly"
          : socialPrice > 0 ? "Badminta checkout opened" : "Free registration",
        ticketCount: 1,
        joinRequestId: joinRequest.joinRequestId,
        checkoutSessionId: checkout?.checkoutSessionId || ""
      });
      const joinedGroups = getJoinedGroups(accountProfile);
      const nextJoinedGroups = joinedGroups.some((joinedGroup) => joinedGroup.id === joinedSnapshot.id)
        ? joinedGroups.map((joinedGroup) => joinedGroup.id === joinedSnapshot.id ? joinedSnapshot : joinedGroup)
        : [...joinedGroups, joinedSnapshot];
      const nextPatch = { joinedGroups: nextJoinedGroups };
      if (socialPrice > 0) {
      const paidAmount = formatCurrencyCents(joinRequest.totalCents, joinRequest.currency || checkoutCurrency);
      nextPatch.payments = [
        {
          id: checkout?.paymentId || joinRequest.joinRequestId,
          kind: "social",
          title: "Social session payment",
          amount: paidAmount,
          amountCents: joinRequest.totalCents || amountToMinorUnits(subtotal, checkoutCurrency),
          currency: joinRequest.currency || checkoutCurrency,
          subject: groupTitle,
          status: joinRequest.settlementMode === "offline_settlement" ? "Pay organiser directly" : "Badminta checkout opened",
          provider: joinRequest.settlementMode === "offline_settlement" ? "offline_settlement" : "stripe_checkout_session",
          checkoutReference: checkout?.checkoutSessionId || "",
          createdAt: new Date().toISOString()
        },
        ...getAccountPayments(accountProfile)
      ];
      }
      saveAccountProfile?.(nextPatch);
      if (checkout?.checkoutUrl) {
        window.location.assign(checkout.checkoutUrl);
        return;
      }
      navigate("messages", { messageFocus: { roleId: "player", threadId: `joined-group-${joinedSnapshot.id}` } });
    } catch (error) {
      setCheckoutMessage(marketplaceFunctionErrorMessage(error));
    } finally {
      setCheckoutBusy(false);
    }
  };
  const confirmCourtBooking = async () => {
    if (!canConfirmPayment) {
      navigate("centreClaimSearch", { venue });
      return;
    }
    setCheckoutMessage("");
    if (selectedPaymentMethod === "membership" && !selectedMembershipPurchase) {
      setCheckoutMessage("Choose an active centre pass with enough credit for this court and time.");
      return;
    }
    if (overlappingReservationSegments) {
      setCheckoutMessage("Court times in one reservation cannot overlap. Adjust or remove the overlapping time.");
      return;
    }

    const booking = normaliseCourtBooking({
      id: makeCourtBookingId(),
      venueId: bookingVenue.id,
      venueName: bookingVenue.name,
      city: bookingVenue.city,
      suburb: bookingVenue.suburb,
      address: getVenueAddressLabel(bookingVenue),
      date: courtDate,
      time: courtTime,
      durationHours: safeDurationHours,
      courtCount: safeCourtCount,
      reservationSegments: pricedCourtReservationSegments.map((segment) => ({
        date: segment.date,
        time: segment.time,
        durationHours: segment.durationHours,
        courtCount: segment.courtCount,
        courtId: segment.courtId,
        courtName: segment.courtName,
        rateType: segment.rate?.label || "Standard",
        subtotal: segment.subtotal
      })),
      reservationSegmentCount: pricedCourtReservationSegments.length,
      courtId: selectedCourt?.id || "",
      courtName: selectedCourt?.name || "",
      courtType: selectedCourt?.type || "",
      centreId: bookingVenue.claimedVenueId || bookingVenue.serverCentreId || bookingVenue.firestoreSourceId || bookingVenue.id || "",
      centreOwnerUid: bookingVenue.ownerUid || bookingVenue.centreOwnerUid || firebaseAuth?.currentUser?.uid || "",
      ownerRole: "centre",
      sourceType: "court_booking",
      rateType: selectedCourtRate?.label || "Standard",
      rateWindow: selectedCourtRate?.window || "",
      hourlyPrice: courtHourlyPrice,
      pricingSchedule: getCourtPricingSummary(bookingVenue.courtPricing, bookingVenue.pricing || bookingVenue.priceLabel || "", checkoutCurrency),
      subtotal,
      fee: 0,
      total: subtotal,
      currency: checkoutCurrency,
      status: "Requested",
      bookingStatus: "requested",
      paymentStatus: usingMembershipCredits ? "Centre credit pending" : courtBackendCheckoutReady && subtotal > 0 ? "Backend checkout pending" : "Payment pending",
      payoutStatus: "Not ready",
      refundStatus: "None",
      lockStatus: courtBackendCheckoutReady && subtotal > 0 ? "hold pending" : "local request",
      bookingRulesVersion: bookingVenue.bookingRulesVersion || "",
      bookedAt: new Date().toISOString()
    });

    if (courtBackendCheckoutReady && canConfirmPayment) {
      setCheckoutBusy(true);
      try {
        const reservationFingerprint = pricedCourtReservationSegments
          .map((segment) => `${segment.date}-${segment.time}-${segment.durationHours}-${segment.courtCount}-${segment.courtId || "any"}`)
          .join("|");
        const idempotencyKey = stripeReferenceId("booking", `${bookingVenue.id}-${reservationFingerprint}-${usingMembershipCredits ? selectedMembershipPurchase.id : "card"}`);
        const hold = await callMarketplaceFunction(
          pricedCourtReservationSegments.length > 1 ? "createCourtBookingBasketRequest" : "createCourtBookingRequest",
          pricedCourtReservationSegments.length > 1 ? {
            centreId: bookingCentreBackendId,
            segments: pricedCourtReservationSegments.map((segment) => ({
              courtId: segment.courtId || "any-court",
              startLocal: `${segment.date}T${segment.time || "00:00"}`,
              durationMinutes: Math.round(segment.durationHours * 60),
              courtCount: segment.courtCount
            })),
            currency: checkoutCurrency,
            ...(usingMembershipCredits ? { membershipPurchaseId: selectedMembershipPurchase.id } : {}),
            idempotencyKey
          } : {
            centreId: bookingCentreBackendId,
            courtId: safeCourtCount === 1 ? selectedCourt?.id || "any-court" : "any-court",
            startLocal: `${courtDate}T${courtTime || "00:00"}`,
            durationMinutes: Math.round(safeDurationHours * 60),
            courtCount: safeCourtCount,
            currency: checkoutCurrency,
            ...(usingMembershipCredits ? { membershipPurchaseId: selectedMembershipPurchase.id } : {}),
            idempotencyKey
          }
        );
        if (hold.status === "confirmed") {
          const paidWithCredits = hold.paymentStatus === "paid_with_credits";
          const offlineSettlement = hold.settlementMode === "offline_settlement";
          const noPaymentRequired = hold.paymentStatus === "not_required";
          const backendBooking = normaliseCourtBooking({
            ...booking,
            id: hold.bookingId || booking.id,
            lockId: hold.lockId || "",
            status: "Confirmed",
            bookingStatus: "confirmed",
            lockStatus: "confirmed",
            paymentStatus: paidWithCredits
              ? "Paid with centre credits"
              : offlineSettlement
                ? "Pay at venue"
                : noPaymentRequired
                  ? "No payment required"
                  : coachLessonStatusLabel(hold.paymentStatus),
            paymentProvider: paidWithCredits
              ? "membership_credit"
              : offlineSettlement
                ? "offline_settlement"
                : "not_required",
            settlementMode: hold.settlementMode || "not_required",
            checkoutRequired: false,
            membershipPurchaseId: paidWithCredits ? hold.membershipPurchaseId || selectedMembershipPurchase?.id || "" : "",
            membershipRedemptionId: hold.membershipRedemptionId || "",
            membershipTitle: paidWithCredits ? selectedMembershipPurchase?.title || "" : "",
            membershipBalanceAfter: paidWithCredits ? {
              remainingCreditHours: hold.remainingCreditHours,
              remainingVisitCredits: hold.remainingVisitCredits
            } : undefined,
            payoutStatus: paidWithCredits
              ? "Covered by prepaid centre credit"
              : offlineSettlement
                ? "Collected directly by centre"
                : "No payout required",
            rateType: hold.rateLabel || booking.rateType,
            rateWindow: hold.rateWindow || booking.rateWindow,
            hourlyPrice: minorUnitsToAmount(hold.hourlyPriceCents || 0, hold.currency || checkoutCurrency) || booking.hourlyPrice,
            listSubtotal: minorUnitsToAmount(hold.listAmountCents || amountToMinorUnits(subtotal, checkoutCurrency), hold.currency || checkoutCurrency),
            subtotal: paidWithCredits || noPaymentRequired ? 0 : minorUnitsToAmount(hold.amountCents || 0, hold.currency || checkoutCurrency),
            fee: 0,
            total: paidWithCredits || noPaymentRequired ? 0 : minorUnitsToAmount(hold.totalCents || 0, hold.currency || checkoutCurrency),
            totalCents: paidWithCredits || noPaymentRequired ? 0 : hold.totalCents || 0,
            currency: hold.currency || checkoutCurrency
          });
          await saveAccountProfile?.({ bookings: [backendBooking, ...getAccountBookings(accountProfile)] });
          setCheckoutMessage(offlineSettlement
            ? "Court confirmed. Pay the centre directly at the venue; Badminta has not collected this amount."
            : paidWithCredits
              ? "Court confirmed using your centre credits."
              : "Court confirmed. No payment is required.");
          navigate("bookings");
          return;
        }
        if (hold.checkoutRequired !== true) {
          throw new Error("The booking was saved without a valid checkout or confirmation state. Refresh the centre and try again.");
        }
        const checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
          kind: "booking",
          bookingId: hold.bookingId,
          idempotencyKey: hold.bookingId,
          successUrl: `${window.location.origin}/account/bookings?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.href}${window.location.href.includes("?") ? "&" : "?"}checkout=cancelled&booking_id=${encodeURIComponent(hold.bookingId)}`
        });
        if (checkout.checkoutUrl) window.location.assign(checkout.checkoutUrl);
        return;
      } catch (error) {
        setCheckoutMessage(marketplaceFunctionErrorMessage(error));
        return;
      } finally {
        setCheckoutBusy(false);
      }
    }

    setCheckoutMessage(firebaseAuth?.currentUser
      ? "Court checkout is not ready for this centre. The centre owner must finish payout setup before Badminta can hold the slot."
      : "Log in to reserve this court. Badminta will keep the booking and payment status with your player account.");
  };
  const confirmCheckout = () => {
    if (isSocial) {
      confirmSocialJoin();
      return;
    }
    confirmCourtBooking();
  };
  const bookingPlayerName = accountProfile.fullName || accountProfile.displayName || firebaseAuth?.currentUser?.displayName || "Badminta player";
  const bookingPlayerImage = accountProfile.player?.photoUrl || accountProfile.photoUrl || "";
  return (
    <section className="page checkout-page">
      <button
        type="button"
        className="back-link"
        onClick={() => navigate(
          isSocial ? "group" : "publicCentreProfile",
          isSocial ? { group } : (publicProfileNavigationPayload("centre", bookingVenue) || { venue: bookingVenue })
        )}
      >
        <ChevronLeft size={17} /> {isSocial ? "Back to group" : "Back to centre"}
      </button>
      <div className="checkout-title">
        <div>
          <span>{isSocial ? "Session reservation" : "Court booking"}</span>
          <h1>{isSocial ? "Join this social session" : `Book a court at ${bookingVenue.name}`}</h1>
        </div>
        <p>{isSocial ? "Review the session and reserve your place." : "Choose a date, time and court. Your price updates before you confirm."}</p>
      </div>
      <div className="checkout-layout">
        <section className="checkout-main">
          <CheckoutStep number="1" title="Booking details">
            <div className="booking-option active">
              {isSocial ? (
                groupImage ? <img src={groupImage} alt="" /> : <StockMediaImage media={getStockMediaFallback("group", `${groupTitle}|checkout`)} alt={`${groupTitle} stock image`} className="booking-media" />
              ) : <VenueMedia venue={bookingVenue} className="booking-media" />}
              <div>
                <strong>{isSocial ? groupTitle : bookingVenue.name}</strong>
                <span>{isSocial ? `${groupVenue} - ${groupWhen}` : <>{getVenueAddressLabel(bookingVenue)} <HoursStatus venue={bookingVenue} /></>}</span>
              </div>
            </div>
            {!isSocial && (
              <div className="court-booking-controls">
                <label>
                  <span>Date</span>
                  <input type="date" value={courtDate} onInput={(event) => setCourtDate(event.currentTarget.value)} onChange={(event) => setCourtDate(event.target.value)} />
                </label>
                <label>
                  <span>Start time</span>
                  <input type="time" value={courtTime} onInput={(event) => setCourtTime(event.currentTarget.value)} onChange={(event) => setCourtTime(event.target.value)} />
                </label>
                <label>
                  <span>Duration</span>
                  <select value={durationHours} onChange={(event) => setDurationHours(Number(event.target.value))}>
                    <option value={1}>1 hour</option>
                    <option value={1.5}>1.5 hours</option>
                    <option value={2}>2 hours</option>
                    <option value={3}>3 hours</option>
                  </select>
                </label>
                <label>
                  <span>Courts</span>
                  <select value={courtCount} onChange={(event) => setCourtCount(Number(event.target.value))}>
                    <option value={1}>1 court</option>
                    <option value={2}>2 courts</option>
                    <option value={3}>3 courts</option>
                    <option value={4}>4 courts</option>
                  </select>
                </label>
                {publishedCourtOptions.length > 0 && safeCourtCount === 1 && (
                  <label className="court-rate-field">
                    <span>Preferred court</span>
                    <select value={selectedCourt?.id || ""} onChange={(event) => setSelectedCourtId(event.target.value)}>
                      {publishedCourtOptions.map((court) => (
                        <option key={court.id} value={court.id}>{`${court.name} - ${court.type}`}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}
            {!isSocial && safeCourtCount > 1 && (
              <div className="court-allocation-note">
                <BadgeCheck size={17} />
                <span>Badminta will allocate {safeCourtCount} available courts together for this full time block.</span>
              </div>
            )}
            {!isSocial && additionalCourtSlots.map((slot, index) => (
              <div className="court-reservation-extra" key={slot.id}>
                <div className="court-reservation-extra-head">
                  <div>
                    <span>Additional time {index + 1}</span>
                    <strong>{slot.date} at {slot.time}</strong>
                  </div>
                  <button type="button" aria-label={`Remove additional time ${index + 1}`} onClick={() => setAdditionalCourtSlots((current) => current.filter((item) => item.id !== slot.id))}>
                    <X size={17} />
                  </button>
                </div>
                <div className="court-booking-controls">
                  <label>
                    <span>Date</span>
                    <input type="date" value={slot.date} onChange={(event) => updateAdditionalCourtSlot(slot.id, { date: event.target.value })} />
                  </label>
                  <label>
                    <span>Start time</span>
                    <input type="time" value={slot.time} onChange={(event) => updateAdditionalCourtSlot(slot.id, { time: event.target.value })} />
                  </label>
                  <label>
                    <span>Duration</span>
                    <select value={slot.durationHours} onChange={(event) => updateAdditionalCourtSlot(slot.id, { durationHours: Number(event.target.value) })}>
                      <option value={1}>1 hour</option>
                      <option value={1.5}>1.5 hours</option>
                      <option value={2}>2 hours</option>
                      <option value={3}>3 hours</option>
                    </select>
                  </label>
                  <label>
                    <span>Courts</span>
                    <select value={slot.courtCount} onChange={(event) => updateAdditionalCourtSlot(slot.id, { courtCount: Number(event.target.value), courtId: "" })}>
                      <option value={1}>1 court</option>
                      <option value={2}>2 courts</option>
                      <option value={3}>3 courts</option>
                      <option value={4}>4 courts</option>
                    </select>
                  </label>
                  {publishedCourtOptions.length > 0 && Number(slot.courtCount) === 1 && (
                    <label className="court-rate-field">
                      <span>Preferred court</span>
                      <select value={slot.courtId || ""} onChange={(event) => updateAdditionalCourtSlot(slot.id, { courtId: event.target.value })}>
                        <option value="">Any available court</option>
                        {publishedCourtOptions.map((court) => (
                          <option key={court.id} value={court.id}>{`${court.name} - ${court.type}`}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              </div>
            ))}
            {!isSocial && (
              <button type="button" className="add-reservation-time" onClick={addCourtReservationTime}>
                <Plus size={17} /> Add another date or time
              </button>
            )}
            {!isSocial && selectedCourtRate && (
              <div className="court-rate-note">
                <CircleDollarSign size={18} />
                <span>{selectedCourt ? `${selectedCourt.name}: ` : ""}{selectedCourtRate.label} rate: {selectedCourtRate.formattedFee}. Badminta selected this from {bookingVenue.timeZone || "the centre's local time"}.</span>
              </div>
            )}
          </CheckoutStep>
          <CheckoutStep number="2" title={isSocial ? "Your place" : "Booking for"}>
            <div className="checkout-booking-person">
              <PlayerCardMedia src={bookingPlayerImage} name={bookingPlayerName} className="player-inline-avatar" rootMargin="100px" />
              <div>
                <strong>{bookingPlayerName}</strong>
                <span>{isSocial ? "One session place" : "Primary booking contact"}</span>
              </div>
              <BadgeCheck size={20} />
            </div>
          </CheckoutStep>
          <CheckoutStep number="3" title="Payment">
            {!isSocial && eligibleMembershipPurchases.length > 0 ? (
              <div className="booking-payment-choices">
                <label className={selectedPaymentMethod === "card" ? "active" : ""}>
                  <input type="radio" name="court-payment-method" checked={selectedPaymentMethod === "card"} onChange={() => setSelectedPaymentMethod("card")} />
                  <CreditCard size={20} />
                  <span><strong>Book with payment</strong><small>Badminta confirms secure checkout or pay-at-venue availability</small></span>
                </label>
                {eligibleMembershipPurchases.map((purchase) => (
                  <label key={purchase.id} className={selectedPaymentMethod === "membership" && selectedMembershipPurchaseId === purchase.id ? "active" : ""}>
                    <input
                      type="radio"
                      name="court-payment-method"
                      checked={selectedPaymentMethod === "membership" && selectedMembershipPurchaseId === purchase.id}
                      onChange={() => {
                        setSelectedPaymentMethod("membership");
                        setSelectedMembershipPurchaseId(purchase.id);
                      }}
                    />
                    <WalletCards size={20} />
                    <span><strong>{purchase.title}</strong><small>{membershipBalanceLabel(purchase)}</small></span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="payment-method">
                {isSocial && socialOfflineSettlement ? <Banknote size={20} /> : <CreditCard size={20} />}
                <span>{isSocial && socialOfflineSettlement
                  ? "Pay organiser directly"
                  : isSocial && socialBackendCheckoutReady && canConfirmPayment ? "Badminta Checkout" : !isSocial && courtBackendCheckoutReady && canConfirmPayment ? "Payment confirmed on reservation" : "Secure payment setup"}</span>
                <span className="payment-method-security"><ShieldCheck size={15} /> Secure checkout</span>
              </div>
            )}
          </CheckoutStep>
        </section>
        <aside className="payment-summary">
          <h2>Price details</h2>
          {!isSocial && pricedCourtReservationSegments.length > 1 && (
            <div className="reservation-price-lines">
              {pricedCourtReservationSegments.map((segment, index) => (
                <div key={`${segment.date}-${segment.time}-${index}`}>
                  <span>{formatDateLabel(segment.date)} · {formatTimeLabel(segment.time)} · {segment.durationHours}h · {segment.courtCount} court{segment.courtCount === 1 ? "" : "s"}</span>
                  <strong>{segment.hasKnownPrice ? formatMoney(segment.subtotal, checkoutCurrency) : "Price unavailable"}</strong>
                </div>
              ))}
            </div>
          )}
          <div><span>{isSocial
            ? "Session spot"
            : pricedCourtReservationSegments.length > 1
              ? `${pricedCourtReservationSegments.length} court time blocks`
              : `Court hire${selectedCourt ? ` - ${selectedCourt.name}` : ""}${selectedCourtRate ? ` (${selectedCourtRate.label})` : ""} x ${safeDurationHours}h${safeCourtCount > 1 ? ` x ${safeCourtCount} courts` : ""}`}</span><strong>{canConfirmPayment ? formatMoney(subtotal, checkoutCurrency) : isSocial ? <PendingAmount /> : <PriceStatus venue={bookingVenue} compact />}</strong></div>
          <div><span>Badminta service fee</span><strong>{usingMembershipCredits ? "Already paid with pass" : isSocial && socialOfflineSettlement ? "No online collection fee" : canConfirmPayment ? "Calculated at checkout" : <PendingAmount />}</strong></div>
          <div><span>Checkout</span><strong>{usingMembershipCredits ? "Centre credit" : isSocial && socialOfflineSettlement ? "Reserve now, pay organiser" : canConfirmPayment ? isSocial && socialBackendCheckoutReady ? "Badminta Checkout" : !isSocial && courtBackendCheckoutReady ? "Confirmed when reserved" : "Setup required" : "Pending setup"}</strong></div>
          <div className="total"><span>{usingMembershipCredits ? "Due now" : `Subtotal ${checkoutCurrency.toUpperCase()}`}</span><strong>{usingMembershipCredits ? formatMoney(0, checkoutCurrency) : canConfirmPayment ? formatMoney(subtotal, checkoutCurrency) : <PendingAmount />}</strong></div>
          <button type="button" onClick={confirmCheckout} disabled={checkoutBusy}>{checkoutBusy ? "Confirming booking" : isSocial ? (hasSocialPrice ? (socialBackendCheckoutReady ? socialPrice > 0 ? socialOfflineSettlement ? "Reserve spot" : "Reserve spot and pay" : "Confirm free registration" : firebaseAuth?.currentUser ? "Session setup required" : "Log in to reserve") : "Waiting for session price") : hasKnownCourtPrice ? (courtBackendCheckoutReady ? usingMembershipCredits ? "Use pass and confirm court" : "Reserve court" : firebaseAuth?.currentUser ? "Centre booking setup required" : "Log in to reserve") : "Add real centre pricing"}</button>
          {checkoutMessage && <div className="auth-message">{checkoutMessage}</div>}
          <p>{isSocial ? socialOfflineSettlement && socialPrice > 0
            ? "Badminta confirms the session place and records that payment is due directly to the organiser or venue. Badminta does not collect, refund or hold offline funds."
            : (socialBackendCheckoutReady && hasSocialPrice && socialPrice > 0 ? "Badminta will reserve the session spot, create a server-side Stripe Checkout Session and reconcile payout through the organiser's connected account." : "Badminta records the joined player and payment status here. Organisers do not need to paste their own Stripe links.") : usingMembershipCredits ? "Badminta will validate and deduct the centre credit at the same time the court is confirmed. An owner cancellation restores the applied credit automatically." : hasKnownCourtPrice ? (courtBackendCheckoutReady ? "Badminta checks the centre country on the server. It opens secure checkout where marketplace payouts are supported, or confirms a pay-at-venue booking without an online fee where they are not." : "Live booking stays blocked until the verified centre completes its court and subscription setup.") : "Badminta will not charge players until centre pricing is confirmed."}</p>
        </aside>
      </div>
    </section>
  );
}

function JoinSocialCheckout({ group, navigate, accountProfile, saveAccountProfile }) {
  if (!group) {
    return (
      <section className="page checkout-page">
        <CommunityEmptyState
          {...emptySocialGroupState("this area")}
          title="No social group available to join"
          text="No organiser has published a social session yet. Create the first group or sign up to be notified when one appears."
          navigate={navigate}
        />
      </section>
    );
  }
  const groupVenue = group.venueId
    ? venues.find((venue) => venue.id === group.venueId)
    : venues.find((venue) => normaliseLocationText(venue.name).toLowerCase() === normaliseLocationText(group.venue).toLowerCase());
  return <BookingCheckout venue={groupVenue || null} group={group} type="social" navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />;
}

function GroupMembershipCheckout({ group, navigate, accountProfile = defaultAccountProfile }) {
  const [membershipState, setMembershipState] = useState({ status: "idle", message: "" });
  if (!group) {
    return (
      <section className="page group-membership-page">
        <CommunityEmptyState
          {...emptySocialGroupState("this area")}
          title="This group is not available"
          text="Return to social groups and choose a published group that is accepting members."
          navigate={navigate}
        />
      </section>
    );
  }
  const normalisedGroup = normaliseJoinedGroup(group);
  const mode = groupMembershipMode(normalisedGroup);
  const membership = getViewerGroupMembership(accountProfile, normalisedGroup);
  const membershipStatus = String(membership?.status || membership?.paymentStatus || "").toLowerCase();
  const membershipActive = membershipState.status === "active" || groupMembershipAccessStatuses.has(membershipStatus);
  const paymentPending = !membershipActive && groupMembershipPendingStatuses.has(membershipStatus);
  const currentUid = String(firebaseAuth?.currentUser?.uid || accountProfile.uid || accountProfile.ownerUid || "").trim();
  const ownerUid = String(normalisedGroup.ownerUid || normalisedGroup.organiserUid || "").trim();
  const ownerView = Boolean((currentUid && ownerUid && currentUid === ownerUid) || groupMatchesAccountPublicProfile(normalisedGroup, accountProfile));
  const priceLabel = groupMembershipPriceLabel(normalisedGroup);
  const memberCount = getGroupMemberCount(normalisedGroup, accountProfile);
  const groupId = groupMembershipId(normalisedGroup);
  const benefits = String(normalisedGroup.membershipBenefits || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const checkoutReturned = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("membership") === "success";

  const joinGroup = async () => {
    if (ownerView) {
      navigate("createGroup");
      return;
    }
    if (!groupId) {
      setMembershipState({ status: "error", message: "This group needs to be republished by its organiser before membership can open." });
      return;
    }
    setMembershipState({ status: "loading", message: mode === "paid" ? "Preparing secure membership checkout..." : "Joining this group..." });
    try {
      const result = await callMarketplaceFunction("requestSocialGroupMembership", {
        groupId,
        participantName: accountProfile.fullName || accountProfile.player?.name || "",
        participantEmail: accountProfile.email || firebaseAuth?.currentUser?.email || ""
      });
      if (result.status === "active" || result.alreadyMember) {
        setMembershipState({
          status: "active",
          message: result.alreadyMember
            ? "You are already a member of this group."
            : "You joined the group. New sessions will still be booked separately."
        });
        return;
      }
      if (result.membershipMode !== "paid") {
        setMembershipState({ status: "active", message: "You joined the group successfully." });
        return;
      }
      if (!result.checkoutReady) {
        setMembershipState({
          status: "warning",
          message: "Your membership request is saved. Payment will open when the organiser finishes payout verification."
        });
        return;
      }
      const returnPath = `${window.location.origin}${window.location.pathname}`;
      const checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
        kind: "membership",
        sourceId: result.membershipProductId,
        membershipPurchaseId: result.membershipPurchaseId,
        idempotencyKey: result.membershipPurchaseId,
        successUrl: `${returnPath}?membership=success`,
        cancelUrl: `${returnPath}?membership=cancelled`
      });
      if (!checkout.checkoutUrl) throw new Error("Secure checkout could not be opened.");
      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setMembershipState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  const actionLabel = ownerView
    ? "Manage membership"
    : membershipActive
      ? "Membership active"
      : membershipState.status === "loading"
        ? mode === "paid" ? "Opening checkout..." : "Joining..."
        : paymentPending
          ? "Continue membership payment"
          : mode === "free"
            ? "Join group for free"
            : "Join and pay";

  return (
    <section className="page group-membership-page">
      <button type="button" className="back-link" onClick={() => navigate("publicGroupProfile", publicProfileNavigationPayload("group", normalisedGroup) || { group: normalisedGroup })}>
        <ChevronLeft size={17} /> Back to group
      </button>
      <div className="group-membership-checkout">
        <main>
          <div className="group-membership-intro">
            <div className="group-membership-mark">
              {normalisedGroup.logoUrl ? <img src={normalisedGroup.logoUrl} alt="" /> : <UsersRound size={26} />}
            </div>
            <div>
              <p>Group membership</p>
              <h1>Join {getGroupTitle(normalisedGroup)}</h1>
              <span>{[getGroupVenueLabel(normalisedGroup), normalisedGroup.suburb || normalisedGroup.city].filter(Boolean).join(" - ")}</span>
            </div>
          </div>

          <article className="group-membership-plan">
            <div className="group-membership-plan-head">
              <div>
                <span>{mode === "free" ? "Open membership" : "Member plan"}</span>
                <strong>{priceLabel}</strong>
              </div>
              <BadgeCheck size={24} />
            </div>
            <div className="group-membership-facts">
              <span><UsersRound size={17} /><strong>{memberCount || "New"}</strong><em>{memberCount === 1 ? "member" : "members"}</em></span>
              <span><CalendarDays size={17} /><strong>{getGroupScheduleLabel(normalisedGroup)}</strong><em>usual schedule</em></span>
              <span><Gauge size={17} /><strong>{normalisedGroup.skillBand || "Open level"}</strong><em>player level</em></span>
            </div>
            <div className="group-membership-benefits">
              <h2>What membership includes</h2>
              {benefits.length ? benefits.map((benefit) => (
                <p key={benefit}><Check size={17} /> <span>{benefit}</span></p>
              )) : (
                <>
                  <p><Check size={17} /> <span>Your membership appears in your Badminta account.</span></p>
                  <p><Check size={17} /> <span>The organiser can recognise you as a group member.</span></p>
                  <p><Check size={17} /> <span>You can still choose which published sessions to attend.</span></p>
                </>
              )}
            </div>
          </article>

          <div className="membership-session-distinction">
            <CalendarClock size={22} />
            <div>
              <strong>Membership does not reserve a session</strong>
              <p>Joining makes you a group member. Each dated game has its own capacity, session fee and join confirmation.</p>
            </div>
          </div>
        </main>

        <aside className="group-membership-summary">
          <p>{ownerView ? "Organiser view" : "Membership summary"}</p>
          <h2>{getGroupTitle(normalisedGroup)}</h2>
          <div><span>Membership</span><strong>{priceLabel}</strong></div>
          <div><span>Session fees</span><strong>Charged separately</strong></div>
          <div><span>Payment</span><strong>{mode === "paid" ? "Stripe Connect" : "No payment"}</strong></div>
          {mode === "paid" && <small>The Badminta service fee is shown before you confirm payment in Stripe Checkout.</small>}
          <button type="button" onClick={joinGroup} disabled={membershipState.status === "loading" || membershipActive || mode === "closed"}>
            {membershipActive ? <BadgeCheck size={18} /> : mode === "paid" ? <WalletCards size={18} /> : <UsersRound size={18} />}
            {actionLabel}
          </button>
          {membershipActive && <button type="button" className="secondary" onClick={() => navigate("playerDashboard")}>View in player dashboard</button>}
          {ownerView && <button type="button" className="secondary" onClick={() => navigate("members")}>Open member management</button>}
          {(membershipState.message || checkoutReturned) && (
            <div className={`auth-message ${membershipState.status === "active" || checkoutReturned ? "success" : membershipState.status}`}>
              {membershipState.message || "Payment received. Your membership will update as soon as Stripe confirms it."}
            </div>
          )}
          <div className="group-membership-trust">
            <ShieldCheck size={17} />
            <span>{mode === "paid" ? "Badminta records payment status and the organiser payout in one place." : "No card or payment details are required for this membership."}</span>
          </div>
        </aside>
      </div>
    </section>
  );
}

function CheckoutStep({ number, title, children }) {
  return (
    <section className="checkout-step">
      <div><span>{number}</span><h2>{title}</h2></div>
      {children}
    </section>
  );
}

const createEventWizardSteps = [
  { id: "event-step-owner", label: "Owner" },
  { id: "event-step-venue", label: "Venue" },
  { id: "event-step-schedule", label: "Schedule" },
  { id: "event-step-tickets", label: "Tickets" },
  { id: "event-step-publish", label: "Publish" }
];

const createGroupWizardSteps = [
  { id: "group-step-venue", label: "Group basics" },
  { id: "group-step-date", label: "Schedule" },
  { id: "group-step-capacity", label: "Capacity" },
  { id: "group-step-publish", label: "Publish settings", afterPublication: true },
  { id: "group-step-skill", label: "Session details", optional: true },
  { id: "group-step-invites", label: "Photos and links", optional: true }
];

const groupSessionWizardSteps = [
  { id: "session-step-schedule", label: "Date and time" },
  { id: "session-step-capacity", label: "Players and price" },
  { id: "session-step-publish", label: "Review and publish" }
];

const playerProfileWizardSteps = [
  { id: "player-step-location", label: "Profile basics" },
  { id: "player-step-publish", label: "Visibility settings", afterPublication: true },
  { id: "player-step-availability", label: "Availability", optional: true, nextLabel: "Continue" },
  { id: "player-step-media", label: "Social and media", optional: true }
];

const coachProfileWizardSteps = [
  { id: "coach-step-identity", label: "Coach profile" },
  { id: "coach-step-credentials", label: "Coaching location" },
  { id: "coach-step-services", label: "Lessons and prices", optional: true },
  { id: "coach-step-availability", label: "Availability", optional: true },
  { id: "coach-step-media", label: "Social and media", optional: true },
  { id: "coach-step-publish", label: "Publish settings", afterPublication: true }
];

const centreProfileWizardSteps = [
  { id: "centre-step-identity", label: "Centre basics" },
  { id: "centre-step-media", label: "Brand and media", optional: true },
  { id: "centre-step-courts", label: "Courts and hours", optional: true },
  { id: "centre-step-contact", label: "Contact details", optional: true },
  { id: "centre-step-publish", label: "Review and publish", afterPublication: true }
];

const firstPublishWizardSteps = (steps) => steps.filter((step) => !step.optional && !step.afterPublication);
const playerFirstPublishWizardSteps = firstPublishWizardSteps(playerProfileWizardSteps);
const coachFirstPublishWizardSteps = firstPublishWizardSteps(coachProfileWizardSteps);
const groupFirstPublishWizardSteps = firstPublishWizardSteps(createGroupWizardSteps);
const centreFirstPublishWizardSteps = firstPublishWizardSteps(centreProfileWizardSteps);

function useWizardScrollSpy(steps, initialStepId = "") {
  const safeInitialStepId = steps.some((step) => step.id === initialStepId) ? initialStepId : steps[0]?.id || "";
  const [activeStepId, setActiveStepId] = useState(safeInitialStepId);
  const stepIds = steps.map((step) => step.id).join("|");

  useEffect(() => {
    setActiveStepId((current) => steps.some((step) => step.id === current) ? current : steps[0]?.id || "");
  }, [stepIds]);

  useEffect(() => {
    const sections = steps
      .map((step) => ({ ...step, element: document.getElementById(step.id) }))
      .filter((step) => step.element);

    if (!sections.length) return undefined;

    const updateActiveStep = () => {
      const triggerLine = window.innerWidth <= 900 ? 118 : 96;
      let currentStep = sections[0].id;
      sections.forEach((step) => {
        if (step.element.getBoundingClientRect().top <= triggerLine) {
          currentStep = step.id;
        }
      });
      setActiveStepId(currentStep);
    };

    updateActiveStep();
    window.addEventListener("scroll", updateActiveStep, { passive: true });
    window.addEventListener("resize", updateActiveStep);
    return () => {
      window.removeEventListener("scroll", updateActiveStep);
      window.removeEventListener("resize", updateActiveStep);
    };
  }, [stepIds]);

  const scrollToStep = (stepId) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return [activeStepId, scrollToStep];
}

function useProfileSetupDraft(roleId, accountProfile, initialForm, steps, options = {}) {
  const draftKey = profileSetupDraftKey(roleId, accountProfile);
  const baseFormSignature = profileFormFingerprint(initialForm);
  const profileComplete = options.profileComplete === true;
  const completedStepId = steps.some((step) => step.id === options.completedStepId)
    ? options.completedStepId
    : steps[0]?.id || "";
  const preferredInitialStepId = steps.some((step) => step.id === options.initialStepId)
    ? options.initialStepId
    : "";
  const draftRef = useRef({ key: "", draft: null });
  const dirtyFieldsRef = useRef(new Set());
  const formRef = useRef(initialForm);
  const hydratedKeyRef = useRef("");
  const savingDisabledRef = useRef(false);

  if (!draftRef.current.key) {
    const draft = loadProfileSetupDraftByKey(draftKey);
    const dirtyFields = getPendingProfileDraftDirtyFields(initialForm, draft);
    draftRef.current = { key: draftKey, draft };
    dirtyFieldsRef.current = new Set(dirtyFields);
    formRef.current = mergeProfileDraftForm(initialForm, draft?.form, dirtyFields);
    savingDisabledRef.current = false;
  }

  const [form, setFormState] = useState(() => formRef.current);
  formRef.current = form;
  const hasPendingDraft = dirtyFieldsRef.current.size > 0 && Boolean(draftRef.current.draft?.form);
  const initialStepId = hasPendingDraft && steps.some((step) => step.id === draftRef.current.draft?.lastStepId)
    ? draftRef.current.draft.lastStepId
    : profileComplete ? completedStepId : preferredInitialStepId || steps[0]?.id || "";
  const [activeStepId, setActiveStepId] = useState(initialStepId);
  const activeStepIdRef = useRef(initialStepId);
  activeStepIdRef.current = activeStepId;
  const restoredStepId = hasPendingDraft ? draftRef.current.draft?.lastStepId || "" : "";
  const draftRestored = hasPendingDraft;
  const activeStepIndex = Math.max(0, steps.findIndex((step) => step.id === activeStepId));

  const setForm = (updater) => {
    setFormState((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      changedProfileFormFields(current, next).forEach((field) => dirtyFieldsRef.current.add(field));
      formRef.current = next;
      return next;
    });
  };

  const scrollToStep = (stepId, options = {}) => {
    if (!steps.some((step) => step.id === stepId)) return;
    activeStepIdRef.current = stepId;
    setActiveStepId(stepId);
    if (options.scroll === false || typeof window === "undefined") return;
    window.setTimeout(() => {
      const section = document.getElementById(stepId);
      section?.scrollIntoView({ behavior: options.behavior || "smooth", block: "start" });
      section?.focus({ preventScroll: true });
    }, 0);
  };

  useEffect(() => {
    const restoringNewIdentity = hydratedKeyRef.current !== draftKey;
    const storedDraft = loadProfileSetupDraftByKey(draftKey);
    const sameIdentity = draftRef.current.key === draftKey;
    const draft = sameIdentity
      ? {
        ...(storedDraft || draftRef.current.draft || {}),
        schema: 2,
        form: formRef.current,
        dirtyFields: [...dirtyFieldsRef.current],
        lastStepId: activeStepId
      }
      : storedDraft;
    const draftForComparison = draft ? {
      ...draft,
      schema: 2,
      dirtyFields: sameIdentity ? [...dirtyFieldsRef.current] : getProfileDraftDirtyFields(initialForm, draft)
    } : null;
    const dirtyFields = getPendingProfileDraftDirtyFields(initialForm, draftForComparison);
    const mergedForm = mergeProfileDraftForm(initialForm, draft?.form, dirtyFields);
    draftRef.current = { key: draftKey, draft };
    dirtyFieldsRef.current = new Set(dirtyFields);
    formRef.current = mergedForm;
    hydratedKeyRef.current = draftKey;
    savingDisabledRef.current = false;
    setFormState(mergedForm);

    const nextStepId = dirtyFields.length > 0 && draft?.lastStepId && steps.some((step) => step.id === draft.lastStepId)
      ? draft.lastStepId
      : profileComplete ? completedStepId : preferredInitialStepId || steps[0]?.id || "";
    const activeStepStillExists = steps.some((step) => step.id === activeStepIdRef.current);
    if (restoringNewIdentity || !activeStepStillExists) {
      activeStepIdRef.current = nextStepId;
      setActiveStepId(nextStepId);
    }
  }, [draftKey, baseFormSignature, completedStepId, preferredInitialStepId, profileComplete]);

  useEffect(() => {
    if (savingDisabledRef.current) return;
    const payload = {
      schema: 2,
      roleId,
      form: toPlainFirestoreValue(form),
      dirtyFields: [...dirtyFieldsRef.current],
      lastStepId: activeStepId,
      updatedAt: new Date().toISOString()
    };
    draftRef.current = { key: draftKey, draft: payload };
    saveProfileSetupDraftByKey(draftKey, payload);
  }, [draftKey, roleId, form, activeStepId]);

  const clearDraft = () => {
    savingDisabledRef.current = true;
    dirtyFieldsRef.current.clear();
    draftRef.current = { key: draftKey, draft: null };
    clearProfileSetupDraftByKey(draftKey);
  };

  const previousStepId = activeStepIndex > 0 ? steps[activeStepIndex - 1]?.id || "" : "";
  const nextStepId = activeStepIndex < steps.length - 1 ? steps[activeStepIndex + 1]?.id || "" : "";
  const draftDirty = dirtyFieldsRef.current.size > 0;

  return {
    form,
    setForm,
    activeStepId,
    activeStepIndex,
    scrollToStep,
    previousStepId,
    nextStepId,
    clearDraft,
    draftRestored,
    restoredStepId,
    draftDirty
  };
}

const profileDraftAuthoritativeFields = new Set([
  "id",
  "status",
  "backendStatus",
  "publicProfilePublished",
  "publicPlayerId",
  "publicCoachId",
  "publicGroupId",
  "publicCentreId",
  "serverGroupId",
  "serverCentreId",
  "firestoreSourceId",
  "publishMissing",
  "bookingMissing",
  "bookingEnabled"
]);

const accountRoleDraftPatch = (profile = {}) => Object.fromEntries(
  Object.entries(profile || {}).filter(([key]) => !profileDraftAuthoritativeFields.has(key))
);

function useCloudProfileDraftAutosave({ enabled, roleId, patch, saveAccountProfile, delayMs = 1800 }) {
  const [status, setStatus] = useState("idle");
  const patchRef = useRef(patch);
  const saveRef = useRef(saveAccountProfile);
  const lastSavedSignatureRef = useRef("");
  const signature = profileFormFingerprint(patch);
  patchRef.current = patch;
  saveRef.current = saveAccountProfile;

  useEffect(() => {
    if (!enabled || !isRealFirebaseUser(firebaseAuth?.currentUser) || typeof saveRef.current !== "function") {
      if (!enabled) setStatus("idle");
      return undefined;
    }
    if (signature === lastSavedSignatureRef.current) {
      setStatus("saved");
      return undefined;
    }

    let cancelled = false;
    setStatus("pending");
    const timer = window.setTimeout(async () => {
      if (cancelled) return;
      setStatus("saving");
      const result = await saveRef.current(patchRef.current);
      if (cancelled) return;
      if (result?.saved) {
        lastSavedSignatureRef.current = signature;
        setStatus("saved");
      } else {
        setStatus("error");
      }
    }, delayMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [delayMs, enabled, roleId, signature]);

  return status;
}

function WizardDraftStatus({ draftRestored, restoredStepId, steps, profileComplete = false, cloudStatus = "idle", idleText = "" }) {
  const restoredLabel = steps.find((step) => step.id === restoredStepId)?.label || "";
  const statusText = cloudStatus === "saving"
    ? "Saving your progress to your Badminta account..."
    : cloudStatus === "pending"
      ? "Your latest change will be saved automatically."
      : cloudStatus === "saved"
        ? "Progress saved to your Badminta account."
        : cloudStatus === "error"
          ? "Progress is saved on this device. Cloud sync will retry with your next change."
          : draftRestored
            ? `Draft restored${restoredLabel ? ` at ${restoredLabel}` : ""}.`
            : profileComplete
              ? "Your public profile is saved. Changes are kept while you edit."
              : idleText || "Your progress is saved on this device while you complete the essentials.";
  return (
    <div className={`wizard-draft-status ${cloudStatus}`}>
      {cloudStatus === "saving" ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
      <span>{statusText}</span>
    </div>
  );
}

function WizardStepRail({ steps, activeStepId, onStepClick, label = "Setup steps", profileComplete = false, completedStepIds = [] }) {
  const railRef = useRef(null);
  const completedSteps = new Set(completedStepIds);

  useEffect(() => {
    if (!activeStepId) return;
    const rail = railRef.current;
    const activeButton = rail?.querySelector(`[data-step-id="${activeStepId}"]`);
    if (!rail || !activeButton) return;

    const railStyle = window.getComputedStyle(rail);
    const isHorizontalRail = railStyle.display === "flex" || rail.scrollWidth > rail.clientWidth;
    if (isHorizontalRail) {
      const nextLeft = activeButton.offsetLeft - ((rail.clientWidth - activeButton.offsetWidth) / 2);
      const targetLeft = Math.max(0, nextLeft);
      rail.scrollTo({ left: targetLeft, behavior: "smooth" });
      const ensureActiveStepVisible = window.setTimeout(() => {
        if (Math.abs(rail.scrollLeft - targetLeft) > 8) rail.scrollLeft = targetLeft;
      }, 240);
      return () => window.clearTimeout(ensureActiveStepVisible);
    }

    const nextTop = activeButton.offsetTop - 12;
    rail.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
  }, [activeStepId]);

  return (
    <section className="wizard-steps" aria-label={label} ref={railRef} data-step-count={steps.length}>
      {steps.map((step, index) => {
        const stepComplete = profileComplete || completedSteps.has(step.id);
        return (
        <button
          type="button"
          key={step.id}
          data-step-id={step.id}
          className={[
            activeStepId === step.id ? "active" : "",
            stepComplete ? "complete" : "",
            step.optional ? "optional" : "required"
          ].filter(Boolean).join(" ")}
          onClick={() => onStepClick(step.id)}
          aria-current={activeStepId === step.id ? "step" : undefined}
          aria-label={`${index + 1}. ${step.label}${step.optional ? " optional" : ""}${activeStepId === step.id ? " current step" : stepComplete ? " completed" : ""}`}
        >
          <span>{stepComplete ? <Check size={14} /> : index + 1}</span>
          <strong>{step.label}{step.optional && <small>Optional</small>}</strong>
        </button>
        );
      })}
    </section>
  );
}

function WizardNavigation({
  steps,
  activeStepId,
  onStepChange,
  readyToSave = true,
  incompleteLabel = "Details still required",
  fastTrackToStepId = "",
  onFinish = null,
  finishLabel = "Save profile",
  finishing = false
}) {
  if (!steps.length) return null;
  const activeIndex = Math.max(0, steps.findIndex((step) => step.id === activeStepId));
  const previousStep = activeIndex > 0 ? steps[activeIndex - 1] : null;
  const sequentialNextStep = activeIndex < steps.length - 1 ? steps[activeIndex + 1] : null;
  const fastTrackIndex = steps.findIndex((step) => step.id === fastTrackToStepId);
  const canFastTrack = readyToSave
    && fastTrackIndex > activeIndex
    && steps.slice(activeIndex + 1, fastTrackIndex).every((step) => step.optional === true);
  const nextStep = canFastTrack ? steps[fastTrackIndex] : sequentialNextStep;
  const nextLabel = steps[activeIndex]?.nextLabel || (canFastTrack
    ? "Continue"
    : steps[activeIndex]?.optional
      ? "Skip"
      : "Continue");

  return (
    <nav className="wizard-navigation" aria-label="Profile setup navigation" data-single-step={steps.length === 1 ? "true" : undefined}>
      <button
        type="button"
        className="secondary"
        onClick={() => previousStep && onStepChange(previousStep.id)}
        disabled={!previousStep}
      >
        <ChevronLeft size={17} />
        Back
      </button>
      <span>
        Step {activeIndex + 1} of {steps.length}
        <strong>{steps[activeIndex]?.label}</strong>
      </span>
      {nextStep ? (
        <button type="button" className="primary" onClick={() => onStepChange(nextStep.id)}>
          {nextLabel}
          <ChevronRight size={17} />
        </button>
      ) : onFinish ? (
        <button
          type="button"
          className="primary wizard-navigation-finish-button"
          onClick={onFinish}
          disabled={finishing}
          aria-label={readyToSave ? finishLabel : "Finish required profile details"}
        >
          {finishing ? "Saving..." : readyToSave ? finishLabel : "Finish details"}
          {!finishing && (readyToSave ? <Check size={17} /> : <ChevronRight size={17} />)}
        </button>
      ) : <span className={`wizard-navigation-finish ${readyToSave ? "" : "needs-work"}`}>{readyToSave ? "Ready to save" : incompleteLabel}</span>}
    </nav>
  );
}

function FieldRequirement({ optional = false }) {
  return <small className={`field-requirement ${optional ? "optional" : "required"}`}>{optional ? "Optional" : "Required"}</small>;
}

function ProfileChoiceField({ label, value, options, onChange, required = false }) {
  return (
    <fieldset className="profile-choice-field">
      <legend>{label} <FieldRequirement optional={!required} /></legend>
      <div className="profile-choice-grid" role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const active = value === option;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={active}
              className={active ? "active" : ""}
              key={option}
              onClick={() => onChange(option)}
            >
              {active && <Check size={15} />}
              <span>{option}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function ProfilePublishReadiness({
  label = "profile",
  missingFields = [],
  fieldStepMap = {},
  publishStepId,
  onStepChange,
  onPublish,
  publishing = false,
  alreadyPublished = false,
  onViewLive
}) {
  const missing = Array.from(new Set(missingFields.filter(Boolean)));
  const ready = missing.length === 0;
  const takeReadyAction = () => {
    if (alreadyPublished && onViewLive) {
      onViewLive();
      return;
    }
    if (!alreadyPublished && onPublish) {
      onPublish();
      return;
    }
    if (publishStepId) onStepChange?.(publishStepId);
  };
  const readyActionAvailable = alreadyPublished ? Boolean(onViewLive || publishStepId) : Boolean(onPublish || publishStepId);
  return (
    <section className={`profile-publish-readiness ${ready ? "ready" : "needs-work"}`} aria-live="polite">
      <div className="profile-publish-readiness-copy">
        <span>{ready ? <Check size={17} /> : <CircleHelp size={17} />}</span>
        <div>
          <strong>{alreadyPublished ? `Your ${label} is live` : ready ? `Your ${label} is ready to publish` : `${missing.length} quick detail${missing.length === 1 ? "" : "s"} before publishing`}</strong>
          <p>{ready ? "Optional photos, availability, social links and extra details can be added now or later." : "Only the items below are required. Everything marked Optional can be skipped for now."}</p>
        </div>
        {ready && alreadyPublished && readyActionAvailable && (
          <button type="button" onClick={takeReadyAction} disabled={publishing}>
            {publishing ? "Publishing..." : alreadyPublished && onViewLive ? "View live profile" : alreadyPublished ? "Review publish settings" : "Publish now"}
            {!publishing && (alreadyPublished && onViewLive ? <ExternalLink size={16} /> : <ChevronRight size={16} />)}
          </button>
        )}
      </div>
      {!ready && (
        <div className="profile-publish-missing-actions">
          {missing.map((field) => {
            const meta = missingFieldMeta(field);
            const stepId = fieldStepMap[field];
            return (
              <button type="button" key={field} onClick={() => stepId && onStepChange(stepId)} disabled={!stepId}>
                <span><CircleHelp size={14} /> {meta.title}</span>
                {stepId && <ChevronRight size={15} />}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ProfileEssentialsAction({
  label = "profile",
  ready = false,
  missingFields = [],
  alreadyPublished = false,
  saving = false,
  onPublish,
  onFixMissing
}) {
  const missing = Array.from(new Set(missingFields.filter(Boolean)));
  return (
    <div className={`profile-essentials-action ${ready ? "ready" : "needs-work"}`} aria-live="polite">
      <span>{ready ? <BadgeCheck size={20} /> : <CircleHelp size={20} />}</span>
      <div>
        <strong>{alreadyPublished ? `Save and open your live ${label}` : ready ? `That is enough to publish your ${label}` : `${missing.length} required detail${missing.length === 1 ? "" : "s"} left`}</strong>
        <p>{ready ? "Optional photos, availability, links and extra details do not block publication. Add them after your live page opens." : `${missingFieldSummary(missing)} must be completed. Everything marked Optional can be added after publishing.`}</p>
      </div>
      {ready ? (
        <button type="button" onClick={onPublish} disabled={saving}>
          {saving ? "Publishing..." : alreadyPublished ? "Save and view live profile" : `Publish ${label}`}
          {!saving && <ExternalLink size={16} />}
        </button>
      ) : (
        <button type="button" className="secondary" onClick={onFixMissing}>
          Finish required details
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}

function FirstPublishPreview({ icon: Icon = BadgeCheck, title = "Ready for your public page", items = [], text = "Optional details can be added after publication." }) {
  return (
    <div className="first-publish-preview">
      <span><Icon size={20} /></span>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
      <div className="first-publish-preview-items">
        {items.map((item) => {
          const row = typeof item === "string" ? { label: item, complete: true } : item;
          return (
            <span className={row.complete === false ? "pending" : "complete"} key={row.label}>
              {row.complete === false ? <CircleHelp size={14} /> : <Check size={14} />}
              {row.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ProfileSetup({ profileComplete, setProfileComplete, navigate, accountProfile, saveAccountProfile }) {
  const player = accountProfile.player || defaultAccountProfile.player;
  const initialPlayerForm = {
    displayName: accountProfile.fullName || accountProfile.displayName || "",
    photoUrl: player.photoUrl || getAccountAvatarUrl(accountProfile),
    skillBand: player.skillBand || "",
    availability: player.availability || "",
    availabilitySchedule: normaliseAvailabilitySchedule(player.availabilitySchedule),
    suburb: player.location ? player.location.suburb || "" : player.suburb || "",
    city: player.location?.city || player.city || "",
    country: player.location?.country || player.country || "",
    countryCode: player.location?.countryCode || player.countryCode || "",
    location: player.location || null,
    formats: player.formats || "",
    about: player.about || player.style || "",
    style: player.about || player.style || "",
    mediaUrl: player.mediaUrl || "",
    mediaLinks: player.mediaLinks?.length ? player.mediaLinks : player.mediaUrl ? [{ label: "Profile media", url: player.mediaUrl }] : [],
    socialLinks: player.socialLinks?.length ? player.socialLinks : [],
    visibility: {
      ...defaultAccountProfile.player.visibility,
      ...(player.visibility || {})
    }
  };
  const savedPlayerProfileComplete = roleHasPublishedBackendProfile(accountProfile, "player");
  const savedPlayerCoreReady = getPublicProfileMissingFields(accountProfile, "player").length === 0;
  const activePlayerWizardSteps = savedPlayerProfileComplete ? playerProfileWizardSteps : playerFirstPublishWizardSteps;
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId,
    draftDirty
  } = useProfileSetupDraft("player", accountProfile, initialPlayerForm, activePlayerWizardSteps, {
    profileComplete: savedPlayerProfileComplete,
    initialStepId: !savedPlayerProfileComplete && savedPlayerCoreReady ? "player-step-location" : ""
  });
  const [saveMessage, setSaveMessage] = useState("");
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [playerLocationStatus, setPlayerLocationStatus] = useState(
    hasPublicPlayerLocation({ suburb: form.suburb, city: form.city, location: form.location }) ? "saved" : "idle"
  );
  const playerAutoLocationAttemptedRef = useRef(false);
  const draftAvailabilitySchedule = normaliseAvailabilitySchedule(form.availabilitySchedule);
  const draftPlayer = {
    ...player,
    ...form,
    about: form.about || form.style || "",
    style: form.about || form.style || "",
    availability: summarizeAvailabilitySchedule(draftAvailabilitySchedule) || form.availability || "",
    availabilitySchedule: draftAvailabilitySchedule,
    mediaLinks: cleanLinkRows(form.mediaLinks, "Player media"),
    socialLinks: cleanLinkRows(form.socialLinks, "Player social")
  };
  const draftPlayerProfile = {
    ...accountProfile,
    fullName: form.displayName || accountProfile.fullName || "",
    roles: Array.from(new Set([...(accountProfile.roles || []), "player"])),
    player: draftPlayer
  };
  const playerCloudDraftStatus = useCloudProfileDraftAutosave({
    enabled: draftDirty,
    roleId: "player",
    patch: {
      fullName: draftPlayerProfile.fullName,
      roles: draftPlayerProfile.roles,
      player: accountRoleDraftPatch(draftPlayer)
    },
    saveAccountProfile
  });
  const playerMissingFields = getPublicProfileMissingFields(draftPlayerProfile, "player");
  const playerDiscoveryMissingFields = getPlayerDiscoveryMissingFields(draftPlayerProfile);
  const playerLocationComplete = hasPublicPlayerLocation(draftPlayer);
  const playerAvailabilityComplete = hasProfileAvailability(draftPlayer);
  const playerMediaComplete = cleanLinkRows(form.socialLinks, "Player social").length > 0 || cleanLinkRows(form.mediaLinks, "Player media").length > 0;
  const playerReadyToSave = playerMissingFields.length === 0;
  const playerCompletedStepIds = [
    playerLocationComplete && "player-step-location",
    playerAvailabilityComplete && "player-step-availability",
    playerMediaComplete && "player-step-media",
    playerReadyToSave && "player-step-publish"
  ].filter(Boolean);
  const playerRequiredTotal = roleDashboardRequiredTotals.player || 1;
  const playerProfileComplete = Math.max(0, Math.min(100, Math.round(((playerRequiredTotal - playerMissingFields.length) / playerRequiredTotal) * 100)));
  const playerFieldStepMap = { "Public display name": "player-step-location" };
  const firstPlayerMissingStep = playerFieldStepMap[playerMissingFields[0]] || "player-step-location";

  const updatePlayerField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const locatePlayerFromDevice = async () => {
    setPlayerLocationStatus("locating");
    try {
      const location = await resolveCurrentProfileLocation();
      const area = locationAreaLabel(location) || location.city || location.label;
      setForm((current) => ({
        ...current,
        suburb: location.suburb || (location.type === "suburb" ? area : ""),
        city: location.city || area,
        country: location.country || "",
        countryCode: location.countryCode || "",
        location
      }));
      setPlayerLocationStatus("saved");
      return location;
    } catch (error) {
      setPlayerLocationStatus(Number(error?.code) === 1 ? "denied" : "error");
      return null;
    }
  };

  useEffect(() => {
    if (playerAutoLocationAttemptedRef.current || hasPublicPlayerLocation({ suburb: form.suburb, city: form.city, location: form.location })) return;
    playerAutoLocationAttemptedRef.current = true;
    locatePlayerFromDevice();
  }, [accountProfile.uid]);

  useEffect(() => {
    setProfileComplete(playerProfileComplete);
  }, [playerProfileComplete, setProfileComplete]);

  const updateVisibility = (field, checked) => {
    setForm((current) => ({ ...current, visibility: { ...current.visibility, [field]: checked } }));
  };

  const savePlayerProfile = async () => {
    setSaveMessage("");
    const nextRoles = new Set(accountProfile.roles || []);
    nextRoles.add("player");
    const cleanedMediaLinks = markUserSubmittedMediaLinks(form.mediaLinks, "Player media");
    const cleanedSocialLinks = cleanLinkRows(form.socialLinks, "Player social");
    const availabilitySchedule = normaliseAvailabilitySchedule(form.availabilitySchedule);
    const availability = summarizeAvailabilitySchedule(availabilitySchedule) || form.availability || "";
    let nextPlayer = {
      ...form,
      suburb: form.location ? form.location.suburb || "" : form.suburb || "",
      city: form.location?.city || form.city || "",
      country: form.location?.country || form.country || "",
      countryCode: normaliseCountryCode(form.location?.countryCode || form.location?.country || form.countryCode || form.country, form.location?.city || form.city),
      location: locationSnapshot(form.location),
      about: form.about || form.style || "",
      style: form.about || form.style || "",
      availability,
      availabilitySchedule,
      mediaLinks: cleanedMediaLinks,
      socialLinks: cleanedSocialLinks,
      mediaUrl: cleanedMediaLinks[0]?.url || form.mediaUrl || ""
    };
    let backendError = null;
    let profileBecamePublic = false;
    let backendAccountProfileSynced = false;
    let backendPublicationConfirmed = false;
    let inviteDelivery = null;
    let inviteDeliveryError = null;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingPlayer(true);
      try {
        const backendPlayer = await callMarketplaceFunction("upsertPlayerProfile", buildPlayerBackendPayload(nextPlayer, { ...accountProfile, fullName: form.displayName || accountProfile.fullName || "", roles: Array.from(nextRoles), player: nextPlayer }));
        profileBecamePublic = backendPlayer.becamePublic === true;
        backendAccountProfileSynced = backendPlayer.accountProfileSynced === true;
        backendPublicationConfirmed = backendPlayer.public === true;
        nextPlayer = {
          ...nextPlayer,
          photoUrl: backendPlayer.photoUrl || nextPlayer.photoUrl,
          publicPlayerId: backendPlayer.playerId || backendPlayer.profileId || nextPlayer.publicPlayerId,
          firestoreSourceId: backendPlayer.playerId || backendPlayer.profileId || nextPlayer.firestoreSourceId,
          backendStatus: backendPlayer.status || nextPlayer.backendStatus,
          publicProfilePublished: backendPlayer.public || false,
          publishMissing: backendPlayer.publishMissing || []
        };
      } catch (error) {
        backendError = error;
        nextPlayer = {
          ...nextPlayer,
          backendStatus: "account_saved_public_publish_pending",
          backendErrorMessage: marketplaceFunctionErrorMessage(error)
        };
      } finally {
        setSavingPlayer(false);
      }
    }
    let savedProfile = {
      ...accountProfile,
      fullName: form.displayName || accountProfile.fullName || "",
      roles: Array.from(nextRoles),
      player: nextPlayer
    };
    const accountSave = await saveAccountProfile({
      fullName: savedProfile.fullName,
      roles: savedProfile.roles,
      player: nextPlayer
    });
    if (accountSave?.playerPublication?.public === true) {
      backendPublicationConfirmed = true;
      backendAccountProfileSynced = true;
      backendError = null;
      nextPlayer = {
        ...nextPlayer,
        publicPlayerId: accountSave.playerPublication.profileId || nextPlayer.publicPlayerId,
        firestoreSourceId: accountSave.playerPublication.profileId || nextPlayer.firestoreSourceId,
        backendStatus: "published",
        publicProfilePublished: true,
        publishMissing: []
      };
      savedProfile = { ...savedProfile, player: nextPlayer };
    }
    if (!accountSave?.saved && !backendAccountProfileSynced) {
      backendError = new Error(accountSave?.error || "Badminta could not save your player profile. Try again.");
    }
    if (playerReadyToSave && !backendPublicationConfirmed && !backendError) {
      backendError = new Error("Your details were saved, but Badminta has not confirmed the public profile yet. Select Publish player profile again.");
    }
    const savedMissing = nextPlayer.publishMissing?.length
      ? nextPlayer.publishMissing
      : getPublicProfileMissingFields(savedProfile, "player");
    if (!savedMissing.length && !backendError) clearDraft();
    setProfileComplete(Math.max(0, Math.min(100, Math.round(((playerRequiredTotal - savedMissing.length) / playerRequiredTotal) * 100))));
    setSaveMessage(publicProfileSaveMessage({
      label: "Player profile",
      backendError,
      missing: savedMissing
    }));
    if (!savedMissing.length && !backendError) {
      completeOnboardingRole("player");
      const directoryPlayer = accountProfileToPublicPlayerDirectoryProfile(savedProfile, nextPlayer.city || nextPlayer.location?.city || "Worldwide");
      if (directoryPlayer) {
        if (profileBecamePublic) {
          trackProfileCreated({
            profileId: nextPlayer.publicPlayerId || nextPlayer.firestoreSourceId || directoryPlayer.id,
            role: "player"
          });
        }
        continueAfterSave(navigate, "player", { player: directoryPlayer }, 250);
      }
    }
  };
  const openLivePlayerProfile = () => {
    const publicPayload = publicProfileNavigationPayload("player", player);
    navigate("publicPlayerProfile", publicPayload || {});
  };

  return (
    <section className="page form-page role-profile-setup-page" data-profile-role="player">
      <section className={`profile-setup-intro ${savedPlayerProfileComplete ? "" : "quick-publish"}`}>
        {savedPlayerProfileComplete && (
          <div className="profile-avatar-frame large">
            {form.photoUrl ? <img src={form.photoUrl} alt="" /> : <UserCircle size={54} />}
          </div>
        )}
        <div>
          <p>{savedPlayerProfileComplete ? "Edit player profile" : "Player profile setup"}</p>
          <h1>{savedPlayerProfileComplete ? "Keep your player profile ready for the next game." : "Publish your player profile in a few minutes."}</h1>
          <span>{savedPlayerProfileComplete ? "Update your photo, about text, level, local area and availability whenever the way you play changes." : "Confirm your public name to publish now. Add your local area, photo and level to appear in relevant map searches and receive better invitations."}</span>
        </div>
      </section>
      <div className="wizard-layout profile-setup-layout">
        <WizardStepRail steps={activePlayerWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Player profile setup steps" profileComplete={savedPlayerProfileComplete} completedStepIds={playerCompletedStepIds} />
        <section className="form-card wizard-form-card" data-wizard-role="player">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={activePlayerWizardSteps} profileComplete={savedPlayerProfileComplete} cloudStatus={playerCloudDraftStatus} />
          <ProfilePublishReadiness
            label="player profile"
            missingFields={playerMissingFields}
            fieldStepMap={playerFieldStepMap}
            publishStepId="player-step-publish"
            onStepChange={scrollToStep}
            onPublish={savePlayerProfile}
            publishing={savingPlayer}
            alreadyPublished={savedPlayerProfileComplete}
            onViewLive={openLivePlayerProfile}
          />
          <section className="wizard-form-section" id="player-step-location" hidden={activeStepId !== "player-step-location"} tabIndex={-1}>
            <h2>Your player profile</h2>
            <div className="progress"><span style={{ width: `${playerProfileComplete}%` }} /></div>
            <p>{playerProfileComplete}% of required details complete</p>
            <div className="player-profile-photo-field">
              <ImageUploadField
                label="Profile photo (recommended)"
                value={form.photoUrl}
                onChange={(value) => updatePlayerField("photoUrl", value)}
                mediaRole="player"
                mediaField="photoUrl"
                emptyLabel="Add a recognisable player photo"
                helper="Profiles with a clear photo are easier to recognise and more likely to receive genuine invitations. Use a photo you own."
              />
            </div>
            <label><span>Public display name <FieldRequirement /></span><input required value={form.displayName} onChange={(event) => updatePlayerField("displayName", event.target.value)} placeholder="Your name" autoComplete="name" /></label>
            <ProfileChoiceField
              label="Playing level"
              value={form.skillBand}
              options={["Beginner", "Intermediate", "Advanced", "Competitive"]}
              onChange={(skillBand) => updatePlayerField("skillBand", skillBand)}
            />
            <SuburbPicker
              label="Your exact suburb or city"
              value={form.suburb}
              selectedLocation={form.location}
              autoLocationStatus={playerLocationStatus}
              onUseCurrentLocation={locatePlayerFromDevice}
              onChange={({ suburb, city, location }) => {
                updatePlayerField("suburb", suburb);
                updatePlayerField("city", city);
                updatePlayerField("country", location?.country || "");
                updatePlayerField("countryCode", location?.countryCode || "");
                updatePlayerField("location", location);
                setPlayerLocationStatus(location ? "saved" : "idle");
              }}
            />
            {form.location && hasPublicPlayerLocation(form) && (
              <div className="player-location-confirmation" role="status">
                <MapPin size={18} />
                <span>
                  <strong>{form.location.suburb || form.location.city}</strong>
                  <small>{[form.location.suburb && form.location.city !== form.location.suburb ? form.location.city : "", form.location.state, form.location.country].filter(Boolean).join(" · ")}</small>
                </span>
              </div>
            )}
            <label>
              <span>About your badminton <FieldRequirement optional /></span>
              <textarea
                value={form.about}
                onChange={(event) => updatePlayerField("about", event.target.value)}
                placeholder="Share your experience, the pace you enjoy, the sessions you prefer, and what other players can expect when they invite you."
              />
            </label>
            <label><span>Preferred format <FieldRequirement optional /></span><select value={form.formats} onChange={(event) => updatePlayerField("formats", event.target.value)}><option value="">Add later</option><option>Rotating doubles</option><option>Singles ladder</option><option>Women friendly social</option><option>Coached session</option><option>Competitive match play</option></select></label>
            {playerDiscoveryMissingFields.length > 0 && (
              <div className="profile-discovery-note" role="status">
                <MapPin size={17} />
                <span>Complete {missingFieldSummary(playerDiscoveryMissingFields)} so nearby players can find and understand your profile.</span>
              </div>
            )}
            <ProfileEssentialsAction
              label="player profile"
              ready={playerReadyToSave}
              missingFields={playerMissingFields}
              alreadyPublished={savedPlayerProfileComplete}
              saving={savingPlayer}
              onPublish={savePlayerProfile}
              onFixMissing={() => scrollToStep(firstPlayerMissingStep)}
            />
          </section>
          <section className="wizard-form-section" id="player-step-availability" hidden={activeStepId !== "player-step-availability"} tabIndex={-1}>
            <h2>Availability <FieldRequirement optional /></h2>
            <WeeklyAvailabilityEditor
              schedule={form.availabilitySchedule}
              legacySummary={form.availability}
              onChange={(availabilitySchedule) => {
                updatePlayerField("availabilitySchedule", availabilitySchedule);
                updatePlayerField("availability", summarizeAvailabilitySchedule(availabilitySchedule));
              }}
            />
          </section>
          <section className="wizard-form-section" id="player-step-media" hidden={activeStepId !== "player-step-media"} tabIndex={-1}>
            <h2>Social and media <FieldRequirement optional /></h2>
            <LinkListEditor
              title="Player social links"
              text="Choose each platform and enter your username. Badminta creates the full profile link for you."
              links={form.socialLinks}
              onChange={(links) => updatePlayerField("socialLinks", links)}
            />
            <LinkListEditor
              title="Player media and embed links"
              text="Paste a video share link or image link. The platform is detected and the public preview appears immediately."
              type="media"
              addLabel="Add media link"
              links={form.mediaLinks}
              onChange={(links) => updatePlayerField("mediaLinks", links)}
            />
          </section>
          <section className="wizard-form-section" id="player-step-publish" hidden={activeStepId !== "player-step-publish"} tabIndex={-1}>
            <h2>Visibility and publish</h2>
            <div className="visibility-toggle-strip" role="group" aria-label="Public player profile visibility">
              {[
                ["rating", "Ratings", "Show ratings on your public profile"],
                ["invites", "Invites", "Allow social group invitations"],
                ["media", "Media", "Show your approved photos and clips"],
                ["history", "History", "Show your attendance history"]
              ].map(([key, item, description]) => (
                <label className="visibility-toggle-item" key={key} title={description}>
                  <span>{item}</span>
                  <input
                    type="checkbox"
                    role="switch"
                    aria-label={`${item}: ${description}`}
                    checked={Boolean(form.visibility[key])}
                    onChange={(event) => updateVisibility(key, event.target.checked)}
                  />
                </label>
              ))}
            </div>
            {saveMessage && (
              <div className={`auth-message profile-save-message ${profileSaveMessageTone(saveMessage)}`}>
                <span>{saveMessage}</span>
                <div>
                  {profileSaveMessageTone(saveMessage) === "success" && <button type="button" onClick={() => navigate("publicPlayerProfile")}>Open live public page</button>}
                  <button type="button" onClick={() => navigate("account")}>Back to account</button>
                </div>
              </div>
            )}
            {playerReadyToSave ? (
              <button type="button" onClick={savePlayerProfile} disabled={savingPlayer}>{savingPlayer ? "Publishing..." : savedPlayerProfileComplete ? "Save and view live profile" : "Publish player profile"}</button>
            ) : (
              <div className="profile-draft-actions">
                <button type="button" onClick={() => scrollToStep(firstPlayerMissingStep)}>Complete required details</button>
                <button type="button" className="secondary" onClick={savePlayerProfile} disabled={savingPlayer}>{savingPlayer ? "Saving..." : "Save private draft"}</button>
              </div>
            )}
          </section>
          <WizardNavigation
            steps={activePlayerWizardSteps}
            activeStepId={activeStepId}
            onStepChange={scrollToStep}
            readyToSave={playerReadyToSave}
            fastTrackToStepId="player-step-publish"
            onFinish={playerReadyToSave ? savePlayerProfile : () => scrollToStep(firstPlayerMissingStep)}
            finishLabel={savedPlayerProfileComplete ? "Save profile" : "Publish profile"}
            finishing={savingPlayer}
          />
        </section>
        <aside className="side-panel">
          <h2>Profile preview</h2>
          <div className="profile-asset-preview">
            {form.photoUrl ? <img src={form.photoUrl} alt="" /> : <UserCircle size={34} />}
            <strong>{form.displayName || accountProfile.fullName || "Your public player profile"}</strong>
            <span>{[form.skillBand, form.location?.suburb || form.suburb, form.location?.city, form.location?.country, form.formats].filter((value, index, values) => value && values.indexOf(value) === index).join(" · ")}</span>
            {form.about && <p className="profile-preview-about">"{form.about}"</p>}
          </div>
          {savedPlayerProfileComplete ? (
            <>
              <PublicLinkList links={form.socialLinks} emptyText="Social links will appear here once added." />
              <h2>Media preview</h2>
              <MediaEmbedGrid
                links={form.mediaLinks}
                emptyTitle="No player media links yet"
                emptyText="YouTube, TikTok, Instagram, Facebook, Vimeo and image links preview here before saving."
                limit={3}
              />
            </>
          ) : (
            <FirstPublishPreview
              icon={UserCircle}
              title="One essential creates your live profile"
              items={["Public display name"]}
              text="Your exact suburb or city, a clear photo, playing level and short badminton introduction improve map discovery and invitations, but can be added later."
            />
          )}
        </aside>
      </div>
    </section>
  );
}

function CoachProfileSetup({ navigate, accountProfile, saveAccountProfile }) {
  const coach = accountProfile.coach || defaultAccountProfile.coach;
  const player = accountProfile.player || defaultAccountProfile.player;
  const initialCoachForm = {
    ...defaultAccountProfile.coach,
    ...coach,
    displayName: accountProfile.fullName || accountProfile.displayName || coach.fullName || coach.name || "",
    photoUrl: coach.photoUrl || player.photoUrl || getAccountAvatarUrl(accountProfile),
    availability: coach.availability || "",
    availabilitySchedule: normaliseAvailabilitySchedule(coach.availabilitySchedule),
    lessonServices: normaliseCoachLessonServices(coach.lessonServices, coach.hourlyRate, coach.currency),
    timeZone: coach.timeZone || currentBrowserTimeZone(),
    bookingNoticeHours: Math.max(0, Number(coach.bookingNoticeHours) || 12),
    bookingWindowDays: Math.max(1, Number(coach.bookingWindowDays) || 30),
    bufferMinutes: Math.max(0, Number(coach.bufferMinutes) || 0),
    suburb: coach.suburb || coach.location?.suburb || (coach.location?.type === "suburb" ? coach.location?.label || "" : ""),
    city: coach.city || coach.location?.city || (coach.location?.type === "city" ? coach.location?.label || "" : ""),
    location: coach.location || null,
    socialLinks: coach.socialLinks?.length ? coach.socialLinks : [],
    mediaLinks: coach.mediaLinks?.length ? coach.mediaLinks : [],
    currency: normaliseCurrencyCode(coach.currency || marketplaceCurrencyForCountry(coach.countryCode || coach.location?.countryCode))
  };
  const savedCoachProfileComplete = roleHasPublishedBackendProfile(accountProfile, "coach");
  const savedCoachCoreReady = getPublicProfileMissingFields(accountProfile, "coach").length === 0;
  const activeCoachWizardSteps = savedCoachProfileComplete ? coachProfileWizardSteps : coachFirstPublishWizardSteps;
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId,
    draftDirty
  } = useProfileSetupDraft("coach", accountProfile, initialCoachForm, activeCoachWizardSteps, {
    profileComplete: savedCoachProfileComplete,
    initialStepId: !savedCoachProfileComplete && savedCoachCoreReady ? "coach-step-credentials" : ""
  });
  const coachFormCurrency = normaliseCurrencyCode(form.currency || marketplaceCurrencyForCountry(form.countryCode || form.location?.countryCode));
  const [saveMessage, setSaveMessage] = useState("");
  const [savingCoach, setSavingCoach] = useState(false);
  const coachDraftAvailability = normaliseAvailabilitySchedule(form.availabilitySchedule);
  const coachDraftServices = normaliseCoachLessonServices(form.lessonServices, form.hourlyRate, coachFormCurrency);
  const coachDraftProfile = {
    ...accountProfile,
    fullName: form.displayName || accountProfile.fullName || "",
    roles: Array.from(new Set([...(accountProfile.roles || []), "coach"])),
    coach: {
      ...coach,
      ...form,
      availability: summarizeAvailabilitySchedule(coachDraftAvailability) || form.availability || "",
      availabilitySchedule: coachDraftAvailability,
      lessonServices: coachDraftServices
    }
  };
  const coachCloudDraftStatus = useCloudProfileDraftAutosave({
    enabled: draftDirty,
    roleId: "coach",
    patch: {
      fullName: coachDraftProfile.fullName,
      roles: coachDraftProfile.roles,
      coach: accountRoleDraftPatch(coachDraftProfile.coach)
    },
    saveAccountProfile
  });
  const coachMissingFields = getPublicProfileMissingFields(coachDraftProfile, "coach");
  const coachMissingSet = new Set(coachMissingFields);
  const coachIdentityComplete = !["Public display name", "Coach headline"].some((field) => coachMissingSet.has(field));
  const coachCredentialsComplete = !coachMissingSet.has("Coaching location");
  const coachAvailabilityComplete = hasProfileAvailability({ ...form, availabilitySchedule: coachDraftAvailability });
  const coachServicesComplete = coachDraftServices.some((service) => service.active && service.priceConfigured);
  const coachMediaComplete = cleanLinkRows(form.socialLinks, "Coach social").length > 0 || cleanLinkRows(form.mediaLinks, "Coach media").length > 0;
  const coachReadyToSave = coachMissingFields.length === 0;
  const coachCompletedStepIds = [
    coachIdentityComplete && "coach-step-identity",
    coachCredentialsComplete && "coach-step-credentials",
    coachServicesComplete && "coach-step-services",
    coachAvailabilityComplete && "coach-step-availability",
    coachMediaComplete && "coach-step-media",
    coachReadyToSave && "coach-step-publish"
  ].filter(Boolean);
  const coachRequiredTotal = roleDashboardRequiredTotals.coach || 4;
  const coachProfileComplete = Math.max(0, Math.min(100, Math.round(((coachRequiredTotal - coachMissingFields.length) / coachRequiredTotal) * 100)));
  const coachFieldStepMap = {
    "Public display name": "coach-step-identity",
    "Coach headline": "coach-step-identity",
    "Coaching location": "coach-step-credentials"
  };
  const firstCoachMissingStep = coachFieldStepMap[coachMissingFields[0]] || "coach-step-identity";

  const updateCoachField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveCoachProfile = async () => {
    setSaveMessage("");
    const nextRoles = new Set(accountProfile.roles || []);
    nextRoles.add("coach");
    const availabilitySchedule = normaliseAvailabilitySchedule(form.availabilitySchedule);
    const availability = summarizeAvailabilitySchedule(availabilitySchedule) || form.availability || "";
    const nextLocation = locationSnapshot(form.location);
    const resolvedSuburb = form.suburb || nextLocation?.suburb || (nextLocation?.type === "suburb" ? nextLocation.label : "");
    const resolvedCity = form.city || nextLocation?.city || (nextLocation?.type === "city" ? nextLocation.label : "");
    const countryCode = normaliseCountryCode(form.countryCode || nextLocation?.countryCode, resolvedCity);
    let nextCoach = {
      ...form,
      name: form.displayName || accountProfile.fullName || "",
      fullName: form.displayName || accountProfile.fullName || "",
      suburb: resolvedSuburb,
      city: resolvedCity,
      country: form.country || nextLocation?.country || "",
      countryCode,
      currency: normaliseCurrencyCode(form.currency || marketplaceCurrencyForCountry(countryCode)),
      location: nextLocation,
      availability,
      availabilitySchedule,
      lessonServices: normaliseCoachLessonServices(form.lessonServices, form.hourlyRate, form.currency),
      timeZone: form.timeZone || currentBrowserTimeZone(),
      bookingNoticeHours: Math.max(0, Math.min(168, Number(form.bookingNoticeHours) || 0)),
      bookingWindowDays: Math.max(1, Math.min(180, Number(form.bookingWindowDays) || 30)),
      bufferMinutes: Math.max(0, Math.min(120, Number(form.bufferMinutes) || 0)),
      paymentLink: "",
      socialLinks: cleanLinkRows(form.socialLinks, "Coach social"),
      mediaLinks: markUserSubmittedMediaLinks(form.mediaLinks, "Coach media")
    };
    let backendError = null;
    let profileBecamePublic = false;
    let backendAccountProfileSynced = false;
    let backendPublicationConfirmed = false;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingCoach(true);
      try {
        const backendCoach = await callMarketplaceFunction("upsertCoachProfile", buildCoachBackendPayload(nextCoach, { ...accountProfile, fullName: nextCoach.fullName, roles: Array.from(nextRoles), coach: nextCoach }));
        profileBecamePublic = backendCoach.becamePublic === true;
        backendAccountProfileSynced = backendCoach.accountProfileSynced === true;
        backendPublicationConfirmed = backendCoach.public === true;
        nextCoach = {
          ...nextCoach,
          photoUrl: backendCoach.photoUrl || nextCoach.photoUrl,
          publicCoachId: backendCoach.coachId || backendCoach.profileId || nextCoach.publicCoachId,
          firestoreSourceId: backendCoach.coachId || backendCoach.profileId || nextCoach.firestoreSourceId,
          hourlyRateCents: backendCoach.hourlyRateCents || nextCoach.hourlyRateCents,
          lessonServices: backendCoach.lessonServices || nextCoach.lessonServices,
          timeZone: backendCoach.timeZone || nextCoach.timeZone,
          backendStatus: backendCoach.status || nextCoach.backendStatus,
          publicProfilePublished: backendCoach.public || false,
          publishMissing: backendCoach.publishMissing || []
        };
      } catch (error) {
        backendError = error;
        nextCoach = {
          ...nextCoach,
          backendStatus: "account_saved_public_publish_pending",
          backendErrorMessage: marketplaceFunctionErrorMessage(error)
        };
      } finally {
        setSavingCoach(false);
      }
    }
    const savedProfile = {
      ...accountProfile,
      fullName: nextCoach.fullName,
      roles: Array.from(nextRoles),
      coach: nextCoach
    };
    const accountSave = await saveAccountProfile({
      fullName: savedProfile.fullName,
      roles: savedProfile.roles,
      coach: nextCoach
    });
    if (!accountSave?.saved && !backendAccountProfileSynced) {
      backendError = new Error(accountSave?.error || "Badminta could not save your coach profile. Try again.");
    }
    if (coachReadyToSave && !backendPublicationConfirmed && !backendError) {
      backendError = new Error("Your details were saved, but Badminta has not confirmed the public profile yet. Select Publish coach profile again.");
    }
    const savedMissing = nextCoach.publishMissing?.length
      ? nextCoach.publishMissing
      : getPublicProfileMissingFields(savedProfile, "coach");
    if (!savedMissing.length && !backendError) clearDraft();
    setSaveMessage(publicProfileSaveMessage({
      label: "Coach profile",
      backendError,
      missing: savedMissing
    }));
    if (!savedMissing.length && !backendError) {
      if (profileBecamePublic) {
        trackProfileCreated({
          profileId: nextCoach.publicCoachId || nextCoach.firestoreSourceId,
          role: "coach"
        });
      }
      completeOnboardingRole("coach");
      const publicPayload = publicProfileNavigationPayload("coach", nextCoach);
      continueAfterSave(navigate, "publicCoachProfile", publicPayload || {}, 250);
    }
  };
  const openLiveCoachProfile = () => {
    const publicPayload = publicProfileNavigationPayload("coach", coach);
    navigate("publicCoachProfile", publicPayload || {});
  };

  return (
    <section className="page form-page role-profile-setup-page" data-profile-role="coach">
      <section className={`profile-setup-intro ${savedCoachProfileComplete ? "" : "quick-publish"}`}>
        {savedCoachProfileComplete && (
          <div className="profile-avatar-frame large">
            {form.photoUrl ? <img src={form.photoUrl} alt="" /> : <Trophy size={54} />}
          </div>
        )}
        <div>
          <p>{savedCoachProfileComplete ? "Edit coach profile" : "Coach profile setup"}</p>
          <h1>{savedCoachProfileComplete ? "Keep your coaching offer current and bookable." : "Publish your coach profile in a few minutes."}</h1>
          <span>{savedCoachProfileComplete ? "Manage lesson types, per-session prices, calendar availability and coaching proof from one setup." : "Your name, coaching headline and location publish the profile. Add lessons, prices and availability to activate player booking requests."}</span>
        </div>
      </section>
      <div className="wizard-layout profile-setup-layout">
        <WizardStepRail steps={activeCoachWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Coach profile setup steps" profileComplete={savedCoachProfileComplete} completedStepIds={coachCompletedStepIds} />
        <section className="form-card wizard-form-card" data-wizard-role="coach">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={activeCoachWizardSteps} profileComplete={savedCoachProfileComplete} cloudStatus={coachCloudDraftStatus} />
          <ProfilePublishReadiness
            label="coach profile"
            missingFields={coachMissingFields}
            fieldStepMap={coachFieldStepMap}
            publishStepId="coach-step-publish"
            onStepChange={scrollToStep}
            onPublish={saveCoachProfile}
            publishing={savingCoach}
            alreadyPublished={savedCoachProfileComplete}
            onViewLive={openLiveCoachProfile}
          />
          {saveMessage && (
            <div className={`auth-message profile-save-message ${profileSaveMessageTone(saveMessage)}`} role="status">
              <span>{saveMessage}</span>
              <div><button type="button" className="secondary" onClick={() => navigate("account")}>Back to account</button></div>
            </div>
          )}
          <section className="wizard-form-section" id="coach-step-identity" hidden={activeStepId !== "coach-step-identity"} tabIndex={-1}>
            <h2>Coach identity</h2>
            <div className="progress"><span style={{ width: `${coachProfileComplete}%` }} /></div>
            <p>{coachProfileComplete}% of required details complete</p>
            <label><span>Public display name <FieldRequirement /></span><input required value={form.displayName} onChange={(event) => updateCoachField("displayName", event.target.value)} placeholder="Your name" autoComplete="name" /></label>
            <label><span>Coach headline <FieldRequirement /></span><input required value={form.headline} onChange={(event) => updateCoachField("headline", event.target.value)} placeholder="Junior doubles coach, weekend clinics, private lessons" /></label>
            <details className="optional-profile-disclosure">
              <summary>Add a coach bio now <span>Optional</span></summary>
              <label><span>Coach bio <FieldRequirement optional /></span><textarea value={form.bio} onChange={(event) => updateCoachField("bio", event.target.value)} placeholder="Tell players what you coach, who you help, and how sessions run." /></label>
            </details>
          </section>
          <section className="wizard-form-section" id="coach-step-credentials" hidden={activeStepId !== "coach-step-credentials"} tabIndex={-1}>
            <h2>Main coaching location</h2>
            <SuburbPicker
              label="Main coaching suburb"
              placeholder="Search suburb where you coach"
              value={form.suburb}
              selectedLocation={form.location}
              required
              onChange={({ suburb, city, location }) => {
                updateCoachField("suburb", suburb);
                updateCoachField("city", city);
                updateCoachField("location", location);
              }}
            />
            <details className="optional-profile-disclosure">
              <summary>Add coaching details now <span>Optional</span></summary>
              <label><span>Accreditation <FieldRequirement optional /></span><input value={form.accreditation} onChange={(event) => updateCoachField("accreditation", event.target.value)} placeholder="National federation Level 1, club coach..." /></label>
              <label><span>Other coaching centres or coverage <FieldRequirement optional /></span><input value={form.locations} onChange={(event) => updateCoachField("locations", event.target.value)} placeholder="Altona, MSAC, Glen Waverley, mobile lessons..." /></label>
              <ManagedCheckoutNote title="Badminta manages lesson payments.">
                Players pay through Badminta after a lesson request is confirmed. Connect payouts from Coach payouts; no payment link is needed.
              </ManagedCheckoutNote>
            </details>
          </section>
          <section className="wizard-form-section" id="coach-step-services" hidden={activeStepId !== "coach-step-services"} tabIndex={-1}>
            <h2>Lessons and prices <FieldRequirement optional /></h2>
            <CoachLessonServicesEditor
              services={form.lessonServices}
              hourlyRate={form.hourlyRate}
              currency={coachFormCurrency}
              onChange={(lessonServices) => {
                updateCoachField("lessonServices", lessonServices);
                const primaryPaidService = lessonServices.find((service) => service.active && coachLessonServicePriceCents(service, coachFormCurrency) > 0);
                updateCoachField("hourlyRate", primaryPaidService?.price || "");
              }}
            />
            <ManagedCheckoutNote title="Players see the full session price before requesting.">
              Free sessions can be confirmed without payment. Paid lessons use Badminta checkout after you accept the requested time, and the server verifies the saved price again.
            </ManagedCheckoutNote>
          </section>
          <section className="wizard-form-section" id="coach-step-availability" hidden={activeStepId !== "coach-step-availability"} tabIndex={-1}>
            <h2>Availability <FieldRequirement optional /></h2>
            <WeeklyAvailabilityEditor
              schedule={form.availabilitySchedule}
              legacySummary={form.availability}
              title="Weekly booking windows"
              description="Turn on the days players can book. Badminta converts each window into session-sized calendar times using the selected lesson duration."
              onChange={(availabilitySchedule) => {
                updateCoachField("availabilitySchedule", availabilitySchedule);
                updateCoachField("availability", summarizeAvailabilitySchedule(availabilitySchedule));
              }}
            />
            <div className="coach-booking-rules">
              <div className="form-section-head compact">
                <div>
                  <h3>Booking rules</h3>
                  <span>Control how far ahead players can request and how much space remains between lessons.</span>
                </div>
              </div>
              <div className="coach-booking-rule-grid">
                <label>
                  <span>Minimum notice</span>
                  <select value={String(form.bookingNoticeHours)} onChange={(event) => updateCoachField("bookingNoticeHours", Number(event.target.value))}>
                    <option value="0">No minimum</option>
                    <option value="2">2 hours</option>
                    <option value="6">6 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24">1 day</option>
                    <option value="48">2 days</option>
                  </select>
                </label>
                <label>
                  <span>Booking window</span>
                  <select value={String(form.bookingWindowDays)} onChange={(event) => updateCoachField("bookingWindowDays", Number(event.target.value))}>
                    <option value="7">7 days ahead</option>
                    <option value="14">14 days ahead</option>
                    <option value="30">30 days ahead</option>
                    <option value="60">60 days ahead</option>
                    <option value="90">90 days ahead</option>
                  </select>
                </label>
                <label>
                  <span>Buffer between lessons</span>
                  <select value={String(form.bufferMinutes)} onChange={(event) => updateCoachField("bufferMinutes", Number(event.target.value))}>
                    <option value="0">No buffer</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </label>
              </div>
              <div className="coach-timezone-note"><Globe2 size={17} /><span>Calendar time zone</span><strong>{form.timeZone || currentBrowserTimeZone()}</strong></div>
            </div>
          </section>
          <section className="wizard-form-section" id="coach-step-media" hidden={activeStepId !== "coach-step-media"} tabIndex={-1}>
            <h2>Social and media <FieldRequirement optional /></h2>
            <ImageUploadField
              label="Coach picture (optional)"
              value={form.photoUrl}
              onChange={(value) => updateCoachField("photoUrl", value)}
              mediaRole="coach"
              mediaField="photoUrl"
              emptyLabel="No coach picture yet"
              helper="Optional. Publish first and add a clear coaching photo later."
            />
            <LinkListEditor
              title="Coach social links"
              text="Choose each platform and enter your coaching username. Full profile links are created automatically."
              links={form.socialLinks}
              onChange={(links) => updateCoachField("socialLinks", links)}
            />
            <LinkListEditor
              title="Coach media links"
              text="Paste a drill, lesson, testimonial or highlight share link to validate and preview it before saving."
              type="media"
              addLabel="Add coach media"
              links={form.mediaLinks}
              onChange={(links) => updateCoachField("mediaLinks", links)}
            />
          </section>
          <section className="wizard-form-section" id="coach-step-publish" hidden={activeStepId !== "coach-step-publish"} tabIndex={-1}>
            <h2>Publish coach profile</h2>
            <div className="event-checkout-note">
              <ShieldCheck size={20} />
              <span>Saving this profile adds the coach role to your account and unlocks the public coach profile once required fields are complete.</span>
            </div>
            <p className="wizard-publish-copy">
              {coachReadyToSave
                ? "Use the final action below to save these details and open the public coach profile."
                : `Complete ${missingFieldSummary(coachMissingFields)} before publishing. Your draft remains saved while you finish.`}
            </p>
          </section>
          <WizardNavigation
            steps={activeCoachWizardSteps}
            activeStepId={activeStepId}
            onStepChange={scrollToStep}
            readyToSave={coachReadyToSave}
            fastTrackToStepId="coach-step-publish"
            onFinish={coachReadyToSave ? saveCoachProfile : () => scrollToStep(firstCoachMissingStep)}
            finishLabel={savedCoachProfileComplete ? "Save profile" : "Publish profile"}
            finishing={savingCoach}
          />
        </section>
        <aside className="side-panel">
          <h2>Coach preview</h2>
          <div className="profile-asset-preview">
            {form.photoUrl ? <img src={form.photoUrl} alt="" /> : <Trophy size={34} />}
            <strong>{form.headline || form.displayName || accountProfile.fullName || "Your coach profile"}</strong>
            <span>{[form.suburb, savedCoachProfileComplete ? form.hourlyRate : "", savedCoachProfileComplete ? form.accreditation : ""].filter(Boolean).join(" · ") || "Your headline and coaching location appear here."}</span>
          </div>
          {savedCoachProfileComplete ? (
            <>
              <div className="availability-summary-box">
                <Clock3 size={17} />
                <span>{summarizeAvailabilityScheduleCompact(form.availabilitySchedule, form.availability) || "No coaching availability selected yet"}</span>
              </div>
              <div className="coach-preview-services">
                {coachDraftServices.filter((service) => service.active).slice(0, 3).map((service) => (
                  <div key={service.id}>
                    <strong>{service.title}</strong>
                    <span>{service.durationMinutes} min · {coachLessonServicePriceLabel(service, coachFormCurrency)}</span>
                  </div>
                ))}
                {!coachDraftServices.length && <span>No lesson prices added yet.</span>}
              </div>
              <PublicLinkList links={form.socialLinks} emptyText="Coach links will appear here." />
              <h2>Coach media preview</h2>
              <MediaEmbedGrid
                links={form.mediaLinks}
                emptyTitle="No coach media links yet"
                emptyText="Training clips, reels and testimonials preview here as embedded media."
                limit={3}
              />
            </>
          ) : (
            <FirstPublishPreview
              icon={Trophy}
              title="Three essentials make this coach discoverable"
              items={[
                { label: "Display name", complete: !coachMissingSet.has("Public display name") },
                { label: "Coach headline", complete: !coachMissingSet.has("Coach headline") },
                { label: "Coaching location", complete: !coachMissingSet.has("Coaching location") }
              ]}
              text="Photo, bio and credentials can wait. Add at least one lesson price and weekly availability when you want players to book."
            />
          )}
        </aside>
      </div>
    </section>
  );
}

function PublicProfileRoutePlaceholder({ type, status, navigate }) {
  const role = publicRoleId(type);
  const loading = !status || status.state === "idle" || status.state === "loading";
  const browseTarget = role === "organiser" ? "groupsIndex" : role === "centre" ? "results" : "playersIndex";
  const roleLabel = role === "organiser" ? "social group" : role;
  return (
    <section className="page public-unpublished-page">
      <div className="public-unpublished-card profile-loading-card">
        <div className="public-unpublished-icon">
          {loading ? <Loader2 size={28} className="spin" /> : <CircleHelp size={28} />}
        </div>
        <p>{loading ? "Loading public profile" : "Profile unavailable"}</p>
        <h1>{loading ? `Finding this published ${roleLabel}.` : `This ${roleLabel} profile is not public.`}</h1>
        <span>{status?.message || (loading ? "Badminta is loading the public listing from the marketplace directory." : "It may have been unpublished, renamed or removed by its owner.")}</span>
        {!loading && (
          <div className="public-unpublished-actions">
            <button type="button" className="primary" onClick={() => navigate(browseTarget)}>Browse public listings</button>
            <button type="button" className="secondary" onClick={() => navigate("home")}>Back to Badminta</button>
          </div>
        )}
      </div>
    </section>
  );
}

function PublicProfilePreview({ type, accountProfile, viewerProfile = accountProfile, navigate, saveAccountItem, saveAccountProfile, following = [], toggleFollowing = null, publicReviews = [], routeStatus = null, externalProfile = false }) {
  if (routeStatus && routeStatus.state !== "ready") {
    return <PublicProfileRoutePlaceholder type={type} status={routeStatus} navigate={navigate} />;
  }
  if (!accountProfile) return <PublicProfileRoutePlaceholder type={type} status={{ state: "loading", message: "Loading the published profile." }} navigate={navigate} />;
  const missingFields = getRoleOnboardingMissingFields(accountProfile, type);
  if (missingFields.length) {
    return externalProfile
      ? <PublicProfileRoutePlaceholder type={type} status={{ state: "error", message: "This profile is not currently published with all required public details." }} navigate={navigate} />
      : <PublicProfileUnpublished type={type} missingFields={missingFields} navigate={navigate} />;
  }
  if (type === "player") return <PublicPlayerListing accountProfile={accountProfile} viewerProfile={viewerProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={following} toggleFollowing={toggleFollowing} publicReviews={publicReviews} />;
  if (type === "coach") return <PublicCoachListing accountProfile={accountProfile} viewerProfile={viewerProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={following} toggleFollowing={toggleFollowing} publicReviews={publicReviews} />;
  if (type === "group") return <PublicGroupListing accountProfile={accountProfile} viewerProfile={viewerProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={following} toggleFollowing={toggleFollowing} publicReviews={publicReviews} />;
  return <PublicCentreListing accountProfile={accountProfile} viewerProfile={viewerProfile} navigate={navigate} saveAccountItem={saveAccountItem} saveAccountProfile={saveAccountProfile} following={following} toggleFollowing={toggleFollowing} publicReviews={publicReviews} />;
}

function PublicProfileUnpublished({ type, missingFields, navigate }) {
  const role = publicRoleId(type);
  const shortcut = publicProfileShortcuts.find((item) => item.role === role);
  const title = shortcut?.title || `${roleTitle(role)} public profile`;
  return (
    <section className="page public-unpublished-page">
      <div className="public-unpublished-card">
        <div className="public-unpublished-icon">
          <LockKeyhole size={28} />
        </div>
        <p>Not published</p>
        <h1>{title} is not public yet.</h1>
        <span>Complete and save every required field before this profile can appear publicly on Badminta.</span>
        <div className="public-missing-grid">
          {missingFields.map((field) => {
            const meta = missingFieldMeta(field);
            return (
              <div key={meta.field}>
                <CircleHelp size={15} />
                <strong>{meta.title}</strong>
                <span>{meta.detail}</span>
              </div>
            );
          })}
        </div>
        <div className="public-unpublished-actions">
          <button type="button" className="primary" onClick={() => navigate(publicSetupTarget(role))}>Complete required fields</button>
          <button type="button" className="secondary" onClick={() => navigate("account")}>Back to account</button>
        </div>
      </div>
    </section>
  );
}

const getPublicProfileImage = (...sources) => {
  const urls = sources.flatMap((source) => collectImageUrls(source));
  const allowedUrls = urls.map(getAllowedUserMediaUrl).filter(Boolean);
  return allowedUrls.find((url) => isImageMediaUrl(url)) || allowedUrls[0] || "";
};

const getMatchedPublicCentre = (centre = {}) => {
  const name = String(centre.name || "").trim().toLowerCase();
  const address = String(centre.address || "").trim().toLowerCase();
  const city = String(centre.city || centre.location?.city || "").trim().toLowerCase();
  if (!name && !address) return null;
  return venues.find((venue) => {
    const venueName = String(venue.name || "").trim().toLowerCase();
    const venueAddress = String(venue.address || "").trim().toLowerCase();
    const venueCity = String(venue.city || "").trim().toLowerCase();
    const addressMatch = address.length >= 8
      && venueAddress.length >= 8
      && (venueAddress === address || venueAddress.includes(address) || address.includes(venueAddress));
    const exactNameMatch = Boolean(name && venueName && venueName === name);
    const compatibleCity = !city || !venueCity || city === venueCity;
    return addressMatch || (exactNameMatch && compatibleCity);
  }) || null;
};

const publicHeroTypeLabels = {
  player: "Player profile",
  coach: "Coach profile",
  group: "Social group",
  centre: "Badminton centre",
  event: "Community event"
};

const plainPublicNarrative = (value = "") => String(value || "")
  .replace(/\*\*([^*]+)\*\*/g, "$1")
  .replace(/__([^_]+)__/g, "$1")
  .trim();

function PublicListingHero({ variant, eyebrow, title, subtitle, image, avatar, icon: Icon, facts = [], qualityLabel, qualityValue, qualityMeta, primaryAction, secondaryAction, socialTarget = null, following = [], toggleFollowing = null, ownerPreview = false, allowStockFallback = false, fallbackVenue = null, planTier = "" }) {
  const isProfileHero = variant === "player" || variant === "coach";
  const roleLabel = publicHeroTypeLabels[variant] || "Public profile";
  const visibleFacts = facts.filter(Boolean);
  const safeImage = getAllowedUserMediaUrl(image);
  const safeAvatar = getAllowedUserMediaUrl(avatar);
  const fallbackMedia = safeImage || !allowStockFallback ? null : getStockMediaFallback(variant || "general", title);
  const visibleImage = safeImage || fallbackMedia?.url || "";
  const [imageFailed, setImageFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => setImageFailed(false), [visibleImage]);
  useEffect(() => setAvatarFailed(false), [safeAvatar]);
  const renderableImage = imageFailed ? "" : visibleImage;
  const showAvatar = Boolean(safeAvatar) && !avatarFailed && (isProfileHero || normaliseImageFingerprint(safeAvatar) !== normaliseImageFingerprint(renderableImage));
  const hasRenderableHeroMedia = Boolean(renderableImage || (isProfileHero && showAvatar));
  const visibleImageIsStock = Boolean(fallbackMedia) || isStockBadmintonImageUrl(visibleImage);
  const fallbackVenuePoint = variant === "centre" && fallbackVenue ? getVenueMediaPoint(fallbackVenue) : null;
  const hasFallbackMap = Boolean(fallbackVenuePoint);
  return (
    <section className={`public-listing-hero ${variant || ""} ${hasRenderableHeroMedia || hasFallbackMap ? "has-hero-media" : "no-hero-media"}`}>
      <div className="public-listing-copy">
        {showAvatar && !isProfileHero && (
          <div className="public-listing-avatar">
            <img src={safeAvatar} alt="" onError={() => setAvatarFailed(true)} />
          </div>
        )}
        <div className="public-hero-kicker">
          <span>{eyebrow}</span>
          <em>{roleLabel}</em>
          {String(planTier).toLowerCase() === "pro" && <strong className="public-pro-badge"><Sparkles size={13} /> Pro</strong>}
        </div>
        <h1>{title}</h1>
        <span>{subtitle}</span>
        {visibleFacts.length > 0 && (
          <div className="public-listing-tags">
            {visibleFacts.map((item) => <em key={item}>{item}</em>)}
          </div>
        )}
        {socialTarget && (
          <PublicFollowControl
            target={socialTarget}
            following={following}
            toggleFollowing={toggleFollowing}
            ownerPreview={ownerPreview}
          />
        )}
        <div className="public-listing-actions">
          {primaryAction && <button type="button" className="primary" onClick={primaryAction[1]}>{primaryAction[0]}</button>}
          {secondaryAction && <button type="button" className="secondary" onClick={secondaryAction[1]}>{secondaryAction[0]}</button>}
        </div>
      </div>
      <div className={`${isProfileHero ? "public-listing-media profile-media" : "public-listing-media"} ${hasRenderableHeroMedia || hasFallbackMap ? "has-media" : "no-media"}`}>
        <div className="public-hero-media-frame">
          {isProfileHero && showAvatar ? (
            <div className="public-profile-photo-card">
              <img src={safeAvatar} alt="" onError={() => setAvatarFailed(true)} />
            </div>
          ) : renderableImage ? (
            <>
              <img src={renderableImage} alt="" onError={() => setImageFailed(true)} />
              {visibleImageIsStock && <span className="stock-media-chip">Stock image</span>}
            </>
          ) : hasFallbackMap ? (
            <VenueMapEmbed venue={fallbackVenue} point={fallbackVenuePoint} className="public-hero-map" />
          ) : <PublicHeroFallback variant={variant} icon={Icon} />}
        </div>
        <div className="public-listing-badge">
          <Icon size={20} />
          <span>{qualityLabel}</span>
          <strong>{qualityValue}</strong>
          {qualityMeta && <small>{qualityMeta}</small>}
        </div>
      </div>
    </section>
  );
}

function PublicHeroFallback({ variant, icon: Icon }) {
  const fallbackCopy = {
    player: ["Player media not added", "This player has not published a profile image or playing clip."],
    coach: ["Coach media not added", "This coach has not published a profile image or lesson clip."],
    group: ["Add a group cover", "Session covers, venue photos and group logos help players understand the session before joining."],
    centre: ["Add centre photos", "Court photos, map context and official media make centre listings feel bookable."],
    event: ["Add event media", "Event posters, venue photos and official media make public events easier to trust."]
  };
  const [title, text] = fallbackCopy[variant] || ["Add public media", "Images make this public profile feel complete."];
  return (
    <div className="public-hero-fallback">
      <div>
        <Icon size={42} />
        <strong>{title}</strong>
        <span>{text}</span>
      </div>
    </div>
  );
}

function PublicStatGrid({ items }) {
  return (
    <div className="public-stat-grid">
      {items.map(([Icon, label, value, detail]) => (
        <article key={label}>
          <Icon size={19} />
          <span>{label}</span>
          <strong>{value}</strong>
          {detail && <em>{detail}</em>}
        </article>
      ))}
    </div>
  );
}

function PublicProfileSection({ eyebrow, title, children }) {
  return (
    <section className="public-profile-section">
      <div>
        {eyebrow && <p>{eyebrow}</p>}
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

const publicProfileOwnerUid = (profile = {}, role = "player") => {
  const roleKey = role === "group" ? "group" : role;
  const roleProfile = profile?.[roleKey] || {};
  return String(
    roleProfile.ownerUid
      || roleProfile.playerUid
      || roleProfile.coachUid
      || roleProfile.organiserUid
      || roleProfile.centreOwnerUid
      || profile.playerUid
      || profile.coachUid
      || profile.organiserUid
      || profile.centreOwnerUid
      || profile.createdByUid
      || profile.ownerUid
      || profile.uid
      || ""
  ).trim();
};

const publicProfileRecordId = (profile = {}, role = "player") => {
  const roleKey = role === "group" ? "group" : role;
  const roleProfile = profile?.[roleKey] || {};
  return String(
    roleProfile.firestoreSourceId
      || roleProfile.serverGroupId
      || roleProfile.publicGroupId
      || roleProfile.publicPlayerId
      || roleProfile.publicCoachId
      || roleProfile.serverCentreId
      || roleProfile.id
      || profile.firestoreSourceId
      || profile.serverGroupId
      || profile.publicGroupId
      || profile.publicPlayerId
      || profile.publicCoachId
      || profile.serverCentreId
      || profile.id
      || ""
  ).trim();
};

const viewerOwnsPublicProfile = (subjectProfile = {}, viewerProfile = {}, role = "player") => {
  const subjectUid = publicProfileOwnerUid(subjectProfile, role);
  const viewerUid = String(viewerProfile?.uid || viewerProfile?.ownerUid || firebaseAuth?.currentUser?.uid || "").trim();
  if (subjectUid && viewerUid && subjectUid === viewerUid) return true;

  const subjectRecordId = publicProfileRecordId(subjectProfile, role);
  const viewerRecordId = publicProfileRecordId(viewerProfile, role);
  if (subjectRecordId && viewerRecordId && subjectRecordId === viewerRecordId) return true;

  const subjectPhone = normalisePhoneNumber(subjectProfile?.phoneNumber || subjectProfile?.phone || "");
  const viewerPhone = normalisePhoneNumber(viewerProfile?.phoneNumber || viewerProfile?.phone || firebaseAuth?.currentUser?.phoneNumber || "");
  if (subjectPhone && viewerPhone && subjectPhone === viewerPhone) return true;

  return subjectProfile === viewerProfile && Boolean(viewerUid || viewerPhone);
};

const socialFollowCollectionByRole = Object.freeze({
  player: "players",
  coach: "coaches",
  group: "groups",
  centre: "centres"
});

const socialFollowRoleLabels = Object.freeze({
  player: "Player",
  coach: "Coach",
  group: "Social group",
  centre: "Centre"
});

const socialEntityIdForTarget = (target = {}) => {
  const targetCollection = String(target.targetCollection || "").trim();
  const targetId = String(target.targetId || "").trim();
  return targetCollection && targetId ? encodeURIComponent(`${targetCollection}:${targetId}`) : "";
};

const normaliseSocialFollowTarget = (target = {}) => {
  const targetCollection = String(target.targetCollection || "").trim();
  const targetId = String(target.targetId || "").trim();
  const role = ["player", "coach", "group", "centre"].includes(target.role) ? target.role : "player";
  return {
    ...target,
    entityId: target.entityId || socialEntityIdForTarget({ targetCollection, targetId }),
    targetCollection,
    targetId,
    role,
    label: String(target.label || socialFollowRoleLabels[role] || "Badminta profile").trim(),
    area: String(target.area || "").trim(),
    image: getAllowedUserMediaUrl(target.image || ""),
    countryCode: String(target.countryCode || "").trim().toUpperCase().slice(0, 3),
    publicPath: String(target.publicPath || "").trim(),
    status: target.status || "active"
  };
};

const normaliseFollowingRecord = (record = {}) => normaliseSocialFollowTarget({
  ...record,
  followedAt: record.followedAt || record.createdAt || record.updatedAt || ""
});

const isFollowingSocialTarget = (following = [], target = {}) => {
  const entityId = socialEntityIdForTarget(target);
  return Boolean(entityId && following.some((item) => (
    String(item.entityId || socialEntityIdForTarget(item)) === entityId && item.status !== "inactive"
  )));
};

const socialTargetFromPublicProfile = (profile = {}, role = "player") => {
  const roleProfile = profile?.[role === "group" ? "group" : role] || {};
  const targetId = publicProfileRecordId(profile, role);
  if (!targetId) return null;
  const label = role === "group"
    ? roleProfile.name
    : role === "centre"
      ? roleProfile.name
      : role === "coach"
        ? roleProfile.headline || profile.fullName
        : profile.fullName;
  const area = role === "player"
    ? publicPlayerAreaLabel(roleProfile)
    : publicProfileAreaLabel(roleProfile, "");
  const image = getPublicProfileImage(
    roleProfile.photoUrl,
    roleProfile.logoUrl,
    roleProfile.coverUrl,
    roleProfile.image,
    profile.photoUrl
  );
  return normaliseSocialFollowTarget({
    targetCollection: socialFollowCollectionByRole[role],
    targetId,
    role,
    label: label || socialFollowRoleLabels[role],
    area,
    image,
    countryCode: roleProfile.countryCode || roleProfile.location?.countryCode,
    publicPath: publicProfilePath(profile, role, roleProfile.city || roleProfile.location?.city || "Worldwide")
  });
};

const socialTargetFromVenue = (venue = {}) => {
  if (!venue?.id || !venue?.name) return null;
  return normaliseSocialFollowTarget({
    targetCollection: "directoryCentres",
    targetId: venue.id,
    role: "centre",
    label: venue.name,
    area: [venue.suburb, venue.city, venue.country].filter(Boolean).join(", "),
    image: getVenueImages(venue)[0] || venue.image || "",
    countryCode: venue.countryCode,
    publicPath: routeHref("venue", { venue })
  });
};

const socialFollowerCountLabel = (count = 0) => `${Math.max(0, Number(count || 0)).toLocaleString()} follower${Number(count || 0) === 1 ? "" : "s"}`;

function useSocialFollowerCount(target) {
  const entityId = socialEntityIdForTarget(target || {});
  const seedCount = Math.max(0, Number(target?.followerCount || 0));
  const [followerCount, setFollowerCount] = useState(seedCount);
  const [syncStatus, setSyncStatus] = useState("idle");

  useEffect(() => {
    setFollowerCount(seedCount);
    if (!entityId) {
      setSyncStatus("idle");
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = null;
    setSyncStatus("loading");
    loadFirebaseRuntime().then(() => {
      if (cancelled || !firebaseDb) return;
      unsubscribe = onSnapshot(doc(firebaseDb, "socialEntities", entityId), (snapshot) => {
        if (cancelled) return;
        setFollowerCount(Math.max(0, Number(snapshot.exists() ? snapshot.data()?.followerCount || 0 : 0)));
        setSyncStatus("ready");
      }, () => {
        if (!cancelled) setSyncStatus("error");
      });
    }).catch(() => {
      if (!cancelled) setSyncStatus("error");
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [entityId, seedCount]);

  return { followerCount, setFollowerCount, syncStatus };
}

function PublicFollowControl({ target, following = [], toggleFollowing, ownerPreview = false, compact = false }) {
  const socialTarget = target ? normaliseSocialFollowTarget(target) : null;
  const followingFromAccount = socialTarget ? isFollowingSocialTarget(following, socialTarget) : false;
  const [followingState, setFollowingState] = useState(followingFromAccount);
  const [actionState, setActionState] = useState({ status: "idle", message: "" });
  const { followerCount, setFollowerCount } = useSocialFollowerCount(socialTarget);

  useEffect(() => {
    setFollowingState(followingFromAccount);
  }, [followingFromAccount, socialTarget?.entityId]);

  if (!socialTarget) return null;

  const toggle = async () => {
    if (!toggleFollowing || actionState.status === "loading") return;
    const nextFollowing = !followingState;
    setFollowingState(nextFollowing);
    setFollowerCount((current) => Math.max(0, current + (nextFollowing ? 1 : -1)));
    setActionState({ status: "loading", message: nextFollowing ? "Following..." : "Unfollowing..." });
    try {
      const result = await toggleFollowing(socialTarget, nextFollowing);
      if (result?.redirected) {
        setFollowingState(followingFromAccount);
        setFollowerCount((current) => Math.max(0, current + (nextFollowing ? -1 : 1)));
        setActionState({ status: "idle", message: "" });
        return;
      }
      const confirmedFollowing = result?.following !== false;
      setFollowingState(confirmedFollowing);
      if (Number.isFinite(Number(result?.followerCount))) setFollowerCount(Math.max(0, Number(result.followerCount)));
      setActionState({ status: "ready", message: confirmedFollowing ? "Following" : "Unfollowed" });
    } catch (error) {
      setFollowingState(followingFromAccount);
      setFollowerCount((current) => Math.max(0, current + (nextFollowing ? -1 : 1)));
      setActionState({ status: "error", message: marketplaceFunctionErrorMessage(error) || "Follow status could not be saved." });
    }
  };

  if (ownerPreview) {
    return (
      <div className={`public-social-row owner ${compact ? "compact" : ""}`} aria-label={socialFollowerCountLabel(followerCount)}>
        <UsersRound size={18} />
        <strong>{socialFollowerCountLabel(followerCount)}</strong>
        <span>People following this profile</span>
      </div>
    );
  }

  return (
    <div className={`public-social-row ${followingState ? "following" : ""} ${compact ? "compact" : ""}`}>
      <button
        type="button"
        className="public-follow-button"
        aria-pressed={followingState}
        onClick={toggle}
        disabled={actionState.status === "loading"}
      >
        {followingState ? <UserCheck size={18} /> : <UserPlus size={18} />}
        <span>{followingState ? "Following" : "Follow"}</span>
      </button>
      <div>
        <strong>{socialFollowerCountLabel(followerCount)}</strong>
        <span>{followingState ? "Updates stay in your Following list" : "Keep up with this profile"}</span>
      </div>
      {actionState.status === "error" && <small role="alert">{actionState.message}</small>}
    </div>
  );
}

function PublicActionPanel({ title, value, caption, rows, primary, secondary, note, links }) {
  return (
    <aside className="public-action-panel">
      <div>
        <span>{title}</span>
        <strong>{value}</strong>
        <p>{caption}</p>
      </div>
      <div className="public-action-rows">
        {rows.map(([Icon, label, text]) => (
          <div key={label}>
            <Icon size={17} />
            <span>{label}</span>
            <strong>{text}</strong>
          </div>
        ))}
      </div>
      {links?.length > 0 && (
        <div className="public-action-links">
          {links.slice(0, 3).map((link) => (
            <a key={link.url} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> {link.label}</a>
          ))}
        </div>
      )}
      <button type="button" className="primary" onClick={primary[1]}>{primary[0]}</button>
      {secondary && <button type="button" className="secondary" onClick={secondary[1]}>{secondary[0]}</button>}
      {note && <small>{note}</small>}
    </aside>
  );
}

function PublicInteractionGrid({ eyebrow = "Actions", title, text, actions }) {
  return (
    <PublicProfileSection eyebrow={eyebrow} title={title}>
      {text && <p className="public-profile-copy">{text}</p>}
      <div className="public-interaction-grid">
        {actions.map(([Icon, label, detail, onClick, tone]) => (
          <button type="button" key={label} className={tone === "primary" ? "primary-action" : ""} onClick={onClick}>
            <Icon size={18} />
            <strong>{label}</strong>
            <span>{detail}</span>
          </button>
        ))}
      </div>
    </PublicProfileSection>
  );
}

const getGroupPublicSessions = (group = {}) => {
  return getGroupPublishedDatedOccurrences(group)
    .map((session) => ({
      ...session,
      sessionId: session.sessionId || session.id,
      displayDate: session.dateLabel,
      displayPrice: session.priceLabel,
      displayCapacity: session.capacityLabel
    }));
};

const groupForPublicSession = (group = {}, session = {}) => ({
  ...group,
  date: session.date || group.date || "",
  time: session.start || session.time || group.time || "",
  price: session.price !== undefined && session.price !== "" ? session.price : group.price,
  capacity: session.capacity || group.capacity,
  sessionId: session.sessionId || session.id || "",
  nextSessionId: session.sessionId || session.id || "",
  sessions: [session],
  nextSession: session
});

function PublicSessionBoard({ sessions, group, navigate, accountProfile, onMessageOrganiser, ownerPreview = false }) {
  const publicMembers = getPublicGroupMembers(group, accountProfile);
  const memberCount = getGroupMemberCount(group, accountProfile);
  return (
    <div className="public-session-board">
      <div className="public-session-list">
        {sessions.length ? sessions.map((session) => (
          <article key={session.id}>
            <span>{session.status}</span>
            <strong>{session.displayDate || session.dateLabel || session.date}</strong>
            <em>{session.time} · {session.displayPrice || session.priceLabel || session.price} · {session.displayCapacity || session.capacityLabel || session.capacity}</em>
            <div>
              {ownerPreview ? (
                <button type="button" onClick={() => session.isRecurring === false ? navigate("editGroupSession", { group, session }) : navigate("createGroupSession")}>{session.isRecurring === false ? "Manage session" : "Create dated session"}</button>
              ) : (
                <>
                  <button type="button" onClick={() => navigate("join", { group: groupForPublicSession(group, session), bookingType: "social" })}>Join</button>
                  <button type="button" onClick={() => navigate("join", { group: groupForPublicSession(group, session), bookingType: "social" })}>Pay</button>
                </>
              )}
            </div>
          </article>
        )) : (
          <div className="public-empty-panel">
            <CalendarDays size={20} />
            <strong>No public sessions yet</strong>
            <span>{ownerPreview ? "Create a dated session from your regular group schedule so players can reserve and pay for a specific game." : "This organiser has not published a joinable session yet. Message the organiser or browse other social groups nearby."}</span>
            <button type="button" onClick={ownerPreview ? () => navigate("createGroupSession") : onMessageOrganiser || (() => navigate("messages"))}>{ownerPreview ? "Create session" : "Message organiser"}</button>
          </div>
        )}
      </div>
      <div className="public-member-list">
        <div>
          <UsersRound size={20} />
          <strong>Group members</strong>
          <span>{memberCount ? `${memberCount} active member${memberCount === 1 ? "" : "s"}` : "No active members yet"}</span>
        </div>
        {publicMembers.length ? publicMembers.map((member) => (
          <article key={member.name}>
            <UserCircle size={18} />
            <strong>{member.name}</strong>
            <span>{member.rating || "Rating pending"}</span>
          </article>
        )) : (
          <p>{memberCount ? "Member identities only appear here when players choose to make their profile public." : "Be the first player to join this group. Session reservations remain separate."}</p>
        )}
      </div>
    </div>
  );
}

function PublicEventStrip({ events, navigate, emptyTitle, emptyText, accountProfile }) {
  const publishedEvents = events.filter((event) => event.status === "published");
  if (!publishedEvents.length) {
    return (
      <div className="public-empty-panel">
        <Star size={20} />
        <strong>{emptyTitle}</strong>
        <span>{emptyText}</span>
        <button type="button" onClick={() => navigate("events")}>Browse events</button>
      </div>
    );
  }
  return (
    <div className="event-mini-grid">
      {publishedEvents.slice(0, 3).map((event) => <EventCard event={event} navigate={navigate} accountProfile={accountProfile} compact key={event.id} />)}
    </div>
  );
}

function PublicCreateCta({ title, text, button, navigate }) {
  return (
    <section className="public-create-cta">
      <div>
        <p>Build your Badminta presence</p>
        <h2>{title}</h2>
        <span>{text}</span>
      </div>
      <button type="button" onClick={() => navigate(button[1])}>{button[0]}</button>
    </section>
  );
}

const getPublicAvailabilityRows = (schedule = [], fallback = "") => {
  const rows = normaliseAvailabilitySchedule(schedule)
    .filter((row) => row.enabled)
    .map((row) => ({
      day: row.label,
      time: `${formatTimeLabel(row.start)} - ${formatTimeLabel(row.end)}`
    }));
  if (rows.length) return rows;
  const fallbackText = String(fallback || "").trim();
  if (fallbackText && !fallbackText.toLowerCase().includes("not added")) return [{ day: "General availability", time: fallbackText }];
  return [];
};

function PublicAvailabilityPanel({
  rows,
  emptyText,
  availableTitle = "Open to play",
  emptyTitle = "Availability not added"
}) {
  return (
    <div className={rows.length ? "public-availability-panel" : "public-availability-panel empty"}>
      <div className="public-availability-head">
        <Clock3 size={20} />
        <div>
          <strong>{rows.length ? availableTitle : emptyTitle}</strong>
          <span>{rows.length ? `${rows.length} public time window${rows.length === 1 ? "" : "s"}` : emptyText}</span>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="public-availability-list">
          {rows.map((row) => (
            <div key={`${row.day}-${row.time}`}>
              <span>{row.day}</span>
              <strong>{row.time}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PublicCourtAvailabilityCalendar({
  days = [],
  status = "idle",
  message = "",
  timeZone = "",
  ownerPreview = false,
  onSelectSlot,
  onManage
}) {
  const firstPlayableDay = days.find((day) => day.slots?.some((slot) => slot.status !== "full")) || days.find((day) => day.open) || days[0];
  const [selectedDate, setSelectedDate] = useState(firstPlayableDay?.date || "");

  useEffect(() => {
    if (!days.length) return;
    if (!days.some((day) => day.date === selectedDate)) setSelectedDate(firstPlayableDay?.date || days[0].date);
  }, [days, firstPlayableDay?.date, selectedDate]);

  const selectedDay = days.find((day) => day.date === selectedDate) || firstPlayableDay;
  return (
    <div className="public-court-calendar">
      <div className="public-court-calendar-head">
        <div>
          <strong>Live court availability</strong>
          <span>{timeZone || "Centre local time"} · one-hour starting times</span>
        </div>
        <CalendarDays size={21} />
      </div>
      {status === "loading" ? (
        <div className="public-court-calendar-state"><Loader2 className="spin" size={20} /><strong>Checking courts and current bookings</strong></div>
      ) : status === "error" ? (
        <div className="public-court-calendar-state"><CircleHelp size={20} /><strong>{message || "Live availability could not be loaded."}</strong></div>
      ) : (
        <>
          <div className="public-court-calendar-days" role="tablist" aria-label="Choose a booking date">
            {days.map((day) => {
              const available = day.slots?.filter((slot) => slot.status !== "full").length || 0;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedDay?.date === day.date}
                  className={selectedDay?.date === day.date ? "active" : ""}
                  onClick={() => setSelectedDate(day.date)}
                  key={day.date}
                >
                  <span>{day.dayLabel}</span>
                  <strong>{day.dateLabel}</strong>
                  <em>{day.open ? `${available} times` : "Closed"}</em>
                </button>
              );
            })}
          </div>
          <div className="public-court-calendar-slots">
            {selectedDay?.slots?.length ? selectedDay.slots.map((slot) => (
              <button
                type="button"
                className={slot.status}
                disabled={slot.status === "full"}
                onClick={() => onSelectSlot?.(slot)}
                key={slot.id}
              >
                <strong>{formatTimeLabel(slot.time)}</strong>
                <span>{slot.status === "full" ? "Full" : `${slot.availableCourts} court${slot.availableCourts === 1 ? "" : "s"} free`}</span>
              </button>
            )) : (
              <div className="public-court-calendar-empty">
                <CalendarClock size={20} />
                <strong>{selectedDay?.open ? "No online times remain for this day" : "Centre closed for online booking"}</strong>
              </div>
            )}
          </div>
          <div className="public-court-calendar-legend">
            <span><i className="available" /> Available</span>
            <span><i className="limited" /> Limited</span>
            <span><i className="full" /> Full</span>
          </div>
          {ownerPreview && <button type="button" className="public-court-calendar-manage" onClick={onManage}>Manage court availability</button>}
        </>
      )}
    </div>
  );
}

const publicPlayerLocationParts = (player = {}) => [
  player.location?.suburb || player.suburb,
  player.location?.city || player.city,
  player.location?.state || player.state,
  player.location?.country || player.country
].map((value) => String(value || "").trim()).filter((value, index, values) => value && values.findIndex((item) => compactLocationText(item) === compactLocationText(value)) === index);

const publicPlayerAreaLabel = (player = {}) => publicPlayerLocationParts(player).join(", ") || "Location not added";
const publicPlayerSuburbLabel = (player = {}) => player.location?.suburb || player.suburb || player.location?.city || player.city || "Location not added";

function PublicPlayerListing({ accountProfile, viewerProfile = defaultAccountProfile, navigate, saveAccountItem, saveAccountProfile, following = [], toggleFollowing = null, publicReviews = [] }) {
  const player = accountProfile.player || defaultAccountProfile.player;
  const name = accountProfile.fullName || "Badminta player";
  const firstName = name.split(" ")[0] || "This player";
  const availability = summarizeAvailabilitySchedule(player.availabilitySchedule) || player.availability || "Availability not added yet";
  const availabilityRows = getPublicAvailabilityRows(player.availabilitySchedule, player.availability);
  const socialLinks = cleanLinkRows(player.socialLinks, "Player social");
  const mediaLinks = cleanLinkRows(player.mediaLinks, "Player media");
  const publicMediaLinks = mediaLinks.filter(isApprovedPublicMediaLink);
  const hiddenMediaCount = pendingMediaReviewCount(mediaLinks);
  const heroImage = getPublicProfileImage(player.photoUrl, accountProfile.photoUrl, publicMediaLinks);
  const playerReviewTarget = getAccountPublicReviewTarget(accountProfile, "player", name, player.city || player.location?.city || "Melbourne");
  const ratingSummary = publicReviewSummary(accountProfile, ["player", "recommendation"], name, { hidden: player.visibility?.rating === false, publicReviews, target: playerReviewTarget });
  const ownerPreview = viewerOwnsPublicProfile(accountProfile, viewerProfile, "player");
  const socialTarget = socialTargetFromPublicProfile(accountProfile, "player");
  const playerAbout = player.about || player.style || "";
  const playerArea = publicPlayerAreaLabel(player);
  const playerSuburb = publicPlayerSuburbLabel(player);
  const hasPlayerExperience = Boolean(String(playerAbout).trim());
  const savePlayerProfile = () => {
    saveAccountItem?.(savedItemFromPublicProfile(accountProfile, "player"));
    navigate("saved");
  };
  const messagePlayer = (intent = "Player enquiry") => openPublicMessageThread({
    accountProfile: viewerProfile,
    saveAccountProfile,
    navigate,
    roleId: "player",
    targetCollection: playerReviewTarget.targetCollection,
    targetId: playerReviewTarget.targetId,
    subject: name,
    threadType: "Player enquiry",
    text: `${intent} started from ${name}'s public player profile. Confirm availability, level fit, suburb, format and the next suitable session before organising a game.`,
    meta: [player.skillBand, playerArea, player.formats, availabilityRows.length ? "Availability added" : "Availability pending"],
    context: [["Player", name], ["Skill", player.skillBand || "Pending"], ["Suburb", playerSuburb], ["Area", playerArea], ["Format", player.formats || "Pending"]],
    actions: [["View player profile", "publicPlayerProfile"], ["Court bookings", "bookings"], ["Find players", "playersIndex"]]
  });
  const playerInteractionActions = ownerPreview ? [
    [UserCircle, "Edit player profile", "Update your level, location, playing note and public details.", () => navigate("profileSetup"), "primary"],
    [LayoutDashboard, "Player dashboard", "Open your bookings, joined groups, payments and profile status.", () => navigate("playerDashboard")],
    [Clock3, "Update availability", "Keep your weekly playing windows accurate for invitations.", () => navigate("profileSetup")],
    [CalendarDays, "Court bookings", "Review court requests and payment status.", () => navigate("bookings")],
    [MessageCircle, "Player messages", "Open invitations, organiser questions and booking conversations.", () => navigate("messages")]
  ] : [
    [MessageCircle, `Message ${firstName}`, "Ask if they are free for your next court or group.", () => messagePlayer(`Message ${firstName}`), "primary"],
    [CalendarDays, "Invite to play", "Send a play request with level, time and venue context.", () => messagePlayer("Ask to play")],
    [Heart, "Save player", "Keep this player handy for future games.", savePlayerProfile],
    [BadgeCheck, "Recommend", "Leave a recommendation after you have actually played together.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("recommendation", "Recommend player", name, playerReviewTarget) })],
    [Star, "Rate after play", "Rate level fit after a real session.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("player", "Rate player", name, playerReviewTarget) })],
    [ShieldCheck, "Report problem", "Report unsafe behaviour, wrong details or misleading level claims.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report player profile", name, playerReviewTarget) })]
  ];
  return (
    <section className="page public-profile-page public-listing-page">
      <PublicListingHero
        variant="player"
        eyebrow="Player public listing"
        title={name}
        subtitle={[player.skillBand, playerArea, player.formats].filter(Boolean).join(" - ")}
        image={heroImage}
        avatar={heroImage}
        icon={UserCircle}
        facts={[player.skillBand, player.formats, playerArea].filter(Boolean)}
        qualityLabel="Player reviews"
        qualityValue={ratingSummary.label}
        qualityMeta={ratingSummary.meta}
        primaryAction={ownerPreview ? ["Edit player profile", () => navigate("profileSetup")] : ["Invite to play", () => messagePlayer("Invite to play")]}
        secondaryAction={ownerPreview ? ["Player dashboard", () => navigate("playerDashboard")] : ["Recommend player", () => navigate("reviewFlow", { reviewContext: reviewContextFor("recommendation", "Recommend player", name, playerReviewTarget) })]}
        socialTarget={socialTarget}
        following={following}
        toggleFollowing={toggleFollowing}
        ownerPreview={ownerPreview}
        planTier={player.planTier || (player.proProfile ? "pro" : "")}
      />
      {mediaLinks.length > 0 && (
        <PublicMediaShowcase
          title="Playing photos and clips"
          links={mediaLinks}
          exclude={[heroImage]}
          requireModeration
          emptyTitle="Playing media is being reviewed"
          emptyText="Approved playing photos and clips will appear here."
        />
      )}
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [Star, "Level", player.skillBand || "Not added", "Helps organisers place this player"],
            ...(player.formats ? [[Grid3X3, "Likes to play", player.formats, "Singles, doubles or social sessions"]] : []),
            [MapPin, player.location?.suburb || player.suburb ? "Home suburb" : "Local area", playerArea, `Exact public area: ${playerSuburb}`],
            [ShieldCheck, "Ratings", player.visibility?.rating === false ? "Hidden" : "Visible", "Controlled by this player"]
          ]} />
          {availabilityRows.length > 0 && (
            <PublicProfileSection eyebrow="Availability" title="When this player is open to play">
              <PublicAvailabilityPanel rows={availabilityRows} />
            </PublicProfileSection>
          )}
          {hasPlayerExperience && (
            <PublicProfileSection title={`Why play with ${firstName}`}>
              <blockquote className="public-player-quote">
                <p>{playerAbout}</p>
              </blockquote>
            </PublicProfileSection>
          )}
          <PublicInteractionGrid
            eyebrow="Player interactions"
            title={ownerPreview ? "Manage your player presence" : `Connect with ${firstName}`}
            text={ownerPreview ? "These controls are shown because this is your published player profile." : "Invite this player only if the level, area and availability fit your game."}
            actions={playerInteractionActions}
          />
          {socialLinks.length > 0 && (
            <PublicProfileSection eyebrow="Public links" title="Social and playing links">
              <PublicLinkList links={socialLinks} />
            </PublicProfileSection>
          )}
        </main>
        <PublicActionPanel
          title={ownerPreview ? "Player controls" : "Player contact"}
          value={ownerPreview ? "Profile live" : player.visibility?.invites === false ? "Invites hidden" : "Invite ready"}
          caption={ownerPreview ? "Keep your public details and availability accurate." : "Message or save this player when the level and area match your game."}
          rows={[
            [ShieldCheck, "Rating visibility", player.visibility?.rating === false ? "Hidden" : "Visible"],
            ...(publicMediaLinks.length || hiddenMediaCount ? [[Video, "Media", publicMediaLinks.length ? `${publicMediaLinks.length} approved` : "Pending review"]] : []),
            ...(socialLinks.length ? [[Globe2, "Links", `${socialLinks.length} public`]] : [])
          ]}
          primary={ownerPreview ? ["Edit player profile", () => navigate("profileSetup")] : ["Message player", () => messagePlayer("Message player")]}
          secondary={ownerPreview ? ["Open player dashboard", () => navigate("playerDashboard")] : ["Save player", savePlayerProfile]}
          note={ownerPreview ? "Only you see these management actions." : "Players control which details are public."}
        />
      </div>
      <PublicCreateCta
        title={ownerPreview ? "Keep your player profile useful." : "Create a player profile that gets better invites."}
        text={ownerPreview ? "Update your availability and playing details whenever your routine changes." : "Show your level, area, availability and preferred games so organisers can invite you to sessions that actually suit you."}
        button={ownerPreview ? ["Edit your player profile", "profileSetup"] : ["Create your player profile", "profileSetup"]}
        navigate={navigate}
      />
    </section>
  );
}

function PublicCoachListing({ accountProfile, viewerProfile = defaultAccountProfile, navigate, saveAccountItem, saveAccountProfile, following = [], toggleFollowing = null, publicReviews = [] }) {
  const coach = accountProfile.coach || defaultAccountProfile.coach;
  const name = accountProfile.fullName || "Badminta coach";
  const availability = summarizeAvailabilitySchedule(coach.availabilitySchedule) || coach.availability || "Availability not added yet";
  const availabilityRows = getPublicAvailabilityRows(coach.availabilitySchedule, coach.availability);
  const availabilityCompactLabel = availabilityRows.length
    ? `${availabilityRows.length} weekly day${availabilityRows.length === 1 ? "" : "s"}${availabilityRows[0]?.time ? ` · ${availabilityRows[0].time}` : ""}`
    : "Not added";
  const coachingArea = publicProfileAreaLabel(coach, coach.locations || "Location not added");
  const socialLinks = cleanLinkRows(coach.socialLinks, "Coach social");
  const mediaLinks = cleanLinkRows(coach.mediaLinks, "Coach media");
  const publicMediaLinks = mediaLinks.filter(isApprovedPublicMediaLink);
  const hiddenMediaCount = pendingMediaReviewCount(mediaLinks);
  const heroImage = getPublicProfileImage(coach.photoUrl, accountProfile.photoUrl, publicMediaLinks);
  const coachSubject = coach.headline || name;
  const coachReviewTarget = getAccountPublicReviewTarget(accountProfile, "coach", coachSubject, coach.city || coach.location?.city || "Melbourne");
  const coachReviewSummary = publicReviewSummary(accountProfile, ["coach", "testimonial"], coachSubject, { publicReviews, target: coachReviewTarget });
  const ownerPreview = viewerOwnsPublicProfile(accountProfile, viewerProfile, "coach");
  const socialTarget = socialTargetFromPublicProfile(accountProfile, "coach");
  const saveCoachProfile = () => {
    saveAccountItem?.(savedItemFromPublicProfile(accountProfile, "coach"));
    navigate("saved");
  };
  const messageCoach = (intent = "Coach enquiry") => openPublicMessageThread({
    accountProfile: viewerProfile,
    saveAccountProfile,
    navigate,
    roleId: "coach",
    targetCollection: coachReviewTarget.targetCollection,
    targetId: coachReviewTarget.targetId,
    subject: coachSubject,
    threadType: "Coach enquiry",
    text: `${intent} started from ${coachSubject}'s public coach profile. Confirm lesson type, location, availability, rate, payment terms and coaching goal before booking.`,
    meta: [coachPrimaryRateLabel(coach), coach.locations || coachingArea, coach.accreditation, availabilityCompactLabel],
    context: [["Coach", coachSubject], ["Rate", coachPrimaryRateLabel(coach) || "Pending"], ["Location", coach.locations || coachingArea], ["Availability", availabilityCompactLabel]],
    actions: [["View coach profile", "publicCoachProfile"], ["Find coaches", "playersIndex"], ["Messages", "messages"]]
  });
  const coachUid = accountProfile.uid || accountProfile.ownerUid || coach.ownerUid || coach.coachUid || "";
  const coachCurrency = normaliseCurrencyCode(coach.currency || marketplaceCurrencyForCountry(coach.countryCode || coach.location?.countryCode));
  const coachServices = activeCoachLessonServices(coach);
  const coachPaidBookingEnabled = coach.proProfile === true || String(coach.planTier || "").toLowerCase() === "pro";
  const coachCalendarReady = Boolean(coachServices.length && availabilityRows.length && coach.timeZone);
  const coachRateLabel = coachPrimaryRateLabel(coach) || "Rate not added";
  const [lessonForm, setLessonForm] = useState({
    serviceId: coachServices[0]?.id || "",
    slotId: "",
    startAt: "",
    note: ""
  });
  const [lessonCheckoutState, setLessonCheckoutState] = useState({ status: "idle", message: "", lessonRequestId: "" });
  const [coachCalendarState, setCoachCalendarState] = useState({ status: "idle", busyWindows: [], message: "" });
  const selectedLessonService = coachServices.find((service) => service.id === lessonForm.serviceId) || coachServices[0] || null;
  const lessonDuration = selectedLessonService?.durationMinutes || 60;
  const lessonAmountCents = selectedLessonService ? coachLessonServicePriceCents(selectedLessonService, coachCurrency) : 0;
  const generatedLessonSlots = useMemo(
    () => buildCoachBookableSlots(coach, selectedLessonService, 14),
    [
      coach.availability,
      coach.availabilitySchedule,
      coach.bookingNoticeHours,
      coach.bookingWindowDays,
      coach.bufferMinutes,
      coach.timeZone,
      selectedLessonService?.id,
      selectedLessonService?.durationMinutes
    ]
  );
  const lessonSlots = useMemo(() => generatedLessonSlots.filter((slot) => {
    const slotStart = new Date(slot.startAt).getTime();
    const slotEnd = slotStart + (lessonDuration + Math.max(0, Number(coach.bufferMinutes) || 0)) * 60 * 1000;
    return !coachCalendarState.busyWindows.some((window) => {
      const busyStart = new Date(window.startAt).getTime();
      const busyEnd = new Date(window.endAt).getTime();
      return Number.isFinite(busyStart) && Number.isFinite(busyEnd) && slotStart < busyEnd && slotEnd > busyStart;
    });
  }), [generatedLessonSlots, lessonDuration, coach.bufferMinutes, coachCalendarState.busyWindows]);
  const lessonSlotDays = lessonSlots.reduce((days, slot) => {
    const current = days.find((day) => day.dateKey === slot.dateKey);
    if (current) current.slots.push(slot);
    else days.push({ dateKey: slot.dateKey, dayLabel: slot.dayLabel, dateLabel: slot.dateLabel, slots: [slot] });
    return days;
  }, []);
  const selectedLessonSlot = lessonSlots.find((slot) => slot.id === lessonForm.slotId) || null;
  useEffect(() => {
    if (!coachServices.length) return;
    if (!coachServices.some((service) => service.id === lessonForm.serviceId)) {
      setLessonForm((current) => ({ ...current, serviceId: coachServices[0].id, slotId: "", startAt: "" }));
    }
  }, [coachServices.map((service) => service.id).join("|"), lessonForm.serviceId]);
  useEffect(() => {
    let active = true;
    if (!coachUid || !coachCalendarReady) {
      setCoachCalendarState({ status: "idle", busyWindows: [], message: "" });
      return () => {
        active = false;
      };
    }
    setCoachCalendarState((current) => ({ ...current, status: "loading", message: "" }));
    callPublicProfileFunction("getCoachCalendarAvailability", {
      coachUid,
      coachId: coach.publicCoachId || coach.firestoreSourceId || ""
    }).then((result) => {
      if (!active) return;
      setCoachCalendarState({
        status: "ready",
        busyWindows: Array.isArray(result.busyWindows) ? result.busyWindows : [],
        message: ""
      });
    }).catch(() => {
      if (!active) return;
      setCoachCalendarState({
        status: "degraded",
        busyWindows: [],
        message: "Live availability will be checked again when the lesson request is sent."
      });
    });
    return () => {
      active = false;
    };
  }, [coachUid, coach.publicCoachId, coach.firestoreSourceId, coachCalendarReady]);
  useEffect(() => {
    if (lessonForm.slotId && !lessonSlots.some((slot) => slot.id === lessonForm.slotId)) {
      setLessonForm((current) => ({ ...current, slotId: "", startAt: "" }));
    }
  }, [lessonForm.slotId, lessonSlots]);
  const viewerCoachLessons = mergeMarketplaceLedgerRows(viewerProfile.lessonRequests || [], viewerProfile.coachBookings || [])
    .map(normaliseCoachLesson)
    .filter((lesson) => (coachUid && lesson.coachUid === coachUid)
      || (coach.publicCoachId && lesson.coachId === coach.publicCoachId)
      || (coach.firestoreSourceId && lesson.coachId === coach.firestoreSourceId))
    .sort((first, second) => (second.startAtDate?.getTime?.() || 0) - (first.startAtDate?.getTime?.() || 0));
  const acceptedViewerLesson = viewerCoachLessons.find((lesson) => ["accepted", "checkout_created"].includes(lesson.status)
    && ["requires_payment", "checkout_created", "failed"].includes(lesson.paymentStatus));
  const selectedServiceCanTakeRequests = Boolean(selectedLessonService && (lessonAmountCents === 0 || coachPaidBookingEnabled));
  const canRequestCoachLesson = Boolean(!ownerPreview && coachCalendarReady && selectedServiceCanTakeRequests && selectedLessonSlot && firebaseFunctions && firebaseAuth?.currentUser && coachUid);
  const coachLessonActionLabel = !coachCalendarReady
    ? "Ask about availability"
    : !selectedLessonService
      ? "Choose a lesson"
      : lessonAmountCents > 0 && !coachPaidBookingEnabled
        ? "Message coach"
    : !firebaseAuth?.currentUser
      ? "Sign in to request"
      : !selectedLessonSlot
        ? "Choose a calendar time"
        : !coachUid
          ? "Message coach"
          : lessonAmountCents === 0
            ? "Request free lesson"
            : "Send lesson request";
  const updateLessonForm = (field, value) => setLessonForm((current) => ({ ...current, [field]: value }));
  const payAcceptedCoachLesson = async (lesson = acceptedViewerLesson) => {
    if (!lesson) {
      setLessonCheckoutState({ status: "needs-action", message: "The coach must accept a lesson time before payment opens.", lessonRequestId: "" });
      return;
    }
    setLessonCheckoutState({ status: "loading", message: "Opening secure lesson checkout...", lessonRequestId: lesson.id });
    try {
      const checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
        kind: "coach",
        sourceId: lesson.lessonRequestId || lesson.id,
        idempotencyKey: lesson.lessonRequestId || lesson.id,
        successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href
      });
      setLessonCheckoutState({ status: "ready", message: "Secure checkout is ready for the accepted lesson.", lessonRequestId: lesson.id });
      if (checkout.checkoutUrl) window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setLessonCheckoutState({ status: "error", message: marketplaceFunctionErrorMessage(error), lessonRequestId: lesson.id });
    }
  };
  const requestCoachLesson = async () => {
    if (ownerPreview) {
      navigate("coachDashboard");
      return;
    }
    if (!coachCalendarReady) {
      messageCoach("Ask about coaching");
      return;
    }
    if (!selectedLessonService) {
      setLessonCheckoutState({ status: "needs-action", message: "Choose a lesson type first.", lessonRequestId: "" });
      return;
    }
    if (lessonAmountCents > 0 && !coachPaidBookingEnabled) {
      messageCoach("Ask about lesson payments");
      return;
    }
    if (!selectedLessonSlot) {
      setLessonCheckoutState({ status: "needs-action", message: "Choose one available calendar time.", lessonRequestId: "" });
      return;
    }
    if (!firebaseAuth?.currentUser) {
      navigate("login");
      return;
    }
    if (!coachUid || !canRequestCoachLesson) {
      messageCoach("Ask about lessons");
      return;
    }
    setLessonCheckoutState({ status: "loading", message: "Sending your lesson request to the coach...", lessonRequestId: "" });
    try {
      const idempotencyKey = stripeReferenceId("coach", `${coachUid}-${selectedLessonService.id}-${selectedLessonSlot.startAt}`);
      const lessonRequest = await callMarketplaceFunction("createCoachLessonRequest", {
        coachUid,
        coachId: coach.publicCoachId || coach.firestoreSourceId || "",
        serviceId: selectedLessonService.id,
        startAt: selectedLessonSlot.startAt,
        durationMinutes: lessonDuration,
        lessonFormat: selectedLessonService.lessonFormat,
        locationLabel: coach.locations || coachingArea,
        note: lessonForm.note,
        playerName: viewerProfile.fullName || "",
        playerEmail: viewerProfile.email || "",
        amountCents: lessonAmountCents,
        currency: coachCurrency,
        idempotencyKey
      });
      setLessonCheckoutState({
        status: "ready",
        message: lessonAmountCents
          ? lessonRequest.settlementMode === "offline_settlement"
            ? "Request sent. If the coach accepts, the lesson will be confirmed with payment due directly to the coach."
            : "Request sent. The coach must accept and reserve the time before secure payment opens."
          : "Free lesson request sent. It will appear in both calendars as soon as the coach accepts.",
        lessonRequestId: lessonRequest.lessonRequestId || ""
      });
    } catch (error) {
      setLessonCheckoutState({ status: "error", message: marketplaceFunctionErrorMessage(error), lessonRequestId: "" });
    }
  };
  const focusLessonRequest = () => {
    if (!coachCalendarReady) {
      messageCoach("Ask about coaching");
      return;
    }
    document.getElementById("coach-lesson-request")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const coachInteractionActions = ownerPreview ? [
    [Trophy, "Edit coach profile", "Update your rate, coaching offer, proof and public details.", () => navigate("coachProfile"), "primary"],
    [LayoutDashboard, "Coach dashboard", "Review lesson activity, profile readiness and business tools.", () => navigate("coachDashboard")],
    [Clock3, "Update availability", "Keep lesson windows and coaching locations accurate.", () => navigate("coachProfile")],
    [WalletCards, "Coach payouts", "Review lesson income, refunds and payout readiness.", () => navigate("coachPayouts")],
    [MessageCircle, "Coach messages", "Reply to lesson enquiries and player goals.", () => navigate("messages")]
  ] : [
    [CalendarDays, coachCalendarReady ? "Request lesson" : "Ask about lessons", coachCalendarReady ? "Choose a published lesson type and an available calendar time." : "This coach accepts messages while calendar booking is inactive.", coachCalendarReady ? focusLessonRequest : () => messageCoach("Ask about lessons"), "primary"],
    [WalletCards, acceptedViewerLesson ? "Pay accepted lesson" : "Payment after acceptance", acceptedViewerLesson ? "Complete secure checkout for the time the coach reserved." : "Paid sessions open checkout only after the coach accepts and reserves the selected time.", acceptedViewerLesson ? () => payAcceptedCoachLesson(acceptedViewerLesson) : focusLessonRequest],
    [Heart, "Save coach", "Shortlist this coach for future training.", saveCoachProfile],
    [Star, "Rate coach", "Rate the lesson after you train.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("coach", "Rate coach", coach.headline || name, coachReviewTarget) })],
    [MessageCircle, "Leave testimonial", "Recommend this coach after a good lesson.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("testimonial", "Leave coach testimonial", coach.headline || name, coachReviewTarget) })],
    [UsersRound, "Ask about clinics", "Ask whether this coach runs group sessions or clinics.", () => messageCoach("Ask about group coaching")],
    [ShieldCheck, "Report concern", "Report incorrect proof, unsafe behaviour or listing issues.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Review coach credentials", coach.headline || name, coachReviewTarget) })]
  ];
  return (
    <section className="page public-profile-page public-listing-page">
      <PublicListingHero
        variant="coach"
        eyebrow="Coach public listing"
        title={coach.headline || name}
        subtitle={[coachingArea, coachRateLabel, coach.accreditation || "Accreditation not added"].join(" - ")}
        image={heroImage}
        avatar={heroImage}
        icon={Trophy}
        facts={[coachRateLabel, coach.locations || coachingArea, coach.accreditation || "Proof pending"]}
        qualityLabel="Coach reviews"
        qualityValue={coachReviewSummary.label}
        qualityMeta={coachReviewSummary.meta}
        primaryAction={ownerPreview ? ["Edit coach profile", () => navigate("coachProfile")] : coachCalendarReady ? ["Request lesson", focusLessonRequest] : ["Message coach", () => messageCoach("Ask about lessons")]}
        secondaryAction={ownerPreview ? ["Coach dashboard", () => navigate("coachDashboard")] : acceptedViewerLesson ? ["Pay accepted lesson", () => payAcceptedCoachLesson(acceptedViewerLesson)] : ["Message coach", () => messageCoach("Request session")]}
        socialTarget={socialTarget}
        following={following}
        toggleFollowing={toggleFollowing}
        ownerPreview={ownerPreview}
        planTier={coach.planTier || (coach.proProfile ? "pro" : "")}
      />
      <PublicMediaShowcase
        title="Lesson videos, proof and testimonials"
        links={mediaLinks}
        exclude={[heroImage]}
        requireModeration
        emptyTitle="No coach media added yet"
        emptyText="Lesson clips, technique videos and testimonials help players decide whether this coach matches their goal."
      />
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [BadgeDollarSign, "Lesson prices", coachRateLabel, coachServices.length ? `${coachServices.length} active lesson type${coachServices.length === 1 ? "" : "s"}` : "Add before booking"],
            [MapPinned, "Coaches in", coach.locations || coachingArea, "Centres, suburbs or mobile coverage"],
            [Clock3, "Available", availabilityRows.length ? `${availabilityRows.length} day${availabilityRows.length === 1 ? "" : "s"}` : "Not added", availabilityRows[0]?.time || "Add public lesson times"],
            [BadgeCheck, "Proof", coach.accreditation || "Not added", "Qualifications or relevant experience"]
          ]} />
          <PublicProfileSection eyebrow="Coaching offer" title="What players can improve here">
            <p className="public-profile-copy">{coach.bio || "This coach has not added a full bio yet. A useful coach profile explains who the coach helps, how lessons run, what level they teach and what players can expect after booking."}</p>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Lesson times" title="Weekly coaching availability">
            <PublicAvailabilityPanel
              rows={availabilityRows}
              availableTitle="Available for lessons"
              emptyTitle="Lesson times not added"
              emptyText="Ask the coach about a suitable time, or check back after weekly availability is published."
            />
          </PublicProfileSection>
          {coachServices.length ? (
            <PublicProfileSection eyebrow={ownerPreview ? "Player booking preview" : "Lesson booking"} title={ownerPreview ? "Your published lessons and calendar" : "Choose a lesson and available time"}>
              <div className="coach-lesson-request-card" id="coach-lesson-request">
                {!ownerPreview && acceptedViewerLesson && (
                  <div className="coach-lesson-accepted-callout">
                    <BadgeCheck size={19} />
                    <div><strong>The coach accepted your lesson.</strong><span>{coachLessonScheduleLabel(acceptedViewerLesson)} · {acceptedViewerLesson.totalLabel}</span></div>
                    <button type="button" onClick={() => payAcceptedCoachLesson(acceptedViewerLesson)} disabled={lessonCheckoutState.status === "loading"}><WalletCards size={16} /> Pay securely</button>
                  </div>
                )}
                <div className="coach-public-service-picker" role="group" aria-label="Choose a coaching lesson">
                  {coachServices.map((service) => {
                    const priceCents = coachLessonServicePriceCents(service, coachCurrency);
                    const paidUnavailable = priceCents > 0 && !coachPaidBookingEnabled;
                    return (
                      <button
                        type="button"
                        key={service.id}
                        className={selectedLessonService?.id === service.id ? "active" : ""}
                        onClick={() => setLessonForm((current) => ({ ...current, serviceId: service.id, slotId: "", startAt: "" }))}
                        aria-pressed={selectedLessonService?.id === service.id}
                      >
                        <span>
                          <strong>{service.title}</strong>
                          <em>{coachLessonFormatLabels[service.lessonFormat] || "Lesson"} · {service.durationMinutes} min</em>
                        </span>
                        <span>
                          <strong>{coachLessonServicePriceLabel(service, coachCurrency)}</strong>
                          {paidUnavailable && <em>Enquiry only</em>}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedLessonService?.description && <p className="coach-selected-service-copy">{selectedLessonService.description}</p>}
                <div className="coach-booking-calendar">
                  <div className="coach-booking-calendar-head">
                    <div>
                      <strong>Available times</strong>
                      <span>{coach.timeZone || currentBrowserTimeZone()} · {coach.bookingNoticeHours || 0}h minimum notice{coachCalendarState.status === "loading" ? " · Checking reservations" : ""}</span>
                    </div>
                    <CalendarDays size={20} />
                  </div>
                  {coachCalendarState.message && <p className="coach-calendar-live-note">{coachCalendarState.message}</p>}
                  {lessonSlotDays.length ? (
                    <div className="coach-calendar-days">
                      {lessonSlotDays.map((day) => (
                        <section key={day.dateKey}>
                          <div><strong>{day.dayLabel}</strong><span>{day.dateLabel}</span></div>
                          <div>
                            {day.slots.map((slot) => (
                              <button
                                type="button"
                                key={slot.id}
                                className={selectedLessonSlot?.id === slot.id ? "active" : ""}
                                onClick={() => setLessonForm((current) => ({ ...current, slotId: slot.id, startAt: slot.startAt }))}
                                aria-pressed={selectedLessonSlot?.id === slot.id}
                              >{formatTimeLabel(slot.time)}</button>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="coach-calendar-empty">
                      <CalendarClock size={20} />
                      <strong>No request times in the next 14 days</strong>
                      <span>The selected lesson duration does not currently fit inside the coach's published windows.</span>
                    </div>
                  )}
                </div>
                {!ownerPreview && <label className="coach-lesson-note"><span>Training goal or note</span><textarea value={lessonForm.note} onChange={(event) => updateLessonForm("note", event.target.value)} placeholder="Example: improve doubles defence, beginner footwork or tournament preparation." /></label>}
                {!ownerPreview && lessonAmountCents > 0 && !coachPaidBookingEnabled && (
                  <div className="coach-booking-upgrade-note">
                    <CircleHelp size={18} />
                    <span>This paid lesson is visible, but online payment is not active for this coach yet. Send a message instead.</span>
                  </div>
                )}
                <div className="coach-lesson-summary">
                  <span>{selectedLessonSlot ? `${selectedLessonSlot.dayLabel} ${selectedLessonSlot.dateLabel} · ${formatTimeLabel(selectedLessonSlot.time)}` : "Choose an available time"}</span>
                  <strong>{selectedLessonService ? coachLessonServicePriceLabel(selectedLessonService, coachCurrency) : "Choose a lesson"}</strong>
                  <button type="button" onClick={ownerPreview ? () => navigate("coachProfile") : requestCoachLesson} disabled={lessonCheckoutState.status === "loading"}>
                    {ownerPreview ? <Pencil size={17} /> : <Send size={17} />}
                    {ownerPreview ? "Edit lessons and calendar" : lessonCheckoutState.status === "loading" ? "Sending request" : coachLessonActionLabel}
                  </button>
                </div>
                {!ownerPreview && lessonCheckoutState.message && <div className={`auth-message ${lessonCheckoutState.status}`}>{lessonCheckoutState.message}</div>}
              </div>
            </PublicProfileSection>
          ) : (
            <PublicProfileSection eyebrow={ownerPreview ? "Booking setup" : "Coaching enquiry"} title={ownerPreview ? "Add lessons before calendar booking can open" : "Message this coach about lessons"}>
              <div className="public-empty-panel">
                {ownerPreview ? <Banknote size={20} /> : <MessageCircle size={20} />}
                <strong>{ownerPreview ? "No active lesson types or per-session prices." : "Online calendar booking is not active yet."}</strong>
                <span>{ownerPreview ? "Add a private lesson, group lesson or clinic with duration and price, then keep weekly availability current." : "Ask about coaching goals, locations, availability and rates directly through Badminta messages."}</span>
                <button type="button" onClick={ownerPreview ? () => navigate("coachProfile") : () => messageCoach("Ask about lessons")}>{ownerPreview ? "Add lessons and prices" : "Message coach"}</button>
              </div>
            </PublicProfileSection>
          )}
          <PublicInteractionGrid
            eyebrow="Coach interactions"
            title={ownerPreview ? "Manage your coaching business" : "Book, ask, review or save this coach"}
            text={ownerPreview ? "These controls are shown because this is your published coach profile." : "Use these actions when the coach's rate, location and style fit what you want to improve."}
            actions={coachInteractionActions}
          />
          <PublicProfileSection eyebrow="Public links" title="Coach socials and booking links">
            <PublicLinkList links={socialLinks} emptyText="No coach social links added yet." />
          </PublicProfileSection>
        </main>
        <PublicActionPanel
          title={ownerPreview ? "Coach controls" : "Coach enquiry"}
          value={ownerPreview ? (coachCalendarReady ? "Calendar ready" : "Setup needed") : coachRateLabel}
          caption={ownerPreview ? "Keep your offer, proof and availability current." : "Check price, location and availability before requesting a lesson."}
          rows={[
            [CalendarDays, "Availability", availabilityCompactLabel],
            [MapPin, "Location", coach.locations || coachingArea],
            [Video, "Media", publicMediaLinks.length ? `${publicMediaLinks.length} approved` : hiddenMediaCount ? "Pending review" : "Not added"]
          ]}
          primary={ownerPreview ? ["Edit coach profile", () => navigate("coachProfile")] : ["Message coach", () => messageCoach("Message coach")]}
          secondary={ownerPreview ? ["Coach dashboard", () => navigate("coachDashboard")] : acceptedViewerLesson ? ["Pay accepted lesson", () => payAcceptedCoachLesson(acceptedViewerLesson)] : [coachCalendarReady ? "Request lesson" : "Ask about lessons", coachCalendarReady ? focusLessonRequest : () => messageCoach("Ask about lessons")]}
          note={ownerPreview ? "Only you see these coach management actions." : "Paid lessons should only proceed after the coach confirms the requested time."}
        />
      </div>
      <PublicCreateCta
        title={ownerPreview ? "Keep your coaching profile ready for enquiries." : "Create a coach profile that brings real lesson enquiries."}
        text={ownerPreview ? "Update your rate, availability, locations and proof whenever your coaching offer changes." : "Show your rate, coaching areas, proof, videos and availability so players can decide quickly and book with confidence."}
        button={ownerPreview ? ["Edit your coach profile", "coachProfile"] : ["Create your coach listing", "coachProfile"]}
        navigate={navigate}
      />
    </section>
  );
}

function PublicGroupListing({ accountProfile, viewerProfile = defaultAccountProfile, navigate, saveAccountItem, saveAccountProfile, following = [], toggleFollowing = null, publicReviews = [] }) {
  const group = accountProfile.group || defaultAccountProfile.group;
  const groupDescription = plainPublicNarrative(group.description);
  const venue = group.venueId ? venues.find((item) => item.id === group.venueId) : null;
  const venuePoint = venue ? getVenueMediaPoint(venue) : null;
  const groupWhen = getGroupWhenLabel(group);
  const priceLabel = getGroupPriceLabel(group) || "Price not added";
  const groupLinks = cleanLinkRows(group.socialLinks, "Group link");
  const heroImage = getPublicProfileImage(group.coverUrl, group.logoUrl);
  const groupSessions = getGroupPublicSessions(group);
  const hasUpcomingGroupSession = groupSessions.length > 0;
  const groupEvents = getOwnerEvents(accountProfile, "organiser");
  const pendingVenue = group.pendingVenue || null;
  const pendingVenueLocation = pendingVenue?.suburb || pendingVenue?.city || group.suburb || group.city || "";
  const groupSubject = group.name || "Social group";
  const groupReviewTarget = getAccountPublicReviewTarget(accountProfile, "group", groupSubject, group.city || group.location?.city || "Melbourne");
  const groupReviewSummary = publicReviewSummary(accountProfile, ["group"], groupSubject, { publicReviews, target: groupReviewTarget });
  const ownerPreview = viewerOwnsPublicProfile(accountProfile, viewerProfile, "group");
  const socialTarget = socialTargetFromPublicProfile(accountProfile, "group");
  const memberCount = getGroupMemberCount(group, viewerProfile);
  const membershipActive = viewerHasActiveGroupMembership(viewerProfile, group);
  const membershipLabel = groupMembershipPriceLabel(group);
  const groupProToolsReady = group.proProfile === true || String(group.planTier || "").toLowerCase() === "pro";
  const paidMembershipNeedsUpgrade = String(group.membershipMode || "").toLowerCase() === "paid" && !groupProToolsReady;
  const openGroupMembership = () => {
    if (paidMembershipNeedsUpgrade) {
      messageOrganiser("Ask when paid membership will open");
      return;
    }
    navigate("groupMembership", { group });
  };
  const groupMediaLinks = [
    ...mediaLinksFromImages([group.coverUrl, group.logoUrl], "Group image"),
    ...cleanLinkRows(group.mediaLinks, "Group media")
  ];
  const additionalGroupMediaLinks = publicMediaWithout(groupMediaLinks, [heroImage, group.logoUrl]);
  const publicGroupMediaLinks = additionalGroupMediaLinks.filter(isApprovedPublicMediaLink);
  const hiddenGroupMediaCount = pendingMediaReviewCount(additionalGroupMediaLinks);
  const messageOrganiser = (intent = "Group enquiry") => openPublicMessageThread({
    accountProfile: viewerProfile,
    saveAccountProfile,
    navigate,
    roleId: "organiser",
    targetCollection: groupReviewTarget.targetCollection,
    targetId: groupReviewTarget.targetId,
    subject: groupSubject,
    threadType: "Group enquiry",
    text: `${intent} started from ${groupSubject}'s public group profile. Confirm session level, venue, price, capacity, guest rules, equipment and waitlist status before joining.`,
    meta: [group.venue, groupWhen, priceLabel],
    context: [["Group", groupSubject], ["Venue", group.venue || "Pending"], ["When", groupWhen], ["Price", priceLabel]],
    actions: [["View public group", "publicGroupProfile"], ["Browse social groups", "groupsIndex"], ["Browse events", "events"]]
  });
  const nextJoinableGroup = hasUpcomingGroupSession ? groupForPublicSession(group, groupSessions[0]) : group;
  const nextSessionIsPaid = Number(nextJoinableGroup.priceCents || nextJoinableGroup.amountCents || 0) > 0
    || Number.parseFloat(String(nextJoinableGroup.sessionPrice || nextJoinableGroup.price || "0").replace(/[^0-9.]/g, "")) > 0;
  const openNextSession = () => {
    if (nextSessionIsPaid && !groupProToolsReady) {
      messageOrganiser("Ask when paid session checkout will open");
      return;
    }
    navigate("join", { group: nextJoinableGroup, bookingType: "social" });
  };
  const groupInteractionActions = ownerPreview ? [
    [CalendarDays, "Create session", "Publish the next dated session from this group's regular schedule.", () => navigate("createGroupSession"), "primary"],
    [ClipboardCheck, "Manage sessions", "Review upcoming sessions, prices, capacity and publishing status.", () => navigate("groupSessions")],
    [UsersRound, "Manage members", "Handle invitations, attendance, substitutions and repeat players.", () => navigate("members")],
    [WalletCards, "Payouts", "Review collected session fees, refunds and organiser payouts.", () => navigate("payouts")],
    [Star, "Manage events", "Create tournaments, clinics or larger public events.", () => navigate("eventManager")],
    [MessageCircle, "Organiser messages", "Reply to player questions and session enquiries.", () => navigate("messages")]
  ] : [
    [UsersRound, membershipActive ? "Membership active" : "Join group", membershipActive ? "Open your active group membership." : membershipLabel, openGroupMembership, "primary"],
    ...(hasUpcomingGroupSession ? [
      [CalendarDays, nextSessionIsPaid && !groupProToolsReady ? "Ask about next session" : "Join next session", nextSessionIsPaid && !groupProToolsReady ? "Paid checkout is not active for this group yet." : "Reserve a place in the next listed game.", openNextSession],
      [WalletCards, nextSessionIsPaid && !groupProToolsReady ? "Payment not open" : "Pay session fee", nextSessionIsPaid && !groupProToolsReady ? "Message the organiser before attending." : "Pay the organiser's listed session price through Badminta.", openNextSession]
    ] : [
      [CalendarDays, "Ask about the next session", "This organiser has not published another playable session yet.", () => messageOrganiser("Ask about the next session")]
    ]),
    [Star, "View events", "See tournaments, clinics or larger sessions from this organiser.", () => navigate("events")],
    [Mail, "Ask about guests", "Ask whether you can bring a friend or join the waitlist.", () => messageOrganiser("Ask about guests")],
    [MessageCircle, "Message organiser", "Ask about level, venue, equipment or availability.", () => messageOrganiser("Message organiser")],
    [BadgeCheck, "Rate group", "Rate organisation, fairness and level fit after attending.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("group", "Rate social group", group.name || "Social group", groupReviewTarget) })],
    [ShieldCheck, "Report session", "Report no-shows, unsafe play, wrong level or payment issues.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report social session", group.name || "Social group", groupReviewTarget) })]
  ];
  return (
    <section className="page public-profile-page public-listing-page public-group-profile-page">
      <PublicListingHero
        variant="group"
        eyebrow="Social group public listing"
        title={group.name || "Social badminton group"}
        subtitle={[group.venue || "Venue not selected", groupWhen, group.skillBand || "Skill band not added"].join(" - ")}
        image={heroImage}
        avatar={group.logoUrl}
        icon={UsersRound}
        facts={[group.format || "Format pending", memberCount ? `${memberCount} members` : "New group", membershipLabel]}
        qualityLabel="Group reviews"
        qualityValue={groupReviewSummary.label}
        qualityMeta={groupReviewSummary.meta}
        primaryAction={ownerPreview
          ? ["Create session", () => navigate("createGroupSession")]
          : [membershipActive ? "Membership active" : "Join group", openGroupMembership]}
        secondaryAction={ownerPreview
          ? ["Edit group profile", () => navigate("createGroup")]
          : hasUpcomingGroupSession
            ? [nextSessionIsPaid && !groupProToolsReady ? "Ask about next session" : "Join next session", openNextSession]
            : ["Ask about next session", () => messageOrganiser("Ask about the next session")]}
        socialTarget={socialTarget}
        following={following}
        toggleFollowing={toggleFollowing}
        ownerPreview={ownerPreview}
        planTier={group.planTier || (group.proProfile ? "pro" : "")}
      />
      <PublicMediaShowcase
        title="Session photos and group proof"
        links={groupMediaLinks}
        exclude={[heroImage, group.logoUrl]}
        requireModeration
        emptyTitle="No group media added yet"
        emptyText="Photos and clips help players understand the venue, atmosphere and level before joining."
      />
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [CalendarDays, "When", groupWhen, group.isRecurring === false ? "Regular schedule" : "Weekly recurring"],
            [MapPin, "Venue", group.venue || "Not selected", "Where to play"],
            [Gauge, "Level", group.skillBand || "Not added", "Who this session suits"],
            [CircleDollarSign, "Session price", priceLabel, "Per player"],
            [UsersRound, "Membership", membershipLabel, memberCount ? `${memberCount} members` : "Open to new members"]
          ]} />
          <PublicProfileSection title={`Why play with ${group.name || "this group"}`}>
            <blockquote className={`public-player-quote public-group-description${groupDescription ? "" : " muted"}`}>
              <p>{groupDescription || "This organiser has not added the full session description yet. A useful group page explains the level, rotation style, what to bring, payment rules and what happens if the session fills."}</p>
            </blockquote>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Group events" title="Public events from this organiser">
            <PublicEventStrip
              events={groupEvents}
              navigate={navigate}
              emptyTitle="No public organiser events yet"
              emptyText="Tournaments, clinics, open days and larger social events created by this organiser will appear here."
              accountProfile={viewerProfile}
            />
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Sessions and members" title="Upcoming sessions and joined players">
            <PublicSessionBoard sessions={groupSessions} group={group} navigate={navigate} accountProfile={viewerProfile} onMessageOrganiser={() => messageOrganiser("Message organiser")} ownerPreview={ownerPreview} />
          </PublicProfileSection>
          <PublicInteractionGrid
            eyebrow="Group interactions"
            title={ownerPreview ? "Run this group" : "Join the group or reserve a session"}
            text={ownerPreview ? "These organiser controls are shown because this is your published group." : "Membership and session attendance stay separate so every payment and booking is clear."}
            actions={groupInteractionActions}
          />
          <PublicProfileSection eyebrow="Session flow" title="Before you join">
            <div className="public-flow-list">
              {[
                ["1", "Check the level", group.skillBand || "Skill band still needs to be added."],
                ["2", "Reserve a spot", group.capacity ? `${group.capacity} player capacity with price shown before joining.` : "Capacity still needs to be added."],
                ["3", "Go to the venue", group.venue || "Venue still needs to be selected."],
                ["4", "Play and review", "After attending, players can rate the session and recommend the organiser."]
              ].map(([step, title, text]) => (
                <article key={step}>
                  <span>{step}</span>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Venue context" title="Where the group plays">
            {venue && venuePoint ? (
              <div className="public-venue-preview">
                <VenueMapEmbed venue={venue} point={venuePoint} className="public-venue-map" />
                <div>
                  <strong>{venue.name}</strong>
                  <span>{getVenueAddressLabel(venue)}</span>
                  <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}>Open centre listing</a>
                </div>
              </div>
            ) : pendingVenue || group.venue ? (
              <div className="public-empty-panel">
                <MapPinned size={20} />
                <strong>{pendingVenue?.name || group.venue}</strong>
                <span>{[pendingVenueLocation, pendingVenue?.country || ""].filter(Boolean).join(" · ") || "Pending centre location added by the organiser."}</span>
                <button type="button" onClick={() => navigate("centreClaimSearch")}>Help verify centre</button>
              </div>
            ) : (
              <div className="public-empty-panel">
                <MapPinned size={20} />
                <strong>Venue not ready yet</strong>
                <span>Add a centre name and suburb or city so players can see where the group happens before they join.</span>
              </div>
            )}
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Public links" title="Group socials and community links">
            <PublicLinkList links={groupLinks} emptyText="No group links added yet." />
          </PublicProfileSection>
        </main>
        <PublicActionPanel
          title={ownerPreview ? "Organiser controls" : "Join card"}
          value={ownerPreview
            ? groupSessions.length ? `${groupSessions.length} dated` : "No dated sessions"
            : membershipLabel}
          caption={ownerPreview ? "Manage sessions without changing the reusable group profile." : group.venue || "Venue not selected yet"}
          rows={[
            [UsersRound, ownerPreview ? "Capacity" : "Members", ownerPreview ? group.capacity ? `${group.capacity} spots` : "Not added" : memberCount ? `${memberCount} active` : "New group"],
            [Gauge, "Level", group.skillBand || "Not added"],
            [Clock3, "Time", groupWhen],
            [CircleDollarSign, "Session fee", priceLabel]
          ]}
          primary={ownerPreview
            ? ["Create session", () => navigate("createGroupSession")]
            : [membershipActive ? "Membership active" : "Join group", openGroupMembership]}
          secondary={ownerPreview
            ? ["Manage sessions", () => navigate("groupSessions")]
            : hasUpcomingGroupSession
              ? [nextSessionIsPaid && !groupProToolsReady ? "Ask about next session" : "Join next session", openNextSession]
              : ["Message organiser", () => messageOrganiser("Message organiser")]}
          note={ownerPreview ? "Only you see these organiser controls." : "Membership does not reserve a place in a dated session. Join each session separately when you want to play."}
          links={groupLinks}
        />
      </div>
      <PublicCreateCta
        title={ownerPreview ? "Keep your group and sessions current." : "Create a group page that fills the session."}
        text={ownerPreview ? "Use the group profile for reusable details and create dated sessions when players can join and pay." : "Show the venue, level, time, price, capacity and rules so players can join without sending endless messages."}
        button={ownerPreview ? ["Manage sessions", "groupSessions"] : ["Create your social group", "createGroup"]}
        navigate={navigate}
      />
    </section>
  );
}

function PublicCentreListing({ accountProfile, viewerProfile = defaultAccountProfile, navigate, saveAccountItem, saveAccountProfile, following = [], toggleFollowing = null, publicReviews = [] }) {
  const centre = accountProfile.centre || defaultAccountProfile.centre;
  const [courtCalendar, setCourtCalendar] = useState({ status: "idle", message: "", days: [], timeZone: "", courtCount: 0 });
  const matchedVenue = getMatchedPublicCentre(centre);
  const publicBookableVenue = managedVenueFromCentreProfile(centre, matchedVenue || {});
  const platformCentreBookingState = getPlatformCentreBookingState(publicBookableVenue, accountProfile);
  const centreBookingReadiness = platformCentreBookingState.readiness;
  const centreBookingReady = platformCentreBookingState.ready;
  const centreOwnerUid = String(accountProfile.uid || accountProfile.ownerUid || centre.ownerUid || centre.centreOwnerUid || "").trim();
  const viewerUid = String(viewerProfile.uid || viewerProfile.ownerUid || firebaseAuth?.currentUser?.uid || "").trim();
  const ownerPreview = accountProfile === viewerProfile || Boolean(centreOwnerUid && viewerUid && centreOwnerUid === viewerUid);
  const ownerCentreSubscription = accountProfileSubscription(viewerProfile, "centre");
  const ownerCentreProTools = ownerCentreSubscription.entitlements?.inPlatformBookings === true;
  const centreSubscriptionSummary = accountProfile.profileSubscriptions?.centre;
  const centrePublicSubscriptionStatus = String(centre.subscriptionStatus || "").toLowerCase();
  const centreProActive = centreSubscriptionSummary && Object.keys(centreSubscriptionSummary).length
    ? accountProfileSubscription(accountProfile, "centre").active
    : centre.proProfile === true && ["active", "trialing"].includes(centrePublicSubscriptionStatus);
  const bookingSetupTarget = getPublicProfileMissingFields(accountProfile, "centre").length
    ? "claimCentre"
    : ownerCentreProTools
      ? "courtManager"
      : "centreDashboard";
  const socialTarget = socialTargetFromPublicProfile(accountProfile, "centre");
  const venuePoint = getVenueMediaPoint(publicBookableVenue) || (matchedVenue ? getVenueMediaPoint(matchedVenue) : null);
  const centreSocialLinks = cleanLinkRows(centre.socialLinks, "Centre social");
  const centrePhotoLinks = cleanLinkRows(centre.photoLinks, "Centre media");
  const publicCentrePhotoLinks = centrePhotoLinks.filter(isApprovedPublicMediaLink);
  const hiddenCentreMediaCount = pendingMediaReviewCount(centrePhotoLinks);
  const officialLinks = [...centreSocialLinks, ...(centre.website ? [{ label: "Website", url: centre.website }] : [])];
  const heroImage = getPublicProfileImage(centre.coverUrl, publicCentrePhotoLinks, matchedVenue?.gallery, matchedVenue?.image, centre.logoUrl);
  const centreEvents = getOwnerEvents(accountProfile, "centre");
  const centreSubject = centre.name || matchedVenue?.name || "Badminton centre";
  const centreReviewTarget = getAccountPublicReviewTarget(accountProfile, "centre", centreSubject, centre.city || centre.location?.city || matchedVenue?.city || "Melbourne", matchedVenue?.id || "");
  const centreReviewSummary = publicReviewSummary(accountProfile, ["centre"], centreSubject, { fallback: reviewSummaryFromVenue(matchedVenue), publicReviews, target: centreReviewTarget });
  const centreCurrency = normaliseCurrencyCode(centre.currency || publicBookableVenue.currency || marketplaceCurrencyForCountry(centre.countryCode || centre.location?.countryCode || publicBookableVenue.countryCode));
  const courtPricingSummary = getCourtPricingSummary(centre.courtPricing, centre.pricing, centreCurrency);
  const centreHours = summarizeAvailabilitySchedule(centre.openingSchedule) || centre.openingHours || "";
  const centreHourRows = getPublicAvailabilityRows(centre.openingSchedule, centre.openingHours);
  const centreHoursCompactLabel = centreHourRows.length
    ? `${centreHourRows.length} weekly day${centreHourRows.length === 1 ? "" : "s"}${centreHourRows[0]?.time ? ` · ${centreHourRows[0].time}` : ""}`
    : "Not added";
  const centreArea = publicProfileAreaLabel(centre, "Location not added");
  const centreBackendId = getCentreBackendSourceId(centre)
    || publicBookableVenue.serverCentreId
    || publicBookableVenue.firestoreSourceId
    || publicBookableVenue.claimedVenueId
    || "";
  useEffect(() => {
    let cancelled = false;
    if (!centreBookingReady || !centreBackendId) {
      setCourtCalendar({ status: "idle", message: "", days: [], timeZone: centre.timeZone || "", courtCount: 0 });
      return undefined;
    }
    setCourtCalendar((current) => ({ ...current, status: "loading", message: "" }));
    callProfileFunctionEndpoint("getCentreCalendarAvailability", { centreId: centreBackendId })
      .then((result) => {
        if (cancelled) return;
        setCourtCalendar({
          status: "ready",
          message: "",
          days: Array.isArray(result.days) ? result.days : [],
          timeZone: result.timeZone || centre.timeZone || "",
          courtCount: Number(result.courtCount || 0)
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setCourtCalendar({
          status: "error",
          message: marketplaceFunctionErrorMessage(error) || "Live court availability could not be loaded.",
          days: [],
          timeZone: centre.timeZone || "",
          courtCount: 0
        });
      });
    return () => {
      cancelled = true;
    };
  }, [centreBackendId, centreBookingReady, centre.timeZone]);
  const centreMembershipProducts = getCentreMembershipProducts(centre);
  const centreMediaLinks = centrePhotoLinks.length
    ? centrePhotoLinks
    : mediaLinksFromImages([matchedVenue?.gallery, matchedVenue?.image], "Centre photo");
  const saveCentreProfile = () => {
    saveAccountItem?.(savedItemFromPublicProfile(accountProfile, "centre"));
    navigate("saved");
  };
  const messageCentre = (intent = "Centre enquiry") => openPublicMessageThread({
    accountProfile: viewerProfile,
    saveAccountProfile,
    navigate,
    roleId: "centre",
    targetCollection: centreReviewTarget.targetCollection,
    targetId: centreReviewTarget.targetId,
    subject: centreSubject,
    threadType: "Centre enquiry",
    text: `${intent} started from ${centreSubject}'s public centre profile. Confirm court inventory, pricing, hours, booking rules, payment status and contact details before the player attends.`,
    meta: [centre.address || centreArea, centre.courtCount ? `${centre.courtCount} courts` : "", courtPricingSummary || centre.pricing, centreHoursCompactLabel],
    context: [["Centre", centreSubject], ["Address", centre.address || centre.suburb || "Pending"], ["Courts", centre.courtCount || "Pending"], ["Pricing", courtPricingSummary || centre.pricing || "Pending"]],
    actions: [["View centre profile", "publicCentreProfile"], ["Find centres", "results"], ["Browse events", "events"]]
  });
  const openCourtBooking = () => {
    if (ownerPreview) {
      navigate(bookingSetupTarget, { venue: platformCentreBookingState.venue });
      return;
    }
    if (centreBookingReady) {
      navigate("booking", { venue: platformCentreBookingState.venue, bookingType: "court" });
      return;
    }
    messageCentre("Ask when court bookings will open");
  };
  const openCourtBookingSlot = (slot) => {
    if (ownerPreview) {
      navigate("courtManager", { venue: platformCentreBookingState.venue });
      return;
    }
    navigate("booking", {
      venue: platformCentreBookingState.venue,
      bookingType: "court",
      bookingDraft: { date: slot.date, time: slot.time, durationHours: 1 }
    });
  };
  const centreBookingActionLabel = ownerPreview
    ? ownerCentreProTools ? "Manage courts" : "View Centre Pro"
    : centreBookingReady ? "Book court" : "Ask about bookings";
  const bookingReadinessRows = [
    ["Centre details", Boolean(centre.name && centre.address && hasPublicProfileLocation(centre))],
    ["Court inventory", Boolean(centreBookingReadiness.publishedCourtCount || centreBookingReadiness.courtCount)],
    ["Fee schedule", hasCourtPricing(centreBookingReadiness.courtPricing)],
    ["Opening hours", Boolean(centreBookingReadiness.openingHours)]
  ];
  const centreInteractionActions = ownerPreview ? ownerCentreProTools ? [
    [Grid3X3, "Manage courts", "Update court inventory, availability, booking rules and fee schedules.", () => navigate("courtManager", { venue: platformCentreBookingState.venue }), "primary"],
    [Building2, "Edit centre profile", "Update address, contact details, photos, hours and public information.", () => navigate("claimCentre", { venue: platformCentreBookingState.venue })],
    [LayoutDashboard, "Centre dashboard", "Review centre readiness, bookings, events and operational status.", () => navigate("centreDashboard")],
    [WalletCards, "Centre payments", "Review court payments, refunds and payout records.", () => navigate("centrePayments")],
    [Star, "Manage events", "Create and manage tournaments, clinics and open days.", () => navigate("eventManager")],
    [MessageCircle, "Centre messages", "Reply to player booking and venue questions.", () => navigate("messages")]
  ] : [
    [ShieldCheck, "View Centre Pro", "Unlock court bookings, pricing automation, memberships, events and payments.", () => navigate("centreDashboard"), "primary"],
    [Building2, "Edit free listing", "Update address, contact details, photos, hours and public information.", () => navigate("claimCentre", { venue: platformCentreBookingState.venue })],
    [LayoutDashboard, "Centre dashboard", "Review listing quality and compare Centre Pro operating tools.", () => navigate("centreDashboard")],
    [MessageCircle, "Centre messages", "Reply to public venue enquiries.", () => navigate("messages")]
  ] : [
    [CalendarDays, centreBookingReady ? "Book court" : "Booking not ready", centreBookingReady ? "Choose an available court, date and time, then confirm the booking." : "Courts, hours and pricing need to be added first.", openCourtBooking, "primary"],
    [WalletCards, "Pay court fee", centreBookingReady ? "Continue to the booking and payment flow." : "Payments unlock after setup is complete.", openCourtBooking],
    [Heart, "Save centre", "Keep this centre in your saved list.", saveCentreProfile],
    [Star, "See centre events", "Browse public tournaments, clinics, open days or promotions.", () => navigate("events")],
    [UsersRound, "Find socials here", "Browse groups and organised sessions near this venue.", () => navigate("groupsIndex")],
    [Trophy, "Find coaching", "Look for coaches connected to this area.", () => navigate("playersIndex", { activeFilter: "coaching" })],
    [MessageCircle, "Message centre", "Ask about availability, court hire or booking rules.", () => messageCentre("Message centre")],
    [BadgeCheck, "Review centre", "Rate courts, lighting, parking, staff and value.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("centre", "Review centre", centre.name || matchedVenue?.name || "Badminton centre", centreReviewTarget) })],
    [ShieldCheck, "Claim listing", "Search for this directory listing and submit an ownership claim.", () => navigate("centreClaimSearch", { venue: publicBookableVenue })],
    [CircleHelp, "Report details", "Report incorrect centre details or safety concerns.", () => navigate("reviewFlow", { reviewContext: reviewContextFor("trust", "Report centre information", centre.name || matchedVenue?.name || "Badminton centre", centreReviewTarget) })]
  ];
  return (
    <section className="page public-profile-page public-listing-page">
      <PublicListingHero
        variant="centre"
        eyebrow="Centre public listing"
        title={centre.name || "Badminton centre"}
        subtitle={[centre.address || "Street address not added", centreArea, centre.courtCount ? `${centre.courtCount} courts` : "Court count not added"].join(" - ")}
        image={heroImage}
        avatar={centre.logoUrl}
        icon={Building2}
        facts={[centre.courtCount ? `${centre.courtCount} courts` : "Courts pending", courtPricingSummary || centre.pricing || "Pricing pending", centreHours ? "Hours added" : "Hours pending"]}
        qualityLabel="Centre reviews"
        qualityValue={centreReviewSummary.label}
        qualityMeta={centreReviewSummary.meta}
        primaryAction={[centreBookingActionLabel, openCourtBooking]}
        secondaryAction={ownerPreview ? ["Centre dashboard", () => navigate("centreDashboard")] : ["Save centre", saveCentreProfile]}
        socialTarget={socialTarget}
        following={following}
        toggleFollowing={toggleFollowing}
        ownerPreview={ownerPreview}
        fallbackVenue={publicBookableVenue}
        planTier={centreProActive ? "pro" : "free"}
      />
      {ownerPreview && !ownerCentreProTools && (
        <section className="centre-public-upgrade-preview">
          <div className="centre-public-upgrade-copy">
            <span><Sparkles size={16} /> Centre Free is live</span>
            <h2>Keep the public listing free. Unlock operations when you are ready.</h2>
            <p>Players can discover, follow and contact this centre now. Centre Pro adds the booking calendar, court inventory controls, peak and off-peak pricing, memberships, events, checkout, refunds and payout records.</p>
          </div>
          <ProfileSubscriptionPanel
            role="centre"
            callFunction={callMarketplaceFunction}
            signedIn={Boolean(firebaseAuth?.currentUser)}
          />
        </section>
      )}
      <PublicMediaShowcase
        title="Court photos and venue media"
        links={centreMediaLinks}
        exclude={[heroImage, centre.logoUrl]}
        requireModeration={!ownerPreview}
        emptyTitle="No centre media added yet"
        emptyText="Court photos and walkthroughs help players decide whether the venue is worth booking."
      />
      <div className="public-profile-layout">
        <main className="public-profile-main">
          <PublicStatGrid items={[
            [Grid3X3, "Courts", centre.courtCount || "Not added", "How many courts players can use"],
            [BadgeDollarSign, "Court fees", courtPricingSummary || centre.pricing || "Not added", "Peak and off-peak price"],
            [Clock3, "Hours", centreHourRows.length ? `${centreHourRows.length} day${centreHourRows.length === 1 ? "" : "s"}` : centreHours || "Not added", centreHourRows[0]?.time || "When players can attend"],
            [Phone, "Contact", centre.phone || centre.contactEmail || "Not added", "For booking questions"]
          ]} />
          <PublicProfileSection eyebrow="Centre details" title="Before players book or travel">
            <p className="public-profile-copy">{centre.description || (centre.address ? `${centre.name || "This centre"} is listed at ${centre.address}. Players should check price, hours and booking rules before travelling.` : "Add the full address, court details, pricing, opening hours and booking rules so players can decide without calling first.")}</p>
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Business hours" title="When the centre is open">
            <PublicAvailabilityPanel
              rows={centreHourRows}
              availableTitle="Centre opening hours"
              emptyTitle="Opening hours not added"
              emptyText="Contact the centre before travelling until weekly opening hours are published."
            />
          </PublicProfileSection>
          {centreBookingReady && (
            <PublicProfileSection eyebrow="Book a court" title="Choose an available date and time">
              <PublicCourtAvailabilityCalendar
                days={courtCalendar.days}
                status={courtCalendar.status}
                message={courtCalendar.message}
                timeZone={courtCalendar.timeZone}
                ownerPreview={ownerPreview}
                onSelectSlot={openCourtBookingSlot}
                onManage={() => navigate("courtManager", { venue: platformCentreBookingState.venue })}
              />
            </PublicProfileSection>
          )}
          <PublicProfileSection eyebrow="Court fees" title="Peak, off-peak and weekend pricing">
            {hasCourtPricing(centre.courtPricing) ? (
              <div className="court-pricing-summary-list public">
                {courtPricingRows(centre.courtPricing).map((row) => (
                  <div key={row.key}>
                    <strong>{row.label}</strong>
                    <span>{formatCourtFeeValue(row.fee, centreCurrency) || "Not set"}</span>
                    <em>{row.window}</em>
                  </div>
                ))}
              </div>
            ) : (
              <div className="public-empty-panel">
                <CircleDollarSign size={20} />
                <strong>Court fee schedule not added yet</strong>
                <span>Add standard, off-peak, peak and weekend pricing so players know the cost before requesting a court.</span>
              </div>
            )}
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Court booking" title={centreBookingReady ? "Players can book a court here" : "Bookings are not open yet"}>
            <div className={`centre-booking-readiness ${centreBookingReady ? "ready" : ""}`}>
              <div>
                <CalendarDays size={22} />
                <strong>{centreBookingReady ? "Court inventory, prices and opening hours are ready for online booking." : "Finish courts, hours and prices before players can book here."}</strong>
                <span>{centreBookingReady ? "Choose a court, date and time without needing to call first." : `Missing: ${centreBookingReadiness.missing.join(", ")}.`}</span>
              </div>
              <div className="centre-booking-checks">
                {bookingReadinessRows.map(([label, complete]) => (
                  <span key={label} className={complete ? "complete" : ""}>
                    {complete ? <Check size={15} /> : <CircleHelp size={15} />}
                    {label}
                  </span>
                ))}
              </div>
              <button type="button" onClick={openCourtBooking}>{ownerPreview ? "Manage court booking setup" : centreBookingReady ? "Book a court" : "Ask about court bookings"}</button>
            </div>
          </PublicProfileSection>
          <PublicCentreMemberships
            centreId={centreBackendId}
            centreName={centreSubject}
            seedProducts={centreMembershipProducts}
            viewerProfile={viewerProfile}
            ownerPreview={ownerPreview}
            navigate={navigate}
          />
          <PublicProfileSection eyebrow="Centre events" title="Public events at this centre">
            <PublicEventStrip
              events={centreEvents}
              navigate={navigate}
              emptyTitle="No public centre events yet"
              emptyText="Centre-run tournaments, open days, junior programs and promotions will appear here once published."
              accountProfile={viewerProfile}
            />
          </PublicProfileSection>
          <PublicInteractionGrid
            eyebrow="Centre interactions"
            title={ownerPreview ? "Run this centre" : "Book, save, ask or review this centre"}
            text={ownerPreview ? "These centre controls are shown because this is your published centre profile." : "Use these actions when the centre details, price and location fit what you need."}
            actions={centreInteractionActions}
          />
          <PublicProfileSection eyebrow="Location" title="Map and venue context">
            {venuePoint ? (
              <div className="public-venue-preview">
                <VenueMapEmbed venue={publicBookableVenue} point={venuePoint} className="public-venue-map" />
                <div>
                  <strong>{publicBookableVenue.name}</strong>
                  <span>{getVenueAddressLabel(publicBookableVenue)}</span>
                </div>
              </div>
            ) : (
              <div className="public-empty-panel">
                <MapPinned size={20} />
                <strong>Map match not ready yet</strong>
                <span>Add a complete street address so this centre can be matched to the directory map.</span>
              </div>
            )}
          </PublicProfileSection>
          <PublicProfileSection eyebrow="Official links" title="Website, socials and booking links">
            <PublicLinkList links={officialLinks} emptyText="No centre links added yet." />
          </PublicProfileSection>
        </main>
        <PublicActionPanel
          title={ownerPreview ? "Centre controls" : "Booking card"}
          value={ownerPreview ? "Owner view" : courtPricingSummary || centre.pricing || "Pricing pending"}
          caption={ownerPreview ? "Manage public details and court booking readiness." : centre.address || centre.suburb || "Location not added yet"}
          rows={[
            [CalendarDays, "Hours", centreHoursCompactLabel],
            [Grid3X3, "Courts", centre.courtCount || "Not added"],
            [WalletCards, "Platform booking", centreBookingReady ? "Ready" : "Setup needed"],
            [Video, "Media", publicCentrePhotoLinks.length ? `${publicCentrePhotoLinks.length} approved` : hiddenCentreMediaCount ? "Pending review" : "Not added"]
          ]}
          primary={[centreBookingActionLabel, openCourtBooking]}
          secondary={ownerPreview ? ["Centre dashboard", () => navigate("centreDashboard")] : ["Message centre", () => messageCentre("Message centre")]}
          note={ownerPreview ? "Only you see these centre management actions." : centreBookingReady ? "Booking uses this centre's court inventory, hours and fee schedule." : `Bookings stay disabled until ${centreBookingReadiness.missing.join(", ")} are complete.`}
          links={officialLinks}
        />
      </div>
      <PublicCreateCta
        title={ownerPreview ? "Keep your centre ready for bookings." : "Create a centre page that turns searches into bookings."}
        text={ownerPreview ? "Update courts, prices, hours and media whenever your centre operations change." : "Show courts, photos, map, prices, hours and contact details so players can choose your venue faster."}
        button={ownerPreview ? ["Manage your centre", "centreDashboard"] : ["List your centre", "claimCentre"]}
        navigate={navigate}
      />
    </section>
  );
}

function RoleControlCentre({ accountProfile, navigate }) {
  const organiserEventCount = getOwnerEvents(accountProfile, "organiser").length;
  const centreEventCount = getOwnerEvents(accountProfile, "centre").length;
  const controlSpecs = [
    {
      role: "player",
      title: "Player",
      text: "Find suitable games, manage invites, save bookings and show your playing level.",
      icon: UserCircle,
      dashboardTarget: "playerDashboard",
      setupTarget: "profileSetup",
      publicTarget: "publicPlayerProfile",
      metrics: [
        ["Bookings", `${getAccountBookings(accountProfile).length}`],
        ["Media", `${cleanLinkRows(accountProfile.player?.mediaLinks, "Player media").length}`],
        ["Readiness", roleReadinessLabel(accountProfile, "player")]
      ],
      actions: [
        ["Dashboard", "playerDashboard"],
        ["Public page", "publicPlayerProfile"],
        ["Edit profile", "profileSetup"],
        ["Bookings", "bookings"],
        ["Messages", "messages"]
      ]
    },
    {
      role: "organiser",
      title: "Social organiser",
      text: "Fill sessions, invite regular players, manage attendance and track money collected.",
      icon: UsersRound,
      dashboardTarget: "organiser",
      setupTarget: "createGroup",
      publicTarget: "publicGroupProfile",
      metrics: [
        ["Sessions", hasGroupPlayingSchedule(accountProfile.group) ? "Recurring" : "0"],
        ["Events", `${organiserEventCount}`],
        ["Readiness", roleReadinessLabel(accountProfile, "organiser")]
      ],
      actions: [
        ["Dashboard", "organiser"],
        ["Public group", "publicGroupProfile"],
        ["Create group", "createGroup"],
        ["Create event", "createEvent"],
        ["Members", "members"]
      ]
    },
    {
      role: "centre",
      title: "Centre owner",
      text: "Publish court details, bring in bookings, promote events and review payments.",
      icon: Building2,
      dashboardTarget: "centreDashboard",
      setupTarget: "claimCentre",
      publicTarget: "publicCentreProfile",
      metrics: [
        ["Courts", accountProfile.centre?.courtCount || "0"],
        ["Events", `${centreEventCount}`],
        ["Readiness", roleReadinessLabel(accountProfile, "centre")]
      ],
      actions: [
        ["Dashboard", "centreDashboard"],
        ["View public centre", "publicCentreProfile"],
        ["Add/edit centre", "claimCentre"],
        ["Courts", "courtManager"],
        ["Search and claim", "centreClaimSearch"]
      ]
    },
    {
      role: "coach",
      title: "Coach or trainer",
      text: "Sell lessons, show proof, handle enquiries and turn students into repeat bookings.",
      icon: Trophy,
      dashboardTarget: "coachDashboard",
      setupTarget: "coachProfile",
      publicTarget: "publicCoachProfile",
      metrics: [
        ["Lessons", `${activeCoachLessonServices(accountProfile.coach || {}).length}`],
        ["Price", coachPrimaryRateLabel(accountProfile.coach || {}) || "Missing"],
        ["Media", `${cleanLinkRows(accountProfile.coach?.mediaLinks, "Coach media").length}`],
        ["Readiness", roleReadinessLabel(accountProfile, "coach")]
      ],
      actions: [
        ["Dashboard", "coachDashboard"],
        ["Public coach", "publicCoachProfile"],
        ["Coach profile", "coachProfile"],
        ["Create clinic", "createGroup"],
        ["Messages", "messages"]
      ]
    }
  ];

  return (
    <section className="account-asset-section role-control-centre">
      <div className="role-control-section-head">
        <div>
          <p>Profile control centre</p>
          <h2>Manage each user type</h2>
          <span>Each role has its own tools because players, organisers, centres and coaches use Badminta for different outcomes.</span>
        </div>
        <button type="button" className="role-manage-button" onClick={() => navigate("signup", { signupStep: "roles" })}>
          <SlidersHorizontal size={17} />
          Edit user types
        </button>
      </div>
      <div className="role-control-grid">
        {controlSpecs.map((spec) => {
          const Icon = spec.icon;
          const selected = accountHasRole(accountProfile, spec.role);
          const ready = isPublicProfileReady(accountProfile, spec.role);
          const missingCount = getPublicProfileMissingFields(accountProfile, spec.role).length;
          const readyToPublish = selected && !ready && missingCount === 0;
          const readiness = roleReadiness(accountProfile, spec.role);
          const primaryTarget = !selected ? "signup" : ready ? spec.dashboardTarget : spec.setupTarget;
          const primaryLabel = !selected
            ? `Use Badminta as ${spec.title.toLowerCase()}`
            : ready
              ? `Open ${spec.title.toLowerCase()} tools`
              : readyToPublish ? `Publish ${spec.title.toLowerCase()}` : `Finish ${spec.title.toLowerCase()} setup`;
          const rawCardActions = !selected
            ? [["Choose user type", "signup", { signupStep: "roles" }], ["Open checklist", "onboarding"]]
            : ready
              ? spec.actions
              : [[readyToPublish ? "Publish profile" : roleSetupSteps[spec.role]?.action || "Complete onboarding", spec.setupTarget], ["Open checklist", "onboarding"]];
          const seenActions = new Set();
          const cardActions = rawCardActions.filter(([label, target]) => {
            const key = `${label}-${target}`;
            if (seenActions.has(key)) return false;
            seenActions.add(key);
            if (!ready && target === spec.publicTarget) return false;
            return true;
          }).slice(0, 4);
          return (
            <article key={spec.role} className={`role-control-card ${ready ? "ready" : readyToPublish ? "ready-to-publish" : selected ? "needs-work" : "not-selected"}`}>
              <div className="role-control-top">
                <span className="role-control-icon"><Icon size={22} /></span>
                <div>
                  <h3>{spec.title}</h3>
                  <p>{spec.text}</p>
                </div>
              </div>
              <div className="role-readiness-meter" aria-label={`${spec.title} readiness ${selected ? readiness.percent : 0}%`}>
                <span style={{ width: `${selected ? readiness.percent : 0}%` }} />
              </div>
              <div className={`role-status-strip ${ready ? "ready" : readyToPublish ? "ready-to-publish" : selected ? "needs-work" : "not-selected"}`}>
                {ready || readyToPublish ? <Check size={16} /> : <CircleHelp size={16} />}
                <span>{ready ? "Live and ready for people to use" : readyToPublish ? "Required details complete; publish to go live" : selected ? `${missingCount} detail${missingCount === 1 ? "" : "s"} left before this role is useful` : "Not selected yet"}</span>
              </div>
              <div className="role-control-metrics">
                {spec.metrics.map(([label, value]) => (
                  <span key={label}><b>{value}</b>{label}</span>
                ))}
              </div>
              <button
                type="button"
                className={`role-primary-action ${ready ? "" : "setup-needed"}`}
                onClick={() => navigate(primaryTarget, !selected ? { signupStep: "roles" } : {})}
              >
                <span>{primaryLabel}</span>
                <ChevronRight size={17} />
              </button>
              <div className="role-control-actions">
                {cardActions.map(([label, target, payload]) => (
                  <button type="button" key={label} onClick={() => navigate(target, payload || {})}>
                    <span>{label}</span>
                    <ChevronRight size={15} />
                  </button>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RatingsSystem({ players, navigate }) {
  return (
    <section className="page analytics-like-page">
      <SplitHero
        eyebrow="Smart rating system"
        title="Make skill level visible without making it hostile."
        text="Start with self-selected bands, then improve confidence using verified attendance, organiser reviews, match results and venue history."
        emptyLabel="Ratings become visual after real player activity"
      />
      <div className="analytics-layout">
        <section className="rating-table">
          <h2>Rating board</h2>
          {players.length ? players.map((player) => (
            <div key={player.id}>
              <PlayerCardMedia src={player.image} name={player.name} className="player-inline-avatar" rootMargin="100px" />
              <strong>{player.name}</strong>
              <span>{player.band}</span>
              <b>{player.rating}</b>
            </div>
          )) : (
            <CommunityEmptyState {...emptyPlayerState("the rating board")} navigate={navigate} compact />
          )}
        </section>
        <aside className="rating-scale">
          <h2>Badminta levels</h2>
          {["1.0 Beginner", "2.0 Social", "3.0 Club", "4.0 Intermediate", "5.0 Advanced", "6.0 Competitive"].map((band) => (
            <div key={band}><span>{band}</span><em /></div>
          ))}
        </aside>
      </div>
    </section>
  );
}

function MessagesPage({ navigate, accountProfile, saveAccountProfile, messageFocus, setMarketplaceConversations, conversationSyncState = { status: "idle", message: "" }, refreshMarketplaceConversations }) {
  const [messageActionState, setMessageActionState] = useState({ status: "idle", message: "" });
  const inboxes = useMemo(() => buildAccountInboxData(accountProfile), [accountProfile]);
  const initialInboxId = messageFocus?.roleId && inboxes.some((inbox) => inbox.id === messageFocus.roleId)
    ? messageFocus.roleId
    : inboxes.find((inbox) => inbox.threads.some(isMarketplaceConversationThread))?.id || inboxes[0]?.id || "player";
  const initialThreadId = messageFocus?.threadId || inboxes.flatMap((inbox) => inbox.threads).find(isMarketplaceConversationThread)?.id || "";
  const [activeInbox, setActiveInbox] = useState(initialInboxId);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [mobileView, setMobileView] = useState("inbox");
  const [messageToolsOpen, setMessageToolsOpen] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [threadStore, setThreadStore] = useState(() => Object.fromEntries(inboxes.map((inbox) => [inbox.id, inbox.threads.filter(isMarketplaceConversationThread)])));
  const [activeThreadId, setActiveThreadId] = useState(initialThreadId);
  const active = inboxes.find((item) => item.id === activeInbox) || inboxes[0];
  const activeThreads = threadStore[active.id] || [];
  const filteredThreads = activeThreads.filter((thread) => `${thread.name} ${thread.type} ${thread.preview} ${thread.meta.join(" ")}`.toLowerCase().includes(query.toLowerCase()));
  const activeThread = activeThreads.find((thread) => thread.id === activeThreadId) || activeThreads[0];
  const ActiveIcon = active.icon;
  const unreadFor = (roleId) => (threadStore[roleId] || [])
    .filter(isMarketplaceConversationThread)
    .reduce((total, thread) => total + Number(thread.unread || 0), 0);
  const allThreads = Object.values(threadStore).flat();
  const allConversationThreads = allThreads.filter(isMarketplaceConversationThread);
  const hasAnyConversations = allConversationThreads.length > 0;
  const totalUnread = inboxes.reduce((total, inbox) => total + unreadFor(inbox.id), 0);
  const activeUnread = unreadFor(active.id);
  const actionThreadCount = allConversationThreads.filter((thread) => ["needs-action", "open"].includes(messageThreadTone(thread))).length;
  const activeNeedsAction = activeThreads.filter(isMarketplaceConversationThread).filter((thread) => messageThreadTone(thread) === "needs-action").length;
  const activeThreadTone = messageThreadTone(activeThread);
  const activeThreadActions = activeThread?.actions || [];
  const primaryThreadActions = activeThreadActions.slice(0, 4);
  const latestMessage = activeThread?.messages?.[activeThread.messages.length - 1];
  const selectInbox = (inboxId) => {
    setActiveInbox(inboxId);
    setQuery("");
    const nextThread = (threadStore[inboxId] || [])[0];
    setActiveThreadId(nextThread?.id || "");
    setMobileView("inbox");
  };
  const runThreadAction = (target, payload = {}) => {
    if (!target) return;
    navigate(target, payload || {});
  };
  const appendDraftText = (text) => {
    setDraft((current) => current.trim() ? `${current.trim()}\n${text}` : text);
  };
  const addAttachment = (label, detail) => {
    setSelectedAttachments((current) => {
      if (current.some((item) => item.label === label && item.detail === detail)) return current;
      return [...current, { label, detail }];
    });
    setMessageToolsOpen(true);
  };
  const removeAttachment = (label) => {
    setSelectedAttachments((current) => current.filter((item) => item.label !== label));
  };
  const messageToolOptions = activeThread ? [
    [LayoutDashboard, `Open ${active.label.toLowerCase()} workspace`, active.cta, () => navigate(active.ctaTarget)],
    [Plus, "Attach conversation context", `${activeThread.name} - ${activeThread.type}`, () => addAttachment("Conversation context", `${activeThread.name} - ${activeThread.type}. ${activeThread.meta.join(" · ")}`)],
    [WalletCards, "Insert payment prompt", "Ask for payment, refund or payout confirmation", () => appendDraftText("Can you please confirm the payment status, amount paid, and whether any refund or payout action is needed?")],
    [CalendarDays, "Insert session prompt", "Ask about date, time and attendance", () => appendDraftText("Can you please confirm the session date, time, venue, attendance count and any waitlist changes?")],
    [Star, "Insert review request", "Ask for rating or recommendation after play", () => appendDraftText("After the session, please leave a rating or recommendation so the public profile reflects real playing history.")],
    [ShieldCheck, "Attach trust note", "Flag this conversation for support context", () => addAttachment("Trust note", `Support context for ${activeThread.name}: ${activeThread.status}.`)]
  ] : [];

  useEffect(() => {
    refreshMarketplaceConversations?.();
  }, [accountProfile.uid, accountProfile.phone]);

  useEffect(() => {
    const nextStore = Object.fromEntries(inboxes.map((inbox) => [inbox.id, inbox.threads.filter(isMarketplaceConversationThread)]));
    setThreadStore(nextStore);
    const focusInbox = messageFocus?.roleId && inboxes.some((inbox) => inbox.id === messageFocus.roleId) ? messageFocus.roleId : "";
    const nextInbox = focusInbox || inboxes.find((inbox) => inbox.threads.some(isMarketplaceConversationThread))?.id || inboxes[0]?.id || "player";
    setActiveInbox((current) => focusInbox || (inboxes.some((inbox) => inbox.id === current) ? current : nextInbox));
    setActiveThreadId((current) => {
      if (messageFocus?.threadId && nextStore[messageFocus.roleId || nextInbox]?.some((thread) => thread.id === messageFocus.threadId)) return messageFocus.threadId;
      const hasCurrent = Object.values(nextStore).some((threads) => threads.some((thread) => thread.id === current));
      return hasCurrent ? current : Object.values(nextStore).flat()[0]?.id || "";
    });
    if (focusInbox) setMobileView("chat");
  }, [inboxes, messageFocus]);

  useEffect(() => {
    const firstThread = (threadStore[activeInbox] || [])[0];
    if (firstThread && !threadStore[activeInbox].some((thread) => thread.id === activeThreadId)) {
      setActiveThreadId(firstThread.id);
    } else if (!firstThread && activeThreadId) {
      setActiveThreadId("");
    }
  }, [activeInbox, activeThreadId, threadStore]);

  useEffect(() => {
    if (!activeThread?.backendThread || !activeThread.conversationId || !activeThread.unread || !firebaseAuth?.currentUser) return;
    callMarketplaceFunction("markMarketplaceConversationRead", { conversationId: activeThread.conversationId })
      .then(() => {
        setThreadStore((current) => ({
          ...current,
          [active.id]: (current[active.id] || []).map((thread) => thread.id === activeThread.id ? { ...thread, unread: 0 } : thread)
        }));
        setMarketplaceConversations?.((current) => current.map((conversation) => conversation.conversationId === activeThread.conversationId ? { ...conversation, unread: 0 } : conversation));
      })
      .catch(() => {});
  }, [activeThread?.id, activeThread?.unread, active.id]);

  const sendMessage = async () => {
    const cleanDraft = draft.trim();
    if ((!cleanDraft && !selectedAttachments.length) || !activeThread || sendingMessage) return;
    const attachmentText = selectedAttachments.length
      ? `\n\nAttached: ${selectedAttachments.map((item) => `${item.label} (${item.detail})`).join("; ")}`
      : "";
    const messageText = `${cleanDraft || "Shared conversation details."}${attachmentText}`;
    const savedMessage = {
      id: `message-${Date.now()}`,
      roleId: active.id,
      threadId: activeThread.id,
      threadName: activeThread.name,
      threadType: activeThread.type,
      status: activeThread.status,
      meta: activeThread.meta,
      context: activeThread.context,
      actions: activeThread.actions,
      recordType: "conversation",
      from: "me",
      text: messageText,
      time: "Now",
      createdAt: new Date().toISOString()
    };
    setSendingMessage(true);
    setMessageActionState({ status: "idle", message: "" });
    if (activeThread.backendThread && activeThread.conversationId) {
      try {
        await callMarketplaceFunction("sendMarketplaceMessage", {
          conversationId: activeThread.conversationId,
          messageId: savedMessage.id,
          text: cleanDraft || "Shared conversation details.",
          attachments: selectedAttachments
        });
      } catch (error) {
        setMessageActionState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
        setSendingMessage(false);
        return;
      }
    }
    setThreadStore((current) => ({
      ...current,
      [active.id]: (current[active.id] || []).map((thread) => thread.id === activeThread.id
        ? {
          ...thread,
          preview: cleanDraft || selectedAttachments.map((item) => item.label).join(", "),
          time: "Now",
          messages: [...thread.messages, ["me", messageText, "Now"]]
        }
        : thread)
    }));
    if (!activeThread.backendThread) {
      saveAccountProfile?.({ messages: [...getStoredMessages(accountProfile), savedMessage] });
    }
    setDraft("");
    setSelectedAttachments([]);
    setMessageToolsOpen(false);
    if (activeThread.backendThread) await refreshMarketplaceConversations?.({ quiet: true });
    setSendingMessage(false);
  };

  return (
    <section className="page messages-page">
      <div className={`messenger-command-bar${hasAnyConversations ? "" : " is-empty"}`}>
        <div className="messenger-command-copy">
          <p>Messages</p>
          <h1>Your badminton inbox</h1>
          <span>Conversations for every profile you manage, without mixing player, organiser, centre and coaching work.</span>
        </div>
        {hasAnyConversations && (
          <div className="messenger-command-stats" aria-label="Message summary">
            <article>
              <MessageCircle size={18} />
              <strong>{messageRoleStatLabel(allConversationThreads.length, "conversation")}</strong>
              <span>{totalUnread} unread</span>
            </article>
            <article className={actionThreadCount ? "needs-action" : ""}>
              <ClipboardCheck size={18} />
              <strong>{messageRoleStatLabel(actionThreadCount, "action")}</strong>
              <span>Needs follow up</span>
            </article>
            <article>
              <ActiveIcon size={18} />
              <strong>{active.label}</strong>
              <span>{messageRoleStatLabel(activeThreads.length, "thread")}</span>
            </article>
          </div>
        )}
      </div>
      {(messageFocus?.error || messageActionState.status === "error" || conversationSyncState.status === "error") && (
        <div className="auth-message error">{messageFocus?.error || messageActionState.message || conversationSyncState.message}</div>
      )}
      {conversationSyncState.status === "loading" && (
        <div className="messenger-sync-status"><Loader2 size={16} className="spin" /> Syncing real conversations</div>
      )}
      <div className={`messenger-shell ${activeThread ? "has-active-thread" : "messenger-empty"} ${mobileView === "chat" ? "mobile-chat-open" : "mobile-inbox-open"}`}>
        <aside className="messenger-sidebar">
          <div className="messenger-title">
            <p>Role inbox</p>
            <h1>{active.title}</h1>
            <span>{active.summary}</span>
          </div>
          <div className="message-role-tabs" aria-label="Message user type">
            {inboxes.map((inbox) => {
              const Icon = inbox.icon;
              const unread = unreadFor(inbox.id);
              const roleThreads = threadStore[inbox.id] || [];
              const threadCount = roleThreads.filter(isMarketplaceConversationThread).length;
              const needsAction = roleThreads.filter(isMarketplaceConversationThread).filter((thread) => messageThreadTone(thread) === "needs-action").length;
              return (
                <button type="button" key={inbox.id} className={activeInbox === inbox.id ? "active" : ""} onClick={() => selectInbox(inbox.id)}>
                  <Icon size={18} />
                  <span>
                    <strong>{inbox.label}</strong>
                    <small>{threadCount
                      ? `${threadCount} conversation${threadCount === 1 ? "" : "s"}${needsAction ? ` · ${needsAction} action${needsAction === 1 ? "" : "s"}` : ""}`
                      : "No conversations"}</small>
                  </span>
                  {unread > 0 && <b>{unread}</b>}
                </button>
              );
            })}
          </div>
          {(activeUnread > 0 || activeNeedsAction > 0) && (
            <div className="messenger-role-summary">
              {activeUnread > 0 && <span><MessageCircle size={14} /> {activeUnread} unread</span>}
              {activeNeedsAction > 0 && <span><ClipboardCheck size={14} /> {activeNeedsAction} needs action</span>}
            </div>
          )}
          {activeThreads.length > 0 && (
            <label className="message-search-field">
              <Search size={17} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${active.label.toLowerCase()} messages`} />
            </label>
          )}
          <div className="thread-list">
            {filteredThreads.length ? filteredThreads.map((thread) => (
              <button type="button" key={thread.id} className={activeThread?.id === thread.id ? "active" : ""} onClick={() => { setActiveThreadId(thread.id); setMobileView("chat"); }}>
                <span className={`thread-avatar ${messageThreadTone(thread)}`}>{messageThreadInitial(thread)}</span>
                <span className="thread-copy">
                  <span className="thread-title-line">
                    <strong>{thread.name}</strong>
                    <em className={`thread-status ${messageThreadTone(thread)}`}>{thread.status}</em>
                  </span>
                  <small>{thread.type}</small>
                  <em>{thread.preview}</em>
                  {thread.meta.length > 0 && (
                    <span className="thread-chip-row">
                      {thread.meta.slice(0, 3).map((item) => <i key={item}>{item}</i>)}
                    </span>
                  )}
                </span>
                <span className="thread-meta">
                  <time>{thread.time}</time>
                  {thread.unread > 0 && <b>{thread.unread}</b>}
                </span>
              </button>
            )) : (
              <div className="thread-empty">
                <Mail size={20} />
                <strong>{query.trim() ? "No matching messages" : `No ${active.label.toLowerCase()} conversations yet`}</strong>
                <span>{query.trim() ? "No conversations match this search." : active.emptyText}</span>
                <button
                  type="button"
                  onClick={() => query.trim() ? setQuery("") : navigate(active.emptyTarget)}
                >
                  <span>{query.trim() ? "Clear search" : active.emptyLabel}</span>
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </div>
        </aside>

        <main className="conversation-panel">
          {activeThread && (
            <div className="conversation-head">
              <button type="button" className="mobile-chat-back" onClick={() => setMobileView("inbox")} aria-label="Back to inbox">
                <ChevronLeft size={20} />
              </button>
              <div className={`conversation-avatar ${activeThreadTone}`}><ActiveIcon size={22} /></div>
              <div>
                <p>{active.title}</p>
                <h2>{activeThread.name}</h2>
                <span>{activeThread.type}</span>
              </div>
              <div className="conversation-head-actions">
                <button type="button" className={messageToolsOpen ? "active" : ""} onClick={() => setMessageToolsOpen((current) => !current)} aria-label="More message actions"><MoreHorizontal size={18} /></button>
              </div>
            </div>
          )}
          {activeThread ? (
            <>
              <div className="conversation-status-row">
                <span className={activeThreadTone}><MessageCircle size={15} /> {activeThread.status}</span>
                <span><Clock3 size={15} /> Last update {activeThread.time}</span>
                {latestMessage && <span><MessageCircle size={15} /> Latest from {latestMessage[0] === "me" ? "you" : latestMessage[0] === "system" ? "Badminta" : activeThread.name}</span>}
                <span><ShieldCheck size={15} /> Badminta protected</span>
              </div>
              <div className="conversation-control-strip" aria-label="Conversation actions">
                <button type="button" className="primary" onClick={() => navigate(active.ctaTarget)}>
                  <LayoutDashboard size={16} />
                  {active.cta}
                </button>
                {primaryThreadActions.map(([label, target, payload]) => (
                  <button type="button" key={`${label}-${target}`} onClick={() => runThreadAction(target, payload)}>
                    <span>{label}</span>
                    <ChevronRight size={15} />
                  </button>
                ))}
              </div>
              {messageToolsOpen && (
                <div className="message-action-panel" aria-label="Message tools">
                  {messageToolOptions.map(([Icon, label, detail, action]) => (
                    <button type="button" key={label} onClick={action}>
                      <Icon size={17} />
                      <span>
                        <strong>{label}</strong>
                        <em>{detail}</em>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="conversation-messages">
                {activeThread.messages.map(([from, text, time], index) => (
                  <div className={`chat-message ${from}`} key={`${from}-${time}-${index}`}>
                    <p>{text}</p>
                    <span>{from === "me" ? "You" : from === "system" ? "Badminta" : activeThread.name} · {time}</span>
                  </div>
                ))}
              </div>
              <div className="message-composer">
                <button type="button" className={messageToolsOpen ? "active" : ""} aria-label="Add attachment" onClick={() => setMessageToolsOpen((current) => !current)}><Plus size={18} /></button>
                {selectedAttachments.length > 0 && (
                  <div className="message-attachment-strip">
                    {selectedAttachments.map((attachment) => (
                      <span key={`${attachment.label}-${attachment.detail}`}>
                        {attachment.label}
                        <button type="button" onClick={() => removeAttachment(attachment.label)} aria-label={`Remove ${attachment.label}`}><X size={13} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={`Message as ${active.label.toLowerCase()}`} />
                <button type="button" className="message-send-button" onClick={sendMessage} disabled={sendingMessage}>
                  <span>{sendingMessage ? "Sending" : "Send"}</span>
                  <Send size={16} />
                </button>
              </div>
            </>
          ) : (
            <CommunityEmptyState
              type="messages"
              title={`No ${active.label.toLowerCase()} conversations yet`}
              text={active.emptyText}
              primary={{ label: active.emptyLabel, target: active.emptyTarget }}
              icon={Mail}
              navigate={navigate}
              compact
            />
          )}
        </main>

        {activeThread && (
          <aside className="conversation-context">
            <div className="context-card">
              <div>
                <ActiveIcon size={20} />
                <span>{active.label} context</span>
              </div>
              <p>{active.summary}</p>
            </div>
              <div className="context-card">
                <h3>Thread details</h3>
                {activeThread.context.length ? activeThread.context.map(([label, value]) => (
                    <div className="context-row" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  )) : (
                    <div className="context-row">
                      <span>Status</span>
                      <strong>{activeThread.status || "Open"}</strong>
                    </div>
                  )}
              </div>
              <div className="context-card">
                <h3>Quick actions</h3>
                <div className="messenger-action-list">
                  {activeThread.actions.length ? activeThread.actions.map(([label, target, payload]) => (
                    <button type="button" key={`${label}-${target}`} onClick={() => runThreadAction(target, payload)}>
                      <span>{label}</span>
                      <ChevronRight size={15} />
                    </button>
                  )) : (
                    <button type="button" onClick={() => navigate(active.ctaTarget)}>
                      <span>{active.cta}</span>
                      <ChevronRight size={15} />
                    </button>
                  )}
                </div>
              </div>
          </aside>
        )}
      </div>
    </section>
  );
}

const roleDashboardRequiredTotals = {
  player: 1,
  organiser: 4,
  centre: 10,
  coach: 3
};

const roleReadiness = (profile, role) => {
  const missing = getRoleOnboardingMissingFields(profile, role);
  const total = roleDashboardRequiredTotals[role] || Math.max(missing.length, 1);
  return {
    missing,
    percent: Math.max(0, Math.min(100, Math.round(((total - missing.length) / total) * 100)))
  };
};

const roleReadinessLabel = (accountProfile = {}, role) => {
  if (!accountHasRole(accountProfile, role)) return "Not selected";
  const missing = getRoleOnboardingMissingFields(accountProfile, role);
  if (!missing.length) return "Ready";
  return `${missing.length} item${missing.length === 1 ? "" : "s"} left`;
};

const roleEventRows = (events = []) => events.map((event) => [
  event.title,
  `${formatDateLabel(event.date)}, ${formatTimeLabel(event.time)}`,
  getEventVenueLabel(event),
  event.status === "published" ? "Public" : "Draft"
]);

const paymentNeedsAction = (status = "") => ["pending", "attention"].includes(paymentStatusTone(status));
const countPaymentsNeedingAction = (rows = []) => rows.filter((row) => paymentNeedsAction(row.paymentStatus || row.status)).length;
const accountRoleCurrency = (accountProfile = {}, role = "player") => {
  const roleId = publicRoleId(role);
  const source = roleId === "organiser" ? accountProfile.group || {} : accountProfile[roleId] || {};
  return normaliseCurrencyCode(source.currency || marketplaceCurrencyForCountry(source.countryCode || source.location?.countryCode || accountProfile.countryCode || accountProfile.location?.countryCode));
};
const moneyRecordAmount = (record = {}, fallbackCurrency = "aud") => {
  const currency = normaliseCurrencyCode(record.currency || fallbackCurrency);
  if (Number.isFinite(Number(record.totalCents)) && Number(record.totalCents) > 0) return minorUnitsToAmount(record.totalCents, currency);
  if (Number.isFinite(Number(record.amountCents)) && Number(record.amountCents) > 0) return minorUnitsToAmount(record.amountCents, currency);
  return Number(String(record.total ?? record.amount ?? record.price ?? "").replace(/[^0-9.]/g, "")) || 0;
};
const formatMoneyBreakdown = (records = [], fallbackCurrency = "aud", emptyLabel = "") => {
  const totals = new globalThis.Map();
  records.forEach((record) => {
    const currency = normaliseCurrencyCode(record?.currency || fallbackCurrency);
    const amount = moneyRecordAmount(record, currency);
    if (amount <= 0) return;
    totals.set(currency, (totals.get(currency) || 0) + amount);
  });
  if (!totals.size) return emptyLabel || formatMoney(0, fallbackCurrency);
  return [...totals.entries()].map(([currency, amount]) => formatMoney(amount, currency)).join(" + ");
};

const dashboardActivityFromThreads = (threads = [], emptyText = "New messages and actions for this role will show here.") => {
  const rows = threads.slice(0, 3).map((thread) => [
    thread.type || thread.name,
    thread.preview,
    thread.time || "Status"
  ]);
  return rows.length ? rows : [["No activity yet", emptyText, "Not started"]];
};

const eventRevenueFromRows = (events = [], accountProfile = {}) => events.reduce((total, event) => {
  const attendeeCount = getPublicEventAttendees(event, accountProfile)
    .filter((attendee) => paymentStatusTone(attendee.paymentStatus || attendee.status) === "paid").length;
  return total + attendeeCount * eventPriceNumber(event);
}, 0);

const paidAttendeeCount = (events = [], accountProfile = {}) => events.reduce((total, event) => {
  const attendees = getPublicEventAttendees(event, accountProfile);
  return total + attendees.filter((attendee) => String(attendee.paymentStatus || "").toLowerCase().includes("paid")).length;
}, 0);

const accountOperatingSummary = (accountProfile = {}) => {
  const selectedRoles = accountRoleOptions.filter((role) => accountHasRole(accountProfile, role.id));
  const readyRoles = selectedRoles.filter((role) => isPublicProfileReady(accountProfile, role.id));
  const inboxes = buildAccountInboxData(accountProfile);
  const threads = inboxes.flatMap((inbox) => inbox.threads).filter(isMarketplaceConversationThread);
  const unread = threads.reduce((total, thread) => total + Number(thread.unread || 0), 0);
  const bookings = getAccountBookings(accountProfile);
  const joinedGroups = getJoinedGroups(accountProfile);
  const events = getAccountEvents(accountProfile);
  const joinedEvents = getJoinedEvents(accountProfile);
  const missingCount = selectedRoles.reduce((total, role) => total + getRoleOnboardingMissingFields(accountProfile, role.id).length, 0);
  return {
    selectedRoles,
    readyRoles,
    threads,
    unread,
    bookings,
    joinedGroups,
    events,
    joinedEvents,
    missingCount,
    nextAction: missingCount ? "Finish setup" : unread ? "Open messages" : joinedGroups.length || bookings.length ? "Review play plan" : "Find play"
  };
};

const roleDashboardMessageFocus = {
  player: { messageFocus: { roleId: "player" } },
  organiser: { messageFocus: { roleId: "organiser" } },
  centre: { messageFocus: { roleId: "centre" } },
  coach: { messageFocus: { roleId: "coach" } }
};

const roleDashboardTargets = {
  player: "playerDashboard",
  organiser: "organiser",
  centre: "centreDashboard",
  coach: "coachDashboard"
};

const roleDashboardSeoCopy = {
  player: {
    title: "Player dashboard for bookings, groups and badminton profile management",
    description: "Manage badminton court bookings, social groups, payment status, messages, availability and your public player profile from one Badminta dashboard."
  },
  organiser: {
    title: "Social organiser dashboard for badminton sessions, members and payments",
    description: "Create recurring badminton groups, manage members, invitations, events, join requests, payment status and organiser payouts from one Badminta dashboard."
  },
  centre: {
    title: "Centre owner dashboard for badminton court bookings and venue operations",
    description: "Manage badminton centre profile details, court inventory, opening hours, fee schedules, booking readiness, events, payments and public centre pages."
  },
  coach: {
    title: "Coach dashboard for badminton lessons, enquiries and public coaching profile",
    description: "Manage badminton coaching profile details, availability, lesson pricing, media, student enquiries, public coach profile and payout tools."
  }
};

const toolAction = (label, target, payload = {}) => ({ label, target, payload });

function stripeConnectOwnerContext(role, accountProfile = defaultAccountProfile) {
  const roleId = publicRoleId(role);
  const ownerProfile = roleId === "centre"
    ? accountProfile.centre || {}
    : roleId === "coach"
      ? accountProfile.coach || {}
      : accountProfile.group || {};
  const ownerProfileId = roleId === "centre"
    ? ownerProfile.serverCentreId || ownerProfile.firestoreSourceId || accountProfile.publicCentreProfileId || ""
    : roleId === "coach"
      ? ownerProfile.publicCoachId || ownerProfile.firestoreSourceId || accountProfile.publicCoachProfileId || ""
      : ownerProfile.publicGroupId || ownerProfile.serverGroupId || ownerProfile.firestoreSourceId || accountProfile.publicGroupProfileId || "";
  return { roleId, ownerProfile, ownerProfileId };
}

function useOwnerStripeConnectReadiness(role, accountProfile = defaultAccountProfile) {
  const { roleId, ownerProfile, ownerProfileId } = stripeConnectOwnerContext(role, accountProfile);
  const country = normaliseCountryCode(
    ownerProfile?.countryCode
    || ownerProfile?.location?.countryCode
    || ownerProfile?.country
    || accountProfile.countryCode
    || accountProfile.country
    || "",
    ownerProfile?.city || accountProfile.city || ""
  );
  const signedIn = Boolean(firebaseAuth?.currentUser);
  const [state, setState] = useState({
    status: signedIn ? "checking" : "signed-out",
    ready: false,
    account: null,
    availability: null,
    message: ""
  });

  useEffect(() => {
    if (!signedIn || !ownerProfileId) {
      setState({ status: signedIn ? "not-started" : "signed-out", ready: false, account: null, availability: null, message: "" });
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, status: "checking", message: "" }));
    (async () => {
      try {
        const availability = await callMarketplaceFunction("getStripeConnectCountryAvailability", {
          role: roleId,
          ownerProfileId,
          country
        });
        if (!active) return;
        if (!availability.supported) {
          setState({
            status: "unavailable",
            ready: false,
            account: null,
            availability,
            message: availability.message || "Badminta Pay payouts are not available for this owner country. Free activities and offline settlement remain available."
          });
          return;
        }
        try {
          const data = await callMarketplaceFunction("refreshStripeConnectAccount", { role: roleId, ownerProfileId });
          if (!active) return;
          const account = data.account || null;
          const ready = Boolean(data.readyForCheckout || isStripeAccountCheckoutReady(account || {}));
          setState({
            status: ready ? "ready" : account ? "needs-action" : "not-started",
            ready,
            account,
            availability,
            message: ready ? "Badminta Pay is ready for checkout and payouts." : account ? "Complete the remaining Stripe verification steps." : "Set up Badminta Pay before accepting paid bookings."
          });
        } catch (error) {
          if (!active) return;
          const message = marketplaceFunctionErrorMessage(error);
          const notStarted = /create stripe onboarding|has not been created|start onboarding|set up payouts/i.test(message);
          setState({
            status: notStarted ? "not-started" : "error",
            ready: false,
            account: null,
            availability,
            message: notStarted ? "Set up Badminta Pay before accepting paid bookings." : message
          });
        }
      } catch (error) {
        if (!active) return;
        setState({
          status: "error",
          ready: false,
          account: null,
          availability: null,
          message: marketplaceFunctionErrorMessage(error)
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [signedIn, roleId, ownerProfileId, country]);

  return {
    ...state,
    roleId,
    ownerProfileId,
    country,
    countrySupported: state.availability?.supported === true,
    settlementMode: state.availability?.settlementMode || "checking"
  };
}

const payoutReadinessCopy = (state = {}) => {
  if (state.status === "checking") return { label: "Checking payout status", stage: "Checking", detail: "Badminta is confirming Stripe payment and payout access." };
  if (state.ready) return { label: "Payments and payouts ready", stage: "Ready", detail: "Badminta Checkout can collect player payments and reconcile owner payouts." };
  if (state.status === "unavailable") return { label: "Payout launch not available here yet", stage: "Regional launch", detail: state.message || "Worldwide profiles remain available while Badminta Pay expands to additional payout countries." };
  if (state.status === "needs-action" || state.account) return { label: "Verification action needed", stage: "Action needed", detail: "Complete the remaining Stripe-hosted identity or bank verification steps." };
  if (state.status === "error") return { label: "Payout status unavailable", stage: "Check status", detail: "Open Badminta Pay to refresh the account before accepting payment." };
  return { label: "Payout setup not started", stage: "Set up", detail: "Add legal and bank details through Stripe-hosted Badminta Pay before accepting payment." };
};

const buildRoleOperatingSections = (role, context = {}) => {
  const {
    player = {},
    group = {},
    centre = {},
    coach = {},
    readiness = { percent: 0, missing: [] },
    organiserEvents = [],
    centreEvents = [],
    courtBookings = [],
    centreBookings = [],
    joinedGroups = [],
    organiserInvites = [],
    organiserGroupMembers = [],
    organiserRevenue = 0,
    organiserRevenueLabel = "",
    centreRevenue = 0,
    centreBookingRevenue = 0,
    centreRevenueLabel = "",
    playerBookingSpend = 0,
    playerBookingSpendLabel = "",
    coachRevenueLabel = "",
    coachPaymentRows = [],
    playerCurrency = "aud",
    organiserCurrency = "aud",
    centreCurrency = "aud",
    coachCurrency = "aud",
    playerWallet = { active: [], courtHours: 0, visits: 0, label: "No active credits" },
    enabledPlayerAvailability = 0,
    enabledCoachAvailability = 0,
    venue = defaultVenue,
    payoutState = {}
  } = context;
  const centrePricingSummary = getCourtPricingSummary(centre.courtPricing, centre.pricing, centreCurrency);
  const centreVenue = managedVenueFromCentreProfile(centre, venue);
  const centreHours = summarizeAvailabilitySchedule(centre.openingSchedule) || centre.openingHours || "";
  const centreHoursCompact = summarizeAvailabilityScheduleCompact(centre.openingSchedule, centre.openingHours);
  const centrePublishedCourts = publishedCourtInventory(centre, centreVenue);
  const publishedCentreProducts = getCentreMembershipProducts(centre).filter((product) => product.status === "published");
  const publishedCentrePasses = publishedCentreProducts.filter((product) => product.productType !== "membership");
  const publishedCentreMemberships = publishedCentreProducts.filter((product) => product.productType === "membership");
  const coachArea = publicProfileAreaLabel(coach, "Missing");
  const payoutStatus = payoutReadinessCopy(payoutState);
  const nextGroupSession = getGroupPublishedDatedOccurrences(group)[0] || null;

  if (role === "centre") {
    return [
      {
        icon: Grid3X3,
        title: "Sell more court time",
        pain: "Players will not book if they cannot see courts, hours, price and booking rules quickly.",
        controls: [
          ["Published courts", `${centrePublishedCourts.length}`, centrePublishedCourts.length ? "These courts are visible in the player booking path." : "Publish at least one court before opening bookings.", centrePublishedCourts.length ? "Live" : "Required", toolAction("Manage courts", "courtManager")],
          ["Business hours", centreHoursCompact || "Missing", "Show when players can play without needing to call.", centreHours ? "Live" : "Required", toolAction("Edit centre", "claimCentre")],
          ["Booking preview", `${centreBookings.length} request${centreBookings.length === 1 ? "" : "s"}`, "Test the player booking path before opening payments.", "Check flow", toolAction("Open booking", "booking", { venue: centreVenue, bookingType: "court" })]
        ]
      },
      {
        icon: CreditCard,
        title: "Get paid before players arrive",
        pain: "Court hire needs deposits, clear refund rules and payout tracking so staff are not chasing payment on the night.",
        controls: [
          ["Badminta Pay", payoutStatus.label, payoutStatus.detail, payoutStatus.stage, toolAction(payoutState.ready ? "Payout dashboard" : "Payment setup", "centrePayments")],
          ["Court checkout", centrePricingSummary || centre.pricing || "Pricing missing", "Players need a visible fee before checkout can open.", hasCourtPricing(centre.courtPricing) || centre.pricing ? "Priced" : "Blocked", toolAction("Set pricing", "courtManager")],
          ["Booking policy", centre.bookingRules ? "Published" : "Not added", "Publish cancellation and refund rules before accepting paid bookings.", centre.bookingRules ? "Live" : "Required", toolAction("Booking rules", "courtManager")]
        ]
      },
      {
        icon: BadgeDollarSign,
        title: "Build repeat revenue",
        pain: "Centres make more when regular players buy credits, memberships and off-peak offers instead of one-off casual bookings.",
        controls: [
          ["Passes and credit packs", publishedCentrePasses.length ? `${publishedCentrePasses.length} published` : "None published", "Sell prepaid court hours or visit passes for regular players.", publishedCentrePasses.length ? "Live" : "Create", toolAction("Centre payments", "centrePayments")],
          ["Memberships", publishedCentreMemberships.length ? `${publishedCentreMemberships.length} published` : "None published", "Create recurring centre memberships with clear benefits and billing.", publishedCentreMemberships.length ? "Live" : "Create", toolAction("Centre payments", "centrePayments")],
          ["Quiet-hour offers", centre.courtPricing?.offPeak || "Set off-peak", "Fill weak time slots with a lower off-peak fee.", centre.courtPricing?.offPeak ? "Active" : "Missing", toolAction("Off-peak fees", "courtManager")]
        ]
      },
      {
        icon: Activity,
        title: "See what is making money",
        pain: "Owners need a clear view of bookings, events, weak hours, refunds and payout records.",
        controls: [
          ["Gross centre revenue", centreRevenueLabel || formatMoney(centreRevenue + centreBookingRevenue, centreCurrency), "Court bookings, centre events and recorded payments.", "Tracked", toolAction("Payments", "centrePayments")],
          ["Hosted events", `${centreEvents.length} event${centreEvents.length === 1 ? "" : "s"}`, "Use empty courts for clinics, tournaments and open days.", "Growth", toolAction("Create event", "createEvent")],
          ["Payout check", centreBookings.length ? `${centreBookings.length} booking rows` : "No rows yet", "Match bookings, Stripe fees, refunds and payouts.", "Finance", toolAction("Open payouts", "centrePayments")]
        ]
      }
    ];
  }

  if (role === "organiser") {
    return [
      {
        icon: UsersRound,
        title: "Fill the session with the right players",
        pain: "A group works when players know the level, venue, time, price and how many spots are left.",
        controls: [
          ["Session capacity", group.capacity || "Missing", "Set the maximum players before people join.", group.capacity ? "Live" : "Required", toolAction("Edit group", "createGroup")],
          ["Invite list", `${organiserInvites.length}`, "Add regular players by email so they are easy to invite again.", organiserInvites.length ? "Ready" : "Add invites", toolAction("Manage members", "members")],
          ["Waitlist and swaps", organiserGroupMembers.length ? "Member pool ready" : "Not started", "Move someone in quickly when a player cancels.", "Control", toolAction("Members", "members")]
        ]
      },
      {
        icon: CreditCard,
        title: "Collect money without chasing players",
        pain: "The organiser should know who paid, who is unpaid and what happens if someone cancels.",
        controls: [
          ["Badminta Pay", payoutStatus.label, payoutStatus.detail, payoutStatus.stage, toolAction(payoutState.ready ? "Payout dashboard" : "Set up payouts", "payouts")],
          ["Player price", getGroupPriceLabel(group, " per player") || "Price missing", "Show the cost before players join.", group.price ? "Priced" : "Required", toolAction("Set price", "createGroup")],
          ["No-show control", "Prepay before confirmed", "Count players as joined only after payment or organiser approval.", "Risk control", toolAction("Manage sessions", "groupSessions")],
          ["Session revenue", organiserRevenue ? organiserRevenueLabel || formatMoney(organiserRevenue, organiserCurrency) : "No revenue yet", "Track what came in, what refunded and what pays out.", "Money", toolAction("Payouts", "payouts")]
        ]
      },
      {
        icon: CalendarDays,
        title: "Run recurring games and bigger events",
        pain: "A good organiser needs weekly sessions, one-off events and member history without rebuilding everything each time.",
        controls: [
          ["Next session", nextGroupSession?.dateLabel || "Date missing", nextGroupSession?.time ? `${nextGroupSession.time}. Reuse the same venue, level, capacity and price for repeat games.` : "Create a dated session from the reusable group schedule.", "Schedule", toolAction(nextGroupSession ? "Manage sessions" : "Create session", nextGroupSession ? "groupSessions" : "createGroupSession")],
          ["Published events", `${organiserEvents.length}`, "Create tournaments, clinics or club nights from the same organiser tools.", "Events", toolAction("Manage events", "eventManager")],
          ["Attendance history", `${organiserGroupMembers.length} player${organiserGroupMembers.length === 1 ? "" : "s"}`, "Know who joined, paid, refunded, waitlisted or no-showed.", "Members", toolAction("Members", "members")]
        ]
      }
    ];
  }

  if (role === "coach") {
    const coachServices = activeCoachLessonServices(coach);
    const coachPriceLabel = coachPrimaryRateLabel(coach);
    return [
      {
        icon: CalendarDays,
        title: "Turn enquiries into booked lessons",
        pain: "Players need to know when you coach, where lessons happen and what type of lesson they can request.",
        controls: [
          ["Availability windows", `${enabledCoachAvailability}`, "Show the times players can request.", enabledCoachAvailability ? "Listed" : "Add times", toolAction("Edit availability", "coachProfile")],
          ["Coaching locations", coach.locations || coachArea, "List centres, suburbs or mobile coverage.", hasPublicProfileLocation(coach) || coach.locations ? "Listed" : "Required", toolAction("Coach profile", "coachProfile")],
          ["Clinics and groups", "Create a session", "Sell a clinic or coached group instead of only one-on-one lessons.", "Growth", toolAction("Create clinic", "createGroup")]
        ]
      },
      {
        icon: CreditCard,
        title: "Price lessons and collect payment",
        pain: "Coaches should not hold time for unpaid enquiries. Price and payment rules need to be clear.",
        controls: [
          ["Badminta Pay", payoutStatus.label, payoutStatus.detail, payoutStatus.stage, toolAction(payoutState.ready ? "Payout dashboard" : "Set up payouts", "coachPayouts")],
          ["Lesson catalogue", coachServices.length ? `${coachServices.length} active` : "Missing", "Publish named sessions with duration, price and group size.", coachServices.length ? "Bookable" : "Required", toolAction("Set lessons", "coachProfile")],
          ["Session prices", coachPriceLabel || "Missing", "Show the full per-session price before players request a time.", coachPriceLabel ? "Priced" : "Required", toolAction("Set prices", "coachProfile")],
          ["Lesson payments", coachPaymentRows.length ? coachRevenueLabel || `${coachPaymentRows.length} recorded` : "No payments yet", "Review paid, pending, refunded and disputed lesson payments.", coachPaymentRows.length ? "Tracked" : "No activity", toolAction("Open payouts", "coachPayouts")],
          ["Payment timing", "After you accept a time", "Players pay only after you accept and reserve a valid lesson time.", "Protected", toolAction("Payment status", "coachPayouts")]
        ]
      },
      {
        icon: BadgeCheck,
        title: "Show why players should choose you",
        pain: "Players compare coaches by rate, location, proof, reviews and whether the coach seems right for their goal.",
        controls: [
          ["Media proof", `${cleanLinkRows(coach.mediaLinks, "Coach media").length}`, "Add lesson clips or social links so players can see your style.", "Proof", toolAction("Add media", "coachProfile")],
          ["Credentials", coach.accreditation || "Not added", "Add qualifications, club roles or relevant playing background.", coach.accreditation ? "Added" : "Recommended", toolAction("Coach profile", "coachProfile")],
          ["Testimonials", "Request after lesson", "Ask students to recommend you after a completed lesson.", "Growth", toolAction("Messages", "messages", roleDashboardMessageFocus.coach)]
        ]
      }
    ];
  }

  return [
    {
      icon: WalletCards,
      title: "Keep your play plans in one place",
      pain: "Players need saved centres, court requests, receipts and group payments without digging through messages.",
      controls: [
        ["Court spend", playerBookingSpend ? playerBookingSpendLabel || formatMoney(playerBookingSpend, playerCurrency) : formatMoney(0, playerCurrency), "Track court requests and payment status.", "Bookings", toolAction("Bookings", "bookings")],
        ["Membership credits", playerWallet.label, playerWallet.active.length ? "Available court hours and visit credits are synced from paid membership purchases." : "Buy a centre credit pack or membership and its live balance will appear here.", playerWallet.active.length ? "Active" : "None", toolAction(playerWallet.active.length ? "Open bookings" : "Find centres", playerWallet.active.length ? "saved" : "results")],
        ["Receipts and refunds", `${courtBookings.length} booking${courtBookings.length === 1 ? "" : "s"}`, "Keep receipts, refund state and support messages attached to each booking.", "Support", toolAction("Messages", "messages", roleDashboardMessageFocus.player)]
      ]
    },
    {
      icon: UsersRound,
      title: "Find games that match your level",
      pain: "The best player experience is joining sessions where the level, time, venue and price are clear.",
      controls: [
        ["Joined socials", `${joinedGroups.length}`, "See the groups you have joined or paid for.", joinedGroups.length ? "Active" : "None yet", toolAction("Find groups", "groupsIndex")],
        ["Playing level", player.skillBand || "Not added", "Help organisers place you in the right session.", player.skillBand ? "Listed" : "Recommended", toolAction("Player profile", "profileSetup")],
        ["Availability", `${enabledPlayerAvailability} window${enabledPlayerAvailability === 1 ? "" : "s"}`, "Let organisers invite you when you are actually free.", enabledPlayerAvailability ? "Invitable" : "Add times", toolAction("Availability", "profileSetup")]
      ]
    },
    {
      icon: ShieldCheck,
      title: "Control your public playing profile",
      pain: "You decide what is visible while still giving organisers enough confidence to invite you.",
      controls: [
        ["Public profile", readiness.percent >= 80 ? "Live" : `${readiness.percent}% ready`, "Your name and local area publish the profile. Adding a playing level helps people find the right match.", readiness.percent >= 80 ? "Visible" : "Finish setup", toolAction("Edit profile", "profileSetup")],
        ["Media visibility", player.visibility?.media === false ? "Hidden" : "Visible", "Control your photo, clips and social links.", "Privacy", toolAction("Profile controls", "profileSetup")],
        ["Recommendations", "Request after play", "Ask people you played with to recommend you.", "Reputation", toolAction("Review flow", "reviewFlow", { reviewContext: reviewContextFor("recommendation", "Request player recommendation", player.name || "Player") })]
      ]
    }
  ];
};

const roleReadyForTools = (accountProfile = {}, role) => accountHasRole(accountProfile, publicRoleId(role)) && isPublicProfileReady(accountProfile, role);
const roleSetupTargetFor = (role) => roleSetupSteps[publicRoleId(role)]?.page || publicSetupTarget(role);
const roleDashboardTargetFor = (role) => roleDashboardTargets[publicRoleId(role)] || "account";

const openRoleSelection = (navigate, role, sourcePage = "roleGate") => {
  const roleId = publicRoleId(role);
  const intent = signupRoleIntents[roleId];
  if (intent) {
    saveSignupIntent({
      ...intent,
      sourcePage,
      sourcePath: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : ""
    });
  }
  navigate("signup", { signupStep: "roles", signupRole: roleId });
};

function RoleOnboardingGate({ role, accountProfile, navigate, context = "dashboard" }) {
  const roleId = publicRoleId(role);
  const option = accountRoleOptions.find((item) => item.id === roleId) || {};
  const setupStep = roleSetupSteps[roleId] || {};
  const selected = accountHasRole(accountProfile, roleId);
  const missing = getPublicProfileMissingFields(accountProfile, roleId);
  const published = selected && roleHasPublishedBackendProfile(accountProfile, roleId);
  const readyToPublish = selected && !published && missing.length === 0;
  const Icon = option.icon || UserCircle;
  const roleLabel = roleTitle(roleId);
  const visibleMissing = !selected
    ? [`Select ${roleLabel} in your account user types`]
    : readyToPublish
      ? ["All required details complete"]
      : missing.length ? missing.slice(0, 10) : ["Profile publication"];
  const setupTarget = roleSetupTargetFor(roleId);

  return (
    <section className="page role-onboarding-gate-page">
      <section className="role-onboarding-gate">
        <span className="role-gate-icon"><Icon size={30} /></span>
        <div className="role-gate-copy">
          <p>{option.title || roleTitle(roleId)} onboarding</p>
          <h1>
            {readyToPublish
              ? `Publish your ${roleLabel.toLowerCase()} profile to unlock the ${context}.`
              : selected
                ? `Finish your ${roleLabel.toLowerCase()} profile to unlock the ${context}.`
              : `Add ${roleLabel.toLowerCase()} to unlock the ${context}.`}
          </h1>
          <span>
            {readyToPublish
              ? "Your required details are complete. Publish now to open the live profile and role tools."
              : selected
                ? `Complete the remaining public details before this role can take bookings, receive enquiries or use its dashboard.`
              : `Confirm ${roleLabel} as a user type and Badminta will open the matching profile setup.`}
          </span>
          <div className="role-gate-actions">
            <button type="button" onClick={() => selected ? navigate(setupTarget) : openRoleSelection(navigate, roleId, `${roleId}Dashboard`)}>
              <Check size={17} />
              {readyToPublish ? "Publish and view profile" : selected ? setupStep.action || "Complete onboarding" : "Add user type"}
            </button>
            <button type="button" onClick={() => navigate(selected ? "onboarding" : "account")}>
              <SlidersHorizontal size={17} />
              {selected ? "Open onboarding checklist" : "Back to account"}
            </button>
          </div>
        </div>
        <aside className="role-gate-progress">
          <strong>{readyToPublish ? "Ready" : selected ? `${Math.max(0, roleDashboardRequiredTotals[roleId] - missing.length)}/${roleDashboardRequiredTotals[roleId] || missing.length}` : "Not added"}</strong>
          <span>{readyToPublish ? "publish to go live" : selected ? "required fields complete" : "user type not selected"}</span>
        </aside>
      </section>
      {selected && !readyToPublish && (
        <section className="role-gate-missing-panel">
          <div>
            <p>Required before access</p>
            <h2>{`${missing.length} item${missing.length === 1 ? "" : "s"} to finish`}</h2>
          </div>
          <div className="role-gate-missing-grid">
            {visibleMissing.map((item) => (
              <span key={item}>
                <CircleHelp size={16} />
                {item}
              </span>
            ))}
          </div>
        </section>
      )}
    </section>
  );
}

function RoleOwnerOnboardingGate({ roles, accountProfile, navigate, context = "owner tools" }) {
  const candidateRoles = roles.map((role) => publicRoleId(role));
  const selectedRoles = candidateRoles.filter((role) => accountHasRole(accountProfile, role));
  const cards = (selectedRoles.length ? selectedRoles : candidateRoles).map((role) => {
    const option = accountRoleOptions.find((item) => item.id === role) || {};
    const missing = getPublicProfileMissingFields(accountProfile, role);
    return {
      role,
      option,
      missing,
      ready: roleReadyForTools(accountProfile, role),
      selected: accountHasRole(accountProfile, role)
    };
  });

  return (
    <section className="page role-onboarding-gate-page">
      <section className="role-onboarding-gate owner-choice">
        <span className="role-gate-icon"><CalendarDays size={30} /></span>
        <div className="role-gate-copy">
          <p>Owner onboarding</p>
          <h1>Finish an organiser or centre profile to unlock {context}.</h1>
          <span>Publish the owner identity players need before joining, paying or contacting support.</span>
          <div className="role-gate-actions">
            <button type="button" onClick={() => cards[0]?.selected
              ? navigate(roleSetupTargetFor(cards[0].role))
              : openRoleSelection(navigate, cards[0]?.role, "ownerTools")}>
              <Check size={17} />
              {cards[0]?.selected ? "Complete selected profile" : "Choose owner type"}
            </button>
            <button type="button" onClick={() => navigate("onboarding")}>
              <SlidersHorizontal size={17} />
              Open onboarding checklist
            </button>
          </div>
        </div>
      </section>
      <div className="role-gate-choice-grid">
        {cards.map(({ role, option, missing, selected }) => {
          const Icon = option.icon || UserCircle;
          return (
            <article key={role}>
              <Icon size={22} />
              <strong>{option.title || roleTitle(role)}</strong>
              <span>{selected ? `${missing.length} required item${missing.length === 1 ? "" : "s"} left.` : "This user type is not selected yet."}</span>
              <button type="button" onClick={() => selected
                ? navigate(roleSetupTargetFor(role))
                : openRoleSelection(navigate, role, "ownerTools")}>
                {selected ? roleSetupSteps[role]?.action || "Complete setup" : "Select this type"}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RequireRoleProfile({ role, accountProfile, navigate, context, children }) {
  if (roleReadyForTools(accountProfile, role)) return children;
  return <RoleOnboardingGate role={role} accountProfile={accountProfile} navigate={navigate} context={context} />;
}

function RequireRoleSubscription({
  role,
  entitlement,
  accountProfile,
  navigate,
  title,
  text,
  children
}) {
  const access = accountProfileSubscription(accountProfile, role);
  if (access.entitlements?.[entitlement] === true) return children;
  const plan = getProfileSubscriptionPlan(role);
  return (
    <section className="page role-subscription-gate-page">
      <section className="role-subscription-gate">
        <span className="role-subscription-gate-icon"><ShieldCheck size={28} /></span>
        <div>
          <p>{plan.freeName}</p>
          <h1>{title || `Upgrade to ${plan.proName} to use this tool.`}</h1>
          <span>{text || `Your public ${plan.roleLabel.toLowerCase()} profile remains live. ${plan.proName} unlocks the paid operating tools for this role.`}</span>
          <div className="role-gate-actions">
            <button type="button" className="secondary" onClick={() => navigate(role === "centre" ? "publicCentreProfile" : roleDashboardTargetFor(role))}>
              <ExternalLink size={17} />
              View public profile
            </button>
            <button type="button" className="secondary" onClick={() => navigate(role === "centre" ? "claimCentre" : roleSetupTargetFor(role))}>
              <Pencil size={17} />
              Edit free listing
            </button>
          </div>
        </div>
      </section>
      <ProfileSubscriptionPanel
        role={role}
        callFunction={callMarketplaceFunction}
        signedIn={Boolean(firebaseAuth?.currentUser)}
      />
    </section>
  );
}

function RequireAnyRoleProfile({ roles, accountProfile, navigate, context, children }) {
  if (roles.some((role) => roleReadyForTools(accountProfile, role))) return children;
  return <RoleOwnerOnboardingGate roles={roles} accountProfile={accountProfile} navigate={navigate} context={context} />;
}

const buildRoleDashboardConfig = (role, accountProfile = {}, venue = defaultVenue, payoutState = {}) => {
  const readiness = roleReadiness(accountProfile, role);
  const player = accountProfile.player || defaultAccountProfile.player;
  const group = accountProfile.group || defaultAccountProfile.group;
  const centre = accountProfile.centre || defaultAccountProfile.centre;
  const coach = accountProfile.coach || defaultAccountProfile.coach;
  const playerCurrency = accountRoleCurrency(accountProfile, "player");
  const organiserCurrency = accountRoleCurrency(accountProfile, "organiser");
  const centreCurrency = accountRoleCurrency(accountProfile, "centre");
  const coachCurrency = accountRoleCurrency(accountProfile, "coach");
  const centreSubscription = accountProfileSubscription(accountProfile, "centre");
  const centreProActive = centreSubscription.entitlements?.inPlatformBookings === true;
  const enabledPlayerAvailability = normaliseAvailabilitySchedule(player.availabilitySchedule).filter((row) => row.enabled).length;
  const enabledCoachAvailability = normaliseAvailabilitySchedule(coach.availabilitySchedule).filter((row) => row.enabled).length;
  const coachLessonServices = activeCoachLessonServices(coach);
  const coachHasPaidServices = coachLessonServices.some((service) => coachLessonServicePriceCents(service, coachCurrency) > 0);
  const coachRateLabel = coachPrimaryRateLabel(coach);
  const coachAreaLabel = publicProfileAreaLabel(coach, "Missing");
  const coachRequestMissing = [
    ...(coachLessonServices.length ? [] : ["lesson type and per-session price"]),
    ...(enabledCoachAvailability ? [] : ["weekly availability"]),
    ...(coach.timeZone ? [] : ["calendar time zone"]),
    ...(hasPublicProfileLocation(coach) ? [] : ["coaching location"])
  ];
  const coachRequestReady = roleHasPublishedBackendProfile(accountProfile, "coach") && coachRequestMissing.length === 0;
  const coachBookingOperationsReady = coachRequestReady && (!coachHasPaidServices || payoutState.ready === true);
  const playerMedia = cleanLinkRows(player.mediaLinks, "Player media");
  const coachMedia = cleanLinkRows(coach.mediaLinks, "Coach media");
  const centrePhotos = cleanLinkRows(centre.photoLinks, "Centre media");
  const organiserInvites = cleanEmailTags(group.inviteEmails?.length ? group.inviteEmails : group.invites);
  const organiserEvents = getOwnerEvents(accountProfile, "organiser");
  const centreEvents = getOwnerEvents(accountProfile, "centre");
  const joinedGroups = getJoinedGroups(accountProfile);
  const courtBookings = getAccountBookings(accountProfile);
  const managedBookings = (Array.isArray(accountProfile.managedBookings) ? accountProfile.managedBookings : []).map(normaliseCourtBooking);
  const centreBookings = mergeMarketplaceLedgerRows(
    courtBookings.filter((booking) => bookingMatchesCentre(booking, centre)),
    managedBookings
  );
  const managedPayments = Array.isArray(accountProfile.managedPayments) ? accountProfile.managedPayments : [];
  const playerLessonRows = mergeMarketplaceLedgerRows(
    accountProfile.lessonRequests || [],
    accountProfile.coachBookings || []
  ).map(normaliseCoachLesson).sort((first, second) => (second.startAtDate?.getTime?.() || 0) - (first.startAtDate?.getTime?.() || 0));
  const coachLessonRows = mergeMarketplaceLedgerRows(
    accountProfile.managedLessonRequests || [],
    accountProfile.managedCoachBookings || []
  ).map(normaliseCoachLesson).sort((first, second) => (second.startAtDate?.getTime?.() || 0) - (first.startAtDate?.getTime?.() || 0));
  const playerWallet = membershipWalletSummary(accountProfile);
  const playerGroupMemberships = getGroupMembershipPurchases(accountProfile);
  const activePlayerGroupMemberships = playerGroupMemberships.filter((purchase) => groupMembershipAccessStatuses.has(String(purchase.status || "").toLowerCase()));
  const reviews = Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [];
  const hostedGroupSessions = accountHasRole(accountProfile, "organiser")
    ? getGroupPublishedDatedOccurrences(group).map((session) => normaliseJoinedGroup({
      ...groupForPublicSession(group, session),
      id: `hosted-session-${session.sessionId || session.id}`,
      sessionId: session.sessionId || session.id,
      ownerHosted: true,
      joinedStatus: "Hosting",
      paymentStatus: "Hosting"
    }))
    : [];
  const upcomingPlayerGroups = mergeMarketplaceLedgerRows(joinedGroups, hostedGroupSessions);
  const weeklyPlan = playerWeeklyPlan(courtBookings, upcomingPlayerGroups);
  const weeklyPlayCount = weeklyPlan.bookings.length + weeklyPlan.groups.length;
  const inboxes = buildAccountInboxData(accountProfile);
  const inboxThreadsFor = (id) => (inboxes.find((inbox) => inbox.id === id)?.threads || []).filter(isMarketplaceConversationThread);
  const unreadForRole = (id) => inboxThreadsFor(id).reduce((total, thread) => total + Number(thread.unread || 0), 0);
  const playerMessageThreads = inboxThreadsFor("player");
  const organiserMessageThreads = inboxThreadsFor("organiser");
  const centreMessageThreads = inboxThreadsFor("centre");
  const coachMessageThreads = inboxThreadsFor("coach");
  const organiserMemberRows = buildMemberRowsFromAccount(accountProfile);
  const organiserGroupMembers = mergeMarketplaceLedgerRows(
    getPublicGroupMembers(group, accountProfile),
    organiserMemberRows.filter((member) => member.source === group.name || member.source === "Marketplace session")
  );
  const organiserPaidGroupMembers = organiserGroupMembers.filter((member) => paymentStatusTone(member.paymentStatus || member.status) === "paid");
  const organiserGroupRevenue = organiserPaidGroupMembers.length * getGroupPriceNumber(group);
  const organiserRevenue = eventRevenueFromRows(organiserEvents, accountProfile) + organiserGroupRevenue;
  const centreRevenue = eventRevenueFromRows(centreEvents, accountProfile);
  const paidCentreBookings = centreBookings.filter((booking) => paymentStatusTone(booking.paymentStatus || booking.status) === "paid");
  const centreBookingRevenue = paidCentreBookings.reduce((total, booking) => total + Number(booking.total || 0), 0);
  const playerBookingSpend = courtBookings.reduce((total, booking) => total + Number(booking.total || 0), 0);
  const accountPayments = getAccountPayments(accountProfile);
  const playerPaymentRows = [
    ...courtBookings,
    ...joinedGroups,
    ...getJoinedEvents(accountProfile),
    ...accountPayments.filter((payment) => ["player", "booking", "social", "event", "membership"].includes(payment.kind))
  ];
  const organiserPaymentRows = [
    ...organiserGroupMembers,
    ...organiserEvents.flatMap((event) => getPublicEventAttendees(event, accountProfile)),
    ...accountPayments.filter((payment) => ["organiser", "social", "group", "event"].includes(payment.kind)),
    ...managedPayments.filter((payment) => ["organiser", "social", "group", "event"].includes(payment.kind))
  ];
  const coachPaymentRows = mergeMarketplaceLedgerRows(
    accountPayments.filter((payment) => ["coach", "lesson", "coaching"].includes(payment.kind)),
    managedPayments.filter((payment) => ["coach", "lesson", "coaching"].includes(payment.kind))
  );
  const playerBookingSpendLabel = formatMoneyBreakdown(courtBookings, playerCurrency);
  const organiserRevenueLabel = formatMoneyBreakdown([
    ...organiserEvents.map((event) => ({
      amount: getPublicEventAttendees(event, accountProfile).filter((attendee) => paymentStatusTone(attendee.paymentStatus || attendee.status) === "paid").length * eventPriceNumber(event),
      currency: event.currency || organiserCurrency
    })),
    { amount: organiserGroupRevenue, currency: group.currency || organiserCurrency }
  ], organiserCurrency);
  const centreRevenueLabel = formatMoneyBreakdown([
    ...centreEvents.map((event) => ({
      amount: getPublicEventAttendees(event, accountProfile).filter((attendee) => paymentStatusTone(attendee.paymentStatus || attendee.status) === "paid").length * eventPriceNumber(event),
      currency: event.currency || centreCurrency
    })),
    ...paidCentreBookings
  ], centreCurrency);
  const coachRevenueLabel = formatMoneyBreakdown(coachPaymentRows, coachCurrency);
  const playerPaymentsNeedingAction = countPaymentsNeedingAction(playerPaymentRows);
  const organiserPaymentsNeedingAction = countPaymentsNeedingAction(organiserPaymentRows);
  const coachPaymentsNeedingAction = countPaymentsNeedingAction(coachPaymentRows);
  const coachLessonsNeedingAction = coachLessonRows.filter((lesson) => (
    ["requested", "reschedule_proposed", "accepted", "checkout_created"].includes(lesson.status)
  )).length;
  const organiserDatedSessions = getGroupPublishedDatedOccurrences(group);
  const organiserNextSession = organiserDatedSessions[0] || null;
  const organiserDashboardSessions = (Array.isArray(group.sessions) ? group.sessions : [])
    .map((session, index) => buildGroupSessionOccurrence(group, { ...session, isRecurring: false }, index))
    .filter((session) => !groupOccurrenceIsPast(session) && !["cancelled", "archived"].includes(String(session.status || "").toLowerCase()))
    .sort((first, second) => String(first.startAt || `${first.date}T${first.start}`).localeCompare(String(second.startAt || `${second.date}T${second.start}`)));
  const organiserScheduleLabel = hasGroupPlayingSchedule(group) ? getGroupScheduleLabel(group) : "Not scheduled";
  const firstMissing = readiness.missing.slice(0, 4);
  const missingAlerts = firstMissing.length
    ? firstMissing.map((item) => {
      const meta = missingFieldMeta(item);
      return [meta.title, meta.detail, "Finish"];
    })
    : [["Ready to use", "This profile has the required details people need before taking action.", "Ready"]];
  const operatingSections = buildRoleOperatingSections(role, {
    player,
    group,
    centre,
    coach,
    readiness,
    organiserEvents,
    centreEvents,
    courtBookings,
    centreBookings,
    joinedGroups,
    organiserInvites,
    organiserGroupMembers,
    organiserRevenue,
    organiserRevenueLabel,
    centreRevenue,
    centreBookingRevenue,
    centreRevenueLabel,
    playerBookingSpend,
    playerBookingSpendLabel,
    coachRevenueLabel,
    coachPaymentRows,
    playerCurrency,
    organiserCurrency,
    centreCurrency,
    coachCurrency,
    playerWallet,
    enabledPlayerAvailability,
    enabledCoachAvailability,
    venue,
    payoutState
  });

  if (role === "player") {
    return {
      role,
      icon: UserCircle,
      eyebrow: "Player",
      title: `${accountProfile.fullName || "Player"}'s player profile`,
      summary: "Keep your level, availability, bookings and invites in one place so organisers know when to include you.",
      readiness,
      operatingSections,
      weeklyPlan,
      playerLessonOperations: { lessons: playerLessonRows },
      commandPanel: {
        variant: "player",
        layout: "summary",
        eyebrow: "Player command",
        title: "Play, payments and invites in one place.",
        summary: "See your upcoming games, outstanding payments, invitations and profile readiness at a glance.",
        metrics: [
          [CalendarDays, "Upcoming play", `${weeklyPlayCount}`, weeklyPlayCount ? `${weeklyPlan.bookings.length} court booking${weeklyPlan.bookings.length === 1 ? "" : "s"} · ${weeklyPlan.groups.length} social session${weeklyPlan.groups.length === 1 ? "" : "s"}` : "No upcoming play"],
          [WalletCards, "Payments to check", `${playerPaymentsNeedingAction}`, playerPaymentsNeedingAction ? "Open bookings and groups with pending payment." : "No pending payment rows"],
          [MessageCircle, "Messages", `${playerMessageThreads.length}`, `${unreadForRole("player")} unread`],
          [UserCircle, "Profile", `${readiness.percent}%`, readiness.missing.length ? `${readiness.missing.length} setup item${readiness.missing.length === 1 ? "" : "s"} left` : "Ready for organisers"]
        ],
        rows: [
          ...weeklyPlan.bookings.slice(0, 2).map((booking) => ({
            icon: Building2,
            title: booking.venueName,
            detail: [formatDateLabel(booking.date), formatTimeLabel(booking.time), getBookingTotalLabel(booking)].filter(Boolean).join(" · "),
            status: booking.paymentStatus || "Payment pending",
            target: "bookings"
          })),
          ...weeklyPlan.groups.slice(0, 2).map((joinedGroup) => ({
            icon: UsersRound,
            title: joinedGroup.title,
            detail: [joinedGroup.venue, joinedGroup.timeLabel || joinedGroup.time, getGroupPriceLabel(joinedGroup)].filter(Boolean).join(" · "),
            status: joinedGroup.ownerHosted ? "Hosting" : joinedGroup.paymentStatus || "Payment pending",
            target: joinedGroup.ownerHosted ? "groupSessions" : paymentNeedsAction(joinedGroup.paymentStatus) ? "join" : "group",
            payload: { group: joinedGroup, bookingType: "social" }
          })),
          ...playerGroupMemberships.slice(0, 2).map((membership) => ({
            icon: UsersRound,
            title: membership.groupName || membership.title,
            detail: membership.membershipMode === "free" ? "Free group membership" : [membership.amount, membershipBillingLabels[membership.billingInterval]].filter(Boolean).join(" · "),
            status: groupMembershipAccessStatuses.has(String(membership.status || "").toLowerCase()) ? "Member" : membership.paymentStatus || membership.status,
            target: "groupsIndex"
          }))
        ],
        emptyTitle: "No upcoming play",
        emptyText: "Book a court, join a social group or publish an organiser session and the date, location and payment status will appear here.",
        actions: [
          ["My bookings", "bookings"],
          ["Find groups", "groupsIndex"],
          ["Find courts", "results"],
          ["Messages", "messages", roleDashboardMessageFocus.player],
          ["Update availability", "profileSetup"]
        ]
      },
      primaryAction: ["Update profile", "profileSetup"],
      secondaryActions: [["Preview public profile", "publicPlayerProfile"], ["Court bookings", "bookings"]],
      stats: [
        [Gauge, "Profile strength", `${readiness.percent}%`, `${readiness.missing.length} detail${readiness.missing.length === 1 ? "" : "s"} left`],
        [CalendarDays, "Court bookings", `${courtBookings.length}`, playerBookingSpend ? `${playerBookingSpendLabel} requested` : "No court bookings"],
        [UsersRound, "Upcoming play", `${weeklyPlayCount}`, weeklyPlayCount ? `${weeklyPlan.groups.length} social session${weeklyPlan.groups.length === 1 ? "" : "s"} · ${weeklyPlan.bookings.length} court booking${weeklyPlan.bookings.length === 1 ? "" : "s"}` : "No upcoming sessions"],
        [CalendarDays, "Availability windows", `${enabledPlayerAvailability}`, enabledPlayerAvailability ? "Ready for invites" : "Add times"],
        [Video, "Playing media", `${playerMedia.length}`, "Photos or social videos"],
        [MessageCircle, "Open messages", `${playerMessageThreads.length}`, `${unreadForRole("player")} unread`],
        [Heart, "Joined socials", `${joinedGroups.length}`, joinedGroups.length ? "Registered sessions" : "Join a group"],
        [UsersRound, "Group memberships", `${activePlayerGroupMemberships.length}`, activePlayerGroupMemberships.length ? "Active memberships" : playerGroupMemberships.length ? "Payment pending" : "No memberships"],
        [BadgeCheck, "Recommendations", `${reviews.filter((review) => review.kind === "recommendation").length}`, "From people you played with"],
        [ShieldCheck, "Privacy controls", player.visibility?.media === false ? "Limited" : "Open", "Media and invites"]
      ],
      operations: [
        ["Playing identity", player.skillBand || "Skill pending", player.suburb || "Suburb pending", readiness.percent >= 80 ? "Strong" : "Needs work"],
        ["Court bookings", `${courtBookings.length} requested`, courtBookings[0]?.venueName || "No bookings", courtBookings.length ? courtBookings[0].paymentStatus : "Not started"],
        ["Availability", enabledPlayerAvailability ? `${enabledPlayerAvailability} windows` : "Not added", player.formats || "Format pending", enabledPlayerAvailability ? "Invitable" : "Blocked"],
        ["Social sessions", `${joinedGroups.length} joined`, joinedGroups[0]?.title || "No joined groups", joinedGroups.length ? "Registered" : "Not started"],
        ["Group memberships", `${activePlayerGroupMemberships.length} active`, playerGroupMemberships[0]?.groupName || "No memberships", playerGroupMemberships.length ? playerGroupMemberships[0].status : "Not started"],
        ["Payment status", `${playerPaymentRows.length} record${playerPaymentRows.length === 1 ? "" : "s"}`, playerPaymentsNeedingAction ? `${playerPaymentsNeedingAction} need attention` : "Nothing due", playerPaymentsNeedingAction ? "Review" : "Current"]
      ],
      alerts: missingAlerts,
      activity: [
        ...dashboardActivityFromThreads(playerMessageThreads, "Invites, joined games, recommendations and booking messages will appear here."),
        ["Player profile", readiness.missing.length ? `${readiness.missing.length} profile detail${readiness.missing.length === 1 ? "" : "s"} still missing.` : "Ready for invites.", "Profile"]
      ].slice(0, 4),
      quickActions: [["Update availability", "profileSetup"], ["Open messages", "messages", roleDashboardMessageFocus.player], ["Find groups", "groupsIndex"], ["Find courts", "results"]]
    };
  }

  if (role === "organiser") {
    const hasGroup = Boolean(group.name || group.venue || hasGroupPlayingSchedule(group));
    return {
      role,
      icon: UsersRound,
      eyebrow: "Social organiser",
      title: "Manage your social games",
      summary: "Create sessions, invite players, track members and keep payments organised without turning it into admin work.",
      readiness,
      operatingSections,
      commandPanel: {
        variant: "organiser",
        eyebrow: "Organiser command",
        title: "Run every upcoming session without chasing people.",
        summary: "See every current session, its publishing status, member follow-up, payments and organiser events from one focused place.",
        primaryAction: hasGroup
          ? organiserNextSession ? null : ["Create next session", "createGroupSession"]
          : ["Create group profile", "createGroup"],
        metrics: [
          [CalendarDays, "Upcoming sessions", `${organiserDashboardSessions.length}`, organiserNextSession ? `Next: ${organiserNextSession.dateLabel} · ${organiserNextSession.time}` : hasGroupPlayingSchedule(group) ? `Regular schedule: ${organiserScheduleLabel}` : "Add a regular schedule, then publish a dated session"],
          [UsersRound, "Players", group.capacity ? `${organiserGroupMembers.length}/${group.capacity}` : `${organiserGroupMembers.length}`, organiserMemberRows.length ? `${organiserMemberRows.length} member record${organiserMemberRows.length === 1 ? "" : "s"}` : "No members yet"],
          [WalletCards, "Payments to chase", `${organiserPaymentsNeedingAction}`, organiserPaymentsNeedingAction ? "Some players need payment or follow-up." : organiserRevenue ? `${organiserRevenueLabel} tracked` : "No paid rows yet"],
          [Star, "Events", `${organiserEvents.length}`, `${organiserEvents.filter((event) => event.status === "published").length} public`]
        ],
        rows: [
          ...organiserDashboardSessions.map((session) => ({
            icon: CalendarDays,
            title: session.title || group.name || "Social session",
            detail: [
              session.dateLabel ? `${session.dateLabel} at ${session.time}` : session.time,
              session.venue || group.venue,
              session.priceLabel || getGroupPriceLabel({ ...group, ...session }, " per player") || "Free",
              session.capacityLabel || (session.capacity ? `${session.capacity} spots` : "")
            ].filter(Boolean).join(" · "),
            status: ["published", "active"].includes(String(session.status || "").toLowerCase()) ? "Session live" : session.status || "Draft",
            target: "editGroupSession",
            payload: { session }
          })),
          ...(!organiserDashboardSessions.length && hasGroup ? [{
            icon: UsersRound,
            title: group.name || "Social group",
            detail: [group.venue, organiserScheduleLabel, "No current dated session"].filter(Boolean).join(" · "),
            status: readiness.missing.length ? "Draft" : "Create session",
            target: "createGroupSession"
          }] : []),
          ...organiserMemberRows.slice(0, 3).map((member) => ({
            icon: UserCircle,
            title: member.name,
            detail: [member.contact, member.source].filter(Boolean).join(" · "),
            status: member.status || "Member",
            target: "members"
          })),
          ...organiserEvents.slice(0, 2).map((event) => ({
            icon: CalendarDays,
            title: event.title,
            detail: [formatDateLabel(event.date), formatTimeLabel(event.time), getEventVenueLabel(event)].filter(Boolean).join(" · "),
            status: event.status === "published" ? "Public" : "Draft",
            target: "manageEvent",
            payload: { event }
          }))
        ],
        emptyTitle: "No dated session activity yet",
        emptyText: "Use the reusable weekly group schedule, then publish the next actual date before players can reserve and pay.",
        actions: hasGroup
          ? [["Members", "members"], ["Events", "eventManager"], ["Payouts", "payouts"], ["Messages", "messages", roleDashboardMessageFocus.organiser]]
          : [["Create group profile", "createGroup"]]
      },
      primaryAction: ["Create session", "createGroupSession"],
      secondaryActions: [["Manage sessions", "groupSessions"], ["View public group", "publicGroupProfile"], ["Members", "members"], ["Payouts", "payouts"]],
      stats: [
        [Gauge, "Group strength", `${readiness.percent}%`, `${readiness.missing.length} detail${readiness.missing.length === 1 ? "" : "s"} left`],
        [UsersRound, "Session capacity", group.capacity || "0", hasGroup ? "Draft group" : "No group"],
        [CalendarDays, "Schedule", hasGroupPlayingSchedule(group) ? getGroupWhenLabel(group) : "Not set", group.isRecurring === false ? "Regular play time" : "Weekly recurring"],
        [Mail, "Invites", `${organiserInvites.length}`, "Email tags"],
        [Star, "Public events", `${organiserEvents.filter((event) => event.status === "published").length}`, `${organiserEvents.length} total`],
        [WalletCards, "Paid players", `${paidAttendeeCount(organiserEvents, accountProfile) + organiserPaidGroupMembers.length}`, organiserRevenue ? `${organiserRevenueLabel} session revenue` : "No paid attendees"],
        [ClipboardCheck, "Members", `${organiserMemberRows.length}`, "Invites and attendees"],
        [MessageCircle, "Unread messages", `${unreadForRole("organiser")}`, `${organiserMessageThreads.length} threads`]
      ],
      operations: [
        ["Group profile", group.name || "No group name", group.venue || "Venue missing", hasGroup ? "Draft" : "Not started"],
        ["Session setup", hasGroupPlayingSchedule(group) ? getGroupWhenLabel(group) : "Schedule missing", group.skillBand || "Level missing", getGroupPriceLabel(group) || "Price missing"],
        ...roleEventRows(organiserEvents)
      ],
      alerts: missingAlerts,
      activity: [
        ...dashboardActivityFromThreads(organiserMessageThreads, "Player questions, invite replies, payments and session updates will appear here."),
        ["Organiser events", organiserEvents.length ? `${organiserEvents.length} organiser event records found.` : "No organiser events created yet.", "Event status"]
      ].slice(0, 4),
      quickActions: [["Create session", "createGroupSession"], ["Manage sessions", "groupSessions"], ["Edit group profile", "createGroup"], ["Manage members", "members"], ["Open messages", "messages", roleDashboardMessageFocus.organiser], ["Review payouts", "payouts"]]
    };
  }

  if (role === "centre") {
    const centreName = centre.name || venue?.name || "Your centre";
    const centrePricingSummary = getCourtPricingSummary(centre.courtPricing, centre.pricing, centreCurrency);
    const centreVenue = managedVenueFromCentreProfile(centre, venue);
    const centreBookingState = getPlatformCentreBookingState(centreVenue, accountProfile);
    const centrePublishedCourts = publishedCourtInventory(centre, centreVenue);
    const centrePaymentRows = mergeMarketplaceLedgerRows(
      getAccountPayments(accountProfile).filter((payment) => payment.kind === "centre" || payment.kind === "booking"),
      managedPayments.filter((payment) => payment.kind === "centre" || payment.kind === "booking")
    );
    const centrePaymentsNeedingAction = countPaymentsNeedingAction([...centreBookings, ...centrePaymentRows]);
    const centreBookingSetupReady = centreBookingState.ready && (hasBookableCourtPrice(centreBookingState.venue) || hasCourtPricing(centreBookingState.venue.courtPricing));
    const centreCheckoutReady = centreBookingSetupReady && payoutState.ready === true;
    const centrePayoutStatus = payoutReadinessCopy(payoutState);
    const centreHours = summarizeAvailabilitySchedule(centre.openingSchedule) || centre.openingHours || centreVenue.openingHours || "";
    const centreHoursCompact = summarizeAvailabilityScheduleCompact(centre.openingSchedule, centre.openingHours || centreVenue.openingHours);
    return {
      role,
      subscription: centreSubscription,
      icon: Building2,
      eyebrow: "Centre owner",
      title: `Manage ${centreName}`,
      summary: centreProActive
        ? "Keep your centre profile, court setup, prices, events and booking requests clear for players."
        : "Keep your public centre listing accurate, then upgrade when you are ready to operate bookings through Badminta.",
      readiness,
      operatingSections: centreProActive ? operatingSections : [],
      centreOperations: {
        centre,
        venue: centreVenue,
        proToolsReady: centreProActive,
        bookingState: centreBookingState,
        bookingSetupReady: centreBookingSetupReady,
        checkoutReady: centreCheckoutReady,
        payoutState,
        publishedCourtCount: centrePublishedCourts.length,
        totalCourtCount: centreCourtCountNumber(centre, centreVenue),
        pricingSummary: centrePricingSummary,
        openingHours: centreHours,
        bookings: centreBookings.slice(0, 4),
        payments: centrePaymentRows.slice(0, 4),
        currency: centreCurrency,
        bookingRevenue: centreBookingRevenue,
        eventRevenue: centreRevenue,
        eventCount: centreEvents.length
      },
      workspaceStatus: {
        ready: centreProActive && centreCheckoutReady,
        eyebrow: "Centre status",
        label: centreProActive ? (centreCheckoutReady ? "Bookings ready" : "Pro setup") : "Free listing",
        detail: centreProActive
          ? centreCheckoutReady
            ? "Courts and checkout are ready"
            : centreBookingSetupReady
              ? centrePayoutStatus.label
              : `${centreBookingState.readiness?.missing?.length || 0} booking setup item${centreBookingState.readiness?.missing?.length === 1 ? "" : "s"} left`
          : "Public discovery stays free; booking and operating tools require Centre Pro."
      },
      commandPanel: centreProActive ? {
        variant: "centre",
        eyebrow: "Centre command",
        title: "Court operations, payments and bookings in one place.",
        summary: "See whether this centre can take bookings, which court requests need payment follow-up, and what owners should fix before public checkout.",
        primaryAction: centreCheckoutReady
          ? ["Preview booking", "booking", { venue: centreVenue, bookingType: "court" }]
          : centreBookingSetupReady
            ? ["Set up payouts", "centrePayments"]
            : ["Finish court setup", "courtManager"],
        metrics: [
          [Grid3X3, "Published courts", centrePublishedCourts.length ? `${centrePublishedCourts.length}` : "0", centreCourtCountNumber(centre, centreVenue) ? `${centreCourtCountNumber(centre, centreVenue)} total courts` : "Add court inventory"],
          [WalletCards, "Payments to check", `${centrePaymentsNeedingAction}`, centrePaymentsNeedingAction ? "Bookings or payment rows need follow-up." : centreBookingRevenue ? `${formatMoneyBreakdown(paidCentreBookings, centreCurrency)} booking revenue` : "No pending court payments"],
          [Clock3, "Opening hours", centreHours ? "Added" : "Missing", centreHoursCompact || "Add hours before checkout"],
          [ShieldCheck, "Booking status", centreCheckoutReady ? "Ready" : centreBookingSetupReady ? centrePayoutStatus.stage : "Blocked", centreCheckoutReady ? "Preview before accepting public payment." : centreBookingSetupReady ? centrePayoutStatus.detail : centreBookingState.readiness?.missing?.slice(0, 2).join(" · ") || "Finish owner setup"]
        ],
        rows: [
          ...centreBookings.slice(0, 3).map((booking) => ({
            icon: CalendarDays,
            title: booking.courtName || booking.venueName,
            detail: [formatDateLabel(booking.date), formatTimeLabel(booking.time), getBookingTotalLabel(booking)].filter(Boolean).join(" · "),
            status: booking.paymentStatus || booking.status || "Booking",
            target: "centrePayments"
          })),
          ...centreEvents.slice(0, 2).map((event) => ({
            icon: Star,
            title: event.title,
            detail: [formatDateLabel(event.date), formatTimeLabel(event.time), getEventVenueLabel(event)].filter(Boolean).join(" · "),
            status: event.status === "published" ? "Public" : "Draft",
            target: "manageEvent",
            payload: { event }
          }))
        ],
        emptyTitle: "No centre booking activity yet",
        emptyText: "Court requests, payment status, centre events and payout checks will appear here once players can book or join centre events.",
        actions: [["Manage courts", "courtManager"], ["Payments", "centrePayments"], ["Add/edit centre", "claimCentre"], ["Public centre", "publicCentreProfile"], ["Messages", "messages", roleDashboardMessageFocus.centre]]
      } : {
        variant: "centre",
        layout: "summary",
        eyebrow: "Centre Free",
        title: "Your centre listing remains public and discoverable.",
        summary: "Update the information players need at no cost. Centre Pro unlocks court booking, pricing automation, memberships, events, payments and the operating workspace.",
        metrics: [
          [Gauge, "Listing strength", `${readiness.percent}%`, readiness.missing.length ? `${readiness.missing.length} public detail${readiness.missing.length === 1 ? "" : "s"} left` : "Public details complete"],
          [ShieldCheck, "Owner status", centreBookingState.verifiedOwner ? "Verified" : centreBookingState.owned ? "In review" : "Directory", centreBookingState.ownershipLabel || "Owner-managed listing"],
          [Grid3X3, "Courts listed", centre.courtCount || centreCourtCountNumber(centre, centreVenue) || "0", "Basic venue information remains free"],
          [MessageCircle, "Messages", `${centreMessageThreads.length}`, `${unreadForRole("centre")} unread`]
        ],
        rows: [],
        actions: [["View public centre", "publicCentreProfile"], ["Add/edit centre", "claimCentre"], ["Search and claim", "centreClaimSearch"], ["Messages", "messages", roleDashboardMessageFocus.centre]]
      },
      primaryAction: centreProActive
        ? centreCheckoutReady
          ? ["Preview booking", "booking", { venue: centreVenue, bookingType: "court" }]
          : centreBookingSetupReady
            ? ["Set up payouts", "centrePayments"]
            : ["Finish court setup", "courtManager"]
        : ["View public centre", "publicCentreProfile"],
      secondaryActions: centreProActive
        ? [["Preview public centre", "publicCentreProfile"], ["Add/edit centre", "claimCentre"], ["Preview booking", "booking", { venue: centreVenue, bookingType: "court" }]]
        : [["Add/edit centre", "claimCentre"], ["Search and claim", "centreClaimSearch"]],
      stats: [
        [Gauge, "Listing strength", `${readiness.percent}%`, `${readiness.missing.length} detail${readiness.missing.length === 1 ? "" : "s"} left`],
        [Grid3X3, "Courts", centre.courtCount || "0", "Inventory count"],
        [CircleDollarSign, "Court fees", centrePricingSummary || centre.pricing || "Missing", hasCourtPricing(centre.courtPricing) ? "Schedule added" : "Add peak/off-peak"],
        [CalendarDays, "Opening hours", centreHours ? "Added" : "Missing", "When players can book"],
        [Camera, "Media assets", `${centrePhotos.length}`, "Photos and videos"],
        [Star, "Public events", `${centreEvents.filter((event) => event.status === "published").length}`, `${centreEvents.length} total`],
        [WalletCards, "Booking revenue", centreRevenueLabel, centreBookingRevenue ? `${paidCentreBookings.length} paid court booking${paidCentreBookings.length === 1 ? "" : "s"}` : centreRevenue ? "From paid centre events" : "No paid bookings"],
        [MessageCircle, "Unread messages", `${unreadForRole("centre")}`, `${centreMessageThreads.length} threads`]
      ],
      operations: [
        ["Centre profile", centre.contactEmail || centre.phone ? "Contact added" : "Contact missing", centre.website || "Website missing", readiness.percent >= 80 ? "Review ready" : "Needs details"],
        ["Court setup", centre.courtCount || "0 courts", centreHoursCompact || "Hours missing", centrePricingSummary || centre.pricing || "Pricing missing"],
        ["Court bookings", `${centreBookings.length} requested`, centreBookings[0]?.venueName || "No matching bookings", centreBookings.length ? centreBookings[0].paymentStatus : "Not started"],
        ...roleEventRows(centreEvents)
      ],
      alerts: missingAlerts,
      activity: [
        ...dashboardActivityFromThreads(centreMessageThreads, "Booking requests, claim updates, event questions and support messages will appear here."),
        ["Centre events", centreEvents.length ? `${centreEvents.length} centre event records found.` : "No centre events created yet.", "Event status"]
      ].slice(0, 4),
      quickActions: centreProActive
        ? [["View public centre", "publicCentreProfile"], ["Manage courts", "courtManager"], ["Preview booking", "booking", { venue: centreVenue, bookingType: "court" }], ["Add/edit centre", "claimCentre"], ["Search and claim", "centreClaimSearch"], ["Open messages", "messages", roleDashboardMessageFocus.centre], ["Payments", "centrePayments"]]
        : [["View public centre", "publicCentreProfile"], ["Add/edit centre", "claimCentre"], ["Search and claim", "centreClaimSearch"], ["Open messages", "messages", roleDashboardMessageFocus.centre]]
    };
  }

  return {
    role: "coach",
    icon: Trophy,
    eyebrow: "Coach",
    title: `${accountProfile.fullName || "Coach"}'s coaching profile`,
    summary: "Show your offer clearly, manage lesson enquiries and keep availability, pricing and payouts easy to find.",
    readiness,
    operatingSections,
    coachLessonOperations: { lessons: coachLessonRows },
    workspaceStatus: {
      ready: coachBookingOperationsReady,
      eyebrow: "Coach status",
      label: coachRequestReady ? (coachHasPaidServices ? (payoutState.ready ? "Paid lessons ready" : "Lesson requests ready") : "Free lessons ready") : "Profile live",
      detail: coachRequestReady
        ? coachHasPaidServices ? payoutState.ready ? "Enquiries and secure payment are ready" : payoutReadinessCopy(payoutState).label : "Free requests and calendar confirmation are ready"
        : `Add ${coachRequestMissing.slice(0, 2).join(" and ")}`
    },
    commandPanel: {
      variant: "coach",
      eyebrow: "Coach command",
      title: "Lessons, enquiries and proof in one place.",
      summary: "Keep the actions that grow coaching revenue close to the information players use before booking.",
      primaryAction: coachMessageThreads.length ? ["Open lesson messages", "messages", roleDashboardMessageFocus.coach] : ["Update lesson offer", "coachProfile"],
      metrics: [
        [CalendarDays, "Availability", `${enabledCoachAvailability}`, enabledCoachAvailability ? "Weekly lesson times listed" : "Add lesson times"],
        [Banknote, "Lessons", `${coachLessonServices.length}`, coachLessonServices.length ? coachRateLabel : "Add per-session prices"],
        [MessageCircle, "Lesson leads", `${coachLessonRows.length + coachMessageThreads.length}`, coachLessonRows.length ? `${coachLessonsNeedingAction} booking action${coachLessonsNeedingAction === 1 ? "" : "s"} pending` : `${unreadForRole("coach")} unread`],
        [WalletCards, "Payments", `${coachPaymentRows.length}`, coachPaymentsNeedingAction ? `${coachPaymentsNeedingAction} need follow-up` : coachPaymentRows.length ? `${coachRevenueLabel} recorded` : "No paid lessons yet"]
      ],
      rows: [
        ...coachLessonRows.slice(0, 3).map((lesson) => ({
          icon: CalendarDays,
          title: lesson.playerName || "Player lesson request",
          detail: [lesson.serviceName, coachLessonScheduleLabel(lesson), lesson.totalLabel].filter(Boolean).join(" · "),
          status: coachLessonStatusLabel(lesson.status),
          target: "coachDashboard"
        })),
        ...coachMessageThreads.slice(0, 3).map((thread) => ({
          icon: MessageCircle,
          title: thread.name || thread.type || "Lesson enquiry",
          detail: thread.preview || "Lesson conversation",
          status: thread.unread ? `${thread.unread} unread` : thread.status || "Open",
          target: "messages",
          payload: roleDashboardMessageFocus.coach
        })),
        ...coachPaymentRows.slice(0, 2).map((payment) => ({
          icon: WalletCards,
          title: payment.title || "Coach payment",
          detail: [payment.subject, payment.amount].filter(Boolean).join(" · "),
          status: payment.status || "Recorded",
          target: "coachPayouts"
        }))
      ].slice(0, 5),
      emptyTitle: "No lesson enquiries yet",
      emptyText: coachRequestMissing.length
        ? `Add ${coachRequestMissing.join(" and ")} before players can send a priced lesson request. Media and credentials remain recommended profile proof.`
        : "New player requests will appear here with the proposed time, goal and payment status.",
      actions: [["Lessons and calendar", "coachProfile"], ["View public coach", "publicCoachProfile"], ["Messages", "messages", roleDashboardMessageFocus.coach], ["Payouts", "coachPayouts"]]
    },
    primaryAction: ["Update coach profile", "coachProfile"],
    secondaryActions: [["Preview public coach", "publicCoachProfile"], ["Messages", "messages", roleDashboardMessageFocus.coach], ["Payouts", "coachPayouts"]],
    stats: [
      [Gauge, "Profile strength", `${readiness.percent}%`, `${readiness.missing.length} detail${readiness.missing.length === 1 ? "" : "s"} left`],
      [Banknote, "Lesson types", `${coachLessonServices.length}`, coachRateLabel || "Add per-session prices"],
      [CalendarDays, "Availability windows", `${enabledCoachAvailability}`, enabledCoachAvailability ? "Bookable" : "Add times"],
      [MapPinned, "Coaching area", coach.locations || coachAreaLabel, "Where you coach"],
    [Video, "Training media", `${coachMedia.length}`, "Clips and examples"],
      [MessageCircle, "Lesson leads", `${coachMessageThreads.length}`, `${unreadForRole("coach")} unread`],
      [BadgeCheck, "Recommendations", `${reviews.filter((review) => review.kind === "coach").length}`, "Student feedback"],
      [WalletCards, "Payouts", coachPaymentRows.length ? coachRevenueLabel : formatMoney(0, coachCurrency), coachPaymentRows.length ? "Recorded lesson payments" : "No paid lessons"]
    ],
    operations: [
      ["Coach profile", coach.headline || "Headline missing", coach.accreditation || "Accreditation missing", readiness.percent >= 80 ? "Strong" : "Needs work"],
      ["Lesson setup", coachLessonServices.length ? `${coachLessonServices.length} active service${coachLessonServices.length === 1 ? "" : "s"}` : "Lesson prices missing", enabledCoachAvailability ? `${enabledCoachAvailability} windows` : "Availability missing", coach.locations || coachAreaLabel],
      ["Why book this coach", `${coachMedia.length} media links`, `${cleanLinkRows(coach.socialLinks, "Coach social").length} public links`, "Profile"]
    ],
    alerts: missingAlerts,
    activity: [
      ...dashboardActivityFromThreads(coachMessageThreads, "Lesson enquiries, testimonials, booking questions and payout updates will appear here."),
      ["Coach profile", readiness.missing.length ? `${readiness.missing.length} coach detail${readiness.missing.length === 1 ? "" : "s"} still missing.` : "Ready for lesson enquiries.", "Profile"]
    ].slice(0, 4),
    quickActions: [["Edit coach profile", "coachProfile"], ["View public coach", "publicCoachProfile"], ["Create clinic", "createGroup"], ["Open messages", "messages", roleDashboardMessageFocus.coach], ["Review payouts", "coachPayouts"]]
  };
};

const roleActionIconFor = (label = "", target = "") => {
  const action = `${label} ${target}`.toLowerCase();
  if (action.includes("message")) return MessageCircle;
  if (action.includes("payout") || action.includes("payment")) return WalletCards;
  if (action.includes("member")) return UsersRound;
  if (action.includes("event")) return Star;
  if (action.includes("court")) return Building2;
  if (action.includes("public") || action.includes("preview")) return ExternalLink;
  if (action.includes("profile")) return UserCircle;
  if (action.includes("create")) return Plus;
  if (action.includes("session") || action.includes("booking") || action.includes("play plan")) return CalendarDays;
  if (action.includes("find")) return Search;
  return ChevronRight;
};

function RoleActionButton({ label, target, payload, navigate, tone = "secondary" }) {
  const ActionIcon = roleActionIconFor(label, target);
  return (
    <button
      type="button"
      className={`role-action-button role-action-button--${tone}`}
      onClick={() => navigate(target, payload || {})}
    >
      <ActionIcon size={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function RoleOperatingSuite({ sections = [], navigate, role = "account", alwaysVisible = false }) {
  if (!sections.length) return null;
  const roleLabel = roleTitle(role).toLowerCase();
  return (
    <details
      className={`role-operating-suite ${alwaysVisible ? "always-visible" : ""}`}
      open={alwaysVisible || undefined}
      onToggle={(event) => {
        if (alwaysVisible && !event.currentTarget.open) event.currentTarget.open = true;
      }}
    >
      <summary className="role-operating-summary" onClick={alwaysVisible ? (event) => event.preventDefault() : undefined}>
        <span><SlidersHorizontal size={19} /></span>
        <div>
          <strong>{alwaysVisible ? `${roleTitle(role)} management` : `More ${roleLabel} tools`}</strong>
          <small>{alwaysVisible ? "Bookings, payments, availability and profile controls" : "Bookings, payments, profile controls and growth tools"}</small>
        </div>
        {!alwaysVisible && <ChevronDown className="role-operating-chevron" size={20} />}
      </summary>
      <div className="role-operating-content">
        <div className="role-operating-head">
          <div>
            <p>Operating tools</p>
            <h2>{alwaysVisible ? "Manage the work behind this profile" : "Everything else for this role"}</h2>
          </div>
          <span><CreditCard size={16} /> These records stay connected to the same account and payment ledger.</span>
        </div>
        <div className="role-operating-grid">
          {sections.map(({ icon: SectionIcon, title, pain, controls }) => (
            <article className="role-operating-card" key={title}>
              <div className="role-operating-card-head">
                <span><SectionIcon size={20} /></span>
                <div>
                  <strong>{title}</strong>
                  <p>{pain}</p>
                </div>
              </div>
              <div className="role-control-list">
                {controls.map(([label, value, detail, status, action]) => (
                  <div className="role-control-row" key={`${title}-${label}`}>
                    <div>
                      <span>{label}</span>
                      <strong>{value}</strong>
                      <p>{detail}</p>
                    </div>
                    <em>{status}</em>
                    <button type="button" onClick={() => navigate(action.target, action.payload || {})}>
                      {action.label}
                      <ChevronRight size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </details>
  );
}

function PlayerWeeklyPlanPanel({ plan = {}, navigate }) {
  const bookings = Array.isArray(plan.bookings) ? plan.bookings : [];
  const groups = Array.isArray(plan.groups) ? plan.groups : [];
  const hasRows = bookings.length || groups.length;
  const openGroup = (group) => {
    if (group.ownerHosted) {
      navigate("groupSessions");
      return;
    }
    const paymentTone = paymentStatusTone(group.paymentStatus);
    navigate(paymentTone === "paid" || paymentTone === "free" ? "group" : "join", { group, bookingType: "social" });
  };
  return (
    <section className="player-week-panel">
      <div className="role-panel-head">
        <p>Upcoming</p>
        <h2>Your courts and groups</h2>
      </div>
      {hasRows ? (
        <div className="player-week-grid">
          <div>
            <div className="player-week-section-head">
              <CalendarDays size={18} />
              <strong>Court bookings</strong>
              <button type="button" onClick={() => navigate("results")}><Search size={14} /> Find courts</button>
            </div>
            <div className="player-week-list">
              {bookings.length ? bookings.map((booking) => (
                <button type="button" className="player-week-row" key={booking.id} onClick={() => navigate("bookings")}>
                  <span className="player-week-icon"><Building2 size={17} /></span>
                  <span>
                    <strong>{booking.venueName}</strong>
                    <small>{[formatDateLabel(booking.date), formatTimeLabel(booking.time), getBookingTotalLabel(booking)].filter(Boolean).join(" · ")}</small>
                  </span>
                  <em className={`payment-chip ${paymentStatusTone(booking.paymentStatus)}`}>{booking.paymentStatus || "Payment pending"}</em>
                  <ChevronRight size={15} />
                </button>
              )) : (
                <div className="player-week-empty">
                  <strong>No upcoming court bookings</strong>
                  <span>Book a court and payment status will appear here.</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="player-week-section-head">
              <UsersRound size={18} />
              <strong>Social sessions</strong>
              <button type="button" onClick={() => navigate("groupsIndex")}>Find groups</button>
            </div>
            <div className="player-week-list">
              {groups.length ? groups.map((group) => (
                <button type="button" className="player-week-row" key={group.id} onClick={() => openGroup(group)}>
                  <span className="player-week-icon"><UsersRound size={17} /></span>
                  <span>
                    <strong>{group.title}</strong>
                    <small>{[group.venue, group.date ? formatDateLabel(group.date) : "", group.timeLabel || (isClockTimeValue(group.time) ? formatTimeLabel(group.time) : group.time), getGroupPriceLabel(group)].filter(Boolean).join(" · ")}</small>
                  </span>
                  <em className={`payment-chip ${group.ownerHosted ? "paid" : paymentStatusTone(group.paymentStatus)}`}>{group.ownerHosted ? "Hosting" : group.paymentStatus || "Payment pending"}</em>
                  <ChevronRight size={15} />
                </button>
              )) : (
                <div className="player-week-empty">
                  <strong>No upcoming social sessions</strong>
                  <span>Joined and hosted sessions will appear here with their payment or hosting status.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="player-week-empty full">
          <strong>No upcoming play</strong>
          <span>Court bookings and joined or hosted social sessions will appear here with their current status.</span>
          <div>
            <RoleActionButton label="Find courts" target="results" navigate={navigate} tone="primary" />
            <RoleActionButton label="Find groups" target="groupsIndex" navigate={navigate} />
          </div>
        </div>
      )}
    </section>
  );
}

function RoleCommandPanel({ panel, navigate }) {
  if (!panel) return null;
  const rows = Array.isArray(panel.rows) ? panel.rows.filter(Boolean) : [];
  const actions = Array.isArray(panel.actions) ? panel.actions.filter(Boolean) : [];
  const actionButtons = actions.map(([label, target, payload]) => {
    const ActionIcon = roleActionIconFor(label, target);
    return (
      <button type="button" key={`${label}-${target}`} onClick={() => navigate(target, payload || {})}>
        <ActionIcon className="role-command-action-icon" size={17} aria-hidden="true" />
        <span>{label}</span>
        <ChevronRight size={15} aria-hidden="true" />
      </button>
    );
  });
  return (
    <section className={`role-command-panel role-command-${panel.variant || "general"}`}>
      <div className="role-command-head">
        <div>
          <p>{panel.eyebrow || "Command centre"}</p>
          <h2>{panel.title}</h2>
          <span>{panel.summary}</span>
        </div>
        {panel.primaryAction && (
          <RoleActionButton
            label={panel.primaryAction[0]}
            target={panel.primaryAction[1]}
            payload={panel.primaryAction[2]}
            navigate={navigate}
            tone="primary"
          />
        )}
      </div>
      <div className="role-command-metrics">
        {(panel.metrics || []).map(([MetricIcon, label, value, detail]) => (
          <article key={label}>
            <MetricIcon size={18} />
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
          </article>
        ))}
      </div>
      {panel.layout === "summary" ? (
        <div className="role-command-summary-actions">
          {actionButtons}
        </div>
      ) : <div className="role-command-body">
        <section>
          <div className="role-panel-head">
            <p>Current activity</p>
            <h2>{rows.length ? "What needs attention" : panel.emptyTitle || "No activity yet"}</h2>
          </div>
          {rows.length ? (
            <div className="role-command-list">
              {rows.map(({ icon: RowIcon = Activity, title, detail, status, target, payload }, index) => (
                <button type="button" key={`${title}-${index}`} onClick={() => navigate(target || panel.primaryAction?.[1] || "account", payload || {})}>
                  <span className="role-command-row-icon"><RowIcon size={17} /></span>
                  <span>
                    <strong>{title}</strong>
                    <small>{detail}</small>
                  </span>
                  {status && <em className={`payment-chip ${paymentStatusTone(status)}`}>{status}</em>}
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          ) : (
            <div className="role-command-empty">
              <strong>{panel.emptyTitle || "No activity yet"}</strong>
              <span>{panel.emptyText || "Real bookings, sessions, messages and payment records will appear here."}</span>
            </div>
          )}
        </section>
        <aside>
          <div className="role-panel-head">
            <p>Next actions</p>
            <h2>Keep moving</h2>
          </div>
          <div className="role-command-actions">
            {actionButtons}
          </div>
        </aside>
      </div>}
    </section>
  );
}

function CentreOperationsPanel({ operations = {}, navigate }) {
  const centre = operations.centre || {};
  const venue = operations.venue || operations.bookingState?.venue || {};
  if (operations.proToolsReady !== true) {
    const centrePlan = getProfileSubscriptionPlan("centre");
    const showCentreUpgrade = () => {
      document.getElementById("centre-subscription")?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    return (
      <section className="centre-operations-panel centre-operations-locked">
        <div className="centre-operations-lock-copy">
          <span className="centre-operations-lock-icon"><ShieldCheck size={24} /></span>
          <div>
            <p>{centrePlan.freeName}</p>
            <h2>Upgrade when you are ready to run bookings through Badminta.</h2>
            <span>Your public listing remains live for free. {centrePlan.proName} is required for revenue and operating tools.</span>
          </div>
          <button type="button" onClick={showCentreUpgrade}>
            View Centre Pro
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="centre-operations-lock-features">
          {[
            "Court booking calendar and slot controls",
            "Peak, off-peak and weekend pricing",
            "Player checkout, refunds and payout records",
            "Memberships and prepaid court credits",
            "Centre-run tournaments, clinics and events"
          ].map((feature) => <span key={feature}><Check size={15} /> {feature}</span>)}
        </div>
      </section>
    );
  }
  const bookingState = operations.bookingState || getPlatformCentreBookingState(venue);
  const readiness = bookingState.readiness || { missing: [] };
  const publishedCourtCount = Number(operations.publishedCourtCount || 0);
  const totalCourtCount = Number(operations.totalCourtCount || 0);
  const currency = normaliseCurrencyCode(operations.currency || centre.currency || venue.currency || marketplaceCurrencyForCountry(centre.countryCode || centre.location?.countryCode || venue.countryCode || venue.country));
  const pricingSummary = operations.pricingSummary || getCourtPricingSummary(centre.courtPricing, centre.pricing || venue.priceLabel || "", currency);
  const openingHours = operations.openingHours || "";
  const missingText = readiness.missing?.length ? readiness.missing.slice(0, 3).join(", ") : "No blocking setup item";
  const bookingSetupReady = Boolean(operations.bookingSetupReady || operations.checkoutReady);
  const payoutState = operations.payoutState || {};
  const payoutStatus = payoutReadinessCopy(payoutState);
  const payoutChecking = payoutState.status === "checking";
  const checkoutReady = Boolean(operations.checkoutReady && payoutState.ready);
  const bookingRows = Array.isArray(operations.bookings) ? operations.bookings : [];
  const paymentRows = Array.isArray(operations.payments) ? operations.payments : [];
  const bookingActionPayload = { venue: bookingState.venue || venue, bookingType: "court" };
  const operationRows = [
    {
      icon: ShieldCheck,
      label: "Owner verification",
      value: bookingState.verifiedOwner ? "Verified owner" : bookingState.owned ? "Awaiting review" : "Directory only",
      detail: bookingState.verifiedOwner ? "This centre is owner-managed and verified by Badminta." : "Bookings stay closed until ownership is claimed or created and then verified.",
      complete: bookingState.verifiedOwner,
      action: bookingState.verifiedOwner ? "Review" : "Verify",
      target: bookingState.owned ? "claimCentre" : "centreClaimSearch",
      payload: { venue }
    },
    {
      icon: Grid3X3,
      label: "Published courts",
      value: totalCourtCount ? `${publishedCourtCount}/${totalCourtCount}` : "Not added",
      detail: "Only published courts appear in the player booking path.",
      complete: publishedCourtCount > 0,
      action: "Courts",
      target: "courtManager"
    },
    {
      icon: CircleDollarSign,
      label: "Court fees",
      value: pricingSummary || "Not added",
      detail: "Players need standard, peak, off-peak or weekend pricing before checkout.",
      complete: Boolean(pricingSummary),
      action: "Fees",
      target: "courtManager"
    },
    {
      icon: Clock3,
      label: "Opening hours",
      value: openingHours ? "Added" : "Not added",
      detail: openingHours || "Use weekly business hours so the booking hold can validate requested play time.",
      complete: Boolean(openingHours),
      action: "Hours",
      target: "courtManager"
    },
    {
      icon: CreditCard,
      label: "Checkout",
      value: checkoutReady ? "Ready for payment" : bookingSetupReady ? payoutStatus.label : "Blocked",
      detail: checkoutReady
        ? "Court setup and Badminta Pay are ready. Preview the player checkout before opening public bookings."
        : bookingSetupReady
          ? payoutStatus.detail
          : `Needs ${missingText}.`,
      complete: checkoutReady,
      action: checkoutReady ? "Preview" : bookingSetupReady ? (payoutChecking ? "Checking" : "Payments") : "Finish setup",
      target: checkoutReady ? "booking" : bookingSetupReady ? "centrePayments" : "courtManager",
      payload: checkoutReady ? bookingActionPayload : {}
    }
  ];

  return (
    <section className="centre-operations-panel">
      <div className="centre-operations-head">
        <div>
          <p>Operations</p>
          <h2>Run your centre</h2>
          <span>{checkoutReady ? "Court booking is live. Manage inventory, fees, hours and payments below." : bookingSetupReady ? `Court setup is complete. ${payoutStatus.label}.` : `Complete ${missingText} to open bookings.`}</span>
        </div>
        <button type="button" disabled={payoutChecking} onClick={() => navigate(checkoutReady ? "booking" : bookingSetupReady ? "centrePayments" : "courtManager", checkoutReady ? bookingActionPayload : {})}>
          {checkoutReady ? "Preview booking" : bookingSetupReady ? payoutChecking ? "Checking payouts" : "Set up payouts" : "Finish setup"}
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="centre-operations-grid">
        {operationRows.map(({ icon: RowIcon, label, value, detail, complete, action, target, payload }) => (
          <article className={complete ? "complete" : ""} key={label}>
            <RowIcon size={19} />
            <span>{label}</span>
            <strong>{value}</strong>
            <p>{detail}</p>
            <button type="button" onClick={() => navigate(target, payload || {})}>{action}</button>
          </article>
        ))}
      </div>
      <div className="centre-operations-bottom">
        <section>
          <div className="role-panel-head">
            <p>Booking requests</p>
            <h2>Latest court ledger</h2>
          </div>
          {bookingRows.length ? (
            <div className="centre-booking-ledger">
              {bookingRows.map((booking) => (
                <button type="button" key={booking.id} onClick={() => navigate("centrePayments")}>
                  <span>
                    <strong>{booking.courtName || booking.venueName}</strong>
                    <small>{[formatDateLabel(booking.date), formatTimeLabel(booking.time), booking.rateType, getBookingTotalLabel(booking)].filter(Boolean).join(" · ")}</small>
                  </span>
                  <em className={`payment-chip ${paymentStatusTone(booking.paymentStatus)}`}>{booking.paymentStatus || "Payment pending"}</em>
                </button>
              ))}
            </div>
          ) : (
            <div className="centre-ops-empty">
              <strong>No court requests yet</strong>
              <span>Player court requests will appear here with date, court, payment status, refund state and payout context.</span>
            </div>
          )}
        </section>
        <section>
          <div className="role-panel-head">
            <p>Money</p>
            <h2>Revenue and payout view</h2>
          </div>
          <div className="centre-money-grid">
            <article>
              <span>Booking revenue</span>
              <strong>{formatMoney(Number(operations.bookingRevenue || 0), operations.currency)}</strong>
            </article>
            <article>
              <span>Centre events</span>
              <strong>{formatMoney(Number(operations.eventRevenue || 0), operations.currency)}</strong>
            </article>
            <article>
              <span>Payment rows</span>
              <strong>{paymentRows.length}</strong>
            </article>
          </div>
          <button type="button" className="centre-money-action" onClick={() => navigate("centrePayments")}>
            Open payments and memberships
            <ChevronRight size={16} />
          </button>
        </section>
      </div>
    </section>
  );
}

const coachLessonStatusLabel = (value = "") => String(value || "requested")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const coachLessonScheduleLabel = (lesson = {}, proposed = false) => {
  const date = proposed ? lesson.proposedDate : lesson.date;
  const time = proposed ? lesson.proposedTime : lesson.time;
  const duration = proposed ? lesson.proposedDurationMinutes : lesson.durationMinutes;
  return [date ? formatDateLabel(date) : "Date pending", time ? formatTimeLabel(time) : "Time pending", duration ? `${duration} min` : ""]
    .filter(Boolean)
    .join(" · ");
};

function PlayerCoachLessonsPanel({ operations = {}, navigate }) {
  const lessons = (operations.lessons || []).map(normaliseCoachLesson).slice(0, 8);
  const [state, setState] = useState({ busyId: "", message: "", status: "idle" });
  const runAction = async (lesson, action) => {
    setState({ busyId: lesson.id, message: "Updating lesson...", status: "loading" });
    try {
      const result = await callMarketplaceFunction("updateCoachLessonRequest", {
        lessonRequestId: lesson.lessonRequestId || lesson.id,
        action
      });
      setState({
        busyId: "",
        message: action === "accept_reschedule"
          ? result.paymentStatus === "not_required"
            ? "New lesson time accepted and confirmed. No payment is required."
            : result.paymentStatus === "pay_at_venue"
              ? "New lesson time accepted and confirmed. Pay the coach directly as arranged."
            : "New lesson time accepted. Secure payment is now available."
          : "Lesson request cancelled.",
        status: "ready"
      });
      return result;
    } catch (error) {
      setState({ busyId: "", message: marketplaceFunctionErrorMessage(error), status: "error" });
      return null;
    }
  };
  const payLesson = async (lesson) => {
    setState({ busyId: lesson.id, message: "Opening secure lesson checkout...", status: "loading" });
    try {
      const checkout = await callMarketplaceFunction("createStripeCheckoutSession", {
        kind: "coach",
        sourceId: lesson.lessonRequestId || lesson.id,
        idempotencyKey: lesson.lessonRequestId || lesson.id,
        successUrl: `${window.location.origin}/payments/stripe/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href
      });
      setState({ busyId: "", message: "Secure checkout is ready.", status: "ready" });
      if (checkout.checkoutUrl) window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setState({ busyId: "", message: marketplaceFunctionErrorMessage(error), status: "error" });
    }
  };

  return (
    <section className="lesson-operations-panel player-lessons-panel">
      <div className="lesson-operations-head">
        <div>
          <p>Coaching lessons</p>
          <h2>Requests, accepted times and payments</h2>
          <span>Payment only opens after the coach accepts and reserves a lesson time.</span>
        </div>
        <button type="button" onClick={() => navigate("playersIndex")}><Search size={16} /> Find coaches</button>
      </div>
      {state.message && <div className={`auth-message ${state.status}`}>{state.message}</div>}
      {lessons.length ? (
        <div className="lesson-operations-list">
          {lessons.map((lesson) => {
            const busy = state.busyId === lesson.id;
            const awaitingPayment = ["accepted", "checkout_created"].includes(lesson.status)
              && ["requires_payment", "checkout_created", "failed"].includes(lesson.paymentStatus);
            const proposed = lesson.status === "reschedule_proposed";
            const closed = ["declined", "cancelled", "refunded", "completed", "payment_expired"].includes(lesson.status);
            return (
              <article key={lesson.id}>
                <span className="lesson-state-icon"><Trophy size={18} /></span>
                <div className="lesson-operations-copy">
                  <strong>{lesson.coachName}</strong>
                  <span>{coachLessonScheduleLabel(lesson)}</span>
                  {proposed && <small>New time: {coachLessonScheduleLabel(lesson, true)}</small>}
                  <small>{[lesson.serviceName, lesson.lessonFormat ? coachLessonStatusLabel(lesson.lessonFormat) : "Lesson", lesson.locationLabel, lesson.totalLabel].filter(Boolean).join(" · ")}</small>
                </div>
                <div className="lesson-status-stack">
                  <em className={`payment-chip ${paymentStatusTone(lesson.status)}`}>{coachLessonStatusLabel(lesson.status)}</em>
                  <span>{coachLessonStatusLabel(lesson.paymentStatus)}</span>
                </div>
                <div className="lesson-row-actions">
                  {proposed && <button type="button" onClick={() => runAction(lesson, "accept_reschedule")} disabled={busy}><Check size={15} /> Accept time</button>}
                  {awaitingPayment && <button type="button" onClick={() => payLesson(lesson)} disabled={busy}><WalletCards size={15} /> Pay securely</button>}
                  {!closed && !["confirmed", "completed", "cancellation_requested"].includes(lesson.status) && (
                    <button type="button" className="secondary" onClick={() => runAction(lesson, "cancel")} disabled={busy}><X size={15} /> Cancel</button>
                  )}
                  {["confirmed"].includes(lesson.status) && <button type="button" className="secondary" onClick={() => runAction(lesson, "cancel")} disabled={busy}>Request cancellation</button>}
                  <button type="button" className="secondary icon-only" aria-label="Open messages" title="Open messages" onClick={() => navigate("messages")}><MessageCircle size={16} /></button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="lesson-operations-empty">
          <Trophy size={21} />
          <strong>No coaching requests yet</strong>
          <span>Request a time from a published coach. The request appears here before any payment is taken.</span>
        </div>
      )}
    </section>
  );
}

function CoachLessonOperationsPanel({ operations = {}, navigate }) {
  const lessons = (operations.lessons || []).map(normaliseCoachLesson).slice(0, 10);
  const [state, setState] = useState({ busyId: "", message: "", status: "idle" });
  const [reschedule, setReschedule] = useState({ lessonId: "", date: "", time: "", durationMinutes: "60", reason: "" });
  const runAction = async (lesson, action, extra = {}) => {
    setState({ busyId: lesson.id, message: "Updating lesson...", status: "loading" });
    try {
      await callMarketplaceFunction("updateCoachLessonRequest", {
        lessonRequestId: lesson.lessonRequestId || lesson.id,
        action,
        ...extra
      });
      setState({ busyId: "", message: `Lesson ${coachLessonStatusLabel(action).toLowerCase()} saved.`, status: "ready" });
      setReschedule({ lessonId: "", date: "", time: "", durationMinutes: "60", reason: "" });
    } catch (error) {
      setState({ busyId: "", message: marketplaceFunctionErrorMessage(error), status: "error" });
    }
  };
  const beginReschedule = (lesson) => setReschedule({
    lessonId: lesson.id,
    date: lesson.date || getIsoDate(1),
    time: lesson.time || "18:00",
    durationMinutes: String(lesson.durationMinutes || 60),
    reason: ""
  });
  const submitReschedule = (lesson) => {
    const startAt = coachLocalDateTimeToUtc(
      reschedule.date,
      reschedule.time || "18:00",
      lesson.timeZone || currentBrowserTimeZone()
    );
    if (!reschedule.date || !startAt || Number.isNaN(startAt.getTime())) {
      setState({ busyId: "", message: "Choose a valid new lesson date and time.", status: "error" });
      return;
    }
    runAction(lesson, "reschedule", {
      startAt: startAt.toISOString(),
      durationMinutes: Number.parseInt(reschedule.durationMinutes, 10) || 60,
      reason: reschedule.reason
    });
  };

  return (
    <section className="lesson-operations-panel coach-lessons-panel">
      <div className="lesson-operations-head">
        <div>
          <p>Lesson operations</p>
          <h2>Accept a time before the player pays</h2>
          <span>Every accepted time is collision-checked and held while the player completes payment.</span>
        </div>
        <button type="button" onClick={() => navigate("coachProfile")}><Clock3 size={16} /> Edit availability</button>
      </div>
      {state.message && <div className={`auth-message ${state.status}`}>{state.message}</div>}
      {lessons.length ? (
        <div className="lesson-operations-list">
          {lessons.map((lesson) => {
            const busy = state.busyId === lesson.id;
            const requested = ["requested", "pending_payment", "payment_expired", "failed"].includes(lesson.status);
            const unpaidAccepted = ["accepted", "checkout_created"].includes(lesson.status) && !["paid", "partially_refunded"].includes(lesson.paymentStatus);
            const confirmed = ["confirmed", "paid"].includes(lesson.status) && ["paid", "partially_refunded", "dispute_won", "not_required"].includes(lesson.paymentStatus);
            const editing = reschedule.lessonId === lesson.id;
            return (
              <article key={lesson.id} className={editing ? "editing" : ""}>
                <span className="lesson-state-icon"><UserCircle size={18} /></span>
                <div className="lesson-operations-copy">
                  <strong>{lesson.playerName}</strong>
                  <span>{coachLessonScheduleLabel(lesson)}</span>
                  <small>{[lesson.serviceName, lesson.lessonFormat ? coachLessonStatusLabel(lesson.lessonFormat) : "Lesson", lesson.locationLabel, lesson.totalLabel].filter(Boolean).join(" · ")}</small>
                  {lesson.note && <small className="lesson-player-note">“{lesson.note}”</small>}
                </div>
                <div className="lesson-status-stack">
                  <em className={`payment-chip ${paymentStatusTone(lesson.status)}`}>{coachLessonStatusLabel(lesson.status)}</em>
                  <span>{coachLessonStatusLabel(lesson.paymentStatus)}</span>
                </div>
                <div className="lesson-row-actions">
                  {requested && <button type="button" onClick={() => runAction(lesson, "accept")} disabled={busy}><Check size={15} /> Accept</button>}
                  {(requested || unpaidAccepted) && <button type="button" className="secondary" onClick={() => beginReschedule(lesson)} disabled={busy}><CalendarClock size={15} /> New time</button>}
                  {(requested || unpaidAccepted) && <button type="button" className="secondary" onClick={() => runAction(lesson, "decline")} disabled={busy}><X size={15} /> Decline</button>}
                  {confirmed && <button type="button" onClick={() => runAction(lesson, "complete")} disabled={busy}><BadgeCheck size={15} /> Complete</button>}
                  {confirmed && <button type="button" className="secondary" onClick={() => runAction(lesson, "cancel")} disabled={busy}>{lesson.paymentStatus === "not_required" ? "Cancel lesson" : "Request cancellation"}</button>}
                  <button type="button" className="secondary icon-only" aria-label="Message player" title="Message player" onClick={() => navigate("messages")}><MessageCircle size={16} /></button>
                </div>
                {editing && (
                  <div className="lesson-reschedule-editor">
                    <label><span>New date</span><input type="date" min={getIsoDate(0)} value={reschedule.date} onChange={(event) => setReschedule((current) => ({ ...current, date: event.target.value }))} /></label>
                    <label><span>New time</span><input type="time" step="900" value={reschedule.time} onChange={(event) => setReschedule((current) => ({ ...current, time: event.target.value }))} /></label>
                    <label><span>Duration</span><strong className="lesson-reschedule-static">{lesson.durationMinutes || 60} minutes</strong></label>
                    <label className="wide"><span>Note</span><input value={reschedule.reason} onChange={(event) => setReschedule((current) => ({ ...current, reason: event.target.value }))} placeholder="Why this time works better" /></label>
                    <div><button type="button" onClick={() => submitReschedule(lesson)} disabled={busy}>Send new time</button><button type="button" className="secondary" onClick={() => setReschedule({ lessonId: "", date: "", time: "", durationMinutes: "60", reason: "" })}>Close</button></div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="lesson-operations-empty">
          <CalendarDays size={21} />
          <strong>No lesson requests yet</strong>
          <span>New player requests will appear here with time, goal, price and clear decision controls.</span>
        </div>
      )}
    </section>
  );
}

function RoleDashboard({ config, navigate }) {
  config.secondaryActions = config.secondaryActions || [];
  config.quickActions = config.quickActions || [];
  const Icon = config.icon;
  const actionSeen = new Set();
  const secondaryActionCards = config.secondaryActions.slice(0, 3).map(([label, target, payload]) => [label, target, payload]);
  const quickActionCards = config.quickActions.map(([label, target, payload]) => [label, target, payload]);
  const actionCards = [
    config.primaryAction,
    ...secondaryActionCards,
    ...quickActionCards
  ].filter(Boolean).filter(([label, target]) => {
    const key = `${label}-${target}`;
    if (actionSeen.has(key)) return false;
    actionSeen.add(key);
    return true;
  }).slice(0, 4);
  const visibleStats = (config.stats || []).slice(0, 3);
  const visibleActivity = (config.activity || []).slice(0, 2);
  const missingItems = (config.readiness?.missing || []).slice(0, 3).map(missingFieldMeta);
  const ready = !missingItems.length;
  const showLegacyOverview = !config.commandPanel;
  const showActionStrip = !config.commandPanel;
  const compactDashboard = Boolean(config.commandPanel);
  const compactSecondaryActions = secondaryActionCards.slice(0, config.role === "organiser" ? 2 : 1);
  const workspaceStatus = config.workspaceStatus || {
    ready,
    eyebrow: ready ? "Public profile" : "Profile setup",
    label: ready ? "Live" : `${config.readiness.percent}% ready`,
    detail: ready ? "Profile ready" : `${config.readiness.missing.length} item${config.readiness.missing.length === 1 ? "" : "s"} remaining`
  };
  return (
    <section className={`page role-dashboard-page role-dashboard-simple role-dashboard-${config.role}`}>
      {compactDashboard ? (
        <section className="role-dashboard-overview-bar">
          <span className="role-dashboard-overview-icon"><Icon size={22} /></span>
          <div className="role-dashboard-overview-copy">
            <p>{config.eyebrow}</p>
            <h1>{config.title}</h1>
            <span>{config.summary}</span>
          </div>
          <aside className={`role-dashboard-overview-status ${workspaceStatus.ready ? "ready" : "needs-work"}`}>
            <span className="role-dashboard-overview-status-icon">
              {workspaceStatus.ready ? <BadgeCheck size={18} aria-hidden="true" /> : <Gauge size={18} aria-hidden="true" />}
            </span>
            <span className="role-dashboard-overview-status-copy">
              <small>{workspaceStatus.eyebrow}</small>
              <strong>{workspaceStatus.label}</strong>
              <span>{workspaceStatus.detail}</span>
            </span>
          </aside>
          <div className="role-dashboard-overview-actions">
            <RoleActionButton
              label={config.primaryAction[0]}
              target={config.primaryAction[1]}
              payload={config.primaryAction[2]}
              navigate={navigate}
              tone="primary"
            />
            {compactSecondaryActions.map(([label, target, payload]) => (
              <RoleActionButton key={label} label={label} target={target} payload={payload} navigate={navigate} />
            ))}
          </div>
        </section>
      ) : (
        <section className="role-dashboard-hero simple">
          <div className="role-dashboard-copy">
            <p>{config.eyebrow}</p>
            <h1>{config.title}</h1>
            <span>{config.summary}</span>
            <div className="role-dashboard-actions">
              <RoleActionButton
                label={config.primaryAction[0]}
                target={config.primaryAction[1]}
                payload={config.primaryAction[2]}
                navigate={navigate}
                tone="primary"
              />
              {config.secondaryActions.slice(0, 1).map(([label, target, payload]) => (
                <RoleActionButton key={label} label={label} target={target} payload={payload} navigate={navigate} />
              ))}
            </div>
          </div>
          <aside className="role-dashboard-score-card">
            <Icon size={28} />
            <strong>{config.readiness.percent}%</strong>
            <span>Setup</span>
            <p>{ready ? "Ready for people to view and act on." : `${config.readiness.missing.length} detail${config.readiness.missing.length === 1 ? "" : "s"} left before this role is useful.`}</p>
          </aside>
        </section>
      )}

      {showActionStrip && (
        <section className="role-dashboard-next">
          <div className="role-panel-head">
            <p>Actions</p>
            <h2>What do you want to do?</h2>
          </div>
          <div className="role-dashboard-card-actions">
            {actionCards.map(([label, target, payload], index) => (
              <button type="button" key={`${label}-${target}`} className={index === 0 ? "primary" : ""} onClick={() => navigate(target, payload || {})}>
                <span>{label}</span>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        </section>
      )}

      <ProfileSubscriptionPanel
        role={config.role}
        callFunction={callMarketplaceFunction}
        signedIn={Boolean(firebaseAuth?.currentUser)}
        initialSubscription={config.subscription || null}
        compact
      />
      {config.commandPanel && config.role !== "centre" && <RoleCommandPanel panel={config.commandPanel} navigate={navigate} />}
      {config.role === "player" && <PlayerWeeklyPlanPanel plan={config.weeklyPlan} navigate={navigate} />}
      {config.role === "player" && <PlayerCoachLessonsPanel operations={config.playerLessonOperations} navigate={navigate} />}
      {config.role === "centre" && <CentreOperationsPanel operations={config.centreOperations} navigate={navigate} />}
      {config.role === "coach" && <CoachLessonOperationsPanel operations={config.coachLessonOperations} navigate={navigate} />}
      {config.role !== "player" && config.role !== "centre" && (
        <RoleOperatingSuite
          sections={config.operatingSections}
          navigate={navigate}
          role={config.role}
          alwaysVisible={config.role === "coach"}
        />
      )}

      {showLegacyOverview && (
        <div className="role-dashboard-simple-layout">
          <section className="role-dashboard-panel status">
            <div className="role-panel-head">
              <p>Setup</p>
              <h2>{ready ? "Ready to use" : "Finish these first"}</h2>
            </div>
            <div className="role-alert-list compact">
              {ready ? (
                <div>
                  <Check size={17} />
                  <strong>This role can be used now</strong>
                  <span>People have enough information to view this profile and take action.</span>
                  <em>Ready</em>
                </div>
              ) : missingItems.map((item) => (
                <div key={item.field}>
                  <CircleHelp size={17} />
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                  <em>Needed</em>
                </div>
              ))}
            </div>
          </section>

          <section className="role-dashboard-panel">
            <div className="role-panel-head">
              <p>Overview</p>
              <h2>At a glance</h2>
            </div>
            <div className="role-dashboard-mini-stats">
              {visibleStats.map(([StatIcon, label, value, detail]) => (
                <article key={label}>
                  <StatIcon size={18} />
                  <span>{label}</span>
                  <strong>{value}</strong>
                  <em>{detail}</em>
                </article>
              ))}
            </div>
          </section>

          <section className="role-dashboard-panel">
            <div className="role-panel-head">
              <p>Activity</p>
              <h2>Recent activity</h2>
            </div>
            <div className="role-activity-list">
              {visibleActivity.map(([title, text, time]) => (
                <div key={`${title}-${time}`}>
                  <Activity size={17} />
                  <strong>{title}</strong>
                  <span>{text}</span>
                  <time>{time}</time>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function PlayerDashboard({ navigate, accountProfile }) {
  return <RoleDashboard config={buildRoleDashboardConfig("player", accountProfile)} navigate={navigate} />;
}

function OrganiserDashboard({ navigate, accountProfile }) {
  const payoutState = useOwnerStripeConnectReadiness("organiser", accountProfile);
  return <RoleDashboard config={buildRoleDashboardConfig("organiser", accountProfile, defaultVenue, payoutState)} navigate={navigate} />;
}

function CreateGroupWizard({ navigate, accountProfile, saveAccountProfile }) {
  const group = accountProfile.group || defaultAccountProfile.group;
  const initialGroupForm = {
    ...defaultAccountProfile.group,
    ...group,
    socialLinks: group.socialLinks?.length ? group.socialLinks : [],
    mediaLinks: group.mediaLinks?.length ? group.mediaLinks : [],
    inviteEmails: cleanEmailTags(group.inviteEmails?.length ? group.inviteEmails : group.invites),
    date: group.date || "",
    schedule: groupScheduleFromLegacyDate(group),
    isRecurring: group.isRecurring !== false,
    paymentManagedByBadminta: true
  };
  const savedGroupProfileComplete = roleHasPublishedBackendProfile(accountProfile, "organiser");
  const savedGroupCoreReady = getPublicProfileMissingFields(accountProfile, "organiser").length === 0;
  const activeGroupWizardSteps = savedGroupProfileComplete ? createGroupWizardSteps : groupFirstPublishWizardSteps;
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId,
    draftDirty
  } = useProfileSetupDraft("organiser-group", accountProfile, initialGroupForm, activeGroupWizardSteps, {
    profileComplete: savedGroupProfileComplete,
    initialStepId: !savedGroupProfileComplete && savedGroupCoreReady ? "group-step-capacity" : ""
  });
  const [groupMessage, setGroupMessage] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const groupFormCurrency = normaliseCurrencyCode(form.currency || (form.location?.countryCode
    ? marketplaceCurrencyForLocation(form.location)
    : accountRoleCurrency(accountProfile, "organiser")));
  const groupCloudDraft = {
    ...form,
    schedule: normaliseGroupSchedule(form),
    paymentManagedByBadminta: true,
    paymentCollectionMode: "badminta_managed"
  };
  const groupCloudDraftStatus = useCloudProfileDraftAutosave({
    enabled: draftDirty,
    roleId: "organiser",
    patch: {
      roles: Array.from(new Set([...(accountProfile.roles || []), "organiser"])),
      group: accountRoleDraftPatch(groupCloudDraft)
    },
    saveAccountProfile
  });
  const formPublishMissing = getPublicProfileMissingFields({
    ...accountProfile,
    roles: Array.from(new Set([...(accountProfile.roles || []), "organiser"])),
    group: groupCloudDraft
  }, "organiser");
  const groupMissingSet = new Set(formPublishMissing);
  const groupVenueStepComplete = !["Group name", "Venue"].some((field) => groupMissingSet.has(field));
  const groupScheduleStepComplete = !groupMissingSet.has("Playing days and times");
  const groupSkillStepComplete = Boolean(
    String(form.skillBand || form.format || form.description || form.price || form.membershipBenefits || "").trim()
    || form.membershipEnabled === false
    || form.membershipMode === "paid"
  );
  const groupCapacityStepComplete = !groupMissingSet.has("Capacity");
  const groupExtrasStepComplete = Boolean(
    String(form.description || form.format || form.price || form.logoUrl || form.coverUrl || "").trim()
    || cleanEmailTags(form.inviteEmails).length
    || cleanLinkRows(form.socialLinks, "Group social").length
    || cleanLinkRows(form.mediaLinks, "Group media").length
  );
  const groupReadyToSave = formPublishMissing.length === 0;
  const groupCompletedStepIds = [
    groupVenueStepComplete && "group-step-venue",
    groupScheduleStepComplete && "group-step-date",
    groupCapacityStepComplete && "group-step-capacity",
    groupSkillStepComplete && "group-step-skill",
    groupExtrasStepComplete && "group-step-invites",
    groupReadyToSave && "group-step-publish"
  ].filter(Boolean);
  const groupFieldStepMap = {
    "Group name": "group-step-venue",
    "Venue": "group-step-venue",
    "Playing days and times": "group-step-date",
    "Capacity": "group-step-capacity"
  };
  const firstGroupMissingStep = groupFieldStepMap[formPublishMissing[0]] || "group-step-venue";

  const updateGroupField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const saveGroupDraft = async () => {
    setGroupMessage("");
    const matchedVenue = form.venueId ? venues.find((venue) => venue.id === form.venueId) : null;
    const groupLocation = locationFromVenue(matchedVenue) || locationSnapshot(form.location);
    const nextRoles = Array.from(new Set([...(accountProfile.roles || []), "organiser"]));
    let nextGroup = {
      ...form,
      id: form.id || `group-${Date.now()}`,
      city: form.city || groupLocation?.city || matchedVenue?.city || "",
      suburb: form.suburb || groupLocation?.suburb || matchedVenue?.suburb || "",
      location: groupLocation,
      venue: form.venue || matchedVenue?.name || "",
      venueId: form.venueId || matchedVenue?.id || "",
      schedule: normaliseGroupSchedule(form),
      isRecurring: form.isRecurring !== false,
      paymentLink: "",
      paymentManagedByBadminta: true,
      pendingVenue: form.pendingVenue || null,
      inviteEmails: cleanEmailTags(form.inviteEmails),
      invites: cleanEmailTags(form.inviteEmails).join("\n"),
      mediaLinks: markUserSubmittedMediaLinks(form.mediaLinks, "Group media"),
      socialLinks: cleanLinkRows(form.socialLinks, "Group social"),
      paymentCollectionMode: "badminta_managed"
    };
    const groupCountryCode = normaliseCountryCode(nextGroup.countryCode || nextGroup.location?.countryCode || matchedVenue?.countryCode || matchedVenue?.country, nextGroup.city);
    const groupCurrency = normaliseCurrencyCode(nextGroup.currency || marketplaceCurrencyForCountry(groupCountryCode));
    nextGroup = { ...nextGroup, countryCode: groupCountryCode, currency: groupCurrency };
    const publishMissing = getPublicProfileMissingFields({ ...accountProfile, roles: nextRoles, group: nextGroup }, "organiser");
    nextGroup = {
      ...nextGroup,
      status: publishMissing.length ? "draft" : "published",
      publishMissing,
      nextSessions: getGroupUpcomingOccurrences(nextGroup, 8).map((session) => ({
        id: session.id,
        date: session.date,
        dateLabel: session.dateLabel,
        start: session.start,
        end: session.end,
        time: session.time,
        status: session.status,
        price: session.price,
        capacity: session.capacity,
        paymentStatus: session.paymentStatus,
        currency: session.currency || groupCurrency
      }))
    };
    let backendError = null;
    let profileBecamePublic = false;
    let backendAccountProfileSynced = false;
    let backendPublicationConfirmed = false;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingGroup(true);
      try {
        const backendGroup = await callMarketplaceFunction("upsertSocialGroup", {
          groupId: nextGroup.firestoreSourceId || nextGroup.serverGroupId || nextGroup.id,
          name: nextGroup.name,
          description: nextGroup.description,
          venue: nextGroup.venue,
          venueId: nextGroup.venueId,
          date: nextGroup.date,
          time: nextGroup.time,
          schedule: nextGroup.schedule,
          isRecurring: nextGroup.isRecurring,
          pendingVenue: nextGroup.pendingVenue,
          format: nextGroup.format,
          skillBand: nextGroup.skillBand,
          city: nextGroup.city,
          suburb: nextGroup.suburb,
          countryCode: groupCountryCode,
          location: locationSnapshot(nextGroup.location),
          capacity: nextGroup.capacity,
      price: nextGroup.price,
      currency: groupCurrency,
      membershipEnabled: nextGroup.membershipEnabled !== false,
      membershipMode: nextGroup.membershipMode === "paid" ? "paid" : "free",
      membershipFee: nextGroup.membershipMode === "paid" ? nextGroup.membershipFee : "0",
      membershipBillingInterval: nextGroup.membershipBillingInterval || "one_time",
      membershipBenefits: nextGroup.membershipBenefits,
      paymentManagedByBadminta: true,
          paymentCollectionMode: nextGroup.paymentCollectionMode,
          inviteEmails: nextGroup.inviteEmails,
          socialLinks: nextGroup.socialLinks,
          mediaLinks: nextGroup.mediaLinks,
          logoUrl: nextGroup.logoUrl,
          coverUrl: nextGroup.coverUrl,
          status: nextGroup.status
        });
        profileBecamePublic = backendGroup.becamePublic === true;
        backendAccountProfileSynced = backendGroup.accountProfileSynced === true;
        backendPublicationConfirmed = backendGroup.public === true;
        nextGroup = {
          ...nextGroup,
          logoUrl: backendGroup.logoUrl || nextGroup.logoUrl,
          coverUrl: backendGroup.coverUrl || nextGroup.coverUrl,
          id: backendGroup.groupId || nextGroup.id,
          firestoreSourceId: backendGroup.groupId || nextGroup.firestoreSourceId,
          serverGroupId: backendGroup.groupId || nextGroup.serverGroupId,
          priceCents: backendGroup.priceCents ?? nextGroup.priceCents,
          currency: backendGroup.currency || nextGroup.currency,
          membershipMode: backendGroup.membershipMode || nextGroup.membershipMode,
          membershipFeeCents: backendGroup.membershipFeeCents ?? nextGroup.membershipFeeCents,
          membershipBillingInterval: backendGroup.membershipBillingInterval || nextGroup.membershipBillingInterval,
          membershipProductId: backendGroup.membershipProductId || "",
          membershipCheckoutReady: backendGroup.membershipCheckoutReady !== false,
          backendStatus: backendGroup.status || nextGroup.status,
          publicProfilePublished: backendGroup.public === true,
          publishMissing: backendGroup.publishMissing || []
        };
      } catch (error) {
        backendError = error;
        nextGroup = {
          ...nextGroup,
          backendStatus: "account_saved_public_publish_pending",
          backendErrorMessage: marketplaceFunctionErrorMessage(error)
        };
      } finally {
        setSavingGroup(false);
      }
    }
    const savedProfile = {
      ...accountProfile,
      roles: nextRoles,
      group: nextGroup
    };
    const accountSave = await saveAccountProfile({
      roles: savedProfile.roles,
      group: nextGroup
    });
    if (!accountSave?.saved && !backendAccountProfileSynced) {
      backendError = new Error(accountSave?.error || "Badminta could not save your social group. Try again.");
    }
    if (!backendError && backendPublicationConfirmed && nextGroup.inviteEmails.length) {
      try {
        inviteDelivery = await callMarketplaceFunction("inviteGroupMembers", {
          groupId: nextGroup.firestoreSourceId || nextGroup.serverGroupId || nextGroup.id,
          inviteEmails: nextGroup.inviteEmails
        });
      } catch (error) {
        inviteDeliveryError = error;
      }
    }
    if (groupReadyToSave && !backendPublicationConfirmed && !backendError) {
      backendError = new Error("Your details were saved, but Badminta has not confirmed the public group yet. Select Publish social group again.");
    }
    const savedMissing = nextGroup.publishMissing?.length
      ? nextGroup.publishMissing
      : getPublicProfileMissingFields(savedProfile, "organiser");
    if (!savedMissing.length && !backendError) clearDraft();
    const baseGroupMessage = publicProfileSaveMessage({
      label: "Social group",
      backendError,
      missing: savedMissing
    });
    setGroupMessage(`${baseGroupMessage}${!backendError && inviteDelivery?.queuedCount ? ` ${inviteDelivery.queuedCount} member invitation${inviteDelivery.queuedCount === 1 ? " was" : "s were"} queued for email delivery.` : ""}${!backendError && inviteDeliveryError ? " Invite emails were saved; open Members to retry delivery." : ""}`);
    if (!savedMissing.length && !backendError) {
      if (profileBecamePublic) {
        trackProfileCreated({
          profileId: nextGroup.firestoreSourceId || nextGroup.serverGroupId || nextGroup.id,
          role: "organiser"
        });
      }
      completeOnboardingRole("organiser");
      const publicPayload = publicProfileNavigationPayload("group", nextGroup);
      continueAfterSave(navigate, "publicGroupProfile", publicPayload || { group: nextGroup }, 250);
    }
  };
  const openLiveGroupProfile = () => {
    const publicPayload = publicProfileNavigationPayload("group", group);
    navigate("publicGroupProfile", publicPayload || { group });
  };

  return (
    <section className="page wizard-page role-profile-setup-page" data-profile-role="organiser">
      <div className="wizard-head">
        <p>Social group profile</p>
        <h1>{group.name ? "Edit your social group" : "Create your social group"}</h1>
        <button type="button" onClick={() => navigate("organiser")} aria-label="Back to organiser dashboard"><ChevronLeft size={18} /><span>Back to organiser dashboard</span></button>
      </div>
      <div className="wizard-layout">
        <WizardStepRail steps={activeGroupWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Social group creation steps" profileComplete={savedGroupProfileComplete} completedStepIds={groupCompletedStepIds} />
        <section className="form-card wizard-form-card" data-wizard-role="organiser-group">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={activeGroupWizardSteps} profileComplete={savedGroupProfileComplete} cloudStatus={groupCloudDraftStatus} />
          <ProfilePublishReadiness
            label="social group"
            missingFields={formPublishMissing}
            fieldStepMap={groupFieldStepMap}
            publishStepId="group-step-publish"
            onStepChange={scrollToStep}
            onPublish={saveGroupDraft}
            publishing={savingGroup}
            alreadyPublished={savedGroupProfileComplete}
            onViewLive={openLiveGroupProfile}
          />
          <section className="wizard-form-section" id="group-step-venue" hidden={activeStepId !== "group-step-venue"} tabIndex={-1}>
            <h2>Group basics</h2>
            <label><span>Group name <FieldRequirement /></span><input required value={form.name} onChange={(event) => updateGroupField("name", event.target.value)} placeholder="Friday mixed doubles social" /></label>
            <VenueTagPicker
              value={form.venue}
              venueId={form.venueId}
              allowQuickAdd
              onChange={({ venue, venueId, pendingVenue, city, suburb, location }) => {
                const matchedVenue = venueId ? venues.find((item) => item.id === venueId) : null;
                const groupLocation = locationFromVenue(matchedVenue) || locationSnapshot(location);
                updateGroupField("venue", venue);
                updateGroupField("venueId", venueId);
                updateGroupField("pendingVenue", pendingVenue || null);
                if (city) updateGroupField("city", city);
                if (suburb) updateGroupField("suburb", suburb);
                if (groupLocation) {
                  updateGroupField("city", groupLocation.city);
                  updateGroupField("suburb", groupLocation.suburb);
                  updateGroupField("location", groupLocation);
                }
              }}
            />
          </section>
          <section className="wizard-form-section" id="group-step-date" hidden={activeStepId !== "group-step-date"} tabIndex={-1}>
            <h2>Playing days and times</h2>
            <label className="toggle-row group-recurring-toggle">
              <input type="checkbox" checked={form.isRecurring !== false} onChange={(event) => updateGroupField("isRecurring", event.target.checked)} />
              <span>
                <strong>Repeats every week</strong>
                <em>Most social groups play once or twice a week. Select the weekly day and time instead of creating a new date each week.</em>
              </span>
            </label>
            <WeeklyAvailabilityEditor
              title="Weekly play schedule"
              description="Select the day or days this group normally plays and the session time players should attend."
              schedule={form.schedule}
              onChange={(schedule) => updateGroupField("schedule", schedule)}
              legacySummary={getGroupWhenLabel(form)}
            />
          </section>
          <section className="wizard-form-section" id="group-step-skill" hidden={activeStepId !== "group-step-skill"} tabIndex={-1}>
            <h2>Session details <FieldRequirement optional /></h2>
            <p className="optional-step-copy">These details help players decide whether the group suits them, but they can all be added after publication.</p>
            <label><span>Group description <FieldRequirement optional /></span><textarea value={form.description} onChange={(event) => updateGroupField("description", event.target.value)} placeholder="Tell players who this group is for, what to bring, rotation style and session expectations." /></label>
            <label><span>Format <FieldRequirement optional /></span><select value={form.format} onChange={(event) => updateGroupField("format", event.target.value)}><option value="">Add later</option><option>Rotating doubles</option><option>Singles ladder</option><option>Coached social</option><option>Women friendly social</option></select></label>
            <label><span>Skill band <FieldRequirement optional /></span><select value={form.skillBand} onChange={(event) => updateGroupField("skillBand", event.target.value)}><option value="">Add later</option><option>Beginner friendly</option><option>Beginner to intermediate</option><option>Intermediate</option><option>Advanced</option><option>Open level</option></select></label>
            <label><span>Session price per player ({groupFormCurrency.toUpperCase()}) <FieldRequirement optional /></span><input value={form.price} onChange={(event) => updateGroupField("price", event.target.value)} inputMode="decimal" placeholder="Add later" /></label>
            <div className="managed-payment-note">
              <WalletCards size={20} />
              <div>
                <strong>Session payments stay separate</strong>
                <span>This price applies each time a player reserves a session. Group membership below controls who can become a member.</span>
              </div>
            </div>
            <div className="group-membership-settings">
              <div className="group-membership-settings-head">
                <span><UsersRound size={20} /></span>
                <div>
                  <strong>Group membership</strong>
                  <p>Let players join the group itself, then book individual sessions separately.</p>
                </div>
                <label className="compact-switch">
                  <input type="checkbox" checked={form.membershipEnabled !== false} onChange={(event) => updateGroupField("membershipEnabled", event.target.checked)} />
                  <span aria-hidden="true" />
                  <em>{form.membershipEnabled !== false ? "Open" : "Closed"}</em>
                </label>
              </div>
              {form.membershipEnabled !== false && (
                <>
                  <div className="membership-mode-grid" role="radiogroup" aria-label="Group membership price">
                    <button type="button" role="radio" aria-checked={form.membershipMode !== "paid"} className={form.membershipMode !== "paid" ? "active" : ""} onClick={() => updateGroupField("membershipMode", "free")}>
                      <BadgeCheck size={20} />
                      <span><strong>Free to join</strong><em>Players become members immediately.</em></span>
                      {form.membershipMode !== "paid" && <Check size={18} />}
                    </button>
                    <button type="button" role="radio" aria-checked={form.membershipMode === "paid"} className={form.membershipMode === "paid" ? "active" : ""} onClick={() => updateGroupField("membershipMode", "paid")}>
                      <WalletCards size={20} />
                      <span><strong>Paid membership</strong><em>Badminta collects the fee through Stripe Connect.</em></span>
                      {form.membershipMode === "paid" && <Check size={18} />}
                    </button>
                  </div>
                  {form.membershipMode === "paid" && (
                    <div className="membership-fee-fields">
                      <label><span>Membership fee ({groupFormCurrency.toUpperCase()})</span><input value={form.membershipFee} onChange={(event) => updateGroupField("membershipFee", event.target.value)} inputMode="decimal" placeholder="25.00" /></label>
                      <label><span>Charge frequency</span><select value={form.membershipBillingInterval || "one_time"} onChange={(event) => updateGroupField("membershipBillingInterval", event.target.value)}><option value="one_time">One-time</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></label>
                    </div>
                  )}
                  <label><span>What members receive <FieldRequirement optional /></span><textarea value={form.membershipBenefits} onChange={(event) => updateGroupField("membershipBenefits", event.target.value)} placeholder="For example: member-only chat, priority session announcements or discounted games." /></label>
                  {form.membershipMode === "paid" && (
                    <div className="managed-payment-note membership-connect-note">
                      <ShieldCheck size={20} />
                      <div>
                        <strong>Badminta managed payment and payout</strong>
                        <span>Players pay on Badminta. The group organiser receives the membership payout through their verified Stripe Connect account.</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
          <section className="wizard-form-section" id="group-step-capacity" hidden={activeStepId !== "group-step-capacity"} tabIndex={-1}>
            <h2>Player capacity</h2>
            <label><span>Capacity <FieldRequirement /></span><input required value={form.capacity} onChange={(event) => updateGroupField("capacity", event.target.value)} inputMode="numeric" placeholder="16" /></label>
            <ProfileEssentialsAction
              label="social group"
              ready={groupReadyToSave}
              missingFields={formPublishMissing}
              alreadyPublished={savedGroupProfileComplete}
              saving={savingGroup}
              onPublish={saveGroupDraft}
              onFixMissing={() => scrollToStep(firstGroupMissingStep)}
            />
          </section>
          <section className="wizard-form-section" id="group-step-invites" hidden={activeStepId !== "group-step-invites"} tabIndex={-1}>
            <h2>Photos, invites and links <FieldRequirement optional /></h2>
            <ImageUploadField
              label="Group logo (optional)"
              value={form.logoUrl}
              onChange={(value) => updateGroupField("logoUrl", value)}
              mediaRole="organiser"
              mediaField="logoUrl"
              emptyLabel="No group logo yet"
              helper="Optional. Publish first and add a group logo later."
            />
            <ImageUploadField
              label="Group cover image (optional)"
              value={form.coverUrl}
              onChange={(value) => updateGroupField("coverUrl", value)}
              mediaRole="organiser"
              mediaField="coverUrl"
              emptyLabel="No group cover yet"
              helper="Optional. Add a session or venue photo when one is available."
            />
            <EmailTagInput emails={form.inviteEmails} onChange={(emails) => updateGroupField("inviteEmails", emails)} />
            <LinkListEditor
              title="Group social links"
              text="Enter a social username, WhatsApp phone or invite link, Meetup group name, or public group page."
              links={form.socialLinks}
              onChange={(links) => updateGroupField("socialLinks", links)}
            />
            <LinkListEditor
              title="Group media links"
              text="Paste a session clip, reel, venue walkthrough or public image link to validate and preview it."
              type="media"
              addLabel="Add group media"
              links={form.mediaLinks}
              onChange={(links) => updateGroupField("mediaLinks", links)}
            />
          </section>
          <section className="wizard-form-section" id="group-step-publish" hidden={activeStepId !== "group-step-publish"} tabIndex={-1}>
            <h2>Publish social group</h2>
            <div className="event-checkout-note">
              <ShieldCheck size={20} />
              <span>Publishing makes the group discoverable. You can add photos, invitations, skill guidance and pricing later without taking it offline.</span>
            </div>
            {groupMessage && <div className={`auth-message ${profileSaveMessageTone(groupMessage)}`}>{groupMessage}</div>}
            {groupReadyToSave ? (
              <button type="button" onClick={saveGroupDraft} disabled={savingGroup}>{savingGroup ? "Publishing..." : savedGroupProfileComplete ? "Save and view live group" : "Publish social group"}</button>
            ) : (
              <div className="profile-draft-actions">
                <button type="button" onClick={() => scrollToStep(firstGroupMissingStep)}>Complete required details</button>
                <button type="button" className="secondary" onClick={saveGroupDraft} disabled={savingGroup}>{savingGroup ? "Saving..." : "Save private draft"}</button>
              </div>
            )}
          </section>
          <WizardNavigation
            steps={activeGroupWizardSteps}
            activeStepId={activeStepId}
            onStepChange={scrollToStep}
            readyToSave={groupReadyToSave}
            incompleteLabel="Saves as a private draft"
            fastTrackToStepId="group-step-publish"
            onFinish={groupReadyToSave ? saveGroupDraft : () => scrollToStep(firstGroupMissingStep)}
            finishLabel={savedGroupProfileComplete ? "Save group" : "Publish group"}
            finishing={savingGroup}
          />
        </section>
        <aside className="preview-card">
          {form.coverUrl ? <img src={form.coverUrl} alt="" /> : form.logoUrl ? <img src={form.logoUrl} alt="" /> : savedGroupProfileComplete ? <NoImagePlaceholder label="Group image not added yet" /> : <div className="preview-icon-placeholder"><UsersRound size={42} /></div>}
          <strong>{form.name || "Your first social group"}</strong>
          <span>{[form.venue, getGroupWhenLabel(form), form.skillBand].filter(Boolean).join(" · ") || "Add a venue, weekly schedule and player capacity to publish."}</span>
          <b>{savedGroupProfileComplete && String(form.price).trim() !== "" ? `${Number(form.price) > 0 ? formatMoney(Number(form.price), groupFormCurrency) : "Free"}` : groupReadyToSave ? "Ready to publish" : "Private draft"}</b>
          {savedGroupProfileComplete ? (
            <>
              <div className="tag-chip-row preview-tags">
                {cleanEmailTags(form.inviteEmails).slice(0, 6).map((email) => <span className="tag-chip" key={email}><Mail size={14} />{email}</span>)}
              </div>
              <PublicLinkList links={form.socialLinks} emptyText="Group links will appear here." />
            </>
          ) : (
            <FirstPublishPreview
              icon={UsersRound}
              title="Only the playable session basics are required"
              items={["Group and venue", "Weekly schedule", "Player capacity"]}
              text="Description, level, price, photos, invites and links can all be added after the public group opens."
            />
          )}
        </aside>
      </div>
    </section>
  );
}

const addMinutesToClock = (value = "18:00", minutesToAdd = 120) => {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  if (!match) return "20:00";
  const total = (Number(match[1]) * 60 + Number(match[2]) + minutesToAdd) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const localSessionDate = (date = "", time = "") => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date)) || !isClockTimeValue(time)) return null;
  const parsed = new Date(`${date}T${time}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sessionFormFromGroup = (group = {}, session = null) => {
  const nextOccurrence = getPrimaryGroupOccurrence(group) || {};
  const start = session?.start || session?.startTime || nextOccurrence.start || (isClockTimeValue(group.time) ? group.time : "18:00");
  return {
    id: session?.sessionId || session?.id || "",
    title: session?.title || group.name || "",
    date: session?.date || nextOccurrence.date || getIsoDate(7),
    start,
    end: session?.end || session?.endTime || nextOccurrence.end || addMinutesToClock(start),
    capacity: String(session?.capacity || group.capacity || "16"),
    price: session?.price !== undefined && session?.price !== "" ? String(session.price) : String(group.price || ""),
    paymentCollectionMode: session?.paymentCollectionMode || session?.settlementMode || "badminta_checkout",
    skillBand: session?.skillBand || group.skillBand || "",
    format: session?.format || group.format || "",
    notes: session?.notes || "",
    publishNow: !session || !["draft", "archived", "cancelled"].includes(String(session.status || "").toLowerCase())
  };
};

const groupSessionValidation = (form = {}, group = {}, accountProfile = defaultAccountProfile) => {
  const missing = [];
  const startAt = localSessionDate(form.date, form.start);
  const endAt = localSessionDate(form.date, form.end);
  const capacity = Number.parseInt(form.capacity, 10);
  const price = Number.parseFloat(form.price);
  if (!(group.firestoreSourceId || group.serverGroupId || group.id)) missing.push("Save the group profile first");
  if (!group.name) missing.push("Add the group name");
  if (!group.venue) missing.push("Select the venue");
  if (!startAt) missing.push("Choose a valid date and start time");
  if (!endAt || (startAt && endAt.getTime() <= startAt.getTime())) missing.push("Choose an end time after the start");
  if (form.publishNow && startAt && startAt.getTime() <= Date.now()) missing.push("Choose a future session time");
  if (!Number.isInteger(capacity) || capacity < 1 || capacity > 500) missing.push("Set capacity between 1 and 500");
  if (String(form.price).trim() === "" || !Number.isFinite(price) || price < 0) missing.push("Set a price, using 0 for a free session");
  if (form.publishNow && !isPublicProfileReady(accountProfile, "group")) missing.push("Publish the group profile before this session");
  return [...new Set(missing)];
};

function GroupSessionWizard({ navigate, accountProfile, saveAccountProfile, session = null }) {
  const group = accountProfile.group || defaultAccountProfile.group;
  const sessionCurrency = normaliseCurrencyCode(group.currency || marketplaceCurrencyForCountry(group.countryCode || group.location?.countryCode));
  const initialSessionForm = useMemo(() => sessionFormFromGroup(group, session), [group, session]);
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId
  } = useProfileSetupDraft(`organiser-session-${session?.sessionId || session?.id || "new"}`, accountProfile, initialSessionForm, groupSessionWizardSteps);
  const [saveState, setSaveState] = useState({ status: "idle", message: "" });
  const groupId = group.firestoreSourceId || group.serverGroupId || group.id || "";
  const payoutOwnerProfileId = group.publicGroupId || group.serverGroupId || group.firestoreSourceId || group.id || accountProfile.publicGroupProfileId || "";
  const paidSession = Boolean(form.publishNow && Number.parseFloat(form.price) > 0);
  const organiserSubscription = accountProfileSubscription(accountProfile, "organiser");
  const paidSessionUpgradeMissing = paidSession && organiserSubscription.entitlements?.paidSessions !== true
    ? ["Upgrade to Group Pro before publishing a paid session"]
    : [];
  const paidPublicationNeedsPayouts = paidSession && form.paymentCollectionMode !== "offline_settlement";
  const [payoutState, setPayoutState] = useState({ status: "idle", ready: false, availability: null, message: "" });
  useEffect(() => {
    if (!paidSession) {
      setPayoutState({ status: "idle", ready: true, availability: null, message: "Free sessions and private drafts do not require payout setup." });
      return undefined;
    }
    if (!firebaseAuth?.currentUser || !firebaseFunctions || !payoutOwnerProfileId) {
      setPayoutState({ status: "needs-action", ready: false, availability: null, message: "Sign in and save the group before selecting payment collection." });
      return undefined;
    }
    let active = true;
    setPayoutState({ status: "checking", ready: false, availability: null, message: "Checking payment availability for this group country..." });
    (async () => {
      try {
        const availability = await callMarketplaceFunction("getStripeConnectCountryAvailability", {
          role: "organiser",
          ownerProfileId: payoutOwnerProfileId,
          country: group.countryCode || group.location?.countryCode || "",
          currency: sessionCurrency
        });
        if (!active) return;
        if (!availability.supported) {
          setForm((current) => ({ ...current, paymentCollectionMode: "offline_settlement" }));
          setPayoutState({
            status: "offline",
            ready: true,
            availability,
            message: availability.paymentCurrencySupported === false
              ? `${sessionCurrency.toUpperCase()} card collection is unavailable here, so players reserve on Badminta and pay the organiser directly or at the venue.`
              : "Stripe payouts are unavailable for this group country, so players reserve on Badminta and pay the organiser directly or at the venue."
          });
          return;
        }
        setForm((current) => ({ ...current, paymentCollectionMode: "badminta_checkout" }));
        try {
          const data = await callMarketplaceFunction("refreshStripeConnectAccount", { role: "organiser", ownerProfileId: payoutOwnerProfileId });
          if (!active) return;
          const ready = Boolean(data.readyForCheckout);
          setPayoutState({
            status: ready ? "ready" : "needs-action",
            ready,
            availability,
            message: ready ? "Badminta Pay is ready for player checkout." : "Finish payout verification before publishing this paid session."
          });
        } catch (error) {
          if (!active) return;
          const message = marketplaceFunctionErrorMessage(error);
          const onboardingNotStarted = /create stripe onboarding|has not been created|start onboarding/i.test(message);
          setPayoutState({
            status: onboardingNotStarted ? "needs-action" : "error",
            ready: false,
            availability,
            message: onboardingNotStarted ? "Set up Badminta Pay before publishing this paid session." : message
          });
        }
      } catch (error) {
        if (!active) return;
        setPayoutState({ status: "error", ready: false, availability: null, message: marketplaceFunctionErrorMessage(error) });
      }
    })();
    return () => {
      active = false;
    };
  }, [paidSession, payoutOwnerProfileId, group.countryCode, group.location?.countryCode, sessionCurrency]);
  const payoutMissing = paidPublicationNeedsPayouts && !payoutState.ready
    ? [payoutState.status === "checking" ? "Wait while Badminta Pay is checked" : "Set up Badminta Pay before publishing a paid session"]
    : [];
  const missing = [...groupSessionValidation(form, group, accountProfile), ...paidSessionUpgradeMissing, ...payoutMissing];
  const sessionScheduleComplete = !missing.some((item) => /date and start time|end time|future session time/i.test(item));
  const sessionCapacityComplete = !missing.some((item) => /capacity|price/i.test(item));
  const sessionReadyToSave = missing.length === 0;
  const activeSessionStepIndex = Math.max(0, groupSessionWizardSteps.findIndex((step) => step.id === activeStepId));
  const sessionWasPublished = ["published", "active"].includes(String(session?.status || "").toLowerCase());
  const sessionCompletedStepIds = [
    sessionScheduleComplete && (Boolean(session) || activeSessionStepIndex > 0) && "session-step-schedule",
    sessionCapacityComplete && (Boolean(session) || activeSessionStepIndex > 1) && "session-step-capacity",
    sessionReadyToSave && sessionWasPublished && "session-step-publish"
  ].filter(Boolean);
  const updateSessionField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const startAt = localSessionDate(form.date, form.start);
  const endAt = localSessionDate(form.date, form.end);

  const saveSession = async () => {
    const validation = missing;
    if (validation.length) {
      setSaveState({ status: "error", message: validation[0] });
      return;
    }
    if (!firebaseAuth?.currentUser || !firebaseFunctions) {
      setSaveState({ status: "error", message: "Sign in with verified phone auth before saving a session." });
      return;
    }
    setSaveState({ status: "loading", message: "Saving this session to the group calendar..." });
    try {
      const localSession = {
        id: form.id || "",
        sessionId: form.id || "",
        groupId,
        title: form.title || group.name,
        venue: group.venue,
        venueId: group.venueId,
        city: group.city,
        suburb: group.suburb,
        countryCode: group.countryCode || group.location?.countryCode || "",
        location: locationSnapshot(group.location),
        date: form.date,
        start: form.start,
        end: form.end,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        capacity: Number.parseInt(form.capacity, 10),
        price: String(Number.parseFloat(form.price)),
        skillBand: form.skillBand,
        format: form.format,
        notes: form.notes,
        isRecurring: false,
        status: form.publishNow ? "published" : "draft",
        paymentManagedByBadminta: form.paymentCollectionMode === "badminta_checkout",
        paymentCollectionMode: form.paymentCollectionMode,
        settlementMode: form.paymentCollectionMode,
        currency: normaliseCurrencyCode(group.currency || marketplaceCurrencyForCountry(group.countryCode || group.location?.countryCode)),
        updatedAt: new Date().toISOString()
      };
      const backend = await callMarketplaceFunction("upsertGroupSession", {
        ...localSession,
        sessionId: localSession.sessionId,
        currency: localSession.currency
      });
      const savedSession = {
        ...localSession,
        ...(backend.session || {}),
        id: backend.sessionId || backend.session?.id || localSession.id,
        sessionId: backend.sessionId || backend.session?.sessionId || localSession.sessionId,
        notes: form.notes,
        isRecurring: false
      };
      const existingSessions = Array.isArray(group.sessions) ? group.sessions : [];
      const nextSessions = existingSessions
        .filter((row) => String(row.sessionId || row.id) !== String(savedSession.sessionId || savedSession.id))
        .concat(savedSession)
        .sort((first, second) => String(first.startAt || `${first.date}T${first.start}`).localeCompare(String(second.startAt || `${second.date}T${second.start}`)));
      const accountSave = await saveAccountProfile({
        group: {
          ...group,
          sessions: nextSessions,
          nextSessionId: backend.upcomingPublicSessions?.[0]?.sessionId || backend.upcomingPublicSessions?.[0]?.id || ""
        }
      });
      if (!accountSave?.saved) throw new Error(accountSave?.error || "The session was published but the account summary could not be refreshed.");
      clearDraft();
      setSaveState({
        status: "success",
        message: form.publishNow
          ? form.paymentCollectionMode === "offline_settlement" && Number.parseFloat(form.price) > 0
            ? "Session published. Players can reserve on Badminta and pay you directly or at the venue."
            : "Session published. Players can now open the dated join and payment flow."
          : "Session saved as a private draft."
      });
      window.setTimeout(() => navigate("groupSessions"), 700);
    } catch (error) {
      setSaveState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  if (!group.name) {
    return (
      <section className="page group-session-manager-page">
        <CommunityEmptyState
          type="organiser session"
          title="Create the social group profile first"
          text="The group stores the reusable name, venue, level and regular schedule. Once that is saved, create dated sessions for joining and payment."
          primary={{ label: "Create group profile", target: "createGroup" }}
          secondary={{ label: "Back to organiser dashboard", target: "organiser" }}
          icon={UsersRound}
          navigate={navigate}
        />
      </section>
    );
  }

  return (
    <section className="page wizard-page group-session-wizard-page">
      <div className="wizard-head">
        <div>
          <p>Organiser session</p>
          <h1>{session ? "Edit this session" : "Create the next session"}</h1>
        </div>
        <button type="button" className="wizard-back-button" onClick={() => navigate("groupSessions")}><ChevronLeft size={16} /> Back to sessions</button>
      </div>
      <div className="wizard-layout">
        <WizardStepRail steps={groupSessionWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Session creation steps" completedStepIds={sessionCompletedStepIds} />
        <section className="form-card wizard-form-card" data-wizard-role="organiser-session">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={groupSessionWizardSteps} idleText="This session draft stays on this device until you publish it." />
          <div className="session-group-context">
            <UsersRound size={20} />
            <div><strong>{group.name}</strong><span>{group.venue || "Venue pending"}</span></div>
            <button type="button" onClick={() => navigate("createGroup")} aria-label="Edit social group" title="Edit social group"><SlidersHorizontal size={16} /><span>Edit group</span></button>
          </div>
          <section className="wizard-form-section" id="session-step-schedule" hidden={activeStepId !== "session-step-schedule"} tabIndex={-1}>
            <h2>Date and time</h2>
            <label><span>Session title</span><input value={form.title} onChange={(event) => updateSessionField("title", event.target.value)} placeholder={group.name || "Wednesday social session"} /></label>
            <div className="field-pair session-date-time-fields">
              <label><span>Date</span><input type="date" min={getIsoDate(0)} value={form.date} onChange={(event) => updateSessionField("date", event.target.value)} /></label>
              <label><span>Starts</span><input type="time" value={form.start} onChange={(event) => updateSessionField("start", event.target.value)} /></label>
              <label><span>Ends</span><input type="time" value={form.end} onChange={(event) => updateSessionField("end", event.target.value)} /></label>
            </div>
            <div className="managed-payment-note session-inheritance-note">
              <CalendarDays size={20} />
              <div><strong>Created from your regular group schedule</strong><span>This is a specific playable date. Changing it does not overwrite the group's reusable weekly schedule.</span></div>
            </div>
          </section>
          <section className="wizard-form-section" id="session-step-capacity" hidden={activeStepId !== "session-step-capacity"} tabIndex={-1}>
            <h2>Players and price</h2>
            <div className="field-pair">
              <label><span>Player capacity</span><input inputMode="numeric" value={form.capacity} onChange={(event) => updateSessionField("capacity", event.target.value)} placeholder="16" /></label>
              <label><span>Price per player ({sessionCurrency.toUpperCase()})</span><input inputMode="decimal" value={form.price} onChange={(event) => updateSessionField("price", event.target.value)} placeholder="18" /></label>
            </div>
            {Number.parseFloat(form.price) > 0 && (
              <div className="booking-payment-choices session-payment-choices" aria-label="Session payment collection">
                <label className={form.paymentCollectionMode === "badminta_checkout" ? "active" : ""}>
                  <input
                    type="radio"
                    name="session-payment-collection"
                    value="badminta_checkout"
                    checked={form.paymentCollectionMode === "badminta_checkout"}
                    disabled={payoutState.availability?.supported === false}
                    onChange={() => updateSessionField("paymentCollectionMode", "badminta_checkout")}
                  />
                  <CreditCard size={20} />
                  <span><strong>Badminta Checkout</strong><small>Card payment, platform ledger and Stripe payout</small></span>
                </label>
                <label className={form.paymentCollectionMode === "offline_settlement" ? "active" : ""}>
                  <input
                    type="radio"
                    name="session-payment-collection"
                    value="offline_settlement"
                    checked={form.paymentCollectionMode === "offline_settlement"}
                    disabled={payoutState.availability?.supported === true}
                    onChange={() => updateSessionField("paymentCollectionMode", "offline_settlement")}
                  />
                  <Banknote size={20} />
                  <span><strong>Pay organiser directly</strong><small>Available only where Stripe payouts are unsupported</small></span>
                </label>
              </div>
            )}
            <div className="field-pair">
              <label><span>Skill band</span><select value={form.skillBand} onChange={(event) => updateSessionField("skillBand", event.target.value)}><option value="">Use group level</option><option>Beginner friendly</option><option>Beginner to intermediate</option><option>Intermediate</option><option>Advanced</option><option>Open level</option></select></label>
              <label><span>Format</span><select value={form.format} onChange={(event) => updateSessionField("format", event.target.value)}><option value="">Use group format</option><option>Rotating doubles</option><option>Singles ladder</option><option>Coached social</option><option>Women friendly social</option></select></label>
            </div>
            <label><span>Session note</span><textarea value={form.notes} onChange={(event) => updateSessionField("notes", event.target.value)} placeholder="Anything specific for this date, such as court numbers, shuttle type, arrival time or guest rules." /></label>
            <div className="managed-payment-note">
              <WalletCards size={20} />
              <div>
                <strong>{form.paymentCollectionMode === "offline_settlement" ? "Badminta manages the reservation" : "Badminta manages collection"}</strong>
                <span>{form.paymentCollectionMode === "offline_settlement"
                  ? "Players reserve this exact session on Badminta, then pay you directly or at the venue. Badminta does not hold those funds."
                  : "Players reserve this exact session and pay through its own capacity-controlled checkout record."}</span>
              </div>
            </div>
          </section>
          <section className="wizard-form-section" id="session-step-publish" hidden={activeStepId !== "session-step-publish"} tabIndex={-1}>
            <h2>Review and publish</h2>
            <label className="toggle-row">
              <input type="checkbox" role="switch" checked={Boolean(form.publishNow)} onChange={(event) => updateSessionField("publishNow", event.target.checked)} />
              <span><strong>Publish for players now</strong><em>Turn this off to keep the dated session private while you finish details.</em></span>
            </label>
            <div className="session-review-summary">
              <div><CalendarDays size={18} /><span>Date</span><strong>{form.date ? formatDateLabel(form.date) : "Missing"}</strong></div>
              <div><Clock3 size={18} /><span>Time</span><strong>{form.start && form.end ? `${formatTimeLabel(form.start)} - ${formatTimeLabel(form.end)}` : "Missing"}</strong></div>
              <div><UsersRound size={18} /><span>Capacity</span><strong>{form.capacity || "Missing"}</strong></div>
              <div><CircleDollarSign size={18} /><span>Player price</span><strong>{String(form.price).trim() !== "" ? formatMoney(Number(form.price), sessionCurrency) : "Missing"}</strong></div>
            </div>
            {paidPublicationNeedsPayouts && <div className={`managed-payment-note session-payout-readiness ${payoutState.status}`}>
              <WalletCards size={20} />
              <div><strong>{payoutState.ready ? "Payouts ready" : payoutState.status === "checking" ? "Checking payouts" : "Payout setup needed"}</strong><span>{payoutState.message}</span></div>
              {!payoutState.ready && payoutState.status !== "checking" && <button type="button" onClick={() => navigate("payouts")}>Set up payouts</button>}
            </div>}
            {paidSessionUpgradeMissing.length > 0 && <div className="managed-payment-note session-payout-readiness needs-action">
              <Sparkles size={20} />
              <div><strong>Group Pro required</strong><span>Paid sessions are protected by the organiser subscription. Free sessions remain available without an upgrade.</span></div>
              <button type="button" onClick={() => navigate("organiser")}>View Group Pro</button>
            </div>}
            <div className="setup-validation-panel">
              <strong>{missing.length ? "Finish these items before saving." : form.publishNow ? "This session is ready to publish." : "This session is ready to save as a draft."}</strong>
              <div>{(missing.length ? missing : [form.publishNow ? "Ready for players" : "Private draft"]).map((item) => <span key={item}>{missing.length ? <CircleHelp size={14} /> : <Check size={14} />}{item}</span>)}</div>
            </div>
            <button type="button" onClick={saveSession} disabled={saveState.status === "loading" || missing.length > 0}>{saveState.status === "loading" ? "Saving session..." : session ? "Save session changes" : form.publishNow ? "Publish session" : "Save session draft"}</button>
            {saveState.message && <div className={`auth-message ${saveState.status}`}>{saveState.message}</div>}
          </section>
          <WizardNavigation steps={groupSessionWizardSteps} activeStepId={activeStepId} onStepChange={scrollToStep} readyToSave={sessionReadyToSave} />
        </section>
        <aside className="preview-card session-preview-card">
          <CalendarDays size={30} />
          <strong>{form.title || group.name}</strong>
          <span>{group.venue || "Venue pending"}</span>
          <b>{form.date ? formatDateLabel(form.date) : "Date pending"}</b>
          <span>{form.start && form.end ? `${formatTimeLabel(form.start)} - ${formatTimeLabel(form.end)}` : "Time pending"}</span>
          <div className="tag-chip-row preview-tags">
            <span className="tag-chip"><UsersRound size={14} />{form.capacity || "0"} spots</span>
            <span className="tag-chip"><WalletCards size={14} />{String(form.price).trim() !== "" ? Number(form.price) > 0 ? formatMoney(Number(form.price), sessionCurrency) : "Free" : "Price pending"}</span>
            <span className="tag-chip"><Gauge size={14} />{form.skillBand || group.skillBand || "Level pending"}</span>
          </div>
          <small>{form.publishNow ? "Public after saving" : "Private draft"}</small>
        </aside>
      </div>
    </section>
  );
}

function GroupSessionManager({ navigate, accountProfile, saveAccountProfile }) {
  const group = accountProfile.group || defaultAccountProfile.group;
  const sessions = (Array.isArray(group.sessions) ? group.sessions : [])
    .map((session, index) => buildGroupSessionOccurrence(group, { ...session, isRecurring: false }, index))
    .sort((first, second) => String(first.startAt || `${first.date}T${first.start}`).localeCompare(String(second.startAt || `${second.date}T${second.start}`)));
  const upcoming = sessions.filter((session) => !groupOccurrenceIsPast(session) && !["cancelled", "archived"].includes(String(session.status || "").toLowerCase()));
  const published = sessions.filter((session) => ["published", "active"].includes(String(session.status || "").toLowerCase()));
  const totalCapacity = upcoming.reduce((sum, session) => sum + (Number(session.capacity) || 0), 0);
  const [managerState, setManagerState] = useState({ busyId: "", message: "", status: "idle" });

  const archiveSession = async (session) => {
    const groupId = group.firestoreSourceId || group.serverGroupId || group.id || "";
    const startAt = session.startAt ? new Date(session.startAt) : localSessionDate(session.date, session.start);
    const endAt = session.endAt ? new Date(session.endAt) : localSessionDate(session.date, session.end);
    if (!groupId || !startAt || !endAt) {
      setManagerState({ busyId: "", status: "error", message: "This session is missing its backend group, date or time." });
      return;
    }
    setManagerState({ busyId: session.sessionId || session.id, status: "loading", message: "Archiving session..." });
    try {
      await callMarketplaceFunction("upsertGroupSession", {
        ...session,
        groupId,
        sessionId: session.sessionId || session.id,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        status: "archived"
      });
      const nextSessions = sessions.map((row) => String(row.sessionId || row.id) === String(session.sessionId || session.id)
        ? { ...row, status: "archived", public: false }
        : row);
      const accountSave = await saveAccountProfile({ group: { ...group, sessions: nextSessions } });
      if (!accountSave?.saved) throw new Error(accountSave?.error || "The backend session was archived, but the account summary did not refresh.");
      setManagerState({ busyId: "", status: "success", message: "Session archived. It no longer appears in the public join list." });
    } catch (error) {
      setManagerState({ busyId: "", status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  return (
    <section className="page group-session-manager-page">
      <div className="role-dashboard-head session-manager-head">
        <div>
          <p>Organiser sessions</p>
          <h1>{group.name ? `${group.name} sessions` : "Manage group sessions"}</h1>
          <span>Each playable session keeps its own date, time, capacity, price and payment status while your regular group profile stays reusable.</span>
        </div>
        <div className="role-dashboard-head-actions session-manager-head-actions">
          <button type="button" className="secondary" onClick={() => navigate("createGroup")}><Pencil size={16} /> Edit group profile</button>
          <button type="button" onClick={() => navigate("createGroupSession")}><Plus size={17} /> Create session</button>
        </div>
      </div>
      <div className="session-manager-stats">
        <article><CalendarDays size={19} /><span>Upcoming</span><strong>{upcoming.length}</strong></article>
        <article><BadgeCheck size={19} /><span>Published</span><strong>{published.length}</strong></article>
        <article><UsersRound size={19} /><span>Available capacity</span><strong>{totalCapacity}</strong></article>
        <article><WalletCards size={19} /><span>Collection</span><strong>Badminta</strong></article>
      </div>
      {managerState.message && <div className={`auth-message ${managerState.status}`}>{managerState.message}</div>}
      {sessions.length ? (
        <div className="group-session-list">
          {sessions.map((session) => {
            const status = String(session.status || "draft").toLowerCase();
            const archived = ["archived", "cancelled"].includes(status);
            return (
              <article key={session.sessionId || session.id} className={archived ? "archived" : ""}>
                <div className="group-session-date"><strong>{session.date ? formatDateLabel(session.date) : "Date pending"}</strong><span>{session.time || "Time pending"}</span></div>
                <div className="group-session-main"><span className={`status-pill ${status}`}>{status}</span><h2>{session.title || group.name}</h2><p>{group.venue || session.venue || "Venue pending"}</p></div>
                <div className="group-session-facts"><span><UsersRound size={15} /> {session.capacity || "0"} spots</span><span><WalletCards size={15} /> {session.priceLabel || "Price pending"}</span><span><Gauge size={15} /> {session.skillBand || group.skillBand || "Level pending"}</span></div>
                <div className="group-session-actions">
                  <button type="button" onClick={() => navigate("editGroupSession", { session })}><Pencil size={15} /> Edit</button>
                  {!archived && <button type="button" className="secondary" disabled={managerState.busyId === (session.sessionId || session.id)} onClick={() => archiveSession(session)}>{managerState.busyId === (session.sessionId || session.id) ? <><Loader2 className="spin" size={15} /> Archiving...</> : <><Archive size={15} /> Archive</>}</button>}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <CommunityEmptyState
          type="organiser sessions"
          title="No upcoming sessions yet"
          text="Create the next playable date so players can reserve a spot and pay in one flow. Your regular group schedule stays unchanged."
          primary={{ label: "Create first session", target: "createGroupSession" }}
          secondary={{ label: "View group profile", target: "publicGroupProfile" }}
          icon={CalendarDays}
          navigate={navigate}
        />
      )}
    </section>
  );
}

function StripeConnectSetupPanel({ role, accountProfile = defaultAccountProfile, auth }) {
  const roleId = role === "Centre" ? "centre" : role === "Coach" ? "coach" : "organiser";
  const ownerProfile = roleId === "centre" ? accountProfile.centre : roleId === "coach" ? accountProfile.coach : accountProfile.group;
  const ownerProfileId = roleId === "centre"
    ? ownerProfile?.serverCentreId || ownerProfile?.firestoreSourceId || accountProfile.publicCentreProfileId || ""
    : roleId === "coach"
      ? ownerProfile?.publicCoachId || ownerProfile?.firestoreSourceId || accountProfile.publicCoachProfileId || ""
      : ownerProfile?.publicGroupId || ownerProfile?.serverGroupId || ownerProfile?.firestoreSourceId || accountProfile.publicGroupProfileId || "";
  const country = normaliseCountryCode(
    ownerProfile?.countryCode
    || ownerProfile?.location?.countryCode
    || ownerProfile?.country
    || accountProfile.countryCode
    || accountProfile.country
    || "",
    ownerProfile?.city || accountProfile.city || ""
  );
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const [state, setState] = useState({ status: "checking", message: "", account: null, availability: null, checked: false });
  const countrySupported = state.availability?.supported === true;
  const busy = state.status === "loading";
  useEffect(() => {
    if (!hasRealFirebaseSession) {
      setState({ status: "idle", message: "", account: null, availability: null, checked: true });
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, status: "checking", message: "", checked: false }));
    (async () => {
      try {
        const availability = await callMarketplaceFunction("getStripeConnectCountryAvailability", {
          role: roleId,
          ownerProfileId,
          country
        });
        if (!active) return;
        if (!availability.supported) {
          setState({
            status: "unavailable",
            message: availability.message || "Badminta Pay payouts are not available for this owner country. Free activities and offline settlement remain available.",
            account: null,
            availability,
            checked: true
          });
          return;
        }
        try {
          const data = await callMarketplaceFunction("refreshStripeConnectAccount", { role: roleId, ownerProfileId });
          if (!active) return;
          setState({
            status: data.readyForCheckout ? "ready" : "needs-action",
            message: "",
            account: data.account || null,
            availability,
            checked: true
          });
        } catch (error) {
          if (!active) return;
          const message = marketplaceFunctionErrorMessage(error);
          const onboardingNotStarted = /create stripe onboarding|has not been created|start onboarding/i.test(message);
          setState({
            status: onboardingNotStarted ? "idle" : "error",
            message: onboardingNotStarted ? "" : message,
            account: null,
            availability,
            checked: true
          });
        }
      } catch (error) {
        if (!active) return;
        setState({
          status: "error",
          message: marketplaceFunctionErrorMessage(error),
          account: null,
          availability: null,
          checked: true
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [hasRealFirebaseSession, roleId, ownerProfileId, country]);
  const runConnectAction = async (action) => {
    if (action === "onboard" && !countrySupported) {
      setState((current) => ({
        ...current,
        status: "unavailable",
        message: country
          ? current.availability?.message || "Badminta Pay payouts are not available in this owner country. Use free activities and offline settlement."
          : "Add the country to this owner profile before setting up payouts.",
        checked: true
      }));
      return;
    }
    setState((current) => ({ ...current, status: "loading", message: "", checked: true }));
    try {
      if (action === "onboard") {
        const data = await callMarketplaceFunction("createStripeConnectOnboardingLink", {
          role: roleId,
          ownerProfileId,
          country,
          email: accountProfile.email || "",
          refreshUrl: window.location.href,
          returnUrl: window.location.href
        });
        setState((current) => ({ ...current, status: "ready", message: "Opening secure payout setup.", account: data.account || null, checked: true }));
        if (data.onboardingUrl) window.location.assign(data.onboardingUrl);
        return;
      }
      if (action === "dashboard") {
        const data = await callMarketplaceFunction("createStripeConnectDashboardLink", { role: roleId, ownerProfileId });
        setState((current) => ({ ...current, status: "ready", message: "Opening your payout dashboard.", account: state.account, checked: true }));
        if (data.dashboardUrl) window.open(data.dashboardUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const data = await callMarketplaceFunction("refreshStripeConnectAccount", { role: roleId, ownerProfileId });
      setState({
        status: data.readyForCheckout ? "ready" : "needs-action",
          message: data.readyForCheckout ? "Payouts are ready." : "Complete the remaining verification steps to receive payouts.",
          account: data.account || null,
          availability: state.availability,
          checked: true
      });
    } catch (error) {
      const message = marketplaceFunctionErrorMessage(error);
      const onboardingNotStarted = /create stripe onboarding|has not been created|start onboarding/i.test(message);
      setState({
        status: onboardingNotStarted ? "idle" : "error",
        message: onboardingNotStarted ? "Set up payouts before checking their status." : message,
        account: onboardingNotStarted ? null : state.account,
        availability: state.availability,
        checked: true
      });
    }
  };
  const account = state.account;
  const accountReady = isStripeAccountCheckoutReady(account || {});
  const requiredCapability = state.availability?.requiredCapability || account?.requiredCapability || "transfers";
  const capabilityReady = requiredCapability === "card_payments"
    ? account?.cardPaymentsCapability === "active" || account?.chargesEnabled === true
    : account?.transfersCapability === "active";
  const readinessRows = account ? [
    ["Status", accountReady ? "Ready" : "Action needed"],
    [requiredCapability === "card_payments" ? "Online payments" : "Marketplace transfers", capabilityReady ? "Enabled" : "Unavailable"],
    ["Payouts", account.payoutsEnabled ? "Enabled" : "Unavailable"],
    ["Details", account.detailsSubmitted ? "Complete" : "Incomplete"]
  ] : [
    ["Status", state.status === "checking" ? "Checking" : state.status === "unavailable" ? "Regional launch" : "Not set up"],
    ["Payments", "Unavailable"],
    ["Payouts", "Unavailable"],
    ["Details", "Not started"]
  ];
  return (
    <section className="stripe-connect-panel">
      <div className="stripe-connect-copy">
        <CreditCard size={22} />
        <div>
          <p>Badminta Pay</p>
          <h2>Get paid as {role === "Organiser" ? "an organiser" : `a ${role.toLowerCase()}`}</h2>
          <span>{hasRealFirebaseSession
            ? countrySupported
              ? "Add your legal details and bank account once. Badminta will then collect player payments and send your earnings to you."
              : "Your public profile and free activities remain worldwide. Where Stripe payouts are unavailable, use pay-at-venue or direct settlement and record the result in Badminta."
            : "Sign in before setting up payouts."}</span>
        </div>
      </div>
      <div className="stripe-connect-status">
        {readinessRows.map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      <div className="stripe-connect-actions">
        <button
          type="button"
          onClick={() => runConnectAction("onboard")}
          disabled={busy || !state.checked || !hasRealFirebaseSession || !ownerProfileId || !countrySupported}
          title={!country ? "Add the owner profile country first" : !countrySupported ? "Badminta Pay payouts are not available in this country yet" : ""}
        >
          <ExternalLink size={15} /> {account ? (accountReady ? "Update payout details" : "Continue verification") : "Set up payouts"}
        </button>
        {account && <button type="button" onClick={() => runConnectAction("refresh")} disabled={busy || !hasRealFirebaseSession}>
          <Activity size={15} /> Refresh status
        </button>}
        {account && <button type="button" onClick={() => runConnectAction("dashboard")} disabled={busy || !hasRealFirebaseSession}>
          <WalletCards size={15} /> Payout dashboard
        </button>}
      </div>
      <small>{countrySupported
        ? "Paid online activities use Badminta Checkout after your identity and bank details are approved."
        : "No Badminta card checkout is shown without a compliant payout route. Free activities and owner-recorded offline settlement remain available."}</small>
    </section>
  );
}

function CentreMembershipProductsPanel({ accountProfile = defaultAccountProfile, saveAccountProfile }) {
  const centre = accountProfile.centre || {};
  const centreCurrency = normaliseCurrencyCode(centre.currency || marketplaceCurrencyForCountry(centre.countryCode || centre.location?.countryCode));
  const products = getCentreMembershipProducts(centre);
  const [form, setForm] = useState(blankMembershipProduct);
  const [message, setMessage] = useState({ status: "idle", text: "" });
  const busy = message.status === "loading";
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const updateForm = (field, value) => setForm((current) => ({
    ...current,
    [field]: value,
    ...(field === "productType" ? { billingInterval: value === "membership" ? "monthly" : current.billingInterval || "one_time" } : {})
  }));
  const persistProducts = (nextProducts) => {
    saveAccountProfile?.({
      centre: {
        ...centre,
        membershipProducts: nextProducts
      }
    });
  };
  const editProduct = (product) => {
    setForm(normaliseMembershipProduct(product));
    setMessage({ status: "idle", text: "" });
  };
  const saveProduct = async (publish = false) => {
    const title = form.title.trim();
    const priceCents = amountToMinorUnits(membershipProductPriceNumber(form.price), centreCurrency);
    if (!title || priceCents < 50) {
      setMessage({ status: "error", text: "Add a product name and a price above the minimum checkout amount." });
      return;
    }
    const now = new Date().toISOString();
    const localId = form.id || `membership-product-${Date.now().toString(36)}`;
    const localProduct = normaliseMembershipProduct({
      ...form,
      id: localId,
      priceCents,
      currency: centreCurrency,
      status: publish ? "published" : form.status || "draft",
      updatedAt: now,
      createdAt: form.createdAt || now
    });
    let savedProduct = localProduct;
    setMessage({ status: "loading", text: publish ? "Publishing membership product..." : "Saving membership product..." });
    if (hasRealFirebaseSession) {
      try {
        const result = await callMarketplaceFunction("upsertMembershipProduct", {
          membershipId: form.serverMembershipId || form.id || "",
          title,
          productType: form.productType,
          status: localProduct.status,
          priceCents,
          currency: centreCurrency,
          creditHours: form.creditHours,
          visitCredits: form.visitCredits,
          expiryDays: form.expiryDays,
          billingInterval: form.billingInterval,
          redemptionRules: form.redemptionRules,
          cancellationPolicy: form.cancellationPolicy,
          centreName: centre.name || "",
          centreId: centre.serverCentreId || centre.firestoreSourceId || accountProfile.publicCentreProfileId || centre.claimedVenueId || centre.id || "",
          countryCode: centre.countryCode || centre.location?.countryCode || "",
          city: centre.city || centre.location?.city || "",
          suburb: centre.suburb || centre.location?.suburb || ""
        });
        savedProduct = {
          ...localProduct,
          id: result.membershipId || localProduct.id,
          serverMembershipId: result.membershipId || localProduct.serverMembershipId,
          status: result.status || localProduct.status,
          priceCents: result.priceCents || localProduct.priceCents,
          currency: result.currency || localProduct.currency
        };
        setMessage({ status: "ready", text: publish ? "Product published to the backend membership ledger." : "Product saved to the backend membership ledger." });
      } catch (error) {
        setMessage({ status: "error", text: `${marketplaceFunctionErrorMessage(error)} Saved locally for now.` });
      }
    } else {
      setMessage({ status: "needs-action", text: "Saved locally. Sign in with real Firebase phone auth to create the backend checkout product." });
    }
    const nextProducts = [
      savedProduct,
      ...products.filter((product) => product.id !== form.id && product.serverMembershipId !== form.serverMembershipId)
    ];
    persistProducts(nextProducts);
    setForm(blankMembershipProduct);
  };
  const archiveProduct = async (product) => {
    const nextProduct = { ...normaliseMembershipProduct(product), status: "archived", updatedAt: new Date().toISOString() };
    setMessage({ status: "loading", text: "Archiving membership product..." });
    if (hasRealFirebaseSession && product.serverMembershipId) {
      try {
        await callMarketplaceFunction("archiveMembershipProduct", { membershipId: product.serverMembershipId });
        setMessage({ status: "ready", text: "Product archived in the backend membership ledger." });
      } catch (error) {
        setMessage({ status: "error", text: `${marketplaceFunctionErrorMessage(error)} Archived locally for now.` });
      }
    } else {
      setMessage({ status: "needs-action", text: "Archived locally. Backend archive needs a real Firebase session and backend product id." });
    }
    persistProducts([nextProduct, ...products.filter((row) => row.id !== product.id && row.serverMembershipId !== product.serverMembershipId)]);
  };

  return (
    <section className="stripe-ops-panel membership-products-panel">
      <div className="stripe-ops-head">
        <BadgeDollarSign size={22} />
        <div>
          <p>Memberships and credit packs</p>
          <h2>Sell prepaid court credits, off-peak passes and memberships.</h2>
          <span>Published products become backend membership checkout sources. Paid checkouts create player membership purchase ledgers.</span>
        </div>
      </div>
      <div className="membership-product-editor">
        <label>
          <span>Product name</span>
          <input value={form.title} onChange={(event) => updateForm("title", event.target.value)} placeholder="10 hour off-peak credit pack" />
        </label>
        <label>
          <span>Product type</span>
          <select value={form.productType} onChange={(event) => updateForm("productType", event.target.value)}>
            {Object.entries(membershipProductTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Price ({centreCurrency.toUpperCase()})</span>
          <input value={form.price} onChange={(event) => updateForm("price", event.target.value)} inputMode="decimal" placeholder="180" />
        </label>
        <label>
          <span>Billing</span>
          <select value={form.billingInterval} onChange={(event) => updateForm("billingInterval", event.target.value)}>
            {Object.entries(membershipBillingLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Court credit hours</span>
          <input value={form.creditHours} onChange={(event) => updateForm("creditHours", event.target.value)} inputMode="decimal" placeholder="10" />
        </label>
        <label>
          <span>Visit credits</span>
          <input value={form.visitCredits} onChange={(event) => updateForm("visitCredits", event.target.value)} inputMode="numeric" placeholder="0" />
        </label>
        <label>
          <span>Expiry days</span>
          <input value={form.expiryDays} onChange={(event) => updateForm("expiryDays", event.target.value)} inputMode="numeric" placeholder="90" />
        </label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="wide">
          <span>Redemption rules</span>
          <textarea value={form.redemptionRules} onChange={(event) => updateForm("redemptionRules", event.target.value)} placeholder="Example: valid weekdays before 4pm, must book 2 hours before play, non-transferable." />
        </label>
        <label className="wide">
          <span>Cancellation policy</span>
          <textarea value={form.cancellationPolicy} onChange={(event) => updateForm("cancellationPolicy", event.target.value)} placeholder="Example: unused credits expire after 90 days; refunds follow centre policy and Stripe dispute rules." />
        </label>
      </div>
      <div className="membership-product-actions">
        <button type="button" onClick={() => saveProduct(false)} disabled={busy}>Save draft</button>
        <button type="button" onClick={() => saveProduct(true)} disabled={busy}>Publish product</button>
      </div>
      {message.text && <div className={`auth-message ${message.status}`}>{message.text}</div>}
      <div className="membership-product-grid">
        {products.length ? products.map((product) => (
          <article key={`${product.id}-${product.serverMembershipId}`}>
            <span>{membershipProductTypeLabels[product.productType]} · {product.status}</span>
            <strong>{product.title}</strong>
            <p>{membershipBillingLabels[product.billingInterval] || product.billingInterval} · {formatMoney(membershipProductPriceNumber(product.price), product.currency || centreCurrency)} · {product.creditHours || 0} court hour{Number(product.creditHours) === 1 ? "" : "s"}</p>
            <em>{product.serverMembershipId ? `Backend source: ${product.serverMembershipId}` : "Local draft only"}</em>
            <div>
              <button type="button" onClick={() => editProduct(product)}>Edit</button>
              {product.status !== "archived" && <button type="button" onClick={() => archiveProduct(product)}>Archive</button>}
            </div>
          </article>
        )) : (
          <div className="ops-empty">
            <strong>No membership products yet</strong>
            <span>Create credit packs or memberships here so centres can collect upfront revenue and players can manage credits in their wallet.</span>
          </div>
        )}
      </div>
    </section>
  );
}

function PayoutsPage({ role, accountProfile = defaultAccountProfile, auth, saveAccountProfile, navigate = () => {} }) {
  const roleId = role === "Centre" ? "centre" : role === "Coach" ? "coach" : "organiser";
  const isCentre = roleId === "centre";
  const isOrganiser = roleId === "organiser";
  const isCoach = roleId === "coach";
  const roleCurrency = accountRoleCurrency(accountProfile, roleId);
  const ownerEvents = isCoach ? [] : getOwnerEvents(accountProfile, isCentre ? "centre" : "organiser");
  const centre = accountProfile.centre || {};
  const group = accountProfile.group || {};
  const groupMembers = isOrganiser ? getPublicGroupMembers(group, accountProfile) : [];
  const groupPrice = getGroupPriceNumber(group);
  const paidGroupMembers = groupMembers.filter((member) => paymentStatusTone(member.paymentStatus || member.status) === "paid");
  const pendingGroupMembers = groupMembers.filter((member) => paymentNeedsAction(member.paymentStatus || member.status));
  const groupRevenue = paidGroupMembers.length * groupPrice;
  const groupPendingValue = pendingGroupMembers.length * groupPrice;
  const centreBookings = isCentre ? getAccountBookings(accountProfile).filter((booking) => bookingMatchesCentre(booking, centre)) : [];
  const payments = getAccountPayments(accountProfile).filter((payment) => {
    if (isCentre) return payment.kind === "centre" || payment.kind === "booking";
    if (isCoach) return payment.kind === "coach" || payment.kind === "lesson" || payment.kind === "coaching";
    return payment.kind === "social" || payment.kind === "event" || payment.kind === "group";
  });
  const paymentAmountNumber = (payment) => moneyRecordAmount(payment, roleCurrency);
  const paymentDuplicatesCentreBooking = (payment) => isCentre && String(payment.kind || "").toLowerCase() === "booking" && centreBookings.some((booking) => {
    const paymentSource = String(payment.sourceId || payment.bookingId || "").trim();
    const sameSource = paymentSource && paymentSource === String(booking.id || "");
    const sameSubject = payment.subject && booking.venueName && normaliseLocationText(payment.subject).toLowerCase() === normaliseLocationText(booking.venueName).toLowerCase();
    const sameAmount = paymentAmountNumber(payment) > 0 && Number(booking.total || 0) > 0 && Math.abs(paymentAmountNumber(payment) - Number(booking.total || 0)) < 0.01;
    return sameSource || (sameSubject && sameAmount);
  });
  const visiblePayments = payments.filter((payment) => !paymentDuplicatesCentreBooking(payment));
  const paidEventMoneyRows = ownerEvents.map((event) => ({
    amount: getPublicEventAttendees(event, accountProfile).filter((attendee) => paymentStatusTone(attendee.paymentStatus || attendee.status) === "paid").length * eventPriceNumber(event),
    currency: event.currency || roleCurrency
  }));
  const pendingEventMoneyRows = ownerEvents.map((event) => ({
    amount: getPublicEventAttendees(event, accountProfile).filter((attendee) => paymentNeedsAction(attendee.paymentStatus || attendee.status)).length * eventPriceNumber(event),
    currency: event.currency || roleCurrency
  }));
  const paidBookingMoneyRows = centreBookings.filter((booking) => paymentStatusTone(booking.paymentStatus || booking.status) === "paid");
  const pendingBookingMoneyRows = centreBookings.filter((booking) => paymentNeedsAction(booking.paymentStatus || booking.status));
  const paidRecordedMoneyRows = visiblePayments.filter((payment) => paymentStatusTone(payment.status || payment.paymentStatus) === "paid");
  const pendingRecordedMoneyRows = visiblePayments.filter((payment) => paymentNeedsAction(payment.status || payment.paymentStatus));
  const paidGroupMoneyRows = groupRevenue ? [{ amount: groupRevenue, currency: group.currency || roleCurrency }] : [];
  const pendingGroupMoneyRows = groupPendingValue ? [{ amount: groupPendingValue, currency: group.currency || roleCurrency }] : [];
  const grossMoneyRows = [...paidEventMoneyRows, ...paidBookingMoneyRows, ...paidRecordedMoneyRows, ...paidGroupMoneyRows];
  const pendingMoneyRows = [...pendingEventMoneyRows, ...pendingBookingMoneyRows, ...pendingRecordedMoneyRows, ...pendingGroupMoneyRows];
  const grossRevenueLabel = formatMoneyBreakdown(grossMoneyRows, roleCurrency);
  const pendingValueLabel = formatMoneyBreakdown(pendingMoneyRows, roleCurrency);
  const recipientLabel = isCentre ? "centre" : isCoach ? "coach" : "organiser";
  const emptyTransactionLabel = isCentre ? "court bookings" : isCoach ? "lesson bookings or coaching packages" : "paid group sessions or event registrations";
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const profileLabel = isCentre
    ? centre.name || "Centre profile"
    : isCoach
      ? accountProfile.coach?.headline || accountProfile.fullName || "Coach profile"
      : group.name || "Social group";
  const paymentActionTarget = isCentre ? "centrePayments" : isCoach ? "coachPayouts" : "payouts";
  const [statementState, setStatementState] = useState({
    status: "idle",
    message: "",
    statement: null
  });
  const statementBusy = statementState.status === "loading";
  const generateStatement = async () => {
    setStatementState({ status: "loading", message: "Generating payout statement from backend ledger records...", statement: statementState.statement });
    try {
      const statement = await callMarketplaceFunction("generateOwnerPayoutStatement", { role: roleId });
      setStatementState({
        status: statement.failedRefundCount ? "needs-action" : "ready",
        message: statement.failedRefundCount ? "Statement generated. Refund failures need admin review before payout is final." : "Statement generated from backend payment and refund records.",
        statement
      });
    } catch (error) {
      setStatementState({
        status: "error",
        message: marketplaceFunctionErrorMessage(error),
        statement: null
      });
    }
  };
  const statement = statementState.statement;
  const statementRows = statement ? [
    ["Gross player charges", formatCurrencyCents(statement.grossSalesCents, statement.currency), `${statement.paymentCount || 0} paid payment${statement.paymentCount === 1 ? "" : "s"}`],
    ["Owner gross", formatCurrencyCents(statement.ownerGrossCents, statement.currency), "Before refunds"],
    ["Badminta fees", formatCurrencyCents(statement.platformFeeNetCents, statement.currency), `${formatCurrencyCents(statement.platformFeeCents, statement.currency)} before fee refunds`],
    ["Refunds", formatCurrencyCents(statement.executedRefundCents, statement.currency), `${statement.refundCount || 0} refund record${statement.refundCount === 1 ? "" : "s"}`],
    ["Estimated payout", formatCurrencyCents(statement.estimatedPayoutCents, statement.currency), statement.failedRefundCount ? `${statement.failedRefundCount} failed refund${statement.failedRefundCount === 1 ? "" : "s"} to review` : "Ready for reconciliation"]
  ] : [];
  const periodLabel = statement
    ? `${new Date(statement.periodStart).toLocaleDateString()} to ${new Date(statement.periodEnd).toLocaleDateString()}`
    : "Current month to date";
  const transactionRows = [
    ...ownerEvents.map((event) => {
      const attendees = getPublicEventAttendees(event, accountProfile);
      const paidCount = attendees.filter((attendee) => paymentStatusTone(attendee.paymentStatus || attendee.status) === "paid").length;
      const pendingCount = attendees.filter((attendee) => paymentNeedsAction(attendee.paymentStatus || attendee.status)).length;
      return [
        event.title,
        "Event fee",
        `${paidCount} paid${pendingCount ? ` · ${pendingCount} pending` : ""}`,
        formatMoney(paidCount * eventPriceNumber(event), event.currency || roleCurrency)
      ];
    }),
    ...(!isCentre && groupRevenue ? [[
      group.name || "Social group session",
      "Social session",
      `${paidGroupMembers.length} paid player${paidGroupMembers.length === 1 ? "" : "s"}`,
      formatMoney(groupRevenue, group.currency || roleCurrency)
    ]] : []),
    ...visiblePayments.filter((payment) => paymentStatusTone(payment.status || payment.paymentStatus) === "paid").map((payment) => [
      payment.subject || payment.title,
      "Recorded payment",
      payment.status,
      payment.amount
    ]),
    ...centreBookings.filter((booking) => paymentStatusTone(booking.paymentStatus || booking.status) === "paid").map((booking) => [
      booking.venueName,
      "Court booking",
      `${formatDateLabel(booking.date)}, ${formatTimeLabel(booking.time)}`,
      getBookingTotalLabel(booking)
    ])
  ].filter((row) => Number(String(row[3]).replace(/[^0-9.]/g, "")) > 0);
  const eventPaymentActionRows = ownerEvents.flatMap((event) => getPublicEventAttendees(event, accountProfile)
    .filter((attendee) => paymentNeedsAction(attendee.paymentStatus || attendee.status))
    .slice(0, 2)
    .map((attendee) => ({
      icon: CalendarDays,
      title: attendee.name || attendee.fullName || "Event attendee",
      detail: [event.title, attendee.email, attendee.paymentStatus || attendee.status].filter(Boolean).join(" · "),
      status: attendee.paymentStatus || attendee.status || "Payment pending",
      target: "manageEvent",
      payload: { event }
    })));
  const groupPaymentActionRows = isOrganiser ? groupMembers
    .filter((member) => paymentNeedsAction(member.paymentStatus || member.status))
    .slice(0, 3)
    .map((member) => ({
      icon: UsersRound,
      title: member.name || member.email || "Group member",
      detail: [group.name || "Social group", member.email || member.contact, member.paymentStatus || member.status].filter(Boolean).join(" · "),
      status: member.paymentStatus || member.status || "Payment pending",
      target: "members"
    })) : [];
  const needsActionRows = [
    ...centreBookings.filter((booking) => paymentNeedsAction(booking.paymentStatus || booking.status)).map((booking) => ({
      icon: Grid3X3,
      title: booking.courtName || booking.venueName || "Court booking",
      detail: [formatDateLabel(booking.date), formatTimeLabel(booking.time), getBookingTotalLabel(booking)].filter(Boolean).join(" · "),
      status: booking.paymentStatus || booking.status || "Payment pending",
      target: "bookings"
    })),
    ...visiblePayments.filter((payment) => paymentNeedsAction(payment.status)).map((payment) => ({
      icon: WalletCards,
      title: payment.title || payment.subject || "Payment record",
      detail: [payment.subject, payment.amount].filter(Boolean).join(" · "),
      status: payment.status || "Payment pending",
      target: paymentActionTarget
    })),
    ...groupPaymentActionRows,
    ...eventPaymentActionRows
  ].slice(0, 5);
  const moneyStats = [
    [WalletCards, "Needs action", `${needsActionRows.length}`, needsActionRows.length ? "Open payment, booking or refund rows." : "No payment rows need action."],
    [BadgeDollarSign, "Recorded paid", grossRevenueLabel, transactionRows.length ? `${transactionRows.length} paid row${transactionRows.length === 1 ? "" : "s"}` : "No paid activity yet."],
    [Clock3, "Awaiting payment", pendingValueLabel, pendingMoneyRows.length ? "Checkout or review is still pending." : "Nothing pending."],
    [Banknote, "Estimated payout", statement ? formatCurrencyCents(statement.estimatedPayoutCents, statement.currency) : "Generate statement", statement ? "Backend fees and executed refunds applied." : "Generate the backend statement to apply country fee rules and refunds."]
  ];
  return (
    <section className="page payouts-page">
      <section className="money-command-panel">
        <div className="money-command-head">
          <div>
            <p>{role} payments</p>
            <h1>{profileLabel}</h1>
            <span>Track player payments, money needing attention and earnings for this {recipientLabel} account.</span>
          </div>
        </div>
        <div className="money-command-stats">
          {moneyStats.map(([StatIcon, label, value, detail]) => (
            <article key={label}>
              <StatIcon size={18} />
              <span>{label}</span>
              <strong>{value}</strong>
              <small>{detail}</small>
            </article>
          ))}
        </div>
        <div className="money-command-grid money-command-grid-single">
          <section className="money-command-worklist money-command-worklist-wide">
            <div className="role-panel-head">
              <p>Current work</p>
              <h2>{needsActionRows.length ? "Needs attention" : "No money actions right now"}</h2>
            </div>
            {needsActionRows.length ? (
              <div className="money-action-list">
                {needsActionRows.map(({ icon: RowIcon, title, detail, status, target, payload }, index) => (
                  <button type="button" key={`${title}-${index}`} onClick={() => navigate(target || paymentActionTarget, payload || {})}>
                    <span className="money-action-icon"><RowIcon size={17} /></span>
                    <span>
                      <strong>{title}</strong>
                      <small>{detail}</small>
                    </span>
                    <em className={`payment-chip ${paymentStatusTone(status)}`}>{status}</em>
                    <ChevronRight size={15} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="ops-empty">
                <strong>No payable rows waiting</strong>
                <span>Real bookings, paid sessions, lesson deposits, events, refunds and disputes will appear here as soon as users create them.</span>
              </div>
            )}
          </section>
        </div>
      </section>
      <StripeConnectSetupPanel role={role} accountProfile={accountProfile} auth={auth} />
      {isCentre && <CentreMembershipProductsPanel accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} />}
      <section className="ops-table payout-transactions-panel">
        <div className="payout-section-heading">
          <div>
            <p>Activity</p>
            <h2>Transactions</h2>
          </div>
          <span>{transactionRows.length ? `${transactionRows.length} paid record${transactionRows.length === 1 ? "" : "s"}` : "No paid activity yet"}</span>
        </div>
        {transactionRows.length ? transactionRows.map((row) => <OpsRow row={row} key={row.join("-")} />) : (
          <div className="ops-empty">
            <strong>No transactions yet</strong>
            <span>Payments and refunds will appear after {emptyTransactionLabel} are created.</span>
          </div>
        )}
      </section>
      <section className="stripe-ops-panel payout-statement-panel">
        <div className="stripe-ops-head">
          <WalletCards size={22} />
          <div>
            <p>Monthly statement</p>
            <h2>Reconcile this month&apos;s earnings.</h2>
            <span>{periodLabel}</span>
          </div>
        </div>
        <div className="payout-statement-actions">
          <button type="button" onClick={generateStatement} disabled={statementBusy || !hasRealFirebaseSession}>
            <WalletCards size={15} /> {statementBusy ? "Generating..." : "Generate statement"}
          </button>
          <small>{hasRealFirebaseSession ? "Statements use completed payment and refund records." : "Sign in to generate a statement."}</small>
        </div>
        {statementState.message && <div className={`auth-message stripe-connect-message ${statementState.status}`}>{statementState.message}</div>}
        {statementRows.length ? (
          <div className="stripe-ops-grid payout-statement-grid">
            {statementRows.map(([label, value, detail]) => (
              <article key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
                <p>{detail}</p>
              </article>
            ))}
          </div>
        ) : (
          <p className="payout-statement-empty">Generate a statement when you need a month-to-date payout summary.</p>
        )}
      </section>
    </section>
  );
}

function MemberManagement({ navigate, accountProfile, saveAccountProfile, playerDirectory = [], playerDirectoryStatus = {} }) {
  const group = accountProfile.group || {};
  const groupId = group.firestoreSourceId || group.serverGroupId || group.publicGroupId || group.id || accountProfile.publicGroupProfileId || "";
  const groupIsPublic = group.publicProfilePublished === true || ["published", "active"].includes(String(group.backendStatus || group.status || "").toLowerCase());
  const storedInviteEmails = cleanEmailTags(group.inviteEmails?.length ? group.inviteEmails : group.invites);
  const [inviteEditorOpen, setInviteEditorOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState(storedInviteEmails);
  const [playerSearch, setPlayerSearch] = useState("");
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [inviteSaveState, setInviteSaveState] = useState({ status: "", message: "" });
  const [savingInvites, setSavingInvites] = useState(false);
  const ownUid = String(accountProfile.uid || accountProfile.ownerUid || accountProfile.playerUid || "").trim();
  const ownPlayerProfileIds = new Set([
    accountProfile.publicPlayerProfileId,
    accountProfile.player?.firestoreSourceId,
    accountProfile.player?.publicPlayerId,
    accountProfile.player?.id
  ].map((value) => String(value || "").trim()).filter(Boolean));
  const searchablePlayers = useMemo(
    () => playerDirectory.filter((player) => {
      if (!isSearchablePlayerInviteProfile(player) || ownPlayerProfileIds.has(playerInviteProfileId(player))) return false;
      const recipientUid = playerInviteRecipientUid(player);
      return !recipientUid || recipientUid !== ownUid;
    }),
    [playerDirectory, ownUid, [...ownPlayerProfileIds].join("|")]
  );
  const playersById = useMemo(() => searchablePlayers.reduce((records, player) => {
    records.set(playerInviteProfileId(player), player);
    return records;
  }, new globalThis.Map()), [searchablePlayers]);
  const selectedPlayers = selectedPlayerIds.map((playerId) => playersById.get(playerId)).filter(Boolean);
  const existingPlatformInviteUids = useMemo(() => new Set(
    (Array.isArray(accountProfile.groupInvites) ? accountProfile.groupInvites : [])
      .filter((invite) => invite.inviteType === "platform_player" && (!invite.groupId || !groupId || String(invite.groupId) === String(groupId)))
      .map(playerInviteRecipientUid)
      .filter(Boolean)
  ), [accountProfile.groupInvites, groupId]);
  const existingPlatformInvitePlayerIds = useMemo(() => new Set(
    (Array.isArray(accountProfile.groupInvites) ? accountProfile.groupInvites : [])
      .filter((invite) => invite.inviteType === "platform_player" && (!invite.groupId || !groupId || String(invite.groupId) === String(groupId)))
      .map((invite) => String(invite.playerProfileId || "").trim())
      .filter(Boolean)
  ), [accountProfile.groupInvites, groupId]);
  const normalisedPlayerSearch = normalisePlayerInviteSearchText(playerSearch);
  const playerSearchResults = useMemo(() => {
    if (normalisedPlayerSearch.length < 2) return [];
    const terms = normalisedPlayerSearch.split(" ").filter(Boolean);
    return searchablePlayers
      .map((player) => {
        const name = publicPlayerDisplayName(player.name || player.fullName || player.title, playerInviteProfileId(player));
        const normalisedName = normalisePlayerInviteSearchText(name);
        const haystack = normalisePlayerInviteSearchText(
          name,
          playerInviteLocationLabel(player),
          player.skillBand,
          player.level
        );
        if (!terms.every((term) => haystack.includes(term))) return null;
        const rank = normalisedName === normalisedPlayerSearch
          ? 0
          : normalisedName.startsWith(normalisedPlayerSearch)
            ? 1
            : normalisedName.includes(normalisedPlayerSearch)
              ? 2
              : 3;
        return { player, name, rank };
      })
      .filter(Boolean)
      .sort((first, second) => first.rank - second.rank || first.name.localeCompare(second.name))
      .slice(0, 8);
  }, [normalisedPlayerSearch, searchablePlayers]);
  const canSaveInviteList = selectedPlayerIds.length > 0 || inviteEmails.length > 0 || storedInviteEmails.length > 0;
  const memberRows = buildMemberRowsFromAccount(accountProfile);
  const toggleSelectedPlayer = (playerId) => {
    setInviteSaveState({ status: "", message: "" });
    setSelectedPlayerIds((current) => current.includes(playerId)
      ? current.filter((id) => id !== playerId)
      : current.length < 50 ? [...current, playerId] : current);
  };
  const saveInvitations = async () => {
    const cleanedInviteEmails = cleanEmailTags(inviteEmails);
    setInviteSaveState({ status: "", message: "" });
    if (!groupId || !groupIsPublic) {
      setInviteSaveState({ status: "error", message: "Publish your social group before sending member invitations." });
      return;
    }
    setSavingInvites(true);
    const accountResult = await saveAccountProfile({
      group: {
        inviteEmails: cleanedInviteEmails,
        invites: cleanedInviteEmails.join("\n")
      }
    });
    if (!accountResult?.saved) {
      setSavingInvites(false);
      setInviteSaveState({ status: "error", message: accountResult?.error || "Badminta could not save these invite emails. Try again." });
      return;
    }
    try {
      const delivery = await callMarketplaceFunction("inviteGroupMembers", {
        groupId,
        inviteEmails: cleanedInviteEmails,
        playerIds: selectedPlayerIds
      });
      setInviteEmails(cleanedInviteEmails);
      setSelectedPlayerIds([]);
      setPlayerSearch("");
      const sentParts = [];
      if (delivery.playerQueuedCount) {
        sentParts.push(`${delivery.playerQueuedCount} Badminta player invitation${delivery.playerQueuedCount === 1 ? "" : "s"} sent`);
      }
      if (delivery.emailQueuedCount) {
        sentParts.push(`${delivery.emailQueuedCount} email invitation${delivery.emailQueuedCount === 1 ? "" : "s"} queued`);
      }
      const alreadyCount = Number(delivery.playerAlreadyQueuedCount || 0) + Number(delivery.emailAlreadyQueuedCount || 0);
      if (alreadyCount) {
        sentParts.push(`${alreadyCount} existing invitation${alreadyCount === 1 ? " was" : "s were"} not sent twice`);
      }
      if (delivery.playerSkippedCount) {
        sentParts.push(`${delivery.playerSkippedCount} unavailable player profile${delivery.playerSkippedCount === 1 ? " was" : "s were"} skipped`);
      }
      setInviteSaveState({
        status: "success",
        message: sentParts.length
          ? `${sentParts.join("; ")}.`
          : !cleanedInviteEmails.length
            ? "The private email invite list is now empty."
            : "These invitations are already in the delivery queue."
      });
    } catch (error) {
      setInviteSaveState({
        status: "error",
        message: `The private invite list was saved, but invitations could not be sent. ${marketplaceFunctionErrorMessage(error)}`
      });
    } finally {
      setSavingInvites(false);
    }
  };
  const messageMember = (member) => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: "organiser",
    targetCollection: member.playerId ? "players" : "",
    targetId: member.playerId || "",
    subject: member.name || member.contact || "Group member",
    threadType: "Member conversation",
    text: `Member conversation opened for ${member.name || member.contact}. Keep invite status, source, rating and attendance context attached before messaging.`,
    status: member.status || "Open",
    meta: [member.status, member.meta, member.source],
    context: [["Member", member.name || "Pending"], ["Contact", member.contact || "Pending"], ["Source", member.source || "Group"], ["Status", member.status || "Pending"]],
    actions: [["Manage members", "members"], ["Edit group", "createGroup"], ["Messages", "messages"]]
  });
  return (
    <section className="page members-page">
      <div className="screen-head">
        <p>Organiser members</p>
        <h1>Manage members, invites, attendance and ratings.</h1>
        <button type="button" onClick={() => {
          setInviteEmails(storedInviteEmails);
          setSelectedPlayerIds([]);
          setPlayerSearch("");
          setInviteSaveState({ status: "", message: "" });
          setInviteEditorOpen((open) => !open);
        }}>{inviteEditorOpen ? "Close invitations" : "Invite players"}</button>
      </div>
      {inviteEditorOpen && (
        <section className="member-invite-editor" aria-label="Invite players to this social group">
          <div className="member-invite-editor-copy">
            <UsersRound size={22} />
            <div>
              <h2>Invite players</h2>
              <p>Find an existing Badminta player or invite someone privately by email.</p>
            </div>
          </div>
          <div className="member-invite-methods">
            <section className="member-player-search">
              <div className="member-invite-method-head">
                <Search size={19} />
                <div>
                  <h3>Search Badminta players</h3>
                  <span>Invite a published player directly in Badminta.</span>
                </div>
              </div>
              <label className="member-player-search-input">
                <Search size={18} />
                <input
                  type="search"
                  aria-label="Search Badminta players"
                  value={playerSearch}
                  onChange={(event) => {
                    setPlayerSearch(event.target.value);
                    setInviteSaveState({ status: "", message: "" });
                  }}
                  placeholder="Search by player name or location"
                  autoComplete="off"
                />
                {playerSearch && (
                  <button type="button" onClick={() => setPlayerSearch("")} aria-label="Clear player search">
                    <X size={16} />
                  </button>
                )}
              </label>
              {selectedPlayers.length > 0 && (
                <div className="member-selected-players" aria-label="Players selected for invitation">
                  {selectedPlayers.map((player) => {
                    const playerId = playerInviteProfileId(player);
                    const name = publicPlayerDisplayName(player.name || player.fullName || player.title, playerId);
                    return (
                      <span key={playerId}>
                        <PlayerCardMedia src={getPublicProfileImage(player.photoUrl, player.image)} name={name} />
                        <strong>{name}</strong>
                        <button type="button" onClick={() => toggleSelectedPlayer(playerId)} aria-label={`Remove ${name}`}>
                          <X size={13} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="member-player-results" aria-live="polite">
                {normalisedPlayerSearch.length < 2 ? (
                  <p>Enter at least two letters to search.</p>
                ) : playerDirectoryStatus.state === "syncing" && !searchablePlayers.length ? (
                  <p>Loading published players...</p>
                ) : playerSearchResults.length ? playerSearchResults.map(({ player, name }) => {
                  const playerId = playerInviteProfileId(player);
                  const recipientUid = playerInviteRecipientUid(player);
                  const selected = selectedPlayerIds.includes(playerId);
                  const alreadyInvited = existingPlatformInvitePlayerIds.has(playerId)
                    || Boolean(recipientUid && existingPlatformInviteUids.has(recipientUid));
                  return (
                    <div key={playerId} className={selected ? "selected" : ""}>
                      <PlayerCardMedia src={getPublicProfileImage(player.photoUrl, player.image)} name={name} />
                      <span>
                        <strong>{name}</strong>
                        <small>{[player.skillBand || player.level, playerInviteLocationLabel(player)].filter(Boolean).join(" · ")}</small>
                      </span>
                      <button
                        type="button"
                        disabled={alreadyInvited}
                        className={selected ? "selected" : ""}
                        onClick={() => toggleSelectedPlayer(playerId)}
                      >
                        {alreadyInvited ? <Check size={15} /> : selected ? <X size={15} /> : <Plus size={15} />}
                        {alreadyInvited ? "Invited" : selected ? "Remove" : "Add"}
                      </button>
                    </div>
                  );
                }) : (
                  <p>No published players match this search.</p>
                )}
              </div>
            </section>
            <section className="member-email-invites">
              <div className="member-invite-method-head">
                <Mail size={19} />
                <div>
                  <h3>Invite by email</h3>
                  <span>Use this when the player is not on Badminta yet.</span>
                </div>
              </div>
              <EmailTagInput emails={inviteEmails} onChange={(emails) => {
                setInviteEmails(emails);
                setInviteSaveState({ status: "", message: "" });
              }} />
            </section>
          </div>
          <div className="member-invite-actions">
            <button className="primary" type="button" onClick={saveInvitations} disabled={savingInvites || !canSaveInviteList}>
              <Send size={16} /> {
                savingInvites
                  ? "Sending invitations..."
                  : selectedPlayerIds.length || inviteEmails.length
                    ? "Send invitations"
                    : storedInviteEmails.length
                      ? "Clear email invite list"
                      : "Select a player or add an email"
              }
            </button>
            <button type="button" className="secondary" onClick={() => {
              setInviteEmails(storedInviteEmails);
              setSelectedPlayerIds([]);
              setPlayerSearch("");
              setInviteSaveState({ status: "", message: "" });
              setInviteEditorOpen(false);
            }}>Cancel</button>
          </div>
          {inviteSaveState.message && <div className={`auth-message ${inviteSaveState.status}`}>{inviteSaveState.message}</div>}
        </section>
      )}
      <div className="member-table">
        {memberRows.length ? memberRows.map((member) => (
          <div key={member.id}>
            {member.kind === "email"
              ? <span className="member-email-avatar"><Mail size={18} /></span>
              : <PlayerCardMedia src={member.photoUrl} name={member.name} className="member-row-avatar" />}
            <span className="member-row-copy">
              <strong title={member.contact}>{member.name}</strong>
              <small>{member.source}</small>
            </span>
            <span className="member-row-state">
              <strong>{member.status}</strong>
              <small>{member.meta}</small>
            </span>
            <button type="button" onClick={() => messageMember(member)}>Message</button>
          </div>
        )) : (
          <CommunityEmptyState
            {...emptyPlayerState("your organiser account")}
            title="No members yet"
            text="Members appear after real users join your group, accept an invite, or register for one of your organiser events."
            primary={{ label: "Create group", target: "createGroup" }}
            secondary={{ label: "Create event", target: "createEvent" }}
            navigate={navigate}
          />
        )}
      </div>
    </section>
  );
}

function CentreDashboard({ venue, navigate, accountProfile }) {
  const payoutState = useOwnerStripeConnectReadiness("centre", accountProfile);
  return <RoleDashboard config={buildRoleDashboardConfig("centre", accountProfile, venue, payoutState)} navigate={navigate} />;
}

function CoachDashboard({ navigate, accountProfile }) {
  const payoutState = useOwnerStripeConnectReadiness("coach", accountProfile);
  return <RoleDashboard config={buildRoleDashboardConfig("coach", accountProfile, defaultVenue, payoutState)} navigate={navigate} />;
}

function CourtPricingEditor({ value, onChange, title = "Court fees", currency = "aud" }) {
  const pricing = normaliseCourtPricing(value);
  const updatePricing = (field, nextValue) => {
    onChange?.({
      ...pricing,
      [field]: nextValue
    });
  };
  const updateRateRule = (key, patch) => {
    const rateRules = normaliseCourtRateRules(pricing.rateRules);
    const nextRule = { ...rateRules[key], ...patch };
    const nextRules = { ...rateRules, [key]: nextRule };
    const dayLabels = nextRule.days.map((day) => availabilityDayOptions.find(([option]) => option === day)?.[1]?.slice(0, 3)).filter(Boolean);
    onChange?.({
      ...pricing,
      rateRules: nextRules,
      [`${key}Window`]: nextRule.enabled ? `${dayLabels.join(", ")} ${formatTimeLabel(nextRule.start)}-${formatTimeLabel(nextRule.end)}` : pricing[`${key}Window`]
    });
  };
  const toggleRateRuleDay = (key, day) => {
    const rule = pricing.rateRules[key];
    const days = rule.days.includes(day) ? rule.days.filter((item) => item !== day) : [...rule.days, day];
    updateRateRule(key, { days });
  };
  return (
    <section className="court-pricing-editor">
      <div className="form-section-head">
        <h3>{title}</h3>
        <span>Set different prices for standard, off-peak, peak and weekend court hire.</span>
      </div>
      <div className="court-pricing-grid">
        <label>
          <span>Standard fee</span>
          <input value={pricing.standard} onChange={(event) => updatePricing("standard", event.target.value)} inputMode="decimal" placeholder="25 per hour" />
        </label>
        <label>
          <span>Off-peak fee</span>
          <input value={pricing.offPeak} onChange={(event) => updatePricing("offPeak", event.target.value)} inputMode="decimal" placeholder="18 per hour" />
        </label>
        <label>
          <span>Peak fee</span>
          <input value={pricing.peak} onChange={(event) => updatePricing("peak", event.target.value)} inputMode="decimal" placeholder="32 per hour" />
        </label>
        <label>
          <span>Weekend fee</span>
          <input value={pricing.weekend} onChange={(event) => updatePricing("weekend", event.target.value)} inputMode="decimal" placeholder="30 per hour" />
        </label>
      </div>
      <div className="court-rate-rule-editor">
        <div className="form-section-head compact">
          <h3>Automatic rate times</h3>
          <span>Badminta selects the correct fee from the local date and time. Players cannot choose a cheaper rate.</span>
        </div>
        {Object.keys(courtRateRuleDefaults).map((key) => {
          const rule = pricing.rateRules[key];
          const hasFee = isConfirmedCourtFeeValue(pricing[key]);
          return (
            <section className={rule.enabled ? "court-rate-rule active" : "court-rate-rule"} key={key}>
              <div className="court-rate-rule-head">
                <label>
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    disabled={!hasFee}
                    onChange={(event) => updateRateRule(key, { enabled: event.target.checked })}
                  />
                  <span>{courtRateRuleLabels[key]}</span>
                </label>
                <em>{hasFee ? formatCourtFeeValue(pricing[key], currency) : "Add fee first"}</em>
              </div>
              <div className="court-rate-rule-days" aria-label={`${courtRateRuleLabels[key]} days`}>
                {availabilityDayOptions.map(([day, label]) => (
                  <button
                    type="button"
                    key={`${key}-${day}`}
                    className={rule.days.includes(day) ? "active" : ""}
                    disabled={!rule.enabled}
                    onClick={() => toggleRateRuleDay(key, day)}
                    aria-pressed={rule.days.includes(day)}
                  >{label.slice(0, 2)}</button>
                ))}
              </div>
              <div className="court-rate-rule-times">
                <label>
                  <span>From</span>
                  <select value={rule.start} disabled={!rule.enabled} onChange={(event) => updateRateRule(key, { start: event.target.value })}>
                    {courtRateTimeOptions.map((time) => <option key={`${key}-start-${time}`} value={time}>{formatTimeLabel(time)}</option>)}
                  </select>
                </label>
                <label>
                  <span>To</span>
                  <select value={rule.end} disabled={!rule.enabled} onChange={(event) => updateRateRule(key, { end: event.target.value })}>
                    {courtRateEndTimeOptions.map((time) => <option key={`${key}-end-${time}`} value={time}>{formatTimeLabel(time)}</option>)}
                  </select>
                </label>
              </div>
            </section>
          );
        })}
      </div>
      <label className="court-pricing-notes">
        <span>Pricing notes</span>
        <textarea value={pricing.notes} onChange={(event) => updatePricing("notes", event.target.value)} placeholder="Add school holiday rules, public holiday rates, member discounts, minimum booking duration or shuttle hire notes." />
      </label>
      {hasCourtPricing(pricing) && (
        <div className="court-pricing-preview">
          <CircleDollarSign size={18} />
          <span>{getCourtPricingSummary(pricing, "", currency)}</span>
        </div>
      )}
    </section>
  );
}

function CentreTimeZoneField({ value, onChange }) {
  const selected = value || browserTimeZone();
  const options = supportedTimeZones.includes(selected) ? supportedTimeZones : [selected, ...supportedTimeZones];
  return (
    <label className="centre-time-zone-field">
      <span>Centre time zone</span>
      <select value={selected} onChange={(event) => onChange?.(event.target.value)}>
        {options.map((timeZone) => <option key={timeZone} value={timeZone}>{timeZone.replace(/_/g, " ")}</option>)}
      </select>
      <em>Times, opening hours and automatic court rates use this local time zone.</em>
    </label>
  );
}

function ClaimCentrePage({ venue, navigate, accountProfile, saveAccountProfile }) {
  const storedCentre = accountProfile.centre || defaultAccountProfile.centre;
  const preferredLocation = locationSnapshot(storedCentre.location)
    || locationSnapshot(accountProfile.player?.location)
    || locationSnapshot(accountProfile.coach?.location)
    || locationSnapshot(accountProfile.group?.location)
    || getDefaultSearchLocation();
  const safeVenue = venue || createCentreDraftFromSearch({ location: preferredLocation });
  const matchedVenue = safeVenue && !safeVenue.isNewCentre ? safeVenue : null;
  const isEditingStoredCentre = Boolean(storedCentre.name || storedCentre.address || storedCentre.website || storedCentre.courtCount);
  const initialCentreForm = {
    ...defaultAccountProfile.centre,
    ...storedCentre,
    name: storedCentre.name || safeVenue.name || "",
    suburb: storedCentre.suburb || [safeVenue.suburb, safeVenue.city].filter(Boolean).join(", "),
    city: storedCentre.city || storedCentre.location?.city || safeVenue.city || "",
    location: storedCentre.location || locationFromVenue(safeVenue),
    address: storedCentre.address || safeVenue.address || "",
    contactEmail: storedCentre.contactEmail || accountProfile.email || "",
    timeZone: storedCentre.timeZone || safeVenue.timeZone || browserTimeZone(),
    courtPricing: normaliseCourtPricing(storedCentre.courtPricing, storedCentre.pricing || safeVenue.priceLabel || ""),
    photoLinks: storedCentre.photoLinks?.length ? storedCentre.photoLinks : [],
    socialLinks: storedCentre.socialLinks?.length ? storedCentre.socialLinks : [],
    currency: normaliseCurrencyCode(storedCentre.currency || marketplaceCurrencyForCountry(storedCentre.countryCode || storedCentre.location?.countryCode || safeVenue.countryCode || safeVenue.country))
  };
  const savedCentreProfileComplete = roleHasPublishedBackendProfile(accountProfile, "centre");
  const savedCentreCoreReady = getPublicProfileMissingFields(accountProfile, "centre").length === 0;
  const activeCentreWizardSteps = savedCentreProfileComplete ? centreProfileWizardSteps : centreFirstPublishWizardSteps;
  const {
    form,
    setForm,
    activeStepId,
    scrollToStep,
    clearDraft,
    draftRestored,
    restoredStepId,
    draftDirty
  } = useProfileSetupDraft("centre", accountProfile, initialCentreForm, activeCentreWizardSteps, {
    profileComplete: savedCentreProfileComplete,
    initialStepId: !savedCentreProfileComplete && savedCentreCoreReady ? "centre-step-identity" : ""
  });
  const [saveMessage, setSaveMessage] = useState("");
  const [savingCentre, setSavingCentre] = useState(false);
  const centreDraftProfile = {
    ...accountProfile,
    roles: Array.from(new Set([...(accountProfile.roles || []), "centre"])),
    centre: { ...storedCentre, ...form }
  };
  const centreCloudDraftStatus = useCloudProfileDraftAutosave({
    enabled: draftDirty,
    roleId: "centre",
    patch: {
      roles: centreDraftProfile.roles,
      centre: accountRoleDraftPatch(centreDraftProfile.centre)
    },
    saveAccountProfile
  });
  const centreMissingFields = getPublicProfileMissingFields(centreDraftProfile, "centre");
  const centreMissingSet = new Set(centreMissingFields);
  const centreIdentityComplete = ![
    "Centre name",
    "Suburb or city",
    "Street address"
  ].some((field) => centreMissingSet.has(field));
  const centreContactComplete = centreHasPublicContact(form);
  const centreCourtsComplete = centreHasCourtCount(form) && centreHasStructuredHours(form);
  const centreMediaComplete = Boolean(form.logoUrl || form.coverUrl || centrePublicMediaRows(form).length);
  const centreReadyToSave = centreMissingFields.length === 0;
  const centreCompletedStepIds = [
    centreIdentityComplete && "centre-step-identity",
    centreContactComplete && "centre-step-contact",
    centreCourtsComplete && "centre-step-courts",
    centreMediaComplete && "centre-step-media",
    centreReadyToSave && "centre-step-publish"
  ].filter(Boolean);
  const centreRequiredTotal = roleDashboardRequiredTotals.centre || 3;
  const centreProfileComplete = Math.max(0, Math.min(100, Math.round(((centreRequiredTotal - centreMissingFields.length) / centreRequiredTotal) * 100)));
  const centreFieldStepMap = {
    "Centre name": "centre-step-identity",
    "Suburb or city": "centre-step-identity",
    "Street address": "centre-step-identity",
    [`Centre description (${centreDescriptionMinimumCharacters}+ characters)`]: "centre-step-identity",
    "Centre logo": "centre-step-media",
    "Cover image": "centre-step-media",
    [`${centreMediaMinimumItems} venue photos or media items`]: "centre-step-media",
    "Phone or contact email": "centre-step-contact",
    "Court count": "centre-step-courts",
    "Weekly opening hours": "centre-step-courts"
  };
  const firstCentreMissingStep = centreFieldStepMap[centreMissingFields[0]] || "centre-step-identity";

  const updateCentreField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateCentrePricing = (courtPricing) => setForm((current) => ({
    ...current,
    courtPricing: normaliseCourtPricing(courtPricing, current.pricing),
    pricing: courtPricing.standard || current.pricing
  }));
  const updateCentreAdditionalMedia = (links) => setForm((current) => ({
    ...current,
    photoLinks: [
      ...cleanLinkRows(current.photoLinks, "Centre photo").filter((link) => isImageMediaUrl(link.url)),
      ...cleanLinkRows(links, "Centre media")
    ]
  }));
  const saveCentreClaim = async () => {
    setSaveMessage("");
    const nextLocation = locationSnapshot(form.location);
    const countryCode = normaliseCountryCode(form.countryCode || nextLocation?.countryCode || matchedVenue?.countryCode || matchedVenue?.country, form.city || matchedVenue?.city);
    const nextCentreBase = {
      ...form,
      city: form.city || matchedVenue?.city || "",
      suburb: form.suburb || matchedVenue?.suburb || "",
      countryCode,
      currency: normaliseCurrencyCode(form.currency || marketplaceCurrencyForCountry(countryCode)),
      location: nextLocation,
      paymentLink: "",
      photoLinks: markUserSubmittedMediaLinks(form.photoLinks, "Centre photo"),
      socialLinks: cleanLinkRows(form.socialLinks, "Centre social")
    };
    let nextCentre = nextCentreBase;
    let backendError = null;
    let profileBecamePublic = false;
    let backendAccountProfileSynced = false;
    let backendPublicationConfirmed = false;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingCentre(true);
      try {
        const backendCentre = await callMarketplaceFunction("upsertOwnedCentre", buildOwnedCentreBackendPayload(nextCentreBase, matchedVenue || safeVenue, { publishWhenReady: true }));
        profileBecamePublic = backendCentre.becamePublic === true;
        backendAccountProfileSynced = backendCentre.accountProfileSynced === true;
        backendPublicationConfirmed = backendCentre.public === true;
        nextCentre = {
          ...nextCentre,
          logoUrl: backendCentre.logoUrl || nextCentre.logoUrl,
          coverUrl: backendCentre.coverUrl || nextCentre.coverUrl,
          id: backendCentre.centreId || nextCentre.id,
          serverCentreId: backendCentre.centreId || nextCentre.serverCentreId,
          firestoreSourceId: backendCentre.centreId || nextCentre.firestoreSourceId,
          backendStatus: backendCentre.status || nextCentre.backendStatus,
          bookingEnabled: backendCentre.bookingEnabled === true,
          publicProfilePublished: backendCentre.public === true,
          publishMissing: backendCentre.publishMissing || [],
          bookingMissing: backendCentre.bookingMissing || []
        };
      } catch (error) {
        backendError = error;
        nextCentre = {
          ...nextCentre,
          backendStatus: "account_saved_public_publish_pending",
          backendErrorMessage: marketplaceFunctionErrorMessage(error)
        };
      } finally {
        setSavingCentre(false);
      }
    }
    const savedProfile = {
      ...accountProfile,
      roles: Array.from(new Set([...(accountProfile.roles || []), "centre"])),
      centre: nextCentre
    };
    const accountSave = await saveAccountProfile({
      roles: savedProfile.roles,
      centre: nextCentre
    });
    if (!accountSave?.saved && !backendAccountProfileSynced) {
      backendError = new Error(accountSave?.error || "Badminta could not save your centre profile. Try again.");
    }
    if (centreReadyToSave && !backendPublicationConfirmed && !backendError) {
      backendError = new Error("Your details were saved, but Badminta has not confirmed the public centre yet. Select Publish centre profile again.");
    }
    const savedMissing = nextCentre.publishMissing?.length
      ? nextCentre.publishMissing
      : getPublicProfileMissingFields(savedProfile, "centre");
    if (!savedMissing.length && !backendError) clearDraft();
    setSaveMessage(publicProfileSaveMessage({
      label: "Centre profile",
      backendError,
      missing: savedMissing
    }));
    if (!savedMissing.length && !backendError) {
      if (profileBecamePublic) {
        trackProfileCreated({
          profileId: nextCentre.firestoreSourceId || nextCentre.serverCentreId || nextCentre.id,
          role: "centre"
        });
      }
      completeOnboardingRole("centre");
      const publicPayload = publicProfileNavigationPayload("centre", nextCentre);
      continueAfterSave(navigate, "publicCentreProfile", publicPayload || {}, 250);
    }
  };
  const openLiveCentreProfile = () => {
	const liveCentre = { ...storedCentre, ...form };
	const publicPayload = publicProfileNavigationPayload("centre", liveCentre);
    navigate("publicCentreProfile", publicPayload || {});
  };

  return (
    <section className="page form-page role-profile-setup-page" data-profile-role="centre">
      <section className="profile-setup-intro">
        <div className="profile-avatar-frame large">
          {form.logoUrl ? <img src={form.logoUrl} alt="" /> : <Building2 size={42} />}
        </div>
        <div>
          <p>{isEditingStoredCentre ? "Edit centre profile" : "Centre profile setup"}</p>
          <h1>{isEditingStoredCentre ? `Keep ${form.name || "your centre"} accurate and bookable.` : "Publish your badminton centre."}</h1>
          <span>Start with the centre name and mapped address. Brand, media, contact details, courts and hours can be added after the free listing is live.</span>
        </div>
      </section>
      <div className="centre-flow-switch" aria-label="Centre setup options">
        <article className="active">
          <Building2 size={20} />
          <div>
            <strong>Add or edit my centre</strong>
            <span>Publish a free centre listing first, then add contact details, photos, courts, pricing and opening hours.</span>
          </div>
        </article>
        <button type="button" onClick={() => navigate("centreClaimSearch")}>
          <Search size={18} />
          Search and claim existing centre
        </button>
      </div>
      <div className="wizard-layout profile-setup-layout">
        <WizardStepRail steps={activeCentreWizardSteps} activeStepId={activeStepId} onStepClick={scrollToStep} label="Centre setup steps" profileComplete={savedCentreProfileComplete} completedStepIds={centreCompletedStepIds} />
        <section className="form-card wizard-form-card" data-wizard-role="centre">
          <WizardDraftStatus draftRestored={draftRestored} restoredStepId={restoredStepId} steps={activeCentreWizardSteps} profileComplete={savedCentreProfileComplete} cloudStatus={centreCloudDraftStatus} />
          <ProfilePublishReadiness
            label="centre profile"
            missingFields={centreMissingFields}
            fieldStepMap={centreFieldStepMap}
            publishStepId="centre-step-publish"
            onStepChange={scrollToStep}
            onPublish={saveCentreClaim}
            publishing={savingCentre}
            alreadyPublished={savedCentreProfileComplete}
            onViewLive={openLiveCentreProfile}
          />
          {saveMessage && (
            <div className={`auth-message profile-save-message ${profileSaveMessageTone(saveMessage)}`} role="status">
              <span>{saveMessage}</span>
              <div><button type="button" className="secondary" onClick={() => navigate("account")}>Back to account</button></div>
            </div>
          )}
          <section className="wizard-form-section" id="centre-step-identity" hidden={activeStepId !== "centre-step-identity"} tabIndex={-1}>
            <h2>Centre basics</h2>
            <div className="progress"><span style={{ width: `${centreProfileComplete}%` }} /></div>
            <p>{centreProfileComplete}% of required details complete</p>
            <label><span>Centre name <FieldRequirement /></span><input required value={form.name} onChange={(event) => updateCentreField("name", event.target.value)} placeholder="Centre name" /></label>
            <SuburbPicker
              label="Suburb or city"
              placeholder="Search suburb or city"
              value={form.suburb || form.city}
              selectedLocation={form.location}
              required
              onChange={({ suburb, city, location }) => {
                updateCentreField("suburb", suburb);
                updateCentreField("city", city);
                updateCentreField("location", location);
              }}
            />
            <CentreAddressPicker
              value={form.address}
              location={form.location}
              onChange={({ address, location }) => {
                setForm((current) => ({
                  ...current,
                  address,
                  location,
                  suburb: location?.suburb || current.suburb,
                  city: location?.city || current.city,
                  state: location?.state || current.state,
                  postcode: location?.postcode || current.postcode,
                  country: location?.country || current.country,
                  countryCode: location?.countryCode || current.countryCode
                }));
              }}
            />
            <label className="centre-description-field">
              <span>About the centre <FieldRequirement optional /></span>
              <textarea
                maxLength={3000}
                value={form.description}
                onChange={(event) => updateCentreField("description", event.target.value)}
                placeholder="Describe the courts, playing environment, facilities, programmes and the players your centre welcomes."
              />
              <em className={String(form.description || "").trim() ? "field-character-count complete" : "field-character-count"}>
                {String(form.description || "").trim().length}/3000 characters · add this now or enrich the free listing later
              </em>
            </label>
          </section>
          <section className="wizard-form-section" id="centre-step-contact" hidden={activeStepId !== "centre-step-contact"} tabIndex={-1}>
            <h2>Contact details</h2>
            <p>Add at least one public contact method so players can ask about the venue before travelling.</p>
            <div className="field-pair">
              <label><span>Contact email <FieldRequirement optional={Boolean(form.phone)} /></span><input value={form.contactEmail} onChange={(event) => updateCentreField("contactEmail", event.target.value)} type="email" placeholder="contact@yourcentre.com" /></label>
              <label><span>Phone <FieldRequirement optional={Boolean(form.contactEmail)} /></span><input value={form.phone} onChange={(event) => updateCentreField("phone", event.target.value)} inputMode="tel" placeholder="Include country code" /></label>
            </div>
            <label><span>Website <FieldRequirement optional /></span><input value={form.website} onChange={(event) => updateCentreField("website", event.target.value)} placeholder="https://..." /></label>
            <ManagedCheckoutNote title="Badminta manages court checkout.">
              Once ownership, courts, prices and opening hours are verified, Badminta creates checkout and tracks receipts, refunds and payouts. No payment link is needed.
            </ManagedCheckoutNote>
            <label><span>Business registration or tax ID <FieldRequirement optional /></span><input value={form.abn} onChange={(event) => updateCentreField("abn", event.target.value)} placeholder="Add during verification" /></label>
          </section>
          <section className="wizard-form-section" id="centre-step-courts" hidden={activeStepId !== "centre-step-courts"} tabIndex={-1}>
            <h2>Courts and opening hours</h2>
            <p>Add the venue capacity and real weekly opening hours when you are ready to offer detailed court information.</p>
            <div className="field-pair">
              <label><span>Court count <FieldRequirement /></span><input required min="1" max="48" value={form.courtCount} onChange={(event) => updateCentreField("courtCount", event.target.value)} inputMode="numeric" type="number" placeholder="8" /></label>
              <label><span>Default court fee <FieldRequirement optional /></span><input value={form.pricing} onChange={(event) => {
                updateCentreField("pricing", event.target.value);
                updateCentrePricing({ ...normaliseCourtPricing(form.courtPricing, form.pricing), standard: event.target.value });
              }} placeholder="25 per hour" /></label>
            </div>
            <CourtPricingEditor value={form.courtPricing} onChange={updateCentrePricing} title="Court fee schedule" currency={form.currency} />
            <CentreTimeZoneField value={form.timeZone} onChange={(timeZone) => updateCentreField("timeZone", timeZone)} />
            <WeeklyAvailabilityEditor
              title="Business hours"
              description="Select each day the centre is open. Court booking validation uses these local hours."
              schedule={form.openingSchedule}
              legacySummary={form.openingHours}
              onChange={(openingSchedule) => {
                updateCentreField("openingSchedule", openingSchedule);
                updateCentreField("openingHours", summarizeAvailabilitySchedule(openingSchedule));
              }}
            />
          </section>
          <section className="wizard-form-section" id="centre-step-media" hidden={activeStepId !== "centre-step-media"} tabIndex={-1}>
            <h2>Brand and venue media</h2>
            <p>Use the official centre identity and current venue media. The cover image leads the public page; the logo identifies the centre across search and maps.</p>
            <ImageUploadField
              label="Centre logo"
              value={form.logoUrl}
              onChange={(value) => updateCentreField("logoUrl", value)}
              mediaRole="centre"
              mediaField="logoUrl"
              emptyLabel="No centre logo yet"
              helper="Upload the official square logo with no extra whitespace. It appears beside the centre name and on centre-owned map results."
            />
            <ImageUploadField
              label="Cover or banner image"
              value={form.coverUrl}
              onChange={(value) => updateCentreField("coverUrl", value)}
              mediaRole="centre"
              mediaField="coverUrl"
              emptyLabel="No centre cover image yet"
              helper="Use a wide, high-quality photo that clearly shows the courts or the venue. This is the main image on the public centre page."
              variant="banner"
            />
            <CentreMediaUploadField
              links={form.photoLinks}
              onChange={(links) => updateCentreField("photoLinks", links)}
            />
            <LinkListEditor
              title="Walkthrough videos or official media links"
              text="Optional: add videos, reels or externally hosted images that the centre owns or has permission to publish."
              type="media"
              defaultMediaPlatform="YouTube"
              addLabel="Add video or media link"
              links={cleanLinkRows(form.photoLinks, "Centre media").filter((link) => !isImageMediaUrl(link.url))}
              onChange={updateCentreAdditionalMedia}
            />
            <LinkListEditor
              title="Centre social links"
              text="Enter official social usernames, or paste the centre website, booking page or Google profile link."
              links={form.socialLinks}
              onChange={(links) => updateCentreField("socialLinks", links)}
            />
          </section>
          <section className="wizard-form-section" id="centre-step-publish" hidden={activeStepId !== "centre-step-publish"} tabIndex={-1}>
            <h2>{isEditingStoredCentre ? "Publish centre updates" : "Publish centre profile"}</h2>
            <div className="event-checkout-note">
              <ShieldCheck size={20} />
              <span>Your free centre listing publishes as soon as its name and mapped address are saved. Centre Pro is only required for bookings, advanced pricing, memberships, events and payment operations.</span>
            </div>
            <p className="wizard-publish-copy">
              {centreReadyToSave
                ? "Use the final action below to save these details and open the public centre profile."
                : `Complete ${missingFieldSummary(centreMissingFields)} before publishing. Your draft remains saved while you finish.`}
            </p>
          </section>
          <WizardNavigation
            steps={activeCentreWizardSteps}
            activeStepId={activeStepId}
            onStepChange={scrollToStep}
            readyToSave={centreReadyToSave}
            incompleteLabel="Saves as a private draft"
            fastTrackToStepId="centre-step-publish"
            onFinish={centreReadyToSave ? saveCentreClaim : () => scrollToStep(firstCentreMissingStep)}
            finishLabel={savedCentreProfileComplete ? "Save centre" : "Publish centre"}
            finishing={savingCentre}
          />
        </section>
        <aside className="side-panel">
          <h2>Centre preview</h2>
          <div className="centre-preview-cover">
            {form.coverUrl ? <img src={form.coverUrl} alt="" /> : <span><ImageIcon size={24} /> Cover image preview</span>}
          </div>
          <div className="profile-asset-preview">
            {form.logoUrl ? <img src={form.logoUrl} alt="" /> : <Building2 size={34} />}
            <strong>{form.name || safeVenue.name || "Centre profile"}</strong>
            <span>{[form.address, form.suburb].filter(Boolean).join(" · ") || "Name and full location appear here."}</span>
          </div>
          {savedCentreProfileComplete ? (
            <>
              <PublicLinkList links={form.socialLinks} emptyText="Centre social links will appear here." />
              <h2>Centre media preview</h2>
              <MediaEmbedGrid
                links={form.photoLinks}
                emptyTitle="No centre media links yet"
                emptyText="Official centre photos, YouTube videos, reels or social clips preview here."
                limit={3}
              />
              <h2>Booking readiness</h2>
              {[
                ["Verify details", "Badminta checks the centre name, address and owner/manager details."],
                ["Add courts", "Set court count, court types, opening hours and booking rules."],
                ["Set pricing", "Add casual hire, peak pricing, coaching and group session prices."],
                ["Connect payouts", "Add payout details before accepting paid court bookings."]
              ].map(([title, text], index) => (
                <div key={title}><span>{index + 1}</span><strong>{title}</strong><em>{text}</em></div>
              ))}
            </>
          ) : (
            <FirstPublishPreview
              icon={Building2}
              title="A complete venue page earns player trust"
              items={[
                { label: "Centre name", complete: !centreMissingSet.has("Centre name") },
                { label: "Exact location", complete: !centreMissingSet.has("Suburb or city") && !centreMissingSet.has("Street address") },
                { label: "Centre description", complete: Boolean(String(form.description || "").trim()) },
                { label: "Logo and cover", complete: Boolean(form.logoUrl && form.coverUrl) },
                { label: `${centreMediaMinimumItems} venue media items`, complete: centrePublicMediaRows(form).length >= centreMediaMinimumItems },
                { label: "Public contact", complete: centreHasPublicContact(form) },
                { label: "Courts and hours", complete: centreHasCourtCount(form) && centreHasStructuredHours(form) }
              ]}
              text="Only the centre name and mapped address are needed for the free public listing. Optional details can be added after publication; Centre Pro unlocks bookings and venue operations."
            />
          )}
        </aside>
      </div>
    </section>
  );
}

const claimVenueAcronymTokens = (venue = {}) => new Set(
  (String(venue.name || "").match(/\b[A-Z]{3,8}\b/g) || [])
    .map((token) => token.toLowerCase())
    .filter((token) => !["vic", "nsw", "qld", "sa", "wa", "nt", "act", "tas", "australia"].includes(token))
);

const claimVenueAddressTokens = (venue = {}) => new Set(
  compactLocationText(venue.address)
    .split(/\s+/)
    .filter((token) => token.length >= 5)
    .filter((token) => !["street", "drive", "road", "avenue", "centre", "center", "sports", "australia", "melbourne"].includes(token))
);

const isHostedVenueProgramName = (venue = {}) => /@|\b(association|academy|social|group|club)\b/i.test(String(venue.name || ""));

const isLikelyClaimDuplicateVenue = (first = {}, second = {}) => {
  if (isSameVenueEntity(first, second)) return true;
  if (isHostedVenueProgramName(first) !== isHostedVenueProgramName(second)) return false;
  const firstAcronyms = claimVenueAcronymTokens(first);
  const sharedAcronym = [...claimVenueAcronymTokens(second)].some((token) => firstAcronyms.has(token));
  if (!sharedAcronym) return false;
  const firstAddressTokens = claimVenueAddressTokens(first);
  const sharedAddressToken = [...claimVenueAddressTokens(second)].some((token) => firstAddressTokens.has(token));
  const firstPoint = getVenueExactPoint(first);
  const secondPoint = getVenueExactPoint(second);
  const nearbyPoint = Boolean(firstPoint && secondPoint && (distanceKm(firstPoint, secondPoint) ?? Infinity) <= 0.35);
  const samePostcode = Boolean(first.postcode && second.postcode && compactLocationText(first.postcode) === compactLocationText(second.postcode));
  return sharedAddressToken || nearbyPoint || samePostcode;
};

const collapseClaimVenueMatches = (rows = []) => {
  const groups = [];
  rows.forEach((row) => {
    const group = groups.find((candidateGroup) => candidateGroup.some((candidate) => isLikelyClaimDuplicateVenue(candidate.venue, row.venue)));
    if (group) group.push(row);
    else groups.push([row]);
  });
  return groups
    .map((group) => {
      const sorted = [...group].sort((first, second) => venueMergePriorityScore(second.venue) - venueMergePriorityScore(first.venue)
        || second.score - first.score
        || first.venue.name.localeCompare(second.venue.name));
      const primary = sorted[0];
      const similarListings = sorted.slice(1).map(({ venue }) => ({
        id: venue.id,
        name: venue.name || "Similar centre listing",
        address: getVenueAddressLabel(venue)
      }));
      return {
        ...primary.venue,
        claimExactName: group.some((row) => row.exactName),
        claimMatchScore: Math.max(...group.map((row) => row.score)),
        claimSimilarListings: similarListings
      };
    })
    .sort((first, second) => second.claimMatchScore - first.claimMatchScore
      || venueMergePriorityScore(second) - venueMergePriorityScore(first)
      || first.name.localeCompare(second.name));
};

function CentreClaimSearchPage({ selectedVenue, venueDirectory, navigate, accountProfile, saveAccountProfile }) {
  const selectedListing = selectedVenue && !selectedVenue.isNewCentre ? selectedVenue : null;
  const [claimQuery, setClaimQuery] = useState(selectedListing?.name || accountProfile.centre?.name || "");
  const [selectedClaimVenue, setSelectedClaimVenue] = useState(selectedListing || null);
  const [claimingVenueId, setClaimingVenueId] = useState("");
  const [claimMessage, setClaimMessage] = useState("");
  const [claimUploadStatus, setClaimUploadStatus] = useState("");
  const [claimFile, setClaimFile] = useState(null);
  const claimProofRef = useRef(null);
  const [claimEvidence, setClaimEvidence] = useState({
    ownerRole: accountProfile.centre?.ownerRole || "Owner or manager",
    ownerEmail: accountProfile.centre?.contactEmail || accountProfile.email || "",
    evidenceUrl: "",
    note: "",
    attachment: accountProfile.centre?.claimEvidenceAttachment || null
  });
  const claimedVenueId = accountProfile.centre?.claimedVenueId || "";
  const claimStatus = accountProfile.centre?.claimStatus || "";
  const updateClaimEvidence = (field, value) => setClaimEvidence((current) => ({ ...current, [field]: value }));
  useEffect(() => {
    if (selectedListing?.id) setSelectedClaimVenue(selectedListing);
  }, [selectedListing?.id]);
  const queryTerms = claimQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const claimCandidates = useMemo(() => {
    if (!queryTerms.length) return selectedClaimVenue?.id ? [selectedClaimVenue] : [];
    const genericTerms = new Set(["badminton", "centre", "center", "club", "court", "courts", "sports", "sport"]);
    const meaningfulTerms = queryTerms.filter((term) => !genericTerms.has(term));
    const matchTerms = meaningfulTerms.length ? meaningfulTerms : queryTerms;
    const rows = uniqueVenueList(venueDirectory)
      .filter((venue) => venue?.id && !venue.isNewCentre)
      .map((venue) => {
        const haystack = filterSearchText(venue.name, venue.address, venue.suburb, venue.city, venue.state, venue.postcode, venue.website, venue.phone);
        const termMatches = matchTerms.filter((term) => haystack.includes(term)).length;
        const exactName = compactLocationText(venue.name) === compactLocationText(claimQuery);
        const allTermsMatch = matchTerms.every((term) => haystack.includes(term));
        const score = (exactName ? 1000 : 0) + (allTermsMatch ? 100 : 0) + termMatches * 12 + (hasVenueContact(venue) ? 4 : 0) + (getVenueExactPoint(venue) ? 3 : 0) + (venue.address ? 2 : 0);
        return { venue, score, exactName, allTermsMatch };
      })
      .filter(({ allTermsMatch }) => allTermsMatch)
      .sort((a, b) => b.score - a.score || getVenueLocationQuality(a.venue) - getVenueLocationQuality(b.venue) || a.venue.name.localeCompare(b.venue.name))
    const collapsedRows = collapseClaimVenueMatches(rows);
    const exactRows = collapsedRows.filter((venue) => venue.claimExactName);
    return (exactRows.length ? exactRows : collapsedRows).slice(0, 18);
  }, [venueDirectory, claimQuery, selectedClaimVenue]);
  const claimCandidateRecordCount = claimCandidates.reduce((total, venue) => total + 1 + (venue.claimSimilarListings?.length || 0), 0);

  const selectClaimVenue = (venue) => {
    setSelectedClaimVenue(venue);
    setClaimMessage("");
    if (!claimQuery.trim()) setClaimQuery(venue.name || "");
    if (typeof window !== "undefined" && window.innerWidth <= 900) {
      window.setTimeout(() => claimProofRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }
  };

  const chooseClaimFile = (file) => {
    const error = claimEvidenceFileError(file);
    if (error) {
      setClaimFile(null);
      updateClaimEvidence("attachment", null);
      setClaimMessage(error);
      return;
    }
    setClaimFile(file);
    updateClaimEvidence("attachment", claimEvidenceAttachmentFromFile(file));
    setClaimMessage("");
  };

  const clearClaimFile = () => {
    setClaimFile(null);
    updateClaimEvidence("attachment", null);
    setClaimUploadStatus("");
  };

  const submitClaim = async (venue) => {
    if (!venue?.id) {
      setClaimMessage("Select the centre you want to claim first.");
      return;
    }
    setClaimingVenueId(venue.id);
    setClaimMessage("");
    setClaimUploadStatus("");
    let backendClaim = null;
    let uploadedAttachment = claimEvidence.attachment || null;
    try {
      if (claimFile && firebaseAuth?.currentUser) {
        setClaimUploadStatus("Uploading proof file...");
        uploadedAttachment = await uploadClaimEvidenceFile({ file: claimFile, user: firebaseAuth.currentUser, venue });
        updateClaimEvidence("attachment", uploadedAttachment);
      }
    } catch (error) {
      setClaimMessage(error.message || "The proof file could not be uploaded. Try a PDF, image, DOC or DOCX under 20 MB.");
      setClaimingVenueId("");
      setClaimUploadStatus("");
      return;
    }
    const attachmentLabel = uploadedAttachment?.name
      ? `${uploadedAttachment.name}${uploadedAttachment.sizeLabel ? ` (${uploadedAttachment.sizeLabel})` : ""}`
      : "";
    const evidenceSummary = [
      claimEvidence.ownerRole ? `Role: ${claimEvidence.ownerRole}` : "",
      claimEvidence.ownerEmail ? `Official email: ${claimEvidence.ownerEmail}` : "",
      claimEvidence.evidenceUrl ? `Evidence URL: ${claimEvidence.evidenceUrl}` : "",
      attachmentLabel ? `Attached proof: ${attachmentLabel}` : "",
      uploadedAttachment?.storagePath ? `Uploaded evidence: ${uploadedAttachment.storagePath}` : "",
      claimEvidence.note ? `Claim note: ${claimEvidence.note}` : ""
    ].filter(Boolean);
    try {
      backendClaim = await callMarketplaceFunction("submitCentreClaim", {
        centreId: venue.id,
        centreName: venue.name,
        relatedCentreIds: (venue.claimSimilarListings || []).map((listing) => listing.id),
        countryCode: normaliseCountryCode(venue.countryCode || venue.country, venue.city),
        ownerEmail: claimEvidence.ownerEmail,
        evidence: evidenceSummary,
        evidenceRecords: [{
          evidenceType: "claim",
          title: "Centre ownership or management evidence",
          note: [
            claimEvidence.ownerRole ? `Role: ${claimEvidence.ownerRole}` : "",
            claimEvidence.ownerEmail ? `Official email/domain: ${claimEvidence.ownerEmail}` : "",
            attachmentLabel ? `Attached proof: ${attachmentLabel}` : "",
            claimEvidence.note
          ].filter(Boolean).join("\n"),
          sourceUrl: claimEvidence.evidenceUrl,
          storagePath: uploadedAttachment?.storagePath || ""
        }]
      });
      setClaimMessage(backendClaim?.alreadyOwned
        ? "This centre is already connected to your account. You can continue managing its public profile and courts."
        : `Claim ${backendClaim?.resubmitted ? "updated" : "submitted"} for Badminta review${backendClaim?.evidenceCount ? ` with ${backendClaim.evidenceCount} evidence item${backendClaim.evidenceCount === 1 ? "" : "s"}` : ""}.`);
    } catch (error) {
      setClaimMessage(marketplaceFunctionErrorMessage(error));
      setClaimingVenueId("");
      setClaimUploadStatus("");
      return;
    }
    const nextCentre = {
      ...defaultAccountProfile.centre,
      ...(accountProfile.centre || {}),
      claimedVenueId: venue.id,
      claimedVenueSlug: entitySlug(venue),
      claimId: backendClaim?.claimId || accountProfile.centre?.claimId || "",
      claimStatus: backendClaim?.status === "approved" ? "Verified" : "Pending verification",
      claimSubmittedAt: new Date().toISOString(),
      claimSource: getVenueSourceLabel(venue),
      claimEvidence: evidenceSummary,
      claimEvidenceRecords: backendClaim?.evidenceCount || 0,
      ownerRole: claimEvidence.ownerRole,
      claimEvidenceAttachment: uploadedAttachment || null,
      claimEvidenceStoragePath: uploadedAttachment?.storagePath || "",
      claimEvidenceUrl: claimEvidence.evidenceUrl || "",
      claimNote: claimEvidence.note || "",
      name: accountProfile.centre?.name || venue.name || "",
      suburb: accountProfile.centre?.suburb || [venue.suburb, venue.city].filter(Boolean).join(", "),
      city: accountProfile.centre?.city || venue.city || "",
      location: accountProfile.centre?.location || locationFromVenue(venue),
      address: accountProfile.centre?.address || venue.address || "",
      phone: accountProfile.centre?.phone || venue.phone || "",
      contactEmail: accountProfile.centre?.contactEmail || accountProfile.email || venue.email || "",
      website: accountProfile.centre?.website || venue.website || "",
      logoUrl: accountProfile.centre?.logoUrl || getVenueImages(venue)[0] || ""
    };
    saveAccountProfile({
      roles: Array.from(new Set([...(accountProfile.roles || []), "centre"])),
      centre: nextCentre
    });
    setClaimingVenueId("");
    setClaimUploadStatus("");
  };

  return (
    <section className="page centre-claim-search-page">
      <div className="screen-head">
        <p>Search and claim existing centre</p>
        <h1>Claim a centre that is already listed on Badminta.</h1>
        <button type="button" onClick={() => navigate("claimCentre")}>Centre not listed? Add it instead</button>
      </div>
      <section className="claim-search-panel" aria-label="Find the centre to claim">
        <div className="claim-step-heading">
          <span>Step 1</span>
          <div>
            <h2>Find the centre</h2>
            <p>Search the directory first so Badminta can connect your ownership claim to the right public listing.</p>
          </div>
        </div>
        <label className="claim-search-box">
          <span>Centre name, suburb, address or postcode</span>
          <div>
            <Search size={18} />
            <input value={claimQuery} onChange={(event) => setClaimQuery(event.target.value)} placeholder="Example: Altona Badminton Centre" />
            {claimQuery && <button type="button" onClick={() => setClaimQuery("")} aria-label="Clear centre search"><X size={16} /></button>}
          </div>
        </label>
        {claimStatus && (
          <div className="event-checkout-note">
            <ShieldCheck size={20} />
            <span>Current claim status: {claimStatus}{claimedVenueId ? " for your selected centre." : "."}</span>
          </div>
        )}
      </section>
      <div className="claim-flow-layout">
        <section className="claim-results-section" aria-label="Centre search results">
          <div className="claim-step-heading compact">
            <span>Step 2</span>
            <div>
              <h2>Select the correct listing</h2>
              <p>{claimCandidates.length
                ? claimCandidateRecordCount > claimCandidates.length
                  ? `${claimCandidateRecordCount} directory records grouped into ${claimCandidates.length} likely centre match${claimCandidates.length === 1 ? "" : "es"}.`
                  : `${claimCandidates.length} possible match${claimCandidates.length === 1 ? "" : "es"} found.`
                : "No matching centre found yet."}</p>
            </div>
          </div>
          {claimCandidates.length ? (
            <div className="claim-result-grid">
              {claimCandidates.map((venue) => {
                const isClaimed = claimedVenueId === venue.id;
                const isSelected = selectedClaimVenue?.id === venue.id;
                return (
                  <article className={`claim-result-card${isSelected ? " selected" : ""}`} key={venue.id}>
                    <VenueMedia venue={venue} className="claim-result-media" mapFallback="any" />
                    <div>
                      <span>{isClaimed ? "Current claim" : getVenueTopline(venue)}</span>
                      <strong>{venue.name}</strong>
                      <p>{getVenueAddressLabel(venue)}</p>
                      <div className="claim-result-meta">
                        <em><RatingStatus venue={venue} /></em>
                        <em>{hasVenuePrice(venue) ? <PriceStatus venue={venue} compact /> : "Price not listed"}</em>
                      </div>
                      {venue.claimSimilarListings?.length > 0 && (
                        <div className="claim-canonical-note">
                          <BadgeCheck size={16} />
                          <span>Recommended listing. {venue.claimSimilarListings.length} likely duplicate record{venue.claimSimilarListings.length === 1 ? " was" : "s were"} grouped here.</span>
                        </div>
                      )}
                      <div className="claim-result-actions">
                        <a href={routeHref("venue", { venue })} onClick={(event) => handleSeoRouteClick(event, navigate, "venue", { venue })}><ExternalLink size={15} /> View listing</a>
                        <button type="button" onClick={() => selectClaimVenue(venue)}>{isSelected ? <><Check size={16} /> Selected</> : "Select centre"}</button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <CommunityEmptyState
              type="centre claims"
              icon={Building2}
              title={claimQuery.trim() ? "No matching centre found" : "Search for the centre you manage"}
              text={claimQuery.trim() ? "Try the exact centre name, suburb, street or postcode. If it is not listed, add a new owner-managed centre profile." : "Enter a centre name, suburb, street or postcode above to find the correct public listing."}
              primary={claimQuery.trim() ? { label: "Add a new centre", target: "claimCentre" } : null}
              secondary={claimQuery.trim() ? { label: "Browse centres", target: "results" } : null}
              navigate={navigate}
            />
          )}
        </section>
        <aside className="claim-proof-card claim-evidence-panel" ref={claimProofRef} aria-label="Submit centre claim proof">
          <div className="claim-step-heading compact">
            <span>Step 3</span>
            <div>
              <h2>Attach proof and submit</h2>
              <p>Badminta reviews the proof before verified status, payouts or in-platform bookings are enabled.</p>
            </div>
          </div>
          {selectedClaimVenue ? (
            <>
              <div className="claim-selected-listing">
                <VenueMedia venue={selectedClaimVenue} className="claim-selected-media" mapFallback="any" />
                <div>
                  <span>Selected listing</span>
                  <strong>{selectedClaimVenue.name}</strong>
                  <p>{getVenueAddressLabel(selectedClaimVenue)}</p>
                  <button type="button" onClick={() => setSelectedClaimVenue(null)}>Change selection</button>
                </div>
              </div>
              <div className="claim-evidence-grid">
                <label>
                  <span>Your role</span>
                  <select value={claimEvidence.ownerRole} onChange={(event) => updateClaimEvidence("ownerRole", event.target.value)}>
                    <option>Owner or manager</option>
                    <option>Centre staff</option>
                    <option>Club committee member</option>
                    <option>Booking administrator</option>
                  </select>
                </label>
                <label>
                  <span>Official email</span>
                  <input value={claimEvidence.ownerEmail} onChange={(event) => updateClaimEvidence("ownerEmail", event.target.value)} placeholder="manager@centre.com.au" />
                </label>
                <label className="wide">
                  <span>Official website, booking page or social proof</span>
                  <input value={claimEvidence.evidenceUrl} onChange={(event) => updateClaimEvidence("evidenceUrl", event.target.value)} placeholder="https://official-page-or-document" />
                </label>
                <div className="claim-file-field wide">
                  <span>Proof file</span>
                  <label className="claim-file-drop">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/webp,image/heic,image/heif"
                      onChange={(event) => chooseClaimFile(event.target.files?.[0] || null)}
                    />
                    <span><ClipboardCheck size={22} /></span>
                    <strong>{claimEvidence.attachment?.name || "Attach a PDF, image or document"}</strong>
                    <em>{claimEvidence.attachment?.sizeLabel || "Accepted: PDF, JPG, PNG, WebP, HEIC, DOC or DOCX under 20 MB."}</em>
                  </label>
                  {claimEvidence.attachment && (
                    <button className="claim-file-clear" type="button" onClick={clearClaimFile}>
                      <X size={14} /> Remove attached file
                    </button>
                  )}
                </div>
                <label className="wide">
                  <span>Short note for Badminta review</span>
                  <textarea value={claimEvidence.note} onChange={(event) => updateClaimEvidence("note", event.target.value)} placeholder="Example: I manage bookings for this centre. The attached bill shows the centre name and official address." />
                </label>
              </div>
              {claimUploadStatus && <div className="auth-message stripe-connect-message">{claimUploadStatus}</div>}
              {claimMessage && <div className="auth-message stripe-connect-message">{claimMessage}</div>}
              <button className="claim-submit-button" type="button" onClick={() => submitClaim(selectedClaimVenue)} disabled={claimingVenueId === selectedClaimVenue.id}>
                {claimingVenueId === selectedClaimVenue.id ? "Submitting claim..." : claimedVenueId === selectedClaimVenue.id ? "Update claim" : "Submit centre claim"}
              </button>
            </>
          ) : (
            <div className="claim-waiting-card">
              <ShieldCheck size={28} />
              <strong>Select a centre to continue</strong>
              <span>Your proof form will appear here after you choose the listing you own or manage.</span>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function CourtManager({ venue, navigate, accountProfile = defaultAccountProfile, saveAccountProfile }) {
  const centre = accountProfile.centre || defaultAccountProfile.centre;
  const safeVenue = venue || {};
  const managedVenue = managedVenueFromCentreProfile(centre, safeVenue);
  const initialCourtCount = centreCourtCountNumber(centre, safeVenue);
  const [saved, setSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [savingCourts, setSavingCourts] = useState(false);
  const [form, setForm] = useState(() => ({
    courtCount: initialCourtCount ? String(initialCourtCount) : "",
    pricing: centre.pricing || managedVenue.priceLabel || "",
    courtPricing: normaliseCourtPricing(centre.courtPricing, centre.pricing || managedVenue.priceLabel || ""),
    timeZone: centre.timeZone || managedVenue.timeZone || browserTimeZone(),
    openingSchedule: normaliseAvailabilitySchedule(centre.openingSchedule),
    openingHours: centre.openingHours || managedVenue.openingHours || "",
    bookingRules: centre.bookingRules || "",
    courtInventory: normaliseCourtInventory(centre, safeVenue)
  }));
  const updateField = (field, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, [field]: value }));
  };
  const updateCourtCount = (value) => {
    const numeric = Math.max(0, Math.min(48, Number.parseInt(value, 10) || 0));
    setSaved(false);
    setForm((current) => {
      const nextRows = Array.from({ length: numeric }, (_, index) => {
        const savedRow = current.courtInventory[index] || {};
        return {
          id: savedRow.id || `court-${index + 1}`,
          name: savedRow.name || `Court ${index + 1}`,
          type: savedRow.type || "Badminton",
          status: savedRow.status || "Published",
          price: savedRow.price || current.courtPricing?.standard || current.pricing || "",
          notes: savedRow.notes || ""
        };
      });
      return { ...current, courtCount: value, courtInventory: nextRows };
    });
  };
  const updateCourtRow = (index, patch) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      courtInventory: current.courtInventory.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row)
    }));
  };
  const buildNextCentre = () => {
    const openingSchedule = normaliseAvailabilitySchedule(form.openingSchedule);
    const openingHours = summarizeAvailabilitySchedule(openingSchedule) || form.openingHours;
    const courtCount = String(Number.parseInt(form.courtCount, 10) || form.courtInventory.length || "");
    return {
      ...centre,
      name: centre.name || safeVenue.name || "",
      suburb: centre.suburb || safeVenue.suburb || "",
      address: centre.address || getVenueAddressLabel(safeVenue),
      countryCode: normaliseCountryCode(centre.countryCode || centre.location?.countryCode || safeVenue.countryCode || safeVenue.country, centre.city || safeVenue.city),
      currency: normaliseCurrencyCode(centre.currency || marketplaceCurrencyForCountry(centre.countryCode || centre.location?.countryCode || safeVenue.countryCode || safeVenue.country)),
      courtCount,
      pricing: form.pricing || form.courtPricing.standard || "",
      paymentLink: "",
      courtPricing: normaliseCourtPricing(form.courtPricing, form.pricing),
      timeZone: form.timeZone || browserTimeZone(),
      openingSchedule,
      openingHours,
      bookingRules: form.bookingRules,
      courtInventory: form.courtInventory.map((row, index) => ({
        id: row.id || `court-${index + 1}`,
        name: row.name || `Court ${index + 1}`,
        type: row.type || "Badminton",
        status: row.status || "Published",
        price: row.price || form.courtPricing.standard || form.pricing,
        notes: row.notes || ""
      }))
    };
  };
  const saveCourtSetup = async ({ publishWhenReady = true } = {}) => {
    setSaveMessage("");
    const nextCentre = buildNextCentre();
    let savedCentre = nextCentre;
    let backendError = null;
    let profileBecamePublic = false;
    if (firebaseFunctions && firebaseAuth?.currentUser) {
      setSavingCourts(true);
      try {
        const backendCentre = await callMarketplaceFunction("upsertOwnedCentre", buildOwnedCentreBackendPayload(nextCentre, managedVenue, { publishWhenReady }));
        profileBecamePublic = backendCentre.becamePublic === true;
        savedCentre = {
          ...savedCentre,
          logoUrl: backendCentre.logoUrl || savedCentre.logoUrl,
          id: backendCentre.centreId || savedCentre.id,
          serverCentreId: backendCentre.centreId || savedCentre.serverCentreId,
          firestoreSourceId: backendCentre.centreId || savedCentre.firestoreSourceId,
          backendStatus: backendCentre.status || savedCentre.backendStatus,
          bookingEnabled: backendCentre.bookingEnabled || false,
          publishMissing: backendCentre.publishMissing || [],
          bookingMissing: backendCentre.bookingMissing || []
        };
      } catch (error) {
        backendError = error;
        savedCentre = {
          ...savedCentre,
          backendStatus: "account_saved_public_publish_pending",
          backendErrorMessage: marketplaceFunctionErrorMessage(error)
        };
      } finally {
        setSavingCourts(false);
      }
    }
    const savedProfile = {
      ...accountProfile,
      roles: Array.from(new Set([...(accountProfile.roles || []), "centre"])),
      centre: savedCentre
    };
    const accountSave = await saveAccountProfile?.({
      roles: savedProfile.roles,
      centre: savedCentre
    });
    if (accountSave && !accountSave.saved) {
      backendError = new Error(accountSave.error || "Badminta could not save this court setup. Try again.");
    }
    setSaved(true);
    setSaveMessage(publicProfileSaveMessage({
      label: "Court inventory",
      backendError,
      missing: savedCentre.publishMissing?.length ? savedCentre.publishMissing : getPublicProfileMissingFields(savedProfile, "centre")
    }));
    if (!backendError && profileBecamePublic) {
      trackProfileCreated({
        profileId: savedCentre.firestoreSourceId || savedCentre.serverCentreId || savedCentre.id,
        role: "centre"
      });
    }
    return backendError ? null : savedCentre;
  };
  const previewBooking = async () => {
    const nextCentre = await saveCourtSetup({ publishWhenReady: true });
    if (!nextCentre) return;
    const nextVenue = managedVenueFromCentreProfile(nextCentre, managedVenue);
    const bookingState = getPlatformCentreBookingState(nextVenue, { ...accountProfile, centre: nextCentre });
    if (!bookingState.ready) {
      setSaveMessage(`Complete ${bookingState.readiness.missing.join(", ")} before opening court checkout.`);
      return;
    }
    navigate("booking", { venue: nextVenue, bookingType: "court" });
  };
  const previewCentre = buildNextCentre();
  const previewVenue = managedVenueFromCentreProfile(previewCentre, managedVenue);
  const previewBookingState = getPlatformCentreBookingState(previewVenue, { ...accountProfile, centre: previewCentre });
  const previewBookingReady = previewBookingState.ready && (hasBookableCourtPrice(previewBookingState.venue) || hasCourtPricing(previewBookingState.venue.courtPricing));
  const previewMissing = previewBookingState.readiness.missing || [];
  const publishedCourtCount = form.courtInventory.filter((court) => court.status === "Published").length;
  const bookingSetupRows = [
    ["Owner verified", previewBookingState.verifiedOwner],
    ["Backend source", Boolean(previewBookingState.backendSourceId)],
    ["Published courts", publishedCourtCount > 0],
    ["Fee schedule", hasCourtPricing(form.courtPricing) || Boolean(form.pricing)],
    ["Centre time zone", Boolean(form.timeZone)],
    ["Structured hours", normaliseAvailabilitySchedule(form.openingSchedule).some((row) => row.enabled)],
    ["Booking rules", Boolean(form.bookingRules)]
  ];
  return (
    <section className="page court-manager-page">
      <div className="screen-head">
        <p>Court inventory</p>
        <h1>Manage court inventory for {previewVenue.name}.</h1>
        <button type="button" onClick={previewBooking} disabled={savingCourts}>{savingCourts ? "Saving..." : "Save and preview booking"}</button>
      </div>
      <div className="court-manager-layout">
        <section className="form-card court-manager-form">
          <h2>Booking settings</h2>
          <div className="field-pair">
            <label>
              <span>Court count</span>
              <input value={form.courtCount} onChange={(event) => updateCourtCount(event.target.value)} inputMode="numeric" placeholder="8" />
            </label>
            <label>
              <span>Default court price</span>
              <input value={form.pricing} onChange={(event) => {
                const nextPrice = event.target.value;
                updateField("pricing", nextPrice);
                updateField("courtPricing", { ...normaliseCourtPricing(form.courtPricing, form.pricing), standard: nextPrice });
              }} placeholder="25 per hour" />
            </label>
          </div>
          <CourtPricingEditor
            value={form.courtPricing}
            onChange={(courtPricing) => {
              updateField("courtPricing", normaliseCourtPricing(courtPricing, form.pricing));
              if (courtPricing.standard) updateField("pricing", courtPricing.standard);
            }}
            title="Peak, off-peak and weekend fees"
            currency={previewVenue.currency}
          />
          <CentreTimeZoneField value={form.timeZone} onChange={(timeZone) => updateField("timeZone", timeZone)} />
          <ManagedCheckoutNote title="Badminta manages every court payment.">
            Booking holds, checkout, receipts, refunds and centre payouts stay connected to the court and booking record. No hosted payment link is required.
          </ManagedCheckoutNote>
          <WeeklyAvailabilityEditor
            schedule={form.openingSchedule}
            legacySummary={form.openingHours}
            onChange={(openingSchedule) => updateField("openingSchedule", openingSchedule)}
          />
          <label>
            <span>Booking rules</span>
            <textarea value={form.bookingRules} onChange={(event) => updateField("bookingRules", event.target.value)} placeholder="Add cancellation windows, equipment rules, check-in instructions, peak pricing notes, and refund rules." />
          </label>
          <div className="court-inventory-editor">
            <div className="form-section-head">
              <h3>Courts</h3>
              <span>{form.courtInventory.length ? `${publishedCourtCount} of ${form.courtInventory.length} courts published for booking.` : "Add a court count to create inventory rows."}</span>
            </div>
            {form.courtInventory.length ? form.courtInventory.map((court, index) => (
              <article className="court-row-editor" key={court.id || index}>
                <label>
                  <span>Name</span>
                  <input value={court.name} onChange={(event) => updateCourtRow(index, { name: event.target.value })} />
                </label>
                <label>
                  <span>Type</span>
                  <select value={court.type} onChange={(event) => updateCourtRow(index, { type: event.target.value })}>
                    {courtTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
                <label>
                  <span>Status</span>
                  <select value={court.status} onChange={(event) => updateCourtRow(index, { status: event.target.value })}>
                    {courtStatusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
                <label>
                  <span>Price</span>
                  <input value={court.price} onChange={(event) => updateCourtRow(index, { price: event.target.value })} placeholder={form.courtPricing.standard || form.pricing || "25 per hour"} />
                </label>
                <label className="court-notes-field">
                  <span>Notes</span>
                  <input value={court.notes} onChange={(event) => updateCourtRow(index, { notes: event.target.value })} placeholder="Lighting, mats, competition court, blocked time..." />
                </label>
              </article>
            )) : (
              <CommunityEmptyState
                type="court inventory"
                title="No courts added yet"
                text="Set a court count to create inventory rows before players can request this centre from Badminta."
                primary={{ label: "Complete centre profile", target: "claimCentre" }}
                secondary={{ label: "Back to dashboard", target: "centreDashboard" }}
                icon={Grid3X3}
                navigate={navigate}
                compact
              />
            )}
          </div>
          {saveMessage && <div className={`auth-message ${profileSaveMessageTone(saveMessage)}`}>{saveMessage}</div>}
          <div className="court-manager-actions">
            <button type="button" onClick={() => saveCourtSetup({ publishWhenReady: true })} disabled={savingCourts}>{savingCourts ? "Saving..." : saved ? "Inventory saved" : "Save court inventory"}</button>
            <button type="button" onClick={previewBooking} disabled={savingCourts}>Save and preview booking</button>
          </div>
        </section>
        <aside className="side-panel court-manager-summary">
          <h2>Booking preview</h2>
          <div className={`court-manager-live-state ${previewBookingReady ? "ready" : ""}`}>
            <ShieldCheck size={20} />
            <div>
              <strong>{previewBookingReady ? "Court checkout is ready to preview" : "Live checkout is still blocked"}</strong>
              <span>{previewBookingReady ? "Players can enter date, time, court count and continue into backend checkout after payouts are connected." : `Needs ${previewMissing.slice(0, 4).join(", ") || "final booking setup"} before public charging opens.`}</span>
            </div>
          </div>
          <div className="court-manager-preview-card">
            <Grid3X3 size={28} />
            <strong>{previewVenue.name}</strong>
            <span>{getVenueAddressLabel(previewVenue)}</span>
          </div>
          <div className="court-manager-checklist">
            {bookingSetupRows.map(([label, complete]) => (
              <span key={label} className={complete ? "complete" : ""}>
                {complete ? <Check size={14} /> : <CircleHelp size={14} />}
                {label}
              </span>
            ))}
          </div>
          <div className="court-manager-stat-grid">
            <article><strong>{form.courtInventory.length || "--"}</strong><span>Total courts</span></article>
            <article><strong>{publishedCourtCount}</strong><span>Published</span></article>
            <article><strong><PriceStatus venue={previewVenue} compact /></strong><span>Price</span></article>
            <article><strong>{previewVenue.openingHours ? "Added" : "Missing"}</strong><span>Hours</span></article>
          </div>
          <div className="court-pricing-summary-list">
            {courtPricingRows(form.courtPricing).map((row) => (
              <div key={row.key}>
                <strong>{row.label}</strong>
                <span>{formatCourtFeeValue(row.fee, previewVenue.currency) || "Not set"}</span>
                <em>{row.window}</em>
              </div>
            ))}
          </div>
          <div className="event-checkout-note">
            <CalendarDays size={20} />
            <span>{previewVenue.openingHours || "Select weekly business hours so players know when the centre can accept booking requests."}</span>
          </div>
          <div className="event-checkout-note">
            <ShieldCheck size={20} />
            <span>{form.bookingRules || "Add booking rules before payments are connected, so refunds, equipment and check-in expectations are clear."}</span>
          </div>
          <button type="button" onClick={() => navigate("publicCentreProfile")}>View public centre profile</button>
          <button type="button" onClick={previewBooking} disabled={savingCourts}>Open booking checkout</button>
        </aside>
      </div>
    </section>
  );
}

const supportNotificationTemplateOptions = [
  ["refund_needs_more_info", "Ask for refund evidence"],
  ["refund_approved", "Refund approved"],
  ["refund_rejected", "Refund rejected"],
  ["refund_failed_follow_up", "Failed refund follow-up"],
  ["refund_processed_receipt", "Refund receipt"],
  ["claim_more_info", "Claim evidence request"],
  ["claim_approved", "Claim approved"],
  ["claim_rejected", "Claim rejected"],
  ["moderation_outcome", "Moderation update"],
  ["media_approved", "Media approved"],
  ["media_rejected", "Media rejected"],
  ["payment_receipt_player", "Player payment receipt"],
  ["payment_receipt_owner", "Owner payment notice"],
  ["booking_hold_created_player", "Court hold created - player"],
  ["booking_hold_created_owner", "Court hold created - owner"],
  ["booking_hold_released_player", "Court hold released - player"],
  ["booking_hold_released_owner", "Court hold released - owner"],
  ["booking_hold_expired_player", "Court hold expired - player"],
  ["booking_hold_expired_owner", "Court hold expired - owner"],
  ["booking_cancelled_player", "Booking cancelled - player"],
  ["booking_cancelled_owner", "Booking cancelled - owner"],
  ["booking_no_show_warning", "No-show warning"],
  ["session_join_receipt_player", "Session join receipt"],
  ["session_owner_payment_notice", "Session owner payment notice"],
  ["payout_statement_ready", "Payout statement ready"],
  ["general_support_follow_up", "General follow-up"]
];
const operationalNotificationTemplateKeys = supportNotificationTemplateOptions.map(([key]) => key);

const trustReportBackendSubject = (report = {}) => ({
  collection: report.ledgerCollection === "refunds" ? "refunds" : report.ledgerCollection === "stripeDisputes" ? "payments" : report.ledgerCollection === "reviews" ? "reviews" : "reports",
  subjectId: report.refundReviewId || report.serverReviewId || (report.ledgerCollection === "stripeDisputes" ? report.paymentId : report.serverReportId) || ""
});

function TrustReportOperations({ report, onAddEvidence, onQueueNotification, notificationTemplates = [] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [evidence, setEvidence] = useState({
    evidenceType: report.ledgerCollection === "refunds" ? "refund" : "other",
    title: "",
    note: "",
    sourceUrl: "",
    storagePath: ""
  });
  const [notification, setNotification] = useState({
    templateKey: report.ledgerCollection === "refunds" && String(report.status || "").toLowerCase().includes("failed") ? "refund_failed_follow_up" : "general_support_follow_up",
    channel: "in_app",
    message: ""
  });
  const { subjectId } = trustReportBackendSubject(report);
  const backendReady = Boolean(subjectId);
  const templateOptions = Array.from(new globalThis.Map([
    ...supportNotificationTemplateOptions,
    ...notificationTemplates
      .filter((template) => template.templateKey || template.id)
      .map((template) => [template.templateKey || template.id, template.title || template.templateKey || template.id])
  ]).entries());
  const updateEvidence = (field, value) => setEvidence((current) => ({ ...current, [field]: value }));
  const updateNotification = (field, value) => setNotification((current) => ({ ...current, [field]: value }));
  const addEvidence = async () => {
    if (!onAddEvidence || !backendReady) return;
    setBusy("evidence");
    setMessage("");
    try {
      const result = await onAddEvidence(report, evidence);
      setMessage(result?.evidenceId ? "Evidence recorded in the support ledger." : "Evidence recorded.");
      setEvidence((current) => ({ ...current, title: "", note: "", sourceUrl: "", storagePath: "" }));
    } catch (error) {
      setMessage(marketplaceFunctionErrorMessage(error));
    } finally {
      setBusy("");
    }
  };
  const queueNotification = async () => {
    if (!onQueueNotification || !backendReady) return;
    setBusy("notification");
    setMessage("");
    try {
      const result = await onQueueNotification(report, notification);
      setMessage(result?.notificationId ? "Follow-up queued in the notification ledger." : "Follow-up queued.");
      setNotification((current) => ({ ...current, message: "" }));
    } catch (error) {
      setMessage(marketplaceFunctionErrorMessage(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="trust-ops-drawer">
      <button type="button" className="trust-ops-toggle" onClick={() => setOpen((current) => !current)}>
        <ClipboardCheck size={15} /> {open ? "Hide operations" : "Evidence and follow-up"}
      </button>
      {!backendReady && open && (
        <div className="auth-message stripe-connect-message error">This item is local-only. Submit it to the backend ledger before adding evidence or notification follow-up.</div>
      )}
      {open && backendReady && (
        <div className="trust-ops-grid">
          <div className="trust-ops-card">
            <strong>Record evidence</strong>
            <div className="trust-ops-fields">
              <select value={evidence.evidenceType} onChange={(event) => updateEvidence("evidenceType", event.target.value)}>
                {["refund", "dispute", "claim", "payment", "attendance", "media", "message", "safety", "other"].map((item) => <option key={item} value={item}>{item.replace(/_/g, " ")}</option>)}
              </select>
              <input value={evidence.title} onChange={(event) => updateEvidence("title", event.target.value)} placeholder="Evidence title" />
              <input value={evidence.sourceUrl} onChange={(event) => updateEvidence("sourceUrl", event.target.value)} placeholder="https evidence URL" />
              <input value={evidence.storagePath} onChange={(event) => updateEvidence("storagePath", event.target.value)} placeholder="support-evidence/{uid}/file.pdf" />
              <textarea value={evidence.note} onChange={(event) => updateEvidence("note", event.target.value)} placeholder="What this evidence proves for the refund, claim, safety report or dispute." />
            </div>
            <button type="button" onClick={addEvidence} disabled={busy === "evidence"}>
              <ClipboardCheck size={15} /> {busy === "evidence" ? "Recording..." : "Record evidence"}
            </button>
          </div>
          <div className="trust-ops-card">
            <strong>Queue follow-up</strong>
            <div className="trust-ops-fields">
              <select value={notification.templateKey} onChange={(event) => updateNotification("templateKey", event.target.value)}>
                {templateOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
              <select value={notification.channel} onChange={(event) => updateNotification("channel", event.target.value)}>
                <option value="in_app">In-app</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
              <textarea value={notification.message} onChange={(event) => updateNotification("message", event.target.value)} placeholder="Optional custom message. Leave blank to use the backend template." />
            </div>
            <button type="button" onClick={queueNotification} disabled={busy === "notification"}>
              <Bell size={15} /> {busy === "notification" ? "Queueing..." : "Queue follow-up"}
            </button>
          </div>
        </div>
      )}
      {message && <div className="auth-message stripe-connect-message">{message}</div>}
    </div>
  );
}

function TrustReportList({ reports, navigate, accountProfile = defaultAccountProfile, saveAccountProfile, onStatusChange, onRefundDecision, onAddEvidence, onQueueNotification, notificationTemplates = [], compact = false }) {
  if (!reports.length) {
    return (
      <CommunityEmptyState
        type="trust reports"
        title="No trust queue items yet"
        text="Player, group, centre and event reports submitted from public profiles will appear here with status, subject, details and action buttons."
        primary={{ label: "Open public groups", target: "groupsIndex" }}
        secondary={{ label: "Account", target: "account" }}
        icon={ShieldCheck}
        navigate={navigate}
        compact
      />
    );
  }
  const messageReport = (report) => openTrustReportMessage({ report, accountProfile, saveAccountProfile, navigate });

  return (
    <div className={compact ? "trust-report-list compact" : "trust-report-list"}>
      {reports.map((report) => (
        <article className="trust-report-card" key={report.id}>
          <div className="trust-report-icon">
            <ShieldCheck size={20} />
          </div>
          <div className="trust-report-copy">
            <span>{reviewKindLabels[report.kind] || "Trust report"} · {report.status}</span>
            <strong>{report.subject}</strong>
            <p>{report.comment}</p>
            <div className="trust-meta-strip">
              <em>{report.title}</em>
              <em>{report.createdAt ? `${formatDateLabel(report.createdAt.slice(0, 10))}` : "Date pending"}</em>
              <em>{report.supportEvidenceCount ? `${report.supportEvidenceCount} evidence` : "No evidence yet"}</em>
              <em>{report.notificationCount ? `${report.notificationCount} follow-up${report.notificationCount === 1 ? "" : "s"}` : "No follow-up queued"}</em>
            </div>
            {report.automated && report.safetyFields?.length > 0 && (
              <details className="trust-safety-detail">
                <summary>
                  <ShieldAlert size={15} />
                  Review {report.safetyFields.length} blocked field{report.safetyFields.length === 1 ? "" : "s"}
                </summary>
                <div className="trust-safety-categories">
                  {report.safetyCategories.map((category) => <span key={category}>{String(category).replace(/_/g, " ")}</span>)}
                </div>
                <div className="trust-safety-fields">
                  {report.safetyFields.map((field, index) => (
                    <div key={`${field.field}-${index}`}>
                      <strong>{String(field.field).replace(/([A-Z])/g, " $1").replace(/_/g, " ")}</strong>
                      <p>{field.value}</p>
                      <small>{field.rules.map((rule) => String(rule).replace(/_/g, " ")).join(" · ")}</small>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="trust-report-actions">
            {onStatusChange && isBackendPublicReview(report) && (
              <>
                <button type="button" onClick={() => onStatusChange(report, "approved")}>Approve review</button>
                <button type="button" onClick={() => onStatusChange(report, "rejected")}>Reject review</button>
                <button type="button" onClick={() => onStatusChange(report, "needs_more_info")}>Need info</button>
              </>
            )}
            {onRefundDecision && report.refundReviewId && String(report.status || "").toLowerCase().includes("approved_pending_stripe") && (
              <button type="button" onClick={() => onRefundDecision(report, "execute_stripe")}>Execute refund</button>
            )}
            {onRefundDecision && report.refundReviewId && !String(report.status || "").toLowerCase().includes("approved") && !String(report.status || "").toLowerCase().includes("resolved") && (
              <button type="button" onClick={() => onRefundDecision(report, "approved")}>Approve refund</button>
            )}
            {onStatusChange && !isBackendPublicReview(report) && !String(report.status || "").toLowerCase().includes("reviewed") && (
              <button type="button" onClick={() => onStatusChange(report, "Reviewed by trust team")}>Mark reviewed</button>
            )}
            {onStatusChange && !isBackendPublicReview(report) && !String(report.status || "").toLowerCase().includes("resolved") && (
              <button type="button" onClick={() => onStatusChange(report, "Resolved")}>Resolve</button>
            )}
            {onRefundDecision && report.refundReviewId && !String(report.status || "").toLowerCase().includes("resolved") && (
              <>
                <button type="button" onClick={() => onRefundDecision(report, "needs_more_info")}>Need info</button>
                <button type="button" onClick={() => onRefundDecision(report, "rejected")}>Reject refund</button>
              </>
            )}
            <button type="button" onClick={() => messageReport(report)}>Message</button>
          </div>
          {!compact && (onAddEvidence || onQueueNotification) && (
            <TrustReportOperations report={report} onAddEvidence={onAddEvidence} onQueueNotification={onQueueNotification} notificationTemplates={notificationTemplates} />
          )}
        </article>
      ))}
    </div>
  );
}

function AdminNetwork({ navigate, accountProfile = defaultAccountProfile, saveAccountProfile, platformLedgerData = defaultPlatformLedgerData }) {
  const trustReports = [...adminTrustReportsFromPlatform(platformLedgerData), ...getTrustReports(accountProfile)];
  const openReports = trustReports.filter(isOpenTrustReport);
  const refundReports = trustReports.filter((report) => /refund|payment|fee|charge/i.test(`${report.title} ${report.comment}`));
  const centreClaimCount = accountProfile.roles?.includes("centre") ? 1 : 0;
  const readyProfiles = publicProfileShortcuts.filter(({ role }) => isPublicProfileReady(accountProfile, role)).length;
  return (
    <section className="page admin-page">
      <SplitHero
        eyebrow="Platform admin"
        title="National operations across venues, groups, players and payments."
        text="The internal admin layer keeps the marketplace trustworthy: claims, reports, refunds, verification, content quality and marketplace health."
        emptyLabel="Operations media appears after real platform activity"
      />
      <div className="admin-grid">
        {[
          ["Venue claims", `${centreClaimCount}`, "centreClaimSearch"],
          ["Open reports", `${openReports.length}`, "trust"],
          ["Refund reviews", `${refundReports.length}`, "payouts"],
          ["Marketplace health", readyProfiles ? `${readyProfiles} live` : "Setup", "analytics"]
        ].map(([label, value, target]) => (
          <button type="button" key={label} onClick={() => navigate(target)}>
            <span>{label}</span>
            <strong>{value}</strong>
          </button>
        ))}
      </div>
      <section className="ops-table">
        <h2>Trust queue</h2>
        <TrustReportList reports={openReports.slice(0, 3)} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} compact />
      </section>
    </section>
  );
}

function TrustModeration({ navigate, accountProfile = defaultAccountProfile, saveAccountProfile, platformLedgerData = defaultPlatformLedgerData }) {
  const trustReports = [...adminTrustReportsFromPlatform(platformLedgerData), ...getTrustReports(accountProfile)];
  const openReports = trustReports.filter(isOpenTrustReport);
  const resolvedReports = trustReports.filter((report) => !isOpenTrustReport(report));
  const categoryRows = [
    ["Public reviews", /review|rating|testimonial|recommendation/i],
    ["No-show reports", /no[- ]?show|attendance|did not arrive/i],
    ["Skill mismatch", /skill|level|mismatch|beginner|advanced/i],
    ["Refund disputes", /refund|payment|fee|charge/i],
    ["Blocked content", /content safety|adult|sexual|abusive|hateful|threat|blocked unsafe/i],
    ["Unsafe behaviour", /unsafe|abuse|injury|behaviour|behavior/i],
    ["Photo review", /photo|image|media|video/i],
    ["Venue quality", /venue|court|lighting|parking|facility/i]
  ].map(([label, pattern]) => [label, openReports.filter((report) => pattern.test(`${report.title} ${report.subject} ${report.comment}`)).length]);
  const updateReportStatus = (report, status) => {
    if (report.ledgerCollection !== "refunds") {
      const collection = isBackendPublicReview(report) ? "reviews" : "reports";
      const recordId = isBackendPublicReview(report) ? report.serverReviewId : (report.serverReportId || report.id);
      callMarketplaceFunction("recordModerationDecision", {
        collection,
        recordId,
        status,
        note: `Updated from trust queue for ${report.subject || report.title || "trust report"}.`
      }).catch(() => {});
    }
    const nextReviews = (Array.isArray(accountProfile.reviews) ? accountProfile.reviews : []).map((review) => (
      review.id === report.id ? { ...review, status, moderationStatus: status, public: status === "approved", moderatedAt: new Date().toISOString() } : review
    ));
    saveAccountProfile?.({ reviews: nextReviews });
  };
  const decideRefundReview = async (report, decision) => {
    if (report.refundReviewId) {
      try {
        if (decision === "execute_stripe") {
          const result = await callMarketplaceFunction("executeApprovedStripeRefund", {
            refundId: report.refundReviewId
          });
          updateReportStatus(report, result.status === "refunded" ? "Refunded through Stripe" : "Stripe refund submitted");
          return;
        }
        await callMarketplaceFunction("reviewRefundRequest", {
          refundId: report.refundReviewId,
          decision,
          note: `Refund review updated from trust queue for ${report.subject || report.title || "payment report"}.`
        });
      } catch {
        if (decision === "execute_stripe") return;
      }
    }
    updateReportStatus(report, decision === "approved" ? "approved_pending_stripe" : decision === "rejected" ? "Refund rejected by trust team" : "Refund needs more information");
  };
  const addSupportEvidence = async (report, evidence) => {
    const subject = trustReportBackendSubject(report);
    if (!subject.subjectId) throw new Error("This report is not linked to a backend ledger record.");
    return callMarketplaceFunction("recordSupportEvidence", {
      subjectCollection: subject.collection,
      subjectId: subject.subjectId,
      subjectType: report.title || report.kind || "trust",
      countryCode: report.countryCode || "",
      evidenceType: evidence.evidenceType,
      title: evidence.title || `${report.subject} evidence`,
      note: evidence.note,
      sourceUrl: evidence.sourceUrl,
      storagePath: evidence.storagePath
    });
  };
  const queueSupportFollowUp = async (report, notification) => {
    const subject = trustReportBackendSubject(report);
    if (!subject.subjectId) throw new Error("This report is not linked to a backend ledger record.");
    return callMarketplaceFunction("queueSupportNotification", {
      subjectCollection: subject.collection,
      subjectId: subject.subjectId,
      countryCode: report.countryCode || "",
      templateKey: notification.templateKey,
      channel: notification.channel,
      message: notification.message
    });
  };
  const openModerationMessages = () => {
    const report = openReports[0] || trustReports[0];
    if (report) {
      openTrustReportMessage({ report, accountProfile, saveAccountProfile, navigate });
      return;
    }
    navigate("messages");
  };
  return (
    <section className="page trust-page">
      <div className="screen-head">
        <p>Trust and moderation</p>
        <h1>Keep groups safe, fair and reliable.</h1>
        <button type="button" onClick={() => navigate("admin")}>Back to admin</button>
      </div>
      <div className="trust-grid">
        {categoryRows.map(([item, count]) => (
          <article key={item} className={count ? "active" : ""}>
            <ShieldCheck size={22} />
            <strong>{item}</strong>
            <span>{count} open</span>
          </article>
        ))}
      </div>
      <section className="ops-table trust-queue-panel">
        <div className="section-title-row">
          <div>
            <p>Moderation queue</p>
            <h2>{openReports.length ? `${openReports.length} open report${openReports.length === 1 ? "" : "s"}` : "No open reports"}</h2>
          </div>
          <button type="button" onClick={openModerationMessages}>Open messages</button>
        </div>
        <TrustReportList
          reports={openReports}
          navigate={navigate}
          accountProfile={accountProfile}
          saveAccountProfile={saveAccountProfile}
          onStatusChange={updateReportStatus}
          onRefundDecision={decideRefundReview}
          onAddEvidence={addSupportEvidence}
          onQueueNotification={queueSupportFollowUp}
          notificationTemplates={platformLedgerData.notificationTemplates || []}
        />
      </section>
      {resolvedReports.length > 0 && (
        <section className="ops-table trust-queue-panel">
          <div className="section-title-row">
            <div>
              <p>Moderated</p>
              <h2>Resolved and reviewed reports</h2>
            </div>
          </div>
          <TrustReportList reports={resolvedReports.slice(0, 6)} navigate={navigate} accountProfile={accountProfile} saveAccountProfile={saveAccountProfile} compact />
        </section>
      )}
    </section>
  );
}

function AnalyticsPage({ accountProfile = {}, venueCount = venues.length }) {
  const accountEvents = getAccountEvents(accountProfile);
  const eventStats = accountEvents.map((event) => getEventDashboardStats(event, accountProfile));
  const totalCapacity = eventStats.reduce((total, item) => total + item.capacity, 0);
  const totalAttendees = eventStats.reduce((total, item) => total + item.attendeeCount, 0);
  const fillRate = totalCapacity ? `${Math.round((totalAttendees / totalCapacity) * 100)}%` : "No events";
  const profileRoles = accountProfile.roles?.length || 0;
  const metricCards = [
    ["Venue supply", `${venueCount}`, "Directory centres loaded from current data", Math.min(95, Math.max(8, Math.round(venueCount / 20)))],
    ["Managed events", `${accountEvents.length}`, "Events created by this account", Math.min(95, Math.max(8, accountEvents.length * 18))],
    ["Average event fill", fillRate, totalCapacity ? `${totalAttendees}/${totalCapacity} attendee capacity` : "Create events to measure fill", totalCapacity ? Math.round((totalAttendees / totalCapacity) * 100) : 8],
    ["Profile roles", `${profileRoles}`, "Player, organiser, centre and coach roles selected", Math.min(100, Math.max(8, profileRoles * 25))]
  ];
  return (
    <section className="page analytics-like-page">
      <div className="screen-head">
        <p>Marketplace analytics</p>
        <h1>Measure venue coverage, liquidity, bookings and player quality.</h1>
      </div>
      <div className="chart-grid">
        {metricCards.map(([title, value, detail, barHeight]) => (
          <article key={title}>
            <span>{title}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
            <div className="bars">{[0.55, 0.72, 0.9, 0.65, 0.8, 1].map((scale, barIndex) => <i key={barIndex} style={{ height: `${Math.max(4, Math.round(barHeight * scale))}%` }} />)}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

function useSuperAdminAccess(auth) {
  const [access, setAccess] = useState({ status: "checking", allowed: false, source: "", reason: "" });

  useEffect(() => {
    const user = auth.user;

    if (auth.loading) {
      setAccess({ status: "checking", allowed: false, source: "", reason: "Checking phone session." });
      return undefined;
    }

    if (!user) {
      setAccess({ status: "signed-out", allowed: false, source: "", reason: "Phone verification required." });
      return undefined;
    }

    if (hasSuperAdminAllowlistAccess(user)) {
      setAccess({ status: "allowed", allowed: true, source: "Owner phone allowlist", reason: "" });
      return undefined;
    }

    setAccess({
      status: "denied",
      allowed: false,
      source: "",
      reason: "This verified phone is not authorised for administrator access."
    });

    return undefined;
  }, [auth.loading, auth.user?.uid, auth.user?.phoneNumber]);

  return access;
}

const adminSectionTabs = [
  ["overview", "Overview", LayoutDashboard],
  ["launch", "Launch", Gauge],
  ["players", "Players", UserCircle],
  ["users", "Users", UsersRound],
  ["centres", "Centres", Building2],
  ["content", "Events", CalendarDays],
  ["trust", "Trust", ShieldCheck],
  ["security", "Security", LockKeyhole],
  ["audit", "Audit", ClipboardCheck]
];

const adminNavigationGroups = [
  {
    label: "Command",
    items: [
      ["overview", "Control room", LayoutDashboard],
      ["launch", "Revenue & launch", Gauge]
    ]
  },
  {
    label: "Marketplace",
    items: [
      ["users", "Accounts", UsersRound],
      ["players", "Player profiles", UserCircle],
      ["centres", "Centre operators", Building2],
      ["content", "Groups & events", CalendarDays]
    ]
  },
  {
    label: "Governance",
    items: [
      ["trust", "Support & safety", ShieldCheck],
      ["security", "Platform health", LockKeyhole],
      ["audit", "Audit trail", ClipboardCheck]
    ]
  }
];

const adminSectionIds = new Set(adminSectionTabs.map(([id]) => id));

const initialAdminSection = () => {
  if (typeof window === "undefined") return "overview";
  const requested = new URLSearchParams(window.location.search).get("workspace") || "";
  return adminSectionIds.has(requested) ? requested : "overview";
};

const adminUnknownCountryCode = "unknown";

const getAdminCountryMeta = (countryCode = "", fallbackCity = "") => {
  const code = normaliseCountryCode(countryCode, fallbackCity);
  if (!code) {
    return {
      countryCode: adminUnknownCountryCode,
      country: "Location not supplied"
    };
  }
  return {
    countryCode: code,
    country: getCountryName(code, fallbackCity) || code
  };
};

const findVenueForAdminSource = (source = {}) => {
  if (!source) return null;
  if (source.venueId) {
    const venue = getEventVenue({ venueId: source.venueId });
    if (venue) return venue;
  }
  const venueText = normaliseSearchText(source.venue || source.venueName || source.name || "");
  if (!venueText) return null;
  return venues.find((venue) => {
    const venueName = normaliseSearchText(venue.name);
    const venueAddress = normaliseSearchText(getVenueAddressLabel(venue));
    return venueName === venueText || venueAddress === venueText;
  }) || null;
};

const resolveAdminCountryMeta = (...sources) => {
  const flattened = sources.flat().filter(Boolean);

  for (const source of flattened) {
    const location = source.location || {};
    const city = source.city || source.suburb || source.venueCity || location.city || location.suburb || "";
    const explicitCountry = source.countryCode || source.country || location.countryCode || location.country;
    const code = normaliseCountryCode(explicitCountry, city);
    if (code) return getAdminCountryMeta(code, city);
  }

  for (const source of flattened) {
    const phone = String(source.phoneNumber || source.phone || source.ownerPhone || "").trim();
    if (!phone || !parsePhoneNumberFromString) continue;
    const phoneCountryCode = parsePhoneNumberFromString(phone)?.country || "";
    if (phoneCountryCode) return getAdminCountryMeta(phoneCountryCode);
  }

  for (const source of flattened) {
    const venue = findVenueForAdminSource(source);
    if (venue) return resolveAdminCountryMeta(venue);
  }

  for (const source of flattened) {
    const location = source.location || {};
    const city = source.city || source.suburb || source.venueCity || location.city || location.suburb || "";
    if (!city) continue;
    const seedCountry = getCityCoordinate(city)?.country;
    const directoryCountry = cityDirectory.find((option) => compactLocationText(option.city) === compactLocationText(city))?.countryCode;
    const code = normaliseCountryCode(seedCountry || directoryCountry, city);
    if (code) return getAdminCountryMeta(code, city);
  }

  return getAdminCountryMeta();
};

const attachAdminCountry = (record, ...sources) => ({
  ...record,
  ...resolveAdminCountryMeta(record, ...sources)
});

const getAdminRecordCountryCode = (record = {}) => {
  if (record.countryCode === adminUnknownCountryCode) return adminUnknownCountryCode;
  return normaliseCountryCode(record.countryCode || record.country, record.city || record.location?.city) || adminUnknownCountryCode;
};

const filterAdminRecordsByCountry = (records = [], countryCode = "") => {
  const code = countryCode === adminUnknownCountryCode ? adminUnknownCountryCode : normaliseCountryCode(countryCode);
  if (!code) return records;
  return records.filter((record) => getAdminRecordCountryCode(record) === code);
};

const buildAdminCountryOptions = ({ userRecords = [], centreRecords = [], contentRecords = [], trustReports = [], profileMediaRows = [] } = {}) => {
  const countries = new globalThis.Map();
  const addRecord = (record, bucket) => {
    const code = getAdminRecordCountryCode(record);
    const current = countries.get(code) || {
      code,
      name: code === adminUnknownCountryCode ? "Location not supplied" : getCountryName(code) || code,
      total: 0,
      itemTotal: 0,
      users: 0,
      centres: 0,
      content: 0,
      trust: 0,
      media: 0
    };
    current.itemTotal += 1;
    if (!["trust", "media"].includes(bucket)) current.total += 1;
    current[bucket] += 1;
    countries.set(code, current);
  };

  userRecords.forEach((record) => addRecord(record, "users"));
  centreRecords.forEach((record) => addRecord(record, "centres"));
  contentRecords.forEach((record) => addRecord(record, "content"));
  trustReports.forEach((record) => addRecord(record, "trust"));
  profileMediaRows.forEach((record) => addRecord(record, "media"));

  return Array.from(countries.values()).sort((a, b) => {
    if (b.itemTotal !== a.itemTotal) return b.itemTotal - a.itemTotal;
    return a.name.localeCompare(b.name);
  });
};

const adminRecordMatchesQuery = (record, query) => {
  const value = query.trim().toLowerCase();
  if (!value) return true;
  const account = record.payload?.account || {};
  return [
    record.name,
    record.type,
    record.scope,
    record.detail,
    record.status,
    record.owner,
    record.country,
    record.countryCode,
    account.phoneNumber,
    account.email,
    account.uid,
    account.marketingAttribution?.utm_source,
    account.marketingAttribution?.utm_campaign
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(value);
};

const adminDecisionSummary = (accountProfile = {}) => accountProfile.adminDecisions || {};
const adminAuditLog = (accountProfile = {}) => Array.isArray(accountProfile.adminAuditLog) ? accountProfile.adminAuditLog : [];
const adminRecordUpdateSummary = (accountProfile = {}) => accountProfile.adminRecordUpdates || {};
const isAccountRestricted = (accountProfile = {}) => Boolean(
  accountProfile.accountRestricted === true
  || String(accountProfile.restrictionStatus || "").toLowerCase() === "restricted"
);

const getAdminRecordBaseFields = (record = {}) => ({
  name: record.name || "",
  owner: record.owner || "",
  detail: record.detail || "",
  status: record.status || "",
  countryCode: getAdminRecordCountryCode(record)
});

const canAdminVerifyRecord = (record = {}) => record.adminCanVerify === false ? false : record.scope !== "Centre" || Boolean(record.adminCanVerify);

const isBackendClaimRecord = (record = {}) => record.claimWorkflow === "centre_claim" || record.type === "Backend ownership claim";

const backendClaimIdFromRecord = (record = {}) => String(record.claimId || record.payload?.claim?.id || record.id || "").replace(/^claim-/, "");

const adminClaimDecisionLabel = (decision = "") => ({
  approved: "Claim approved",
  rejected: "Claim rejected",
  needs_more_info: "More claim evidence needed"
})[decision] || "Claim reviewed";

const formatAdminDateTime = (value = "") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(date);
};

const claimVerificationDetail = (claim = {}) => {
  const missing = Array.isArray(claim.missingVerificationRequirements) ? claim.missingVerificationRequirements : [];
  return [
    claim.evidenceStatus || "Evidence status pending",
    claim.supportEvidenceCount || claim.evidenceRecordCount ? `${claim.supportEvidenceCount || claim.evidenceRecordCount} evidence record${(claim.supportEvidenceCount || claim.evidenceRecordCount) === 1 ? "" : "s"}` : "",
    Number.isFinite(Number(claim.verificationScore)) ? `Verification score ${claim.verificationScore}/5` : "",
    claim.fraudRiskLevel ? `Risk ${claim.fraudRiskLevel}` : "",
    claim.slaDueAt ? `SLA due ${formatAdminDateTime(claim.slaDueAt)}` : "",
    missing.length ? `Missing: ${missing.slice(0, 2).join(" ")}` : "",
    claim.claimSource || ""
  ].filter(Boolean).join(" · ");
};

const getAdminDisplayStatus = (record = {}, decisions = {}) => {
  const decision = decisions[record.id];
  if (decision === "Verified" && !canAdminVerifyRecord(record)) {
    return `${record.status || "Directory listing"} · claim required`;
  }
  return decision || record.status || "";
};

const isAdminCentreRecordOwnerManaged = (record = {}) => {
  if (record.scope !== "Centre" || isBackendClaimRecord(record)) return false;
  return Boolean(
    record.verifiedSource
    || record.ownerManaged
    || record.payload?.venue?.platformOwnerVerified
    || record.payload?.venue?.source === "owner"
    || record.payload?.venue?.source === "owner-created"
  );
};

const isAdminCentreRecordBookable = (record = {}) => {
  if (!isAdminCentreRecordOwnerManaged(record)) return false;
  const venue = record.payload?.venue || {};
  return Boolean(
    record.bookingReady
    || venue.isPlatformBookable
    || (venue.serverCentreId && venue.bookingReadiness?.ready)
  );
};

const isStripeAccountCheckoutReady = (account = {}) => {
  const fundsFlow = String(account.checkoutFundsFlow || "connect_destination_charge");
  const capabilityReady = fundsFlow.includes("direct_charge")
    ? account.cardPaymentsCapability
      ? account.cardPaymentsCapability === "active"
      : account.chargesEnabled === true
    : account.transfersCapability
      ? account.transfersCapability === "active"
      : account.chargesEnabled === true;
  return Boolean(
    account.accountId
    && capabilityReady
    && account.payoutsEnabled
    && account.detailsSubmitted
    && !account.checkoutBlocked
  );
};

const isStripeAccountNeedsReview = (account = {}) => Boolean(
  account.checkoutBlocked
  || account.supportReviewStatus === "open"
  || account.disabledReason
  || (Array.isArray(account.pastDue) && account.pastDue.length)
  || (Array.isArray(account.currentlyDue) && account.currentlyDue.length)
  || (account.accountId && !isStripeAccountCheckoutReady(account))
);

const isCentreClaimedByAccount = (venue = {}, accountProfile = defaultAccountProfile) => {
  const centre = accountProfile.centre || {};
  return Boolean(venue.id && centre.claimedVenueId && String(venue.id) === String(centre.claimedVenueId));
};

const centreAdminVerificationState = (venue = {}, accountProfile = defaultAccountProfile) => {
  const claimedByAccount = isCentreClaimedByAccount(venue, accountProfile);
  const ownerSupplied = venue.source === "account" || venue.source === "owner" || venue.id === "account-public-centre";
  const verifiedSource = isVerifiedCentreListing(venue);
  return {
    claimedByAccount,
    ownerSupplied,
    verifiedSource
  };
};

const applyAdminRecordUpdates = (records = [], updates = {}) => records.map((record) => {
  const update = updates[record.id];
  if (!update) return record;
  const nextCountryCode = update.countryCode || record.countryCode;
  const next = {
    ...record,
    adminUpdated: true,
    adminUpdatedAt: update.updatedAt || "",
    name: update.name?.trim() || record.name,
    owner: update.owner?.trim() || record.owner,
    detail: update.detail?.trim() || record.detail,
    status: update.status?.trim() || record.status,
    countryCode: nextCountryCode,
    country: getCountryName(nextCountryCode, record.city || record.location?.city) || record.country
  };
  if (record.scope === "Centre" && /verified/i.test(next.status || "") && !canAdminVerifyRecord(record)) {
    next.status = record.status;
  }
  return attachAdminCountry(next, { countryCode: update.countryCode || record.countryCode }, record);
});

const buildSuperAdminRoleRecords = (accountProfile = defaultAccountProfile) => accountRoleOptions
  .filter((role) => accountHasRole(accountProfile, role.id) || roleProfileStarted(accountProfile, role.id))
  .map((role) => {
    const ready = isPublicProfileReady(accountProfile, role.id);
    const missing = getRoleOnboardingMissingFields(accountProfile, role.id);
    const readyToPublish = !ready && accountHasRole(accountProfile, role.id) && getPublicProfileMissingFields(accountProfile, role.id).length === 0;
    const roleProfile = role.id === "organiser" ? accountProfile.group : accountProfile[role.id];
    const name = role.id === "player" || role.id === "coach"
      ? accountProfile.fullName || role.title
      : roleProfile?.name || accountProfile.fullName || role.title;
    return attachAdminCountry({
      id: `account-role-${role.id}`,
      recordKind: "role",
      scope: "User profile",
      type: role.title,
      name,
      owner: accountProfile.email || accountProfile.phone || "",
      detail: ready ? "Public profile is live" : readyToPublish ? "All required fields complete; publication pending" : `${missing.length} required field${missing.length === 1 ? "" : "s"} missing`,
      status: ready ? "Published" : readyToPublish ? "Ready to publish" : accountHasRole(accountProfile, role.id) ? "Onboarding incomplete" : "Started, not selected",
      target: ready ? roleDashboardTargetFor(role.id) : roleSetupTargetFor(role.id),
      payload: {}
    }, roleProfile, accountProfile);
  });

const buildSuperAdminUserRecords = (accountProfile = defaultAccountProfile, playerDirectory = [], coachDirectory = [], platformUsers = [], options = {}) => {
  const includeCurrentAccount = options.includeCurrentAccount !== false;
  const accountRecord = includeCurrentAccount && (accountProfile.fullName || accountProfile.email || accountProfile.roles?.length) ? [{
    id: "account-main",
    recordKind: "account",
    scope: "Account",
    type: "Verified account",
    name: accountProfile.fullName || "Verified Badminta account",
    owner: accountProfile.email || "",
    detail: accountProfile.roles?.length ? accountProfile.roles.map(roleTitle).join(", ") : "No roles selected",
    status: accountProfile.roles?.length ? "Active" : "Needs roles",
    target: "account",
    payload: {}
  }] : [];
  const playerRecords = playerDirectory.map((player) => attachAdminCountry({
    id: `directory-player-${player.id || slugifyPath(player.name)}`,
    recordKind: "player",
    scope: "Directory",
    type: "Player",
    name: player.name || "Player",
    owner: player.city || "",
    detail: [player.band, player.rating ? `${player.rating} rating` : "", player.city].filter(Boolean).join(" · ") || "Directory player",
    status: player.public === true ? "Published directory profile" : "Draft player profile",
    target: "player",
    openLabel: "View profile",
    payload: { player }
  }, player));
  const coachRecords = coachDirectory.map((coach) => attachAdminCountry({
    id: `directory-coach-${coach.id || slugifyPath(coach.name)}`,
    recordKind: "coach",
    scope: "Directory",
    type: "Coach",
    name: coach.name || "Coach",
    owner: coach.city || "",
    detail: [coach.specialty, coach.rating ? `${coach.rating} rating` : "", coach.city].filter(Boolean).join(" · ") || "Directory coach",
    status: coach.public === true ? "Published directory profile" : "Draft coach profile",
    target: "coach",
    payload: { coach }
  }, coach));
  const platformUserRecords = platformUsers.map((user) => {
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const roleLabels = roles.length ? roles.map(roleTitle).join(", ") : "No roles selected";
    const roleProgress = user.roleProgress && typeof user.roleProgress === "object" ? user.roleProgress : {};
    const startedProfiles = accountRoleOptions
      .filter((role) => roleProgress[role.id]?.started || roleProfileStarted(user, role.id))
      .map((role) => role.title);
    const readyProfiles = accountRoleOptions
      .filter((role) => roleProgress[role.id]?.published || isPublicProfileReady(user, role.id))
      .map((role) => role.title);
    const signupProgress = user.signupProgress || {};
    const onboardingStatus = signupProgress.onboardingStatus || "";
    const hasPublishedProfile = readyProfiles.length > 0 || Number(user.publishedRoleCount || 0) > 0;
    const status = isAccountRestricted(user)
      ? "Restricted"
      : user.disabled
        ? "Disabled in Firebase Auth"
        : onboardingStatus === "phone_verified_only"
          ? "Phone verified only"
          : onboardingStatus === "account_details_incomplete"
            ? "Account details incomplete"
            : onboardingStatus === "profiles_complete"
              ? "Profiles complete"
              : roles.length
                ? hasPublishedProfile
                  ? "Published profile live · additional roles unfinished"
                  : "First profile setup unfinished"
                : "Needs roles";
    return attachAdminCountry({
      id: `platform-user-${user.uid || user.id}`,
      recordKind: "account",
      userId: user.uid || user.id || "",
      backendUserRecord: true,
      restrictedUser: isAccountRestricted(user),
      scope: "Account",
      type: user.authOnly ? "Firebase Auth signup" : "Firebase account",
      name: user.fullName || user.displayName || user.email || user.phoneNumber || "Badminta user",
      owner: user.email || user.phoneNumber || user.uid || user.id || "",
      detail: [
        roleLabels,
        signupProgress.totalSteps ? `${signupProgress.completedSteps || 0}/${signupProgress.totalSteps} core signup steps` : "",
        startedProfiles.length ? `${startedProfiles.length} started profile${startedProfiles.length === 1 ? "" : "s"}` : "",
        readyProfiles.length ? `${readyProfiles.length} published` : "",
        user.authOnly ? "No Firestore account document" : ""
      ].filter(Boolean).join(" · "),
      status,
      payload: { account: user },
      authCreatedAt: user.authCreatedAt || user.createdAt || "",
      lastSignInAt: user.lastSignInAt || ""
    }, user, user.player, user.group, user.centre, user.coach);
  });
  const platformPlayerRecords = platformUsers.flatMap((user) => {
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const player = user.player && typeof user.player === "object" ? user.player : {};
    const progress = user.roleProgress?.player || {};
    const selected = roles.includes("player");
    const started = progress.started === true || selected || roleProfileStarted(user, "player");
    if (!started) return [];
    const profileId = progress.profileId
      || player.publicPlayerId
      || player.firestoreSourceId
      || player.id
      || "";
    const missingFields = Array.isArray(progress.missingFields)
      ? progress.missingFields
      : getPublicProfileMissingFields({ ...user, roles: Array.from(new Set([...roles, "player"])), player }, "player");
    const published = progress.published === true || roleHasPublishedBackendProfile(user, "player");
    const name = [player.name, player.fullName, user.fullName, user.displayName]
      .map(safePublicPlayerDisplayName)
      .find(Boolean)
      || publicPlayerDisplayName("", profileId || user.uid || user.id);
    const status = published
      ? "Published directory profile"
      : missingFields.length
        ? "Incomplete player profile"
        : "Private player draft";
    const playerSnapshot = {
      ...player,
      id: profileId,
      ownerUid: user.uid || user.id || "",
      name,
      fullName: name,
      public: published,
      status: published ? "published" : progress.status || player.backendStatus || "draft",
      backendStatus: published ? "published" : progress.status || player.backendStatus || "draft",
      publicProfilePublished: published,
      publishMissing: missingFields
    };
    return [attachAdminCountry({
      id: `account-player-${user.uid || user.id || profileId || slugifyPath(name)}`,
      recordKind: "player",
      scope: "Account profile",
      type: "Player",
      name,
      owner: player.city || player.suburb || user.city || user.suburb || user.email || user.phoneNumber || "",
      detail: missingFields.length
        ? `Missing ${missingFields.join(", ").toLowerCase()}`
        : [player.skillBand || player.band, player.city || player.suburb].filter(Boolean).join(" · ") || status,
      status,
      target: "player",
      openLabel: published ? "View profile" : "Preview draft",
      authCreatedAt: user.authCreatedAt || user.createdAt || "",
      lastSignInAt: user.lastSignInAt || "",
      payload: { player: playerSnapshot, account: user, progress }
    }, user, playerSnapshot)];
  });
  const authoritativePlayerProfileIds = new Set(platformPlayerRecords
    .map((record) => record.payload?.player?.id)
    .filter(Boolean));
  const authoritativePlayerOwnerUids = new Set(platformPlayerRecords
    .map((record) => record.payload?.player?.ownerUid)
    .filter(Boolean));
  const unmergedDirectoryPlayerRecords = playerRecords.filter((record) => {
    const player = record.payload?.player || {};
    const profileId = player.id || player.publicPlayerId || player.firestoreSourceId || "";
    const ownerUid = player.ownerUid || player.playerUid || "";
    return !(profileId && authoritativePlayerProfileIds.has(profileId))
      && !(ownerUid && authoritativePlayerOwnerUids.has(ownerUid));
  });
  return [
    ...platformUserRecords,
    ...accountRecord.map((record) => attachAdminCountry(record, accountProfile, accountProfile.player, accountProfile.group, accountProfile.centre, accountProfile.coach)),
    ...buildSuperAdminRoleRecords(accountProfile),
    ...platformPlayerRecords,
    ...unmergedDirectoryPlayerRecords,
    ...coachRecords
  ];
};

const buildSuperAdminCentreRecords = (venueDirectory = [], accountProfile = defaultAccountProfile, platformClaims = []) => {
  const centre = accountProfile.centre || {};
  const accountCentreStarted = Boolean(centre.name || centre.address || centre.website || centre.claimedVenueId);
  const accountCentreVenue = accountCentreStarted ? managedVenueFromCentreProfile(centre, venueDirectory.find((venue) => venue.id === centre.claimedVenueId) || {}) : null;
  const accountCentreReadinessMissing = accountCentreVenue?.bookingReadiness?.missing || [];
  const accountCentreVerified = accountCentreVenue ? isVerifiedCentreListing(accountCentreVenue) : false;
  const accountCentreCanVerify = Boolean(accountCentreVenue?.serverCentreId && accountCentreVenue?.verificationReadiness?.ready);
  const accountCentreRecord = accountCentreStarted ? attachAdminCountry({
    id: "centre-account-owned",
    scope: "Centre",
    type: centre.claimedVenueId ? "Claimed centre profile" : "Owner-created centre profile",
    name: centre.name || accountCentreVenue?.name || "Centre owner profile",
    owner: accountProfile.email || accountProfile.phone || accountProfile.fullName || "Centre owner",
    detail: getVenueAddressLabel(accountCentreVenue) || centre.address || "Address not listed",
    status: accountCentreVerified ? "Verified centre" : centre.claimedVenueId ? "Claim awaiting Badminta verification" : "Owner profile awaiting verification",
    target: isPublicProfileReady(accountProfile, "centre") ? "publicCentreProfile" : "claimCentre",
    editTarget: "claimCentre",
    editLabel: "Centre form",
    payload: { venue: accountCentreVenue },
    adminCanVerify: accountCentreCanVerify,
    verifiedSource: accountCentreVerified,
    ownerManaged: true,
    bookingReady: Boolean(accountCentreVenue?.isPlatformBookable),
    bookingReadinessMissing: accountCentreReadinessMissing,
    backendCentreId: accountCentreVenue?.serverCentreId || accountCentreVenue?.firestoreSourceId || ""
  }, centre, accountCentreVenue, accountProfile) : null;

  const directoryRecords = venueDirectory.map((venue) => {
    const verification = centreAdminVerificationState(venue, accountProfile);
    const platformBookingState = getPlatformCentreBookingState(venue, accountProfile);
    const adminCanVerify = Boolean(
      isOwnerManagedCentreListing(venue)
      && getCentreVerifiedProfileReadiness(venue, venue).ready
      && (venue.serverCentreId || venue.firestoreSourceId)
    );
    const status = verification.claimedByAccount
      ? (verification.verifiedSource ? "Verified centre" : "Claim awaiting Badminta verification")
      : verification.ownerSupplied
        ? (verification.verifiedSource ? "Verified owner listing" : "Owner profile awaiting verification")
        : "Directory listing";
    return attachAdminCountry({
      id: `centre-${venue.id || slugifyPath(venue.name)}`,
      scope: "Centre",
      type: verification.verifiedSource ? "Verified centre" : venue.kind || venue.category || "Badminton centre",
      name: venue.name || "Unnamed centre",
      owner: verification.claimedByAccount ? accountProfile.email || accountProfile.phone || "Claimed by centre owner" : [venue.suburb, venue.city].filter(Boolean).join(", "),
      detail: getVenueAddressLabel(venue) || "Address not listed",
      status,
      target: "venue",
      editTarget: verification.claimedByAccount ? "claimCentre" : "centreClaimSearch",
      editLabel: verification.claimedByAccount ? "Centre form" : "Claim flow",
      payload: { venue },
      adminCanVerify,
      verifiedSource: verification.verifiedSource,
      ownerManaged: platformBookingState.owned,
      bookingReady: platformBookingState.ready,
      bookingReadinessMissing: platformBookingState.readiness?.missing || [],
      backendCentreId: platformBookingState.backendSourceId || ""
    }, venue);
  });

  const claimRecords = (platformClaims || []).map((claim) => attachAdminCountry({
    id: `claim-${claim.id}`,
    claimId: claim.id,
    claimWorkflow: "centre_claim",
    scope: "Centre",
    type: "Backend ownership claim",
    name: claim.centreName || claim.centreId || "Centre claim",
    owner: claim.ownerEmail || claim.ownerPhone || claim.ownerUid || "Claim owner",
    detail: claimVerificationDetail(claim) || "Claim submitted for admin review",
    status: claim.status || claim.decision || "pending_review",
    target: "centreClaimSearch",
    editTarget: "centreClaimSearch",
    editLabel: "Claim search",
    payload: { claim },
    claimApprovalBlocked: claim.approvalReadiness === false || claim.fraudRiskLevel === "high",
    claimApprovalReason: Array.isArray(claim.missingVerificationRequirements) && claim.missingVerificationRequirements.length
      ? claim.missingVerificationRequirements.join(" ")
      : "Approval needs stronger claim evidence.",
    adminCanVerify: false,
    verifiedSource: false
  }, claim));

  return [
    ...(accountCentreRecord ? [accountCentreRecord] : []),
    ...claimRecords,
    ...directoryRecords
  ];
};

const buildSuperAdminContentRecords = (accountProfile = defaultAccountProfile, groupDirectory = [], events = []) => {
  const accountGroup = accountProfile.group?.name || accountProfile.group?.venue ? [{
    id: `account-group-${getGroupStableId(accountProfile.group)}`,
    scope: "Social group",
    type: "Group",
    name: accountProfile.group.name || "Social group draft",
    owner: accountProfile.fullName || accountProfile.email || "",
    detail: [accountProfile.group.venue, accountProfile.group.date, accountProfile.group.skillBand].filter(Boolean).join(" · ") || "Group setup in progress",
    status: isPublicProfileReady(accountProfile, "group") ? "Joinable profile ready" : "Onboarding incomplete",
    target: isPublicProfileReady(accountProfile, "group") ? "publicGroupProfile" : "createGroup",
    payload: {}
  }] : [];
  const groupRows = groupDirectory.map((group) => attachAdminCountry({
    id: `group-${getGroupStableId(group)}`,
    scope: "Social group",
    type: "Group",
    name: getGroupTitle(group),
    owner: group.organiser || group.owner || "",
    detail: [getGroupVenueLabel(group), getGroupWhenLabel(group)].filter(Boolean).join(" · "),
    status: group.public === true ? "Published directory group" : "Draft social group",
    target: "group",
    payload: { group }
  }, group, getEventVenue({ venueId: group.venueId })));
  const eventRows = events.map((event) => attachAdminCountry({
    id: `event-${event.id || slugifyPath(event.title)}`,
    scope: "Event",
    type: eventOwnerRoleLabel(event.ownerType),
    name: event.title || "Event",
    owner: getEventOwnerLabel(event),
    detail: [getEventVenueLabel(event), event.date ? formatDateLabel(event.date) : "", event.time ? formatTimeLabel(event.time) : ""].filter(Boolean).join(" · "),
    status: event.status === "published" ? "Published" : "Draft",
    target: "event",
    payload: { event }
  }, event, getEventVenue(event)));
  return [
    ...accountGroup.map((record) => attachAdminCountry(record, accountProfile.group, getEventVenue({ venueId: accountProfile.group?.venueId }))),
    ...groupRows,
    ...eventRows
  ];
};

function SuperAdminEditPanel({ record, update, countries = [], onSave, onReset, onCancel }) {
  const [form, setForm] = useState(() => ({ ...getAdminRecordBaseFields(record), ...(update || {}) }));

  useEffect(() => {
    setForm({ ...getAdminRecordBaseFields(record), ...(update || {}) });
  }, [record?.id, update?.updatedAt]);

  if (!record) return null;

  const canVerify = canAdminVerifyRecord(record);
  const statusOptions = [
    record.status || "Directory listing",
    "Needs review",
    "Restricted",
    canVerify ? "Verified" : "",
    canVerify && record.scope === "Centre" ? "Verified claim" : ""
  ].filter(Boolean);
  const uniqueStatusOptions = Array.from(new Set(statusOptions));
  const countryOptions = Array.from(new globalThis.Map([
    [record.countryCode, { code: record.countryCode, name: record.country }],
    ...countries.map((country) => [country.code, country])
  ].filter(([code]) => Boolean(code))).values());

  return (
    <section className="super-admin-edit-panel">
      <div className="section-title-row">
        <div>
          <p>Edit admin record</p>
          <h2>{record.name}</h2>
          <span>Update admin-facing corrections, moderation notes and routing context. Source directory data stays intact.</span>
        </div>
        <button type="button" onClick={onCancel}>Close</button>
      </div>
      {record.scope === "Centre" && !canVerify && (
        <div className="event-checkout-note">
          <ShieldCheck size={20} />
          <span>Directory centres can be reviewed or restricted, but they become verified only after a centre owner creates or claims that centre.</span>
        </div>
      )}
      <div className="super-admin-edit-grid">
        <label>
          <span>Name</span>
          <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
        </label>
        <label>
          <span>Owner or location</span>
          <input value={form.owner} onChange={(event) => setForm((current) => ({ ...current, owner: event.target.value }))} />
        </label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
            {uniqueStatusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        <label>
          <span>Country</span>
          <select value={form.countryCode} onChange={(event) => setForm((current) => ({ ...current, countryCode: event.target.value }))}>
            {countryOptions.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
          </select>
        </label>
        <label className="wide">
          <span>Details</span>
          <textarea value={form.detail} onChange={(event) => setForm((current) => ({ ...current, detail: event.target.value }))} />
        </label>
      </div>
      <div className="super-admin-edit-actions">
        <button type="button" onClick={() => onSave(record, form)}>Save updates</button>
        {update && <button type="button" onClick={() => onReset(record)}>Reset edits</button>}
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function SuperAdminPlayerImage({ src = "", name = "Player" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) return <PlayerInitialsMedia name={name} />;
  return (
    <img
      src={src}
      alt={`${name} player profile`}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

const getAdminPlayerPublicationState = (record = {}) => {
  const player = record.payload?.player || {};
  const internalTest = compactLocationText(record.name) === "badmintamultiroletest";
  const missing = getPublicProfileMissingFields({ fullName: record.name, roles: ["player"], player }, "player");
  const statusValues = [player.status, player.backendStatus]
    .map((value) => String(value || "").toLowerCase());
  const superseded = statusValues.some((status) => ["superseded", "archived", "removed", "deleted"].includes(status));
  const backendPublished = record.payload?.progress?.published === true
    || player.public === true
    || player.publicProfilePublished === true
    || statusValues.some((status) => ["published", "active"].includes(status));
  return {
    backendPublished,
    missing,
    internalTest,
    superseded,
    published: !internalTest && !superseded && backendPublished && missing.length === 0
  };
};

function PlayerIntegrityRepairControl({ onRefresh }) {
  const [recovery, setRecovery] = useState({ status: "idle", eligibleCount: 0, invalidPublicCount: 0, locationRepairCount: 0, supersededCount: 0, message: "" });
  const busy = recovery.status === "loading" || recovery.status === "publishing";
  const checkRecords = async () => {
    setRecovery({ status: "loading", eligibleCount: 0, invalidPublicCount: 0, locationRepairCount: 0, supersededCount: 0, message: "Checking player publication, country, suburb and discovery records..." });
    try {
      const result = await callMarketplaceFunction("recoverEligibleLegacyPlayerDrafts", { limit: 150, confirm: false });
      const eligibleCount = Number(result.eligibleCount || 0);
      const invalidPublicCount = Number(result.invalidPublicCount || 0);
      const locationRepairCount = Number(result.privateLocationRepairCount || 0);
      const supersededCount = Number(result.supersededCount || 0);
      const actionCount = eligibleCount + invalidPublicCount + locationRepairCount + supersededCount;
      setRecovery({
        status: actionCount ? "ready" : "complete",
        eligibleCount,
        invalidPublicCount,
        locationRepairCount,
        supersededCount,
        message: actionCount
          ? `${eligibleCount} player profile${eligibleCount === 1 ? " is" : "s are"} ready for publication or discovery repair. ${supersededCount} older duplicate record${supersededCount === 1 ? " will" : "s will"} be retained as superseded audit history. ${locationRepairCount} private record${locationRepairCount === 1 ? " needs" : "s need"} country or suburb normalisation.`
          : "Player publication, country, suburb and discovery records are already consistent."
      });
    } catch (error) {
      setRecovery({ status: "error", eligibleCount: 0, invalidPublicCount: 0, locationRepairCount: 0, supersededCount: 0, message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const repairRecords = async () => {
    setRecovery((current) => ({ ...current, status: "publishing", message: "Repairing complete profiles and protecting incomplete public records..." }));
    try {
      const result = await callMarketplaceFunction("recoverEligibleLegacyPlayerDrafts", { limit: 150, confirm: true });
      const publishedCount = Number(result.publishedCount || 0);
      const repairedCount = Number(result.repairedCount || publishedCount);
      const locationRecoveredCount = Number(result.locationRecoveredCount || 0);
      const locationNormalisedCount = Number(result.locationNormalisedCount || 0);
      const unpublishedCount = Number(result.unpublishedCount || 0);
      const supersededCount = Number(result.supersededCount || 0);
      setRecovery({
        status: "complete",
        eligibleCount: 0,
        invalidPublicCount: 0,
        locationRepairCount: 0,
        supersededCount: 0,
        message: `${repairedCount} player record${repairedCount === 1 ? " was" : "s were"} repaired; ${publishedCount} moved from draft to live, ${supersededCount} older duplicate record${supersededCount === 1 ? " was" : "s were"} marked as superseded, ${locationRecoveredCount} recovered a discovery location, and ${locationNormalisedCount} corrected country or suburb data.`
      });
      await onRefresh?.();
    } catch (error) {
      setRecovery((current) => ({ ...current, status: "error", message: marketplaceFunctionErrorMessage(error) }));
    }
  };
  useEffect(() => {
    checkRecords();
  }, []);
  const actionCount = recovery.eligibleCount + recovery.invalidPublicCount + recovery.locationRepairCount + recovery.supersededCount;

  return (
    <div className="admin-legacy-draft-recovery admin-player-integrity-repair">
      <div>
        <strong>Player publication and discovery repair</strong>
        <span>Publish every saved player account, keep private contact details protected, and repair country and suburb fields. Local area and playing level improve discovery but no longer trap a named profile in draft.</span>
        {recovery.message && <small className={recovery.status}>{recovery.message}</small>}
      </div>
      <div>
        <button type="button" onClick={checkRecords} disabled={busy}>
          {recovery.status === "loading" ? <Loader2 size={15} className="spin" /> : <Search size={15} />}
          Check player records
        </button>
        {actionCount > 0 && (
          <button type="button" className="primary" onClick={repairRecords} disabled={busy}>
            {recovery.status === "publishing" ? <Loader2 size={15} className="spin" /> : <BadgeCheck size={15} />}
            Repair {actionCount} profile{actionCount === 1 ? "" : "s"}
          </button>
        )}
      </div>
    </div>
  );
}

function SuperAdminPlayerProfileGallery({ records = [], query = "", navigate, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleLimit, setVisibleLimit] = useState(12);
  const matchingRecords = records
    .filter((record) => adminRecordMatchesQuery(record, query))
    .sort((first, second) => String(
      second.authCreatedAt
      || second.payload?.account?.firestoreUpdatedAt
      || second.payload?.player?.updatedAt
      || ""
    ).localeCompare(String(
      first.authCreatedAt
      || first.payload?.account?.firestoreUpdatedAt
      || first.payload?.player?.updatedAt
      || ""
    )));
  const publicationCounts = matchingRecords.reduce((counts, record) => {
    const state = getAdminPlayerPublicationState(record);
    if (state.internalTest) counts.test += 1;
    else if (state.superseded) counts.superseded += 1;
    else if (state.published) counts.published += 1;
    else if (state.missing.length) counts.incomplete += 1;
    else counts.draft += 1;
    return counts;
  }, { published: 0, incomplete: 0, draft: 0, superseded: 0, test: 0 });
  const filteredRecords = matchingRecords.filter((record) => {
    if (statusFilter === "all") return true;
    const state = getAdminPlayerPublicationState(record);
    if (statusFilter === "test") return state.internalTest;
    if (state.internalTest) return false;
    if (statusFilter === "published") return state.published;
    if (statusFilter === "incomplete") return !state.superseded && !state.published && state.missing.length > 0;
    if (statusFilter === "superseded") return state.superseded;
    return !state.superseded && !state.published && state.missing.length === 0;
  });
  const visibleRecords = filteredRecords.slice(0, visibleLimit);

  useEffect(() => {
    setVisibleLimit(12);
  }, [query, statusFilter]);

  return (
    <section className="super-admin-panel super-admin-player-panel">
      <div className="section-title-row">
        <div>
          <p>Player profiles</p>
          <h2>{filteredRecords.length} profile{filteredRecords.length === 1 ? "" : "s"}</h2>
          <span>Open every live profile and identify location or playing-level details that still need enrichment. Named player accounts remain public while those optional discovery details are completed.</span>
        </div>
      </div>
      <div className="super-admin-player-summary" aria-label="Player publication summary">
        <article>
          <span>Live profiles</span>
          <strong>{publicationCounts.published}</strong>
          <small>Complete and publicly accessible</small>
        </article>
        <article className={publicationCounts.incomplete ? "needs-action" : ""}>
          <span>Incomplete</span>
          <strong>{publicationCounts.incomplete}</strong>
          <small>Missing a public display name</small>
        </article>
        <article>
          <span>Private drafts</span>
          <strong>{publicationCounts.draft}</strong>
          <small>Complete but not currently public</small>
        </article>
        <article>
          <span>Superseded</span>
          <strong>{publicationCounts.superseded}</strong>
          <small>Older duplicate records retained for audit</small>
        </article>
        <article>
          <span>Internal tests</span>
          <strong>{publicationCounts.test}</strong>
          <small>Excluded from public user totals</small>
        </article>
      </div>
      <div className="super-admin-player-filters" role="group" aria-label="Filter player profiles by publication state">
        {[
          ["all", "All profiles", matchingRecords.length],
          ["published", "Live", publicationCounts.published],
          ["incomplete", "Incomplete", publicationCounts.incomplete],
          ["draft", "Private drafts", publicationCounts.draft],
          ["superseded", "Superseded", publicationCounts.superseded],
          ["test", "Internal tests", publicationCounts.test]
        ].map(([value, label, count]) => (
          <button
            type="button"
            key={value}
            className={statusFilter === value ? "active" : ""}
            aria-pressed={statusFilter === value}
            onClick={() => setStatusFilter(value)}
          >
            <span>{label}</span>
            <strong>{count}</strong>
          </button>
        ))}
      </div>
      <PlayerIntegrityRepairControl onRefresh={onRefresh} />
      {visibleRecords.length ? (
        <div className="super-admin-player-grid">
          {visibleRecords.map((record) => {
            const player = record.payload?.player || {};
            const image = getPublicProfileImage(player.photoUrl, player.image, approvedPublicMediaLinks(player.mediaLinks, "Player media"));
            const mediaItems = [...new Set([
              ...collectImageUrls([player.photoUrl, player.image]),
              ...cleanLinkRows(player.mediaLinks, "Player media").map((item) => item.url)
            ].filter(Boolean))];
            const profileHref = routeHref("player", { player });
            const publicationState = getAdminPlayerPublicationState(record);
            const publicationMissing = publicationState.missing;
            const isPublished = publicationState.published;
            const isSuperseded = publicationState.superseded;
            const isInternalTest = publicationState.internalTest;
            const locationParts = [player.suburb, player.city, record.country].filter(Boolean);
            const locationLabel = locationParts
              .filter((part, index) => locationParts.findIndex((candidate) => compactLocationText(candidate) === compactLocationText(part)) === index)
              .join(", ") || "Location not published";
            return (
              <article className="super-admin-player-card" key={record.id}>
                <div className="super-admin-player-media">
                  <SuperAdminPlayerImage src={image} name={record.name} />
                  <span>{isPublished ? "Published" : isInternalTest ? "Internal test" : isSuperseded ? "Superseded" : publicationMissing.length ? "Incomplete draft" : "Draft"}</span>
                </div>
                <div className="super-admin-player-copy">
                  <small>{publicationMissing.length
                    ? `Missing ${publicationMissing.join(", ").toLowerCase()}`
                    : isInternalTest ? "Internal QA account excluded from public profile totals"
                    : isSuperseded ? "Older duplicate retained for audit history"
                    : [player.skillBand || player.band, player.formats].filter(Boolean).join(" · ") || "Ready for publication"}</small>
                  <strong>{record.name}</strong>
                  <p><MapPin size={14} /> {locationLabel}</p>
                  <div>
                    <span>{player.rating || "New"} rating</span>
                    <span>{Number(player.plays || 0)} verified play{Number(player.plays || 0) === 1 ? "" : "s"}</span>
                    <span>{mediaItems.length} media item{mediaItems.length === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <div className="super-admin-player-actions">
                  {isPublished && (
                    <a href={profileHref} target="_blank" rel="noopener noreferrer">
                      View public profile <ExternalLink size={15} />
                    </a>
                  )}
                  {!isSuperseded && (
                    <button type="button" className={!isPublished ? "primary" : ""} onClick={() => navigate("player", { player })}>
                      {isPublished ? "Open here" : "Preview draft"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>No matching player profiles</strong>
          <span>Published and draft player profile documents appear here as soon as they are saved to the platform ledger.</span>
        </div>
      )}
      {filteredRecords.length > 12 && (
        <div className="super-admin-pagination">
          <span>Showing {visibleRecords.length} of {filteredRecords.length} matching profiles</span>
          <div>
            {visibleLimit > 12 && <button type="button" onClick={() => setVisibleLimit(12)}>Show fewer</button>}
            {visibleRecords.length < filteredRecords.length && (
              <button type="button" className="primary" onClick={() => setVisibleLimit((current) => current + 12)}>
                Show next {Math.min(12, filteredRecords.length - visibleRecords.length)}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

const adminRoleStepTotals = {
  player: 2,
  organiser: 4,
  centre: 3,
  coach: 3
};

const adminOnboardingStatusLabels = {
  phone_verified_only: "Stopped after phone verification",
  account_details_incomplete: "Account details incomplete",
  profile_onboarding_incomplete: "Profile setup unfinished",
  profiles_ready_to_publish: "Required details complete · ready to publish",
  profiles_complete: "Selected profiles complete"
};

const getAdminAccountSignupProgress = (account = {}) => {
  if (account.signupProgress?.totalSteps) return account.signupProgress;
  const rolesSelected = Boolean(account.roles?.length);
  const accountDetailsComplete = Boolean(account.fullName && account.email);
  return {
    completedSteps: 1 + (rolesSelected ? 1 : 0) + (accountDetailsComplete ? 1 : 0),
    totalSteps: 3,
    phoneVerified: Boolean(account.phoneNumber),
    rolesSelected,
    accountDetailsComplete,
    onboardingStatus: !rolesSelected
      ? "phone_verified_only"
      : !accountDetailsComplete
        ? "account_details_incomplete"
        : "profile_onboarding_incomplete"
  };
};

const getAdminAccountRoleProgress = (account = {}, role) => {
  if (account.roleProgress?.[role]?.totalSteps) return account.roleProgress[role];
  const selected = Boolean(account.roles?.includes(role));
  const missingFields = getRoleOnboardingMissingFields(account, role);
  const totalSteps = adminRoleStepTotals[role] || Math.max(1, missingFields.length);
  const roleProfile = role === "organiser" ? account.group || {} : account[role] || {};
  const profileId = roleProfile.publicPlayerId
    || roleProfile.publicCoachId
    || roleProfile.publicGroupId
    || roleProfile.serverCentreId
    || roleProfile.firestoreSourceId
    || "";
  const name = role === "player" || role === "coach"
    ? account.fullName || account.displayName || roleTitle(role)
    : roleProfile.name || account.fullName || roleTitle(role);
  return {
    role,
    selected,
    started: roleProfileStarted(account, role),
    published: roleHasPublishedBackendProfile(account, role),
    readyToPublish: selected && !roleHasPublishedBackendProfile(account, role) && missingFields.length === 0,
    status: roleProfile.backendStatus || "draft",
    completedSteps: Math.max(0, totalSteps - missingFields.length),
    totalSteps,
    missingFields,
    profileId,
    publicEntity: profileId ? {
      ...roleProfile,
      id: profileId,
      name,
      fullName: name,
      title: roleProfile.name || name,
      public: roleHasPublishedBackendProfile(account, role),
      status: roleProfile.backendStatus || "draft"
    } : null
  };
};

const getAdminRolePublicLink = (role, progress = {}) => {
  if (!progress.published || !progress.publicEntity) return "";
  if (role === "player") return routeHref("player", { player: progress.publicEntity });
  if (role === "coach") return routeHref("coach", { coach: progress.publicEntity });
  if (role === "organiser") return routeHref("group", { group: progress.publicEntity });
  if (role === "centre") return routeHref("venue", { venue: progress.publicEntity });
  return "";
};

function SuperAdminSignupAccountsPanel({ records = [], query = "", selectedCountryLabel = "All countries", ledger = {}, onRefresh }) {
  const [privacyScrub, setPrivacyScrub] = useState({ status: "idle", processedCount: 0, message: "" });
  const [visibleLimit, setVisibleLimit] = useState(12);
  const filteredRecords = records
    .filter((record) => adminRecordMatchesQuery(record, query))
    .sort((first, second) => String(second.authCreatedAt || "").localeCompare(String(first.authCreatedAt || "")));
  const accounts = filteredRecords.map((record) => record.payload?.account || {}).filter((account) => account.uid || account.id);
  const rolesSelected = accounts.filter((account) => getAdminAccountSignupProgress(account).rolesSelected).length;
  const detailsComplete = accounts.filter((account) => getAdminAccountSignupProgress(account).accountDetailsComplete).length;
  const authOnly = accounts.filter((account) => account.authOnly || account.hasFirestoreAccount === false).length;
  const publishedAccounts = accounts.filter((account) => account.publishedRoleCount > 0
    || Object.values(account.roleProgress || {}).some((progress) => progress.published)).length;
  const readyToPublish = accounts.filter((account) => getAdminAccountSignupProgress(account).onboardingStatus === "profiles_ready_to_publish"
    || Object.values(account.roleProgress || {}).some((progress) => progress.readyToPublish)).length;
  const coreIncomplete = accounts.filter((account) => ["phone_verified_only", "account_details_incomplete"].includes(getAdminAccountSignupProgress(account).onboardingStatus)).length;
  const profileSetupIncompleteAccounts = accounts.filter((account) => getAdminAccountSignupProgress(account).onboardingStatus === "profile_onboarding_incomplete");
  const firstProfilesPending = profileSetupIncompleteAccounts.filter((account) => !(account.publishedRoleCount > 0
    || Object.values(account.roleProgress || {}).some((progress) => progress.published))).length;
  const additionalRolesPending = profileSetupIncompleteAccounts.length - firstProfilesPending;
  const visibleRecords = filteredRecords.slice(0, visibleLimit);
  const busy = ledger.status === "loading" || ledger.status === "refreshing";
  const privacyScrubBusy = privacyScrub.status === "loading";

  useEffect(() => {
    setVisibleLimit(12);
  }, [query, selectedCountryLabel]);
  const scrubPublicProfilePrivacy = async () => {
    const targets = ["players", "coaches", "groups", "groupSessions", "centres", "events", "reviews", "memberships"];
    let processedCount = 0;
    let migratedInviteCount = 0;
    setPrivacyScrub({ status: "loading", processedCount: 0, message: "Removing private fields from public marketplace records..." });
    try {
      for (const target of targets) {
        let cursor = "";
        let pages = 0;
        do {
          const result = await callMarketplaceFunction("scrubPublicMarketplacePrivateFields", { target, cursor, limit: 150 });
          processedCount += Number(result.processedCount || 0);
          migratedInviteCount += Number(result.migratedInviteCount || 0);
          cursor = result.complete ? "" : result.nextCursor || "";
          pages += 1;
          setPrivacyScrub({
            status: "loading",
            processedCount,
            message: `Privacy scrub in progress: ${processedCount} records checked across ${target}.`
          });
          if (pages > 250) throw new Error(`Privacy scrub pagination did not finish for ${target}.`);
        } while (cursor);
      }
      setPrivacyScrub({
        status: "complete",
        processedCount,
        message: `${processedCount} public marketplace records checked. ${migratedInviteCount} private invite email${migratedInviteCount === 1 ? " was" : "s were"} retained in owner-only storage.`
      });
      await onRefresh?.();
    } catch (error) {
      setPrivacyScrub({ status: "error", processedCount, message: marketplaceFunctionErrorMessage(error) });
    }
  };

  return (
    <section className="super-admin-panel admin-signup-ledger">
      <div className="section-title-row">
        <div>
          <p>Firebase Authentication signups</p>
          <h2>{accounts.length} Badminta account{accounts.length === 1 ? "" : "s"} in {selectedCountryLabel}</h2>
          <span>Every Badminta phone signup appears here, including people who verified their phone but stopped before a Firestore account or public profile was saved. Identities belonging to other apps in the shared Firebase project stay excluded.</span>
        </div>
        <button type="button" className="admin-signup-refresh" onClick={onRefresh} disabled={busy}>
          <Loader2 size={16} className={busy ? "spin" : ""} /> {busy ? "Loading accounts" : "Refresh signups"}
        </button>
      </div>
      <div className="admin-signup-summary">
        {[
          ["Badminta accounts", accounts.length, "Matched to Badminta signup evidence"],
          ["Roles selected", rolesSelected, "Chose at least one user type"],
          ["Details saved", detailsComplete, "Name and email are in Firestore"],
          ["Published", publishedAccounts, "At least one public role profile"],
          ["Ready to publish", readyToPublish, "Required details are complete; one publish action remains"],
          ["Auth only", authOnly, "No Firestore account document yet"],
          ["Core incomplete", coreIncomplete, "Phone, role or account details remain"],
          ["First profile unfinished", firstProfilesPending, "No public role profile is live yet"],
          ["Additional roles unfinished", additionalRolesPending, "At least one profile is already live; only other selected roles remain"]
        ].map(([label, value, text]) => (
            <article key={label} className={["Ready to publish", "Auth only", "Core incomplete", "First profile unfinished", "Additional roles unfinished"].includes(label) && value ? "needs-action" : ""}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{text}</em>
          </article>
        ))}
      </div>
      <div className="admin-legacy-draft-recovery admin-public-privacy-scrub">
        <div>
          <strong>Public profile privacy</strong>
          <span>Keep phone numbers, owner emails, invite lists and internal audit fields out of anonymously readable marketplace profiles.</span>
          {privacyScrub.message && <small className={privacyScrub.status}>{privacyScrub.message}</small>}
        </div>
        <div>
          <button type="button" onClick={scrubPublicProfilePrivacy} disabled={busy || privacyScrubBusy}>
            {privacyScrubBusy ? <Loader2 size={15} className="spin" /> : <ShieldCheck size={15} />}
            {privacyScrubBusy ? "Checking public records" : "Run privacy scrub"}
          </button>
        </div>
      </div>
      {ledger.message && (
        <div className={`admin-signup-ledger-state ${ledger.status || "idle"}`}>
          {busy && <Loader2 size={16} className="spin" />}
          {!busy && ledger.status === "ready" && <BadgeCheck size={16} />}
          {!busy && ledger.status === "error" && <CircleHelp size={16} />}
          <span>{ledger.message}</span>
        </div>
      )}
      {visibleRecords.length ? (
        <div className="admin-signup-account-grid">
          {visibleRecords.map((record) => {
            const account = record.payload?.account || {};
            const signup = getAdminAccountSignupProgress(account);
            const signupPercent = Math.round((Number(signup.completedSteps || 0) / Math.max(1, Number(signup.totalSteps || 3))) * 100);
            const roleIds = Array.from(new Set([
              ...(account.roles || []),
              ...Object.entries(account.roleProgress || {}).filter(([, progress]) => progress?.started).map(([role]) => role)
            ])).filter((role) => accountRoleOptions.some((option) => option.id === role));
            const initials = String(account.fullName || account.displayName || account.phoneNumber || "B")
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase())
              .join("") || "B";
            const attribution = account.marketingAttribution || {};
            return (
              <article className="admin-signup-account-card" key={record.id}>
                <header>
                  <span className="admin-signup-avatar">{initials}</span>
                  <div>
                    <strong>{record.name}</strong>
                    <span>{account.phoneNumber || "Phone not available"}</span>
                    <span>{account.email || "Email not saved"}</span>
                  </div>
                  <div className="admin-signup-badges">
                    <span className="verified"><BadgeCheck size={13} /> {account.phoneNumber ? "Phone verified" : "Firebase verified"}</span>
                    {(account.authOnly || account.hasFirestoreAccount === false) && <span className="warning">Auth only</span>}
                    {account.disabled && <span className="danger">Disabled</span>}
                  </div>
                </header>
                <div className="admin-signup-stage">
                  <div>
                    <strong>Core signup</strong>
                    <span>{signup.completedSteps || 0}/{signup.totalSteps || 3} steps · {adminOnboardingStatusLabels[signup.onboardingStatus] || "Signup in progress"}</span>
                  </div>
                  <div className="admin-signup-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={signupPercent}>
                    <i style={{ width: `${signupPercent}%` }} />
                  </div>
                  <div className="admin-signup-core-steps">
                    {[
                      ["Phone verified", signup.phoneVerified !== false],
                      ["User types selected", signup.rolesSelected],
                      ["Name and email saved", signup.accountDetailsComplete]
                    ].map(([label, complete]) => (
                      <span key={label} className={complete ? "complete" : "missing"}>
                        {complete ? <Check size={13} /> : <CircleHelp size={13} />} {label}
                      </span>
                    ))}
                  </div>
                </div>
                {roleIds.length ? (
                  <div className="admin-signup-role-list">
                    {roleIds.map((role) => {
                      const progress = getAdminAccountRoleProgress(account, role);
                      const roleOption = accountRoleOptions.find((option) => option.id === role);
                      const RoleIcon = roleOption?.icon || UserCircle;
                      const publicHref = getAdminRolePublicLink(role, progress);
                      const percent = Math.round((Number(progress.completedSteps || 0) / Math.max(1, Number(progress.totalSteps || 1))) * 100);
                      return (
                        <div className="admin-signup-role" key={role}>
                          <div className="admin-signup-role-head">
                            <span><RoleIcon size={16} /></span>
                            <div>
                              <strong>{roleOption?.title || roleTitle(role)}</strong>
                              <small>{progress.completedSteps || 0}/{progress.totalSteps || 0} steps complete</small>
                            </div>
                            <em className={progress.published ? "published" : progress.readyToPublish ? "ready-to-publish" : progress.started ? "draft" : "not-started"}>
                              {progress.published ? "Published" : progress.readyToPublish ? "Ready to publish" : progress.started ? "Draft" : "Not started"}
                            </em>
                          </div>
                          <div className="admin-signup-progress compact" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={percent}>
                            <i style={{ width: `${percent}%` }} />
                          </div>
                          {progress.missingFields?.length ? (
                            <p>Missing: {progress.missingFields.slice(0, 3).join(", ")}{progress.missingFields.length > 3 ? ` +${progress.missingFields.length - 3} more` : ""}</p>
                          ) : (
                            <p className="complete"><Check size={13} /> {progress.published ? "All required profile fields are complete." : "Required details are complete. The user can publish immediately."}</p>
                          )}
                          {publicHref && (
                            <a href={publicHref} target="_blank" rel="noopener noreferrer">
                              View public {role === "organiser" ? "group" : role} profile <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="admin-signup-stopped">
                    <CircleHelp size={18} />
                    <div>
                      <strong>No user type selected</strong>
                      <span>This person verified with Firebase but did not continue to player, group, centre or coach onboarding.</span>
                    </div>
                  </div>
                )}
                <footer>
                  <div>
                    <span>Created {formatAdminDateTime(account.authCreatedAt) || "date unavailable"}</span>
                    <span>Last sign-in {formatAdminDateTime(account.lastSignInAt) || "not recorded"}</span>
                  </div>
                  {(attribution.utm_source || attribution.utm_campaign) && (
                    <span className="admin-signup-attribution">
                      {attribution.utm_source || "campaign"}{attribution.utm_campaign ? ` · ${attribution.utm_campaign}` : ""}
                    </span>
                  )}
                  <code title={account.uid || account.id}>{account.uid || account.id}</code>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>{busy ? "Loading Firebase signups" : "No matching Firebase accounts"}</strong>
          <span>{busy ? "The protected account ledger is merging Auth users with their saved onboarding and profile documents." : "Clear the search or country filter to see verified signups."}</span>
        </div>
      )}
      {filteredRecords.length > 12 && (
        <div className="super-admin-pagination">
          <span>Showing {visibleRecords.length} of {filteredRecords.length} matching accounts</span>
          <div>
            {visibleLimit > 12 && <button type="button" onClick={() => setVisibleLimit(12)}>Show fewer</button>}
            {visibleRecords.length < filteredRecords.length && (
              <button type="button" className="primary" onClick={() => setVisibleLimit((current) => current + 12)}>
                Show next {Math.min(12, filteredRecords.length - visibleRecords.length)}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function SuperAdminRecordTable({ title, text, records, decisions, updates = {}, onDecision, onUndoDecision, onClaimDecision, onUndoClaimDecision, onEditRecord, onResetUpdate, navigate, emptyTitle = "No records yet", query = "" }) {
  const [visibleLimit, setVisibleLimit] = useState(12);
  const filteredRecords = records.filter((record) => adminRecordMatchesQuery(record, query));
  const visibleRecords = filteredRecords.slice(0, visibleLimit);

  useEffect(() => {
    setVisibleLimit(12);
  }, [query, title]);
  return (
    <section className="super-admin-panel">
      <div className="section-title-row">
        <div>
          <p>{title}</p>
          <h2>{filteredRecords.length} record{filteredRecords.length === 1 ? "" : "s"}</h2>
          {text && <span>{text}</span>}
        </div>
      </div>
      {visibleRecords.length ? (
        <div className="super-admin-table">
          {visibleRecords.map((record) => {
            const hasDecision = Boolean(decisions[record.id]);
            const hasUpdate = Boolean(updates[record.id]);
            const canVerify = canAdminVerifyRecord(record);
            const isClaimRecord = isBackendClaimRecord(record);
            const claimCanUndo = isClaimRecord && (hasDecision || /approved|rejected|needs_more_info/i.test(record.status || ""));
            const claimApprovalBlocked = Boolean(isClaimRecord && record.claimApprovalBlocked && !/approved/i.test(record.status || ""));
            const canUndoUserRestriction = Boolean(record.backendUserRecord && record.restrictedUser);
            return (
              <article key={record.id}>
                <div>
                  <span>{record.scope} · {record.type} · {record.country || "Country unknown"}{hasUpdate ? " · edited" : ""}</span>
                  <strong>{record.name}</strong>
                  <p>{record.detail}</p>
                </div>
                <em>{getAdminDisplayStatus(record, decisions)}</em>
                <div className="super-admin-row-actions">
                  {record.target && <button type="button" onClick={() => navigate(record.target, record.payload || {})}>{record.openLabel || "Open"}</button>}
                  <button type="button" onClick={() => onEditRecord(record)}>Edit</button>
                  {record.editTarget && <button type="button" onClick={() => navigate(record.editTarget, record.payload || {})}>{record.editLabel || "Source"}</button>}
                  {isClaimRecord ? (
                    <>
                      <button type="button" onClick={() => onClaimDecision?.(record, "approved")} disabled={claimApprovalBlocked} title={claimApprovalBlocked ? record.claimApprovalReason : ""}>Approve claim</button>
                      <button type="button" onClick={() => onClaimDecision?.(record, "needs_more_info")}>More info</button>
                      <button type="button" onClick={() => onClaimDecision?.(record, "rejected")}>Reject claim</button>
                      {claimCanUndo && <button type="button" onClick={() => onUndoClaimDecision?.(record)}>Undo claim</button>}
                    </>
                  ) : (
                    <>
                      {canVerify && <button type="button" onClick={() => onDecision(record, "Verified")}>Verify</button>}
                      <button type="button" onClick={() => onDecision(record, "Needs review")}>Review</button>
                      {!record.restrictedUser && <button type="button" onClick={() => onDecision(record, "Restricted")}>Restrict</button>}
                      {(hasDecision || canUndoUserRestriction) && <button type="button" onClick={() => onUndoDecision(record)}>Undo</button>}
                    </>
                  )}
                  {hasUpdate && <button type="button" onClick={() => onResetUpdate(record)}>Reset edits</button>}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>{emptyTitle}</strong>
          <span>Records will appear here after real users, centres, events or moderation activity exist in the platform data.</span>
        </div>
      )}
      {filteredRecords.length > 12 && (
        <div className="super-admin-pagination">
          <span>Showing {visibleRecords.length} of {filteredRecords.length} matching records</span>
          <div>
            {visibleLimit > 12 && <button type="button" onClick={() => setVisibleLimit(12)}>Show fewer</button>}
            {visibleRecords.length < filteredRecords.length && (
              <button type="button" className="primary" onClick={() => setVisibleLimit((current) => current + 12)}>
                Show next {Math.min(12, filteredRecords.length - visibleRecords.length)}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function SuperAdminEmailCampaignPanel({ campaigns = [], templates = [], deliveryStats = [], selectedCountryCode = "", onRefresh }) {
  const [campaignRows, setCampaignRows] = useState(campaigns);
  const [form, setForm] = useState({
    name: "",
    templateKey: "profile_needs_photo",
    role: "all",
    incompleteProfilesOnly: false
  });
  const [actionState, setActionState] = useState({ status: "idle", message: "" });
  const emailTemplates = templates
    .filter((template) => template.channel === "email" && !String(template.templateKey || template.id).startsWith("_"))
    .sort((a, b) => String(a.title || a.templateKey).localeCompare(String(b.title || b.templateKey)));
  const campaignTemplates = emailTemplates
    .filter((template) => template.campaignEligible === true)
    .map((template) => [template.templateKey, template.title || template.description || template.templateKey]);
  const templateTotals = deliveryStats.reduce((totals, row) => {
    const key = row.templateKey;
    if (!key) return totals;
    totals[key] ||= { queued: 0, accepted: 0, sent: 0, failed: 0, deferred: 0 };
    totals[key].queued += Number(row.queuedCount || 0);
    totals[key].sent += Number(row.sentCount || 0);
    totals[key].accepted += Number(row.acceptedCount ?? row.sentCount ?? 0);
    totals[key].failed += Number(row.failedCount || 0);
    totals[key].deferred += Number(row.deferredCount || 0);
    return totals;
  }, {});
  useEffect(() => setCampaignRows(campaigns), [campaigns]);
  const saveDraft = async () => {
    if (!form.name.trim()) {
      setActionState({ status: "error", message: "Add a clear internal campaign name." });
      return;
    }
    setActionState({ status: "loading", message: "Saving campaign draft..." });
    try {
      const result = await callMarketplaceFunction("upsertEmailCampaign", {
        name: form.name.trim(),
        templateKey: form.templateKey,
        audience: {
          role: form.role,
          countryCode: selectedCountryCode || "",
          incompleteProfilesOnly: form.incompleteProfilesOnly
        },
        batchSize: 25
      });
      setActionState({ status: "ready", message: `Draft saved. ${result.campaignId} will not send until launched.` });
      setCampaignRows((current) => [{
        id: result.campaignId,
        name: form.name.trim(),
        templateKey: form.templateKey,
        templateTitle: campaignTemplates.find(([key]) => key === form.templateKey)?.[1] || form.templateKey,
        audience: result.audience,
        status: "draft",
        scannedCount: 0,
        queuedCount: 0,
        updatedAt: new Date().toISOString()
      }, ...current.filter((row) => row.id !== result.campaignId)]);
      setForm((current) => ({ ...current, name: "" }));
      await onRefresh?.();
    } catch (error) {
      setActionState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const updateCampaignStatus = async (campaignId, action) => {
    setActionState({ status: "loading", message: action === "launch" ? "Launching controlled campaign..." : "Cancelling campaign..." });
    try {
      const result = await callMarketplaceFunction(action === "launch" ? "launchEmailCampaign" : "cancelEmailCampaign", { campaignId });
      setActionState({ status: "ready", message: `Campaign ${result.status}.` });
      setCampaignRows((current) => current.map((row) => row.id === campaignId ? { ...row, status: result.status, updatedAt: new Date().toISOString() } : row));
      await onRefresh?.();
    } catch (error) {
      setActionState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  return (
    <section className="email-campaign-console" aria-label="Email campaigns">
      <div className="section-title-row">
        <div>
          <p>Email campaigns</p>
          <h2>Controlled audience automation</h2>
          <span>Draft first, review the audience, then launch. Only opted-in recipients are eligible and delivery remains subject to suppression, cooldown and weekly limits.</span>
        </div>
      </div>
      <div className="email-programme-heading">
        <div>
          <strong>Templates and automations</strong>
          <span>{emailTemplates.length} configured emails with all-time provider delivery totals.</span>
        </div>
        <em>{emailTemplates.filter((template) => (templateTotals[template.templateKey]?.sent || 0) > 0).length} have delivered</em>
      </div>
      <div className="email-automation-list">
        {emailTemplates.map((template) => {
          const totals = templateTotals[template.templateKey] || { queued: 0, sent: 0, failed: 0, deferred: 0 };
          const mode = template.automationMode === "scheduled_lifecycle"
            ? "Scheduled lifecycle"
            : template.automationMode === "event_triggered"
              ? "Event triggered"
              : "Campaign eligible";
          const active = template.status === "active" && template.deliveryEnabled !== false;
          return (
            <article key={template.templateKey || template.id}>
              <div className="email-automation-identity">
                <span>{template.category || "Email"} · {mode}</span>
                <strong>{template.title || template.templateKey}</strong>
                <small>{template.description || template.subject || template.templateKey}</small>
              </div>
              <div className="email-automation-delivery">
                <span><strong>{totals.sent.toLocaleString()}</strong> sent</span>
                <span>{totals.queued.toLocaleString()} queued</span>
                <span>{totals.failed.toLocaleString()} failed</span>
                <em className={active ? "live" : "configured"}>{active ? (totals.sent ? "Live · delivered" : "Live · waiting for trigger") : "Paused"}</em>
              </div>
            </article>
          );
        })}
      </div>
      <div className="email-programme-heading campaign-runs-heading">
        <div>
          <strong>Campaign runs</strong>
          <span>Audience sends created by an administrator or a system lifecycle process.</span>
        </div>
        <em>{campaignRows.length} total</em>
      </div>
      <div className="email-campaign-builder">
        <label>
          <span>Internal campaign name</span>
          <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Example: Complete player profiles - July" />
        </label>
        <label>
          <span>Email</span>
          <select value={form.templateKey} onChange={(event) => setForm((current) => ({ ...current, templateKey: event.target.value }))}>
            {campaignTemplates.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>User type</span>
          <select value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}>
            <option value="all">All opted-in users</option>
            <option value="player">Players</option>
            <option value="organiser">Group organisers</option>
            <option value="coach">Coaches</option>
            <option value="centre">Centre owners</option>
          </select>
        </label>
        <label className="email-campaign-toggle">
          <input type="checkbox" checked={form.incompleteProfilesOnly} onChange={(event) => setForm((current) => ({ ...current, incompleteProfilesOnly: event.target.checked }))} />
          <span>Only profiles not yet published</span>
        </label>
        <button type="button" onClick={saveDraft} disabled={actionState.status === "loading"}>Save campaign draft</button>
      </div>
      {actionState.message && <div className={`admin-action-message ${actionState.status}`}>{actionState.message}</div>}
      <div className="email-campaign-list">
        {campaignRows.length ? campaignRows.slice().sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))).slice(0, 12).map((campaign) => (
          <article key={campaign.id}>
            <div>
              <span>{campaign.templateTitle || campaign.templateKey}</span>
              <strong>{campaign.name}</strong>
              <small>{campaign.audience?.role || "all"} · {campaign.audience?.countryCode || "Worldwide"} · {campaign.audience?.incompleteProfilesOnly ? "Incomplete profiles" : "All eligible profiles"}</small>
            </div>
            <div className="email-campaign-counts">
              <span><strong>{Number(campaign.scannedCount || 0).toLocaleString()}</strong> scanned</span>
              <span><strong>{Number(campaign.queuedCount || 0).toLocaleString()}</strong> queued</span>
              <em>{campaign.status || "draft"}</em>
            </div>
            <div className="email-campaign-actions">
              {["draft", "cancelled"].includes(String(campaign.status || "draft").toLowerCase()) && (
                <button type="button" onClick={() => updateCampaignStatus(campaign.id, "launch")}>Launch</button>
              )}
              {!["completed", "cancelled"].includes(String(campaign.status || "").toLowerCase()) && (
                <button type="button" className="secondary" onClick={() => updateCampaignStatus(campaign.id, "cancel")}>Cancel</button>
              )}
            </div>
          </article>
        )) : (
          <div className="ops-empty compact"><strong>No campaigns created</strong><span>Saved drafts appear here before anything can be sent.</span></div>
        )}
      </div>
    </section>
  );
}

function SuperAdminSupportOperationsPanel({ selectedCountryLabel, selectedCountryCode = "", centreRecords = [], contentRecords = [], trustReports = [], openTrustReports = [], supportEvidence = [], notificationTemplates = [], notificationQueue = [], notificationReceipts = [], emailDeliveryStats = [], emailCampaigns = [], auditEntries = [], bookingHolds = [], bookings = [], sessionJoinRequests = [], lessonRequests = [], stripeAccounts = [], navigate, setActiveSection, onProcessNotification, onReleaseExpiredHolds, onReleaseExpiredReservations, onSeedTemplates, onTestNotificationWebhook, onRefresh }) {
  const refundReports = trustReports.filter((report) => /refund|payment|fee|charge|payout|stripe/i.test(`${report.title} ${report.subject} ${report.comment} ${report.detail}`));
  const claimRecords = centreRecords.filter((record) => /claimed|owner-created|verified owner|needs review|restricted|edited|updated/i.test(`${record.type} ${record.status} ${record.detail}`));
  const incompleteContent = contentRecords.filter((record) => /draft|incomplete|review|payment|price|refund/i.test(`${record.status} ${record.detail}`));
  const disputeEvidenceCount = supportEvidence.filter((item) => /refund|dispute|payment/i.test(`${item.evidenceType} ${item.retentionCategory} ${item.subjectCollection}`)).length;
  const evidenceDateMillis = (value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") return Date.parse(value) || 0;
    if (value.seconds) return value.seconds * 1000;
    return 0;
  };
  const retentionTaggedEvidence = supportEvidence.filter((item) => item.retentionPolicy && item.retainUntil);
  const retentionDueSoon = retentionTaggedEvidence.filter((item) => {
    const retainUntilMs = evidenceDateMillis(item.retainUntil);
    return retainUntilMs && retainUntilMs <= Date.now() + 30 * 24 * 60 * 60 * 1000;
  });
  const openHoldRows = bookingHolds.filter((item) => !/released|expired|cancelled|canceled|paid|confirmed|refunded/i.test(`${item.status} ${item.lockStatus} ${item.paymentStatus}`));
  const expiredHoldRows = openHoldRows.filter((item) => {
    const lockedUntilMs = evidenceDateMillis(item.lockedUntil || item.expiresAt || item.checkoutExpiresAt);
    return lockedUntilMs && lockedUntilMs <= Date.now();
  });
  const pendingBookingRows = bookings.filter((item) => /pending|requested|hold|checkout/i.test(`${item.status} ${item.paymentStatus}`) && !/paid|confirmed|released|expired|cancelled|canceled|refunded/i.test(`${item.status} ${item.paymentStatus}`));
  const reservationRows = [...sessionJoinRequests, ...lessonRequests];
  const openReservationRows = reservationRows.filter((item) => /held|checkout|pending|requested|open/i.test(`${item.status} ${item.paymentStatus} ${item.reservationStatus}`) && !/paid|confirmed|released|expired|cancelled|canceled|refunded|disputed/i.test(`${item.status} ${item.paymentStatus} ${item.reservationStatus}`));
  const expiredReservationRows = openReservationRows.filter((item) => {
    const expiresAtMs = evidenceDateMillis(item.reservationExpiresAt || item.checkoutExpiresAt || item.expiresAt);
    return expiresAtMs && expiresAtMs <= Date.now();
  });
  const readyStripeAccountRows = stripeAccounts.filter(isStripeAccountCheckoutReady);
  const stripeAccountReviewRows = stripeAccounts.filter(isStripeAccountNeedsReview);
  const queuedNotifications = notificationQueue.filter((item) => /queued|pending/i.test(`${item.status} ${item.deliveryStatus}`));
  const failedRefundFollowUps = notificationQueue.filter((item) => item.templateKey === "refund_failed_follow_up" || /refund failed|failed refund/i.test(`${item.templateTitle} ${item.notificationSubject}`));
  const failedNotificationReceipts = notificationReceipts.filter((item) => /failed|bounced|complained|rejected|undelivered|unmatched/i.test(`${item.status} ${item.deliveryStatus} ${item.receiptStatus}`));
  const seededTemplateKeys = new Set(notificationTemplates.map((item) => item.templateKey || item.id).filter(Boolean));
  const seededOperationalTemplateCount = operationalNotificationTemplateKeys.filter((key) => seededTemplateKeys.has(key)).length;
  const missingOperationalTemplateCount = Math.max(0, operationalNotificationTemplateKeys.length - seededOperationalTemplateCount);
  const todayKey = new Date().toISOString().slice(0, 10);
  const visibleEmailStats = emailDeliveryStats.filter((row) => {
    if (row.dateKey !== todayKey) return false;
    if (!selectedCountryCode || selectedCountryCode === "all") return true;
    return !row.countryCode || normaliseCountryCode(row.countryCode) === normaliseCountryCode(selectedCountryCode);
  });
  const todayEmailTotals = visibleEmailStats.reduce((totals, row) => ({
    queued: totals.queued + Number(row.queuedCount || 0),
    accepted: totals.accepted + Number(row.acceptedCount ?? row.sentCount ?? 0),
    sent: totals.sent + Number(row.sentCount || 0),
    failed: totals.failed + Number(row.failedCount || 0),
    deferred: totals.deferred + Number(row.deferredCount || 0)
  }), { queued: 0, accepted: 0, sent: 0, failed: 0, deferred: 0 });
  const topEmailTemplates = [...visibleEmailStats]
    .sort((a, b) => Number(b.sentCount || 0) - Number(a.sentCount || 0))
    .slice(0, 8);
  const openSection = (section) => setActiveSection?.(section);
  const supportLanes = [
    {
      title: "Refund and dispute desk",
      metric: refundReports.length,
      meta: `${refundReports.length} payment-related report${refundReports.length === 1 ? "" : "s"}`,
      text: "Review cancellations, no-shows, payment evidence, chargebacks and refund decisions from one queue.",
      Icon: WalletCards,
      actions: [["Trust queue", () => openSection("trust")], ["Refund policy", () => navigate("refundPolicy")], ["Messages", () => navigate("messages")]]
    },
    {
      title: "Centre claim verification",
      metric: claimRecords.length,
      meta: `${claimRecords.length} claim/review record${claimRecords.length === 1 ? "" : "s"} of ${centreRecords.length} centre${centreRecords.length === 1 ? "" : "s"}`,
      text: "Only owner-created or claimed centres can become verified and accept in-platform bookings.",
      Icon: ShieldCheck,
      actions: [["Centre records", () => openSection("centres")], ["Claim search", () => navigate("centreClaimSearch")], ["Centre terms", () => navigate("centrePolicy")]]
    },
    {
      title: "Safety and moderation",
      metric: openTrustReports.length,
      meta: `${openTrustReports.length} open trust report${openTrustReports.length === 1 ? "" : "s"}`,
      text: "Handle unsafe behaviour, wrong level reports, media moderation and duplicate or incorrect listings.",
      Icon: BadgeCheck,
      actions: [["Moderation", () => navigate("trust")], ["Safety policy", () => navigate("communityPolicy")], ["Audit log", () => openSection("audit")]]
    },
    {
      title: "Groups and events support",
      metric: incompleteContent.length,
      meta: `${incompleteContent.length} content record${incompleteContent.length === 1 ? "" : "s"} needing review`,
      text: "Check organiser sessions, event publishing, pricing, refund rules and attendee support readiness.",
      Icon: CalendarDays,
      actions: [["Events/groups", () => openSection("content")], ["Create event", () => navigate("createEvent")], ["Organiser terms", () => navigate("organiserPolicy")]]
    },
	    {
	      title: "Booking holds",
	      metric: expiredHoldRows.length || openHoldRows.length || pendingBookingRows.length,
	      meta: `${expiredHoldRows.length} expired · ${openHoldRows.length} open · ${pendingBookingRows.length} pending booking${pendingBookingRows.length === 1 ? "" : "s"}`,
	      text: "Release unpaid expired court holds and reconcile closed booking locks so abandoned checkout does not block courts.",
	      Icon: Clock3,
	      actions: [["Sweep expired holds", () => onReleaseExpiredHolds?.()], ["Centre records", () => openSection("centres")], ["Launch gate", () => openSection("launch")]]
	    },
	    {
	      title: "Marketplace reservations",
	      metric: expiredReservationRows.length || openReservationRows.length,
	      meta: `${expiredReservationRows.length} expired · ${openReservationRows.length} open social/event/coach hold${openReservationRows.length === 1 ? "" : "s"}`,
	      text: "Release unpaid expired social, event and coaching reservations so abandoned checkout does not block session capacity or lesson requests.",
	      Icon: Clock3,
	      actions: [["Sweep reservations", () => onReleaseExpiredReservations?.()], ["Events/groups", () => openSection("content")], ["Launch gate", () => openSection("launch")]]
	    },
	    {
	      title: "Stripe payout accounts",
	      metric: stripeAccountReviewRows.length || readyStripeAccountRows.length,
	      meta: `${readyStripeAccountRows.length} ready · ${stripeAccountReviewRows.length} need review`,
	      text: "Review checkout-blocked Connect accounts before payments, refunds or payout reconciliation continue.",
	      Icon: WalletCards,
	      actions: [["Finance", () => openSection("launch")], ["Payouts", () => navigate("payouts")], ["Security checks", () => openSection("security")]]
	    },
    {
      title: "Operational evidence",
      metric: supportEvidence.length || auditEntries.length,
      meta: `${supportEvidence.length} evidence record${supportEvidence.length === 1 ? "" : "s"} · ${auditEntries.length} audit action${auditEntries.length === 1 ? "" : "s"}`,
      text: `${disputeEvidenceCount} refund/payment evidence record${disputeEvidenceCount === 1 ? "" : "s"} available for chargebacks, claims, refunds and disputes.`,
      Icon: ClipboardCheck,
      actions: [["Audit log", () => openSection("audit")], ["Launch gate", () => openSection("launch")], ["Help centre", () => navigate("help")]]
    },
    {
      title: "Evidence retention",
      metric: retentionTaggedEvidence.length,
      meta: `${retentionTaggedEvidence.length} of ${supportEvidence.length} evidence record${supportEvidence.length === 1 ? "" : "s"} have retention policy`,
      text: retentionDueSoon.length
        ? `${retentionDueSoon.length} evidence record${retentionDueSoon.length === 1 ? "" : "s"} need retention review within 30 days.`
        : "New support evidence is tagged with policy and retain-until dates for legal review and future disposal workflows.",
      Icon: ShieldCheck,
      actions: [["Privacy policy", () => navigate("privacyPolicy")], ["Audit log", () => openSection("audit")], ["Launch gate", () => openSection("launch")]]
    },
    {
      title: "Notification queue",
      metric: queuedNotifications.length || notificationReceipts.length,
      meta: `${queuedNotifications.length} queued · ${notificationReceipts.length} receipt${notificationReceipts.length === 1 ? "" : "s"}`,
      text: failedNotificationReceipts.length
        ? `${failedNotificationReceipts.length} provider receipt${failedNotificationReceipts.length === 1 ? "" : "s"} need delivery review.`
        : `Provider delivery receipts reconcile through notificationProviderReceiptWebhook. ${failedRefundFollowUps.length} failed-refund follow-up${failedRefundFollowUps.length === 1 ? "" : "s"} queued for support, owner or player communication.`,
      Icon: Bell,
      actions: [["Trust queue", () => openSection("trust")], ["Messages", () => navigate("messages")], ["Refunds", () => navigate("refundPolicy")]]
    },
    {
      title: "Operational templates",
      metric: seededOperationalTemplateCount,
      meta: `${seededOperationalTemplateCount}/${operationalNotificationTemplateKeys.length} template${operationalNotificationTemplateKeys.length === 1 ? "" : "s"} seeded`,
      text: missingOperationalTemplateCount
        ? `${missingOperationalTemplateCount} receipt, booking, claim, refund, moderation or payout template${missingOperationalTemplateCount === 1 ? "" : "s"} still need to be seeded.`
        : "Receipts, booking holds, cancellations, no-shows, claims, refunds, moderation and payout templates are available for support workflows.",
      Icon: Mail,
      actions: [["Seed templates", () => onSeedTemplates?.()], ["Trust queue", () => openSection("trust")], ["Launch gate", () => openSection("launch")]]
    }
  ];

  return (
    <section className="super-admin-panel admin-support-ops-panel">
      <div className="section-title-row">
        <div>
          <p>Support operations</p>
          <h2>Daily marketplace workflows for {selectedCountryLabel}.</h2>
          <span>Use these lanes to move from an issue type to the right queue, policy page, message thread or admin table.</span>
        </div>
      </div>
      <section className="email-operations-summary" aria-label="Email delivery today">
        <div className="email-operations-heading">
          <div>
            <span>Email operations</span>
            <h3>Today in {selectedCountryLabel}</h3>
          </div>
          <em>{todayKey}</em>
        </div>
        <div className="email-operations-metrics">
          {[
            ["Queued", todayEmailTotals.queued],
            ["SMTP accepted", todayEmailTotals.accepted ?? todayEmailTotals.sent],
            ["Deferred", todayEmailTotals.deferred],
            ["Failed", todayEmailTotals.failed]
          ].map(([label, value]) => <article key={label}><span>{label}</span><strong>{value.toLocaleString()}</strong></article>)}
        </div>
        {topEmailTemplates.length ? (
          <div className="email-template-performance">
            {topEmailTemplates.map((row) => (
              <article key={row.id || `${row.dateKey}-${row.templateKey}-${row.countryCode || "global"}`}>
                <div>
                  <strong>{row.templateTitle || row.templateKey}</strong>
                  <span>{[row.category, row.countryCode || "Global"].filter(Boolean).join(" · ")}</span>
                </div>
                <div>
                  <strong>{Number(row.acceptedCount ?? row.sentCount ?? 0).toLocaleString()} SMTP accepted</strong>
                  <span>{Number(row.queuedCount || 0).toLocaleString()} queued · {Number(row.failedCount || 0).toLocaleString()} failed</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="ops-empty compact">
            <strong>No email delivery recorded today</strong>
            <span>Queued, sent, deferred and failed totals will appear here automatically.</span>
          </div>
        )}
      </section>
      <SuperAdminEmailCampaignPanel
        campaigns={emailCampaigns}
        templates={notificationTemplates}
        deliveryStats={emailDeliveryStats}
        selectedCountryCode={selectedCountryCode}
        onRefresh={onRefresh}
      />
      <div className="notification-webhook-test">
        <div>
          <strong>Controlled delivery test</strong>
          <span>Send one controlled payload through the same server-side delivery path used by queued notifications.</span>
        </div>
        <button type="button" onClick={() => onTestNotificationWebhook?.("email")}>Test SMTP email</button>
        <button type="button" onClick={() => onTestNotificationWebhook?.("sms")}>Test SMS webhook</button>
      </div>
      <div className="admin-support-flow">
        {supportLanes.map(({ title, metric, meta, text, Icon, actions }) => (
          <article key={title} className={metric ? "active" : ""}>
            <div>
              <Icon size={20} />
              <span>{meta}</span>
            </div>
            <strong>{title}</strong>
            <p>{text}</p>
            <div className="admin-support-actions">
              {actions.map(([label, action]) => <button type="button" key={label} onClick={action}>{label}</button>)}
            </div>
          </article>
        ))}
      </div>
      {notificationQueue.length > 0 && (
        <div className="admin-notification-queue-list">
          <div className="form-section-head">
            <h3>Queued notifications</h3>
            <span>Process in-app messages now. Email and SMS provider delivery can be enabled later with the signed webhook and provider secrets.</span>
          </div>
          {notificationQueue.slice(0, 8).map((item) => {
            const queued = /queued|pending/i.test(`${item.status} ${item.deliveryStatus}`);
            return (
              <article key={item.id}>
                <div>
                  <strong>{item.notificationSubject || item.templateTitle || "Support notification"}</strong>
                  <span>{[item.channel, item.deliveryStatus || item.status, item.subjectLabel].filter(Boolean).join(" · ")}</span>
                </div>
                <button type="button" onClick={() => onProcessNotification?.(item)} disabled={!queued}>
                  {queued ? "Process" : item.deliveryStatus || item.status || "Processed"}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

const evidenceDateMillis = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") return Date.parse(value) || 0;
  if (value.seconds) return value.seconds * 1000;
  return 0;
};

const supportEvidenceRetentionDecisionOptions = [
  ["retain", "Retain"],
  ["legal_hold", "Legal hold"],
  ["archive_approved", "Archive approved"],
  ["deletion_approved", "Deletion approved"]
];

function SuperAdminEvidenceRetentionPanel({ selectedCountryLabel, countryCode, supportEvidence = [], onRecordRetentionDecision }) {
  const selectedCountryCode = countryCode === "all" ? "" : normaliseCountryCode(countryCode);
  const visibleEvidence = [...supportEvidence]
    .filter((item) => !selectedCountryCode || !item.countryCode || normaliseCountryCode(item.countryCode) === selectedCountryCode)
    .sort((a, b) => {
      const aDue = evidenceDateMillis(a.retainUntil) || Number.MAX_SAFE_INTEGER;
      const bDue = evidenceDateMillis(b.retainUntil) || Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue) return aDue - bDue;
      return String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || ""));
    });
  const firstEvidence = visibleEvidence[0] || {};
  const [form, setForm] = useState({
    evidenceId: firstEvidence.id || firstEvidence.evidenceId || "",
    decision: "retain",
    note: "",
    approvalReference: ""
  });
  const [state, setState] = useState({ status: "idle", message: "" });
  useEffect(() => {
    setForm((current) => {
      if (current.evidenceId && visibleEvidence.some((item) => (item.id || item.evidenceId) === current.evidenceId)) return current;
      const next = visibleEvidence[0] || {};
      return { ...current, evidenceId: next.id || next.evidenceId || "" };
    });
  }, [visibleEvidence.map((item) => item.id || item.evidenceId).join("|")]);
  const busy = state.status === "loading";
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selectedEvidence = visibleEvidence.find((item) => (item.id || item.evidenceId) === form.evidenceId) || firstEvidence;
  const retentionTaggedEvidence = visibleEvidence.filter((item) => item.retentionPolicy && item.retainUntil);
  const dueSoonEvidence = retentionTaggedEvidence.filter((item) => {
    const due = evidenceDateMillis(item.retainUntil);
    return due && due <= Date.now() + 30 * 24 * 60 * 60 * 1000;
  });
  const reviewedEvidence = visibleEvidence.filter((item) => item.retentionDecision || item.retentionDecisionStatus);
  const legalHoldEvidence = visibleEvidence.filter((item) => item.legalHold || item.retentionDecision === "legal_hold");
  const submitDecision = async () => {
    if (!form.evidenceId || !onRecordRetentionDecision) return;
    setState({ status: "loading", message: "Recording support evidence retention decision..." });
    try {
      const result = await onRecordRetentionDecision({
        evidenceId: form.evidenceId,
        decision: form.decision,
        note: form.note,
        approvalReference: form.approvalReference
      });
      setState({ status: "ready", message: `Retention decision recorded: ${result?.decision || form.decision}.` });
      setForm((current) => ({ ...current, note: "", approvalReference: "" }));
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const loadQuickDecision = (item, decision) => {
    setForm({
      evidenceId: item.id || item.evidenceId || "",
      decision,
      note: decision === "retain"
        ? `Reviewed ${item.title || "support evidence"} and retained under the current ${item.retentionPolicy || "support evidence"} policy.`
        : "",
      approvalReference: ""
    });
  };

  return (
    <section className="super-admin-panel evidence-retention-panel">
      <div className="section-title-row">
        <div>
          <p>Evidence retention decisions</p>
          <h2>Review support evidence lifecycle for {selectedCountryLabel}.</h2>
          <span>Retention decisions are recorded in the backend evidence ledger and admin audit log. Deletion approval marks evidence only; it does not delete Storage files.</span>
        </div>
      </div>
      <div className="evidence-retention-summary">
        {[
          ["Evidence rows", visibleEvidence.length, "Loaded support evidence"],
          ["Due within 30 days", dueSoonEvidence.length, "Retention date needs review"],
          ["Reviewed", reviewedEvidence.length, "Has a retention decision"],
          ["Legal holds", legalHoldEvidence.length, "Protected from archive/delete"]
        ].map(([label, value, text]) => (
          <article key={label} className={label === "Due within 30 days" && value ? "warning" : ""}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{text}</em>
          </article>
        ))}
      </div>
      {visibleEvidence.length ? (
        <>
          <div className="evidence-retention-form">
            <label>
              <span>Evidence</span>
              <select value={form.evidenceId} onChange={(event) => updateField("evidenceId", event.target.value)}>
                {visibleEvidence.slice(0, 50).map((item) => {
                  const id = item.id || item.evidenceId;
                  return <option key={id} value={id}>{item.title || item.subjectLabel || id}</option>;
                })}
              </select>
            </label>
            <label>
              <span>Decision</span>
              <select value={form.decision} onChange={(event) => updateField("decision", event.target.value)}>
                {supportEvidenceRetentionDecisionOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              <span>Approval reference</span>
              <input value={form.approvalReference} onChange={(event) => updateField("approvalReference", event.target.value)} placeholder="Legal ticket, founder approval or policy ref" />
            </label>
            <label className="wide">
              <span>Decision note</span>
              <textarea value={form.note} onChange={(event) => updateField("note", event.target.value)} placeholder="Why the evidence is being retained, placed on legal hold, approved for archive, or approved for deletion." />
            </label>
            <button type="button" onClick={submitDecision} disabled={busy || !form.evidenceId}>
              <ClipboardCheck size={15} /> {busy ? "Recording..." : "Record retention decision"}
            </button>
          </div>
          {(selectedEvidence?.id || selectedEvidence?.evidenceId) && (
            <div className="evidence-retention-selected">
              <strong>{selectedEvidence.title || selectedEvidence.subjectLabel || "Selected evidence"}</strong>
              <span>{[selectedEvidence.subjectCollection, selectedEvidence.evidenceType, selectedEvidence.retentionPolicy].filter(Boolean).join(" · ")}</span>
              <em>{selectedEvidence.retainUntil ? `Retain until ${formatAdminDateTime(selectedEvidence.retainUntil)}` : "Retain-until date pending"}</em>
            </div>
          )}
          <div className="evidence-retention-list">
            {visibleEvidence.slice(0, 8).map((item) => {
              const id = item.id || item.evidenceId;
              const decision = item.retentionDecision || item.retentionDecisionStatus || "pending";
              const due = evidenceDateMillis(item.retainUntil);
              const dueSoon = due && due <= Date.now() + 30 * 24 * 60 * 60 * 1000;
              return (
                <article key={id} className={dueSoon ? "warning" : ""}>
                  <div>
                    <strong>{item.title || item.subjectLabel || id}</strong>
                    <span>{[item.countryCode || "Global", item.subjectCollection, item.evidenceType, decision].filter(Boolean).join(" · ")}</span>
                  </div>
                  <p>{item.note || item.subjectLabel || "No evidence note recorded."}</p>
                  <small>{item.retentionPolicy || "Retention policy pending"}{item.retainUntil ? ` · Retain until ${formatAdminDateTime(item.retainUntil)}` : ""}</small>
                  <div>
                    <button type="button" onClick={() => loadQuickDecision(item, "retain")}>Retain</button>
                    <button type="button" onClick={() => loadQuickDecision(item, "legal_hold")}>Legal hold</button>
                    <button type="button" onClick={() => loadQuickDecision(item, "archive_approved")}>Archive</button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      ) : (
        <div className="ops-empty">
          <strong>No support evidence yet</strong>
          <span>Retention decisions will appear after backend support, claim, refund, booking or moderation evidence has been recorded.</span>
        </div>
      )}
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
    </section>
  );
}

const supportSlaPolicyKindOptions = [
  { id: "claims", label: "Centre claims", responseHours: 24, escalationHours: 48, resolutionHours: 72 },
  { id: "refunds", label: "Refund reviews", responseHours: 24, escalationHours: 48, resolutionHours: 96 },
  { id: "safety_reports", label: "Safety reports", responseHours: 12, escalationHours: 24, resolutionHours: 72 },
  { id: "payment_disputes", label: "Payment disputes", responseHours: 12, escalationHours: 24, resolutionHours: 120 },
  { id: "media_reviews", label: "Media reviews", responseHours: 24, escalationHours: 48, resolutionHours: 72 },
  { id: "booking_disputes", label: "Booking disputes", responseHours: 12, escalationHours: 24, resolutionHours: 72 },
  { id: "general_support", label: "General support", responseHours: 48, escalationHours: 72, resolutionHours: 168 }
];

function SuperAdminSupportSlaPolicyPanel({ selectedCountryLabel, countryCode, policies = [] }) {
  const selectedCountryCode = countryCode === "all" ? "" : normaliseCountryCode(countryCode);
  const defaultKind = supportSlaPolicyKindOptions[0];
  const [form, setForm] = useState({
    countryCode: selectedCountryCode,
    kind: defaultKind.id,
    label: defaultKind.label,
    ownerTeam: "Badminta support",
    ownerEmail: "support@badminta.com",
    responseHours: String(defaultKind.responseHours),
    escalationHours: String(defaultKind.escalationHours),
    resolutionHours: String(defaultKind.resolutionHours),
    escalationOwner: "Badminta support lead",
    status: "active",
    notes: "",
    overwriteSeed: false
  });
  const [state, setState] = useState({ status: "idle", message: "" });
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const busy = state.status === "loading" || state.status === "seeding";
  useEffect(() => {
    setForm((current) => ({ ...current, countryCode: selectedCountryCode }));
  }, [selectedCountryCode]);
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateKind = (kind) => {
    const defaults = supportSlaPolicyKindOptions.find((item) => item.id === kind) || defaultKind;
    setForm((current) => ({
      ...current,
      kind,
      label: defaults.label,
      responseHours: String(defaults.responseHours),
      escalationHours: String(defaults.escalationHours),
      resolutionHours: String(defaults.resolutionHours)
    }));
  };
  const visiblePolicies = [...policies]
    .filter((policy) => !selectedCountryCode || !policy.countryCode || normaliseCountryCode(policy.countryCode) === selectedCountryCode)
    .sort((a, b) => String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")));
  const activePolicies = visiblePolicies.filter((policy) => String(policy.status || "active").toLowerCase() === "active");
  const assignedPolicies = activePolicies.filter((policy) => policy.ownerTeam && policy.ownerEmail && policy.escalationOwner);
  const activeKindSet = new Set(activePolicies.map((policy) => policy.kind).filter(Boolean));
  const missingKindLabels = supportSlaPolicyKindOptions
    .filter((option) => !activeKindSet.has(option.id))
    .map((option) => option.label);
  const savePolicy = async () => {
    setState({ status: "loading", message: "Saving support SLA policy..." });
    try {
      const result = await callMarketplaceFunction("upsertSupportSlaPolicy", {
        countryCode: form.countryCode,
        kind: form.kind,
        label: form.label,
        ownerTeam: form.ownerTeam,
        ownerEmail: form.ownerEmail,
        responseHours: Number.parseInt(form.responseHours || "0", 10),
        escalationHours: Number.parseInt(form.escalationHours || "0", 10),
        resolutionHours: Number.parseInt(form.resolutionHours || "0", 10),
        escalationOwner: form.escalationOwner,
        status: form.status,
        notes: form.notes
      });
      setState({ status: "ready", message: `Support SLA policy saved: ${result.policyId}.` });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const seedPolicies = async () => {
    setState({ status: "seeding", message: "Seeding launch support SLA policies..." });
    try {
      const result = await callMarketplaceFunction("seedSupportSlaPolicies", {
        countryCode: form.countryCode,
        ownerEmail: form.ownerEmail,
        overwrite: form.overwriteSeed === true
      });
      setState({
        status: "ready",
        message: `Seeded ${result.seededCount || 0} support SLA polic${result.seededCount === 1 ? "y" : "ies"} and skipped ${result.skippedCount || 0}.`
      });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  return (
    <section className="super-admin-panel support-sla-panel">
      <div className="section-title-row">
        <div>
          <p>Support SLA policies</p>
          <h2>Named owners and response targets for {selectedCountryLabel}.</h2>
          <span>Launch support cannot rely on loose notes. Each support lane needs an owner, escalation owner, first-response target and resolution target.</span>
        </div>
      </div>
      <div className="support-sla-summary">
        {[
          ["Loaded policies", visiblePolicies.length, "Country and global rows visible here"],
          ["Active lanes", activePolicies.length, "Usable in launch operations"],
          ["Assigned owners", assignedPolicies.length, "Active lanes with owner and escalation fields"],
          ["Missing lanes", missingKindLabels.length, missingKindLabels.length ? missingKindLabels.join(", ") : "All launch lanes covered"]
        ].map(([label, value, text]) => (
          <article key={label} className={label === "Missing lanes" && value ? "warning" : ""}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{text}</em>
          </article>
        ))}
      </div>
      <div className="support-sla-form">
        <label>
          <span>Country</span>
          <input value={form.countryCode} onChange={(event) => updateField("countryCode", event.target.value.toUpperCase().slice(0, 2))} placeholder="Global" />
        </label>
        <label>
          <span>Support lane</span>
          <select value={form.kind} onChange={(event) => updateKind(event.target.value)}>
            {supportSlaPolicyKindOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
            <option value="active">active</option>
            <option value="draft">draft</option>
            <option value="archived">archived</option>
          </select>
        </label>
        <label>
          <span>Public/admin label</span>
          <input value={form.label} onChange={(event) => updateField("label", event.target.value)} />
        </label>
        <label>
          <span>Owner team</span>
          <input value={form.ownerTeam} onChange={(event) => updateField("ownerTeam", event.target.value)} />
        </label>
        <label>
          <span>Owner email</span>
          <input value={form.ownerEmail} onChange={(event) => updateField("ownerEmail", event.target.value)} />
        </label>
        <label>
          <span>First response hours</span>
          <input type="number" min="1" value={form.responseHours} onChange={(event) => updateField("responseHours", event.target.value)} />
        </label>
        <label>
          <span>Escalation hours</span>
          <input type="number" min="1" value={form.escalationHours} onChange={(event) => updateField("escalationHours", event.target.value)} />
        </label>
        <label>
          <span>Resolution hours</span>
          <input type="number" min="1" value={form.resolutionHours} onChange={(event) => updateField("resolutionHours", event.target.value)} />
        </label>
        <label>
          <span>Escalation owner</span>
          <input value={form.escalationOwner} onChange={(event) => updateField("escalationOwner", event.target.value)} />
        </label>
        <label className="wide">
          <span>Policy notes</span>
          <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} placeholder="Evidence required, review path, escalation triggers and approval notes." />
        </label>
        <button type="button" onClick={savePolicy} disabled={busy || !hasRealFirebaseSession}>
          <ShieldCheck size={15} /> {busy ? "Saving..." : "Save SLA policy"}
        </button>
      </div>
      <div className="support-sla-seed">
        <div>
          <strong>Seed launch support lanes</strong>
          <span>Create active SLA policies for claims, refunds, safety, payment disputes, media reviews, booking disputes and general support for the selected country.</span>
        </div>
        <label>
          <input type="checkbox" checked={form.overwriteSeed} onChange={(event) => updateField("overwriteSeed", event.target.checked)} />
          <span>Overwrite existing SLA rows</span>
        </label>
        <button type="button" onClick={seedPolicies} disabled={busy || !hasRealFirebaseSession}>
          <ClipboardCheck size={15} /> {state.status === "seeding" ? "Seeding..." : "Seed support SLAs"}
        </button>
      </div>
      <small>{hasRealFirebaseSession ? "Only the authorised super admin can save support SLA policies." : "Sign in with real Firebase phone auth as the authorised super admin to save SLA policies."}</small>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      {visiblePolicies.length ? (
        <div className="support-sla-list">
          {visiblePolicies.slice(0, 12).map((policy) => (
            <article key={policy.id || policy.policyId}>
              <div>
                <strong>{policy.label || supportSlaPolicyKindOptions.find((item) => item.id === policy.kind)?.label || String(policy.kind || "Support").replace(/_/g, " ")}</strong>
                <span>{policy.countryCode || "Global"} · {policy.status || "active"} · {policy.ownerTeam || "Owner team missing"}</span>
              </div>
              <p>{policy.notes || "No support policy notes recorded yet."}</p>
              <div>
                <em><Clock3 size={14} /> {policy.responseHours || "?"}h response</em>
                <em><ShieldCheck size={14} /> {policy.escalationHours || "?"}h escalation</em>
                <em><Check size={14} /> {policy.resolutionHours || "?"}h resolution</em>
              </div>
              <small>{policy.ownerEmail || "Owner email missing"} · {policy.escalationOwner || "Escalation owner missing"}{policy.updatedAt ? ` · Updated ${formatAdminDateTime(policy.updatedAt)}` : ""}</small>
            </article>
          ))}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>No support SLA policies saved yet</strong>
          <span>Seed launch SLAs or save the first support lane before opening public marketplace operations.</span>
        </div>
      )}
    </section>
  );
}

const rateLimitWindowResetAt = (row = {}) => {
  const startedMs = row.windowStartedAt ? Date.parse(row.windowStartedAt) : 0;
  const windowSeconds = Number(row.windowSeconds || 0);
  if (startedMs && windowSeconds) return new Date(startedMs + windowSeconds * 1000).toISOString();
  return row.expiresAt || row.updatedAt || row.createdAt || "";
};

const isActiveRateLimitWindow = (row = {}) => {
  const resetAt = rateLimitWindowResetAt(row);
  return resetAt ? Date.parse(resetAt) > Date.now() : false;
};

const isExhaustedRateLimitWindow = (row = {}) => {
  const count = Number(row.count || 0);
  const limit = Number(row.limit || 0);
  return Boolean(limit && count >= limit);
};

function SuperAdminRateLimitPanel({ selectedCountryLabel, rateLimits = [], setActiveSection }) {
  const visibleRows = [...rateLimits]
    .sort((a, b) => String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")))
    .slice(0, 12);
  const activeRows = rateLimits.filter(isActiveRateLimitWindow);
  const exhaustedRows = rateLimits.filter(isExhaustedRateLimitWindow);
  const actionCounts = rateLimits.reduce((summary, row) => {
    const key = row.action || "unknown_action";
    summary[key] = (summary[key] || 0) + Number(row.count || 0);
    return summary;
  }, {});
  const topActions = Object.entries(actionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const uniqueOwners = new Set(rateLimits.map((row) => row.ownerUid).filter(Boolean)).size;

  return (
    <section className="super-admin-panel rate-limit-panel">
      <div className="section-title-row">
        <div>
          <p>Abuse controls</p>
          <h2>Backend rate-limit counters.</h2>
          <span>These counters are global by account/action window and are visible only to the authorised super admin. The {selectedCountryLabel} country filter does not hide abuse signals.</span>
        </div>
        <button type="button" onClick={() => setActiveSection?.("security")}>Open security</button>
      </div>
      <div className="rate-limit-grid">
        {[
          ["Tracked windows", rateLimits.length, "Backend-created records"],
          ["Active windows", activeRows.length, "Still inside the current reset window"],
          ["Exhausted windows", exhaustedRows.length, "Count has reached the action limit"],
          ["Accounts seen", uniqueOwners, "Unique Firebase accounts"]
        ].map(([label, value, text]) => (
          <article key={label} className={label === "Exhausted windows" && value ? "warning" : ""}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{text}</em>
          </article>
        ))}
      </div>
      {topActions.length > 0 && (
        <div className="rate-limit-actions">
          {topActions.map(([action, count]) => (
            <span key={action}><Activity size={14} /> {action.replace(/_/g, " ")} <b>{count}</b></span>
          ))}
        </div>
      )}
      {visibleRows.length ? (
        <div className="rate-limit-list">
          {visibleRows.map((row) => {
            const count = Number(row.count || 0);
            const limit = Number(row.limit || 0);
            const resetAt = rateLimitWindowResetAt(row);
            const remaining = Math.max(0, limit - count);
            return (
              <article key={row.id} className={isExhaustedRateLimitWindow(row) ? "exhausted" : ""}>
                <div>
                  <strong>{String(row.action || "unknown_action").replace(/_/g, " ")}</strong>
                  <span>{row.ownerPhone || row.ownerUid || "Owner pending"} · {row.windowSeconds ? `${Math.round(Number(row.windowSeconds) / 60)} min window` : "Window pending"}</span>
                </div>
                <div>
                  <b>{count}/{limit || "?"}</b>
                  <em>{remaining ? `${remaining} remaining` : "Limit reached"}</em>
                </div>
                <small>{resetAt ? `Resets ${formatAdminDateTime(resetAt)}` : "Reset time pending"}</small>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>No rate-limit counters yet</strong>
          <span>Backend counters appear after real Firebase users trigger booking, checkout, claim, refund, support or notification actions.</span>
        </div>
      )}
    </section>
  );
}

const profileMediaClientSafetyLabel = (row = {}) => {
  const safety = row.clientSafety && typeof row.clientSafety === "object" ? row.clientSafety : null;
  if (!safety) return "";
  const score = Math.max(
    Number(safety.scores?.porn || 0),
    Number(safety.scores?.hentai || 0),
    Number(safety.scores?.sexy || 0)
  );
  const confidence = score > 0 ? ` · highest adult-content score ${Math.round(score * 100)}%` : "";
  if (safety.result === "passed") return `Local image check passed${confidence}`;
  if (safety.result === "blocked") return `Local image check blocked this upload${confidence}`;
  return "Local image check was unavailable; the secure backend will decide automatically";
};

function SuperAdminProfileMediaReviewPanel({ selectedCountryLabel, mediaRows = [] }) {
  const pendingRows = mediaRows.filter((row) => row.status === "pending_review");
  const approvedRows = mediaRows.filter((row) => row.status === "approved");
  const rejectedRows = mediaRows.filter((row) => row.status === "rejected");
  const visibleRows = [
    ...pendingRows,
    ...mediaRows.filter((row) => row.status !== "pending_review")
  ].slice(0, 12);

  return (
    <section className="super-admin-panel admin-profile-media-panel">
      <div className="section-title-row">
        <div>
          <p>Automated media safety</p>
          <h2>Media decisions for {selectedCountryLabel}.</h2>
          <span>Badminta automatically validates supported media links and scans uploaded images. Safe media publishes, unsafe or inconclusive media is rejected, and administrators can review the audit history without approving each file.</span>
        </div>
      </div>
      <div className="profile-media-review-stats">
        {[
          ["Pending", pendingRows.length],
          ["Approved", approvedRows.length],
          ["Rejected", rejectedRows.length],
          ["Total", mediaRows.length]
        ].map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      {visibleRows.length ? (
        <div className="profile-media-review-list">
          {visibleRows.map((row) => (
            <article key={row.id} className={`profile-media-review-card ${row.status}`}>
              <a
                className="profile-media-review-preview"
                href={row.mediaUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={row.status === "pending_review" ? "Open potentially sensitive media in a new tab" : "Open reviewed media in a new tab"}
              >
                {row.mediaType === "image" ? (
                  <img src={row.mediaUrl} alt="" loading="lazy" decoding="async" />
                ) : (
                  <span>
                    {row.mediaType === "embed" ? <PlayCircle size={28} /> : <ExternalLink size={26} />}
                    <em>{row.provider || "Open media"}</em>
                  </span>
                )}
              </a>
              <div className="profile-media-review-copy">
                <div>
                  <span className={`media-review-pill ${row.status}`}>{row.statusLabel}</span>
                  <em>{row.roleLabel}</em>
                </div>
                <strong>{row.label}</strong>
                <p>{row.ownerName} · {[row.city, row.country].filter(Boolean).join(", ") || "Location pending"} · {row.rightsLabel}</p>
                <a href={row.mediaUrl} target="_blank" rel="noreferrer">{row.mediaUrl}</a>
                {profileMediaClientSafetyLabel(row) && <small>{profileMediaClientSafetyLabel(row)}</small>}
                {row.reviewNote && <small>{row.reviewNote}</small>}
              </div>
              <div className="profile-media-review-actions"><span>{row.status === "pending_review" ? "Automatic check queued" : "Automatic decision recorded"}</span></div>
            </article>
          ))}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>No media safety records yet</strong>
          <span>Automated decisions appear here after players, coaches, groups or centres save media.</span>
        </div>
      )}
      {mediaRows.length > visibleRows.length && (
        <div className="super-admin-limit-note">Showing the first {visibleRows.length} media records. Pending items are sorted first.</div>
      )}
    </section>
  );
}

function SuperAdminDuplicateMergePanel({ selectedCountryLabel, countryCode, mergeDecisions = [] }) {
  const [form, setForm] = useState({
    primaryCentreId: "",
    duplicateCentreIds: "",
    countryCode: countryCode === "all" ? "" : countryCode || "",
    note: ""
  });
  const [state, setState] = useState({ status: "idle", message: "" });
  const busy = state.status === "loading";
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submitMerge = async () => {
    setState({ status: "loading", message: "Recording duplicate-centre merge decision..." });
    try {
      const result = await callMarketplaceFunction("recordDuplicateCentreMerge", {
        primaryCentreId: form.primaryCentreId,
        duplicateCentreIds: form.duplicateCentreIds,
        countryCode: form.countryCode,
        note: form.note
      });
      setState({ status: "ready", message: `Merge decision recorded for ${result.duplicateCount} duplicate centre${result.duplicateCount === 1 ? "" : "s"}.` });
      setForm((current) => ({ ...current, duplicateCentreIds: "", note: "" }));
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const filteredDecisions = (mergeDecisions || [])
    .filter((item) => !countryCode || countryCode === "all" || normaliseCountryCode(item.countryCode) === normaliseCountryCode(countryCode))
    .slice(0, 6);

  return (
    <section className="super-admin-panel duplicate-merge-panel">
      <div className="section-title-row">
        <div>
          <p>Duplicate centre merge</p>
          <h2>Record duplicate-centre cleanup for {selectedCountryLabel}.</h2>
          <span>Use this when two or more centre records refer to the same real venue. Firestore duplicate centre docs are marked private and linked to the primary centre.</span>
        </div>
      </div>
      <div className="duplicate-merge-grid">
        <label>
          <span>Primary centre id</span>
          <input value={form.primaryCentreId} onChange={(event) => updateField("primaryCentreId", event.target.value)} placeholder="centre-primary-id" />
        </label>
        <label>
          <span>Country</span>
          <input value={form.countryCode} onChange={(event) => updateField("countryCode", event.target.value.toUpperCase().slice(0, 3))} placeholder="AU" />
        </label>
        <label className="wide">
          <span>Duplicate centre ids</span>
          <textarea value={form.duplicateCentreIds} onChange={(event) => updateField("duplicateCentreIds", event.target.value)} placeholder="Paste duplicate ids separated by commas, spaces or new lines" />
        </label>
        <label className="wide">
          <span>Cleanup note</span>
          <textarea value={form.note} onChange={(event) => updateField("note", event.target.value)} placeholder="Why these records are duplicates, what source was checked, and whether any public listing needs review." />
        </label>
      </div>
      <div className="duplicate-merge-actions">
        <button type="button" onClick={submitMerge} disabled={busy || !hasRealFirebaseSession}>
          {busy ? "Recording..." : "Record merge decision"}
        </button>
        <small>{hasRealFirebaseSession ? "Writes to backend merge ledger and admin audit." : "Sign in as the authorised Firebase super admin before recording merge decisions."}</small>
      </div>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      {filteredDecisions.length > 0 && (
        <div className="duplicate-merge-history">
          {filteredDecisions.map((item) => (
            <article key={item.id}>
              <strong>{item.primaryCentreName || item.primaryCentreId}</strong>
              <span>{item.duplicateCount || item.duplicateCentreIds?.length || 0} duplicate{(item.duplicateCount || item.duplicateCentreIds?.length || 0) === 1 ? "" : "s"} · {item.status || "recorded"}</span>
              <p>{item.note || "No note recorded."}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

const platformFeeKindOptions = ["all", "booking", "centre", "social", "event", "coach", "membership"];

function SuperAdminPlatformFeeSettingsPanel({ selectedCountryLabel, countryCode, feeSettings = [] }) {
  const [form, setForm] = useState({
    countryCode: countryCode === "all" ? "" : countryCode || "",
    currency: marketplaceCurrencyForCountry(countryCode === "all" ? "AU" : countryCode || "AU"),
    kind: "all",
    feeBps: "800",
    minimumFeeCents: "0",
    maximumFeeCents: "0",
    status: "active",
    overwriteSeed: false
  });
  const [state, setState] = useState({ status: "idle", message: "" });
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const busy = state.status === "loading";
  useEffect(() => {
    const nextCountryCode = countryCode === "all" ? "" : countryCode || "";
    setForm((current) => ({
      ...current,
      countryCode: nextCountryCode,
      currency: marketplaceCurrencyForCountry(nextCountryCode || "AU")
    }));
  }, [countryCode]);
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const saveFeeSetting = async () => {
    setState({ status: "loading", message: "Saving platform fee setting..." });
    try {
      const result = await callMarketplaceFunction("upsertPlatformFeeSetting", {
        countryCode: form.countryCode,
        currency: form.currency,
        kind: form.kind,
        feeBps: Number.parseInt(form.feeBps || "0", 10),
        minimumFeeCents: Number.parseInt(form.minimumFeeCents || "0", 10),
        maximumFeeCents: Number.parseInt(form.maximumFeeCents || "0", 10),
        status: form.status
      });
      setState({ status: "ready", message: `Fee setting saved: ${result.settingId}.` });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const seedFeeSettings = async () => {
    setState({ status: "loading", message: "Seeding launch fee schedule..." });
    try {
      const result = await callMarketplaceFunction("seedPlatformFeeSettings", {
        countryCode: form.countryCode,
        currency: form.currency,
        overwrite: form.overwriteSeed === true
      });
      setState({
        status: "ready",
        message: `Seeded ${result.seededCount || 0} launch fee rule${result.seededCount === 1 ? "" : "s"} and skipped ${result.skippedCount || 0}.`
      });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };
  const visibleSettings = [...feeSettings]
    .sort((a, b) => String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")))
    .slice(0, 8);

  return (
    <section className="super-admin-panel platform-fee-panel">
      <div className="section-title-row">
        <div>
          <p>Platform fee settings</p>
          <h2>Country and currency fee rules for {selectedCountryLabel}.</h2>
          <span>Bookings, sessions, coaching, events and memberships snapshot the active backend fee rule before checkout opens.</span>
        </div>
      </div>
      <div className="platform-fee-controls">
        <label>
          <span>Country</span>
          <input value={form.countryCode} onChange={(event) => updateField("countryCode", event.target.value.toUpperCase().slice(0, 2))} placeholder="Global" />
        </label>
        <label>
          <span>Currency</span>
          <input value={form.currency} onChange={(event) => updateField("currency", event.target.value.toLowerCase().slice(0, 3))} />
        </label>
        <label>
          <span>Checkout kind</span>
          <select value={form.kind} onChange={(event) => updateField("kind", event.target.value)}>
            {platformFeeKindOptions.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
          </select>
        </label>
        <label>
          <span>Fee bps</span>
          <input type="number" min="0" max="3000" value={form.feeBps} onChange={(event) => updateField("feeBps", event.target.value)} />
        </label>
        <label>
          <span>Min fee cents</span>
          <input type="number" min="0" value={form.minimumFeeCents} onChange={(event) => updateField("minimumFeeCents", event.target.value)} />
        </label>
        <label>
          <span>Max fee cents</span>
          <input type="number" min="0" value={form.maximumFeeCents} onChange={(event) => updateField("maximumFeeCents", event.target.value)} />
        </label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
            <option value="active">active</option>
            <option value="draft">draft</option>
            <option value="archived">archived</option>
          </select>
        </label>
        <button type="button" onClick={saveFeeSetting} disabled={busy || !hasRealFirebaseSession}>
          <BadgeDollarSign size={15} /> {busy ? "Saving..." : "Save fee rule"}
        </button>
      </div>
      <div className="platform-fee-seed">
        <div>
          <strong>Launch revenue schedule</strong>
          <span>Seed booking, social, event, coach and membership fee rules for the selected country/currency. Existing rules are skipped unless overwrite is enabled.</span>
        </div>
        <label>
          <input type="checkbox" checked={form.overwriteSeed} onChange={(event) => updateField("overwriteSeed", event.target.checked)} />
          <span>Overwrite existing seed rules</span>
        </label>
        <button type="button" onClick={seedFeeSettings} disabled={busy || !hasRealFirebaseSession}>
          <BadgeDollarSign size={15} /> {busy ? "Seeding..." : "Seed launch fees"}
        </button>
      </div>
      <small>{hasRealFirebaseSession ? "Only the authorised super admin can save platform fee rules." : "Sign in with real Firebase phone auth as the authorised super admin to save fee rules."}</small>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      {visibleSettings.length ? (
        <div className="platform-fee-list">
          {visibleSettings.map((setting) => (
            <article key={setting.id || setting.settingId}>
              <strong>{setting.displayLabel || `${setting.countryCode || "Global"} ${String(setting.currency || "").toUpperCase()} ${setting.kind || "all"}`}</strong>
              <span>{Number(setting.feeBps || 0) / 100}% fee · {setting.status || "active"}</span>
              <em>Min {formatCurrencyCents(setting.minimumFeeCents, setting.currency)} · Max {Number(setting.maximumFeeCents || 0) ? formatCurrencyCents(setting.maximumFeeCents, setting.currency) : "No cap"}</em>
            </article>
          ))}
        </div>
      ) : (
        <div className="ops-empty">
          <strong>No platform fee rules saved yet</strong>
          <span>The backend falls back to the runtime default until country/currency rules are created here.</span>
        </div>
      )}
    </section>
  );
}

function SuperAdminSettlementExportPanel({ selectedCountryLabel, countryCode }) {
  const monthStart = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    startAt: monthStart(),
    endAt: getIsoDate(0),
    countryCode: countryCode === "all" ? "" : countryCode || ""
  });
  const [state, setState] = useState({ status: "idle", message: "", export: null });
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const busy = state.status === "loading" || state.status === "reconciling";
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const generateExport = async () => {
    setState({ status: "loading", message: "Generating settlement export from backend payment and refund ledgers...", export: state.export });
    try {
      const result = await callMarketplaceFunction("generatePlatformSettlementExport", {
        startAt: form.startAt,
        endAt: form.endAt,
        countryCode: form.countryCode
      });
      setState({
        status: result.failedRefundCount ? "needs-action" : "ready",
        message: result.failedRefundCount ? "Settlement export generated. Failed refunds need support review before payout reconciliation is final." : "Settlement export generated from backend ledger records.",
        export: result
      });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error), export: null });
    }
  };
  const reconcileExport = async () => {
    if (!state.export?.exportId) {
      setState({ status: "needs-action", message: "Generate a settlement export before running Stripe balance reconciliation.", export: state.export });
      return;
    }
    setState({ status: "reconciling", message: "Reconciling export against Stripe balance transactions...", export: state.export });
    try {
      const result = await callMarketplaceFunction("reconcilePlatformSettlementExport", {
        exportId: state.export.exportId
      });
      const nextExport = { ...state.export, ...result };
      setState({
        status: result.reconciliationStatus === "reconciled" ? "ready" : "needs-action",
        message: result.reconciliationStatus === "reconciled"
          ? "Stripe balance transactions matched the settlement export."
          : "Stripe reconciliation needs review before payout is final.",
        export: nextExport
      });
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error), export: state.export });
    }
  };
  const result = state.export;
  const rows = result ? [
    ["Gross player charges", formatCurrencyCents(result.grossSalesCents, result.currency), `${result.paymentCount || 0} captured payment${result.paymentCount === 1 ? "" : "s"}`],
    ["Owner gross", formatCurrencyCents(result.ownerGrossCents, result.currency), "Before payout timing and refunds"],
    ["Badminta fees", formatCurrencyCents(result.platformFeeCents, result.currency), "Application fees recorded on payments"],
    ["Refunds", formatCurrencyCents(result.refundCents, result.currency), `${result.refundCount || 0} refund row${result.refundCount === 1 ? "" : "s"}`],
    ["Net sales", formatCurrencyCents(result.netSalesCents, result.currency), result.failedRefundCount ? `${result.failedRefundCount} failed refund${result.failedRefundCount === 1 ? "" : "s"}` : "Ready for accounting review"],
    ...(result.reconciliationStatus ? [
      ["Stripe reconciliation", result.reconciliationStatus === "reconciled" ? "Matched" : "Needs review", `${result.matchedPaymentCount || 0}/${result.paymentCount || 0} payments · ${result.matchedRefundCount || 0}/${result.refundCount || 0} refunds`],
      ["Stripe balance net", formatCurrencyCents(result.stripeBalanceNetCents, result.currency), `${result.stripeBalanceTransactionCount || 0} balance transaction${result.stripeBalanceTransactionCount === 1 ? "" : "s"}`]
    ] : [])
  ] : [];

  return (
    <section className="super-admin-panel settlement-export-panel">
      <div className="section-title-row">
        <div>
          <p>Finance reconciliation</p>
          <h2>Settlement export for {selectedCountryLabel}.</h2>
          <span>Generate admin-only CSV rows for Stripe payments, refunds, application fees, owner gross and subscription invoice references.</span>
        </div>
      </div>
      <div className="settlement-export-controls">
        <label>
          <span>Start</span>
          <input type="date" value={form.startAt} onChange={(event) => updateField("startAt", event.target.value)} />
        </label>
        <label>
          <span>End</span>
          <input type="date" value={form.endAt} onChange={(event) => updateField("endAt", event.target.value)} />
        </label>
        <label>
          <span>Country code</span>
          <input value={form.countryCode} onChange={(event) => updateField("countryCode", event.target.value.toUpperCase().slice(0, 3))} placeholder="All" />
        </label>
        <button type="button" onClick={generateExport} disabled={busy || !hasRealFirebaseSession}>
          <Download size={15} /> {state.status === "loading" ? "Generating..." : "Generate export"}
        </button>
        <button type="button" onClick={reconcileExport} disabled={busy || !hasRealFirebaseSession || !state.export?.exportId}>
          <ShieldCheck size={15} /> {state.status === "reconciling" ? "Reconciling..." : "Reconcile Stripe"}
        </button>
      </div>
      <small>{hasRealFirebaseSession ? "Only the authorised super-admin phone can create backend settlement exports and reconcile Stripe balance transactions." : "Sign in with real Firebase phone auth as the authorised super admin to generate exports."}</small>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      {rows.length ? (
        <>
          <div className="stripe-ops-grid payout-statement-grid">
            {rows.map(([label, value, detail]) => (
              <article key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
                <p>{detail}</p>
              </article>
            ))}
          </div>
          <pre className="settlement-export-preview">{result.csvPreview}</pre>
        </>
      ) : (
        <div className="ops-empty">
          <strong>No settlement export generated yet</strong>
          <span>Generate after backend Stripe checkout payments, recurring invoices or refunds exist for the selected period.</span>
        </div>
      )}
    </section>
  );
}

const marketplaceBackendCollections = [
  {
    collection: "centres",
    owner: "Centre owners",
    writePath: "Owner creates/claims centre, admin verifies, backend locks bookings",
    purpose: "Public centre profiles, court inventory, pricing, opening hours, claim status and verification history"
  },
  {
    collection: "bookings",
    owner: "Players and centre owners",
    writePath: "Cloud Function creates booking after slot lock succeeds",
    purpose: "Court bookings, slot, price, Stripe reference, cancellation window, refund state and support thread"
  },
  {
    collection: "groups",
    owner: "Social organisers",
    writePath: "Organiser publishes group, players join sessions through payment flow",
    purpose: "Social group profiles, venue, sessions, capacity, invited members, waitlist, attendance and payout state"
  },
  {
    collection: "events",
    owner: "Centres and organisers",
    writePath: "Completed centre or organiser profile publishes event",
    purpose: "Tournaments, clinics and public events with tickets, attendees, refunds and owner dashboard stats"
  },
  {
    collection: "sessionJoinRequests",
    owner: "Players and session owners",
    writePath: "Backend join request reserves social/event capacity before Stripe checkout",
    purpose: "Capacity-controlled social group and event registrations with participant, payment, refund and owner payout state"
  },
  {
    collection: "lessonRequests",
    owner: "Players and coaches",
    writePath: "Backend creates coach lesson request before Stripe checkout",
    purpose: "Coach lesson date, type, participant, price, service fee, payment, refund and payout state"
  },
  {
    collection: "coachBookings",
    owner: "Coaches and players",
    writePath: "Backend or migration path for confirmed coach bookings",
    purpose: "Legacy and future coach booking records with participant, coach, Stripe and dispute references"
  },
  {
    collection: "payments",
    owner: "Platform ledger",
    writePath: "Stripe webhook or admin-only reconciliation",
    purpose: "Payment intent/link reference, platform fee, Stripe fee, payout, refund and dispute state"
  },
  {
    collection: "stripeDisputes",
    owner: "Platform finance and support",
    writePath: "Stripe dispute webhooks create chargeback ledger, support report, evidence row and owner follow-up",
    purpose: "Chargeback status, evidence deadline, payment linkage and support queue history"
  },
  {
    collection: "claims",
    owner: "Centre owners and admins",
    writePath: "Owner submits claim, admin reviews, every decision is auditable and undoable",
    purpose: "Centre ownership evidence, verification state, admin decision, fraud notes and claim history"
  },
  {
    collection: "centreMergeDecisions",
    owner: "Super admin trust operations",
    writePath: "Super admin records primary and duplicate centre ids through callable backend",
    purpose: "Duplicate-centre merge history, data cleanup notes and Firestore duplicate status changes"
  },
  {
    collection: "reviews_reports",
    owner: "Members and trust team",
    writePath: "Members create reviews/reports, trust team moderates status",
    purpose: "Ratings, recommendations, report evidence, moderation queue and private support conversations"
  },
  {
    collection: "memberships",
    owner: "Centres and players",
    writePath: "Centre creates products, player purchases credit packs through payments",
    purpose: "Membership plans, prepaid court credits, expiry, redemption ledger and recurring revenue reporting"
  },
		  {
		    collection: "membershipPurchases",
		    owner: "Players and centre owners",
		    writePath: "Stripe webhook activates purchases after checkout or recurring invoice payment succeeds",
		    purpose: "Purchased memberships, remaining credit hours, visit credits, expiry, refund state and player wallet access"
		  },
		  {
		    collection: "membershipRedemptions",
		    owner: "Players, centre owners and support",
		    writePath: "Court booking transaction applies credit; owner cancellation transaction restores it once",
		    purpose: "Immutable booking-to-credit linkage, balances before and after use, and cancellation restoration evidence"
		  },
		  {
		    collection: "settlementExports",
		    owner: "Super admin finance",
		    writePath: "Super admin generates country/date exports from backend payment and refund ledgers",
		    purpose: "Accounting-ready CSV rows for Stripe payments, refunds, platform fees, owner gross, invoices and subscriptions"
		  },
		  {
		    collection: "supportEvidence",
		    owner: "Trust operations",
		    writePath: "Callable backend records support evidence against reports, refunds, claims, bookings or payments",
		    purpose: "Dispute, refund, claim, safety and payment evidence with owner-scoped Storage paths and audit ids"
		  },
		  {
		    collection: "notificationTemplates",
		    owner: "Super admin support",
		    writePath: "Super admin seeds or customises controlled templates for receipts, bookings, claims, refunds, moderation and payouts",
		    purpose: "Reusable owner/player/support messages for operational workflows without exposing provider keys to the browser"
		  },
		  {
		    collection: "notificationQueue",
		    owner: "Support delivery",
		    writePath: "Backend workflows and super admin queue/process in-app, email or SMS follow-ups through signed delivery",
		    purpose: "Processable notification records for receipts, booking holds, cancellations, disputes, claim decisions and moderation outcomes"
		  },
		  {
		    collection: "notificationReceipts",
		    owner: "Support delivery",
		    writePath: "Signed provider receipt webhook records email/SMS delivered, bounced, failed or unmatched callbacks",
		    purpose: "Auditable provider delivery receipts linked back to notificationQueue rows and support subjects"
		  },
		  {
		    collection: "supportSlaPolicies",
		    owner: "Support operations",
		    writePath: "Super admin seeds or updates country-specific support lane owners, first-response times, escalation windows and resolution targets",
		    purpose: "Launch-ready service levels for claims, refunds, safety reports, disputes, media reviews, booking issues and general support"
		  },
		  {
		    collection: "rateLimits",
		    owner: "Platform security",
		    writePath: "Callable backend increments uid/action windows before risky marketplace side effects",
		    purpose: "Abuse protection for bookings, session joins, Stripe checkout, claims, reports, refunds, evidence and notifications"
		  },
		  {
		    collection: "launchEvidence",
		    owner: "Release operations",
		    writePath: "Super admin records external Firebase, Stripe, notification, legal and production smoke evidence",
		    purpose: "Auditable strict-launch confirmations before Badminta opens a public marketplace launch"
		  },
		  {
		    collection: "profileMediaReviews",
		    owner: "Trust operations",
		    writePath: "Super admin approves or rejects user-submitted media from Firestore user profile docs",
		    purpose: "Media moderation history for player, coach, group and centre public galleries"
		  },
		  {
		    collection: "platformFeeSettings",
		    owner: "Platform finance",
		    writePath: "Super admin manages active fee bps, minimums and caps by country, currency and checkout kind",
		    purpose: "Country-aware Badminta revenue rules snapped onto bookings, payments, subscriptions and settlement exports"
		  }
		];

const launchStatusMeta = {
  ready: { label: "Operationally ready", tone: "ready", Icon: BadgeCheck },
  beta: { label: "Limited rollout", tone: "beta", Icon: ShieldCheck },
  blocked: { label: "Action required", tone: "blocked", Icon: LockKeyhole },
  build: { label: "Setup required", tone: "build", Icon: CircleHelp }
};

const productionCertificationItems = [
  {
    evidenceKey: "firebase_deploy",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_FIREBASE_DEPLOY_CONFIRMED",
    title: "Firebase deploy",
    owner: "Platform engineering",
    evidence: "Deploy Cloud Functions, Firestore rules, Storage rules, indexes and Hosting headers to the production Firebase project.",
    action: "Run strict preflight after deploy"
  },
  {
    evidenceKey: "app_check",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_APP_CHECK_CONFIRMED",
    title: "Firebase App Check",
    owner: "Platform security",
    evidence: "Register the deployed domain, configure the production reCAPTCHA/App Check key and verify callable functions reject untrusted clients.",
    action: "Confirm App Check enforcement"
  },
  {
    evidenceKey: "firestore_backups",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_FIRESTORE_BACKUPS_CONFIRMED",
    title: "Firestore backups",
    owner: "Platform operations",
    evidence: "Enable scheduled Firestore exports or managed backups for users, bookings, payments, claims, support and admin ledgers.",
    action: "Attach backup schedule evidence"
  },
  {
    evidenceKey: "billing_alerts",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_BILLING_ALERTS_CONFIRMED",
    title: "Billing alerts",
    owner: "Platform operations",
    evidence: "Configure Firebase/GCP billing budgets and alert recipients before public traffic and payment webhooks are opened.",
    action: "Attach budget alert evidence"
  },
  {
    evidenceKey: "production_monitoring",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_MONITORING_CONFIRMED",
    title: "Production monitoring",
    owner: "Platform operations",
    evidence: "Confirm Cloud Functions errors, webhook failures, refund failures, rate limits and hosting availability are monitored.",
    action: "Attach monitoring evidence"
  },
  {
    evidenceKey: "stripe_secrets",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_STRIPE_SECRETS_CONFIRMED",
    title: "Stripe marketplace secrets",
    owner: "Platform finance",
    evidence: "Confirm Stripe secret key and webhook secret are stored in Firebase Secret Manager, never browser env files.",
    action: "Test Checkout and webhook reconciliation"
  },
  {
    evidenceKey: "stripe_webhook_transaction",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_STRIPE_WEBHOOK_TESTED",
    title: "Stripe webhook transaction",
    owner: "Platform finance",
    evidence: "Run one controlled live booking/session transaction and verify payment, refund, fee and payout ledgers reconcile.",
    action: "Capture settlement evidence"
  },
  {
    evidenceKey: "notification_dispatcher",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_NOTIFICATION_DISPATCHER_TESTED",
    title: "Notification dispatcher",
    owner: "Support operations",
    evidence: "Test the production notification queue, scheduled dispatcher and in-app delivery path. Provider email/SMS delivery can be connected later.",
    action: "Record dispatcher evidence"
  },
  {
    evidenceKey: "legal_approval",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_LEGAL_APPROVED",
    title: "Legal approval",
    owner: "Founder + legal",
    evidence: "Approve Terms, Privacy, Refunds, Community Safety, Centre, Organiser and Coach terms for the launch country.",
    action: "Record policy approval evidence"
  },
  {
    evidenceKey: "production_smoke_test",
    confirmationEnvKey: "BADMINTA_PREFLIGHT_PRODUCTION_SMOKE_TESTED",
    title: "Production smoke test",
    owner: "Release owner",
    evidence: "Run the deployed-domain smoke test with production data mode on and local profile fallback disabled.",
    action: "Set smoke confirmation only after pass"
  }
];

const buildProductionLaunchTracks = ({
  accountProfile = defaultAccountProfile,
  centreRecords = [],
  contentRecords = [],
  openTrustReports = [],
  auditEntries = [],
  supportEvidence = [],
  notificationTemplates = [],
  notificationQueue = [],
  lessonRequests = [],
  coachBookings = [],
  stripeAccounts = [],
  stripeDisputes = [],
  supportSlaPolicies = [],
  rateLimits = [],
  launchEvidence = [],
  platformFeeSettings = [],
  centreMergeDecisions = [],
  profileMediaRows = [],
  selectedCountryLabel = "All countries"
} = {}) => {
  const bookings = getAccountBookings(accountProfile);
  const accountEvents = getAccountEvents(accountProfile);
  const claimedCentreCount = centreRecords.filter(isAdminCentreRecordOwnerManaged).length;
  const backendClaimCount = centreRecords.filter((record) => isBackendClaimRecord(record)).length;
  const bookableCentreCount = centreRecords.filter(isAdminCentreRecordBookable).length;
  const paymentReadyContentCount = contentRecords.filter((record) => /payment|price|paid|published/i.test(`${record.detail} ${record.status}`)).length;
  const publicProfileCount = accountRoleOptions.filter((role) => isPublicProfileReady(accountProfile, role.id)).length;
  const membershipProductCount = getCentreMembershipProducts(accountProfile.centre || {}).filter((product) => product.status !== "archived").length;
  const disputeEvidenceCount = supportEvidence.filter((item) => /dispute|refund|payment/i.test(`${item.evidenceType} ${item.retentionCategory} ${item.subjectCollection}`)).length;
  const retentionDecisionCount = supportEvidence.filter((item) => item.retentionDecision || item.retentionDecisionStatus).length;
  const retentionDueSoonCount = supportEvidence.filter((item) => {
    const due = evidenceDateMillis(item.retainUntil);
    return due && due <= Date.now() + 30 * 24 * 60 * 60 * 1000;
  }).length;
  const queuedFollowUpCount = notificationQueue.filter((item) => /queued|pending/i.test(`${item.status} ${item.deliveryStatus}`)).length;
  const readyStripeAccountCount = stripeAccounts.filter(isStripeAccountCheckoutReady).length;
  const stripeAccountReviewCount = stripeAccounts.filter(isStripeAccountNeedsReview).length;
  const exhaustedRateLimitCount = rateLimits.filter(isExhaustedRateLimitWindow).length;
  const activeRateLimitCount = rateLimits.filter(isActiveRateLimitWindow).length;
  const confirmedLaunchEvidenceCount = productionCertificationItems.filter((item) => launchEvidence.some((row) => row.evidenceKey === item.evidenceKey && row.environment === "production" && row.status === "confirmed")).length;
  const seededTemplateKeys = new Set(notificationTemplates.map((item) => item.templateKey || item.id).filter(Boolean));
  const seededOperationalTemplateCount = operationalNotificationTemplateKeys.filter((key) => seededTemplateKeys.has(key)).length;
  const activeSupportSlaPolicies = supportSlaPolicies.filter((policy) => String(policy.status || "active").toLowerCase() === "active");
  const assignedSupportSlaPolicies = activeSupportSlaPolicies.filter((policy) => policy.ownerTeam && policy.ownerEmail && policy.escalationOwner);
  const activeSupportSlaKinds = new Set(activeSupportSlaPolicies.map((policy) => policy.kind).filter(Boolean));
  const missingSupportSlaPolicyCount = Math.max(0, supportSlaPolicyKindOptions.length - activeSupportSlaKinds.size);
  const mergeDecisionCount = centreMergeDecisions.length;
  const pendingProfileMediaCount = profileMediaRows.filter((row) => row.status === "pending_review").length;

  return [
    {
      id: "payments",
      title: "Payments, fees and payouts",
      owner: "Platform finance",
      status: readyStripeAccountCount ? "beta" : "blocked",
      progress: readyStripeAccountCount ? 68 : 20,
      summary: readyStripeAccountCount
        ? `${readyStripeAccountCount} Stripe Connect account${readyStripeAccountCount === 1 ? "" : "s"} can receive charges and payouts.`
        : "No Stripe Connect account is ready for marketplace payouts, so live card collection must stay disabled.",
      evidence: [
        `${bookings.length} booking ledger row${bookings.length === 1 ? "" : "s"} in this account`,
        `${lessonRequests.length} coach lesson request${lessonRequests.length === 1 ? "" : "s"} and ${coachBookings.length} coach booking ledger row${coachBookings.length === 1 ? "" : "s"} loaded for coaching checkout`,
        `${paymentReadyContentCount} group/event record${paymentReadyContentCount === 1 ? "" : "s"} mention payment readiness`,
        `${readyStripeAccountCount}/${stripeAccounts.length} Stripe Connect account${stripeAccounts.length === 1 ? "" : "s"} checkout-ready`,
        `${stripeAccountReviewCount} Stripe payout account${stripeAccountReviewCount === 1 ? "" : "s"} need support review`,
        "Backend payout statements now separate gross sales, Badminta fees, refunds and estimated owner payouts",
        "Settlement exports can be reconciled against Stripe balance transactions from the super-admin finance panel",
        "Browser never stores Stripe secret keys"
      ],
      blockers: [
        readyStripeAccountCount ? "Any Stripe Connect account marked checkout-blocked must be resolved before public checkout" : "Stripe Connect accounts, payout schedules and platform application fees need backend ownership",
        "Webhook reconciliation is required before refunds, disputes and payouts can be trusted",
        disputeEvidenceCount && queuedFollowUpCount
          ? "Stripe settlement reconciliation exceptions must be reviewed before payout runs"
          : "Stripe settlement exports need dispute evidence and failed-refund follow-up before payout runs"
      ],
      actions: [
        { label: "Centre payments", page: "centrePayments" },
        { label: "Payouts", page: "payouts" },
        { label: "Security checks", section: "security" }
      ]
    },
    {
      id: "bookings",
      title: "Court bookings and slot locking",
      owner: "Marketplace backend",
      status: bookableCentreCount ? "beta" : "blocked",
      progress: bookableCentreCount ? 66 : 18,
      summary: bookableCentreCount
        ? `${bookableCentreCount} centre record${bookableCentreCount === 1 ? "" : "s"} in ${selectedCountryLabel} can use backend booking holds once the owner has a Firestore centre, court pricing and payout setup.`
        : `No centre record in ${selectedCountryLabel} is booking-ready yet.`,
      evidence: [
        `${claimedCentreCount} claimed or owner-created centre${claimedCentreCount === 1 ? "" : "s"}`,
        `${bookings.length} saved booking request${bookings.length === 1 ? "" : "s"}`,
        "Court inventory and fee schedules exist in centre tools",
        "Backend booking holds calculate server-side court pricing and reject stale browser totals",
        "Structured opening hours are checked before a booking lock is written",
        "Booking hold creation, release and expiry create immutable owner/player notification queue rows",
        "Backend booking operations record paid cancellation requests, centre cancellations, attendance, no-shows and disputes with support evidence",
        "Scheduled stale-hold sweep releases unpaid expired booking locks"
      ],
      blockers: [
        "Deploy callable booking functions, Firestore rules and indexes to the production Firebase project",
        "Connect every bookable centre to Stripe Connect before live payment collection",
        "Each centre must publish its cancellation window and no-show rule before accepting checkout"
      ],
      actions: [
        { label: "Manage courts", page: "courtManager" },
        { label: "Preview booking", page: "booking", payload: { bookingType: "court" } },
        { label: "Centre records", section: "centres" }
      ]
    },
    {
      id: "firebase",
      title: "Firebase rules and data security",
      owner: "Platform engineering",
      status: "beta",
      progress: 68,
      summary: "Firestore, Storage and Hosting are configured for deployable rules, and signed-in account profiles plus user activity streams now sync through `/users/{uid}` with local fallback.",
      evidence: [
        "Owner phone is the only super-admin account in the UI and rules",
        "Account profile writes use Firestore user documents for real Firebase phone users",
        "Admin-managed account restriction fields are protected from normal user writes",
        "Messages, bookings, payments, reviews, saved items, joined events, joined groups and admin audit rows sync into owner-scoped user subcollections",
        "Production data mode blocks anonymous local account profiles and legacy global profile fallback from being treated as live account data",
        "Admin decisions, private support reports and refund review requests have callable backend ledger paths",
        "Coach lesson requests use backend-created payment sources before Stripe checkout opens",
        `${supportEvidence.length} support evidence record${supportEvidence.length === 1 ? "" : "s"} and ${notificationQueue.length} notification queue row${notificationQueue.length === 1 ? "" : "s"} visible to super admin`,
        `${stripeDisputes.length} Stripe dispute ledger row${stripeDisputes.length === 1 ? "" : "s"} loaded for chargeback support`,
        `${activeSupportSlaPolicies.length} active support SLA polic${activeSupportSlaPolicies.length === 1 ? "y" : "ies"} loaded for launch operations`,
        `${rateLimits.length} backend rate-limit counter${rateLimits.length === 1 ? "" : "s"} visible to super admin`,
        `${confirmedLaunchEvidenceCount}/${productionCertificationItems.length} strict production evidence item${productionCertificationItems.length === 1 ? "" : "s"} confirmed in the launch ledger`,
        exhaustedRateLimitCount
          ? `${exhaustedRateLimitCount} exhausted rate-limit window${exhaustedRateLimitCount === 1 ? "" : "s"} need abuse review`
          : `${activeRateLimitCount} active rate-limit window${activeRateLimitCount === 1 ? "" : "s"} and no exhausted counter loaded`,
        `${seededOperationalTemplateCount}/${operationalNotificationTemplateKeys.length} operational notification template${operationalNotificationTemplateKeys.length === 1 ? "" : "s"} seeded`,
        "Queued in-app support notifications can be processed into user message ledgers",
        `${auditEntries.length} admin audit entr${auditEntries.length === 1 ? "y" : "ies"} saved locally`,
        "Public profiles, private reports, payments and booking ledgers have separate rule paths"
      ],
      blockers: [
        "Test the production notification dispatcher and in-app delivery queue",
        confirmedLaunchEvidenceCount === productionCertificationItems.length
          ? "Strict launch evidence is recorded; keep evidence links current after material changes"
          : "Record Firebase, Stripe, notification, legal and deployed-domain smoke evidence in the launch ledger",
        "Enable Firebase App Check, backups, alerting and billing limits in the Firebase project",
        "Run the production-domain smoke test with local profile fallback disabled after every release"
      ],
      actions: [
        { label: "Security tab", section: "security" },
        { label: "Audit log", section: "audit" },
        { label: "Trust queue", section: "trust" }
      ]
    },
    {
      id: "claims",
      title: "Centre claims and verification",
      owner: "Trust operations",
      status: claimedCentreCount || backendClaimCount ? "beta" : "build",
      progress: claimedCentreCount || backendClaimCount ? 72 : 34,
      summary: claimedCentreCount
        ? `${claimedCentreCount} claimed or owner-created centre${claimedCentreCount === 1 ? "" : "s"} can be reviewed in ${selectedCountryLabel}.`
        : backendClaimCount
          ? `${backendClaimCount} backend ownership claim${backendClaimCount === 1 ? "" : "s"} can be approved, rejected, returned for evidence or undone from admin.`
          : "Claim search and admin decisions exist, but no claimed centre is ready in the selected country.",
      evidence: [
        "Admin verify/review/restrict decisions can be undone",
        "Admin edits can be reset without changing source directory data",
        "Search-and-claim submissions can create backend claim records with support evidence rows",
        "Admin claim approval, rejection, evidence requests and undo actions call the backend claim review workflow",
        "Claim approval is gated by verification score, official evidence signals and fraud risk level",
        "Claim records show SLA due time, missing requirements and evidence-readiness status",
        `${assignedSupportSlaPolicies.length} support SLA polic${assignedSupportSlaPolicies.length === 1 ? "y has" : "ies have"} named owner, email and escalation owner`,
        `${mergeDecisionCount} duplicate-centre merge decision${mergeDecisionCount === 1 ? "" : "s"} recorded`,
        "Verified centre status is blocked for unclaimed directory-only listings"
      ],
      blockers: [
        assignedSupportSlaPolicies.length
          ? "Fraud escalation playbook still needs founder/legal sign-off before full public launch"
          : "Assign support SLA owner team, email and escalation owner before approving live centre claims",
        missingSupportSlaPolicyCount
          ? `Seed or create ${missingSupportSlaPolicyCount} missing support SLA polic${missingSupportSlaPolicyCount === 1 ? "y" : "ies"} before opening the affected support lane`
          : "Owner/company verification SLA policies are recorded; keep escalation owners current"
      ],
      actions: [
        { label: "Search and claim", page: "centreClaimSearch" },
        { label: "Centre directory", section: "centres" },
        { label: "Trust operations", section: "trust" }
      ]
    },
    {
      id: "moderation",
      title: "Trust, reports and moderation",
      owner: "Support team",
      status: openTrustReports.length || profileMediaRows.length ? "beta" : "build",
      progress: openTrustReports.length || profileMediaRows.length ? 70 : 45,
      summary: openTrustReports.length || pendingProfileMediaCount
        ? `${openTrustReports.length} open trust report${openTrustReports.length === 1 ? "" : "s"} and ${pendingProfileMediaCount} pending media review${pendingProfileMediaCount === 1 ? "" : "s"} are visible to admin.`
        : "Report, review and media moderation flows exist, but the live queues are empty for this account.",
      evidence: [
        "Reviews, recommendations and private reports are separated",
        "Reports can be marked reviewed or resolved",
        "Super admin can restrict or unrestrict real Firebase users and backend marketplace actions check restriction state",
        `${disputeEvidenceCount} refund/payment/dispute evidence record${disputeEvidenceCount === 1 ? "" : "s"} in backend support evidence`,
        `${retentionDecisionCount} evidence retention decision${retentionDecisionCount === 1 ? "" : "s"} recorded in the backend audit trail`,
        "User-submitted images are scanned automatically; safe media publishes and unsafe or inconclusive media is rejected",
        "Submitted media links carry rights/source status for copyright-aware review",
        "Super admin can inspect automated media decisions and audit history without becoming a publication bottleneck",
        "Support messages can be opened from report context"
      ],
      blockers: [
        "Media copyright/source review playbook exists and needs founder/legal approval before wider publication",
        retentionDueSoonCount
          ? `${retentionDueSoonCount} evidence record${retentionDueSoonCount === 1 ? "" : "s"} need retention review within 30 days`
          : "Evidence retention decisions can be recorded before archive/delete operations",
        "Child-safety and community standards need final legal text"
      ],
      actions: [
        { label: "Open moderation", page: "trust" },
        { label: "Messages", page: "messages" },
        { label: "Help centre", page: "help" }
      ]
    },
    {
      id: "profiles",
      title: "Public profiles and marketplace liquidity",
      owner: "Growth operations",
      status: publicProfileCount ? "beta" : "build",
      progress: Math.min(78, 25 + publicProfileCount * 13),
      summary: `${publicProfileCount} of ${accountRoleOptions.length} role profile${publicProfileCount === 1 ? " is" : "s are"} public-ready for the current account.`,
      evidence: [
        `${centreRecords.length} centre record${centreRecords.length === 1 ? "" : "s"} loaded in ${selectedCountryLabel}`,
        `${contentRecords.length} group/event record${contentRecords.length === 1 ? "" : "s"} loaded in ${selectedCountryLabel}`,
        "Incomplete role profiles stay hidden from public profile shortcuts",
        `${profileMediaRows.length} automated media decision record${profileMediaRows.length === 1 ? "" : "s"} available in the admin audit view`
      ],
      blockers: [
        "Profiles need a final deployed-domain check proving public/private fields load from Firestore ownership paths only",
        "Media galleries need approved review owners before wider publication",
        "Priority SEO routes now prerender after build; scale the route cap or add SSR before a full worldwide organic push"
      ],
      actions: [
        { label: "Users", section: "users" },
        { label: "Analytics", page: "analytics" },
        { label: "Public directory", page: "home" }
      ]
    },
    {
      id: "legal",
      title: "Legal, policies and support operations",
      owner: "Founder + legal",
      status: "ready",
      progress: 100,
      summary: `Terms, Privacy, Refund, Community Safety and role-specific policies are effective from ${legalConfig.policyUpdated}.`,
      evidence: [
        "Help centre links to Terms, Privacy, Refunds and Community Safety",
        "Centre, organiser and coach operating terms have dedicated pages",
        `Operator, support contact, governing law and worldwide scope are published for ${legalConfig.entity}`,
        "Payment flows display refund and reconciliation context",
        "Admin can track trust and refund disputes",
        `${activeSupportSlaPolicies.length} support lane SLA polic${activeSupportSlaPolicies.length === 1 ? "y is" : "ies are"} active`,
        "Operational notification template library covers receipts, booking holds, cancellations, no-shows, claims, refunds, moderation and payout statements"
      ],
      blockers: [
        "Review policies after material product, payment or regulatory changes",
        "Suppliers must publish country-appropriate cancellation windows before accepting payment",
        missingSupportSlaPolicyCount
          ? "Seed support SLA owners before opening missing claims, refund, safety, payment, media or booking support lanes"
          : "Keep support SLA wording and escalation owners current",
        seededOperationalTemplateCount === operationalNotificationTemplateKeys.length
          ? "Review operational notification wording after policy changes"
          : "Seed operational templates for receipts, claim requests, refund decisions, cancellations, no-shows and moderation outcomes"
      ],
      actions: [
        { label: "Help centre", page: "help" },
        { label: "Refund policy", page: "refundPolicy" },
        { label: "Privacy policy", page: "privacyPolicy" },
        { label: "Trust queue", section: "trust" }
      ]
    },
    {
      id: "revenue",
      title: "Revenue model and reporting",
      owner: "Platform finance",
      status: "build",
      progress: readyStripeAccountCount ? 46 : 24,
      summary: "Court booking fees, social session fees, coach bookings, memberships, events and featured listings are mapped as revenue lines, but live reporting needs backend ledger data.",
      evidence: [
        "Role dashboards include payments, payouts, refunds and membership/credit concepts",
	        `${membershipProductCount} centre membership or credit-pack product${membershipProductCount === 1 ? "" : "s"} configured in this account`,
	        "Recurring membership products can use backend Stripe subscription checkout with Connect payout routing",
	        "Backend payout statements can be generated from payments and refunds for centre, organiser and coach roles",
	        "Super admin settlement exports generate country/date CSV rows from backend payment and refund ledgers",
	        "Stripe balance transaction reconciliation can be run from settlement exports before payout review",
	        `${platformFeeSettings.length} platform fee setting${platformFeeSettings.length === 1 ? "" : "s"} available for country/currency checkout rules`,
	        "Hosted payment references can be stored for beta validation",
        `${accountEvents.length} event${accountEvents.length === 1 ? "" : "s"} exist in this account`
      ],
		      blockers: [
		        platformFeeSettings.length ? "Review platform fee settings by country before opening more payment categories" : "Create platform fee settings per country/currency",
		        "Review Stripe reconciliation exceptions before final payout runs",
		        "Signed email/SMS notification webhook can deliver provider-backed queue rows once provider credentials are connected"
		      ],
      actions: [
        { label: "Analytics", page: "analytics" },
        { label: "Centre payments", page: "centrePayments" },
        { label: "Create event", page: "createEvent" }
      ]
    }
  ];
};

function LaunchReadinessPanel({ tracks, selectedCountryLabel, navigate, setActiveSection }) {
  const blockers = tracks.reduce((total, track) => total + track.blockers.length, 0);
  const betaTracks = tracks.filter((track) => ["ready", "beta"].includes(track.status)).length;
  const blockedTracks = tracks.filter((track) => track.status === "blocked").length;
  const runAction = (action) => {
    if (action.section) {
      setActiveSection(action.section);
      return;
    }
    navigate(action.page, action.payload || {});
  };

  return (
    <section className="super-admin-panel launch-readiness-panel">
      <div className="section-title-row">
        <div>
          <p>Launch readiness</p>
          <h2>Production operations for {selectedCountryLabel}.</h2>
          <span>Each track separates what can be used for beta from what must stay blocked until backend, rules, Stripe Connect and legal workflows are complete.</span>
        </div>
      </div>
      <div className="launch-readiness-summary">
        {[
          ["Beta-usable tracks", betaTracks],
          ["Public-launch blockers", blockers],
          ["Hard blocked tracks", blockedTracks]
        ].map(([label, value]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      <div className="launch-track-grid">
        {tracks.map((track) => {
          const meta = launchStatusMeta[track.status] || launchStatusMeta.build;
          const Icon = meta.Icon;
          return (
            <article key={track.id} className={`launch-track ${meta.tone}`}>
              <div className="launch-track-head">
                <div>
                  <span>{track.owner}</span>
                  <strong>{track.title}</strong>
                </div>
                <em><Icon size={15} /> {meta.label}</em>
              </div>
              <p>{track.summary}</p>
              <div className="launch-progress" aria-label={`${track.title} ${track.progress}% complete`}>
                <i style={{ width: `${track.progress}%` }} />
              </div>
              <div className="launch-track-columns">
                <div>
                  <h3>Evidence</h3>
                  {track.evidence.map((item) => <span key={item}><Check size={14} /> {item}</span>)}
                </div>
                <div>
                  <h3>Blockers</h3>
                  {track.blockers.map((item) => <span key={item}><CircleHelp size={14} /> {item}</span>)}
                </div>
              </div>
              <div className="launch-track-actions">
                {track.actions.map((action) => (
                  <button type="button" key={action.label} onClick={() => runAction(action)}>{action.label}</button>
                ))}
              </div>
            </article>
          );
        })}
      </div>
      <section className="backend-model-panel">
        <div className="section-title-row compact">
          <div>
            <p>Backend data model</p>
            <h2>Collections required for real marketplace launch.</h2>
            <span>These collections match the Firebase rules and give bookings, payouts, claims, reports and memberships separate ownership boundaries.</span>
          </div>
        </div>
        <div className="backend-model-grid">
          {marketplaceBackendCollections.map((item) => (
            <article key={item.collection}>
              <strong>{item.collection}</strong>
              <span>{item.owner}</span>
              <p>{item.purpose}</p>
              <em>{item.writePath}</em>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function ProductionCertificationPanel({ launchEvidence = [], countryCode = "all", setActiveSection, navigate }) {
  const activeCountryCode = countryCode === "all" ? "" : normaliseCountryCode(countryCode);
  const [form, setForm] = useState({
    evidenceKey: productionCertificationItems[0]?.evidenceKey || "firebase_deploy",
    status: "needs_review",
    evidenceUrl: "",
    note: "",
    environment: "production",
    countryCode: activeCountryCode
  });
  const [state, setState] = useState({ status: "idle", message: "" });
  const hasRealFirebaseSession = Boolean(firebaseAuth?.currentUser);
  const evidenceForItem = (item) => [...launchEvidence]
    .filter((row) => row.evidenceKey === item.evidenceKey && (row.environment || "production") === "production")
    .filter((row) => activeCountryCode ? row.countryCode === activeCountryCode : true)
    .sort((a, b) => String(b.updatedAt || b.checkedAt || "").localeCompare(String(a.updatedAt || a.checkedAt || "")))[0];
  const evidenceByKey = Object.fromEntries(productionCertificationItems.map((item) => [item.evidenceKey, evidenceForItem(item)]));
  const confirmedCount = productionCertificationItems.filter((item) => evidenceByKey[item.evidenceKey]?.status === "confirmed").length;
  const needsReviewCount = productionCertificationItems.length - confirmedCount;
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selectedItem = productionCertificationItems.find((item) => item.evidenceKey === form.evidenceKey) || productionCertificationItems[0];
  const recordEvidence = async () => {
    setState({ status: "loading", message: "Recording launch evidence..." });
    try {
      const result = await callMarketplaceFunction("recordLaunchEvidence", {
        ...form,
        evidenceTitle: selectedItem.title,
        confirmationEnvKey: selectedItem.confirmationEnvKey,
        countryCode: form.countryCode === "all" ? "" : form.countryCode
      });
      setState({ status: "ready", message: `${selectedItem.title} evidence saved as ${result.status}.` });
      setForm((current) => ({ ...current, evidenceUrl: "", note: "", status: "needs_review" }));
    } catch (error) {
      setState({ status: "error", message: marketplaceFunctionErrorMessage(error) });
    }
  };

  return (
    <section className="super-admin-panel production-certification-panel">
      <div className="section-title-row">
        <div>
          <p>Strict production evidence</p>
          <h2>Public launch confirmations still need real external proof.</h2>
          <span>Record confirmations only after Firebase, Stripe, webhook, legal and deployed-domain evidence exists, and refresh them after material production changes.</span>
        </div>
        <button type="button" onClick={() => setActiveSection?.("security")}>Security checks</button>
      </div>
      <div className="production-certification-summary">
        {[
          ["Confirmed", confirmedCount, "Strict launch evidence recorded"],
          ["Needs proof", needsReviewCount, "External confirmations still open"],
          ["Ledger rows", launchEvidence.length, "Firestore launchEvidence records"]
        ].map(([label, value, text]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{text}</em>
          </article>
        ))}
      </div>
      <div className="production-certification-grid">
        {productionCertificationItems.map((item) => (
          <article key={item.evidenceKey} className={evidenceByKey[item.evidenceKey]?.status === "confirmed" ? "confirmed" : ""}>
            <div>
              {evidenceByKey[item.evidenceKey]?.status === "confirmed" ? <BadgeCheck size={18} /> : <CircleHelp size={18} />}
              <span>{item.owner}</span>
            </div>
            <strong>{item.title}</strong>
            <p>{item.evidence}</p>
            <em>{item.action}</em>
            <small className={`launch-evidence-pill ${evidenceByKey[item.evidenceKey]?.status || "pending"}`}>
              {evidenceByKey[item.evidenceKey]?.status === "confirmed" ? "Evidence confirmed" : "Evidence needed"}
            </small>
            {evidenceByKey[item.evidenceKey] && (
              <small>
                {formatAdminDateTime(evidenceByKey[item.evidenceKey].updatedAt || evidenceByKey[item.evidenceKey].checkedAt)}
                {evidenceByKey[item.evidenceKey].evidenceUrl ? " · link attached" : ""}
              </small>
            )}
          </article>
        ))}
      </div>
      <div className="production-evidence-form">
        <label>
          <span>Evidence item</span>
          <select value={form.evidenceKey} onChange={(event) => updateField("evidenceKey", event.target.value)}>
            {productionCertificationItems.map((item) => <option key={item.evidenceKey} value={item.evidenceKey}>{item.title}</option>)}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
            <option value="needs_review">Needs review</option>
            <option value="confirmed">Confirmed</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </label>
        <label>
          <span>Country</span>
          <input value={form.countryCode} onChange={(event) => updateField("countryCode", event.target.value.toUpperCase().slice(0, 2))} placeholder="Global" />
        </label>
        <label>
          <span>Evidence URL</span>
          <input value={form.evidenceUrl} onChange={(event) => updateField("evidenceUrl", event.target.value)} placeholder="https://..." />
        </label>
        <label className="wide">
          <span>Evidence note</span>
          <textarea value={form.note} onChange={(event) => updateField("note", event.target.value)} placeholder="Summarise what was checked, who checked it, and where the proof is stored." />
        </label>
        <button type="button" onClick={recordEvidence} disabled={!hasRealFirebaseSession || state.status === "loading"}>
          {state.status === "loading" ? "Recording..." : "Record launch evidence"}
        </button>
      </div>
      <small>{hasRealFirebaseSession ? "Every saved evidence item writes a launchEvidence row and admin audit entry." : "Sign in with the real Firebase super-admin phone to record launch evidence."}</small>
      {state.message && <div className={`auth-message stripe-connect-message ${state.status}`}>{state.message}</div>}
      <div className="production-certification-command">
        <code>BADMINTA_PREFLIGHT_FIREBASE_DEPLOY_CONFIRMED=true BADMINTA_PREFLIGHT_APP_CHECK_CONFIRMED=true BADMINTA_PREFLIGHT_FIRESTORE_BACKUPS_CONFIRMED=true BADMINTA_PREFLIGHT_BILLING_ALERTS_CONFIRMED=true BADMINTA_PREFLIGHT_MONITORING_CONFIRMED=true BADMINTA_PREFLIGHT_STRIPE_SECRETS_CONFIRMED=true BADMINTA_PREFLIGHT_STRIPE_WEBHOOK_TESTED=true BADMINTA_PREFLIGHT_NOTIFICATION_DISPATCHER_TESTED=true BADMINTA_PREFLIGHT_LEGAL_APPROVED=true BADMINTA_PREFLIGHT_PRODUCTION_SMOKE_TESTED=true npm run audit:production-preflight -- --strict --env=.env.production</code>
        <button type="button" onClick={() => navigate("help")}>Open support docs</button>
      </div>
    </section>
  );
}

function SuperAdministratorPage({ auth, accountProfile, accountSyncStatus = {}, platformLedgerData = defaultPlatformLedgerData, saveAccountProfile, navigate, venueDirectory = [], adminDirectoryStatus = {}, groupDirectory = [], playerDirectory = [], coachDirectory = [], events = [], preferredPhoneCountryCode = "" }) {
  const access = useSuperAdminAccess(auth, accountProfile);
  const [activeSection, setActiveSection] = useState(initialAdminSection);
  const [query, setQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [adminActionMessage, setAdminActionMessage] = useState({ status: "idle", text: "" });
  const [adminSignupLedger, setAdminSignupLedger] = useState({
    status: "idle",
    accounts: [],
    projectIdentityCount: 0,
    excludedIdentityCount: 0,
    fetchedAt: "",
    message: "Waiting for administrator authentication."
  });
  const adminSignupRequestRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (activeSection === "overview") url.searchParams.delete("workspace");
    else url.searchParams.set("workspace", activeSection);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, [activeSection]);

  const refreshAdminSignupAccounts = async () => {
    if (!access.allowed || !auth.user) return;
    const requestId = adminSignupRequestRef.current + 1;
    adminSignupRequestRef.current = requestId;
    setAdminSignupLedger((current) => ({
      ...current,
      status: current.accounts.length ? "refreshing" : "loading",
      message: current.accounts.length
        ? "Refreshing Firebase Authentication accounts and onboarding progress."
        : "Loading every Firebase Authentication account and merging saved onboarding progress."
    }));
    try {
      await loadPhoneNumberRuntime().catch(() => []);
      const accountMap = new globalThis.Map();
      let pageToken = "";
      let pageCount = 0;
      do {
        const result = await callMarketplaceFunction("listAdminSignupAccounts", {
          limit: 500,
          pageToken
        });
        (result.accounts || []).forEach((account) => accountMap.set(account.uid || account.id, account));
        pageToken = result.nextPageToken || "";
        pageCount += 1;
        if (pageCount >= 100 && pageToken) throw new Error("The signup ledger exceeded 50,000 accounts. Add server-side admin pagination before continuing.");
      } while (pageToken);
      if (adminSignupRequestRef.current !== requestId) return;
      const projectAccounts = Array.from(accountMap.values());
      const accounts = projectAccounts
        .filter((account) => account.isBadmintaSignup === true)
        .sort((first, second) => String(second.authCreatedAt || "").localeCompare(String(first.authCreatedAt || "")));
      const excludedIdentityCount = projectAccounts.length - accounts.length;
      const phoneOnlyCount = accounts.filter((account) => account.signupProgress?.onboardingStatus === "phone_verified_only").length;
      setAdminSignupLedger({
        status: "ready",
        accounts,
        projectIdentityCount: projectAccounts.length,
        excludedIdentityCount,
        fetchedAt: new Date().toISOString(),
        message: `${accounts.length} Badminta signup account${accounts.length === 1 ? "" : "s"} loaded from Firebase Auth. ${phoneOnlyCount} stopped after phone verification. ${excludedIdentityCount} unrelated identit${excludedIdentityCount === 1 ? "y" : "ies"} from the shared Firebase project are excluded.`
      });
    } catch (error) {
      if (adminSignupRequestRef.current !== requestId) return;
      setAdminSignupLedger((current) => ({
        ...current,
        status: "error",
        message: marketplaceFunctionErrorMessage(error) || "Firebase Authentication signups could not be loaded."
      }));
    }
  };

  useEffect(() => {
    if (!access.allowed || !auth.user?.uid || !["overview", "users", "players", "centres"].includes(activeSection)) return undefined;
    refreshAdminSignupAccounts();
    return () => {
      adminSignupRequestRef.current += 1;
    };
  }, [access.allowed, auth.user?.uid, activeSection]);

  const decisions = { ...platformLedgerDecisionSummary(platformLedgerData), ...adminDecisionSummary(accountProfile) };
  const auditEntries = adminAuditLog(accountProfile);
  const adminUpdates = adminRecordUpdateSummary(accountProfile);
  const signupLedgerLoading = ["idle", "loading"].includes(adminSignupLedger.status);
  const signupLedgerLimited = adminSignupLedger.status === "error";
  const hasAuthoritativeSignupLedger = ["ready", "refreshing"].includes(adminSignupLedger.status) || adminSignupLedger.accounts.length > 0;
  const authoritativeUsers = hasAuthoritativeSignupLedger
    ? adminSignupLedger.accounts
    : signupLedgerLimited ? platformLedgerData.users || [] : [];
  const rawUserAndProfileRecords = applyAdminRecordUpdates(buildSuperAdminUserRecords(
    accountProfile,
    playerDirectory,
    coachDirectory,
    authoritativeUsers,
    { includeCurrentAccount: signupLedgerLimited && authoritativeUsers.length === 0 }
  ), adminUpdates);
  const rawUserRecords = rawUserAndProfileRecords.filter((record) => record.recordKind === "account");
  const rawCentreDirectoryRecords = applyAdminRecordUpdates(buildSuperAdminCentreRecords(venueDirectory, accountProfile, platformLedgerData.claims || []), adminUpdates);
  const rawOwnerCentreRecords = rawCentreDirectoryRecords.filter(isAdminCentreRecordOwnerManaged);
  const rawCentreClaimRecords = rawCentreDirectoryRecords.filter(isBackendClaimRecord);
  const rawCentreRecords = [...rawOwnerCentreRecords, ...rawCentreClaimRecords];
  const rawContentRecords = applyAdminRecordUpdates(buildSuperAdminContentRecords(accountProfile, groupDirectory, events), adminUpdates);
  const rawTrustReports = applyAdminRecordUpdates([
    ...adminTrustReportsFromPlatform(platformLedgerData),
    ...getTrustReports(accountProfile)
  ].map((report) => attachAdminCountry(report, report, accountProfile)), adminUpdates);
  const rawProfileMediaRows = adminProfileMediaRowsFromPlatform(platformLedgerData.users || [], platformLedgerData.profileMediaReviews || []);
  const rawPlayerProfileRecords = rawUserAndProfileRecords.filter((record) => record.recordKind === "player");
  const rateLimitRows = platformLedgerData.rateLimits || [];
  const stripeAccountRows = platformLedgerData.stripeAccounts || [];
  const profileSubscriptionRows = platformLedgerData.profileSubscriptions || [];
  const activeProfileSubscriptionRows = profileSubscriptionRows.filter((subscription) => (
    subscription.entitlementsActive === true
    && ["active", "trialing"].includes(String(subscription.status || "").toLowerCase())
  ));
  const profileSubscriptionMrrCents = activeProfileSubscriptionRows.reduce((total, subscription) => (
    total + (String(subscription.billingInterval || "monthly").toLowerCase() === "annual"
      ? Math.round(Number(subscription.amountCents || 0) / 12)
      : Number(subscription.amountCents || 0))
  ), 0);
  const activeProfileSubscriptionRoleCounts = profileSubscriptionRoleOrder.reduce((counts, role) => ({
    ...counts,
    [role]: activeProfileSubscriptionRows.filter((subscription) => String(subscription.role || "").toLowerCase() === role).length
  }), {});
  const launchEvidenceRows = platformLedgerData.launchEvidence || [];
  const supportSlaPolicyRows = platformLedgerData.supportSlaPolicies || [];
  const activeRateLimitRows = rateLimitRows.filter(isActiveRateLimitWindow);
  const exhaustedRateLimitRows = rateLimitRows.filter(isExhaustedRateLimitWindow);
  const readyStripeAccountRows = stripeAccountRows.filter(isStripeAccountCheckoutReady);
  const stripeAccountReviewRows = stripeAccountRows.filter(isStripeAccountNeedsReview);
  const rawAllRecords = [...rawUserRecords, ...rawCentreRecords, ...rawContentRecords];
  const countryOptions = buildAdminCountryOptions({
    userRecords: rawUserRecords,
    centreRecords: rawCentreRecords,
    contentRecords: rawContentRecords,
    trustReports: rawTrustReports,
    profileMediaRows: rawProfileMediaRows
  });
  const selectedCountryCode = countryFilter === "all"
    ? ""
    : countryFilter === adminUnknownCountryCode ? adminUnknownCountryCode : normaliseCountryCode(countryFilter);
  const selectedCountry = countryOptions.find((option) => option.code === selectedCountryCode);
  const selectedCountryLabel = selectedCountryCode ? selectedCountry?.name || getCountryName(selectedCountryCode) || selectedCountryCode : "All countries";
  const visibleCountryOptions = selectedCountryCode
    ? countryOptions.filter((country) => country.code === selectedCountryCode)
    : showAllCountries ? countryOptions : countryOptions.slice(0, 12);
  const rawRecordById = new globalThis.Map(rawAllRecords.map((record) => [record.id, record]));
  const userRecords = filterAdminRecordsByCountry(rawUserRecords, selectedCountryCode);
  const playerProfileRecords = filterAdminRecordsByCountry(rawPlayerProfileRecords, selectedCountryCode);
  const centreRecords = filterAdminRecordsByCountry(rawCentreRecords, selectedCountryCode);
  const ownerCentreRecords = filterAdminRecordsByCountry(rawOwnerCentreRecords, selectedCountryCode);
  const centreClaimRecords = filterAdminRecordsByCountry(rawCentreClaimRecords, selectedCountryCode);
  const contentRecords = filterAdminRecordsByCountry(rawContentRecords, selectedCountryCode);
  const trustReports = filterAdminRecordsByCountry(rawTrustReports, selectedCountryCode);
  const profileMediaRows = filterAdminRecordsByCountry(rawProfileMediaRows, selectedCountryCode);
  const pendingProfileMediaRows = profileMediaRows.filter((row) => row.status === "pending_review");
  const auditEntriesForCountry = selectedCountryCode
    ? auditEntries.filter((entry) => {
      const explicitCountry = normaliseCountryCode(entry.countryCode || entry.country);
      if (explicitCountry) return explicitCountry === selectedCountryCode;
      const record = rawRecordById.get(entry.subjectId);
      return record ? getAdminRecordCountryCode(record) === selectedCountryCode : false;
    })
    : auditEntries;
  const openTrustReports = trustReports.filter(isOpenTrustReport);
  const allRecords = [...userRecords, ...centreRecords, ...contentRecords];
  const pendingRecords = allRecords.filter((record) => /missing|incomplete|review|draft|not listed/i.test(`${record.status} ${record.detail}`));
  const launchReadinessTracks = buildProductionLaunchTracks({
    accountProfile,
    centreRecords,
    contentRecords,
    openTrustReports,
    auditEntries: auditEntriesForCountry,
    supportEvidence: platformLedgerData.supportEvidence || [],
    notificationTemplates: platformLedgerData.notificationTemplates || [],
    notificationQueue: platformLedgerData.notificationQueue || [],
    lessonRequests: platformLedgerData.lessonRequests || [],
    coachBookings: platformLedgerData.coachBookings || [],
    stripeAccounts: stripeAccountRows,
    stripeDisputes: platformLedgerData.stripeDisputes || [],
    supportSlaPolicies: supportSlaPolicyRows.filter((policy) => !selectedCountryCode || !policy.countryCode || normaliseCountryCode(policy.countryCode) === selectedCountryCode),
    rateLimits: rateLimitRows,
    launchEvidence: launchEvidenceRows,
    platformFeeSettings: platformLedgerData.platformFeeSettings || [],
    centreMergeDecisions: platformLedgerData.centreMergeDecisions || [],
    profileMediaRows,
    selectedCountryLabel
  });

  const mirrorAdminDecisionToBackend = (record, decision, detail = {}) => {
    callMarketplaceFunction("recordAdminDecision", {
      recordId: record.id,
      recordName: record.name,
      scope: record.scope,
      countryCode: record.countryCode,
      decision,
      detail: {
        country: record.country,
        status: record.status,
        owner: record.owner,
        backendCentreId: record.backendCentreId || record.payload?.venue?.serverCentreId || record.payload?.venue?.firestoreSourceId || "",
        ownerManaged: Boolean(record.ownerManaged),
        bookingReady: Boolean(record.bookingReady),
        bookingReadinessMissing: record.bookingReadinessMissing || [],
        ...detail
      }
    }).catch(() => {});
  };

  const mirrorAdminUndoToBackend = (record, previousDecision) => {
    callMarketplaceFunction("undoAdminDecision", {
      recordId: record.id,
      recordName: record.name,
      scope: record.scope,
      countryCode: record.countryCode,
      previousDecision
    }).catch(() => {});
  };

  const recordUserRestriction = async (record, restricted) => {
    if (!record.backendUserRecord || !record.userId) return false;
    const decision = restricted ? "Restricted" : "Restriction removed";
    setAdminActionMessage({
      status: "loading",
      text: restricted ? `Restricting ${record.name}...` : `Removing restriction for ${record.name}...`
    });
    try {
      await callMarketplaceFunction("recordUserRestrictionDecision", {
        userId: record.userId,
        restricted,
        countryCode: record.countryCode,
        note: restricted
          ? "Restricted from the administrator command centre."
          : "Restriction removed from the administrator command centre."
      });
      const nextDecisions = { ...decisions };
      if (restricted) nextDecisions[record.id] = "Restricted";
      else delete nextDecisions[record.id];
      const entry = {
        id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-${decision}`)}`,
        subjectId: record.id,
        subject: record.name,
        scope: record.scope,
        country: record.country,
        countryCode: record.countryCode,
        decision,
        actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
        createdAt: new Date().toISOString()
      };
      saveAccountProfile?.({
        adminDecisions: nextDecisions,
        adminAuditLog: [entry, ...auditEntries].slice(0, 100)
      });
      setAdminActionMessage({
        status: restricted ? "needs-action" : "ready",
        text: restricted ? `${record.name} is restricted from marketplace actions.` : `${record.name} can use marketplace actions again.`
      });
      return true;
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
      return true;
    }
  };

  const recordClaimDecision = async (record, decision) => {
    const claimId = backendClaimIdFromRecord(record);
    if (!claimId) return;
    const label = adminClaimDecisionLabel(decision);
    setAdminActionMessage({ status: "loading", text: `${label} is being sent to the backend claim review workflow...` });
    try {
      await callMarketplaceFunction("reviewCentreClaim", {
        claimId,
        decision,
        note: `${label} from the administrator command centre.`
      });
      const entry = {
        id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-${decision}`)}`,
        subjectId: record.id,
        subject: record.name,
        scope: record.scope,
        country: record.country,
        countryCode: record.countryCode,
        decision: label,
        actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
        createdAt: new Date().toISOString()
      };
      saveAccountProfile?.({
        adminDecisions: { ...decisions, [record.id]: label },
        adminAuditLog: [entry, ...auditEntries].slice(0, 100)
      });
      setAdminActionMessage({ status: "ready", text: `${label} saved against backend claim ${claimId}.` });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
  };

  const undoClaimDecision = async (record) => {
    const claimId = backendClaimIdFromRecord(record);
    if (!claimId) return;
    setAdminActionMessage({ status: "loading", text: "Undoing backend claim decision..." });
    try {
      await callMarketplaceFunction("undoCentreClaimDecision", { claimId });
      const previousDecision = decisions[record.id] || record.status || "claim decision";
      const nextDecisions = { ...decisions };
      delete nextDecisions[record.id];
      const entry = {
        id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-undo-claim`)}`,
        subjectId: record.id,
        subject: record.name,
        scope: record.scope,
        country: record.country,
        countryCode: record.countryCode,
        decision: `Undo ${previousDecision}`,
        actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
        createdAt: new Date().toISOString()
      };
      saveAccountProfile?.({
        adminDecisions: nextDecisions,
        adminAuditLog: [entry, ...auditEntries].slice(0, 100)
      });
      setAdminActionMessage({ status: "ready", text: `Backend claim ${claimId} returned to pending review.` });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
  };

  const recordDecision = async (record, decision) => {
    if (decision === "Verified" && !canAdminVerifyRecord(record)) return;
    if (decision === "Restricted" && record.backendUserRecord) {
      await recordUserRestriction(record, true);
      return;
    }
    mirrorAdminDecisionToBackend(record, decision);
    const entry = {
      id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-${decision}`)}`,
      subjectId: record.id,
      subject: record.name,
      scope: record.scope,
      country: record.country,
      countryCode: record.countryCode,
      decision,
      actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
      createdAt: new Date().toISOString()
    };
    saveAccountProfile?.({
      adminDecisions: { ...decisions, [record.id]: decision },
      adminAuditLog: [entry, ...auditEntries].slice(0, 100)
    });
  };

  const undoRecordDecision = async (record) => {
    const previousDecision = decisions[record.id];
    if (record.backendUserRecord && (record.restrictedUser || previousDecision === "Restricted")) {
      await recordUserRestriction(record, false);
      return;
    }
    if (!previousDecision) return;
    mirrorAdminUndoToBackend(record, previousDecision);
    const nextDecisions = { ...decisions };
    delete nextDecisions[record.id];
    const entry = {
      id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-undo-${previousDecision}`)}`,
      subjectId: record.id,
      subject: record.name,
      scope: record.scope,
      country: record.country,
      countryCode: record.countryCode,
      decision: `Undo ${previousDecision}`,
      actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
      createdAt: new Date().toISOString()
    };
    saveAccountProfile?.({
      adminDecisions: nextDecisions,
      adminAuditLog: [entry, ...auditEntries].slice(0, 100)
    });
  };

  const saveRecordUpdate = (record, form) => {
    const cleanStatus = form.status === "Verified" && !canAdminVerifyRecord(record) ? record.status : form.status;
    mirrorAdminDecisionToBackend(record, "Updated admin record", {
      newName: form.name || record.name,
      newOwner: form.owner || record.owner,
      newStatus: cleanStatus || record.status,
      newCountryCode: form.countryCode || record.countryCode
    });
    const nextUpdates = {
      ...adminUpdates,
      [record.id]: {
        name: form.name || record.name,
        owner: form.owner || record.owner,
        detail: form.detail || record.detail,
        status: cleanStatus || record.status,
        countryCode: form.countryCode || record.countryCode,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.user?.phoneNumber || accountProfile.email || "Super admin"
      }
    };
    const entry = {
      id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-edit`)}`,
      subjectId: record.id,
      subject: form.name || record.name,
      scope: record.scope,
      country: getCountryName(form.countryCode || record.countryCode) || record.country,
      countryCode: form.countryCode || record.countryCode,
      decision: "Updated admin record",
      actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
      createdAt: new Date().toISOString()
    };
    saveAccountProfile?.({
      adminRecordUpdates: nextUpdates,
      adminAuditLog: [entry, ...auditEntries].slice(0, 100)
    });
    setEditingRecord(null);
  };

  const resetRecordUpdate = (record) => {
    if (!adminUpdates[record.id]) return;
    mirrorAdminDecisionToBackend(record, "Reset admin edits", {
      resetUpdate: adminUpdates[record.id]
    });
    const nextUpdates = { ...adminUpdates };
    delete nextUpdates[record.id];
    const entry = {
      id: `admin-audit-${Date.now()}-${shortStableHash(`${record.id}-reset-edit`)}`,
      subjectId: record.id,
      subject: record.name,
      scope: record.scope,
      country: record.country,
      countryCode: record.countryCode,
      decision: "Reset admin edits",
      actor: auth.user?.phoneNumber || accountProfile.email || "Super admin",
      createdAt: new Date().toISOString()
    };
    saveAccountProfile?.({
      adminRecordUpdates: nextUpdates,
      adminAuditLog: [entry, ...auditEntries].slice(0, 100)
    });
    if (editingRecord?.id === record.id) setEditingRecord(null);
  };

  const addAdminSupportEvidence = async (report, evidence) => {
    const subject = trustReportBackendSubject(report);
    if (!subject.subjectId) throw new Error("This report is not linked to a backend ledger record.");
    return callMarketplaceFunction("recordSupportEvidence", {
      subjectCollection: subject.collection,
      subjectId: subject.subjectId,
      subjectType: report.title || report.kind || "trust",
      countryCode: report.countryCode || selectedCountryCode || "",
      evidenceType: evidence.evidenceType,
      title: evidence.title || `${report.subject} evidence`,
      note: evidence.note,
      sourceUrl: evidence.sourceUrl,
      storagePath: evidence.storagePath
    });
  };

  const queueAdminSupportFollowUp = async (report, notification) => {
    const subject = trustReportBackendSubject(report);
    if (!subject.subjectId) throw new Error("This report is not linked to a backend ledger record.");
    return callMarketplaceFunction("queueSupportNotification", {
      subjectCollection: subject.collection,
      subjectId: subject.subjectId,
      countryCode: report.countryCode || selectedCountryCode || "",
      templateKey: notification.templateKey,
      channel: notification.channel,
      message: notification.message
    });
  };

  const recordEvidenceRetentionDecision = async (decisionPayload) => {
    setAdminActionMessage({ status: "loading", text: "Recording evidence retention decision..." });
    try {
      const result = await callMarketplaceFunction("recordSupportEvidenceRetentionDecision", decisionPayload);
      setAdminActionMessage({
        status: "ready",
        text: `Evidence retention decision recorded for ${result.evidenceId}.`
      });
      return result;
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
      throw error;
    }
  };

  const processAdminNotification = async (notification) => {
    if (!notification?.id) return;
    setAdminActionMessage({ status: "loading", text: "Processing queued support notification..." });
    try {
      const result = await callMarketplaceFunction("processQueuedNotification", {
        notificationId: notification.id
      });
      setAdminActionMessage({
        status: result.deliveryStatus === "delivered" ? "ready" : "needs-action",
        text: result.deliveryStatus === "delivered"
          ? `${String(result.channel || "in-app").replace("_", " ")} support notification delivered.`
          : result.deliveryStatus === "provider_not_configured"
            ? "Notification is ready, but the signed email/SMS webhook URL and secret still need to be configured in Firebase."
            : `Notification processed with status: ${result.deliveryStatus || result.status || "needs attention"}.`
      });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
  };

  const testNotificationWebhook = async (channel = "email") => {
    const recipientEmail = accountProfile.email || auth.user?.email || "support@badminta.com";
    const recipientPhone = auth.user?.phoneNumber || "+61472729564";
    setAdminActionMessage({ status: "loading", text: `Testing ${channel} notification webhook...` });
    try {
      const result = await callMarketplaceFunction("testNotificationWebhookDelivery", {
        channel,
        countryCode: countryFilter === "all" ? "" : countryFilter,
        recipientEmail,
        recipientPhone,
        subject: `Badminta ${channel} webhook test`,
        message: `Badminta ${channel} provider webhook test from super admin launch operations.`
      });
      setAdminActionMessage({
        status: result.deliveryStatus === "delivered" ? "ready" : "needs-action",
        text: result.deliveryStatus === "delivered"
          ? `${channel.toUpperCase()} webhook delivered through ${result.provider || "provider"}.`
          : result.deliveryStatus === "provider_not_configured"
            ? "Signed notification webhook URL or secret is not configured in Firebase yet."
            : `${channel.toUpperCase()} webhook test returned ${result.deliveryStatus || result.status || "needs attention"}.`
      });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
  };

  const seedOperationalTemplates = async () => {
    setAdminActionMessage({ status: "loading", text: "Seeding operational notification templates..." });
    try {
      const result = await callMarketplaceFunction("seedOperationalNotificationTemplates", {
        countryCode: countryFilter === "all" ? "" : countryFilter,
        overwrite: false
      });
      setAdminActionMessage({
        status: "ready",
        text: `Operational templates checked: ${result.seededCount || 0} seeded, ${result.skippedCount || 0} already existed.`
      });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
  };

	  const releaseExpiredBookingHolds = async () => {
	    setAdminActionMessage({ status: "loading", text: "Sweeping expired court booking holds..." });
	    try {
	      const result = await callMarketplaceFunction("releaseExpiredCourtBookingHolds", {
        limit: 100
      });
      const changed = Number(result.released || 0) + Number(result.reconciled || 0);
      setAdminActionMessage({
        status: changed ? "ready" : "needs-action",
        text: changed
          ? `Expired hold sweep completed: ${result.released || 0} released, ${result.reconciled || 0} reconciled.`
          : `Expired hold sweep checked ${result.checked || 0} hold${Number(result.checked || 0) === 1 ? "" : "s"} and found no changes.`
      });
    } catch (error) {
	      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
	    }
	  };

	  const releaseExpiredMarketplaceReservations = async () => {
	    setAdminActionMessage({ status: "loading", text: "Sweeping expired group, event and coaching reservations..." });
	    try {
	      const result = await callMarketplaceFunction("releaseExpiredMarketplaceReservations", {
	        limit: 100
	      });
	      setAdminActionMessage({
	        status: Number(result.released || 0) ? "ready" : "needs-action",
	        text: Number(result.released || 0)
	          ? `Reservation sweep completed: ${result.released || 0} released across groups, events and lessons.`
	          : `Reservation sweep checked ${result.checked || 0} record${Number(result.checked || 0) === 1 ? "" : "s"} and found no expired holds.`
	      });
	    } catch (error) {
	      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
	    }
	  };

		  const reviewProfileMedia = async (row, decision) => {
    if (!row?.userId || !row.mediaUrl) return;
    const statusLabel = decision === "approved" ? "Approving" : "Rejecting";
    setAdminActionMessage({ status: "loading", text: `${statusLabel} ${row.roleLabel.toLowerCase()} media for ${row.ownerName}...` });
    try {
      await callMarketplaceFunction("recordProfileMediaDecision", {
        userId: row.userId,
        role: row.role,
        field: row.field,
        mediaUrl: row.mediaUrl,
        reviewId: row.latestReviewId || "",
        decision,
        note: `${decision === "approved" ? "Approved" : "Rejected"} from the administrator media review queue.`
      });
      setAdminActionMessage({
        status: decision === "approved" ? "ready" : "needs-action",
        text: `${row.roleLabel} media for ${row.ownerName} marked ${decision}.`
      });
    } catch (error) {
      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
    }
	  };

	  const processPendingProfileMedia = async () => {
	    setAdminActionMessage({ status: "loading", text: "Checking pending profile images on the secure backend..." });
	    try {
	      const result = await callMarketplaceFunction("moderatePendingProfileMedia", { limit: 30 });
	      const approved = Number(result.counts?.approved || 0);
	      const rejected = Number(result.counts?.rejected || 0);
	      const manual = Number(result.counts?.manual_review || 0);
	      const reconciled = Number(result.updatedProfiles || 0);
	      setAdminActionMessage({
	        status: approved ? "ready" : manual || rejected ? "needs-action" : "ready",
	        text: `Media safety check finished: ${approved} approved, ${rejected} rejected, ${manual} left for manual review, ${reconciled} public profile link${reconciled === 1 ? "" : "s"} reconciled.`
	      });
	    } catch (error) {
	      setAdminActionMessage({ status: "error", text: marketplaceFunctionErrorMessage(error) });
	    }
	  };

  if (!auth.user) {
    return (
      <section className="page super-admin-login-page">
        <div className="super-admin-login-card">
          <div className="super-admin-lock">
            <LockKeyhole size={34} />
          </div>
          <PhoneAuthPanel
            auth={auth}
            title="Administrator login"
            text="Administrator access is restricted to the verified owner phone number only."
            preferredCountryCode={preferredPhoneCountryCode}
          />
        </div>
      </section>
    );
  }

  if (access.status === "checking") {
    return (
      <section className="page super-admin-login-page">
        <div className="auth-status-card super-admin-status-card">
          <SearchingIndicator />
          <strong>Checking administrator access</strong>
          <span>{access.reason}</span>
        </div>
      </section>
    );
  }

  if (!access.allowed) {
    return (
      <section className="page super-admin-login-page">
        <div className="super-admin-denied">
          <LockKeyhole size={34} />
          <p>Administrator access denied</p>
          <h1>This verified account is not authorised for `/administrator`.</h1>
          <span>{access.reason} Only the authorised owner phone can open administrator tools.</span>
          <div>
            <button type="button" onClick={() => navigate("account")}>Back to account</button>
            <button type="button" onClick={async () => { await auth.logOut(); navigate("administrator"); }}>Use another phone</button>
          </div>
        </div>
      </section>
    );
  }

  const securityChecks = [
    ["Phone verified", auth.user.phoneNumber || auth.user.email || "Verified session", true],
    ["Admin authorization", access.source, true],
    ["Firestore profile sync", accountSyncStatus.message || "Profile sync state unknown", accountSyncStatus.state === "cloud" || accountSyncStatus.state === "syncing"],
    ["Platform ledgers", platformLedgerData.message || "Platform ledger sync waits for Firebase super-admin sign in", ["cloud", "syncing"].includes(platformLedgerData.status)],
    ["Firebase Auth signups", adminSignupLedger.message || "Protected signup ledger has not loaded", ["ready", "refreshing"].includes(adminSignupLedger.status)],
    ["Audit log", `${auditEntries.length} admin action${auditEntries.length === 1 ? "" : "s"}`, true],
    ["Server enforcement", "Owner phone is enforced by Cloud Functions, Firestore rules and Storage rules", true]
  ];
  const adminDirectoryState = adminDirectoryStatus.state || "idle";
  const adminDirectoryLoadedRows = Number(adminDirectoryStatus.loadedRows || venueDirectory.length || 0);
  const adminDirectoryTotalRows = Number(adminDirectoryStatus.totalRows || globalDirectoryTotalCount || rawCentreRecords.length || 0);
  const adminDirectoryMessage = adminDirectoryStatus.message || `${adminDirectoryLoadedRows.toLocaleString()} centre records loaded for administrator tools.`;
  const adminDirectoryMessageTone = adminDirectoryState === "loaded" ? "ready" : adminDirectoryState === "error" ? "error" : "needs-action";
  const phoneOnlySignupCount = adminSignupLedger.accounts.filter((account) => account.signupProgress?.onboardingStatus === "phone_verified_only").length;
  const failedDeliveryCount = (platformLedgerData.notificationQueue || []).filter((row) => /failed|error|blocked/i.test(`${row.status} ${row.deliveryStatus}`)).length;
  const unresolvedCentreClaimCount = centreClaimRecords.filter((record) => !/approved|rejected/i.test(record.status || "")).length;
  const activeBookingHoldCount = (platformLedgerData.slotLocks || []).filter((row) => /active|held|pending/i.test(row.status || "active")).length;
  const activeSectionMeta = adminSectionTabs.find(([id]) => id === activeSection) || adminSectionTabs[0];
  const adminNavigationBadge = {
    overview: pendingRecords.length + openTrustReports.length,
    launch: stripeAccountReviewRows.length,
    users: phoneOnlySignupCount,
    players: playerProfileRecords.length,
    centres: unresolvedCentreClaimCount,
    content: contentRecords.length,
    trust: openTrustReports.length + pendingProfileMediaRows.length,
    security: exhaustedRateLimitRows.length + failedDeliveryCount,
    audit: auditEntriesForCountry.length
  };
  const operationalKpis = [
    { label: "Badminta accounts", value: hasAuthoritativeSignupLedger ? userRecords.length.toLocaleString() : "Syncing", detail: `${phoneOnlySignupCount} stopped after phone verification`, section: "users", tone: phoneOnlySignupCount ? "warning" : "neutral" },
    { label: "Public players", value: playerProfileRecords.length.toLocaleString(), detail: `${pendingProfileMediaRows.length} media decisions pending`, section: "players", tone: pendingProfileMediaRows.length ? "warning" : "neutral" },
    { label: "Centre operators", value: ownerCentreRecords.length.toLocaleString(), detail: `${unresolvedCentreClaimCount} ownership claims open`, section: "centres", tone: unresolvedCentreClaimCount ? "warning" : "neutral" },
    { label: "Active upgrades", value: activeProfileSubscriptionRows.length.toLocaleString(), detail: `${stripeAccountReviewRows.length} payout accounts need review`, section: "launch", tone: stripeAccountReviewRows.length ? "critical" : "positive" },
    { label: "Live booking holds", value: activeBookingHoldCount.toLocaleString(), detail: "Court, session and lesson inventory", section: "launch", tone: "neutral" },
    { label: "Safety queue", value: (openTrustReports.length + pendingProfileMediaRows.length).toLocaleString(), detail: `${openTrustReports.length} open reports`, section: "trust", tone: openTrustReports.length ? "critical" : "positive" }
  ];

  return (
    <section className="page super-admin-page">
      <section className="super-admin-hero">
        <div>
          <div className="admin-console-heading">
            <span className="admin-live-indicator"><i /> Production</span>
            <span>{selectedCountryLabel}</span>
          </div>
          <h1>Badminta operations</h1>
          <span>Accounts, marketplace supply, bookings, revenue, support and platform health in one protected operating console.</span>
          <div className="super-admin-controls">
            <label className="super-admin-search">
              <Search size={18} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search admin records" />
            </label>
            <label className="super-admin-country-filter">
              <span><Globe2 size={17} /> Country</span>
              <select value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)}>
                <option value="all">All countries ({rawAllRecords.length})</option>
                {countryOptions.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({country.total} records{country.trust ? `, ${country.trust} trust` : ""})
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <aside>
          <span>Signed in as</span>
          <strong>{accountProfile.fullName || "Platform owner"}</strong>
          <small>{auth.user.phoneNumber || accountProfile.email || "Verified administrator"}</small>
          <button type="button" onClick={() => navigate("home")}>View marketplace <ExternalLink size={15} /></button>
        </aside>
      </section>
      {!hasAuthoritativeSignupLedger && ["users", "players"].includes(activeSection) && (
        <div className={`auth-message stripe-connect-message ${signupLedgerLimited ? "error" : "needs-action"}`}>
          {adminSignupLedger.message || "Loading Firebase Authentication accounts and public profile status."}
        </div>
      )}
      <div className="admin-kpi-grid">
        {operationalKpis.map((metric) => (
          <button type="button" key={metric.label} className={metric.tone} onClick={() => setActiveSection(metric.section)}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.detail}</small>
            <ChevronRight size={17} />
          </button>
        ))}
      </div>
      {editingRecord && (
        <SuperAdminEditPanel
          record={editingRecord}
          update={adminUpdates[editingRecord.id]}
          countries={countryOptions}
          onSave={saveRecordUpdate}
          onReset={resetRecordUpdate}
          onCancel={() => setEditingRecord(null)}
        />
      )}
      <div className="super-admin-layout">
        <aside className="super-admin-tabs">
          <div className="admin-nav-brand"><ShieldCheck size={18} /><span><strong>Admin console</strong><small>Owner access</small></span></div>
          {adminNavigationGroups.map((group) => (
            <div className="admin-nav-group" key={group.label}>
              <p>{group.label}</p>
              {group.items.map(([id, label, Icon]) => (
                <button type="button" key={id} className={activeSection === id ? "active" : ""} onClick={() => setActiveSection(id)} aria-current={activeSection === id ? "page" : undefined}>
                  <Icon size={18} />
                  <span>{label}</span>
                  {adminNavigationBadge[id] > 0 && <em>{adminNavigationBadge[id] > 999 ? "999+" : adminNavigationBadge[id]}</em>}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <main className="super-admin-workspace">
          <header className="admin-workspace-header">
            <div>
              <span>Workspace</span>
              <h2>{activeSectionMeta[1]}</h2>
            </div>
            <div>
              <span>{platformLedgerData.status === "cloud" ? "Live data" : platformLedgerData.status || "Connecting"}</span>
              {(["users", "players", "centres"].includes(activeSection)) && (
                <button type="button" onClick={refreshAdminSignupAccounts} disabled={signupLedgerLoading}>
                  {signupLedgerLoading ? "Syncing" : "Refresh records"}
                </button>
              )}
            </div>
          </header>
          {adminActionMessage.text && (
            <div className={`auth-message stripe-connect-message ${adminActionMessage.status}`}>{adminActionMessage.text}</div>
          )}
          {activeSection === "overview" && (
            <>
              <section className="super-admin-panel">
                <div className="section-title-row">
                  <div>
                    <p>Country breakdown</p>
                    <h2>{selectedCountryLabel}</h2>
                    <span>Click a country to filter all administrator stats, tables and trust queues.</span>
                  </div>
                  {countryFilter !== "all" && <button type="button" onClick={() => setCountryFilter("all")}>Show all countries</button>}
                </div>
                <div className="super-admin-country-grid">
                  <button type="button" className={countryFilter === "all" ? "active" : ""} onClick={() => setCountryFilter("all")}>
                    <strong>All countries</strong>
                    <span>{rawAllRecords.length} records</span>
                    <em>{rawCentreRecords.length} centres · {rawUserRecords.length} users · {rawContentRecords.length} events/groups · {rawTrustReports.length} trust · {rawProfileMediaRows.length} media</em>
                  </button>
                  {visibleCountryOptions.map((country) => (
                    <button type="button" key={country.code} className={countryFilter === country.code ? "active" : ""} onClick={() => setCountryFilter(country.code)}>
                      <strong>{country.name}</strong>
                      <span>{country.total} records</span>
                    <em>{country.centres} centres · {country.users} users · {country.content} events/groups · {country.trust} trust · {country.media} media</em>
                  </button>
                  ))}
                </div>
                {!selectedCountryCode && countryOptions.length > 12 && (
                  <button type="button" className="super-admin-country-expand" onClick={() => setShowAllCountries((current) => !current)}>
                    <Globe2 size={17} />
                    {showAllCountries ? "Show top countries" : `Show all ${countryOptions.length} countries`}
                    <ChevronDown size={17} className={showAllCountries ? "expanded" : ""} />
                  </button>
                )}
            </section>
              <section className="super-admin-panel admin-command-panel">
                <div className="section-title-row">
                  <div>
                    <p>Work queues</p>
                    <h2>Choose what needs attention</h2>
                    <span>Each workspace loads its own records and controls so the administrator home stays fast and readable.</span>
                  </div>
                </div>
                <div className="admin-command-grid">
                  {[
                    ["players", "Player profiles", hasAuthoritativeSignupLedger ? `${playerProfileRecords.length} loaded` : "Load accounts and profiles", UserCheck],
                    ["users", "Signup accounts", hasAuthoritativeSignupLedger ? `${userRecords.length} loaded` : "Load Firebase signups", UsersRound],
                    ["centres", "Centre signups", `${ownerCentreRecords.length.toLocaleString()} owner centre${ownerCentreRecords.length === 1 ? "" : "s"}`, Building2],
                    ["content", "Groups and events", `${contentRecords.length} public records`, CalendarDays],
                    ["trust", "Trust and support", `${openTrustReports.length + pendingProfileMediaRows.length} items need review`, ShieldCheck],
                    ["launch", "Payments and launch", `${stripeAccountReviewRows.length} payout accounts need review`, WalletCards]
                  ].map(([id, title, detail, Icon]) => (
                    <button type="button" key={id} onClick={() => setActiveSection(id)}>
                      <Icon size={20} />
                      <span><strong>{title}</strong><small>{detail}</small></span>
                      <ChevronRight size={18} />
                    </button>
                  ))}
                </div>
              </section>
	              <SuperAdminRecordTable
	                title="Needs attention"
                text={`Profiles, listings and platform records that need admin review first in ${selectedCountryLabel}.`}
                records={pendingRecords}
                decisions={decisions}
                updates={adminUpdates}
                onDecision={recordDecision}
                onUndoDecision={undoRecordDecision}
                onClaimDecision={recordClaimDecision}
                onUndoClaimDecision={undoClaimDecision}
                onEditRecord={setEditingRecord}
                onResetUpdate={resetRecordUpdate}
                navigate={navigate}
                emptyTitle="No pending records"
                query={query}
              />
              <div className="super-admin-overview-grid">
                <SuperAdminRecordTable title="Recent users" records={userRecords.slice(0, 6)} decisions={decisions} updates={adminUpdates} onDecision={recordDecision} onUndoDecision={undoRecordDecision} onClaimDecision={recordClaimDecision} onUndoClaimDecision={undoClaimDecision} onEditRecord={setEditingRecord} onResetUpdate={resetRecordUpdate} navigate={navigate} query={query} />
                <SuperAdminRecordTable title="Recent centre signups" records={ownerCentreRecords.slice(0, 6)} decisions={decisions} updates={adminUpdates} onDecision={recordDecision} onUndoDecision={undoRecordDecision} onClaimDecision={recordClaimDecision} onUndoClaimDecision={undoClaimDecision} onEditRecord={setEditingRecord} onResetUpdate={resetRecordUpdate} navigate={navigate} query={query} />
              </div>
            </>
	          )}
		          {activeSection === "launch" && (
		            <>
		              <ProductionCertificationPanel launchEvidence={launchEvidenceRows} countryCode={countryFilter} setActiveSection={setActiveSection} navigate={navigate} />
		              <SuperAdminEvidenceRetentionPanel
		                selectedCountryLabel={selectedCountryLabel}
		                countryCode={countryFilter}
		                supportEvidence={platformLedgerData.supportEvidence || []}
		                onRecordRetentionDecision={recordEvidenceRetentionDecision}
		              />
		              <SuperAdminSupportSlaPolicyPanel selectedCountryLabel={selectedCountryLabel} countryCode={countryFilter} policies={supportSlaPolicyRows} />
		              <SuperAdminPlatformFeeSettingsPanel selectedCountryLabel={selectedCountryLabel} countryCode={countryFilter} feeSettings={platformLedgerData.platformFeeSettings || []} />
		              <SuperAdminDuplicateMergePanel selectedCountryLabel={selectedCountryLabel} countryCode={countryFilter} mergeDecisions={platformLedgerData.centreMergeDecisions || []} />
		              <SuperAdminSettlementExportPanel selectedCountryLabel={selectedCountryLabel} countryCode={countryFilter} />
		              <SuperAdminRateLimitPanel selectedCountryLabel={selectedCountryLabel} rateLimits={rateLimitRows} setActiveSection={setActiveSection} />
	              <LaunchReadinessPanel
	                tracks={launchReadinessTracks}
	                selectedCountryLabel={selectedCountryLabel}
	                navigate={navigate}
	                setActiveSection={setActiveSection}
	              />
	            </>
	          )}
          {activeSection === "users" && (
            <>
              <SuperAdminSignupAccountsPanel
                records={userRecords}
                query={query}
                selectedCountryLabel={selectedCountryLabel}
                ledger={adminSignupLedger}
                onRefresh={refreshAdminSignupAccounts}
              />
              <SuperAdminRecordTable
                title="Account moderation controls"
                text={`Review, restrict or correct Firebase accounts in ${selectedCountryLabel}. Public player profiles remain in the dedicated Players tab.`}
                records={userRecords}
                decisions={decisions}
                updates={adminUpdates}
                onDecision={recordDecision}
                onUndoDecision={undoRecordDecision}
                onClaimDecision={recordClaimDecision}
                onUndoClaimDecision={undoClaimDecision}
                onEditRecord={setEditingRecord}
                onResetUpdate={resetRecordUpdate}
                navigate={navigate}
                query={query}
              />
            </>
          )}
          {activeSection === "players" && (
            <>
              <SuperAdminPlayerProfileGallery
                records={playerProfileRecords}
                query={query}
                navigate={navigate}
                onRefresh={refreshAdminSignupAccounts}
              />
              <SuperAdminRecordTable
                title="Player profile controls"
                text={`View, edit, review or restrict every loaded player profile in ${selectedCountryLabel}.`}
                records={playerProfileRecords}
                decisions={decisions}
                updates={adminUpdates}
                onDecision={recordDecision}
                onUndoDecision={undoRecordDecision}
                onClaimDecision={recordClaimDecision}
                onUndoClaimDecision={undoClaimDecision}
                onEditRecord={setEditingRecord}
                onResetUpdate={resetRecordUpdate}
                navigate={navigate}
                emptyTitle="No player profiles found"
                query={query}
              />
            </>
          )}
          {activeSection === "centres" && (
            <>
              <SuperAdminRecordTable
                title="Centre owner signups"
                text={`Centres created or managed by signed-up centre owners in ${selectedCountryLabel}. Automatically imported directory listings are excluded.`}
                records={ownerCentreRecords}
                decisions={decisions}
                updates={adminUpdates}
                onDecision={recordDecision}
                onUndoDecision={undoRecordDecision}
                onClaimDecision={recordClaimDecision}
                onUndoClaimDecision={undoClaimDecision}
                onEditRecord={setEditingRecord}
                onResetUpdate={resetRecordUpdate}
                navigate={navigate}
                emptyTitle="No centre owner signups found"
                query={query}
              />
              <SuperAdminRecordTable
                title="Centre ownership claims"
                text={`Claims submitted by signed-up users for existing centre listings in ${selectedCountryLabel}.`}
                records={centreClaimRecords}
                decisions={decisions}
                updates={adminUpdates}
                onDecision={recordDecision}
                onUndoDecision={undoRecordDecision}
                onClaimDecision={recordClaimDecision}
                onUndoClaimDecision={undoClaimDecision}
                onEditRecord={setEditingRecord}
                onResetUpdate={resetRecordUpdate}
                navigate={navigate}
                emptyTitle="No centre ownership claims found"
                query={query}
              />
            </>
          )}
          {activeSection === "content" && (
            <SuperAdminRecordTable
              title="Groups and events"
              text={`Monitor public sessions, event ownership, publishing status and payment-ready content in ${selectedCountryLabel}.`}
              records={contentRecords}
              decisions={decisions}
              updates={adminUpdates}
              onDecision={recordDecision}
              onUndoDecision={undoRecordDecision}
              onClaimDecision={recordClaimDecision}
              onUndoClaimDecision={undoClaimDecision}
              onEditRecord={setEditingRecord}
              onResetUpdate={resetRecordUpdate}
              navigate={navigate}
              query={query}
            />
          )}
          {activeSection === "trust" && (
            <>
              <SuperAdminSupportOperationsPanel
                selectedCountryLabel={selectedCountryLabel}
                selectedCountryCode={selectedCountryCode}
                centreRecords={centreRecords}
                contentRecords={contentRecords}
	                trustReports={trustReports}
	                openTrustReports={openTrustReports}
	                supportEvidence={platformLedgerData.supportEvidence || []}
	                notificationTemplates={platformLedgerData.notificationTemplates || []}
		                notificationQueue={platformLedgerData.notificationQueue || []}
		                notificationReceipts={platformLedgerData.notificationReceipts || []}
		                emailDeliveryStats={platformLedgerData.emailDeliveryStats || []}
		                emailCampaigns={platformLedgerData.emailCampaigns || []}
		                auditEntries={auditEntriesForCountry}
		                bookingHolds={platformLedgerData.slotLocks || []}
		                bookings={platformLedgerData.bookings || []}
		                sessionJoinRequests={platformLedgerData.sessionJoinRequests || []}
		                lessonRequests={platformLedgerData.lessonRequests || []}
		                stripeAccounts={stripeAccountRows}
		                navigate={navigate}
			                setActiveSection={setActiveSection}
		                onProcessNotification={processAdminNotification}
		                onReleaseExpiredHolds={releaseExpiredBookingHolds}
		                onReleaseExpiredReservations={releaseExpiredMarketplaceReservations}
		                onSeedTemplates={seedOperationalTemplates}
		                onTestNotificationWebhook={testNotificationWebhook}
              />
              <SuperAdminEvidenceRetentionPanel
                selectedCountryLabel={selectedCountryLabel}
                countryCode={countryFilter}
                supportEvidence={platformLedgerData.supportEvidence || []}
                onRecordRetentionDecision={recordEvidenceRetentionDecision}
              />
              <SuperAdminSupportSlaPolicyPanel selectedCountryLabel={selectedCountryLabel} countryCode={countryFilter} policies={supportSlaPolicyRows} />
              <SuperAdminProfileMediaReviewPanel
                selectedCountryLabel={selectedCountryLabel}
                mediaRows={profileMediaRows}
              />
              <section className="super-admin-panel">
                <div className="section-title-row">
                  <div>
                    <p>Trust operations</p>
                    <h2>{openTrustReports.length} open report{openTrustReports.length === 1 ? "" : "s"}</h2>
                    <span>{`Review unsafe behaviour, payment disputes, no-shows and marketplace quality reports in ${selectedCountryLabel}.`}</span>
                  </div>
                  <button type="button" onClick={() => navigate("trust")}>Open moderation</button>
                </div>
	                <TrustReportList
	                  reports={openTrustReports}
	                  navigate={navigate}
	                  accountProfile={accountProfile}
	                  saveAccountProfile={saveAccountProfile}
	                  onAddEvidence={addAdminSupportEvidence}
	                  onQueueNotification={queueAdminSupportFollowUp}
	                  notificationTemplates={platformLedgerData.notificationTemplates || []}
	                />
              </section>
            </>
          )}
          {activeSection === "security" && (
            <>
              <SuperAdminRateLimitPanel selectedCountryLabel={selectedCountryLabel} rateLimits={rateLimitRows} setActiveSection={setActiveSection} />
              <section className="super-admin-panel">
                <div className="section-title-row">
                  <div>
                    <p>Security model</p>
                    <h2>Admin access checks</h2>
                    <span>Production security should enforce the same owner-phone restriction in Firebase rules and server-side admin functions.</span>
                  </div>
                </div>
                <div className="super-admin-security-grid">
                  {securityChecks.map(([title, value, passed]) => (
                    <article key={title} className={passed ? "passed" : "warning"}>
                      {passed ? <BadgeCheck size={20} /> : <CircleHelp size={20} />}
                      <strong>{title}</strong>
                      <span>{value}</span>
                    </article>
                  ))}
                </div>
              </section>
            </>
          )}
          {activeSection === "audit" && (
            <section className="super-admin-panel">
              <div className="section-title-row">
                <div>
                  <p>Admin audit log</p>
                  <h2>{auditEntriesForCountry.length} recorded action{auditEntriesForCountry.length === 1 ? "" : "s"}</h2>
                  <span>{`Every admin decision made in this UI for ${selectedCountryLabel} is captured here for review.`}</span>
                </div>
              </div>
              {auditEntriesForCountry.length ? (
                <div className="super-admin-audit-list">
                  {auditEntriesForCountry.map((entry) => (
                    <article key={entry.id}>
                      <strong>{entry.decision}</strong>
                      <span>{entry.scope} · {entry.subject} · {entry.country || "Country pending"}</span>
                      <em>{new Date(entry.createdAt).toLocaleString()}</em>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="ops-empty">
                  <strong>No admin actions yet</strong>
                  <span>Verify, review or restrict a record to create the first audit entry.</span>
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </section>
  );
}

function FirebaseSetupNotice() {
  return (
    <div className="auth-alert">
      <LockKeyhole size={18} />
      <span>Add `VITE_FIREBASE_API_KEY`, `VITE_FIREBASE_AUTH_DOMAIN`, `VITE_FIREBASE_PROJECT_ID` and `VITE_FIREBASE_APP_ID` to enable live SMS verification.</span>
    </div>
  );
}

function PhoneAuthPanel({ auth, title = "Verify your phone", text = "Badminta uses phone verification before account details, player profiles, centre claims or group tools are created.", onVerified, beforeSend, preferredCountryCode = "", funnelRole = "", compactIntro = false }) {
  const initialCountryCode = browserPhoneCountryCode(preferredCountryCode);
  const [phoneCountryCode, setPhoneCountryCode] = useState(initialCountryCode);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [localMessage, setLocalMessage] = useState("");
  const [messageKind, setMessageKind] = useState("info");
  const [phoneCountries, setPhoneCountries] = useState(() => phoneDialingCountries);
  const [countriesReady, setCountriesReady] = useState(() => phoneDialingCountries.length > 1);
  const [submittedPhonePreview, setSubmittedPhonePreview] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [ageBand, setAgeBand] = useState("");
  const phoneInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const selectedPhoneCountry = getPhoneCountry(phoneCountryCode);
  const phonePreview = phone ? normalisePhoneNumber(phone, selectedPhoneCountry.code) : `${selectedPhoneCountry.dialCode} ...`;
  const usesBadmintaTestCode = isLocalTestPhone(phone, selectedPhoneCountry.code);
  const verificationCodeLength = usesBadmintaTestCode ? localTestSmsCode.length : 6;
  const codeRequested = auth.smsStatus === "sent" || auth.smsStatus === "verifying";

  useEffect(() => {
    let cancelled = false;
    loadPhoneNumberRuntime()
      .then((countries) => {
        if (cancelled) return;
        setPhoneCountries(countries);
        if (!phone) setPhoneCountryCode(browserPhoneCountryCode(preferredCountryCode));
        setCountriesReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setMessageKind("error");
          setLocalMessage("Country codes could not load. Refresh the page and try again.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [preferredCountryCode, phone]);

  useEffect(() => {
    if (codeRequested) codeInputRef.current?.focus?.();
  }, [codeRequested]);

  const updatePhone = (value) => {
    setPhone(value);
    const pastedCountry = phoneCountryFromInternationalNumber(value);
    if (pastedCountry) setPhoneCountryCode(pastedCountry.code);
  };

  const sendSms = async (event) => {
    event.preventDefault();
    setMessageKind("info");
    setLocalMessage(auth.captchaMode === "visible"
      ? "Complete the security check below. Your SMS will send as soon as it passes."
      : "Requesting your verification code...");
    try {
      await beforeSend?.();
      setSubmittedPhonePreview(phonePreview);
      await auth.sendCode(phone, selectedPhoneCountry.code);
      if (funnelRole) {
        trackMarketingFunnelEvent("phone_verification_requested", {
          role: funnelRole,
          dedupeId: `${funnelRole}_signup`
        });
      }
      setMessageKind("success");
      setLocalMessage(usesBadmintaTestCode ? `Badminta local test code ready. Enter ${localTestSmsCode} to continue.` : `SMS sent to ${phonePreview}. Enter the 6 digit code to continue.`);
    } catch (error) {
      setMessageKind("error");
      setLocalMessage(error.message);
      if (/valid mobile number/i.test(String(error.message || ""))) phoneInputRef.current?.focus?.();
    }
  };

  const confirmSms = async (event) => {
    event.preventDefault();
    if (!legalAccepted || !ageBand || ageBand === "under-13") {
      setMessageKind("error");
      setLocalMessage(ageBand === "under-13"
        ? "A parent or legal guardian must create and control the account in their own name for a child under 13."
        : "Choose your age group and accept the Terms, Privacy Policy and Youth Safety Notice before continuing.");
      return;
    }
    if (ageBand !== "18-plus") {
      const currentConsent = readPrivacyConsent();
      if (currentConsent?.advertising) {
        try { window.localStorage.setItem(privacyConsentStorageKey, JSON.stringify({ ...currentConsent, advertising: false, updatedAt: new Date().toISOString() })); } catch { /* Consent is still enforced for this page session. */ }
      }
    }
    try {
      window.localStorage.setItem(legalDeclarationStorageKey, JSON.stringify({
        termsAccepted: true,
        privacyAcknowledged: true,
        youthNoticeAcknowledged: ageBand !== "18-plus",
        ageBand,
        policyVersion: legalPolicyVersion,
        acceptedAt: new Date().toISOString()
      }));
    } catch {
      // The declaration is also enforced in the current signup UI when storage is unavailable.
    }
    setMessageKind("info");
    setLocalMessage("Checking your verification code...");
    try {
      const verifiedUser = await auth.verifyCode(code);
      if (funnelRole) {
        trackMarketingFunnelEvent("phone_verification_completed", {
          role: funnelRole,
          dedupeId: verifiedUser?.uid || `${funnelRole}_signup`,
          dedupeScope: "persistent"
        });
      }
      setMessageKind("success");
      setLocalMessage("Phone verified.");
      onVerified?.(verifiedUser);
    } catch (error) {
      setMessageKind("error");
      setLocalMessage(error.message);
    }
  };

  const changePhoneNumber = () => {
    auth.resetPhoneVerification?.();
    setCode("");
    setSubmittedPhonePreview("");
    setMessageKind("info");
    setLocalMessage("");
  };

  if (auth.loading) {
    return (
      <>
        {compactIntro ? (
          <div className="phone-auth-compact-heading">
            <span><Phone size={19} /></span>
            <div>
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
          </div>
        ) : (
          <>
            <p>Phone verification</p>
            <h1>{title}</h1>
            <span>{text}</span>
          </>
        )}
        <div className="auth-status-card session-check">
          <SearchingIndicator />
          <div>
            <strong>Preparing secure login</strong>
            <span>Checking for your verified Badminta account before opening the phone form.</span>
          </div>
        </div>
      </>
    );
  }

  if (auth.user) {
    return (
      <div className="verified-account">
        <BadgeCheck size={24} />
        <div>
          <strong>Phone verified</strong>
          <span>{auth.user.phoneNumber || "Signed in with Firebase phone auth"}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {compactIntro ? (
        <div className="phone-auth-compact-heading">
          <span><Phone size={19} /></span>
          <div>
            <strong>{title}</strong>
            <p>{text}</p>
          </div>
        </div>
      ) : (
        <>
          <p>Phone verification</p>
          <h1>{title}</h1>
          <span>{text}</span>
        </>
      )}
      {!auth.configured && <FirebaseSetupNotice />}
      {!countriesReady ? (
        <div className="auth-status-card">
          <SearchingIndicator />
          <strong>Loading worldwide country codes</strong>
        </div>
      ) : !codeRequested ? (
        <form className="phone-auth-form" onSubmit={sendSms}>
          <label>
            <span>Country code and mobile number</span>
            <div className="phone-number-row">
              <select
                value={selectedPhoneCountry.code}
                onChange={(event) => setPhoneCountryCode(event.target.value)}
                autoComplete="tel-country-code"
                aria-label="Phone country code"
              >
                {phoneCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name} {country.dialCode}
                  </option>
                ))}
              </select>
              <input
                ref={phoneInputRef}
                value={phone}
                onChange={(event) => updatePhone(event.target.value)}
                inputMode="tel"
                placeholder={selectedPhoneCountry.placeholder}
                autoComplete="tel-national"
                aria-label="Mobile number"
                required
              />
            </div>
          </label>
          <div className="phone-dial-preview">
            <Globe2 size={16} />
            <span>{selectedPhoneCountry.code === initialCountryCode ? "Auto-selected" : "Selected"} {selectedPhoneCountry.name}. SMS will be sent to <strong>{phonePreview}</strong>.</span>
          </div>
          {auth.captchaMode === "visible" && (
            <div className="captcha-help">
              <ShieldCheck size={17} />
              <span>Complete the security check once, then Badminta will send the SMS.</span>
            </div>
          )}
          <div id="firebase-recaptcha-container" className={auth.captchaMode === "visible" ? "visible" : "invisible"} />
          <button type="submit" disabled={!auth.configured || !phone || ["checking", "sending"].includes(auth.smsStatus)}>
            {auth.smsStatus === "checking" ? "Complete security check" : auth.smsStatus === "sending" ? "Sending SMS" : "Send verification code"}
          </button>
        </form>
      ) : (
        <form className="phone-auth-form phone-code-form" onSubmit={confirmSms}>
          <div className="phone-code-sent">
            <BadgeCheck size={20} />
            <div><strong>Code sent</strong><span>{submittedPhonePreview || phonePreview}</span></div>
          </div>
          <label>
            <span>{verificationCodeLength} digit SMS code</span>
            <input
              ref={codeInputRef}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, verificationCodeLength))}
              inputMode="numeric"
              placeholder={usesBadmintaTestCode ? localTestSmsCode : "000000"}
              autoComplete="one-time-code"
              aria-label="SMS verification code"
              required
            />
          </label>
          <div className="signup-legal-consent">
            <label>
              <span>Age group</span>
              <select value={ageBand} onChange={(event) => setAgeBand(event.target.value)} required>
                <option value="">Select age group</option>
                <option value="under-13">Under 13</option>
                <option value="13-15">13 to 15</option>
                <option value="16-17">16 to 17</option>
                <option value="18-plus">18 or older</option>
              </select>
            </label>
            {ageBand === "under-13" && <p>A child under 13 cannot create an independent account. A parent or legal guardian must create and control the account in their own name.</p>}
            {ageBand && !["under-13", "18-plus"].includes(ageBand) && <p>A parent or legal guardian must approve this account. Exact home address and private contact details must not be published. Paid provider, payout and centre-owner accounts require an adult.</p>}
            <label className="signup-legal-check">
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(event) => setLegalAccepted(event.target.checked)}
                aria-describedby="signup-consent-copy"
              />
              <span className="signup-legal-check-copy" id="signup-consent-copy">
                <strong>Agree to Badminta's account policies</strong>
                <small>
                  I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer">Terms</a> and acknowledge the <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a href="/legal/youth-safety" target="_blank" rel="noreferrer">Youth Safety Notice</a>. I confirm that I can provide consent, or that my parent or guardian has approved this account.
                </small>
              </span>
            </label>
          </div>
          <button type="submit" disabled={!auth.configured || code.length < verificationCodeLength || !legalAccepted || !ageBand || ageBand === "under-13" || auth.smsStatus === "verifying"}>
            {auth.smsStatus === "verifying" ? "Verifying code" : "Verify and continue"}
          </button>
          <button type="button" className="phone-change-button" onClick={changePhoneNumber}>Use a different number</button>
        </form>
      )}
      {(localMessage || auth.authError) && (
        <div className={`auth-message ${messageKind}`} role={messageKind === "error" ? "alert" : "status"} aria-live={messageKind === "error" ? "assertive" : "polite"}>
          {localMessage || auth.authError}
        </div>
      )}
    </>
  );
}

function AccountSessionRestoring() {
  return (
    <section className="page auth-page account-session-restoring">
      <div className="auth-panel wide">
        <div className="account-session-restoring-card" role="status" aria-live="polite">
          <SearchingIndicator />
          <div>
            <p>Secure account</p>
            <h1>Restoring your Badminta account</h1>
            <span>Your saved profiles and role tools will appear as soon as the verified Firebase session is ready.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

const accountProfileIsRestoring = (auth, accountProfile = defaultAccountProfile, accountSyncStatus = {}) => {
	if (!isRealFirebaseUser(auth?.user)) return false;
	const profileOwnerUid = String(accountProfile.uid || accountProfile.ownerUid || "").trim();
	if (accountSyncStatus.state === "error") return false;
	return profileOwnerUid !== auth.user.uid;
};

function RequireVerifiedAccount({ auth, navigate, intent, accountProfile = defaultAccountProfile, accountSyncStatus = {}, preferredPhoneCountryCode = "", completionIntent = null, authContext = null, children }) {
  if (auth.loading) return <AccountSessionRestoring />;
	if (accountProfileIsRestoring(auth, accountProfile, accountSyncStatus)) return <AccountSessionRestoring />;

  if (auth.user && isAccountRestricted(accountProfile)) {
    return (
      <section className="page auth-page">
        <div className="auth-panel wide account-restricted-panel">
          <LockKeyhole size={32} />
          <p>Account restricted</p>
          <h1>Marketplace actions are paused for this account.</h1>
          <span>{accountProfile.restrictionReason || "Badminta support needs to review this account before bookings, payments, claims, groups or public profile changes can continue."}</span>
          <div className="auth-links">
            <button type="button" onClick={() => navigate("help")}>Contact support</button>
            <button type="button" onClick={() => navigate("account")}>Back to account</button>
          </div>
        </div>
      </section>
    );
  }
  const hasPublishedProfile = (accountProfile.roles || []).some((role) => roleHasPublishedBackendProfile(accountProfile, role));
  const accountComplete = Boolean(
    accountProfile.fullName
    && accountProfile.roles?.length
    && (accountProfile.email || hasPublishedProfile)
  );
  if (auth.user && completionIntent && !accountComplete) {
    return (
      <section className="page auth-page">
        <div className="auth-panel wide account-completion-gate">
          <BadgeCheck size={28} />
          <p>Phone verified</p>
          <h1>Finish the two account details needed for your profile.</h1>
          <span>Your {roleTitle(completionIntent.role).toLowerCase()} role is already selected. Add your name and email, then continue directly to its profile setup.</span>
          <button type="button" onClick={() => {
            saveSignupIntent(completionIntent);
            navigate("signup");
          }}>Continue account setup</button>
        </div>
      </section>
    );
  }
  if (auth.user) return children;
  return (
    <section className="page auth-page">
      <div className="auth-panel wide">
        {authContext && (
          <div className="auth-intent-summary" role="status">
            <span><CalendarDays size={20} /></span>
            <div>
              <small>{authContext.label || "Selected"}</small>
              <strong>{authContext.title}</strong>
              {authContext.detail && <p>{authContext.detail}</p>}
            </div>
            <BadgeCheck size={18} />
          </div>
        )}
        <PhoneAuthPanel
          auth={auth}
          title={`${intent} with phone verification`}
          text={authContext
            ? "Verify your phone to keep this selection, prevent duplicate reservations and attach payment status to your account."
            : "Verify your phone first. After that you can save account details, create player profiles, add centres and publish social groups."}
          preferredCountryCode={preferredPhoneCountryCode}
          funnelRole={completionIntent?.role || ""}
          onVerified={() => {
            if (!completionIntent) return;
            saveSignupIntent(completionIntent);
            navigate("signup");
          }}
        />
        {authContext && <button type="button" className="auth-intent-back" onClick={() => navigate(authContext.backTarget || "home", authContext.backPayload || {})}><ChevronLeft size={16} /> Back without booking</button>}
      </div>
    </section>
  );
}

function RoleSelectionStep({ accountProfile, saveAccountProfile, onContinue, intendedRole = "" }) {
  const [selectedRoles, setSelectedRoles] = useState(() => Array.from(new Set([
    "player",
    ...(accountProfile.roles || []),
    ...(["player", "coach", "organiser", "centre"].includes(intendedRole) ? [intendedRole] : [])
  ])));
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!["player", "coach", "organiser", "centre"].includes(intendedRole)) return;
    setSelectedRoles((current) => current.includes(intendedRole) ? current : [...current, intendedRole]);
  }, [intendedRole]);

  const toggleRole = (role) => {
    if (role === "player") return;
    setMessage("");
    setSelectedRoles((current) => {
      const exists = current.includes(role);
      return exists ? current.filter((item) => item !== role) : [...current, role];
    });
  };

  const saveRoles = async () => {
    if (!selectedRoles.length) {
      setMessage("Choose at least one user type to continue.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const result = await saveAccountProfile({
        roles: selectedRoles,
        roleUpdatedAt: new Date().toISOString()
      });
      if (!result?.saved) throw new Error(result?.error || "Badminta could not save these user types.");
      trackMarketingFunnelEvent("role_selected", {
        role: selectedRoles[0],
        dedupeId: selectedRoles.slice().sort().join("|"),
        extra: { role_count: selectedRoles.length }
      });
      onContinue?.(selectedRoles);
    } catch (error) {
      setMessage(error.message || "Badminta could not save these user types. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="role-selection-step">
      <div className="verified-account compact">
        <UsersRound size={22} />
        <div>
          <strong>Choose one or more user types</strong>
          <span>Pick how you want to benefit from Badminta. You can use more than one role.</span>
        </div>
      </div>
      <div className="role-choice-grid compact">
        {accountRoleOptions.map(({ id, title, description, icon: Icon }) => {
          const active = selectedRoles.includes(id);
          return (
            <button type="button" key={id} className={active ? "active" : ""} onClick={() => toggleRole(id)} aria-pressed={active}>
              <Icon size={22} />
              <strong>{title}</strong>
              <span>{description}</span>
              {active && <Check size={17} />}
            </button>
          );
        })}
      </div>
      <div className="role-selection-guidance">
        <CircleHelp size={17} />
        <span>{intendedRole
          ? `${roleTitle(intendedRole)} is selected for this setup. Review your other user types, then continue.`
          : "Start with the profile you will publish now. You can add more user types later from Account."}</span>
      </div>
      {selectedRoles.length > 0 && (
        <div className="role-summary-strip">
          {selectedRoles.map((role) => <span key={role}>{roleTitle(role)}</span>)}
        </div>
      )}
      {message && <div className="auth-message">{message}</div>}
      <button type="button" className="primary-auth-action" onClick={saveRoles} disabled={!selectedRoles.length || saving}>
        {saving ? "Saving user types..." : "Continue with selected roles"}
      </button>
    </section>
  );
}

function BasicSignupForm({ auth, accountProfile, saveAccountProfile, navigate, onEditRoles, signupIntent = {} }) {
  const initialRoleLocation = accountProfile.player?.location
    || accountProfile.coach?.location
    || accountProfile.group?.location
    || accountProfile.centre?.location
    || null;
  const [form, setForm] = useState({
    fullName: accountProfile.fullName || auth.user?.displayName || "",
    email: accountProfile.email || "",
    location: initialRoleLocation
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [locationStatus, setLocationStatus] = useState(initialRoleLocation ? "saved" : "locating");
  const locationAttemptedRef = useRef(false);
  const selectedRoleIds = accountProfile.roles || [];
  const intendedSelectedRole = selectedRoleIds.includes(signupIntent.role) ? signupIntent.role : "";
  const firstSelectedRole = intendedSelectedRole || selectedRoleIds[0] || "";
  const firstSelectedRoleSetup = roleSetupSteps[firstSelectedRole]?.page || "";
  const submitLabel = signupIntent.role === "player" || signupIntent.returnPage === "profileSetup" || firstSelectedRole === "player"
    ? "Continue to player profile"
    : firstSelectedRole
      ? `Continue to ${roleTitle(firstSelectedRole).toLowerCase()} setup`
      : "Save account and continue";

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const locateSignupAccount = async () => {
    setLocationStatus("locating");
    try {
      const location = await resolveCurrentProfileLocation();
      updateField("location", location);
      setLocationStatus("saved");
      return location;
    } catch (error) {
      setLocationStatus(Number(error?.code) === 1 ? "denied" : "error");
      return null;
    }
  };

  useEffect(() => {
    if (locationAttemptedRef.current || form.location) return;
    locationAttemptedRef.current = true;
    locateSignupAccount();
  }, [auth.user?.uid]);

  const saveBasicAccount = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      if (!accountProfile.roles?.length) {
        throw new Error("Choose at least one user type before saving account details.");
      }
      const fullName = form.fullName.trim();
      const email = form.email.trim().toLowerCase();
      if (!fullName) throw new Error("Add your full name before continuing.");
      if (!emailPattern.test(email)) throw new Error("Add a valid email address before continuing.");
      const marketingAttribution = readStoredMarketingAttribution();
      let legalDeclaration = {};
      try { legalDeclaration = JSON.parse(window.localStorage.getItem(legalDeclarationStorageKey) || "{}"); } catch { legalDeclaration = {}; }
      const accountSave = await saveAccountProfile({
        fullName,
        email,
        ...signupRolesLocationPatch(accountProfile.roles, form.location, accountProfile),
        marketingAttribution: Object.fromEntries(
          [...marketingAttributionParams, "landing_path", "captured_at"]
            .filter((key) => marketingAttribution[key])
            .map((key) => [key, cleanMarketingValue(marketingAttribution[key], key === "landing_path" ? 500 : 180)])
        )
      });
      if (!accountSave?.saved) throw new Error(accountSave?.error || "Badminta could not save your account details.");
      await auth.updateDisplayName(fullName);
      const selectedRole = accountProfile.roles?.[0] || signupIntent.role || "player";
      trackMarketingFunnelEvent("sign_up", {
        role: selectedRole,
        dedupeId: auth.user?.uid || email,
        dedupeScope: "persistent",
        extra: { method: "phone" }
      });
      const returnPage = ["profileSetup", "coachProfile", "createGroup", "claimCentre"].includes(signupIntent.returnPage)
        ? signupIntent.returnPage
        : firstSelectedRoleSetup || "onboarding";
      if (firstSelectedRole && returnPage === firstSelectedRoleSetup) rememberOnboardingRole(firstSelectedRole);
      clearSignupIntent();
      navigate(returnPage);
    } catch (error) {
      setMessage(error.message || "Could not save account details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="basic-signup-form" onSubmit={saveBasicAccount}>
      <div className="verified-account">
        <BadgeCheck size={24} />
        <div>
          <strong>Phone verified first</strong>
          <span>{auth.user?.phoneNumber || "Firebase phone account active"}</span>
        </div>
      </div>
      <div className="role-summary-card">
        <div>
          <strong>Selected account menus</strong>
          <span>{accountProfile.roles?.map(roleTitle).join(", ") || "No user types selected yet"}</span>
        </div>
        <button type="button" onClick={onEditRoles}>Change</button>
      </div>
      <label><span>Full name</span><input value={form.fullName} onChange={(event) => updateField("fullName", event.target.value)} placeholder="Your name" required /></label>
      <label><span>Email</span><input value={form.email} onChange={(event) => updateField("email", event.target.value)} type="email" placeholder="you@example.com" required /></label>
      <SuburbPicker
        label="Your suburb or city"
        value={locationAreaLabel(form.location)}
        selectedLocation={form.location}
        autoLocationStatus={locationStatus}
        onUseCurrentLocation={locateSignupAccount}
        helper="Used to place public profiles in the right local discovery results."
        onChange={({ location }) => {
          updateField("location", location);
          setLocationStatus("saved");
        }}
      />
      <div className="signup-next-note"><UserCircle size={18} /><span>Profile photo, playing details and optional social links come next. They are not required to create your account.</span></div>
      {message && <div className="auth-message">{message}</div>}
      <button type="submit" disabled={saving || !accountProfile.roles?.length}>{saving ? "Saving account" : submitLabel}</button>
    </form>
  );
}

function LoginPage({ navigate, auth, preferredPhoneCountryCode = "" }) {
  const continueAfterLogin = () => {
    const intent = consumeAuthReturnIntent();
    if (intent.path) {
      window.location.assign(intent.path);
      return;
    }
    navigate("account");
  };
  return (
    <section className="page auth-page">
      <div className="auth-panel">
        <PhoneAuthPanel
          auth={auth}
          title="Log in with your phone"
          text="Enter your mobile number and verify the SMS code. The same verified account is used for bookings, groups, centre tools and messages."
          onVerified={continueAfterLogin}
          preferredCountryCode={preferredPhoneCountryCode}
        />
        {auth.user && <button type="button" onClick={continueAfterLogin}>Continue</button>}
        <div className="auth-links">
          <button type="button" onClick={() => navigate("signup")}>Create an account</button>
          <button type="button" onClick={() => navigate("help")}>Need help?</button>
        </div>
      </div>
      <aside className="auth-side">
        <StockMediaImage media={getStockMediaFallback("group", "signup community media")} alt="Badminton community stock image" className="auth-stock-media" />
        <div>
          <strong>Tonight on Badminta</strong>
          <span>Social groups, players, trainers and events appear only after real users sign up and publish them.</span>
        </div>
      </aside>
    </section>
  );
}

const signupRoleFromSolutionPath = (path = "") => {
  const cleanPath = String(path || "").toLowerCase();
  if (["/solutions/badminton-players", "badminton-players", "solution-players", "solutionplayers"].some((value) => cleanPath.includes(value))) return "player";
  if (["/solutions/badminton-coaches", "badminton-coaches", "solution-coaches", "solutioncoaches"].some((value) => cleanPath.includes(value))) return "coach";
  if (["/solutions/social-badminton-groups", "social-badminton-groups", "solution-organisers", "solutionorganisers"].some((value) => cleanPath.includes(value))) return "organiser";
  if (["/solutions/badminton-centres", "badminton-centres", "solution-centres", "solutioncentres"].some((value) => cleanPath.includes(value))) return "centre";
  return "";
};

const signupSolutionPathByRole = {
  player: "/solutions/badminton-players",
  coach: "/solutions/badminton-coaches",
  organiser: "/solutions/social-badminton-groups",
  centre: "/solutions/badminton-centres"
};

const signupIntentFromEntry = () => {
  const stored = loadSignupIntent();
  if (typeof window === "undefined") return stored;
  const params = new URLSearchParams(window.location.search);
  const urlRole = params.get("role") || "";
  const urlSource = params.get("from") || "";
  const sourceRole = signupRoleFromSolutionPath(urlSource);
  let referrerPath = "";
  try {
    const referrer = document.referrer ? new URL(document.referrer) : null;
    if (referrer?.origin === window.location.origin) referrerPath = `${referrer.pathname}${referrer.search}`;
  } catch {
    referrerPath = "";
  }
  const inferredRole = ["player", "coach", "organiser", "centre"].includes(urlRole)
    ? urlRole
    : sourceRole
      ? sourceRole
    : ["player", "coach", "organiser", "centre"].includes(stored.role)
      ? stored.role
      : signupRoleFromSolutionPath(referrerPath);
  if (!inferredRole) return stored;
  const config = signupRoleIntents[inferredRole];
  const intent = {
    ...config,
    ...stored,
    role: inferredRole,
    returnPage: stored.role === inferredRole && stored.returnPage ? stored.returnPage : config.returnPage,
    sourcePage: stored.role === inferredRole && stored.sourcePage ? stored.sourcePage : config.sourcePage,
    sourcePath: stored.role === inferredRole && stored.sourcePath
      ? stored.sourcePath
      : signupRoleFromSolutionPath(referrerPath) === inferredRole
        ? referrerPath
        : signupSolutionPathByRole[inferredRole]
  };
  saveSignupIntent(intent);
  return intent;
};

function SolutionSignupIdentityFields({ role, draft, onChange, locationStatus, onRetryLocation }) {
  const roleLabel = roleTitle(role).toLowerCase();
  return (
    <section className="solution-signup-identity" aria-label={`${roleTitle(role)} account details`}>
      <div className="solution-signup-heading">
        <span><UserCircle size={20} /></span>
        <div>
          <strong>Start your {roleLabel} account</strong>
          <p>These details carry straight into {role === "organiser" ? "group" : roleLabel} setup after phone verification.</p>
        </div>
      </div>
      <div className="solution-signup-fields">
        <label>
          <span>Your name</span>
          <input value={draft.fullName} onChange={(event) => onChange("fullName", event.target.value)} autoComplete="name" placeholder="Full name" required />
        </label>
        <label>
          <span>Email</span>
          <input value={draft.email} onChange={(event) => onChange("email", event.target.value)} autoComplete="email" type="email" placeholder="you@example.com" required />
        </label>
      </div>
      <SuburbPicker
        label="Your suburb or city"
        value={locationAreaLabel(draft.location)}
        selectedLocation={draft.location}
        autoLocationStatus={locationStatus}
        onUseCurrentLocation={onRetryLocation}
        helper="Badminta tries your current location first. Search manually if the browser cannot provide it."
        onChange={({ location }) => onChange("location", location)}
      />
    </section>
  );
}

function SignupPage({ navigate, auth, accountProfile, saveAccountProfile, preferredPhoneCountryCode = "" }) {
  const hasVerifiedPhone = Boolean(auth.user);
  const hasSelectedRoles = Boolean(accountProfile.roles?.length);
  const [signupIntent] = useState(signupIntentFromEntry);
  const intendedRole = ["player", "coach", "organiser", "centre"].includes(signupIntent.role) ? signupIntent.role : "";
  const hasSolutionRoleIntent = Boolean(
    intendedRole
    && signupRoleFromSolutionPath(`${signupIntent.sourcePage || ""} ${signupIntent.sourcePath || ""}`) === intendedRole
  );
  const forceRoleSelection = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("step") === "roles"
    && !hasSolutionRoleIntent;
  const [editingRoles, setEditingRoles] = useState(forceRoleSelection);
  const storedIdentityDraft = loadSignupIdentityDraft();
  const [identityDraft, setIdentityDraft] = useState(() => ({
    role: intendedRole,
    fullName: accountProfile.fullName || (storedIdentityDraft.role === intendedRole ? storedIdentityDraft.fullName : "") || "",
    email: accountProfile.email || (storedIdentityDraft.role === intendedRole ? storedIdentityDraft.email : "") || "",
    location: signupIntent.location || (storedIdentityDraft.role === intendedRole ? storedIdentityDraft.location : null) || null
  }));
  const [locationStatus, setLocationStatus] = useState(identityDraft.location ? "saved" : "idle");
  const [solutionSaveStatus, setSolutionSaveStatus] = useState({ state: "idle", message: "" });
  const [solutionIdentityConfirmed, setSolutionIdentityConfirmed] = useState(false);
  const signupLocationAttemptedRef = useRef(false);
  const solutionCompletionRef = useRef("");
  const identityReady = Boolean(identityDraft.fullName.trim() && emailPattern.test(identityDraft.email.trim().toLowerCase()));
  const signupStage = !hasVerifiedPhone ? "phone" : forceRoleSelection ? "roles" : intendedRole ? "profile" : (!hasSelectedRoles || editingRoles) ? "roles" : "details";
  const previousSignupStage = useRef(signupStage);

  const updateIdentityDraft = (field, value) => {
    setSolutionSaveStatus({ state: "idle", message: "" });
    setIdentityDraft((current) => ({ ...current, [field]: value }));
    if (field === "location" && value) setLocationStatus("saved");
  };

  const locateSignupUser = async () => {
    if (!intendedRole) return null;
    setLocationStatus("locating");
    try {
      const location = await resolveCurrentProfileLocation();
      setIdentityDraft((current) => ({ ...current, location }));
      saveSignupIntent({ ...signupIntent, location });
      setLocationStatus("saved");
      return location;
    } catch (error) {
      setLocationStatus(Number(error?.code) === 1 ? "denied" : "error");
      return null;
    }
  };

  const validateSolutionIdentity = async () => {
    const fullName = identityDraft.fullName.trim();
    const email = identityDraft.email.trim().toLowerCase();
    if (!fullName) throw new Error("Add your name to continue.");
    if (!emailPattern.test(email)) throw new Error("Add a valid email address to continue.");
    saveSignupIdentityDraft({ ...identityDraft, role: intendedRole, fullName, email });
    setSolutionIdentityConfirmed(true);
  };

  const completeSolutionSignup = async () => {
    if (!auth.user || !intendedRole || !identityReady) return;
    const attemptKey = `${auth.user.uid || auth.user.phoneNumber || "verified"}:${intendedRole}`;
    if (solutionCompletionRef.current === attemptKey) return;
    solutionCompletionRef.current = attemptKey;
    setSolutionSaveStatus({ state: "saving", message: `Creating your ${roleTitle(intendedRole).toLowerCase()} account.` });
    try {
      const fullName = identityDraft.fullName.trim();
      const email = identityDraft.email.trim().toLowerCase();
      const roles = Array.from(new Set(["player", ...(accountProfile.roles || []), intendedRole]));
      const marketingAttribution = readStoredMarketingAttribution();
      const legalDeclaration = readLegalDeclaration();
      const accountSave = await saveAccountProfile({
        fullName,
        email,
        roles,
        roleUpdatedAt: new Date().toISOString(),
        marketingAttribution: {
          ...marketingAttribution,
          landing_path: marketingAttribution.landing_path || signupIntent.sourcePath || "",
          signup_source_path: signupIntent.sourcePath || "",
          signup_source_page: signupIntent.sourcePage || "",
          signup_role: intendedRole
        },
        legalDeclaration,
        ...signupRoleLocationPatch(intendedRole, identityDraft.location, accountProfile)
      });
      if (!accountSave?.saved) throw new Error(accountSave?.error || "Badminta could not save your account details.");
      try {
        await auth.updateDisplayName(fullName);
      } catch {
        // The account document remains the display-name source when Auth metadata is delayed.
      }
      trackMarketingFunnelEvent("role_selected", {
        role: intendedRole,
        dedupeId: `${auth.user.uid || "verified"}:${intendedRole}`,
        dedupeScope: "persistent",
        extra: { role_count: roles.length, role_preselected: true, entry_point: signupIntent.sourcePage || "solution" }
      });
      trackMarketingFunnelEvent("sign_up", {
        role: intendedRole,
        dedupeId: auth.user.uid || email,
        dedupeScope: "persistent",
        extra: { method: "phone", entry_point: signupIntent.sourcePage || "solution" }
      });
      rememberOnboardingRole(intendedRole);
      clearSignupIntent();
      clearSignupIdentityDraft();
      setSolutionSaveStatus({ state: "saved", message: "Account saved. Opening your profile setup." });
      navigate(signupIntent.returnPage || signupRoleIntents[intendedRole].returnPage);
    } catch (error) {
      solutionCompletionRef.current = "";
      setSolutionSaveStatus({ state: "error", message: error.message || "Badminta could not finish this account. Try again." });
    }
  };

  useEffect(() => {
    if (previousSignupStage.current === signupStage) return;
    previousSignupStage.current = signupStage;
    resetPageScroll();
  }, [signupStage]);

  useEffect(() => {
    if (forceRoleSelection) setEditingRoles(true);
  }, [forceRoleSelection]);

  useEffect(() => {
    if (!intendedRole || forceRoleSelection) return;
    saveSignupIdentityDraft({ ...identityDraft, role: intendedRole });
  }, [intendedRole, forceRoleSelection, identityDraft.fullName, identityDraft.email, identityDraft.location]);

  useEffect(() => {
    if (!hasVerifiedPhone || !intendedRole || forceRoleSelection || identityDraft.location || signupLocationAttemptedRef.current) return;
    signupLocationAttemptedRef.current = true;
    locateSignupUser();
  }, [hasVerifiedPhone, intendedRole, forceRoleSelection]);

  useEffect(() => {
    if (!hasVerifiedPhone || forceRoleSelection || !intendedRole || !identityReady || solutionIdentityConfirmed) return;
    setSolutionIdentityConfirmed(true);
  }, [hasVerifiedPhone, forceRoleSelection, intendedRole, identityReady, solutionIdentityConfirmed]);

  useEffect(() => {
    trackMarketingFunnelEvent("signup_started", {
      role: intendedRole || "player",
      dedupeId: intendedRole || "general"
    });
  }, [intendedRole]);

  useEffect(() => {
    if (!hasVerifiedPhone || forceRoleSelection || !intendedRole || !identityReady || !solutionIdentityConfirmed) return;
    completeSolutionSignup();
  }, [auth.user?.uid, hasVerifiedPhone, forceRoleSelection, intendedRole, identityReady, solutionIdentityConfirmed]);

  return (
    <section className={`page auth-page${intendedRole === "player" ? " player-signup" : ""}`}>
      <div className="auth-panel wide">
        <div className="auth-stepper">
          {(intendedRole ? [
            ["1", "Verify phone", hasVerifiedPhone],
            ["2", "Account details", identityReady],
            ["3", `${roleTitle(intendedRole)} setup`, false]
          ] : [
            ["1", "Verify phone", hasVerifiedPhone],
            ["2", "Choose user type", hasSelectedRoles],
            ["3", "Name and email", Boolean(accountProfile.fullName && accountProfile.email)]
          ]).map(([number, label, done]) => (
            <span key={label} className={done ? "done" : ""}><b>{number}</b>{label}</span>
          ))}
        </div>
        {!hasVerifiedPhone ? (
          <PhoneAuthPanel
            auth={auth}
            title={intendedRole ? `Create your free ${roleTitle(intendedRole).toLowerCase()} account` : "Create your free Badminta account"}
            text={intendedRole ? `Verify your mobile number first. Your ${roleTitle(intendedRole).toLowerCase()} role is already selected and its profile details come next.` : undefined}
            preferredCountryCode={preferredPhoneCountryCode}
            funnelRole={intendedRole || "account"}
            compactIntro={Boolean(intendedRole)}
          />
        ) : forceRoleSelection ? (
          <>
            <p>Manage user types</p>
            <h1>{intendedRole ? `Add ${roleTitle(intendedRole).toLowerCase()} to your account.` : "Choose how you will use Badminta."}</h1>
            <span>{intendedRole
              ? `The matching user type is selected below. Confirm it to open your ${roleTitle(intendedRole).toLowerCase()} setup.`
              : "Select the roles that match what you want to do. Your completed profiles stay unchanged."}</span>
            <RoleSelectionStep
              accountProfile={accountProfile}
              saveAccountProfile={saveAccountProfile}
              intendedRole={intendedRole}
              onContinue={(roles) => {
                setEditingRoles(false);
                if (intendedRole && roles.includes(intendedRole)) {
                  rememberOnboardingRole(intendedRole);
                  clearSignupIntent();
                  navigate(roleSetupTargetFor(intendedRole));
                  return;
                }
                navigate("account");
              }}
            />
          </>
        ) : intendedRole && !solutionIdentityConfirmed ? (
          <div className="solution-signup-verified-details">
            <div className="verified-account compact">
              <BadgeCheck size={22} />
              <div><strong>Phone verified</strong><span>Add your name and email to open {roleTitle(intendedRole).toLowerCase()} setup. Location is optional and can be added automatically.</span></div>
            </div>
            <SolutionSignupIdentityFields
              role={intendedRole}
              draft={identityDraft}
              onChange={updateIdentityDraft}
              locationStatus={locationStatus}
              onRetryLocation={locateSignupUser}
            />
            {solutionSaveStatus.state === "error" && <div className="auth-message">{solutionSaveStatus.message}</div>}
            <button type="button" className="primary-auth-action" disabled={!identityReady} onClick={async () => {
              try {
                await validateSolutionIdentity();
                await completeSolutionSignup();
              } catch (error) {
                setSolutionSaveStatus({ state: "error", message: error.message || "Add your account details to continue." });
              }
            }}>Continue to {roleTitle(intendedRole).toLowerCase()} setup</button>
          </div>
        ) : intendedRole ? (
          <div className={`solution-signup-completing ${solutionSaveStatus.state}`} role="status" aria-live="polite">
            {solutionSaveStatus.state === "error" ? <CircleHelp size={28} /> : <Loader2 className={solutionSaveStatus.state === "saved" ? "" : "spin"} size={28} />}
            <p>Phone verified</p>
            <h1>{solutionSaveStatus.state === "error" ? "Your account details need one more try." : `Preparing your ${roleTitle(intendedRole).toLowerCase()} profile.`}</h1>
            <span>{solutionSaveStatus.message || "Saving your name, email, selected role and location before opening profile setup."}</span>
            {solutionSaveStatus.state === "error" && <button type="button" onClick={completeSolutionSignup}>Try saving again</button>}
          </div>
        ) : (!hasSelectedRoles || editingRoles) ? (
          <>
            <p>Create account</p>
            <h1>Choose how you will use Badminta.</h1>
            <span>Select the roles that match what you want to do: play, organise sessions, run a centre or sell coaching.</span>
            <RoleSelectionStep
              accountProfile={accountProfile}
              saveAccountProfile={saveAccountProfile}
              intendedRole={intendedRole}
              onContinue={() => setEditingRoles(false)}
            />
          </>
        ) : (
          <>
            <p>Create account</p>
            <h1>Add your name and email.</h1>
            <span>Add the basic details people will see when they interact with your selected roles.</span>
            <BasicSignupForm
              auth={auth}
              accountProfile={accountProfile}
              saveAccountProfile={saveAccountProfile}
              navigate={navigate}
              onEditRoles={() => setEditingRoles(true)}
              signupIntent={signupIntent}
            />
          </>
        )}
        <div className="auth-links">
          <button type="button" onClick={() => navigate("login")}>Already have an account?</button>
        </div>
      </div>
    </section>
  );
}

function OnboardingPage({ navigate, accountProfile }) {
  const roleSet = new Set(accountProfile.roles || []);
  const selectedRoles = accountRoleOptions.filter((role) => roleSet.has(role.id));
  const initialStepIndex = () => {
    const completedRole = getCompletedOnboardingRole();
    const completedIndex = selectedRoles.findIndex((role) => role.id === completedRole && isPublicProfileReady(accountProfile, role.id));
    if (completedIndex >= 0) return Math.min(completedIndex + 1, Math.max(selectedRoles.length - 1, 0));
    return selectedRoles.findIndex((role) => !isPublicProfileReady(accountProfile, role.id));
  };
  const [stepIndex, setStepIndex] = useState(() => Math.max(initialStepIndex(), 0));
  const safeStepIndex = Math.min(stepIndex, Math.max(selectedRoles.length - 1, 0));
  const currentRole = selectedRoles[safeStepIndex];
  const setupStep = currentRole ? roleSetupSteps[currentRole.id] : null;
  const profileStarted = currentRole ? roleProfileStarted(accountProfile, currentRole.id) : false;
  const profileReady = currentRole ? isPublicProfileReady(accountProfile, currentRole.id) : false;
  const missingFields = currentRole ? getPublicProfileMissingFields(accountProfile, currentRole.id) : [];
  const profileCoreReady = Boolean(currentRole && missingFields.length === 0);
  const readyToPublish = Boolean(currentRole && profileCoreReady && !profileReady);
  const completedCount = selectedRoles.filter((role) => isPublicProfileReady(accountProfile, role.id)).length;
  const isFirstStep = safeStepIndex === 0;
  const isLastStep = safeStepIndex >= selectedRoles.length - 1;
  const CurrentRoleIcon = currentRole?.icon || UserCircle;

  useEffect(() => {
    if (selectedRoles.length && stepIndex > selectedRoles.length - 1) setStepIndex(selectedRoles.length - 1);
  }, [selectedRoles.length, stepIndex]);

  const openCurrentRoleSetup = () => {
    if (!currentRole || !setupStep) return;
    rememberOnboardingRole(currentRole.id);
    navigate(setupStep.page);
  };

  return (
    <section className="page onboarding-page">
      <div className="screen-head">
        <p>Account onboarding</p>
        <h1>Publish one useful profile at a time.</h1>
        <button type="button" onClick={() => navigate("signup", { signupStep: "roles" })}>Change user types</button>
      </div>
      {selectedRoles.length && currentRole && setupStep ? (
        <section className="onboarding-step-card">
          <div className="onboarding-progress-head">
            <span>Step {safeStepIndex + 1} of {selectedRoles.length}</span>
            <strong>{completedCount} of {selectedRoles.length} profile types complete</strong>
          </div>
          <div className="onboarding-step-rail" aria-label="Selected profile types">
            {selectedRoles.map(({ id, title, icon: Icon }, index) => {
              const started = roleProfileStarted(accountProfile, id);
              const ready = isPublicProfileReady(accountProfile, id);
              const coreReady = getPublicProfileMissingFields(accountProfile, id).length === 0;
              const active = index === safeStepIndex;
              return (
                <button
                  type="button"
                  key={id}
                  className={`${active ? "active" : ""} ${ready ? "complete" : coreReady ? "ready-to-publish" : started ? "in-progress" : ""}`}
                  onClick={() => setStepIndex(index)}
                  aria-current={active ? "step" : undefined}
                >
                  <Icon size={17} />
                  <span>{title}</span>
                  {ready ? <Check size={15} /> : coreReady ? <BadgeCheck size={15} /> : null}
                </button>
              );
            })}
          </div>
          <div className="role-menu-panel single-step">
            <div className="role-menu-head">
              <CurrentRoleIcon size={26} />
              <div>
                <strong>{setupStep.title}</strong>
                <span>{setupStep.text}</span>
              </div>
            </div>
            <div className={profileReady ? "role-step-status complete" : readyToPublish ? "role-step-status ready-to-publish" : profileStarted ? "role-step-status progress" : "role-step-status"}>
              {profileReady || readyToPublish ? <Check size={18} /> : <CircleHelp size={18} />}
              <span>
                {profileReady
                  ? "This role is published and its public page is live."
                  : readyToPublish
                    ? "Required details are complete. Publish now to open the live public page and unlock this role's tools."
                  : profileStarted
                    ? `${missingFields.length} detail${missingFields.length === 1 ? "" : "s"} still need attention before this role is useful.`
                    : "This role is still empty. Complete it before publishing or using its tools."}
              </span>
            </div>
            <div className="role-action-list one-up">
              <button type="button" onClick={openCurrentRoleSetup}>
                <strong>{profileReady ? `Review ${currentRole.title}` : readyToPublish ? `Publish ${currentRole.title}` : profileStarted ? `Finish ${currentRole.title}` : setupStep.action}</strong>
                <span>{currentRole.description}</span>
              </button>
            </div>
            <div className="onboarding-step-actions">
              <button type="button" disabled={isFirstStep} onClick={() => setStepIndex((current) => Math.max(current - 1, 0))}>Back</button>
              {!isLastStep ? (
                <button type="button" disabled={!profileReady} onClick={() => setStepIndex((current) => Math.min(current + 1, selectedRoles.length - 1))}>
                  {profileReady ? "Next profile type" : "Publish this profile to continue"}
                </button>
              ) : (
                <button type="button" disabled={!profileReady} onClick={() => navigate("account")}>{profileReady ? "Finish and open account" : "Publish this profile to finish"}</button>
              )}
            </div>
          </div>
        </section>
      ) : (
        <CommunityEmptyState
          type="account roles"
          title="Choose a user type first"
          text="Pick one or more user types so Badminta can build the right player, organiser, centre or coach menus."
          primary={{ label: "Choose user types", target: "signup", payload: { signupStep: "roles" } }}
          icon={UsersRound}
          navigate={navigate}
        />
      )}
    </section>
  );
}

const bookingDisplayState = (booking = {}) => {
  const source = `${booking.status || ""} ${booking.paymentStatus || ""} ${booking.bookingStatus || ""}`.toLowerCase();
  if (/cancel|released|expired/.test(source)) return { label: "Cancelled", tone: "cancelled", Icon: XCircle };
  if (/refund/.test(source)) return { label: "Refund in progress", tone: "attention", Icon: RotateCcw };
  if (/paid_with_credits|paid with|payment.*paid|\\bpaid\\b/.test(source)) return { label: "Paid", tone: "confirmed", Icon: BadgeCheck };
  if (/pay_at_venue|pay at venue|offline/.test(source)) return { label: "Pay at venue", tone: "attention", Icon: Banknote };
  if (/confirmed|not_required|free/.test(source)) return { label: "Confirmed", tone: "confirmed", Icon: BadgeCheck };
  if (/pending_payment|payment pending|requires_payment|checkout/.test(source)) return { label: "Payment required", tone: "attention", Icon: CreditCard };
  return { label: "Awaiting confirmation", tone: "pending", Icon: Clock3 };
};

function BookingRecordCard({ booking, navigate, onRefundReview, onBookingOperation }) {
  const openBookingMessages = () => navigate("messages", { messageFocus: { roleId: "player", threadId: `court-booking-${booking.id}` } });
  const statusText = `${booking.status || ""} ${booking.paymentStatus || ""}`.toLowerCase();
  const displayState = bookingDisplayState(booking);
  const StatusIcon = displayState.Icon;
  const canRequestCancellation = !/cancel|refund|expired|no[_ -]?show/i.test(`${booking.status} ${booking.cancellationStatus} ${booking.refundReviewStatus}`)
    && /(confirmed|paid|completed)/i.test(statusText);
  const canRequestRefund = /paid/i.test(statusText) && !/refund|cancelled|expired/i.test(`${booking.refundReviewStatus} ${booking.status}`);
  return (
    <article className="booking-record-card">
      <div className="booking-record-icon">
        <CalendarDays size={22} />
      </div>
      <div className="booking-record-copy">
        <span className={`booking-state ${displayState.tone}`}><StatusIcon size={14} /> {displayState.label}</span>
        <strong>{booking.venueName}</strong>
        {booking.reservationSegments?.length > 1 ? (
          <div className="booking-record-segments">
            {booking.reservationSegments.map((segment, index) => (
              <p key={`${segment.date}-${segment.time}-${index}`}>
                {formatDateLabel(segment.date)} · {formatTimeLabel(segment.time)} · {segment.durationHours}h · {segment.courtCount} court{segment.courtCount === 1 ? "" : "s"}
              </p>
            ))}
          </div>
        ) : (
          <p>{[formatDateLabel(booking.date), formatTimeLabel(booking.time), `${booking.durationHours}h`, `${booking.courtCount} court${booking.courtCount === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}</p>
        )}
        {[booking.cancellationStatus, booking.attendanceStatus, booking.lastOperationalAction].filter(Boolean).length > 0 && (
          <em>{[booking.cancellationStatus, booking.attendanceStatus, booking.lastOperationalAction].filter(Boolean).join(" · ")}</em>
        )}
      </div>
      <div className="booking-record-total">
        <strong>{getBookingTotalLabel(booking) || "Pending"}</strong>
        {booking.refundReviewStatus && <span>{booking.refundReviewStatus}</span>}
        <div className="booking-record-actions">
          <button type="button" onClick={() => navigate("venue", { venue: venueFromBooking(booking) })}>View centre</button>
          <button type="button" onClick={openBookingMessages}>Message centre</button>
          {canRequestCancellation && <button type="button" className="danger-subtle" onClick={() => onBookingOperation?.(booking, "player_cancel_request")}>Cancel booking</button>}
          {canRequestRefund && <button type="button" onClick={() => onRefundReview?.(booking, "booking")}>Request refund review</button>}
        </div>
      </div>
    </article>
  );
}

function PaymentRecordCard({ payment, onRefundReview }) {
  const paid = /paid|succeeded|complete/i.test(`${payment.status || ""} ${payment.paymentStatus || ""}`);
  return (
    <article className="booking-record-card payment-record-card">
      <div className="booking-record-icon">
        <WalletCards size={22} />
      </div>
      <div>
        <span>{payment.status || "Recorded"} · {payment.kind || "payment"}</span>
        <strong>{payment.title || "Payment"}</strong>
        <p>{[payment.subject, payment.provider, payment.checkoutReference].filter(Boolean).join(" · ") || "Account payment record"}</p>
      </div>
      <div className="booking-record-total">
        <strong>{payment.amount || "Pending"}</strong>
        {payment.refundReviewStatus && <span>{payment.refundReviewStatus}</span>}
        {paid && <button type="button" onClick={() => onRefundReview?.(payment, "payment")}>Request refund review</button>}
      </div>
    </article>
  );
}

const followingFilterOptions = [
  ["all", "All", UsersRound],
  ["player", "Players", UserCircle],
  ["coach", "Coaches", Trophy],
  ["group", "Groups", UsersRound],
  ["centre", "Centres", Building2]
];

const openSocialFollowPath = (path = "", navigate) => {
  const publicPath = String(path || "").trim();
  if (!/^\/(?:players|coaches|social-groups|centres)\/[^?#\s]+/i.test(publicPath)) {
    navigate("home");
    return;
  }
  window.history.pushState({}, "", publicPath);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const followingInitials = (label = "") => String(label || "Badminta")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((word) => word[0]?.toUpperCase())
  .join("") || "B";

function FollowingCard({ item, navigate, toggleFollowing }) {
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const safeImage = getAllowedUserMediaUrl(item.image);
  const Icon = {
    player: UserCircle,
    coach: Trophy,
    group: UsersRound,
    centre: Building2
  }[item.role] || UserCircle;
  const unfollow = async () => {
    if (busy) return;
    setBusy(true);
    setErrorMessage("");
    try {
      await toggleFollowing(item, false);
    } catch (error) {
      setErrorMessage(marketplaceFunctionErrorMessage(error) || "Follow status could not be updated.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className={`following-card role-${item.role}`}>
      <button type="button" className="following-card-main" onClick={() => openSocialFollowPath(item.publicPath, navigate)}>
        <span className="following-card-media">
          {safeImage
            ? <img src={safeImage} alt="" loading="lazy" decoding="async" />
            : <b aria-hidden="true">{followingInitials(item.label)}</b>}
        </span>
        <span className="following-card-copy">
          <small><Icon size={14} /> {socialFollowRoleLabels[item.role] || "Profile"}</small>
          <strong>{item.label}</strong>
          <span>{item.area || "Location not added"}</span>
        </span>
        <ChevronRight size={18} />
      </button>
      <div className="following-card-actions">
        <button type="button" onClick={() => openSocialFollowPath(item.publicPath, navigate)}>
          View profile
        </button>
        <button type="button" className="following-state-button" onClick={unfollow} disabled={busy}>
          {busy ? <Loader2 size={16} className="spin" /> : <UserCheck size={16} />}
          {busy ? "Updating" : "Following"}
        </button>
      </div>
      {errorMessage && <small className="following-card-error" role="alert">{errorMessage}</small>}
    </article>
  );
}

function FollowingPage({ following = [], syncState = {}, navigate, toggleFollowing }) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const cleanSearch = compactLocationText(search);
  const filtered = following.filter((item) => (
    (activeFilter === "all" || item.role === activeFilter)
    && (!cleanSearch || compactLocationText(`${item.label} ${item.area} ${item.role}`).includes(cleanSearch))
  ));
  const counts = following.reduce((output, item) => ({
    ...output,
    [item.role]: Number(output[item.role] || 0) + 1
  }), {});

  return (
    <section className="page following-page">
      <header className="following-head">
        <div>
          <p>Your badminton network</p>
          <h1>Following</h1>
          <span>Return to players, coaches, social groups and centres whose updates matter to you.</span>
        </div>
        <div className="following-total">
          <UsersRound size={20} />
          <strong>{following.length.toLocaleString()}</strong>
          <span>profiles followed</span>
        </div>
      </header>

      <div className="following-toolbar">
        <div className="following-tabs" role="tablist" aria-label="Filter followed profiles">
          {followingFilterOptions.map(([key, label, Icon]) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeFilter === key}
              className={activeFilter === key ? "active" : ""}
              onClick={() => setActiveFilter(key)}
              key={key}
            >
              <Icon size={16} />
              <span>{label}</span>
              <b>{key === "all" ? following.length : counts[key] || 0}</b>
            </button>
          ))}
        </div>
        <label className="following-search">
          <Search size={17} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people, groups or centres" />
          {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear following search"><X size={15} /></button>}
        </label>
      </div>

      {syncState.status === "loading" && !following.length ? (
        <div className="following-loading" role="status">
          <SearchingIndicator />
          <span>Loading the profiles you follow.</span>
        </div>
      ) : filtered.length ? (
        <div className="following-grid">
          {filtered.map((item) => (
            <FollowingCard
              item={item}
              navigate={navigate}
              toggleFollowing={toggleFollowing}
              key={item.entityId || `${item.targetCollection}-${item.targetId}`}
            />
          ))}
        </div>
      ) : (
        <div className="following-empty">
          <UserPlus size={24} />
          <strong>{following.length ? "No followed profiles match this view" : "Your Following list is ready"}</strong>
          <span>{following.length
            ? "Choose another type or clear the search."
            : "Follow a player, coach, social group or centre from its public page and it will stay here across devices."}</span>
          <div>
            <button type="button" onClick={() => navigate("playersIndex")}>Find players</button>
            <button type="button" onClick={() => navigate("groupsIndex")}>Find groups</button>
            <button type="button" onClick={() => navigate("results")}>Find centres</button>
          </div>
        </div>
      )}
      {syncState.status === "error" && <div className="auth-message error" role="alert">{syncState.message}</div>}
    </section>
  );
}

function SavedItemCard({ item, navigate }) {
  const Icon = savedItemTypeIcons[item.type] || Heart;
  const targetPath = item.targetPath || routeHref(item.targetPage, item.targetPayload || {});
  const openItem = (event) => handleSeoRouteClick(event, navigate, item.targetPage, item.targetPayload || {});
  return (
    <a className={`saved-item-card ${item.type}`} href={targetPath} onClick={openItem}>
      <div className="saved-item-media">
        {item.image ? <img src={item.image} alt="" loading="lazy" decoding="async" /> : (
          <div className="saved-item-placeholder">
            <Icon size={24} />
          </div>
        )}
      </div>
      <div className="saved-item-copy">
        <span><Icon size={15} /> {savedItemTypeLabels[item.type] || "Saved"}</span>
        <strong>{item.title}</strong>
        {item.subtitle && <p>{item.subtitle}</p>}
        {item.meta && <em>{item.meta}</em>}
      </div>
      <ChevronRight size={18} />
    </a>
  );
}

function SavedItemGrid({ items, emptyState, navigate }) {
  return items.length ? (
    <div className="saved-item-grid">
      {items.map((item) => <SavedItemCard key={item.key} item={item} navigate={navigate} />)}
    </div>
  ) : (
    <CommunityEmptyState {...emptyState} navigate={navigate} compact />
  );
}

function SavedPage({ mode = "saved", venues, bookings = [], savedItems = [], accountProfile = defaultAccountProfile, saveAccountProfile, navigate, toggleSaved }) {
  const bookingsView = mode === "bookings";
  const checkoutReturned = bookingsView && new URLSearchParams(window.location.search).get("checkout") === "success";
  const savedVenueIds = new Set(venues.map((venue) => venue.id));
  const savedProfiles = savedItems.filter((item) => item.type === "player" || item.type === "coach");
  const savedCommunityItems = savedItems.filter((item) => item.type === "group" || item.type === "event");
  const savedCentreItems = savedItems.filter((item) => item.type === "centre" && !savedVenueIds.has(item.targetPayload?.venue?.id));
  const payments = getAccountPayments(accountProfile).filter((payment) => (
    ["booking", "centre", "court"].includes(String(payment.kind || "").toLowerCase())
      || Boolean(payment.bookingId)
  )
    && /paid|succeeded|completed|refunded/i.test(`${payment.status || ""} ${payment.paymentStatus || ""}`)
    && !/pending|requires|checkout opened|setup task|setup saved|payment setup/i.test(`${payment.title || ""} ${payment.status || ""}`));
  const [refundMessage, setRefundMessage] = useState("");
  const totalSavedCount = savedProfiles.length + savedCommunityItems.length + savedCentreItems.length + venues.length;
  const refundReportForRecord = (record, type, refundId, status) => ({
    id: `refund-report-${refundId || Date.now()}`,
    kind: "trust",
    title: "Refund review request",
    subject: type === "booking" ? record.venueName || "Court booking" : record.subject || record.title || "Payment record",
    comment: type === "booking"
      ? `Refund review requested for ${record.venueName || "court booking"} on ${formatDateLabel(record.date)} at ${formatTimeLabel(record.time)}.`
      : `Refund review requested for ${record.title || "payment"} ${record.amount ? `(${record.amount})` : ""}.`,
    status,
    refundReviewId: refundId,
    paymentId: type === "payment" ? record.id : record.checkoutReference || "",
    bookingId: type === "booking" ? record.id : record.bookingId || "",
    createdAt: new Date().toISOString()
  });
  const updateLocalRefundState = (record, type, refundId, status = "Refund review requested") => {
    if (!saveAccountProfile) return;
    const report = refundReportForRecord(record, type, refundId, status);
    const nextReviews = [
      report,
      ...(Array.isArray(accountProfile.reviews) ? accountProfile.reviews : []).filter((review) => review.refundReviewId !== refundId)
    ];
    if (type === "booking") {
      saveAccountProfile({
        bookings: getAccountBookings(accountProfile).map((booking) => booking.id === record.id ? {
          ...booking,
          refundReviewStatus: status,
          refundReviewId: refundId,
          refundReviewRequestedAt: new Date().toISOString()
        } : booking),
        reviews: nextReviews
      });
      return;
    }
    saveAccountProfile({
      payments: getAccountPayments(accountProfile).map((payment) => payment.id === record.id ? {
        ...payment,
        refundReviewStatus: status,
        refundReviewId: refundId,
        refundReviewRequestedAt: new Date().toISOString()
      } : payment),
      reviews: nextReviews
    });
  };
  const requestRefundReviewFor = async (record, type) => {
    const currency = normaliseCurrencyCode(record.currency || marketplaceCurrencyForCountry(record.countryCode));
    const amount = type === "booking"
      ? Number(record.total) || 0
      : Number(String(record.amount || "").replace(/[^0-9.]/g, "")) || 0;
    const amountCents = Number(record.totalCents || record.amountCents) || amountToMinorUnits(amount, currency);
    const localRefundId = `refund-review-${Date.now()}-${shortStableHash(record.id || record.title || type)}`;
    setRefundMessage("");
    try {
      const result = await callMarketplaceFunction("requestRefundReview", {
        bookingId: type === "booking" ? record.id : record.bookingId || "",
        paymentId: type === "payment" ? record.id : record.checkoutReference || "",
        sourceId: record.venueId || record.subject || record.title || "",
        sourceType: type,
        reason: "Customer requested refund review from saved account activity.",
        amountCents,
        currency
      });
      updateLocalRefundState(record, type, result.refundId || localRefundId, "Refund review requested");
      setRefundMessage("Refund review request saved to the support ledger.");
    } catch (error) {
      updateLocalRefundState(record, type, localRefundId, "Refund review saved locally");
      setRefundMessage(`${marketplaceFunctionErrorMessage(error)} The request was saved locally so support can follow up from messages.`);
    }
  };
  const updateLocalBookingOperation = (booking, action, result = {}) => {
    if (!saveAccountProfile) return;
    const now = new Date().toISOString();
    const nextBookings = getAccountBookings(accountProfile).map((item) => item.id === booking.id ? {
      ...item,
      status: result.status || (action === "player_cancel_request" ? "cancellation_requested" : item.status),
      cancellationStatus: action === "player_cancel_request" ? "player_requested" : item.cancellationStatus,
      refundReviewStatus: result.refundId ? "requested" : item.refundReviewStatus || "cancellation requested",
      refundReviewId: result.refundId || item.refundReviewId || "",
      lastOperationalAction: action,
      lastOperationalAt: now
    } : item);
    const report = {
      id: `booking-operation-${result.evidenceId || Date.now()}-${shortStableHash(booking.id || action)}`,
      kind: "trust",
      title: action === "player_cancel_request" ? "Booking cancellation request" : "Booking operation update",
      subject: booking.venueName || "Court booking",
      comment: action === "player_cancel_request"
        ? `Cancellation requested for ${booking.venueName || "court booking"} on ${formatDateLabel(booking.date)} at ${formatTimeLabel(booking.time)}.`
        : `Booking operation recorded: ${action.replace(/_/g, " ")}.`,
      status: result.status || "cancellation_requested",
      bookingId: booking.id,
      refundReviewId: result.refundId || "",
      serverReportId: result.evidenceId || "",
      createdAt: now
    };
    saveAccountProfile({
      bookings: nextBookings,
      reviews: [report, ...(Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [])].slice(0, 120)
    });
  };
  const recordBookingOperationFor = async (booking, action) => {
    setRefundMessage("");
    try {
      const result = await callMarketplaceFunction("recordCourtBookingOperationalEvent", {
        bookingId: booking.id,
        action,
        note: action === "player_cancel_request"
          ? "Player requested cancellation from saved booking activity."
          : `Booking operation recorded from account activity: ${action}.`
      });
      updateLocalBookingOperation(booking, action, result);
      setRefundMessage(action === "player_cancel_request"
        ? "Cancellation request saved to the booking support ledger."
        : "Booking operation saved to the support ledger.");
    } catch (error) {
      updateLocalBookingOperation(booking, action, {});
      setRefundMessage(`${marketplaceFunctionErrorMessage(error)} The booking update was saved locally so support can follow up.`);
    }
  };
  return (
    <section className="page index-page">
      <div className="index-hero">
        <div>
          <p>{bookingsView ? "Your play" : "Saved"}</p>
          <h1>{bookingsView ? "Bookings and payments." : "Players, groups and places you saved."}</h1>
          <span>{bookingsView
            ? "Track upcoming court time, payment confirmation, cancellations and receipts without leaving your player account."
            : "Keep the people, sessions and places you want to come back to without mixing them with bookings or payment tasks."}</span>
        </div>
        <div className="index-actions">
          <button type="button" onClick={() => navigate("results")}>{bookingsView ? "Book a court" : "Find more"}</button>
          <button type="button" onClick={() => navigate(bookingsView ? "playerDashboard" : "messages")}>{bookingsView ? "Player dashboard" : "Open messages"}</button>
        </div>
      </div>
      {checkoutReturned && (
        <div className="booking-return-banner" role="status">
          <BadgeCheck size={20} />
          <div>
            <strong>Payment received</strong>
            <span>Your booking confirmation and receipt are syncing below. This normally takes only a moment.</span>
          </div>
        </div>
      )}
      <div className="saved-summary-strip">
        {(bookingsView ? [
          ["Court bookings", bookings.length],
          ["Confirmed", bookings.filter((booking) => /confirmed|paid/i.test(`${booking.status} ${booking.paymentStatus}`)).length],
          ["Payment records", payments.length],
          ["Needs attention", bookings.filter((booking) => paymentNeedsAction(booking.paymentStatus || booking.status)).length]
        ] : [
          ["Saved items", totalSavedCount],
          ["Profiles", savedProfiles.length],
          ["Groups and events", savedCommunityItems.length],
          ["Centres", venues.length + savedCentreItems.length]
        ]).map(([label, value]) => (
          <article key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </article>
        ))}
      </div>
      {bookingsView && <RowSection title="Court bookings" action="Book another court" onAction={() => navigate("results")}>
        {refundMessage && <div className="auth-message saved-refund-message">{refundMessage}</div>}
        {bookings.length ? bookings.map((booking) => (
          <BookingRecordCard key={booking.id} booking={booking} navigate={navigate} onRefundReview={requestRefundReviewFor} onBookingOperation={recordBookingOperationFor} />
        )) : (
          <CommunityEmptyState
            type="court bookings"
            title="No court bookings yet"
            text="Book a court from a centre listing and the booking request, payment status and centre messages will appear here."
            primary={{ label: "Find courts", target: "results" }}
            secondary={{ label: "Court hire", target: "courtAvailability" }}
            icon={CalendarDays}
            navigate={navigate}
            compact
          />
        )}
      </RowSection>}
      {bookingsView && <RowSection title="Payments and receipts" action="Payment help" onAction={() => navigate("refundPolicy")}>
        {payments.length ? payments.map((payment) => (
          <PaymentRecordCard key={payment.id || payment.reference || payment.title} payment={payment} onRefundReview={requestRefundReviewFor} />
        )) : (
          <CommunityEmptyState
            type="payment records"
            title="No payment records yet"
            text="Court bookings, social sessions, events and coach payments will appear here as soon as a checkout, payment or refund workflow begins."
            primary={{ label: "Find courts", target: "results" }}
            secondary={{ label: "Refund policy", target: "refundPolicy" }}
            icon={WalletCards}
            navigate={navigate}
            compact
          />
        )}
      </RowSection>}
      {!bookingsView && <RowSection title="Saved player and coach profiles" action="Find players" onAction={() => navigate("playersIndex")}>
        <SavedItemGrid
          items={savedProfiles}
          navigate={navigate}
          emptyState={{
            type: "saved profiles",
            title: "No player or coach profiles saved yet",
            text: "Save public player and coach profiles when you want to invite, rate, book or message them later.",
            primary: { label: "Find players", target: "playersIndex" },
            secondary: { label: "Find coaches", target: "playersIndex" },
            icon: UserCircle
          }}
        />
      </RowSection>}
      {!bookingsView && <RowSection title="Saved social groups and events" action="View groups" onAction={() => navigate("groupsIndex")}>
        <SavedItemGrid
          items={savedCommunityItems}
          navigate={navigate}
          emptyState={{
            ...emptySocialGroupState("your saved list"),
            title: "No saved social groups or events yet",
            text: "Save social groups and events from their public pages so you can join, pay or message the organiser later.",
            primary: { label: "Find social groups", target: "groupsIndex" },
            secondary: { label: "Find events", target: "events" }
          }}
        />
      </RowSection>}
      {!bookingsView && <RowSection title="Saved centres" action="Search centres" onAction={() => navigate("results")}>
        {venues.length || savedCentreItems.length ? (
          <>
            {savedCentreItems.length > 0 && (
              <div className="saved-item-grid">
                {savedCentreItems.map((item) => <SavedItemCard key={item.key} item={item} navigate={navigate} />)}
              </div>
            )}
            {venues.length > 0 && (
              <div className="saved-centre-grid">
                {venues.map((venue) => (
                  <VenueTile key={venue.id} venue={venue} saved toggleSaved={toggleSaved} navigate={navigate} />
                ))}
              </div>
            )}
          </>
        ) : (
          <CommunityEmptyState
            type="saved centres"
            title="No saved centres yet"
            text="Save real centre listings from search results and they will appear here."
            primary={{ label: "Search centres", target: "results" }}
            secondary={{ label: "Add a centre", target: "claimCentre" }}
            icon={Heart}
            navigate={navigate}
            compact
          />
        )}
      </RowSection>}
    </section>
  );
}

const accountRolePublicTargets = {
  player: "publicPlayerProfile",
  organiser: "publicGroupProfile",
  centre: "publicCentreProfile",
  coach: "publicCoachProfile"
};

const accountRoleProfileLabel = (accountProfile = {}, role = "") => {
  if (role === "organiser") return accountProfile.group?.name || "Social group profile";
  if (role === "centre") return accountProfile.centre?.name || "Centre profile";
  if (role === "coach") return accountProfile.coach?.headline || accountProfile.fullName || "Coach profile";
  return accountProfile.fullName || "Player profile";
};

function AccountWorkspacePanel({ accountProfile, navigate }) {
  const coreAccountMissing = [
    !String(accountProfile.fullName || "").trim() ? "Full name" : "",
    !String(accountProfile.email || "").trim() ? "Email" : ""
  ].filter(Boolean);
  const roleRows = accountRoleOptions.map((role) => {
    const selected = accountHasRole(accountProfile, role.id);
    const missing = selected ? getPublicProfileMissingFields(accountProfile, role.id) : [];
    const coreReady = selected && missing.length === 0;
    const published = selected && roleHasPublishedBackendProfile(accountProfile, role.id);
    const readyToPublish = coreReady && !published;
    const ready = published;
    const readiness = selected ? roleReadiness(accountProfile, role.id) : { percent: 0, missing: [] };
    const primaryTarget = !selected ? "signup" : ready ? roleDashboardTargetFor(role.id) : roleSetupTargetFor(role.id);
    return {
      ...role,
      selected,
      coreReady,
      published,
      readyToPublish,
      ready,
      missing,
      readiness,
      profileLabel: accountRoleProfileLabel(accountProfile, role.id),
      primaryTarget,
      publicTarget: accountRolePublicTargets[role.id],
      statusText: ready ? "Live" : readyToPublish ? "Ready to publish" : selected ? `${missing.length} left` : "Add role"
    };
  }).sort((a, b) => Number(b.selected) - Number(a.selected) || Number(b.ready) - Number(a.ready));

  const inboxes = buildAccountInboxData(accountProfile);
  const accountThreads = inboxes.flatMap((inbox) => inbox.threads).filter(isMarketplaceConversationThread);
  const unreadThreads = inboxes.flatMap((inbox) => inbox.threads
    .filter((thread) => isMarketplaceConversationThread(thread) && Number(thread.unread || 0) > 0)
    .map((thread) => ({ ...thread, roleId: inbox.id, roleLabel: inbox.label })));
  const payments = getAccountPayments(accountProfile);
  const pendingPayments = payments.filter((payment) => paymentNeedsAction(payment.status)).slice(0, 4);
  const allBookings = getAccountBookings(accountProfile);
  const allJoinedGroups = getJoinedGroups(accountProfile);
  const allJoinedEvents = getJoinedEvents(accountProfile);
  const allManagedEvents = getAccountEvents(accountProfile);
  const bookings = allBookings.slice(0, 3);
  const joinedGroups = allJoinedGroups.slice(0, 3);
  const joinedEvents = allJoinedEvents.slice(0, 2);
  const managedEvents = allManagedEvents.slice(0, 2);
  const coreAccountRows = coreAccountMissing.length ? [{
    icon: UserCircle,
    title: "Complete account details",
    detail: `Add ${coreAccountMissing.join(" and ").toLowerCase()} so login recovery, receipts and profile ownership stay connected.`,
    status: `${coreAccountMissing.length} left`,
    target: "signup",
    payload: { signupStep: "details" },
    tone: "attention"
  }] : [];
  const incompleteRows = roleRows.filter((role) => role.selected && !role.ready).map((role) => ({
    icon: CircleHelp,
    title: role.readyToPublish ? `Publish ${role.title.toLowerCase()}` : `${role.title} setup`,
    detail: role.readyToPublish ? "Required details are complete. Review and publish the profile now." : role.missing.slice(0, 2).join(" · ") || "Complete onboarding",
    status: role.statusText,
    target: role.primaryTarget,
    tone: "attention"
  }));
  const paymentRows = pendingPayments.map((payment) => ({
    icon: WalletCards,
    title: payment.title || "Payment needs attention",
    detail: [payment.subject, payment.amount].filter(Boolean).join(" · ") || "Open payment record",
    status: payment.status || "Payment pending",
    target: payment.kind === "coach" ? "coachPayouts" : payment.kind === "centre" ? "centrePayments" : payment.kind === "group" ? "payouts" : "saved",
    tone: paymentStatusTone(payment.status)
  }));
  const messageRows = unreadThreads.slice(0, 3).map((thread) => ({
    icon: MessageCircle,
    title: thread.name || "Unread message",
    detail: [thread.roleLabel, thread.preview].filter(Boolean).join(" · "),
    status: `${thread.unread} unread`,
    target: "messages",
    payload: { messageFocus: { roleId: thread.roleId, threadId: thread.id } },
    tone: "pending"
  }));
  const playRows = [
    ...bookings.map((booking) => ({
      icon: Building2,
      title: booking.venueName || "Court booking",
      detail: [formatDateLabel(booking.date), formatTimeLabel(booking.time), getBookingTotalLabel(booking)].filter(Boolean).join(" · "),
      status: booking.paymentStatus || booking.status || "Booking",
      target: "bookings",
      tone: paymentStatusTone(booking.paymentStatus)
    })),
    ...joinedGroups.map((group) => ({
      icon: UsersRound,
      title: group.title || "Joined social group",
      detail: [group.venue, group.timeLabel || group.time, getGroupPriceLabel(group)].filter(Boolean).join(" · "),
      status: group.paymentStatus || group.joinedStatus || "Joined",
      target: paymentNeedsAction(group.paymentStatus) ? "join" : "group",
      payload: { group, bookingType: "social" },
      tone: paymentStatusTone(group.paymentStatus)
    })),
    ...joinedEvents.map((event) => ({
      icon: CalendarDays,
      title: event.title || "Joined event",
      detail: [formatDateLabel(event.date), formatTimeLabel(event.time), getEventVenueLabel(event)].filter(Boolean).join(" · "),
      status: event.paymentStatus || event.joinedStatus || "Joined",
      target: "event",
      payload: { event },
      tone: paymentStatusTone(event.paymentStatus)
    })),
    ...managedEvents.map((event) => ({
      icon: Star,
      title: event.title || "Managed event",
      detail: [formatDateLabel(event.date), formatTimeLabel(event.time), getEventVenueLabel(event)].filter(Boolean).join(" · "),
      status: event.status === "published" ? "Public" : "Draft",
      target: "manageEvent",
      payload: { event },
      tone: event.status === "published" ? "paid" : "neutral"
    }))
  ];
  const priorityRows = [...coreAccountRows, ...incompleteRows, ...paymentRows, ...messageRows, ...playRows].slice(0, 8);
  const selectedCount = roleRows.filter((role) => role.selected).length;
  const readyCount = roleRows.filter((role) => role.ready).length;
  const publishPendingCount = roleRows.filter((role) => role.readyToPublish).length;
  const setupLeft = coreAccountMissing.length
    + publishPendingCount
    + roleRows.filter((role) => role.selected && !role.readyToPublish).reduce((total, role) => total + role.missing.length, 0);
  const unreadCount = accountThreads.reduce((total, thread) => total + Number(thread.unread || 0), 0);
  const paymentActionCount = countPaymentsNeedingAction(payments);
  const playRecordCount = allBookings.length + allJoinedGroups.length + allJoinedEvents.length + allManagedEvents.length;
  const nextIncompleteRole = roleRows
    .filter((role) => role.selected && !role.ready)
    .sort((first, second) => Number(second.readyToPublish) - Number(first.readyToPublish) || first.missing.length - second.missing.length)[0];
  const nextIncompleteProfileLabel = nextIncompleteRole?.id === "organiser"
    ? "social group"
    : nextIncompleteRole ? `${nextIncompleteRole.id} profile` : "profile";
  const nextAction = coreAccountMissing.length
    ? { label: "Complete account", target: "signup", payload: { signupStep: "details" } }
    : nextIncompleteRole
      ? {
        label: nextIncompleteRole.readyToPublish ? `Publish ${nextIncompleteProfileLabel}` : `Finish ${nextIncompleteProfileLabel}`,
        target: nextIncompleteRole.primaryTarget
      }
      : unreadCount
      ? { label: "Open messages", target: "messages" }
      : paymentActionCount
        ? { label: "Review payments", target: "bookings" }
        : playRecordCount
          ? { label: "Review play plan", target: "bookings" }
          : { label: "Find play", target: "results" };
  const workspaceStats = [
    [BadgeCheck, `${readyCount}/${selectedCount || 0}`, "ready roles"],
    [CircleHelp, setupLeft, "setup left"],
    [MessageCircle, unreadCount, "unread"],
    [WalletCards, paymentActionCount, "payments"],
    [CalendarDays, playRecordCount, "play records"]
  ];
  const roleCommandCards = roleRows.filter((role) => role.selected).map((role) => {
    const ready = role.ready;
    const roleMissingText = role.readyToPublish ? "Required details are complete. Publish now to make this profile discoverable." : role.missing.slice(0, 2).join(" · ") || "Complete profile basics";
    const setupAction = { label: role.readyToPublish ? "Publish profile" : "Finish setup", target: role.primaryTarget, Icon: role.readyToPublish ? BadgeCheck : CircleHelp, primary: true };
    const commonMessageAction = { label: "Messages", target: "messages", Icon: MessageCircle };
    const publicAction = ready && role.publicTarget
      ? { label: "Public page", target: role.publicTarget, Icon: ExternalLink }
      : null;
    if (role.id === "player") {
      return {
        ...role,
        eyebrow: "Play",
        headline: ready ? "Manage your weekly play." : "Finish player setup before public discovery.",
        detail: ready
          ? "Court bookings, joined sessions, payments and player messages stay together here."
          : roleMissingText,
        metrics: [
          ["Bookings", allBookings.length],
          ["Groups", allJoinedGroups.length],
          ["Events", allJoinedEvents.length]
        ],
        actions: ready ? [
          { label: "Bookings", target: "bookings", Icon: WalletCards, primary: true },
          { label: "Find play", target: "results", Icon: Search },
          publicAction,
          commonMessageAction
        ].filter(Boolean) : [setupAction, { label: "Find play", target: "results", Icon: Search }]
      };
    }
    if (role.id === "organiser") {
      const groupMembers = getPublicGroupMembers(accountProfile.group || {}, accountProfile);
      const organiserSessions = Array.isArray(accountProfile.group?.sessions) ? accountProfile.group.sessions : [];
      const unpaidMembers = groupMembers.filter((member) => paymentNeedsAction(member.paymentStatus || member.status)).length;
      return {
        ...role,
        eyebrow: "Groups",
        headline: ready ? "Run sessions and members." : "Finish group setup before players can join.",
        detail: ready
          ? "Dated sessions, invited players, attendance and payment status are grouped here."
          : roleMissingText,
        metrics: [
          ["Sessions", organiserSessions.length],
          ["Members", groupMembers.length],
          ["Unpaid", unpaidMembers]
        ],
        actions: ready ? [
          { label: "Organiser tools", target: "organiser", Icon: LayoutDashboard, primary: true },
          { label: "Sessions", target: "groupSessions", Icon: CalendarDays },
          { label: "Members", target: "members", Icon: UsersRound },
          publicAction
        ].filter(Boolean) : [setupAction, { label: "Create group", target: "createGroup", Icon: Plus }]
      };
    }
    if (role.id === "centre") {
      const centreProducts = getCentreMembershipProducts(accountProfile.centre || {}).filter((product) => product.status !== "archived");
      const centreEvents = getOwnerEvents(accountProfile, "centre");
      return {
        ...role,
        eyebrow: "Centre",
        headline: ready ? "Manage courts, bookings and revenue." : "Finish centre setup before booking tools open.",
        detail: ready
          ? "Court setup, booking readiness, centre events, memberships and payments are grouped here."
          : roleMissingText,
        metrics: [
          ["Courts", accountProfile.centre?.courtCount || 0],
          ["Events", centreEvents.length],
          ["Products", centreProducts.length]
        ],
        actions: ready ? [
          { label: "Centre tools", target: "centreDashboard", Icon: LayoutDashboard, primary: true },
          { label: "Courts", target: "courtManager", Icon: Grid3X3 },
          { label: "Payments", target: "centrePayments", Icon: WalletCards },
          publicAction
        ].filter(Boolean) : [setupAction, { label: "Claim centre", target: "centreClaimSearch", Icon: BadgeCheck }]
      };
    }
    const coachMedia = cleanLinkRows(accountProfile.coach?.mediaLinks, "Coach media").length;
    return {
      ...role,
      eyebrow: "Coaching",
      headline: ready ? "Manage lessons and enquiries." : "Finish coach setup before players can book.",
      detail: ready
        ? "Lesson price, availability, proof, messages and payout setup stay visible here."
        : roleMissingText,
      metrics: [
        ["Rate", accountProfile.coach?.hourlyRate || "Missing"],
        ["Media", coachMedia],
        ["Unread", unreadThreads.filter((thread) => thread.roleId === "coach").length]
      ],
      actions: ready ? [
        { label: "Coach tools", target: "coachDashboard", Icon: LayoutDashboard, primary: true },
        { label: "Profile", target: "coachProfile", Icon: Trophy },
        { label: "Payments", target: "coachPayouts", Icon: WalletCards },
        publicAction
      ].filter(Boolean) : [setupAction, { label: "Coach profile", target: "coachProfile", Icon: Trophy }]
    };
  });

  return (
    <section className="account-workspace-panel">
      <div className="account-workspace-head">
        <div>
          <p>Account workspace</p>
          <h2>One place to manage your Badminta account.</h2>
          <span>{coreAccountMissing.length
            ? `Add ${coreAccountMissing.join(" and ").toLowerCase()} to finish the account itself. Your saved role profiles stay intact.`
            : selectedCount ? `${readyCount}/${selectedCount} selected role${selectedCount === 1 ? "" : "s"} ready.` : "Choose at least one user type to unlock the right tools."}</span>
        </div>
        <div className="account-workspace-actions">
          <button type="button" className="primary" onClick={() => navigate(nextAction.target, nextAction.payload || {})}>
            <span>{nextAction.label}</span>
            <ChevronRight size={17} />
          </button>
          <button type="button" onClick={() => navigate("messages")}>Messages</button>
          <button type="button" onClick={() => navigate("signup", { signupStep: "roles" })}>
            <SlidersHorizontal size={17} />
            User types
          </button>
        </div>
      </div>
      <div className="account-workspace-stats">
        {workspaceStats.map(([StatIcon, value, label]) => (
          <article key={label}>
            <StatIcon size={17} />
            <strong>{value}</strong>
            <span>{label}</span>
          </article>
        ))}
      </div>
      {roleCommandCards.length ? (
        <div className={`account-role-command-grid role-count-${Math.min(roleCommandCards.length, 4)}`}>
          {roleCommandCards.map((role) => {
            const Icon = role.icon;
            return (
              <article key={role.id} className={role.ready ? "ready" : "needs-work"}>
                <div className="account-role-command-head">
                  <span><Icon size={19} /></span>
                  <div>
                    <p>{role.eyebrow}</p>
                    <h3>{role.title}</h3>
                  </div>
                  <em>{role.statusText}</em>
                </div>
                <strong>{role.headline}</strong>
                <small>{role.detail}</small>
                <div className="account-role-command-metrics">
                  {role.metrics.map(([label, value]) => (
                    <span key={label}><b>{value}</b>{label}</span>
                  ))}
                </div>
                <div className="account-role-command-actions">
                  {role.actions.map(({ label, target, Icon: ActionIcon, primary, payload }) => (
                    <button type="button" key={label} className={primary ? "primary" : ""} onClick={() => navigate(target, payload || {})}>
                      <ActionIcon size={15} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="account-role-command-empty">
          <SlidersHorizontal size={20} />
          <strong>Choose how you want to use Badminta</strong>
          <span>Select player, organiser, centre or coach so the account page only shows tools that match your role.</span>
          <button type="button" onClick={() => navigate("signup", { signupStep: "roles" })}>Choose user types</button>
        </div>
      )}
      <div className="account-workspace-grid">
        <section className="account-priority-lane">
          <div className="role-panel-head">
            <p>Account activity</p>
            <h2>What needs attention</h2>
          </div>
          {priorityRows.length ? (
            <div className="account-priority-list">
              {priorityRows.map(({ icon: RowIcon, title, detail, status, target, payload, tone = "neutral" }, index) => (
                <button type="button" key={`${title}-${index}`} onClick={() => navigate(target, payload || {})}>
                  <span><RowIcon size={17} /></span>
                  <div>
                    <strong>{title}</strong>
                    <small>{detail}</small>
                  </div>
                  <em className={`payment-chip ${tone}`}>{status}</em>
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          ) : (
            <div className="account-workspace-empty">
              <BadgeCheck size={20} />
              <strong>No urgent account activity</strong>
              <span>Bookings, joined groups, payment records, messages and event actions will appear here when they exist.</span>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

function AccountPage({ navigate, auth, accountProfile, accountSyncStatus = {}, preferredPhoneCountryCode = "" }) {
  if (auth.loading) return <AccountSessionRestoring />;
	if (accountProfileIsRestoring(auth, accountProfile, accountSyncStatus)) return <AccountSessionRestoring />;

  if (!auth.user) {
    return (
      <section className="page auth-page">
        <div className="auth-panel wide">
          <PhoneAuthPanel auth={auth} title="Log in to your Badminta account" onVerified={() => navigate("account")} preferredCountryCode={preferredPhoneCountryCode} />
          <div className="auth-links">
            <button type="button" onClick={() => navigate("signup")}>Create account after phone verification</button>
          </div>
        </div>
      </section>
    );
  }

  const name = accountProfile.fullName || auth.user.displayName || "Verified Badminta member";
  const selectedRoles = accountRoleOptions.filter((role) => accountProfile.roles?.includes(role.id));
  const roles = selectedRoles.length ? selectedRoles.map((role) => role.title).join(", ") : "No user types selected yet";
  const accountAvatar = getAccountAvatarUrl(accountProfile);
  const accountLinks = cleanLinkRows(accountProfile.socialLinks, "Account link");
  const accountRatingSummary = reviewSummaryFromRecords(accountProfile);
  const accountReviews = Array.isArray(accountProfile.reviews) ? accountProfile.reviews : [];
  const syncState = accountSyncStatus.state || "local";
  const syncCopy = {
    cloud: ["Cloud synced", "Your account profile and activity streams are saved to Firestore under your verified phone account.", BadgeCheck],
    syncing: ["Syncing", "Badminta is syncing this profile and activity streams with Firestore.", Clock3],
    local: ["Local only", "This profile is stored on this device until a real Firebase phone session is available.", CircleHelp],
    error: ["Sync needs attention", accountSyncStatus.message || "Badminta saved this profile locally, but Firestore sync needs attention.", CircleHelp]
  }[syncState] || ["Profile storage", accountSyncStatus.message || "Account profile storage status is being checked.", CircleHelp];
  const SyncIcon = syncCopy[2];
  return (
    <section className="page account-page">
      <div className={`profile-cover compact ${accountAvatar ? "has-avatar" : "no-avatar"}`}>
        {accountAvatar ? <img src={accountAvatar} alt="" /> : <NoImagePlaceholder label="No profile image yet" />}
        <div className="account-profile-main">
          <div className="account-profile-copy">
            <p>Account</p>
            <h1>{name}</h1>
            <span>{auth.user.phoneNumber} · {roles}</span>
          </div>
          <div className="account-rating-summary" aria-label={accountRatingSummary.count ? accountRatingSummary.meta : "No account reviews yet"}>
            <Star size={18} fill={accountRatingSummary.count ? "currentColor" : "none"} />
            <div>
              <strong>{accountRatingSummary.average ? accountRatingSummary.average.toFixed(1) : "New"}</strong>
              <span>{accountRatingSummary.count ? `${accountRatingSummary.count} review${accountRatingSummary.count === 1 ? "" : "s"}` : "No reviews"}</span>
            </div>
          </div>
          <div className="profile-actions">
            <button type="button" onClick={async () => { await auth.logOut(); navigate("login"); }}>Log out</button>
          </div>
        </div>
      </div>
      <section className={`account-sync-note ${syncState}`}>
        <SyncIcon size={20} />
        <div>
          <strong>{syncCopy[0]}</strong>
          <span>{syncCopy[1]}</span>
        </div>
      </section>
      <AccountWorkspacePanel accountProfile={accountProfile} navigate={navigate} />
      {accountLinks.length > 0 && (
        <section className="account-asset-section">
          <h2>Public account links</h2>
          <PublicLinkList links={accountLinks} />
        </section>
      )}
      <AccountReviewActivity reviews={accountReviews} navigate={navigate} />
    </section>
  );
}

function AccountReviewActivity({ reviews = [], navigate }) {
  const publicReviews = reviews.filter(isRatingEligibleReview);
  const privateReports = reviews.filter((review) => review.kind === "trust" || review.ratingEligible === false);
  const latestRows = reviews.slice(0, 5);
  return (
    <section className="account-asset-section account-review-activity">
      <div className="section-title-row">
        <div>
          <p>Reviews and trust</p>
          <h2>Feedback activity</h2>
        </div>
        <button type="button" onClick={() => navigate("ratings")}>Open ratings</button>
      </div>
      <div className="review-activity-summary">
        <article>
          <strong>{publicReviews.length}</strong>
          <span>Rating reviews</span>
        </article>
        <article>
          <strong>{privateReports.length}</strong>
          <span>Private reports</span>
        </article>
        <article>
          <strong>{publicReviews.length ? (publicReviews.reduce((total, review) => total + Number(review.score), 0) / publicReviews.length).toFixed(1) : "--"}</strong>
          <span>Public average</span>
        </article>
      </div>
      {latestRows.length ? (
        <div className="review-activity-list">
          {latestRows.map((review) => (
            <article key={review.id}>
              <div>
                <span>{reviewKindLabels[review.kind] || "Feedback"}</span>
                <strong>{review.subject || review.title}</strong>
                <p>{review.comment || (review.kind === "trust" ? "Private report submitted." : "No comment added.")}</p>
              </div>
              <b>{isRatingEligibleReview(review) ? `${Number(review.score).toFixed(0)}/5` : "Private"}</b>
            </article>
          ))}
        </div>
      ) : (
        <CommunityEmptyState
          type="feedback"
          title="No reviews or reports yet"
          text="Ratings, recommendations, testimonials and private trust reports submitted from public profiles will appear here."
          primary={{ label: "Find players", target: "playersIndex" }}
          secondary={{ label: "Open messages", target: "messages" }}
          icon={Star}
          navigate={navigate}
          compact
        />
      )}
    </section>
  );
}

function AccountDeletionPage({ navigate, auth, accountProfile = defaultAccountProfile }) {
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState({ status: "idle", message: "" });
  const user = auth.user;
  const accountName = accountProfile.fullName || user?.displayName || "Badminta member";
  const accountContact = user?.phoneNumber || accountProfile.email || "Verified account";

  const submitDeletionRequest = async () => {
    if (!user) {
      navigate("login");
      return;
    }
    if (!confirmed || state.status === "saving") return;
    setState({ status: "saving", message: "Submitting your deletion request..." });
    try {
      const result = await callMarketplaceFunction("submitSupportReport", {
        kind: "account_deletion",
        title: "Account deletion request",
        subject: accountName,
        subjectId: user.uid,
        subjectType: "user",
        comment:
          "The signed-in member requested deletion from the public Badminta account deletion page.",
        countryCode: String(accountProfile.countryCode || accountProfile.player?.countryCode || ""),
        sourcePath: "/account/delete"
      });
      setState({
        status: "success",
        message: `Request received${result?.reportId ? ` (${result.reportId})` : ""}. Badminta support will process the account and profile deletion and contact you only if identity, transaction, fraud-prevention or legal checks are required.`
      });
    } catch (error) {
      setState({
        status: "error",
        message:
          error?.message ||
          "Badminta could not submit the request. Try again or email support@badminta.com."
      });
    }
  };

  return (
    <section className="page account-deletion-page">
      <div className="account-deletion-hero">
        <div>
          <p>Account control</p>
          <h1>Delete your Badminta account.</h1>
          <span>
            Start deletion of your account, public profiles and optional media
            from one verified request.
          </span>
        </div>
        <ShieldAlert size={64} aria-hidden="true" />
      </div>

      <div className="account-deletion-layout">
        <main className="account-deletion-content">
          <section>
            <ShieldCheck size={22} />
            <div>
              <h2>What will be deleted</h2>
              <p>
                Your account access, public player, coach, group and centre
                profiles, optional media, follows and non-required profile
                information will be removed or de-published when the request is
                completed.
              </p>
            </div>
          </section>
          <section>
            <LockKeyhole size={22} />
            <div>
              <h2>Records that may be retained</h2>
              <p>
                Booking, payment, refund, payout, fraud-prevention, safety,
                dispute and legal records may be retained only where reasonably
                required. They are not kept as a public profile.
              </p>
            </div>
          </section>
          <section>
            <Check size={22} />
            <div>
              <h2>How the request is verified</h2>
              <p>
                Sign in with the phone number attached to the account. The
                request is recorded privately for support review and cannot be
                submitted for another member.
              </p>
            </div>
          </section>
        </main>

        <aside className="account-deletion-request">
          {user ? (
            <>
              <span className="account-deletion-label">Signed-in account</span>
              <strong>{accountName}</strong>
              <small>{accountContact}</small>
              {state.status === "success" ? (
                <div className="account-deletion-status success" role="status">
                  <Check size={20} />
                  <p>{state.message}</p>
                </div>
              ) : (
                <>
                  <label className="account-deletion-confirm">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <span>
                      I understand that this request will close my account and
                      remove my public profiles.
                    </span>
                  </label>
                  {state.message && (
                    <p
                      className={`account-deletion-message ${state.status}`}
                      role={state.status === "error" ? "alert" : "status"}
                    >
                      {state.message}
                    </p>
                  )}
                  <button
                    type="button"
                    className="account-deletion-submit"
                    disabled={!confirmed || state.status === "saving"}
                    onClick={submitDeletionRequest}
                  >
                    {state.status === "saving" ? (
                      <Loader2 size={18} className="spin" />
                    ) : (
                      <ShieldAlert size={18} />
                    )}
                    Request account deletion
                  </button>
                </>
              )}
              <button
                type="button"
                className="account-deletion-secondary"
                onClick={() => navigate("account")}
              >
                Return to account
              </button>
            </>
          ) : (
            <>
              <span className="account-deletion-label">Identity required</span>
              <strong>Sign in before requesting deletion</strong>
              <p>
                Phone verification protects accounts from deletion requests
                submitted by someone else.
              </p>
              <button
                type="button"
                className="account-deletion-submit"
                onClick={() => navigate("login")}
              >
                <LockKeyhole size={18} />
                Sign in to continue
              </button>
              <a href="mailto:support@badminta.com">Contact support</a>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

function HelpPage({ navigate, accountProfile = defaultAccountProfile, saveAccountProfile }) {
  const contactSupport = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: "player",
    subject: "Badminta support",
    threadType: "Support conversation",
    text: "Support conversation opened from the help centre. Include booking, group, centre, event or account details before sending a support request.",
    status: "Open",
    meta: ["Help centre", accountProfile.fullName || "Account member", accountProfile.email],
    context: [["Area", "Help centre"], ["Account", accountProfile.fullName || "Pending"], ["Email", accountProfile.email || "Pending"]],
    actions: [["Help", "help"], ["Account", "account"], ["Messages", "messages"]]
  });
  return (
    <section className="page help-page">
      <div className="screen-head">
        <p>Help centre</p>
        <h1>Support for players, organisers and centres.</h1>
        <button type="button" onClick={contactSupport}>Contact support</button>
      </div>
      <div className="help-grid">
        {[
          ["Booking help", "Court changes, cancellations, refunds and no-shows.", "refundPolicy"],
          ["Group help", "Invite members, fill waitlists, set levels and manage payments.", "organiserPolicy"],
          ["Centre help", "Claim a listing, set courts, manage pricing and receive payouts.", "centrePolicy"],
          ["Trust and safety", "Rating disputes, unsafe behaviour, moderation and reports.", "communityPolicy"],
          ["Privacy", "Phone login, profile media, messages, payment records and account controls.", "privacyPolicy"],
          ["Cookie choices", "Control optional analytics and advertising measurement.", "cookiePolicy"],
          ["Youth safety", "Privacy, supervision and account safeguards for junior badminton.", "youthPolicy"],
          ["Delete account", "Request deletion of your account, public profiles and optional media.", "accountDeletion"],
          ["Terms", "Marketplace rules for players, centres, organisers, coaches and events.", "legalTerms"]
        ].map(([title, text, target]) => (
          <button type="button" key={title} onClick={() => navigate(target)}>
            <LifeBuoyIcon />
            <strong>{title}</strong>
            <p>{text}</p>
          </button>
        ))}
      </div>
      <section className="support-ops-grid">
        {[
          ["Refund and dispute workflow", "Payments, cancellations, no-shows and chargebacks must be handled through ledger records, support messages and admin review.", "refundPolicy"],
          ["Community reporting", "Unsafe play, wrong level, harassment, duplicate listings and incorrect centre data route into private trust reports.", "communityPolicy"],
          ["Owner responsibilities", "Centres, organisers and coaches must keep availability, pricing, refund rules and payout setup accurate before taking payments.", "centrePolicy"]
        ].map(([title, text, target]) => (
          <article key={title}>
            <ShieldCheck size={20} />
            <strong>{title}</strong>
            <span>{text}</span>
            <button type="button" onClick={() => navigate(target)}>Open policy</button>
          </article>
        ))}
      </section>
      <section className="support-workflow-section">
        <div className="support-workflow-heading">
          <p>Resolution guide</p>
          <h2>What to report, what happens next and where to escalate.</h2>
          <span>Choose the issue that best matches your situation. Do not include passwords, verification codes or full payment credentials in a support message.</span>
        </div>
        <div className="support-workflow-list">
          {supportWorkflows.map(([title, text], index) => (
            <article key={title}>
              <b>{String(index + 1).padStart(2, "0")}</b>
              <div><h3>{title}</h3><p>{text}</p></div>
            </article>
          ))}
        </div>
        <div className="support-workflow-cta">
          <div><strong>Start with one clear case</strong><span>Include the relevant URL or record ID, a concise timeline, evidence you are allowed to share and the outcome you are seeking.</span></div>
          <button type="button" onClick={contactSupport}>Open support conversation</button>
        </div>
      </section>
    </section>
  );
}

function LifeBuoyIcon() {
  return <ShieldCheck size={22} />;
}

const policyPages = {
  terms: {
    eyebrow: "Terms",
    title: "Badminta marketplace terms.",
    updated: "Effective 6 August 2026",
    summary: "These terms govern use of Badminta by players, centres, organisers, coaches and event hosts worldwide, including profiles, bookings, payments, payouts, messages and community content.",
    actions: [["Privacy", "privacyPolicy"], ["Refunds", "refundPolicy"], ["Community safety", "communityPolicy"]],
    sections: [
      ["Agreement and eligibility", "By creating an account, publishing a profile, booking or accepting payment, you agree to these terms and the role-specific terms that apply to you. Children under 13 may not manage an independent public account. A parent or legal guardian must control their participation and provide legally required consent. Commercial provider, centre-owner, payout and Stripe onboarding roles require an adult or authorised adult representative."],
      ["Platform role", "Badminta provides discovery, communications, booking, payment, payout and support technology. Centres, organisers, coaches and event hosts supply the underlying badminton service and remain responsible for its accuracy, safety, legality and delivery. Badminta may act as a disclosed payment collection agent for a supplier but does not become the venue, organiser or coach."],
      ["Accounts and content", "You must provide current, accurate account, profile, availability, price and contact information; protect access to your account; and only publish content you own or are authorised to use. Impersonation, fabricated reviews, misleading qualifications, unlawful content, scraping, payment circumvention and interference with the service are prohibited."],
      ["Bookings and payments", "The total price, applicable service fee, currency, cancellation rule and supplier are shown before checkout. Payments are processed through Stripe. A booking is confirmed only when Badminta records successful payment and the relevant inventory or capacity remains reserved. Owners must complete Stripe-hosted identity and payout onboarding before paid listings can accept checkout."],
      ["Payouts, refunds and disputes", "Badminta may deduct disclosed platform fees, payment processing fees, refunds, reversals, chargebacks, taxes and permitted reserves before payout. Payout timing can be affected by Stripe verification, banking systems, disputes or legal requirements. Refunds follow the published booking rule, the Refund Policy and any non-excludable rights under applicable law."],
      ["Safety and offline activity", "Badminton and in-person meetings carry risks. Users must assess venue, skill, health, supervision and personal safety, follow venue and organiser rules, and seek emergency assistance when needed. Nothing in these terms excludes a liability or right that cannot lawfully be excluded."],
      ["Trust and enforcement", "Badminta may investigate reports, require evidence, correct or hide listings, remove content, reverse verification, restrict messaging or bookings, hold payouts where permitted, suspend accounts and report suspected unlawful conduct. Material decisions can be challenged through support and are recorded for review."],
      ["Changes, law and contact", `We may update these terms prospectively and will publish the effective date. Mandatory local consumer and privacy rights continue to apply. Subject to those rights, these terms are governed by ${legalConfig.governingLaw} and disputes may be brought in ${legalConfig.disputeVenue}. Contact ${legalConfig.supportEmail} for notices or complaints.`]
    ],
    checklists: [
      ["Your booking record", "Supplier and service", "Date and time", "Total price and currency", "Cancellation rule", "Payment status"],
      ["Marketplace safeguards", "Owner verification", "Payment ledger", "Support evidence", "Moderation and appeals", "Audit history"]
    ]
  },
  privacy: {
    eyebrow: "Privacy",
    title: "Privacy and personal information policy.",
    updated: "Effective 6 August 2026",
    summary: "This policy explains what personal information Badminta collects, why it is used, when it is disclosed, how long it is retained and how users can access, correct or delete it.",
    actions: [["Terms", "legalTerms"], ["Community safety", "communityPolicy"], ["Account", "account"]],
    sections: [
      ["Information collected", "We collect information you provide, including phone number, name, email, role, skill and coaching details, availability, location, public profile text, media, social links, venue details and ownership evidence. We also collect bookings, attendance, messages, reviews, support reports, device/security signals and transaction references."],
      ["Purposes and lawful use", "We use information to authenticate accounts, provide chosen public profiles, personalise nearby discovery, operate bookings and payouts, send service notices, prevent fraud and abuse, moderate content, resolve complaints, comply with law and improve reliability. We do not sell raw personal information."],
      ["Public and private information", "Published profile, group, coach, event and centre fields are visible to the public and search engines. Phone verification data, private messages, payout details, claim evidence, reports and internal moderation records are not public. Visibility controls apply to optional player media and profile details."],
      ["Service providers and overseas processing", "We disclose only the information needed to providers that operate the service, including Firebase/Google Cloud for hosting and data, Stripe for identity, payments and payouts, analytics and communications providers, and professional advisers or authorities where legally required. These providers may process data outside your country under their own safeguards and contracts."],
      ["Payments", "Stripe-hosted pages collect card, bank, identity and payout information. Badminta does not store raw card numbers or online banking credentials. We retain Stripe account and transaction identifiers, amounts, status, fees, refunds, disputes and evidence needed to operate and reconcile the marketplace."],
      ["Location, analytics and cookies", "With browser permission, location can rank nearby results. Essential local storage keeps sessions and drafts. Optional Google Analytics 4, Microsoft Clarity, Meta and TikTok tools stay off until the relevant consent is granted. We do not intentionally send profile text, phone numbers, email addresses, private messages or payment details in conversion events. You can deny location, use a typed area and choose essential-only tracking."],
      ["Security and retention", "We use access rules, role ownership checks, App Check, encryption in transit, restricted secrets, audit records and backups. No system is risk-free. Account/profile data is kept while your account is active; financial, fraud, safety and dispute records may be kept longer where reasonably necessary or legally required; optional media is removed or de-published when deletion is completed."],
      ["International privacy rights", "Depending on where you live, you may have rights to access, correct, delete, restrict or object to processing, withdraw consent, obtain a portable copy, opt out of targeted advertising or certain disclosures, and complain to a privacy regulator. Badminta will apply mandatory local rights even where this policy describes a narrower baseline."],
      ["Access, correction, deletion and complaints", `Use account controls to update profile data and visibility. To request access, correction, export, deletion, restriction, advertising opt-out or to make a complaint, contact ${legalConfig.supportEmail}. We may verify identity and retain records required for transactions, fraud prevention, safety, disputes or law. Australian users may contact the OAIC if a complaint remains unresolved.`],
      ["Data incidents", "Badminta investigates suspected data breaches, contains and documents incidents, assesses likely harm and notifies affected people and regulators where required, including under Australia's Notifiable Data Breaches scheme."]
    ],
    checklists: [
      ["Your privacy controls", "Access account data", "Correct profile data", "Control public fields", "Remove optional media", "Request deletion review"],
      ["Protective controls", "Firebase access rules", "Storage ownership", "App Check", "Restricted secrets", "Audit logs", "Backups"]
    ]
  },
  refunds: {
    eyebrow: "Refunds",
    title: "Refund, cancellation and dispute policy.",
    updated: "Effective 6 August 2026",
    summary: "This policy applies to court bookings, social sessions, coaching, events, memberships, cancellations, no-shows, refunds and payment disputes made through Badminta.",
    actions: [["Terms", "legalTerms"], ["Centre terms", "centrePolicy"], ["Contact support", "help"]],
    sections: [
      ["Consumer rights", "Nothing in this policy limits a consumer guarantee or remedy that cannot be excluded under applicable law. In Australia, services come with automatic guarantees, including due care and skill, fitness for a disclosed purpose and supply within a reasonable time where no time is agreed."],
      ["Before payment", "The checkout summary identifies the supplier, service, total price, currency and applicable cancellation rule. A stricter supplier rule cannot override mandatory law. A pending or expired checkout is not a confirmed booking."],
      ["User cancellation", "Where a published cancellation window allows it, request cancellation from the booking or support record before the deadline. The displayed rule determines any refund, credit or retained amount, subject to mandatory consumer rights. Payment and platform fees may be non-refundable only where the law and the disclosed rule permit."],
      ["Supplier cancellation or service failure", "If a centre, organiser, coach or event host cancels, cannot supply the booked service, materially changes it without agreement, or does not meet an applicable consumer guarantee, the user may be entitled to a reschedule, credit, partial refund, full refund or other legal remedy."],
      ["No-shows and late arrival", "A user who does not attend or arrives too late may lose the booking amount where the rule was clearly disclosed and the service remained available. Safety emergencies, duplicate charges and exceptional circumstances can be reviewed through support with evidence."],
      ["Memberships and packages", "The product page must state included credits, expiry, renewal and cancellation terms. Used credits are recorded in the membership ledger. Refund rights for unused value depend on the published terms and applicable law; automatic renewal can be stopped before the next billing date."],
      ["Refund review and timing", "Submit the booking/payment ID, requested outcome and supporting evidence through support. Badminta may obtain information from both parties. Approved refunds are sent to the original payment method through Stripe; bank processing time is outside Badminta's control and the booking ledger will show the current state."],
      ["Chargebacks and disputes", "Contact support first where practical. A card dispute may pause the related payout and access while Stripe and the card network review evidence. Badminta records dispute events, preserves relevant evidence and updates the booking, refund and payout ledgers when an outcome is received."]
    ],
    checklists: [
      ["Refund request evidence", "Booking or session ID", "Payment ID", "Published rule", "Attendance status", "Messages or evidence"],
      ["Possible outcomes", "Full refund", "Partial refund", "Credit", "No refund", "Owner cancellation", "Escalate dispute"]
    ]
  },
  community: {
    eyebrow: "Community safety",
    title: "Community standards and trust policy.",
    updated: "Effective 6 August 2026",
    summary: "These standards apply to profiles, listings, messages, reviews, media and in-person activity arranged through Badminta.",
    actions: [["Report issue", "reviewFlow"], ["Privacy", "privacyPolicy"], ["Help", "help"]],
    sections: [
      ["Respectful play", "Players, coaches, centres and organisers must treat others respectfully, follow venue rules, avoid harassment, intimidation, discrimination and unsafe conduct, and respect skill-level expectations."],
      ["Accurate profiles", "Ratings, media, social links, coaching claims, centre details, group prices, schedules and event details must not mislead users."],
      ["Prohibited conduct", "Threats, stalking, sexual harassment, hate or discriminatory abuse, scams, impersonation, coordinated review manipulation, spam, dangerous instruction, illegal activity and attempts to move payments off-platform to avoid safeguards are prohibited."],
      ["Media and minors", "Only upload or link media you are authorised to use. Obtain permission from identifiable people and a parent or guardian where required for a child. Sexualised content involving minors, intimate imagery without consent and other illegal or seriously harmful content are prohibited and may be reported to authorities."],
      ["Reporting and emergencies", "Report accounts, listings, messages, reviews, media, safety incidents, payment issues, copyright concerns or suspicious claims through the relevant action or support. Reports are private to authorised reviewers. In immediate danger contact local emergency services; Australian users can also seek help from eSafety for eligible serious online abuse or illegal content."],
      ["Moderation and appeals", `Badminta may preserve evidence, hide content, remove reviews, restrict profiles, pause bookings or payouts where permitted, reverse verification, suspend accounts or report unlawful conduct. We consider severity, context, history and available evidence. To request review of a material decision, contact ${legalConfig.supportEmail} with the case reference.`]
    ],
    checklists: [
      ["Report categories", "Safety", "Payment", "No-show", "Incorrect listing", "Harassment", "Copyright/media"],
      ["Review process", "Triage", "Preserve evidence", "Request context", "Decide", "Notify", "Appeal", "Audit"]
    ]
  },
  centre: {
    eyebrow: "Centre terms",
    title: "Centre owner operating terms.",
    updated: "Effective 6 August 2026",
    summary: "These additional terms apply when an authorised centre owner claims or creates a venue, publishes court inventory and accepts bookings or payments.",
    actions: [["Centre payments", "centrePayments"], ["Claim centre", "centreClaimSearch"], ["Refunds", "refundPolicy"]],
    sections: [
      ["Authority and verification", "You confirm that you are authorised to represent the centre and provide truthful ownership evidence. A centre can accept Badminta checkout only after the claim or owner-created listing is verified, required details are complete and the payout account is ready. Verification may be reviewed or withdrawn."],
      ["Inventory and pricing", "Centres must keep court count, opening hours, court inventory, peak/off-peak pricing, booking rules and availability accurate before accepting payment."],
      ["Bookings and service", "Honour confirmed bookings, keep facilities reasonably safe and fit for the published activity, communicate material changes promptly and avoid double-booking. Staff access must be limited to authorised users and operational records must remain accurate."],
      ["Payments, fees and payouts", "Complete Stripe-hosted Connect onboarding and keep business and bank details current. Badminta may collect the customer payment, deduct disclosed platform and payment fees, refunds, disputes and permitted reserves, then instruct Stripe to pay the remaining amount. Payout is not guaranteed until funds clear and compliance checks pass."],
      ["Cancellations and complaints", "Publish a clear cancellation window, honour it consistently and provide evidence when cancelling or disputing a refund. Mandatory consumer remedies apply regardless of a centre rule. Respond promptly to safety, accessibility, incorrect-data and service complaints."],
      ["Listing and data rights", "Centre names, address, contact details, facilities, prices, images, video and official links must be accurate and lawfully usable. Do not claim another business, reuse protected media without permission or direct users to unrelated directories."]
    ],
    checklists: [
      ["Centre launch checklist", "Claim verified", "Courts added", "Hours added", "Pricing added", "Stripe Connect ready"],
      ["Admin evidence", "Owner phone", "Official email/domain", "ABN/registration", "Venue proof", "Audit entry"]
    ]
  },
  organiser: {
    eyebrow: "Organiser terms",
    title: "Social group organiser terms.",
    updated: "Effective 6 August 2026",
    summary: "These additional terms apply to organisers who create groups, recurring sessions or events, invite players and collect money through Badminta.",
    actions: [["Create group", "createGroup"], ["Payouts", "payouts"], ["Refunds", "refundPolicy"]],
    sections: [
      ["Session details", "Publish the actual venue, recurring day or event date, time, skill level, format, capacity, price, inclusions, accessibility or age restrictions, no-show rule and refund window before accepting payment."],
      ["Members and invites", "Invite emails and member tags should be used for attendance and messaging only. Organisers should not misuse player contact details."],
      ["Venue authority and capacity", "Confirm that the venue permits the activity and that booked court capacity supports the published number of players. A user-added venue pin does not prove ownership or guarantee availability."],
      ["Payments and payouts", "Complete Stripe-hosted payout onboarding before charging players. Paid sessions must use Badminta checkout so payment, capacity, attendance, refunds and disputes remain linked. Disclosed fees, refunds, reversals and reserves may be deducted before payout."],
      ["Attendance and changes", "Record joined, paid, waitlisted, substituted, attended, refunded and no-show states. Notify players promptly of venue, time or format changes and do not exceed safe capacity."],
      ["Safety and conduct", "Take reasonable steps to provide a fair and safe session, apply level and conduct rules consistently, protect member contact details, escalate emergencies and report serious incidents. Organisers remain responsible for the activity they host."]
    ],
    checklists: [
      ["Session launch checklist", "Venue selected", "Level clear", "Capacity set", "Price set", "Refund rules set"],
      ["Member ledger states", "Invited", "Registered", "Paid", "Waitlisted", "Refunded", "No-show"]
    ]
  },
  coach: {
    eyebrow: "Coach terms",
    title: "Coach operating terms.",
    updated: "Effective 6 August 2026",
    summary: "These additional terms apply to coaches who publish services, accept lesson requests, sell packages or receive payouts through Badminta.",
    actions: [["Coach profile", "coachProfile"], ["Payouts", "coachPayouts"], ["Community safety", "communityPolicy"]],
    sections: [
      ["Profile claims", "Coaches must accurately state experience, accreditation, location, rates, age groups, lesson types and availability."],
      ["Lesson bookings", "Confirm the location, player goals, date and time, format, total price, deposit, cancellation window, package or credit terms and equipment expectations before payment."],
      ["Payments and packages", "Complete Stripe-hosted payout onboarding before accepting Badminta checkout. Deposits, packages and credits must be recorded in the platform ledger. Disclosed fees, refunds, disputes and permitted reserves may be deducted before payout."],
      ["Media and testimonials", "Use coaching videos, student media, qualifications and testimonials only with appropriate rights and consent. Do not fabricate results or imply accreditation you do not hold."],
      ["Safety, minors and professional conduct", "Follow venue rules, work within your competence, maintain appropriate boundaries, obtain guardian involvement where required for minors, communicate professionally and respond to safety reports, cancellations and disputes."],
      ["Independent service provider", "Unless separately agreed in writing, a coach supplies their own coaching service and is not an employee of Badminta. The coach is responsible for applicable licences, screening, insurance, taxes and local legal obligations."]
    ],
    checklists: [
      ["Coach launch checklist", "Profile complete", "Availability set", "Rate set", "Media reviewed", "Stripe Connect ready"],
      ["Lesson evidence", "Request", "Payment", "Attendance", "Messages", "Recommendation"]
    ]
  },
  cookies: {
    eyebrow: "Privacy choices",
    title: "Cookie and tracking notice.",
    updated: "Effective 6 August 2026",
    summary: "How Badminta uses essential storage and optional analytics and advertising measurement.",
    actions: [["Privacy", "privacyPolicy"], ["Terms", "legalTerms"], ["Account", "account"]],
    sections: [
      ["Essential technology", "Essential browser storage and security technology support phone authentication, fraud prevention, session restoration, saved drafts, bookings and consent records. The requested service may not work without them."],
      ["Analytics", "With permission, Google Analytics 4 measures visits and product usage and Microsoft Clarity provides masked interaction diagnostics. These tools stay off until analytics consent is granted."],
      ["Advertising measurement", "With permission, Meta Pixel and TikTok Pixel measure campaign visits and signup actions. Badminta does not intentionally send profile text, phone numbers, emails, private messages or payment details in advertising events."],
      ["Your choices", "Accept all optional tools, choose categories or continue with essential technology only. Refusing optional tracking does not prevent account creation or marketplace use. Clearing site storage resets your choice and may remove local drafts."],
      ["Children and advertising", "Badminta does not use optional advertising measurement for a user known to be under 18. Provider technologies may process device, browser, IP address and event information under their own privacy terms."],
      ["Updates and contact", `The consent record includes the policy version and time chosen. Material changes prompt a new choice. Contact ${legalConfig.supportEmail} with privacy questions.`]
    ],
    checklists: [["Preference categories", "Essential", "Analytics", "Advertising"], ["Optional providers", "Google Analytics 4", "Microsoft Clarity", "Meta", "TikTok"]]
  },
  youth: {
    eyebrow: "Youth safety",
    title: "Children and young people safety notice.",
    updated: "Effective 6 August 2026",
    summary: "Badminta supports junior badminton with stronger privacy, supervision and marketplace safeguards.",
    actions: [["Community safety", "communityPolicy"], ["Privacy", "privacyPolicy"], ["Report concern", "help"]],
    sections: [
      ["Who may use Badminta", "A person under 18 may participate where they understand the service and local law permits it, with parent or guardian involvement where required. Children under 13 must not manage an independent public account; a parent or guardian must create and control it and provide legally required verifiable consent."],
      ["Adult-only roles", "Centre ownership or claiming, paid provider services, organiser or coach payouts, Stripe onboarding and binding commercial decisions require an adult or authorised adult representative."],
      ["Privacy by default", "Do not publish a child's exact home address, school, personal phone, private email, live precise location or unsupervised routine. Use a suburb or general playing area. Optional media, social links, discoverability and direct messaging require appropriate guardian oversight."],
      ["Images and video", "Uploaders must have permission from identifiable people and, where required, their parent or guardian. Intimate, sexualised, exploitative or humiliating content is prohibited and may be reported to authorities."],
      ["Sessions and coaching", "Parents and guardians must check supervision, pickup arrangements, venue rules, qualifications or screening where relevant, emergency contacts and local child-safety requirements. Verification and reviews are not a background check unless expressly stated."],
      ["Messaging and reporting", "Communication about junior participation must remain appropriate and preferably visible to a parent, guardian or organisation. Grooming, sexual contact, requests for secrecy, harassment and moving a child into secret private channels are prohibited."],
      ["Parent and guardian rights", `A verified parent or guardian may request access, correction, restriction or deletion of a child's information, subject to safety, legal and transaction-record requirements. Contact ${legalConfig.supportEmail}.`]
    ],
    checklists: [["Youth privacy defaults", "General location only", "No public contact details", "Guardian involvement"], ["Before junior play", "Confirm organiser", "Confirm supervision", "Review venue rules", "Emergency contact"]]
  }
};

const legalPolicyDetails = [
  ["Operator", legalConfig.entity],
  ["Support contact", legalConfig.supportEmail],
  ["Support URL", legalConfig.supportUrl],
  ["Country scope", legalConfig.launchCountry],
  ["Governing law", legalConfig.governingLaw],
  ["Dispute venue", legalConfig.disputeVenue],
  ["Policy version", legalConfig.policyUpdated]
];

function PrivacyConsentPanel({ navigate }) {
  const [consent, setConsent] = useState(readPrivacyConsent);
  const [customising, setCustomising] = useState(false);
  const [analytics, setAnalytics] = useState(Boolean(consent?.analytics));
  const [advertising, setAdvertising] = useState(Boolean(consent?.advertising));

  const saveConsent = (next) => {
    const record = { essential: true, analytics: Boolean(next.analytics), advertising: Boolean(next.advertising), version: legalPolicyVersion, updatedAt: new Date().toISOString() };
    try { window.localStorage.setItem(privacyConsentStorageKey, JSON.stringify(record)); } catch { /* Essential use remains available without storage. */ }
    setConsent(record);
    if (record.analytics || record.advertising) initialiseMarketingAnalytics();
  };

  if (consent) return null;
  return (
    <aside className="privacy-consent-panel" role="dialog" aria-modal="false" aria-label="Privacy choices">
      <div>
        <strong>Your privacy choices</strong>
        <p>Badminta uses essential storage for sign-in, security and saved progress. With your permission, analytics help improve the service and advertising tools measure campaigns. Optional tools stay off until you choose.</p>
        <button type="button" className="privacy-text-action" onClick={() => navigate("cookiePolicy")}>Read cookie and tracking notice</button>
      </div>
      {customising && <div className="privacy-consent-options">
        <label><span><b>Essential</b><small>Always on for login, safety and core functions.</small></span><input type="checkbox" checked disabled /></label>
        <label><span><b>Analytics</b><small>GA4 and Clarity performance insights.</small></span><input type="checkbox" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} /></label>
        <label><span><b>Advertising</b><small>Meta and TikTok campaign measurement.</small></span><input type="checkbox" checked={advertising} onChange={(event) => setAdvertising(event.target.checked)} /></label>
      </div>}
      <div className="privacy-consent-actions">
        <button type="button" onClick={() => saveConsent({ analytics: false, advertising: false })}>Essential only</button>
        {customising
          ? <button type="button" className="primary" onClick={() => saveConsent({ analytics, advertising })}>Save choices</button>
          : <button type="button" onClick={() => setCustomising(true)}>Choose</button>}
        <button type="button" className="primary" onClick={() => saveConsent({ analytics: true, advertising: true })}>Accept all</button>
      </div>
    </aside>
  );
}

function PolicyPage({ type, navigate, accountProfile = defaultAccountProfile, saveAccountProfile }) {
  const policy = policyPages[type] || policyPages.terms;
  const sections = [...policy.sections, ...(policyDetailSections[type] || [])];
  const contactSupport = () => openPublicMessageThread({
    accountProfile,
    saveAccountProfile,
    navigate,
    roleId: "player",
    subject: `${policy.title} support`,
    threadType: "Policy support",
    text: `Support conversation opened from ${policy.title}. Attach policy section, booking, payment or profile context before escalating.`,
    status: "Open",
    meta: [policy.eyebrow, policy.updated, accountProfile.email],
    context: [["Policy", policy.title], ["Account", accountProfile.fullName || "Pending"], ["Email", accountProfile.email || "Pending"]],
    actions: [["Help", "help"], ["Messages", "messages"], ["Account", "account"]]
  });
  return (
    <section className="page policy-page">
      <div className="policy-hero">
        <div>
          <p>{policy.eyebrow}</p>
          <h1>{policy.title}</h1>
          <span>{policy.summary}</span>
          <small>{policy.updated}</small>
        </div>
        <div className="policy-action-stack">
          {policy.actions.map(([label, target]) => (
            <button type="button" key={label} onClick={() => navigate(target)}>
              <ChevronRight size={16} /> {label}
            </button>
          ))}
          <button type="button" onClick={contactSupport}>
            <MessageCircle size={16} /> Contact support
          </button>
        </div>
      </div>
      <div className="policy-layout">
        <main className="policy-sections">
          {sections.map(([title, text]) => (
            <article key={title}>
              <ShieldCheck size={20} />
              <div>
                <h2>{title}</h2>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </main>
        <aside className="policy-checklists">
          <section>
            <strong>Operator details</strong>
            {legalPolicyDetails.map(([label, value]) => (
              <span key={label}><Check size={14} /> {label}: {value}</span>
            ))}
          </section>
          {policy.checklists.map(([title, ...items]) => (
            <section key={title}>
              <strong>{title}</strong>
              {items.map((item) => <span key={item}><Check size={14} /> {item}</span>)}
            </section>
          ))}
          <section>
            <strong>Policy contact</strong>
            <span><CircleHelp size={14} /> Questions, complaints and review requests: {legalConfig.supportEmail}</span>
          </section>
        </aside>
      </div>
    </section>
  );
}

function DashboardPage({ eyebrow, title, image, metrics, primaryAction, secondaryAction, rows, empty, emptyLabel = "Dashboard media appears after setup" }) {
  return (
    <section className="page dashboard-page">
      <div className="dashboard-hero">
        <div>
          <p>{eyebrow}</p>
          <h1>{title}</h1>
          <div>
            <button type="button" onClick={primaryAction[1]}>{primaryAction[0]}</button>
            <button type="button" onClick={secondaryAction[1]}>{secondaryAction[0]}</button>
          </div>
        </div>
        {image ? <img src={image} alt="" /> : <NoImagePlaceholder label={emptyLabel} className="hero-placeholder" />}
      </div>
      <div className="metric-grid">
        {metrics.map(([label, value, trend]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <em>{trend}</em>
          </article>
        ))}
      </div>
      <section className="ops-table">
        <h2>Operations</h2>
        {rows.length ? rows.map((row) => <OpsRow key={row.join("-")} row={row} />) : (
          <div className="ops-empty">
            <strong>{empty?.title || "No operations yet"}</strong>
            <span>{empty?.text || "Real activity will appear here after it is created on Badminta."}</span>
            {empty?.action && <button type="button" onClick={empty.action[1]}>{empty.action[0]}</button>}
          </div>
        )}
      </section>
    </section>
  );
}

function SplitHero({ eyebrow, title, text, image, emptyLabel = "Media appears after real activity is added" }) {
  return (
    <section className="split-hero">
      <div>
        <p>{eyebrow}</p>
        <h1>{title}</h1>
        <span>{text}</span>
      </div>
      {image ? <img src={image} alt="" loading="lazy" decoding="async" /> : <NoImagePlaceholder label={emptyLabel} className="hero-placeholder" />}
    </section>
  );
}

function OpsRow({ row }) {
  return (
    <div className="ops-row">
      {row.map((cell, index) => index === 0 ? <strong key={cell}>{cell}</strong> : <span key={`${cell}-${index}`}>{cell}</span>)}
    </div>
  );
}

function AvatarStack({ images }) {
  return (
    <div className="avatar-stack">
      {images.map((image, index) => <img src={image} alt="" key={`${image}-${index}`} />)}
    </div>
  );
}

function AccountMenu({ page, navigate, close, auth, accountProfile, unreadMessageCount = 0, unreadNotificationCount = 0 }) {
  const isSignedIn = Boolean(auth.user);
  const accountName = accountProfile.fullName || auth.user?.displayName || "Your account";
  const accountLine = isSignedIn ? auth.user?.phoneNumber || "Phone verified" : "Sign in to create profiles and tools";
  const accountAvatar = getAccountAvatarUrl(accountProfile);
  const menuSections = [
    {
      label: "Account",
      items: isSignedIn
        ? [
          ["account", "My account", UserCircle],
          ["following", "Following", UserPlus],
          ["saved", "Saved", Heart],
          ["notifications", "Notifications", Bell, unreadNotificationCount],
          ["messages", "Messages", MessageCircle, unreadMessageCount]
        ]
        : [
          ["signup", "Sign up", UserCircle],
          ["login", "Log in", LockKeyhole]
        ]
    },
    ...(isSignedIn ? [{
      label: "Manage",
      items: [
        ["playerDashboard", "Player", UserCircle],
        ["organiser", "Social organiser", UsersRound],
        ["centreDashboard", "Centre owner", Building2],
        ["coachDashboard", "Coach", Trophy]
      ]
    },
    {
      label: "Events",
      items: [
        ["eventManager", "Manage events", CalendarDays]
      ]
    }] : []),
    ...(isSignedIn && hasSuperAdminAllowlistAccess(auth.user) ? [{
      label: "Administrator",
      items: [
        ["administrator", "Super admin", ShieldCheck]
      ]
    }] : []),
    {
      label: "Solutions",
      items: [
        ["solutionIndex", "Overview", Sparkles],
        ["solutionCentres", "Centres", Building2],
        ["solutionOrganisers", "Social groups", UsersRound],
        ["solutionCoaches", "Coaches", Trophy],
        ["solutionPlayers", "Players", UserCircle],
        ["solutionEvents", "Events", CalendarDays]
      ]
    },
    {
      label: "Discover",
      items: [
        ["blogIndex", "Badminton guides", BookOpen]
      ]
    },
    {
      label: "Support",
      items: isSignedIn ? [
        ["help", "Help centre", ShieldCheck],
        ["refundPolicy", "Refunds", WalletCards],
        ["privacyPolicy", "Privacy", LockKeyhole],
        ["accountDeletion", "Delete account", ShieldAlert],
        ["trust", "Trust and safety", BadgeCheck]
      ] : [
        ["help", "Help centre", ShieldCheck]
      ]
    }
  ];

  return (
    <div className="menu-overlay">
      <button type="button" className="menu-backdrop" onClick={close} aria-label="Close menu" />
      <aside className="page-menu">
        <div className="menu-head">
          <div>
            <strong>Menu</strong>
            <span>Account, roles and platform tools</span>
          </div>
          <button type="button" onClick={close}><X size={20} /></button>
        </div>
        <div className="menu-account-card">
          {accountAvatar ? <img src={accountAvatar} alt="" /> : <NoImagePlaceholder label="No profile yet" />}
          <div>
            <strong>{accountName}</strong>
            <span>{accountLine}</span>
          </div>
        </div>
        {menuSections.map((group) => (
          <section key={group.label}>
            <h3>{group.label}</h3>
            {group.items.map(([key, label, Icon, badge]) => (
              <button type="button" key={key} className={page === key ? "active" : ""} onClick={() => navigate(key)}>
                <Icon size={18} />
                <span>{label}</span>
                {badge > 0 && <b className="menu-item-badge" aria-label={`${badge} unread ${key === "messages" ? "messages" : "notifications"}`}>{Math.min(99, badge)}</b>}
              </button>
            ))}
          </section>
        ))}
        {isSignedIn && (
          <section>
            <button type="button" onClick={async () => { await auth.logOut(); close(); navigate("login"); }}>
              <LockKeyhole size={18} />
              <span>Log out</span>
            </button>
          </section>
        )}
      </aside>
    </div>
  );
}

function MobileNav({ page, navigate, openMenu, unreadMessageCount = 0 }) {
  const items = [
    ["home", MapPinned, "Explore"],
    ["results", Search, "Search"],
    ["groupsIndex", UsersRound, "Groups"],
    ["events", CalendarDays, "Events"],
    ["menu", MoreHorizontal, "Menu"]
  ];
  return (
    <nav className="mobile-nav" aria-label="Mobile navigation">
      {items.map(([key, Icon, label]) => (
        <button
          type="button"
          key={key}
          className={page === key ? "active" : ""}
          onClick={() => key === "menu" ? openMenu() : navigate(key)}
        >
          <Icon size={19} />
          <span>{label}</span>
          {key === "menu" && unreadMessageCount > 0 && <b className="mobile-nav-badge" aria-label={`${unreadMessageCount} unread messages`}>{Math.min(99, unreadMessageCount)}</b>}
        </button>
      ))}
    </nav>
  );
}

const hubResultLabels = {
  centres: "Badminton centres",
  groups: "Social badminton groups",
  players: "Badminton players",
  coaches: "Badminton coaches",
  events: "Badminton events"
};

const cleanMetaText = (value = "", fallback = "") => {
  const text = String(value || fallback || "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 156 ? `${text.slice(0, 153).trim()}...` : text;
};

const absoluteSiteUrl = (path = "/") => {
  if (/^https?:\/\//i.test(path)) return path;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://badminta.com";
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
};

const firstMetaImage = (...sources) => {
  for (const source of sources.flat(Infinity)) {
    if (typeof source === "string" && source.trim()) return absoluteSiteUrl(source.trim());
  }
  return absoluteSiteUrl(brandLogo);
};

const getSeoAreaSeoData = (context = {}) => {
  const city = context.city || "Melbourne";
  const location = ["country", "city", "suburb"].includes(context.searchCriteria?.location?.type)
    ? context.searchCriteria.location
    : locationOptions.find((option) => option.type === "city" && option.city === city) || context.searchCriteria?.location || getDefaultSearchLocation();
  const activeCity = location?.type === "country" ? "" : location?.city || city;
  const areaLabel = getSeoAreaLocationLabel(location, activeCity);
  const intentId = normaliseSeoAreaIntent(context.seoAreaIntent);
  const intent = seoAreaIntents[intentId] || seoAreaIntents.overview;
  const accountCards = buildAccountProfileCards(context.accountProfile || loadStoredAccountProfile(), activeCity);
  const areaSearchCriteria = { ...(context.searchCriteria || {}), location };
  const areaVenues = dedupeSeoVenues(venues.filter((venue) => venueMatchesSeoArea(venue, location)));
  const areaGroups = groups.filter((group) => recordMatchesSeoArea(group, location, "group"));
  const areaPlayers = players.filter((player) => recordMatchesSeoArea(player, location, "player"));
  const areaCoaches = trainers.filter((coach) => recordMatchesSeoArea(coach, location, "coach"));
  const areaEvents = getPublicEventsForLocation(context.accountProfile || loadStoredAccountProfile(), activeCity, areaSearchCriteria).filter((event) => recordMatchesSeoArea(event, location, "event"));
  const centreCards = accountCards.centres
    .filter((card) => profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaVenues.some((venue) => directoryRecordMatchesAccountCard(card, venue)));
  const groupCards = accountCards.groups
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "group") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaGroups.some((group) => directoryRecordMatchesAccountCard(card, group)));
  const playerCards = accountCards.players
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "player") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaPlayers.some((player) => directoryRecordMatchesAccountCard(card, player)));
  const coachCards = accountCards.coaches
    .filter((card) => recordMatchesSeoArea(card.profile || card, location, "coach") || profileCardMatchesLocation(card, activeCity, areaSearchCriteria))
    .filter((card) => !areaCoaches.some((coach) => directoryRecordMatchesAccountCard(card, coach)));
  const counts = {
    centres: areaVenues.length + centreCards.length,
    groups: areaGroups.length + groupCards.length,
    players: areaPlayers.length + playerCards.length,
    coaches: areaCoaches.length + coachCards.length,
    events: areaEvents.length
  };
  return {
    location,
    activeCity,
    areaLabel,
    intentId,
    intent,
    counts,
    areaVenues,
    areaGroups,
    groupCards,
    areaPlayers,
    playerCards,
    areaCoaches,
    coachCards,
    areaEvents
  };
};

const buildSeoAreaSchema = (context = {}, meta = {}) => {
  const seoData = getSeoAreaSeoData(context);
  const pageUrl = meta.url || absoluteSiteUrl(buildRoutePath("seoArea", context));
  const title = meta.title || seoData.intent.title(seoData.areaLabel);
  const description = meta.description || `Find badminton in ${seoData.areaLabel}.`;
  const addressCountry = getLocationCountryName(seoData.location, seoData.activeCity);
  const countryPath = `/badminton/${countrySlug(seoData.location?.countryCode || seoData.location?.country, seoData.activeCity)}`;
  const faqItems = [
    [`Where can I play badminton in ${seoData.areaLabel}?`, `Compare listed badminton centres, social groups, players, coaches and events around ${seoData.areaLabel}.`],
    [`How do I join a social badminton group in ${seoData.areaLabel}?`, "Open a group profile, review the venue, date, level and price, then join when public joining is enabled."],
    [`Can a centre add court booking details for ${seoData.areaLabel}?`, "A centre owner can add or claim a centre and publish opening hours, court details, pricing, booking rules and events."],
    [`Can players and coaches create public profiles in ${seoData.areaLabel}?`, "Players and coaches can publish profiles with skill level, availability, media, reviews and links."]
  ];
  const recordsByIntent = {
    centres: seoData.areaVenues.slice(0, 12).map((venue) => ({
      "@type": "SportsActivityLocation",
      name: venue.name,
      address: getVenueAddressLabel(venue),
      url: absoluteSiteUrl(buildRoutePath("venue", { selectedVenue: venue, city: venue.city || seoData.activeCity })),
      ...(getVenueExactPoint(venue) ? { geo: { "@type": "GeoCoordinates", latitude: getVenueExactPoint(venue).lat, longitude: getVenueExactPoint(venue).lon } } : {})
    })),
    groups: [...seoData.groupCards.map((card) => ({ "@type": "SportsTeam", name: card.title, url: absoluteSiteUrl(routeHref(card.target, card.payload || {})) })), ...seoData.areaGroups.slice(0, 10).map((group) => ({ "@type": "SportsTeam", name: getGroupTitle(group), url: absoluteSiteUrl(buildRoutePath("group", { selectedGroup: group, city: group.city || seoData.activeCity })) }))],
    players: [...seoData.playerCards.map((card) => ({ "@type": "Person", name: card.title, url: absoluteSiteUrl(routeHref(card.target, card.payload || {})) })), ...seoData.areaPlayers.slice(0, 10).map((player) => ({ "@type": "Person", name: player.name, url: absoluteSiteUrl(buildRoutePath("player", { selectedPlayer: player, city: player.city || seoData.activeCity })) }))],
    coaches: [...seoData.coachCards.map((card) => ({ "@type": "Person", name: card.title, jobTitle: "Badminton coach", url: absoluteSiteUrl(routeHref(card.target, card.payload || {})) })), ...seoData.areaCoaches.slice(0, 10).map((coach) => ({ "@type": "Person", name: coach.name, jobTitle: "Badminton coach", url: absoluteSiteUrl(buildRoutePath("coach", { selectedCoach: coach, city: coach.city || seoData.activeCity })) }))],
    events: seoData.areaEvents.slice(0, 10).map((event) => ({ "@type": "SportsEvent", name: event.title, startDate: event.date || undefined, eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode", url: absoluteSiteUrl(buildRoutePath("event", { selectedEvent: event, city: getEventCity(event) })) }))
  };
  const selectedRecords = seoData.intentId === "overview"
    ? Object.values(recordsByIntent).flat().slice(0, 20)
    : recordsByIntent[seoData.intent.resultType] || [];
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": absoluteSiteUrl("/#website"),
        name: "Badminta",
        url: absoluteSiteUrl("/"),
        potentialAction: {
          "@type": "SearchAction",
          target: absoluteSiteUrl(`${countryPath}/${seoData.activeCity ? `${citySlug(seoData.activeCity)}/` : ""}{search_term_string}`),
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: title,
        description,
        isPartOf: { "@id": absoluteSiteUrl("/#website") },
        about: { "@type": "Thing", name: seoData.intent.keyword },
        spatialCoverage: {
          "@type": "Place",
          name: `${seoData.areaLabel}, ${seoData.activeCity}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: seoData.areaLabel,
            ...(seoData.activeCity ? { addressRegion: seoData.activeCity } : {}),
            ...(addressCountry ? { addressCountry } : {})
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: absoluteSiteUrl("/") },
          { "@type": "ListItem", position: 2, name: "Badminton worldwide", item: absoluteSiteUrl("/") },
          { "@type": "ListItem", position: 3, name: addressCountry || "Country", item: absoluteSiteUrl(countryPath) },
          { "@type": "ListItem", position: 4, name: seoData.areaLabel, item: absoluteSiteUrl(seoAreaLocationPath(seoData.location, "overview")) },
          { "@type": "ListItem", position: 5, name: seoData.intent.label, item: pageUrl }
        ]
      },
      {
        "@type": "ItemList",
        name: `${seoData.intent.keyword} in ${seoData.areaLabel}`,
        itemListElement: selectedRecords.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item
        }))
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer }
        }))
      }
    ]
  };
};

const buildSolutionSeoSchema = (page) => {
  const solution = solutionPageByRoute[page];
  const subscriptionRole = profileSubscriptionRoleBySolutionPage[solution?.page] || "";
  const subscriptionPlan = subscriptionRole ? getProfileSubscriptionPlan(subscriptionRole) : null;
  const faqItems = solution
    ? [...solution.faq, ...(subscriptionRole === "player" ? [] : profileSubscriptionFaq(subscriptionRole))]
    : solutionOverviewFaq;
  const pagePath = solution ? solution.path : "/solutions";
  const title = solution ? `${solution.heroTitle} | Badminta` : "Badminta solutions for badminton centres, groups, coaches, players and events";
  const description = solution
    ? cleanMetaText(`${solution.heroText} ${solution.seoIntro}`)
    : "Badminta is a worldwide badminton marketplace and operating platform for centres, groups, coaches, players and events.";
  const isPlayerLanding = solution?.page === "solutionPlayers";
  const graph = [
    {
      "@type": "WebSite",
      "@id": absoluteSiteUrl("/#website"),
      name: "Badminta",
      url: absoluteSiteUrl("/")
    },
    {
      "@type": "WebPage",
      "@id": `${absoluteSiteUrl(pagePath)}#webpage`,
      url: absoluteSiteUrl(pagePath),
      name: title,
      description,
      inLanguage: "en",
      dateModified: "2026-07-24",
      isPartOf: { "@id": absoluteSiteUrl("/#website") },
      primaryImageOfPage: isPlayerLanding ? { "@id": `${absoluteSiteUrl(pagePath)}#primaryimage` } : undefined,
      about: {
        "@type": "SoftwareApplication",
        name: "Badminta",
        applicationCategory: "SportsApplication",
        operatingSystem: "Web"
      }
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: absoluteSiteUrl("/") },
        { "@type": "ListItem", position: 2, name: "Solutions", item: absoluteSiteUrl("/solutions") },
        ...(solution ? [{ "@type": "ListItem", position: 3, name: solution.navLabel, item: absoluteSiteUrl(pagePath) }] : [])
      ]
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer }
      }))
    }
  ];
  if (isPlayerLanding) {
    graph.push(
      {
        "@type": "ImageObject",
        "@id": `${absoluteSiteUrl(pagePath)}#primaryimage`,
        url: absoluteSiteUrl("/media/player-landing-hero.jpg"),
        contentUrl: absoluteSiteUrl("/media/player-landing-hero.jpg"),
        caption: "Badminton player celebrating after an indoor match"
      },
      {
        "@type": "VideoObject",
        "@id": `${absoluteSiteUrl(pagePath)}#video`,
        name: "Badminta player profile walkthrough",
        description: "A short walkthrough showing how a Badminta player profile helps people understand level, location, availability and playing preferences.",
        thumbnailUrl: [absoluteSiteUrl("/media/player-landing-hero.jpg")],
        uploadDate: "2026-06-26",
        duration: "PT15S",
        contentUrl: absoluteSiteUrl(playerLandingVideo.src)
      }
    );
  }
  if (subscriptionPlan) {
    graph.push({
      "@type": "Service",
      "@id": `${absoluteSiteUrl(pagePath)}#profile-subscription`,
      name: `${subscriptionPlan.roleLabel} profile plans`,
      description: `${subscriptionPlan.freeName} is free. ${subscriptionPlan.proName} adds role-specific profile and operating tools.`,
      provider: {
        "@type": "Organization",
        name: "Badminta",
        url: absoluteSiteUrl("/")
      },
      areaServed: "Worldwide",
      offers: [
        {
          "@type": "Offer",
          name: subscriptionPlan.freeName,
          price: "0.00",
          priceCurrency: subscriptionPlan.currency.toUpperCase(),
          url: absoluteSiteUrl(pagePath),
          availability: "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          name: `${subscriptionPlan.proName} monthly`,
          price: (subscriptionPlan.monthlyCents / 100).toFixed(2),
          priceCurrency: subscriptionPlan.currency.toUpperCase(),
          url: absoluteSiteUrl(pagePath),
          availability: "https://schema.org/InStock",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: (subscriptionPlan.monthlyCents / 100).toFixed(2),
            priceCurrency: subscriptionPlan.currency.toUpperCase(),
            billingDuration: "P1M"
          }
        },
        {
          "@type": "Offer",
          name: `${subscriptionPlan.proName} annual`,
          price: (subscriptionPlan.annualCents / 100).toFixed(2),
          priceCurrency: subscriptionPlan.currency.toUpperCase(),
          url: absoluteSiteUrl(pagePath),
          availability: "https://schema.org/InStock",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: (subscriptionPlan.annualCents / 100).toFixed(2),
            priceCurrency: subscriptionPlan.currency.toUpperCase(),
            billingDuration: "P1Y"
          }
        }
      ]
    });
  }
  return {
    "@context": "https://schema.org",
    "@graph": graph
  };
};

const buildHomeLandingSeoSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": absoluteSiteUrl("/#organization"),
      name: "Badminta",
      url: absoluteSiteUrl("/"),
      logo: {
        "@type": "ImageObject",
        url: absoluteSiteUrl("/badminta-brand-logo.png")
      }
    },
    {
      "@type": "WebSite",
      "@id": absoluteSiteUrl("/#website"),
      name: "Badminta",
      url: absoluteSiteUrl("/"),
      publisher: { "@id": absoluteSiteUrl("/#organization") },
      potentialAction: {
        "@type": "SearchAction",
        target: absoluteSiteUrl("/badminton/{search_term_string}"),
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "WebPage",
      "@id": `${absoluteSiteUrl("/")}#webpage`,
      url: absoluteSiteUrl("/"),
      name: "Badminta | Worldwide badminton marketplace",
      description: "Find badminton centres, social groups, players, coaches and events worldwide, then manage bookings, memberships, sessions, payments and trust through Badminta.",
      isPartOf: { "@id": absoluteSiteUrl("/#website") },
      about: { "@id": absoluteSiteUrl("/#organization") }
    },
    {
      "@type": "FAQPage",
      mainEntity: solutionOverviewFaq.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer }
      }))
    }
  ]
});

const privateAppSeoPages = new Set([
  "login",
  "signup",
  "account",
  "accountDeletion",
  "playerDashboard",
  "organiser",
  "centreDashboard",
  "coachDashboard",
  "messages",
  "notifications",
  "saved",
  "following",
  "eventManager",
  "joinedEvents",
  "manageEvent",
  "createGroup",
  "groupSessions",
  "createGroupSession",
  "editGroupSession",
  "createEvent",
  "profileSetup",
  "coachProfile",
  "claimCentre",
  "centreClaimSearch",
  "courtManager",
  "centrePayments",
  "payouts",
  "coachPayouts",
  "members",
  "onboarding"
]);

const privatePageSeoCopy = {
  notifications: ["Your Badminta notifications", "Review account, message, booking, session, membership, coaching, payment and trust updates from across your Badminta roles."],
  following: ["People, groups, coaches and centres you follow", "Return to the badminton players, social groups, coaches and centres you follow on Badminta."],
  onboarding: ["Choose and publish your Badminta profiles", "Select the Badminta roles you use and finish the minimum details needed to publish one useful profile at a time."],
  profileSetup: ["Create or edit your badminton player profile", "Manage the player details that appear on your public Badminta profile, including level, location, availability and media."],
  coachProfile: ["Create or edit your badminton coach profile", "Manage coaching services, locations, pricing, availability and public profile details on Badminta."],
  createGroup: ["Create or edit your social badminton group", "Manage your group identity, regular playing schedule, venue and joining information on Badminta."],
  groupSessions: ["Manage social badminton sessions", "Publish dated group sessions, monitor capacity and open joining and payment for players on Badminta."],
  createGroupSession: ["Create a social badminton session", "Publish a dated badminton session with venue, time, capacity, price and joining rules."],
  editGroupSession: ["Edit a social badminton session", "Update a dated badminton session, including venue, time, capacity, price and publication status."],
  payouts: ["Organiser payouts and Stripe Connect", "Review social-session revenue, payout readiness and Stripe Connect status for your Badminta organiser profile."],
  members: ["Manage social group members", "Review group members, invites, session participation and payment context for your Badminta social group."],
  coachPayouts: ["Coach payouts and Stripe Connect", "Review coaching revenue, payout readiness and Stripe Connect status for your Badminta coach profile."],
  claimCentre: ["Add or edit a badminton centre", "Create or update a centre-owned Badminta listing with location, courts, opening hours, pricing and booking information."],
  centreClaimSearch: ["Search and claim a badminton centre", "Find an existing centre listing and submit a clear ownership claim for Badminta review."],
  courtManager: ["Manage badminton courts and availability", "Manage court inventory, availability, off-peak fees and booking rules for your Badminta centre."],
  centrePayments: ["Manage centre payments and memberships", "Manage court revenue, memberships, credit packs, payment status and Stripe Connect for your Badminta centre."],
  createEvent: ["Create a badminton event", "Create a public badminton event with venue, schedule, capacity, ticketing and organiser details."],
  joinedEvents: ["Your joined badminton events", "Review the badminton events you joined, including registration and payment status."],
  manageEvent: ["Manage badminton event registrations", "Manage event publication, attendees, registration status, payments and event operations on Badminta."]
};

const buildSeoMeta = (page, context = {}) => {
  const city = context.city || "Melbourne";
  const searchCriteria = context.searchCriteria || { location: { label: city }, tags: [] };
  const activeLocation = locationTitle(searchCriteria, city);
  const filterKey = context.activeFilter || pageFallbackFilter[page] || "nearby";
  const filterCopy = filterContent[filterKey] || filterContent.nearby;
  const resultType = normaliseHubResultType(context.activeResultType) || getPreferredHubResultType(filterKey, searchCriteria.tags);
  const resultLabel = hubResultLabels[resultType] || "Badminton";
  const venue = context.selectedVenue;
  const group = context.selectedGroup;
  const player = context.selectedPlayer;
  const coach = context.selectedCoach;
  const event = context.selectedEvent;
  const blogPost = context.blogPost;
  const roleDashboardRoute = {
    playerDashboard: "player",
    organiser: "organiser",
    centreDashboard: "centre",
    coachDashboard: "coach"
  }[page];
  let title = `Badminta | Badminton courts, groups and players in ${activeLocation}`;
  let description = `Find badminton centres, social groups, court hire details, players, coaches and events around ${activeLocation}.`;
  let image = absoluteSiteUrl(brandLogo);
  let type = "website";
  let schema = null;

  if (page === "home") {
    title = "Badminta | Worldwide badminton marketplace for centres, groups, players, coaches and events";
    description = "Find badminton centres, social groups, players, coaches and events worldwide, then use Badminta profiles and tools for bookings, sessions, memberships, payments and trust.";
    image = absoluteSiteUrl(getLandingMedia("home").url);
    schema = buildHomeLandingSeoSchema();
  } else if (page === "solutionIndex") {
    title = "Badminta solutions for badminton centres, groups, coaches, players and events";
    description = "Badminta is a worldwide badminton marketplace and operating platform for centre bookings, social group sessions, coach profiles, player discovery, events, payments and trust.";
    schema = buildSolutionSeoSchema(page);
  } else if (solutionPageByRoute[page]) {
    const solution = solutionPageByRoute[page];
    title = `${solution.heroTitle} | Badminta`;
    description = cleanMetaText(`${solution.heroText} ${solution.seoIntro}`);
    image = absoluteSiteUrl(getLandingMedia(Object.keys(solutionPages).find((key) => solutionPages[key].page === page) || "overview").url);
    schema = buildSolutionSeoSchema(page);
  } else if (page === "blogIndex") {
    title = "Badminton guides for players, groups and centres | Badminta blog";
    description = "Read practical badminton guides about finding players, joining social groups, booking courts, choosing centres, coaching and running events.";
    image = absoluteSiteUrl(blogPosts[0]?.image || brandLogo);
    schema = {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Badminta journal",
      url: absoluteSiteUrl("/blog"),
      description,
      publisher: { "@type": "Organization", name: "Badminta", url: absoluteSiteUrl("/") },
      blogPost: blogPosts.map((post) => ({ "@type": "BlogPosting", headline: post.title, url: absoluteSiteUrl(`/blog/${post.slug}`), datePublished: post.publishedAt }))
    };
  } else if (page === "blogArticle" && blogPost) {
    title = `${blogPost.title} | Badminta`;
    description = blogPost.description;
    image = absoluteSiteUrl(blogPost.image);
    type = "article";
    const articleUrl = absoluteSiteUrl(`/blog/${blogPost.slug}`);
    const articleImage = absoluteSiteUrl(blogPost.image);
    schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": `${articleUrl}#article`,
          headline: blogPost.title,
          description: blogPost.description,
          image: { "@type": "ImageObject", url: articleImage, width: 1800, height: 1125, caption: blogPost.imageAlt },
          datePublished: blogPost.publishedAt,
          dateModified: blogPost.updatedAt,
          mainEntityOfPage: articleUrl,
          author: { "@type": "Organization", name: "Badminta editorial team", url: absoluteSiteUrl("/blog") },
          publisher: { "@type": "Organization", name: "Badminta", url: absoluteSiteUrl("/"), logo: { "@type": "ImageObject", url: absoluteSiteUrl(brandLogo) } },
          articleSection: blogPost.category,
          keywords: blogPost.keywords.join(", "),
          about: blogPost.keywords.map((name) => ({ "@type": "Thing", name })),
          isAccessibleForFree: true
        },
        {
          "@type": "HowTo",
          "@id": `${articleUrl}#howto`,
          name: blogPost.title,
          description: blogPost.answer,
          image: articleImage,
          step: blogPost.sections.map((section, index) => ({ "@type": "HowToStep", position: index + 1, name: section.heading, text: section.paragraphs.join(" "), url: `${articleUrl}#section-${index + 1}` }))
        },
        {
          "@type": "FAQPage",
          "@id": `${articleUrl}#faq`,
          mainEntity: blogPost.faq.map(([question, answer]) => ({ "@type": "Question", name: question, acceptedAnswer: { "@type": "Answer", text: answer } }))
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: absoluteSiteUrl("/") },
            { "@type": "ListItem", position: 2, name: "Blog", item: absoluteSiteUrl("/blog") },
            { "@type": "ListItem", position: 3, name: blogPost.title, item: articleUrl }
          ]
        }
      ]
    };
  } else if (page === "filterHub") {
    title = `${resultLabel} for ${filterCopy.eyebrow || "badminton"} in ${activeLocation} | Badminta`;
    description = cleanMetaText(`${filterCopy.marketplaceText || "Browse badminton options."} Showing ${resultLabel.toLowerCase()} around ${activeLocation}.`);
  } else if (page === "seoArea") {
    const seoData = getSeoAreaSeoData(context);
    const countryName = getLocationCountryName(seoData.location, seoData.activeCity);
    const areaContext = [
      seoData.areaLabel,
      seoData.activeCity && compactLocationText(seoData.activeCity) !== compactLocationText(seoData.areaLabel) ? seoData.activeCity : "",
      countryName && compactLocationText(countryName) !== compactLocationText(seoData.areaLabel) ? countryName : ""
    ].filter(Boolean).join(", ");
    const countText = [
      seoData.counts.centres ? `${seoData.counts.centres} centre${seoData.counts.centres === 1 ? "" : "s"}` : "",
      seoData.counts.groups ? `${seoData.counts.groups} social group${seoData.counts.groups === 1 ? "" : "s"}` : "",
      seoData.counts.players ? `${seoData.counts.players} player${seoData.counts.players === 1 ? "" : "s"}` : "",
      seoData.counts.coaches ? `${seoData.counts.coaches} coach${seoData.counts.coaches === 1 ? "" : "es"}` : "",
      seoData.counts.events ? `${seoData.counts.events} event${seoData.counts.events === 1 ? "" : "s"}` : ""
    ].filter(Boolean).join(", ");
    title = `${seoData.intent.title(seoData.areaLabel)} | Badminta`;
    description = cleanMetaText(`Find ${seoData.intent.keyword} in ${areaContext || seoData.areaLabel}. Compare courts, social sessions, players, coaches and events${countText ? ` including ${countText}` : ""}.`);
  } else if (page === "results") {
    title = `Badminton centres in ${activeLocation} | Badminta`;
    description = `Compare badminton centre listings, map locations and court-hire details around ${activeLocation}.`;
  } else if (page === "groupsIndex") {
    title = `Social badminton groups in ${activeLocation} | Badminta`;
    description = `Find organised badminton groups where players join sessions, share costs and play at real centres around ${activeLocation}.`;
  } else if (page === "playersIndex") {
    title = filterKey === "coaching" ? `Badminton coaches in ${activeLocation} | Badminta` : `Badminton players in ${activeLocation} | Badminta`;
    description = filterKey === "coaching"
      ? `Find badminton coaches, training profiles and coaching sessions around ${activeLocation}.`
      : `Find badminton player profiles, skill levels, media and availability around ${activeLocation}.`;
  } else if (page === "courtAvailability") {
    title = `Court hire details in ${activeLocation} | Badminta`;
    description = `Compare court hire information, pricing where listed and centre details for badminton venues around ${activeLocation}.`;
  } else if (page === "events") {
    title = `Badminton events in ${activeLocation} | Badminta`;
    description = `Find badminton events, tournaments, clinics and public play dates around ${activeLocation}.`;
  } else if (page === "venue" && venue) {
    title = `${venue.name} badminton centre in ${venue.suburb || venue.city || activeLocation} | Badminta`;
    description = cleanMetaText(getVenueOverview(venue), `${venue.name} listing with address, court, contact and booking-readiness details on Badminta.`);
    image = firstMetaImage(getVenueImages(venue));
  } else if (page === "centreUnavailable") {
    title = venue?.name && venue.name !== "Centre listing unavailable"
      ? `${venue.name} listing status | Badminta`
      : "Centre listing unavailable | Badminta";
    description = "This record is not currently confirmed as a public court-hire centre on Badminta. Browse current facilities or add the physical venue.";
  } else if (page === "group" && group) {
    title = `${getGroupTitle(group)} social badminton group | Badminta`;
    description = cleanMetaText([getGroupVenueLabel(group), getGroupWhenLabel(group), group.level || group.skillBand, getGroupPriceLabel(group)].filter(Boolean).join(" - "));
    image = firstMetaImage(group.image, group.logoUrl, group.coverUrl);
  } else if (page === "player" && player) {
    title = `${player.name} badminton player profile | Badminta`;
    description = cleanMetaText([player.band, player.city, player.style, player.plays].filter(Boolean).join(" - "));
    image = firstMetaImage(player.image);
    type = "profile";
  } else if (page === "coach" && coach) {
    title = `${coach.name} badminton coach profile | Badminta`;
    description = cleanMetaText([coach.band, coach.city, coach.style, coach.headline].filter(Boolean).join(" - "));
    image = firstMetaImage(coach.image);
    type = "profile";
  } else if ((page === "event" || page === "eventJoin") && event) {
    title = `${event.title} badminton event | Badminta`;
    description = cleanMetaText([getEventVenueLabel(event), formatDateLabel(event.date), formatTimeLabel(event.time), event.description].filter(Boolean).join(" - "));
    image = firstMetaImage(getEventImage(event));
  } else if (page === "publicPlayerProfile") {
    title = `${context.accountProfile?.fullName || "Player"} public badminton profile | Badminta`;
    description = cleanMetaText(context.accountProfile?.player?.style, "Public player profile with skill, availability, media and reviews on Badminta.");
    image = firstMetaImage(context.accountProfile?.player?.photoUrl, context.accountProfile?.photoUrl);
    type = "profile";
  } else if (page === "publicCoachProfile") {
    title = `${context.accountProfile?.coach?.headline || context.accountProfile?.fullName || "Coach"} public coaching profile | Badminta`;
    description = cleanMetaText(context.accountProfile?.coach?.bio, "Public coach profile with coaching locations, rates, media and reviews on Badminta.");
    image = firstMetaImage(context.accountProfile?.coach?.photoUrl, context.accountProfile?.photoUrl);
    type = "profile";
  } else if (page === "publicGroupProfile") {
    title = `${context.accountProfile?.group?.name || "Social badminton group"} public group profile | Badminta`;
    description = cleanMetaText(context.accountProfile?.group?.description, "Public social group profile with sessions, joining, members and payments on Badminta.");
    image = firstMetaImage(context.accountProfile?.group?.coverUrl, context.accountProfile?.group?.logoUrl);
  } else if (page === "publicCentreProfile") {
    title = `${context.accountProfile?.centre?.name || "Badminton centre"} public centre profile | Badminta`;
    description = cleanMetaText(context.accountProfile?.centre?.description, "Public centre profile with courts, pricing, booking rules and centre details on Badminta.");
    image = firstMetaImage(context.accountProfile?.centre?.coverUrl, context.accountProfile?.centre?.logoUrl, cleanLinkRows(context.accountProfile?.centre?.photoLinks, "Centre media").map((link) => link.url));
  } else if (privatePageSeoCopy[page]) {
    const [privateTitle, privateDescription] = privatePageSeoCopy[page];
    title = `${privateTitle} | Badminta`;
    description = privateDescription;
  } else if (page === "administrator") {
    title = "Badminta administration | Badminta";
    description = "Secure Badminta administrator tools for user signups, profiles, centres, trust, payments and platform operations.";
  } else if (page === "help") {
    title = "Badminta help, trust and support";
    description = "Get help with Badminta accounts, player profiles, centre listings, social groups, events, payments and trust reports.";
  } else if (roleDashboardRoute) {
    const roleCopy = roleDashboardSeoCopy[roleDashboardRoute];
    title = `${roleCopy.title} | Badminta`;
    description = roleCopy.description;
  } else if (page === "eventManager") {
    title = "Manage badminton events, registrations and payments | Badminta";
    description = "Badminta event tools help organisers and centres manage event pages, registrations, attendee payment status, refunds, messages and payouts.";
  } else if (page === "messages") {
    title = "Badminta messages for bookings, groups, coaching and centre support";
    description = "Manage Badminta messages by player, organiser, centre and coach roles, with booking, payment, event and profile context attached.";
  } else if (page === "saved") {
    title = "Badminta saved centres, bookings and play records";
    description = "Review saved badminton centres, court booking records, joined social sessions, payment status and useful marketplace items in your Badminta account.";
  } else if (["legalTerms", "privacyPolicy", "refundPolicy", "communityPolicy", "cookiePolicy", "youthPolicy", "centrePolicy", "organiserPolicy", "coachPolicy"].includes(page)) {
    const policyByRoute = {
      legalTerms: policyPages.terms,
      privacyPolicy: policyPages.privacy,
      refundPolicy: policyPages.refunds,
      communityPolicy: policyPages.community,
      cookiePolicy: policyPages.cookies,
      youthPolicy: policyPages.youth,
      centrePolicy: policyPages.centre,
      organiserPolicy: policyPages.organiser,
      coachPolicy: policyPages.coach
    };
    const policy = policyByRoute[page] || policyPages.terms;
    title = `${policy.title} | Badminta`;
    description = cleanMetaText(policy.summary, "Badminta marketplace policy.");
  } else if (page === "account") {
    title = "Badminta account dashboard";
    description = "Manage player, organiser, centre and coach roles from one Badminta account.";
  } else if (page === "accountDeletion") {
    title = "Delete your Badminta account";
    description = "Sign in to request deletion of your Badminta account, public profiles and optional media.";
  } else if (page === "signup") {
    title = "Create your free Badminta account";
    description = "Verify your phone and create a Badminta account before publishing a player, organiser, centre or coach profile.";
  } else if (page === "login") {
    title = "Log in to Badminta";
    description = "Log in to manage your Badminta profiles, bookings, groups, events, messages and payments.";
  }

  const meta = {
    title: cleanMetaText(title, "Badminta"),
    description: cleanMetaText(description, "Badminta helps badminton players, groups, coaches and centres connect worldwide."),
    url: absoluteSiteUrl(buildRoutePath(page, context)),
    image,
    type,
    robots: page === "centreUnavailable" ? "noindex, follow" : privateAppSeoPages.has(page) ? "noindex, nofollow" : "index, follow",
    schema
  };
  if (page === "blogArticle" && blogPost) {
    meta.keywords = blogPost.keywords.join(", ");
    meta.imageAlt = blogPost.imageAlt;
    meta.publishedAt = blogPost.publishedAt;
    meta.updatedAt = blogPost.updatedAt;
  }
  if (page === "seoArea") meta.schema = buildSeoAreaSchema(context, meta);
  return meta;
};

const upsertMetaTag = (selector, attributes) => {
  if (typeof document === "undefined") return;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertCanonicalLink = (href) => {
  if (typeof document === "undefined") return;
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const upsertStructuredData = (id, schema) => {
  if (typeof document === "undefined") return;
  const selector = `script[type="application/ld+json"][data-seo-schema="${id}"]`;
  const prerenderSelector = `script[type="application/ld+json"][data-seo-prerender="${id}"]`;
  let element = document.head.querySelector(selector);
  const prerenderElement = document.head.querySelector(prerenderSelector);
  if (!schema) {
    if (element) element.remove();
    // Keep route-specific server-rendered JSON-LD when hydration has no richer
    // client schema for this public URL.
    if (prerenderElement) return;
    return;
  }
  if (!element && prerenderElement) {
    element = prerenderElement;
    element.removeAttribute("data-seo-prerender");
    element.setAttribute("data-seo-schema", id);
  } else if (element && prerenderElement && prerenderElement !== element) {
    prerenderElement.remove();
  }
  if (!element) {
    element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-seo-schema", id);
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(schema);
};

const applySeoMeta = (meta) => {
  if (typeof document === "undefined" || !meta) return;
  document.title = meta.title;
  upsertCanonicalLink(meta.url);
  upsertMetaTag('meta[name="description"]', { name: "description", content: meta.description });
  if (meta.keywords) upsertMetaTag('meta[name="keywords"]', { name: "keywords", content: meta.keywords });
  upsertMetaTag('meta[name="robots"]', { name: "robots", content: meta.robots || "index, follow" });
  upsertMetaTag('meta[property="og:site_name"]', { property: "og:site_name", content: "Badminta" });
  upsertMetaTag('meta[property="og:title"]', { property: "og:title", content: meta.title });
  upsertMetaTag('meta[property="og:description"]', { property: "og:description", content: meta.description });
  upsertMetaTag('meta[property="og:type"]', { property: "og:type", content: meta.type || "website" });
  upsertMetaTag('meta[property="og:url"]', { property: "og:url", content: meta.url });
  upsertMetaTag('meta[property="og:image"]', { property: "og:image", content: meta.image });
  if (meta.imageAlt) upsertMetaTag('meta[property="og:image:alt"]', { property: "og:image:alt", content: meta.imageAlt });
  if (meta.type === "article") {
    upsertMetaTag('meta[property="og:image:width"]', { property: "og:image:width", content: "1800" });
    upsertMetaTag('meta[property="og:image:height"]', { property: "og:image:height", content: "1125" });
    upsertMetaTag('meta[property="article:published_time"]', { property: "article:published_time", content: meta.publishedAt });
    upsertMetaTag('meta[property="article:modified_time"]', { property: "article:modified_time", content: meta.updatedAt });
  }
  upsertMetaTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMetaTag('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
  upsertMetaTag('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
  upsertMetaTag('meta[name="twitter:image"]', { name: "twitter:image", content: meta.image });
  upsertStructuredData("primary", meta.schema);
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error(error);
  }

  render() {
    if (this.state.error) {
      return (
        <main className="app-shell">
          <section className="page auth-page">
            <div className="auth-panel wide">
              <p>Something went wrong</p>
              <h1>Badminta could not open this page.</h1>
              <span>{this.state.error.message || "Refresh the page and try again."}</span>
              <div className="auth-links">
                <button type="button" onClick={() => window.location.assign("/")}>Go home</button>
                <button type="button" onClick={() => window.location.reload()}>Refresh page</button>
              </div>
            </div>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

createRoot(document.getElementById("root")).render(<AppErrorBoundary><App /></AppErrorBoundary>);
